/**
* @license
* 
* (C) Copyright IBM Corp. 2017, 2025. All Rights Reserved.
* 
* Licensed under the Apache License, Version 2.0 (the "License"); you may not use this file except
* in compliance with the License. You may obtain a copy of the License at
* 
* http://www.apache.org/licenses/LICENSE-2.0
* 
* Unless required by applicable law or agreed to in writing, software distributed under the License
* is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express
* or implied. See the License for the specific language governing permissions and limitations under
* the License.
* 
* @carbon/ai-chat 0.3.2
* 
* Built: Jun 11 2025 5:48 pm +00:00
* 
* 
*/

import * as React$1 from 'react';
import React__default, { useContext, useRef, useCallback, useEffect, forwardRef, useImperativeHandle, useState, PureComponent, Suspense, useLayoutEffect, Component, Fragment, useMemo } from 'react';
import { O as OnErrorType, e as CatastrophicErrorType, i as FileStatusValue, C as CarbonTheme, g as CornersType, H as HomeScreenBackgroundType, M as MessageInputType, j as MessageResponseTypes, A as AgentMessageType, b as ButtonItemType, V as ViewChangeReason, k as MessageSendSource, n as TourStartReason, a as AgentsOnlineStatus, p as ViewType, s as InputFieldType, L as LauncherType, W as WriteableElementName, t as __assign, v as __extends, w as __rest$1, x as __spreadArray$1, u as useOnMount, _ as __decorate, c as carbonElement, o as o$1, T as TourEndReason, l as MinimizeButtonIconType, y as ClosePanelIconDirection, q as ChatHeaderObjectType, d as ButtonItemKind, h as CustomEventType, r as WidthOptions, I as IFrameItemDisplayOption, S as ScreenShareState } from './customElement.js';
import { createIntl, useIntl, FormattedMessage, injectIntl, RawIntlProvider } from 'react-intl';
import { ErrorFilled, AiLaunch, ChatLaunch, ArrowUpLeft, Close, Subtract, Chat as Chat$3, ArrowLeft, ArrowRight, Music, Restart, Information, ChevronUp, ChevronDown, SidePanelClose, CloseLarge, SubtractLarge, Menu as Menu$1, DownToBottom, Home, UserAvatar, ScreenOff, Checkmark, Headset, Logout, HelpDesk, TouchInteraction, Send, Launch, DocumentPdf, Link as Link$1, Maximize, Attachment, ChatBot, CheckmarkFilled, DocumentView, Bookmark, UpToTop, SatelliteRadar, SendFilled } from '@carbon/icons-react';
import { Button, Tag, Tile, unstable__AiSkeletonPlaceholder, SkeletonPlaceholder as SkeletonPlaceholder$1, AISkeletonText, SkeletonText as SkeletonText$1, AISkeletonPlaceholder, InlineNotification, Menu, MenuItem, MenuItemRadioGroup, MenuItemDivider, unstable__ChatButton, ActionableNotification, Link, Loading, OperationalTag, Layer, DatePicker, DatePickerInput, Dropdown, Theme, FileUploaderItem, unstable_LayoutDirection, unstable_TextDirection } from '@carbon/react';
import { useSelector, connect, Provider, useDispatch } from 'react-redux';
import { html as html$2, LitElement, css, unsafeCSS, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import '@carbon/web-components/es-custom/components/button/index.js';
import '@carbon/web-components/es-custom/components/overflow-menu/index.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import '@carbon/web-components/es-custom/components/data-table/index.js';
import '@carbon/web-components/es-custom/components/checkbox/index.js';
import { toString as toString$1 } from '@carbon/icon-helpers';
import { Download16, ChevronRight16, CheckmarkFilled16, ErrorFilled16, ThumbsDown16, ThumbsDownFilled16, ThumbsUp16, ThumbsUpFilled16, StopFilled16 } from '@carbon/icons';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { repeat } from 'lit-html/directives/repeat.js';
import '@carbon/web-components/es-custom/components/pagination/index.js';
import '@carbon/web-components/es-custom/components/select/index.js';
import '@carbon/web-components/es-custom/components/data-table/table-skeleton.js';
import '@carbon/web-components/es-custom/components/slug/index.js';
import { AI_LABEL_SIZE } from '@carbon/web-components/es-custom/components/ai-label/defs.js';
import { POPOVER_ALIGNMENT } from '@carbon/web-components/es-custom/components/popover/defs.js';
import '@carbon/web-components/es-custom/components/skeleton-icon/index.js';
import * as ReactDOM from 'react-dom';
import ReactDOM__default from 'react-dom';
import '@carbon/web-components/es-custom/components/ai-label/ai-label-action-button.js';
import CDSAILabel from '@carbon/web-components/es-custom/components/ai-label/ai-label.js';
import '@carbon/web-components/es-custom/components/inline-loading/index.js';
import '@carbon/web-components/es-custom/components/textarea/index.js';
import '@carbon/web-components/es-custom/components/icon-button/index.js';
import '@carbon/web-components/es-custom/components/tag/index.js';
import '@carbon/web-components/es-custom/components/chat-button/index.js';
import CDSButton from '@carbon/web-components/es-custom/components/button/button.js';
import '@carbon/web-components/es-custom/components/layer/index.js';

function _mergeNamespaces(n, m) {
  m.forEach(function (e) {
    e && typeof e !== 'string' && !Array.isArray(e) && Object.keys(e).forEach(function (k) {
      if (k !== 'default' && !(k in n)) {
        var d = Object.getOwnPropertyDescriptor(e, k);
        Object.defineProperty(n, k, d.get ? d : {
          enumerable: true,
          get: function () { return e[k]; }
        });
      }
    });
  });
  return Object.freeze(n);
}

/**
 * This file contains the generic types for the API between a general chat widget and a chat back-end. It is
 * intended to provide base types for a standalone widget and should not contain any imports of other types. This file
 * is the same as wa-types-chat.ts except that contains extensions beyond the watsonx Assistant store API.
 */
const EVENT_NAME_UPDATE_HISTORY = 'update_history';

/**
 * Removes all key-value entries from the list cache.
 *
 * @private
 * @name clear
 * @memberOf ListCache
 */
function listCacheClear() {
  this.__data__ = [];
  this.size = 0;
}

/**
 * Performs a
 * [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * comparison between two values to determine if they are equivalent.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.eq(object, object);
 * // => true
 *
 * _.eq(object, other);
 * // => false
 *
 * _.eq('a', 'a');
 * // => true
 *
 * _.eq('a', Object('a'));
 * // => false
 *
 * _.eq(NaN, NaN);
 * // => true
 */
function eq(value, other) {
  return value === other || (value !== value && other !== other);
}

/**
 * Gets the index at which the `key` is found in `array` of key-value pairs.
 *
 * @private
 * @param {Array} array The array to inspect.
 * @param {*} key The key to search for.
 * @returns {number} Returns the index of the matched value, else `-1`.
 */
function assocIndexOf(array, key) {
  var length = array.length;
  while (length--) {
    if (eq(array[length][0], key)) {
      return length;
    }
  }
  return -1;
}

/** Used for built-in method references. */
var arrayProto = Array.prototype;

/** Built-in value references. */
var splice = arrayProto.splice;

/**
 * Removes `key` and its value from the list cache.
 *
 * @private
 * @name delete
 * @memberOf ListCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function listCacheDelete(key) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    return false;
  }
  var lastIndex = data.length - 1;
  if (index == lastIndex) {
    data.pop();
  } else {
    splice.call(data, index, 1);
  }
  --this.size;
  return true;
}

/**
 * Gets the list cache value for `key`.
 *
 * @private
 * @name get
 * @memberOf ListCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function listCacheGet(key) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  return index < 0 ? undefined : data[index][1];
}

/**
 * Checks if a list cache value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf ListCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function listCacheHas(key) {
  return assocIndexOf(this.__data__, key) > -1;
}

/**
 * Sets the list cache `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf ListCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the list cache instance.
 */
function listCacheSet(key, value) {
  var data = this.__data__,
      index = assocIndexOf(data, key);

  if (index < 0) {
    ++this.size;
    data.push([key, value]);
  } else {
    data[index][1] = value;
  }
  return this;
}

/**
 * Creates an list cache object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function ListCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `ListCache`.
ListCache.prototype.clear = listCacheClear;
ListCache.prototype['delete'] = listCacheDelete;
ListCache.prototype.get = listCacheGet;
ListCache.prototype.has = listCacheHas;
ListCache.prototype.set = listCacheSet;

/**
 * Removes all key-value entries from the stack.
 *
 * @private
 * @name clear
 * @memberOf Stack
 */
function stackClear() {
  this.__data__ = new ListCache;
  this.size = 0;
}

/**
 * Removes `key` and its value from the stack.
 *
 * @private
 * @name delete
 * @memberOf Stack
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function stackDelete(key) {
  var data = this.__data__,
      result = data['delete'](key);

  this.size = data.size;
  return result;
}

/**
 * Gets the stack value for `key`.
 *
 * @private
 * @name get
 * @memberOf Stack
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function stackGet(key) {
  return this.__data__.get(key);
}

/**
 * Checks if a stack value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Stack
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function stackHas(key) {
  return this.__data__.has(key);
}

/** Detect free variable `global` from Node.js. */
var freeGlobal = typeof global == 'object' && global && global.Object === Object && global;

/** Detect free variable `self`. */
var freeSelf = typeof self == 'object' && self && self.Object === Object && self;

/** Used as a reference to the global object. */
var root = freeGlobal || freeSelf || Function('return this')();

/** Built-in value references. */
var Symbol$1 = root.Symbol;

/** Used for built-in method references. */
var objectProto$g = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$e = objectProto$g.hasOwnProperty;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString$1 = objectProto$g.toString;

/** Built-in value references. */
var symToStringTag$1 = Symbol$1 ? Symbol$1.toStringTag : undefined;

/**
 * A specialized version of `baseGetTag` which ignores `Symbol.toStringTag` values.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the raw `toStringTag`.
 */
function getRawTag(value) {
  var isOwn = hasOwnProperty$e.call(value, symToStringTag$1),
      tag = value[symToStringTag$1];

  try {
    value[symToStringTag$1] = undefined;
    var unmasked = true;
  } catch (e) {}

  var result = nativeObjectToString$1.call(value);
  if (unmasked) {
    if (isOwn) {
      value[symToStringTag$1] = tag;
    } else {
      delete value[symToStringTag$1];
    }
  }
  return result;
}

/** Used for built-in method references. */
var objectProto$f = Object.prototype;

/**
 * Used to resolve the
 * [`toStringTag`](http://ecma-international.org/ecma-262/7.0/#sec-object.prototype.tostring)
 * of values.
 */
var nativeObjectToString = objectProto$f.toString;

/**
 * Converts `value` to a string using `Object.prototype.toString`.
 *
 * @private
 * @param {*} value The value to convert.
 * @returns {string} Returns the converted string.
 */
function objectToString(value) {
  return nativeObjectToString.call(value);
}

/** `Object#toString` result references. */
var nullTag = '[object Null]',
    undefinedTag = '[object Undefined]';

/** Built-in value references. */
var symToStringTag = Symbol$1 ? Symbol$1.toStringTag : undefined;

/**
 * The base implementation of `getTag` without fallbacks for buggy environments.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
function baseGetTag(value) {
  if (value == null) {
    return value === undefined ? undefinedTag : nullTag;
  }
  return (symToStringTag && symToStringTag in Object(value))
    ? getRawTag(value)
    : objectToString(value);
}

/**
 * Checks if `value` is the
 * [language type](http://www.ecma-international.org/ecma-262/7.0/#sec-ecmascript-language-types)
 * of `Object`. (e.g. arrays, functions, objects, regexes, `new Number(0)`, and `new String('')`)
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an object, else `false`.
 * @example
 *
 * _.isObject({});
 * // => true
 *
 * _.isObject([1, 2, 3]);
 * // => true
 *
 * _.isObject(_.noop);
 * // => true
 *
 * _.isObject(null);
 * // => false
 */
function isObject$1(value) {
  var type = typeof value;
  return value != null && (type == 'object' || type == 'function');
}

/** `Object#toString` result references. */
var asyncTag = '[object AsyncFunction]',
    funcTag$2 = '[object Function]',
    genTag$1 = '[object GeneratorFunction]',
    proxyTag = '[object Proxy]';

/**
 * Checks if `value` is classified as a `Function` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a function, else `false`.
 * @example
 *
 * _.isFunction(_);
 * // => true
 *
 * _.isFunction(/abc/);
 * // => false
 */
function isFunction$1(value) {
  if (!isObject$1(value)) {
    return false;
  }
  // The use of `Object#toString` avoids issues with the `typeof` operator
  // in Safari 9 which returns 'object' for typed arrays and other constructors.
  var tag = baseGetTag(value);
  return tag == funcTag$2 || tag == genTag$1 || tag == asyncTag || tag == proxyTag;
}

/** Used to detect overreaching core-js shims. */
var coreJsData = root['__core-js_shared__'];

/** Used to detect methods masquerading as native. */
var maskSrcKey = (function() {
  var uid = /[^.]+$/.exec(coreJsData && coreJsData.keys && coreJsData.keys.IE_PROTO || '');
  return uid ? ('Symbol(src)_1.' + uid) : '';
}());

/**
 * Checks if `func` has its source masked.
 *
 * @private
 * @param {Function} func The function to check.
 * @returns {boolean} Returns `true` if `func` is masked, else `false`.
 */
function isMasked(func) {
  return !!maskSrcKey && (maskSrcKey in func);
}

/** Used for built-in method references. */
var funcProto$2 = Function.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$2 = funcProto$2.toString;

/**
 * Converts `func` to its source code.
 *
 * @private
 * @param {Function} func The function to convert.
 * @returns {string} Returns the source code.
 */
function toSource(func) {
  if (func != null) {
    try {
      return funcToString$2.call(func);
    } catch (e) {}
    try {
      return (func + '');
    } catch (e) {}
  }
  return '';
}

/**
 * Used to match `RegExp`
 * [syntax characters](http://ecma-international.org/ecma-262/7.0/#sec-patterns).
 */
var reRegExpChar = /[\\^$.*+?()[\]{}|]/g;

/** Used to detect host constructors (Safari). */
var reIsHostCtor = /^\[object .+?Constructor\]$/;

/** Used for built-in method references. */
var funcProto$1 = Function.prototype,
    objectProto$e = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString$1 = funcProto$1.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$d = objectProto$e.hasOwnProperty;

/** Used to detect if a method is native. */
var reIsNative = RegExp('^' +
  funcToString$1.call(hasOwnProperty$d).replace(reRegExpChar, '\\$&')
  .replace(/hasOwnProperty|(function).*?(?=\\\()| for .+?(?=\\\])/g, '$1.*?') + '$'
);

/**
 * The base implementation of `_.isNative` without bad shim checks.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a native function,
 *  else `false`.
 */
function baseIsNative(value) {
  if (!isObject$1(value) || isMasked(value)) {
    return false;
  }
  var pattern = isFunction$1(value) ? reIsNative : reIsHostCtor;
  return pattern.test(toSource(value));
}

/**
 * Gets the value at `key` of `object`.
 *
 * @private
 * @param {Object} [object] The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function getValue(object, key) {
  return object == null ? undefined : object[key];
}

/**
 * Gets the native function at `key` of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the method to get.
 * @returns {*} Returns the function if it's native, else `undefined`.
 */
function getNative(object, key) {
  var value = getValue(object, key);
  return baseIsNative(value) ? value : undefined;
}

/* Built-in method references that are verified to be native. */
var Map$1 = getNative(root, 'Map');

/* Built-in method references that are verified to be native. */
var nativeCreate = getNative(Object, 'create');

/**
 * Removes all key-value entries from the hash.
 *
 * @private
 * @name clear
 * @memberOf Hash
 */
function hashClear() {
  this.__data__ = nativeCreate ? nativeCreate(null) : {};
  this.size = 0;
}

/**
 * Removes `key` and its value from the hash.
 *
 * @private
 * @name delete
 * @memberOf Hash
 * @param {Object} hash The hash to modify.
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function hashDelete(key) {
  var result = this.has(key) && delete this.__data__[key];
  this.size -= result ? 1 : 0;
  return result;
}

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$2 = '__lodash_hash_undefined__';

/** Used for built-in method references. */
var objectProto$d = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$c = objectProto$d.hasOwnProperty;

/**
 * Gets the hash value for `key`.
 *
 * @private
 * @name get
 * @memberOf Hash
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function hashGet(key) {
  var data = this.__data__;
  if (nativeCreate) {
    var result = data[key];
    return result === HASH_UNDEFINED$2 ? undefined : result;
  }
  return hasOwnProperty$c.call(data, key) ? data[key] : undefined;
}

/** Used for built-in method references. */
var objectProto$c = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$b = objectProto$c.hasOwnProperty;

/**
 * Checks if a hash value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf Hash
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function hashHas(key) {
  var data = this.__data__;
  return nativeCreate ? (data[key] !== undefined) : hasOwnProperty$b.call(data, key);
}

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED$1 = '__lodash_hash_undefined__';

/**
 * Sets the hash `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Hash
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the hash instance.
 */
function hashSet(key, value) {
  var data = this.__data__;
  this.size += this.has(key) ? 0 : 1;
  data[key] = (nativeCreate && value === undefined) ? HASH_UNDEFINED$1 : value;
  return this;
}

/**
 * Creates a hash object.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Hash(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `Hash`.
Hash.prototype.clear = hashClear;
Hash.prototype['delete'] = hashDelete;
Hash.prototype.get = hashGet;
Hash.prototype.has = hashHas;
Hash.prototype.set = hashSet;

/**
 * Removes all key-value entries from the map.
 *
 * @private
 * @name clear
 * @memberOf MapCache
 */
function mapCacheClear() {
  this.size = 0;
  this.__data__ = {
    'hash': new Hash,
    'map': new (Map$1 || ListCache),
    'string': new Hash
  };
}

/**
 * Checks if `value` is suitable for use as unique object key.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is suitable, else `false`.
 */
function isKeyable(value) {
  var type = typeof value;
  return (type == 'string' || type == 'number' || type == 'symbol' || type == 'boolean')
    ? (value !== '__proto__')
    : (value === null);
}

/**
 * Gets the data for `map`.
 *
 * @private
 * @param {Object} map The map to query.
 * @param {string} key The reference key.
 * @returns {*} Returns the map data.
 */
function getMapData(map, key) {
  var data = map.__data__;
  return isKeyable(key)
    ? data[typeof key == 'string' ? 'string' : 'hash']
    : data.map;
}

/**
 * Removes `key` and its value from the map.
 *
 * @private
 * @name delete
 * @memberOf MapCache
 * @param {string} key The key of the value to remove.
 * @returns {boolean} Returns `true` if the entry was removed, else `false`.
 */
function mapCacheDelete(key) {
  var result = getMapData(this, key)['delete'](key);
  this.size -= result ? 1 : 0;
  return result;
}

/**
 * Gets the map value for `key`.
 *
 * @private
 * @name get
 * @memberOf MapCache
 * @param {string} key The key of the value to get.
 * @returns {*} Returns the entry value.
 */
function mapCacheGet(key) {
  return getMapData(this, key).get(key);
}

/**
 * Checks if a map value for `key` exists.
 *
 * @private
 * @name has
 * @memberOf MapCache
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function mapCacheHas(key) {
  return getMapData(this, key).has(key);
}

/**
 * Sets the map `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf MapCache
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the map cache instance.
 */
function mapCacheSet(key, value) {
  var data = getMapData(this, key),
      size = data.size;

  data.set(key, value);
  this.size += data.size == size ? 0 : 1;
  return this;
}

/**
 * Creates a map cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function MapCache(entries) {
  var index = -1,
      length = entries == null ? 0 : entries.length;

  this.clear();
  while (++index < length) {
    var entry = entries[index];
    this.set(entry[0], entry[1]);
  }
}

// Add methods to `MapCache`.
MapCache.prototype.clear = mapCacheClear;
MapCache.prototype['delete'] = mapCacheDelete;
MapCache.prototype.get = mapCacheGet;
MapCache.prototype.has = mapCacheHas;
MapCache.prototype.set = mapCacheSet;

/** Used as the size to enable large array optimizations. */
var LARGE_ARRAY_SIZE = 200;

/**
 * Sets the stack `key` to `value`.
 *
 * @private
 * @name set
 * @memberOf Stack
 * @param {string} key The key of the value to set.
 * @param {*} value The value to set.
 * @returns {Object} Returns the stack cache instance.
 */
function stackSet(key, value) {
  var data = this.__data__;
  if (data instanceof ListCache) {
    var pairs = data.__data__;
    if (!Map$1 || (pairs.length < LARGE_ARRAY_SIZE - 1)) {
      pairs.push([key, value]);
      this.size = ++data.size;
      return this;
    }
    data = this.__data__ = new MapCache(pairs);
  }
  data.set(key, value);
  this.size = data.size;
  return this;
}

/**
 * Creates a stack cache object to store key-value pairs.
 *
 * @private
 * @constructor
 * @param {Array} [entries] The key-value pairs to cache.
 */
function Stack(entries) {
  var data = this.__data__ = new ListCache(entries);
  this.size = data.size;
}

// Add methods to `Stack`.
Stack.prototype.clear = stackClear;
Stack.prototype['delete'] = stackDelete;
Stack.prototype.get = stackGet;
Stack.prototype.has = stackHas;
Stack.prototype.set = stackSet;

/** Used to stand-in for `undefined` hash values. */
var HASH_UNDEFINED = '__lodash_hash_undefined__';

/**
 * Adds `value` to the array cache.
 *
 * @private
 * @name add
 * @memberOf SetCache
 * @alias push
 * @param {*} value The value to cache.
 * @returns {Object} Returns the cache instance.
 */
function setCacheAdd(value) {
  this.__data__.set(value, HASH_UNDEFINED);
  return this;
}

/**
 * Checks if `value` is in the array cache.
 *
 * @private
 * @name has
 * @memberOf SetCache
 * @param {*} value The value to search for.
 * @returns {number} Returns `true` if `value` is found, else `false`.
 */
function setCacheHas(value) {
  return this.__data__.has(value);
}

/**
 *
 * Creates an array cache object to store unique values.
 *
 * @private
 * @constructor
 * @param {Array} [values] The values to cache.
 */
function SetCache(values) {
  var index = -1,
      length = values == null ? 0 : values.length;

  this.__data__ = new MapCache;
  while (++index < length) {
    this.add(values[index]);
  }
}

// Add methods to `SetCache`.
SetCache.prototype.add = SetCache.prototype.push = setCacheAdd;
SetCache.prototype.has = setCacheHas;

/**
 * A specialized version of `_.some` for arrays without support for iteratee
 * shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {boolean} Returns `true` if any element passes the predicate check,
 *  else `false`.
 */
function arraySome(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (predicate(array[index], index, array)) {
      return true;
    }
  }
  return false;
}

/**
 * Checks if a `cache` value for `key` exists.
 *
 * @private
 * @param {Object} cache The cache to query.
 * @param {string} key The key of the entry to check.
 * @returns {boolean} Returns `true` if an entry for `key` exists, else `false`.
 */
function cacheHas(cache, key) {
  return cache.has(key);
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$3 = 1,
    COMPARE_UNORDERED_FLAG$1 = 2;

/**
 * A specialized version of `baseIsEqualDeep` for arrays with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Array} array The array to compare.
 * @param {Array} other The other array to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `array` and `other` objects.
 * @returns {boolean} Returns `true` if the arrays are equivalent, else `false`.
 */
function equalArrays(array, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$3,
      arrLength = array.length,
      othLength = other.length;

  if (arrLength != othLength && !(isPartial && othLength > arrLength)) {
    return false;
  }
  // Check that cyclic values are equal.
  var arrStacked = stack.get(array);
  var othStacked = stack.get(other);
  if (arrStacked && othStacked) {
    return arrStacked == other && othStacked == array;
  }
  var index = -1,
      result = true,
      seen = (bitmask & COMPARE_UNORDERED_FLAG$1) ? new SetCache : undefined;

  stack.set(array, other);
  stack.set(other, array);

  // Ignore non-index properties.
  while (++index < arrLength) {
    var arrValue = array[index],
        othValue = other[index];

    if (customizer) {
      var compared = isPartial
        ? customizer(othValue, arrValue, index, other, array, stack)
        : customizer(arrValue, othValue, index, array, other, stack);
    }
    if (compared !== undefined) {
      if (compared) {
        continue;
      }
      result = false;
      break;
    }
    // Recursively compare arrays (susceptible to call stack limits).
    if (seen) {
      if (!arraySome(other, function(othValue, othIndex) {
            if (!cacheHas(seen, othIndex) &&
                (arrValue === othValue || equalFunc(arrValue, othValue, bitmask, customizer, stack))) {
              return seen.push(othIndex);
            }
          })) {
        result = false;
        break;
      }
    } else if (!(
          arrValue === othValue ||
            equalFunc(arrValue, othValue, bitmask, customizer, stack)
        )) {
      result = false;
      break;
    }
  }
  stack['delete'](array);
  stack['delete'](other);
  return result;
}

/** Built-in value references. */
var Uint8Array$1 = root.Uint8Array;

/**
 * Converts `map` to its key-value pairs.
 *
 * @private
 * @param {Object} map The map to convert.
 * @returns {Array} Returns the key-value pairs.
 */
function mapToArray(map) {
  var index = -1,
      result = Array(map.size);

  map.forEach(function(value, key) {
    result[++index] = [key, value];
  });
  return result;
}

/**
 * Converts `set` to an array of its values.
 *
 * @private
 * @param {Object} set The set to convert.
 * @returns {Array} Returns the values.
 */
function setToArray(set) {
  var index = -1,
      result = Array(set.size);

  set.forEach(function(value) {
    result[++index] = value;
  });
  return result;
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$2 = 1,
    COMPARE_UNORDERED_FLAG = 2;

/** `Object#toString` result references. */
var boolTag$3 = '[object Boolean]',
    dateTag$3 = '[object Date]',
    errorTag$2 = '[object Error]',
    mapTag$6 = '[object Map]',
    numberTag$3 = '[object Number]',
    regexpTag$3 = '[object RegExp]',
    setTag$6 = '[object Set]',
    stringTag$3 = '[object String]',
    symbolTag$3 = '[object Symbol]';

var arrayBufferTag$3 = '[object ArrayBuffer]',
    dataViewTag$4 = '[object DataView]';

/** Used to convert symbols to primitives and strings. */
var symbolProto$1 = Symbol$1 ? Symbol$1.prototype : undefined,
    symbolValueOf$1 = symbolProto$1 ? symbolProto$1.valueOf : undefined;

/**
 * A specialized version of `baseIsEqualDeep` for comparing objects of
 * the same `toStringTag`.
 *
 * **Note:** This function only supports comparing values with tags of
 * `Boolean`, `Date`, `Error`, `Number`, `RegExp`, or `String`.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {string} tag The `toStringTag` of the objects to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalByTag(object, other, tag, bitmask, customizer, equalFunc, stack) {
  switch (tag) {
    case dataViewTag$4:
      if ((object.byteLength != other.byteLength) ||
          (object.byteOffset != other.byteOffset)) {
        return false;
      }
      object = object.buffer;
      other = other.buffer;

    case arrayBufferTag$3:
      if ((object.byteLength != other.byteLength) ||
          !equalFunc(new Uint8Array$1(object), new Uint8Array$1(other))) {
        return false;
      }
      return true;

    case boolTag$3:
    case dateTag$3:
    case numberTag$3:
      // Coerce booleans to `1` or `0` and dates to milliseconds.
      // Invalid dates are coerced to `NaN`.
      return eq(+object, +other);

    case errorTag$2:
      return object.name == other.name && object.message == other.message;

    case regexpTag$3:
    case stringTag$3:
      // Coerce regexes to strings and treat strings, primitives and objects,
      // as equal. See http://www.ecma-international.org/ecma-262/7.0/#sec-regexp.prototype.tostring
      // for more details.
      return object == (other + '');

    case mapTag$6:
      var convert = mapToArray;

    case setTag$6:
      var isPartial = bitmask & COMPARE_PARTIAL_FLAG$2;
      convert || (convert = setToArray);

      if (object.size != other.size && !isPartial) {
        return false;
      }
      // Assume cyclic values are equal.
      var stacked = stack.get(object);
      if (stacked) {
        return stacked == other;
      }
      bitmask |= COMPARE_UNORDERED_FLAG;

      // Recursively compare objects (susceptible to call stack limits).
      stack.set(object, other);
      var result = equalArrays(convert(object), convert(other), bitmask, customizer, equalFunc, stack);
      stack['delete'](object);
      return result;

    case symbolTag$3:
      if (symbolValueOf$1) {
        return symbolValueOf$1.call(object) == symbolValueOf$1.call(other);
      }
  }
  return false;
}

/**
 * Appends the elements of `values` to `array`.
 *
 * @private
 * @param {Array} array The array to modify.
 * @param {Array} values The values to append.
 * @returns {Array} Returns `array`.
 */
function arrayPush$1(array, values) {
  var index = -1,
      length = values.length,
      offset = array.length;

  while (++index < length) {
    array[offset + index] = values[index];
  }
  return array;
}

/**
 * Checks if `value` is classified as an `Array` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array, else `false`.
 * @example
 *
 * _.isArray([1, 2, 3]);
 * // => true
 *
 * _.isArray(document.body.children);
 * // => false
 *
 * _.isArray('abc');
 * // => false
 *
 * _.isArray(_.noop);
 * // => false
 */
var isArray$1 = Array.isArray;

/**
 * The base implementation of `getAllKeys` and `getAllKeysIn` which uses
 * `keysFunc` and `symbolsFunc` to get the enumerable property names and
 * symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @param {Function} symbolsFunc The function to get the symbols of `object`.
 * @returns {Array} Returns the array of property names and symbols.
 */
function baseGetAllKeys(object, keysFunc, symbolsFunc) {
  var result = keysFunc(object);
  return isArray$1(object) ? result : arrayPush$1(result, symbolsFunc(object));
}

/**
 * A specialized version of `_.filter` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} predicate The function invoked per iteration.
 * @returns {Array} Returns the new filtered array.
 */
function arrayFilter(array, predicate) {
  var index = -1,
      length = array == null ? 0 : array.length,
      resIndex = 0,
      result = [];

  while (++index < length) {
    var value = array[index];
    if (predicate(value, index, array)) {
      result[resIndex++] = value;
    }
  }
  return result;
}

/**
 * This method returns a new empty array.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {Array} Returns the new empty array.
 * @example
 *
 * var arrays = _.times(2, _.stubArray);
 *
 * console.log(arrays);
 * // => [[], []]
 *
 * console.log(arrays[0] === arrays[1]);
 * // => false
 */
function stubArray() {
  return [];
}

/** Used for built-in method references. */
var objectProto$b = Object.prototype;

/** Built-in value references. */
var propertyIsEnumerable$1 = objectProto$b.propertyIsEnumerable;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeGetSymbols = Object.getOwnPropertySymbols;

/**
 * Creates an array of the own enumerable symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of symbols.
 */
var getSymbols = !nativeGetSymbols ? stubArray : function(object) {
  if (object == null) {
    return [];
  }
  object = Object(object);
  return arrayFilter(nativeGetSymbols(object), function(symbol) {
    return propertyIsEnumerable$1.call(object, symbol);
  });
};

/**
 * The base implementation of `_.times` without support for iteratee shorthands
 * or max array length checks.
 *
 * @private
 * @param {number} n The number of times to invoke `iteratee`.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns the array of results.
 */
function baseTimes(n, iteratee) {
  var index = -1,
      result = Array(n);

  while (++index < n) {
    result[index] = iteratee(index);
  }
  return result;
}

/**
 * Checks if `value` is object-like. A value is object-like if it's not `null`
 * and has a `typeof` result of "object".
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is object-like, else `false`.
 * @example
 *
 * _.isObjectLike({});
 * // => true
 *
 * _.isObjectLike([1, 2, 3]);
 * // => true
 *
 * _.isObjectLike(_.noop);
 * // => false
 *
 * _.isObjectLike(null);
 * // => false
 */
function isObjectLike(value) {
  return value != null && typeof value == 'object';
}

/** `Object#toString` result references. */
var argsTag$3 = '[object Arguments]';

/**
 * The base implementation of `_.isArguments`.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 */
function baseIsArguments(value) {
  return isObjectLike(value) && baseGetTag(value) == argsTag$3;
}

/** Used for built-in method references. */
var objectProto$a = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$a = objectProto$a.hasOwnProperty;

/** Built-in value references. */
var propertyIsEnumerable = objectProto$a.propertyIsEnumerable;

/**
 * Checks if `value` is likely an `arguments` object.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an `arguments` object,
 *  else `false`.
 * @example
 *
 * _.isArguments(function() { return arguments; }());
 * // => true
 *
 * _.isArguments([1, 2, 3]);
 * // => false
 */
var isArguments = baseIsArguments(function() { return arguments; }()) ? baseIsArguments : function(value) {
  return isObjectLike(value) && hasOwnProperty$a.call(value, 'callee') &&
    !propertyIsEnumerable.call(value, 'callee');
};

var commonjsGlobal$1 = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof global !== 'undefined' ? global : typeof self !== 'undefined' ? self : {};

function getDefaultExportFromCjs$1 (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

function getDefaultExportFromNamespaceIfNotNamed (n) {
	return n && Object.prototype.hasOwnProperty.call(n, 'default') && Object.keys(n).length === 1 ? n['default'] : n;
}

/**
 * This method returns `false`.
 *
 * @static
 * @memberOf _
 * @since 4.13.0
 * @category Util
 * @returns {boolean} Returns `false`.
 * @example
 *
 * _.times(2, _.stubFalse);
 * // => [false, false]
 */
function stubFalse() {
  return false;
}

/** Detect free variable `exports`. */
var freeExports$2 = typeof exports == 'object' && exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule$2 = freeExports$2 && typeof module == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports$2 = freeModule$2 && freeModule$2.exports === freeExports$2;

/** Built-in value references. */
var Buffer$2 = moduleExports$2 ? root.Buffer : undefined;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeIsBuffer = Buffer$2 ? Buffer$2.isBuffer : undefined;

/**
 * Checks if `value` is a buffer.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a buffer, else `false`.
 * @example
 *
 * _.isBuffer(new Buffer(2));
 * // => true
 *
 * _.isBuffer(new Uint8Array(2));
 * // => false
 */
var isBuffer$1 = nativeIsBuffer || stubFalse;

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER$1 = 9007199254740991;

/** Used to detect unsigned integer values. */
var reIsUint = /^(?:0|[1-9]\d*)$/;

/**
 * Checks if `value` is a valid array-like index.
 *
 * @private
 * @param {*} value The value to check.
 * @param {number} [length=MAX_SAFE_INTEGER] The upper bounds of a valid index.
 * @returns {boolean} Returns `true` if `value` is a valid index, else `false`.
 */
function isIndex(value, length) {
  var type = typeof value;
  length = length == null ? MAX_SAFE_INTEGER$1 : length;

  return !!length &&
    (type == 'number' ||
      (type != 'symbol' && reIsUint.test(value))) &&
        (value > -1 && value % 1 == 0 && value < length);
}

/** Used as references for various `Number` constants. */
var MAX_SAFE_INTEGER = 9007199254740991;

/**
 * Checks if `value` is a valid array-like length.
 *
 * **Note:** This method is loosely based on
 * [`ToLength`](http://ecma-international.org/ecma-262/7.0/#sec-tolength).
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a valid length, else `false`.
 * @example
 *
 * _.isLength(3);
 * // => true
 *
 * _.isLength(Number.MIN_VALUE);
 * // => false
 *
 * _.isLength(Infinity);
 * // => false
 *
 * _.isLength('3');
 * // => false
 */
function isLength(value) {
  return typeof value == 'number' &&
    value > -1 && value % 1 == 0 && value <= MAX_SAFE_INTEGER;
}

/** `Object#toString` result references. */
var argsTag$2 = '[object Arguments]',
    arrayTag$2 = '[object Array]',
    boolTag$2 = '[object Boolean]',
    dateTag$2 = '[object Date]',
    errorTag$1 = '[object Error]',
    funcTag$1 = '[object Function]',
    mapTag$5 = '[object Map]',
    numberTag$2 = '[object Number]',
    objectTag$4 = '[object Object]',
    regexpTag$2 = '[object RegExp]',
    setTag$5 = '[object Set]',
    stringTag$2 = '[object String]',
    weakMapTag$2 = '[object WeakMap]';

var arrayBufferTag$2 = '[object ArrayBuffer]',
    dataViewTag$3 = '[object DataView]',
    float32Tag$2 = '[object Float32Array]',
    float64Tag$2 = '[object Float64Array]',
    int8Tag$2 = '[object Int8Array]',
    int16Tag$2 = '[object Int16Array]',
    int32Tag$2 = '[object Int32Array]',
    uint8Tag$2 = '[object Uint8Array]',
    uint8ClampedTag$2 = '[object Uint8ClampedArray]',
    uint16Tag$2 = '[object Uint16Array]',
    uint32Tag$2 = '[object Uint32Array]';

/** Used to identify `toStringTag` values of typed arrays. */
var typedArrayTags = {};
typedArrayTags[float32Tag$2] = typedArrayTags[float64Tag$2] =
typedArrayTags[int8Tag$2] = typedArrayTags[int16Tag$2] =
typedArrayTags[int32Tag$2] = typedArrayTags[uint8Tag$2] =
typedArrayTags[uint8ClampedTag$2] = typedArrayTags[uint16Tag$2] =
typedArrayTags[uint32Tag$2] = true;
typedArrayTags[argsTag$2] = typedArrayTags[arrayTag$2] =
typedArrayTags[arrayBufferTag$2] = typedArrayTags[boolTag$2] =
typedArrayTags[dataViewTag$3] = typedArrayTags[dateTag$2] =
typedArrayTags[errorTag$1] = typedArrayTags[funcTag$1] =
typedArrayTags[mapTag$5] = typedArrayTags[numberTag$2] =
typedArrayTags[objectTag$4] = typedArrayTags[regexpTag$2] =
typedArrayTags[setTag$5] = typedArrayTags[stringTag$2] =
typedArrayTags[weakMapTag$2] = false;

/**
 * The base implementation of `_.isTypedArray` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 */
function baseIsTypedArray(value) {
  return isObjectLike(value) &&
    isLength(value.length) && !!typedArrayTags[baseGetTag(value)];
}

/**
 * The base implementation of `_.unary` without support for storing metadata.
 *
 * @private
 * @param {Function} func The function to cap arguments for.
 * @returns {Function} Returns the new capped function.
 */
function baseUnary(func) {
  return function(value) {
    return func(value);
  };
}

/** Detect free variable `exports`. */
var freeExports$1 = typeof exports == 'object' && exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule$1 = freeExports$1 && typeof module == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports$1 = freeModule$1 && freeModule$1.exports === freeExports$1;

/** Detect free variable `process` from Node.js. */
var freeProcess = moduleExports$1 && freeGlobal.process;

/** Used to access faster Node.js helpers. */
var nodeUtil = (function() {
  try {
    // Use `util.types` for Node.js 10+.
    var types = freeModule$1 && freeModule$1.require && freeModule$1.require('util').types;

    if (types) {
      return types;
    }

    // Legacy `process.binding('util')` for Node.js < 10.
    return freeProcess && freeProcess.binding && freeProcess.binding('util');
  } catch (e) {}
}());

/* Node.js helper references. */
var nodeIsTypedArray = nodeUtil && nodeUtil.isTypedArray;

/**
 * Checks if `value` is classified as a typed array.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a typed array, else `false`.
 * @example
 *
 * _.isTypedArray(new Uint8Array);
 * // => true
 *
 * _.isTypedArray([]);
 * // => false
 */
var isTypedArray = nodeIsTypedArray ? baseUnary(nodeIsTypedArray) : baseIsTypedArray;

/** Used for built-in method references. */
var objectProto$9 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$9 = objectProto$9.hasOwnProperty;

/**
 * Creates an array of the enumerable property names of the array-like `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @param {boolean} inherited Specify returning inherited property names.
 * @returns {Array} Returns the array of property names.
 */
function arrayLikeKeys(value, inherited) {
  var isArr = isArray$1(value),
      isArg = !isArr && isArguments(value),
      isBuff = !isArr && !isArg && isBuffer$1(value),
      isType = !isArr && !isArg && !isBuff && isTypedArray(value),
      skipIndexes = isArr || isArg || isBuff || isType,
      result = skipIndexes ? baseTimes(value.length, String) : [],
      length = result.length;

  for (var key in value) {
    if ((inherited || hasOwnProperty$9.call(value, key)) &&
        !(skipIndexes && (
           // Safari 9 has enumerable `arguments.length` in strict mode.
           key == 'length' ||
           // Node.js 0.10 has enumerable non-index properties on buffers.
           (isBuff && (key == 'offset' || key == 'parent')) ||
           // PhantomJS 2 has enumerable non-index properties on typed arrays.
           (isType && (key == 'buffer' || key == 'byteLength' || key == 'byteOffset')) ||
           // Skip index properties.
           isIndex(key, length)
        ))) {
      result.push(key);
    }
  }
  return result;
}

/** Used for built-in method references. */
var objectProto$8 = Object.prototype;

/**
 * Checks if `value` is likely a prototype object.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a prototype, else `false`.
 */
function isPrototype(value) {
  var Ctor = value && value.constructor,
      proto = (typeof Ctor == 'function' && Ctor.prototype) || objectProto$8;

  return value === proto;
}

/**
 * Creates a unary function that invokes `func` with its argument transformed.
 *
 * @private
 * @param {Function} func The function to wrap.
 * @param {Function} transform The argument transform.
 * @returns {Function} Returns the new function.
 */
function overArg(func, transform) {
  return function(arg) {
    return func(transform(arg));
  };
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeKeys = overArg(Object.keys, Object);

/** Used for built-in method references. */
var objectProto$7 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$8 = objectProto$7.hasOwnProperty;

/**
 * The base implementation of `_.keys` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeys(object) {
  if (!isPrototype(object)) {
    return nativeKeys(object);
  }
  var result = [];
  for (var key in Object(object)) {
    if (hasOwnProperty$8.call(object, key) && key != 'constructor') {
      result.push(key);
    }
  }
  return result;
}

/**
 * Checks if `value` is array-like. A value is considered array-like if it's
 * not a function and has a `value.length` that's an integer greater than or
 * equal to `0` and less than or equal to `Number.MAX_SAFE_INTEGER`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is array-like, else `false`.
 * @example
 *
 * _.isArrayLike([1, 2, 3]);
 * // => true
 *
 * _.isArrayLike(document.body.children);
 * // => true
 *
 * _.isArrayLike('abc');
 * // => true
 *
 * _.isArrayLike(_.noop);
 * // => false
 */
function isArrayLike(value) {
  return value != null && isLength(value.length) && !isFunction$1(value);
}

/**
 * Creates an array of the own enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects. See the
 * [ES spec](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * for more details.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keys(new Foo);
 * // => ['a', 'b'] (iteration order is not guaranteed)
 *
 * _.keys('hi');
 * // => ['0', '1']
 */
function keys(object) {
  return isArrayLike(object) ? arrayLikeKeys(object) : baseKeys(object);
}

/**
 * Creates an array of own enumerable property names and symbols of `object`.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names and symbols.
 */
function getAllKeys(object) {
  return baseGetAllKeys(object, keys, getSymbols);
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG$1 = 1;

/** Used for built-in method references. */
var objectProto$6 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$7 = objectProto$6.hasOwnProperty;

/**
 * A specialized version of `baseIsEqualDeep` for objects with support for
 * partial deep comparisons.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} stack Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function equalObjects(object, other, bitmask, customizer, equalFunc, stack) {
  var isPartial = bitmask & COMPARE_PARTIAL_FLAG$1,
      objProps = getAllKeys(object),
      objLength = objProps.length,
      othProps = getAllKeys(other),
      othLength = othProps.length;

  if (objLength != othLength && !isPartial) {
    return false;
  }
  var index = objLength;
  while (index--) {
    var key = objProps[index];
    if (!(isPartial ? key in other : hasOwnProperty$7.call(other, key))) {
      return false;
    }
  }
  // Check that cyclic values are equal.
  var objStacked = stack.get(object);
  var othStacked = stack.get(other);
  if (objStacked && othStacked) {
    return objStacked == other && othStacked == object;
  }
  var result = true;
  stack.set(object, other);
  stack.set(other, object);

  var skipCtor = isPartial;
  while (++index < objLength) {
    key = objProps[index];
    var objValue = object[key],
        othValue = other[key];

    if (customizer) {
      var compared = isPartial
        ? customizer(othValue, objValue, key, other, object, stack)
        : customizer(objValue, othValue, key, object, other, stack);
    }
    // Recursively compare objects (susceptible to call stack limits).
    if (!(compared === undefined
          ? (objValue === othValue || equalFunc(objValue, othValue, bitmask, customizer, stack))
          : compared
        )) {
      result = false;
      break;
    }
    skipCtor || (skipCtor = key == 'constructor');
  }
  if (result && !skipCtor) {
    var objCtor = object.constructor,
        othCtor = other.constructor;

    // Non `Object` object instances with different constructors are not equal.
    if (objCtor != othCtor &&
        ('constructor' in object && 'constructor' in other) &&
        !(typeof objCtor == 'function' && objCtor instanceof objCtor &&
          typeof othCtor == 'function' && othCtor instanceof othCtor)) {
      result = false;
    }
  }
  stack['delete'](object);
  stack['delete'](other);
  return result;
}

/* Built-in method references that are verified to be native. */
var DataView = getNative(root, 'DataView');

/* Built-in method references that are verified to be native. */
var Promise$1 = getNative(root, 'Promise');

/* Built-in method references that are verified to be native. */
var Set$1 = getNative(root, 'Set');

/* Built-in method references that are verified to be native. */
var WeakMap$1 = getNative(root, 'WeakMap');

/** `Object#toString` result references. */
var mapTag$4 = '[object Map]',
    objectTag$3 = '[object Object]',
    promiseTag = '[object Promise]',
    setTag$4 = '[object Set]',
    weakMapTag$1 = '[object WeakMap]';

var dataViewTag$2 = '[object DataView]';

/** Used to detect maps, sets, and weakmaps. */
var dataViewCtorString = toSource(DataView),
    mapCtorString = toSource(Map$1),
    promiseCtorString = toSource(Promise$1),
    setCtorString = toSource(Set$1),
    weakMapCtorString = toSource(WeakMap$1);

/**
 * Gets the `toStringTag` of `value`.
 *
 * @private
 * @param {*} value The value to query.
 * @returns {string} Returns the `toStringTag`.
 */
var getTag$1 = baseGetTag;

// Fallback for data views, maps, sets, and weak maps in IE 11 and promises in Node.js < 6.
if ((DataView && getTag$1(new DataView(new ArrayBuffer(1))) != dataViewTag$2) ||
    (Map$1 && getTag$1(new Map$1) != mapTag$4) ||
    (Promise$1 && getTag$1(Promise$1.resolve()) != promiseTag) ||
    (Set$1 && getTag$1(new Set$1) != setTag$4) ||
    (WeakMap$1 && getTag$1(new WeakMap$1) != weakMapTag$1)) {
  getTag$1 = function(value) {
    var result = baseGetTag(value),
        Ctor = result == objectTag$3 ? value.constructor : undefined,
        ctorString = Ctor ? toSource(Ctor) : '';

    if (ctorString) {
      switch (ctorString) {
        case dataViewCtorString: return dataViewTag$2;
        case mapCtorString: return mapTag$4;
        case promiseCtorString: return promiseTag;
        case setCtorString: return setTag$4;
        case weakMapCtorString: return weakMapTag$1;
      }
    }
    return result;
  };
}

/** Used to compose bitmasks for value comparisons. */
var COMPARE_PARTIAL_FLAG = 1;

/** `Object#toString` result references. */
var argsTag$1 = '[object Arguments]',
    arrayTag$1 = '[object Array]',
    objectTag$2 = '[object Object]';

/** Used for built-in method references. */
var objectProto$5 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$6 = objectProto$5.hasOwnProperty;

/**
 * A specialized version of `baseIsEqual` for arrays and objects which performs
 * deep comparisons and tracks traversed objects enabling objects with circular
 * references to be compared.
 *
 * @private
 * @param {Object} object The object to compare.
 * @param {Object} other The other object to compare.
 * @param {number} bitmask The bitmask flags. See `baseIsEqual` for more details.
 * @param {Function} customizer The function to customize comparisons.
 * @param {Function} equalFunc The function to determine equivalents of values.
 * @param {Object} [stack] Tracks traversed `object` and `other` objects.
 * @returns {boolean} Returns `true` if the objects are equivalent, else `false`.
 */
function baseIsEqualDeep(object, other, bitmask, customizer, equalFunc, stack) {
  var objIsArr = isArray$1(object),
      othIsArr = isArray$1(other),
      objTag = objIsArr ? arrayTag$1 : getTag$1(object),
      othTag = othIsArr ? arrayTag$1 : getTag$1(other);

  objTag = objTag == argsTag$1 ? objectTag$2 : objTag;
  othTag = othTag == argsTag$1 ? objectTag$2 : othTag;

  var objIsObj = objTag == objectTag$2,
      othIsObj = othTag == objectTag$2,
      isSameTag = objTag == othTag;

  if (isSameTag && isBuffer$1(object)) {
    if (!isBuffer$1(other)) {
      return false;
    }
    objIsArr = true;
    objIsObj = false;
  }
  if (isSameTag && !objIsObj) {
    stack || (stack = new Stack);
    return (objIsArr || isTypedArray(object))
      ? equalArrays(object, other, bitmask, customizer, equalFunc, stack)
      : equalByTag(object, other, objTag, bitmask, customizer, equalFunc, stack);
  }
  if (!(bitmask & COMPARE_PARTIAL_FLAG)) {
    var objIsWrapped = objIsObj && hasOwnProperty$6.call(object, '__wrapped__'),
        othIsWrapped = othIsObj && hasOwnProperty$6.call(other, '__wrapped__');

    if (objIsWrapped || othIsWrapped) {
      var objUnwrapped = objIsWrapped ? object.value() : object,
          othUnwrapped = othIsWrapped ? other.value() : other;

      stack || (stack = new Stack);
      return equalFunc(objUnwrapped, othUnwrapped, bitmask, customizer, stack);
    }
  }
  if (!isSameTag) {
    return false;
  }
  stack || (stack = new Stack);
  return equalObjects(object, other, bitmask, customizer, equalFunc, stack);
}

/**
 * The base implementation of `_.isEqual` which supports partial comparisons
 * and tracks traversed objects.
 *
 * @private
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Unordered comparison
 *  2 - Partial comparison
 * @param {Function} [customizer] The function to customize comparisons.
 * @param {Object} [stack] Tracks traversed `value` and `other` objects.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 */
function baseIsEqual(value, other, bitmask, customizer, stack) {
  if (value === other) {
    return true;
  }
  if (value == null || other == null || (!isObjectLike(value) && !isObjectLike(other))) {
    return value !== value && other !== other;
  }
  return baseIsEqualDeep(value, other, bitmask, customizer, baseIsEqual, stack);
}

/**
 * Performs a deep comparison between two values to determine if they are
 * equivalent.
 *
 * **Note:** This method supports comparing arrays, array buffers, booleans,
 * date objects, error objects, maps, numbers, `Object` objects, regexes,
 * sets, strings, symbols, and typed arrays. `Object` objects are compared
 * by their own, not inherited, enumerable properties. Functions and DOM
 * nodes are compared by strict equality, i.e. `===`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to compare.
 * @param {*} other The other value to compare.
 * @returns {boolean} Returns `true` if the values are equivalent, else `false`.
 * @example
 *
 * var object = { 'a': 1 };
 * var other = { 'a': 1 };
 *
 * _.isEqual(object, other);
 * // => true
 *
 * object === other;
 * // => false
 */
function isEqual$1(value, other) {
  return baseIsEqual(value, other);
}

var dayjs_min = {exports: {}};

(function (module, exports) {
	!function(t,e){module.exports=e();}(commonjsGlobal$1,(function(){var t=1e3,e=6e4,n=36e5,r="millisecond",i="second",s="minute",u="hour",a="day",o="week",c="month",f="quarter",h="year",d="date",l="Invalid Date",$=/^(\d{4})[-/]?(\d{1,2})?[-/]?(\d{0,2})[Tt\s]*(\d{1,2})?:?(\d{1,2})?:?(\d{1,2})?[.:]?(\d+)?$/,y=/\[([^\]]+)]|Y{1,4}|M{1,4}|D{1,2}|d{1,4}|H{1,2}|h{1,2}|a|A|m{1,2}|s{1,2}|Z{1,2}|SSS/g,M={name:"en",weekdays:"Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"),months:"January_February_March_April_May_June_July_August_September_October_November_December".split("_"),ordinal:function(t){var e=["th","st","nd","rd"],n=t%100;return "["+t+(e[(n-20)%10]||e[n]||e[0])+"]"}},m=function(t,e,n){var r=String(t);return !r||r.length>=e?t:""+Array(e+1-r.length).join(n)+t},v={s:m,z:function(t){var e=-t.utcOffset(),n=Math.abs(e),r=Math.floor(n/60),i=n%60;return (e<=0?"+":"-")+m(r,2,"0")+":"+m(i,2,"0")},m:function t(e,n){if(e.date()<n.date())return -t(n,e);var r=12*(n.year()-e.year())+(n.month()-e.month()),i=e.clone().add(r,c),s=n-i<0,u=e.clone().add(r+(s?-1:1),c);return +(-(r+(n-i)/(s?i-u:u-i))||0)},a:function(t){return t<0?Math.ceil(t)||0:Math.floor(t)},p:function(t){return {M:c,y:h,w:o,d:a,D:d,h:u,m:s,s:i,ms:r,Q:f}[t]||String(t||"").toLowerCase().replace(/s$/,"")},u:function(t){return void 0===t}},g="en",D={};D[g]=M;var p="$isDayjsObject",S=function(t){return t instanceof _||!(!t||!t[p])},w=function t(e,n,r){var i;if(!e)return g;if("string"==typeof e){var s=e.toLowerCase();D[s]&&(i=s),n&&(D[s]=n,i=s);var u=e.split("-");if(!i&&u.length>1)return t(u[0])}else {var a=e.name;D[a]=e,i=a;}return !r&&i&&(g=i),i||!r&&g},O=function(t,e){if(S(t))return t.clone();var n="object"==typeof e?e:{};return n.date=t,n.args=arguments,new _(n)},b=v;b.l=w,b.i=S,b.w=function(t,e){return O(t,{locale:e.$L,utc:e.$u,x:e.$x,$offset:e.$offset})};var _=function(){function M(t){this.$L=w(t.locale,null,!0),this.parse(t),this.$x=this.$x||t.x||{},this[p]=!0;}var m=M.prototype;return m.parse=function(t){this.$d=function(t){var e=t.date,n=t.utc;if(null===e)return new Date(NaN);if(b.u(e))return new Date;if(e instanceof Date)return new Date(e);if("string"==typeof e&&!/Z$/i.test(e)){var r=e.match($);if(r){var i=r[2]-1||0,s=(r[7]||"0").substring(0,3);return n?new Date(Date.UTC(r[1],i,r[3]||1,r[4]||0,r[5]||0,r[6]||0,s)):new Date(r[1],i,r[3]||1,r[4]||0,r[5]||0,r[6]||0,s)}}return new Date(e)}(t),this.init();},m.init=function(){var t=this.$d;this.$y=t.getFullYear(),this.$M=t.getMonth(),this.$D=t.getDate(),this.$W=t.getDay(),this.$H=t.getHours(),this.$m=t.getMinutes(),this.$s=t.getSeconds(),this.$ms=t.getMilliseconds();},m.$utils=function(){return b},m.isValid=function(){return !(this.$d.toString()===l)},m.isSame=function(t,e){var n=O(t);return this.startOf(e)<=n&&n<=this.endOf(e)},m.isAfter=function(t,e){return O(t)<this.startOf(e)},m.isBefore=function(t,e){return this.endOf(e)<O(t)},m.$g=function(t,e,n){return b.u(t)?this[e]:this.set(n,t)},m.unix=function(){return Math.floor(this.valueOf()/1e3)},m.valueOf=function(){return this.$d.getTime()},m.startOf=function(t,e){var n=this,r=!!b.u(e)||e,f=b.p(t),l=function(t,e){var i=b.w(n.$u?Date.UTC(n.$y,e,t):new Date(n.$y,e,t),n);return r?i:i.endOf(a)},$=function(t,e){return b.w(n.toDate()[t].apply(n.toDate("s"),(r?[0,0,0,0]:[23,59,59,999]).slice(e)),n)},y=this.$W,M=this.$M,m=this.$D,v="set"+(this.$u?"UTC":"");switch(f){case h:return r?l(1,0):l(31,11);case c:return r?l(1,M):l(0,M+1);case o:var g=this.$locale().weekStart||0,D=(y<g?y+7:y)-g;return l(r?m-D:m+(6-D),M);case a:case d:return $(v+"Hours",0);case u:return $(v+"Minutes",1);case s:return $(v+"Seconds",2);case i:return $(v+"Milliseconds",3);default:return this.clone()}},m.endOf=function(t){return this.startOf(t,!1)},m.$set=function(t,e){var n,o=b.p(t),f="set"+(this.$u?"UTC":""),l=(n={},n[a]=f+"Date",n[d]=f+"Date",n[c]=f+"Month",n[h]=f+"FullYear",n[u]=f+"Hours",n[s]=f+"Minutes",n[i]=f+"Seconds",n[r]=f+"Milliseconds",n)[o],$=o===a?this.$D+(e-this.$W):e;if(o===c||o===h){var y=this.clone().set(d,1);y.$d[l]($),y.init(),this.$d=y.set(d,Math.min(this.$D,y.daysInMonth())).$d;}else l&&this.$d[l]($);return this.init(),this},m.set=function(t,e){return this.clone().$set(t,e)},m.get=function(t){return this[b.p(t)]()},m.add=function(r,f){var d,l=this;r=Number(r);var $=b.p(f),y=function(t){var e=O(l);return b.w(e.date(e.date()+Math.round(t*r)),l)};if($===c)return this.set(c,this.$M+r);if($===h)return this.set(h,this.$y+r);if($===a)return y(1);if($===o)return y(7);var M=(d={},d[s]=e,d[u]=n,d[i]=t,d)[$]||1,m=this.$d.getTime()+r*M;return b.w(m,this)},m.subtract=function(t,e){return this.add(-1*t,e)},m.format=function(t){var e=this,n=this.$locale();if(!this.isValid())return n.invalidDate||l;var r=t||"YYYY-MM-DDTHH:mm:ssZ",i=b.z(this),s=this.$H,u=this.$m,a=this.$M,o=n.weekdays,c=n.months,f=n.meridiem,h=function(t,n,i,s){return t&&(t[n]||t(e,r))||i[n].slice(0,s)},d=function(t){return b.s(s%12||12,t,"0")},$=f||function(t,e,n){var r=t<12?"AM":"PM";return n?r.toLowerCase():r};return r.replace(y,(function(t,r){return r||function(t){switch(t){case"YY":return String(e.$y).slice(-2);case"YYYY":return b.s(e.$y,4,"0");case"M":return a+1;case"MM":return b.s(a+1,2,"0");case"MMM":return h(n.monthsShort,a,c,3);case"MMMM":return h(c,a);case"D":return e.$D;case"DD":return b.s(e.$D,2,"0");case"d":return String(e.$W);case"dd":return h(n.weekdaysMin,e.$W,o,2);case"ddd":return h(n.weekdaysShort,e.$W,o,3);case"dddd":return o[e.$W];case"H":return String(s);case"HH":return b.s(s,2,"0");case"h":return d(1);case"hh":return d(2);case"a":return $(s,u,!0);case"A":return $(s,u,!1);case"m":return String(u);case"mm":return b.s(u,2,"0");case"s":return String(e.$s);case"ss":return b.s(e.$s,2,"0");case"SSS":return b.s(e.$ms,3,"0");case"Z":return i}return null}(t)||i.replace(":","")}))},m.utcOffset=function(){return 15*-Math.round(this.$d.getTimezoneOffset()/15)},m.diff=function(r,d,l){var $,y=this,M=b.p(d),m=O(r),v=(m.utcOffset()-this.utcOffset())*e,g=this-m,D=function(){return b.m(y,m)};switch(M){case h:$=D()/12;break;case c:$=D();break;case f:$=D()/3;break;case o:$=(g-v)/6048e5;break;case a:$=(g-v)/864e5;break;case u:$=g/n;break;case s:$=g/e;break;case i:$=g/t;break;default:$=g;}return l?$:b.a($)},m.daysInMonth=function(){return this.endOf(c).$D},m.$locale=function(){return D[this.$L]},m.locale=function(t,e){if(!t)return this.$L;var n=this.clone(),r=w(t,e,!0);return r&&(n.$L=r),n},m.clone=function(){return b.w(this.$d,this)},m.toDate=function(){return new Date(this.valueOf())},m.toJSON=function(){return this.isValid()?this.toISOString():null},m.toISOString=function(){return this.$d.toISOString()},m.toString=function(){return this.$d.toUTCString()},M}(),k=_.prototype;return O.prototype=k,[["$ms",r],["$s",i],["$m",s],["$H",u],["$W",a],["$M",c],["$y",h],["$D",d]].forEach((function(t){k[t[1]]=function(e){return this.$g(e,t[0],t[1])};})),O.extend=function(t,e){return t.$i||(t(e,_,O),t.$i=!0),O},O.locale=w,O.isDayjs=S,O.unix=function(t){return O(1e3*t)},O.en=D[g],O.Ls=D,O.p={},O})); 
} (dayjs_min));

var dayjs_minExports = dayjs_min.exports;
var dayjs = /*@__PURE__*/getDefaultExportFromCjs$1(dayjs_minExports);

var localizedFormat = {exports: {}};

(function (module, exports) {
	!function(e,t){module.exports=t();}(commonjsGlobal$1,(function(){var e={LTS:"h:mm:ss A",LT:"h:mm A",L:"MM/DD/YYYY",LL:"MMMM D, YYYY",LLL:"MMMM D, YYYY h:mm A",LLLL:"dddd, MMMM D, YYYY h:mm A"};return function(t,o,n){var r=o.prototype,i=r.format;n.en.formats=e,r.format=function(t){void 0===t&&(t="YYYY-MM-DDTHH:mm:ssZ");var o=this.$locale().formats,n=function(t,o){return t.replace(/(\[[^\]]+])|(LTS?|l{1,4}|L{1,4})/g,(function(t,n,r){var i=r&&r.toUpperCase();return n||o[r]||e[r]||o[i].replace(/(\[[^\]]+])|(MMMM|MM|DD|dddd)/g,(function(e,t,o){return t||o.slice(1)}))}))}(t,void 0===o?{}:o);return i.call(this,n)};}})); 
} (localizedFormat));

var localizedFormatExports = localizedFormat.exports;
var LocalizedFormat = /*@__PURE__*/getDefaultExportFromCjs$1(localizedFormatExports);

var defineProperty = (function() {
  try {
    var func = getNative(Object, 'defineProperty');
    func({}, '', {});
    return func;
  } catch (e) {}
}());

/**
 * The base implementation of `assignValue` and `assignMergeValue` without
 * value checks.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function baseAssignValue(object, key, value) {
  if (key == '__proto__' && defineProperty) {
    defineProperty(object, key, {
      'configurable': true,
      'enumerable': true,
      'value': value,
      'writable': true
    });
  } else {
    object[key] = value;
  }
}

/**
 * This function is like `assignValue` except that it doesn't assign
 * `undefined` values.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignMergeValue(object, key, value) {
  if ((value !== undefined && !eq(object[key], value)) ||
      (value === undefined && !(key in object))) {
    baseAssignValue(object, key, value);
  }
}

/**
 * Creates a base function for methods like `_.forIn` and `_.forOwn`.
 *
 * @private
 * @param {boolean} [fromRight] Specify iterating from right to left.
 * @returns {Function} Returns the new base function.
 */
function createBaseFor(fromRight) {
  return function(object, iteratee, keysFunc) {
    var index = -1,
        iterable = Object(object),
        props = keysFunc(object),
        length = props.length;

    while (length--) {
      var key = props[++index];
      if (iteratee(iterable[key], key, iterable) === false) {
        break;
      }
    }
    return object;
  };
}

/**
 * The base implementation of `baseForOwn` which iterates over `object`
 * properties returned by `keysFunc` and invokes `iteratee` for each property.
 * Iteratee functions may exit iteration early by explicitly returning `false`.
 *
 * @private
 * @param {Object} object The object to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @param {Function} keysFunc The function to get the keys of `object`.
 * @returns {Object} Returns `object`.
 */
var baseFor = createBaseFor();

/** Detect free variable `exports`. */
var freeExports = typeof exports == 'object' && exports && !exports.nodeType && exports;

/** Detect free variable `module`. */
var freeModule = freeExports && typeof module == 'object' && module && !module.nodeType && module;

/** Detect the popular CommonJS extension `module.exports`. */
var moduleExports = freeModule && freeModule.exports === freeExports;

/** Built-in value references. */
var Buffer$1 = moduleExports ? root.Buffer : undefined,
    allocUnsafe$1 = Buffer$1 ? Buffer$1.allocUnsafe : undefined;

/**
 * Creates a clone of  `buffer`.
 *
 * @private
 * @param {Buffer} buffer The buffer to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Buffer} Returns the cloned buffer.
 */
function cloneBuffer(buffer, isDeep) {
  if (isDeep) {
    return buffer.slice();
  }
  var length = buffer.length,
      result = allocUnsafe$1 ? allocUnsafe$1(length) : new buffer.constructor(length);

  buffer.copy(result);
  return result;
}

/**
 * Creates a clone of `arrayBuffer`.
 *
 * @private
 * @param {ArrayBuffer} arrayBuffer The array buffer to clone.
 * @returns {ArrayBuffer} Returns the cloned array buffer.
 */
function cloneArrayBuffer(arrayBuffer) {
  var result = new arrayBuffer.constructor(arrayBuffer.byteLength);
  new Uint8Array$1(result).set(new Uint8Array$1(arrayBuffer));
  return result;
}

/**
 * Creates a clone of `typedArray`.
 *
 * @private
 * @param {Object} typedArray The typed array to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned typed array.
 */
function cloneTypedArray(typedArray, isDeep) {
  var buffer = isDeep ? cloneArrayBuffer(typedArray.buffer) : typedArray.buffer;
  return new typedArray.constructor(buffer, typedArray.byteOffset, typedArray.length);
}

/**
 * Copies the values of `source` to `array`.
 *
 * @private
 * @param {Array} source The array to copy values from.
 * @param {Array} [array=[]] The array to copy values to.
 * @returns {Array} Returns `array`.
 */
function copyArray(source, array) {
  var index = -1,
      length = source.length;

  array || (array = Array(length));
  while (++index < length) {
    array[index] = source[index];
  }
  return array;
}

/** Built-in value references. */
var objectCreate = Object.create;

/**
 * The base implementation of `_.create` without support for assigning
 * properties to the created object.
 *
 * @private
 * @param {Object} proto The object to inherit from.
 * @returns {Object} Returns the new object.
 */
var baseCreate = (function() {
  function object() {}
  return function(proto) {
    if (!isObject$1(proto)) {
      return {};
    }
    if (objectCreate) {
      return objectCreate(proto);
    }
    object.prototype = proto;
    var result = new object;
    object.prototype = undefined;
    return result;
  };
}());

/** Built-in value references. */
var getPrototype = overArg(Object.getPrototypeOf, Object);

/**
 * Initializes an object clone.
 *
 * @private
 * @param {Object} object The object to clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneObject(object) {
  return (typeof object.constructor == 'function' && !isPrototype(object))
    ? baseCreate(getPrototype(object))
    : {};
}

/**
 * This method is like `_.isArrayLike` except that it also checks if `value`
 * is an object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is an array-like object,
 *  else `false`.
 * @example
 *
 * _.isArrayLikeObject([1, 2, 3]);
 * // => true
 *
 * _.isArrayLikeObject(document.body.children);
 * // => true
 *
 * _.isArrayLikeObject('abc');
 * // => false
 *
 * _.isArrayLikeObject(_.noop);
 * // => false
 */
function isArrayLikeObject(value) {
  return isObjectLike(value) && isArrayLike(value);
}

/** `Object#toString` result references. */
var objectTag$1 = '[object Object]';

/** Used for built-in method references. */
var funcProto = Function.prototype,
    objectProto$4 = Object.prototype;

/** Used to resolve the decompiled source of functions. */
var funcToString = funcProto.toString;

/** Used to check objects for own properties. */
var hasOwnProperty$5 = objectProto$4.hasOwnProperty;

/** Used to infer the `Object` constructor. */
var objectCtorString = funcToString.call(Object);

/**
 * Checks if `value` is a plain object, that is, an object created by the
 * `Object` constructor or one with a `[[Prototype]]` of `null`.
 *
 * @static
 * @memberOf _
 * @since 0.8.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a plain object, else `false`.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 * }
 *
 * _.isPlainObject(new Foo);
 * // => false
 *
 * _.isPlainObject([1, 2, 3]);
 * // => false
 *
 * _.isPlainObject({ 'x': 0, 'y': 0 });
 * // => true
 *
 * _.isPlainObject(Object.create(null));
 * // => true
 */
function isPlainObject$1(value) {
  if (!isObjectLike(value) || baseGetTag(value) != objectTag$1) {
    return false;
  }
  var proto = getPrototype(value);
  if (proto === null) {
    return true;
  }
  var Ctor = hasOwnProperty$5.call(proto, 'constructor') && proto.constructor;
  return typeof Ctor == 'function' && Ctor instanceof Ctor &&
    funcToString.call(Ctor) == objectCtorString;
}

/**
 * Gets the value at `key`, unless `key` is "__proto__" or "constructor".
 *
 * @private
 * @param {Object} object The object to query.
 * @param {string} key The key of the property to get.
 * @returns {*} Returns the property value.
 */
function safeGet(object, key) {
  if (key === 'constructor' && typeof object[key] === 'function') {
    return;
  }

  if (key == '__proto__') {
    return;
  }

  return object[key];
}

/** Used for built-in method references. */
var objectProto$3 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$4 = objectProto$3.hasOwnProperty;

/**
 * Assigns `value` to `key` of `object` if the existing value is not equivalent
 * using [`SameValueZero`](http://ecma-international.org/ecma-262/7.0/#sec-samevaluezero)
 * for equality comparisons.
 *
 * @private
 * @param {Object} object The object to modify.
 * @param {string} key The key of the property to assign.
 * @param {*} value The value to assign.
 */
function assignValue(object, key, value) {
  var objValue = object[key];
  if (!(hasOwnProperty$4.call(object, key) && eq(objValue, value)) ||
      (value === undefined && !(key in object))) {
    baseAssignValue(object, key, value);
  }
}

/**
 * Copies properties of `source` to `object`.
 *
 * @private
 * @param {Object} source The object to copy properties from.
 * @param {Array} props The property identifiers to copy.
 * @param {Object} [object={}] The object to copy properties to.
 * @param {Function} [customizer] The function to customize copied values.
 * @returns {Object} Returns `object`.
 */
function copyObject(source, props, object, customizer) {
  var isNew = !object;
  object || (object = {});

  var index = -1,
      length = props.length;

  while (++index < length) {
    var key = props[index];

    var newValue = undefined;

    if (newValue === undefined) {
      newValue = source[key];
    }
    if (isNew) {
      baseAssignValue(object, key, newValue);
    } else {
      assignValue(object, key, newValue);
    }
  }
  return object;
}

/**
 * This function is like
 * [`Object.keys`](http://ecma-international.org/ecma-262/7.0/#sec-object.keys)
 * except that it includes inherited enumerable properties.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function nativeKeysIn(object) {
  var result = [];
  if (object != null) {
    for (var key in Object(object)) {
      result.push(key);
    }
  }
  return result;
}

/** Used for built-in method references. */
var objectProto$2 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$3 = objectProto$2.hasOwnProperty;

/**
 * The base implementation of `_.keysIn` which doesn't treat sparse arrays as dense.
 *
 * @private
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 */
function baseKeysIn(object) {
  if (!isObject$1(object)) {
    return nativeKeysIn(object);
  }
  var isProto = isPrototype(object),
      result = [];

  for (var key in object) {
    if (!(key == 'constructor' && (isProto || !hasOwnProperty$3.call(object, key)))) {
      result.push(key);
    }
  }
  return result;
}

/**
 * Creates an array of the own and inherited enumerable property names of `object`.
 *
 * **Note:** Non-object values are coerced to objects.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Object
 * @param {Object} object The object to query.
 * @returns {Array} Returns the array of property names.
 * @example
 *
 * function Foo() {
 *   this.a = 1;
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.keysIn(new Foo);
 * // => ['a', 'b', 'c'] (iteration order is not guaranteed)
 */
function keysIn(object) {
  return isArrayLike(object) ? arrayLikeKeys(object, true) : baseKeysIn(object);
}

/**
 * Converts `value` to a plain object flattening inherited enumerable string
 * keyed properties of `value` to own properties of the plain object.
 *
 * @static
 * @memberOf _
 * @since 3.0.0
 * @category Lang
 * @param {*} value The value to convert.
 * @returns {Object} Returns the converted plain object.
 * @example
 *
 * function Foo() {
 *   this.b = 2;
 * }
 *
 * Foo.prototype.c = 3;
 *
 * _.assign({ 'a': 1 }, new Foo);
 * // => { 'a': 1, 'b': 2 }
 *
 * _.assign({ 'a': 1 }, _.toPlainObject(new Foo));
 * // => { 'a': 1, 'b': 2, 'c': 3 }
 */
function toPlainObject(value) {
  return copyObject(value, keysIn(value));
}

/**
 * A specialized version of `baseMerge` for arrays and objects which performs
 * deep merges and tracks traversed objects enabling objects with circular
 * references to be merged.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @param {string} key The key of the value to merge.
 * @param {number} srcIndex The index of `source`.
 * @param {Function} mergeFunc The function to merge values.
 * @param {Function} [customizer] The function to customize assigned values.
 * @param {Object} [stack] Tracks traversed source values and their merged
 *  counterparts.
 */
function baseMergeDeep(object, source, key, srcIndex, mergeFunc, customizer, stack) {
  var objValue = safeGet(object, key),
      srcValue = safeGet(source, key),
      stacked = stack.get(srcValue);

  if (stacked) {
    assignMergeValue(object, key, stacked);
    return;
  }
  var newValue = customizer
    ? customizer(objValue, srcValue, (key + ''), object, source, stack)
    : undefined;

  var isCommon = newValue === undefined;

  if (isCommon) {
    var isArr = isArray$1(srcValue),
        isBuff = !isArr && isBuffer$1(srcValue),
        isTyped = !isArr && !isBuff && isTypedArray(srcValue);

    newValue = srcValue;
    if (isArr || isBuff || isTyped) {
      if (isArray$1(objValue)) {
        newValue = objValue;
      }
      else if (isArrayLikeObject(objValue)) {
        newValue = copyArray(objValue);
      }
      else if (isBuff) {
        isCommon = false;
        newValue = cloneBuffer(srcValue, true);
      }
      else if (isTyped) {
        isCommon = false;
        newValue = cloneTypedArray(srcValue, true);
      }
      else {
        newValue = [];
      }
    }
    else if (isPlainObject$1(srcValue) || isArguments(srcValue)) {
      newValue = objValue;
      if (isArguments(objValue)) {
        newValue = toPlainObject(objValue);
      }
      else if (!isObject$1(objValue) || isFunction$1(objValue)) {
        newValue = initCloneObject(srcValue);
      }
    }
    else {
      isCommon = false;
    }
  }
  if (isCommon) {
    // Recursively merge objects and arrays (susceptible to call stack limits).
    stack.set(srcValue, newValue);
    mergeFunc(newValue, srcValue, srcIndex, customizer, stack);
    stack['delete'](srcValue);
  }
  assignMergeValue(object, key, newValue);
}

/**
 * The base implementation of `_.merge` without support for multiple sources.
 *
 * @private
 * @param {Object} object The destination object.
 * @param {Object} source The source object.
 * @param {number} srcIndex The index of `source`.
 * @param {Function} [customizer] The function to customize merged values.
 * @param {Object} [stack] Tracks traversed source values and their merged
 *  counterparts.
 */
function baseMerge(object, source, srcIndex, customizer, stack) {
  if (object === source) {
    return;
  }
  baseFor(source, function(srcValue, key) {
    stack || (stack = new Stack);
    if (isObject$1(srcValue)) {
      baseMergeDeep(object, source, key, srcIndex, baseMerge, customizer, stack);
    }
    else {
      var newValue = customizer
        ? customizer(safeGet(object, key), srcValue, (key + ''), object, source, stack)
        : undefined;

      if (newValue === undefined) {
        newValue = srcValue;
      }
      assignMergeValue(object, key, newValue);
    }
  }, keysIn);
}

/**
 * This method returns the first argument it receives.
 *
 * @static
 * @since 0.1.0
 * @memberOf _
 * @category Util
 * @param {*} value Any value.
 * @returns {*} Returns `value`.
 * @example
 *
 * var object = { 'a': 1 };
 *
 * console.log(_.identity(object) === object);
 * // => true
 */
function identity(value) {
  return value;
}

/**
 * A faster alternative to `Function#apply`, this function invokes `func`
 * with the `this` binding of `thisArg` and the arguments of `args`.
 *
 * @private
 * @param {Function} func The function to invoke.
 * @param {*} thisArg The `this` binding of `func`.
 * @param {Array} args The arguments to invoke `func` with.
 * @returns {*} Returns the result of `func`.
 */
function apply$1(func, thisArg, args) {
  switch (args.length) {
    case 0: return func.call(thisArg);
    case 1: return func.call(thisArg, args[0]);
    case 2: return func.call(thisArg, args[0], args[1]);
    case 3: return func.call(thisArg, args[0], args[1], args[2]);
  }
  return func.apply(thisArg, args);
}

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax$1 = Math.max;

/**
 * A specialized version of `baseRest` which transforms the rest array.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @param {Function} transform The rest array transform.
 * @returns {Function} Returns the new function.
 */
function overRest(func, start, transform) {
  start = nativeMax$1(start === undefined ? (func.length - 1) : start, 0);
  return function() {
    var args = arguments,
        index = -1,
        length = nativeMax$1(args.length - start, 0),
        array = Array(length);

    while (++index < length) {
      array[index] = args[start + index];
    }
    index = -1;
    var otherArgs = Array(start + 1);
    while (++index < start) {
      otherArgs[index] = args[index];
    }
    otherArgs[start] = transform(array);
    return apply$1(func, this, otherArgs);
  };
}

/**
 * Creates a function that returns `value`.
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Util
 * @param {*} value The value to return from the new function.
 * @returns {Function} Returns the new constant function.
 * @example
 *
 * var objects = _.times(2, _.constant({ 'a': 1 }));
 *
 * console.log(objects);
 * // => [{ 'a': 1 }, { 'a': 1 }]
 *
 * console.log(objects[0] === objects[1]);
 * // => true
 */
function constant(value) {
  return function() {
    return value;
  };
}

/**
 * The base implementation of `setToString` without support for hot loop shorting.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var baseSetToString = !defineProperty ? identity : function(func, string) {
  return defineProperty(func, 'toString', {
    'configurable': true,
    'enumerable': false,
    'value': constant(string),
    'writable': true
  });
};

/** Used to detect hot functions by number of calls within a span of milliseconds. */
var HOT_COUNT = 800,
    HOT_SPAN = 16;

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeNow = Date.now;

/**
 * Creates a function that'll short out and invoke `identity` instead
 * of `func` when it's called `HOT_COUNT` or more times in `HOT_SPAN`
 * milliseconds.
 *
 * @private
 * @param {Function} func The function to restrict.
 * @returns {Function} Returns the new shortable function.
 */
function shortOut(func) {
  var count = 0,
      lastCalled = 0;

  return function() {
    var stamp = nativeNow(),
        remaining = HOT_SPAN - (stamp - lastCalled);

    lastCalled = stamp;
    if (remaining > 0) {
      if (++count >= HOT_COUNT) {
        return arguments[0];
      }
    } else {
      count = 0;
    }
    return func.apply(undefined, arguments);
  };
}

/**
 * Sets the `toString` method of `func` to return `string`.
 *
 * @private
 * @param {Function} func The function to modify.
 * @param {Function} string The `toString` result.
 * @returns {Function} Returns `func`.
 */
var setToString = shortOut(baseSetToString);

/**
 * The base implementation of `_.rest` which doesn't validate or coerce arguments.
 *
 * @private
 * @param {Function} func The function to apply a rest parameter to.
 * @param {number} [start=func.length-1] The start position of the rest parameter.
 * @returns {Function} Returns the new function.
 */
function baseRest(func, start) {
  return setToString(overRest(func, start, identity), func + '');
}

/**
 * Checks if the given arguments are from an iteratee call.
 *
 * @private
 * @param {*} value The potential iteratee value argument.
 * @param {*} index The potential iteratee index or key argument.
 * @param {*} object The potential iteratee object argument.
 * @returns {boolean} Returns `true` if the arguments are from an iteratee call,
 *  else `false`.
 */
function isIterateeCall(value, index, object) {
  if (!isObject$1(object)) {
    return false;
  }
  var type = typeof index;
  if (type == 'number'
        ? (isArrayLike(object) && isIndex(index, object.length))
        : (type == 'string' && index in object)
      ) {
    return eq(object[index], value);
  }
  return false;
}

/**
 * Creates a function like `_.assign`.
 *
 * @private
 * @param {Function} assigner The function to assign values.
 * @returns {Function} Returns the new assigner function.
 */
function createAssigner(assigner) {
  return baseRest(function(object, sources) {
    var index = -1,
        length = sources.length,
        customizer = length > 1 ? sources[length - 1] : undefined,
        guard = length > 2 ? sources[2] : undefined;

    customizer = (assigner.length > 3 && typeof customizer == 'function')
      ? (length--, customizer)
      : undefined;

    if (guard && isIterateeCall(sources[0], sources[1], guard)) {
      customizer = length < 3 ? undefined : customizer;
      length = 1;
    }
    object = Object(object);
    while (++index < length) {
      var source = sources[index];
      if (source) {
        assigner(object, source, index, customizer);
      }
    }
    return object;
  });
}

/**
 * This method is like `_.assign` except that it recursively merges own and
 * inherited enumerable string keyed properties of source objects into the
 * destination object. Source properties that resolve to `undefined` are
 * skipped if a destination value exists. Array and plain object properties
 * are merged recursively. Other objects and value types are overridden by
 * assignment. Source objects are applied from left to right. Subsequent
 * sources overwrite property assignments of previous sources.
 *
 * **Note:** This method mutates `object`.
 *
 * @static
 * @memberOf _
 * @since 0.5.0
 * @category Object
 * @param {Object} object The destination object.
 * @param {...Object} [sources] The source objects.
 * @returns {Object} Returns `object`.
 * @example
 *
 * var object = {
 *   'a': [{ 'b': 2 }, { 'd': 4 }]
 * };
 *
 * var other = {
 *   'a': [{ 'c': 3 }, { 'e': 5 }]
 * };
 *
 * _.merge(object, other);
 * // => { 'a': [{ 'b': 2, 'c': 3 }, { 'd': 4, 'e': 5 }] }
 */
var merge = createAssigner(function(object, source, srcIndex) {
  baseMerge(object, source, srcIndex);
});

/**
 * A specialized version of `_.forEach` for arrays without support for
 * iteratee shorthands.
 *
 * @private
 * @param {Array} [array] The array to iterate over.
 * @param {Function} iteratee The function invoked per iteration.
 * @returns {Array} Returns `array`.
 */
function arrayEach(array, iteratee) {
  var index = -1,
      length = array == null ? 0 : array.length;

  while (++index < length) {
    if (iteratee(array[index], index, array) === false) {
      break;
    }
  }
  return array;
}

/** Used for built-in method references. */
var objectProto$1 = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$2 = objectProto$1.hasOwnProperty;

/**
 * Initializes an array clone.
 *
 * @private
 * @param {Array} array The array to clone.
 * @returns {Array} Returns the initialized clone.
 */
function initCloneArray(array) {
  var length = array.length,
      result = new array.constructor(length);

  // Add properties assigned by `RegExp#exec`.
  if (length && typeof array[0] == 'string' && hasOwnProperty$2.call(array, 'index')) {
    result.index = array.index;
    result.input = array.input;
  }
  return result;
}

/**
 * Creates a clone of `dataView`.
 *
 * @private
 * @param {Object} dataView The data view to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the cloned data view.
 */
function cloneDataView(dataView, isDeep) {
  var buffer = cloneArrayBuffer(dataView.buffer) ;
  return new dataView.constructor(buffer, dataView.byteOffset, dataView.byteLength);
}

/** Used to match `RegExp` flags from their coerced string values. */
var reFlags = /\w*$/;

/**
 * Creates a clone of `regexp`.
 *
 * @private
 * @param {Object} regexp The regexp to clone.
 * @returns {Object} Returns the cloned regexp.
 */
function cloneRegExp(regexp) {
  var result = new regexp.constructor(regexp.source, reFlags.exec(regexp));
  result.lastIndex = regexp.lastIndex;
  return result;
}

/** Used to convert symbols to primitives and strings. */
var symbolProto = Symbol$1 ? Symbol$1.prototype : undefined,
    symbolValueOf = symbolProto ? symbolProto.valueOf : undefined;

/**
 * Creates a clone of the `symbol` object.
 *
 * @private
 * @param {Object} symbol The symbol object to clone.
 * @returns {Object} Returns the cloned symbol object.
 */
function cloneSymbol(symbol) {
  return symbolValueOf ? Object(symbolValueOf.call(symbol)) : {};
}

/** `Object#toString` result references. */
var boolTag$1 = '[object Boolean]',
    dateTag$1 = '[object Date]',
    mapTag$3 = '[object Map]',
    numberTag$1 = '[object Number]',
    regexpTag$1 = '[object RegExp]',
    setTag$3 = '[object Set]',
    stringTag$1 = '[object String]',
    symbolTag$2 = '[object Symbol]';

var arrayBufferTag$1 = '[object ArrayBuffer]',
    dataViewTag$1 = '[object DataView]',
    float32Tag$1 = '[object Float32Array]',
    float64Tag$1 = '[object Float64Array]',
    int8Tag$1 = '[object Int8Array]',
    int16Tag$1 = '[object Int16Array]',
    int32Tag$1 = '[object Int32Array]',
    uint8Tag$1 = '[object Uint8Array]',
    uint8ClampedTag$1 = '[object Uint8ClampedArray]',
    uint16Tag$1 = '[object Uint16Array]',
    uint32Tag$1 = '[object Uint32Array]';

/**
 * Initializes an object clone based on its `toStringTag`.
 *
 * **Note:** This function only supports cloning values with tags of
 * `Boolean`, `Date`, `Error`, `Map`, `Number`, `RegExp`, `Set`, or `String`.
 *
 * @private
 * @param {Object} object The object to clone.
 * @param {string} tag The `toStringTag` of the object to clone.
 * @param {boolean} [isDeep] Specify a deep clone.
 * @returns {Object} Returns the initialized clone.
 */
function initCloneByTag(object, tag, isDeep) {
  var Ctor = object.constructor;
  switch (tag) {
    case arrayBufferTag$1:
      return cloneArrayBuffer(object);

    case boolTag$1:
    case dateTag$1:
      return new Ctor(+object);

    case dataViewTag$1:
      return cloneDataView(object);

    case float32Tag$1: case float64Tag$1:
    case int8Tag$1: case int16Tag$1: case int32Tag$1:
    case uint8Tag$1: case uint8ClampedTag$1: case uint16Tag$1: case uint32Tag$1:
      return cloneTypedArray(object, isDeep);

    case mapTag$3:
      return new Ctor;

    case numberTag$1:
    case stringTag$1:
      return new Ctor(object);

    case regexpTag$1:
      return cloneRegExp(object);

    case setTag$3:
      return new Ctor;

    case symbolTag$2:
      return cloneSymbol(object);
  }
}

/** `Object#toString` result references. */
var mapTag$2 = '[object Map]';

/**
 * The base implementation of `_.isMap` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a map, else `false`.
 */
function baseIsMap(value) {
  return isObjectLike(value) && getTag$1(value) == mapTag$2;
}

/* Node.js helper references. */
var nodeIsMap = nodeUtil && nodeUtil.isMap;

/**
 * Checks if `value` is classified as a `Map` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a map, else `false`.
 * @example
 *
 * _.isMap(new Map);
 * // => true
 *
 * _.isMap(new WeakMap);
 * // => false
 */
var isMap = nodeIsMap ? baseUnary(nodeIsMap) : baseIsMap;

/** `Object#toString` result references. */
var setTag$2 = '[object Set]';

/**
 * The base implementation of `_.isSet` without Node.js optimizations.
 *
 * @private
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a set, else `false`.
 */
function baseIsSet(value) {
  return isObjectLike(value) && getTag$1(value) == setTag$2;
}

/* Node.js helper references. */
var nodeIsSet = nodeUtil && nodeUtil.isSet;

/**
 * Checks if `value` is classified as a `Set` object.
 *
 * @static
 * @memberOf _
 * @since 4.3.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a set, else `false`.
 * @example
 *
 * _.isSet(new Set);
 * // => true
 *
 * _.isSet(new WeakSet);
 * // => false
 */
var isSet = nodeIsSet ? baseUnary(nodeIsSet) : baseIsSet;

/** Used to compose bitmasks for cloning. */
var CLONE_DEEP_FLAG$1 = 1;

/** `Object#toString` result references. */
var argsTag = '[object Arguments]',
    arrayTag = '[object Array]',
    boolTag = '[object Boolean]',
    dateTag = '[object Date]',
    errorTag = '[object Error]',
    funcTag = '[object Function]',
    genTag = '[object GeneratorFunction]',
    mapTag$1 = '[object Map]',
    numberTag = '[object Number]',
    objectTag = '[object Object]',
    regexpTag = '[object RegExp]',
    setTag$1 = '[object Set]',
    stringTag = '[object String]',
    symbolTag$1 = '[object Symbol]',
    weakMapTag = '[object WeakMap]';

var arrayBufferTag = '[object ArrayBuffer]',
    dataViewTag = '[object DataView]',
    float32Tag = '[object Float32Array]',
    float64Tag = '[object Float64Array]',
    int8Tag = '[object Int8Array]',
    int16Tag = '[object Int16Array]',
    int32Tag = '[object Int32Array]',
    uint8Tag = '[object Uint8Array]',
    uint8ClampedTag = '[object Uint8ClampedArray]',
    uint16Tag = '[object Uint16Array]',
    uint32Tag = '[object Uint32Array]';

/** Used to identify `toStringTag` values supported by `_.clone`. */
var cloneableTags = {};
cloneableTags[argsTag] = cloneableTags[arrayTag] =
cloneableTags[arrayBufferTag] = cloneableTags[dataViewTag] =
cloneableTags[boolTag] = cloneableTags[dateTag] =
cloneableTags[float32Tag] = cloneableTags[float64Tag] =
cloneableTags[int8Tag] = cloneableTags[int16Tag] =
cloneableTags[int32Tag] = cloneableTags[mapTag$1] =
cloneableTags[numberTag] = cloneableTags[objectTag] =
cloneableTags[regexpTag] = cloneableTags[setTag$1] =
cloneableTags[stringTag] = cloneableTags[symbolTag$1] =
cloneableTags[uint8Tag] = cloneableTags[uint8ClampedTag] =
cloneableTags[uint16Tag] = cloneableTags[uint32Tag] = true;
cloneableTags[errorTag] = cloneableTags[funcTag] =
cloneableTags[weakMapTag] = false;

/**
 * The base implementation of `_.clone` and `_.cloneDeep` which tracks
 * traversed objects.
 *
 * @private
 * @param {*} value The value to clone.
 * @param {boolean} bitmask The bitmask flags.
 *  1 - Deep clone
 *  2 - Flatten inherited properties
 *  4 - Clone symbols
 * @param {Function} [customizer] The function to customize cloning.
 * @param {string} [key] The key of `value`.
 * @param {Object} [object] The parent object of `value`.
 * @param {Object} [stack] Tracks traversed objects and their clone counterparts.
 * @returns {*} Returns the cloned value.
 */
function baseClone(value, bitmask, customizer, key, object, stack) {
  var result,
      isDeep = bitmask & CLONE_DEEP_FLAG$1;
  if (result !== undefined) {
    return result;
  }
  if (!isObject$1(value)) {
    return value;
  }
  var isArr = isArray$1(value);
  if (isArr) {
    result = initCloneArray(value);
  } else {
    var tag = getTag$1(value),
        isFunc = tag == funcTag || tag == genTag;

    if (isBuffer$1(value)) {
      return cloneBuffer(value, isDeep);
    }
    if (tag == objectTag || tag == argsTag || (isFunc && !object)) {
      result = (isFunc) ? {} : initCloneObject(value);
    } else {
      if (!cloneableTags[tag]) {
        return object ? value : {};
      }
      result = initCloneByTag(value, tag, isDeep);
    }
  }
  // Check for circular references and return its corresponding clone.
  stack || (stack = new Stack);
  var stacked = stack.get(value);
  if (stacked) {
    return stacked;
  }
  stack.set(value, result);

  if (isSet(value)) {
    value.forEach(function(subValue) {
      result.add(baseClone(subValue, bitmask, customizer, subValue, value, stack));
    });
  } else if (isMap(value)) {
    value.forEach(function(subValue, key) {
      result.set(key, baseClone(subValue, bitmask, customizer, key, value, stack));
    });
  }

  var keysFunc = (getAllKeys)
    ;

  var props = isArr ? undefined : keysFunc(value);
  arrayEach(props || value, function(subValue, key) {
    if (props) {
      key = subValue;
      subValue = value[key];
    }
    // Recursively populate clone (susceptible to call stack limits).
    assignValue(result, key, baseClone(subValue, bitmask, customizer, key, value, stack));
  });
  return result;
}

/** Used to compose bitmasks for cloning. */
var CLONE_DEEP_FLAG = 1,
    CLONE_SYMBOLS_FLAG = 4;

/**
 * This method is like `_.clone` except that it recursively clones `value`.
 *
 * @static
 * @memberOf _
 * @since 1.0.0
 * @category Lang
 * @param {*} value The value to recursively clone.
 * @returns {*} Returns the deep cloned value.
 * @see _.clone
 * @example
 *
 * var objects = [{ 'a': 1 }, { 'b': 2 }];
 *
 * var deep = _.cloneDeep(objects);
 * console.log(deep[0] === objects[0]);
 * // => false
 */
function cloneDeep(value) {
  return baseClone(value, CLONE_DEEP_FLAG | CLONE_SYMBOLS_FLAG);
}

/**
 * The different available widths of a web chat.
 */
var ChatWidthBreakpoint;
(function (ChatWidthBreakpoint) {
    // < 360px
    ChatWidthBreakpoint["NARROW"] = "narrow";
    // >= 360px
    ChatWidthBreakpoint["STANDARD"] = "standard";
    // > 672 + 16 + 16px
    ChatWidthBreakpoint["WIDE"] = "wide";
})(ChatWidthBreakpoint || (ChatWidthBreakpoint = {}));
/**
 * The possible states of the suggestions options panel.
 */
var AlternateSuggestionsPanelState;
(function (AlternateSuggestionsPanelState) {
    /**
     * The panel is closed.
     */
    AlternateSuggestionsPanelState[AlternateSuggestionsPanelState["CLOSED"] = 1] = "CLOSED";
    /**
     * The panel is half open in the "strike" state.
     */
    AlternateSuggestionsPanelState[AlternateSuggestionsPanelState["OPEN_STRIKE"] = 2] = "OPEN_STRIKE";
    /**
     * The panel is fully open.
     */
    AlternateSuggestionsPanelState[AlternateSuggestionsPanelState["OPEN_FULL"] = 3] = "OPEN_FULL";
})(AlternateSuggestionsPanelState || (AlternateSuggestionsPanelState = {}));
var AlternateSuggestionsSection;
(function (AlternateSuggestionsSection) {
    /**
     * Indicates the display of the alternate responses.
     */
    AlternateSuggestionsSection[AlternateSuggestionsSection["ALTERNATE_RESPONSES"] = 1] = "ALTERNATE_RESPONSES";
    /**
     * The other contact option section. This is the section that includes the "connect to agent" option.
     */
    AlternateSuggestionsSection[AlternateSuggestionsSection["CONTACT_OPTIONS"] = 2] = "CONTACT_OPTIONS";
    /**
     * The section that contains the home screen starters.
     */
    AlternateSuggestionsSection[AlternateSuggestionsSection["STARTERS"] = 3] = "STARTERS";
})(AlternateSuggestionsSection || (AlternateSuggestionsSection = {}));

const HEADER_ACCEPT = 'accept';
const HEADER_CONTENT_TYPE = 'content-type';
const HEADER_AUTHORIZATION = 'authorization';
// The mime type for JSON.
const MIME_JSON = 'application/json';
// The mime type for text streaming.
const MIME_STREAM = 'text/event-stream';
const MIME_JSON_STREAM = `${MIME_JSON}, ${MIME_STREAM}`;
// Prefix used to distinguish console logs omitted from our code
const WA_CONSOLE_PREFIX = '[Chat]';
// This is a header respected by the IBM stack for tracking requests as they move from service to service. By default,
// Cerberus will add a transaction id x-request-id to the response which could be used for tracking. However,
// if we set one here we can reference it when debugging and downstream services can search in their logs for this
// value.
const API_DEBUGGING_HEADER_NAME = 'X-Global-Transaction-ID';
// Header identifying requests are coming from a web chat embedded as an agent app. We use this to know we need to check
// for an auth code bearer token (instead of JWT security, for instance).
const AGENT_APP_HEADER_NAME = 'x-watson-is-agent-app';
// The right-to-left mark character string which mixes the direction of a string.
// For more info on right-to-left mark: https://www.w3.org/TR/WCAG20-TECHS/H34.html
const RIGHT_TO_LEFT_MARK = String.fromCharCode(0x200f);
const ENGLISH_US_DATE_FORMAT = 'mm/dd/yyyy';
// The timeout, in milliseconds, to wait for a response type to load content.
const RESPONSE_TYPE_TIMEOUT_MS = 10000;
// These are custom panel ids.
const DEFAULT_CUSTOM_PANEL_ID = 'wac-default-panel';
// The version of the integrations server API that web chat is using. Note, this version does not necessarily match
// the version of the store API that is being used. The integrations server does a mapping from this version to the
// store version. Integrations version "2023-09-15" corresponds to Store version "2023-06-15".
const INTEGRATIONS_VERSION = '2023-09-15';
const WINDOW_WARNING = 'The window related events and methods have been deprecated and will be disabled in the next' +
    ' major release of web chat. Please update your code to use the view change events and methods. See' +
    ' https://web-chat.global.assistant.watson.cloud.ibm.com/docs.html?to=api-events#windowviewmigration for more' +
    ' information.';
const CUSTOM_RESPONSE_WARNING = 'The customResponse event has been renamed to userDefinedResponse. The existing event will continue to function' +
    ' but is deprecated and will be removed in a future release.';
// The piece of a region code that includes we're running in AWS.
const AWS_REGION_FRAGMENT = 'aws';
// The piece of a region code that includes we're running WXO on IBM Cloud.
const WXO_CLOUD_REGION_FRAGMENT = 'wxo';
/**
 * This function serves as a placeholder in places where a functional value is required, but not expected to be
 * fired. In the event that it is, it will throw an error, letting you know it shouldn't be.
 */
function THROW_ERROR() {
    throw Error('Not implemented.');
}
/**
 * The brand color types configurable for web chat.
 */
var BrandColorKind;
(function (BrandColorKind) {
    BrandColorKind["PRIMARY"] = "primary";
    BrandColorKind["ACCENT"] = "accent";
})(BrandColorKind || (BrandColorKind = {}));
// Max width for which we show a carousel.
const CAROUSEL_MAX_WIDTH = 455;
// Max width we allow non-carousel citations to be full width. We are trying to avoid having skinny citations just
// sitting alone on a row and being stacked so we allow them to grow full width until we get to a size where we can
// show more than one on a line.
const FULL_WIDTH_CITATION_MAX_WIDTH = 580;
// When we auto-scroll to a message, we want to scroll a bit more than necessary because messages have a lot of
// padding on the top that we want to cut off when scrolling. This is the extra amount we scroll by. There's 28px of
// padding above the message and we want to cut that down to just 8 so we scroll an extra 20px (28 - 8).
const AUTO_SCROLL_EXTRA = 28 - 8;
const LIGHT_THEMES = ['white', 'g10'];
let isJest = false;
try {
    // "process" is undefined when not in a jest environment, but I don't know how to check its value without the code
    // throwing a "not defined" error.
    if (process?.env.JEST_WORKER_ID) {
        isJest = true;
    }
}
catch (error) {
    // Ignore
}
const IS_JEST = isJest;
// How much to throttle auto scrolling. When we are in test mode, we set this to zero.
const AUTO_SCROLL_THROTTLE_TIMEOUT = IS_JEST ? 0 : 100;

/**
 * Miscellaneous utils for dealing with promises.
 */
/**
 * This is an async function that will simply wait for the given amount of time.
 *
 * @param milliseconds The amount of time in milliseconds to wait.
 */
async function sleep(milliseconds) {
    await new Promise(resolve => {
        setTimeout(resolve, milliseconds);
    });
}
/**
 * This function returns a Promise that will be resolved if the provided Promise has resolved within the duration
 * specified. Otherwise the promise will be rejected.
 *
 * @param promise The Promise which will be resolved or timed out.
 * @param duration The duration of the timeout in milliseconds.
 * @param errorMessage An optional message to display.
 */
function resolveOrTimeout(promise, duration, errorMessage) {
    // Create a promise that rejects in <ms> milliseconds
    const timeout = new Promise((resolve, reject) => {
        setTimeout(() => {
            const message = errorMessage || `The operation timed out after ${duration}ms`;
            reject(message);
        }, duration);
    });
    // Returns a race between the timeout and the original in promise
    return Promise.race([promise, timeout]);
}

/**
 * A global flag to indicate if we want to show debug messages in the browser console. This is generally set from
 * {@link PublicConfig.debug}.
 */
let enableDebugLog = false;
/**
 * A global flag to indicate if we want to show stack trace debug messages in the browser console. This is generally set
 * from {@link PublicConfig.debugStackTraces}.
 */
let enableDebugStackTracesLog = false;
/**
 * This is a no-op function that's for the purpose of verifying at build time that a given item matches a given
 * type. To use, pass the item as the "item" parameter and pass the type as the "TItemType" type parameter. Since
 * this is incurring a runtime call for what is really a build-time check, this function should be used sparingly.
 */
function assertType(item) {
    return item;
}
/**
 * A simple utility to send a message to the console log but only id debug logging is enabled.
 */
function debugLog(message, ...args) {
    if (enableDebugLog) {
        console.log(`${WA_CONSOLE_PREFIX} ${message}`, ...args);
    }
}
/**
 * A simple utility to send a message to the console log but only if debug stack traces logging is enabled.
 */
function debugStackTrace(message, ...args) {
    if (enableDebugStackTracesLog) {
        const stack = new Error('This is not an error; it is a stack trace used for debugging purposes. It is enabled by the "debugStackTraces: true" configuration option.');
        debugLog(message, stack, ...args);
    }
    else {
        debugLog(message, ...args);
    }
}
/**
 * A simple utility to send an error message to the console log.
 */
function consoleError(message, ...args) {
    console.error(`${WA_CONSOLE_PREFIX} ${message}`, ...args);
}
/**
 * A simple utility to send an error message to the console log.
 */
function consoleLog(message, ...args) {
    console.log(`${WA_CONSOLE_PREFIX} ${message}`, ...args);
}
/**
 * A simple utility to send a message to the console log.
 */
function consoleInfo(message, ...args) {
    console.info(`${WA_CONSOLE_PREFIX} ${message}`, ...args);
}
/**
 * A simple utility to send a message to the console log.
 */
function consoleDebug(message, ...args) {
    console.debug(`${WA_CONSOLE_PREFIX} ${message}`, ...args);
}
/**
 * A simple utility to send an warning message to the console log.
 */
function consoleWarn(message, ...args) {
    console.warn(`${WA_CONSOLE_PREFIX} ${message}`, ...args);
}
/**
 * Sets a global flag to indicate if we want to show debug messages in the browser console. This is generally set from
 * {@link PublicConfig.debug}.
 */
function setEnableDebugLog(debug) {
    enableDebugLog = debug;
}
/**
 * Sets a global flag to indicate if we want to show debug stack trace messages in the browser console. This is
 * generally set from {@link PublicConfig.debugStackTraces}.
 */
function setEnableDebugStackTracesLog(debug) {
    enableDebugStackTracesLog = debug;
}
/**
 * Indicates if the global flag to indicate if we want to show debug messages in the browser console is enabled.
 */
function isEnableDebugLog() {
    return enableDebugLog;
}
/**
 * Safely returns the text from the given fetch response or undefined if there is an error.
 */
async function safeFetchText(response) {
    try {
        if (response) {
            return await response.text();
        }
    }
    catch (error) {
        consoleError('Error getting fetch text', error);
    }
    return undefined;
}
/**
 * Safely returns the text from the given fetch response or undefined if there is an error. This will also impose a
 * timeout on getting the text
 */
async function safeFetchTextWithTimeout(response) {
    try {
        if (response) {
            return resolveOrTimeout(response.text(), 2000, 'Getting response text');
        }
    }
    catch (error) {
        consoleError('Error getting fetch text', error);
    }
    return undefined;
}
/**
 * Safely returns the json from the given fetch response or undefined if there is an error.
 */
async function safeFetchJSON(response) {
    try {
        if (response) {
            return await response.json();
        }
    }
    catch (error) {
        consoleError('Error getting fetch json', error);
    }
    return undefined;
}
/**
 * Produces a hash of the given string using an implementation of djb2. This value may be negative. Note that this is
 * not a cryptographically secure hash and should not be used for security related items.
 *
 * @see http://www.cse.yorku.ca/~oz/hash.html
 */
function djb2Hash(value) {
    let index = value.length;
    let hash = 5381;
    while (index) {
        // eslint-disable-next-line no-bitwise
        hash = (hash * 33) ^ value.charCodeAt(--index);
    }
    return hash;
}
/**
 * Returns a {@link OnErrorData} that represents an error that occurred while rendering a component.
 */
function createDidCatchErrorData(component, error, errorInfo, isCatastrophicError) {
    return {
        errorType: OnErrorType.RENDER,
        message: `${component}.componentDidCatch`,
        otherData: {
            error,
            errorInfo,
        },
        catastrophicErrorType: isCatastrophicError ? CatastrophicErrorType.FAILED : null,
    };
}
/**
 * This function will calculate and return the necessary top padding percentage value that will help render a media
 * player with a responsive aspect ratio.
 */
function getResponsiveElementPaddingValue(baseHeight = 180) {
    return `${100 / (320 / baseHeight)}%`;
}
/**
 * Indicates if the given file is valid for uploading. The file must still be in the edit step and it must not
 * contain an error.
 */
function isValidForUpload(upload) {
    return upload.status === FileStatusValue.EDIT && !upload.isError;
}
/**
 * Calls the given onError function.
 */
function callOnError(onError, data) {
    if (onError) {
        try {
            onError(data);
        }
        catch (error) {
            consoleError('Error calling onError', error);
        }
    }
}
function getBotName(useAITheme, config) {
    let botName;
    if (useAITheme) {
        botName = 'AI';
    }
    else {
        botName =
            config.public.__ibm__?.botNameOverride ||
                config.remote.initConfig.botName ||
                (config.asCarbonChat ? 'watsonx' : 'watsonx Assistant');
    }
    return botName;
}

/**
 * Pre-populate global web chat config. This is stored on window to make sure that if there are multiple web chats
 * loaded on a page in the same or different ways, that they are forced to use the same version of web chat and use
 * unique namespaces. Note that these object are read and set by multiple versions of web chat.
 *
 * watsonAssistantChatSharedConfig should not contain breaking changes.
 */
function ensureChatBrowserWindowConfigExists() {
    window.watsonAssistantChatSharedConfig = window.watsonAssistantChatSharedConfig || {};
    window.watsonAssistantChatSharedConfig.namespaces = window.watsonAssistantChatSharedConfig.namespaces || [];
    window.watsonAssistantChatSharedConfig.loadScriptPromises =
        window.watsonAssistantChatSharedConfig.loadScriptPromises || {};
}
function getSharedConfig() {
    return window.watsonAssistantChatSharedConfig;
}

class NamespaceService {
    constructor(namespace) {
        this.originalName = namespace;
        this.attributeSafe = getAttributeSafe(namespace);
        this.suffix = getSuffix(namespace);
    }
    /**
     * Remove a current namespace when the chat is destroyed.
     */
    remove() {
        removeNamespace(this.originalName);
    }
}
/**
 * From a given namespace, generate a unique token safe to be used as an attribute or as part of one.
 * If no namespace provided, returns an empty string.
 */
function getAttributeSafe(namespace) {
    const computedName = computeNamespaceName(namespace);
    return computedName?.length ? `ns${djb2Hash(computedName)}` : '';
}
/**
 * From a given namespace, generate a unique token safe to be used as a suffix to a classname or attribute.
 * If no namespace provided, returns an empty string.
 */
function getSuffix(namespace) {
    const computedName = computeNamespaceName(namespace);
    return computedName?.length ? `--${getAttributeSafe(namespace)}` : '';
}
/**
 * Given a namespace, trims and returns. If no namespace provided, returns an empty string.
 */
function computeNamespaceName(namespace) {
    return namespace ? namespace.trim() : '';
}
/**
 * Validates if a namespace is already in use.
 */
function validateNamespace(namespace, andAdd) {
    ensureChatBrowserWindowConfigExists();
    const computedNamespace = computeNamespaceName(namespace);
    const sharedConfig = getSharedConfig();
    if (sharedConfig.namespaces.includes(computedNamespace)) {
        const namespaceForError = computedNamespace || 'No namespace provided';
        consoleError(`You have multiple web chats attempting to use the same namespace. (${namespaceForError})`);
    }
    else {
        addNamespace(namespace);
    }
}
/**
 * Adds a namespace to the array of namespaces in use.
 */
function addNamespace(namespace) {
    ensureChatBrowserWindowConfigExists();
    const computedNamespace = computeNamespaceName(namespace);
    const { namespaces } = getSharedConfig();
    namespaces.push(computedNamespace);
}
/**
 * Removes a namespace from the array of namespaces in use.
 */
function removeNamespace(namespace) {
    ensureChatBrowserWindowConfigExists();
    const computedNamespace = computeNamespaceName(namespace);
    const { namespaces } = getSharedConfig();
    const namespaceIndexToRemove = namespaces.indexOf(computedNamespace);
    if (namespaceIndexToRemove !== -1) {
        namespaces.splice(namespaceIndexToRemove, 1);
    }
}

var WXOMentionType;
(function (WXOMentionType) {
    WXOMentionType["APP"] = "app";
    WXOMentionType["ASSISTANT"] = "assistant";
})(WXOMentionType || (WXOMentionType = {}));

/**
 * Web chat is used in a bunch of different places in tooling for different reasons. This enum is meant to represent
 * each of those and will help us identify and control the behavior in each of the different places.
 */
var ToolingType;
(function (ToolingType) {
    /**
     * This represents the web chat previews that are displayed within the web chat integration settings pages.
     */
    ToolingType["WEB_CHAT_SETTINGS"] = "web-chat-settings";
    /**
     * This the web chat that appears on the page where you get the preview link but it is not the actual preview link
     * itself. In the old IA this web chat is the preview link integration. In the new IA, this web chat is the draft
     * web chat integration.
     */
    ToolingType["ASSISTANT_PREVIEW"] = "assistant-preview";
    /**
     * This is the web chat used by on the preview share link page.
     */
    ToolingType["PREVIEW_LINK"] = "preview-link";
    /**
     * This is the web chat used for preview when authoring actions.
     */
    ToolingType["ACTIONS"] = "actions";
    /**
     * This is the web chat used for preview of a search skill.
     */
    ToolingType["SEARCH"] = "search";
    /**
     * This is the web chat used for displaying the conversation history from the logs on the analytics pages.
     */
    ToolingType["ANALYTICS"] = "analytics";
})(ToolingType || (ToolingType = {}));
/**
 * When the user gets a "connect_to_agent" response the normal behavior is to show a "Connect to agent" card.
 * However, this only works if a service desk has been configured. In some tooling places that use web chat there is
 * no service desk configuration. This enum represents the different ways the web chat can respond to a CTA.
 */
var ConnectToAgentBehaviorType;
(function (ConnectToAgentBehaviorType) {
    /**
     * This is the default behavior and it will cause an error message to be shown to the user when there is no
     * service desk configured. This error is targeted at the end user.
     */
    ConnectToAgentBehaviorType["ERROR_MISSING"] = "error";
    /**
     * This behavior is the same as the preview case except that it also displays a warning targeted for the authoring
     * user instead of the end user. This is to help her understand that she is missing some configuration.
     */
    ConnectToAgentBehaviorType["WARN_MISSING"] = "warning";
    /**
     * This indicates that the CTA card should be displayed in a disabled state if there is no service desk configured.
     */
    ConnectToAgentBehaviorType["PREVIEW"] = "preview";
})(ConnectToAgentBehaviorType || (ConnectToAgentBehaviorType = {}));
var LauncherToolingPreviewType;
(function (LauncherToolingPreviewType) {
    LauncherToolingPreviewType["DESKTOP"] = "desktop";
    LauncherToolingPreviewType["MOBILE"] = "mobile";
})(LauncherToolingPreviewType || (LauncherToolingPreviewType = {}));

const DEFAULT_PUBLIC_CONFIG = {
    openChatByDefault: false,
    showLauncher: true,
    shouldTakeFocusIfOpensAutomatically: true,
    agentAppConfig: {
        is_on: false,
    },
    servers: {},
    serviceDesk: {},
    messaging: {
        copyPreviousContextOnRequest: true,
    },
    __ibm__: {
        isTrackingEnabled: true,
        logTrackObjects: false,
        connectToAgentBehavior: ConnectToAgentBehaviorType.ERROR_MISSING,
        enableFeatures: {},
    },
    wxo: {},
};
const CARBON_DEFAULT_PUBLIC_CONFIG = {
    themeConfig: {
        // Carbon Chat only supports AI Theme
        useAITheme: true,
    },
    servers: {
        // Carbon Chat doesn't call an external location for config.
        skipConfig: true,
    },
    __ibm__: {
        // No amplitude stuff in Carbon Chat.
        isTrackingEnabled: false,
        useShadowRoot: true,
    },
};
/**
 * Create new web chat instance.
 *
 * @param pagePublicConfig The public config options object that came from the page.
 * @param fetch The instance of the fetch function to use to make network calls.
 * @param chatConstructorPromise A promise that resolves with the WatsonAssistantChat class. This can either be a
 * script tab with some onload handlers, or could be an import() statement. Because of our use of MFEs, we are
 * currently limited to the former.
 * @param remoteInitConfig watsonx Assistant specific config, this should be removed and we should update the public config
 * with the appropriate needed options to only have one config.
 * @param asCarbonChat Lets us know we are in generic "carbon chat" mode.
 * @param renderOverride Function to pass into Chat class to call instead of render function.
 * @returns Promise object that resolves with your new instance. See [./instance.md](./instance.md).
 */
async function instantiateWidget(pagePublicConfig, fetch, chatConstructorPromise, remoteInitConfig, asCarbonChat, renderOverride) {
    const loadStartTime = Date.now();
    let config = cloneDeep(pagePublicConfig);
    if (config?.debug) {
        consoleDebug('[ChatEntry] Called instantiateWidget', config);
    }
    if (document.location.protocol !== 'https:') {
        consoleWarn('Your page is not running with "https"; your data will not be sent  securely.');
    }
    if (document.compatMode !== 'CSS1Compat') {
        consoleWarn('Your page is running in quirks mode; you may experience layout issues with the chat. Add "<!DOCTYPE html>" to the page to run in standards mode.');
    }
    // Set carbon chat defaults
    {
        const carbonConfig = cloneDeep(CARBON_DEFAULT_PUBLIC_CONFIG);
        // If the Carbon Chat is using their own setup for history rather than customLoadHistory,
        // we make sure to skip loading history the native way.
        if (!config?.messaging?.customLoadHistory) {
            carbonConfig.servers = carbonConfig.servers || {};
            carbonConfig.servers.skipHistory = true;
        }
        config = merge(config, carbonConfig);
        if (!config.messaging?.customSendMessage) {
            throw new Error(`You must set messaging.customSendMessage in your configuration object.`);
        }
    }
    // Extract the extra properties from the page config we don't want to put in to the redux store.
    const { element, onLoad, onError, ...publicConfig } = config;
    const Chat = await chatConstructorPromise(publicConfig);
    const additionalChatParameters = {
        loadVersion: 'WatsonAssistantChatEntry' ,
        onError,
        fetch,
        loadStartTime,
        asCarbonChat,
        renderOverride,
    };
    validateNamespace(config.namespace);
    return new Chat(publicConfig, remoteInitConfig, element, additionalChatParameters);
}

/**
 * The default set of response codes allowed by a fetch call.
 */
const DEFAULT_ALLOWED_RESPONSES = new Set([200]);
/**
 * Performs the requested operation and executes a retry if it fails. The number of retries and the delays between
 * each attempt are governed by the given array of delay times. A call is considered a failure if it throws an error
 * (returns a rejected Promise).
 *
 * @param retryFunction The function to call for each attempt. If this function returns, if will be considered a success
 * and the returned value will be returned from this function. If the function throws an error (or rejects the
 * Promise), that will be considered a failure and the function will be retried.
 * @param delayTimes The delays in milliseconds to wait after every failed attempt. This function will make one
 * attempt more than what's in the array since there will be no delay after the final attempt if it fails.
 * @param operationTitle A label to use in the console output to describe this operation for debugging purposes.
 * @returns Returns the result from the last successful executor attempt.
 * @throws If all the attempts fail and the retries give up, this will throw the last error that was thrown by the
 * retry function.
 */
async function doWithRetry(retryFunction, delayTimes, operationTitle) {
    let attemptNumber = 1;
    while (attemptNumber - 1 <= delayTimes.length) {
        try {
            // eslint-disable-next-line no-await-in-loop
            const result = await retryFunction(attemptNumber);
            return result;
        }
        catch (error) {
            if (attemptNumber - 1 === delayTimes.length) {
                // This was the last attempt, so just output the error message and continue.
                const message = `The operation "${operationTitle}" failed after ${attemptNumber} attempts and will not be retried.`;
                consoleError(message, error);
                throw error;
            }
            else {
                // The operation failed and a retry is available, so output an error and then sleep for the desired amount
                // of time.
                const delayTime = delayTimes[attemptNumber - 1];
                consoleError(`The operation "${operationTitle}" failed after ${attemptNumber} attempts will be retried in ${delayTime}ms.`, error);
                // eslint-disable-next-line no-await-in-loop
                await sleep(delayTime);
            }
        }
        attemptNumber++;
    }
    // This condition shouldn't actually happen.
    throw new Error(`Unexpected retry state for operation "${operationTitle}".`);
}
/**
 * Executes a {@link doWithRetry} with the additional condition that it must complete within the given timeout or
 * the call will fail.
 *
 * @param retryFunction The function to call for each attempt. If this function returns, if will be considered a success
 * and the returned value will be returned from this function. If the function throws an error (or rejects the
 * Promise), that will be considered a failure and the function will be retried.
 * @param delayTimes The delays in milliseconds to wait after every failed attempt. This function will make one
 * attempt more than what's in the array since there will be no delay after the final attempt if it fails.
 * @param operationTitle A label to use in the console output to describe this operation for debugging purposes.
 * @param timeoutMs The amount of time in milliseconds to wait before a timeout occurs.
 * @returns Returns the result from the last successful executor attempt.
 * @throws If all the attempts fail and the retries give up or if the timeout occurs, this will throw an error.
 */
async function doWithRetryWithTimeout(retryFunction, delayTimes, operationTitle, timeoutMs) {
    return resolveOrTimeout(doWithRetry(retryFunction, delayTimes, operationTitle), timeoutMs);
}
/**
 * A simple utility function that will wrap a call to fetch that will throw an error is the result is not one of the
 * expected status codes (200 by default).
 *
 * @param responsePromise The response promise to wait for and then check the status code.
 * @param allowedCodes The set of allowed response codes that will not result in an error being thrown.
 */
async function throwOnStatus(responsePromise, allowedCodes = DEFAULT_ALLOWED_RESPONSES) {
    const response = await responsePromise;
    if (!allowedCodes.has(response.status)) {
        throw new Error(`Status code error ${response.status}`);
    }
    return responsePromise;
}

/* eslint-disable prefer-destructuring */
const NODE_ENV = "production";
const VERSION = "0.3.2";

var CarbonThemeClassNames;
(function (CarbonThemeClassNames) {
    CarbonThemeClassNames["WHITE"] = "cds--white";
    CarbonThemeClassNames["G10"] = "cds--g10";
    CarbonThemeClassNames["G90"] = "cds--g90";
    CarbonThemeClassNames["G100"] = "cds--g100";
})(CarbonThemeClassNames || (CarbonThemeClassNames = {}));
var ButtonKindEnum;
(function (ButtonKindEnum) {
    ButtonKindEnum["PRIMARY"] = "primary";
    ButtonKindEnum["SECONDARY"] = "secondary";
    ButtonKindEnum["DANGER"] = "danger";
    ButtonKindEnum["GHOST"] = "ghost";
    ButtonKindEnum["DANGER_PRIMARY"] = "danger--primary";
    ButtonKindEnum["DANGER_GHOST"] = "danger--ghost";
    ButtonKindEnum["DANGER_TERTIARY"] = "danger--tertiary";
    ButtonKindEnum["TERTIARY"] = "tertiary";
})(ButtonKindEnum || (ButtonKindEnum = {}));
var ButtonSizeEnum;
(function (ButtonSizeEnum) {
    ButtonSizeEnum["SMALL"] = "sm";
    ButtonSizeEnum["MEDIUM"] = "md";
    ButtonSizeEnum["LARGE"] = "lg";
    ButtonSizeEnum["XLARGE"] = "xl";
    ButtonSizeEnum["XXLARGE"] = "2xl";
})(ButtonSizeEnum || (ButtonSizeEnum = {}));
var ButtonTooltipAlignmentEnum;
(function (ButtonTooltipAlignmentEnum) {
    ButtonTooltipAlignmentEnum["START"] = "start";
    ButtonTooltipAlignmentEnum["CENTER"] = "center";
    ButtonTooltipAlignmentEnum["END"] = "end";
})(ButtonTooltipAlignmentEnum || (ButtonTooltipAlignmentEnum = {}));

// In the future we may want different types of notification messages, for example one of them will be a
// text_notification. We're doing this to align with BED's notification schema.
var NotificationType;
(function (NotificationType) {
    NotificationType["TEXT_NOTIFICATION"] = "text_notification";
})(NotificationType || (NotificationType = {}));
// The array of timeouts that will dictate the amount of intervals the bounce animation should play for the launcher.
const BOUNCING_ANIMATION_TIMEOUTS = [15000, 60000];
// The amount of time until the entrance animation is automatically triggered for either launcher.
const TIME_TO_ENTRANCE_ANIMATION_START = 15000;

/**
 * This method is like `_.merge` except that it accepts `customizer` which
 * is invoked to produce the merged values of the destination and source
 * properties. If `customizer` returns `undefined`, merging is handled by the
 * method instead. The `customizer` is invoked with six arguments:
 * (objValue, srcValue, key, object, source, stack).
 *
 * **Note:** This method mutates `object`.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Object
 * @param {Object} object The destination object.
 * @param {...Object} sources The source objects.
 * @param {Function} customizer The function to customize assigned values.
 * @returns {Object} Returns `object`.
 * @example
 *
 * function customizer(objValue, srcValue) {
 *   if (_.isArray(objValue)) {
 *     return objValue.concat(srcValue);
 *   }
 * }
 *
 * var object = { 'a': [1], 'b': [2] };
 * var other = { 'a': [3], 'b': [4] };
 *
 * _.mergeWith(object, other, customizer);
 * // => { 'a': [1, 3], 'b': [2, 4] }
 */
var mergeWith = createAssigner(function(object, source, srcIndex, customizer) {
  baseMerge(object, source, srcIndex, customizer);
});

/** `Object#toString` result references. */
var symbolTag = '[object Symbol]';

/**
 * Checks if `value` is classified as a `Symbol` primitive or object.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is a symbol, else `false`.
 * @example
 *
 * _.isSymbol(Symbol.iterator);
 * // => true
 *
 * _.isSymbol('abc');
 * // => false
 */
function isSymbol$1(value) {
  return typeof value == 'symbol' ||
    (isObjectLike(value) && baseGetTag(value) == symbolTag);
}

/**
 * This file contains customizer functions for lodash utility functions that accept them.
 */
/**
 * Returns the new array value which will replace the current value since we don't want both values to get merged.
 */
function replaceCurrentArrayValue(currentValue, newValue) {
    if (Array.isArray(newValue)) {
        return newValue;
    }
    // Let the method itself handle merging non-array values.
    return undefined;
}

/* eslint-disable no-restricted-syntax */
/**
 * Indicates if the given object contains any properties.
 */
// eslint-disable-next-line @typescript-eslint/ban-types
function isEmptyObject(object) {
    // This is more efficient than using Object.keys because it will stop at the first property.
    for (const property in object) {
        if (Object.prototype.hasOwnProperty.call(object, property)) {
            return false;
        }
    }
    return true;
}
/**
 * Merges the provided objects but does not merge arrays. Any new arrays found will overwrite a previous array.
 */
function mergeNoArrays(...objects) {
    return mergeWith(...objects, replaceCurrentArrayValue);
}
/**
 * Recursively deep freezes an object.
 */
function deepFreeze$1(object) {
    Object.freeze(object);
    Object.getOwnPropertyNames(object).forEach(prop => {
        if (Object.prototype.hasOwnProperty.call(object, prop) &&
            object[prop] !== null &&
            (typeof object[prop] === 'object' || typeof object[prop] === 'function') &&
            !Object.isFrozen(object[prop])) {
            deepFreeze$1(object[prop]);
        }
    });
    return object;
}

/**
 * Miscellaneous utilities to help in reducers.
 */
const DEFAULT_LAUNCHER = {
    config: {
        is_on: true,
        mobile: {
            is_on: true,
            title: '',
            time_to_expand: TIME_TO_ENTRANCE_ANIMATION_START,
            new_expand_time: false,
            time_to_reduce: 10000,
            notification_type: NotificationType.TEXT_NOTIFICATION,
        },
        desktop: {
            is_on: true,
            title: '',
            new_expand_time: false,
            time_to_expand: TIME_TO_ENTRANCE_ANIMATION_START,
            notification_type: NotificationType.TEXT_NOTIFICATION,
        },
    },
};
deepFreeze$1(DEFAULT_LAUNCHER);
const DEFAULT_CUSTOM_PANEL_CONFIG_OPTIONS = {
    title: null,
    hideBackButton: false,
    hidePanelHeader: false,
    disableAnimation: false,
};
deepFreeze$1(DEFAULT_CUSTOM_PANEL_CONFIG_OPTIONS);
const DEFAULT_CUSTOM_PANEL_STATE = {
    isOpen: false,
    panelID: DEFAULT_CUSTOM_PANEL_ID,
    options: DEFAULT_CUSTOM_PANEL_CONFIG_OPTIONS,
};
deepFreeze$1(DEFAULT_CUSTOM_PANEL_STATE);
const DEFAULT_IFRAME_PANEL_STATE = {
    isOpen: false,
    messageItem: null,
};
deepFreeze$1(DEFAULT_IFRAME_PANEL_STATE);
const DEFAULT_CITATION_PANEL_STATE = {
    isOpen: false,
    citationItem: null,
    relatedSearchResult: null,
};
deepFreeze$1(DEFAULT_CITATION_PANEL_STATE);
const DEFAULT_BRANDING_STATE = {
    config: {
        is_on: false,
    },
    isOpen: false,
    showBanner: true,
};
deepFreeze$1(DEFAULT_BRANDING_STATE);
const DEFAULT_TOUR_STATE = {
    activeTourStepItems: null,
};
deepFreeze$1(DEFAULT_TOUR_STATE);
const DEFAULT_MESSAGE_PANEL_STATE = {
    isOpen: false,
    localMessageItem: null,
    isMessageForInput: false,
};
deepFreeze$1(DEFAULT_MESSAGE_PANEL_STATE);
const DEFAULT_ALTERNATE_SUGGESTIONS_STATE = {
    showButton: false,
    allowCollapse: false,
    panelState: AlternateSuggestionsPanelState.CLOSED,
    config: {
        is_on: false,
    },
    contactItem: null,
    starters: null,
    suggestionListItems: null,
    showSearchSection: false,
    searchResults: null,
    searchResultsLoading: false,
    searchResultsText: null,
};
deepFreeze$1(DEFAULT_ALTERNATE_SUGGESTIONS_STATE);
const VIEW_STATE_ALL_CLOSED = {
    launcher: false,
    mainWindow: false,
    tour: false,
};
deepFreeze$1(VIEW_STATE_ALL_CLOSED);
const VIEW_STATE_LAUNCHER_OPEN = {
    launcher: true,
    mainWindow: false,
    tour: false,
};
deepFreeze$1(VIEW_STATE_LAUNCHER_OPEN);
const VIEW_STATE_MAIN_WINDOW_OPEN = {
    mainWindow: true,
    launcher: false,
    tour: false,
};
deepFreeze$1(VIEW_STATE_MAIN_WINDOW_OPEN);
const VIEW_STATE_TOUR_OPEN = {
    tour: true,
    mainWindow: false,
    launcher: false,
};
deepFreeze$1(VIEW_STATE_TOUR_OPEN);
const DEFAULT_PERSISTED_TO_BROWSER = {
    chatState: {
        version: VERSION,
        sessionID: null,
        sessionStartedTimestamp: null,
        updateHistoryUserDefinedCount: {},
        disclaimersAccepted: {},
        homeScreenState: {
            isHomeScreenOpen: false,
            showBackToBot: false,
        },
        hasSentNonWelcomeMessage: false,
        suggestionsPersistedState: {
            numStrikes: 0,
            selectedIndex: -1,
            selectedSection: null,
        },
        persistedTourState: {
            activeTourID: null,
            activeTourCurrentStepIndex: null,
        },
        agentState: {
            isConnected: false,
            isSuspended: false,
            agentProfiles: {},
        },
    },
    launcherState: {
        wasLoadedFromBrowser: false,
        version: VERSION,
        viewState: VIEW_STATE_ALL_CLOSED,
        activeTour: false,
        showUnreadIndicator: false,
        mobileLauncherIsExtended: false,
        mobileLauncherWasReduced: false,
        mobileLauncherDisableBounce: false,
        desktopLauncherIsExpanded: false,
        desktopLauncherWasMinimized: false,
        bounceTurn: 1,
        hasSentNonWelcomeMessage: false,
    },
};
deepFreeze$1(DEFAULT_PERSISTED_TO_BROWSER);
const DEFAULT_CHAT_MESSAGES_STATE = {
    localMessageIDs: [],
    messageIDs: [],
    isTypingCounter: 0,
    isLoadingCounter: 0,
    isHydratingCounter: 0,
    isScrollAnchored: false,
};
deepFreeze$1(DEFAULT_CHAT_MESSAGES_STATE);
const DEFAULT_MESSAGE_STATE = {
    allMessageItemsByID: {},
    allMessagesByID: {},
    botMessageState: {
        ...DEFAULT_CHAT_MESSAGES_STATE,
    },
};
deepFreeze$1(DEFAULT_MESSAGE_STATE);
const DEFAULT_INPUT_STATE = () => ({
    fieldVisible: true,
    isReadonly: false,
    files: [],
    allowFileUploads: false,
    allowMultipleFileUploads: false,
    allowedFileUploadTypes: null,
    stopStreamingButtonState: {
        currentStreamID: null,
        isVisible: false,
        isDisabled: false,
    },
});
const DEFAULT_AGENT_STATE = {
    isConnecting: false,
    isReconnecting: false,
    numUnreadMessages: 0,
    fileUploadInProgress: false,
    showScreenShareRequest: false,
    isScreenSharing: false,
    isAgentTyping: false,
    inputState: DEFAULT_INPUT_STATE(),
};
deepFreeze$1(DEFAULT_AGENT_STATE);
const DEFAULT_THEME_STATE = {
    carbonTheme: CarbonTheme.G10,
    useAITheme: false,
    corners: CornersType.ROUND,
};
deepFreeze$1(DEFAULT_THEME_STATE);
// Could be used to set the default for the remote config. However, the above default state value is used when first
// setting the theme object in doCreateStore. That object in store should be used to get an up-to-date value for whether
// the theme state is enabled instead of the remote config.
const DEFAULT_THEME_CONFIG = {
    carbon_theme: CarbonTheme.G10,
    use_ai_theme: false,
};
deepFreeze$1(DEFAULT_THEME_CONFIG);
const DEFAULT_STREAMING_STATE = {
    enabled: false,
};
deepFreeze$1(DEFAULT_STREAMING_STATE);
// Could be used to set the default for the remote config. However, the above default state value is used when first
// setting the streaming object in doCreateStore. That object in store should be used to get an up-to-date value for
// whether streaming is enabled instead of the remote config.
const DEFAULT_STREAMING_CONFIG = {
    is_on: false,
};
deepFreeze$1(DEFAULT_STREAMING_CONFIG);
const DEFAULT_LAYOUT_STATE = {
    showFrame: true,
    hasContentMaxWidth: false,
};
deepFreeze$1(DEFAULT_LAYOUT_STATE);
function calcAnnouncementForBrandingPanel(previousState, newIsPanelOpen) {
    if (previousState.brandingState.isOpen === newIsPanelOpen) {
        // No change in the panel state so return the current announcement.
        return previousState.announceMessage;
    }
    return {
        messageID: newIsPanelOpen
            ? 'general_ariaAnnounceOpenedInformationOverlay'
            : 'general_ariaAnnounceClosedInformationOverlay',
    };
}
/**
 * Determines the {@link AnnounceMessage} to show based on a potential change in the visibility of the widget. If the
 * widget is either opened or closed, an announcement should be made and this will set that announcement. If the state
 * of the widget hasn't changed, this will return the current announcement unchanged.
 *
 * @param previousState The previous state of the application.
 * @param newViewState Indicates the widgets new view state.
 */
function calcAnnouncementForWidgetOpen(previousState, newViewState) {
    if (isEqual$1(previousState.persistedToBrowserStorage.launcherState.viewState, newViewState)) {
        // No change in the view state so return the current announcement.
        return previousState.announceMessage;
    }
    // The view has changed so show the appropriate message.
    // TODO TOUR: I18N. This will need to be updated before ga. For now the announcement only cares if the main window is
    // opened. If the tour is opened, the announcement will be that the window is closed.
    return {
        messageID: newViewState.mainWindow ? 'window_ariaWindowOpened' : 'window_ariaWindowClosed',
    };
}
/**
 * Returns a new state that has the {@link ChatMessagesState} modified for the given chat type with the new properties.
 * If the chat state is for a thread, then the thread that is currently being viewed will be modified.
 */
function applyBotMessageState(state, newState) {
    return {
        ...state,
        botMessageState: {
            ...state.botMessageState,
            ...newState,
        },
    };
}
function handleViewStateChange(state, viewState) {
    // If the main window is opened and the page is visible, mark any unread messages as read.
    let { agentState } = state;
    let { showUnreadIndicator } = state.persistedToBrowserStorage.launcherState;
    if (viewState.mainWindow && state.isBrowserPageVisible) {
        if (agentState.numUnreadMessages !== 0) {
            agentState = {
                ...agentState,
                numUnreadMessages: 0,
            };
        }
        showUnreadIndicator = false;
    }
    return {
        ...state,
        announceMessage: calcAnnouncementForWidgetOpen(state, viewState),
        agentState,
        persistedToBrowserStorage: {
            ...state.persistedToBrowserStorage,
            launcherState: {
                ...state.persistedToBrowserStorage.launcherState,
                viewState,
                showUnreadIndicator,
            },
        },
    };
}
function setHomeScreenOpenState(state, isOpen, showBackToBot) {
    if (showBackToBot === undefined) {
        showBackToBot = state.persistedToBrowserStorage.chatState.homeScreenState.showBackToBot;
    }
    return {
        ...state,
        persistedToBrowserStorage: {
            ...state.persistedToBrowserStorage,
            chatState: {
                ...state.persistedToBrowserStorage.chatState,
                homeScreenState: {
                    ...state.persistedToBrowserStorage.chatState.homeScreenState,
                    isHomeScreenOpen: isOpen,
                    showBackToBot,
                },
            },
        },
    };
}
/**
 * Returns the current (old) object if it is equal to another new object. Otherwise, the new object will be
 * returned. This will perform a deep equal of the given objects.
 *
 * This function is useful for optimizing out changes to parts of an object graph where object identity is normally
 * use for comparisons and where an unnecessary change in an object may produce unnecessary processing.
 */
function keepIfEqual(oldObject, newObject) {
    return oldObject === newObject || isEqual$1(oldObject, newObject) ? oldObject : newObject;
}

/** `Object#toString` result references. */
var mapTag = '[object Map]',
    setTag = '[object Set]';

/** Used for built-in method references. */
var objectProto = Object.prototype;

/** Used to check objects for own properties. */
var hasOwnProperty$1 = objectProto.hasOwnProperty;

/**
 * Checks if `value` is an empty object, collection, map, or set.
 *
 * Objects are considered empty if they have no own enumerable string keyed
 * properties.
 *
 * Array-like values such as `arguments` objects, arrays, buffers, strings, or
 * jQuery-like collections are considered empty if they have a `length` of `0`.
 * Similarly, maps and sets are considered empty if they have a `size` of `0`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Lang
 * @param {*} value The value to check.
 * @returns {boolean} Returns `true` if `value` is empty, else `false`.
 * @example
 *
 * _.isEmpty(null);
 * // => true
 *
 * _.isEmpty(true);
 * // => true
 *
 * _.isEmpty(1);
 * // => true
 *
 * _.isEmpty([1, 2, 3]);
 * // => false
 *
 * _.isEmpty({ 'a': 1 });
 * // => false
 */
function isEmpty(value) {
  if (value == null) {
    return true;
  }
  if (isArrayLike(value) &&
      (isArray$1(value) || typeof value == 'string' || typeof value.splice == 'function' ||
        isBuffer$1(value) || isTypedArray(value) || isArguments(value))) {
    return !value.length;
  }
  var tag = getTag$1(value);
  if (tag == mapTag || tag == setTag) {
    return !value.size;
  }
  if (isPrototype(value)) {
    return !baseKeys(value).length;
  }
  for (var key in value) {
    if (hasOwnProperty$1.call(value, key)) {
      return false;
    }
  }
  return true;
}

const DEFAULT_HOME_SCREEN_CONFIG = { is_on: false, allow_return: true };
function mergeHomeScreenConfig(publicHomeScreenConfig, remoteHomeScreenConfig) {
    // Merge together the remote config and the public config. When merging make sure the public config overrides the
    // remote config. If neither config are set then use the default home screen config that sets home screen to disabled.
    return merge({}, convertHomeScreenConfigToBackgroundIfBackgroundGradient(remoteHomeScreenConfig), convertHomeScreenConfigToBackgroundIfBackgroundGradient(publicHomeScreenConfig));
}
/**
 * Returns a copy of the given config but converts any background_gradient object to the background prop if the
 * background prop is not set. This is required in order to keep track of the intended background setting in one place,
 * otherwise it's quite difficult to tell which setting should be used if both are set. This can be removed once the
 * deprecated background_gradient property is removed.
 */
function convertHomeScreenConfigToBackgroundIfBackgroundGradient(config) {
    let newHomeScreenConfig = config;
    if (config && !config.background) {
        // If there is a config and the config is not setting a background property then we may need to set one. If the new
        // config is setting a background then we rely on that value and ignore the background_gradient. If the new config
        // is not setting a background or background_gradient then the home screen component will default to background type
        // "none".
        if (!isEmpty(config.background_gradient)) {
            // If the config is using the background_gradient object then we should update background to match the
            // background_gradient settings. We do this because "background" is what we rely on within the home screen
            // component.
            let background;
            if (config.background_gradient.is_on) {
                if (config.background_gradient?.gradient_direction === HomeScreenBackgroundType.BOTTOM_UP) {
                    background = HomeScreenBackgroundType.BOTTOM_UP;
                }
                else {
                    // We allow deb to leave gradient_direction undefined when setting is_on to true. The expectation is that we
                    // will default to top corner out gradient.
                    background = HomeScreenBackgroundType.TOP_CORNER_OUT;
                }
            }
            else {
                // If the background_gradient config has been intentionally set to is_on false then we should not show a
                // background.
                background = HomeScreenBackgroundType.NONE;
            }
            // Add the background to the home screen config.
            newHomeScreenConfig = {
                ...newHomeScreenConfig,
                background,
            };
        }
    }
    return newHomeScreenConfig;
}
/**
 * Returns a copy of the given config, but removes any starters that are empty.
 */
function withoutEmptyStarters(config) {
    let newConfig = config;
    if (config?.starters?.buttons?.length) {
        newConfig = {
            ...config,
            starters: {
                ...config.starters,
                buttons: config.starters.buttons.filter(button => Boolean(button.label?.trim())),
            },
        };
        if (!newConfig?.starters?.buttons?.length) {
            // If we end up with no starters, then turn them off.
            newConfig.starters.is_on = false;
        }
    }
    return newConfig;
}

// Generated file. Do not edit
var StatusCodes;
(function (StatusCodes) {
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.2.1
     *
     * This interim response indicates that everything so far is OK and that the client should continue with the request or ignore it if it is already finished.
     */
    StatusCodes[StatusCodes["CONTINUE"] = 100] = "CONTINUE";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.2.2
     *
     * This code is sent in response to an Upgrade request header by the client, and indicates the protocol the server is switching too.
     */
    StatusCodes[StatusCodes["SWITCHING_PROTOCOLS"] = 101] = "SWITCHING_PROTOCOLS";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc2518#section-10.1
     *
     * This code indicates that the server has received and is processing the request, but no response is available yet.
     */
    StatusCodes[StatusCodes["PROCESSING"] = 102] = "PROCESSING";
    /**
     * Official Documentation @ https://www.rfc-editor.org/rfc/rfc8297#page-3
     *
     * This code indicates to the client that the server is likely to send a final response with the header fields included in the informational response.
     */
    StatusCodes[StatusCodes["EARLY_HINTS"] = 103] = "EARLY_HINTS";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.3.1
     *
     * The request has succeeded. The meaning of a success varies depending on the HTTP method:
     * GET: The resource has been fetched and is transmitted in the message body.
     * HEAD: The entity headers are in the message body.
     * POST: The resource describing the result of the action is transmitted in the message body.
     * TRACE: The message body contains the request message as received by the server
     */
    StatusCodes[StatusCodes["OK"] = 200] = "OK";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.3.2
     *
     * The request has succeeded and a new resource has been created as a result of it. This is typically the response sent after a PUT request.
     */
    StatusCodes[StatusCodes["CREATED"] = 201] = "CREATED";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.3.3
     *
     * The request has been received but not yet acted upon. It is non-committal, meaning that there is no way in HTTP to later send an asynchronous response indicating the outcome of processing the request. It is intended for cases where another process or server handles the request, or for batch processing.
     */
    StatusCodes[StatusCodes["ACCEPTED"] = 202] = "ACCEPTED";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.3.4
     *
     * This response code means returned meta-information set is not exact set as available from the origin server, but collected from a local or a third party copy. Except this condition, 200 OK response should be preferred instead of this response.
     */
    StatusCodes[StatusCodes["NON_AUTHORITATIVE_INFORMATION"] = 203] = "NON_AUTHORITATIVE_INFORMATION";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.3.5
     *
     * There is no content to send for this request, but the headers may be useful. The user-agent may update its cached headers for this resource with the new ones.
     */
    StatusCodes[StatusCodes["NO_CONTENT"] = 204] = "NO_CONTENT";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.3.6
     *
     * This response code is sent after accomplishing request to tell user agent reset document view which sent this request.
     */
    StatusCodes[StatusCodes["RESET_CONTENT"] = 205] = "RESET_CONTENT";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7233#section-4.1
     *
     * This response code is used because of range header sent by the client to separate download into multiple streams.
     */
    StatusCodes[StatusCodes["PARTIAL_CONTENT"] = 206] = "PARTIAL_CONTENT";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc2518#section-10.2
     *
     * A Multi-Status response conveys information about multiple resources in situations where multiple status codes might be appropriate.
     */
    StatusCodes[StatusCodes["MULTI_STATUS"] = 207] = "MULTI_STATUS";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.4.1
     *
     * The request has more than one possible responses. User-agent or user should choose one of them. There is no standardized way to choose one of the responses.
     */
    StatusCodes[StatusCodes["MULTIPLE_CHOICES"] = 300] = "MULTIPLE_CHOICES";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.4.2
     *
     * This response code means that URI of requested resource has been changed. Probably, new URI would be given in the response.
     */
    StatusCodes[StatusCodes["MOVED_PERMANENTLY"] = 301] = "MOVED_PERMANENTLY";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.4.3
     *
     * This response code means that URI of requested resource has been changed temporarily. New changes in the URI might be made in the future. Therefore, this same URI should be used by the client in future requests.
     */
    StatusCodes[StatusCodes["MOVED_TEMPORARILY"] = 302] = "MOVED_TEMPORARILY";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.4.4
     *
     * Server sent this response to directing client to get requested resource to another URI with an GET request.
     */
    StatusCodes[StatusCodes["SEE_OTHER"] = 303] = "SEE_OTHER";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7232#section-4.1
     *
     * This is used for caching purposes. It is telling to client that response has not been modified. So, client can continue to use same cached version of response.
     */
    StatusCodes[StatusCodes["NOT_MODIFIED"] = 304] = "NOT_MODIFIED";
    /**
     * @deprecated
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.4.6
     *
     * Was defined in a previous version of the HTTP specification to indicate that a requested response must be accessed by a proxy. It has been deprecated due to security concerns regarding in-band configuration of a proxy.
     */
    StatusCodes[StatusCodes["USE_PROXY"] = 305] = "USE_PROXY";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.4.7
     *
     * Server sent this response to directing client to get requested resource to another URI with same method that used prior request. This has the same semantic than the 302 Found HTTP response code, with the exception that the user agent must not change the HTTP method used: if a POST was used in the first request, a POST must be used in the second request.
     */
    StatusCodes[StatusCodes["TEMPORARY_REDIRECT"] = 307] = "TEMPORARY_REDIRECT";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7538#section-3
     *
     * This means that the resource is now permanently located at another URI, specified by the Location: HTTP Response header. This has the same semantics as the 301 Moved Permanently HTTP response code, with the exception that the user agent must not change the HTTP method used: if a POST was used in the first request, a POST must be used in the second request.
     */
    StatusCodes[StatusCodes["PERMANENT_REDIRECT"] = 308] = "PERMANENT_REDIRECT";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.5.1
     *
     * This response means that server could not understand the request due to invalid syntax.
     */
    StatusCodes[StatusCodes["BAD_REQUEST"] = 400] = "BAD_REQUEST";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7235#section-3.1
     *
     * Although the HTTP standard specifies "unauthorized", semantically this response means "unauthenticated". That is, the client must authenticate itself to get the requested response.
     */
    StatusCodes[StatusCodes["UNAUTHORIZED"] = 401] = "UNAUTHORIZED";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.5.2
     *
     * This response code is reserved for future use. Initial aim for creating this code was using it for digital payment systems however this is not used currently.
     */
    StatusCodes[StatusCodes["PAYMENT_REQUIRED"] = 402] = "PAYMENT_REQUIRED";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.5.3
     *
     * The client does not have access rights to the content, i.e. they are unauthorized, so server is rejecting to give proper response. Unlike 401, the client's identity is known to the server.
     */
    StatusCodes[StatusCodes["FORBIDDEN"] = 403] = "FORBIDDEN";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.5.4
     *
     * The server can not find requested resource. In the browser, this means the URL is not recognized. In an API, this can also mean that the endpoint is valid but the resource itself does not exist. Servers may also send this response instead of 403 to hide the existence of a resource from an unauthorized client. This response code is probably the most famous one due to its frequent occurence on the web.
     */
    StatusCodes[StatusCodes["NOT_FOUND"] = 404] = "NOT_FOUND";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.5.5
     *
     * The request method is known by the server but has been disabled and cannot be used. For example, an API may forbid DELETE-ing a resource. The two mandatory methods, GET and HEAD, must never be disabled and should not return this error code.
     */
    StatusCodes[StatusCodes["METHOD_NOT_ALLOWED"] = 405] = "METHOD_NOT_ALLOWED";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.5.6
     *
     * This response is sent when the web server, after performing server-driven content negotiation, doesn't find any content following the criteria given by the user agent.
     */
    StatusCodes[StatusCodes["NOT_ACCEPTABLE"] = 406] = "NOT_ACCEPTABLE";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7235#section-3.2
     *
     * This is similar to 401 but authentication is needed to be done by a proxy.
     */
    StatusCodes[StatusCodes["PROXY_AUTHENTICATION_REQUIRED"] = 407] = "PROXY_AUTHENTICATION_REQUIRED";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.5.7
     *
     * This response is sent on an idle connection by some servers, even without any previous request by the client. It means that the server would like to shut down this unused connection. This response is used much more since some browsers, like Chrome, Firefox 27+, or IE9, use HTTP pre-connection mechanisms to speed up surfing. Also note that some servers merely shut down the connection without sending this message.
     */
    StatusCodes[StatusCodes["REQUEST_TIMEOUT"] = 408] = "REQUEST_TIMEOUT";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.5.8
     *
     * This response is sent when a request conflicts with the current state of the server.
     */
    StatusCodes[StatusCodes["CONFLICT"] = 409] = "CONFLICT";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.5.9
     *
     * This response would be sent when the requested content has been permenantly deleted from server, with no forwarding address. Clients are expected to remove their caches and links to the resource. The HTTP specification intends this status code to be used for "limited-time, promotional services". APIs should not feel compelled to indicate resources that have been deleted with this status code.
     */
    StatusCodes[StatusCodes["GONE"] = 410] = "GONE";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.5.10
     *
     * The server rejected the request because the Content-Length header field is not defined and the server requires it.
     */
    StatusCodes[StatusCodes["LENGTH_REQUIRED"] = 411] = "LENGTH_REQUIRED";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7232#section-4.2
     *
     * The client has indicated preconditions in its headers which the server does not meet.
     */
    StatusCodes[StatusCodes["PRECONDITION_FAILED"] = 412] = "PRECONDITION_FAILED";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.5.11
     *
     * Request entity is larger than limits defined by server; the server might close the connection or return an Retry-After header field.
     */
    StatusCodes[StatusCodes["REQUEST_TOO_LONG"] = 413] = "REQUEST_TOO_LONG";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.5.12
     *
     * The URI requested by the client is longer than the server is willing to interpret.
     */
    StatusCodes[StatusCodes["REQUEST_URI_TOO_LONG"] = 414] = "REQUEST_URI_TOO_LONG";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.5.13
     *
     * The media format of the requested data is not supported by the server, so the server is rejecting the request.
     */
    StatusCodes[StatusCodes["UNSUPPORTED_MEDIA_TYPE"] = 415] = "UNSUPPORTED_MEDIA_TYPE";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7233#section-4.4
     *
     * The range specified by the Range header field in the request can't be fulfilled; it's possible that the range is outside the size of the target URI's data.
     */
    StatusCodes[StatusCodes["REQUESTED_RANGE_NOT_SATISFIABLE"] = 416] = "REQUESTED_RANGE_NOT_SATISFIABLE";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.5.14
     *
     * This response code means the expectation indicated by the Expect request header field can't be met by the server.
     */
    StatusCodes[StatusCodes["EXPECTATION_FAILED"] = 417] = "EXPECTATION_FAILED";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc2324#section-2.3.2
     *
     * Any attempt to brew coffee with a teapot should result in the error code "418 I'm a teapot". The resulting entity body MAY be short and stout.
     */
    StatusCodes[StatusCodes["IM_A_TEAPOT"] = 418] = "IM_A_TEAPOT";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc2518#section-10.6
     *
     * The 507 (Insufficient Storage) status code means the method could not be performed on the resource because the server is unable to store the representation needed to successfully complete the request. This condition is considered to be temporary. If the request which received this status code was the result of a user action, the request MUST NOT be repeated until it is requested by a separate user action.
     */
    StatusCodes[StatusCodes["INSUFFICIENT_SPACE_ON_RESOURCE"] = 419] = "INSUFFICIENT_SPACE_ON_RESOURCE";
    /**
     * @deprecated
     * Official Documentation @ https://tools.ietf.org/rfcdiff?difftype=--hwdiff&url2=draft-ietf-webdav-protocol-06.txt
     *
     * A deprecated response used by the Spring Framework when a method has failed.
     */
    StatusCodes[StatusCodes["METHOD_FAILURE"] = 420] = "METHOD_FAILURE";
    /**
     * Official Documentation @ https://datatracker.ietf.org/doc/html/rfc7540#section-9.1.2
     *
     * Defined in the specification of HTTP/2 to indicate that a server is not able to produce a response for the combination of scheme and authority that are included in the request URI.
     */
    StatusCodes[StatusCodes["MISDIRECTED_REQUEST"] = 421] = "MISDIRECTED_REQUEST";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc2518#section-10.3
     *
     * The request was well-formed but was unable to be followed due to semantic errors.
     */
    StatusCodes[StatusCodes["UNPROCESSABLE_ENTITY"] = 422] = "UNPROCESSABLE_ENTITY";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc2518#section-10.4
     *
     * The resource that is being accessed is locked.
     */
    StatusCodes[StatusCodes["LOCKED"] = 423] = "LOCKED";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc2518#section-10.5
     *
     * The request failed due to failure of a previous request.
     */
    StatusCodes[StatusCodes["FAILED_DEPENDENCY"] = 424] = "FAILED_DEPENDENCY";
    /**
     * Official Documentation @ https://datatracker.ietf.org/doc/html/rfc7231#section-6.5.15
     *
     * The server refuses to perform the request using the current protocol but might be willing to do so after the client upgrades to a different protocol.
     */
    StatusCodes[StatusCodes["UPGRADE_REQUIRED"] = 426] = "UPGRADE_REQUIRED";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc6585#section-3
     *
     * The origin server requires the request to be conditional. Intended to prevent the 'lost update' problem, where a client GETs a resource's state, modifies it, and PUTs it back to the server, when meanwhile a third party has modified the state on the server, leading to a conflict.
     */
    StatusCodes[StatusCodes["PRECONDITION_REQUIRED"] = 428] = "PRECONDITION_REQUIRED";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc6585#section-4
     *
     * The user has sent too many requests in a given amount of time ("rate limiting").
     */
    StatusCodes[StatusCodes["TOO_MANY_REQUESTS"] = 429] = "TOO_MANY_REQUESTS";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc6585#section-5
     *
     * The server is unwilling to process the request because its header fields are too large. The request MAY be resubmitted after reducing the size of the request header fields.
     */
    StatusCodes[StatusCodes["REQUEST_HEADER_FIELDS_TOO_LARGE"] = 431] = "REQUEST_HEADER_FIELDS_TOO_LARGE";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7725
     *
     * The user-agent requested a resource that cannot legally be provided, such as a web page censored by a government.
     */
    StatusCodes[StatusCodes["UNAVAILABLE_FOR_LEGAL_REASONS"] = 451] = "UNAVAILABLE_FOR_LEGAL_REASONS";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.6.1
     *
     * The server encountered an unexpected condition that prevented it from fulfilling the request.
     */
    StatusCodes[StatusCodes["INTERNAL_SERVER_ERROR"] = 500] = "INTERNAL_SERVER_ERROR";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.6.2
     *
     * The request method is not supported by the server and cannot be handled. The only methods that servers are required to support (and therefore that must not return this code) are GET and HEAD.
     */
    StatusCodes[StatusCodes["NOT_IMPLEMENTED"] = 501] = "NOT_IMPLEMENTED";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.6.3
     *
     * This error response means that the server, while working as a gateway to get a response needed to handle the request, got an invalid response.
     */
    StatusCodes[StatusCodes["BAD_GATEWAY"] = 502] = "BAD_GATEWAY";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.6.4
     *
     * The server is not ready to handle the request. Common causes are a server that is down for maintenance or that is overloaded. Note that together with this response, a user-friendly page explaining the problem should be sent. This responses should be used for temporary conditions and the Retry-After: HTTP header should, if possible, contain the estimated time before the recovery of the service. The webmaster must also take care about the caching-related headers that are sent along with this response, as these temporary condition responses should usually not be cached.
     */
    StatusCodes[StatusCodes["SERVICE_UNAVAILABLE"] = 503] = "SERVICE_UNAVAILABLE";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.6.5
     *
     * This error response is given when the server is acting as a gateway and cannot get a response in time.
     */
    StatusCodes[StatusCodes["GATEWAY_TIMEOUT"] = 504] = "GATEWAY_TIMEOUT";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc7231#section-6.6.6
     *
     * The HTTP version used in the request is not supported by the server.
     */
    StatusCodes[StatusCodes["HTTP_VERSION_NOT_SUPPORTED"] = 505] = "HTTP_VERSION_NOT_SUPPORTED";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc2518#section-10.6
     *
     * The server has an internal configuration error: the chosen variant resource is configured to engage in transparent content negotiation itself, and is therefore not a proper end point in the negotiation process.
     */
    StatusCodes[StatusCodes["INSUFFICIENT_STORAGE"] = 507] = "INSUFFICIENT_STORAGE";
    /**
     * Official Documentation @ https://tools.ietf.org/html/rfc6585#section-6
     *
     * The 511 status code indicates that the client needs to authenticate to gain network access.
     */
    StatusCodes[StatusCodes["NETWORK_AUTHENTICATION_REQUIRED"] = 511] = "NETWORK_AUTHENTICATION_REQUIRED";
})(StatusCodes || (StatusCodes = {}));

class InvalidTokenError extends Error {
}
InvalidTokenError.prototype.name = "InvalidTokenError";
function b64DecodeUnicode(str) {
    return decodeURIComponent(atob(str).replace(/(.)/g, (m, p) => {
        let code = p.charCodeAt(0).toString(16).toUpperCase();
        if (code.length < 2) {
            code = "0" + code;
        }
        return "%" + code;
    }));
}
function base64UrlDecode(str) {
    let output = str.replace(/-/g, "+").replace(/_/g, "/");
    switch (output.length % 4) {
        case 0:
            break;
        case 2:
            output += "==";
            break;
        case 3:
            output += "=";
            break;
        default:
            throw new Error("base64 string is not of the correct length");
    }
    try {
        return b64DecodeUnicode(output);
    }
    catch (err) {
        return atob(output);
    }
}
function jwtDecode(token, options) {
    if (typeof token !== "string") {
        throw new InvalidTokenError("Invalid token specified: must be a string");
    }
    options || (options = {});
    const pos = options.header === true ? 0 : 1;
    const part = token.split(".")[pos];
    if (typeof part !== "string") {
        throw new InvalidTokenError(`Invalid token specified: missing part #${pos + 1}`);
    }
    let decoded;
    try {
        decoded = base64UrlDecode(part);
    }
    catch (e) {
        throw new InvalidTokenError(`Invalid token specified: invalid base64 for part #${pos + 1} (${e.message})`);
    }
    try {
        return JSON.parse(decoded);
    }
    catch (e) {
        throw new InvalidTokenError(`Invalid token specified: invalid json for part #${pos + 1} (${e.message})`);
    }
}

// The text that is returned that indicated the JWT token has expired.
const JWT_MALFORMED_TEXT = 'token provided is malformed';
const JWT_BAD_FORMAT = 'Authorization header provided but does not follow JWT format';
const JWT_EXPIRED_TEXT = 'token expired';
// Amount of time we wait before giving up on identity being updated.
const IDENTITY_EXPIRED_TIMEOUT = 30000;
/**
 * Given a status code and text, decide if an error is the result of an expired or invalid JWT.
 */
function isJWTError(statusCode, isSecurityEnabled, resultText) {
    return ((statusCode === StatusCodes.FORBIDDEN && isSecurityEnabled) ||
        (statusCode === StatusCodes.BAD_REQUEST && resultText?.includes(JWT_MALFORMED_TEXT)) ||
        (statusCode === StatusCodes.BAD_REQUEST && resultText?.includes(JWT_BAD_FORMAT)) ||
        (statusCode === StatusCodes.UNAUTHORIZED && resultText?.includes(JWT_EXPIRED_TEXT)));
}
/**
 * Checks if JWT passed as identityToken is valid.
 */
function assertJWTValid(jwt) {
    if (!jwt.sub) {
        throw new Error(`${WA_CONSOLE_PREFIX}: The "sub" claim is required in your identityToken.`);
    }
    if (!jwt.exp) {
        consoleInfo('It is recommended you set your JWT to expire with an exp claim.');
    }
}
/**
 * This should be called when it is detected that the user's identity token has expired. It will request a new
 * identity token from the host page and update the stored value when a new one is received. If the request times out
 * this function will throw an error.
 *
 * @param serviceManager The instance of the serviceManager for this web chat.
 * @param catastrophicErrorType If the identity token fails when loading the main config, the web chat cannot load.
 */
async function getNewIdentityToken(serviceManager, catastrophicErrorType) {
    const identityExpiredMessage = {
        type: "identityTokenExpired" /* BusEventType.IDENTITY_EXPIRED */,
        identityToken: null,
    };
    try {
        await resolveOrTimeout(serviceManager.eventBus.fire(identityExpiredMessage, serviceManager.instance), IDENTITY_EXPIRED_TIMEOUT);
    }
    catch (error) {
        serviceManager.actions.errorOccurred({
            errorType: OnErrorType.IDENTITY_TOKEN,
            message: `A request for a new identity token was made using the ${"identityTokenExpired" /* BusEventType.IDENTITY_EXPIRED */} event but an error was thrown by a handler.`,
            catastrophicErrorType,
            otherData: error,
        });
        throw error;
    }
    if (!identityExpiredMessage.identityToken || typeof identityExpiredMessage.identityToken !== 'string') {
        let message;
        if (!identityExpiredMessage.identityToken) {
            message = `A request for a new identity token was made using the ${"identityTokenExpired" /* BusEventType.IDENTITY_EXPIRED */} event but no token was set by a handler.`;
        }
        else {
            message = `A request for a new identity token was made using the ${"identityTokenExpired" /* BusEventType.IDENTITY_EXPIRED */} event but the handler returned a non-string token.`;
        }
        serviceManager.actions.errorOccurred({
            errorType: OnErrorType.IDENTITY_TOKEN,
            message,
            catastrophicErrorType,
        });
        throw new Error(message);
    }
    try {
        serviceManager.actions.updateIdentityToken(identityExpiredMessage.identityToken);
    }
    catch (error) {
        serviceManager.actions.errorOccurred({
            errorType: OnErrorType.IDENTITY_TOKEN,
            message: `A request for a new identity token was made using the ${"identityTokenExpired" /* BusEventType.IDENTITY_EXPIRED */} event but an error was thrown.`,
            catastrophicErrorType,
            otherData: error,
        });
        throw error;
    }
}
/**
 * This should be called when we want to get a new identity token if it's going to expire soon. For now, we have
 * hard-coded "soon" to mean 10 seconds. If it will expire, it will request a new identity token from the host page
 * and update the stored value when a new one is received. If the request times out this function will throw an error.
 *
 * @param serviceManager The instance of the serviceManager for this web chat.
 * @param catastrophicErrorType If the identity token fails when loading the main config, the web chat cannot load.
 */
const getNewIdentityTokenIfExpiring = async (serviceManager, catastrophicErrorType) => {
    const currentToken = serviceManager.store.getState().identityToken;
    const decodedToken = doDecodeJWT(currentToken, console.error);
    if (decodedToken.exp) {
        const expiration = new Date(decodedToken.exp * 1000);
        // If the token is going to expire in the next 10 seconds, then get a new one now.
        const timeRemaining = expiration.getTime() - Date.now();
        if (timeRemaining < 10000) {
            await getNewIdentityToken(serviceManager, catastrophicErrorType);
        }
    }
    else {
        await getNewIdentityToken(serviceManager, catastrophicErrorType);
    }
};
/**
 * Decodes the given string provided by the customer as a JWT along with some error handling.
 */
function doDecodeJWT(jwtString, onError) {
    let jwt;
    try {
        jwt = jwtDecode(jwtString);
        assertJWTValid(jwt);
    }
    catch (error) {
        callOnError(onError, {
            errorType: OnErrorType.IDENTITY_TOKEN,
            message: `The provided JWT (identity token) is not valid.`,
            otherData: error,
        });
        throw error;
    }
    return jwt;
}

/**
 * Determines if the given string is a "empty" string. That is a string that has no value or is the literal string
 * "null".
 */
function isEmptyString(value) {
    return !value || value === 'null';
}
/**
 * Parses out the text directive from the given URL if it is present and returns the "start" piece from it. The format
 * of the hash is "[prefix-,]start[,end][,-suffix]".
 *
 * @see "URL Fragment Text Directives" - https://wicg.github.io/scroll-to-text-fragment/
 */
function parseTextDirective(url) {
    if (url) {
        const parsedURL = new URL(url);
        if (parsedURL.hash?.startsWith('#:~:text=')) {
            const parameters = parsedURL.hash.split('=');
            const textFragment = parameters[1];
            if (textFragment) {
                // Strip out the prefix (*-,) and suffix (,-*).
                const startAndEnd = textFragment.replace(/(.+-,)*(.*)(,-.+)/, '$2');
                const textPieces = startAndEnd.split(',');
                // Only return the start text.
                const start = textPieces[0];
                if (start) {
                    // Percent-decode the final result.
                    return decodeURIComponent(start);
                }
            }
        }
    }
    return null;
}
/**
 * Attempts to parse the given value into a JSON object. If that fails, this will just return the value.
 */
function tryJSONParse(data) {
    if (data) {
        try {
            return JSON.parse(data);
        }
        catch (e) {
            // Ignore.
        }
    }
    return data;
}
/**
 * Attempts to parse the given value into a JSON object. Returns a boolean indicating success.
 */
function isJsonString(data) {
    try {
        JSON.parse(data);
    }
    catch (e) {
        return false;
    }
    return true;
}
function parseUnknownDataToMarkdown(data) {
    let content;
    if (data) {
        try {
            if (typeof data === 'object') {
                content = `\`\`\`\n${JSON.stringify(data, null, 2)}\n\`\`\`\n`;
            }
            else if (typeof data === 'string') {
                if (isJsonString(data)) {
                    content = `\`\`\`\n${JSON.stringify(JSON.parse(data), null, 2)}\n\`\`\`\n`;
                }
                else {
                    content = data;
                }
            }
            else {
                content = String(data);
            }
            return content;
        }
        catch (e) {
            consoleError('Cannot parse step content', e);
        }
    }
    return undefined;
}
/**
 * Sometimes we are passed back "null" as a string! In that case, instead of showing "null" as a title or body, we
 * convert it to a real null value.
 *
 * @param str A string for the title or body of the search card.
 */
function convertToEmptyStringIfStringifiedNull(str) {
    return str === 'null' ? null : str;
}
/**
 * Under the covers before it gets to us, a title/body may have been a stringified array. We need to remove the [""]
 * if that is the case.
 *
 * @param str A string for the title or body of the search card.
 */
function convertPossibleStringifiedArrayToFirstString$1(str) {
    if (typeof str === 'string' && str.startsWith('["') && str.endsWith('"]')) {
        try {
            [str] = JSON.parse(str);
        }
        catch (error) {
            // Not an array of strings, so do nothing.
        }
    }
    return str;
}

// Unique ID creation requires a high quality random # generator. In the browser we therefore
// require the crypto API and do not support built-in fallback to lower quality random number
// generators (like Math.random()).
let getRandomValues;
const rnds8 = new Uint8Array(16);
function rng() {
  // lazy load so that environments that need to polyfill have a chance to do so
  if (!getRandomValues) {
    // getRandomValues needs to be invoked in a context where "this" is a Crypto implementation.
    getRandomValues = typeof crypto !== 'undefined' && crypto.getRandomValues && crypto.getRandomValues.bind(crypto);

    if (!getRandomValues) {
      throw new Error('crypto.getRandomValues() not supported. See https://github.com/uuidjs/uuid#getrandomvalues-not-supported');
    }
  }

  return getRandomValues(rnds8);
}

/**
 * Convert array of 16 byte values to UUID string format of the form:
 * XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX
 */

const byteToHex = [];

for (let i = 0; i < 256; ++i) {
  byteToHex.push((i + 0x100).toString(16).slice(1));
}

function unsafeStringify(arr, offset = 0) {
  // Note: Be careful editing this code!  It's been tuned for performance
  // and works in ways you may not expect. See https://github.com/uuidjs/uuid/pull/434
  return byteToHex[arr[offset + 0]] + byteToHex[arr[offset + 1]] + byteToHex[arr[offset + 2]] + byteToHex[arr[offset + 3]] + '-' + byteToHex[arr[offset + 4]] + byteToHex[arr[offset + 5]] + '-' + byteToHex[arr[offset + 6]] + byteToHex[arr[offset + 7]] + '-' + byteToHex[arr[offset + 8]] + byteToHex[arr[offset + 9]] + '-' + byteToHex[arr[offset + 10]] + byteToHex[arr[offset + 11]] + byteToHex[arr[offset + 12]] + byteToHex[arr[offset + 13]] + byteToHex[arr[offset + 14]] + byteToHex[arr[offset + 15]];
}

const randomUUID = typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID.bind(crypto);
var native = {
  randomUUID
};

function v4(options, buf, offset) {
  if (native.randomUUID && !buf && !options) {
    return native.randomUUID();
  }

  options = options || {};
  const rnds = options.random || (options.rng || rng)(); // Per 4.4, set bits for version and `clock_seq_hi_and_reserved`

  rnds[6] = rnds[6] & 0x0f | 0x40;
  rnds[8] = rnds[8] & 0x3f | 0x80; // Copy bytes to buffer, if provided

  return unsafeStringify(rnds);
}

/**
 * The different types of things we can generate UUIDs for. Our test cases generate sequential IDs instead of random
 * UUIDs to produce deterministic IDs that can be hardcoded in tests and snapshots. To minimize the disruption that
 * changes in these IDs can cause (like if a new ID gets inserted), we divide IDs by type where each type gets its own
 * sequence. The type is set in the highest segment of the sequential IDs that are generated. This enum is only used
 * for testing purposes and has no impact on production code. If you change the values of any of these items, it
 * might break test cases that have IDs hardcoded in them.
 */
var UUIDType;
(function (UUIDType) {
    UUIDType[UUIDType["MISCELLANEOUS"] = 1] = "MISCELLANEOUS";
    UUIDType[UUIDType["LOCAL_MESSAGE"] = 2] = "LOCAL_MESSAGE";
    UUIDType[UUIDType["MESSAGE"] = 3] = "MESSAGE";
    UUIDType[UUIDType["COMPONENT"] = 4] = "COMPONENT";
    UUIDType[UUIDType["USER"] = 6] = "USER";
    UUIDType[UUIDType["DEVICE_ID"] = 8] = "DEVICE_ID";
    UUIDType[UUIDType["FILE"] = 9] = "FILE";
})(UUIDType || (UUIDType = {}));

/**
 * The function generates a new v4 UUID. This function is here to provide a nicer mechanism for mocking UUIDs in
 * test cases.
 *
 * @param type This is the "type" of the UUID that is being generated. This is only used by the mock UUID generator
 * for test cases. Each "type" will get it's own set of IDs that don't overlap with other types.
 */
function uuid(type) {
    return v4();
}

/**
 * Returns the version we should be using for requests. If we want to use a random named version for development purposes,
 * we must set __ibm__?.useExactVersion to true.
 *
 * @param publicConfig public config object
 */
function getVersionLoaded(publicConfig) {
    return publicConfig.__ibm__?.useExactVersion ? publicConfig.clientVersion : VERSION;
}

/* eslint-disable prefer-destructuring */
const AMPLITUDE_API_KEY = false;
const WWX_CDN_ENDPOINT = false;

/**
 * Used in loadWAC to determine the right route for the public configs, the /message endpoint, and the history endpoint
 * to use. It has been moved to loadWac so that once watsonplatform.net is deprecated this is the only file that needs
 * to be changed and any version after the initial moving of this mapping will still work.
 */
const REGION_MAP = {
    // WXA on IBM Cloud
    dev: 'integrations.us-south.assistant.dev.watson.appdomain.cloud',
    staging: 'integrations.us-south.assistant.test.watson.appdomain.cloud',
    'us-south': 'integrations.us-south.assistant.watson.appdomain.cloud',
    'us-east': 'integrations.us-east.assistant.watson.appdomain.cloud',
    'jp-tok': 'integrations.jp-tok.assistant.watson.appdomain.cloud',
    'au-syd': 'integrations.au-syd.assistant.watson.appdomain.cloud',
    'eu-gb': 'integrations.eu-gb.assistant.watson.appdomain.cloud',
    'eu-de': 'integrations.eu-de.assistant.watson.appdomain.cloud',
    'kr-seo': 'integrations.kr-seo.assistant.watson.appdomain.cloud',
    // WXO on AWS
    'aws-dev': 'integrations.aws-us-east-1.assistant.dev.watson.appdomain.cloud',
    'aws-staging': 'integrations.aws-us-east-1.assistant.test.watson.appdomain.cloud',
    'aws-us-east-1': 'integrations.aws-us-east-1.assistant.watson.appdomain.cloud',
    'aws-preprod': 'integrations.aws-us-east-1.assistant.preprod.watson.appdomain.cloud',
    'aws-eu-central-1': 'integrations.aws-eu-central-1.assistant.watson.appdomain.cloud',
    'aws-ap-southeast-1': 'integrations.aws-ap-southeast-1.assistant.watson.appdomain.cloud',
    'aws-ap-south-1': 'integrations.aws-ap-south-1.assistant.watson.appdomain.cloud',
    // WXO on IBM Cloud
    'wxo-dev': 'integrations.us-south.assistant-builder.dev.watson.appdomain.cloud',
    'wxo-staging': 'integrations.us-south.assistant-builder.test.watson.appdomain.cloud',
    'wxo-us-south': 'integrations.us-south.assistant-builder.watson.appdomain.cloud',
    'wxo-us-east': 'integrations.us-east.assistant-builder.watson.appdomain.cloud',
    'wxo-eu-gb': 'integrations.eu-gb.assistant-builder.watson.appdomain.cloud',
    'wxo-ca-tor': 'integrations.ca-tor.assistant-builder.watson.appdomain.cloud',
    'wxo-eu-de': 'integrations.eu-de.assistant-builder.watson.appdomain.cloud',
    'wxo-jp-tok': 'integrations.jp-tok.assistant-builder.watson.appdomain.cloud',
    'wxo-au-syd': 'integrations.au-syd.assistant-builder.watson.appdomain.cloud',
};
/**
 * Gets the region hostname for calls to the integrations server.
 *
 * @param publicConfig The public config object.
 */
function getRegionHostname(publicConfig) {
    return REGION_MAP[publicConfig.region];
}

const DEFAULT_REMOTE_INIT_CONFIG = {
    home_screen: DEFAULT_HOME_SCREEN_CONFIG,
    alternate_suggestions: DEFAULT_ALTERNATE_SUGGESTIONS_STATE.config,
    branding_config: DEFAULT_BRANDING_STATE.config,
    launcher_config: DEFAULT_LAUNCHER.config,
};
const AGENT_APP_DEFAULT_PUBLIC_CONFIG = {
    headerConfig: {
        hideMinimizeButton: true,
    },
    openChatByDefault: true,
    showLauncher: false,
    disableCustomElementMobileEnhancements: true,
    disclaimer: {
        isOn: false,
    },
    __ibm__: {
        isReadonly: true,
    },
};
const AGENT_APP_DEFAULT_REMOTE_INIT_CONFIG = {
    alternate_suggestions: {
        is_on: false,
    },
    home_screen: {
        is_on: false,
    },
    // When using agent app, we use auth tokens instead.
    authorization: {
        is_on: false,
    },
};
const AGENT_APP_SLUG = 'agentapp';
const CHAT_SLUG = 'chat';
/**
 * Gets the base url for config and post url.
 *
 * @param publicConfig The public config object.
 */
function getBaseURL(publicConfig) {
    const regionHostname = getRegionHostname(publicConfig);
    const prefix = publicConfig.servers?.assistantURLPrefix || `https://${regionHostname}`;
    return `${prefix}/${publicConfig.subscriptionID || 'public'}`;
}
/**
 * Gets the CPD base url for config, message, and history urls. IntegrationID added since CDN is the only place that
 * doesn't need it, so its handled differently
 *
 * @param publicConfig The public config object.
 * @param agentAppOverride If true, we call the agentapp channel instead of the chat channel.
 */
function getCPDBaseChatURL(publicConfig, agentAppOverride) {
    let cpdURL = null;
    if (publicConfig.cloudPrivateHostURL) {
        const integrationSlug = agentAppOverride ? AGENT_APP_SLUG : CHAT_SLUG;
        // Make sure the host url ends with a trailing slash.
        cpdURL = `${publicConfig.cloudPrivateHostURL.replace(/\/?$/, '/')}v1/${integrationSlug}/${publicConfig.integrationID}`;
    }
    return cpdURL;
}
/**
 * Given a config object, either returns the cloudPrivateHostURL used in CP4D or the url for the CDN provided in the
 * environment.
 *
 * @param publicConfig The public config object.
 */
function getCDNURL(publicConfig) {
    if (publicConfig?.cloudPrivateHostURL) {
        // If we are in CPD we are using the cloudPrivateHostURL with /static/webchat appended.
        // Make sure the host url ends with a trailing slash.
        return `${publicConfig.cloudPrivateHostURL.replace(/\/?$/, '/')}static/webchat`;
    }
    if (publicConfig?.servers?.webChatScriptPrefix) {
        return publicConfig?.servers?.webChatScriptPrefix;
    }
    // If no overrides, use the cdn endpoint provided in the environment on build.
    return WWX_CDN_ENDPOINT;
}
/**
 * Given a config object, either returns the WXO_ENDPOINT override or a location of the remoteEntry file for the
 * wxo mfe.
 *
 * @param publicConfig The public config object.
 */
function getWXOUrl(publicConfig) {
    const versionLoaded = getVersionLoaded(publicConfig);
    const cdnEndpoint = getCDNURL(publicConfig);
    const baseURL = `${cdnEndpoint}/versions/${versionLoaded}`;
    return `${baseURL}/wxo-mfe/remoteEntry.js`;
}
/**
 * Given a config object, returns the URL to the config endpoint used for getting widget configuration. This URL
 * will have the integration ID added to it if necessary and if no custom URL was specified.
 *
 * @param publicConfig The public config object.
 * @param initConfig Indicates if we want the URL for the main config or the "init" config (the "init" config is
 * loaded immediately when the page is loaded).
 */
function getConfigURL(publicConfig, initConfig) {
    // First check for a custom config url.
    const customURL = publicConfig.__ibm__?.customConfigURL;
    if (customURL) {
        // If the public config specifies a custom URL, then return it as-is.
        return customURL;
    }
    // Next, check if we're in cp4d and should use a cp4d url.
    const cpdBaseURL = getCPDBaseChatURL(publicConfig);
    if (cpdBaseURL) {
        // If the public config specifies a cpd URL, then return it with /config appended as well as ?type=init if the init config
        // was requested.
        return `${cpdBaseURL}/config${''}`;
    }
    const baseURL = getBaseURL(publicConfig);
    const configTypeParam = '';
    // If the agent app config is to be fetched, it should be done through the /agentapp channel. This also fixes
    // security holes through mix-match channel APIs.
    // For more info: https://github.ibm.com/watson-engagement-advisor/wea-backlog/issues/48720
    if (publicConfig.agentAppConfig?.is_on) {
        return `${baseURL}/agentapp/${publicConfig.integrationID}/config${configTypeParam}`;
    }
    return `${baseURL}/chat/${publicConfig.integrationID}/config${configTypeParam}`;
}
/**
 * Given a config object, returns the URL to the message endpoint used for sending messages. This URL will have the
 * integration ID added to it if necessary and if no custom URL was specified.
 */
function getMessageURL(publicConfig) {
    // First check for a custom message url.
    if (publicConfig.__ibm__?.customMessageURL) {
        // If the config specifies a custom URL, then return it as-is.
        return publicConfig.__ibm__.customMessageURL;
    }
    // Next, check if we're in cp4d and should use a cp4d url.
    const cpdBaseURL = getCPDBaseChatURL(publicConfig);
    if (cpdBaseURL) {
        // If the public config specifies a cpd URL, then return it with /message appended.
        return `${cpdBaseURL}/message`;
    }
    const baseURL = getBaseURL(publicConfig);
    return `${baseURL}/chat/${publicConfig.integrationID}/message`;
}
/**
 * Given a config object, returns the URL to the message endpoint used for sending messages. This URL will have the
 * integration ID added to it if necessary and if no custom URL was specified.
 */
function getSessionURL(publicConfig, sessionID) {
    // Next, check if we're in cp4d and should use a cp4d url.
    const cpdBaseURL = getCPDBaseChatURL(publicConfig);
    if (cpdBaseURL) {
        // If the public config specifies a cpd URL, then use it as the base of the url.
        return `${cpdBaseURL}/sessions/${sessionID}?version=${INTEGRATIONS_VERSION}`;
    }
    const baseURL = getBaseURL(publicConfig);
    return `${baseURL}/chat/${publicConfig.integrationID}/sessions/${sessionID}?version=${INTEGRATIONS_VERSION}`;
}
/**
 * Returns the prefix for the endpoint used to make calls to Amplitude.
 */
function getAnalyticsPath(publicConfig) {
    const prefix = publicConfig.servers?.assistantURLPrefix || `https://${getRegionHostname(publicConfig)}`;
    return `${prefix}/analytics`;
}
/**
 * Given a config object, returns the URL to the history endpoint used for loading historical messages. This URL will
 * have the integration ID and session ID added to it if necessary and if no custom URL was specified.
 *
 * @param publicConfig The public config object.
 * @param sessionID The ID of the session to return the history of.
 */
function getHistoryURL(publicConfig, sessionID) {
    // First check for a custom history url.
    if (publicConfig.__ibm__?.customHistoryPrefix) {
        // If the config specifies a custom URL, then return it as-is with the session ID added.
        return `${publicConfig.__ibm__.customHistoryPrefix}/${sessionID}/notes?version=${INTEGRATIONS_VERSION}`;
    }
    // Next, check if we're in cp4d and should use a cp4d url.
    const cpdBaseURL = getCPDBaseChatURL(publicConfig, publicConfig.agentAppConfig?.is_on);
    if (cpdBaseURL) {
        // If the public config specifies a cpd URL, then return it with /config appended as well as ?type=init if the
        // init config was requested.
        return `${cpdBaseURL}/channel_conversations/${sessionID}/notes?version=${INTEGRATIONS_VERSION}`;
    }
    const baseURL = getBaseURL(publicConfig);
    // If we are calling history in the context of the agentApp, we use the generic 'agentapp' integration instead of
    // 'chat'.
    const integrationSlug = publicConfig.agentAppConfig?.is_on ? AGENT_APP_SLUG : CHAT_SLUG;
    return `${baseURL}/${integrationSlug}/${publicConfig.integrationID}/channel_conversations/${sessionID}/notes?version=${INTEGRATIONS_VERSION}`;
}
/**
 * Fetches configuration data for the widget. This function can also return a JWTError to be handled.
 *
 * @param configUrl URL to fetch config data for widget.
 * @param fetch The fetch function to use.
 * @param isSecurityEnabled Indicates if web chat security is enabled.
 * @param fetchParameters Fetch call parameters.
 */
async function doConfigFetch(configUrl, fetch, isSecurityEnabled, fetchParameters) {
    const result = await fetch(configUrl, fetchParameters);
    if (!result.ok) {
        // If too many requests were made we want to end the retry cycle when this function is used in doWithRetry.
        if (result.status === StatusCodes.TOO_MANY_REQUESTS) {
            return null;
        }
        const responseText = await safeFetchText(result);
        if (isJWTError(result.status, isSecurityEnabled, responseText)) {
            return result;
        }
        throw result;
    }
    return result;
}
/**
 * Loads the main configuration data for the widget from the integrations server. The call is authenticated with the
 * customer's JWT if one is required.
 */
async function getRemoteMainConfig(serviceManager, allowJWTRetry = true) {
    const state = serviceManager.store.getState();
    const { config } = state;
    let { identityToken } = state;
    const publicConfig = config.public;
    const isSecurityEnabled = config.remote.initConfig.authorization?.is_on;
    // Some integrations will not have this set in the remote config. If they do not, we won't grab the new id token
    // until the request to main config fails. If folks are complaining about the extra request, they can just flip
    // the toggle for security off and back on to get the correct remote config shape. We also ignore this condition
    // in tooling where we bypass the security setting using a custom endpoint.
    if (!identityToken && isSecurityEnabled && !publicConfig.__ibm__?.toolingType) {
        await getNewIdentityToken(serviceManager, CatastrophicErrorType.FAILED);
        identityToken = serviceManager.store.getState().identityToken;
    }
    const url = getConfigURL(publicConfig);
    // If an identityToken is set, at it to the headers so that our call to /config does not get rejected.
    let fetchParameters;
    let transactionID;
    if (identityToken) {
        transactionID = uuid(UUIDType.MISCELLANEOUS);
        fetchParameters = {
            headers: {
                authorization: `Bearer ${identityToken}`,
                [API_DEBUGGING_HEADER_NAME]: transactionID,
            },
        };
    }
    let result;
    try {
        result = await doWithRetry(
        // The remote init config is not protected by web chat security so we pass in false.
        () => doConfigFetch(url, serviceManager.fetch, isSecurityEnabled, fetchParameters), [100, 1000, 2000, 5000], 'fetch main config');
    }
    catch (error) {
        serviceManager.actions.errorOccurred({
            errorType: OnErrorType.OPEN_CONFIG,
            catastrophicErrorType: CatastrophicErrorType.FAILED,
            message: 'The main configuration data could not be loaded.',
            errorCode: error?.status || undefined,
            otherData: await safeFetchTextWithTimeout(error),
            transactionID,
        });
        throw new Error(`${WA_CONSOLE_PREFIX} The main configuration data could not be loaded.`);
    }
    // If the result is not okay, it must be a JWT error because any other kind of errors are handled in doConfigFetch
    // which would result in that function throwing an error instead of returning a result.
    if (result && !result.ok) {
        if (allowJWTRetry) {
            // Get a new token and then recursively call this method with allowJWTRetry set to false. You get one chance!
            await getNewIdentityToken(serviceManager, CatastrophicErrorType.FAILED);
            return getRemoteMainConfig(serviceManager, false);
        }
        serviceManager.actions.errorOccurred({
            errorType: OnErrorType.IDENTITY_TOKEN,
            catastrophicErrorType: CatastrophicErrorType.FAILED,
            message: `A valid identity token is required to communicate with the integration.`,
            errorCode: result.status,
            otherData: await safeFetchTextWithTimeout(result),
            transactionID,
        });
        throw new Error();
    }
    if (!result) {
        throw new Error(`${WA_CONSOLE_PREFIX} The main configuration data could not be loaded.`);
    }
    // At the moment, this call returns the old complete configuration data but we only want the service desk and
    // assistant data.
    const resultJSON = await result.json();
    const mainConfig = merge({
        service_desk: resultJSON.service_desk?.config,
        assistant: resultJSON.assistant,
    }, publicConfig.servers?.mainConfig);
    return mainConfig;
}

/**
 * In this file we manage our dynamic imports for the entry of web chat. See https://webpack.js.org/guides/code-splitting/#dynamic-imports.
 */
async function loadRender() {
    const { render } = await import(/* webpackChunkName: "cds-ai-chat-render" */ './scriptRender.js');
    return render;
}
async function loadHAA() {
    const { createService } = await import(
    /* webpackChunkName: "cds-ai-chat-haa" */ './HumanAgentServiceImpl.js');
    return createService;
}
/**
 * Utility function to create a new mock service desk using code splitting to avoid including this code in the main
 * bundle.
 */
async function createMockServiceDesk(parameters) {
    const { MockServiceDesk } = await import(
    /* webpackChunkName: "cds-ai-chat-mock-sd" */ './mockServiceDesk.js');
    return new MockServiceDesk(parameters);
}
const loadLegacyStyles = async () => {
    const { default: styles } = await import(
    /* webpackChunkName: "cds-ai-chat-styles-reset" */ './export.legacy.js');
    return styles;
};
const loadLegacyCarbon = async () => {
    const { default: carbon } = await import(
    /* webpackChunkName: "cds-ai-chat-carbon-custom-styles-reset" */ './export.legacy.carbon.js');
    return carbon;
};
async function loadStylesWithReset() {
    const [styles, carbonStyles] = await Promise.all([loadLegacyStyles(), loadLegacyCarbon()]);
    return carbonStyles + styles;
}
const loadBaseStyles = async () => {
    const { default: styles } = await import(/* webpackChunkName: "cds-ai-chat-styles" */ './export.js');
    return styles;
};
const loadCarbon = async () => {
    const { default: carbon } = await import(
    /* webpackChunkName: "cds-ai-chat-carbon-custom-styles" */ './export.carbon.js');
    return carbon;
};
async function loadStyles() {
    const [styles, carbonStyles] = await Promise.all([loadBaseStyles(), loadCarbon()]);
    return carbonStyles + styles;
}

/**
 * This file documents the interfaces used by the API for communicating with IBM watsonx Assistant. The published
 * documentation for the API can be found here: https://cloud.ibm.com/apidocs/assistant-v2#send-user-input-to-assistant.
 */
/**
 * This is the name of the main dialog skill. A recent change did allow for skills with other names to be created so
 * this is mainly used for legacy or default purposes. This is used when looking up dialog specific data from the
 * context.
 */
const MAIN_SKILL = 'main skill';
/**
 * ID of the start of the dialog as it appears in the dialog state. This is used when identifying dialog responses
 * that are part of a multi-turn response.
 */
const DIALOG_ROOT = 'root';
/**
 * Enum values added by module augmentation do not have values set.
 * This seems to not be an issue for web chat when using webpack but node.js as done by haa-integrations needs to have these values assigned separately.
 */
Object.assign(MessageInputType, {
    REQUEST_CONNECT_TO_AGENT: 'request_connect_to_agent',
    SEARCH: 'search',
});
Object.assign(MessageResponseTypes, {
    SUGGESTION: 'suggestion',
    CONVERSATIONAL_SEARCH: 'conversational_search',
    SEARCH: 'search',
    CHANNEL_TRANSFER: 'channel_transfer',
    TEXT_TO_SPEECH: 'text_to_speech',
    SPEECH_TO_TEXT: 'speech_to_text',
    START_ACTIVITIES: 'start_activities',
    STOP_ACTIVITIES: 'stop_activities',
    DTMF: 'dtmf',
    END_SESSION: 'end_session',
    RECORD: 'record',
});
/**
 * A suggestion can be an indication of a single turn (FAQ) or multi-turn. Typically, the type of suggestion will be
 * used when offering a suggestion to an agent. If it is a single turn, the agent can just copy/paste it to the
 * user. If it is a multi-turn the agent might hand it off back to Watson.
 */
var SuggestionTypes;
(function (SuggestionTypes) {
    /**
     * Single turn.
     */
    SuggestionTypes["ANSWER"] = "answer";
    /**
     * Multi turn.
     */
    SuggestionTypes["FLOW"] = "flow";
})(SuggestionTypes || (SuggestionTypes = {}));

/**
 * Takes data from the v2 message API and transforms into something usable by WAC.
 *
 * @param  message Takes an object in the shape of a v2 message API Send object. With an additional `ui_state`
 * object for local state.
 * @param originalUserText The original text provided by the user before it was potentially modified on the
 * underlying {@link MessageRequest}. This value may be null if there is no text from the user to display.
 * instance.
 * @param id If you have already generated an id, it's ok to pass it in. If not, one will be generated.
 */
function inputItemToLocalItem(message, originalUserText, id = uuid(UUIDType.LOCAL_MESSAGE)) {
    const localMessage = {
        // The individual message in the format of an item from output.generic in the docs above.
        item: {
            response_type: MessageResponseTypes.TEXT,
            ...message.input,
        },
        // ui_state is for rendering concerns and is not persistent.
        ui_state: {
            id,
            originalUserText,
            needsAnnouncement: false, // We don't announce text that came from the user.
        },
        fullMessageID: message.id,
    };
    return localMessage;
}

const EVENT_STOP_STREAMING = 'stop_streaming';
/**
 * These are custom message types that are not part of the v2 API but are generated by the widget for internal use.
 * Note: INLINE_ERROR can be used by custom service desks and is part of the public API.
 */
var InternalMessageResponseType;
(function (InternalMessageResponseType) {
    InternalMessageResponseType["STREAM_LOADING"] = "stream_loading";
    InternalMessageResponseType["SESSION_EXPIRED"] = "session_expired";
    InternalMessageResponseType["CHANNEL_TRANSFER_FAILED"] = "channel_transfer_failed";
    // TODO: Add to wa-fd-types when ready.
    InternalMessageResponseType["PANEL"] = "panel";
    InternalMessageResponseType["WATSON_ORCHESTRATE_FORM"] = "user_defined_ibm_wo_form";
})(InternalMessageResponseType || (InternalMessageResponseType = {}));
/**
 * These are custom message types that are not part of the v2 API but are generated by the widget for internal use.
 */
var InternalMessageRequestType;
(function (InternalMessageRequestType) {
    InternalMessageRequestType["FILE"] = "file";
})(InternalMessageRequestType || (InternalMessageRequestType = {}));
/**
 * The names of channels that support channel transfer.
 */
var CHANNEL_NAME;
(function (CHANNEL_NAME) {
    CHANNEL_NAME["CHAT"] = "chat";
    CHANNEL_NAME["WHATSAPP"] = "whatsapp";
    CHANNEL_NAME["FACEBOOK"] = "facebook";
    CHANNEL_NAME["SLACK"] = "slack";
})(CHANNEL_NAME || (CHANNEL_NAME = {}));
/**
 * The different places a custom menu may be configured.
 *
 * @deprecated
 */
var CustomMenuType;
(function (CustomMenuType) {
    /**
     * The overflow menu on the bot view.
     */
    CustomMenuType["BOT"] = "bot";
    /**
     * The overflow menu on the human agent view.
     */
    CustomMenuType["AGENT"] = "agent";
})(CustomMenuType || (CustomMenuType = {}));

var safeIsNaN = Number.isNaN ||
    function ponyfill(value) {
        return typeof value === 'number' && value !== value;
    };
function isEqual(first, second) {
    if (first === second) {
        return true;
    }
    if (safeIsNaN(first) && safeIsNaN(second)) {
        return true;
    }
    return false;
}
function areInputsEqual(newInputs, lastInputs) {
    if (newInputs.length !== lastInputs.length) {
        return false;
    }
    for (var i = 0; i < newInputs.length; i++) {
        if (!isEqual(newInputs[i], lastInputs[i])) {
            return false;
        }
    }
    return true;
}

function memoizeOne(resultFn, isEqual) {
    if (isEqual === void 0) { isEqual = areInputsEqual; }
    var cache = null;
    function memoized() {
        var newArgs = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            newArgs[_i] = arguments[_i];
        }
        if (cache && cache.lastThis === this && isEqual(newArgs, cache.lastArgs)) {
            return cache.lastResult;
        }
        var lastResult = resultFn.apply(this, newArgs);
        cache = {
            lastResult: lastResult,
            lastArgs: newArgs,
            lastThis: this,
        };
        return lastResult;
    }
    memoized.clear = function clear() {
        cache = null;
    };
    return memoized;
}

const EVENT_NAME_CHANNEL_TRANSFER = 'channel_transfer';
/**
 * The different types of triggers for a meaningful conversation.
 */
var MeaningfulConversationType;
(function (MeaningfulConversationType) {
    /**
     * Indicates the user clicked on the "request agent" button from a connect-to-agent card.
     */
    MeaningfulConversationType["CONNECT_TO_AGENT"] = "connect_to_agent";
    /**
     * Indicates the user clicked on some link in the web chat (which could include any random link from a custom
     * response or an html/markdown response).
     */
    MeaningfulConversationType["LINK"] = "link";
    /**
     * Indicates the user clicked on a search result.
     */
    MeaningfulConversationType["SEARCH"] = "search_result";
})(MeaningfulConversationType || (MeaningfulConversationType = {}));

var SkillType;
(function (SkillType) {
    SkillType["SEARCH"] = "search";
    SkillType["DIALOG"] = "dialog";
    SkillType["ACTIONS"] = "actions";
})(SkillType || (SkillType = {}));

/**
 * Indicates the different states a thread can be in for a given message.
 */
var ThreadStatus;
(function (ThreadStatus) {
    /**
     * Indicates that the response is starting a new thread.
     */
    ThreadStatus[ThreadStatus["STARTED"] = 1] = "STARTED";
    /**
     * Indicates that the request and response are in the same thread and the thread is continuing.
     */
    ThreadStatus[ThreadStatus["CONTINUING"] = 2] = "CONTINUING";
    /**
     * Indicates that the request and response are in the same thread but the response has indicated that thread has
     * ended.
     * This is currently determined by examining the dialog state. If the
     * dialog stack first item is the root then it means this response is a leaf in the dialog tree.
     */
    ThreadStatus[ThreadStatus["ENDED"] = 3] = "ENDED";
})(ThreadStatus || (ThreadStatus = {}));
/**
 * The thread id of the main thread for every channel. It is used in both MessageRequest and Message Response.
 * This ID is specifically for communicating with the bot and is distinct from the id of a sub-thread or the threads
 * with any human agent.
 */
const THREAD_ID_MAIN = 'main';
/**
 * The reason for the escalation.
 */
var ConnectToAgentReason;
(function (ConnectToAgentReason) {
    /**
     * An intentional escalation designed in the dialog.
     */
    ConnectToAgentReason["ASSISTANT_REQUESTED"] = "assistant_requested";
    /**
     * An escalation which occurred due to the user requesting an agent.
     */
    ConnectToAgentReason["USER_REQUESTED"] = "user_requested";
    /**
     * An escalation which occurred due to an error in the service.
     */
    ConnectToAgentReason["GENERAL_ERROR"] = "general_error";
    /**
     * An escalation which occurred due to a failure to transfer a conversation to Watson. This may happen only in
     * backdoor integrations (Intercom), in which an agent has approved a suggestion but the virtual agent failed to
     * continue with the conversation.
     */
    ConnectToAgentReason["TRANSFER_FAILURE"] = "transfer_failure";
})(ConnectToAgentReason || (ConnectToAgentReason = {}));
// ************ END OF EXPERIMENTAL ******************* //
// ************* User Profile ***************** //
/**
 * The set of possible conversation companions.
 */
var UserTypes;
(function (UserTypes) {
    UserTypes["HUMAN_AGENT"] = "human_agent";
    UserTypes["USER"] = "user";
    /**
     * The bot which replies to the user.
     */
    UserTypes["WATSON"] = "watson";
    /**
     * Used to denote that an event was processed/logged by the service
     */
    UserTypes["SERVICE"] = "service";
})(UserTypes || (UserTypes = {}));
/**
 * Gender used in user info provided to Watson by client, typically within a JWT.
 */
var UserGender;
(function (UserGender) {
    UserGender["MALE"] = "male";
    UserGender["FEMALE"] = "female";
    UserGender["OTHER"] = "other";
})(UserGender || (UserGender = {}));

/**
 * Coerces the given value into an array. If the value is already an array, it is returned as-is. Otherwise it is
 * returned as a new array that contains the value as a single entry.
 *
 * @param value The value that's either an array or should be put into an array.
 */
function asArray(value) {
    return Array.isArray(value) ? value : [value];
}
/**
 * Executes an asynchronous "forEach" over the given array with the given callback function. The values are executed
 * in series (as opposed to in parallel). The array may not be modified during the loop.
 *
 * @param array The array to execute the loop over.
 * @param callbackFunction The function to call for each value in the array. The return value from the function will
 * be awaited which means if the value is a Promise, the loop will block until the Promise is resolved. The function
 * does not have to return a Promise in which case no waiting will occur.
 */
async function asyncForEach(array, callbackFunction) {
    for (let index = 0; index < array.length; index++) {
        const value = array[index];
        // eslint-disable-next-line no-await-in-loop
        await callbackFunction(value, index, array);
    }
}
/**
 * Finds the last value in the given array that matches according to the provided predicate. The search will
 * begin from the end of the array and continue backwards until a match is found.
 *
 * @param array The array to search through.
 * @param predicate The function used to determine if each value matches.
 * @param startAt The index to start at in the array. Defaults to length -1.
 * @returns Returns the array value that was found to match or undefined if no match was found.
 */
function findLast(array, predicate, startAt) {
    const lastIndex = findLastIndex(array, predicate);
    return lastIndex === -1 ? undefined : array[lastIndex];
}
/**
 * Finds the last value in the given array that matches according to the provided predicate. The search will
 * begin from the end of the array and continue backwards until a match is found.
 *
 * @param array The array to search through.
 * @param predicate The function used to determine if each value matches.
 * @param startAt The index to start at in the array. Defaults to length -1.
 * @returns Returns the array index that was found to match or -1 if no match was found.
 */
function findLastIndex(array, predicate, startAt) {
    if (array) {
        const startingIndex = array.length - 1 ;
        for (let index = startingIndex; index >= 0; index--) {
            const value = array[index];
            if (predicate(value, index, array)) {
                return index;
            }
        }
    }
    return -1;
}
/**
 * Finds the last value in the given array whose matching object from the given map matches according to the
 * provided predicate. The search will begin from the end of the array and continue backwards until a match is
 * found. This functionality basically works the same as {@link findLast} except that it will retrieve the object
 * from the map for the given id and use the predicate on that object.
 *
 * @param keys The array to search through.
 * @param map The map that contains the objects to look for. The object will be looked up using the key from the
 * provided array.
 * @param predicate The function used to determine if each value matches.
 * @returns Returns the array value that was found to match or undefined if no match was found.
 */
function findLastWithMap(keys, map, predicate) {
    for (let index = keys.length - 1; index >= 0; index--) {
        const key = keys[index];
        const value = map[key];
        if (predicate(value, index, keys)) {
            return value;
        }
    }
    return undefined;
}
/**
 * Pushes all the elements from the source array to the end of the target array.
 */
function pushAll(targetArray, sourceArray) {
    // Resize the target array.
    const originalSize = targetArray.length;
    targetArray.length = originalSize + sourceArray.length;
    // Assign all the values.
    for (let offset = 0; offset < sourceArray.length; offset++) {
        targetArray[originalSize + offset] = sourceArray[offset];
    }
    return targetArray;
}
/**
 * Returns the last value of the given array.
 */
function arrayLastValue(array) {
    return array && array.length ? array[array.length - 1] : null;
}

/**
 * The regular expression for identifying the anything else node. It's basically looking for the word
 * "anything_else" as a separate word in the string surrounding by any non-alphanumeric characters and underscore.
 */
const ANYTHING_ELSE_REGEXP = /([^#a-zA-Z0-9_]|^)anything_else([^a-zA-Z0-9_]|$)/;
const EMPTY_CONTEXT = memoizeOne((assistant) => {
    const context = {};
    if (assistant?.skills) {
        assistant.skills.forEach(skill => {
            if (skill.type === SkillType.DIALOG || skill.type === SkillType.ACTIONS) {
                merge(context, {
                    global: {
                        system: {},
                    },
                    skills: {
                        [skill.skill_reference]: {
                            system: {},
                            user_defined: {},
                        },
                    },
                });
            }
        });
    }
    return context;
});
/**
 * A default request for fetching the welcome node. The ID will need to be filled in when the request is made.
 */
const WELCOME_MESSAGE_REQUEST = {
    id: null,
    input: {
        text: '',
    },
    history: {
        silent: true,
        is_welcome_request: true,
    },
    thread_id: THREAD_ID_MAIN,
};
deepFreeze$1(WELCOME_MESSAGE_REQUEST);
/**
 * This function determines if the given message is an output message (i.e. a message output from the assistant) and
 * is a {@link MessageResponse}. This function acts as a type guard which will narrow the type to
 * {@link MessageResponse} if it returns true.
 */
function isResponse(message) {
    return message && message.output !== undefined;
}
/**
 * Determines if the message is a response from actions.
 *
 * Note: Actions has 'history' under output.user_defined.debug. This will be changed in the near future with history
 * being renamed to turn_events and moved to output.debug. Checks for both are in place in case actions updates.
 */
function isActionsResponse(message) {
    return (isResponse(message) &&
        (message.output.user_defined?.debug?.history !== undefined ||
            message.output.debug?.turn_events !== undefined));
}
// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
function isDateResponseType(localMessage) {
    return localMessage?.item.response_type === MessageResponseTypes.DATE;
}
/**
 * Adds default values to the given MessageResponse.
 */
function addDefaultsToMessage(fullMessage) {
    if (!fullMessage.id) {
        fullMessage.id = uuid(UUIDType.MESSAGE);
    }
    if (!fullMessage.thread_id) {
        fullMessage.thread_id = THREAD_ID_MAIN;
    }
    if (!fullMessage.history) {
        fullMessage.history = {};
    }
    if (!fullMessage.history.timestamp) {
        fullMessage.history.timestamp = Date.now();
    }
    if (fullMessage.history.from_history === undefined) {
        fullMessage.history.from_history = false;
    }
    return fullMessage;
}
/**
 * Indicates if the given local message is either an option or a suggestion response.
 */
function isOptionOrSuggestion(localMessage) {
    switch (localMessage?.item.response_type) {
        case MessageResponseTypes.OPTION:
        case MessageResponseTypes.SUGGESTION:
        case MessageResponseTypes.BUTTON:
            return true;
        default:
            return false;
    }
}
/**
 * This function determines if the given message is a message input to the assistant and is a {@link MessageRequest}.
 * This function will return false if the MessageRequest is an internal event. This function acts as a type guard which
 * will narrow the type to {@link MessageRequest} if it returns true.
 */
function isRequest(message) {
    return message?.input !== undefined;
}
/**
 * Indicates if this message was part of a conversation with a live agent.
 */
function isLiveAgentMessage(message) {
    return Boolean(message.item.agent_message_type);
}
/**
 * Indicates if this message contains a message that was part of a conversation with a live agent.
 */
function hasLiveAgentMessage(message) {
    return ((isResponse(message) && Boolean(message.output.generic?.find(item => item?.agent_message_type))) ||
        (isRequest(message) && Boolean(message.input.agent_message_type)));
}
/**
 * This function determines if the given message is an internal event message that was input (i.e. a message we sent to
 * the back-end that is a {@link MessageRequest} with {@link EventInput} in it). This function acts as a type guard
 * which will narrow the type to 'MessageRequest<EventInput>' if it returns true.
 */
function isEventRequest(message) {
    return message?.input?.message_type === MessageInputType.EVENT;
}
/**
 * This function determines if the given message is an internal event message that was input in order to update the
 * message history (i.e. a message we sent to the back-end that is a {@link MessageEvent} with {@link EventInput} in it
 * which contains {@link UpdateHistoryEvent} data). This function acts as a type guard which will narrow the type to
 * 'MessageRequest<EventInput<UpdateHistoryEvent>>' if it returns true.
 */
function isUpdateHistoryRequest(message) {
    return (isEventRequest(message) &&
        message?.input?.event?.name === EVENT_NAME_UPDATE_HISTORY);
}
/**
 * This function determines if the given generic item is a text item. This function acts as a type guard which will
 * narrow the type to {@link TextItem} if it returns true.
 */
function isTextItem(item) {
    return item && item.response_type === 'text' && item.text !== undefined;
}
function isTyping(message) {
    // eslint-disable-next-line eqeqeq
    return message.response_type === MessageResponseTypes.PAUSE && message.typing == true;
}
function isPause(message) {
    return message.response_type === MessageResponseTypes.PAUSE;
}
/**
 * This is a type guard that determines if the given item is an {@link OptionItem} item.
 */
function isOptionItem(item) {
    return item?.response_type === MessageResponseTypes.OPTION && item.options !== undefined;
}
function isChannelTransfer(response) {
    return response?.response_type === MessageResponseTypes.CHANNEL_TRANSFER;
}
/**
 * Determines if the message is a transfer to agent response.
 */
function isChannelTransferToAgent(message) {
    const { generic } = message.output;
    return generic.some(isChannelTransfer) && generic.some(isConnectToAgent);
}
/**
 * Determines if the message contains a panel response type.
 */
function hasMessageResponsePanel(message) {
    return message.output.generic.some(item => {
        let messageItem = item;
        // TODO: This check is temporary until there is back-end support for the panel response type.
        if (betaUserDefinedResponses[item.user_defined?.user_defined_type]) {
            messageItem = item.user_defined;
        }
        return isPanelResponseType(messageItem);
    });
}
/**
 * Determines if the given item contains 'chat' as a channel in order to determine if it should be rendered by web chat.
 * If the item does not contain the 'channel' property, then we can return 'true' because this means all channels can
 * render the item.
 */
function isForChatChannel(item) {
    if (item.channels) {
        return item.channels.some(channel => channel.channel === 'chat');
    }
    return true;
}
/**
 * Determines if the message response is from a main thread.
 */
function isFromAMainThread(message, request) {
    // We know we're in a multi-turn if it has a thread id. It's a little harder to tell if it's the first message though.
    // We can check the request to see if the request thinks it was already in a thread.
    return message.thread_id === THREAD_ID_MAIN || request?.thread_id === THREAD_ID_MAIN;
}
/**
 * Determines if the message response is a fallback.
 */
function isFallbackResponse(message) {
    return message.output.debug?.branch_exited_reason === 'fallback';
}
/**
 * Determines if the current message is a repeat response of the previous.
 *
 * @param currentResponse The last response the user sees.
 * @param previousResponse The previous response from the last one.
 */
function isRepeatResponse(currentResponse, previousResponse) {
    // We can't determine a repeat response if either responses are not provided.
    if (!currentResponse || !previousResponse) {
        return false;
    }
    const currentLastDialogNode = arrayLastValue(currentResponse.output.debug?.nodes_visited)?.dialog_node;
    const previousLastDialogNode = arrayLastValue(previousResponse.output.debug?.nodes_visited)?.dialog_node;
    // The current response is a duplicate of the previous if their last dialog nodes are the same.
    return currentLastDialogNode && currentLastDialogNode === previousLastDialogNode;
}
/**
 * Indicates if the dialog state indicates that dialog is now at the root.
 */
function isAtStackRoot(state) {
    // In the case of disambiguation, the stack may be empty in which case, we'll still count it as the root.
    return Boolean(!state.dialog_stack?.[0] || state.dialog_stack[0].dialog_node === DIALOG_ROOT);
}
/**
 * Converts any line breaks to spaces.
 */
function replaceLineBreak(text) {
    return text.replace(/(\r\n|\n|\r)/gm, ' ');
}
/**
 * Generates a {@link MessageRequest} for the given {@link Option} that the user has selected. This is used for
 * generating the request to the server once the user has selected a choice from an option or suggestion list.
 *
 * @param choice The selected value.
 * @param relatedResponseID If this choice was made as the result of the choice choosing a value from a previous
 * {@link MessageResponse}, this should be the ID of that message.
 */
function createMessageRequestForChoice(choice, relatedResponseID) {
    // The "value" of the choice contains the data that is to be sent to the server when this choice is selected.
    // We'll clone it and add in the history value which stores the user-visible label in the history store.
    const messageRequest = {
        id: uuid(UUIDType.MESSAGE),
        thread_id: THREAD_ID_MAIN,
        ...cloneDeep(choice.value),
    };
    messageRequest.history = {
        label: choice.label,
        relatedMessageID: relatedResponseID,
    };
    return messageRequest;
}
/**
 * Generates a {@link MessageRequest} for the given {@link Option} that the user has selected. This is used for
 * generating the request to the server once the user has selected a choice from an option or suggestion list.
 *
 * @param buttonItem The button item.
 * @param relatedResponseID The id of the {@link MessageResponse} the button item came from.
 * {@link MessageResponse}, this should be the ID of that message.
 */
function createMessageRequestForButtonItemOption(buttonItem, relatedResponseID) {
    // The "value" of the choice contains the data that is to be sent to the server when this choice is selected.
    const messageRequest = {
        id: uuid(UUIDType.MESSAGE),
        thread_id: THREAD_ID_MAIN,
        input: null,
    };
    if (buttonItem.value?.input?.text) {
        messageRequest.input = cloneDeep(buttonItem.value.input);
    }
    else {
        messageRequest.input = { text: buttonItem.label };
    }
    messageRequest.history = { relatedMessageID: relatedResponseID };
    return messageRequest;
}
/**
 * Generates a {@link MessageRequest} to request the welcome node.
 */
function createWelcomeRequest() {
    return addDefaultsToMessage({
        id: uuid(UUIDType.MESSAGE),
        input: {
            text: '',
        },
        history: {
            silent: true,
            is_welcome_request: true,
        },
        thread_id: THREAD_ID_MAIN,
    });
}
/**
 * Generates a {@link MessageRequest} for the given text message sent by the user. This is used for generating the
 * request to send to the server when the user has typed something into the input field.
 */
function createMessageRequestForText(text) {
    // The "value" of the choice contains the data that is to be sent to the server when this choice is selected.
    // We'll clone it and add in the history value which stores the user-visible label in the history store.
    return addDefaultsToMessage({
        input: {
            // The assistant will choke if we send it text with line breaks in it, so we have to remove them first.
            text: replaceLineBreak(text),
            message_type: MessageInputType.TEXT,
        },
    });
}
/**
 * Generates a {@link MessageRequest} to represent a file upload.
 */
function createMessageRequestForFileUpload(upload) {
    return addDefaultsToMessage({
        id: upload.id,
        input: {
            text: upload.file.name,
            message_type: InternalMessageRequestType.FILE,
            agent_message_type: AgentMessageType.FROM_USER,
        },
        history: {
            file_upload_status: FileStatusValue.UPLOADING,
        },
    });
}
function createMessageRequestForDate(inputString, userString, relatedResponseID) {
    const messageRequest = createMessageRequestForText(inputString);
    messageRequest.history = {
        label: userString,
        relatedMessageID: relatedResponseID,
    };
    return messageRequest;
}
/**
 * Generates a {@link MessageResponse} for the given text message sent to the user.
 */
function createMessageResponseForText(text, threadID = THREAD_ID_MAIN, responseType = MessageResponseTypes.TEXT, context) {
    const textItem = {
        response_type: responseType,
        text,
    };
    const messageResponse = {
        id: uuid(UUIDType.MESSAGE),
        thread_id: threadID,
        output: {
            generic: [textItem],
        },
    };
    return messageResponse;
}
/**
 * Generates a {@link MessageResponse} for the given item sent to the user.
 */
function createMessageResponseForItem(item, context) {
    const messageResponse = {
        output: {
            generic: [item],
        },
    };
    return addDefaultsToMessage(messageResponse);
}
/**
 * Generates a {@link MessageRequest<EventInput} for the given eventData. This is used to properly format the event
 * before sending it to the back-end with the messageService.
 */
function createEventMessageRequest(eventData) {
    // Build the full eventMessage now that we have the eventData to send.
    const eventMessage = {
        input: {
            message_type: MessageInputType.EVENT,
            event: eventData,
        },
    };
    return addDefaultsToMessage(eventMessage);
}
/**
 * Indicates if the dialog response is a "connect_to_agent" message.
 */
function isConnectToAgent(response) {
    return response?.response_type === MessageResponseTypes.CONNECT_TO_AGENT;
}
function isCardResponseType(response) {
    // TODO: Add Card to wa-fd-types MessageResponseTypes enum.
    return response?.response_type === MessageResponseTypes.CARD;
}
function isCarouselResponseType(response) {
    // TODO: Add Carousel to MessageResponseTypes enum.
    return response?.response_type === MessageResponseTypes.CAROUSEL;
}
function isButtonResponseType(response) {
    // TODO: Add Button to MessageResponseTypes enum.
    return response?.response_type === MessageResponseTypes.BUTTON;
}
function isShowPanelButtonType(response) {
    return isButtonResponseType(response) && response.button_type === ButtonItemType.SHOW_PANEL;
}
/**
 * Determines if the provided message item is a response type that supports nesting response types.
 */
function isResponseWithNestedItems(item) {
    if (isButtonResponseType(item)) {
        return hasBodyOrFooter(item.panel);
    }
    if (isCardResponseType(item) || isPanelResponseType(item)) {
        return hasBodyOrFooter(item);
    }
    if (isCarouselResponseType(item)) {
        return item.items !== undefined;
    }
    return isGridResponseType(item);
}
function hasBodyOrFooter(item) {
    return item?.body !== undefined || item?.footer !== undefined;
}
/**
 * Object containing mapping `LocalMessage.message.user_defined.user_defined_type` values to the function to transform
 * the user_defined response to a real response inside web chat.
 */
const betaUserDefinedResponses = {
    /**
     * Transform the temporary user_defined response type for a wXo form to the temporary internal response type.
     */
    user_defined_ibm_wo_form(localMessage) {
        const localMessageCopy = cloneDeep(localMessage);
        localMessageCopy.item.response_type =
            InternalMessageResponseType.WATSON_ORCHESTRATE_FORM;
        return localMessageCopy;
    },
    /*
    Example:
    {
      response_type: "user_defined",
      user_defined: {
        user_defined_type: "IBM_BETA_TRANSFORM_RESPONSE",
        response_type: "card",
        body: [{ "response_type": "text", "text: "Hello, world!" }]
      }
    }
  
    to
  
    {
      response_type: "card",
      body: [{ "response_type": "text", "text: "Hello, world!" }]
    }
  
    */
    IBM_BETA_TRANSFORM_RESPONSE(localMessage) {
        const localMessageCopy = cloneDeep(localMessage);
        const userDefinedCopy = cloneDeep(localMessage.item.user_defined);
        localMessageCopy.item = userDefinedCopy;
        delete localMessageCopy.item.user_defined_type;
        return localMessageCopy;
    },
};
/**
 * Returns if the given local message has a user_defined response type with user_defined.user_defined_type that matches
 * one of the responses in our beta list.
 *
 * @param localMessage A message we are checking to see if it needs to be transformed.
 */
function isBetaUserDefinedResponse(localMessage) {
    return (localMessage.item.response_type === MessageResponseTypes.USER_DEFINED &&
        betaUserDefinedResponses[localMessage.item.user_defined?.user_defined_type] !== undefined);
}
/**
 * Transforms a given user_defined response with a user_defined.user_defined_type that matches
 * one of the responses in our beta list to a new format using a InternalMessageResponseType.
 *
 * @param localMessage A message that needs to be transformed.
 */
function transformBetaUserDefinedResponse(localMessage) {
    const betaUserDefinedResponseFunction = betaUserDefinedResponses[localMessage.item.user_defined?.user_defined_type];
    return betaUserDefinedResponseFunction(localMessage);
}
/**
 * Determines if the given message should be rendered as custom message.
 */
function renderAsUserDefinedMessage(messageItem) {
    const responseType = messageItem.response_type;
    switch (responseType) {
        case MessageResponseTypes.TEXT:
        case MessageResponseTypes.IMAGE:
        case MessageResponseTypes.OPTION:
        case MessageResponseTypes.SUGGESTION:
        case MessageResponseTypes.SEARCH:
        case MessageResponseTypes.CONNECT_TO_AGENT:
        case MessageResponseTypes.CHANNEL_TRANSFER:
        case MessageResponseTypes.IFRAME:
        case MessageResponseTypes.VIDEO:
        case MessageResponseTypes.AUDIO:
        case MessageResponseTypes.DATE:
        case MessageResponseTypes.CONVERSATIONAL_SEARCH:
        case MessageResponseTypes.TABLE:
        case MessageResponseTypes.INLINE_ERROR:
        case InternalMessageResponseType.STREAM_LOADING:
        case InternalMessageResponseType.SESSION_EXPIRED:
        case InternalMessageResponseType.CHANNEL_TRANSFER_FAILED:
        case MessageResponseTypes.CARD:
        case MessageResponseTypes.CAROUSEL:
        case MessageResponseTypes.BUTTON:
        case MessageResponseTypes.GRID:
        case InternalMessageResponseType.PANEL:
        case InternalMessageResponseType.WATSON_ORCHESTRATE_FORM:
            return false;
        default:
            // If the custom response is for the tour feature then don't render as a custom message since it will be rendered
            // as a tour instead.
            return !renderAsTour(messageItem);
    }
}
/**
 * Determines if the LocalMessage should be rendered as a tour.
 */
function renderAsTour(messageItem) {
    return hasTourUserDefinedType(messageItem);
}
/**
 * Determines if the Generic item's user_defined_type matched the type for the tour beta.
 */
function hasTourUserDefinedType(message) {
    // For now the tour response will be a custom message with a specific user_defined_type.
    return message?.user_defined?.user_defined_type === 'IBM_BETA_JOURNEYS_TOUR';
}
/**
 * Indicates if the given remote config indicates that a service desk is configured.
 */
function hasServiceDesk(config) {
    return Boolean(config.public.serviceDeskFactory || hasSupportedServiceDesk(config));
}
/**
 * Indicates if the given config indicates that a supported service desk is configured. This may either be a
 * server-side configuration service desk or a client-only service desk.
 */
function hasSupportedServiceDesk(config) {
    return Boolean(config.remote.initConfig.service_desk?.is_on || config.public.serviceDesk?.integrationType);
}
/**
 * Determines if in the given array of messages there is at least one response to a request from the user.
 */
function hasResponseToRequest(messageIDs, allMessageItemsByID, allMessagesByID) {
    let foundRequest = false;
    for (let index = 0; index < messageIDs.length; index++) {
        const message = allMessageItemsByID[messageIDs[index]];
        const originalMessage = allMessagesByID[message?.fullMessageID];
        if (foundRequest && isResponse(originalMessage)) {
            return true;
        }
        if (isRequest(originalMessage) && !originalMessage?.history?.silent) {
            foundRequest = true;
        }
    }
    return false;
}
/**
 * Indicates if the given message came from the "anything else" node.
 */
function isAnythingElse(message) {
    // We have to go look at the debug info to find the anything_else condition.
    // Note: Probably need to limit this to a root AE Node, but I don't know how.
    const nodes = message?.output.debug?.nodes_visited;
    return Boolean(nodes?.find(node => ANYTHING_ELSE_REGEXP.test(node.conditions)));
}
/**
 * Creates a new {@link TextItem}.
 */
function createTextItem(text, streamID) {
    const item = {
        response_type: MessageResponseTypes.TEXT,
        text,
    };
    return item;
}
/**
 * Determines if the given search result item is empty (has no results in it).
 */
function isSearchResultItem(item) {
    return item?.response_type === MessageResponseTypes.SEARCH;
}
/**
 * Determines if the given search result item is a conversational search item.
 */
function isConversationalSearchItem(item) {
    return item?.response_type === MessageResponseTypes.CONVERSATIONAL_SEARCH;
}
/**
 * Determines if the given search result item is empty (has no results in it).
 */
function isEmptySearchResultItem(item) {
    return !item?.primary_results?.length && !item?.results?.length;
}
/**
 * Determines if the provided local message has a chat status response type message.
 */
function isChatStatusResponseType(message) {
    const responseType = message?.item.response_type;
    switch (responseType) {
        case InternalMessageResponseType.SESSION_EXPIRED:
        case InternalMessageResponseType.CHANNEL_TRANSFER_FAILED:
            return true;
        default:
            return false;
    }
}
/**
 * Determines if the provided message item can be rendered in a response body item.
 */
function isItemSupportedInResponseBody(item) {
    switch (item.response_type) {
        case MessageResponseTypes.IMAGE:
        case MessageResponseTypes.IFRAME:
        case MessageResponseTypes.VIDEO:
        case MessageResponseTypes.AUDIO:
        case MessageResponseTypes.TEXT:
        case MessageResponseTypes.USER_DEFINED:
        case MessageResponseTypes.CARD:
        case MessageResponseTypes.GRID:
            return true;
        default:
            return false;
    }
}
/**
 * Determines if the message item is a carousel response type with a single item.
 */
function isSingleItemCarousel(messageItem) {
    return isCarouselResponseType(messageItem) && messageItem.items.length === 1;
}
function isGridResponseType(item) {
    return item?.response_type === MessageResponseTypes.GRID;
}
function isPanelResponseType(item) {
    return item?.response_type === InternalMessageResponseType.PANEL;
}
function getOptionType(preference, totalOptions) {
    let type = 'button';
    if (preference && preference === 'button') {
        type = 'button';
    }
    else if (preference && preference === 'dropdown') {
        type = 'dropdown';
    }
    else if (totalOptions > 4) {
        type = 'dropdown';
    }
    return type;
}
/**
 * Indicates if the given stream chunk is a partial item.
 */
function isStreamPartialItem(chunk) {
    return Boolean(chunk.partial_item);
}
/**
 * Indicates if the given stream chunk is a complete item.
 */
function isStreamCompleteItem(chunk) {
    return Boolean(chunk.complete_item);
}
/**
 * Indicates if the given stream chunk is a complete item.
 */
function isStreamPartialOrCompleteItem(chunk) {
    return isStreamPartialItem(chunk) || isStreamCompleteItem(chunk);
}
/**
 * Indicates if the given stream chunk is a partial item.
 */
function isStreamFinalResponse(chunk) {
    return Boolean(chunk.final_response);
}
/**
 * Returns the ID of a streaming item that is part of a message response. If the item does not have a streaming ID,
 * this will return null;
 */
function streamItemID(messageID, item) {
    const itemID = item?.streaming_metadata?.id;
    if (!itemID) {
        return null;
    }
    return `${messageID}-${itemID}`;
}
/**
 * Returns the dimensions info for the given media item.
 */
function getMediaDimensions(item) {
    return item.dimensions || item.channel_options?.chat?.dimensions;
}
/**
 * Returns the last response message from the bot (excludes agent messages) that has a context object on it.
 */
function getLastBotResponseWithContext(state) {
    const messageIDs = state.botMessageState.messageIDs || [];
    return findLastWithMap(messageIDs, state.allMessagesByID, message => isResponse(message) && !hasLiveAgentMessage(message) && Boolean(message.context));
}

/**
 * Takes data from the v2 message API ({@link MessageResponse}) and transforms into something usable by WAC
 * ({@link LocalMessageItem}).
 *
 * @param messageItem The individual item from the message to convert.
 * @param fullMessage The message object that came from the server.
 * instance.
 * @param isLatestWelcomeNode Indicates if this message is a new welcome message that has just been shown to the user
 * and isn't a historical welcome message.
 * ID as the message.
 * @param disableFadeAnimation Indicates if the entrance fade animation for the message should be disabled.
 */
function outputItemToLocalItem(messageItem, fullMessage, isLatestWelcomeNode = false, disableFadeAnimation = false) {
    // If the item comes with a streaming id, use that. Otherwise assign a new id.
    const id = streamItemID(fullMessage.id, messageItem) || uuid(UUIDType.LOCAL_MESSAGE);
    // Create the LocalMessage. It will temporarily have the extra "output" property it gets from the original
    // MessageResponse object.
    const localMessage = {
        ui_state: {
            id,
            needsAnnouncement: !fullMessage.history.from_history,
            disableFadeAnimation,
        },
        item: messageItem,
        fullMessageID: fullMessage.id,
    };
    if (isLatestWelcomeNode) {
        localMessage.ui_state.isWelcomeResponse = true;
    }
    // If this is a user_defined response included in our "beta" response types, we transform the response here.
    return isBetaUserDefinedResponse(localMessage)
        ? transformBetaUserDefinedResponse(localMessage)
        : localMessage;
}
/**
 * Creates an empty skeleton of a {@link LocalMessageItem} with the inline_error response type.
 */
function createLocalMessageForInlineError(text) {
    const messageItem = {
        response_type: MessageResponseTypes.INLINE_ERROR,
        text,
    };
    return createLocalMessageForItem(messageItem);
}
/**
 * Creates an empty skeleton of a {@link LocalMessageItem} with the given item.
 */
function createLocalMessageForItem(messageItem) {
    const originalMessage = createMessageResponseForItem(messageItem);
    const localMessage = outputItemToLocalItem(messageItem, originalMessage);
    return { originalMessage, localMessage };
}
/**
 * Creates an empty skeleton of a {@link LocalMessageItem} for a chat status message.
 */
function createLocalMessageForChatStatusMessage(internalMessageResponseType, text) {
    const messageItem = {
        response_type: internalMessageResponseType,
        text,
    };
    const originalMessage = createMessageResponseForItem(messageItem);
    const localMessage = outputItemToLocalItem(messageItem, originalMessage);
    return { originalMessage, localMessage };
}
/**
 * Creates an empty skeleton of a {@link LocalMessageItem} with the session_expired response type.
 */
function createLocalMessageForSessionExpiration(text) {
    return createLocalMessageForChatStatusMessage(InternalMessageResponseType.SESSION_EXPIRED, text);
}
/**
 * Creates an empty skeleton of a {@link LocalMessageItem} with the failed_channel_transfer response type.
 */
function createLocalMessageForChannelTransferFailure(text) {
    return createLocalMessageForChatStatusMessage(InternalMessageResponseType.CHANNEL_TRANSFER_FAILED, text);
}
/**
 * Loops through the give list of message items to create local message items for each of them. This allows us to reuse
 * the existing ui_state functionality to update nested messages like we currently do with normal messages.
 *
 * @param localMessageItem The local message item to store nested local message items in.
 * @param originalMessage The original message response these nested messages came from.
 * @param fromHistory Indicates if the message was fetched from session history.
 * @param nestedLocalMessageItems A list to add local message items to as they're created.
 * @param allowFooter Determines whether buttons in the footer should render. This allows us to prevent deeply
 * nested buttons from rendering, such as a card with a footer nested in a panel.
 */
function createLocalMessageItemsForNestedMessageItems(localMessageItem, originalMessage, fromHistory, nestedLocalMessageItems, allowFooter) {
    const { item } = localMessageItem;
    if (isGridResponseType(item)) {
        localMessageItem.ui_state.gridLocalMessageItemIDs = item.rows.map(row => {
            return row.cells.map(cell => {
                const cellLocalMessageItemIDs = [];
                createLocalMessageItemsForNestedType('items', localMessageItem, cell.items, cellLocalMessageItemIDs, originalMessage, fromHistory, nestedLocalMessageItems, nestedMessageItem => isSupportedMessageItemInBody(localMessageItem.item, nestedMessageItem), false);
                return cellLocalMessageItemIDs;
            });
        });
    }
    else if (isCarouselResponseType(item)) {
        localMessageItem.ui_state.itemsLocalMessageItemIDs = [];
        createLocalMessageItemsForNestedType('items', localMessageItem, item.items, localMessageItem.ui_state.itemsLocalMessageItemIDs, originalMessage, fromHistory, nestedLocalMessageItems, nestedMessageItem => isSupportedMessageItemInBody(item, nestedMessageItem), 
        // A carousel as standalone response type should allow buttons. If a carousel is allowed to be nested in the
        // future, this would be helpful to prevent buttons in it.
        allowFooter);
    }
    else {
        const bodyItems = item.body || item.panel?.body;
        if (bodyItems) {
            localMessageItem.ui_state.bodyLocalMessageItemIDs = [];
            createLocalMessageItemsForNestedType('body', localMessageItem, bodyItems, localMessageItem.ui_state.bodyLocalMessageItemIDs, originalMessage, fromHistory, nestedLocalMessageItems, nestedMessageItem => isSupportedMessageItemInBody(item, nestedMessageItem), 
            // If nested items are being rendered in a panel, the footer should not be allowed.
            !isShowPanelButtonType(item) && !isPanelResponseType(item));
        }
        if (!allowFooter) {
            return;
        }
        const footerItems = item.footer || item.panel?.footer;
        if (footerItems) {
            localMessageItem.ui_state.footerLocalMessageItemIDs = [];
            createLocalMessageItemsForNestedType('footer', localMessageItem, footerItems, localMessageItem.ui_state.footerLocalMessageItemIDs, originalMessage, fromHistory, nestedLocalMessageItems, nestedMessageItem => isSupportedMessageItemInFooter(item, nestedMessageItem), 
            // A show panel button in a footer may open a panel that itself also has a footer. Nothing else in a footer can
            // have nested items with footers.
            !isShowPanelButtonType(item));
        }
    }
}
function createLocalMessageItemsForNestedType(type, localMessageItem, items, nestedMessageItemIDs, originalMessage, fromHistory, nestedLocalMessageItems, isSupported, allowFooter) {
    items.forEach(nestedMessageItem => {
        if (isSupported(nestedMessageItem)) {
            const nestedLocalMessageItem = outputItemToLocalItem(nestedMessageItem, originalMessage, false, true);
            nestedMessageItemIDs.push(nestedLocalMessageItem.ui_state.id);
            nestedLocalMessageItems.push(nestedLocalMessageItem);
            if (isResponseWithNestedItems(nestedLocalMessageItem.item)) {
                createLocalMessageItemsForNestedMessageItems(nestedLocalMessageItem, originalMessage, fromHistory, nestedLocalMessageItems, allowFooter);
            }
        }
        else {
            consoleError(`The "${localMessageItem.item.response_type}" response type does not support "${nestedMessageItem.response_type}" in "${type}" array.`);
        }
    });
}
/**
 * Determines if the given nested item is allowed to be displayed inside the given root message item body.
 */
function isSupportedMessageItemInBody(rootMessageItem, nestedMessageItem) {
    switch (rootMessageItem.response_type) {
        case MessageResponseTypes.CARD:
            return !isCardResponseType(nestedMessageItem) && isItemSupportedInResponseBody(nestedMessageItem);
        case MessageResponseTypes.CAROUSEL:
            return isCardResponseType(nestedMessageItem);
        case MessageResponseTypes.BUTTON:
            return (rootMessageItem.button_type === ButtonItemType.SHOW_PANEL &&
                isItemSupportedInResponseBody(nestedMessageItem));
        case MessageResponseTypes.GRID:
            return !isCardResponseType(nestedMessageItem) && isItemSupportedInResponseBody(nestedMessageItem);
        case InternalMessageResponseType.PANEL:
            return isItemSupportedInResponseBody(nestedMessageItem);
        default:
            return false;
    }
}
/**
 * Determines if the given nested item is allowed to be displayed inside the given root message item footer. Only
 * the button response type should be allowed in the footer. Depending on the root message item, the show_panel
 * button type won't be allowed to render.
 */
function isSupportedMessageItemInFooter(rootMessageItem, nestedMessageItem) {
    if (isButtonResponseType(nestedMessageItem)) {
        // The panel response type and show_panel button type should not support the button type "show_panel" in the
        // footer. This is to prevent the user from opening a panel when a panel is already open.
        if (isPanelResponseType(rootMessageItem) || isShowPanelButtonType(rootMessageItem)) {
            return !isShowPanelButtonType(nestedMessageItem);
        }
        return true;
    }
    return false;
}

/*! js-cookie v3.0.5 | MIT */
/* eslint-disable no-var */
function assign$2 (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      target[key] = source[key];
    }
  }
  return target
}
/* eslint-enable no-var */

/* eslint-disable no-var */
var defaultConverter = {
  read: function (value) {
    if (value[0] === '"') {
      value = value.slice(1, -1);
    }
    return value.replace(/(%[\dA-F]{2})+/gi, decodeURIComponent)
  },
  write: function (value) {
    return encodeURIComponent(value).replace(
      /%(2[346BF]|3[AC-F]|40|5[BDE]|60|7[BCD])/g,
      decodeURIComponent
    )
  }
};
/* eslint-enable no-var */

/* eslint-disable no-var */

function init$1 (converter, defaultAttributes) {
  function set (name, value, attributes) {
    if (typeof document === 'undefined') {
      return
    }

    attributes = assign$2({}, defaultAttributes, attributes);

    if (typeof attributes.expires === 'number') {
      attributes.expires = new Date(Date.now() + attributes.expires * 864e5);
    }
    if (attributes.expires) {
      attributes.expires = attributes.expires.toUTCString();
    }

    name = encodeURIComponent(name)
      .replace(/%(2[346B]|5E|60|7C)/g, decodeURIComponent)
      .replace(/[()]/g, escape);

    var stringifiedAttributes = '';
    for (var attributeName in attributes) {
      if (!attributes[attributeName]) {
        continue
      }

      stringifiedAttributes += '; ' + attributeName;

      if (attributes[attributeName] === true) {
        continue
      }

      // Considers RFC 6265 section 5.2:
      // ...
      // 3.  If the remaining unparsed-attributes contains a %x3B (";")
      //     character:
      // Consume the characters of the unparsed-attributes up to,
      // not including, the first %x3B (";") character.
      // ...
      stringifiedAttributes += '=' + attributes[attributeName].split(';')[0];
    }

    return (document.cookie =
      name + '=' + converter.write(value, name) + stringifiedAttributes)
  }

  function get (name) {
    if (typeof document === 'undefined' || (arguments.length && !name)) {
      return
    }

    // To prevent the for loop in the first place assign an empty array
    // in case there are no cookies at all.
    var cookies = document.cookie ? document.cookie.split('; ') : [];
    var jar = {};
    for (var i = 0; i < cookies.length; i++) {
      var parts = cookies[i].split('=');
      var value = parts.slice(1).join('=');

      try {
        var found = decodeURIComponent(parts[0]);
        jar[found] = converter.read(value, found);

        if (name === found) {
          break
        }
      } catch (e) {}
    }

    return name ? jar[name] : jar
  }

  return Object.create(
    {
      set,
      get,
      remove: function (name, attributes) {
        set(
          name,
          '',
          assign$2({}, attributes, {
            expires: -1
          })
        );
      },
      withAttributes: function (attributes) {
        return init$1(this.converter, assign$2({}, this.attributes, attributes))
      },
      withConverter: function (converter) {
        return init$1(assign$2({}, this.converter, converter), this.attributes)
      }
    },
    {
      attributes: { value: Object.freeze(defaultAttributes) },
      converter: { value: Object.freeze(converter) }
    }
  )
}

var api = init$1(defaultConverter, { path: '/' });

var __spreadArray = (undefined && undefined.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var BrowserInfo = /** @class */ (function () {
    function BrowserInfo(name, version, os) {
        this.name = name;
        this.version = version;
        this.os = os;
        this.type = 'browser';
    }
    return BrowserInfo;
}());
var NodeInfo = /** @class */ (function () {
    function NodeInfo(version) {
        this.version = version;
        this.type = 'node';
        this.name = 'node';
        this.os = process.platform;
    }
    return NodeInfo;
}());
var SearchBotDeviceInfo = /** @class */ (function () {
    function SearchBotDeviceInfo(name, version, os, bot) {
        this.name = name;
        this.version = version;
        this.os = os;
        this.bot = bot;
        this.type = 'bot-device';
    }
    return SearchBotDeviceInfo;
}());
var BotInfo = /** @class */ (function () {
    function BotInfo() {
        this.type = 'bot';
        this.bot = true; // NOTE: deprecated test name instead
        this.name = 'bot';
        this.version = null;
        this.os = null;
    }
    return BotInfo;
}());
var ReactNativeInfo = /** @class */ (function () {
    function ReactNativeInfo() {
        this.type = 'react-native';
        this.name = 'react-native';
        this.version = null;
        this.os = null;
    }
    return ReactNativeInfo;
}());
// tslint:disable-next-line:max-line-length
var SEARCHBOX_UA_REGEX = /alexa|bot|crawl(er|ing)|facebookexternalhit|feedburner|google web preview|nagios|postrank|pingdom|slurp|spider|yahoo!|yandex/;
var SEARCHBOT_OS_REGEX = /(nuhk|curl|Googlebot|Yammybot|Openbot|Slurp|MSNBot|Ask\ Jeeves\/Teoma|ia_archiver)/;
var REQUIRED_VERSION_PARTS = 3;
var userAgentRules = [
    ['aol', /AOLShield\/([0-9\._]+)/],
    ['edge', /Edge\/([0-9\._]+)/],
    ['edge-ios', /EdgiOS\/([0-9\._]+)/],
    ['yandexbrowser', /YaBrowser\/([0-9\._]+)/],
    ['kakaotalk', /KAKAOTALK\s([0-9\.]+)/],
    ['samsung', /SamsungBrowser\/([0-9\.]+)/],
    ['silk', /\bSilk\/([0-9._-]+)\b/],
    ['miui', /MiuiBrowser\/([0-9\.]+)$/],
    ['beaker', /BeakerBrowser\/([0-9\.]+)/],
    ['edge-chromium', /EdgA?\/([0-9\.]+)/],
    [
        'chromium-webview',
        /(?!Chrom.*OPR)wv\).*Chrom(?:e|ium)\/([0-9\.]+)(:?\s|$)/,
    ],
    ['chrome', /(?!Chrom.*OPR)Chrom(?:e|ium)\/([0-9\.]+)(:?\s|$)/],
    ['phantomjs', /PhantomJS\/([0-9\.]+)(:?\s|$)/],
    ['crios', /CriOS\/([0-9\.]+)(:?\s|$)/],
    ['firefox', /Firefox\/([0-9\.]+)(?:\s|$)/],
    ['fxios', /FxiOS\/([0-9\.]+)/],
    ['opera-mini', /Opera Mini.*Version\/([0-9\.]+)/],
    ['opera', /Opera\/([0-9\.]+)(?:\s|$)/],
    ['opera', /OPR\/([0-9\.]+)(:?\s|$)/],
    ['pie', /^Microsoft Pocket Internet Explorer\/(\d+\.\d+)$/],
    ['pie', /^Mozilla\/\d\.\d+\s\(compatible;\s(?:MSP?IE|MSInternet Explorer) (\d+\.\d+);.*Windows CE.*\)$/],
    ['netfront', /^Mozilla\/\d\.\d+.*NetFront\/(\d.\d)/],
    ['ie', /Trident\/7\.0.*rv\:([0-9\.]+).*\).*Gecko$/],
    ['ie', /MSIE\s([0-9\.]+);.*Trident\/[4-7].0/],
    ['ie', /MSIE\s(7\.0)/],
    ['bb10', /BB10;\sTouch.*Version\/([0-9\.]+)/],
    ['android', /Android\s([0-9\.]+)/],
    ['ios', /Version\/([0-9\._]+).*Mobile.*Safari.*/],
    ['safari', /Version\/([0-9\._]+).*Safari/],
    ['facebook', /FB[AS]V\/([0-9\.]+)/],
    ['instagram', /Instagram\s([0-9\.]+)/],
    ['ios-webview', /AppleWebKit\/([0-9\.]+).*Mobile/],
    ['ios-webview', /AppleWebKit\/([0-9\.]+).*Gecko\)$/],
    ['curl', /^curl\/([0-9\.]+)$/],
    ['searchbot', SEARCHBOX_UA_REGEX],
];
var operatingSystemRules = [
    ['iOS', /iP(hone|od|ad)/],
    ['Android OS', /Android/],
    ['BlackBerry OS', /BlackBerry|BB10/],
    ['Windows Mobile', /IEMobile/],
    ['Amazon OS', /Kindle/],
    ['Windows 3.11', /Win16/],
    ['Windows 95', /(Windows 95)|(Win95)|(Windows_95)/],
    ['Windows 98', /(Windows 98)|(Win98)/],
    ['Windows 2000', /(Windows NT 5.0)|(Windows 2000)/],
    ['Windows XP', /(Windows NT 5.1)|(Windows XP)/],
    ['Windows Server 2003', /(Windows NT 5.2)/],
    ['Windows Vista', /(Windows NT 6.0)/],
    ['Windows 7', /(Windows NT 6.1)/],
    ['Windows 8', /(Windows NT 6.2)/],
    ['Windows 8.1', /(Windows NT 6.3)/],
    ['Windows 10', /(Windows NT 10.0)/],
    ['Windows ME', /Windows ME/],
    ['Windows CE', /Windows CE|WinCE|Microsoft Pocket Internet Explorer/],
    ['Open BSD', /OpenBSD/],
    ['Sun OS', /SunOS/],
    ['Chrome OS', /CrOS/],
    ['Linux', /(Linux)|(X11)/],
    ['Mac OS', /(Mac_PowerPC)|(Macintosh)/],
    ['QNX', /QNX/],
    ['BeOS', /BeOS/],
    ['OS/2', /OS\/2/],
];
function detect(userAgent) {
    if (typeof document === 'undefined' &&
        typeof navigator !== 'undefined' &&
        navigator.product === 'ReactNative') {
        return new ReactNativeInfo();
    }
    if (typeof navigator !== 'undefined') {
        return parseUserAgent(navigator.userAgent);
    }
    return getNodeVersion();
}
function matchUserAgent(ua) {
    // opted for using reduce here rather than Array#first with a regex.test call
    // this is primarily because using the reduce we only perform the regex
    // execution once rather than once for the test and for the exec again below
    // probably something that needs to be benchmarked though
    return (ua !== '' &&
        userAgentRules.reduce(function (matched, _a) {
            var browser = _a[0], regex = _a[1];
            if (matched) {
                return matched;
            }
            var uaMatch = regex.exec(ua);
            return !!uaMatch && [browser, uaMatch];
        }, false));
}
function parseUserAgent(ua) {
    var matchedRule = matchUserAgent(ua);
    if (!matchedRule) {
        return null;
    }
    var name = matchedRule[0], match = matchedRule[1];
    if (name === 'searchbot') {
        return new BotInfo();
    }
    // Do not use RegExp for split operation as some browser do not support it (See: http://blog.stevenlevithan.com/archives/cross-browser-split)
    var versionParts = match[1] && match[1].split('.').join('_').split('_').slice(0, 3);
    if (versionParts) {
        if (versionParts.length < REQUIRED_VERSION_PARTS) {
            versionParts = __spreadArray(__spreadArray([], versionParts, true), createVersionParts(REQUIRED_VERSION_PARTS - versionParts.length), true);
        }
    }
    else {
        versionParts = [];
    }
    var version = versionParts.join('.');
    var os = detectOS(ua);
    var searchBotMatch = SEARCHBOT_OS_REGEX.exec(ua);
    if (searchBotMatch && searchBotMatch[1]) {
        return new SearchBotDeviceInfo(name, version, os, searchBotMatch[1]);
    }
    return new BrowserInfo(name, version, os);
}
function detectOS(ua) {
    for (var ii = 0, count = operatingSystemRules.length; ii < count; ii++) {
        var _a = operatingSystemRules[ii], os = _a[0], regex = _a[1];
        var match = regex.exec(ua);
        if (match) {
            return os;
        }
    }
    return null;
}
function getNodeVersion() {
    var isNode = typeof process !== 'undefined' && process.version;
    return isNode ? new NodeInfo(process.version.slice(1)) : null;
}
function createVersionParts(count) {
    var output = [];
    for (var ii = 0; ii < count; ii++) {
        output.push('0');
    }
    return output;
}

const browser = detect();
const BROWSER_INFO = browser;
// The user agent string in version 13 of the operating system no longer distinguishes between macOS and iPads so
// need add an extra check that may not be reliable in the future.
const IS_IPAD = /iPad/.test(window.navigator.platform) ||
    (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
const IS_IOS = browser?.os === 'iOS';
const IS_ANDROID = browser?.os === 'Android OS';
const IS_MOBILE = IS_IOS || IS_ANDROID || IS_IPAD;
// The width and height checks here are how we differentiate between mobile android devices and tablets. Eventually new
// phones may get wide enough that the width check needs to be increased.
const IS_PHONE = IS_MOBILE && (window.screen.width < 500 || window.screen.height < 500);
// Assume the phone is in portrait mode if the width is small.
const IS_PHONE_IN_PORTRAIT_MODE = IS_PHONE && window.screen.width < 500;
const IS_TABLET = IS_MOBILE && !IS_PHONE;
// The minimum window width in which we will display the button for opening the PDF viewer.
const MIN_PDF_VIEWER_WIDTH = 550;
// For reference here is a list of screen dimensions observed for some of the iPhone devices in browser stack.
//
// 414x896 XS Max
// 414x896 XR
//
// 375x812 XS
// 375x812 X
//
// 414x736 8 Plus
// 414x736 6S Plus
// 414x736 6 Plus
//
// 375x667 8
// 375x667 7
// 375x667 6s
// 375x667 6
//
// 320x568 SE
// This array maps from a screen height on iOS device to a height offset to apply to the widget for devices of that
// height.
const IOS_HEIGHT_MAP = [
    [896, 114],
    [812, 114],
    [736, 75],
    [667, 75],
];
/**
 * The Browser info supplied in context in ActionsImpl.tsx. This info is used by the integrations
 * back-end for agent routing within dialog and to help enable the human agent on the other end of a service desk.
 */
function getBrowserInfo() {
    return {
        browser_name: BROWSER_INFO?.name,
        browser_version: BROWSER_INFO?.version,
        browser_OS: BROWSER_INFO?.os,
        language: window.navigator?.language,
        page_url: window.location.origin + window.location.pathname,
        screen_resolution: `width: ${window.screen?.width}, height: ${window.screen?.height}`,
        user_agent: window.navigator?.userAgent,
    };
}
/**
 * This function is responsible for returning a height offset to apply to older iOS devices. On these devices the
 * navigation bars at the top and bottom of the screen consume a portion of the 100vh viewport height which causes
 * the text field to be cut off at the bottom. By applying this offset we make it visible. Unfortunately this offset
 * is different on different devices so we make a guess based on the screen height. Another drawback is that this
 * offset causes a gap to appear below the text field when the navigation bars are hiding.
 */
function getIPhoneHeightOffset() {
    // eslint-disable-next-line no-restricted-globals
    const screenHeight = Math.max(screen.height, screen.width);
    for (let index = 0; index < IOS_HEIGHT_MAP.length; index++) {
        const [height, offset] = IOS_HEIGHT_MAP[index];
        if (screenHeight >= height) {
            return offset;
        }
    }
    return 75;
}
/**
 * Gets the hostname and pathname of the page web chat is injected in.
 */
function getURLHostnameAndPath() {
    const { origin, pathname } = window.location;
    return origin + pathname;
}
/**
 * Determines if the host page is using a secure protocol.
 */
function isSecureHost() {
    return window.location.protocol === 'https:';
}
/**
 * In some conditions (iFrames) window.sessionStorage is DEFINED, but not accessible.
 * Rather than doing window.sessionStorage || alternate checks, this actually checks if sessionStorage
 * can be used.
 *
 * @returns If window.sessionStorage is read and writeable.
 */
function isSessionStorageAvailable() {
    let isAvailable = false;
    try {
        window.sessionStorage.setItem('web-chat-test-item', 'true');
        window.sessionStorage.getItem('web-chat-test-item');
        window.sessionStorage.removeItem('web-chat-test-item');
        isAvailable = true;
    }
    catch {
        // Ignore.
    }
    return isAvailable;
}
const IS_SESSION_STORAGE = memoizeOne(isSessionStorageAvailable);
/**
 * Attempts to return the hostname of the provided URL. If an invalid url is returned, we just return the provided url
 * value.
 */
function getURLHostName(url) {
    try {
        return new URL(url).hostname;
    }
    catch {
        return url;
    }
}
/**
 * Removes the parameter with the given name from the URL.
 */
function removeParameter(paramName) {
    const { location } = window;
    const params = new URLSearchParams(location.search);
    if (params.has(paramName)) {
        params.delete(paramName);
        let paramsString = params.toString();
        if (paramsString) {
            paramsString = `?${paramsString}`;
        }
        const newURL = location.origin + location.pathname + paramsString + location.hash;
        // eslint-disable-next-line no-restricted-globals
        history.replaceState(history.state, null, newURL);
    }
}
/**
 * Retrieves from search params in the URL and removes them.
 */
function pullValueParam(paramName) {
    const searchParams = new URLSearchParams(window.location.search);
    const paramValue = searchParams.get(paramName);
    if (paramValue) {
        removeParameter(paramName);
        return paramValue;
    }
    return null;
}
/**
 * Executes the given operation in a setTimeout if the timeout value is specified. If not, then the operation is
 * executed immediately without using a setTimeout.
 */
function conditionalSetTimeout(operation, timeout) {
    if (timeout) {
        return setTimeout(operation, timeout);
    }
    // Execute the operation immediately.
    operation();
    return null;
}

/**
 * This module is responsible for managing the storage of anonymousUserId. We use an anonymousUserId if the userId is
 * not set in order to not repeat bill unique users that are anonymous. The id is stored (preferentially) as a 1st
 * party cookie that expires in 45 days. The expiration date is so we don't leave data sitting in the user's browser
 * for a long period of time per IBM policy. The period of 45 days is chosen so that the cookie does not expire
 * within a given month of so which would result in the user being detected as a new user and thus causing the
 * client to be billed for an additional user. The number should be between 32 days (the minimum to cover a whole month)
 * and 365 days (the maximum allowed by IBM policy).
 */
// The length of cookie the anonymous userID cookie should live in days. This may be fractions of days if needed.
const COOKIE_DURATION = 45; // Days
// The prefix attached to all anonymous user ids.
const ANONYMOUS_USER_ID_PREFIX = 'anonymous_IBMuid-';
// The key in browser storage that contains the session id for a particular user.
const ANONYMOUS_USER_ID_KEY = 'IBM_WAC_ANONYMOUS_USER_ID';
/**
 * Returns the anonymousUserId as recorded in local storage. If there is no defined anonymousUserId, this
 * will return undefined.
 */
function getAnonymousUserID(useCookie) {
    // Return the value of the cookie, looking in session storage first as that's our fallback if cookies aren't
    // available.
    let userID;
    if (IS_SESSION_STORAGE()) {
        if (useCookie) {
            userID = window.sessionStorage.getItem(ANONYMOUS_USER_ID_KEY) || api.get(ANONYMOUS_USER_ID_KEY);
        }
        else {
            userID = window.sessionStorage.getItem(ANONYMOUS_USER_ID_KEY);
        }
    }
    else if (useCookie) {
        userID = api.get(ANONYMOUS_USER_ID_KEY);
    }
    // If there is an ID, we need to do a "set" again which will update the expiration date on the cookie.
    if (userID) {
        setAnonymousUserId(userID, useCookie);
    }
    return userID || null;
}
/**
 * Sets the anonymousUserId in local storage.
 */
function setAnonymousUserId(anonymousUserId, useCookie) {
    if (useCookie) {
        api.set(ANONYMOUS_USER_ID_KEY, anonymousUserId, {
            expires: COOKIE_DURATION,
            sameSite: 'lax',
            secure: isSecureHost(),
        });
    }
    if ((!useCookie || !api.get(ANONYMOUS_USER_ID_KEY)) && IS_SESSION_STORAGE()) {
        // If the cookie failed to get set, it means we're in a higher security level and we need to attempt to use
        // session storage instead. If tracking is disabled, browsers may disable localStorage and cookies, but still allow
        // session storage. Falling back to session storage at least allows us to not bill each page view as an
        // individual user! https://developer.mozilla.org/en-US/docs/Mozilla/Firefox/Privacy/Storage_access_policy
        window.sessionStorage.setItem(ANONYMOUS_USER_ID_KEY, anonymousUserId);
    }
}
/**
 * Generated a new anonymousUserId (MUST start with anonymous_ to be handled correctly downstream).
 */
function generateAndSetAnonymousUserId(useCookie) {
    const anonymousUserId = `${ANONYMOUS_USER_ID_PREFIX}${uuid(UUIDType.USER)}`;
    setAnonymousUserId(anonymousUserId, useCookie);
    return anonymousUserId;
}
/**
 * Clear the anonymousUserId from localStorage. Called when a real userId is set.
 *
 */
function clearAnonymousUserId() {
    api.remove(ANONYMOUS_USER_ID_KEY);
    if (IS_SESSION_STORAGE()) {
        window.sessionStorage.removeItem(ANONYMOUS_USER_ID_KEY);
    }
}

const CHANGE_STATE = 'CHANGE_STATE';
const UPDATE_BOT_NAME = 'UPDATE_BOT_NAME';
const UPDATE_BOT_AVATAR_URL = 'UPDATE_BOT_AVATAR_URL';
const UPDATE_LAUNCHER_AVATAR_URL = 'UPDATE_LAUNCHER_AVATAR_URL';
const UPDATE_MAIN_HEADER_TITLE = 'UPDATE_MAIN_HEADER_TITLE';
const HYDRATE_CHAT = 'HYDRATE_CHAT';
const SET_MAIN_CONFIG = 'SET_MAIN_CONFIG';
const HYDRATE_MESSAGE_HISTORY = 'HYDRATE_MESSAGE_HISTORY';
const ADD_LOCAL_MESSAGE_ITEM = 'ADD_LOCAL_MESSAGE_ITEM';
const REMOVE_MESSAGES = 'REMOVE_MESSAGES';
const UPDATE_LOCAL_MESSAGE_ITEM = 'UPDATE_LOCAL_MESSAGE_ITEM';
const SET_APP_STATE_VALUE = 'SET_APP_STATE_VALUE';
const ADD_IS_TYPING_COUNTER = 'ADD_IS_TYPING_COUNTER';
const ADD_IS_LOADING_COUNTER = 'ADD_IS_LOADING_COUNTER';
const ADD_IS_HYDRATING_COUNTER = 'ADD_IS_HYDRATING_COUNTER';
const SET_VIEW_STATE = 'SET_VIEW_STATE';
const SET_VIEW_CHANGING = 'SET_VIEW_CHANGING';
const SET_INITIAL_VIEW_CHANGE_COMPLETE = 'SET_INITIAL_VIEW_CHANGE_COMPLETE';
const UPDATE_CSS_VARIABLES = 'UPDATE_CSS_VARIABLES';
const UPDATE_THEME_CONFIG = 'UPDATE_THEME_CONFIG';
const UPDATE_STREAMING_CONFIG = 'UPDATE_STREAMING_CONFIG';
const MESSAGE_SET_OPTION_SELECTED = 'MESSAGE_SET_OPTION_SELECTED';
const SET_MESSAGE_UI_PROPERTY = 'SET_MESSAGE_UI_PROPERTY';
const SET_MESSAGE_HISTORY_PROPERTY = 'SET_MESSAGE_HISTORY_PROPERTY';
const MERGE_HISTORY = 'MERGE_HISTORY';
const SET_LAUNCHER_PROPERTY = 'SET_LAUNCHER_PROPERTY';
const SET_LAUNCHER_CONFIG_PROPERTY = 'SET_LAUNCHER_CONFIG_PROPERTY';
const ANNOUNCE_MESSAGE = 'ANNOUNCE_MESSAGE';
const SET_ALTERNATE_SUGGESTIONS_OPEN = 'SET_ALTERNATE_SUGGESTIONS_OPEN';
const SET_CHAT_MESSAGES_PROPERTY = 'SET_CHAT_MESSAGES_PROPERTY';
const RESTART_CONVERSATION = 'RESTART_CONVERSATION';
const SET_ALTERNATE_SUGGESTIONS_THIRD_STRIKE = 'SET_ALTERNATE_SUGGESTIONS_THIRD_STRIKE';
const ACCEPTED_DISCLAIMER = 'ACCEPTED_DISCLAIMER';
const ADD_MESSAGE = 'ADD_MESSAGE';
const UPDATE_HOME_SCREEN_CONFIG = 'UPDATE_HOME_SCREEN_CONFIG';
const UPDATE_ALTERNATE_SUGGESTIONS_CONFIG = 'UPDATE_ALTERNATE_SUGGESTIONS_CONFIG';
const SUGGESTIONS_SET_SELECTED_INDEX = 'SUGGESTIONS_SET_SELECTED_INDEX';
const UPDATE_SESSION_STARTED_TIMESTAMP = 'UPDATE_SESSION_STARTED_TIMESTAMP';
const UPDATE_SESSION_ID = 'UPDATE_SESSION_ID';
const UPDATE_HAS_SENT_NON_WELCOME_MESSAGE = 'UPDATE_HAS_SENT_NON_WELCOME_MESSAGE';
const UPDATE_PERSISTED_CHAT_STATE = 'UPDATE_PERSISTED_CHAT_STATE';
const SET_TOUR_DATA = 'SET_TOUR_DATA';
const CLEAR_TOUR_DATA = 'CLEAR_TOUR_DATA';
const CHANGE_STEP_IN_TOUR = 'CHANGE_STEP_IN_TOUR';
const SET_HOME_SCREEN_IS_OPEN = 'SET_HOME_SCREEN_IS_OPEN';
const SET_UPDATE_HISTORY_USER_DEFINED_COUNT = 'SET_UPDATE_HISTORY_USER_DEFINED_COUNT';
const UPDATE_LAUNCHER_CONFIG = 'UPDATE_LAUNCHER_CONFIG';
const UPDATE_BRANDING_CONFIG = 'UPDATE_BRANDING_CONFIG';
const SET_BRANDING_OVERLAY_IS_OPEN = 'SET_BRANDING_OVERLAY_IS_OPEN';
const SET_BRANDING_BANNER_SHOWN = 'SET_BRANDING_BANNER_SHOWN';
const SET_SUGGESTIONS_SEARCH_RESULTS = 'SET_SUGGESTIONS_SEARCH_RESULTS';
const UPDATE_MESSAGE = 'UPDATE_MESSAGE';
const SET_LAUNCHER_MINIMIZED = 'SET_LAUNCHER_MINIMIZED';
const CLOSE_IFRAME_PANEL = 'CLOSE_IFRAME_PANEL';
const OPEN_IFRAME_CONTENT = 'OPEN_IFRAME_CONTENT';
const SET_CONVERSATIONAL_SEARCH_CITATION_PANEL_IS_OPEN = 'SET_CONVERSATIONAL_SEARCH_CITATION_PANEL_IS_OPEN';
const SET_CUSTOM_PANEL_OPTIONS = 'SET_CUSTOM_PANEL_OPTIONS';
const SET_CUSTOM_PANEL_OPEN = 'SET_CUSTOM_PANEL_OPEN';
const TOGGLE_HOME_SCREEN = 'GO_BACK_TO_HOME';
const UPDATE_INPUT_STATE = 'UPDATE_INPUT_STATE';
const SET_IS_BROWSER_PAGE_VISIBLE = 'SET_IS_PAGE_VISIBLE';
const ADD_INPUT_FILE = 'ADD_INPUT_FILE';
const CLEAR_INPUT_FILES = 'CLEAR_INPUT_FILES';
const REMOVE_INPUT_FILE = 'REMOVE_INPUT_FILE';
const REMOVE_LOCAL_MESSAGE_ITEM = 'REMOVE_LOCAL_MESSAGE_ITEM';
const FILE_UPLOAD_INPUT_ERROR = 'FILE_UPLOAD_INPUT_ERROR';
const ADD_NESTED_MESSAGES = 'ADD_NESTED_MESSAGES';
const SET_RESPONSE_PANEL_IS_OPEN = 'SET_RESPONSE_PANEL_IS_OPEN';
const SET_RESPONSE_PANEL_CONTENT = 'SET_PANEL_RESPONSE_CONTENT';
const STREAMING_ADD_CHUNK = 'STREAMING_ADD_CHUNK';
const STREAMING_START = 'STREAMING_START';
const STREAMING_MERGE_HISTORY = 'STREAMING_MERGE_HISTORY';
const ADD_NOTIFICATION = 'ADD_NOTIFICATION';
const REMOVE_ALL_NOTIFICATIONS = 'REMOVE_ALL_NOTIFICATIONS';
const REMOVE_NOTIFICATIONS = 'REMOVE_NOTIFICATIONS';
const UPDATE_CHAT_HEADER_CONFIG = 'UPDATE_CHAT_HEADER_CONFIG';
const UPDATE_MAX_VISIBLE_HEADER_OBJECTS = 'UPDATE_MAX_VISIBLE_HEADER_OBJECTS';
const SET_STOP_STREAMING_BUTTON_VISIBLE = 'SET_STOP_STREAMING_BUTTON_VISIBLE';
const SET_STOP_STREAMING_BUTTON_DISABLED = 'SET_STOP_STREAMING_BUTTON_DISABLED';
const SET_STREAM_ID = 'SET_STREAM_ID';
const UPDATE_MAIN_HEADER_AVATAR = 'UPDATE_MAIN_HEADER_AVATAR';
const actions = {
    /**
     * Allows you to change any portion of the app state.
     */
    changeState(partialState) {
        // Using "UnknownAction" here seems to be required or our dts bundler blows up on this with a "this node exceeds the
        // maximum length" error.
        return { type: CHANGE_STATE, partialState };
    },
    setMainConfig(mainConfig) {
        return {
            type: SET_MAIN_CONFIG,
            mainConfig,
        };
    },
    chatWasHydrated() {
        return { type: HYDRATE_CHAT };
    },
    hydrateMessageHistory(messageHistory) {
        return { type: HYDRATE_MESSAGE_HISTORY, messageHistory };
    },
    removeMessages(messageIDs) {
        return { type: REMOVE_MESSAGES, messageIDs };
    },
    restartConversation() {
        return {
            type: RESTART_CONVERSATION,
        };
    },
    /**
     * Adds the given message item to the message list. If the message item is already in the list, it will not be
     * added again and will be left at its current position (unless addAfterID is provided) but the contents of the
     * message will be replaced with the new item.
     *
     * @param messageItem The message item to add or replaced.
     * @param message The full message the item belongs to.
     * @param addMessage Indicates if the full message should also be added to the store along with the item.
     * @param addAfterID Indicates if the message item should be inserted immediately after another item that may
     * already be in the list. If this value is not provided, the item will be inserted at the end of the list (if it
     * does not already exist in the list).
     */
    addLocalMessageItem(messageItem, message, addMessage, addAfterID) {
        return {
            type: ADD_LOCAL_MESSAGE_ITEM,
            messageItem,
            message,
            addMessage,
            addAfterID,
        };
    },
    /**
     * Adds the given message to the message list. This may also re-order any existing message items that are already
     * visible due to being received from previous streaming chunks.
     */
    addMessage(message) {
        return { type: ADD_MESSAGE, message };
    },
    updateLocalMessageItem(messageItem) {
        return { type: UPDATE_LOCAL_MESSAGE_ITEM, messageItem };
    },
    updateMessage(message) {
        return { type: UPDATE_MESSAGE, message };
    },
    messageSetOptionSelected(messageID, sentMessage) {
        return {
            type: MESSAGE_SET_OPTION_SELECTED,
            messageID,
            sentMessage,
        };
    },
    updatePersistedChatState(chatState) {
        return {
            type: UPDATE_PERSISTED_CHAT_STATE,
            chatState,
        };
    },
    setTourData(newActiveTourMessageID) {
        return {
            type: SET_TOUR_DATA,
            newActiveTourMessageID,
        };
    },
    clearTourData() {
        return {
            type: CLEAR_TOUR_DATA,
        };
    },
    changeStepInTour(newStepNumber) {
        return {
            type: CHANGE_STEP_IN_TOUR,
            newStepNumber,
        };
    },
    updateSessionID(sessionID) {
        return {
            type: UPDATE_SESSION_ID,
            sessionID,
        };
    },
    updateSessionStartedTimestamp(sessionStartedTimestamp) {
        return {
            type: UPDATE_SESSION_STARTED_TIMESTAMP,
            sessionStartedTimestamp,
        };
    },
    updateHasSentNonWelcomeMessage(hasSentNonWelcomeMessage) {
        return {
            type: UPDATE_HAS_SENT_NON_WELCOME_MESSAGE,
            hasSentNonWelcomeMessage,
        };
    },
    setAppStateValue(key, value) {
        return {
            type: SET_APP_STATE_VALUE,
            key,
            value,
        };
    },
    addIsTypingCounter(addToIsTyping) {
        return {
            type: ADD_IS_TYPING_COUNTER,
            addToIsTyping,
        };
    },
    addIsLoadingCounter(addToIsLoading) {
        return {
            type: ADD_IS_LOADING_COUNTER,
            addToIsLoading,
        };
    },
    addIsHydratingCounter(addToIsHydrating) {
        return {
            type: ADD_IS_HYDRATING_COUNTER,
            addToIsHydrating,
        };
    },
    updateBotName(name) {
        return { type: UPDATE_BOT_NAME, name };
    },
    updateMainHeaderTitle(title) {
        return { type: UPDATE_MAIN_HEADER_TITLE, title };
    },
    updateBotAvatarURL(url) {
        return { type: UPDATE_BOT_AVATAR_URL, url };
    },
    updateLauncherAvatarURL(source) {
        return { type: UPDATE_LAUNCHER_AVATAR_URL, source };
    },
    updateCSSVariables(variables, publicVars, remoteStyles) {
        return { type: UPDATE_CSS_VARIABLES, variables, publicVars, remoteStyles };
    },
    updateThemeConfig(themeConfig) {
        return { type: UPDATE_THEME_CONFIG, themeConfig };
    },
    updateStreamingConfig(streamingConfig) {
        return { type: UPDATE_STREAMING_CONFIG, streamingConfig };
    },
    updateHomeScreenConfig(homeScreenConfig) {
        return { type: UPDATE_HOME_SCREEN_CONFIG, homeScreenConfig };
    },
    /**
     * Updates the configuration for the suggestions feature. Only the properties that are defined here will be
     * applied to the updated config.
     */
    updateAlternateSuggestionsConfig(config) {
        return { type: UPDATE_ALTERNATE_SUGGESTIONS_CONFIG, config };
    },
    setViewState(viewState) {
        return { type: SET_VIEW_STATE, viewState };
    },
    setViewChanging(viewChanging) {
        return { type: SET_VIEW_CHANGING, viewChanging };
    },
    setInitialViewChangeComplete(changeComplete) {
        return { type: SET_INITIAL_VIEW_CHANGE_COMPLETE, changeComplete };
    },
    /**
     * Sets the give property of the {@link LocalMessageUIState} associated with the message of the given ID to the
     * given value.
     *
     * @param localMessageID The ID of the message to update.
     * @param propertyName The name of the property to update.
     * @param propertyValue The value to set on the property.
     */
    setMessageUIProperty(localMessageID, propertyName, propertyValue) {
        return { type: SET_MESSAGE_UI_PROPERTY, localMessageID, propertyName, propertyValue };
    },
    /**
     * Sets the value of one of the properties of {@link PersistedLauncherState}.
     */
    setLauncherProperty(propertyName, propertyValue) {
        return { type: SET_LAUNCHER_PROPERTY, propertyName, propertyValue };
    },
    setLauncherConfigProperty(propertyName, propertyValue, launcherType) {
        return { type: SET_LAUNCHER_CONFIG_PROPERTY, propertyName, propertyValue, launcherType };
    },
    /**
     * Sets the give property of the {@link MessageHistory} associated with the message of the given ID to the given
     * value.
     *
     * @param messageID The ID of the message to update.
     * @param propertyName The name of the property to update.
     * @param propertyValue The value to set on the property.
     */
    setMessageHistoryProperty(messageID, propertyName, propertyValue) {
        return { type: SET_MESSAGE_HISTORY_PROPERTY, messageID, propertyName, propertyValue };
    },
    /**
     * Merges the given object into the history for the given message.
     */
    mergeMessageHistory(messageID, history) {
        return { type: MERGE_HISTORY, messageID, history };
    },
    setMessageErrorState(messageID, errorState) {
        return actions.setMessageHistoryProperty(messageID, 'error_state', errorState);
    },
    /**
     * Marks the given message to indicate that it has been announced and doesn't need to be announced again.
     */
    setMessageWasAnnounced(messageID) {
        return actions.setMessageUIProperty(messageID, 'needsAnnouncement', false);
    },
    /**
     * Sets the given message as the current accessibility announcement so that it will immediately be read by a
     * screen reader.
     */
    announceMessage(message) {
        return { type: ANNOUNCE_MESSAGE, message };
    },
    /**
     * Changes the open status of the alternate suggestions.
     *
     * @param panelState The new state to set for the alternate suggestions open state.
     * @param suggestionsToggledReason Indicates the reason for the change.
     */
    setAlternateSuggestionsPanelState(panelState, suggestionsToggledReason) {
        return { type: SET_ALTERNATE_SUGGESTIONS_OPEN, panelState, suggestionsToggledReason };
    },
    /**
     * Indicates that the third strike against the alternate suggestions has been triggered.
     */
    setAlternateSuggestionsThirdStrike() {
        return { type: SET_ALTERNATE_SUGGESTIONS_THIRD_STRIKE };
    },
    /**
     * Sets the property on one of the {@link ChatMessagesState} values.
     */
    setChatMessagesStateProperty(propertyName, propertyValue) {
        return { type: SET_CHAT_MESSAGES_PROPERTY, propertyName, propertyValue };
    },
    /**
     * Add a notification to the state.
     */
    addNotification(notification) {
        const notificationID = uuid();
        return { type: ADD_NOTIFICATION, notificationID, notification };
    },
    /**
     * Remove notifications using the given ids.
     */
    removeNotifications({ groupID, notificationID }) {
        return { type: REMOVE_NOTIFICATIONS, groupID, notificationID };
    },
    /**
     * Remove all notifications from the state.
     */
    removeAllNotifications() {
        return { type: REMOVE_ALL_NOTIFICATIONS };
    },
    /**
     * Sets the disclaimer state for the current domain to true.
     */
    acceptDisclaimer() {
        return { type: ACCEPTED_DISCLAIMER };
    },
    /**
     * Marks the suggestion with the given index as being the currently selected one.
     */
    setSuggestionsSelectedIndex(index, section) {
        return { type: SUGGESTIONS_SET_SELECTED_INDEX, index, section };
    },
    /**
     * For toggling Home Screen open state.
     */
    setHomeScreenIsOpen(isOpen) {
        return { type: SET_HOME_SCREEN_IS_OPEN, isOpen };
    },
    updateHistoryUserDefinedCount(messageID) {
        return { type: SET_UPDATE_HISTORY_USER_DEFINED_COUNT, messageID };
    },
    updateLauncherConfig(launcherConfig) {
        return { type: UPDATE_LAUNCHER_CONFIG, launcherConfig };
    },
    updateBrandingConfig(brandingConfig) {
        return { type: UPDATE_BRANDING_CONFIG, brandingConfig };
    },
    setBrandingOverlayIsOpen(isOpen) {
        return { type: SET_BRANDING_OVERLAY_IS_OPEN, isOpen };
    },
    setBrandingBannerShown(showBanner) {
        return { type: SET_BRANDING_BANNER_SHOWN, showBanner };
    },
    setSuggestionsSearchResults(searchResultItem) {
        return { type: SET_SUGGESTIONS_SEARCH_RESULTS, searchResultItem };
    },
    setLauncherMinimized() {
        return { type: SET_LAUNCHER_MINIMIZED };
    },
    closeIFramePanel() {
        return { type: CLOSE_IFRAME_PANEL };
    },
    setIFrameContent(messageItem) {
        return { type: OPEN_IFRAME_CONTENT, messageItem };
    },
    setViewSourcePanelIsOpen(isOpen, citationItem, relatedSearchResult) {
        return {
            type: SET_CONVERSATIONAL_SEARCH_CITATION_PANEL_IS_OPEN,
            isOpen,
            citationItem,
            relatedSearchResult,
        };
    },
    setCustomPanelConfigOptions(options) {
        return { type: SET_CUSTOM_PANEL_OPTIONS, options };
    },
    setCustomPanelOpen(isOpen) {
        return { type: SET_CUSTOM_PANEL_OPEN, isOpen };
    },
    /**
     * Switches between the bot and home screen views.
     */
    toggleHomeScreen() {
        return { type: TOGGLE_HOME_SCREEN };
    },
    /**
     * Updates the state of the input field.
     */
    updateInputState(newState, isInputToAgent) {
        return { type: UPDATE_INPUT_STATE, newState, isInputToAgent };
    },
    /**
     * Changes the values that indicates whether the browser page is visible.
     */
    setIsBrowserPageVisible(isVisible) {
        return { type: SET_IS_BROWSER_PAGE_VISIBLE, isVisible };
    },
    /**
     * Adds a new file to the input area for uploaded.
     */
    addInputFile(file, isInputToAgent) {
        return { type: ADD_INPUT_FILE, file, isInputToAgent };
    },
    /**
     * Removes a file attachment from the upload attachments area.
     */
    removeFileUpload(fileID, isInputToAgent) {
        return { type: REMOVE_INPUT_FILE, fileID, isInputToAgent };
    },
    /**
     * Removes the local message item with the given ID from the message list.
     */
    removeLocalMessageItem(localMessageItemID) {
        return { type: REMOVE_LOCAL_MESSAGE_ITEM, localMessageItemID };
    },
    /**
     * Updates the input area to indicate that a file upload is in error.
     */
    fileUploadInputError(fileID, errorMessage, isInputToAgent) {
        return { type: FILE_UPLOAD_INPUT_ERROR, fileID, errorMessage, isInputToAgent };
    },
    /**
     * Removes all the files from the input area.
     */
    clearInputFiles(isInputToAgent) {
        return { type: CLEAR_INPUT_FILES, isInputToAgent };
    },
    addNestedMessages(localMessageItems) {
        return { type: ADD_NESTED_MESSAGES, localMessageItems };
    },
    setResponsePanelIsOpen(isOpen) {
        return { type: SET_RESPONSE_PANEL_IS_OPEN, isOpen };
    },
    setResponsePanelContent(localMessageItem, isMessageForInput = false) {
        return { type: SET_RESPONSE_PANEL_CONTENT, localMessageItem, isMessageForInput };
    },
    /**
     * Adds a message to the store to begin the streaming process.
     */
    streamingStart(messageID) {
        return { type: STREAMING_START, messageID };
    },
    /**
     * Merges the given message history object into an existing message object.
     */
    streamingMergeHistory(messageID, history) {
        return { type: STREAMING_MERGE_HISTORY, messageID, history };
    },
    /**
     * Adds a new chunk of a streaming response to an existing message.
     */
    streamingAddChunk(fullMessageID, chunkItem, isCompleteItem, disableFadeAnimation) {
        return { type: STREAMING_ADD_CHUNK, fullMessageID, chunkItem, isCompleteItem, disableFadeAnimation };
    },
    updateChatHeaderConfig(chatHeaderConfig) {
        return { type: UPDATE_CHAT_HEADER_CONFIG, chatHeaderConfig };
    },
    setMaxVisibleHeaderObjects(maxTotal) {
        return { type: UPDATE_MAX_VISIBLE_HEADER_OBJECTS, maxTotal };
    },
    setStopStreamingButtonVisible(isVisible) {
        return { type: SET_STOP_STREAMING_BUTTON_VISIBLE, isVisible };
    },
    setStopStreamingButtonDisabled(isDisabled) {
        return { type: SET_STOP_STREAMING_BUTTON_DISABLED, isDisabled };
    },
    setStreamID(currentStreamID) {
        return { type: SET_STREAM_ID, currentStreamID };
    },
    updateMainHeaderAvatar(config) {
        return { type: UPDATE_MAIN_HEADER_AVATAR, config };
    },
};

/**
 * Redux action creators for human agent actions.
 */
const HA_SET_AGENT_AVAILABILITY = 'HA_SET_AGENT_AVAILABILITY';
const HA_SET_IS_CONNECTING = 'HA_SET_IS_CONNECTING';
const HA_SET_IS_RECONNECTING = 'HA_SET_IS_RECONNECTING';
const HA_SET_AGENT_JOINED = 'HA_SET_AGENT_JOINED';
const HA_SET_AGENT_LEFT_CHAT = 'HA_SET_AGENT_LEFT_CHAT';
const HA_END_CHAT = 'HA_END_CHAT';
const HA_UPDATE_CAPABILITIES = 'HA_UPDATE_CAPABILITIES';
const HA_UPDATE_FILE_UPLOAD_IN_PROGRESS = 'HA_UPDATE_FILE_UPLOAD_IN_PROGRESS';
const HA_SET_SHOW_SCREEN_SHARE_REQUEST = 'HA_SET_SHOW_SCREEN_SHARE_REQUEST';
const HA_SET_IS_SCREEN_SHARING = 'HA_SET_IS_SCREEN_SHARING';
const HA_SET_PERSISTED_STATE = 'HA_SET_PERSISTED_STATE';
const HA_UPDATE_IS_SUSPENDED = 'HA_UPDATE_IS_SUSPENDED';
const HA_UPDATE_IS_TYPING = 'HA_UPDATE_IS_TYPING';
/**
 * Sets the "is connecting" status for a human agent.
 */
function setIsConnecting(isConnecting, localMessageID) {
    return {
        type: HA_SET_IS_CONNECTING,
        isConnecting,
        localMessageID,
    };
}
/**
 * Sets the "is reconnecting" status for a human agent.
 */
function setIsReconnecting(isReconnecting) {
    return { type: HA_SET_IS_RECONNECTING, isReconnecting };
}
/**
 * Indicate agent has left chat.
 */
function setAgentLeftChat() {
    return { type: HA_SET_AGENT_LEFT_CHAT };
}
/**
 * Ends the current chat.
 */
function endChat() {
    return { type: HA_END_CHAT };
}
/**
 * Sets the availability information for a user who is waiting to be connected to an agent.
 */
function setAgentAvailability(availability) {
    return {
        type: HA_SET_AGENT_AVAILABILITY,
        availability,
    };
}
/**
 * Sets the availability information for a user who is waiting to be connected to an agent.
 */
function setAgentJoined(agentProfile) {
    return {
        type: HA_SET_AGENT_JOINED,
        agentProfile,
    };
}
/**
 * Updates web chat with the capabilities supported by the service desk.
 */
function updateCapabilities(capabilities) {
    return { type: HA_UPDATE_CAPABILITIES, capabilities };
}
/**
 * Updates the indicator for if any files are being uploaded.
 */
function updateFilesUploadInProgress(fileUploadInProgress) {
    return { type: HA_UPDATE_FILE_UPLOAD_IN_PROGRESS, fileUploadInProgress };
}
/**
 * Sets the state of the screen sharing modal.
 */
function setShowScreenShareRequest(showRequest) {
    return { type: HA_SET_SHOW_SCREEN_SHARE_REQUEST, showRequest };
}
/**
 * Sets the state of screen sharing.
 */
function setIsScreenSharing(isSharing) {
    return { type: HA_SET_IS_SCREEN_SHARING, isSharing };
}
/**
 * Updates the object that is stored containing state for the service desk.
 */
function setPersistedServiceDeskState(state) {
    return { type: HA_SET_PERSISTED_STATE, state };
}
/**
 * Updates the suspended status for the current service desk.
 */
function agentUpdateIsSuspended(isSuspended) {
    return { type: HA_UPDATE_IS_SUSPENDED, isSuspended };
}
/**
 * Updates the suspended status for the current service desk.
 */
function agentUpdateIsTyping(isTyping) {
    return { type: HA_UPDATE_IS_TYPING, isTyping };
}

/**
 * This enum represents the different ways the "contact us/agent" option may be presented in suggestions list.
 */
var AlternateSuggestionsContactType;
(function (AlternateSuggestionsContactType) {
    /**
     * Indicates that the contact option should never be shown as a suggestion.
     */
    AlternateSuggestionsContactType["NEVER"] = "never";
    /**
     * Indicates that the contact option should always be shown as a suggestion.
     */
    AlternateSuggestionsContactType["ALWAYS"] = "always";
    /**
     * Indicates that the contact option should only be shown as an option if there is currently one or more strikes.
     */
    AlternateSuggestionsContactType["ONE_STRIKE"] = "one_strike";
})(AlternateSuggestionsContactType || (AlternateSuggestionsContactType = {}));

/**
 * The possible reasons why the chat window may be opened.
 */
var MainWindowOpenReason;
(function (MainWindowOpenReason) {
    /**
     * Indicates the user clicked on our built-in launcher button that opened the main window.
     */
    MainWindowOpenReason["DEFAULT_LAUNCHER"] = "default_launcher";
    /**
     * Indicates the main window was opened because {@link PublicConfig.openChatByDefault} was set to true.
     */
    MainWindowOpenReason["OPEN_BY_DEFAULT"] = "open_by_default";
    /**
     * Indicates the main window was opened as a result of session history.
     */
    MainWindowOpenReason["SESSION_HISTORY"] = "session_history";
    /**
     * Indicates the main window was opened by a call to {@link ChatInstance.openWindow} or
     * {@link ChatInstance.toggleOpen}.
     */
    MainWindowOpenReason["CALLED_OPEN_WINDOW"] = "called_open_window";
    /**
     * Indicates the main window was opened because a link ID was found that started a specific conversation.
     */
    MainWindowOpenReason["LINK"] = "link";
    /**
     * Indicates the main window was opened from a tour.
     *
     * @deprecated This reason is unclear so it's no longer being used. window:open events that used this reason will now
     * use the {@link TOUR_OPENED_OTHER_VIEW} reason instead. Since this reason was only added for beta tours we're
     * ok with removing it without a major release.
     */
    MainWindowOpenReason["FROM_TOUR"] = "from_tour";
    /**
     * Indicates the main window was opened from a tour.
     */
    MainWindowOpenReason["TOUR_OPENED_OTHER_VIEW"] = "tour_opened_other_view";
    /**
     * Indicates the {@link ChatInstance.restartConversation} method was used while a tour was visible.
     */
    MainWindowOpenReason["CALLED_RESTART_CONVERSATION"] = "called_restart_conversation";
    /**
     * Indicates the main window is being opened as a result of a channel transfer.
     */
    MainWindowOpenReason["CHANNEL_TRANSFER"] = "channel_transfer";
})(MainWindowOpenReason || (MainWindowOpenReason = {}));
/**
 * The possible reasons why the chat window may be closed.
 */
var MainWindowCloseReason;
(function (MainWindowCloseReason) {
    /**
     * Indicates the user clicked on our built-in minimize button that closed to the launcher.
     */
    MainWindowCloseReason["DEFAULT_MINIMIZE"] = "default_minimize";
    /**
     * Indicates the main window was closed by a call to {@link ChatInstance.closeWindow} or
     * {@link ChatInstance.toggleOpen}.
     */
    MainWindowCloseReason["CALLED_CLOSE_WINDOW"] = "called_close_window";
    /**
     * Indicates the main window was closed to open a tour. This either happens when a tour is started from a TourCard, or
     * when the main window is closed with the minimize button, or the {@link ChatInstance.closeWindow}
     * function, and there's an active tour to be shown.
     *
     * @deprecated This reason is unclear and was being used for many different scenarios so it's been removed.
     * window:close events that used this reason now use one of the following reasons; {@link TOUR_CARD_STARTED_TOUR},
     * {@link TOUR_CARD_RESUMED_TOUR}, {@link TOUR_CARD_RESTARTED_TOUR}, {@link CALLED_START_TOUR}, and
     * {@link TOUR_SKIP_CARD}. Since this reason was only added for beta tours we're ok with removing it without a major
     * release.
     */
    MainWindowCloseReason["OPEN_TOUR"] = "open_tour";
    /**
     * Indicates a tour was started by the start tour button in the main window.
     */
    MainWindowCloseReason["TOUR_CARD_STARTED_TOUR"] = "tour_card_started_tour";
    /**
     * Indicates a tour was resumed by the resume button in the main window.
     */
    MainWindowCloseReason["TOUR_CARD_RESUMED_TOUR"] = "tour_card_resumed_tour";
    /**
     * Indicates a tour was restarted by the restart button in the main window.
     */
    MainWindowCloseReason["TOUR_CARD_RESTARTED_TOUR"] = "tour_card_restarted_tour";
    /**
     * Indicates a tour was started by the {@link startTour} instance method.
     */
    MainWindowCloseReason["CALLED_START_TOUR"] = "called_start_tour";
    /**
     * Indicates a tour was started by a tour response type that included the 'skip_card' flag.
     */
    MainWindowCloseReason["TOUR_SKIP_CARD"] = "tour_skip_card";
    /**
     * Indicates the user clicked the close and restart button that minimized to the launcher.
     */
    MainWindowCloseReason["MAIN_WINDOW_CLOSED_AND_RESTARTED"] = "main_window_closed_and_restarted";
})(MainWindowCloseReason || (MainWindowCloseReason = {}));
/**
 * A map from the old {@link MainWindowOpenReason}s to the new {@link ViewChangeReason}s.
 */
const OpenReasonsToViewReasons = new Map([
    [MainWindowOpenReason.CALLED_OPEN_WINDOW, ViewChangeReason.CALLED_OPEN_WINDOW],
    [MainWindowOpenReason.DEFAULT_LAUNCHER, ViewChangeReason.LAUNCHER_CLICKED],
    [MainWindowOpenReason.LINK, ViewChangeReason.LINK_ID],
    [MainWindowOpenReason.OPEN_BY_DEFAULT, ViewChangeReason.WEB_CHAT_LOADED],
    [MainWindowOpenReason.SESSION_HISTORY, ViewChangeReason.SESSION_HISTORY],
    [MainWindowOpenReason.TOUR_OPENED_OTHER_VIEW, ViewChangeReason.TOUR_OPENED_OTHER_VIEW],
    [MainWindowOpenReason.CALLED_RESTART_CONVERSATION, ViewChangeReason.CALLED_RESTART_CONVERSATION],
    [MainWindowOpenReason.CHANNEL_TRANSFER, ViewChangeReason.CHANNEL_TRANSFER],
]);
/**
 * A map from the old {@link MainWindowCloseReason}s to the new {@link ViewChangeReason}s.
 */
const CloseReasonsToViewReasons = new Map([
    [MainWindowCloseReason.CALLED_CLOSE_WINDOW, ViewChangeReason.CALLED_CLOSE_WINDOW],
    [MainWindowCloseReason.DEFAULT_MINIMIZE, ViewChangeReason.MAIN_WINDOW_MINIMIZED],
    [MainWindowCloseReason.TOUR_CARD_STARTED_TOUR, ViewChangeReason.TOUR_CARD_STARTED_TOUR],
    [MainWindowCloseReason.TOUR_CARD_RESUMED_TOUR, ViewChangeReason.TOUR_CARD_RESUMED_TOUR],
    [MainWindowCloseReason.TOUR_CARD_RESTARTED_TOUR, ViewChangeReason.TOUR_CARD_RESTARTED_TOUR],
    [MainWindowCloseReason.CALLED_START_TOUR, ViewChangeReason.CALLED_START_TOUR],
    [MainWindowCloseReason.TOUR_SKIP_CARD, ViewChangeReason.TOUR_SKIP_CARD],
    [MainWindowCloseReason.MAIN_WINDOW_CLOSED_AND_RESTARTED, ViewChangeReason.MAIN_WINDOW_CLOSED_AND_RESTARTED],
]);

// Cache implementation based on Erik Rasmussen's `lru-memoize`:
// https://github.com/erikras/lru-memoize
var NOT_FOUND = 'NOT_FOUND';

function createSingletonCache(equals) {
  var entry;
  return {
    get: function get(key) {
      if (entry && equals(entry.key, key)) {
        return entry.value;
      }

      return NOT_FOUND;
    },
    put: function put(key, value) {
      entry = {
        key: key,
        value: value
      };
    },
    getEntries: function getEntries() {
      return entry ? [entry] : [];
    },
    clear: function clear() {
      entry = undefined;
    }
  };
}

function createLruCache(maxSize, equals) {
  var entries = [];

  function get(key) {
    var cacheIndex = entries.findIndex(function (entry) {
      return equals(key, entry.key);
    }); // We found a cached entry

    if (cacheIndex > -1) {
      var entry = entries[cacheIndex]; // Cached entry not at top of cache, move it to the top

      if (cacheIndex > 0) {
        entries.splice(cacheIndex, 1);
        entries.unshift(entry);
      }

      return entry.value;
    } // No entry found in cache, return sentinel


    return NOT_FOUND;
  }

  function put(key, value) {
    if (get(key) === NOT_FOUND) {
      // TODO Is unshift slow?
      entries.unshift({
        key: key,
        value: value
      });

      if (entries.length > maxSize) {
        entries.pop();
      }
    }
  }

  function getEntries() {
    return entries;
  }

  function clear() {
    entries = [];
  }

  return {
    get: get,
    put: put,
    getEntries: getEntries,
    clear: clear
  };
}

var defaultEqualityCheck = function defaultEqualityCheck(a, b) {
  return a === b;
};
function createCacheKeyComparator(equalityCheck) {
  return function areArgumentsShallowlyEqual(prev, next) {
    if (prev === null || next === null || prev.length !== next.length) {
      return false;
    } // Do this in a for loop (and not a `forEach` or an `every`) so we can determine equality as fast as possible.


    var length = prev.length;

    for (var i = 0; i < length; i++) {
      if (!equalityCheck(prev[i], next[i])) {
        return false;
      }
    }

    return true;
  };
}
// defaultMemoize now supports a configurable cache size with LRU behavior,
// and optional comparison of the result value with existing values
function defaultMemoize(func, equalityCheckOrOptions) {
  var providedOptions = typeof equalityCheckOrOptions === 'object' ? equalityCheckOrOptions : {
    equalityCheck: equalityCheckOrOptions
  };
  var _providedOptions$equa = providedOptions.equalityCheck,
      equalityCheck = _providedOptions$equa === void 0 ? defaultEqualityCheck : _providedOptions$equa,
      _providedOptions$maxS = providedOptions.maxSize,
      maxSize = _providedOptions$maxS === void 0 ? 1 : _providedOptions$maxS,
      resultEqualityCheck = providedOptions.resultEqualityCheck;
  var comparator = createCacheKeyComparator(equalityCheck);
  var cache = maxSize === 1 ? createSingletonCache(comparator) : createLruCache(maxSize, comparator); // we reference arguments instead of spreading them for performance reasons

  function memoized() {
    var value = cache.get(arguments);

    if (value === NOT_FOUND) {
      // @ts-ignore
      value = func.apply(null, arguments);

      if (resultEqualityCheck) {
        var entries = cache.getEntries();
        var matchingEntry = entries.find(function (entry) {
          return resultEqualityCheck(entry.value, value);
        });

        if (matchingEntry) {
          value = matchingEntry.value;
        }
      }

      cache.put(arguments, value);
    }

    return value;
  }

  memoized.clearCache = function () {
    return cache.clear();
  };

  return memoized;
}

function getDependencies(funcs) {
  var dependencies = Array.isArray(funcs[0]) ? funcs[0] : funcs;

  if (!dependencies.every(function (dep) {
    return typeof dep === 'function';
  })) {
    var dependencyTypes = dependencies.map(function (dep) {
      return typeof dep === 'function' ? "function " + (dep.name || 'unnamed') + "()" : typeof dep;
    }).join(', ');
    throw new Error("createSelector expects all input-selectors to be functions, but received the following types: [" + dependencyTypes + "]");
  }

  return dependencies;
}

function createSelectorCreator(memoize) {
  for (var _len = arguments.length, memoizeOptionsFromArgs = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    memoizeOptionsFromArgs[_key - 1] = arguments[_key];
  }

  var createSelector = function createSelector() {
    for (var _len2 = arguments.length, funcs = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      funcs[_key2] = arguments[_key2];
    }

    var _recomputations = 0;

    var _lastResult; // Due to the intricacies of rest params, we can't do an optional arg after `...funcs`.
    // So, start by declaring the default value here.
    // (And yes, the words 'memoize' and 'options' appear too many times in this next sequence.)


    var directlyPassedOptions = {
      memoizeOptions: undefined
    }; // Normally, the result func or "output selector" is the last arg

    var resultFunc = funcs.pop(); // If the result func is actually an _object_, assume it's our options object

    if (typeof resultFunc === 'object') {
      directlyPassedOptions = resultFunc; // and pop the real result func off

      resultFunc = funcs.pop();
    }

    if (typeof resultFunc !== 'function') {
      throw new Error("createSelector expects an output function after the inputs, but received: [" + typeof resultFunc + "]");
    } // Determine which set of options we're using. Prefer options passed directly,
    // but fall back to options given to createSelectorCreator.


    var _directlyPassedOption = directlyPassedOptions,
        _directlyPassedOption2 = _directlyPassedOption.memoizeOptions,
        memoizeOptions = _directlyPassedOption2 === void 0 ? memoizeOptionsFromArgs : _directlyPassedOption2; // Simplifying assumption: it's unlikely that the first options arg of the provided memoizer
    // is an array. In most libs I've looked at, it's an equality function or options object.
    // Based on that, if `memoizeOptions` _is_ an array, we assume it's a full
    // user-provided array of options. Otherwise, it must be just the _first_ arg, and so
    // we wrap it in an array so we can apply it.

    var finalMemoizeOptions = Array.isArray(memoizeOptions) ? memoizeOptions : [memoizeOptions];
    var dependencies = getDependencies(funcs);
    var memoizedResultFunc = memoize.apply(void 0, [function recomputationWrapper() {
      _recomputations++; // apply arguments instead of spreading for performance.

      return resultFunc.apply(null, arguments);
    }].concat(finalMemoizeOptions)); // If a selector is called with the exact same arguments we don't need to traverse our dependencies again.

    var selector = memoize(function dependenciesChecker() {
      var params = [];
      var length = dependencies.length;

      for (var i = 0; i < length; i++) {
        // apply arguments instead of spreading and mutate a local list of params for performance.
        // @ts-ignore
        params.push(dependencies[i].apply(null, arguments));
      } // apply arguments instead of spreading for performance.


      _lastResult = memoizedResultFunc.apply(null, params);
      return _lastResult;
    });
    Object.assign(selector, {
      resultFunc: resultFunc,
      memoizedResultFunc: memoizedResultFunc,
      dependencies: dependencies,
      lastResult: function lastResult() {
        return _lastResult;
      },
      recomputations: function recomputations() {
        return _recomputations;
      },
      resetRecomputations: function resetRecomputations() {
        return _recomputations = 0;
      }
    });
    return selector;
  }; // @ts-ignore


  return createSelector;
}
var createSelector = /* #__PURE__ */createSelectorCreator(defaultMemoize);

/**
 * Returns the main config from the app state.
 */
const selectMainConfig = (state) => state.config.remote.mainConfig;
/**
 * Returns the current bot input state.
 */
const selectBotInputState = (state) => state.botInputState;
/**
 * Returns the current agent input state.
 */
const selectAgentInputState = (state) => state.agentState.inputState;
/**
 * Returns the agent state from the app state.
 */
const selectAgentState = (state) => state.agentState;
/**
 * Returns the persisted agent state from the app state.
 */
const selectPersistedAgentState = (state) => state.persistedToBrowserStorage.chatState.agentState;
/**
 * Determines if the main config indicates that a search skill is attached to the assistant. Note that this only
 * works after the main config has been loaded.
 */
const selectHasSearchSkill = createSelector([selectMainConfig], mainConfig => mainConfig?.assistant?.skills?.some((skill) => skill.type === SkillType.SEARCH) ?? false);
/**
 * Returns the agent state for the purposes of display of the chat widget. This state is different then the real state
 * in that it takes into account if the agent conversation is suspended. If it is suspended, then various elements about
 * the agent are hidden from the UI and user messages are routed to the assistant instead of the service desk.
 */
const selectAgentDisplayState = createSelector([selectAgentState, selectPersistedAgentState], (agentState, persistedAgentState) => {
    if (persistedAgentState.isSuspended) {
        return {
            isConnectingOrConnected: false,
            disableInput: false,
            isAgentTyping: false,
            inputPlaceholderKey: null,
        };
    }
    const { isReconnecting, isConnecting, isAgentTyping } = agentState;
    const { isConnected } = persistedAgentState;
    let inputPlaceholderKey;
    if (isConnecting) {
        inputPlaceholderKey = 'agent_inputPlaceholderConnecting';
    }
    else if (isReconnecting) {
        inputPlaceholderKey = 'agent_inputPlaceholderReconnecting';
    }
    return {
        isAgentTyping,
        isConnectingOrConnected: isConnecting || isConnected,
        disableInput: isConnecting || isReconnecting,
        inputPlaceholderKey,
    };
});
/**
 * Indicates if the chat is configured currently for interacting with a human agent. If true, the user is either
 * connecting or connected to a human agent with a service desk and the interaction is not suspended.
 */
const selectIsInputToAgent = createSelector([selectAgentDisplayState], agentDisplayState => agentDisplayState.isConnectingOrConnected);
/**
 * Returns the current input state. Which state is used depends on whether the user is connected or connecting to a
 * human agent.
 */
const selectInputState = createSelector([selectIsInputToAgent, selectAgentInputState, selectBotInputState], (isInputToAgent, agentInputState, botInputState) => (isInputToAgent ? agentInputState : botInputState));

/**
 * Returns the internal name of the dialog skill given a message. This will always return some value even if there isn't
 * actually a dialog skill attached so any code should use this defensively if acting on a non-dialog skill would be
 * bad.
 */
function getDialogSkillID(context, mainConfig) {
    // Look for the dialog skill.
    const info = mainConfig.assistant?.skills?.find(info => info.type === SkillType.DIALOG);
    if (info?.skill_reference) {
        return info.skill_reference;
    }
    if (context?.skills) {
        // If it's not found, then try the "main skill". This is the default name for dialog skills.
        if (context.skills[MAIN_SKILL]) {
            return MAIN_SKILL;
        }
        // If still not found, return the first skill. This really shouldn't happen but make sure we return something in
        // case the skill was somehow renamed or there is no dialog skill.
        return Object.keys(context.skills)[0];
    }
    // If no skills are found, just default to the "main skill". This can happen in the classic experience preview
    // link where the web chat config is missing. It is a reasonable guess that the skill has a dialog skill but it's not
    // guaranteed.
    return MAIN_SKILL;
}
/**
 * Gets the dialog state out of the LocalMessage object.  The state is stored as a base64 encoded JSON string,
 * so this function has to decode (atob) and parse it into an object (JSON.parse).
 *
 * @param message The message request object that contains the message context and dialog state.
 * @param mainConfig The main config that contains info about the skills.
 */
function getDialogState(message, mainConfig) {
    const DEFAULT_DIALOG_STATE = {
        dialog_stack: [{ dialog_node: DIALOG_ROOT }],
        digressed: false,
    };
    if (!isResponse(message)) {
        return DEFAULT_DIALOG_STATE;
    }
    let dialogState;
    const dialogSkillID = getDialogSkillID(message?.context, mainConfig);
    try {
        if (dialogSkillID && message?.context?.skills?.[dialogSkillID]?.system?.state) {
            dialogState = JSON.parse(atob(message.context.skills[dialogSkillID].system.state));
        }
        else {
            dialogState = DEFAULT_DIALOG_STATE;
        }
    }
    catch (error) {
        consoleError('Error parsing skill state', error);
        dialogState = DEFAULT_DIALOG_STATE;
    }
    return dialogState;
}

/**
 * A default {@link Option} object that can be used to for contacting the customer or an agent.
 */
const CONTACT_OPTION = (label) => {
    return {
        label,
        value: {
            input: {
                text: label,
            },
        },
    };
};
/**
 * A default {@link SuggestionListItem} object that can be used to for contacting the customer or an agent.
 */
const CONTACT_SUGGESTION_ITEM = (label) => {
    return {
        isSelected: false,
        isContactSuggestion: true,
        option: CONTACT_OPTION(label),
    };
};
/**
 * Returns the label to use for the "contact" button on the suggestions list.
 */
function getContactLabel(config, languagePack) {
    return config.contact_label || languagePack.suggestions_connectToAgent;
}
/**
 * Determines if the given message is a repeat of the previous response from the bot. This function decides that a
 * duplicate has occurred if the nodes for both messages are the same. We exclude search results from this chance
 * since it's different to determine if search results are a repeat.
 */
function isDuplicateOfPrevious(state, response) {
    if (isResponse(response) && !isSearchResultItem(response.output.generic[0])) {
        const { allMessageItemsByID, allMessagesByID, botMessageState, config } = state;
        const { localMessageIDs } = botMessageState;
        const { mainConfig } = config.remote;
        // We need to start at the end of the message list and work our way backward until we find a message item
        // belonging to a different original message and that is a message response (output).
        let index = localMessageIDs.length - 1;
        let previousResponse;
        for (; index >= 0; index--) {
            const message = allMessagesByID[allMessageItemsByID[localMessageIDs[index]].fullMessageID];
            if (message.id !== response.id && isResponse(message)) {
                previousResponse = message;
                break;
            }
        }
        if (isRepeatResponse(response, previousResponse)) {
            // We've hit a duplicate node but it's possible it's a return from a digression which we don't want to count.
            const dialogState = getDialogState(response, mainConfig);
            return !dialogState.digressed;
        }
    }
    return false;
}
/**
 * Determines if the given response is an alternate suggestions strike. This occurs if the response is the anything else
 * node or if the response is a repeat of the same node as the previous response.
 */
function isStrike(response, state, trackEvent) {
    const agentDisplayState = selectAgentDisplayState(state);
    if (state.persistedToBrowserStorage.chatState.suggestionsPersistedState.numStrikes === 2 &&
        agentDisplayState.isConnectingOrConnected) {
        // If we're at the second strike and the user is already connected to an agent. Don't trigger a third strike
        // which will just result in another agent card appearing.
        return false;
    }
    const request = state.allMessagesByID[response.request_id];
    if (response.context?.global?.system?.skip_user_input || request?.context?.global?.system?.skip_user_input) {
        // When we get a skip_user_input response, that will not contribute to strikes.
        return false;
    }
    const firstItem = response.output.generic[0];
    const isEmptySearch = isSearchResultItem(firstItem) && isEmptySearchResultItem(firstItem);
    // If we get empty search results, that counts as a strike.
    if (isEmptySearch) {
        if (trackEvent) {
            trackEvent.suggestionsStrikeReason = 'Empty Search';
        }
        return true;
    }
    // Conversational search items should not trigger strikes.
    if (isConversationalSearchItem(firstItem)) {
        return false;
    }
    // The anything else node counts as a strike unless it has (non-empty) search results.
    if (isAnythingElse(response) && !isSearchResultItem(firstItem)) {
        if (trackEvent) {
            trackEvent.suggestionsStrikeReason = 'Anything Else';
        }
        return true;
    }
    if (isDuplicateOfPrevious(state, response)) {
        if (trackEvent) {
            trackEvent.suggestionsStrikeReason = 'Bot Repeating';
        }
        return true;
    }
    return false;
}
/**
 * Indicates if the given messages should trigger a third strike for suggestions. This will result in the
 * suggestions "contact" option being activated. We only do this on the third strike and if the configuration says
 * that using the contact option is allowed.
 */
function shouldMessageActivateThirdStrike(message, state) {
    const { config } = state.alternateSuggestionsState;
    const { numStrikes } = state.persistedToBrowserStorage.chatState.suggestionsPersistedState;
    return (config.is_on &&
        config.contact_option !== AlternateSuggestionsContactType.NEVER &&
        numStrikes === 2 &&
        isStrike(message, state));
}
/**
 * Determines if the given item is either a {@link SuggestionListItem} or a {@link HomeScreenStarterButton}.
 */
function isSuggestionListItem(value) {
    return Boolean(value?.option);
}

// The amount of time to delay after displaying "New message" in the window title before it changes to "(n) Original
// message".
const UNREAD_COUNT_TITLE_TIME = 10000;
/**
 * Returns a handler that will watch for changes related to suggestions. This includes looking for changes to the
 * number of alternate suggestions strikes and will request an agent once the count gets to 3. It will also look for
 * changes to the search property to determine if search results should be loaded.
 */
function createHandleAlternateSuggestions(serviceManager) {
    const { store } = serviceManager;
    let previousStrikes = 0;
    let previousSearchText = null;
    let currentSearchQuery = null;
    return () => {
        const state = store.getState();
        const { config, searchResultsText } = state.alternateSuggestionsState;
        const { numStrikes } = state.persistedToBrowserStorage.chatState.suggestionsPersistedState;
        // If the number of strikes has changed and is now 3, that means we've hit the strike limit and need to
        // explicitly ask for an agent on behalf of the user. But don't do that if the config says the contact option is
        // set to never.
        if (numStrikes === 3 &&
            numStrikes !== previousStrikes &&
            config.contact_option !== AlternateSuggestionsContactType.NEVER) {
            const { languagePack } = state;
            const label = getContactLabel(config, languagePack);
            const message = createMessageRequestForChoice(CONTACT_OPTION(label));
            serviceManager.actions.sendWithCatch(message, MessageSendSource.SUGGESTIONS_3RD_STRIKE, {
                silent: true,
                isSuggestion: true,
            });
        }
        // Determine if we need to initiate a request for search results for suggestions.
        if (searchResultsText && searchResultsText !== previousSearchText) {
            // Fetch the search results. We don't use await here because this function isn't async.
            const thisSearchQuery = serviceManager.messageService.queryForSearchResults(searchResultsText);
            const initialRestartCount = serviceManager.restartCount;
            currentSearchQuery = thisSearchQuery;
            thisSearchQuery
                .then(result => {
                if (thisSearchQuery !== currentSearchQuery || initialRestartCount !== serviceManager.restartCount) {
                    // If this query is no longer the current one, then just ignore it.
                    return;
                }
                serviceManager.store.dispatch(actions.setSuggestionsSearchResults(result));
            })
                .catch(error => consoleError('An error occurred executing a search', error));
        }
        previousStrikes = numStrikes;
        previousSearchText = searchResultsText;
    };
}
/**
 * Watches for changes in the state properties that the service desks care about and updates them if they change.
 */
function createHandleServiceDesk(serviceManager) {
    const { humanAgentService, store } = serviceManager;
    let previousState = store.getState();
    humanAgentService.updateState({
        sessionID: previousState.persistedToBrowserStorage.chatState.sessionID,
        userID: previousState.userID,
        locale: previousState.locale,
    });
    return () => {
        const state = store.getState();
        // Checking to see if languagePack has changed is sufficient to determine if serviceManager.intl has changed
        // since the two are always changed together.
        if (state.persistedToBrowserStorage.chatState.sessionID !==
            previousState.persistedToBrowserStorage.chatState.sessionID ||
            state.userID !== previousState.userID ||
            state.languagePack !== previousState.languagePack ||
            state.locale !== previousState.locale) {
            humanAgentService.updateState({
                sessionID: state.persistedToBrowserStorage.chatState.sessionID,
                userID: state.userID,
                locale: state.locale,
            });
        }
        previousState = state;
    };
}
/**
 * Copies persistedToBrowserStorage to the session history.
 */
function copyToSessionStorage(serviceManager) {
    let previousPersistedToBrowserStorage = serviceManager.store.getState().persistedToBrowserStorage;
    let previousUserID = serviceManager.store.getState().userID;
    return () => {
        const { persistedToBrowserStorage, userID, config } = serviceManager.store.getState();
        const { disableSessionHistory } = config.public;
        let persistChatSession = previousPersistedToBrowserStorage !== persistedToBrowserStorage;
        if (userID && previousUserID !== userID) {
            // Save a copy of the previousUserID so we can overwrite it with the new one.
            const tempPreviousUserID = previousUserID;
            // Update previousUserID with the new userID because the updatePersistedChatState call below will trigger this
            // subscription to re-enter. If we don't update this value now then we'll be in an infinite loop trying to switch
            // from an anonymous session to the new userID.
            previousUserID = userID;
            if (tempPreviousUserID) {
                // We previously had a userID so we need to clear the old chat session.
                serviceManager.userSessionStorageService.clearChatSession(tempPreviousUserID, config.public.integrationID);
                // We need to persist the chat state we have in redux into sessionStorage under the new userID.
                persistChatSession = true;
            }
            else {
                // This is the first time we've seen a userID so we need to retrieve the chat session if there is one.
                const session = serviceManager.userSessionStorageService.loadChatSession(userID, config.public.integrationID, config.public.sessionID);
                // Update our local chat session state which will trigger this subscription to re-enter.
                serviceManager.store.dispatch(actions.updatePersistedChatState(session));
            }
        }
        if (persistChatSession) {
            previousPersistedToBrowserStorage = persistedToBrowserStorage;
            if (disableSessionHistory) {
                const { disclaimersAccepted, version } = persistedToBrowserStorage.chatState;
                // If session history is disabled then only save the disclaimer and version state in sessionStorage.
                serviceManager.userSessionStorageService.persistChatSession(userID, config.public.integrationID, {
                    disclaimersAccepted,
                    version,
                });
            }
            else {
                // If we have a userID then save the chatSession state. This subscription will be called when the launcherState
                // changes in redux which will often happen before the web chat has been opened and likely before we have the userID.
                if (userID) {
                    serviceManager.userSessionStorageService.persistChatSession(userID, config.public.integrationID, persistedToBrowserStorage.chatState);
                }
                serviceManager.userSessionStorageService.persistLauncherSession(config.public.integrationID, persistedToBrowserStorage.launcherState);
            }
        }
    };
}
/**
 * Returns a function that can be used as a redux subscription that will fire off Amplitude tracking events when
 * requested by a reducer.
 */
function createHandleTrackEvents(serviceManager) {
    const { store } = serviceManager;
    return () => {
        const { pendingLogs } = store.getState();
        const { trackEvents, eventMessages } = pendingLogs;
        if (eventMessages.length || trackEvents.length) {
            trackEvents.forEach(event => {
                serviceManager.trackService.track(event);
            });
            eventMessages.forEach(message => {
                // Generate the correctly formatted eventMessage.
                const eventMessage = createEventMessageRequest(message);
                // Send eventMessage to back-end to save it.
                serviceManager.messageService.send(eventMessage, MessageSendSource.EVENT);
            });
            // Now clear out the track events.
            store.dispatch(actions.setAppStateValue('pendingLogs', {
                trackEvents: [],
                eventMessages: [],
            }));
        }
    };
}
/**
 * This creates a subscription that listens for changes to unread live agent messages and updates the window title
 * as appropriate to show the user that messages are unread.
 */
function createHandleWindowTitle(serviceManager) {
    const { store } = serviceManager;
    let originalTitle;
    let changeTitleTimer;
    let previousState = store.getState();
    return () => {
        const state = store.getState();
        const { agentState } = store.getState();
        const { numUnreadMessages } = agentState;
        if (numUnreadMessages !== previousState.agentState.numUnreadMessages) {
            if (!numUnreadMessages) {
                // Nothing unread anymore so reset the window title.
                clearTimeout(changeTitleTimer);
                if (originalTitle) {
                    window.document.title = originalTitle;
                    originalTitle = null;
                }
            }
            else {
                // A new message has appeared so change the title to "New Message" and then set a timer to change it to a
                // version that has "(n)" in it.
                clearTimeout(changeTitleTimer);
                if (!originalTitle) {
                    originalTitle = window.document.title;
                }
                window.document.title = state.languagePack.agent_newMessage;
                changeTitleTimer = setTimeout(() => {
                    window.document.title = `(${numUnreadMessages}) ${originalTitle}`;
                }, UNREAD_COUNT_TITLE_TIME);
            }
        }
        previousState = state;
    };
}

/**
 * Denotes the type of note.
 */
var NoteType;
(function (NoteType) {
    /**
     * Represents the history of the conversation. The only type currently used by Web chat client.
     */
    NoteType["HISTORY"] = "HISTORY";
    /**
     * Arbitrary text or/and image to presented to the human agent.
     */
    NoteType["MESSAGE"] = "MESSAGE";
    /**
     * An non multi-turn Answer.
     */
    NoteType["ANSWER"] = "ANSWER";
    /**
     * A multi-turn suggestion that an agent can approve or reject.
     */
    NoteType["FLOW"] = "FLOW";
    /**
     * An error message to be presented to the human agent.
     */
    NoteType["ERROR"] = "ERROR";
})(NoteType || (NoteType = {}));

// Auth code param key for channel transfer.
const PARAM_AUTH_CODE = 'ibm_wa_auth_code';
// Session id param key for channel transfer.
const PARAM_SESSION_ID = 'ibm_wa_session_id';
// The name of the parameter that identifies a "link id".
const PARAM_LINK_ID = 'wa_lid';
/**
 * Determines if a channel transfer event has occurred.
 */
function isChannelTransferPage() {
    const params = new URLSearchParams(window.location.search);
    return params.has(PARAM_AUTH_CODE) && params.has(PARAM_SESSION_ID);
}
/**
 * Gets the auth_code and session_id values provided to us from a channel transfer.
 */
function getChannelTransferParams() {
    const params = new URLSearchParams(window.location.search);
    const authCode = params.get(PARAM_AUTH_CODE);
    const sessionID = params.get(PARAM_SESSION_ID);
    if (!authCode || !sessionID) {
        return null;
    }
    return { authCode, sessionID };
}
/**
 * This function will verify and associate the target user id with the session provided from the source channel the
 * user is transferring from. We send a channel transfer event message to the back-end with the provided auth code and
 * target user id, generated by web chat, so that when the user reaches the transfer page we can associate the web chat
 * instance with the session started from an external channel allowing the user to continue their conversation on the
 * chat channel.
 */
async function verifyAndAssociateUserToChannelSession(serviceManager) {
    const { store, messageService } = serviceManager;
    // Get channel transfer query param values.
    const channelTransferValues = getChannelTransferParams();
    if (channelTransferValues) {
        const { authCode, sessionID } = channelTransferValues;
        store.dispatch(actions.updateSessionID(sessionID));
        store.dispatch(actions.updateSessionStartedTimestamp(Date.now()));
        const { userID } = store.getState();
        const eventMessage = createEventMessageRequest({
            name: EVENT_NAME_CHANNEL_TRANSFER,
            auth_code: authCode,
            target_user_id: userID,
        });
        try {
            // If this call is rejected, web chat gets stuck on the hydration panel, so we need to catch to allow web chat to
            // continue as normal.
            await messageService.send(eventMessage, MessageSendSource.EVENT);
        }
        catch (error) {
            consoleError('Error in verifyAndAssociateUserToChannelSession', error);
        }
    }
}
/**
 * Handles the state of home screen based on whether or not history was successfully fetched and processes the latest
 * transfer to agent response if there is one.
 */
function toggleHomeScreenAndProcessTransferResponse(history, serviceManager) {
    bypassHomeScreen(serviceManager);
    if (history) {
        const message = history.latestTransferToAgentResponse;
        // If we have a latest transfer to agent response, let's process it.
        if (message) {
            const { allMessagesByID } = serviceManager.store.getState();
            const request = allMessagesByID[message.request_id];
            serviceManager.actions.processMessageResponse(message, false, request, true, {});
        }
    }
}
/**
 * Exits the home screen if it is open.
 */
function bypassHomeScreen(serviceManager) {
    const { persistedToBrowserStorage } = serviceManager.store.getState();
    const { isHomeScreenOpen } = persistedToBrowserStorage.chatState.homeScreenState;
    const { homeScreenConfig } = serviceManager.store.getState();
    const homeScreenEnabled = homeScreenConfig?.is_on;
    // Home screen should be closed on channel transfer. This is initially set to false when calling
    // doCreateStore, but if session history is enabled, this could potentially be updated to true and this check
    // will make sure to close it again.
    if (homeScreenEnabled && isHomeScreenOpen) {
        serviceManager.store.dispatch(actions.setHomeScreenIsOpen(false));
    }
}

/**
 * This code was copied from https://github.com/dispix/deep-diff (return-deep-diff on npm) but there was a bug in it so
 * this copy fixes it. The  fix is noted below.
 *
 * The first fix was not recorded in any issues but the first fix caused a new bug that is detailed in
 * https://github.com/carbon-design-system/carbon-ai-chat/issues/160 and is now covered by tests.
 *
 * If we have any more issues with this code, we may want to investigate switching to json-diff-ts which has more usage.
 * The return-deep-diff library has not had any updates in 6 years.
 */
/* eslint-disable no-prototype-builtins */
/**
 * Return an object containing the keys that have different values in
 * two structurally similar objects.
 */
function deepDiff(obj1, obj2, keepNewKeys = false) {
    if (typeof obj1 !== 'object') {
        throw new TypeError('First parameter must be an object');
    }
    if (typeof obj2 !== 'object') {
        throw new TypeError('Second parameter must be an object');
    }
    const diff = {};
    Object.keys(obj2).forEach(key => {
        if (!obj1.hasOwnProperty(key)) {
            if (keepNewKeys) {
                diff[key] = obj2[key];
            }
            return;
        }
        if (obj1[key] === null || obj2[key] === null) {
            if (obj1[key] !== obj2[key]) {
                diff[key] = obj2[key];
            }
            return;
        }
        if (typeof obj2[key] === 'object') {
            if (obj2[key] instanceof Array) {
                // FIX: The two values passed to arrayEquals were reversed and the order matters.
                // if (!arrayEquals(obj2[key], obj1[key])) {
                if (!(obj1[key] instanceof Array) || !arrayEquals(obj1[key], obj2[key])) {
                    diff[key] = obj2[key];
                }
                return;
            }
            if (!(obj1[key] instanceof Object)) {
                diff[key] = obj2[key];
                return;
            }
            diff[key] = deepDiff(obj1[key], obj2[key], keepNewKeys);
            //  Don't keep trace of a null key if it was generated by a recursive use of deepDiff.
            if (diff[key] === null) {
                delete diff[key];
            }
            return;
        }
        if (obj1[key] !== obj2[key]) {
            diff[key] = obj2[key];
        }
    });
    // If there's no difference between the two objects, return null
    if (Object.keys(diff).length === 0) {
        return null;
    }
    return diff;
}
/**
 * Checks if two arrays are strictly equals
 */
function arrayEquals(arr1, arr2) {
    if (!(arr1 instanceof Array)) {
        throw new TypeError('arrayEqual 1st parameter must be an array');
    }
    if (!(arr2 instanceof Array)) {
        return false;
    }
    if (arr1.length !== arr2.length) {
        return false;
    }
    for (let i = 0, l = arr1.length; i < l; i++) {
        if (arr1[i] instanceof Array && arr2[i] instanceof Array) {
            if (!arrayEquals(arr1[i], arr2[i])) {
                return false;
            }
        }
        else if (arr1[i] instanceof Object && arr2[i] instanceof Object) {
            if (deepDiff(arr1[i], arr2[i], true) !== null) {
                return false;
            }
        }
        else if (arr1[i] !== arr2[i]) {
            return false;
        }
    }
    return true;
}

/**
 * This function adds properties that are only defined in 'propertiesToNullify' into 'original' with values for these
 * newly added properties as null.
 *
 * In order to return back to a point in time of a conversation, skill-global variables
 * that were not previous defined need to have their values nullified in order to be 'deleted'.
 *
 * ```
 * // Example
 * const original = { a: 1 }, propertiesToNullify = { b: 2 };
 *
 * // output: original = { a: 1, b: null }
 * nullifyUndefinedProperties(original, propertiesToNullify)
 * // or
 * const original = { a: { b: {} } }, propertiesToNullify = { a: { b: { c: 1 } } };
 *
 * // output: original = { a: 1, b: { c: null } }
 * nullifyUndefinedProperties(original, propertiesToNullify)
 * ```
 *
 * @param original The object to include properties that exist in the reference object but nullified.
 * @param propertiesToNullify A reference object with properties to nullify into the original object.
 */
function nullifyUndefinedProperties(original, propertiesToNullify) {
    if (original && !isEmptyObject(propertiesToNullify)) {
        // Iterate over keys in reference since it should have properties that are not in the original.
        Object.keys(propertiesToNullify).forEach(key => {
            const value = original[key];
            // If a value was not previously defined, then it should be nullified in order for it to be 'deleted'.
            if (value === undefined) {
                original[key] = null;
            }
            else if (typeof value === 'object' && value !== null) {
                const oldValue = propertiesToNullify[key];
                // Make sure that both object values are actual objects before attempting to nullify.
                if (typeof oldValue === 'object' && oldValue !== null) {
                    // We should also perform the same action of nullifying undefined properties to object values since they could
                    // also have properties that should be 'deleted'.
                    nullifyUndefinedProperties(original[key], propertiesToNullify[key]);
                }
            }
        });
    }
}

/**
 * Creates the parameters necessary for making the fetch call to the assistant.
 */
function createFetchParameters(serviceManager, transactionID, allowStream) {
    const { identityToken, config } = serviceManager.store.getState();
    // It's important this is a lowercase "accept" because the fetch-event-source library uses that as a header name
    // and if the case doesn't match, the fetch will end up merging them (it merges all the values of this header
    // regardless of the case used) instead of overwriting.
    const accept = allowStream ? MIME_JSON_STREAM : MIME_JSON;
    const fetchParameters = {
        method: 'POST',
        headers: {
            ...config.public.__ibm__.messageHeaders,
            [HEADER_ACCEPT]: accept,
            [HEADER_CONTENT_TYPE]: MIME_JSON,
            [API_DEBUGGING_HEADER_NAME]: transactionID,
            [HEADER_AUTHORIZATION]: identityToken ? `Bearer ${identityToken}` : undefined,
        },
    };
    // Make sure we send the right headers to the assistant so we can change how the messages are processed.
    let channel;
    switch (config.public.__ibm__?.toolingType) {
        case ToolingType.ASSISTANT_PREVIEW:
            channel = 'tooling-previewpage';
            break;
        case ToolingType.WEB_CHAT_SETTINGS:
            channel = 'tooling-webchat';
            break;
        case ToolingType.PREVIEW_LINK:
            channel = 'preview';
            break;
        case ToolingType.ACTIONS:
            channel = 'tooling-actions';
            break;
        case ToolingType.ANALYTICS:
            channel = 'tooling-analytics';
            break;
        case ToolingType.SEARCH:
            channel = 'tooling-search';
            break;
        default:
            channel = 'chat';
            break;
    }
    fetchParameters.headers['x-watson-assistant-channel'] = channel;
    // See https://cloud.ibm.com/apidocs/assistant/assistant-v2#data-collection
    if (config.public.learningOptOut) {
        fetchParameters.headers['x-watson-learning-opt-out'] = true;
    }
    return fetchParameters;
}
/**
 * A class that manages if the message is taking to long to load. Allows you to identify if behaviors for if a request
 * exceeds an amount of time to timeout or exceeds an amount of time to not show a loading indicator.
 */
class MessageLoadingManager {
    /**
     * Start the counters.
     *
     * @param onExceededMaxSilentLoading The callback to call if the call exceeds the max loading time.
     * @param onEnd The callback when .end() is called. If .end() is called with .end(boolean), that will be passed.
     * @param onTimeout The callback if the timer times out and we should error out the message.
     * @param msMaxSilentLoading The amount of time in MS we wait before showing a progress bar.
     * @param msMaxAttempt The max amount of time that has passed before we give up.
     */
    start(onExceededMaxSilentLoading, onEnd, onTimeout, msMaxSilentLoading, msMaxAttempt) {
        this.hasExceededMaxSilentLoading = false;
        this.onEnd = onEnd;
        this.onSilentLoading = setTimeout(() => {
            this.hasExceededMaxSilentLoading = true;
            onExceededMaxSilentLoading();
        }, msMaxSilentLoading);
        this.onMaxAttempt = setTimeout(() => {
            onTimeout();
        }, msMaxAttempt);
    }
    /**
     * Called when message succeeds/fails the loading manager is no longer needed. Calls this.end and resets the class.
     */
    end() {
        if (this.onMaxAttempt) {
            clearTimeout(this.onMaxAttempt);
        }
        if (this.onSilentLoading) {
            clearTimeout(this.onSilentLoading);
        }
        if (this.onEnd) {
            this.onEnd(this.hasExceededMaxSilentLoading);
        }
        this.hasExceededMaxSilentLoading = null;
        this.onEnd = null;
    }
}
async function doWithJWTRetry(executor, serviceManager) {
    let result = await executor();
    if (!result.ok) {
        const resultText = await safeFetchText(result);
        const isSecurityEnabled = serviceManager.store.getState().config.remote.initConfig.authorization?.is_on;
        if (isJWTError(result.status, isSecurityEnabled, resultText)) {
            await getNewIdentityToken(serviceManager);
            result = await executor();
        }
    }
    return result;
}

/**
 * This function will return a new promise that keeps a reference to its own resolve and reject functions so that they
 * can be called on demand by the code using the promise.
 */
function resolvablePromise() {
    let resolveFunction;
    let rejectFunction;
    const promise = new Promise((resolve, reject) => {
        resolveFunction = resolve;
        rejectFunction = reject;
    });
    promise.doResolve = (resolveValue) => {
        promise.isResolved = true;
        promise.isComplete = true;
        resolveFunction(resolveValue);
    };
    promise.doReject = (rejectValue) => {
        promise.isRejected = true;
        promise.isComplete = true;
        rejectFunction(rejectValue);
    };
    promise.isResolved = false;
    promise.isRejected = false;
    promise.isComplete = false;
    return promise;
}

/**
 * Copyright IBM Corp. 2018, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

var white = '#ffffff';
var blue60 = '#0f62fe';
var gray10 = '#f4f4f4';
var gray80 = '#393939';
var gray100 = '#161616';

var colorString$1 = {exports: {}};

var colorName$1 = {
	"aliceblue": [240, 248, 255],
	"antiquewhite": [250, 235, 215],
	"aqua": [0, 255, 255],
	"aquamarine": [127, 255, 212],
	"azure": [240, 255, 255],
	"beige": [245, 245, 220],
	"bisque": [255, 228, 196],
	"black": [0, 0, 0],
	"blanchedalmond": [255, 235, 205],
	"blue": [0, 0, 255],
	"blueviolet": [138, 43, 226],
	"brown": [165, 42, 42],
	"burlywood": [222, 184, 135],
	"cadetblue": [95, 158, 160],
	"chartreuse": [127, 255, 0],
	"chocolate": [210, 105, 30],
	"coral": [255, 127, 80],
	"cornflowerblue": [100, 149, 237],
	"cornsilk": [255, 248, 220],
	"crimson": [220, 20, 60],
	"cyan": [0, 255, 255],
	"darkblue": [0, 0, 139],
	"darkcyan": [0, 139, 139],
	"darkgoldenrod": [184, 134, 11],
	"darkgray": [169, 169, 169],
	"darkgreen": [0, 100, 0],
	"darkgrey": [169, 169, 169],
	"darkkhaki": [189, 183, 107],
	"darkmagenta": [139, 0, 139],
	"darkolivegreen": [85, 107, 47],
	"darkorange": [255, 140, 0],
	"darkorchid": [153, 50, 204],
	"darkred": [139, 0, 0],
	"darksalmon": [233, 150, 122],
	"darkseagreen": [143, 188, 143],
	"darkslateblue": [72, 61, 139],
	"darkslategray": [47, 79, 79],
	"darkslategrey": [47, 79, 79],
	"darkturquoise": [0, 206, 209],
	"darkviolet": [148, 0, 211],
	"deeppink": [255, 20, 147],
	"deepskyblue": [0, 191, 255],
	"dimgray": [105, 105, 105],
	"dimgrey": [105, 105, 105],
	"dodgerblue": [30, 144, 255],
	"firebrick": [178, 34, 34],
	"floralwhite": [255, 250, 240],
	"forestgreen": [34, 139, 34],
	"fuchsia": [255, 0, 255],
	"gainsboro": [220, 220, 220],
	"ghostwhite": [248, 248, 255],
	"gold": [255, 215, 0],
	"goldenrod": [218, 165, 32],
	"gray": [128, 128, 128],
	"green": [0, 128, 0],
	"greenyellow": [173, 255, 47],
	"grey": [128, 128, 128],
	"honeydew": [240, 255, 240],
	"hotpink": [255, 105, 180],
	"indianred": [205, 92, 92],
	"indigo": [75, 0, 130],
	"ivory": [255, 255, 240],
	"khaki": [240, 230, 140],
	"lavender": [230, 230, 250],
	"lavenderblush": [255, 240, 245],
	"lawngreen": [124, 252, 0],
	"lemonchiffon": [255, 250, 205],
	"lightblue": [173, 216, 230],
	"lightcoral": [240, 128, 128],
	"lightcyan": [224, 255, 255],
	"lightgoldenrodyellow": [250, 250, 210],
	"lightgray": [211, 211, 211],
	"lightgreen": [144, 238, 144],
	"lightgrey": [211, 211, 211],
	"lightpink": [255, 182, 193],
	"lightsalmon": [255, 160, 122],
	"lightseagreen": [32, 178, 170],
	"lightskyblue": [135, 206, 250],
	"lightslategray": [119, 136, 153],
	"lightslategrey": [119, 136, 153],
	"lightsteelblue": [176, 196, 222],
	"lightyellow": [255, 255, 224],
	"lime": [0, 255, 0],
	"limegreen": [50, 205, 50],
	"linen": [250, 240, 230],
	"magenta": [255, 0, 255],
	"maroon": [128, 0, 0],
	"mediumaquamarine": [102, 205, 170],
	"mediumblue": [0, 0, 205],
	"mediumorchid": [186, 85, 211],
	"mediumpurple": [147, 112, 219],
	"mediumseagreen": [60, 179, 113],
	"mediumslateblue": [123, 104, 238],
	"mediumspringgreen": [0, 250, 154],
	"mediumturquoise": [72, 209, 204],
	"mediumvioletred": [199, 21, 133],
	"midnightblue": [25, 25, 112],
	"mintcream": [245, 255, 250],
	"mistyrose": [255, 228, 225],
	"moccasin": [255, 228, 181],
	"navajowhite": [255, 222, 173],
	"navy": [0, 0, 128],
	"oldlace": [253, 245, 230],
	"olive": [128, 128, 0],
	"olivedrab": [107, 142, 35],
	"orange": [255, 165, 0],
	"orangered": [255, 69, 0],
	"orchid": [218, 112, 214],
	"palegoldenrod": [238, 232, 170],
	"palegreen": [152, 251, 152],
	"paleturquoise": [175, 238, 238],
	"palevioletred": [219, 112, 147],
	"papayawhip": [255, 239, 213],
	"peachpuff": [255, 218, 185],
	"peru": [205, 133, 63],
	"pink": [255, 192, 203],
	"plum": [221, 160, 221],
	"powderblue": [176, 224, 230],
	"purple": [128, 0, 128],
	"rebeccapurple": [102, 51, 153],
	"red": [255, 0, 0],
	"rosybrown": [188, 143, 143],
	"royalblue": [65, 105, 225],
	"saddlebrown": [139, 69, 19],
	"salmon": [250, 128, 114],
	"sandybrown": [244, 164, 96],
	"seagreen": [46, 139, 87],
	"seashell": [255, 245, 238],
	"sienna": [160, 82, 45],
	"silver": [192, 192, 192],
	"skyblue": [135, 206, 235],
	"slateblue": [106, 90, 205],
	"slategray": [112, 128, 144],
	"slategrey": [112, 128, 144],
	"snow": [255, 250, 250],
	"springgreen": [0, 255, 127],
	"steelblue": [70, 130, 180],
	"tan": [210, 180, 140],
	"teal": [0, 128, 128],
	"thistle": [216, 191, 216],
	"tomato": [255, 99, 71],
	"turquoise": [64, 224, 208],
	"violet": [238, 130, 238],
	"wheat": [245, 222, 179],
	"white": [255, 255, 255],
	"whitesmoke": [245, 245, 245],
	"yellow": [255, 255, 0],
	"yellowgreen": [154, 205, 50]
};

var simpleSwizzle = {exports: {}};

var isArrayish$1 = function isArrayish(obj) {
	if (!obj || typeof obj === 'string') {
		return false;
	}

	return obj instanceof Array || Array.isArray(obj) ||
		(obj.length >= 0 && (obj.splice instanceof Function ||
			(Object.getOwnPropertyDescriptor(obj, (obj.length - 1)) && obj.constructor.name !== 'String')));
};

var isArrayish = isArrayish$1;

var concat$2 = Array.prototype.concat;
var slice = Array.prototype.slice;

var swizzle$1 = simpleSwizzle.exports = function swizzle(args) {
	var results = [];

	for (var i = 0, len = args.length; i < len; i++) {
		var arg = args[i];

		if (isArrayish(arg)) {
			// http://jsperf.com/javascript-array-concat-vs-push/98
			results = concat$2.call(results, slice.call(arg));
		} else {
			results.push(arg);
		}
	}

	return results;
};

swizzle$1.wrap = function (fn) {
	return function () {
		return fn(swizzle$1(arguments));
	};
};

var simpleSwizzleExports = simpleSwizzle.exports;

/* MIT license */

var colorNames = colorName$1;
var swizzle = simpleSwizzleExports;
var hasOwnProperty = Object.hasOwnProperty;

var reverseNames = Object.create(null);

// create a list of reverse color names
for (var name in colorNames) {
	if (hasOwnProperty.call(colorNames, name)) {
		reverseNames[colorNames[name]] = name;
	}
}

var cs = colorString$1.exports = {
	to: {},
	get: {}
};

cs.get = function (string) {
	var prefix = string.substring(0, 3).toLowerCase();
	var val;
	var model;
	switch (prefix) {
		case 'hsl':
			val = cs.get.hsl(string);
			model = 'hsl';
			break;
		case 'hwb':
			val = cs.get.hwb(string);
			model = 'hwb';
			break;
		default:
			val = cs.get.rgb(string);
			model = 'rgb';
			break;
	}

	if (!val) {
		return null;
	}

	return {model: model, value: val};
};

cs.get.rgb = function (string) {
	if (!string) {
		return null;
	}

	var abbr = /^#([a-f0-9]{3,4})$/i;
	var hex = /^#([a-f0-9]{6})([a-f0-9]{2})?$/i;
	var rgba = /^rgba?\(\s*([+-]?\d+)(?=[\s,])\s*(?:,\s*)?([+-]?\d+)(?=[\s,])\s*(?:,\s*)?([+-]?\d+)\s*(?:[,|\/]\s*([+-]?[\d\.]+)(%?)\s*)?\)$/;
	var per = /^rgba?\(\s*([+-]?[\d\.]+)\%\s*,?\s*([+-]?[\d\.]+)\%\s*,?\s*([+-]?[\d\.]+)\%\s*(?:[,|\/]\s*([+-]?[\d\.]+)(%?)\s*)?\)$/;
	var keyword = /^(\w+)$/;

	var rgb = [0, 0, 0, 1];
	var match;
	var i;
	var hexAlpha;

	if (match = string.match(hex)) {
		hexAlpha = match[2];
		match = match[1];

		for (i = 0; i < 3; i++) {
			// https://jsperf.com/slice-vs-substr-vs-substring-methods-long-string/19
			var i2 = i * 2;
			rgb[i] = parseInt(match.slice(i2, i2 + 2), 16);
		}

		if (hexAlpha) {
			rgb[3] = parseInt(hexAlpha, 16) / 255;
		}
	} else if (match = string.match(abbr)) {
		match = match[1];
		hexAlpha = match[3];

		for (i = 0; i < 3; i++) {
			rgb[i] = parseInt(match[i] + match[i], 16);
		}

		if (hexAlpha) {
			rgb[3] = parseInt(hexAlpha + hexAlpha, 16) / 255;
		}
	} else if (match = string.match(rgba)) {
		for (i = 0; i < 3; i++) {
			rgb[i] = parseInt(match[i + 1], 0);
		}

		if (match[4]) {
			if (match[5]) {
				rgb[3] = parseFloat(match[4]) * 0.01;
			} else {
				rgb[3] = parseFloat(match[4]);
			}
		}
	} else if (match = string.match(per)) {
		for (i = 0; i < 3; i++) {
			rgb[i] = Math.round(parseFloat(match[i + 1]) * 2.55);
		}

		if (match[4]) {
			if (match[5]) {
				rgb[3] = parseFloat(match[4]) * 0.01;
			} else {
				rgb[3] = parseFloat(match[4]);
			}
		}
	} else if (match = string.match(keyword)) {
		if (match[1] === 'transparent') {
			return [0, 0, 0, 0];
		}

		if (!hasOwnProperty.call(colorNames, match[1])) {
			return null;
		}

		rgb = colorNames[match[1]];
		rgb[3] = 1;

		return rgb;
	} else {
		return null;
	}

	for (i = 0; i < 3; i++) {
		rgb[i] = clamp(rgb[i], 0, 255);
	}
	rgb[3] = clamp(rgb[3], 0, 1);

	return rgb;
};

cs.get.hsl = function (string) {
	if (!string) {
		return null;
	}

	var hsl = /^hsla?\(\s*([+-]?(?:\d{0,3}\.)?\d+)(?:deg)?\s*,?\s*([+-]?[\d\.]+)%\s*,?\s*([+-]?[\d\.]+)%\s*(?:[,|\/]\s*([+-]?(?=\.\d|\d)(?:0|[1-9]\d*)?(?:\.\d*)?(?:[eE][+-]?\d+)?)\s*)?\)$/;
	var match = string.match(hsl);

	if (match) {
		var alpha = parseFloat(match[4]);
		var h = ((parseFloat(match[1]) % 360) + 360) % 360;
		var s = clamp(parseFloat(match[2]), 0, 100);
		var l = clamp(parseFloat(match[3]), 0, 100);
		var a = clamp(isNaN(alpha) ? 1 : alpha, 0, 1);

		return [h, s, l, a];
	}

	return null;
};

cs.get.hwb = function (string) {
	if (!string) {
		return null;
	}

	var hwb = /^hwb\(\s*([+-]?\d{0,3}(?:\.\d+)?)(?:deg)?\s*,\s*([+-]?[\d\.]+)%\s*,\s*([+-]?[\d\.]+)%\s*(?:,\s*([+-]?(?=\.\d|\d)(?:0|[1-9]\d*)?(?:\.\d*)?(?:[eE][+-]?\d+)?)\s*)?\)$/;
	var match = string.match(hwb);

	if (match) {
		var alpha = parseFloat(match[4]);
		var h = ((parseFloat(match[1]) % 360) + 360) % 360;
		var w = clamp(parseFloat(match[2]), 0, 100);
		var b = clamp(parseFloat(match[3]), 0, 100);
		var a = clamp(isNaN(alpha) ? 1 : alpha, 0, 1);
		return [h, w, b, a];
	}

	return null;
};

cs.to.hex = function () {
	var rgba = swizzle(arguments);

	return (
		'#' +
		hexDouble(rgba[0]) +
		hexDouble(rgba[1]) +
		hexDouble(rgba[2]) +
		(rgba[3] < 1
			? (hexDouble(Math.round(rgba[3] * 255)))
			: '')
	);
};

cs.to.rgb = function () {
	var rgba = swizzle(arguments);

	return rgba.length < 4 || rgba[3] === 1
		? 'rgb(' + Math.round(rgba[0]) + ', ' + Math.round(rgba[1]) + ', ' + Math.round(rgba[2]) + ')'
		: 'rgba(' + Math.round(rgba[0]) + ', ' + Math.round(rgba[1]) + ', ' + Math.round(rgba[2]) + ', ' + rgba[3] + ')';
};

cs.to.rgb.percent = function () {
	var rgba = swizzle(arguments);

	var r = Math.round(rgba[0] / 255 * 100);
	var g = Math.round(rgba[1] / 255 * 100);
	var b = Math.round(rgba[2] / 255 * 100);

	return rgba.length < 4 || rgba[3] === 1
		? 'rgb(' + r + '%, ' + g + '%, ' + b + '%)'
		: 'rgba(' + r + '%, ' + g + '%, ' + b + '%, ' + rgba[3] + ')';
};

cs.to.hsl = function () {
	var hsla = swizzle(arguments);
	return hsla.length < 4 || hsla[3] === 1
		? 'hsl(' + hsla[0] + ', ' + hsla[1] + '%, ' + hsla[2] + '%)'
		: 'hsla(' + hsla[0] + ', ' + hsla[1] + '%, ' + hsla[2] + '%, ' + hsla[3] + ')';
};

// hwb is a bit different than rgb(a) & hsl(a) since there is no alpha specific syntax
// (hwb have alpha optional & 1 is default value)
cs.to.hwb = function () {
	var hwba = swizzle(arguments);

	var a = '';
	if (hwba.length >= 4 && hwba[3] !== 1) {
		a = ', ' + hwba[3];
	}

	return 'hwb(' + hwba[0] + ', ' + hwba[1] + '%, ' + hwba[2] + '%' + a + ')';
};

cs.to.keyword = function (rgb) {
	return reverseNames[rgb.slice(0, 3)];
};

// helpers
function clamp(num, min, max) {
	return Math.min(Math.max(min, num), max);
}

function hexDouble(num) {
	var str = Math.round(num).toString(16).toUpperCase();
	return (str.length < 2) ? '0' + str : str;
}

var colorStringExports = colorString$1.exports;

var colorName = {
	"aliceblue": [240, 248, 255],
	"antiquewhite": [250, 235, 215],
	"aqua": [0, 255, 255],
	"aquamarine": [127, 255, 212],
	"azure": [240, 255, 255],
	"beige": [245, 245, 220],
	"bisque": [255, 228, 196],
	"black": [0, 0, 0],
	"blanchedalmond": [255, 235, 205],
	"blue": [0, 0, 255],
	"blueviolet": [138, 43, 226],
	"brown": [165, 42, 42],
	"burlywood": [222, 184, 135],
	"cadetblue": [95, 158, 160],
	"chartreuse": [127, 255, 0],
	"chocolate": [210, 105, 30],
	"coral": [255, 127, 80],
	"cornflowerblue": [100, 149, 237],
	"cornsilk": [255, 248, 220],
	"crimson": [220, 20, 60],
	"cyan": [0, 255, 255],
	"darkblue": [0, 0, 139],
	"darkcyan": [0, 139, 139],
	"darkgoldenrod": [184, 134, 11],
	"darkgray": [169, 169, 169],
	"darkgreen": [0, 100, 0],
	"darkgrey": [169, 169, 169],
	"darkkhaki": [189, 183, 107],
	"darkmagenta": [139, 0, 139],
	"darkolivegreen": [85, 107, 47],
	"darkorange": [255, 140, 0],
	"darkorchid": [153, 50, 204],
	"darkred": [139, 0, 0],
	"darksalmon": [233, 150, 122],
	"darkseagreen": [143, 188, 143],
	"darkslateblue": [72, 61, 139],
	"darkslategray": [47, 79, 79],
	"darkslategrey": [47, 79, 79],
	"darkturquoise": [0, 206, 209],
	"darkviolet": [148, 0, 211],
	"deeppink": [255, 20, 147],
	"deepskyblue": [0, 191, 255],
	"dimgray": [105, 105, 105],
	"dimgrey": [105, 105, 105],
	"dodgerblue": [30, 144, 255],
	"firebrick": [178, 34, 34],
	"floralwhite": [255, 250, 240],
	"forestgreen": [34, 139, 34],
	"fuchsia": [255, 0, 255],
	"gainsboro": [220, 220, 220],
	"ghostwhite": [248, 248, 255],
	"gold": [255, 215, 0],
	"goldenrod": [218, 165, 32],
	"gray": [128, 128, 128],
	"green": [0, 128, 0],
	"greenyellow": [173, 255, 47],
	"grey": [128, 128, 128],
	"honeydew": [240, 255, 240],
	"hotpink": [255, 105, 180],
	"indianred": [205, 92, 92],
	"indigo": [75, 0, 130],
	"ivory": [255, 255, 240],
	"khaki": [240, 230, 140],
	"lavender": [230, 230, 250],
	"lavenderblush": [255, 240, 245],
	"lawngreen": [124, 252, 0],
	"lemonchiffon": [255, 250, 205],
	"lightblue": [173, 216, 230],
	"lightcoral": [240, 128, 128],
	"lightcyan": [224, 255, 255],
	"lightgoldenrodyellow": [250, 250, 210],
	"lightgray": [211, 211, 211],
	"lightgreen": [144, 238, 144],
	"lightgrey": [211, 211, 211],
	"lightpink": [255, 182, 193],
	"lightsalmon": [255, 160, 122],
	"lightseagreen": [32, 178, 170],
	"lightskyblue": [135, 206, 250],
	"lightslategray": [119, 136, 153],
	"lightslategrey": [119, 136, 153],
	"lightsteelblue": [176, 196, 222],
	"lightyellow": [255, 255, 224],
	"lime": [0, 255, 0],
	"limegreen": [50, 205, 50],
	"linen": [250, 240, 230],
	"magenta": [255, 0, 255],
	"maroon": [128, 0, 0],
	"mediumaquamarine": [102, 205, 170],
	"mediumblue": [0, 0, 205],
	"mediumorchid": [186, 85, 211],
	"mediumpurple": [147, 112, 219],
	"mediumseagreen": [60, 179, 113],
	"mediumslateblue": [123, 104, 238],
	"mediumspringgreen": [0, 250, 154],
	"mediumturquoise": [72, 209, 204],
	"mediumvioletred": [199, 21, 133],
	"midnightblue": [25, 25, 112],
	"mintcream": [245, 255, 250],
	"mistyrose": [255, 228, 225],
	"moccasin": [255, 228, 181],
	"navajowhite": [255, 222, 173],
	"navy": [0, 0, 128],
	"oldlace": [253, 245, 230],
	"olive": [128, 128, 0],
	"olivedrab": [107, 142, 35],
	"orange": [255, 165, 0],
	"orangered": [255, 69, 0],
	"orchid": [218, 112, 214],
	"palegoldenrod": [238, 232, 170],
	"palegreen": [152, 251, 152],
	"paleturquoise": [175, 238, 238],
	"palevioletred": [219, 112, 147],
	"papayawhip": [255, 239, 213],
	"peachpuff": [255, 218, 185],
	"peru": [205, 133, 63],
	"pink": [255, 192, 203],
	"plum": [221, 160, 221],
	"powderblue": [176, 224, 230],
	"purple": [128, 0, 128],
	"rebeccapurple": [102, 51, 153],
	"red": [255, 0, 0],
	"rosybrown": [188, 143, 143],
	"royalblue": [65, 105, 225],
	"saddlebrown": [139, 69, 19],
	"salmon": [250, 128, 114],
	"sandybrown": [244, 164, 96],
	"seagreen": [46, 139, 87],
	"seashell": [255, 245, 238],
	"sienna": [160, 82, 45],
	"silver": [192, 192, 192],
	"skyblue": [135, 206, 235],
	"slateblue": [106, 90, 205],
	"slategray": [112, 128, 144],
	"slategrey": [112, 128, 144],
	"snow": [255, 250, 250],
	"springgreen": [0, 255, 127],
	"steelblue": [70, 130, 180],
	"tan": [210, 180, 140],
	"teal": [0, 128, 128],
	"thistle": [216, 191, 216],
	"tomato": [255, 99, 71],
	"turquoise": [64, 224, 208],
	"violet": [238, 130, 238],
	"wheat": [245, 222, 179],
	"white": [255, 255, 255],
	"whitesmoke": [245, 245, 245],
	"yellow": [255, 255, 0],
	"yellowgreen": [154, 205, 50]
};

/* MIT license */

/* eslint-disable no-mixed-operators */
const cssKeywords = colorName;

// NOTE: conversions should only return primitive values (i.e. arrays, or
//       values that give correct `typeof` results).
//       do not use box values types (i.e. Number(), String(), etc.)

const reverseKeywords = {};
for (const key of Object.keys(cssKeywords)) {
	reverseKeywords[cssKeywords[key]] = key;
}

const convert$2 = {
	rgb: {channels: 3, labels: 'rgb'},
	hsl: {channels: 3, labels: 'hsl'},
	hsv: {channels: 3, labels: 'hsv'},
	hwb: {channels: 3, labels: 'hwb'},
	cmyk: {channels: 4, labels: 'cmyk'},
	xyz: {channels: 3, labels: 'xyz'},
	lab: {channels: 3, labels: 'lab'},
	lch: {channels: 3, labels: 'lch'},
	hex: {channels: 1, labels: ['hex']},
	keyword: {channels: 1, labels: ['keyword']},
	ansi16: {channels: 1, labels: ['ansi16']},
	ansi256: {channels: 1, labels: ['ansi256']},
	hcg: {channels: 3, labels: ['h', 'c', 'g']},
	apple: {channels: 3, labels: ['r16', 'g16', 'b16']},
	gray: {channels: 1, labels: ['gray']}
};

var conversions$2 = convert$2;

// Hide .channels and .labels properties
for (const model of Object.keys(convert$2)) {
	if (!('channels' in convert$2[model])) {
		throw new Error('missing channels property: ' + model);
	}

	if (!('labels' in convert$2[model])) {
		throw new Error('missing channel labels property: ' + model);
	}

	if (convert$2[model].labels.length !== convert$2[model].channels) {
		throw new Error('channel and label counts mismatch: ' + model);
	}

	const {channels, labels} = convert$2[model];
	delete convert$2[model].channels;
	delete convert$2[model].labels;
	Object.defineProperty(convert$2[model], 'channels', {value: channels});
	Object.defineProperty(convert$2[model], 'labels', {value: labels});
}

convert$2.rgb.hsl = function (rgb) {
	const r = rgb[0] / 255;
	const g = rgb[1] / 255;
	const b = rgb[2] / 255;
	const min = Math.min(r, g, b);
	const max = Math.max(r, g, b);
	const delta = max - min;
	let h;
	let s;

	if (max === min) {
		h = 0;
	} else if (r === max) {
		h = (g - b) / delta;
	} else if (g === max) {
		h = 2 + (b - r) / delta;
	} else if (b === max) {
		h = 4 + (r - g) / delta;
	}

	h = Math.min(h * 60, 360);

	if (h < 0) {
		h += 360;
	}

	const l = (min + max) / 2;

	if (max === min) {
		s = 0;
	} else if (l <= 0.5) {
		s = delta / (max + min);
	} else {
		s = delta / (2 - max - min);
	}

	return [h, s * 100, l * 100];
};

convert$2.rgb.hsv = function (rgb) {
	let rdif;
	let gdif;
	let bdif;
	let h;
	let s;

	const r = rgb[0] / 255;
	const g = rgb[1] / 255;
	const b = rgb[2] / 255;
	const v = Math.max(r, g, b);
	const diff = v - Math.min(r, g, b);
	const diffc = function (c) {
		return (v - c) / 6 / diff + 1 / 2;
	};

	if (diff === 0) {
		h = 0;
		s = 0;
	} else {
		s = diff / v;
		rdif = diffc(r);
		gdif = diffc(g);
		bdif = diffc(b);

		if (r === v) {
			h = bdif - gdif;
		} else if (g === v) {
			h = (1 / 3) + rdif - bdif;
		} else if (b === v) {
			h = (2 / 3) + gdif - rdif;
		}

		if (h < 0) {
			h += 1;
		} else if (h > 1) {
			h -= 1;
		}
	}

	return [
		h * 360,
		s * 100,
		v * 100
	];
};

convert$2.rgb.hwb = function (rgb) {
	const r = rgb[0];
	const g = rgb[1];
	let b = rgb[2];
	const h = convert$2.rgb.hsl(rgb)[0];
	const w = 1 / 255 * Math.min(r, Math.min(g, b));

	b = 1 - 1 / 255 * Math.max(r, Math.max(g, b));

	return [h, w * 100, b * 100];
};

convert$2.rgb.cmyk = function (rgb) {
	const r = rgb[0] / 255;
	const g = rgb[1] / 255;
	const b = rgb[2] / 255;

	const k = Math.min(1 - r, 1 - g, 1 - b);
	const c = (1 - r - k) / (1 - k) || 0;
	const m = (1 - g - k) / (1 - k) || 0;
	const y = (1 - b - k) / (1 - k) || 0;

	return [c * 100, m * 100, y * 100, k * 100];
};

function comparativeDistance(x, y) {
	/*
		See https://en.m.wikipedia.org/wiki/Euclidean_distance#Squared_Euclidean_distance
	*/
	return (
		((x[0] - y[0]) ** 2) +
		((x[1] - y[1]) ** 2) +
		((x[2] - y[2]) ** 2)
	);
}

convert$2.rgb.keyword = function (rgb) {
	const reversed = reverseKeywords[rgb];
	if (reversed) {
		return reversed;
	}

	let currentClosestDistance = Infinity;
	let currentClosestKeyword;

	for (const keyword of Object.keys(cssKeywords)) {
		const value = cssKeywords[keyword];

		// Compute comparative distance
		const distance = comparativeDistance(rgb, value);

		// Check if its less, if so set as closest
		if (distance < currentClosestDistance) {
			currentClosestDistance = distance;
			currentClosestKeyword = keyword;
		}
	}

	return currentClosestKeyword;
};

convert$2.keyword.rgb = function (keyword) {
	return cssKeywords[keyword];
};

convert$2.rgb.xyz = function (rgb) {
	let r = rgb[0] / 255;
	let g = rgb[1] / 255;
	let b = rgb[2] / 255;

	// Assume sRGB
	r = r > 0.04045 ? (((r + 0.055) / 1.055) ** 2.4) : (r / 12.92);
	g = g > 0.04045 ? (((g + 0.055) / 1.055) ** 2.4) : (g / 12.92);
	b = b > 0.04045 ? (((b + 0.055) / 1.055) ** 2.4) : (b / 12.92);

	const x = (r * 0.4124) + (g * 0.3576) + (b * 0.1805);
	const y = (r * 0.2126) + (g * 0.7152) + (b * 0.0722);
	const z = (r * 0.0193) + (g * 0.1192) + (b * 0.9505);

	return [x * 100, y * 100, z * 100];
};

convert$2.rgb.lab = function (rgb) {
	const xyz = convert$2.rgb.xyz(rgb);
	let x = xyz[0];
	let y = xyz[1];
	let z = xyz[2];

	x /= 95.047;
	y /= 100;
	z /= 108.883;

	x = x > 0.008856 ? (x ** (1 / 3)) : (7.787 * x) + (16 / 116);
	y = y > 0.008856 ? (y ** (1 / 3)) : (7.787 * y) + (16 / 116);
	z = z > 0.008856 ? (z ** (1 / 3)) : (7.787 * z) + (16 / 116);

	const l = (116 * y) - 16;
	const a = 500 * (x - y);
	const b = 200 * (y - z);

	return [l, a, b];
};

convert$2.hsl.rgb = function (hsl) {
	const h = hsl[0] / 360;
	const s = hsl[1] / 100;
	const l = hsl[2] / 100;
	let t2;
	let t3;
	let val;

	if (s === 0) {
		val = l * 255;
		return [val, val, val];
	}

	if (l < 0.5) {
		t2 = l * (1 + s);
	} else {
		t2 = l + s - l * s;
	}

	const t1 = 2 * l - t2;

	const rgb = [0, 0, 0];
	for (let i = 0; i < 3; i++) {
		t3 = h + 1 / 3 * -(i - 1);
		if (t3 < 0) {
			t3++;
		}

		if (t3 > 1) {
			t3--;
		}

		if (6 * t3 < 1) {
			val = t1 + (t2 - t1) * 6 * t3;
		} else if (2 * t3 < 1) {
			val = t2;
		} else if (3 * t3 < 2) {
			val = t1 + (t2 - t1) * (2 / 3 - t3) * 6;
		} else {
			val = t1;
		}

		rgb[i] = val * 255;
	}

	return rgb;
};

convert$2.hsl.hsv = function (hsl) {
	const h = hsl[0];
	let s = hsl[1] / 100;
	let l = hsl[2] / 100;
	let smin = s;
	const lmin = Math.max(l, 0.01);

	l *= 2;
	s *= (l <= 1) ? l : 2 - l;
	smin *= lmin <= 1 ? lmin : 2 - lmin;
	const v = (l + s) / 2;
	const sv = l === 0 ? (2 * smin) / (lmin + smin) : (2 * s) / (l + s);

	return [h, sv * 100, v * 100];
};

convert$2.hsv.rgb = function (hsv) {
	const h = hsv[0] / 60;
	const s = hsv[1] / 100;
	let v = hsv[2] / 100;
	const hi = Math.floor(h) % 6;

	const f = h - Math.floor(h);
	const p = 255 * v * (1 - s);
	const q = 255 * v * (1 - (s * f));
	const t = 255 * v * (1 - (s * (1 - f)));
	v *= 255;

	switch (hi) {
		case 0:
			return [v, t, p];
		case 1:
			return [q, v, p];
		case 2:
			return [p, v, t];
		case 3:
			return [p, q, v];
		case 4:
			return [t, p, v];
		case 5:
			return [v, p, q];
	}
};

convert$2.hsv.hsl = function (hsv) {
	const h = hsv[0];
	const s = hsv[1] / 100;
	const v = hsv[2] / 100;
	const vmin = Math.max(v, 0.01);
	let sl;
	let l;

	l = (2 - s) * v;
	const lmin = (2 - s) * vmin;
	sl = s * vmin;
	sl /= (lmin <= 1) ? lmin : 2 - lmin;
	sl = sl || 0;
	l /= 2;

	return [h, sl * 100, l * 100];
};

// http://dev.w3.org/csswg/css-color/#hwb-to-rgb
convert$2.hwb.rgb = function (hwb) {
	const h = hwb[0] / 360;
	let wh = hwb[1] / 100;
	let bl = hwb[2] / 100;
	const ratio = wh + bl;
	let f;

	// Wh + bl cant be > 1
	if (ratio > 1) {
		wh /= ratio;
		bl /= ratio;
	}

	const i = Math.floor(6 * h);
	const v = 1 - bl;
	f = 6 * h - i;

	if ((i & 0x01) !== 0) {
		f = 1 - f;
	}

	const n = wh + f * (v - wh); // Linear interpolation

	let r;
	let g;
	let b;
	/* eslint-disable max-statements-per-line,no-multi-spaces */
	switch (i) {
		default:
		case 6:
		case 0: r = v;  g = n;  b = wh; break;
		case 1: r = n;  g = v;  b = wh; break;
		case 2: r = wh; g = v;  b = n; break;
		case 3: r = wh; g = n;  b = v; break;
		case 4: r = n;  g = wh; b = v; break;
		case 5: r = v;  g = wh; b = n; break;
	}
	/* eslint-enable max-statements-per-line,no-multi-spaces */

	return [r * 255, g * 255, b * 255];
};

convert$2.cmyk.rgb = function (cmyk) {
	const c = cmyk[0] / 100;
	const m = cmyk[1] / 100;
	const y = cmyk[2] / 100;
	const k = cmyk[3] / 100;

	const r = 1 - Math.min(1, c * (1 - k) + k);
	const g = 1 - Math.min(1, m * (1 - k) + k);
	const b = 1 - Math.min(1, y * (1 - k) + k);

	return [r * 255, g * 255, b * 255];
};

convert$2.xyz.rgb = function (xyz) {
	const x = xyz[0] / 100;
	const y = xyz[1] / 100;
	const z = xyz[2] / 100;
	let r;
	let g;
	let b;

	r = (x * 3.2406) + (y * -1.5372) + (z * -0.4986);
	g = (x * -0.9689) + (y * 1.8758) + (z * 0.0415);
	b = (x * 0.0557) + (y * -0.2040) + (z * 1.0570);

	// Assume sRGB
	r = r > 0.0031308
		? ((1.055 * (r ** (1.0 / 2.4))) - 0.055)
		: r * 12.92;

	g = g > 0.0031308
		? ((1.055 * (g ** (1.0 / 2.4))) - 0.055)
		: g * 12.92;

	b = b > 0.0031308
		? ((1.055 * (b ** (1.0 / 2.4))) - 0.055)
		: b * 12.92;

	r = Math.min(Math.max(0, r), 1);
	g = Math.min(Math.max(0, g), 1);
	b = Math.min(Math.max(0, b), 1);

	return [r * 255, g * 255, b * 255];
};

convert$2.xyz.lab = function (xyz) {
	let x = xyz[0];
	let y = xyz[1];
	let z = xyz[2];

	x /= 95.047;
	y /= 100;
	z /= 108.883;

	x = x > 0.008856 ? (x ** (1 / 3)) : (7.787 * x) + (16 / 116);
	y = y > 0.008856 ? (y ** (1 / 3)) : (7.787 * y) + (16 / 116);
	z = z > 0.008856 ? (z ** (1 / 3)) : (7.787 * z) + (16 / 116);

	const l = (116 * y) - 16;
	const a = 500 * (x - y);
	const b = 200 * (y - z);

	return [l, a, b];
};

convert$2.lab.xyz = function (lab) {
	const l = lab[0];
	const a = lab[1];
	const b = lab[2];
	let x;
	let y;
	let z;

	y = (l + 16) / 116;
	x = a / 500 + y;
	z = y - b / 200;

	const y2 = y ** 3;
	const x2 = x ** 3;
	const z2 = z ** 3;
	y = y2 > 0.008856 ? y2 : (y - 16 / 116) / 7.787;
	x = x2 > 0.008856 ? x2 : (x - 16 / 116) / 7.787;
	z = z2 > 0.008856 ? z2 : (z - 16 / 116) / 7.787;

	x *= 95.047;
	y *= 100;
	z *= 108.883;

	return [x, y, z];
};

convert$2.lab.lch = function (lab) {
	const l = lab[0];
	const a = lab[1];
	const b = lab[2];
	let h;

	const hr = Math.atan2(b, a);
	h = hr * 360 / 2 / Math.PI;

	if (h < 0) {
		h += 360;
	}

	const c = Math.sqrt(a * a + b * b);

	return [l, c, h];
};

convert$2.lch.lab = function (lch) {
	const l = lch[0];
	const c = lch[1];
	const h = lch[2];

	const hr = h / 360 * 2 * Math.PI;
	const a = c * Math.cos(hr);
	const b = c * Math.sin(hr);

	return [l, a, b];
};

convert$2.rgb.ansi16 = function (args, saturation = null) {
	const [r, g, b] = args;
	let value = saturation === null ? convert$2.rgb.hsv(args)[2] : saturation; // Hsv -> ansi16 optimization

	value = Math.round(value / 50);

	if (value === 0) {
		return 30;
	}

	let ansi = 30
		+ ((Math.round(b / 255) << 2)
		| (Math.round(g / 255) << 1)
		| Math.round(r / 255));

	if (value === 2) {
		ansi += 60;
	}

	return ansi;
};

convert$2.hsv.ansi16 = function (args) {
	// Optimization here; we already know the value and don't need to get
	// it converted for us.
	return convert$2.rgb.ansi16(convert$2.hsv.rgb(args), args[2]);
};

convert$2.rgb.ansi256 = function (args) {
	const r = args[0];
	const g = args[1];
	const b = args[2];

	// We use the extended greyscale palette here, with the exception of
	// black and white. normal palette only has 4 greyscale shades.
	if (r === g && g === b) {
		if (r < 8) {
			return 16;
		}

		if (r > 248) {
			return 231;
		}

		return Math.round(((r - 8) / 247) * 24) + 232;
	}

	const ansi = 16
		+ (36 * Math.round(r / 255 * 5))
		+ (6 * Math.round(g / 255 * 5))
		+ Math.round(b / 255 * 5);

	return ansi;
};

convert$2.ansi16.rgb = function (args) {
	let color = args % 10;

	// Handle greyscale
	if (color === 0 || color === 7) {
		if (args > 50) {
			color += 3.5;
		}

		color = color / 10.5 * 255;

		return [color, color, color];
	}

	const mult = (~~(args > 50) + 1) * 0.5;
	const r = ((color & 1) * mult) * 255;
	const g = (((color >> 1) & 1) * mult) * 255;
	const b = (((color >> 2) & 1) * mult) * 255;

	return [r, g, b];
};

convert$2.ansi256.rgb = function (args) {
	// Handle greyscale
	if (args >= 232) {
		const c = (args - 232) * 10 + 8;
		return [c, c, c];
	}

	args -= 16;

	let rem;
	const r = Math.floor(args / 36) / 5 * 255;
	const g = Math.floor((rem = args % 36) / 6) / 5 * 255;
	const b = (rem % 6) / 5 * 255;

	return [r, g, b];
};

convert$2.rgb.hex = function (args) {
	const integer = ((Math.round(args[0]) & 0xFF) << 16)
		+ ((Math.round(args[1]) & 0xFF) << 8)
		+ (Math.round(args[2]) & 0xFF);

	const string = integer.toString(16).toUpperCase();
	return '000000'.substring(string.length) + string;
};

convert$2.hex.rgb = function (args) {
	const match = args.toString(16).match(/[a-f0-9]{6}|[a-f0-9]{3}/i);
	if (!match) {
		return [0, 0, 0];
	}

	let colorString = match[0];

	if (match[0].length === 3) {
		colorString = colorString.split('').map(char => {
			return char + char;
		}).join('');
	}

	const integer = parseInt(colorString, 16);
	const r = (integer >> 16) & 0xFF;
	const g = (integer >> 8) & 0xFF;
	const b = integer & 0xFF;

	return [r, g, b];
};

convert$2.rgb.hcg = function (rgb) {
	const r = rgb[0] / 255;
	const g = rgb[1] / 255;
	const b = rgb[2] / 255;
	const max = Math.max(Math.max(r, g), b);
	const min = Math.min(Math.min(r, g), b);
	const chroma = (max - min);
	let grayscale;
	let hue;

	if (chroma < 1) {
		grayscale = min / (1 - chroma);
	} else {
		grayscale = 0;
	}

	if (chroma <= 0) {
		hue = 0;
	} else
	if (max === r) {
		hue = ((g - b) / chroma) % 6;
	} else
	if (max === g) {
		hue = 2 + (b - r) / chroma;
	} else {
		hue = 4 + (r - g) / chroma;
	}

	hue /= 6;
	hue %= 1;

	return [hue * 360, chroma * 100, grayscale * 100];
};

convert$2.hsl.hcg = function (hsl) {
	const s = hsl[1] / 100;
	const l = hsl[2] / 100;

	const c = l < 0.5 ? (2.0 * s * l) : (2.0 * s * (1.0 - l));

	let f = 0;
	if (c < 1.0) {
		f = (l - 0.5 * c) / (1.0 - c);
	}

	return [hsl[0], c * 100, f * 100];
};

convert$2.hsv.hcg = function (hsv) {
	const s = hsv[1] / 100;
	const v = hsv[2] / 100;

	const c = s * v;
	let f = 0;

	if (c < 1.0) {
		f = (v - c) / (1 - c);
	}

	return [hsv[0], c * 100, f * 100];
};

convert$2.hcg.rgb = function (hcg) {
	const h = hcg[0] / 360;
	const c = hcg[1] / 100;
	const g = hcg[2] / 100;

	if (c === 0.0) {
		return [g * 255, g * 255, g * 255];
	}

	const pure = [0, 0, 0];
	const hi = (h % 1) * 6;
	const v = hi % 1;
	const w = 1 - v;
	let mg = 0;

	/* eslint-disable max-statements-per-line */
	switch (Math.floor(hi)) {
		case 0:
			pure[0] = 1; pure[1] = v; pure[2] = 0; break;
		case 1:
			pure[0] = w; pure[1] = 1; pure[2] = 0; break;
		case 2:
			pure[0] = 0; pure[1] = 1; pure[2] = v; break;
		case 3:
			pure[0] = 0; pure[1] = w; pure[2] = 1; break;
		case 4:
			pure[0] = v; pure[1] = 0; pure[2] = 1; break;
		default:
			pure[0] = 1; pure[1] = 0; pure[2] = w;
	}
	/* eslint-enable max-statements-per-line */

	mg = (1.0 - c) * g;

	return [
		(c * pure[0] + mg) * 255,
		(c * pure[1] + mg) * 255,
		(c * pure[2] + mg) * 255
	];
};

convert$2.hcg.hsv = function (hcg) {
	const c = hcg[1] / 100;
	const g = hcg[2] / 100;

	const v = c + g * (1.0 - c);
	let f = 0;

	if (v > 0.0) {
		f = c / v;
	}

	return [hcg[0], f * 100, v * 100];
};

convert$2.hcg.hsl = function (hcg) {
	const c = hcg[1] / 100;
	const g = hcg[2] / 100;

	const l = g * (1.0 - c) + 0.5 * c;
	let s = 0;

	if (l > 0.0 && l < 0.5) {
		s = c / (2 * l);
	} else
	if (l >= 0.5 && l < 1.0) {
		s = c / (2 * (1 - l));
	}

	return [hcg[0], s * 100, l * 100];
};

convert$2.hcg.hwb = function (hcg) {
	const c = hcg[1] / 100;
	const g = hcg[2] / 100;
	const v = c + g * (1.0 - c);
	return [hcg[0], (v - c) * 100, (1 - v) * 100];
};

convert$2.hwb.hcg = function (hwb) {
	const w = hwb[1] / 100;
	const b = hwb[2] / 100;
	const v = 1 - b;
	const c = v - w;
	let g = 0;

	if (c < 1) {
		g = (v - c) / (1 - c);
	}

	return [hwb[0], c * 100, g * 100];
};

convert$2.apple.rgb = function (apple) {
	return [(apple[0] / 65535) * 255, (apple[1] / 65535) * 255, (apple[2] / 65535) * 255];
};

convert$2.rgb.apple = function (rgb) {
	return [(rgb[0] / 255) * 65535, (rgb[1] / 255) * 65535, (rgb[2] / 255) * 65535];
};

convert$2.gray.rgb = function (args) {
	return [args[0] / 100 * 255, args[0] / 100 * 255, args[0] / 100 * 255];
};

convert$2.gray.hsl = function (args) {
	return [0, 0, args[0]];
};

convert$2.gray.hsv = convert$2.gray.hsl;

convert$2.gray.hwb = function (gray) {
	return [0, 100, gray[0]];
};

convert$2.gray.cmyk = function (gray) {
	return [0, 0, 0, gray[0]];
};

convert$2.gray.lab = function (gray) {
	return [gray[0], 0, 0];
};

convert$2.gray.hex = function (gray) {
	const val = Math.round(gray[0] / 100 * 255) & 0xFF;
	const integer = (val << 16) + (val << 8) + val;

	const string = integer.toString(16).toUpperCase();
	return '000000'.substring(string.length) + string;
};

convert$2.rgb.gray = function (rgb) {
	const val = (rgb[0] + rgb[1] + rgb[2]) / 3;
	return [val / 255 * 100];
};

const conversions$1 = conversions$2;

/*
	This function routes a model to all other models.

	all functions that are routed have a property `.conversion` attached
	to the returned synthetic function. This property is an array
	of strings, each with the steps in between the 'from' and 'to'
	color models (inclusive).

	conversions that are not possible simply are not included.
*/

function buildGraph() {
	const graph = {};
	// https://jsperf.com/object-keys-vs-for-in-with-closure/3
	const models = Object.keys(conversions$1);

	for (let len = models.length, i = 0; i < len; i++) {
		graph[models[i]] = {
			// http://jsperf.com/1-vs-infinity
			// micro-opt, but this is simple.
			distance: -1,
			parent: null
		};
	}

	return graph;
}

// https://en.wikipedia.org/wiki/Breadth-first_search
function deriveBFS(fromModel) {
	const graph = buildGraph();
	const queue = [fromModel]; // Unshift -> queue -> pop

	graph[fromModel].distance = 0;

	while (queue.length) {
		const current = queue.pop();
		const adjacents = Object.keys(conversions$1[current]);

		for (let len = adjacents.length, i = 0; i < len; i++) {
			const adjacent = adjacents[i];
			const node = graph[adjacent];

			if (node.distance === -1) {
				node.distance = graph[current].distance + 1;
				node.parent = current;
				queue.unshift(adjacent);
			}
		}
	}

	return graph;
}

function link$1(from, to) {
	return function (args) {
		return to(from(args));
	};
}

function wrapConversion(toModel, graph) {
	const path = [graph[toModel].parent, toModel];
	let fn = conversions$1[graph[toModel].parent][toModel];

	let cur = graph[toModel].parent;
	while (graph[cur].parent) {
		path.unshift(graph[cur].parent);
		fn = link$1(conversions$1[graph[cur].parent][cur], fn);
		cur = graph[cur].parent;
	}

	fn.conversion = path;
	return fn;
}

var route$1 = function (fromModel) {
	const graph = deriveBFS(fromModel);
	const conversion = {};

	const models = Object.keys(graph);
	for (let len = models.length, i = 0; i < len; i++) {
		const toModel = models[i];
		const node = graph[toModel];

		if (node.parent === null) {
			// No possible conversion, or this node is the source model.
			continue;
		}

		conversion[toModel] = wrapConversion(toModel, graph);
	}

	return conversion;
};

const conversions = conversions$2;
const route = route$1;

const convert$1 = {};

const models = Object.keys(conversions);

function wrapRaw(fn) {
	const wrappedFn = function (...args) {
		const arg0 = args[0];
		if (arg0 === undefined || arg0 === null) {
			return arg0;
		}

		if (arg0.length > 1) {
			args = arg0;
		}

		return fn(args);
	};

	// Preserve .conversion property if there is one
	if ('conversion' in fn) {
		wrappedFn.conversion = fn.conversion;
	}

	return wrappedFn;
}

function wrapRounded(fn) {
	const wrappedFn = function (...args) {
		const arg0 = args[0];

		if (arg0 === undefined || arg0 === null) {
			return arg0;
		}

		if (arg0.length > 1) {
			args = arg0;
		}

		const result = fn(args);

		// We're assuming the result is an array here.
		// see notice in conversions.js; don't use box types
		// in conversion functions.
		if (typeof result === 'object') {
			for (let len = result.length, i = 0; i < len; i++) {
				result[i] = Math.round(result[i]);
			}
		}

		return result;
	};

	// Preserve .conversion property if there is one
	if ('conversion' in fn) {
		wrappedFn.conversion = fn.conversion;
	}

	return wrappedFn;
}

models.forEach(fromModel => {
	convert$1[fromModel] = {};

	Object.defineProperty(convert$1[fromModel], 'channels', {value: conversions[fromModel].channels});
	Object.defineProperty(convert$1[fromModel], 'labels', {value: conversions[fromModel].labels});

	const routes = route(fromModel);
	const routeModels = Object.keys(routes);

	routeModels.forEach(toModel => {
		const fn = routes[toModel];

		convert$1[fromModel][toModel] = wrapRounded(fn);
		convert$1[fromModel][toModel].raw = wrapRaw(fn);
	});
});

var colorConvert = convert$1;

const colorString = colorStringExports;
const convert = colorConvert;

const skippedModels = [
	// To be honest, I don't really feel like keyword belongs in color convert, but eh.
	'keyword',

	// Gray conflicts with some method names, and has its own method defined.
	'gray',

	// Shouldn't really be in color-convert either...
	'hex',
];

const hashedModelKeys = {};
for (const model of Object.keys(convert)) {
	hashedModelKeys[[...convert[model].labels].sort().join('')] = model;
}

const limiters = {};

function Color(object, model) {
	if (!(this instanceof Color)) {
		return new Color(object, model);
	}

	if (model && model in skippedModels) {
		model = null;
	}

	if (model && !(model in convert)) {
		throw new Error('Unknown model: ' + model);
	}

	let i;
	let channels;

	if (object == null) { // eslint-disable-line no-eq-null,eqeqeq
		this.model = 'rgb';
		this.color = [0, 0, 0];
		this.valpha = 1;
	} else if (object instanceof Color) {
		this.model = object.model;
		this.color = [...object.color];
		this.valpha = object.valpha;
	} else if (typeof object === 'string') {
		const result = colorString.get(object);
		if (result === null) {
			throw new Error('Unable to parse color from string: ' + object);
		}

		this.model = result.model;
		channels = convert[this.model].channels;
		this.color = result.value.slice(0, channels);
		this.valpha = typeof result.value[channels] === 'number' ? result.value[channels] : 1;
	} else if (object.length > 0) {
		this.model = model || 'rgb';
		channels = convert[this.model].channels;
		const newArray = Array.prototype.slice.call(object, 0, channels);
		this.color = zeroArray(newArray, channels);
		this.valpha = typeof object[channels] === 'number' ? object[channels] : 1;
	} else if (typeof object === 'number') {
		// This is always RGB - can be converted later on.
		this.model = 'rgb';
		this.color = [
			(object >> 16) & 0xFF,
			(object >> 8) & 0xFF,
			object & 0xFF,
		];
		this.valpha = 1;
	} else {
		this.valpha = 1;

		const keys = Object.keys(object);
		if ('alpha' in object) {
			keys.splice(keys.indexOf('alpha'), 1);
			this.valpha = typeof object.alpha === 'number' ? object.alpha : 0;
		}

		const hashedKeys = keys.sort().join('');
		if (!(hashedKeys in hashedModelKeys)) {
			throw new Error('Unable to parse color from object: ' + JSON.stringify(object));
		}

		this.model = hashedModelKeys[hashedKeys];

		const {labels} = convert[this.model];
		const color = [];
		for (i = 0; i < labels.length; i++) {
			color.push(object[labels[i]]);
		}

		this.color = zeroArray(color);
	}

	// Perform limitations (clamping, etc.)
	if (limiters[this.model]) {
		channels = convert[this.model].channels;
		for (i = 0; i < channels; i++) {
			const limit = limiters[this.model][i];
			if (limit) {
				this.color[i] = limit(this.color[i]);
			}
		}
	}

	this.valpha = Math.max(0, Math.min(1, this.valpha));

	if (Object.freeze) {
		Object.freeze(this);
	}
}

Color.prototype = {
	toString() {
		return this.string();
	},

	toJSON() {
		return this[this.model]();
	},

	string(places) {
		let self = this.model in colorString.to ? this : this.rgb();
		self = self.round(typeof places === 'number' ? places : 1);
		const args = self.valpha === 1 ? self.color : [...self.color, this.valpha];
		return colorString.to[self.model](args);
	},

	percentString(places) {
		const self = this.rgb().round(typeof places === 'number' ? places : 1);
		const args = self.valpha === 1 ? self.color : [...self.color, this.valpha];
		return colorString.to.rgb.percent(args);
	},

	array() {
		return this.valpha === 1 ? [...this.color] : [...this.color, this.valpha];
	},

	object() {
		const result = {};
		const {channels} = convert[this.model];
		const {labels} = convert[this.model];

		for (let i = 0; i < channels; i++) {
			result[labels[i]] = this.color[i];
		}

		if (this.valpha !== 1) {
			result.alpha = this.valpha;
		}

		return result;
	},

	unitArray() {
		const rgb = this.rgb().color;
		rgb[0] /= 255;
		rgb[1] /= 255;
		rgb[2] /= 255;

		if (this.valpha !== 1) {
			rgb.push(this.valpha);
		}

		return rgb;
	},

	unitObject() {
		const rgb = this.rgb().object();
		rgb.r /= 255;
		rgb.g /= 255;
		rgb.b /= 255;

		if (this.valpha !== 1) {
			rgb.alpha = this.valpha;
		}

		return rgb;
	},

	round(places) {
		places = Math.max(places || 0, 0);
		return new Color([...this.color.map(roundToPlace(places)), this.valpha], this.model);
	},

	alpha(value) {
		if (value !== undefined) {
			return new Color([...this.color, Math.max(0, Math.min(1, value))], this.model);
		}

		return this.valpha;
	},

	// Rgb
	red: getset('rgb', 0, maxfn(255)),
	green: getset('rgb', 1, maxfn(255)),
	blue: getset('rgb', 2, maxfn(255)),

	hue: getset(['hsl', 'hsv', 'hsl', 'hwb', 'hcg'], 0, value => ((value % 360) + 360) % 360),

	saturationl: getset('hsl', 1, maxfn(100)),
	lightness: getset('hsl', 2, maxfn(100)),

	saturationv: getset('hsv', 1, maxfn(100)),
	value: getset('hsv', 2, maxfn(100)),

	chroma: getset('hcg', 1, maxfn(100)),
	gray: getset('hcg', 2, maxfn(100)),

	white: getset('hwb', 1, maxfn(100)),
	wblack: getset('hwb', 2, maxfn(100)),

	cyan: getset('cmyk', 0, maxfn(100)),
	magenta: getset('cmyk', 1, maxfn(100)),
	yellow: getset('cmyk', 2, maxfn(100)),
	black: getset('cmyk', 3, maxfn(100)),

	x: getset('xyz', 0, maxfn(95.047)),
	y: getset('xyz', 1, maxfn(100)),
	z: getset('xyz', 2, maxfn(108.833)),

	l: getset('lab', 0, maxfn(100)),
	a: getset('lab', 1),
	b: getset('lab', 2),

	keyword(value) {
		if (value !== undefined) {
			return new Color(value);
		}

		return convert[this.model].keyword(this.color);
	},

	hex(value) {
		if (value !== undefined) {
			return new Color(value);
		}

		return colorString.to.hex(this.rgb().round().color);
	},

	hexa(value) {
		if (value !== undefined) {
			return new Color(value);
		}

		const rgbArray = this.rgb().round().color;

		let alphaHex = Math.round(this.valpha * 255).toString(16).toUpperCase();
		if (alphaHex.length === 1) {
			alphaHex = '0' + alphaHex;
		}

		return colorString.to.hex(rgbArray) + alphaHex;
	},

	rgbNumber() {
		const rgb = this.rgb().color;
		return ((rgb[0] & 0xFF) << 16) | ((rgb[1] & 0xFF) << 8) | (rgb[2] & 0xFF);
	},

	luminosity() {
		// http://www.w3.org/TR/WCAG20/#relativeluminancedef
		const rgb = this.rgb().color;

		const lum = [];
		for (const [i, element] of rgb.entries()) {
			const chan = element / 255;
			lum[i] = (chan <= 0.04045) ? chan / 12.92 : ((chan + 0.055) / 1.055) ** 2.4;
		}

		return 0.2126 * lum[0] + 0.7152 * lum[1] + 0.0722 * lum[2];
	},

	contrast(color2) {
		// http://www.w3.org/TR/WCAG20/#contrast-ratiodef
		const lum1 = this.luminosity();
		const lum2 = color2.luminosity();

		if (lum1 > lum2) {
			return (lum1 + 0.05) / (lum2 + 0.05);
		}

		return (lum2 + 0.05) / (lum1 + 0.05);
	},

	level(color2) {
		// https://www.w3.org/TR/WCAG/#contrast-enhanced
		const contrastRatio = this.contrast(color2);
		if (contrastRatio >= 7) {
			return 'AAA';
		}

		return (contrastRatio >= 4.5) ? 'AA' : '';
	},

	isDark() {
		// YIQ equation from http://24ways.org/2010/calculating-color-contrast
		const rgb = this.rgb().color;
		const yiq = (rgb[0] * 2126 + rgb[1] * 7152 + rgb[2] * 722) / 10000;
		return yiq < 128;
	},

	isLight() {
		return !this.isDark();
	},

	negate() {
		const rgb = this.rgb();
		for (let i = 0; i < 3; i++) {
			rgb.color[i] = 255 - rgb.color[i];
		}

		return rgb;
	},

	lighten(ratio) {
		const hsl = this.hsl();
		hsl.color[2] += hsl.color[2] * ratio;
		return hsl;
	},

	darken(ratio) {
		const hsl = this.hsl();
		hsl.color[2] -= hsl.color[2] * ratio;
		return hsl;
	},

	saturate(ratio) {
		const hsl = this.hsl();
		hsl.color[1] += hsl.color[1] * ratio;
		return hsl;
	},

	desaturate(ratio) {
		const hsl = this.hsl();
		hsl.color[1] -= hsl.color[1] * ratio;
		return hsl;
	},

	whiten(ratio) {
		const hwb = this.hwb();
		hwb.color[1] += hwb.color[1] * ratio;
		return hwb;
	},

	blacken(ratio) {
		const hwb = this.hwb();
		hwb.color[2] += hwb.color[2] * ratio;
		return hwb;
	},

	grayscale() {
		// http://en.wikipedia.org/wiki/Grayscale#Converting_color_to_grayscale
		const rgb = this.rgb().color;
		const value = rgb[0] * 0.3 + rgb[1] * 0.59 + rgb[2] * 0.11;
		return Color.rgb(value, value, value);
	},

	fade(ratio) {
		return this.alpha(this.valpha - (this.valpha * ratio));
	},

	opaquer(ratio) {
		return this.alpha(this.valpha + (this.valpha * ratio));
	},

	rotate(degrees) {
		const hsl = this.hsl();
		let hue = hsl.color[0];
		hue = (hue + degrees) % 360;
		hue = hue < 0 ? 360 + hue : hue;
		hsl.color[0] = hue;
		return hsl;
	},

	mix(mixinColor, weight) {
		// Ported from sass implementation in C
		// https://github.com/sass/libsass/blob/0e6b4a2850092356aa3ece07c6b249f0221caced/functions.cpp#L209
		if (!mixinColor || !mixinColor.rgb) {
			throw new Error('Argument to "mix" was not a Color instance, but rather an instance of ' + typeof mixinColor);
		}

		const color1 = mixinColor.rgb();
		const color2 = this.rgb();
		const p = weight === undefined ? 0.5 : weight;

		const w = 2 * p - 1;
		const a = color1.alpha() - color2.alpha();

		const w1 = (((w * a === -1) ? w : (w + a) / (1 + w * a)) + 1) / 2;
		const w2 = 1 - w1;

		return Color.rgb(
			w1 * color1.red() + w2 * color2.red(),
			w1 * color1.green() + w2 * color2.green(),
			w1 * color1.blue() + w2 * color2.blue(),
			color1.alpha() * p + color2.alpha() * (1 - p));
	},
};

// Model conversion methods and static constructors
for (const model of Object.keys(convert)) {
	if (skippedModels.includes(model)) {
		continue;
	}

	const {channels} = convert[model];

	// Conversion methods
	Color.prototype[model] = function (...args) {
		if (this.model === model) {
			return new Color(this);
		}

		if (args.length > 0) {
			return new Color(args, model);
		}

		return new Color([...assertArray(convert[this.model][model].raw(this.color)), this.valpha], model);
	};

	// 'static' construction methods
	Color[model] = function (...args) {
		let color = args[0];
		if (typeof color === 'number') {
			color = zeroArray(args, channels);
		}

		return new Color(color, model);
	};
}

function roundTo(number, places) {
	return Number(number.toFixed(places));
}

function roundToPlace(places) {
	return function (number) {
		return roundTo(number, places);
	};
}

function getset(model, channel, modifier) {
	model = Array.isArray(model) ? model : [model];

	for (const m of model) {
		(limiters[m] || (limiters[m] = []))[channel] = modifier;
	}

	model = model[0];

	return function (value) {
		let result;

		if (value !== undefined) {
			if (modifier) {
				value = modifier(value);
			}

			result = this[model]();
			result.color[channel] = value;
			return result;
		}

		result = this[model]().color[channel];
		if (modifier) {
			result = modifier(result);
		}

		return result;
	};
}

function maxfn(max) {
	return function (v) {
		return Math.max(0, Math.min(max, v));
	};
}

function assertArray(value) {
	return Array.isArray(value) ? value : [value];
}

function zeroArray(array, length) {
	for (let i = 0; i < length; i++) {
		if (typeof array[i] !== 'number') {
			array[i] = 0;
		}
	}

	return array;
}

var color = Color;

var Color$1 = /*@__PURE__*/getDefaultExportFromCjs$1(color);

// The minimum allowed contrast for accessibility rules.
const MIN_CONTRAST = 4.5;
/**
 * Converts the given hexadecimal formatted color string into an array with the red, blue and green components
 * separated. This function requires the string to be either a 3 or 6 digit hexadecimal code with a leading hash
 * mark. It does not validate that the string is in the proper format.
 */
function hexCodeToRGB(color) {
    if (color.length === 7) {
        const red = color.substring(1, 3);
        const blue = color.substring(3, 5);
        const green = color.substring(5, 7);
        return [parseInt(red, 16), parseInt(blue, 16), parseInt(green, 16)];
    }
    if (color.length === 4) {
        const red = color.substring(1, 2);
        const blue = color.substring(2, 3);
        const green = color.substring(3, 4);
        return [parseInt(red + red, 16), parseInt(blue + blue, 16), parseInt(green + green, 16)];
    }
    consoleError(`Unsupported color code: "${color}"`);
    return [0, 0, 0];
}
/**
 * Calculates the contrast ratio between the two colors. Contrast values can range from 1 to 21. A value of 4.5 is
 * considered the minimum between a foreground and background color to meet accessibility guidelines.
 *
 * @see https://www.w3.org/TR/WCAG20/#contrast-ratiodef
 */
function calculateContrast(color1, color2) {
    const rgb1 = hexCodeToRGB(color1);
    const rgb2 = hexCodeToRGB(color2);
    const luminance1 = calculateRelativeLuminance(rgb1);
    const luminance2 = calculateRelativeLuminance(rgb2);
    let contrast;
    if (luminance1 > luminance2) {
        contrast = (luminance1 + 0.05) / (luminance2 + 0.05);
    }
    else {
        contrast = (luminance2 + 0.05) / (luminance1 + 0.05);
    }
    return contrast;
}
/**
 * Calculates the relative luminance of the given color (provided as separate RGB values).
 *
 * @see https://www.w3.org/TR/WCAG20/#relativeluminancedef
 */
function calculateRelativeLuminance([r8, g8, b8]) {
    const rRGB = r8 / 255;
    const gRGB = g8 / 255;
    const bRGB = b8 / 255;
    const R = rRGB <= 0.03928 ? rRGB / 12.92 : ((rRGB + 0.055) / 1.055) ** 2.4;
    const G = gRGB <= 0.03928 ? gRGB / 12.92 : ((gRGB + 0.055) / 1.055) ** 2.4;
    const B = bRGB <= 0.03928 ? bRGB / 12.92 : ((bRGB + 0.055) / 1.055) ** 2.4;
    const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;
    return luminance;
}
/**
 * Returns either a white or a black color to use as the text color on a background of the given background color.
 * This will ensure that the text color chosen is of sufficient contrast.
 */
function whiteOrBlackText(background) {
    return calculateContrast(gray100, background) >= MIN_CONTRAST ? gray100 : white;
}
/**
 * Adjust a given color's lightness by a specified number of percentage points.
 */
function adjustLightness(token, shift) {
    const original = Color$1(token).hsl().object();
    return Color$1({ ...original, l: original.l + shift })
        .round()
        .hex()
        .toLowerCase();
}

// The prefix that is added to each CSS variable in the application.
const CSS_VAR_PREFIX = '--cds-';
const CSS_CHAT_PREFIX = 'chat-';
// Regex to determine a 3 or 6 digit hexadecimal color
const HEXADECIMAL_REGEX = /#([a-f0-9]{3}){1,2}\b/i;
// Some carbon colors need to be overridden in order to support our theming options. Map the overrides for the light
// themes here.
const INTERNAL_OVERRIDES_LIGHT_THEME_MAP = {
    // In light themes make the quick action chat buttons black since the default link blue may not match the users theme.
    '$chat-button': '#000000',
    '$chat-button-text-hover': '#525252',
};
// Some carbon colors need to be overridden in order to support our theming options. Map the overrides for the dark
// themes here.
const INTERNAL_OVERRIDES_DARK_THEME_MAP = {
    // In dark themes make the quick action chat buttons white since the default light link blue may not match the users
    // theme.
    '$chat-button': '#ffffff',
    '$chat-button-text-hover': '#f4f4f4',
};
/**
 * Converts the given map of CSS variable into a string that is formatted for inserting into a style tag.
 */
function convertCSSVariablesToString(cssVariables, namespace, useShadowRoot) {
    // First convert the variables to a CSS string.
    const pieces = Object.keys(cssVariables).map(key => {
        const value = cssVariables[key];
        if (value === undefined) {
            return '';
        }
        const fullName = key.startsWith('$')
            ? `${CSS_VAR_PREFIX}${key.replace(/^\$/, '')}`
            : `${CSS_VAR_PREFIX}${CSS_CHAT_PREFIX}${key}`;
        return `${fullName}:${value};`;
    });
    let cssVariablesString = '';
    const allValues = pieces.join('');
    const prefix = useShadowRoot ? '' : `#WACContainer${namespace.suffix}`;
    if (allValues.length > 0) {
        // Including a namespace in the styles allows us to support multiple widgets on the same page without their styles
        // conflicting.
        const rule = `${prefix}.WACContainer .cds--white, ${prefix}.WACContainer .cds--g10, ${prefix}.WACContainer .cds--g90, ${prefix}.WACContainer .cds--g100`;
        cssVariablesString = `${rule}${useShadowRoot ? `, :host` : ''}{${allValues}}`;
    }
    return cssVariablesString;
}
/**
 * This will generate a set of CSS variables that will overwrite the default values based on the customizations that
 * are specified in the given remote config.
 *
 * @param remoteStyles The set of customized styles.
 * @param carbonTheme The Carbon theme that is being used.
 */
function remoteStylesToCSSVars(remoteStyles, carbonTheme) {
    const cssOverrides = {};
    const primaryColor = remoteStyles['BASE-primary-color'];
    const secondaryColor = remoteStyles['BASE-secondary-color'];
    const accentColor = remoteStyles['BASE-accent-color'];
    if (!IS_MOBILE) {
        if (remoteStyles['BASE-height']) {
            // We need to change the max-height value in order for web chat's height to change.
            cssOverrides['BASE-max-height'] = remoteStyles['BASE-height'];
        }
        if (remoteStyles['BASE-width']) {
            cssOverrides['BASE-width'] = remoteStyles['BASE-width'];
        }
    }
    if (primaryColor) {
        cssOverrides['PRIMARY-color'] = primaryColor;
        cssOverrides['PRIMARY-color-text'] = whiteOrBlackText(primaryColor);
        cssOverrides['PRIMARY-color-hover'] = adjustLightness(primaryColor, -8);
        cssOverrides['PRIMARY-color-active'] = adjustLightness(primaryColor, -10);
        // We need to calculate the focus color for the buttons in the header. The focus color for the white and g10
        // themes is the same as the accent color. For g90 and g100, the focus color is white.
        const useAccentColor = accentColor || blue60;
        const tryFocusColor = carbonTheme === CarbonTheme.G90 || carbonTheme === CarbonTheme.G100 ? white : useAccentColor;
        let useFocusColor;
        if (calculateContrast(primaryColor, tryFocusColor) >= MIN_CONTRAST) {
            // The default color works fine.
            useFocusColor = tryFocusColor;
        }
        else if (tryFocusColor !== useAccentColor && calculateContrast(primaryColor, useAccentColor) >= MIN_CONTRAST) {
            // The default doesn't work so let's try the accent.
            useFocusColor = useAccentColor;
        }
        else if (tryFocusColor !== white && calculateContrast(primaryColor, white) >= MIN_CONTRAST) {
            // The accent doesn't work, so let's try white.
            useFocusColor = white;
        }
        else {
            // If white doesn't work, then gray100 will.
            useFocusColor = gray100;
        }
        if (useFocusColor !== tryFocusColor) {
            cssOverrides['PRIMARY-color-focus'] = useFocusColor;
        }
    }
    if (secondaryColor) {
        cssOverrides['SECONDARY-color'] = secondaryColor;
        cssOverrides['SECONDARY-color-text'] = whiteOrBlackText(secondaryColor);
    }
    else if (carbonTheme === CarbonTheme.G90 || carbonTheme === CarbonTheme.G100) {
        // We don't like the default Carbon color for the sent text bubble in the g90 and g100 color themes.
        cssOverrides['SECONDARY-color'] = `var(${CSS_VAR_PREFIX}layer-02)`;
        cssOverrides['SECONDARY-color-text'] = `var(${CSS_VAR_PREFIX}text-primary);`;
    }
    if (accentColor) {
        const colorMap = ACCENT_COLOR_MAPS[carbonTheme];
        // The custom color basically corresponds to Blue 60 are we will replace all the occurrences of Blue 60 with
        // that custom color. For the other shades of blue, we will calculate a relative color from the custom color and
        // replace those colors with this calculated color.
        const accentBlue20 = adjustLightness(accentColor, 40);
        const accentBlue60Hover = adjustLightness(accentColor, -8);
        const accentBlue80 = adjustLightness(accentColor, -20);
        fillValues(cssOverrides, colorMap.blue20, accentBlue20);
        fillValues(cssOverrides, colorMap.blue60, accentColor);
        fillValues(cssOverrides, colorMap.blue60Hover, accentBlue60Hover);
        fillValues(cssOverrides, colorMap.blue80, accentBlue80);
        // Update the launcher variables with the appropriate accent colors for the button states.
        cssOverrides['LAUNCHER-color-background'] = accentColor;
        cssOverrides['LAUNCHER-color-background-hover'] = accentBlue60Hover;
        cssOverrides['LAUNCHER-color-background-active'] = accentBlue80;
        cssOverrides['LAUNCHER-EXPANDED-MESSAGE-color-background'] = accentColor;
        cssOverrides['LAUNCHER-EXPANDED-MESSAGE-color-hover'] = accentBlue60Hover;
        cssOverrides['LAUNCHER-EXPANDED-MESSAGE-color-active'] = accentBlue80;
        cssOverrides['ACCENT-color'] = accentColor;
        const accentColorRGB = hexCodeToRGB(accentColor);
        cssOverrides['ACCENT-color-r'] = accentColorRGB[0].toString();
        cssOverrides['ACCENT-color-g'] = accentColorRGB[1].toString();
        cssOverrides['ACCENT-color-b'] = accentColorRGB[2].toString();
        // The ghost button text color defaults to $link-01 but since we've decided to not override $link-01, we need to
        // change the color of the ghost button separately.
        cssOverrides['ACCENT-color-ghost-text'] = accentColor;
        // Now figure out what font color would go with a background color that's "Blue 60".
        const accentColorBW = whiteOrBlackText(accentColor);
        cssOverrides['ACCENT-color-text'] = accentColorBW;
        cssOverrides['ACCENT-color-background-hover'] = accentBlue60Hover;
        cssOverrides['ACCENT-color-background-active'] = accentBlue80;
        // Update the launcher variables with the appropriate BW colors.
        cssOverrides['LAUNCHER-color-focus-border'] = accentColorBW;
        cssOverrides['LAUNCHER-color-avatar'] = accentColorBW;
        cssOverrides['LAUNCHER-EXPANDED-MESSAGE-color-text'] = accentColorBW;
        cssOverrides['LAUNCHER-EXPANDED-MESSAGE-color-focus-border'] = accentColorBW;
        cssOverrides['LAUNCHER-MOBILE-color-text'] = accentColorBW;
        // This color is either black or white and is based on the contrast difference with the accent color. Its primary use
        // is the color of button on top of the accent color.
        cssOverrides['ACCENT-color-bw'] = accentColorBW;
        // When ACCENT-color-bw is used as a button color we need a hover and active color.
        cssOverrides['ACCENT-color-bw-hover'] = adjustLightness(accentColorBW, -8);
        // The active color is a little darker than the hover color.
        cssOverrides['ACCENT-color-bw-active'] = adjustLightness(accentColorBW, -10);
        // Also need an inverse of ACCENT-color-bw so that we can have accessible text within our bw buttons.
        cssOverrides['ACCENT-color-bw-inverse'] = accentColorBW === gray100 ? white : gray100;
        // Need a slightly more gray version of the bw accent color.
        cssOverrides['ACCENT-color-bw-gray'] = accentColorBW === gray100 ? gray80 : gray10;
        // A slightly darker or lighter accent color (darker if the accent color was already dark, lighter if it was already
        // light). Used for the launcher experiments where we only have one accent color but really need two.
        cssOverrides['ACCENT-color-pastel'] =
            accentColorBW === gray100 ? adjustLightness(accentColor, 20) : adjustLightness(accentColor, -15);
    }
    return cssOverrides;
}
/**
 * This structure maintains a map for each of the named colors in Carbon that are a shade of blue. When the tooling
 * specifies a custom accent color, we will replace all occurrences of Blue 60 in each of the Carbon color themes
 * with that accent color as well as appropriate adjustments of the accent color for each of the shades of blue.
 *
 * Note: to preserve the color of links as their default Carbon colors, $link-01 and $inverse-link are excluded from
 * these maps.
 */
const ACCENT_COLOR_MAPS = {
    white: {
        blue20: ['$highlight'],
        blue60: [
            '$background-brand',
            '$interactive',
            '$border-interactive',
            '$button-primary',
            '$button-tertiary',
            '$icon-interactive',
            '$focus',
        ],
        blue60Hover: ['$button-primary-hover', '$button-tertiary-hover'],
        blue80: ['$button-primary-active', '$button-tertiary-active'],
    },
    g10: {
        blue20: ['$highlight'],
        blue60: [
            '$background-brand',
            '$interactive',
            '$border-interactive',
            '$button-primary',
            '$button-tertiary',
            '$icon-interactive',
            '$focus',
        ],
        blue60Hover: ['$button-primary-hover', '$button-tertiary-hover'],
        blue80: ['$button-primary-active', '$button-tertiary-active'],
    },
    g90: {
        blue20: [],
        blue60: [
            '$background-brand',
            '$interactive',
            '$border-interactive',
            '$button-primary',
            '$button-tertiary',
            '$focus-inverse',
        ],
        blue60Hover: ['$button-primary-hover', '$button-tertiary-hover'],
        blue80: ['$button-primary-active', '$highlight', '$button-tertiary-active'],
    },
    g100: {
        blue20: [],
        blue60: [
            '$background-brand',
            '$interactive',
            '$border-interactive',
            '$button-primary',
            '$button-tertiary',
            '$focus-inverse',
        ],
        blue60Hover: ['$button-primary-hover', '$button-tertiary-hover'],
        blue80: ['$button-primary-active', '$highlight', '$button-tertiary-active'],
    },
};
/**
 * Sets the given value for each property of the given set of names in the given map.
 *
 * @param styles The set of styles that need to be replaced.
 * @param propertyNames The names of the styles to replace.
 * @param value The value to replace each of the styles with.
 */
function fillValues(styles, propertyNames, value) {
    propertyNames.forEach(propertyName => {
        styles[propertyName] = value;
    });
}
/**
 * This function will merge the CSS variables from the public and remote configurations. Any variables in the public
 * configuration will override values in the remote configuration. Any values in the remote configuration that are
 * the empty string will be ignored.
 */
function mergeCSSVariables(publicVars, remoteStyles, carbonTheme, useAITheme) {
    carbonTheme = carbonTheme || CarbonTheme.G10;
    useAITheme = useAITheme || false;
    publicVars = publicVars || {};
    const internalOverrides = createInternalCSSOverridesMap(carbonTheme, useAITheme);
    const result = { ...internalOverrides, ...publicVars };
    Object.entries(result).forEach(([key, value]) => {
        // Variables starting with "$" are carbon theme tokens and should all be colors
        if (key.startsWith('$') && !value.match(HEXADECIMAL_REGEX)) {
            console.warn(`${WA_CONSOLE_PREFIX} You tried to call "updateCSSVariables" with an invalid value for "${key}": "${publicVars[key]}". You must use hexadecimal values for colors.`);
            // Delete color values that are not in hexadecimal format to ensure we can use them in methods in ./colors.
            delete result[key];
        }
    });
    const remoteVars = remoteStylesToCSSVars(remoteStyles || {}, carbonTheme);
    Object.entries(remoteVars).forEach(([key, value]) => {
        if (value !== '' && publicVars[key] === undefined) {
            result[key] = value;
        }
    });
    return result;
}
/**
 * This function replaces the default carbon colors for some specific scss variables. After this function is called the
 * public and remote values in mergeCSSVariables can override whatever is set here.
 */
function createInternalCSSOverridesMap(carbonTheme, useAITheme) {
    let internalOverridesMap = {};
    if (!useAITheme) {
        // Some carbon colors need to be overridden in order to support our theming options (when the user isn't using the
        // AI theme). For now these overrides only apply to the quick action chat buttons since their carbon default, link
        // blue, may not match the users theme. But this could be extended to other overrides in the future.
        if ([CarbonTheme.G10, CarbonTheme.WHITE].includes(carbonTheme)) {
            // In light themes make the quick action chat buttons black.
            internalOverridesMap = {
                ...internalOverridesMap,
                ...INTERNAL_OVERRIDES_LIGHT_THEME_MAP,
            };
        }
        else if ([CarbonTheme.G90, CarbonTheme.G100].includes(carbonTheme)) {
            // In dark themes make the quick action chat buttons white.
            internalOverridesMap = {
                ...internalOverridesMap,
                ...INTERNAL_OVERRIDES_DARK_THEME_MAP,
            };
        }
    }
    return internalOverridesMap;
}
// Given a themeState determine which classNames should be used on the "WACContainer--render" element.
function getThemeClassNames(themeState) {
    let themeClassnames;
    switch (themeState?.carbonTheme) {
        case CarbonTheme.WHITE:
            themeClassnames = CarbonThemeClassNames.WHITE;
            break;
        case CarbonTheme.G10:
            themeClassnames = CarbonThemeClassNames.G10;
            break;
        case CarbonTheme.G90:
            themeClassnames = CarbonThemeClassNames.G90;
            break;
        case CarbonTheme.G100:
            themeClassnames = CarbonThemeClassNames.G100;
            break;
        default:
            themeClassnames = CarbonThemeClassNames.G10;
            break;
    }
    if (themeState?.useAITheme) {
        themeClassnames += ' WAC--aiTheme';
    }
    return themeClassnames;
}

/**
 * Take a newView, either in string format or as a partial {@link ViewState}, and combine it with the current viewState
 * to form a complete view state that is then returned.
 */
function constructViewState(newView, appState) {
    const { viewState } = appState.persistedToBrowserStorage.launcherState;
    // Start with the existing view state.
    let newViewState;
    // Depending on the type of newView go through different steps to construct a new viewState.
    if (typeof newView === 'string') {
        // If the newView is of type string then set all the views to false except for the view of the provided string
        // which should be true.
        newViewState = { ...VIEW_STATE_ALL_CLOSED, [newView]: true };
    }
    else {
        // If the newView is not a string then merge the newView with the existing viewState. This will cause any views
        // provided in newView to overwrite the existing view state, while preserving the view state of any views not
        // included in the newView.
        newViewState = { ...viewState, ...newView };
    }
    return newViewState;
}
/**
 * Take a viewState and validate that it makes sense against other pieces of state. For example, we only want to show a
 * tour if there is a tour to be shown. If the validation fails then false will be returned, otherwise true will be.
 */
function validateViewState(viewState, appState) {
    const { activeTour } = appState.persistedToBrowserStorage.launcherState;
    if (viewState.tour && !activeTour) {
        // If the new viewState is supposed to show a tour, but there is no active tour, then log an error and return false.
        consoleError('Error changing the view. The new view was supposed to show a tour however there is no active tour to show.' +
            ' Changing the view has been canceled.');
        return false;
    }
    return true;
}

// The amount of time we artificially delay a message for in milliseconds.
const MINIMUM_RESPONSE_TIME = 1000;
const VALID_PUBLIC_VARS_IN_AI_THEME_LIST = ['BASE-height', 'BASE-max-height', 'BASE-width', 'BASE-z-index'];
const VALID_PUBLIC_VARS_IN_AI_THEME_SET = new Set(VALID_PUBLIC_VARS_IN_AI_THEME_LIST);
const UPDATE_CSS_VARS_AI_THEME_WARNING_MESSAGE = `[updateCSSVariables] The AI theme is enabled and only ${VALID_PUBLIC_VARS_IN_AI_THEME_LIST.join(', ')} can be updated.`;
/**
 * This class is responsible for handling various "actions" that the system can perform including actions that can
 * be initiated by custom code running in the host page and is an implementation of the public interface to the widget.
 */
class ChatActionsImpl {
    constructor(serviceManager) {
        /**
         * Indicates if we are currently hydrating (the Promise above is unresolved).
         */
        this.hydrating = false;
        /**
         * Indicates if a restart is currently in progress.
         */
        this.restarting = false;
        /**
         * Indicates if web chat has been hydrated at least once. This is used when a rehydration occurs so that we avoid
         * performing certain operations more than once.
         */
        this.alreadyHydrated = false;
        /**
         * Queue of received chunks.
         */
        this.chunkQueue = [];
        /**
         * If a channel transfer failed, we should let the user know by inserting a message.
         */
        this.renderChannelTransferFailedMessage = () => {
            const { languagePack } = this.serviceManager.store.getState();
            const { originalMessage, localMessage } = createLocalMessageForChannelTransferFailure(languagePack.channelTransfer_failed);
            this.serviceManager.store.dispatch(actions.addLocalMessageItem(localMessage, originalMessage, true));
        };
        /**
         * If the session has hit its time limit it's time to show a message to the end user to let them know.
         */
        this.renderSessionExpiredMessage = () => {
            const { languagePack, config } = this.serviceManager.store.getState();
            const { sessionHistory_expired, sessionHistory_expiredNoCopy } = languagePack;
            const { messaging } = config.public;
            const message = messaging.copyPreviousContextOnRequest ? sessionHistory_expired : sessionHistory_expiredNoCopy;
            const { originalMessage, localMessage } = createLocalMessageForSessionExpiration(message);
            this.serviceManager.store.dispatch(actions.addLocalMessageItem(localMessage, originalMessage, true));
        };
        /**
         * The default track props for a send method call. This should be overwritten by anything but a developer calling the
         * .send() method directly via this API.
         */
        this.defaultSendTrackEventProps = {
            eventName: 'Send method called',
            eventDescription: 'Data was sent to watsonx Assistant and did not have a tracking event attached. Usually, this is someone calling via the API.',
        };
        this.serviceManager = serviceManager;
    }
    /**
     * Loads the main config from the server.
     */
    async loadMainConfig() {
        const { store } = this.serviceManager;
        // If we are waiting for a userID from a JWT, this will fill it in to the store.
        // If this is agentApp, or we don't need to fetch mainConfig.
        const publicConfig = store.getState().config.public;
        const mainConfig = publicConfig.agentAppConfig?.is_on || publicConfig.servers?.skipConfig
            ? {}
            : await getRemoteMainConfig(this.serviceManager);
        if (!store.getState().userID) {
            // If we are not in carbonChat mode, we should use a cookie to store the userID.
            const useCookie = !this.serviceManager.store.getState().config.asCarbonChat;
            // If no userID exists, we supply one prefixed by "anonymous_". This will trigger the copyToSessionStorage
            // subscription to retrieve the previous anonymous session if there is one.
            this.serviceManager.store.dispatch(actions.setAppStateValue('userID', getAnonymousUserID(useCookie) || generateAndSetAnonymousUserId(useCookie)));
        }
        // In the event of a channel transfer we should first verify and associate the web chat user id with the
        // session id provided by the source channel the user is coming from.
        await verifyAndAssociateUserToChannelSession(this.serviceManager);
        return mainConfig;
    }
    /**
     * Fetch welcome node and (if applicable) history store.
     *
     * @param alternateWelcomeRequest Indicates if a different message should be used as a message requesting the
     * welcome node. This message behaves a little differently from the welcome node in that it's assumed that this
     * message is actively needed. It will bypass the home screen if it is enabled and it was always append this
     * message to the end of any session history that is retrieved.
     * @param alternateWelcomeRequestSource The source of the alternate welcome message.
     * @param alternateOptions The send to send along with the alternate welcome request.
     */
    async hydrateChat(alternateWelcomeRequest, alternateWelcomeRequestSource, alternateOptions) {
        // Make sure we only fire this event once after the thread that actually does the hydration is finished.
        let fireReady = false;
        try {
            if (!this.hydrationPromise) {
                this.hydrating = true;
                this.hydrationPromise = this.doHydrateChat(alternateWelcomeRequest, alternateWelcomeRequestSource, alternateOptions);
                fireReady = true;
            }
            await this.hydrationPromise;
        }
        finally {
            this.hydrating = false;
        }
        if (fireReady) {
            await this.serviceManager.fire({ type: "chat:ready" /* BusEventType.CHAT_READY */ });
        }
    }
    /**
     * Fetch welcome node and (if applicable) history.
     *
     * @param alternateWelcomeRequest Indicates if a different message should be used as a message requesting the
     * welcome node. This message behaves a little differently from the welcome node in that it's assumed that this
     * message is actively needed. It will bypass the home screen if it is enabled and it was always append this
     * message to the end of any session history that is retrieved.
     * @param alternateWelcomeRequestSource The source of the alternate welcome message.
     * @param alternateOptions The options to send along with the alternate welcome request.
     */
    async doHydrateChat(alternateWelcomeRequest, alternateWelcomeRequestSource, alternateOptions) {
        debugLog('Hydrating web chat', alternateWelcomeRequest, alternateWelcomeRequestSource, alternateOptions);
        // Load the history and main config but only if it's the first time we are hydrating.
        let history;
        const { serviceManager } = this;
        serviceManager.store.dispatch(actions.addIsHydratingCounter(1));
        if (!this.alreadyHydrated) {
            const mainConfig = await this.loadMainConfig();
            serviceManager.store.dispatch(actions.setMainConfig(mainConfig));
            history = await this.serviceManager.historyService.loadHistory();
            const { config } = serviceManager.store.getState();
            if (!config.public.agentAppConfig.is_on && serviceManager.humanAgentService) {
                // Once we've got the main config which contains the details for connecting to a service desk, we can
                // initialize the human agent service.
                debugLog('Initializing the human agent service');
                await serviceManager.humanAgentService.initialize();
                serviceManager.store.subscribe(createHandleServiceDesk(serviceManager));
            }
            else {
                debugLog('No service desk integrations present');
            }
            // Track browser data.
            this.serviceManager.trackService.identifyGroupAndUser();
        }
        const { config } = serviceManager.store.getState();
        if (!history) {
            // We've failed to complete a channel transfer if history hasn't been fetched and we're in a channel transfer page.
            if (isChannelTransferPage() && !this.alreadyHydrated) {
                this.renderChannelTransferFailedMessage();
            }
            if (config.public.agentAppConfig.is_on) {
                const { languagePack } = serviceManager.store.getState();
                serviceManager.actions.errorOccurred({
                    errorType: OnErrorType.AGENT_APP_EXPIRED_SESSION,
                    message: languagePack.errors_agentAppSessionExpired,
                    catastrophicErrorType: CatastrophicErrorType.AGENT_APP_EXPIRED_SESSION,
                });
            }
            else if (!alternateWelcomeRequest) {
                const state = serviceManager.store.getState();
                if (state.homeScreenConfig?.is_on) {
                    // If no history was loaded, there are no messages already sent, and there is a home screen,
                    // then we need to show the home screen.
                    serviceManager.store.dispatch(actions.setHomeScreenIsOpen(true));
                }
                else if (!config.public.messaging?.skipWelcome) {
                    // If no history was loaded, there are no messages already sent, and there is no home screen, then we need
                    // to fetch the welcome node. We use returnBeforeStreaming to ensure that we don't get stuck in the
                    // loading state if the welcome message happens to return a streaming response.
                    await serviceManager.actions.send(createWelcomeRequest(), MessageSendSource.WELCOME_REQUEST, { returnBeforeStreaming: true }, true);
                }
            }
        }
        else {
            // Need to populate the history in redux (specifically botMessageState) before creating elements for custom
            // responses. createElementsForUserDefinedResponse() fires a userDefinedResponse event where users may choose
            // to call instance.updateHistoryUserDefined(messageID, data). That instance function will fail to save user
            // data if the messageID provided can't be found in the botMessageState array. So we need to hydrate that
            // state before firing userDefinedResponse events.
            serviceManager.store.dispatch(actions.hydrateMessageHistory(history.messageHistory));
            await this.createElementsForUserDefinedResponses(history.messageHistory);
            // If the latest message is a panel response type, we should open it.
            if (history.latestPanelLocalMessageItem) {
                this.openResponsePanel(history.latestPanelLocalMessageItem, true);
            }
        }
        if (alternateWelcomeRequest) {
            // If there was an alternate welcome request, we always want to send it and bypass the home screen.
            serviceManager.store.dispatch(actions.setHomeScreenIsOpen(false));
            await serviceManager.actions.send(alternateWelcomeRequest, alternateWelcomeRequestSource, alternateOptions, true);
        }
        // After both history and welcome are loaded indicate we've got everything.
        serviceManager.store.dispatch(actions.chatWasHydrated());
        serviceManager.store.dispatch(actions.addIsHydratingCounter(-1));
        if (isChannelTransferPage() && !this.alreadyHydrated) {
            toggleHomeScreenAndProcessTransferResponse(history, serviceManager);
        }
        // If web chat is in a readonly mode or if this is an agent app, then don't attempt to add new messages that
        // might be missing.
        const allowNewMessages = !config.public.__ibm__.isReadonly && !config.public.agentAppConfig?.is_on;
        if (history) {
            const lastMessageID = arrayLastValue(history.messageHistory.botMessageState.localMessageIDs);
            const lastMessage = history.messageHistory.allMessageItemsByID[lastMessageID];
            const lastOriginalMessage = history.messageHistory.allMessagesByID[lastMessage?.fullMessageID];
            if (allowNewMessages) {
                if (!isLiveAgentMessage(lastMessage) && isRequest(lastOriginalMessage)) {
                    // If the last message in history is a request that means that the user left the page before we received the
                    // response and the response is actually still being calculated. In this case we want to "reconnect" to the
                    // back-end so that we can receive the response when it becomes available. To do that all we have to do is
                    // resend the original request. But we don't want the resend to appear in the message list (because it is
                    // already there).
                    serviceManager.messageService.resendMessage(lastOriginalMessage, lastMessage.ui_state.id);
                }
            }
        }
        // Note, we're not waiting for the human agent service to handle the hydration. It may start an asynchronous
        // process to reconnect the user to an agent but that is considered separate from the main hydration.
        const allowReconnect = config.public.serviceDesk.allowReconnect ?? true;
        this.serviceManager?.humanAgentService?.handleHydration(allowReconnect, allowNewMessages && Boolean(history));
        this.alreadyHydrated = true;
    }
    /**
     * Adds browser info to the context object.
     */
    addBrowserInfo(message) {
        message.context = merge({}, { integrations: { chat: { browser_info: getBrowserInfo() } }, global: { system: {} } }, message.context);
    }
    getPublicWebChatState() {
        const state = this.serviceManager.store.getState();
        const { persistedToBrowserStorage, config } = state;
        const { asCarbonChat } = config;
        const { chatState, launcherState } = persistedToBrowserStorage;
        const publicWebChatState = {
            isConnectedWithHumanAgent: chatState.agentState.isConnected,
            isWebChatOpen: launcherState.viewState.mainWindow,
            isConnectingWithHumanAgent: state.agentState.isConnecting,
            isHomeScreenOpen: chatState.homeScreenState.isHomeScreenOpen,
            isDebugEnabled: isEnableDebugLog(),
            hasUserSentMessage: chatState.hasSentNonWelcomeMessage,
            isTourActive: launcherState.activeTour,
            viewState: { ...launcherState.viewState },
            userID: state.userID,
            serviceDesk: {
                isConnected: chatState.agentState.isConnected,
                isConnecting: state.agentState.isConnecting,
                isSuspended: chatState.agentState.isSuspended ?? false,
            },
        };
        if (!asCarbonChat) {
            publicWebChatState.sessionID = chatState.sessionID;
        }
        return publicWebChatState;
    }
    /**
     * Calls the send function but catches any errors and logs them to avoid us having any uncaught exceptions thrown
     * to the browser.
     */
    async sendWithCatch(message, source, options = {}, ignoreHydration = false) {
        try {
            await this.send(message, source, options, ignoreHydration);
        }
        catch (error) {
            consoleError('An error occurred sending the message', error);
        }
    }
    /**
     * Sends the given message to the assistant on the remote server. This will result in a "pre:send" and "send" event
     * being fired on the event bus. The returned promise will resolve once a response has received and processed and
     * both the "pre:receive" and "receive" events have fired. It will reject when too many errors have occurred and
     * the system gives up retrying.
     *
     * @param message The message to send.
     * @param source The source of the message.
     * @param options Options for the sent message.
     * @param ignoreHydration Indicates if this function should not check to see if the web chat is hydrated before
     * performing send.
     */
    async send(message, source, options = {}, ignoreHydration = false) {
        const messageRequest = typeof message === 'string' ? createMessageRequestForText(message) : message;
        const state = this.serviceManager.store.getState();
        if (options.skipPause === undefined &&
            (state.persistedToBrowserStorage.launcherState.viewState.launcher || !state.isHydrated)) {
            // If the web chat isn't currently open or hydrated, we'll assume that any messages sent now should be shown
            // without delay (presumably the web chat will be opened right after this).
            options.skipPause = true;
        }
        // If the home screen is open, we want to close it as soon as a message is sent. Note that this will also apply
        // if the web chat hasn't been opened yet.
        if (this.serviceManager.store.getState().persistedToBrowserStorage.chatState.homeScreenState.isHomeScreenOpen) {
            this.serviceManager.store.dispatch(actions.setHomeScreenIsOpen(false));
        }
        // If the response panel is open, it should be closed on every message sent.
        if (this.serviceManager.store.getState().responsePanelState.isOpen) {
            this.serviceManager.store.dispatch(actions.setResponsePanelIsOpen(false));
        }
        if (this.hydrationPromise || ignoreHydration) {
            if (!ignoreHydration) {
                // If hydration has already started, then make sure to wait for it to finish.
                await this.hydrationPromise;
            }
            await this.doSend(messageRequest, source, options);
        }
        else {
            // If no hydration has started, then we need to start the hydration and use this message as the alternate for
            // the welcome node.
            await this.hydrateChat(messageRequest, source, options);
        }
    }
    /**
     * Sends the given message to the assistant on the remote server. This will result in a "pre:send" and "send" event
     * being fired on the event bus. The returned promise will resolve once a response has received and processed and
     * both the "pre:receive" and "receive" events have fired. It will reject when too many errors have occurred and
     * the system gives up retrying.
     *
     * @param message The message to send.
     * @param source The source of the message.
     * @param options Options for sending the message.
     */
    async doSend(message, source, options = {}) {
        const { store } = this.serviceManager;
        const state = store.getState();
        const { alternateSuggestionsState, config, streaming } = state;
        addDefaultsToMessage(message);
        // Grab the original text before it can be modified by a pre:send handler.
        const originalUserText = message.history?.label || message.input.text;
        this.addBrowserInfo(message);
        // Add options to get dialog state on response.
        message.input.options = {
            ...message.input.options,
            export: true,
            debug: true,
            return_context: true,
            alternate_responses: alternateSuggestionsState.config.is_on,
            disambiguation: {
                ...message.input.options?.disambiguation,
                alternate_responses: alternateSuggestionsState.config.is_on,
            },
        };
        if (options.additionalInputOptions) {
            merge(message.input.options, options.additionalInputOptions);
        }
        // If there is not tracking passed in on a message (usually when .send is called by an external developer) we add
        // the default tracking props.
        if (!options.trackEventProps) {
            options.trackEventProps = this.defaultSendTrackEventProps;
        }
        // If the options object instructs us to create a silent message, update the history object to respect the silent
        // setting. This means that the message will not show in the UI, but will be sent to the API.
        if (options.silent) {
            message.history.silent = true;
        }
        if (options.isSuggestion) {
            message.history.is_suggestion = true;
        }
        const localMessage = inputItemToLocalItem(message, originalUserText);
        // If history.silent is set to true, we don't add the message to the redux store as we do not want to show it.
        // Likewise, in schema/historyToMessages, if the message is coming from the history store, we do not add it to redux
        // either.
        if (!message.history.silent) {
            store.dispatch(actions.addLocalMessageItem(localMessage, message, true));
        }
        else {
            store.dispatch(actions.addMessage(message));
        }
        // This message is coming from an option/suggestion response type, and we need to let the previous message that
        // displayed the options which item should be marked in state as selected.
        if (options.setValueSelectedForMessageID) {
            store.dispatch(actions.messageSetOptionSelected(options.setValueSelectedForMessageID, message));
        }
        // Track the input request through amplitude.
        this.track(options.trackEventProps);
        // Now freeze the message so nobody can mess with it since that object came from outside. We'll then create a
        // clone of this message so that it may be modifiable by a pre:send listener when the message is ready to be
        // sent (which may happen later if other messages are in the queue). We'll have to replace our store object once
        // that happens.
        deepFreeze$1(message);
        await this.serviceManager.messageService.send(cloneDeep(message), source, localMessage.ui_state.id, options);
    }
    /**
     * Instructs the widget to process the given message as an incoming message received from the assistant. This will
     * fire a "pre:receive" event immediately and a "receive" event after the event has been processed by the widget.
     * This method completes when all message items have been processed (including the time delay that may be introduced
     * by a pause).
     *
     * @param message A v2 message API Response object.
     * @param isLatestWelcomeNode Indicates if this message is a new welcome message that has just been shown to the user
     * and isn't a historical welcome message.
     * @param requestMessage The optional {@link MessageRequest} that this response is a response to.
     * @param requestOptions The options that were included when the request was sent.
     * @param responseTrackData Optional data to add for tracking purposes.
     */
    async receive(message, isLatestWelcomeNode = false, requestMessage, requestOptions, responseTrackData) {
        const { store, restartCount: initialRestartCount } = this.serviceManager;
        // Received messages should be given an id if they don't have one.
        if (!message.id) {
            message.id = uuid(UUIDType.MESSAGE);
        }
        // Save a copy of the original message to compare against after pre:receive.
        const messageClone = cloneDeep(message);
        const preReceiveEvent = {
            type: "pre:receive" /* BusEventType.PRE_RECEIVE */,
            data: message,
            updateHistory: true,
        };
        // Fire the pre:receive event. User code is allowed to modify the message at this point.
        await this.serviceManager.fire(preReceiveEvent);
        if (initialRestartCount !== this.serviceManager.restartCount) {
            // If a restart occurred during the await above, we need to exit.
            return;
        }
        // Check to see if we're receiving a response that will count as the third strike for the alternate suggestions
        // functionality. If so, we want to ignore the message and dispatch an action to put us into the third strike
        // state (where we'll end up showing a Connect to agent card).
        if (shouldMessageActivateThirdStrike(message, store.getState())) {
            store.dispatch(actions.setAlternateSuggestionsThirdStrike());
            // We also need to update the message in session history so it doesn't appear there.
            const partialMessage = { history: { silent: true } };
            // Note that we can't await this call because we're currently in the middle of the message queue running and
            // the following statement adds a message to the queue. It can't be processed until the current message is
            // done but if we wait for it, the current message will never finish.
            this.sendUpdateHistoryEvent(message.id, partialMessage);
            return;
        }
        if (!isLatestWelcomeNode) {
            const isEmptyResponse = !message.output?.generic?.length;
            this.serviceManager.store.dispatch(actions.updateHasSentNonWelcomeMessage(true));
            // Jest gets angry with our deeply nested use of the sleep utility here, so when running tests we just remove it.
            // See https://stackoverflow.com/questions/46710564/testing-a-promise-using-settimeout-with-jest
            if (!IS_JEST && !requestOptions?.skipPause && requestOptions?.pauseStartTime && !isEmptyResponse) {
                const requestTime = Date.now() - requestOptions?.pauseStartTime;
                const delayTime = MINIMUM_RESPONSE_TIME - requestTime;
                if (delayTime > 0) {
                    // Make sure the message has been sent for the minimum amount of time expected to seem "natural". We may be able to
                    // remove this if Dialog adds a "natural" mode and pre-pends pause response types.
                    debugLog(`Adding user experience delay of ${delayTime}ms.`);
                    await sleep(delayTime);
                }
            }
        }
        if (initialRestartCount !== this.serviceManager.restartCount) {
            // If a restart occurred during the await above, we need to exit.
            return;
        }
        // If the user wants their changes saved, then compare the originalMessage with the
        // message after pre:receive. If the message has changed then save the new message.
        if (!store.getState().config.public.disableSessionHistory &&
            preReceiveEvent.updateHistory &&
            !isEqual$1(messageClone, message)) {
            // Get the difference between the original message and the modified message after pre:receive and keep newly added
            // keys by setting the last parameter (keepNewKeys) to true. If there is no difference, this value will default to
            // null.
            // Note: deepDiff ignores properties that have been removed.
            const messageDifference = deepDiff(messageClone, message, true);
            if (messageDifference) {
                this.sendUpdateHistoryEvent(message.id, messageDifference);
            }
        }
        const { languagePack } = this.serviceManager.store.getState();
        if (isResponse(message)) {
            // Even though processMessageResponse is an async function we do not await it in case a pause response type is
            // being processed. If we waited for the function to finished when a pause response type is being processed there
            // would be a pause before firing the receive event lower down, which would be incorrect since we have actually
            // received the event.
            this.processMessageResponse(message, isLatestWelcomeNode, requestMessage, false, requestOptions).catch(error => {
                consoleError('Error processing the message response', error);
            });
        }
        else {
            const inlineError = createMessageResponseForText(languagePack.errors_singleMessage, message.thread_id, MessageResponseTypes.INLINE_ERROR);
            this.receive(inlineError, false);
        }
        // Track the received responses through amplitude.
        if (this.serviceManager.trackService) {
            this.serviceManager.trackService.trackResponse(message, isLatestWelcomeNode, responseTrackData);
        }
        // Now freeze the message so nobody can mess with it since that object came from outside.
        deepFreeze$1(message);
        // Don't fire with the cloned message since we don't want to let anyone mess with it.
        await this.serviceManager.fire({ type: "receive" /* BusEventType.RECEIVE */, data: message });
    }
    /**
     * Removes the messages with the given IDs from the chat view.
     */
    async removeMessages(messageIDs) {
        this.serviceManager.store.dispatch(actions.removeMessages(messageIDs));
    }
    /**
     * Inserts the given messages into the chat window as part of the chat history. This will fire the history:begin
     * and history:end events.
     */
    async insertHistory(messages) {
        // Note: there is currently a gap here. If this is called with a partial list of messages that include
        // "update_history" event messages to add updates to messages not also in this list, then they will not update
        // correctly. I'm going to wait to see how this functionality shakes out and see if this is really going to end
        // up being necessary.
        // If we're inserting more history into a chat that already has messages, we want to preserve the relative
        // scroll position of the existing messages from the bottom.
        const scrollBottom = this.serviceManager.mainWindow?.getMessagesScrollBottom();
        const state = this.serviceManager.store.getState();
        // TODO: This doesn't work right if this is called more than once.
        const notes = { notes: [{ type: NoteType.HISTORY, body: messages }] };
        const history = await this.serviceManager.historyService.loadHistory(notes);
        // Merge the existing state on top of the new state (with the current state taking precedence over anything
        // that that's in the inserted state).
        const currentAppStateMessages = {
            allMessageItemsByID: state.allMessageItemsByID,
            allMessagesByID: state.allMessagesByID,
            botMessageState: state.botMessageState,
        };
        const newAppStateMessages = merge({}, history.messageHistory, currentAppStateMessages);
        // Now make sure the message arrays are merged correctly.
        newAppStateMessages.botMessageState.messageIDs = [
            ...history.messageHistory.botMessageState.messageIDs,
            ...currentAppStateMessages.botMessageState.messageIDs,
        ];
        newAppStateMessages.botMessageState.localMessageIDs = [
            ...history.messageHistory.botMessageState.localMessageIDs,
            ...currentAppStateMessages.botMessageState.localMessageIDs,
        ];
        this.serviceManager.store.dispatch(actions.hydrateMessageHistory(newAppStateMessages));
        await this.createElementsForUserDefinedResponses(history.messageHistory);
        // Restore the scroll position.
        this.serviceManager.mainWindow?.doAutoScroll({ scrollToBottom: scrollBottom });
    }
    /**
     * Receives a chunk from a stream.
     */
    async receiveChunk(chunk, messageID, options = {}) {
        const chunkPromise = resolvablePromise();
        this.chunkQueue.push({ chunk, messageID, options, chunkPromise });
        if (this.chunkQueue.length === 1) {
            this.processChunkQueue();
        }
        return chunkPromise;
    }
    async processChunkQueue() {
        const { chunk, options, chunkPromise } = this.chunkQueue[0];
        let { messageID } = this.chunkQueue[0];
        const isCompleteItem = isStreamCompleteItem(chunk);
        const isPartialItem = isStreamPartialItem(chunk);
        const { store } = this.serviceManager;
        const isStopGeneratingVisible = store.getState().botInputState.stopStreamingButtonState.isVisible;
        const { currentStreamID } = store.getState().botInputState.stopStreamingButtonState;
        if (isPartialItem) {
            const streamingData = chunk.partial_item.streaming_metadata;
            if (streamingData.stream_id && streamingData.stream_id !== currentStreamID) {
                store.dispatch(actions.setStreamID(streamingData.stream_id));
            }
            if (streamingData.cancellable && !isStopGeneratingVisible) {
                store.dispatch(actions.setStopStreamingButtonVisible(true));
            }
        }
        if (isCompleteItem || isPartialItem) {
            if (!messageID) {
                messageID = chunk.streaming_metadata.response_id;
            }
            if (!store.getState().allMessagesByID[messageID]) {
                store.dispatch(actions.streamingStart(messageID));
            }
            const item = chunk.partial_item || chunk.complete_item;
            store.dispatch(actions.streamingAddChunk(messageID, item, isCompleteItem, options.disableFadeAnimation ?? true));
            if (chunk.partial_response?.history) {
                if (Object.keys(chunk.partial_response).length > 1) {
                    throw new Error(`The partial_response object only supports the "history" property.`);
                }
                store.dispatch(actions.streamingMergeHistory(messageID, chunk.partial_response?.history));
            }
            // Now make sure to handle any user_defined response items in the chunk.
            await this.handleUserDefinedResponseItemsChunk(messageID, chunk, item);
        }
        else if (isStreamFinalResponse(chunk)) {
            // Note that while this function is called from the streaming handler in the MessageService, the final_response
            // path here is not taken. The MessageService uses the processSuccess path instead after the stream is
            // complete. This path is only taken by custom code calling the public receiveChunk method.
            this.receive(chunk.final_response, options.isLatestWelcomeNode, null, {
                skipPause: true,
                disableFadeAnimation: true,
            });
        }
        if ((isCompleteItem || isStreamFinalResponse(chunk)) && isStopGeneratingVisible) {
            // Once a complete or final response is received, reset the visible and disabled states for the "stop streaming"
            // button.
            store.dispatch(actions.setStopStreamingButtonDisabled(false));
            store.dispatch(actions.setStopStreamingButtonVisible(false));
        }
        this.chunkQueue.shift();
        chunkPromise.doResolve();
        if (this.chunkQueue[0]) {
            this.processChunkQueue();
        }
    }
    /**
     * Provides the store parameters to track the event accordingly.
     *
     * @param eventProps The properties of the triggered event being tracked.
     */
    track(eventProps) {
        if (this.serviceManager.trackService) {
            this.serviceManager.trackService.track(eventProps);
        }
    }
    /**
     * Used in requests to /message and /notes to kick off a session timer with the end time that is returned from the
     * back-end. If there was an existing timer going then stop it. Use the new sessionLength to start a new timer that
     * will call renderSessionExpiredMessage once it's done.
     *
     * @param sessionHeaderString a string returned by the back-end that includes the session length in seconds. ex "60".
     */
    startSessionTimer(sessionHeaderString) {
        const { config, persistedToBrowserStorage } = this.serviceManager.store.getState();
        // If session history is enabled lets kick off the session timer.
        if (!config.public.disableSessionHistory && !config.public.agentAppConfig?.is_on) {
            // If there's an existing timer going then stop it.
            if (this.sessionTimer) {
                clearTimeout(this.sessionTimer);
            }
            // Change the session length from a string to a number.
            const sessionLength = Number(sessionHeaderString);
            // Convert the sessionLength to milliseconds. Subtract 3 seconds to give the user a slightly early heads up in
            // case they were about to refresh right as the session expired but before they saw the timeout message.
            const timeToEnd = sessionLength * 1000 - 3000;
            // Begin a timer that will call renderSessionExpiredMessage and throw an event after that time has elapsed.
            this.sessionTimer = setTimeout(() => {
                this.serviceManager
                    .fire({ type: "sessionExpired" /* BusEventType.SESSION_EXPIRED */, session: { id: persistedToBrowserStorage.chatState.sessionID } })
                    .catch(error => consoleError('An error occurred', error));
                this.renderSessionExpiredMessage();
            }, timeToEnd);
        }
    }
    /**
     * Creates the HTML element for a user defined response and adds it to the registry (if it does not already exist).
     */
    getOrCreateUserDefinedElement(messageItemID) {
        const { config } = this.serviceManager.store.getState();
        let userDefinedItem = this.serviceManager.userDefinedElementRegistry.get(messageItemID);
        if (!userDefinedItem) {
            userDefinedItem = { element: document.createElement('div'), slotName: `slot-user-defined-${uuid()}` };
            if (config.public.__ibm__?.useShadowRoot) {
                userDefinedItem.element.setAttribute('slot', userDefinedItem.slotName);
            }
            this.serviceManager.userDefinedElementRegistry.set(messageItemID, userDefinedItem);
        }
        return userDefinedItem;
    }
    /**
     * If the given message should be rendered as a user defined message, this will create a host element for the message
     * and fire the {@link BusEventType.USER_DEFINED_RESPONSE} event so that the event listeners can attach whatever they
     * want to the host element.
     */
    async handleUserDefinedResponseItems(localMessage, originalMessage) {
        if (renderAsUserDefinedMessage(localMessage.item)) {
            let element;
            let slotName;
            if (!localMessage.item.user_defined?.silent) {
                // If the message is silent, don't create a host element for it since it's not going to be rendered.
                ({ element, slotName } = this.getOrCreateUserDefinedElement(localMessage.ui_state.id));
            }
            const userDefinedResponseEvent = {
                type: "userDefinedResponse" /* BusEventType.USER_DEFINED_RESPONSE */,
                data: {
                    message: localMessage.item,
                    fullMessage: originalMessage,
                    element,
                    slot: slotName,
                    fullWidth: false,
                },
            };
            await this.serviceManager.fire(userDefinedResponseEvent);
            // For backwards compatibility, we need to fire the old customResponse event as well. Note that these two
            // event objects share the instance of the "data" object which means if one event changes them, it will
            // affect both.
            const customResponseEvent = {
                ...userDefinedResponseEvent,
                type: "customResponse" /* BusEventType.CUSTOM_RESPONSE */,
            };
            await this.serviceManager.fire(customResponseEvent);
            if (userDefinedResponseEvent.data.fullWidth) {
                this.serviceManager.store.dispatch(actions.setMessageUIProperty(localMessage.ui_state.id, 'fullWidth', true));
            }
        }
        else if (isResponseWithNestedItems(localMessage.item)) {
            const { itemsLocalMessageItemIDs, bodyLocalMessageItemIDs, footerLocalMessageItemIDs, gridLocalMessageItemIDs } = localMessage.ui_state;
            const { allMessageItemsByID } = this.serviceManager.store.getState();
            /**
             * Will attempt to create an element for the custom response using the provided local message id.
             */
            const createElementForNestedUserDefinedResponse = (localMessageItemID) => {
                const nestedLocalMessage = allMessageItemsByID[localMessageItemID];
                return this.handleUserDefinedResponseItems(nestedLocalMessage, originalMessage);
            };
            if (gridLocalMessageItemIDs?.length) {
                await asyncForEach(gridLocalMessageItemIDs, row => asyncForEach(row, cell => asyncForEach(cell, itemID => createElementForNestedUserDefinedResponse(itemID))));
            }
            if (itemsLocalMessageItemIDs?.length) {
                await asyncForEach(itemsLocalMessageItemIDs, createElementForNestedUserDefinedResponse);
            }
            if (bodyLocalMessageItemIDs?.length) {
                await asyncForEach(bodyLocalMessageItemIDs, createElementForNestedUserDefinedResponse);
            }
            if (footerLocalMessageItemIDs?.length) {
                await asyncForEach(footerLocalMessageItemIDs, createElementForNestedUserDefinedResponse);
            }
        }
    }
    /**
     * If the given message should be rendered as a user defined message, this will create a host element for the message
     * and fire the {@link BusEventType.CHUNK_USER_DEFINED_RESPONSE} event so that the event listeners can attach whatever
     * they want to the host element.
     *
     * Note, this function does not currently support nested items inside the chunk.
     */
    async handleUserDefinedResponseItemsChunk(messageID, chunk, messageItem) {
        if (renderAsUserDefinedMessage(messageItem)) {
            const { store } = this.serviceManager;
            const itemID = streamItemID(messageID, messageItem);
            let element;
            let slotName;
            if (!messageItem.user_defined?.silent) {
                // If the message is silent, don't create a host element for it since it's not going to be rendered.
                ({ element, slotName } = this.getOrCreateUserDefinedElement(itemID));
            }
            const userDefinedResponseEvent = {
                type: "chunk:userDefinedResponse" /* BusEventType.CHUNK_USER_DEFINED_RESPONSE */,
                data: {
                    messageItem,
                    chunk,
                    element,
                    slot: slotName,
                    fullWidth: false,
                },
            };
            await this.serviceManager.fire(userDefinedResponseEvent);
            if (store.getState().allMessageItemsByID[itemID].ui_state.fullWidth !== userDefinedResponseEvent.data.fullWidth) {
                store.dispatch(actions.setMessageUIProperty(itemID, 'fullWidth', userDefinedResponseEvent.data.fullWidth));
            }
        }
    }
    /**
     * Takes each item in the appropriate output array and dispatches correct actions. We may want to look into
     * turning this into a formal queue as the pause response_type may cause us to lose correct order in fast
     * conversations.
     *
     * @param fullMessage A v2 message API Response object.
     * @param isLatestWelcomeNode If it is a new welcome node, we want to pass that data along.
     * @param requestMessage The optional {@link MessageRequest} that this response is a response to.
     * @param isProvidedFromTransfer Indicates that the message was provided from a channel transfer.
     * @param requestOptions The options that were included when the request was sent.
     */
    async processMessageResponse(fullMessage, isLatestWelcomeNode, requestMessage, isProvidedFromTransfer, requestOptions = {}) {
        const { store } = this.serviceManager;
        const { config } = store.getState();
        const initialRestartCount = this.serviceManager.restartCount;
        const output = fullMessage.output.generic;
        fullMessage.request_id = requestMessage?.id;
        addDefaultsToMessage(fullMessage);
        store.dispatch(actions.addMessage(fullMessage));
        // The ID of the previous (visible) message item that was added to the store. When adding new items from the
        // response, this is used to ensure that each item is added in the right position.
        let previousItemID = null;
        // Need a regular for loop to allow for the await below.
        for (let index = 0; index < output.length && initialRestartCount === this.serviceManager.restartCount; index++) {
            const messageItem = output[index];
            if (messageItem && isForChatChannel(messageItem)) {
                const pause = isPause(messageItem);
                const agent = isConnectToAgent(messageItem);
                const tour = hasTourUserDefinedType(messageItem);
                const localMessageItem = outputItemToLocalItem(messageItem, fullMessage, isLatestWelcomeNode, requestOptions.disableFadeAnimation);
                // If the message item is a tour and the tour card is supposed to be skipped then start the tour now.
                if (tour && (requestOptions.skipTourCard || messageItem.user_defined?.skip_card)) {
                    // Determine the reason the tour is starting. If the startTour method was used and skip_card is true then the
                    // method being used will take priority.
                    const startTourReason = requestOptions.skipTourCard
                        ? TourStartReason.START_TOUR_METHOD
                        : TourStartReason.SKIP_CARD;
                    // Determine the reason the view is changing.
                    const { viewState } = store.getState().persistedToBrowserStorage.launcherState;
                    if (viewState.mainWindow) {
                        // If the mainWindow is visible then use the MainWindowCloseReasons and fire the window:close events. If the
                        // startTour method was used and skip_card is true then the method being used will take priority.
                        const mainWindowCloseReason = requestOptions.skipTourCard
                            ? MainWindowCloseReason.CALLED_START_TOUR
                            : MainWindowCloseReason.TOUR_SKIP_CARD;
                        // No need to await startTour() here since there is nothing else in this function that is dependent on
                        // startTours completion.
                        this.startTour(localMessageItem, startTourReason, { mainWindowCloseReason }, true).catch(error => {
                            consoleError('Error starting the tour', error);
                        });
                    }
                    else {
                        // If the mainWindow is not visible then use the ViewChangeReasons and do not fire the window:close events.
                        // If the startTour method was used and skip_card is true then the method being used will take priority.
                        const viewChangeReason = requestOptions.skipTourCard
                            ? ViewChangeReason.CALLED_START_TOUR
                            : ViewChangeReason.TOUR_SKIP_CARD;
                        // No need to await startTour() here since there is nothing else in this function that is dependent on
                        // startTours completion.
                        this.startTour(localMessageItem, startTourReason, { viewChangeReason }, true).catch(error => {
                            consoleError('Error starting the tour', error);
                        });
                    }
                }
                else if (!tour && requestOptions.skipTourCard) {
                    // If instance.startTour() was used but a tour response was not received then log an error.
                    consoleError('The message response received was not a tour, so the tour card was not skipped nor was a tour started by the startTour() call.');
                }
                const nestedLocalMessageItems = [];
                createLocalMessageItemsForNestedMessageItems(localMessageItem, fullMessage, false, nestedLocalMessageItems, true);
                store.dispatch(actions.addNestedMessages(nestedLocalMessageItems));
                if (agent && isResponse(fullMessage)) {
                    // For the "connect_to_agent" response, we need to determine the agents' availability before we can
                    // continue to process the message items. Let's increment the typing counter while we're waiting for a
                    // result from areAnyAgentsOnline.
                    store.dispatch(actions.addIsTypingCounter(1));
                    // Create a partial message to record the current state of agent availability and any service desk errors.
                    const partialMessage = { history: {} };
                    // Determine if the CTA card should display a service desk error.
                    if (!hasServiceDesk(config)) {
                        if (config.public.__ibm__.connectToAgentBehavior === ConnectToAgentBehaviorType.ERROR_MISSING) {
                            // Report this error.
                            const message = 'Web chat received a "connect_to_agent" message but there is no service desk configured. Check your web chat configuration.';
                            this.errorOccurred({
                                errorType: OnErrorType.INTEGRATION_ERROR,
                                message,
                            });
                        }
                        // Make sure this state is reflected in history.
                        store.dispatch(actions.setMessageHistoryProperty(localMessageItem.fullMessageID, 'agent_no_service_desk', true));
                        partialMessage.history.agent_no_service_desk = true;
                    }
                    // eslint-disable-next-line no-await-in-loop
                    const agentAvailability = await this.serviceManager.humanAgentService?.checkAreAnyAgentsOnline(fullMessage);
                    // If a restart occurred while waiting for the agents online check, then skip the processing below.
                    if (initialRestartCount === this.serviceManager.restartCount) {
                        // Update the value in the redux store.
                        store.dispatch(actions.setMessageHistoryProperty(localMessageItem.fullMessageID, 'agent_availability', agentAvailability));
                        // Send event to back-end to save the current agent availability state so session history can use it on reload.
                        partialMessage.history.agent_availability = agentAvailability;
                        this.sendUpdateHistoryEvent(fullMessage.id, partialMessage);
                        let shouldAutoRequestAgent = false;
                        // If this transfer to agent message was provided from a transfer, the agent card should make an auto request
                        // on behalf of the user. It's possible to receive a transfer to agent response from web chat if the response
                        // types are not provided the "channels" attribute, so this check exists to make sure we only add
                        // shouldAutoRequestAgent if the agent response was provided from a transfer as opposed to a regular message
                        // request.
                        if (isProvidedFromTransfer) {
                            shouldAutoRequestAgent = isChannelTransferToAgent(fullMessage);
                        }
                        // If configured, then auto-connect right now.
                        if (config.public.serviceDesk?.skipConnectAgentCard) {
                            shouldAutoRequestAgent = true;
                        }
                        // Decrement the typing counter to get rid of the pause.
                        store.dispatch(actions.addIsTypingCounter(-1));
                        if (shouldAutoRequestAgent && agentAvailability === AgentsOnlineStatus.ONLINE) {
                            this.serviceManager.humanAgentService.startChat(localMessageItem, fullMessage);
                        }
                    }
                }
                if (pause) {
                    const showIsTyping = isTyping(messageItem);
                    if (showIsTyping) {
                        store.dispatch(actions.addIsTypingCounter(1));
                    }
                    // If this message is a pause, then just sleep for the pause duration before continuing. We don't actually
                    // render anything for this message since it's really an instruction so we won't create a LocalMessage for
                    // it and it won't be added to the redux store.
                    // eslint-disable-next-line no-await-in-loop
                    await sleep(messageItem.time);
                    if (showIsTyping && initialRestartCount === this.serviceManager.restartCount) {
                        store.dispatch(actions.addIsTypingCounter(-1));
                    }
                }
                else if (isPanelResponseType(localMessageItem.item)) {
                    this.openResponsePanel(localMessageItem, true);
                }
                else {
                    // In order to ensure that the addMessages get called in correct order, we need to add an `await` here to
                    // pause further processing until this one is sent.
                    // eslint-disable-next-line no-await-in-loop
                    await this.handleUserDefinedResponseItems(localMessageItem, fullMessage);
                    if (!localMessageItem.item.user_defined?.silent && initialRestartCount === this.serviceManager.restartCount) {
                        this.serviceManager.store.dispatch(actions.addLocalMessageItem(localMessageItem, fullMessage, false, previousItemID));
                        previousItemID = localMessageItem.ui_state.id;
                    }
                }
            }
        }
    }
    /**
     * Opens the response panel using the provided local message item to render the content in the panel.
     */
    openResponsePanel(localMessageItem, isMessageForInput) {
        this.serviceManager.store.dispatch(actions.setResponsePanelContent(localMessageItem, isMessageForInput));
        this.serviceManager.store.dispatch(actions.setResponsePanelIsOpen(true));
    }
    /**
     * Inserts a locally created {@link MessageResponse} message into the message system.
     */
    async insertLocalMessageResponse(message, saveToHistory) {
        message.id = uuid(UUIDType.MESSAGE);
        await this.processMessageResponse(message, false, null, false, {});
        if (saveToHistory) {
            // Send this new message to the back-end so it will get saved in history.
            await this.sendUpdateHistoryEvent(message.id, message);
        }
    }
    /**
     * Updates the language pack in use by the widget. This will merge in the provided language pack with the existing
     * one, leaving any missing keys unchanged.
     */
    updateLanguagePack(changes) {
        const { languagePack, locale } = this.serviceManager.store.getState();
        const messages = {
            ...languagePack,
            ...changes,
            // Branding translations should not be customizable.
            branding_headline: languagePack.branding_headline,
            branding_description: languagePack.branding_description,
            branding_sign_up: languagePack.branding_sign_up,
            branding_ariaInformationButton: languagePack.branding_ariaInformationButton,
        };
        setIntl(this.serviceManager, locale, messages);
    }
    /**
     * Adds a new notification to be shown in the UI.
     */
    addNotification(notification) {
        this.serviceManager.store.dispatch(actions.addNotification(notification));
    }
    /**
     * Removes a notification with the provided groupId.
     */
    removeNotification(groupID) {
        this.serviceManager.store.dispatch(actions.removeNotifications({ groupID }));
    }
    /**
     * Removes all notifications.
     */
    removeAllNotifications() {
        this.serviceManager.store.dispatch(actions.removeAllNotifications());
    }
    /**
     * Sets the ID of the current user in use by the widget. This may only be used to set the ID if one is not already
     * set. It may not be used to change the ID from one user to another user. It can be used to change from an
     * anonymousUserId to an actual user.
     */
    updateUserID(newID, newIdentityToken) {
        const { userID, identityToken, config } = this.serviceManager.store.getState();
        this.serviceManager.trackService.track({
            eventName: 'userID updated (non JWT)',
            eventDescription: 'Instance method used.',
        });
        // New identityToken is not an argument that is documented, it is just so updateIdentityToken can reuse this logic.
        if (identityToken && !newIdentityToken) {
            throw new Error(`${WA_CONSOLE_PREFIX} Unable to update the userID. Once the user is set in as the "sub" claim in your JWT identity token, it cannot be changed.`);
        }
        // New identityToken is not an argument that is documented, it is just so updateIdentityToken can reuse this logic.
        if (config.remote.initConfig.authorization?.is_on && !newIdentityToken) {
            throw new Error(`${WA_CONSOLE_PREFIX} Unable to update the userID directly. You must use the "sub" claim inside your JWT identity token.`);
        }
        if (newID !== userID) {
            // If we already have a userID set, we need to check if it's an anonymousID or not before continuing.
            if (userID) {
                const useCookie = !config.asCarbonChat;
                if (userID === getAnonymousUserID(useCookie) && !identityToken) {
                    // If we are changing from the anonymous id to the userID, then update the userID. We need to move the
                    // sessionID over to the new userID but that will be handled by the copyToSessionStorage subscription once it
                    // sees the new userID.
                    this.serviceManager.store.dispatch(actions.setAppStateValue('userID', newID));
                    // If a userID is set, we want to clear out the anonymousUserId.
                    clearAnonymousUserId();
                }
                else {
                    // If there is already an identityToken set, they can't update the userID.
                    if (identityToken) {
                        // If there isn't a new identityToken, they are trying to call updateUserID directly, which they shouldn't with a JWT.
                        if (!newIdentityToken) {
                            throw new Error(`${WA_CONSOLE_PREFIX} Unable to update the userID directly. You must use the "sub" claim inside your JWT identity token.`);
                        }
                        // If we get here, it means they are trying to change the "sub" claim, which we do not currently allow on the back-end.
                        throw new Error(`${WA_CONSOLE_PREFIX} The "sub" claim is already set to "${userID}" and cannot be changed to "${newID}"`);
                    }
                    throw new Error(`${WA_CONSOLE_PREFIX} The userID is already set to "${userID}" and cannot be changed to "${newID}"`);
                }
            }
            // If we don't have a userID in state then update the userID.
            this.serviceManager.store.dispatch(actions.setAppStateValue('userID', newID));
            // If a userID is set, we want to clear out the anonymousUserId.
            clearAnonymousUserId();
            // If there is a new identityToken we update it even if there wasn't a previous userID.
            if (newIdentityToken) {
                this.serviceManager.store.dispatch(actions.setAppStateValue('identityToken', newIdentityToken));
            }
        }
        else if (newIdentityToken) {
            // The old and new userID's are the same, but we still need to update the identityToken.
            this.serviceManager.store.dispatch(actions.setAppStateValue('identityToken', newIdentityToken));
        }
    }
    /**
     * Update the identity token (JWT) used to make secure calls to back-end.
     */
    updateIdentityToken(identityToken) {
        const currentState = this.serviceManager.store.getState();
        // initConfig.security does not exist in older integrations. In newer integrations we don't allow calling this method
        // if security is not turned on.
        if (currentState.config.remote.initConfig.authorization &&
            !currentState.config.remote.initConfig.authorization.is_on) {
            throw new Error('You cannot add an identity token if security is not turned on.');
        }
        const jwt = doDecodeJWT(identityToken, this.serviceManager.additionalChatParameters.onError);
        this.updateUserID(jwt.sub, identityToken);
    }
    /**
     * Used to send an eventMessage to the back-end to save some data about a previous MessageResponse that was updated
     * with instance.updateHistoryUserDefined(). This data will be returned in the notes array (if session history is
     * enabled) and will be added to the original message in history.user_defined.
     *
     * @param messageID The messageID of the original message that we want to save data for.
     * @param data The data that we want to save in an eventMessage.
     */
    async updateHistoryUserDefined(messageID, data) {
        // Add data to the history.user_defined section of an otherwise empty message response.
        const partialMessage = { history: { user_defined: data } };
        // Update the count of calls made to instance.updateHistoryUserDefined() for this particular messageID.
        this.serviceManager.store.dispatch(actions.updateHistoryUserDefinedCount(messageID));
        // Send event to back-end to save data that was passed through instance.updateHistoryUserDefined().
        await this.sendUpdateHistoryEvent(messageID, partialMessage);
    }
    /**
     * Used to send an event message to the back-end to save updates made to a MessageResponse. This partial message
     * will be saved in the session history and it will be merged into the original message when the session history
     * is loaded.
     *
     * @param messageID The messageID of the original message that we want to save data for.
     * @param partialMessage The partial message that contains the updates for the message.
     */
    async sendUpdateHistoryEvent(messageID, partialMessage) {
        // If session history is enabled then create an event message and try to send it.
        if (!this.serviceManager.store.getState().config.public.disableSessionHistory) {
            // Generate the correctly formatted eventMessage.
            const eventMessage = createEventMessageRequest({
                name: EVENT_NAME_UPDATE_HISTORY,
                messageID,
                partial_message: partialMessage,
            });
            // Send eventMessage to back-end to save it.
            await this.serviceManager.messageService.send(eventMessage, MessageSendSource.HISTORY_UPDATE);
        }
    }
    /**
     * This updates the map that can be used to override the values for CSS variables in the application. Each key of the
     * map is the name of a variable (without the "--cds-chat-" prefix) and the value is whatever the value of
     * the variable should be set at. This function may only be called if publicConfig.__ibm__.allowPrivateMethods has
     * been set. The values in the provided map will be merged with any variables that may already be defined in
     * the public config which allows this function to update only the specific variables desired.
     *
     * @param publicVars A map of CSS variables. Each key of the map is the name of a variable (without the
     * "--cds-chat-" prefix) and the value is whatever the value of the variable should be set at.
     * @param remoteStyles The set of variables that would normally be provided as part of the remote config. These
     * are not directly set as CSS variables but go through a translation process first that turns them into CSS
     * variables. This argument is mainly intended to support the preview of the widget in the tooling config page.
     */
    updateCSSVariables(publicVars, remoteStyles = {}) {
        const { store } = this.serviceManager;
        const { config, theme } = store.getState();
        const { carbonTheme, useAITheme } = theme;
        // If the AI theme is enabled, only a set amount of public variables should be allowed.
        if (useAITheme) {
            const usePublicVars = publicVars;
            publicVars = {};
            remoteStyles = {};
            usePublicVars &&
                Object.entries(usePublicVars).forEach(([variable]) => {
                    if (VALID_PUBLIC_VARS_IN_AI_THEME_SET.has(variable)) {
                        publicVars[variable] = usePublicVars[variable];
                    }
                    else {
                        consoleWarn(UPDATE_CSS_VARS_AI_THEME_WARNING_MESSAGE);
                    }
                });
        }
        else {
            // Merge css variables in config objects with those passed into this function.
            publicVars = {
                ...config.public.__ibm__.cssVariables,
                ...publicVars,
            };
            remoteStyles = {
                ...config.remote.initConfig.css_variables,
                ...remoteStyles,
            };
        }
        const allVariables = mergeCSSVariables(publicVars, remoteStyles, carbonTheme, useAITheme);
        store.dispatch(actions.updateCSSVariables(allVariables, publicVars, remoteStyles));
    }
    /**
     * Given a new theme config, update store to set the new theme state and then redetermine the bot name and theme
     * classNames.
     */
    updateThemeConfig(themeConfig = {}) {
        this.serviceManager.store.dispatch(actions.updateThemeConfig(themeConfig));
        const { store } = this.serviceManager;
        const { theme, config, originalConfig } = store.getState();
        // Update the bot name in case the theme config has switched whether or not the AI theme is on.
        this.serviceManager.store.dispatch(actions.updateBotName(getBotName(theme.useAITheme, config)));
        // Using the original config values for css_variables, reset the css variables.
        this.serviceManager.actions.updateCSSVariables(originalConfig.public.__ibm__?.cssVariables || {}, originalConfig.remote.initConfig.css_variables);
    }
    /**
     * Given a new streaming config, update store to set streaming to enabled or disabled.
     */
    updateStreamingConfig(streamingConfig) {
        this.serviceManager.store.dispatch(actions.updateStreamingConfig(streamingConfig));
    }
    /**
     * Updates the bot name used in, amongst other places, as the default for the title in the header bar of the chat
     * widget when AI theme is off. We use this method currently only when in WYSIWYG mode in the tooling configuration
     * page.
     *
     * @param name A new name for the bot.
     */
    updateBotName(name) {
        this.serviceManager.store.dispatch(actions.updateBotName(name));
    }
    /**
     * Public method to update the title of the main bot header. This would be used instead of the botName if it is
     * defined.
     */
    updateMainHeaderTitle(title) {
        this.serviceManager.store.dispatch(actions.updateMainHeaderTitle(title));
    }
    /**
     * Updates the bot avatar in the header bar of the chat widget. We use this method currently only when in WYSIWYG mode
     * in the tooling configuration page.
     *
     * @param url A new avatar image url for the bot.
     */
    updateBotAvatarURL(url) {
        this.serviceManager.store.dispatch(actions.updateBotAvatarURL(url));
    }
    updateLauncherAvatarURL(source) {
        this.serviceManager.store.dispatch(actions.updateLauncherAvatarURL(source));
    }
    /**
     * Updates the currently active homeScreenConfig. Currently only used in tooling to show live updates when editing web
     * chat configuration.
     */
    updateHomeScreenConfig(homeScreenConfig) {
        this.serviceManager.store.dispatch(actions.updateHomeScreenConfig(homeScreenConfig));
    }
    /**
     * Updates the currently active suggestions configuration. Currently only used in tooling to show live updates when
     * editing web chat configuration.
     */
    updateAlternateSuggestionsConfig(config) {
        this.serviceManager.store.dispatch(actions.updateAlternateSuggestionsConfig(config));
    }
    /**
     * Updates the current state of the branding feature.
     */
    updateBrandingConfig(brandingConfig) {
        this.serviceManager.store.dispatch(actions.updateBrandingConfig(brandingConfig));
    }
    /**
     * Updates the current state of the launcher.
     */
    updateLauncherConfig(launcherConfig) {
        this.serviceManager.store.dispatch(actions.updateLauncherConfig(launcherConfig));
    }
    /**
     * Set the greeting message text for the launcher. Optionally specify which launcher, desktop or mobile, that the
     * greeting message is being set for.
     */
    updateLauncherGreetingMessage(greetingMessage, launcherType) {
        this.serviceManager.store.dispatch(actions.setLauncherConfigProperty('title', greetingMessage, launcherType));
        this.serviceManager.trackService.track({
            eventName: 'Launcher greeting text updated',
            eventDescription: `A user changed the launcher greeting text${launcherType ? ` for ${launcherType}` : ''}.`,
        });
    }
    /**
     * Control when the greeting message should appear, if no time is passed it will appear immediately. Optionally
     * specify which launcher, desktop or mobile, that the time should be set for.
     */
    showLauncherGreetingMessage(time, launcherType) {
        this.serviceManager.store.dispatch(actions.setLauncherConfigProperty('time_to_expand', time, launcherType));
        this.serviceManager.store.dispatch(actions.setLauncherConfigProperty('new_expand_time', true, launcherType));
        this.serviceManager.trackService.track({
            eventName: 'Launcher greeting time updated',
            eventDescription: `A user changed the launcher greeting time${launcherType ? ` for ${launcherType}` : ''}.`,
        });
    }
    /**
     * Construct the newViewState from the newView provided. Fire the view:pre:change and view:change events, as well as
     * window:pre:open, window:open, or window:pre:close, window:close if instructed to do so. If the view change isn't
     * canceled by the events then change the view. If the main window or tour are open after changing the view, and
     * doNotHydrate isn't true and the chat is not already hydrated, then hydrate the chat.
     */
    async changeView(newView, reason, tryHydrating = true, forceViewChange = false) {
        const { store } = this.serviceManager;
        const { viewState } = store.getState().persistedToBrowserStorage.launcherState;
        // Build the new viewState object.
        let newViewState = constructViewState(newView, store.getState());
        if (!validateViewState(newViewState, store.getState())) {
            // If the newViewState had an issue and was unable to be verified then do not change the view.
            return viewState;
        }
        if (!isEqual$1(newViewState, viewState) || forceViewChange) {
            // If the newViewState is different from the current viewState, or the viewChange is being forced to happen, fire
            // the view:change events and change which views are visible.
            await this.fireViewChangeEventsAndChangeView(newViewState, reason);
            // Check and see if the chat should be hydrated.
            newViewState = store.getState().persistedToBrowserStorage.launcherState.viewState;
            if (tryHydrating && (newViewState.mainWindow || newViewState.tour) && !store.getState().isHydrated) {
                // If it's ok to hydrate, the main window or tour are now visible, and the chat isn't hydrated, then hydrate
                // the chat. Since this function is only responsible for changing the view don't await hydrateChat(), instead
                // let hydrateChat complete on its own time.
                this.hydrateChat().catch(error => {
                    consoleError('Error hydrating the chat', error);
                });
            }
        }
        // Return the newViewState. This could be the same as the original viewState if there was no difference between the
        // original viewState and the proposed newViewState, or it could be an updated viewState. The updated viewState
        // could be what was originally sent to fireViewChangeEventsAndChangeView, or it could be a viewState that has been
        // modified by Deb during the view:pre:change event.
        return newViewState;
    }
    /**
     * Fire the "view:pre:change" and "view:change" events. This will return a boolean to indicate if the process was
     * cancelled and the view should remain unchanged. If the view change isn't canceled by the events then this will
     * switch to the newViewState that's been provided. This method is private to force the use of the changeView method
     * above as an entry point to this method.
     *
     * @returns True to indicate that the view was changed. False indicates the view change was cancelled.
     */
    async fireViewChangeEventsAndChangeView(newViewState, reason) {
        const { store } = this.serviceManager;
        if (store.getState().viewChanging) {
            // If the view is already in the middle of changing then throw an error.
            throw new Error('The view may not be changed while a view change event is already running. Please make sure to resolve any promises from these events.');
        }
        store.dispatch(actions.setViewChanging(true));
        const { viewState } = store.getState().persistedToBrowserStorage.launcherState;
        const { mainWindowOpenReason, mainWindowCloseReason } = reason;
        // If we have a mainWindowOpenReason or mainWindowCloseReason then this viewChangeReason will be determined lower down.
        let { viewChangeReason } = reason;
        // Freeze the previous viewState since we don't want to allow Deb to modify it.
        const oldViewState = deepFreeze$1(viewState);
        try {
            if (mainWindowOpenReason) {
                // If there is a mainWindowOpenReason then we need to fire the window:pre:open event as well as determine the
                // appropriate viewChangeReason for the view:change events.
                viewChangeReason = OpenReasonsToViewReasons.get(mainWindowOpenReason);
                const preOpenEvent = {
                    type: "window:pre:open" /* BusEventType.WINDOW_PRE_OPEN */,
                    reason: mainWindowOpenReason,
                    cancelOpen: false,
                };
                await this.serviceManager.fire(preOpenEvent);
                if (preOpenEvent.cancelOpen) {
                    // If the view changing was canceled in the event then log a message and don't change the view.
                    debugLog('Web chat opening was cancelled by a window open event.');
                    // For customers using the window events, the previous behavior was that canceling the window open event would
                    // result in the launcher being visible. That's not always true with a view:change so let's special case
                    // the old behavior here for the window event and open the launcher.
                    store.dispatch(actions.setViewState({ ...oldViewState, launcher: true }));
                    return;
                }
            }
            else if (mainWindowCloseReason) {
                // If there is a mainWindowCloseReason then we need to fire the window:pre:close event as well as determine the
                // appropriate viewChangeReason for the view:change events.
                viewChangeReason = CloseReasonsToViewReasons.get(mainWindowCloseReason);
                const preCloseEvent = {
                    type: "window:pre:close" /* BusEventType.WINDOW_PRE_CLOSE */,
                    reason: mainWindowCloseReason,
                    cancelClose: false,
                };
                await this.serviceManager.fire(preCloseEvent);
                if (preCloseEvent.cancelClose) {
                    // If the view changing was canceled in the event then log a message and don't change the view.
                    debugLog('Web chat closing was cancelled by a window close event.');
                    return;
                }
            }
            // Create the view:pre:change event and fire it.
            const preViewChangeEvent = {
                type: "view:pre:change" /* BusEventType.VIEW_PRE_CHANGE */,
                reason: viewChangeReason,
                oldViewState,
                newViewState,
                cancelViewChange: false,
            };
            await this.serviceManager.fire(preViewChangeEvent);
            if (preViewChangeEvent.cancelViewChange) {
                // If the view changing was canceled in the event then log a message and don't change the view.
                debugLog('The view changing was cancelled by a view:pre:change event.');
                return;
            }
            // Validate the view state returned from the view:pre:change event.
            if (!validateViewState(preViewChangeEvent.newViewState, store.getState())) {
                // If the view state had an issue and was unable to be verified then do not change the view.
                return;
            }
            // If there were no issues with the new view state then use it.
            newViewState = preViewChangeEvent.newViewState;
            // Actually change the viewState in store.
            store.dispatch(actions.setViewState(deepFreeze$1(newViewState)));
            if (mainWindowOpenReason) {
                // If there is a mainWindowOpenReason then we need to fire the window:open event.
                const openEvent = {
                    type: "window:open" /* BusEventType.WINDOW_OPEN */,
                    reason: mainWindowOpenReason,
                    cancelOpen: false,
                };
                await this.serviceManager.fire(openEvent);
                if (openEvent.cancelOpen) {
                    debugLog('Web chat opening was cancelled by a window open event.');
                    // If the view changing was canceled in the event then log a message and switch the viewState back to what it
                    // was originally. Except for customers using the window events, the previous behavior was that canceling the
                    // window open event would result in the launcher being visible. That's not always true with a view:change
                    // so let's special case the old behavior here for the window event and open the launcher.
                    store.dispatch(actions.setViewState({ ...oldViewState, launcher: true }));
                    return;
                }
            }
            else if (mainWindowCloseReason) {
                // If there is a mainWindowCloseReason then we need to fire the window:close event.
                const closeEvent = { type: "window:close" /* BusEventType.WINDOW_CLOSE */, reason: mainWindowCloseReason };
                await this.serviceManager.fire(closeEvent);
            }
            // Create the view:change event and fire it.
            const viewChangeEvent = {
                type: "view:change" /* BusEventType.VIEW_CHANGE */,
                reason: viewChangeReason,
                oldViewState,
                newViewState,
                cancelViewChange: false,
            };
            await this.serviceManager.fire(viewChangeEvent);
            if (viewChangeEvent.cancelViewChange) {
                // If the view changing was canceled in the event then log a message and switch the viewState back to what it was
                // originally.
                store.dispatch(actions.setViewState(oldViewState));
                debugLog('The view changing was cancelled by a view:change event.');
                return;
            }
            // Validate the view state returned from the view:change event.
            if (!validateViewState(viewChangeEvent.newViewState, store.getState())) {
                // If the view state had an issue and was unable to be verified then switch the viewState back to what it was
                // originally.
                store.dispatch(actions.setViewState(oldViewState));
                return;
            }
            // If there were no issues with the new view state then use it.
            newViewState = viewChangeEvent.newViewState;
            // Actually change the viewState in store for the last time.
            store.dispatch(actions.setViewState(deepFreeze$1(newViewState)));
        }
        finally {
            store.dispatch(actions.setViewChanging(false));
        }
    }
    /**
     * To start the tour save all the new tour data in store, fire the view:change events (as well as the window:close
     * events if the main window is open), and then switch to the tour view. If the view:change events are successful then
     * fire the tour:start and tour:step events as well.
     */
    async startTour(message, startTourReason, changeViewReason, skipTourCard = false) {
        // Save all the new tour info in store.
        this.serviceManager.store.dispatch(actions.setTourData(message.fullMessageID));
        // Fire the appropriate events and try to open the tour.
        const newViewState = await this.changeView(ViewType.TOUR, changeViewReason);
        if (newViewState.tour) {
            // If the tour is now visible then fire the tour:start event.
            await this.serviceManager.fire({ type: "tour:start" /* BusEventType.TOUR_START */, reason: startTourReason });
            // Then fire the tour:step event with the first steps info.
            await this.serviceManager.fire({
                type: "tour:step" /* BusEventType.TOUR_STEP */,
                step: this.serviceManager.store.getState().tourState.activeTourStepItems[0],
            });
            // Send a tracking event for the tour starting.
            this.serviceManager.trackService.track({
                eventName: 'Tour interaction',
                eventDescription: 'Tour started.',
                numberOfSteps: this.serviceManager.store.getState().tourState.activeTourStepItems.length,
                skipTourCard,
            });
        }
    }
    /**
     * To end the tour fire the view:change events and switch to the launcher. If the view:change events are successful
     * then clear all the tour data from store.
     */
    async endTour(reason) {
        const { viewState } = this.serviceManager.store.getState().persistedToBrowserStorage.launcherState;
        let newViewState = viewState;
        if (viewState.tour) {
            // If a tour is open then change to the launcher.
            newViewState = await this.changeView(ViewType.LAUNCHER, reason);
        }
        if (!newViewState.tour) {
            // If the tour is no longer visible or wasn't visible then clear the tour data.
            this.serviceManager.store.dispatch(actions.clearTourData());
            return true;
        }
        // If changeView didn't hide the tour, then the tour data was not cleared, so return false.
        return false;
    }
    /**
     * Look for the provided stepId string within the tour step items. If a step with a matching step_id is found then
     * move to that step within the tour.
     */
    goToSpecificTourStep(stepId) {
        const { activeTourStepItems } = this.serviceManager.store.getState().tourState;
        // Find the index of the step with a step_id string matching the provided stepId.
        const newStepIndex = activeTourStepItems.findIndex(stepItem => stepItem.step_id === stepId);
        if (newStepIndex) {
            // If a matching step is found then change to that step in the tour.
            this.changeStepInTour({ newStepIndex });
        }
        else {
            // If a matching step is not found then log an error and don't change the tour step.
            consoleError(`No step with the id "${stepId}" was found within the current tour. As a result, goToTourStep() did not change the current tour step.`);
        }
    }
    /**
     * Change the tour step, either to a specific step, the next step, or the previous step. After changing the step send
     * a tour:step event.
     */
    async changeStepInTour(changeStepOptions) {
        const { store } = this.serviceManager;
        const { activeTourStepItems } = store.getState().tourState;
        const { activeTourCurrentStepIndex } = store.getState().persistedToBrowserStorage.chatState.persistedTourState;
        // Get the new default step number if there is one.
        let { newStepIndex } = changeStepOptions;
        if (changeStepOptions.nextStep) {
            newStepIndex = activeTourCurrentStepIndex + 1;
        }
        else if (changeStepOptions.previousStep) {
            newStepIndex = activeTourCurrentStepIndex - 1;
        }
        // Change to the new step in the tour.
        store.dispatch(actions.changeStepInTour(newStepIndex));
        // Get the new step number from store in case the number that was provided had been out of bounds for the step array.
        const actualNewStepIndex = store.getState().persistedToBrowserStorage.chatState.persistedTourState.activeTourCurrentStepIndex;
        if (actualNewStepIndex === newStepIndex) {
            // Fire the tour step event with the new step info.
            await this.serviceManager.fire({ type: "tour:step" /* BusEventType.TOUR_STEP */, step: activeTourStepItems[actualNewStepIndex] });
        }
        else {
            consoleWarn('The tour tried to change to a step that was out of bounds for the step array, so no step change was made.');
        }
    }
    /**
     * Fires an error event to notify listeners that an error occurred.
     *
     * @param error Details about the error or the error object.
     */
    errorOccurred(error) {
        consoleError('An error has occurred', error);
        if (error.catastrophicErrorType) {
            this.serviceManager.store.dispatch(actions.setAppStateValue('catastrophicErrorType', error.catastrophicErrorType));
        }
        callOnError(this.serviceManager.additionalChatParameters.onError, error);
    }
    /**
     * Restarts the conversation with the assistant. This does not make any changes to a conversation with a human agent.
     * This will clear all the current assistant messages from the main bot view and cancel any outstanding messages.
     * This will also clear any active tour data and re-show the input field if it was hidden. Lastly, this will clear
     * the current assistant session which will force a new session to start on the next message.
     */
    async restartConversation(options = {}) {
        const { skipHydration = false, deleteServerSession = true, endAgentConversation = true, fireEvents = true, } = options;
        debugLog('Restarting conversation');
        if (this.restarting) {
            consoleWarn('You cannot restart a conversation while a previous restart is still pending.');
            return;
        }
        this.restarting = true;
        try {
            const { serviceManager } = this;
            const { store } = serviceManager;
            if (fireEvents) {
                await serviceManager.fire({ type: "pre:restartConversation" /* BusEventType.PRE_RESTART_CONVERSATION */ });
            }
            serviceManager.restartCount++;
            if (!this.serviceManager.store.getState().userID) {
                // We're trying to restart a conversation before we've loaded any user conversations so apply this to the
                // next one.
                this.serviceManager.discardNextUserSession = true;
            }
            if (this.hydrating) {
                await this.hydrationPromise;
            }
            const currentState = store.getState();
            const { persistedToBrowserStorage, config } = currentState;
            const { viewState } = persistedToBrowserStorage.launcherState;
            // If we're connected to an agent, we need to end the agent chat.
            const { isConnecting } = currentState.agentState;
            const { isConnected } = currentState.persistedToBrowserStorage.chatState.agentState;
            if ((isConnected || isConnecting) && endAgentConversation) {
                await serviceManager.humanAgentService.endChat(true, false, false);
            }
            if (viewState.tour) {
                // If a tour is open then try to open the main window. Specify not to hydrate the chat because hydration is
                // planned to happen after restart:conversation has fired below. We don't need to clear the tour data in store
                // since the restartConversation redux action will do that.
                await this.serviceManager.actions.changeView(ViewType.MAIN_WINDOW, { mainWindowOpenReason: MainWindowOpenReason.CALLED_RESTART_CONVERSATION }, false);
            }
            this.serviceManager.instance.updateAssistantInputFieldVisibility(true);
            this.serviceManager.messageService.cancelAllMessageRequests();
            if (deleteServerSession) {
                const { sessionID } = persistedToBrowserStorage.chatState;
                await this.deleteServerSession(sessionID, config.public);
            }
            store.dispatch(actions.restartConversation());
            if (!skipHydration) {
                // Clear this promise in case the restart event below triggers another hydration.
                this.hydrationPromise = null;
            }
            if (fireEvents) {
                await serviceManager.fire({ type: "restartConversation" /* BusEventType.RESTART_CONVERSATION */ });
            }
            if (this.hydrating) {
                await this.hydrationPromise;
            }
            if (!skipHydration && !serviceManager.store.getState().isHydrated) {
                // Trigger re-hydration.
                this.hydrationPromise = null;
                if (store.getState().persistedToBrowserStorage.launcherState.viewState.mainWindow) {
                    await serviceManager.actions.hydrateChat();
                }
            }
            else {
                store.dispatch(actions.chatWasHydrated());
            }
        }
        finally {
            this.restarting = false;
        }
    }
    /**
     * Remove any record of the current session from the browser.
     *
     * @param keepOpenState If we are destroying the session to restart the chat this can be used to preserve if the web
     * chat is open.
     */
    async destroySession(keepOpenState) {
        const { store } = this.serviceManager;
        const { userID, config, persistedToBrowserStorage } = store.getState();
        const { sessionID } = persistedToBrowserStorage.chatState;
        const originalViewState = persistedToBrowserStorage.launcherState.viewState;
        const newPersistedToBrowserStorage = cloneDeep(DEFAULT_PERSISTED_TO_BROWSER);
        if (keepOpenState) {
            // If we want to keep the open state then copy it from browser storage.
            newPersistedToBrowserStorage.launcherState.viewState = originalViewState;
            if (originalViewState.tour) {
                // If a tour was previously open then change the viewState to close the tour and open the main window instead.
                newPersistedToBrowserStorage.launcherState.viewState = { ...originalViewState, tour: false, mainWindow: true };
            }
        }
        else {
            // If we don't want to keep the open state then set the launcher to be open.
            newPersistedToBrowserStorage.launcherState.viewState = VIEW_STATE_LAUNCHER_OPEN;
        }
        this.serviceManager.messageService.cancelAllMessageRequests();
        this.serviceManager.userSessionStorageService.clearLauncherSession(config.public.integrationID);
        this.serviceManager.userSessionStorageService.clearChatSession(userID, config.public.integrationID);
        this.serviceManager.store.dispatch(actions.setAppStateValue('persistedToBrowserStorage', newPersistedToBrowserStorage));
        // Now delete the session from the server.
        await this.deleteServerSession(sessionID, config.public);
    }
    /**
     * Deletes the session from the server.
     */
    async deleteServerSession(sessionID, publicConfig) {
        if (sessionID) {
            const toolingType = this.serviceManager.store.getState().config.public.__ibm__?.toolingType;
            if (toolingType && toolingType !== ToolingType.PREVIEW_LINK) {
                // Don't try to delete the session in tooling. This would need to call the tooling proxy server but we don't
                // currently have support for that.
                return;
            }
            const sessionURL = getSessionURL(publicConfig, sessionID);
            try {
                // Note that this allows for a JWT retry but if this code is being called as part of an onbeforeunload event,
                // then this will not work. The browser will not allow the retry to occur.
                const result = await doWithJWTRetry(() => {
                    const { identityToken } = this.serviceManager.store.getState();
                    return this.serviceManager.fetch(sessionURL, {
                        method: 'DELETE',
                        // This is necessary for the use case where this method is being called from onbeforeunload. In that use
                        // case, the browser will normally cancel all outstanding requests presenting this from working. This flag
                        // will allow the browser to continue running the call.
                        keepalive: true,
                        headers: {
                            [API_DEBUGGING_HEADER_NAME]: uuid(UUIDType.MISCELLANEOUS),
                            [HEADER_AUTHORIZATION]: identityToken ? `Bearer ${identityToken}` : undefined,
                        },
                    });
                }, this.serviceManager);
                if (!result.ok) {
                    const message = await safeFetchText(result);
                    consoleError('Failed to delete server session or session is already deleted', message);
                }
            }
            catch (error) {
                consoleError('Failed to delete server session or session is already deleted', error);
            }
        }
    }
    /**
     * Sends a message to the bot that is created from a given suggestion item.
     *
     * @param suggestionItem The suggestion that was selected and should be sent to the bot.
     * @param sourceResponse The source response that produced the current set of suggestions. This is used to
     * determine which context to send with the request.
     * @param index The index within the suggestion list that this suggestion is from.
     * @param section The section containing the selected item.
     */
    async sendSuggestionChoice(suggestionItem, sourceResponse, index, section) {
        let messageRequest;
        if (isSuggestionListItem(suggestionItem)) {
            messageRequest = createMessageRequestForChoice(suggestionItem.option, sourceResponse?.id);
        }
        else {
            messageRequest = createMessageRequestForText(suggestionItem.label);
        }
        // It's possible we don't have a source response in the case where a third strike has occurred because we throw
        // away the original response and replace it with a request and response for the contact (connect to agent)
        // option. This is perhaps not ideal and we should try to save the original context but this works for now. When
        // this happens, we'll fall back to the default send behavior that copies the context from whatever the last
        // response was.
        if (sourceResponse) {
            // We want to copy the context from the original response that created these suggestions so we always go back to
            // that point in time if the user re-opens the suggestions menu and chooses a different suggestion.
            messageRequest.context = cloneDeep(sourceResponse.context);
            if (messageRequest.context?.global?.system) {
                // We don't want reset the turn_count. This will allow the back-end to advance the turn count normally.
                delete messageRequest.context.global.system.turn_count;
            }
        }
        let messageSource;
        switch (section) {
            case AlternateSuggestionsSection.ALTERNATE_RESPONSES:
                messageSource = MessageSendSource.SUGGESTIONS_MESSAGE;
                break;
            case AlternateSuggestionsSection.CONTACT_OPTIONS:
                messageSource = MessageSendSource.SUGGESTIONS_CONTACT;
                break;
            case AlternateSuggestionsSection.STARTERS:
                messageSource = MessageSendSource.SUGGESTIONS_STARTER;
                break;
            default: {
                messageSource = MessageSendSource.OTHER;
                break;
            }
        }
        this.serviceManager.store.dispatch(actions.setSuggestionsSelectedIndex(index, section));
        let suggestionType;
        switch (section) {
            case AlternateSuggestionsSection.ALTERNATE_RESPONSES:
                suggestionType = 'Alternate Response';
                break;
            case AlternateSuggestionsSection.CONTACT_OPTIONS:
                suggestionType = 'Contact Option';
                break;
            case AlternateSuggestionsSection.STARTERS:
                suggestionType = 'Home Screen Starter';
                break;
            default:
                suggestionType = 'Unknown';
                break;
        }
        let suggestionsMenuIsOpened;
        const { panelState } = this.serviceManager.store.getState().alternateSuggestionsState;
        if (panelState === AlternateSuggestionsPanelState.OPEN_FULL) {
            suggestionsMenuIsOpened = 'Expanded';
        }
        else if (panelState === AlternateSuggestionsPanelState.OPEN_STRIKE) {
            suggestionsMenuIsOpened = 'Collapsed';
        }
        else {
            suggestionsMenuIsOpened = 'Closed';
        }
        // Send the message based on the button the user clicked.
        await this.send(messageRequest, messageSource, {
            trackEventProps: {
                eventName: 'Suggestion Selected',
                eventDescription: 'User chose an option from the suggestion menu.',
                suggestionsMenuIsOpened,
                suggestionType,
            },
            isSuggestion: true,
            // Also make sure we only use the context we provided here on the message and don't try to merge in anything
            // from a previous message (unless we don't happen to have a source response in which case we should just use
            // whatever was the last response).
            dontCopyContext: Boolean(sourceResponse),
            additionalInputOptions: {
                // If the user chooses a suggestion, we don't want to allow that to be a digression that may return to the
                // current flow. Choosing a suggestion is generally done because the user is not on the right flow at all.
                // This will restart processing from the dialog root.
                restart: true,
            },
        });
    }
    /**
     * Sends a message to the bot that is created from a given option. Variables set after the disambiguation response
     * will be reset depending on the skill type.
     *
     * Note: User defined variables set after a disambiguation response will be reset to reflect the response context for
     * dialog. As for actions, only session variables set in the middle of an incomplete action will be reset when
     * clicking a disambiguation option.
     *
     * @param option The disambiguation option that was selected.
     * @param localMessage The local message of the original disambiguation response the option is from.
     * @param originalMessage The original message of the original disambiguation response the option is from.
     * @param index The index position of the selected option.
     * @param isDialog Indicates if the disambiguation response is from a dialog skill.
     */
    async sendDisambiguationOption(option, localMessage, originalMessage, index, isDialog) {
        const disambiguationResponse = originalMessage;
        const localMessageID = localMessage.ui_state.id;
        const { id: responseID } = disambiguationResponse;
        const { mainConfig } = this.serviceManager.store.getState().config.remote;
        const messageRequest = createMessageRequestForChoice(option, responseID);
        // Copy response context from disambiguation response to request in order to rollback conversation at the point in
        // time of the response for dialog. For actions, the 'actions skill' context will not be used to roll back the
        // conversation and instead reset variables that were not previously defined before an action was completed.
        messageRequest.context = cloneDeep(disambiguationResponse.context);
        if (messageRequest.context?.global?.system) {
            // We don't want reset the turn_count. This will allow the back-end to advance the turn count normally.
            delete messageRequest.context.global.system.turn_count;
        }
        // Get the last response to determine the skill type in use to help resetting variables that should no longer be defined.
        const storeState = this.serviceManager.store.getState();
        const lastResponse = getLastBotResponseWithContext(storeState);
        // We check if the last response contains an actions skill context so that we may determine if session variables
        // need to be reset.
        // Note: Uncomment this block of code when actions is updated with the necessary data this feature depends on.
        // if (ACTIONS_SKILL in lastResponse.context.skills) {
        //   // Check that the last response isn't a disambiguation response since we should only be resetting context while in
        //   // the middle of an incomplete action. Disambiguation responses only appear in between actions.
        //   const isSuggestionResponse = lastResponse.output.generic[0].response_type === MessageResponseTypes.SUGGESTION;
        //
        //   if (!isSuggestionResponse && isIncompleteAction(lastResponse)) {
        //     // Get the previous response from the one that started the current action the user is in so that we can revert
        //     // back to that point in time before the current action started since it wasn't completed.
        //     const beforeLatestActionResponse = this.getResponseBeforeActionVisited(storeState);
        //     (messageRequest.context as TempMessageContext).skills[ACTIONS_SKILL] = cloneDeep(
        //       beforeLatestActionResponse.context.skills[ACTIONS_SKILL] || { skill_variables: {} },
        //     );
        //
        //     // It could be the case that the context we received from the last response before the current action started
        //     // does not have a skill_variables property.
        //     if (!(messageRequest.context as TempMessageContext).skills[ACTIONS_SKILL].skill_variables) {
        //       (messageRequest.context as TempMessageContext).skills[ACTIONS_SKILL].skill_variables = {};
        //     }
        //
        //     const currentVariables = (lastResponse.context as TempMessageContext).skills[ACTIONS_SKILL].skill_variables;
        //     const oldVariables = (messageRequest.context as TempMessageContext).skills[ACTIONS_SKILL].skill_variables;
        //     nullifyUndefinedProperties(oldVariables, currentVariables);
        //   } else {
        //     // Since the user is not in the middle of an action we can keep the context at the point in time of the last
        //     // response.
        //     (messageRequest.context as TempMessageContext).skills[ACTIONS_SKILL] = cloneDeep(
        //       lastResponse.context.skills[ACTIONS_SKILL],
        //     );
        //   }
        // }
        // During a channel transfer the user_defined property may not be provided so we have to check for it.
        if (isDialog && lastResponse) {
            const dialogSkillID = getDialogSkillID(lastResponse.context, mainConfig);
            const currentVariables = lastResponse.context?.skills?.[dialogSkillID]?.user_defined;
            // Context variables received from disambiguation response.
            const oldVariables = messageRequest.context?.skills?.[dialogSkillID]?.user_defined || {};
            nullifyUndefinedProperties(oldVariables, currentVariables);
        }
        // Track when user clicks on suggestion/disambiguation option.
        await this.sendWithCatch(messageRequest, MessageSendSource.DISAMBIGUATION, {
            trackEventProps: {
                eventName: 'Assistant Message Received from User',
                type: 'disambiguation',
                optionSelected: index + 1, // Start index at 1 for better reading in amplitude.
            },
            setValueSelectedForMessageID: localMessageID,
            // We only want to use the context from the original disambiguation response, and any 'user_defined' or
            // 'skill variables' that were reset.
            dontCopyContext: true,
        });
    }
    /**
     * Ends the conversation with a human agent. This does not request confirmation from the user first. If the user
     * is not connected or connecting to a human agent, this function has no effect. You can determine if the user is
     * connected or connecting by calling {@link ChatInstance.getState}. Note that this function
     * returns a Promise that only resolves when the conversation has ended. This includes after the
     * {@link BusEventType.AGENT_PRE_END_CHAT} and {@link BusEventType.AGENT_END_CHAT} events have been fired and
     * resolved.
     */
    agentEndConversation(endedByUser) {
        return this.serviceManager.humanAgentService.endChat(endedByUser);
    }
    /**
     * Sets the suspended state for an agent conversation. A conversation can be suspended or un-suspended only if the
     * user is currently connecting or connected to an agent. If a conversation is suspended, then messages from the user
     * will no longer be routed to the service desk and incoming messages from the service desk will not be displayed. In
     * addition, the current connection status with an agent will not be shown.
     */
    agentUpdateIsSuspended(isSuspended) {
        this.serviceManager.store.dispatch(agentUpdateIsSuspended(isSuspended));
    }
    /**
     * Creates the custom response elements for all the messages in the given set. This is used in particular when
     * loading a list of messages from history.
     */
    async createElementsForUserDefinedResponses(messages) {
        await asyncForEach(Object.values(messages.allMessageItemsByID), localMessage => {
            const originalMessage = messages.allMessagesByID[localMessage.fullMessageID];
            return this.handleUserDefinedResponseItems(localMessage, originalMessage);
        });
    }
}

const HANDLER_NOT_FUNCTION = 'The event handler is not a function.';
class EventBus {
    constructor() {
        /**
         * This is a map of all the event handlers by type with the map key being the type of event (e.g. "send").
         */
        this.handlersByType = new Map();
        /**
         * This set is used to keep track of which events are currently running. This is to prevent the same event from
         * running more than once at the same time. This check is only performed on asynchronous events and does not cover
         * the "*" event.
         */
        this.eventsTypesRunning = new Set();
        /**
         * The current number of async events that are currently running.
         */
        this.eventsRunningCount = 0;
    }
    /**
     * Fires the given event and notifiers all listeners for this event type. All event listeners that listen for all
     * ("*") events will also be notified. Events will be fired in the order in which they were registered.
     *
     * @param busEvent A single event.
     * @param instance The current instance of the web chat that is passed to the event handlers
     */
    async fire(busEvent, instance) {
        logEvent('Before fire', busEvent);
        const { type } = busEvent;
        if (!type) {
            throw new Error(`Attempted to fire an event with no type! ${JSON.stringify(busEvent)}`);
        }
        function wrappedHandler(handler) {
            const result = handler(busEvent, instance);
            if (result && !(result instanceof Promise)) {
                consoleWarn(`An event handler for event ${type} returned a non-promise. This might be a mistake.`, result);
            }
            return result;
        }
        if (this.eventsTypesRunning.has(type)) {
            throw new Error(`An event of type ${type} is already running. Please make sure that you have resolved the Promises for any earlier events that were fired.`);
        }
        try {
            this.eventsRunningCount++;
            try {
                this.eventsTypesRunning.add(type);
                // Run all the handlers for the given type.
                const handlersForType = this.handlersByType.get(type);
                if (handlersForType && handlersForType.length) {
                    // Copy the array in case it's modified by an event handler.
                    const handlersCopy = handlersForType.slice();
                    await asyncForEach(handlersCopy, wrappedHandler);
                }
            }
            finally {
                this.eventsTypesRunning.delete(type);
            }
            // What about this??
            // Then we rerun with '*'.
            const handlersForAllType = this.handlersByType.get("*" /* BusEventType.ALL_EVENTS */);
            if (handlersForAllType && handlersForAllType.length) {
                // Copy the array in case it's modified by an event handler.
                const handlersCopy = handlersForAllType.slice();
                await asyncForEach(handlersCopy, wrappedHandler);
            }
        }
        finally {
            this.eventsRunningCount--;
            if (this.waitForEmptyPromise && this.eventsRunningCount === 0) {
                // If waitForEmpty is waiting for all the events to finish and we've just finished the last one, then let it
                // know.
                this.waitForEmptyPromise.doResolve();
            }
        }
        logEvent('After fire', busEvent);
    }
    /**
     * Fires the given event and notifiers all listeners for this event type. All event listeners that listen for all
     * ("*") events will also be notified. Events will be fired in the order in which they were registered. This
     * function fires the events synchronously.
     *
     * @param busEvent A single event.
     * @param instance The current instance of the web chat that is passed to the event handlers
     */
    fireSync(busEvent, instance) {
        logEvent('Before fire', busEvent);
        const { type } = busEvent;
        // Run all the handlers for the given type.
        const handlersForType = this.handlersByType.get(type);
        if (handlersForType && handlersForType.length) {
            // Copy the array in case it's modified by an event handler.
            const handlersCopy = handlersForType.slice();
            handlersCopy.forEach(handler => handler(busEvent, instance));
        }
        // Then we rerun with '*'.
        const handlersForAllType = this.handlersByType.get("*" /* BusEventType.ALL_EVENTS */);
        if (handlersForAllType && handlersForAllType.length) {
            // Copy the array in case it's modified by an event handler.
            const handlersCopy = handlersForAllType.slice();
            handlersCopy.forEach(handler => handler(busEvent, instance));
        }
        logEvent('After fire', busEvent);
    }
    /**
     * This function will wait for all executing async events to finish. If any new events are fired while this
     * function is waiting, it will wait for those as well.
     */
    async waitForEmpty() {
        if (this.eventsRunningCount === 0) {
            return;
        }
        if (!this.waitForEmptyPromise) {
            this.waitForEmptyPromise = resolvablePromise();
        }
        await this.waitForEmptyPromise;
        this.waitForEmptyPromise = null;
    }
    /**
     * Adds the given event handler as a listener for events of the given type.
     *
     * @param handlers The handler or handlers along with the event type to start listening for events.
     * @returns The instance for method chaining.
     */
    on(handlers) {
        const data = asArray(handlers);
        data.forEach(({ type, handler }) => {
            if (!type) {
                throw new Error(`Attempted to listen to an event with no type: "${type}"!`);
            }
            if (typeof handler === 'function') {
                if (!this.handlersByType.has(type)) {
                    this.handlersByType.set(type, []);
                }
                const handlersForType = this.handlersByType.get(type);
                logWarnings(type);
                debugStackTrace(`[EventBus] Adding ${type} event handler`, handler);
                handlersForType.push(handler);
            }
            else {
                consoleError(HANDLER_NOT_FUNCTION, handler);
            }
        });
        return this;
    }
    /**
     * Removes an event listener that was previously added via {@link on} or {@link once}.
     *
     * @param handlers The handler or handlers along with the event type to stop listening for events.
     * @returns The instance for method chaining.
     */
    off(handlers) {
        const data = asArray(handlers);
        data.forEach(({ type, handler }) => {
            const handlersForType = this.handlersByType.get(type);
            if (handlersForType) {
                if (handler) {
                    const index = handlersForType.indexOf(handler);
                    if (index !== -1) {
                        const removed = handlersForType.splice(index, 1);
                        debugStackTrace(`[EventBus] Removing ${type} event handlers`, removed);
                    }
                }
                else {
                    // If no handler is specified, unsubscribe all the handlers.
                    debugStackTrace(`[EventBus] Removing all ${type} event handlers`);
                    this.handlersByType.set(type, []);
                }
            }
        });
        return this;
    }
    /**
     * Adds the given event handler as a listener for events of the given type. After the first event is handled, this
     * handler will automatically be removed.
     *
     * @param handlers The handler or handlers along with the event type to start listening for an event.
     * @returns The instance for method chaining.
     */
    once(handlers) {
        const data = asArray(handlers);
        data.forEach(({ type, handler }) => {
            if (typeof handler === 'function') {
                const onceHandler = (event, instance) => {
                    this.off({ type, handler: onceHandler });
                    return handler(event, instance);
                };
                this.on({ type, handler: onceHandler });
            }
            else {
                consoleError(HANDLER_NOT_FUNCTION, handler);
            }
        });
        return this;
    }
    /**
     * Outputs debug information for all of the currently registered event bus listeners.
     */
    logListeners() {
        this.handlersByType.forEach((listeners, type) => {
            console.group(`Event ${type} (${listeners.length})`);
            listeners.forEach(listener => {
                consoleLog('Listener', listener);
            });
            console.groupEnd();
        });
    }
    clear() {
        this.handlersByType.clear();
        return this;
    }
}
/**
 * Outputs the given event to the console.
 */
function logEvent(message, busEvent) {
    if (isEnableDebugLog()) {
        // If this object is modified after we log it, the output may not actually show the original value so making a
        // copy ensure we see the actual value that it had at this moment.
        const eventCopy = cloneDeep(busEvent);
        debugLog(`[EventBus] ${message}`, eventCopy);
    }
}
/**
 * Logs warning for deprecated events.
 */
function logWarnings(type) {
    switch (type) {
        case "window:close" /* BusEventType.WINDOW_CLOSE */:
        case "window:open" /* BusEventType.WINDOW_OPEN */:
        case "window:pre:close" /* BusEventType.WINDOW_PRE_CLOSE */:
        case "window:pre:open" /* BusEventType.WINDOW_PRE_OPEN */:
            consoleWarn(`Listening for event ${type}. ${WINDOW_WARNING}`);
            break;
        case "customResponse" /* BusEventType.CUSTOM_RESPONSE */:
            consoleWarn(`Listening for event ${type}. ${CUSTOM_RESPONSE_WARNING}`);
            break;
    }
}

/**
 * This function takes in the service manager to help create a custom panel instance. The panel instance is created
 * using a function instead of a class because a private property at runtime can still be accessible. The service
 * manager is passed in instead made a private property.
 */
function createCustomPanelInstance(serviceManager) {
    const { config } = serviceManager.store.getState();
    let hostElement;
    if (!config.public.__ibm__?.useShadowRoot) {
        hostElement = document.createElement('div');
        hostElement.className = 'WACCustomPanel__HostElement';
    }
    const customPanelInstance = {
        open(options = DEFAULT_CUSTOM_PANEL_CONFIG_OPTIONS) {
            const { store } = serviceManager;
            store.dispatch(actions.setCustomPanelConfigOptions(options));
            store.dispatch(actions.setCustomPanelOpen(true));
        },
        close() {
            serviceManager.store.dispatch(actions.setCustomPanelOpen(false));
        },
    };
    if (hostElement) {
        customPanelInstance.hostElement = hostElement;
    }
    return Object.freeze(customPanelInstance);
}

/**
 * This function takes in the service manager to help create a custom panel manager. The panel manager is created
 * using a function instead of a class because a private property at runtime can still be accessible. Instead of
 * creating a private panels property we create the variable within the scope of the function.
 */
function createCustomPanelManager(serviceManger) {
    // A panels object holding all created panels. In the future if we ever support multiple panels, Deb would be able to
    // populate this object.
    const panels = {
        [DEFAULT_CUSTOM_PANEL_ID]: createCustomPanelInstance(serviceManger),
    };
    return Object.freeze({
        getPanel() {
            return panels[DEFAULT_CUSTOM_PANEL_ID];
        },
    });
}

/**
 * The different type of error states a given message can be in.
 */
var MessageErrorState;
(function (MessageErrorState) {
    /**
     * No errors.
     */
    MessageErrorState[MessageErrorState["NONE"] = 1] = "NONE";
    /**
     * The message failed to be sent and no more attempts will be made.
     */
    MessageErrorState[MessageErrorState["FAILED"] = 2] = "FAILED";
    /**
     * There was an error sending the message but the system is retrying the message.
     */
    MessageErrorState[MessageErrorState["RETRYING"] = 3] = "RETRYING";
    /**
     * Indicates that the previous message has entered the retrying state and that this message is waiting for it to
     * finish or fail. This message will remain in the waiting state until it finishes successfully or it enters a
     * retrying state itself.
     */
    MessageErrorState[MessageErrorState["WAITING"] = 4] = "WAITING";
})(MessageErrorState || (MessageErrorState = {}));

/**
 * Given an array of every received and sent message, change to correct data format. Eventually, this is going to
 * have to work with paginated data.
 *
 * Note that this function also has side effects because it fires history events as it is processing the loaded history.
 *
 * @param notes An array of the {@link Note} objects returned from the history store.
 * @param serviceManager A reference to the {@link ServiceManager}.
 */
async function notesToLoadedHistory(notes, serviceManager) {
    // Create an empty version of our state and the final result object.
    const allLocalMessagesByID = {};
    const allMessagesByID = {};
    const loadingState = {
        serviceManager,
        allMessages: [],
        allMessagesByID,
        allLocalMessagesByID,
        threadMessagesByThreadID: {},
        responsesByRequestID: {},
        relatedMessageByID: {},
        localMessagesByOriginalMessageID: {},
        lastThreadID: null,
        loadedHistory: {
            messageHistory: {
                allMessageItemsByID: allLocalMessagesByID,
                allMessagesByID,
                botMessageState: null,
            },
            latestTransferToAgentResponse: null,
            latestPanelLocalMessageItem: null,
        },
        latestMessageResponsePanelID: null,
    };
    const sessionHistoryEnabled = !serviceManager.store?.getState().config.public.disableSessionHistory;
    // First locate all the messages from history we need.
    await notesToMessages(notes, loadingState, sessionHistoryEnabled);
    if (!loadingState.allMessages.length) {
        // If we have the latest transfer to agent response and no other message, it means session history is disabled, and
        // we only have this response to render.
        if (loadingState.loadedHistory.latestTransferToAgentResponse) {
            return {
                ...loadingState.loadedHistory,
                messageHistory: DEFAULT_MESSAGE_STATE,
            };
        }
        // If we didn't actually find any messages, just return null to indicate that. This will trigger the widget to
        // get fetch the welcome node.
        return null;
    }
    // Generate the LocalMessage objects for the messages and divide into threads.
    createLocalMessages(loadingState);
    // Create the ChatMessagesState that correspond to each of the threads.
    createChatStates(loadingState);
    // Locate the most recent welcome node and mark it as such.
    markIsLatestWelcomeNode(loadingState);
    // Find all the options and suggestions and figure out which, if any, options the user chose.
    markSelectedOptions(loadingState);
    // Note: We need to consider if we should clone the LocalMessage objects that get put into multiple threads
    // (namely the first and last message in a thread that's also added to the main thread). If anything attempts to
    // modify the ui_state value in those messages, it will affect both. Right now the only time that happens is the
    // property for doing a11y announcements but since that property isn't active for historical messages, we don't
    // really have to do this now. This is a bit of a landmine I'm leaving here for now so sorry :-). To do the clone
    // the only thing that needs to be different is the ID.
    return loadingState.loadedHistory;
}
/**
 * Converts the given list of {@link Note} objects into a flat list of all the message objects to process.
 */
async function notesToMessages(notes, loadingState, sessionHistoryEnabled) {
    const { allMessages, allMessagesByID, responsesByRequestID, relatedMessageByID, serviceManager, localMessagesByOriginalMessageID, } = loadingState;
    if (!notes?.length) {
        return;
    }
    // Find all the messages from history.
    notes.forEach(note => {
        if (note.type === NoteType.HISTORY) {
            const sessionHistory = note.body;
            const pushAndPrepareMessage = (historyItem) => {
                const { message } = historyItem;
                // If this is a message event we need to do special things.
                if (isEventRequest(message)) {
                    if (isUpdateHistoryRequest(message)) {
                        const partialMessage = message.input.event.partial_message;
                        // If there's a thread_id saved on the message event remove it since we should use the original message thread.
                        if (partialMessage?.thread_id) {
                            delete partialMessage.thread_id;
                        }
                        // If there's history on the message event make sure the timestamp is cleared since we want to use the original message timestamp.
                        if (partialMessage?.history?.timestamp) {
                            delete partialMessage.history.timestamp;
                        }
                        if (allMessagesByID[message.input.event.messageID]) {
                            // Combine the updates from the eventMessage with the original message.
                            mergeWith(allMessagesByID[message.input.event.messageID], partialMessage, replaceCurrentArrayValue);
                        }
                        else {
                            // If the message doesn't exist we'll add it to the list.
                            addMessage(partialMessage, loadingState, historyItem);
                        }
                    }
                }
                else if (isRequest(message) || isResponse(message)) {
                    addMessage(message, loadingState, historyItem);
                }
            };
            if (isChannelTransferPage()) {
                // We want to display only the latest message in the conversation when session history is disabled.
                if (!sessionHistoryEnabled) {
                    const latestHistoryItemIndex = findLastIndex(sessionHistory, item => !isEventRequest(item.message));
                    if (latestHistoryItemIndex) {
                        // We want to keep only the last real message but that message may be modified by history update events that
                        // come after it so we need to keep the last non-event message plus all the event messages that come after it.
                        for (let index = latestHistoryItemIndex; index < sessionHistory.length; index++) {
                            pushAndPrepareMessage(sessionHistory[index]);
                        }
                    }
                }
                else {
                    sessionHistory.forEach(pushAndPrepareMessage);
                }
                // Get the channel transfer to agent response if it's the latest message.
                findAndRemoveTransferToAgentResponse(allMessages, loadingState.loadedHistory);
            }
            else {
                sessionHistory.forEach(pushAndPrepareMessage);
                storeLatestMessageResponsePanelID(allMessages, loadingState);
            }
        }
    });
    // We need to do a little more processing on all the events now. We iterate backwards just to make it easier to
    // remove items.
    for (let index = allMessages.length - 1; index >= 0; index--) {
        const message = allMessages[index];
        if (message.history?.file_upload_status === FileStatusValue.UPLOADING) {
            // If a file upload was in the middle of uploading and the user left before it was complete, we need to mark
            // it as complete and also display it as an error.
            message.history.file_upload_status = FileStatusValue.COMPLETE;
            message.history.error_state = MessageErrorState.FAILED;
        }
        if (isResponse(message) && message.history.silent) {
            // If we find a message response that was silent, we need to actually throw it away. We treat these like
            // messages that were never actually received. This is currently only used for hiding the responses we get on
            // a 3rd strike from suggestions.
            allMessages.splice(index, 1);
            delete allMessagesByID[message.id];
        }
        else {
            localMessagesByOriginalMessageID[message.id] = [];
            if (isResponse(message) && message.request_id) {
                responsesByRequestID[message.request_id] = message;
            }
            if (message.history.relatedMessageID) {
                relatedMessageByID[message.history.relatedMessageID] = message;
            }
        }
    }
    if (!allMessages.length) {
        // If we didn't find any messages, then return right now.
        return;
    }
    // Freeze the array so the listeners can't mess with it.
    Object.freeze(allMessages);
    // Fire the event that says we're loading from history. The messages are allowed to be modified at this point.
    const beginEvent = { type: "history:begin" /* BusEventType.HISTORY_BEGIN */, messages: allMessages };
    await serviceManager.eventBus.fire(beginEvent, serviceManager.instance);
    if (beginEvent.updateMessageIDs?.length) {
        // Trigger updates to each message that was changed.
        await asyncForEach(beginEvent.updateMessageIDs, async (messageID) => {
            const message = allMessagesByID[messageID];
            await serviceManager.actions.sendUpdateHistoryEvent(messageID, message);
        });
    }
    // The message is not allowed to be modified so freeze it.
    allMessages.forEach(deepFreeze$1);
    // Fire the event that says we're done loading from history.
    await serviceManager.eventBus.fire({ type: "history:end" /* BusEventType.HISTORY_END */, messages: allMessages }, serviceManager.instance);
}
/**
 * Adds the given message to the messages lists.
 */
function addMessage(message, loadingState, historyItem) {
    // Make sure the message has the timestamp that was generated by the service and not the potentially
    // client generated timestamp it originally had.
    message.history = message.history || {};
    message.history.from_history = true;
    message.history.timestamp = new Date(historyItem.time).getTime();
    if (message.thread_id !== THREAD_ID_MAIN) {
        loadingState.lastThreadID = message.thread_id;
    }
    loadingState.allMessagesByID[message.id] = message;
    loadingState.allMessages.push(message);
}
/**
 * Creates all the {@link LocalMessageItem} objects that correspond to all the {@link GenericItem} values in all the
 * given message requests and responses.
 */
function createLocalMessages(loadingState) {
    const { allMessages, allLocalMessagesByID, localMessagesByOriginalMessageID } = loadingState;
    allMessages.forEach(message => {
        if (isRequest(message)) {
            if (!message.history?.silent) {
                const text = message.history?.label || message.input.text;
                const localMessage = inputItemToLocalItem(message, text);
                localMessagesByOriginalMessageID[message.id].push(localMessage);
                allLocalMessagesByID[localMessage.ui_state.id] = localMessage;
            }
        }
        else {
            const items = getResponseItems(message);
            if (items?.length) {
                items.forEach(messageItem => {
                    // Pause messages should be ignored when loaded from history. We should also ignore any messages that aren't
                    // targeted for the chat channel.
                    if (isForChatChannel(messageItem) && !isPause(messageItem)) {
                        const localMessage = outputItemToLocalItem(messageItem, message, false);
                        // Since the panel response type doesn't render a message, its local message item doesn't need to be stored.
                        if (!isPanelResponseType(localMessage.item)) {
                            localMessagesByOriginalMessageID[message.id].push(localMessage);
                            allLocalMessagesByID[localMessage.ui_state.id] = localMessage;
                        }
                        if (isResponseWithNestedItems(localMessage.item)) {
                            const nestedLocalMessageItems = [];
                            createLocalMessageItemsForNestedMessageItems(localMessage, message, true, nestedLocalMessageItems, true);
                            nestedLocalMessageItems.forEach(localMessageItem => {
                                const localMessageID = localMessageItem.ui_state.id;
                                loadingState.loadedHistory.messageHistory.allMessageItemsByID[localMessageID] = localMessageItem;
                            });
                        }
                        // Store the local message item id of the latest panel response type using the stored message response id
                        // associated with the item so the response panel can be opened on refresh.
                        if (loadingState.latestMessageResponsePanelID === localMessage.fullMessageID &&
                            !loadingState.loadedHistory.latestPanelLocalMessageItem &&
                            isPanelResponseType(localMessage.item)) {
                            loadingState.loadedHistory.latestPanelLocalMessageItem = localMessage;
                        }
                    }
                });
            }
        }
        // Add the message to the right thread or threads but only if it actually has something visible.
        if (localMessagesByOriginalMessageID[message.id]?.length) {
            addMessageToThread(message, loadingState);
        }
    });
}
/**
 * Returns all the generic items for the given message either if it's a message response or a local response.
 */
function getResponseItems(message) {
    if (isResponse(message)) {
        return message.output.generic;
    }
    return null;
}
/**
 * Adds the given message to the given thread. If the message is the first message in its thread, it will also be
 * added to the main thread.
 */
function addMessageToThread(message, loadingState) {
    // Messages from the server may have a thread ID if a multi-turn has started. Make sure anything that's not an
    // agent thread is put into the main thread.
    const { threadMessagesByThreadID } = loadingState;
    let thread = threadMessagesByThreadID[THREAD_ID_MAIN];
    if (!thread) {
        thread = [];
        threadMessagesByThreadID[THREAD_ID_MAIN] = thread;
    }
    thread.push(message);
}
/**
 * Creates all of the {@link ChatMessagesState} objects that all the appropriate threads.
 */
function createChatStates(loadingState) {
    const { loadedHistory, threadMessagesByThreadID, localMessagesByOriginalMessageID } = loadingState;
    loadedHistory.messageHistory.botMessageState = toChatMessageState(threadMessagesByThreadID[THREAD_ID_MAIN], localMessagesByOriginalMessageID);
}
/**
 * Creates a {@link ChatMessagesState} for the given array of local messages.
 */
function toChatMessageState(messages, localMessagesByFullMessageID) {
    const localMessageIDs = [];
    const messageIDs = [];
    if (messages) {
        messages.forEach(message => {
            messageIDs.push(message.id);
            localMessagesByFullMessageID[message.id].forEach(localMessage => {
                localMessageIDs.push(localMessage.ui_state.id);
            });
        });
    }
    return {
        ...DEFAULT_CHAT_MESSAGES_STATE,
        localMessageIDs,
        messageIDs,
    };
}
/**
 * Checks if most recent input is a request for a welcome node. If it is, it marks the response ui_state as
 * isLatestWelcomeNode.
 */
function markIsLatestWelcomeNode(loadingState) {
    const { responsesByRequestID, threadMessagesByThreadID, localMessagesByOriginalMessageID } = loadingState;
    const mainThreadMessages = threadMessagesByThreadID[THREAD_ID_MAIN];
    // Look for the most recent welcome message.
    const welcomeRequest = findLast(mainThreadMessages, message => isRequest(message) && message.history.is_welcome_request);
    if (welcomeRequest) {
        // See if we have a response to this request and if so, mark it.
        const welcomeResponse = responsesByRequestID[welcomeRequest.id];
        if (welcomeResponse) {
            localMessagesByOriginalMessageID[welcomeResponse.id].forEach(localMessage => {
                localMessage.ui_state.isWelcomeResponse = true;
            });
        }
    }
}
/**
 * Goes through all the messages looking for the option and suggestion responses. For each of those, this will
 * look to see if there's a related message request where the user chose one of the options and then marks the
 * option or suggestion with the value that was chosen.
 */
function markSelectedOptions({ allMessages, relatedMessageByID, localMessagesByOriginalMessageID }) {
    allMessages.forEach(message => {
        if (isResponse(message)) {
            localMessagesByOriginalMessageID[message.id].forEach(localMessage => {
                if (isOptionOrSuggestion(localMessage)) {
                    // This is either an option or suggestion response. Let's see if another message said it's related to this
                    // message in which case, that other message should tell us which option the user chose.
                    const relatedRequest = relatedMessageByID[message.id];
                    if (isRequest(relatedRequest)) {
                        localMessage.ui_state.optionSelected = relatedRequest;
                    }
                }
                else if (isDateResponseType(localMessage)) {
                    const relatedRequest = relatedMessageByID[message.id];
                    if (isRequest(relatedRequest)) {
                        localMessage.ui_state.originalUserText = relatedRequest.history.label;
                    }
                }
            });
        }
    });
}
/**
 * Removes the transfer to agent response if it's the latest message that hasn't already made a check for agent
 * availability. Once that check is performed, the history of the message is updated so we won't try again.
 */
function findAndRemoveTransferToAgentResponse(messages, loadedHistory) {
    const latestMessage = messages[messages.length - 1];
    // Remove and store the transfer to agent response if it hasn't previously.
    if (isResponse(latestMessage) &&
        isChannelTransferToAgent(latestMessage) &&
        latestMessage.history?.agent_availability === undefined) {
        loadedHistory.latestTransferToAgentResponse = latestMessage;
        messages.pop();
    }
}
/**
 * Retrieves the latest message from the messages list and determine if it contains a panel response type. This will
 * be useful later for retrieving the local message item of the panel response type that should surface if it's the
 * latest message. If the panel was closed, the message id won't be stored.
 */
function storeLatestMessageResponsePanelID(messages, loadingState) {
    const latestMessage = messages[messages.length - 1];
    if (!loadingState.latestMessageResponsePanelID &&
        isResponse(latestMessage) &&
        hasMessageResponsePanel(latestMessage) &&
        !latestMessage.history.is_panel_closed) {
        loadingState.latestMessageResponsePanelID = latestMessage.id;
    }
}

// The parameters to send on fetch requests.
const FETCH_PARAMETERS$1 = {
    method: 'GET',
    headers: {
        [HEADER_ACCEPT]: 'application/json',
        [HEADER_CONTENT_TYPE]: 'application/json',
    },
};
class HistoryService {
    constructor(serviceManager) {
        this.serviceManager = serviceManager;
    }
    /**
     * Fetch from history store. If no history is found (no session or the session has expired), this will return null.
     */
    async loadHistory(useHistory) {
        const state = this.serviceManager.store.getState();
        const { config, persistedToBrowserStorage } = state;
        const publicConfig = config.public;
        const { viewState } = persistedToBrowserStorage.launcherState;
        // If a session ID has been provided in the config, then use this to get the history
        const sessionID = publicConfig.sessionID || persistedToBrowserStorage.chatState?.sessionID;
        const channelTransferWithoutSessionHistory = publicConfig.disableSessionHistory && isChannelTransferPage();
        try {
            let resultData;
            if (useHistory) {
                resultData = useHistory;
            }
            else if (publicConfig.__ibm__?.customLoadHistory) {
                debugLog(`Retrieving conversation history from __ibm__ custom hook`);
                const regionHostname = getRegionHostname(publicConfig);
                resultData = await publicConfig.__ibm__.customLoadHistory(sessionID, regionHostname, publicConfig);
            }
            else if (publicConfig.messaging?.customLoadHistory) {
                const items = await publicConfig.messaging.customLoadHistory(this.serviceManager.instance);
                // The "author" property is not currently included in our public NoteItem type. Web chat does not use it.
                const note = { type: NoteType.HISTORY, body: items };
                resultData = { notes: [note] };
            }
            else if (sessionID &&
                ((!publicConfig.disableSessionHistory && !publicConfig.servers.skipHistory) ||
                    channelTransferWithoutSessionHistory)) {
                // Even if session history is disabled, we still want to fetch the history for a conversation on a channel
                // transfer.
                const historyURL = getHistoryURL(publicConfig, sessionID);
                const fetchParameters = this.createFetchParameters();
                try {
                    debugLog(`Retrieving conversation history from "${historyURL}"`);
                    const result = await doWithRetryWithTimeout(() => this.doFetch(historyURL, fetchParameters), [1000, 1000, 1000], 'fetching history', 5000);
                    if (result.ok) {
                        // Retrieve the session length header that's been provided by the back-end.
                        const sessionLengthHeader = result.headers?.get('x-watson-session-timeout');
                        if (sessionLengthHeader) {
                            // Use the header value provided by the back-end to start a session timer internally.
                            this.serviceManager.actions.startSessionTimer(sessionLengthHeader);
                        }
                        resultData = await result.json();
                    }
                }
                catch (error) {
                    consoleError('An error occurred while attempting to load the conversation history', error);
                }
                if (!resultData) {
                    // If the result wasn't ok then destroy the old session and preserve the open state. If a tour was open then
                    // destroySession will close the tour and open the main window.
                    this.serviceManager.actions.destroySession(true);
                }
            }
            if (resultData) {
                // If there is result data then grab the notes array, transform it into a LoadedHistory, and return it.
                const historyNotes = resultData?.notes;
                return notesToLoadedHistory(historyNotes, this.serviceManager);
            }
            if (viewState.tour) {
                // If there is no resultData, and a tour was open then try to open the main window. Specify not to hydrate the
                // chat because we're already in the middle of hydrating. This is done below the above checks for a sessionID,
                // since it's possible the sessionID will not exist while the viewState still does. If this happened it would
                // cause the tour to stay stuck open, with no content to show, which we don't want.
                await this.serviceManager.actions.changeView(ViewType.MAIN_WINDOW, { mainWindowOpenReason: MainWindowOpenReason.SESSION_HISTORY }, false);
                // Clear the tour state, regardless if changeView was successful or not, since there is no tour data to be
                // shown. This is done instead of calling serviceManager.actions.endTour() because this scenario is a little
                // more complicated than the generic endTour scenario.
                this.serviceManager.store.dispatch(actions.clearTourData());
            }
        }
        catch (error) {
            consoleError('An error occurred while attempting to load the conversation history', error);
        }
        return null;
    }
    /**
     * Executes the fetch to retrieve the session history. This will throw an error if the response is not either a
     * 200 or 404.
     */
    async doFetch(historyURL, fetchParameters) {
        return throwOnStatus(this.serviceManager.fetch(historyURL, fetchParameters), new Set([200, 404]));
    }
    /**
     * Creates the parameters necessary for making the fetch call to the assistant.
     */
    createFetchParameters() {
        const { identityToken, config } = this.serviceManager.store.getState();
        let bearer;
        // If it is agent app, we want to pass along the auth_code.
        if (config.public.agentAppConfig.is_on) {
            bearer = config.public.agentAppConfig.auth_code;
        }
        else if (identityToken) {
            bearer = identityToken;
        }
        const fetchParameters = {
            ...FETCH_PARAMETERS$1,
            headers: {
                ...FETCH_PARAMETERS$1.headers,
                [API_DEBUGGING_HEADER_NAME]: uuid(UUIDType.MISCELLANEOUS),
                authorization: bearer ? `Bearer ${bearer}` : undefined,
            },
        };
        if (config.public.agentAppConfig.is_on) {
            fetchParameters.headers[AGENT_APP_HEADER_NAME] = true;
        }
        return fetchParameters;
    }
}

async function getBytes(stream, onChunk) {
    const reader = stream.getReader();
    let result;
    while (!(result = await reader.read()).done) {
        onChunk(result.value);
    }
}
function getLines(onLine) {
    let buffer;
    let position;
    let fieldLength;
    let discardTrailingNewline = false;
    return function onChunk(arr) {
        if (buffer === undefined) {
            buffer = arr;
            position = 0;
            fieldLength = -1;
        }
        else {
            buffer = concat$1(buffer, arr);
        }
        const bufLength = buffer.length;
        let lineStart = 0;
        while (position < bufLength) {
            if (discardTrailingNewline) {
                if (buffer[position] === 10) {
                    lineStart = ++position;
                }
                discardTrailingNewline = false;
            }
            let lineEnd = -1;
            for (; position < bufLength && lineEnd === -1; ++position) {
                switch (buffer[position]) {
                    case 58:
                        if (fieldLength === -1) {
                            fieldLength = position - lineStart;
                        }
                        break;
                    case 13:
                        discardTrailingNewline = true;
                    case 10:
                        lineEnd = position;
                        break;
                }
            }
            if (lineEnd === -1) {
                break;
            }
            onLine(buffer.subarray(lineStart, lineEnd), fieldLength);
            lineStart = position;
            fieldLength = -1;
        }
        if (lineStart === bufLength) {
            buffer = undefined;
        }
        else if (lineStart !== 0) {
            buffer = buffer.subarray(lineStart);
            position -= lineStart;
        }
    };
}
function getMessages(onId, onRetry, onMessage) {
    let message = newMessage();
    const decoder = new TextDecoder();
    return function onLine(line, fieldLength) {
        if (line.length === 0) {
            onMessage === null || onMessage === void 0 ? void 0 : onMessage(message);
            message = newMessage();
        }
        else if (fieldLength > 0) {
            const field = decoder.decode(line.subarray(0, fieldLength));
            const valueOffset = fieldLength + (line[fieldLength + 1] === 32 ? 2 : 1);
            const value = decoder.decode(line.subarray(valueOffset));
            switch (field) {
                case 'data':
                    message.data = message.data
                        ? message.data + '\n' + value
                        : value;
                    break;
                case 'event':
                    message.event = value;
                    break;
                case 'id':
                    onId(message.id = value);
                    break;
                case 'retry':
                    const retry = parseInt(value, 10);
                    if (!isNaN(retry)) {
                        onRetry(message.retry = retry);
                    }
                    break;
            }
        }
    };
}
function concat$1(a, b) {
    const res = new Uint8Array(a.length + b.length);
    res.set(a);
    res.set(b, a.length);
    return res;
}
function newMessage() {
    return {
        data: '',
        event: '',
        id: '',
        retry: undefined,
    };
}

var __rest = (undefined && undefined.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
const EventStreamContentType = 'text/event-stream';
const DefaultRetryInterval = 1000;
const LastEventId = 'last-event-id';
function fetchEventSource(input, _a) {
    var { signal: inputSignal, headers: inputHeaders, onopen: inputOnOpen, onmessage, onclose, onerror, openWhenHidden, fetch: inputFetch } = _a, rest = __rest(_a, ["signal", "headers", "onopen", "onmessage", "onclose", "onerror", "openWhenHidden", "fetch"]);
    return new Promise((resolve, reject) => {
        const headers = Object.assign({}, inputHeaders);
        if (!headers.accept) {
            headers.accept = EventStreamContentType;
        }
        let curRequestController;
        function onVisibilityChange() {
            curRequestController.abort();
            if (!document.hidden) {
                create();
            }
        }
        if (!openWhenHidden) {
            document.addEventListener('visibilitychange', onVisibilityChange);
        }
        let retryInterval = DefaultRetryInterval;
        let retryTimer = 0;
        function dispose() {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.clearTimeout(retryTimer);
            curRequestController.abort();
        }
        inputSignal === null || inputSignal === void 0 ? void 0 : inputSignal.addEventListener('abort', () => {
            dispose();
            resolve();
        });
        const fetch = inputFetch !== null && inputFetch !== void 0 ? inputFetch : window.fetch;
        const onopen = inputOnOpen !== null && inputOnOpen !== void 0 ? inputOnOpen : defaultOnOpen;
        async function create() {
            var _a;
            curRequestController = new AbortController();
            try {
                const response = await fetch(input, Object.assign(Object.assign({}, rest), { headers, signal: curRequestController.signal }));
                await onopen(response);
                await getBytes(response.body, getLines(getMessages(id => {
                    if (id) {
                        headers[LastEventId] = id;
                    }
                    else {
                        delete headers[LastEventId];
                    }
                }, retry => {
                    retryInterval = retry;
                }, onmessage)));
                onclose === null || onclose === void 0 ? void 0 : onclose();
                dispose();
                resolve();
            }
            catch (err) {
                if (!curRequestController.signal.aborted) {
                    try {
                        const interval = (_a = onerror === null || onerror === void 0 ? void 0 : onerror(err)) !== null && _a !== void 0 ? _a : retryInterval;
                        window.clearTimeout(retryTimer);
                        retryTimer = window.setTimeout(create, interval);
                    }
                    catch (innerErr) {
                        dispose();
                        reject(innerErr);
                    }
                }
            }
        }
        create();
    });
}
function defaultOnOpen(response) {
    const contentType = response.headers.get('content-type');
    if (!(contentType === null || contentType === void 0 ? void 0 : contentType.startsWith(EventStreamContentType))) {
        throw new Error(`Expected content-type to be ${EventStreamContentType}, Actual: ${contentType}`);
    }
}

/**
 * The name of the system default timezone that is lazily calculated. An example returned value is "America/Chicago".
 */
let cachedTimeZone;
/**
 * Returns the time from the given timestamp localized into the user's current timezone and formatted with the
 * current locale.
 */
function timestampToTimeString(timestamp) {
    return dayjs(timestamp).format('LT');
}
/**
 * Returns the name of the system default timezone. An example returned value is "America/Chicago".
 */
async function getTimeZone() {
    if (!cachedTimeZone) {
        cachedTimeZone =
            Intl?.DateTimeFormat && typeof Intl.DateTimeFormat().resolvedOptions === 'function'
                ? Intl.DateTimeFormat().resolvedOptions().timeZone
                : undefined;
    }
    return cachedTimeZone;
}

// This file is a mock of the localStorage API. In IE11 when developing on localhost, we can get access denied errors.
// This is just a small fallback for that use case.
let innerStorage = {};
const storage$2 = {
    getItem(key) {
        return innerStorage[key];
    },
    setItem(key, value) {
        innerStorage[key] = value;
    },
    removeItem(key) {
        delete innerStorage[key];
    },
    length: Object.keys(innerStorage).length,
    clear() {
        innerStorage = {};
    },
    key(index) {
        return Object.keys(innerStorage)[index];
    },
};

// The prefix of the key in browser storage that contains the session id for a particular user.
const DEVICE_ID_KEY = 'IBM_WAC_DEVICE_ID';
// We use sessionStorage instead of localStorage to not have to have a public cookie policy that must be accepted in EU.
const storage$1 = IS_SESSION_STORAGE() ? window.sessionStorage : storage$2;
/**
 * Get the session object for the device id. Use integrationID here just to super duper ensure uniqueness.
 */
function getOrSetDeviceID(integrationID) {
    try {
        const deviceID = storage$1.getItem(DEVICE_ID_KEY);
        if (deviceID) {
            return deviceID;
        }
        const id = uuid(UUIDType.DEVICE_ID);
        const newDeviceID = `${integrationID}:${id}`;
        storage$1.setItem(DEVICE_ID_KEY, newDeviceID);
        return newDeviceID;
    }
    catch (error) {
        consoleError('Error in getOrSetDeviceID', error);
        return undefined;
    }
}

/**
 * Calculates the text to display for a given agent message type.
 */
function getAgentStatusMessageText(agentMessageType, agentProfile, intl) {
    const name = agentProfile?.nickname;
    let messageKey;
    switch (agentMessageType) {
        case AgentMessageType.AGENT_JOINED: {
            messageKey = name ? 'agent_agentJoinedName' : 'agent_agentJoinedNoName';
            break;
        }
        case AgentMessageType.RELOAD_WARNING: {
            messageKey = 'agent_youConnectedWarning';
            break;
        }
        case AgentMessageType.AGENT_LEFT_CHAT: {
            messageKey = name ? 'agent_agentLeftChat' : 'agent_agentLeftChatNoName';
            break;
        }
        case AgentMessageType.AGENT_ENDED_CHAT: {
            messageKey = name ? 'agent_agentEndedChat' : 'agent_agentEndedChatNoName';
            break;
        }
        case AgentMessageType.TRANSFER_TO_AGENT: {
            messageKey = name ? 'agent_transferring' : 'agent_transferringNoName';
            break;
        }
        case AgentMessageType.USER_ENDED_CHAT: {
            messageKey = 'agent_youEndedChat';
            break;
        }
        case AgentMessageType.CHAT_WAS_ENDED: {
            messageKey = 'agent_conversationWasEnded';
            break;
        }
        case AgentMessageType.DISCONNECTED: {
            messageKey = 'agent_disconnected';
            break;
        }
        case AgentMessageType.RECONNECTED: {
            messageKey = 'agent_reconnected';
            break;
        }
        case AgentMessageType.SHARING_REQUESTED: {
            messageKey = 'agent_sharingRequested';
            break;
        }
        case AgentMessageType.SHARING_ACCEPTED: {
            messageKey = 'agent_sharingAccepted';
            break;
        }
        case AgentMessageType.SHARING_DECLINED: {
            messageKey = 'agent_sharingDeclined';
            break;
        }
        case AgentMessageType.SHARING_CANCELLED: {
            messageKey = 'agent_sharingCancelled';
            break;
        }
        case AgentMessageType.SHARING_ENDED: {
            messageKey = 'agent_sharingEnded';
            break;
        }
        default:
            return '';
    }
    return messageKey && intl.formatMessage({ id: messageKey }, { personName: name });
}

/**
 * Creates a LocalAndOriginalMessagesPair.
 */
function toPair(localMessages, originalMessage) {
    return { localMessages, originalMessage };
}
/**
 * Create a local message that represent a status message to display to the user.
 */
async function createAgentLocalMessage(agentMessageType, serviceManager, agentProfile, fireEvents = true) {
    const text = getAgentStatusMessageText(agentMessageType, agentProfile, serviceManager.intl);
    const result = createAgentLocalMessageForType(agentMessageType);
    const { originalMessage, localMessage } = result;
    localMessage.item.text = text;
    if (agentProfile) {
        if (!originalMessage.history) {
            originalMessage.history = {};
        }
        originalMessage.history.agent_profile = agentProfile;
    }
    if (fireEvents) {
        await serviceManager.fire({ type: "agent:pre:receive" /* BusEventType.AGENT_PRE_RECEIVE */, data: originalMessage });
    }
    deepFreeze$1(originalMessage);
    if (fireEvents) {
        await serviceManager.fire({ type: "agent:receive" /* BusEventType.AGENT_RECEIVE */, data: originalMessage });
    }
    return result;
}
/**
 * Creates an empty skeleton of a {@link LocalMessageItem} with the given agent message type.
 */
function createAgentLocalMessageForType(agentMessageType) {
    const messageItem = {
        response_type: MessageResponseTypes.TEXT,
        agent_message_type: agentMessageType,
    };
    const originalMessage = createMessageResponseForItem(messageItem);
    const localMessage = outputItemToLocalItem(messageItem, originalMessage);
    return { localMessage, originalMessage };
}
/**
 * Creates a message for the "bot returns" message that is displayed after a chat is ended. If there is no text in
 * the language pack for this message, the message will be undefined.
 */
function createBotReturnMessage(languagePack) {
    // Create a bot message to let the user know the bot has returned.
    const { agent_botReturned } = languagePack;
    if (!agent_botReturned) {
        // No text, so don't show anything.
        return null;
    }
    const { originalMessage, localMessage } = createAgentLocalMessageForType(null);
    localMessage.item.text = agent_botReturned;
    return { originalMessage, localMessage };
}
/**
 * Adds the given messages to the redux store and optionally to session history as well.
 */
async function addMessages(messagePairs, saveInHistory, fireHistoryEvent, showLiveMessages, serviceManager) {
    if (showLiveMessages) {
        // Add to the redux store and fire any custom response events that are needed.
        await asyncForEach(messagePairs, async ({ localMessages, originalMessage }) => {
            await asyncForEach(localMessages, async (localMessage, index) => {
                await serviceManager.actions.handleUserDefinedResponseItems(localMessage, originalMessage);
                serviceManager.store.dispatch(actions.addLocalMessageItem(localMessage, originalMessage, index === 0));
            });
        });
    }
    // Send to session history.
    await asyncForEach(messagePairs, async ({ originalMessage }) => {
        const { disableAgentSessionHistory } = serviceManager.store.getState().config.public.serviceDesk;
        if (saveInHistory && !disableAgentSessionHistory) {
            try {
                let sessionHistoryMessage = originalMessage;
                if (fireHistoryEvent) {
                    // If desired, fire an event to allow custom code to modify this message before we put it into session history.
                    sessionHistoryMessage = cloneDeep(originalMessage);
                    const event = {
                        type: "agent:pre:sessionHistory" /* BusEventType.AGENT_PRE_SESSION_HISTORY */,
                        message: sessionHistoryMessage,
                    };
                    await serviceManager.eventBus.fire(event, serviceManager.instance);
                    // Custom code can assign this to null to avoid saving anything.
                    sessionHistoryMessage = event.message;
                }
                if (sessionHistoryMessage) {
                    await serviceManager.actions.sendUpdateHistoryEvent(sessionHistoryMessage.id, sessionHistoryMessage);
                }
            }
            catch (error) {
                consoleError('Error in HumanAgentServiceImpl.addMessage', error);
            }
        }
        deepFreeze$1(originalMessage);
    });
}
/**
 * Adds a "bot return" message from the bot after (on an optional delay). This messages will also be sent to the server
 * to be included in the session history.
 *
 * @param botReturnDelay The delay before adding the "bot return" message.
 * @param wasSuspended Indicates if the conversation was suspended before it was ended.
 * @param serviceManager The service manager to use.
 */
async function addBotReturnMessage(botReturnDelay, wasSuspended, serviceManager) {
    const botReturn = createBotReturnMessage(serviceManager.store.getState().languagePack);
    if (botReturn) {
        const initialRestartCount = serviceManager.restartCount;
        conditionalSetTimeout(() => {
            if (initialRestartCount === serviceManager.restartCount) {
                // Don't show this message if the chat has been restarted since we started waiting.
                addMessages([toPair([botReturn.localMessage], botReturn.originalMessage)], true, false, !wasSuspended, serviceManager);
            }
        }, botReturnDelay);
    }
}
/**
 * Adds an "end chat" message to the message list. This messages will also be sent to the server to be included in the
 * session history.
 *
 * @param agentMessageType The type of the "end chat" message.
 * @param agentProfile The profile of the agent who ended the chat (or null if not ended by an agent).
 * @param fireEvents Indicates if the agent events should be fired for the "end chat" message.
 * @param wasSuspended Indicates if the conversation was suspended before it was ended.
 * @param serviceManager The service manager to use.
 */
async function addAgentEndChatMessage(agentMessageType, agentProfile, fireEvents, wasSuspended, serviceManager) {
    const endChatMessage = await createAgentLocalMessage(agentMessageType, serviceManager, agentProfile, fireEvents);
    await addMessages([toPair([endChatMessage.localMessage], endChatMessage.originalMessage)], true, false, !wasSuspended, serviceManager);
}
/**
 * Returns an array of text strings for the initial summary messages that are to be sent to an agent. If there are
 * no texts found, it will return an array with default text in it.
 */
function getSummaryMessages(connectItem, defaultText) {
    const messagesToAgent = connectItem?.transfer_info?.summary_message_to_agent;
    const texts = messagesToAgent?.map(message => message.text).filter(text => text);
    return texts?.length ? texts : [defaultText];
}
/**
 * The different types of built-in/out-of-the-box service desk integrations supported by web chat. The values used
 * here come from tooling and may refer to old or out-dated values so care should be made in modifying this list.
 */
var BuiltInServiceDesk;
(function (BuiltInServiceDesk) {
    BuiltInServiceDesk["NICE_DFO"] = "nicedfo";
    BuiltInServiceDesk["GENESYS_MESSENGER"] = "genesyswebmessenger";
    BuiltInServiceDesk["SALES_FORCE"] = "salesforceweb";
    BuiltInServiceDesk["ZENDESK"] = "zendeskweb";
})(BuiltInServiceDesk || (BuiltInServiceDesk = {}));

/**
 * The Set of valid out-of-the-box service desk integration types that can be reported from tooling. We need this
 * because previous version of tooling would record the user clicking custom service desk tiles in the UI which is
 * not info that is reliable. We're not filtering those out to just the OOTBs.
 */
const BuiltInServiceDeskValues = new Set(Object.values(BuiltInServiceDesk));
var MessageRetryStatus;
(function (MessageRetryStatus) {
    MessageRetryStatus["RETRYING"] = "Retrying";
    MessageRetryStatus["FAILED"] = "Failed";
    MessageRetryStatus["SUCCESS_AFTER_RETRY"] = "SuccessAfterRetry";
})(MessageRetryStatus || (MessageRetryStatus = {}));
// The parameters to send on fetch requests.
// Note: The content-type must be application/json per amplitude's HTTP API v2 docs.
// https://developers.amplitude.com/docs/http-api-v2#request-format
const FETCH_PARAMETERS = {
    method: 'POST',
    headers: {
        [HEADER_CONTENT_TYPE]: MIME_JSON,
    },
};
const NOT_SET = 'not set';
const API_KEY = AMPLITUDE_API_KEY; // Defaults to Amplitude testing environment API key if none provided.
const POST_URL = '/2/httpapi';
// Use the Group Identify API to set or update properties of particular groups. However, these updates will only affect events going forward.
const GROUP_POST_URL = `/groupidentify?api_key=${API_KEY}`;
// Use the Identify API to set the User ID for a particular Device ID or update user properties of a particular user without sending an event.
// However, these updates will only affect events going forward.
const IDENTIFY_POST_URL = `/identify?api_key=${API_KEY}`;
const MAX_AMPLITUDE_EVENTS = 10;
// Returns the current device type, differentiating between; phone, tablet, and desktop.
// eslint-disable-next-line no-nested-ternary
const BROWSER_DEVICE_TYPE = IS_TABLET ? 'tablet' : IS_PHONE ? 'phone' : 'desktop';
class TrackService {
    constructor(serviceManager, isIdentifyEnabled, isEventEnabled, endpointPath) {
        /**
         * Queue for events that are ready to send to Amplitude. We do not send events immediately to prevent overloading the
         * limit of 10/second per device.
         */
        this.eventQueue = [];
        /**
         * We need to add user_properties to the first batch of events, this variable is used to determine if the properties
         * have been added to the first batch or not.
         */
        this.firstBatchUserProperties = false;
        this.serviceManager = serviceManager;
        this.isIdentifyEnabled = isIdentifyEnabled;
        this.isEventEnabled = isEventEnabled;
        this.endpointPath = endpointPath;
    }
    /**
     * Returns the type of launcher being used, so we can compare open rates across different launchers.
     */
    getLauncherType() {
        const { config } = this.serviceManager.store.getState();
        if (config.public.showLauncher === false) {
            return 'Custom';
        }
        return 'Default';
    }
    /**
     * Pushes an event onto the eventQueue and starts the sendEvents loop if event is the first event in the eventQueue
     *
     * @param event The event to send to Amplitude
     */
    pushEvent(event) {
        this.eventQueue.push(event);
        // If this is the only event in the queue start the sendEvents loop
        if (this.eventQueue.length === 1) {
            this.sendEvents();
        }
    }
    /**
     * Batches events from the eventQueue and sends them to Amplitude. Repeats for additional events in the queue.
     * For more information see: https://developers.amplitude.com/#Http-Api-V2
     */
    sendEvents() {
        // Using setTimeout to allow for multiple events to be added to the eventQueue in a single browser tick before
        // they are sent to Amplitude in a batch.
        setTimeout(() => {
            if (this.eventQueue.length) {
                // Amplitude v2 api has an event batch size limit of 10 events
                const eventBatch = this.eventQueue.splice(0, MAX_AMPLITUDE_EVENTS);
                if (!this.firstBatchUserProperties) {
                    // Need to add user properties to all events in the first batch otherwise there will be events in the first
                    // batch that don't have the below user properties.
                    eventBatch.map(event => {
                        event.user_properties = {
                            'Browser name': BROWSER_INFO?.name,
                            'Browser version': BROWSER_INFO?.version,
                            'Browser OS': BROWSER_INFO?.os,
                            'Browser device type': BROWSER_DEVICE_TYPE,
                            'Deployed full path': getURLHostnameAndPath(),
                            'Deployed hostname': window.location.hostname,
                            'User loaded version': "0.3.2",
                            ...event.user_properties,
                        };
                        return event;
                    });
                    this.firstBatchUserProperties = true;
                }
                this.serviceManager
                    .fetch(`${this.endpointPath}${POST_URL}`, {
                    ...FETCH_PARAMETERS,
                    body: JSON.stringify({ api_key: API_KEY, events: eventBatch }),
                })
                    .catch(error => {
                    consoleError('Failed to track events', error);
                });
                // If events remain in the queue, loop through sendEvents again to ensure they are all sent.
                this.sendEvents();
            }
        });
    }
    /**
     * Identifies the current group and user to Amplitude. Called on the 'Browser Identity' event. When this is called,
     * the userID has been defined and is guaranteed to exist either through JWT, anonymous method, or updateUserID.
     *
     * For more information on the group identify API see: https://developers.amplitude.com/#group-identify-api
     * For more information on the identify API see: https://developers.amplitude.com/#identify-api
     */
    identifyGroupAndUser() {
        const { additionalChatParameters, store } = this.serviceManager;
        const { accountOwnerDomain, userID, config, homeScreenConfig, identityToken, recentEditorDomain, persistedToBrowserStorage, } = store.getState();
        // The serviceInstanceID, accountOwnerDomain, and recentEditorDomain might be empty since this was added in the tooling after web chat was
        // released and requires you to re-save for it to show.
        const { initConfig } = config.remote;
        const serviceInstanceIDOrNotSet = initConfig.service_instance_id || config.public.serviceInstanceID || NOT_SET;
        const accountOwnerDomainOrNotSet = accountOwnerDomain || NOT_SET;
        const recentEditorDomainOrNotSet = recentEditorDomain || NOT_SET;
        const trackUserID = this.getTrackUserID(serviceInstanceIDOrNotSet, userID);
        // In Amplitude, we group users by service instance id. We do so by including the identification object.
        // Set the group_type as "Account" and the group_value as the value of serviceInstanceID.
        // For more information on grouping, visit https://help.amplitude.com/hc/en-us/articles/115001765532#group-identify-api
        const identifyGroup = [
            {
                group_type: 'Service Instance',
                // This might be null since this was added in the tooling after web chat was released and requires you to
                // re-save for it to show.
                group_value: serviceInstanceIDOrNotSet,
                device_id: getOrSetDeviceID(config.public.integrationID),
                user_id: trackUserID,
                group_properties: {
                    $set: {
                        // This might be null since this was added in the tooling after web chat was released and requires you to
                        // re-save for it to show.
                        'Integration owner email domain': accountOwnerDomainOrNotSet,
                        'Recent editor email domain': recentEditorDomainOrNotSet,
                        'Deployed hostname': window.location.hostname,
                        'Plan Type': config.remote.initConfig.plan || 'unknown',
                    },
                },
            },
            {
                group_type: 'Integration',
                group_value: config.public.integrationID,
                device_id: getOrSetDeviceID(config.public.integrationID),
                user_id: trackUserID,
                group_properties: {
                    $set: {
                        // These values need to be set on this group instead of the Service Instance group because Service Instances
                        // can contain more than one integration. This way we can track integration specific configurations.
                        'Primary color changed': Boolean(initConfig.css_variables?.['BASE-primary-color']),
                        'Secondary color changed': Boolean(initConfig.css_variables?.['BASE-secondary-color']),
                        'Accent color changed': Boolean(initConfig.css_variables?.['BASE-accent-color']),
                        'Secure login enabled': Boolean(identityToken || initConfig.authorization?.is_on),
                        'Loaded version': "0.3.2",
                        'Requested version': config.public.clientVersion || 'unknown',
                        'LoadWAC version': additionalChatParameters?.loadVersion || 'unknown',
                        'Suggestions enabled': Boolean(initConfig.alternate_suggestions?.is_on),
                        'Session history enabled': !config.public.disableSessionHistory,
                        'Web chat home screen shown': Boolean(homeScreenConfig?.is_on),
                        'Is Embedded Agent App': config.public.agentAppConfig.is_on,
                        'Service Desk': this.getServiceDesk(),
                        'Service desk enabled': hasSupportedServiceDesk(config),
                        'Custom Service Desk': this.getCustomServiceDeskName(),
                        'Browser name': BROWSER_INFO?.name,
                        'Browser version': BROWSER_INFO?.version,
                        'Browser OS': BROWSER_INFO?.os,
                        'Browser device type': BROWSER_DEVICE_TYPE,
                        'Widget starting state': config.public.openChatByDefault ? 'Opened' : 'Closed',
                        'Deployed full path': getURLHostnameAndPath(),
                        'Launcher Type': this.getLauncherType(),
                        'Branding Enabled': Boolean(initConfig.branding_config?.is_on),
                        'PDF viewer disabled': Boolean(config.public.disablePDFViewer),
                        'Launcher title updated': Boolean(initConfig.launcher_config?.desktop.title || initConfig.launcher_config?.mobile.title),
                        'Disclaimer beta enabled': Boolean(config.public.disclaimer?.isOn),
                    },
                },
            },
        ];
        if (this.isIdentifyEnabled) {
            const identifyGroupUrl = `${this.endpointPath}${GROUP_POST_URL}&identification=${JSON.stringify(identifyGroup)}`;
            this.serviceManager.fetch(identifyGroupUrl, FETCH_PARAMETERS).catch(error => {
                consoleError('Failed to identify group', error);
            });
        }
        // For testing/development purposes - log what we send as updated group properties
        if (config.public.__ibm__?.logTrackObjects) {
            debugLog('Amplitude track - group properties', identifyGroup);
        }
    }
    /**
     * Get the correct {@link TrackEventProps} format for a specific response.
     *
     * @param response The response being tracked.
     * @param isLatestWelcomeNode If the response is a welcome node or not.
     * @param isAnythingElseNode If the response is an anything else node or not.
     * @param responseTrackData Optional data to add for tracking purposes.
     */
    getTrackResponseProps(response, isLatestWelcomeNode, isAnythingElseNode, responseTrackData) {
        const eventProps = {
            eventName: 'Assistant Message Sent to User',
            eventDescription: 'Bot gives the user response.',
            isWelcomeNode: isLatestWelcomeNode,
            type: response ? response.response_type : 'user_defined',
        };
        if (response?.response_type === 'option') {
            eventProps.eventDescription = 'Bot gives the user option response type.';
            eventProps.type = getOptionType(response.preference, response.options.length);
        }
        else if (response?.response_type === 'suggestion') {
            eventProps.eventDescription = 'Bot gives the user disambiguation option.';
            eventProps.optionsShown = response.suggestions.length;
        }
        else if (hasTourUserDefinedType(response)) {
            eventProps.type = 'Tour Card';
        }
        eventProps.isAnythingElseNode = !!isAnythingElseNode;
        if (responseTrackData) {
            eventProps.totalRequestTime = responseTrackData.totalRequestTime;
            eventProps.lastRequestTime = responseTrackData.lastRequestTime;
            eventProps.numErrors = responseTrackData.numErrors;
        }
        return eventProps;
    }
    /**
     * Converts the data returned by a response from the Assistant into the TrackEventProps needed for sending
     * tracking data to Amplitude about the response. Neither userID nor sessionID must be defined when this is
     * called, and they won't be for initial rendering events. They do get filled in later after the web chat has
     * been opened.
     *
     * @param options The included properties {@link TrackEventProps} wished to be tracked.
     */
    track(options) {
        if (options) {
            const { userID, persistedToBrowserStorage, config } = this.serviceManager.store.getState();
            const { chatState } = persistedToBrowserStorage;
            const { sessionID, sessionStartedTimestamp } = chatState;
            const { eventName, user_properties, ...rest } = options;
            // Add the session id to the event_properties
            rest.assistantSessionID = sessionID;
            // The serviceInstanceID and accountOwnerDomain might be empty since this was added in the tooling after web chat was
            // released and requires you to re-save for it to show.
            const serviceInstanceIDOrNotSet = config.remote.initConfig.service_instance_id || config.public.serviceInstanceID || NOT_SET;
            const trackUserID = this.getTrackUserID(serviceInstanceIDOrNotSet, userID);
            // For Amplitude, we need to send a unique event_type to capture the overall event we're tracking.
            // The event is sent with the user_properties update.
            // Informational doc: https://help.amplitude.com/hc/en-us/articles/207108327-Step-4-Set-User-Properties-and-Event-Properties#applying-user-properties-to-events
            const event = {
                device_id: getOrSetDeviceID(config.public.integrationID),
                user_id: trackUserID,
                event_type: eventName,
                event_properties: rest,
                user_properties,
                session_id: sessionStartedTimestamp,
                groups: {
                    'Service Instance': serviceInstanceIDOrNotSet,
                    Integration: config.public.integrationID,
                },
            };
            if (this.isEventEnabled) {
                this.pushEvent(event);
            }
            // For testing/development purposes - log what we send to amplitude as an event.
            if (config.public.__ibm__?.logTrackObjects) {
                debugLog('Amplitude track - event properties', event);
            }
        }
    }
    /**
     * Get the unique tracking user id to use in amplitude
     * Prepend "WAEndUser-" to show who are our end users.
     *
     * @param serviceInstanceID The user's service instance id.
     * @param userID The user's id (if provided).
     */
    getTrackUserID(serviceInstanceID, userID) {
        // If a user ID isn't set, yet, just return undefined.
        if (!userID) {
            return undefined;
        }
        // If we have a userID already defined, just return it.
        if (this.firstUserID) {
            return this.firstUserID;
        }
        // If there is a userID set, but not a firstUserID, we set it.
        this.firstUserID = `WAEndUser-${serviceInstanceID}:${userID}`;
        return this.firstUserID;
    }
    /**
     * Track each response within a received message accordingly.
     *
     * @param message The entire message response received.
     * @param isLatestWelcomeNode If the response is a welcome node or not.
     * @param responseTrackData Optional data to add for tracking purposes.
     */
    trackResponse(message, isLatestWelcomeNode, responseTrackData) {
        if (message.output) {
            const messages = message.output.generic;
            const isAnythingElseNode = message.output.intents && message.output.intents.length === 0;
            if (messages) {
                messages.forEach(response => {
                    const trackProps = this.getTrackResponseProps(response, isLatestWelcomeNode, isAnythingElseNode, responseTrackData);
                    this.track(trackProps);
                });
            }
        }
    }
    /**
     * Used to track usage of our event system (instance.once({ type: "history:begin", handler: handler });, etc.). Fires
     * when a subscription is configured, not when the subscribed to event is actually fired.
     *
     * @param handlers The type of event subscribed to and handler function for an event subscription
     * {@link TypeAndHandler}.
     * @param subscriptionMethod The way an event was subscribed to {@link EventHandlers}.
     */
    eventSystemTrack(handlers, subscriptionMethod) {
        const data = asArray(handlers);
        const subscriptionEventType = data.map(handler => handler.type);
        const trackProps = {
            eventName: 'Event system used',
            eventDescription: subscriptionMethod,
            subscriptionEventType,
        };
        this.track(trackProps);
    }
    /**
     * Identifies the user and then pushes the event onto the eventQueue.
     *
     * @param options The included properties {@link TrackEventProps} wished to be tracked.
     * @param userID The userID to use for the track event. Because the docs and demo site do not use the same
     * serviceManager as web chat they both need to create their own userID's for tracking and pass them to this function.
     */
    docsTrack(options, userID) {
        if (options) {
            const { eventName, ...rest } = options;
            const identifyUser = {
                user_id: userID,
            };
            // We cannot guarantee the user has already been identified with Amplitude in the docs site or the demo site, so we
            // identify the user with Amplitude before each demoOrDocsTrack event. This does not cost us events.
            const identifyUserUrl = `${this.endpointPath}${IDENTIFY_POST_URL}&identification=${JSON.stringify(identifyUser)}`;
            this.serviceManager.fetch(identifyUserUrl, FETCH_PARAMETERS).catch(error => {
                consoleError('Failed to identify user', error);
            });
            const event = {
                user_id: userID,
                event_type: eventName,
                event_properties: rest,
            };
            this.pushEvent(event);
        }
    }
    /**
     * Get the service desk being used by the web chat, if any.
     */
    getServiceDesk() {
        const { config } = this.serviceManager.store.getState();
        if (config.public.agentAppConfig.is_on) {
            // Don't report the type of the service desk if we're in the agent app.
            return undefined;
        }
        if (config.public.serviceDeskFactory) {
            return 'custom';
        }
        // Filter out the old custom types reported by tooling and only report the OOTB ones.
        const type = config.public.serviceDesk.integrationType ||
            config.remote.initConfig.service_desk?.integration_type;
        if (BuiltInServiceDeskValues.has(type)) {
            return type;
        }
        return 'no SD';
    }
    /**
     * If a custom service desk is configured, returns the name.
     */
    getCustomServiceDeskName() {
        return this.serviceManager.humanAgentService?.getCustomServiceDeskName();
    }
}

// The text that the assistant returns that indicates there are no skills on the assistant.
const NO_SKILLS_ERROR_TEXT = 'No skills found for the assistant';
// Time in ms between retry attempts.
const MS_BETWEEN_RETRIES = [1000, 3000, 5000];
// The maximum amount of time we allow retries to take place. If we pass this time limit, we throw an error, stop
// retrying, and move on to the next item in the queue. 120 seconds is the longest Cerberus allows for, so we'll
// set this a little higher than that.
const MS_MAX_ATTEMPT = 150 * 1000;
// The maximum amount of time we allow to pass before the error indicator becomes visible.
const MS_MAX_SILENT_ERROR = 6000;
// The maximum amount of time we allow to pass before the loading indicator becomes visible.
const MS_MAX_SILENT_LOADING = 4000;
// The maximum amount of time to wait before timing out a request for search results.
const MS_MAX_SEARCH_TIME = 7000;
// The maximum amount of time before timing out an extension. The back-end should time out at 120 seconds so we'll
// set this to a little higher than that.
const MS_MAX_EXTENSION_TIME = 150 * 1000;
// The error response text when a session has expired
const SESSION_NOT_FOUND_ERROR_TEXT = 'session not found';
// The amount of time to wait to receive a message from streaming source before considering it a timeout.
const STREAMING_MESSAGE_TIMEOUT_MS = 10000;
// A static error object used when indicating that a message response is not streaming. This does not actually
// represent that an error has occurred and its stack trace has no meaning.
const NOT_STREAMING = new Error('Not streaming');
// The set of language to locale fallback values.
const LANGUAGE_LOCALE_FALLBACKS = new Map([
    ['en', 'en-us'],
    ['ar', 'ar-ar'],
    ['cs', 'cs-cz'],
    ['de', 'de-de'],
    ['es', 'es-es'],
    ['fr', 'fr-fr'],
    ['it', 'it-it'],
    ['ja', 'ja-jp'],
    ['ko', 'ko-kr'],
    ['nl', 'nl-nl'],
    ['pt', 'pt-br'],
    ['zh', 'zh-cn'],
]);
// Types of different retry behaviors. SILENT will retry without letting the end user know we are retrying, VISIBLE will
// show the user that we are retrying.
var RetryType;
(function (RetryType) {
    RetryType[RetryType["SILENT"] = 1] = "SILENT";
    RetryType[RetryType["VISIBLE"] = 2] = "VISIBLE";
})(RetryType || (RetryType = {}));
class MessageService {
    constructor(serviceManager, publicConfig) {
        /**
         * The value indicates that there is a pending locale change that needs to be sent to the assistant on the next
         * message request.
         */
        this.pendingLocale = false;
        /**
         * Indicates if the locale has been explicitly set by the host page. This is used to ensure we only send a locale
         * to the assistant when it has been explicitly set.
         */
        this.localeIsExplicit = false;
        this.serviceManager = serviceManager;
        this.messageLoadingManager = new MessageLoadingManager();
        this.queue = {
            waiting: [],
            current: null,
        };
        const timeoutOverride = publicConfig.__ibm__?.messageTimeoutSecs || publicConfig.messaging?.messageTimeoutSecs;
        this.timeoutMS = timeoutOverride ? timeoutOverride * 1000 : MS_MAX_ATTEMPT;
        const messageURLFromPublicConfig = getMessageURL(publicConfig);
        this.messageURL =
            messageURLFromPublicConfig.indexOf('?') > -1
                ? `${messageURLFromPublicConfig}&version=${INTEGRATIONS_VERSION}`
                : `${messageURLFromPublicConfig}?version=${INTEGRATIONS_VERSION}`;
    }
    /**
     * Process a response from assistant with 200 response code, send and return the messageResponse.
     *
     * @param current The current item in the send queue.
     * @param received JSON output from v2 API.
     */
    async processSuccess(current, received) {
        const { requestOptions, isProcessed } = current;
        const { persistedToBrowserStorage, config } = this.serviceManager.store.getState();
        const { chatState } = persistedToBrowserStorage;
        const { sessionID } = chatState;
        const isWelcomeNode = Boolean(current.message.history.is_welcome_request);
        // If this message was already invalidated, don't do anything.
        if (isProcessed) {
            return;
        }
        // If this request succeeded because of a retry, track it.
        if (current.trackData?.numErrors > 0) {
            this.trackMessageRetry(MessageRetryStatus.SUCCESS_AFTER_RETRY, current);
        }
        // Clear any error state that may be associated with the message.
        this.setMessageErrorState(current, MessageErrorState.NONE);
        // After updating the error state get the message from the pendingRequest since it has potentially been updated by
        // setting the error state.
        const { message } = current;
        // Do all the normal things for our general message requests, however for event messages we skip this.
        if (received) {
            if (message.input.message_type !== MessageInputType.EVENT) {
                if (!isWelcomeNode) {
                    this.messageLoadingManager.end();
                }
                if (isWelcomeNode &&
                    !config.public.disableSessionHistory &&
                    received.session_id &&
                    received.session_id === sessionID) {
                    // If the session_id is the same, we don't really want a new Welcome node and we short circuit.
                    // When there is a server side history store, this won't work as the item will be stored in history
                    // whether we decide to show it here or not. "Store" will be giving us an endpoint to call to check if a session
                    // is still active so we can avoid this logic in the future:
                    // https://github.ibm.com/watson-engagement-advisor/wea-backlog/issues/29085
                    debugLog(`Session ${sessionID} still active.`);
                }
                else {
                    if (received.session_id && received.session_id !== sessionID) {
                        this.serviceManager.store.dispatch(actions.updateSessionID(received.session_id));
                        this.serviceManager.store.dispatch(actions.updateSessionStartedTimestamp(Date.now()));
                    }
                    // REMOVE when we have real history store. Add timestamp to input and push to history store. This should all
                    // happen on the server side later.  Also mark messages with their parent process ID if needed i.e. all messages
                    // inside a process except for the first and last messages as they also appear in the main chat.
                    received.history = received.history || {};
                    received.history.timestamp = received.history.timestamp || Date.now();
                    received.history.transaction_id = current.transactionID;
                    current.trackData.lastRequestTime = Date.now() - current.timeLastRequest;
                    current.trackData.totalRequestTime = Date.now() - current.timeFirstRequest;
                    // Send receive event.
                    await this.serviceManager.actions.receive(received, isWelcomeNode, message, requestOptions, current.trackData);
                }
            }
            else if (received.session_id && received.session_id !== sessionID) {
                // Allow an event message to update the session ID.
                // TODO: This needs to be tested.
                this.serviceManager.store.dispatch(actions.updateSessionID(received.session_id));
                this.serviceManager.store.dispatch(actions.updateSessionStartedTimestamp(Date.now()));
            }
        }
        else if (!isWelcomeNode) {
            this.messageLoadingManager.end();
        }
        if (current.isProcessed) {
            // If the response has already been processed (perhaps the message was cancelled) then stop processing.
            return;
        }
        // If the received message is part one of a two-step response then we need to respond a little differently. We
        // need to disable user input, then send another request to the assistant to get the second part. When that
        // message is completed, we can "resume" the original message. We will not resolve the promise for the original
        // message until the entire process is completed.
        let rejected;
        const isSkipUserInput = received?.context?.global?.system?.skip_user_input;
        if (isSkipUserInput) {
            try {
                const isEmptyResponse = !received.output?.generic?.length;
                await this.handleSkipUserInput(isEmptyResponse);
            }
            catch (error) {
                current.sendMessagePromise.doReject(error);
                current.isProcessed = true;
                rejected = true;
            }
        }
        // Resolve the promise that lets the original caller who sent the message know that the message has been sent
        // successfully.
        if (!rejected) {
            current.sendMessagePromise.doResolve();
            current.isProcessed = true;
        }
        if (!isSkipUserInput) {
            // Move on to next item in queue. We don't want to do this with skip_user_input because the final response
            // after the skip will already move the queue forward and we don't want to do that again here. The final
            // response is nested inside the original skip response.
            this.moveToNextQueueItem();
        }
    }
    /**
     * Handles the case where we receive a two-step response. This occurs when we receive a response that triggers a
     * second request to WA that in turn returns a second response. This occurs with "skip_user_input" when we get the
     * initial response from a call to an extension and the second request finishes the extension.
     *
     * When this occurs we will disable user input, and then send the next request to the server so that it can
     * continue handling the original request. Once this second request is complete, user input will be restored.
     */
    async handleSkipUserInput(isEmptyResponse) {
        const { current } = this.queue;
        const noInputMessage = {
            thread_id: current.message.thread_id,
            id: null,
            input: {
                text: '',
            },
            context: assertType({
                global: { system: { skip_user_input: true } },
            }),
        };
        // If the current response is empty, we want the pause start time for the next request to continue from the
        // original request. Otherwise, we'll start over with a new pause time as normal. If the current response is not
        // empty, we will pause right now (that would have already happened in the receive call) and then skip all future
        // pauses.
        const pauseStartTime = isEmptyResponse ? current.requestOptions.pauseStartTime : undefined;
        const skipPause = current.requestOptions.skipPause || !isEmptyResponse;
        // Remove the current message from the queue. This will allow the next message to run immediately.
        this.clearCurrentQueueItem();
        // We pass true for ignoreHydration below because if this skip user input occurred while fetching the welcome
        // node we will be in the middle of hydration and this second request will deadlock waiting for it to finish.
        await this.serviceManager.actions.send(noInputMessage, MessageSendSource.SKIP_USER_INPUT, {
            skipQueue: true,
            isTwoStepResponse: true,
            silent: true,
            pauseStartTime,
            skipPause,
        }, true);
    }
    /**
     * Inserts a stream loading indicator into the message list.
     */
    insertStreamLoading() {
        // Add a temporary loading message that will be displayed until we start getting streaming data.
        const { originalMessage, localMessage } = createLocalMessageForItem({
            response_type: InternalMessageResponseType.STREAM_LOADING,
        });
        this.serviceManager.store.dispatch(actions.addLocalMessageItem(localMessage, originalMessage, true));
        return localMessage.ui_state.id;
    }
    /**
     * Adds an inline error message to the list.
     */
    addErrorMessage() {
        const { store } = this.serviceManager;
        const errorMessage = store.getState().languagePack.errors_singleMessage;
        const { originalMessage, localMessage } = createLocalMessageForInlineError(errorMessage);
        store.dispatch(actions.addLocalMessageItem(localMessage, originalMessage, true));
    }
    /**
     * This function is used to resend a message that had been previously sent, but for which we never received a
     * response. The below will resend the message so we can wait for the response once it is available.
     */
    async resendMessage(message, localMessageID) {
        const skippingUserInput = message.context?.global?.system?.skip_user_input;
        await this.send(cloneDeep(message), MessageSendSource.HYDRATE_RESEND, localMessageID, {
            skipQueue: true,
            isTwoStepResponse: skippingUserInput,
            silent: true,
        });
    }
    /**
     * Send to the assistant API, IF we are inside the window to show an error, also update the error state on the
     * message.
     */
    sendToAssistantAndUpdateErrorState(current) {
        // If this message was already invalidated, don't do anything.
        if (current.isProcessed) {
            return;
        }
        this.sendToAssistant(current);
        const now = Date.now();
        const msSinceStarted = now - current.timeFirstRequest;
        const isSilentErrorWindow = MS_MAX_SILENT_ERROR > msSinceStarted;
        const type = isSilentErrorWindow ? RetryType.SILENT : RetryType.VISIBLE;
        if (type === RetryType.VISIBLE && !current.requestOptions?.isTwoStepResponse) {
            // Once we've hit the visible retry state, we need to mark the message as retrying and we need to mark all
            // the other messages that are still waiting as waiting.
            this.setMessageErrorState(current, MessageErrorState.RETRYING);
            this.queue.waiting.forEach(waitingMessage => {
                this.setMessageErrorState(waitingMessage, MessageErrorState.WAITING);
            });
        }
    }
    /**
     * Process a message returned from assistant with non-200 error code. This function will attempt to retry the request
     * up to the length of RETRY_BEHAVIOR. If more than we require more retires than that, we fail the message.
     *
     * @param pendingRequest The current item in the send queue.
     * @param statusCode The status code (if any) returned from the request.
     * @param resultText The raw result text or error message (if any) returned from the request.
     * @param allowRetry Indicates if a retry is permitted.
     */
    async processError(pendingRequest, statusCode, resultText, allowRetry) {
        const { message, timeFirstRequest, timeLastRequest, tryCount, isProcessed, trackData, requestOptions, transactionID, } = pendingRequest;
        // If we got a 400 response code for a welcome message and it contains the "no skills" message, we should just
        // stop now and put the widget into a general error state.
        const isWelcome = message.history.is_welcome_request;
        const noSkillsError = statusCode === StatusCodes.BAD_REQUEST && isWelcome && resultText?.indexOf(NO_SKILLS_ERROR_TEXT) !== -1;
        const now = Date.now();
        const msSinceStarted = now - timeFirstRequest;
        // We are still in the "allow attempts" window if we have not exceeded the total amount of time allowed and if
        // we have not exceeded the number of retries allowed.
        const isInAttemptWindow = this.timeoutMS > msSinceStarted && tryCount < MS_BETWEEN_RETRIES.length;
        const wasThrottled = statusCode === StatusCodes.TOO_MANY_REQUESTS;
        // If this message was already invalidated, don't do anything.
        if (isProcessed) {
            return;
        }
        const isSecurityEnabled = this.serviceManager.store.getState().config.remote.initConfig.authorization?.is_on;
        // Let's check and see if there was a JWT error, and if there was one then ask for a new identity token.
        const wasJWTError = await this.checkAndProcessJWTError(pendingRequest, statusCode, resultText, isInAttemptWindow, isWelcome, isSecurityEnabled);
        if (isProcessed) {
            // If the promise has already been resolved (perhaps the message was cancelled) then stop processing.
            return;
        }
        trackData.lastRequestTime = Date.now() - timeLastRequest;
        trackData.totalRequestTime = Date.now() - timeFirstRequest;
        if (!wasJWTError) {
            // We will handle specific use cases for different error codes like 403s here so we need raw result.
            // If we get a 429 error we will skip straight to error message and not retry.
            if (statusCode === StatusCodes.NOT_FOUND &&
                resultText &&
                resultText.toLowerCase().includes(SESSION_NOT_FOUND_ERROR_TEXT)) {
                // Session has expired, so clear session info to force new session.
                this.serviceManager.store.dispatch(actions.updateSessionID(null));
                // Try before trying again.  Backend will auto-create new session.
                this.sendToAssistantAndUpdateErrorState(pendingRequest);
            }
            else if (!noSkillsError && isInAttemptWindow && !wasThrottled && allowRetry) {
                // This is the general/unknown error case. Pause before trying again.
                trackData.numErrors++;
                const retryDelay = MS_BETWEEN_RETRIES[pendingRequest.tryCount++];
                // Throw an analytics event that we have errored and will retry.
                // We throw another event in the case of this request later succeeding.
                this.trackMessageRetry(MessageRetryStatus.RETRYING, pendingRequest);
                setTimeout(() => {
                    this.sendToAssistantAndUpdateErrorState(pendingRequest);
                }, retryDelay);
            }
            else {
                this.trackMessageRetry(MessageRetryStatus.FAILED, pendingRequest);
                const isTooLargeError = statusCode === StatusCodes.REQUEST_TOO_LONG &&
                    resultText.startsWith('<!DOCTYPE html>') &&
                    this.serviceManager.store.getState().config.public.__ibm__?.toolingType !== ToolingType.ACTIONS;
                // Show a catastrophic error if we are just starting out.
                let catastrophicErrorType;
                if (wasThrottled) {
                    if (isWelcome) {
                        catastrophicErrorType = CatastrophicErrorType.THROTTLED;
                    }
                    else if (message.input.message_type !== MessageInputType.EVENT) {
                        // If a non-welcome message failed, and this wasn't an event messageRequest we need to insert an error
                        // message to display a message to the user.
                        const { languagePack } = this.serviceManager.store.getState();
                        const { originalMessage, localMessage } = createLocalMessageForInlineError(languagePack.errors_busy);
                        this.serviceManager.store.dispatch(actions.addLocalMessageItem(localMessage, originalMessage, true));
                    }
                }
                else if (noSkillsError) {
                    catastrophicErrorType = CatastrophicErrorType.NO_SKILL;
                }
                else if (isWelcome || isTooLargeError) {
                    // If the request was too long and the error has HTML, it means that the request body was too large because
                    // context was too big. This has been happening with the second request for extensions in particular, which
                    // leaves the chat in an unusable state. When this happens the only "fix" it to start the conversation over.
                    // If we are in actions preview we don't blow up and let tooling show debug info.
                    catastrophicErrorType = CatastrophicErrorType.FAILED;
                }
                else if (requestOptions.isTwoStepResponse || requestOptions.silent) {
                    // If we are in the middle of a two-step response or the message that was sent was silent, we have to throw an
                    // error manually since there isn't any user message to reference.
                    this.addErrorMessage();
                }
                this.serviceManager.actions.errorOccurred({
                    errorType: OnErrorType.MESSAGE_COMMUNICATION,
                    message: 'An error occurred sending a message',
                    errorCode: statusCode,
                    otherData: resultText,
                    catastrophicErrorType,
                    transactionID,
                });
                this.rejectFinalErrorOnMessage(pendingRequest, resultText || String(statusCode), true);
            }
        }
    }
    /**
     * If we are no longer going to retry sending a message, we call this function to mark the message as failed.
     */
    rejectFinalErrorOnMessage(pendingRequest, resultText = 'An undefined error occurred trying to send your message.', sendUpdateHistoryEvent = true) {
        const { sendMessagePromise, requestOptions } = pendingRequest;
        // At this point we've either failed too many times or we failed right away while trying to load the welcome node.
        this.setMessageErrorState(pendingRequest, MessageErrorState.FAILED);
        // After updating the error state get the message from the pendingRequest since it has potentially been updated by
        // setting the error state.
        const { message } = pendingRequest;
        if (!isEventRequest(message) && sendUpdateHistoryEvent) {
            // Send an update to the back-end to record this failure on the message so we can show this state in session
            // history. Note that if we're having trouble communicating with the back-end this message may itself fail
            // depending on where in the chain the failure is occurring.
            setTimeout(() => {
                this.serviceManager.actions
                    .sendUpdateHistoryEvent(message.id, {
                    history: { error_state: MessageErrorState.FAILED },
                })
                    .catch(error => consoleError('Error updating history', error));
            });
        }
        // No need to call this if it's an event message or a welcome node request.
        if (pendingRequest === this.queue.current &&
            message.input.message_type !== MessageInputType.EVENT &&
            !message.history.is_welcome_request) {
            this.messageLoadingManager.end();
        }
        // Reject the promise that lets the original caller who sent the message know that the message failed to be sent.
        sendMessagePromise.doReject(new Error(resultText));
        pendingRequest.isProcessed = true;
        if (pendingRequest === this.queue.current) {
            // Move on to next item in queue.
            this.moveToNextQueueItem();
        }
    }
    /**
     * Sends the message to watsonx Assistant Backend. Returns "any" in the error case.
     *
     * @param current The current item in the send queue.
     */
    async sendToAssistant(current) {
        const { store } = this.serviceManager;
        const state = store.getState();
        const { customSendMessage } = state.config.public.messaging;
        current.timeLastRequest = Date.now();
        if (current.isProcessed) {
            return;
        }
        try {
            // We may update the timezone and locale on this message so we need to clone it and then update the store with
            // the new object.
            const message = cloneDeep(current.message);
            current.message = message;
            const chatRequest = await this.createChatRequest(message);
            store.dispatch(actions.updateMessage(message));
            if (customSendMessage) {
                const controller = new AbortController();
                current.sendMessageController = controller;
                debugLog('Called customSendMessage', message);
                await customSendMessage(message, { signal: controller.signal }, this.serviceManager.instance);
                await this.processSuccess(current, null);
            }
            else {
                const allowStream = state.streaming.enabled;
                const fetchParameters = createFetchParameters(this.serviceManager, current.transactionID, allowStream);
                await this.doFetch(chatRequest, fetchParameters, current);
            }
        }
        catch (error) {
            consoleError('An error occurred while sending a message', error);
            const resultText = (error && (typeof error === 'string' ? error : JSON.stringify(error))) || 'There was an unidentified error.';
            this.processError(current, 0, resultText, !customSendMessage);
        }
    }
    /**
     * This function will open a fetch event source for receiving the response from the server. This will support both
     * streaming and non-streaming responses. This function will resolve when the response is fully received and
     * processed.
     */
    async doFetch(chatRequest, fetchParameters, current) {
        const { serviceManager, messageURL } = this;
        const { store, fetch } = serviceManager;
        const abortController = new AbortController();
        // The promise that will be resolved when the whole response is done (streaming or not).
        const donePromise = resolvablePromise();
        // The ID of the temporary loading message inserted while waiting for the first streaming chunk to come in.
        let streamLoadingItemID;
        // Indicate if the streaming was completed successfully (we got a final, complete response).
        let streamFinalResponse = null;
        // The timeout timer used to trigger a timeout if we stop getting streaming messages.
        let streamTimeoutTimer;
        // The ID of the MessageResponse that is being streamed.
        let streamMessageID;
        // Indicates if we've received a final response from the stream. This will occur immediately before the stream
        // is closed but since receiving the final response is asynchronous, we need this to ensure we end the streaming
        // before the final response is processed.
        let streamFinalResponseReceived = false;
        // If the response is not for streaming, this will be the raw response returned from the fetch call.
        let nonStreamResponse;
        // When web chat gets the beginning of a response from the server, it does not know right away if the response
        // will just be an immediate regular message response or if it will contain a stream to come later (there seems
        // to be a short delay between onopen and onmessage). This timer is a hack to try and determine which case we're
        // dealing with. This affects the display on the stream loading indicator as well as disabling the input field.
        let streamStartingTimer;
        /**
         * Resolve the done promise, but only if we have not seen the final response on a stream (the final response
         * will resolve this on its own and we don't want to say we're done until the final response is processed).
         */
        const doneWithoutFinal = () => {
            if (!streamFinalResponseReceived) {
                donePromise.doResolve();
            }
        };
        /**
         * Starts the timer for showing the stream loading message.
         */
        const startStreamingStartingTimer = () => {
            streamStartingTimer = setTimeout(() => {
                streamLoadingItemID = this.insertStreamLoading();
            }, 250);
        };
        /**
         * Handles when a streaming timeout or error occurs.
         */
        const handleError = () => {
            if (!streamFinalResponse) {
                consoleError('[streaming] A timeout or error while streaming has occurred.');
                // Make sure to actually close the stream.
                abortController.abort();
                if (!streamMessageID) {
                    // The streaming never even started.
                    this.addErrorMessage();
                }
                else {
                    store.dispatch(actions.setMessageHistoryProperty(streamMessageID, 'is_streaming_error', true));
                }
                doneWithoutFinal();
            }
        };
        /**
         * Starts or restarts the timeout on the streaming.
         */
        const startTimeout = () => {
            if (streamTimeoutTimer) {
                clearTimeout(streamTimeoutTimer);
            }
            streamTimeoutTimer = setTimeout(handleError, STREAMING_MESSAGE_TIMEOUT_MS);
        };
        /**
         * Called when a response is received from the fetch call. If an error is thrown from here, it will abort the
         * processing of the stream.
         */
        const onOpen = async (response) => {
            debugLog('[streaming] onOpen', response);
            if (!response) {
                // This generally only occurs in test cases that failed to configure a response on the mock.
                consoleError('No result was received for message request', JSON.stringify(chatRequest, null, 2));
                throw new Error('No result was received for message request');
            }
            if (response.headers.get(HEADER_CONTENT_TYPE) !== MIME_STREAM) {
                nonStreamResponse = response;
                // Make sure to sink the entire response so we guarantee it will be available after the fetch closes the
                // connection. This relies on the fetchWrapper to cache the result.
                await response.text();
                donePromise.doResolve();
                // Throwing this stops the library from processing the response as a streaming response. This will be
                // caught by the onerror handler. This will terminate the whole fetch process and we will then process the
                // response below.
                throw NOT_STREAMING;
            }
            // We don't want the message loading states to continue.
            this.messageLoadingManager.end();
            // At this point, it means that we are going to start streaming.
            debugLog('[streaming] onOpen - Waiting for streaming to start');
            // Start the timer to add the loading message and start the timeout timer.
            startStreamingStartingTimer();
            startTimeout();
        };
        /**
         * Called when an error occurs. If an error is thrown from here, it will prevent the library from attempting to
         * retry the connection.
         */
        const onError = (error) => {
            if (error !== NOT_STREAMING) {
                consoleError('[streaming] onError', error);
                handleError();
            }
            // This will stop the client from attempting to retry the stream.
            throw error;
        };
        /**
         * Called when the response is closed.
         */
        const onClose = () => {
            debugLog('[streaming] onClose');
            doneWithoutFinal();
        };
        /**
         * Called when a message event is received on a streaming response.
         */
        const onMessage = (event) => {
            if (donePromise.isResolved) {
                debugLog('[streaming] onMessage Ignoring message', tryJSONParse(event.data), event);
                return;
            }
            if (isEnableDebugLog()) {
                debugLog('[streaming] onMessage', tryJSONParse(event.data), event);
            }
            const eventType = event.event;
            if (eventType === 'done' || eventType === 'server_error') {
                // This is a signal for the client to close the stream.
                abortController.abort();
                doneWithoutFinal();
                return;
            }
            if (eventType === 'message' || !eventType) {
                clearTimeout(streamStartingTimer);
                const data = JSON.parse(event.data);
                if (!streamMessageID && isStreamPartialOrCompleteItem(data) && data.streaming_metadata?.response_id) {
                    // The first chunk should include the ID of the final message response we are building.
                    streamMessageID = data.streaming_metadata.response_id;
                    store.dispatch(actions.removeLocalMessageItem(streamLoadingItemID));
                    store.dispatch(actions.streamingStart(streamMessageID));
                    // If the send options say that we shouldn't wait for the streaming to finish, then resolve the promise.
                    if (current.requestOptions.returnBeforeStreaming) {
                        current.sendMessagePromise.doResolve();
                    }
                }
                if (isStreamPartialOrCompleteItem(data)) {
                    // We don't want the artificial pause when we're streaming chunks of items.
                    current.requestOptions.skipPause = true;
                    // Once we get individual items, we don't want to apply the full body message animations anymore. That
                    // will cause the message to flicker when the final response comes in.
                    current.requestOptions.disableFadeAnimation = true;
                    serviceManager.actions.receiveChunk(data, streamMessageID);
                }
                else if (data.final_response) {
                    streamFinalResponseReceived = true;
                    streamFinalResponse = data.final_response;
                    donePromise.doResolve();
                }
            }
            startTimeout();
        };
        // Kick off the fetch request.
        fetchEventSource(messageURL, {
            ...fetchParameters,
            // Used to close the stream by the client.
            signal: abortController.signal,
            fetch,
            // This prevents the library from closing the stream if the document becomes visibly hidden (like if the user
            // minimizes the browser window).
            openWhenHidden: true,
            body: JSON.stringify(chatRequest),
            onopen: onOpen,
            onerror: onError,
            onclose: onClose,
            onmessage: onMessage,
        }).catch(error => {
            if (error !== NOT_STREAMING) {
                consoleError('Error occurred during message fetch', error);
            }
        });
        // This will wait for either a regular response to complete or for an entire stream to complete or for a timeout
        // (while streaming) to occur.
        await donePromise;
        clearTimeout(streamStartingTimer);
        if (nonStreamResponse) {
            // We just received a regular, non-streaming response.
            await this.handleRegularResponse(nonStreamResponse, current);
        }
        else {
            // We handled a streaming response. We need to figure out if it was completed successfully.
            // This may be redundant but just in case an error occurred, let's make sure this gets removed.
            store.dispatch(actions.removeLocalMessageItem(streamLoadingItemID));
            clearTimeout(streamTimeoutTimer);
            if (streamFinalResponse) {
                this.processSuccess(current, streamFinalResponse);
            }
            else {
                if (streamMessageID) {
                    // The stream sent us some chunks but never finished.
                    store.dispatch(actions.setMessageHistoryProperty(streamMessageID, 'is_streaming_error', true));
                }
                this.processError(current, 0, 'Stream incomplete', false);
                this.moveToNextQueueItem();
            }
        }
    }
    /**
     * This function sends a message event request to the assistant to trigger stop streaming from the watsonx
     * Assistant backend.
     *
     * @param streamID The ID of the stream to stop.
     */
    async stopStreaming(streamID) {
        const { store } = this.serviceManager;
        const result = await this.sendEventMessage({
            name: EVENT_STOP_STREAMING,
            stream_id: streamID,
        });
        // Re-enable the stop streaming button if the stop streaming message event fails to let the user try again.
        if (!result?.ok) {
            store.dispatch(actions.setStopStreamingButtonDisabled(false));
        }
    }
    /**
     * This function will send a message event request to the server.
     */
    async sendEventMessage(eventData) {
        try {
            const eventMessage = createEventMessageRequest(eventData);
            const chatRequest = await this.createChatRequest(eventMessage);
            const { response } = await this.doFetchSimple(chatRequest);
            return response;
        }
        catch (error) {
            consoleError('An error occurred sending an event message request', error);
        }
        return null;
    }
    /**
     * Handles a regular, non-streaming response from the server.
     */
    async handleRegularResponse(fetchResponse, current) {
        current.lastResponse = fetchResponse;
        if (fetchResponse.ok) {
            const response = await safeFetchJSON(fetchResponse);
            // Retrieve the session length header that's been provided by the back-end.
            const sessionLengthHeader = fetchResponse.headers?.get('x-watson-session-timeout');
            if (sessionLengthHeader) {
                // Use the header value provided by the back-end to start a session timer internally.
                this.serviceManager.actions.startSessionTimer(sessionLengthHeader);
            }
            this.processSuccess(current, response);
        }
        else {
            const responseText = await fetchResponse.text();
            const { toolingType } = this.serviceManager.store.getState().config.public.__ibm__;
            // Allow a retry outside of tooling, or if we're in tooling, only allow retries on the preview link.
            const allowRetry = !toolingType || toolingType === ToolingType.PREVIEW_LINK;
            this.processError(current, fetchResponse.status, responseText, allowRetry);
        }
    }
    /**
     * Makes a request on the message endpoint for search results based on the given text from the user.
     */
    async queryForSearchResults(searchText) {
        try {
            return await resolveOrTimeout(this.doQueryForSearchResults(searchText), MS_MAX_SEARCH_TIME);
        }
        catch (error) {
            this.serviceManager.actions.errorOccurred({
                errorType: OnErrorType.MESSAGE_COMMUNICATION,
                message: 'Request for search results timed out',
            });
            return null;
        }
    }
    /**
     * Makes a request on the message endpoint for search results based on the given text from the user.
     */
    async doQueryForSearchResults(searchText) {
        const request = createMessageRequestForText(searchText);
        request.input.message_type = MessageInputType.SEARCH;
        const chatRequest = await this.createChatRequest(request);
        chatRequest.exclude_history = true;
        const { response, transactionID } = await this.doFetchSimple(chatRequest);
        try {
            if (response?.ok) {
                const searchResponse = await response.json();
                const item = searchResponse?.output.generic[0];
                if (isSearchResultItem(item) && !isEmptySearchResultItem(item)) {
                    return item;
                }
            }
            else {
                this.serviceManager.actions.errorOccurred({
                    errorType: OnErrorType.MESSAGE_COMMUNICATION,
                    message: 'An error occurred getting search results',
                    errorCode: response.status,
                    otherData: await safeFetchTextWithTimeout(response),
                    transactionID,
                });
            }
        }
        catch (error) {
            consoleError('Error in doQueryForSearchResults', error);
        }
        return null;
    }
    /**
     * Sends a request to the message endpoint and returns an object containing the result and the request transaction
     * id.
     */
    async doFetchSimple(chatRequest) {
        const fetchParameters = createFetchParameters(this.serviceManager, uuid(UUIDType.MISCELLANEOUS), false);
        const transactionID = fetchParameters.headers[API_DEBUGGING_HEADER_NAME];
        try {
            const result = await doWithJWTRetry(() => {
                return this.serviceManager.fetch(this.messageURL, {
                    ...fetchParameters,
                    body: JSON.stringify(chatRequest),
                });
            }, this.serviceManager);
            return { response: result, transactionID };
        }
        catch (error) {
            consoleError('Error in doFetchSingly', error);
        }
        return { response: null, transactionID };
    }
    /**
     * Gets the context state of the previous message and adds it to the current message.
     *
     * @param message The current message we are sending.
     */
    copyPreviousContextToMessage(message) {
        // Retrieve the context from the last message.
        const storeState = this.serviceManager.store.getState();
        const isWelcomeNodeRequest = message.history && message.history.is_welcome_request;
        const { config } = storeState;
        const lastResponse = getLastBotResponseWithContext(storeState);
        const currentContext = lastResponse?.context;
        // If it's a request for a welcome node, there is no previous context to worry about, but we do want to scaffold to
        // allow us to add timezone info and the like. Similarly, if there isn't a last context.
        if (isWelcomeNodeRequest || !currentContext) {
            message.context = mergeWith({}, EMPTY_CONTEXT(config.remote.mainConfig?.assistant), message.context, replaceCurrentArrayValue);
        }
        else {
            // Merge in the skill context from the previous context with the new message or ensure there's some sensible
            // default values.
            message.context = mergeWith({}, EMPTY_CONTEXT(config.remote.mainConfig?.assistant), { skills: currentContext?.skills || {} }, { integrations: currentContext?.integrations || {} }, message.context, replaceCurrentArrayValue);
        }
    }
    /**
     * If there are items in the send queue, will grab the zero index item and send it to the assistant back-end via
     * this.sendToAssistant.
     */
    async runQueueIfReady() {
        if (!this.queue.current && this.queue.waiting.length > 0) {
            const { eventBus, store } = this.serviceManager;
            this.clearCurrentQueueItem();
            this.queue.current = this.queue.waiting.shift();
            const { current } = this.queue;
            const { message, source, requestOptions } = current;
            const state = store.getState();
            const { config } = store.getState();
            const { public: publicConfig, remote: remoteConfig } = config;
            const { mainConfig } = remoteConfig;
            current.timeFirstRequest = Date.now();
            // Do all the normal things for our general messageRequests, however for event messages we skip this.
            if (message.input.message_type !== MessageInputType.EVENT) {
                // If needed, copy the context from the last response we received.
                if (!requestOptions.dontCopyContext && publicConfig.messaging.copyPreviousContextOnRequest) {
                    this.copyPreviousContextToMessage(message);
                }
                const lastResponse = getLastBotResponseWithContext(state);
                if (lastResponse) {
                    if (!isAtStackRoot(getDialogState(lastResponse, mainConfig))) {
                        message.thread_id = lastResponse.thread_id;
                    }
                    else {
                        message.thread_id = THREAD_ID_MAIN;
                    }
                }
                // Welcome node fetching gets the spinner instead of the progress bar.
                if (!message.history.is_welcome_request) {
                    const LOADING_INDICATOR_TIMER = !publicConfig.messaging?.messageLoadingIndicatorTimeoutSecs &&
                        publicConfig.messaging?.messageLoadingIndicatorTimeoutSecs !== 0
                        ? MS_MAX_SILENT_LOADING
                        : publicConfig.messaging.messageLoadingIndicatorTimeoutSecs * 1000;
                    this.messageLoadingManager.start(() => {
                        this.serviceManager.store.dispatch(actions.addIsLoadingCounter(1));
                    }, (didExceedMaxLoading) => {
                        if (didExceedMaxLoading) {
                            this.serviceManager.store.dispatch(actions.addIsLoadingCounter(-1));
                        }
                    }, () => {
                        this.cancelMessageRequestByID(message.id, true);
                    }, LOADING_INDICATOR_TIMER, requestOptions?.isTwoStepResponse ? MS_MAX_EXTENSION_TIME : this.timeoutMS);
                }
                if (current.isProcessed) {
                    // This message was cancelled.
                    return;
                }
                // Grab the original text before it can be modified by a pre:send handler.
                const originalUserText = message.history?.label || message.input.text;
                const beforeSendPayload = requestOptions?.sendEventPayload;
                // Fire the pre:send event. User code is allowed to modify the message at this point. If this takes longer than MS_MAX_SILENT_LOADING
                // we show a loading state.
                await eventBus.fire({ type: "pre:send" /* BusEventType.PRE_SEND */, data: message, source, beforeSendPayload }, this.serviceManager.instance);
                if (current.isProcessed) {
                    // This message was cancelled.
                    return;
                }
                // We now want to update the store with whatever edits have been made to the message.
                const localMessage = inputItemToLocalItem(message, originalUserText, current.localMessageID);
                // If history.silent is set to true, we don't add the message to the redux store as we do not want to show it, so
                // we don't need to update it here either.
                if (!message.history.silent) {
                    store.dispatch(actions.updateLocalMessageItem(localMessage));
                    store.dispatch(actions.updateMessage(message));
                }
                deepFreeze$1(message);
                await eventBus.fire({ type: "send" /* BusEventType.SEND */, data: message, source, beforeSendPayload }, this.serviceManager.instance);
            }
            this.sendToAssistant(current);
        }
    }
    /**
     * Add a new message to the message queue.
     *
     * @param message A new message to add to the message queue.
     @param source The source of the message.
     * @param localMessageID The ID of the {@link LocalMessageItem} created from the current request.
     * @param sendMessagePromise A promise that we will resolve or reject if the message is sent.
     * @param requestOptions The options that were included when the request was sent.
     */
    addToMessageQueue(message, source, localMessageID, sendMessagePromise, requestOptions = {}) {
        const newPendingMessage = {
            localMessageID,
            message,
            sendMessagePromise,
            requestOptions: requestOptions || {},
            timeFirstRequest: 0,
            timeLastRequest: 0,
            trackData: {
                numErrors: 0,
                lastRequestTime: 0,
                totalRequestTime: 0,
            },
            tryCount: 0,
            transactionID: uuid(UUIDType.MISCELLANEOUS),
            isProcessed: false,
            source,
        };
        if (!requestOptions.pauseStartTime) {
            requestOptions.pauseStartTime = Date.now();
        }
        if (requestOptions?.skipQueue) {
            this.queue.waiting.splice(0, 0, newPendingMessage);
        }
        else {
            this.queue.waiting.push(newPendingMessage);
        }
        // If the current message has entered the retrying state, then we need to update the new message to put it into
        // the waiting state.
        if (this.queue.current && !this.queue.current.requestOptions?.isTwoStepResponse) {
            if (message.history?.error_state === MessageErrorState.RETRYING) {
                this.setMessageErrorState(newPendingMessage, MessageErrorState.WAITING);
            }
        }
    }
    /**
     * Performs any finishes steps necessary to complete the current queue item.
     */
    clearCurrentQueueItem() {
        if (this.queue.current) {
            this.queue.current = null;
        }
    }
    /**
     * Move to next step in queue.
     */
    moveToNextQueueItem() {
        this.clearCurrentQueueItem();
        this.runQueueIfReady();
    }
    /**
     * Changes the error state for the message with the given id and makes an a11y announcement if appropriate.
     */
    setMessageErrorState(pendingRequest, errorState) {
        const { message } = pendingRequest;
        // Find the current state for the message. Note that we want to look up the current state from the store which
        // might be different from the message object we originally sent.
        const { allMessagesByID } = this.serviceManager.store.getState();
        // Update the error state if it's changed (but don't try to change an undefined state to NONE).
        const messageToUpdate = allMessagesByID[message.id];
        if (messageToUpdate) {
            const currentState = messageToUpdate.history?.error_state;
            const errorSame = currentState === errorState || (errorState === MessageErrorState.NONE && !currentState);
            if (!errorSame) {
                // Figure out what announcement we need to make. Note that we don't announce changes in to the WAITING state.
                let announceMessageID;
                // eslint-disable-next-line default-case
                switch (errorState) {
                    case MessageErrorState.FAILED: {
                        announceMessageID = 'errors_ariaMessageFailed';
                        break;
                    }
                }
                // Announce the change if necessary.
                if (announceMessageID) {
                    this.serviceManager.store.dispatch(actions.announceMessage({ messageID: announceMessageID }));
                }
                this.serviceManager.store.dispatch(actions.setMessageErrorState(message.id, errorState));
                // After updating store get the updated message back from store and use it within the messageService. If we
                // don't get the updated message back within the message service we could try to save an updated version of this
                // message in store in the future but the copy within this service will be out of date.
                const { allMessagesByID } = this.serviceManager.store.getState();
                pendingRequest.message = allMessagesByID[message.id];
            }
        }
    }
    /**
     * Send a message to watsonx Assistant back-end. Returns "any" in the error case.
     *
     * @param message Takes an object in the shape of a v2 message API Send object. See
     * [Docs](https://cloud.ibm.com/apidocs/assistant-v2#send-user-input-to-assistant).
     * @param source The source of the message.
     * @param localMessageID The ID of the {@link LocalMessageItem} created from the current request.
     * @param requestOptions The options that were included when the request was sent.
     */
    send(message, source, localMessageID, requestOptions) {
        if (this.serviceManager.store.getState().config.public.__ibm__?.disableMessageService) {
            debugLog('An attempt to send a message was made but the message service is disabled.', message);
            return null;
        }
        message.history.timestamp = message.history.timestamp || Date.now();
        // The messageService does different things based off the message type so lets make sure one exists.
        message.input = message.input || {};
        message.input.message_type = message.input.message_type || MessageInputType.TEXT;
        // Create a Promise that the caller can wait on that we'll resolve if/when the message is finally successfully sent
        // to the assistant. This gets resolved or rejected in this.processSuccess or this.processError respectively.
        const sendMessagePromise = resolvablePromise();
        // Add our new message to the queue and kick off the queue.
        this.addToMessageQueue(message, source, localMessageID, sendMessagePromise, requestOptions);
        this.runQueueIfReady();
        // Return the promise that is either successfully resolve or rejected in this.processSuccess or this.processError.
        return sendMessagePromise;
    }
    /**
     * Cancels all message requests including any that are running now and any that are waiting in the queue.
     */
    cancelAllMessageRequests() {
        while (this.queue.waiting.length) {
            this.cancelMessageRequestByID(this.queue.waiting[0].message.id, false);
        }
        if (this.queue.current) {
            this.cancelMessageRequestByID(this.queue.current.message.id, false);
            this.clearCurrentQueueItem();
        }
    }
    /**
     * Cancel a message given an ID. Can be a message in process or one that is waiting to be processed.
     */
    async cancelMessageRequestByID(messageID, logError) {
        let pendingRequest;
        if (this.queue.current?.message.id === messageID) {
            pendingRequest = this.queue.current;
        }
        else {
            const index = this.queue.waiting.findIndex(item => item.message.id === messageID);
            if (index !== -1) {
                pendingRequest = this.queue.waiting[index];
                this.queue.waiting.splice(index, 1);
            }
        }
        if (pendingRequest) {
            const { lastResponse, transactionID, sendMessageController } = pendingRequest;
            // If someone is using customMessageSend, we let them know they should abort the request.
            sendMessageController?.abort('Message was cancelled');
            this.rejectFinalErrorOnMessage(pendingRequest, 'Message was cancelled', false);
            if (logError) {
                this.serviceManager.actions.errorOccurred({
                    errorType: OnErrorType.MESSAGE_COMMUNICATION,
                    message: 'Message was cancelled',
                    errorCode: lastResponse?.status,
                    otherData: await safeFetchTextWithTimeout(lastResponse),
                    transactionID,
                });
            }
        }
    }
    /**
     * Returns the {@link ChatRequest} object that is sent to the assistant.
     */
    async createChatRequest(message) {
        const { userID, persistedToBrowserStorage, locale } = this.serviceManager.store.getState();
        const { chatState } = persistedToBrowserStorage;
        const { sessionID } = chatState;
        if (!sessionID && !message.context?.global?.system?.timezone) {
            const timezone = await getTimeZone();
            merge(message, { context: { global: { system: { timezone } } } });
        }
        if (this.localeIsExplicit &&
            (!sessionID || this.pendingLocale) &&
            !message.context?.global?.system?.locale) {
            // If we're starting a new session or the locale has been changed but not sent to the assistant yet, we need to
            // set it in the context. But don't override any existing value in the context and don't set the locale unless
            // it was explicitly set in the configuration.
            const sanitizedLocale = sanitizeLocale(locale);
            if (sanitizedLocale) {
                merge(message, { context: { global: { system: { locale: sanitizedLocale } } } });
            }
            this.pendingLocale = false;
        }
        return {
            value: message,
            session_id: sessionID,
            user_id: userID,
        };
    }
    /**
     * If there was a JWT error then ask for a new identity token and send the error state. Returns true if there was a
     * JWT error, false if there wasn't.
     */
    async checkAndProcessJWTError(pendingRequest, statusCode, resultText, isInAttemptWindow, isWelcome, isSecurityEnabled) {
        if (isJWTError(statusCode, isSecurityEnabled, resultText) && isInAttemptWindow) {
            // Fire event with callback to allow Deb to continue the process queue.
            try {
                await getNewIdentityToken(this.serviceManager, isWelcome ? CatastrophicErrorType.FAILED : undefined);
                this.sendToAssistantAndUpdateErrorState(pendingRequest);
            }
            catch (error) {
                this.rejectFinalErrorOnMessage(pendingRequest, resultText, false);
            }
            return true;
        }
        return false;
    }
    /**
     * Sends a track event about a message retry state.
     */
    trackMessageRetry(messageStatus, request) {
        this.serviceManager.trackService.track({
            eventName: 'Message retry',
            eventDescription: 'A message had to be retried because of an error',
            messageStatus,
            lastRequestTime: request.trackData?.lastRequestTime,
            retryNumber: request.trackData?.numErrors,
            lastResponseCode: request.lastResponse?.status,
        });
    }
}
/**
 * watsonx Assistant requires a full locale. If the user provides a language only then we need to switch to a
 * fallback locale.
 */
function sanitizeLocale(desiredLocale) {
    const fallback = LANGUAGE_LOCALE_FALLBACKS.get(desiredLocale);
    return fallback || desiredLocale;
}

/**
 * This is a global class responsible for managing and providing access to references of "services" in the application.
 * Services should not hold references to each other but rather should always use the service manager to access
 * other services. This will allow for services to be created lazily and to support circular dependencies.
 */
class ServiceManager {
    constructor() {
        /**
         * This is a registry of the elements that act as the hosts for custom responses. The key of the map is the ID of
         * the message and the value is an object with the Element created by the widget that was provided to event listeners that they
         * can attach their own elements to. These elements are attached to the appropriate React component when rendered. Optionally, this
         * object can also include a slotName for when rendering the element into a slot when shadowRoot is enabled.
         */
        this.userDefinedElementRegistry = new Map();
        /**
         * Indicates the number of times that a restart has occurred. This can be used by various asynchronous operations to
         * determine if a restart occurred during the operation and if the results should be ignored.
         */
        this.restartCount = 0;
        /**
         * Indicates that we should discard the session for the next user who is loaded. This is used to cover the case
         * where restartConversation is called before a user has been loaded.
         */
        this.discardNextUserSession = false;
    }
    /**
     * Convenience functions for firing events on the event bus.
     */
    async fire(busEvent) {
        return this.eventBus.fire(busEvent, this.instance);
    }
}

// We use sessionStorage instead of localStorage to not have to have a public cookie policy that must be accepted in EU.
const storage = IS_SESSION_STORAGE() ? window.sessionStorage : storage$2;
class UserSessionStorageService {
    constructor(serviceManager) {
        this.serviceManager = serviceManager;
        this.prefix = `CARBON_CHAT_SESSION${this.serviceManager?.namespace?.suffix || ''}_`;
        this.defaultIntegrationID = this.serviceManager?.namespace?.suffix || 'default';
    }
    /**
     * Get the session object for the user id.
     */
    loadChatSession(userID = null, integrationID = this.defaultIntegrationID, sessionID) {
        try {
            const chatSessionString = storage.getItem(this.getChatSessionKey(userID, integrationID));
            const chatSession = chatSessionString ? JSON.parse(chatSessionString) : null;
            // If the saved session is from a previous version of web chat, we just throw it away to avoid having to deal with
            // having to make sure these sessions are backwards compatible.
            if (chatSession?.version === VERSION) {
                // If we're being asked to load a specific session or discard the previous session, then make sure to
                // delete the previous session (as long as it's actually a different session).
                if ((sessionID || this.serviceManager?.discardNextUserSession) &&
                    chatSession?.sessionID &&
                    sessionID !== chatSession?.sessionID) {
                    // If we don't want to use the session stored here, then delete it.
                    debugLog("A restart or alternate session was used before the first user was loaded. Discarding the first user's session.");
                    const publicConfig = this.serviceManager?.store.getState().config.public;
                    this.serviceManager?.actions.deleteServerSession(chatSession.sessionID, publicConfig);
                    chatSession.sessionID = null;
                }
                if (sessionID) {
                    // If the config object specifies a session ID, use that instead of what was previously stored.
                    chatSession.sessionID = sessionID;
                }
                if (this.serviceManager) {
                    this.serviceManager.discardNextUserSession = false;
                }
                return chatSession;
            }
            this.clearChatSession(userID, integrationID);
            return null;
        }
        catch (error) {
            this.clearChatSession(userID, integrationID);
            return null;
        }
    }
    /**
     * Get the session object stored based on the integrationID.
     */
    loadLauncherSession(integrationID = this.defaultIntegrationID) {
        try {
            const launcherSessionString = storage.getItem(this.getLauncherSessionKey(integrationID));
            const launcherSession = launcherSessionString ? JSON.parse(launcherSessionString) : null;
            // If the saved session is from a previous version of web chat, we just throw it away to avoid having to deal with
            // having to make sure these sessions are backwards compatible.
            if (launcherSession?.version === VERSION) {
                launcherSession.wasLoadedFromBrowser = true;
                return launcherSession;
            }
            this.clearLauncherSession(integrationID);
            return null;
        }
        catch (error) {
            this.clearLauncherSession(integrationID);
            return null;
        }
    }
    /**
     * Set a new version of the user based session.
     */
    persistChatSession(userID = null, integrationID = this.defaultIntegrationID, session) {
        try {
            storage.setItem(this.getChatSessionKey(userID, integrationID), JSON.stringify(session));
        }
        catch (error) {
            consoleError('Error in persistChatSession', error);
        }
    }
    /**
     * Set a new version of the user based session.
     */
    persistLauncherSession(integrationID = this.defaultIntegrationID, session) {
        try {
            storage.setItem(this.getLauncherSessionKey(integrationID), JSON.stringify(session));
        }
        catch (error) {
            consoleError('Error in persistLauncherSession', error);
        }
    }
    /**
     * Remove the given userID session from storage.
     */
    clearChatSession(userID = null, integrationID = this.defaultIntegrationID) {
        try {
            storage.removeItem(this.getChatSessionKey(userID, integrationID));
        }
        catch (error) {
            consoleError('Error in clearChatSession', error);
        }
    }
    /**
     * Remove the given integrationID session from storage.
     */
    clearLauncherSession(integrationID = this.defaultIntegrationID) {
        try {
            storage.removeItem(this.getLauncherSessionKey(integrationID));
        }
        catch (error) {
            consoleError('Error in clearLauncherSession', error);
        }
    }
    /**
     * Returns the sessionStorage key for the session id for the given user.
     */
    getChatSessionKey(userID = null, integrationID = this.defaultIntegrationID) {
        return `${this.prefix}${userID}:${integrationID}`;
    }
    /**
     * Returns the sessionStorage key for the session id for the given user.
     */
    getLauncherSessionKey(integrationID = this.defaultIntegrationID) {
        return `${this.prefix}${integrationID}`;
    }
}

/**
 * Adapted from React: https://github.com/facebook/react/blob/master/packages/shared/formatProdErrorMessage.js
 *
 * Do not require this module directly! Use normal throw error calls. These messages will be replaced with error codes
 * during build.
 * @param {number} code
 */
function formatProdErrorMessage(code) {
  return "Minified Redux error #" + code + "; visit https://redux.js.org/Errors?code=" + code + " for the full message or " + 'use the non-minified dev environment for full errors. ';
}

// Inlined version of the `symbol-observable` polyfill
var $$observable = (function () {
  return typeof Symbol === 'function' && Symbol.observable || '@@observable';
})();

/**
 * These are private action types reserved by Redux.
 * For any unknown actions, you must return the current state.
 * If the current state is undefined, you must return the initial state.
 * Do not reference these action types directly in your code.
 */
var randomString = function randomString() {
  return Math.random().toString(36).substring(7).split('').join('.');
};

var ActionTypes = {
  INIT: "@@redux/INIT" + randomString(),
  REPLACE: "@@redux/REPLACE" + randomString(),
  PROBE_UNKNOWN_ACTION: function PROBE_UNKNOWN_ACTION() {
    return "@@redux/PROBE_UNKNOWN_ACTION" + randomString();
  }
};

/**
 * @param {any} obj The object to inspect.
 * @returns {boolean} True if the argument appears to be a plain object.
 */
function isPlainObject(obj) {
  if (typeof obj !== 'object' || obj === null) return false;
  var proto = obj;

  while (Object.getPrototypeOf(proto) !== null) {
    proto = Object.getPrototypeOf(proto);
  }

  return Object.getPrototypeOf(obj) === proto;
}

/**
 * @deprecated
 *
 * **We recommend using the `configureStore` method
 * of the `@reduxjs/toolkit` package**, which replaces `createStore`.
 *
 * Redux Toolkit is our recommended approach for writing Redux logic today,
 * including store setup, reducers, data fetching, and more.
 *
 * **For more details, please read this Redux docs page:**
 * **https://redux.js.org/introduction/why-rtk-is-redux-today**
 *
 * `configureStore` from Redux Toolkit is an improved version of `createStore` that
 * simplifies setup and helps avoid common bugs.
 *
 * You should not be using the `redux` core package by itself today, except for learning purposes.
 * The `createStore` method from the core `redux` package will not be removed, but we encourage
 * all users to migrate to using Redux Toolkit for all Redux code.
 *
 * If you want to use `createStore` without this visual deprecation warning, use
 * the `legacy_createStore` import instead:
 *
 * `import { legacy_createStore as createStore} from 'redux'`
 *
 */

function createStore(reducer, preloadedState, enhancer) {
  var _ref2;

  if (typeof preloadedState === 'function' && typeof enhancer === 'function' || typeof enhancer === 'function' && typeof arguments[3] === 'function') {
    throw new Error(formatProdErrorMessage(0) );
  }

  if (typeof preloadedState === 'function' && typeof enhancer === 'undefined') {
    enhancer = preloadedState;
    preloadedState = undefined;
  }

  if (typeof enhancer !== 'undefined') {
    if (typeof enhancer !== 'function') {
      throw new Error(formatProdErrorMessage(1) );
    }

    return enhancer(createStore)(reducer, preloadedState);
  }

  if (typeof reducer !== 'function') {
    throw new Error(formatProdErrorMessage(2) );
  }

  var currentReducer = reducer;
  var currentState = preloadedState;
  var currentListeners = [];
  var nextListeners = currentListeners;
  var isDispatching = false;
  /**
   * This makes a shallow copy of currentListeners so we can use
   * nextListeners as a temporary list while dispatching.
   *
   * This prevents any bugs around consumers calling
   * subscribe/unsubscribe in the middle of a dispatch.
   */

  function ensureCanMutateNextListeners() {
    if (nextListeners === currentListeners) {
      nextListeners = currentListeners.slice();
    }
  }
  /**
   * Reads the state tree managed by the store.
   *
   * @returns {any} The current state tree of your application.
   */


  function getState() {
    if (isDispatching) {
      throw new Error(formatProdErrorMessage(3) );
    }

    return currentState;
  }
  /**
   * Adds a change listener. It will be called any time an action is dispatched,
   * and some part of the state tree may potentially have changed. You may then
   * call `getState()` to read the current state tree inside the callback.
   *
   * You may call `dispatch()` from a change listener, with the following
   * caveats:
   *
   * 1. The subscriptions are snapshotted just before every `dispatch()` call.
   * If you subscribe or unsubscribe while the listeners are being invoked, this
   * will not have any effect on the `dispatch()` that is currently in progress.
   * However, the next `dispatch()` call, whether nested or not, will use a more
   * recent snapshot of the subscription list.
   *
   * 2. The listener should not expect to see all state changes, as the state
   * might have been updated multiple times during a nested `dispatch()` before
   * the listener is called. It is, however, guaranteed that all subscribers
   * registered before the `dispatch()` started will be called with the latest
   * state by the time it exits.
   *
   * @param {Function} listener A callback to be invoked on every dispatch.
   * @returns {Function} A function to remove this change listener.
   */


  function subscribe(listener) {
    if (typeof listener !== 'function') {
      throw new Error(formatProdErrorMessage(4) );
    }

    if (isDispatching) {
      throw new Error(formatProdErrorMessage(5) );
    }

    var isSubscribed = true;
    ensureCanMutateNextListeners();
    nextListeners.push(listener);
    return function unsubscribe() {
      if (!isSubscribed) {
        return;
      }

      if (isDispatching) {
        throw new Error(formatProdErrorMessage(6) );
      }

      isSubscribed = false;
      ensureCanMutateNextListeners();
      var index = nextListeners.indexOf(listener);
      nextListeners.splice(index, 1);
      currentListeners = null;
    };
  }
  /**
   * Dispatches an action. It is the only way to trigger a state change.
   *
   * The `reducer` function, used to create the store, will be called with the
   * current state tree and the given `action`. Its return value will
   * be considered the **next** state of the tree, and the change listeners
   * will be notified.
   *
   * The base implementation only supports plain object actions. If you want to
   * dispatch a Promise, an Observable, a thunk, or something else, you need to
   * wrap your store creating function into the corresponding middleware. For
   * example, see the documentation for the `redux-thunk` package. Even the
   * middleware will eventually dispatch plain object actions using this method.
   *
   * @param {Object} action A plain object representing “what changed”. It is
   * a good idea to keep actions serializable so you can record and replay user
   * sessions, or use the time travelling `redux-devtools`. An action must have
   * a `type` property which may not be `undefined`. It is a good idea to use
   * string constants for action types.
   *
   * @returns {Object} For convenience, the same action object you dispatched.
   *
   * Note that, if you use a custom middleware, it may wrap `dispatch()` to
   * return something else (for example, a Promise you can await).
   */


  function dispatch(action) {
    if (!isPlainObject(action)) {
      throw new Error(formatProdErrorMessage(7) );
    }

    if (typeof action.type === 'undefined') {
      throw new Error(formatProdErrorMessage(8) );
    }

    if (isDispatching) {
      throw new Error(formatProdErrorMessage(9) );
    }

    try {
      isDispatching = true;
      currentState = currentReducer(currentState, action);
    } finally {
      isDispatching = false;
    }

    var listeners = currentListeners = nextListeners;

    for (var i = 0; i < listeners.length; i++) {
      var listener = listeners[i];
      listener();
    }

    return action;
  }
  /**
   * Replaces the reducer currently used by the store to calculate the state.
   *
   * You might need this if your app implements code splitting and you want to
   * load some of the reducers dynamically. You might also need this if you
   * implement a hot reloading mechanism for Redux.
   *
   * @param {Function} nextReducer The reducer for the store to use instead.
   * @returns {void}
   */


  function replaceReducer(nextReducer) {
    if (typeof nextReducer !== 'function') {
      throw new Error(formatProdErrorMessage(10) );
    }

    currentReducer = nextReducer; // This action has a similiar effect to ActionTypes.INIT.
    // Any reducers that existed in both the new and old rootReducer
    // will receive the previous state. This effectively populates
    // the new state tree with any relevant data from the old one.

    dispatch({
      type: ActionTypes.REPLACE
    });
  }
  /**
   * Interoperability point for observable/reactive libraries.
   * @returns {observable} A minimal observable of state changes.
   * For more information, see the observable proposal:
   * https://github.com/tc39/proposal-observable
   */


  function observable() {
    var _ref;

    var outerSubscribe = subscribe;
    return _ref = {
      /**
       * The minimal observable subscription method.
       * @param {Object} observer Any object that can be used as an observer.
       * The observer object should have a `next` method.
       * @returns {subscription} An object with an `unsubscribe` method that can
       * be used to unsubscribe the observable from the store, and prevent further
       * emission of values from the observable.
       */
      subscribe: function subscribe(observer) {
        if (typeof observer !== 'object' || observer === null) {
          throw new Error(formatProdErrorMessage(11) );
        }

        function observeState() {
          if (observer.next) {
            observer.next(getState());
          }
        }

        observeState();
        var unsubscribe = outerSubscribe(observeState);
        return {
          unsubscribe: unsubscribe
        };
      }
    }, _ref[$$observable] = function () {
      return this;
    }, _ref;
  } // When a store is created, an "INIT" action is dispatched so that every
  // reducer returns their initial state. This effectively populates
  // the initial state tree.


  dispatch({
    type: ActionTypes.INIT
  });
  return _ref2 = {
    dispatch: dispatch,
    subscribe: subscribe,
    getState: getState,
    replaceReducer: replaceReducer
  }, _ref2[$$observable] = observable, _ref2;
}

var ai_slug_title = "Powered by IBM watsonx";
var ai_slug_description = "IBM watsonx is powered by the latest AI models to intelligently process conversations and provide help whenever and wherever you may need it.";
var components_overflow_ariaLabel = "Open and close list of options";
var components_swiper_currentLabel = "{currentSlideNumber}/{totalSlideCount}";
var errors_communicating = "{botName} isn't available right now. Problems with a related system are preventing the data from being supplied.";
var errors_imageSource = "Image is not available.";
var errors_videoSource = "Video is not available.";
var errors_audioSource = "Audio is not available.";
var errors_iframeSource = "The web page is not available.";
var errors_singleMessage = "There is an error with the message you just sent, but feel free to ask me something else.";
var errors_ariaMessageRetrying = "We are having some trouble sending your message but are still trying";
var errors_ariaMessageFailed = "Your message failed to be sent";
var errors_noAgentsAvailable = "No agents are available.";
var errors_noAgentsJoined = "No agents accepted the chat";
var errors_connectingToAgent = "I'm sorry. Something went wrong and I cannot connect you to an agent right now.";
var errors_busy = "I'm sorry, but I can't help you right now. I'm answering questions from lots of people at the moment. Please try again later.";
var errors_agentAppSessionExpired = "I'm sorry, but access to the chat history has expired.";
var errors_generalContent = "There was an error displaying this content";
var errors_somethingWrong = "Something went wrong";
var input_ariaLabel = "Message to send";
var input_placeholder = "Type something...";
var input_buttonLabel = "Click to send message";
var input_uploadButtonLabel = "Add files to upload";
var window_title = "Chat window";
var window_ariaTourRegion = "Tour";
var window_ariaTourRegionNamespace = "Tour {namespace}";
var window_ariaChatRegion = "Chat";
var window_ariaChatRegionNamespace = "Chat {namespace}";
var window_ariaWindowOpened = "The chat window has been opened";
var window_ariaWindowClosed = "The chat window has been closed";
var window_ariaWindowLoading = "The chat is loading.";
var launcher_isOpen = "Close the chat window";
var launcher_isClosed = "Open the chat window";
var launcher_isTourOpen = "Close the tour window";
var launcher_isTourClosed = "Open the tour window";
var launcher_desktopGreeting = "Hi! I’m a virtual assistant. How can I help you today?";
var launcher_mobileGreeting = "Hi! How can I help you today?";
var launcher_ariaIsExpanded = "Close the chat launcher";
var launcher_closeButton = "Close";
var messages_youSaid = "You said";
var messages_botSaid = "{botName} said";
var messages_agentSaid = "The live agent said";
var messages_searchResults = "Search results";
var messages_searchResultsLink = "Open this search result in a new window";
var messages_searchResultsOpenDocument = "Open document";
var messages_searchResultsOpenDocumentWithLabel = "Open document \"{documentName}\"";
var messages_searchResultsExpand = "Expand";
var messages_searchResultsCollapse = "Collapse";
var messages_botIsLoading = "{botName} is loading";
var messages_botIsTyping = "{botName} is typing";
var messages_agentIsTyping = "The live agent is typing";
var messages_scrollHandle = "Chat history begin";
var messages_scrollHandleDetailed = "Chat history begin. Activate to focus the first message then use the arrow, home, and end keys to move between messages. Press escape to exit.";
var messages_scrollHandleEnd = "Chat history end";
var messages_scrollHandleEndDetailed = "Chat history end. Activate to focus the last message then use the arrow, home, and end keys to move between messages. Press escape to exit.";
var message_labelBot = "{actorName} {timestamp}";
var message_labelYou = "You {timestamp}";
var notifications_toastClose = "Close notification";
var buttons_restart = "Restart conversation";
var buttons_cancel = "Cancel";
var buttons_retry = "Retry";
var options_select = "Select an option";
var options_ariaOptionsDisabled = "These options are disabled and cannot be selected";
var header_previewLinkTitle = "Assistant preview";
var header_ariaCloseRestart = "End chat and close the window";
var header_ariaBotAvatar = "{botName} avatar image";
var header_overflowMenu_options = "Options";
var homeScreen_returnToAssistant = "Return to assistant";
var homeScreen_returnToHome = "Return to the home screen";
var homeScreen_overflowMenuHomeScreen = "Home screen";
var homeScreen_ariaQuickStartListButton = "Quick start menu";
var homeScreen_ariaQuickStartListOpened = "The quick start menu has been opened.";
var homeScreen_ariaQuickStartListClosed = "The quick start menu has been closed.";
var default_agent_availableMessage = "Request an agent, and I'll notify you when they're ready. Your wait time may vary based on availability.";
var default_agent_unavailableMessage = "Sorry, no agents are available right now.";
var agent_reason_error = "Hmmm... I'm experiencing some difficulties. I need a human agent to manually continue the chat.";
var agent_sdMissingWarning = "No service desk is configured. Unless you have a custom service desk implemented, users will see an error instead of the message below. See the <a href=\"https://cloud.ibm.com/docs/watson-assistant?topic=watson-assistant-deploy-web-chat-haa\" rel=\"noreferrer noopener\" target=\"_blank\">documentation</a> for more information.";
var agent_noName = "Live agent";
var agent_chatTitle = "Live agent support";
var agent_startChat = "Connect to agent";
var agent_connecting = "Request for agent sent...";
var agent_agentNoNameTitle = "Agent";
var agent_agentJoinedName = "{personName} connected.";
var agent_agentJoinedNoName = "A live agent connected.";
var agent_youConnectedWarning = "If you refresh or leave the current page, you'll have to request a new agent.";
var agent_connectingMinutes = "Current wait time is <b>{time, number} {time, plural, one {minute} other {minutes}}</b>.";
var agent_connectingQueue = "You're number <b>{position, number}</b> in line.";
var agent_ariaAgentAvatar = "Live agent avatar image";
var agent_ariaGenericAvatar = "Avatar image";
var agent_ariaGenericBotAvatar = "Avatar image";
var agent_youEndedChat = "You disconnected from the live agent.";
var agent_conversationWasEnded = "You disconnected from the live agent.";
var agent_disconnected = "Something went wrong and your connection to the live agent was lost. Check your internet connection and then try again to connect to an agent.";
var agent_reconnected = "The agent reconnected.";
var agent_agentLeftChat = "{personName} disconnected.";
var agent_agentLeftChatNoName = "The live agent disconnected.";
var agent_agentEndedChat = "{personName} ended the chat.";
var agent_agentEndedChatNoName = "The live agent ended the chat.";
var agent_transferring = "You're being transferred.";
var agent_transferringNoName = "You're being transferred.";
var agent_endChat = "Disconnect from live agent?";
var agent_confirmSuspendedEndChatTitle = "Disconnect from previous agent?";
var agent_confirmSuspendedEndChatMessage = "You are currently connected to an agent. Continuing will disconnect you from the agent and connect you to a new one. Do you want to continue?";
var agent_confirmCancelRequestTitle = "Cancel request?";
var agent_confirmCancelRequestMessage = "If you continue, you'll cancel your request for an agent.";
var agent_confirmCancelRequestNo = "Go back";
var agent_confirmCancelRequestYes = "Cancel request";
var agent_confirmEndChat = "If you disconnect, you'll have to request a new live agent.";
var agent_confirmEndChatNo = "Go back";
var agent_confirmEndChatYes = "Disconnect";
var agent_confirmEndSuspendedYes = "Continue";
var agent_botReturned = "Can I help you with anything else?";
var agent_newMessage = "New message";
var agent_cardButtonChatRequested = "Connecting...";
var agent_cardButtonConnected = "Connected";
var agent_cardButtonChatEnded = "Disconnected";
var agent_cardMessageChatEnded = "You disconnected from the live agent.";
var agent_cardMessageConnected = "You're now connected.";
var agent_connectButtonCancel = "Cancel";
var agent_connectedButtonEndChat = "Disconnect";
var agent_connectWaiting = "Waiting...";
var agent_defaultMessageToAgent = "Begin conversation";
var agent_inputPlaceholderConnecting = "Waiting for agent...";
var agent_inputPlaceholderReconnecting = "Reconnecting to agent...";
var agent_sharingStopSharingButton = "Stop sharing screen";
var agent_sharingRequestTitle = "Screen sharing";
var agent_sharingRequestMessage = "The agent has requested you share your screen. You can stop sharing at any time.";
var agent_sharingAcceptButton = "Share screen";
var agent_sharingDeclineButton = "Decline";
var agent_sharingRequested = "You were requested to share your screen.";
var agent_sharingAccepted = "You shared your screen.";
var agent_sharingDeclined = "You declined to share your screen.";
var agent_sharingCancelled = "The screen sharing request was cancelled.";
var agent_sharingEnded = "You stopped sharing your screen.";
var agent_suspendedWarning = "You are currently connected to an agent.";
var icon_ariaUnreadMessages = "There {count, plural, one {is} other {are}} {count, number} unread {count, plural, one {message} other {messages}}";
var showMore = "See more";
var suggestions_title = "Suggestions";
var suggestions_sectionTitleAlternateResponses = "Send one of these messages";
var suggestions_sectionTitleSearch = "View related content";
var suggestions_sectionTitleContact = "Get help another way";
var suggestions_sectionTitleStarters = "People are also interested in";
var suggestions_noSearchResults = "I couldn't find anything to share with you that's related to the current chat.";
var suggestions_connectToAgent = "Connect to agent";
var suggestions_ariaButtonToClose = "Click to close suggestions.";
var suggestions_ariaMessageButtons = "Suggestions message buttons";
var suggestions_ariaButtonToOpen = "Suggestions button. Click to open suggestions.";
var suggestions_ariaPanelButtonToggle = "Click to toggle more or fewer suggestions.";
var suggestions_ariaAnnounceButtonShow = "Click the suggestions button to view suggestions.";
var suggestions_ariaAnnounceListOpen = "Suggestions has been opened. Press escape to close.";
var suggestions_ariaAnnounceListClosed = "Suggestions has been closed.";
var suggestions_ariaAnnounceListExpanded = "Suggestions has been expanded.";
var suggestions_ariaAnnounceListCollapsed = "Suggestions has been collapsed.";
var suggestions_searchBookmarkHeader = "Glad you found related content. I'll save this for you here in our chat.";
var suggestions_searchBookmarkFooter = "Can I help you with anything else?";
var suggestions_searchBookmarkButtonDescription = "Bookmark";
var showMoreResults = "See more";
var disclaimer_title = "Disclaimer";
var disclaimer_accept = "I accept";
var sessionHistory_expired = "Chat session inactive. Send any message to continue. If you refresh the page now, you'll have to start a new chat.";
var sessionHistory_expiredNoCopy = "Chat session inactive.";
var branding_headline = "Built with IBM <strong>watsonx</strong>";
var branding_description = "Remove the frustration of long wait times, tedious searches, and unhelpful chatbots with watsonx Assistant. Save money and time by providing your customers with fast and accurate answers anywhere they need it.";
var branding_sign_up = "Sign up today.";
var general_ariaCloseInformationOverlay = "Close information panel.";
var general_ariaAnnounceOpenedInformationOverlay = "An information panel has been opened.";
var general_ariaAnnounceClosedInformationOverlay = "An information panel has been closed.";
var general_ariaAnnounceEscapeOverlay = "Press escape or click the close button to close.";
var general_returnToAssistant = "Return to assistant";
var branding_ariaInformationButton = "Learn more about IBM Watson.";
var channelTransfer_failed = "There was a problem transferring you. To try again either click the original link or send your message to the assistant here.";
var conversationalSearch_streamingIncomplete = "This message was not completed. Please try again.";
var conversationalSearch_viewPDF = "View PDF";
var conversationalSearch_viewSourceDocument = "View source";
var conversationalSearch_toggleCitations = "Open or close the list of sources";
var conversationalSearch_responseStopped = "Response stopped";
var launcher_chatNow = "Chat now";
var iframe_ariaSourceLoaded = "The web page has loaded.";
var iframe_ariaImageAltText = "Preview image for the web page panel.";
var iframe_ariaClosePanel = "Close the web page panel.";
var iframe_ariaOpenedPanel = "Web page panel has opened.";
var iframe_ariaClosedPanel = "Web page panel has closed.";
var iframe_ariaClickPreviewCard = "Click to open the web page panel and visit {source}.";
var closeAndRestartModal_title = "End chat";
var closeAndRestartModal_message = "Are you sure you want to end your chat?";
var closeAndRestartModal_confirm = "Yes";
var closeAndRestartModal_cancel = "No";
var datePicker_chooseDate = "Choose a date ({format})";
var datePicker_confirmDate = "Confirm date";
var tour_resumeButton = "Resume";
var tour_restartButton = "Restart";
var tour_startButton = "Get started";
var tour_doneButton = "Done";
var tour_ariaMinimizeButton = "Hide tour window";
var tour_ariaCloseButton = "Close tour window";
var tour_ariaChatButton = "Open the chat window";
var tour_ariaPreviousButton = "Go to previous step";
var tour_ariaNextButton = "Go to next step";
var tour_instructions = "Use the previous and next buttons to navigate between steps of the tour. Use the {chatButtonText} button to open that chat window.";
var tour_errorFetchingStep = "There was a problem fetching the next step.";
var tour_errorTitle = "Unable to continue";
var tour_errorBody = "The content failed to load. Please try again.";
var fileSharing_fileTooLarge = "The maximum file size allowed is {maxSize}.";
var fileSharing_ariaAnnounceSuccess = "The file was uploaded successfully.";
var fileSharing_fileIcon = "File icon";
var fileSharing_removeButtonTitle = "Remove file";
var fileSharing_statusUploading = "Uploading file";
var fileSharing_uploadFailed = "There was an error uploading the file.";
var fileSharing_agentMessageText = "File upload";
var fileSharing_request = "The agent has requested you upload a file.";
var serviceDesk_niceDFO_fileUploadError = "The maximum file size is {fileSize}MB. The allowed file types are {allowedTypes}.";
var documentViewer_ariaRoleLabel = "PDF Viewer";
var documentViewer_title = "Document preview";
var documentViewer_zoomIn = "Zoom in";
var documentViewer_zoomOut = "Zoom out";
var documentViewer_zoomReset = "Reset zoom";
var documentViewer_download = "Download";
var documentViewer_close = "Close";
var documentViewer_previousPage = "Previous page";
var documentViewer_nextPage = "Next page";
var documentViewer_previousMatch = "Previous match";
var documentViewer_nextMatch = "Next match";
var documentViewer_search = "Search";
var documentViewer_currentPage = "Current page";
var documentViewer_clearSearch = "Clear search";
var documentViewer_pagesLabel = "{count, number} {count, plural, one {page} other {pages}}";
var documentViewer_loadErrorTitle = "Something's wrong.";
var documentViewer_loadError = "We were unable to load the document. Click Retry to try again, or try downloading the document.";
var documentViewer_loadErrorRetry = "Retry";
var carousel_prevNavButton = "Go to previous slide.";
var carousel_nextNavButton = "Go to next slide.";
var input_completionsTagApp = "App";
var input_completionsTagAssistant = "Assistant";
var table_filterPlaceholder = "Filter table";
var table_previousPage = "Previous page";
var table_nextPage = "Next page";
var table_itemsPerPage = "Items per page:";
var table_paginationSupplementalText = "of {pagesCount, number} {pagesCount, plural, one {page} other {pages}}";
var table_paginationStatus = "{start, number}–{end, number} of {count, number} {count, plural, one {item} other {items}}";
var feedback_positiveLabel = "Good response";
var feedback_negativeLabel = "Bad response";
var feedback_defaultTitle = "Additional feedback";
var feedback_defaultPrompt = "Why did you choose this rating?";
var feedback_defaultPlaceholder = "Add a comment";
var feedback_submitLabel = "Submit";
var feedback_cancelLabel = "Cancel";
var input_stopResponse = "Stop response";
var messages_responseStopped = "Response stopped";
var chainOfThought_stepTitle = "{stepNumber, number}: {stepTitle}";
var chainOfThought_inputLabel = "Input";
var chainOfThought_outputLabel = "Output";
var chainOfThought_toolLabel = "Tool";
var chainOfThought_statusSucceededLabel = "Succeeded";
var chainOfThought_statusFailedLabel = "Failed";
var chainOfThought_statusProcessingLabel = "Processing";
var chainOfThought_explainabilityLabel = "How did I get this answer?";
var enLanguagePack = {
	ai_slug_title: ai_slug_title,
	ai_slug_description: ai_slug_description,
	components_overflow_ariaLabel: components_overflow_ariaLabel,
	components_swiper_currentLabel: components_swiper_currentLabel,
	errors_communicating: errors_communicating,
	errors_imageSource: errors_imageSource,
	errors_videoSource: errors_videoSource,
	errors_audioSource: errors_audioSource,
	errors_iframeSource: errors_iframeSource,
	errors_singleMessage: errors_singleMessage,
	errors_ariaMessageRetrying: errors_ariaMessageRetrying,
	errors_ariaMessageFailed: errors_ariaMessageFailed,
	errors_noAgentsAvailable: errors_noAgentsAvailable,
	errors_noAgentsJoined: errors_noAgentsJoined,
	errors_connectingToAgent: errors_connectingToAgent,
	errors_busy: errors_busy,
	errors_agentAppSessionExpired: errors_agentAppSessionExpired,
	errors_generalContent: errors_generalContent,
	errors_somethingWrong: errors_somethingWrong,
	input_ariaLabel: input_ariaLabel,
	input_placeholder: input_placeholder,
	input_buttonLabel: input_buttonLabel,
	input_uploadButtonLabel: input_uploadButtonLabel,
	window_title: window_title,
	window_ariaTourRegion: window_ariaTourRegion,
	window_ariaTourRegionNamespace: window_ariaTourRegionNamespace,
	window_ariaChatRegion: window_ariaChatRegion,
	window_ariaChatRegionNamespace: window_ariaChatRegionNamespace,
	window_ariaWindowOpened: window_ariaWindowOpened,
	window_ariaWindowClosed: window_ariaWindowClosed,
	window_ariaWindowLoading: window_ariaWindowLoading,
	launcher_isOpen: launcher_isOpen,
	launcher_isClosed: launcher_isClosed,
	launcher_isTourOpen: launcher_isTourOpen,
	launcher_isTourClosed: launcher_isTourClosed,
	launcher_desktopGreeting: launcher_desktopGreeting,
	launcher_mobileGreeting: launcher_mobileGreeting,
	launcher_ariaIsExpanded: launcher_ariaIsExpanded,
	launcher_closeButton: launcher_closeButton,
	messages_youSaid: messages_youSaid,
	messages_botSaid: messages_botSaid,
	messages_agentSaid: messages_agentSaid,
	messages_searchResults: messages_searchResults,
	messages_searchResultsLink: messages_searchResultsLink,
	messages_searchResultsOpenDocument: messages_searchResultsOpenDocument,
	messages_searchResultsOpenDocumentWithLabel: messages_searchResultsOpenDocumentWithLabel,
	messages_searchResultsExpand: messages_searchResultsExpand,
	messages_searchResultsCollapse: messages_searchResultsCollapse,
	messages_botIsLoading: messages_botIsLoading,
	messages_botIsTyping: messages_botIsTyping,
	messages_agentIsTyping: messages_agentIsTyping,
	messages_scrollHandle: messages_scrollHandle,
	messages_scrollHandleDetailed: messages_scrollHandleDetailed,
	messages_scrollHandleEnd: messages_scrollHandleEnd,
	messages_scrollHandleEndDetailed: messages_scrollHandleEndDetailed,
	message_labelBot: message_labelBot,
	message_labelYou: message_labelYou,
	notifications_toastClose: notifications_toastClose,
	buttons_restart: buttons_restart,
	buttons_cancel: buttons_cancel,
	buttons_retry: buttons_retry,
	options_select: options_select,
	options_ariaOptionsDisabled: options_ariaOptionsDisabled,
	header_previewLinkTitle: header_previewLinkTitle,
	header_ariaCloseRestart: header_ariaCloseRestart,
	header_ariaBotAvatar: header_ariaBotAvatar,
	header_overflowMenu_options: header_overflowMenu_options,
	homeScreen_returnToAssistant: homeScreen_returnToAssistant,
	homeScreen_returnToHome: homeScreen_returnToHome,
	homeScreen_overflowMenuHomeScreen: homeScreen_overflowMenuHomeScreen,
	homeScreen_ariaQuickStartListButton: homeScreen_ariaQuickStartListButton,
	homeScreen_ariaQuickStartListOpened: homeScreen_ariaQuickStartListOpened,
	homeScreen_ariaQuickStartListClosed: homeScreen_ariaQuickStartListClosed,
	default_agent_availableMessage: default_agent_availableMessage,
	default_agent_unavailableMessage: default_agent_unavailableMessage,
	agent_reason_error: agent_reason_error,
	agent_sdMissingWarning: agent_sdMissingWarning,
	agent_noName: agent_noName,
	agent_chatTitle: agent_chatTitle,
	agent_startChat: agent_startChat,
	agent_connecting: agent_connecting,
	agent_agentNoNameTitle: agent_agentNoNameTitle,
	agent_agentJoinedName: agent_agentJoinedName,
	agent_agentJoinedNoName: agent_agentJoinedNoName,
	agent_youConnectedWarning: agent_youConnectedWarning,
	agent_connectingMinutes: agent_connectingMinutes,
	agent_connectingQueue: agent_connectingQueue,
	agent_ariaAgentAvatar: agent_ariaAgentAvatar,
	agent_ariaGenericAvatar: agent_ariaGenericAvatar,
	agent_ariaGenericBotAvatar: agent_ariaGenericBotAvatar,
	agent_youEndedChat: agent_youEndedChat,
	agent_conversationWasEnded: agent_conversationWasEnded,
	agent_disconnected: agent_disconnected,
	agent_reconnected: agent_reconnected,
	agent_agentLeftChat: agent_agentLeftChat,
	agent_agentLeftChatNoName: agent_agentLeftChatNoName,
	agent_agentEndedChat: agent_agentEndedChat,
	agent_agentEndedChatNoName: agent_agentEndedChatNoName,
	agent_transferring: agent_transferring,
	agent_transferringNoName: agent_transferringNoName,
	agent_endChat: agent_endChat,
	agent_confirmSuspendedEndChatTitle: agent_confirmSuspendedEndChatTitle,
	agent_confirmSuspendedEndChatMessage: agent_confirmSuspendedEndChatMessage,
	agent_confirmCancelRequestTitle: agent_confirmCancelRequestTitle,
	agent_confirmCancelRequestMessage: agent_confirmCancelRequestMessage,
	agent_confirmCancelRequestNo: agent_confirmCancelRequestNo,
	agent_confirmCancelRequestYes: agent_confirmCancelRequestYes,
	agent_confirmEndChat: agent_confirmEndChat,
	agent_confirmEndChatNo: agent_confirmEndChatNo,
	agent_confirmEndChatYes: agent_confirmEndChatYes,
	agent_confirmEndSuspendedYes: agent_confirmEndSuspendedYes,
	agent_botReturned: agent_botReturned,
	agent_newMessage: agent_newMessage,
	agent_cardButtonChatRequested: agent_cardButtonChatRequested,
	agent_cardButtonConnected: agent_cardButtonConnected,
	agent_cardButtonChatEnded: agent_cardButtonChatEnded,
	agent_cardMessageChatEnded: agent_cardMessageChatEnded,
	agent_cardMessageConnected: agent_cardMessageConnected,
	agent_connectButtonCancel: agent_connectButtonCancel,
	agent_connectedButtonEndChat: agent_connectedButtonEndChat,
	agent_connectWaiting: agent_connectWaiting,
	agent_defaultMessageToAgent: agent_defaultMessageToAgent,
	agent_inputPlaceholderConnecting: agent_inputPlaceholderConnecting,
	agent_inputPlaceholderReconnecting: agent_inputPlaceholderReconnecting,
	agent_sharingStopSharingButton: agent_sharingStopSharingButton,
	agent_sharingRequestTitle: agent_sharingRequestTitle,
	agent_sharingRequestMessage: agent_sharingRequestMessage,
	agent_sharingAcceptButton: agent_sharingAcceptButton,
	agent_sharingDeclineButton: agent_sharingDeclineButton,
	agent_sharingRequested: agent_sharingRequested,
	agent_sharingAccepted: agent_sharingAccepted,
	agent_sharingDeclined: agent_sharingDeclined,
	agent_sharingCancelled: agent_sharingCancelled,
	agent_sharingEnded: agent_sharingEnded,
	agent_suspendedWarning: agent_suspendedWarning,
	icon_ariaUnreadMessages: icon_ariaUnreadMessages,
	showMore: showMore,
	suggestions_title: suggestions_title,
	suggestions_sectionTitleAlternateResponses: suggestions_sectionTitleAlternateResponses,
	suggestions_sectionTitleSearch: suggestions_sectionTitleSearch,
	suggestions_sectionTitleContact: suggestions_sectionTitleContact,
	suggestions_sectionTitleStarters: suggestions_sectionTitleStarters,
	suggestions_noSearchResults: suggestions_noSearchResults,
	suggestions_connectToAgent: suggestions_connectToAgent,
	suggestions_ariaButtonToClose: suggestions_ariaButtonToClose,
	suggestions_ariaMessageButtons: suggestions_ariaMessageButtons,
	suggestions_ariaButtonToOpen: suggestions_ariaButtonToOpen,
	suggestions_ariaPanelButtonToggle: suggestions_ariaPanelButtonToggle,
	suggestions_ariaAnnounceButtonShow: suggestions_ariaAnnounceButtonShow,
	suggestions_ariaAnnounceListOpen: suggestions_ariaAnnounceListOpen,
	suggestions_ariaAnnounceListClosed: suggestions_ariaAnnounceListClosed,
	suggestions_ariaAnnounceListExpanded: suggestions_ariaAnnounceListExpanded,
	suggestions_ariaAnnounceListCollapsed: suggestions_ariaAnnounceListCollapsed,
	suggestions_searchBookmarkHeader: suggestions_searchBookmarkHeader,
	suggestions_searchBookmarkFooter: suggestions_searchBookmarkFooter,
	suggestions_searchBookmarkButtonDescription: suggestions_searchBookmarkButtonDescription,
	showMoreResults: showMoreResults,
	disclaimer_title: disclaimer_title,
	disclaimer_accept: disclaimer_accept,
	sessionHistory_expired: sessionHistory_expired,
	sessionHistory_expiredNoCopy: sessionHistory_expiredNoCopy,
	branding_headline: branding_headline,
	branding_description: branding_description,
	branding_sign_up: branding_sign_up,
	general_ariaCloseInformationOverlay: general_ariaCloseInformationOverlay,
	general_ariaAnnounceOpenedInformationOverlay: general_ariaAnnounceOpenedInformationOverlay,
	general_ariaAnnounceClosedInformationOverlay: general_ariaAnnounceClosedInformationOverlay,
	general_ariaAnnounceEscapeOverlay: general_ariaAnnounceEscapeOverlay,
	general_returnToAssistant: general_returnToAssistant,
	branding_ariaInformationButton: branding_ariaInformationButton,
	channelTransfer_failed: channelTransfer_failed,
	conversationalSearch_streamingIncomplete: conversationalSearch_streamingIncomplete,
	conversationalSearch_viewPDF: conversationalSearch_viewPDF,
	conversationalSearch_viewSourceDocument: conversationalSearch_viewSourceDocument,
	conversationalSearch_toggleCitations: conversationalSearch_toggleCitations,
	conversationalSearch_responseStopped: conversationalSearch_responseStopped,
	launcher_chatNow: launcher_chatNow,
	iframe_ariaSourceLoaded: iframe_ariaSourceLoaded,
	iframe_ariaImageAltText: iframe_ariaImageAltText,
	iframe_ariaClosePanel: iframe_ariaClosePanel,
	iframe_ariaOpenedPanel: iframe_ariaOpenedPanel,
	iframe_ariaClosedPanel: iframe_ariaClosedPanel,
	iframe_ariaClickPreviewCard: iframe_ariaClickPreviewCard,
	closeAndRestartModal_title: closeAndRestartModal_title,
	closeAndRestartModal_message: closeAndRestartModal_message,
	closeAndRestartModal_confirm: closeAndRestartModal_confirm,
	closeAndRestartModal_cancel: closeAndRestartModal_cancel,
	datePicker_chooseDate: datePicker_chooseDate,
	datePicker_confirmDate: datePicker_confirmDate,
	tour_resumeButton: tour_resumeButton,
	tour_restartButton: tour_restartButton,
	tour_startButton: tour_startButton,
	tour_doneButton: tour_doneButton,
	tour_ariaMinimizeButton: tour_ariaMinimizeButton,
	tour_ariaCloseButton: tour_ariaCloseButton,
	tour_ariaChatButton: tour_ariaChatButton,
	tour_ariaPreviousButton: tour_ariaPreviousButton,
	tour_ariaNextButton: tour_ariaNextButton,
	tour_instructions: tour_instructions,
	tour_errorFetchingStep: tour_errorFetchingStep,
	tour_errorTitle: tour_errorTitle,
	tour_errorBody: tour_errorBody,
	fileSharing_fileTooLarge: fileSharing_fileTooLarge,
	fileSharing_ariaAnnounceSuccess: fileSharing_ariaAnnounceSuccess,
	fileSharing_fileIcon: fileSharing_fileIcon,
	fileSharing_removeButtonTitle: fileSharing_removeButtonTitle,
	fileSharing_statusUploading: fileSharing_statusUploading,
	fileSharing_uploadFailed: fileSharing_uploadFailed,
	fileSharing_agentMessageText: fileSharing_agentMessageText,
	fileSharing_request: fileSharing_request,
	serviceDesk_niceDFO_fileUploadError: serviceDesk_niceDFO_fileUploadError,
	documentViewer_ariaRoleLabel: documentViewer_ariaRoleLabel,
	documentViewer_title: documentViewer_title,
	documentViewer_zoomIn: documentViewer_zoomIn,
	documentViewer_zoomOut: documentViewer_zoomOut,
	documentViewer_zoomReset: documentViewer_zoomReset,
	documentViewer_download: documentViewer_download,
	documentViewer_close: documentViewer_close,
	documentViewer_previousPage: documentViewer_previousPage,
	documentViewer_nextPage: documentViewer_nextPage,
	documentViewer_previousMatch: documentViewer_previousMatch,
	documentViewer_nextMatch: documentViewer_nextMatch,
	documentViewer_search: documentViewer_search,
	documentViewer_currentPage: documentViewer_currentPage,
	documentViewer_clearSearch: documentViewer_clearSearch,
	documentViewer_pagesLabel: documentViewer_pagesLabel,
	documentViewer_loadErrorTitle: documentViewer_loadErrorTitle,
	documentViewer_loadError: documentViewer_loadError,
	documentViewer_loadErrorRetry: documentViewer_loadErrorRetry,
	carousel_prevNavButton: carousel_prevNavButton,
	carousel_nextNavButton: carousel_nextNavButton,
	input_completionsTagApp: input_completionsTagApp,
	input_completionsTagAssistant: input_completionsTagAssistant,
	table_filterPlaceholder: table_filterPlaceholder,
	table_previousPage: table_previousPage,
	table_nextPage: table_nextPage,
	table_itemsPerPage: table_itemsPerPage,
	table_paginationSupplementalText: table_paginationSupplementalText,
	table_paginationStatus: table_paginationStatus,
	feedback_positiveLabel: feedback_positiveLabel,
	feedback_negativeLabel: feedback_negativeLabel,
	feedback_defaultTitle: feedback_defaultTitle,
	feedback_defaultPrompt: feedback_defaultPrompt,
	feedback_defaultPlaceholder: feedback_defaultPlaceholder,
	feedback_submitLabel: feedback_submitLabel,
	feedback_cancelLabel: feedback_cancelLabel,
	input_stopResponse: input_stopResponse,
	messages_responseStopped: messages_responseStopped,
	chainOfThought_stepTitle: chainOfThought_stepTitle,
	chainOfThought_inputLabel: chainOfThought_inputLabel,
	chainOfThought_outputLabel: chainOfThought_outputLabel,
	chainOfThought_toolLabel: chainOfThought_toolLabel,
	chainOfThought_statusSucceededLabel: chainOfThought_statusSucceededLabel,
	chainOfThought_statusFailedLabel: chainOfThought_statusFailedLabel,
	chainOfThought_statusProcessingLabel: chainOfThought_statusProcessingLabel,
	chainOfThought_explainabilityLabel: chainOfThought_explainabilityLabel
};

var en$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  agent_agentEndedChat: agent_agentEndedChat,
  agent_agentEndedChatNoName: agent_agentEndedChatNoName,
  agent_agentJoinedName: agent_agentJoinedName,
  agent_agentJoinedNoName: agent_agentJoinedNoName,
  agent_agentLeftChat: agent_agentLeftChat,
  agent_agentLeftChatNoName: agent_agentLeftChatNoName,
  agent_agentNoNameTitle: agent_agentNoNameTitle,
  agent_ariaAgentAvatar: agent_ariaAgentAvatar,
  agent_ariaGenericAvatar: agent_ariaGenericAvatar,
  agent_ariaGenericBotAvatar: agent_ariaGenericBotAvatar,
  agent_botReturned: agent_botReturned,
  agent_cardButtonChatEnded: agent_cardButtonChatEnded,
  agent_cardButtonChatRequested: agent_cardButtonChatRequested,
  agent_cardButtonConnected: agent_cardButtonConnected,
  agent_cardMessageChatEnded: agent_cardMessageChatEnded,
  agent_cardMessageConnected: agent_cardMessageConnected,
  agent_chatTitle: agent_chatTitle,
  agent_confirmCancelRequestMessage: agent_confirmCancelRequestMessage,
  agent_confirmCancelRequestNo: agent_confirmCancelRequestNo,
  agent_confirmCancelRequestTitle: agent_confirmCancelRequestTitle,
  agent_confirmCancelRequestYes: agent_confirmCancelRequestYes,
  agent_confirmEndChat: agent_confirmEndChat,
  agent_confirmEndChatNo: agent_confirmEndChatNo,
  agent_confirmEndChatYes: agent_confirmEndChatYes,
  agent_confirmEndSuspendedYes: agent_confirmEndSuspendedYes,
  agent_confirmSuspendedEndChatMessage: agent_confirmSuspendedEndChatMessage,
  agent_confirmSuspendedEndChatTitle: agent_confirmSuspendedEndChatTitle,
  agent_connectButtonCancel: agent_connectButtonCancel,
  agent_connectWaiting: agent_connectWaiting,
  agent_connectedButtonEndChat: agent_connectedButtonEndChat,
  agent_connecting: agent_connecting,
  agent_connectingMinutes: agent_connectingMinutes,
  agent_connectingQueue: agent_connectingQueue,
  agent_conversationWasEnded: agent_conversationWasEnded,
  agent_defaultMessageToAgent: agent_defaultMessageToAgent,
  agent_disconnected: agent_disconnected,
  agent_endChat: agent_endChat,
  agent_inputPlaceholderConnecting: agent_inputPlaceholderConnecting,
  agent_inputPlaceholderReconnecting: agent_inputPlaceholderReconnecting,
  agent_newMessage: agent_newMessage,
  agent_noName: agent_noName,
  agent_reason_error: agent_reason_error,
  agent_reconnected: agent_reconnected,
  agent_sdMissingWarning: agent_sdMissingWarning,
  agent_sharingAcceptButton: agent_sharingAcceptButton,
  agent_sharingAccepted: agent_sharingAccepted,
  agent_sharingCancelled: agent_sharingCancelled,
  agent_sharingDeclineButton: agent_sharingDeclineButton,
  agent_sharingDeclined: agent_sharingDeclined,
  agent_sharingEnded: agent_sharingEnded,
  agent_sharingRequestMessage: agent_sharingRequestMessage,
  agent_sharingRequestTitle: agent_sharingRequestTitle,
  agent_sharingRequested: agent_sharingRequested,
  agent_sharingStopSharingButton: agent_sharingStopSharingButton,
  agent_startChat: agent_startChat,
  agent_suspendedWarning: agent_suspendedWarning,
  agent_transferring: agent_transferring,
  agent_transferringNoName: agent_transferringNoName,
  agent_youConnectedWarning: agent_youConnectedWarning,
  agent_youEndedChat: agent_youEndedChat,
  ai_slug_description: ai_slug_description,
  ai_slug_title: ai_slug_title,
  branding_ariaInformationButton: branding_ariaInformationButton,
  branding_description: branding_description,
  branding_headline: branding_headline,
  branding_sign_up: branding_sign_up,
  buttons_cancel: buttons_cancel,
  buttons_restart: buttons_restart,
  buttons_retry: buttons_retry,
  carousel_nextNavButton: carousel_nextNavButton,
  carousel_prevNavButton: carousel_prevNavButton,
  chainOfThought_explainabilityLabel: chainOfThought_explainabilityLabel,
  chainOfThought_inputLabel: chainOfThought_inputLabel,
  chainOfThought_outputLabel: chainOfThought_outputLabel,
  chainOfThought_statusFailedLabel: chainOfThought_statusFailedLabel,
  chainOfThought_statusProcessingLabel: chainOfThought_statusProcessingLabel,
  chainOfThought_statusSucceededLabel: chainOfThought_statusSucceededLabel,
  chainOfThought_stepTitle: chainOfThought_stepTitle,
  chainOfThought_toolLabel: chainOfThought_toolLabel,
  channelTransfer_failed: channelTransfer_failed,
  closeAndRestartModal_cancel: closeAndRestartModal_cancel,
  closeAndRestartModal_confirm: closeAndRestartModal_confirm,
  closeAndRestartModal_message: closeAndRestartModal_message,
  closeAndRestartModal_title: closeAndRestartModal_title,
  components_overflow_ariaLabel: components_overflow_ariaLabel,
  components_swiper_currentLabel: components_swiper_currentLabel,
  conversationalSearch_responseStopped: conversationalSearch_responseStopped,
  conversationalSearch_streamingIncomplete: conversationalSearch_streamingIncomplete,
  conversationalSearch_toggleCitations: conversationalSearch_toggleCitations,
  conversationalSearch_viewPDF: conversationalSearch_viewPDF,
  conversationalSearch_viewSourceDocument: conversationalSearch_viewSourceDocument,
  datePicker_chooseDate: datePicker_chooseDate,
  datePicker_confirmDate: datePicker_confirmDate,
  default: enLanguagePack,
  default_agent_availableMessage: default_agent_availableMessage,
  default_agent_unavailableMessage: default_agent_unavailableMessage,
  disclaimer_accept: disclaimer_accept,
  disclaimer_title: disclaimer_title,
  documentViewer_ariaRoleLabel: documentViewer_ariaRoleLabel,
  documentViewer_clearSearch: documentViewer_clearSearch,
  documentViewer_close: documentViewer_close,
  documentViewer_currentPage: documentViewer_currentPage,
  documentViewer_download: documentViewer_download,
  documentViewer_loadError: documentViewer_loadError,
  documentViewer_loadErrorRetry: documentViewer_loadErrorRetry,
  documentViewer_loadErrorTitle: documentViewer_loadErrorTitle,
  documentViewer_nextMatch: documentViewer_nextMatch,
  documentViewer_nextPage: documentViewer_nextPage,
  documentViewer_pagesLabel: documentViewer_pagesLabel,
  documentViewer_previousMatch: documentViewer_previousMatch,
  documentViewer_previousPage: documentViewer_previousPage,
  documentViewer_search: documentViewer_search,
  documentViewer_title: documentViewer_title,
  documentViewer_zoomIn: documentViewer_zoomIn,
  documentViewer_zoomOut: documentViewer_zoomOut,
  documentViewer_zoomReset: documentViewer_zoomReset,
  errors_agentAppSessionExpired: errors_agentAppSessionExpired,
  errors_ariaMessageFailed: errors_ariaMessageFailed,
  errors_ariaMessageRetrying: errors_ariaMessageRetrying,
  errors_audioSource: errors_audioSource,
  errors_busy: errors_busy,
  errors_communicating: errors_communicating,
  errors_connectingToAgent: errors_connectingToAgent,
  errors_generalContent: errors_generalContent,
  errors_iframeSource: errors_iframeSource,
  errors_imageSource: errors_imageSource,
  errors_noAgentsAvailable: errors_noAgentsAvailable,
  errors_noAgentsJoined: errors_noAgentsJoined,
  errors_singleMessage: errors_singleMessage,
  errors_somethingWrong: errors_somethingWrong,
  errors_videoSource: errors_videoSource,
  feedback_cancelLabel: feedback_cancelLabel,
  feedback_defaultPlaceholder: feedback_defaultPlaceholder,
  feedback_defaultPrompt: feedback_defaultPrompt,
  feedback_defaultTitle: feedback_defaultTitle,
  feedback_negativeLabel: feedback_negativeLabel,
  feedback_positiveLabel: feedback_positiveLabel,
  feedback_submitLabel: feedback_submitLabel,
  fileSharing_agentMessageText: fileSharing_agentMessageText,
  fileSharing_ariaAnnounceSuccess: fileSharing_ariaAnnounceSuccess,
  fileSharing_fileIcon: fileSharing_fileIcon,
  fileSharing_fileTooLarge: fileSharing_fileTooLarge,
  fileSharing_removeButtonTitle: fileSharing_removeButtonTitle,
  fileSharing_request: fileSharing_request,
  fileSharing_statusUploading: fileSharing_statusUploading,
  fileSharing_uploadFailed: fileSharing_uploadFailed,
  general_ariaAnnounceClosedInformationOverlay: general_ariaAnnounceClosedInformationOverlay,
  general_ariaAnnounceEscapeOverlay: general_ariaAnnounceEscapeOverlay,
  general_ariaAnnounceOpenedInformationOverlay: general_ariaAnnounceOpenedInformationOverlay,
  general_ariaCloseInformationOverlay: general_ariaCloseInformationOverlay,
  general_returnToAssistant: general_returnToAssistant,
  header_ariaBotAvatar: header_ariaBotAvatar,
  header_ariaCloseRestart: header_ariaCloseRestart,
  header_overflowMenu_options: header_overflowMenu_options,
  header_previewLinkTitle: header_previewLinkTitle,
  homeScreen_ariaQuickStartListButton: homeScreen_ariaQuickStartListButton,
  homeScreen_ariaQuickStartListClosed: homeScreen_ariaQuickStartListClosed,
  homeScreen_ariaQuickStartListOpened: homeScreen_ariaQuickStartListOpened,
  homeScreen_overflowMenuHomeScreen: homeScreen_overflowMenuHomeScreen,
  homeScreen_returnToAssistant: homeScreen_returnToAssistant,
  homeScreen_returnToHome: homeScreen_returnToHome,
  icon_ariaUnreadMessages: icon_ariaUnreadMessages,
  iframe_ariaClickPreviewCard: iframe_ariaClickPreviewCard,
  iframe_ariaClosePanel: iframe_ariaClosePanel,
  iframe_ariaClosedPanel: iframe_ariaClosedPanel,
  iframe_ariaImageAltText: iframe_ariaImageAltText,
  iframe_ariaOpenedPanel: iframe_ariaOpenedPanel,
  iframe_ariaSourceLoaded: iframe_ariaSourceLoaded,
  input_ariaLabel: input_ariaLabel,
  input_buttonLabel: input_buttonLabel,
  input_completionsTagApp: input_completionsTagApp,
  input_completionsTagAssistant: input_completionsTagAssistant,
  input_placeholder: input_placeholder,
  input_stopResponse: input_stopResponse,
  input_uploadButtonLabel: input_uploadButtonLabel,
  launcher_ariaIsExpanded: launcher_ariaIsExpanded,
  launcher_chatNow: launcher_chatNow,
  launcher_closeButton: launcher_closeButton,
  launcher_desktopGreeting: launcher_desktopGreeting,
  launcher_isClosed: launcher_isClosed,
  launcher_isOpen: launcher_isOpen,
  launcher_isTourClosed: launcher_isTourClosed,
  launcher_isTourOpen: launcher_isTourOpen,
  launcher_mobileGreeting: launcher_mobileGreeting,
  message_labelBot: message_labelBot,
  message_labelYou: message_labelYou,
  messages_agentIsTyping: messages_agentIsTyping,
  messages_agentSaid: messages_agentSaid,
  messages_botIsLoading: messages_botIsLoading,
  messages_botIsTyping: messages_botIsTyping,
  messages_botSaid: messages_botSaid,
  messages_responseStopped: messages_responseStopped,
  messages_scrollHandle: messages_scrollHandle,
  messages_scrollHandleDetailed: messages_scrollHandleDetailed,
  messages_scrollHandleEnd: messages_scrollHandleEnd,
  messages_scrollHandleEndDetailed: messages_scrollHandleEndDetailed,
  messages_searchResults: messages_searchResults,
  messages_searchResultsCollapse: messages_searchResultsCollapse,
  messages_searchResultsExpand: messages_searchResultsExpand,
  messages_searchResultsLink: messages_searchResultsLink,
  messages_searchResultsOpenDocument: messages_searchResultsOpenDocument,
  messages_searchResultsOpenDocumentWithLabel: messages_searchResultsOpenDocumentWithLabel,
  messages_youSaid: messages_youSaid,
  notifications_toastClose: notifications_toastClose,
  options_ariaOptionsDisabled: options_ariaOptionsDisabled,
  options_select: options_select,
  serviceDesk_niceDFO_fileUploadError: serviceDesk_niceDFO_fileUploadError,
  sessionHistory_expired: sessionHistory_expired,
  sessionHistory_expiredNoCopy: sessionHistory_expiredNoCopy,
  showMore: showMore,
  showMoreResults: showMoreResults,
  suggestions_ariaAnnounceButtonShow: suggestions_ariaAnnounceButtonShow,
  suggestions_ariaAnnounceListClosed: suggestions_ariaAnnounceListClosed,
  suggestions_ariaAnnounceListCollapsed: suggestions_ariaAnnounceListCollapsed,
  suggestions_ariaAnnounceListExpanded: suggestions_ariaAnnounceListExpanded,
  suggestions_ariaAnnounceListOpen: suggestions_ariaAnnounceListOpen,
  suggestions_ariaButtonToClose: suggestions_ariaButtonToClose,
  suggestions_ariaButtonToOpen: suggestions_ariaButtonToOpen,
  suggestions_ariaMessageButtons: suggestions_ariaMessageButtons,
  suggestions_ariaPanelButtonToggle: suggestions_ariaPanelButtonToggle,
  suggestions_connectToAgent: suggestions_connectToAgent,
  suggestions_noSearchResults: suggestions_noSearchResults,
  suggestions_searchBookmarkButtonDescription: suggestions_searchBookmarkButtonDescription,
  suggestions_searchBookmarkFooter: suggestions_searchBookmarkFooter,
  suggestions_searchBookmarkHeader: suggestions_searchBookmarkHeader,
  suggestions_sectionTitleAlternateResponses: suggestions_sectionTitleAlternateResponses,
  suggestions_sectionTitleContact: suggestions_sectionTitleContact,
  suggestions_sectionTitleSearch: suggestions_sectionTitleSearch,
  suggestions_sectionTitleStarters: suggestions_sectionTitleStarters,
  suggestions_title: suggestions_title,
  table_filterPlaceholder: table_filterPlaceholder,
  table_itemsPerPage: table_itemsPerPage,
  table_nextPage: table_nextPage,
  table_paginationStatus: table_paginationStatus,
  table_paginationSupplementalText: table_paginationSupplementalText,
  table_previousPage: table_previousPage,
  tour_ariaChatButton: tour_ariaChatButton,
  tour_ariaCloseButton: tour_ariaCloseButton,
  tour_ariaMinimizeButton: tour_ariaMinimizeButton,
  tour_ariaNextButton: tour_ariaNextButton,
  tour_ariaPreviousButton: tour_ariaPreviousButton,
  tour_doneButton: tour_doneButton,
  tour_errorBody: tour_errorBody,
  tour_errorFetchingStep: tour_errorFetchingStep,
  tour_errorTitle: tour_errorTitle,
  tour_instructions: tour_instructions,
  tour_restartButton: tour_restartButton,
  tour_resumeButton: tour_resumeButton,
  tour_startButton: tour_startButton,
  window_ariaChatRegion: window_ariaChatRegion,
  window_ariaChatRegionNamespace: window_ariaChatRegionNamespace,
  window_ariaTourRegion: window_ariaTourRegion,
  window_ariaTourRegionNamespace: window_ariaTourRegionNamespace,
  window_ariaWindowClosed: window_ariaWindowClosed,
  window_ariaWindowLoading: window_ariaWindowLoading,
  window_ariaWindowOpened: window_ariaWindowOpened,
  window_title: window_title
});

var TagType;
(function (TagType) {
    TagType["RED"] = "red";
    TagType["MAGENTA"] = "magenta";
    TagType["PURPLE"] = "purple";
    TagType["BLUE"] = "blue";
    TagType["CYAN"] = "cyan";
    TagType["TEAL"] = "teal";
    TagType["GREEN"] = "green";
    TagType["GRAY"] = "gray";
    TagType["COOL_GRAY"] = "cool-gray";
    TagType["WARM_GRAY"] = "warm-gray";
    TagType["HIGH_CONTRAST"] = "high-contrast";
    TagType["OUTLINE"] = "outline";
})(TagType || (TagType = {}));
var TagSize;
(function (TagSize) {
    TagSize["SMALL"] = "sm";
    TagSize["MEDIUM"] = "md";
    TagSize["LARGE"] = "lg";
})(TagSize || (TagSize = {}));

// The regular expression that determine if a character is a letter, number or underscore. The "p{Letter}" is unicode
// syntax for the letter category. This is a string constant because IntelliJ will complain about the "p{Letter}"
// syntax.
const REGEXP_STRING_LETTERS_NUMBERS = '\\p{Letter}0-9_';
const REGEXP_LETTERS_NUMBERS = new RegExp(`[${REGEXP_STRING_LETTERS_NUMBERS}]`, 'u');
/**
 * Returns the name of the provided completion option, whether from {@link WXOMentionOption} or {@link WXOAssistantOption}.
 */
function getCompletionOptionName(option) {
    return option.name || option.assistantName;
}
/**
 * Escapes all the values in the given string so it may be matched in a regular expression.
 */
function escapeRegExp$1(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string.
}
/**
 * Returns the list of completions for assistant names for the wxo use case.
 */
async function getAssistantNameCompletions(inputText, cursorPosition, field, wxoState, serviceManager) {
    const mentionState = wxoState.mentionState[field];
    // First, if there are any existing completions, then go through each one and see if it is still valid.
    if (mentionState.mentions.length) {
        mentionState.mentions = mentionState.mentions.filter(mention => {
            const name = getCompletionOptionName(mention);
            const mentionRegExp = new RegExp(`([^${REGEXP_STRING_LETTERS_NUMBERS}])${escapeRegExp$1(name)}([^${REGEXP_STRING_LETTERS_NUMBERS}])`, 'u');
            // If the assistant name is found as a mention, then leave it in the list.
            return inputText.match(mentionRegExp);
        });
    }
    // For the moment, we are only allowing one mention so if we already have one, don't allow anymore.
    if (mentionState.mentions.length) {
        return null;
    }
    // Go backwards from the cursor until we find an "@" or we hit a new line. Also make sure the "@" isn't in the
    // middle of a word.
    let foundStart = cursorPosition - 1;
    while (foundStart >= 0) {
        if (inputText.charAt(foundStart) === '@') {
            if (foundStart !== 0) {
                // If we found a "@", it only counts if the character before it is not a letter or number.
                const previousChar = inputText.charAt(foundStart - 1);
                if (previousChar.match(REGEXP_LETTERS_NUMBERS)) {
                    // The previous character was a letter or number, so give up.
                    foundStart = -1;
                }
            }
            break;
        }
        if (inputText.charAt(foundStart) === '\n') {
            // If we hit a newline, then give up.
            foundStart = -1;
            break;
        }
        foundStart--;
    }
    if (foundStart >= 0) {
        const prefix = inputText.substring(foundStart + 1, cursorPosition).toLowerCase();
        if (!wxoState.mentionList) {
            wxoState.mentionList = serviceManager.store.getState().config.public.wxo.getMentionList?.();
            // If there is no list provided using getMentionList, use getAssistantList.
            if (!wxoState.mentionList) {
                wxoState.mentionList = serviceManager.store.getState().config.public.wxo.getAssistantList?.();
            }
        }
        const mentionList = (await wxoState.mentionList) || [];
        const matches = mentionList.filter(mention => {
            const name = getCompletionOptionName(mention);
            return name.toLowerCase().startsWith(prefix);
        });
        if (!matches.length) {
            return null;
        }
        // If the value following the cursor is a letter or number, we'll want to insert a space after the completion to
        // make sure there's space between the completion and whatever was already there.
        const isFollowedByLetter = inputText.length > cursorPosition && inputText.charAt(cursorPosition).match(REGEXP_LETTERS_NUMBERS);
        const maybeSpace = isFollowedByLetter ? ' ' : '';
        const inputCompletionsTagApp = serviceManager.intl.formatMessage({
            id: 'input_completionsTagApp',
        });
        const completions = matches.map(match => {
            const name = getCompletionOptionName(match);
            return {
                label: `@${name}`,
                targetValue: `@${name}${maybeSpace}`,
                mention: match,
                tag: match.type === WXOMentionType.APP
                    ? {
                        label: inputCompletionsTagApp,
                        type: TagType.BLUE,
                    }
                    : undefined,
            };
        });
        const result = {
            completionStart: foundStart,
            completionEnd: cursorPosition,
            completions,
        };
        return result;
    }
    return null;
}
/**
 * Adds orchestrate specific configuration.
 */
function installWXOServices(publicConfig, serviceManager) {
    // If wxo.getMentionList is provided, that means we need to configure web chat to allow mentions for different options.
    // This object will hold the state used when capturing mentions in the input fields.
    const wxoState = {
        mentionList: null,
        mentionState: {
            [InputFieldType.HOME_SCREEN]: {
                mentions: [],
            },
            [InputFieldType.MESSAGE]: {
                mentions: [],
            },
        },
    };
    serviceManager.eventBus.on({
        type: "pre:send" /* BusEventType.PRE_SEND */,
        handler: (event) => {
            // Whenever a message is sent, clear the assistant names list so we'll go fetch it again.
            wxoState.mentionList = null;
            // If this message includes some mentions (which come from the text input fields), add them to the request.
            const mentions = event.beforeSendPayload;
            if (mentions?.length) {
                event.data.input.mentions = mentions;
            }
        },
    });
    function onBeforeSend(field) {
        return wxoState.mentionState[field].mentions;
    }
    function onGetCompletions(field, inputText, cursorPosition) {
        return getAssistantNameCompletions(inputText, cursorPosition, field, wxoState, serviceManager);
    }
    function onCompletionSelected(field, completion) {
        wxoState.mentionState[field].mentions.push(completion.mention);
    }
    publicConfig.messaging.inputs = {
        homeScreen: {
            onBeforeSend: () => onBeforeSend(InputFieldType.HOME_SCREEN),
            getCompletions: (inputText, cursorPosition) => onGetCompletions(InputFieldType.HOME_SCREEN, inputText, cursorPosition),
            onCompletionSelected: (completion) => onCompletionSelected(InputFieldType.HOME_SCREEN, completion),
        },
        mainWindow: {
            onBeforeSend: () => onBeforeSend(InputFieldType.MESSAGE),
            getCompletions: (inputText, cursorPosition) => onGetCompletions(InputFieldType.MESSAGE, inputText, cursorPosition),
            onCompletionSelected: (completion) => onCompletionSelected(InputFieldType.MESSAGE, completion),
        },
    };
}

const { CLOSED: CLOSED$2, OPEN_FULL: OPEN_FULL$2, OPEN_STRIKE: OPEN_STRIKE$2 } = AlternateSuggestionsPanelState;
const { CONTACT_OPTIONS: CONTACT_OPTIONS$1, ALTERNATE_RESPONSES: ALTERNATE_RESPONSES$1, STARTERS: STARTERS$1 } = AlternateSuggestionsSection;
const { NEVER, ONE_STRIKE } = AlternateSuggestionsContactType;
const alternateSuggestionsReducers = {
    [SET_ALTERNATE_SUGGESTIONS_OPEN]: setAlternateSuggestionsOpen,
    [SET_ALTERNATE_SUGGESTIONS_THIRD_STRIKE]: setSuggestionsThirdStrike,
    [UPDATE_ALTERNATE_SUGGESTIONS_CONFIG]: updateConfig,
    [SUGGESTIONS_SET_SELECTED_INDEX]: setSuggestionsSelectedIndex,
    [SET_SUGGESTIONS_SEARCH_RESULTS]: setSuggestionsSearchResults,
};
/**
 * Marks the suggestion with the given index as being the currently selected one.
 */
function setSuggestionsSelectedIndex(state, action) {
    const newState = {
        ...state,
        persistedToBrowserStorage: {
            ...state.persistedToBrowserStorage,
            chatState: {
                ...state.persistedToBrowserStorage.chatState,
                suggestionsPersistedState: {
                    ...state.persistedToBrowserStorage.chatState.suggestionsPersistedState,
                    selectedIndex: action.index,
                    selectedSection: action.section,
                },
            },
        },
    };
    return applyAlternateSuggestionsState(newState);
}
/**
 * Changes the open status of the alternate suggestions.
 */
function setAlternateSuggestionsOpen(state, action) {
    return doSetAlternateSuggestionsPanelState(state, action);
}
/**
 * Indicates that the third strike against the alternate suggestions has been triggered.
 */
function setSuggestionsThirdStrike(state) {
    if (!state.alternateSuggestionsState.config.is_on) {
        return state;
    }
    if (shouldIgnoreThirdStrike(state)) {
        // If we're already connected or connecting to an agent, don't count this as a strike and trigger another
        // unnecessary agent card to appear.
        return state;
    }
    const trackEvent = {
        eventName: 'Assistant Message Sent to User with Suggestions',
        eventDescription: 'A message was received by the user from the bot with suggestions enabled.',
    };
    return applyAlternateSuggestionsState(state, undefined, 3, trackEvent);
}
/**
 * Updates the configuration for the suggestions feature. Only the properties that are defined here will be
 * applied to the updated config.
 */
function updateConfig(state, action) {
    const newConfig = merge({}, state.alternateSuggestionsState.config, action.config);
    if (!newConfig.is_on) {
        return {
            ...state,
            alternateSuggestionsState: {
                ...DEFAULT_ALTERNATE_SUGGESTIONS_STATE,
                config: newConfig,
            },
        };
    }
    const panelState = newConfig.is_on ? state.alternateSuggestionsState.panelState : CLOSED$2;
    const newState = {
        ...state,
        alternateSuggestionsState: {
            ...state.alternateSuggestionsState,
            config: newConfig,
            showButton: newConfig.is_on && state.alternateSuggestionsState.showButton,
            panelState,
        },
    };
    // Re-apply the current state using the new config.
    return applyAlternateSuggestionsState(newState, panelState);
}
/**
 * Changes the open state of the alternate suggestions menu.
 */
function doSetAlternateSuggestionsPanelState(state, action) {
    const { panelState, suggestionsToggledReason } = action;
    if (state.alternateSuggestionsState.panelState !== panelState) {
        const trackEvent = {
            eventName: `Suggestions Menu Opened or Closed`,
            eventDescription: `The suggestions menu was opened or closed.`,
            suggestionsToggledReason,
        };
        return applyAlternateSuggestionsState(state, panelState, undefined, trackEvent);
    }
    return state;
}
/**
 * Assuming that another strike has occurred, this determines if it should actually be ignored. The third strike is
 * ignored when the user is already connected to an agent.
 */
function shouldIgnoreThirdStrike(state) {
    const agentDisplayState = selectAgentDisplayState(state);
    return (state.persistedToBrowserStorage.chatState.suggestionsPersistedState.numStrikes === 2 &&
        agentDisplayState.isConnectingOrConnected);
}
/**
 * Indicates if the "Connect to agent"/contact suggestion should appear in the alternate suggestions list.
 */
function shouldShowContactSuggestion(state, numStrikes, contactIsSelected) {
    if (contactIsSelected) {
        // If the contact suggestion is selected, then we should show it.
        return true;
    }
    const { agentState, botMessageState, alternateSuggestionsState, allMessagesByID } = state;
    const { messageIDs } = botMessageState;
    const contactOption = alternateSuggestionsState.config.contact_option;
    if (contactOption === NEVER || (contactOption === ONE_STRIKE && numStrikes === 0)) {
        // The config option either never allows the contact option or requires a strike.
        return false;
    }
    const agentDisplayState = selectAgentDisplayState(state);
    if (agentDisplayState.isConnectingOrConnected) {
        // If we're currently connected or connecting to an agent, don't show the option.
        return false;
    }
    // If the last message is from the user or if it does not contain any connect_to_agent items, then show the option.
    const lastMessageID = arrayLastValue(messageIDs);
    const lastMessage = allMessagesByID[lastMessageID];
    return (!isResponse(lastMessage) ||
        !lastMessage.output.generic.find(item => item.response_type === MessageResponseTypes.CONNECT_TO_AGENT));
}
/**
 * Applies alternate suggestions specific behavior to the {@link RESET_CONTENT} action. This is called after the given
 * message restart has been done.
 */
function alternateSuggestionsHandleRestart(newState) {
    const { alternateSuggestionsState } = newState;
    if (!alternateSuggestionsState.config.is_on) {
        return newState;
    }
    return applyAlternateSuggestionsState(newState, CLOSED$2, 0, null);
}
/**
 * Applies alternate suggestions specific behavior to the {@link ADD_MESSAGE} action. This is called after the given
 * message has been added to the store.
 */
function alternateSuggestionsHandleAddMessage(state, action) {
    const { messageItem, message } = action;
    const { alternateSuggestionsState, persistedToBrowserStorage } = state;
    const { config } = alternateSuggestionsState;
    const { numStrikes, selectedIndex, selectedSection } = persistedToBrowserStorage.chatState.suggestionsPersistedState;
    if (!config.is_on) {
        return state;
    }
    if (!messageItem.ui_state.isWelcomeResponse && isResponse(message) && isNewMessage(state, messageItem)) {
        let setNumStrikes = numStrikes;
        let trackEvent;
        if (!isLiveAgentMessage(messageItem)) {
            trackEvent = {
                eventName: 'Assistant Message Sent to User with Suggestions',
                eventDescription: 'A message was received by the user from the bot with suggestions enabled.',
            };
        }
        if (isStrike(message, state, trackEvent)) {
            // If we get the anything else node (the bot didn't understand the user) or a duplicate response from the bot,
            // that's a strike so increase the counter.
            setNumStrikes++;
        }
        else {
            // Reset the counter.
            setNumStrikes = 0;
        }
        const newState = applyAlternateSuggestionsState(state, undefined, setNumStrikes, trackEvent);
        if (trackEvent?.suggestionsToggledReason === 'Strike') {
            // If this message resulted in the suggestions menu opening due to a strike, we want to record both the
            // original track event that goes with the new message but also log a second event that's clearly about the
            // suggestions menu opening and closing.
            const newEvent = {
                ...trackEvent,
                eventName: `Suggestions Menu Opened or Closed`,
                eventDescription: `The suggestions menu was opened or closed.`,
            };
            return {
                ...newState,
                pendingLogs: {
                    ...newState.pendingLogs,
                    trackEvents: [...newState.pendingLogs.trackEvents, newEvent],
                },
            };
        }
        return newState;
    }
    if (isRequest(message)) {
        // Close the list when the user sends a message and reset the selected index if the user did not choose a
        // suggestion (choosing a suggestion separately changes the state in a different action).
        const newSelectedIndex = message.history?.is_suggestion ? selectedIndex : -1;
        const newSelectedSection = message.history?.is_suggestion ? selectedSection : null;
        const newState = {
            ...state,
            persistedToBrowserStorage: keepIfEqual(state.persistedToBrowserStorage, {
                ...state.persistedToBrowserStorage,
                chatState: {
                    ...state.persistedToBrowserStorage.chatState,
                    suggestionsPersistedState: {
                        ...state.persistedToBrowserStorage.chatState.suggestionsPersistedState,
                        selectedIndex: newSelectedIndex,
                        selectedSection: newSelectedSection,
                    },
                },
            }),
        };
        return applyAlternateSuggestionsState(newState, CLOSED$2, numStrikes);
    }
    return state;
}
/**
 * Determines if the given message is the first message item (GenericItem) in the message list for the original
 * message that it is part of. This assumes this message is already in the message list so this function will look
 * at the message before it to check for a match.
 */
function isNewMessage(state, lastMessage) {
    const { allMessageItemsByID, botMessageState } = state;
    const { localMessageIDs } = botMessageState;
    const nextToLastMessage = allMessageItemsByID[localMessageIDs[localMessageIDs.length - 2]];
    return !nextToLastMessage || nextToLastMessage.fullMessageID !== lastMessage.fullMessageID;
}
/**
 * Calculates the button and options state for the alternate suggestions panel based on it's currently open state.
 * The panel may become closed if there are no options to show.
 *
 * @param state The current state from which to calculate the alternate suggestions state.
 * @param wantPanelState Indicates if the caller wants the panel to be open or not. If this value is undefined, the
 * current state will be used.
 * @param setNumStrikes Indicates if the caller wants to change the number of strikes. If this value is undefined, the
 * current state will be used.
 * @param trackEvent An optional track event to which additional data can be added to explain the state of the alternate
 * suggestions.
 */
function applyAlternateSuggestionsState(state, wantPanelState, setNumStrikes, trackEvent) {
    const { allMessageItemsByID, allMessagesByID, languagePack, botMessageState, alternateSuggestionsState, persistedToBrowserStorage, } = state;
    const { localMessageIDs } = botMessageState;
    const { config } = alternateSuggestionsState;
    const { suggestionsPersistedState } = persistedToBrowserStorage.chatState;
    const { selectedIndex, selectedSection } = suggestionsPersistedState;
    const { contact_option } = config;
    if (!config.is_on) {
        return state;
    }
    const { eventMessages } = state.pendingLogs;
    const sourceResponse = getResponseWithAlternateResponses(state);
    let suggestionStarters = getStarters(state);
    // We will show the search section if there is a search skill attached to the integration. But if the source
    // response from the bot was a search result, we won't show it (since it would just be the same results).
    const hasSearchSkill = selectHasSearchSkill(state);
    const showSearchSection = hasSearchSkill && !isSearchResultItem(sourceResponse?.output.generic[0]);
    if (wantPanelState === undefined) {
        wantPanelState = alternateSuggestionsState.panelState;
    }
    if (setNumStrikes === undefined) {
        setNumStrikes = suggestionsPersistedState.numStrikes;
    }
    if (setNumStrikes !== suggestionsPersistedState.numStrikes) {
        if (setNumStrikes === 1 || setNumStrikes === 2 || (setNumStrikes > 0 && contact_option === NEVER)) {
            // On the first and second strike, make sure the alternate suggestions is opened. Or if no contact option is
            // provided, always open on any strike.
            if (alternateSuggestionsState.panelState !== OPEN_FULL$2) {
                wantPanelState = OPEN_STRIKE$2;
                if (alternateSuggestionsState.panelState !== OPEN_STRIKE$2 && trackEvent) {
                    trackEvent.suggestionsToggledReason = 'Strike';
                }
            }
        }
    }
    // Get the alternate responses from the message.
    const sourceResponseRequest = allMessagesByID[sourceResponse?.request_id];
    let alternateResponseItems = getSuggestionListItems(sourceResponse, sourceResponseRequest);
    if (trackEvent) {
        trackEvent.suggestionsNumAlternateResponses = alternateResponseItems.length;
        trackEvent.suggestionsNumStrikes = setNumStrikes;
    }
    // Determine if the "contact" suggestion should appear in the list.
    const contactIsSelected = selectedSection === CONTACT_OPTIONS$1;
    const showContactSuggestion = shouldShowContactSuggestion(state, setNumStrikes, contactIsSelected);
    let contactItem;
    if (trackEvent) {
        trackEvent.suggestionsConnectToAgentAvailable = showContactSuggestion;
    }
    if (showContactSuggestion) {
        // Add an extra "connect to agent" response.
        contactItem = CONTACT_SUGGESTION_ITEM(getContactLabel(config, languagePack));
        if (contactIsSelected) {
            contactItem.isSelected = true;
        }
        if (wantPanelState === AlternateSuggestionsPanelState.OPEN_STRIKE) {
            // In the strike panel, we actually put the contact option inside the AR list instead of in its own section.
            alternateResponseItems.push(contactItem);
            contactItem = null;
        }
    }
    // Disable the last option the user selected and show as selected.
    if (selectedSection === ALTERNATE_RESPONSES$1 && alternateResponseItems[selectedIndex]) {
        alternateResponseItems[selectedIndex].isSelected = true;
    }
    const anyStarters = suggestionStarters?.length;
    const allowCollapse = Boolean(contactItem || alternateResponseItems?.length);
    const anyAvailable = allowCollapse || showSearchSection || anyStarters;
    if (trackEvent) {
        trackEvent.suggestionsNumAvailable = alternateResponseItems.length;
    }
    let panelState;
    if (!anyAvailable || (wantPanelState === OPEN_STRIKE$2 && !allowCollapse)) {
        // If there's nothing to show, then just keep the panel closed.
        panelState = CLOSED$2;
    }
    else {
        panelState = wantPanelState;
    }
    // Figure out if the button should be shown.
    const agentDisplayState = selectAgentDisplayState(state);
    let showButton;
    if (agentDisplayState.isConnectingOrConnected) {
        // Don't show suggestions if we're connecting or connected to an agent.
        showButton = false;
        panelState = CLOSED$2;
    }
    else if (anyStarters) {
        // If there are any home screen starters, then we always show the button.
        showButton = true;
    }
    else if (!anyAvailable || !hasResponseToRequest(localMessageIDs, allMessageItemsByID, allMessagesByID)) {
        // If there are no options or the user hasn't yet gotten a response to any messages, don't show the button.
        showButton = false;
        panelState = CLOSED$2;
    }
    else {
        const lastMessage = allMessagesByID[allMessageItemsByID[arrayLastValue(localMessageIDs)]?.fullMessageID];
        if (lastMessage && isRequest(lastMessage)) {
            // If the last message is a user message, then don't change the button state.
            showButton = state.alternateSuggestionsState.showButton;
        }
        else {
            showButton = true;
        }
    }
    let announceMessage;
    if (showButton && showButton !== state.alternateSuggestionsState.showButton) {
        // If the button becomes visible, we need to announce it.
        announceMessage = {
            messageID: 'suggestions_ariaAnnounceButtonShow',
        };
    }
    const prevPanelState = state.alternateSuggestionsState.panelState;
    if (panelState !== prevPanelState) {
        // If the list is opened or closed, we need to announce it and track it.
        let trackState;
        if (panelState !== CLOSED$2) {
            if (panelState === OPEN_FULL$2 && prevPanelState === OPEN_STRIKE$2) {
                trackState = 'Expanded';
                announceMessage = { messageID: 'suggestions_ariaAnnounceListExpanded' };
            }
            else if (panelState === OPEN_STRIKE$2 && prevPanelState === OPEN_FULL$2) {
                trackState = 'Collapsed';
                announceMessage = { messageID: 'suggestions_ariaAnnounceListCollapsed' };
            }
            else {
                trackState = 'Opened';
                announceMessage = { messageID: 'suggestions_ariaAnnounceListOpen' };
            }
        }
        else {
            trackState = 'Closed';
            announceMessage = {
                messageID: 'suggestions_ariaAnnounceListClosed',
            };
        }
        if (trackEvent) {
            trackEvent.suggestionsMenuIsOpened = trackState;
        }
    }
    const trackEvents = trackEvent ? [...state.pendingLogs.trackEvents, trackEvent] : state.pendingLogs.trackEvents;
    // When the panel is opened to the full state and there is a search skill attached, we need to show search results.
    let { searchResultsLoading, searchResults, searchResultsText } = state.alternateSuggestionsState;
    if (panelState === OPEN_FULL$2 && showSearchSection) {
        const request = allMessagesByID[sourceResponse?.request_id];
        const newText = request?.input?.text;
        const oldText = searchResultsText;
        if (newText && newText !== oldText) {
            // If the text has changed from the last time we did a search, we need to do another search. This will trigger
            // the subscriber to actually execute the search.
            searchResultsLoading = true;
            searchResultsText = newText;
            searchResults = null;
        }
    }
    if (panelState === OPEN_STRIKE$2) {
        // We don't show these items in the strike panel.
        suggestionStarters = null;
    }
    else if (panelState === CLOSED$2) {
        // We don't show these items in the closed panel.
        contactItem = null;
        suggestionStarters = null;
        alternateResponseItems = null;
    }
    return {
        ...state,
        announceMessage: announceMessage || state.announceMessage,
        alternateSuggestionsState: {
            ...state.alternateSuggestionsState,
            suggestionListItems: alternateResponseItems,
            starters: suggestionStarters,
            contactItem,
            sourceResponse,
            showSearchSection,
            panelState,
            showButton,
            allowCollapse,
            searchResults,
            searchResultsLoading,
            searchResultsText,
        },
        persistedToBrowserStorage: keepIfEqual(state.persistedToBrowserStorage, {
            ...state.persistedToBrowserStorage,
            chatState: {
                ...state.persistedToBrowserStorage.chatState,
                suggestionsPersistedState: {
                    ...state.persistedToBrowserStorage.chatState.suggestionsPersistedState,
                    numStrikes: setNumStrikes,
                },
            },
        }),
        pendingLogs: {
            ...state.pendingLogs,
            eventMessages,
            trackEvents,
        },
    };
}
/**
 * Applies alternate suggestions specific behavior to the {@link HYDRATE_MESSAGE_HISTORY} action.
 */
function alternateSuggestionsHandleHistory(state) {
    // If suggestions is disabled the contact suggestion shouldn't appear.
    if (!state.alternateSuggestionsState.config.is_on) {
        return state;
    }
    return applyAlternateSuggestionsState(state, DEFAULT_ALTERNATE_SUGGESTIONS_STATE.panelState);
}
/**
 * Applies alternate suggestions specific behavior to the {@link UPDATE_HOME_SCREEN_CONFIG} action.
 */
function alternateSuggestionsHandleUpdateHomeScreenConfig(state) {
    if (!state.alternateSuggestionsState.config.is_on) {
        return state;
    }
    return applyAlternateSuggestionsState(state);
}
/**
 * Returns the {@link MessageResponse} that may contain alternate responses that we wish to display as suggestions.
 * This will be the last response that was received that was not from a suggestion. When the user clicks a
 * suggestion and creates a request, the following responses doesn't count.
 */
function getResponseWithAlternateResponses(state) {
    const { botMessageState, allMessagesByID } = state;
    const { messageIDs } = botMessageState;
    // Search backwards until we find the last non-suggestions request that had a response. We want to skip over the
    // last request if there has been no response yet because we don't want the suggestions to change until we
    // actually get a new response.
    let hasSeenResponse = false;
    let stoppedByAgent = false;
    let index = findLastIndex(messageIDs, messageID => {
        const message = allMessagesByID[messageID];
        if (isResponse(message)) {
            hasSeenResponse = true;
        }
        if (hasLiveAgentMessage(message)) {
            // This means we've entered into an agent conversation. We won't return any suggestions from before this.
            stoppedByAgent = true;
        }
        return hasSeenResponse && isRequest(message) && !message.history?.is_suggestion;
    });
    // If we've found an appropriate request, we need to move forward and look for the matching response (this should
    // almost always be the next message).
    if (index !== -1 && !stoppedByAgent) {
        const lastRequestID = messageIDs[index];
        for (index++; index < messageIDs.length; index++) {
            const message = allMessagesByID[messageIDs[index]];
            if (isResponse(message) && message.request_id === lastRequestID) {
                return message;
            }
        }
    }
    return null;
}
/**
 * Returns the list of {@link HomeScreenStarterButton} that can be displayed in the suggestions menu.
 */
function getStarters(state) {
    const { persistedToBrowserStorage, homeScreenConfig } = state;
    const { starters } = homeScreenConfig;
    const { suggestionsPersistedState } = persistedToBrowserStorage.chatState;
    const { selectedIndex, selectedSection } = suggestionsPersistedState;
    let suggestionStarters = [];
    if (homeScreenConfig?.is_on && starters?.is_on && starters?.buttons.length) {
        suggestionStarters = starters?.buttons.map(button => ({ ...button, isSelected: false }));
        if (selectedSection === STARTERS$1 && suggestionStarters[selectedIndex]) {
            suggestionStarters[selectedIndex].isSelected = true;
        }
    }
    return suggestionStarters;
}
/**
 * Returns all of the alternate options for the given message.
 */
function getSuggestionListItems(lastOutput, lastOutputRequest) {
    const options = [];
    if (lastOutput?.output.alternate_responses) {
        // We don't want to show alternate responses in the middle of a multi-turn but only after the first message. The
        // first message should still show them. We know we're in a multi-turn if it has a thread id. It's a little harder
        // to tell if it's the first message though. We can check the request to see if the request thinks it was already
        // in a thread. We can also check to see if this response has "branch_exited" which could mean we're still in a
        // thread but the previous one ended and this is now the first message in a new thread.
        const isNotInTheMiddleOfMultiTurn = isFromAMainThread(lastOutput, lastOutputRequest) || isFallbackResponse(lastOutput);
        // If this is a main message thread or a new thread, then we can show alternate responses. We want to suppress
        // alternate responses when we're in the middle of a multi-turn (but show them on the first message).
        if (isNotInTheMiddleOfMultiTurn) {
            lastOutput.output.alternate_responses.forEach(item => {
                if (item.suggestions) {
                    item.suggestions.forEach(suggestion => {
                        // Drop the "None of the above" suggestions.
                        if (suggestion.value?.input?.intents?.length) {
                            options.push({
                                isContactSuggestion: false,
                                isSelected: false,
                                option: suggestion,
                            });
                        }
                    });
                }
            });
        }
    }
    return options;
}
/**
 * Updates the current search results for display in suggestions.
 */
function setSuggestionsSearchResults(state, action) {
    return {
        ...state,
        alternateSuggestionsState: {
            ...state.alternateSuggestionsState,
            searchResultsLoading: false,
            searchResults: action.searchResultItem,
        },
    };
}

/**
 * Redux reducers for human agent actions.
 */
const agentReducers = {
    [HA_SET_IS_CONNECTING]: (state, action) => {
        const { isConnecting, localMessageID } = action;
        return applyAlternateSuggestionsState({
            ...state,
            agentState: {
                ...state.agentState,
                isConnecting,
                activeLocalMessageID: localMessageID,
                // When connecting, clear any unread messages from a previous conversation.
                numUnreadMessages: isConnecting ? 0 : state.agentState.numUnreadMessages,
            },
            persistedToBrowserStorage: {
                ...state.persistedToBrowserStorage,
                chatState: {
                    ...state.persistedToBrowserStorage.chatState,
                    agentState: {
                        ...state.persistedToBrowserStorage.chatState.agentState,
                        isSuspended: isConnecting ? state.persistedToBrowserStorage.chatState.agentState.isSuspended : false,
                    },
                },
            },
        });
    },
    [HA_SET_IS_RECONNECTING]: (state, action) => {
        const { isReconnecting } = action;
        return {
            ...state,
            agentState: {
                ...state.agentState,
                isReconnecting,
            },
        };
    },
    [HA_SET_AGENT_AVAILABILITY]: (state, action) => {
        if (!state.agentState.isConnecting) {
            // If the agent is not currently connecting, just ignore the availability update.
            return state;
        }
        return {
            ...state,
            agentState: {
                ...state.agentState,
                availability: state.agentState.isConnecting ? action.availability : null,
            },
        };
    },
    [HA_SET_SHOW_SCREEN_SHARE_REQUEST]: (state, { showRequest }) => {
        return {
            ...state,
            agentState: {
                ...state.agentState,
                showScreenShareRequest: showRequest,
            },
        };
    },
    [HA_SET_AGENT_JOINED]: (state, action) => {
        const agentProfiles = { ...state.persistedToBrowserStorage.chatState.agentState.agentProfiles };
        const { agentProfile } = action;
        if (agentProfile) {
            agentProfiles[agentProfile.id] = agentProfile;
        }
        return {
            ...state,
            agentState: {
                ...state.agentState,
                isConnecting: false,
                isReconnecting: false,
                availability: null,
            },
            persistedToBrowserStorage: {
                ...state.persistedToBrowserStorage,
                chatState: {
                    ...state.persistedToBrowserStorage.chatState,
                    agentState: {
                        ...state.persistedToBrowserStorage.chatState.agentState,
                        isConnected: true,
                        agentProfile,
                        agentProfiles,
                    },
                },
            },
        };
    },
    [HA_SET_PERSISTED_STATE]: (state, action) => ({
        ...state,
        persistedToBrowserStorage: {
            ...state.persistedToBrowserStorage,
            chatState: {
                ...state.persistedToBrowserStorage.chatState,
                agentState: {
                    ...state.persistedToBrowserStorage.chatState.agentState,
                    serviceDeskState: action.state,
                },
            },
        },
    }),
    [HA_UPDATE_IS_SUSPENDED]: (state, action) => {
        if (!state.agentState.isConnecting && !state.persistedToBrowserStorage.chatState.agentState.isConnected) {
            // If the user is not connecting or connected to an agent, then we can't update the suspended state.
            return state;
        }
        return {
            ...state,
            persistedToBrowserStorage: {
                ...state.persistedToBrowserStorage,
                chatState: {
                    ...state.persistedToBrowserStorage.chatState,
                    agentState: {
                        ...state.persistedToBrowserStorage.chatState.agentState,
                        isSuspended: action.isSuspended,
                    },
                },
            },
        };
    },
    [HA_UPDATE_IS_TYPING]: (state, action) => {
        return {
            ...state,
            agentState: {
                ...state.agentState,
                isAgentTyping: action.isTyping,
            },
        };
    },
    [HA_SET_AGENT_LEFT_CHAT]: (state) => 
    // Remove the agent's profile and typing indicator.
    ({
        ...state,
        botMessageState: {
            ...state.botMessageState,
        },
        agentState: {
            ...state.agentState,
            isAgentTyping: false,
        },
        persistedToBrowserStorage: {
            ...state.persistedToBrowserStorage,
            chatState: {
                ...state.persistedToBrowserStorage.chatState,
                agentState: {
                    ...state.persistedToBrowserStorage.chatState.agentState,
                    agentProfile: null,
                },
            },
        },
    }),
    [HA_UPDATE_CAPABILITIES]: (state, action) => {
        const newInputState = {
            ...state.agentState.inputState,
            ...action.capabilities,
        };
        if (!newInputState.allowFileUploads) {
            newInputState.files = [];
        }
        return {
            ...state,
            agentState: {
                ...state.agentState,
                inputState: newInputState,
            },
        };
    },
    [HA_SET_IS_SCREEN_SHARING]: (state, { isSharing }) => ({
        ...state,
        agentState: {
            ...state.agentState,
            isScreenSharing: isSharing,
        },
    }),
    [HA_UPDATE_FILE_UPLOAD_IN_PROGRESS]: (state, action) => ({
        ...state,
        agentState: {
            ...state.agentState,
            fileUploadInProgress: action.fileUploadInProgress,
        },
    }),
    [HA_END_CHAT]: (state) => {
        // Update the UI state of the current CTA message to indicate that chat was ended.
        let newState = applyLocalMessageUIState(state, state.agentState.activeLocalMessageID, 'wasAgentChatEnded', true);
        // End the chat.
        newState = {
            ...newState,
            agentState: {
                ...newState.agentState,
                isConnecting: false,
                isReconnecting: false,
                availability: null,
                activeLocalMessageID: null,
                isAgentTyping: false,
                inputState: {
                    ...newState.agentState.inputState,
                    isReadonly: false,
                },
            },
            persistedToBrowserStorage: {
                ...state.persistedToBrowserStorage,
                chatState: {
                    ...state.persistedToBrowserStorage.chatState,
                    agentState: {
                        ...state.persistedToBrowserStorage.chatState.agentState,
                        isConnected: false,
                        isSuspended: false,
                        agentProfile: null,
                    },
                },
            },
        };
        // Update the suggestions state.
        newState = applyAlternateSuggestionsState(newState);
        return newState;
    },
};

/**
 * When the message history is being hydrated and there was an active tour, or when starting a tour, populate the tour
 * step items. If the message with the tour data can not be found, or there is no activeTourID, clear all the existing
 * tour data.
 */
function populateTourStepItems(state) {
    const { activeTourID } = state.persistedToBrowserStorage.chatState.persistedTourState;
    if (activeTourID) {
        // If there's an activeTourID find the originalMessage that contains a localMessage that triggered the current
        // active tour.
        const originalMessage = state.allMessagesByID[activeTourID];
        // Find the GenericItem within the originalMessage that has the active tour data.
        const tourMessageItem = originalMessage?.output?.generic?.find(message => Boolean(message.user_defined?.steps));
        if (tourMessageItem) {
            // Populate the tourState now that we have the active tour data.
            return {
                ...state,
                tourState: {
                    ...state.tourState,
                    activeTourStepItems: tourMessageItem.user_defined?.steps,
                },
            };
        }
    }
    // If the message with the tour data can not be found, or there is no activeTourID, clear all the existing tour data.
    return clearTourState(state);
}
/**
 * Clear all tour state.
 */
function clearTourState(state) {
    return {
        ...state,
        persistedToBrowserStorage: {
            ...state.persistedToBrowserStorage,
            chatState: {
                ...state.persistedToBrowserStorage.chatState,
                persistedTourState: {
                    activeTourID: null,
                    activeTourCurrentStepIndex: null,
                },
            },
            launcherState: {
                ...state.persistedToBrowserStorage.launcherState,
                activeTour: false,
            },
        },
        tourState: {
            ...state.tourState,
            activeTourStepItems: null,
        },
    };
}

// The set of agent message types that should be excluded on the unread agent message count.
const EXCLUDE_AGENT_UNREAD = new Set([
    AgentMessageType.USER_ENDED_CHAT,
    AgentMessageType.CHAT_WAS_ENDED,
    AgentMessageType.RELOAD_WARNING,
]);
const reducers = {
    [CHANGE_STATE]: (state, action) => merge({}, state, action.partialState),
    [SET_MAIN_CONFIG]: (state, action) => ({
        ...state,
        config: {
            ...state.config,
            remote: {
                ...state.config.remote,
                mainConfig: action.mainConfig,
            },
        },
    }),
    [HYDRATE_CHAT]: (state) => ({
        ...state,
        isHydrated: true,
    }),
    [RESTART_CONVERSATION]: (state) => {
        let newState = {
            ...state,
            botMessageState: {
                ...state.botMessageState,
                localMessageIDs: [],
                messageIDs: [],
                isTypingCounter: 0,
                isScrollAnchored: false,
            },
            allMessageItemsByID: {},
            allMessagesByID: {},
            iFramePanelState: {
                ...DEFAULT_IFRAME_PANEL_STATE,
            },
            viewSourcePanelState: {
                ...DEFAULT_CITATION_PANEL_STATE,
            },
            customPanelState: {
                ...DEFAULT_CUSTOM_PANEL_STATE,
            },
            persistedToBrowserStorage: {
                ...state.persistedToBrowserStorage,
                chatState: {
                    ...state.persistedToBrowserStorage.chatState,
                    sessionID: null,
                    sessionStartedTimestamp: null,
                    homeScreenState: {
                        ...state.persistedToBrowserStorage.chatState.homeScreenState,
                        showBackToBot: false,
                    },
                },
            },
            isHydrated: false,
            catastrophicErrorType: null,
        };
        // Clear the tour state on restart.
        newState = clearTourState(newState);
        if (newState.homeScreenConfig.is_on) {
            newState = setHomeScreenOpenState(newState, true);
        }
        return alternateSuggestionsHandleRestart(newState);
    },
    // TODO: Need session history test case for setting the alternate suggestions values
    [HYDRATE_MESSAGE_HISTORY]: (state, action) => {
        let newState = {
            ...state,
            ...action.messageHistory,
        };
        // If there's an active tour then use the new state with message history to populate the tour state.
        if (state.persistedToBrowserStorage.chatState.persistedTourState.activeTourID) {
            newState = populateTourStepItems(newState);
        }
        return alternateSuggestionsHandleHistory(newState);
    },
    [ADD_LOCAL_MESSAGE_ITEM]: (state, action) => {
        const { messageItem, message, addMessage, addAfterID } = action;
        const { id } = messageItem.ui_state;
        // If we receive back a silent message, we don't want to add to the store.
        const isSilent = message.history.silent;
        let newState = state;
        if (addMessage) {
            newState = applyFullMessage(newState, message);
        }
        const currentIndex = newState.botMessageState.localMessageIDs.findIndex(existingID => existingID === id);
        const newLocalMessageIDs = [...newState.botMessageState.localMessageIDs];
        let insertAtIndex = currentIndex;
        if (currentIndex !== -1) {
            // Remove the ID from the array. We may insert it back at this index.
            newLocalMessageIDs.splice(currentIndex, 1);
        }
        else {
            // By default, insert the new ID at the end.
            insertAtIndex = newLocalMessageIDs.length;
        }
        // If an "addAfterID" was provided, use that to determine where to put this new ID.
        if (addAfterID) {
            const afterIDIndex = newLocalMessageIDs.findIndex(existingID => existingID === addAfterID);
            if (afterIDIndex !== -1) {
                insertAtIndex = afterIDIndex + 1;
            }
        }
        // Insert the ID.
        newLocalMessageIDs.splice(insertAtIndex, 0, id);
        if (!isSilent) {
            newState = {
                ...newState,
                allMessageItemsByID: {
                    ...newState.allMessageItemsByID,
                    [id]: messageItem,
                },
                botMessageState: {
                    ...newState.botMessageState,
                    localMessageIDs: newLocalMessageIDs,
                },
            };
            if (newState.persistedToBrowserStorage.chatState.homeScreenState.isHomeScreenOpen) {
                // When a message has been sent, we don't want the home screen open anymore.
                newState = setHomeScreenOpenState(newState, false);
            }
            newState = alternateSuggestionsHandleAddMessage(newState, action);
            const isBotMessage = !messageItem.item.agent_message_type;
            const isMainWindowOpen = state.persistedToBrowserStorage.launcherState.viewState.mainWindow;
            if (!isBotMessage && (!isMainWindowOpen || !state.isBrowserPageVisible)) {
                // This message is with an agent, and it occurred while the main window was closed or the page is not
                // visible, so it may need to be counted as an unread message.
                const fromAgent = !isRequest(message);
                if (fromAgent && !EXCLUDE_AGENT_UNREAD.has(messageItem.item.agent_message_type)) {
                    // If this message came from an agent, then add one to the unread count, but not if it's one of the excluded
                    // types.
                    newState = {
                        ...newState,
                        agentState: {
                            ...newState.agentState,
                            numUnreadMessages: newState.agentState.numUnreadMessages + 1,
                        },
                    };
                }
            }
        }
        return newState;
    },
    [REMOVE_MESSAGES]: (state, { messageIDs }) => {
        const idsSet = new Set(messageIDs);
        const newAllMessages = { ...state.allMessagesByID };
        const newAllMessageItems = { ...state.allMessageItemsByID };
        // Remove all the message IDs from the message list.
        const newMessageIDs = state.botMessageState.messageIDs.filter(messageID => !idsSet.has(messageID));
        // Remove all the message items from the items list for items that are part of one of the messages being
        // removed. Also remove the matching items from the map.
        const newMessageItemsIDs = state.botMessageState.localMessageIDs.filter(messageItemID => {
            const messageItem = newAllMessageItems[messageItemID];
            const removeItem = idsSet.has(messageItem?.fullMessageID);
            if (removeItem) {
                delete newAllMessageItems[messageItemID];
            }
            return !removeItem;
        });
        // Remove the message objects from the map.
        messageIDs.forEach(messageID => {
            delete newAllMessages[messageID];
        });
        const newState = {
            ...state,
            allMessagesByID: newAllMessages,
            allMessageItemsByID: newAllMessageItems,
            botMessageState: {
                ...state.botMessageState,
                messageIDs: newMessageIDs,
                localMessageIDs: newMessageItemsIDs,
            },
        };
        return applyAlternateSuggestionsState(newState);
    },
    [UPDATE_LOCAL_MESSAGE_ITEM]: (state, action) => {
        const { messageItem } = action;
        return {
            ...state,
            allMessageItemsByID: {
                ...state.allMessageItemsByID,
                [messageItem.ui_state.id]: messageItem,
            },
        };
    },
    [UPDATE_MESSAGE]: (state, action) => {
        const { message } = action;
        return {
            ...state,
            allMessagesByID: {
                ...state.allMessagesByID,
                [message.id]: message,
            },
        };
    },
    [ADD_MESSAGE]: (state, action) => {
        const { message } = action;
        const messageID = message.id;
        let newState = state;
        if (isResponse(message)) {
            // For message responses, we need to re-order any items that may already be present in the store if they had
            // been added during a previous stream. We're going to use the following algorithm.
            //
            // 1. Locate the first item already in the list. This will be the insertion point for the re-ordered items.
            // 2. Remove all existing items from the list.
            // 3. Insert the new items back into the list at the insertion point but only items that were previously in
            //    the list.
            //
            // Example: if we've got response 1 with items 1.1 and 1.2, response 2 with items 2.1, 2.2, 2.3, and response 3
            // with 3.1. We start with.
            //
            //  [1.1, 1.2, 2.1, 2.2, 2.3, 3.1]
            //
            // Now, we "re-add" message 2 expect that we 2.1 and 2.2 are reversed in order and 2.3 and 2.4 is going to be
            // added ([2.2, 2.1, 2.4]).
            //
            // 1. The first item is "2.1" at index 2.
            // 2. Remove all items for response 2 giving us [1.1, 1.2, 3.1]
            // 3. Insert 2.1 and 2.2 (those were the only items we already had) back in the list at index 2 in the new order.
            //    item 2.4 will be inserted later as an individual item.
            //
            // Result: [1.1, 1.2, 2.2, 2.1, 3.1]
            // Get the ordered list of the new items. Only items with a stream ID can be re-ordered at this point.
            const itemIDsInNewMessage = [];
            message.output.generic.forEach(item => {
                const id = streamItemID(messageID, item);
                if (id) {
                    itemIDsInNewMessage.push(id);
                }
            });
            const newAllMessageItemsByID = { ...state.allMessageItemsByID };
            const existingItemIDs = [];
            let firstFoundIndex;
            // Remove all the existing items for this message. Also keep track of where the first one was found.
            const newLocalMessageIDs = state.botMessageState.localMessageIDs.filter((itemID, index) => {
                const item = state.allMessageItemsByID[itemID];
                const isItemInMessage = item.fullMessageID === messageID;
                if (isItemInMessage) {
                    if (firstFoundIndex === undefined) {
                        firstFoundIndex = index;
                    }
                    if (!itemIDsInNewMessage.includes(itemID)) {
                        // If this item is not in the new message, then remove the whole item object.
                        delete newAllMessageItemsByID[itemID];
                    }
                    else {
                        // Otherwise, this item will may get re-inserted back into the list (if it still exists).
                        existingItemIDs.push(itemID);
                    }
                }
                // Keep the item if it's not in the new message.
                return !isItemInMessage;
            });
            // Now insert the message items back into the list at the right spot, but only the items we already had.
            if (existingItemIDs.length) {
                const itemIDsToInsert = itemIDsInNewMessage.filter(itemID => existingItemIDs.includes(itemID));
                if (itemIDsToInsert.length) {
                    newLocalMessageIDs.splice(firstFoundIndex, 0, ...itemIDsToInsert);
                }
            }
            newState = {
                ...newState,
                allMessageItemsByID: newAllMessageItemsByID,
                botMessageState: {
                    ...newState.botMessageState,
                    localMessageIDs: newLocalMessageIDs,
                },
            };
        }
        return applyFullMessage(newState, message);
    },
    [MESSAGE_SET_OPTION_SELECTED]: (state, action) => {
        const newMessagesByID = {
            ...state.allMessageItemsByID,
        };
        newMessagesByID[action.messageID] = {
            ...state.allMessageItemsByID[action.messageID],
            ui_state: {
                ...state.allMessageItemsByID[action.messageID].ui_state,
                optionSelected: action.sentMessage,
            },
        };
        return {
            ...state,
            allMessageItemsByID: newMessagesByID,
        };
    },
    [ADD_IS_TYPING_COUNTER]: (state, action) => {
        return {
            ...state,
            botMessageState: {
                ...state.botMessageState,
                isTypingCounter: Math.max(state.botMessageState.isTypingCounter + action.addToIsTyping, 0),
            },
        };
    },
    [ADD_IS_LOADING_COUNTER]: (state, action) => {
        return {
            ...state,
            botMessageState: {
                ...state.botMessageState,
                isLoadingCounter: Math.max(state.botMessageState.isLoadingCounter + action.addToIsLoading, 0),
            },
        };
    },
    [ADD_IS_HYDRATING_COUNTER]: (state, action) => {
        return {
            ...state,
            botMessageState: {
                ...state.botMessageState,
                isHydratingCounter: Math.max(state.botMessageState.isHydratingCounter + action.addToIsHydrating, 0),
            },
        };
    },
    [SET_APP_STATE_VALUE]: (state, action) => ({
        ...state,
        [action.key]: action.value,
    }),
    [UPDATE_SESSION_ID]: (state, action) => ({
        ...state,
        persistedToBrowserStorage: {
            ...state.persistedToBrowserStorage,
            chatState: {
                ...state.persistedToBrowserStorage.chatState,
                sessionID: action.sessionID,
            },
        },
    }),
    [UPDATE_SESSION_STARTED_TIMESTAMP]: (state, action) => ({
        ...state,
        persistedToBrowserStorage: {
            ...state.persistedToBrowserStorage,
            chatState: {
                ...state.persistedToBrowserStorage.chatState,
                sessionStartedTimestamp: action.sessionStartedTimestamp,
            },
        },
    }),
    [UPDATE_PERSISTED_CHAT_STATE]: (state, action) => ({
        ...state,
        persistedToBrowserStorage: {
            ...state.persistedToBrowserStorage,
            chatState: {
                ...state.persistedToBrowserStorage.chatState,
                ...action.chatState,
            },
        },
    }),
    [UPDATE_HAS_SENT_NON_WELCOME_MESSAGE]: (state, action) => {
        if (state.persistedToBrowserStorage.chatState.hasSentNonWelcomeMessage === action.hasSentNonWelcomeMessage) {
            return state;
        }
        return {
            ...state,
            persistedToBrowserStorage: {
                ...state.persistedToBrowserStorage,
                chatState: {
                    ...state.persistedToBrowserStorage.chatState,
                    hasSentNonWelcomeMessage: action.hasSentNonWelcomeMessage,
                },
                launcherState: {
                    ...state.persistedToBrowserStorage.launcherState,
                    hasSentNonWelcomeMessage: action.hasSentNonWelcomeMessage,
                },
            },
        };
    },
    [SET_VIEW_STATE]: (state, action) => {
        return handleViewStateChange(state, action.viewState);
    },
    [SET_VIEW_CHANGING]: (state, action) => ({
        ...state,
        viewChanging: action.viewChanging,
    }),
    [SET_INITIAL_VIEW_CHANGE_COMPLETE]: (state, action) => ({
        ...state,
        initialViewChangeComplete: action.changeComplete,
    }),
    [UPDATE_BOT_NAME]: (state, action) => {
        return {
            ...state,
            botName: action.name,
            headerDisplayName: state.theme.useAITheme ? state.headerDisplayName : action.name,
        };
    },
    [UPDATE_BOT_AVATAR_URL]: (state, action) => ({
        ...state,
        botAvatarURL: action.url,
    }),
    [UPDATE_LAUNCHER_AVATAR_URL]: (state, action) => ({
        ...state,
        launcher: {
            ...state.launcher,
            config: {
                ...state.launcher.config,
                mobile: {
                    ...state.launcher.config.mobile,
                    avatar_url_override: action.source,
                },
                desktop: {
                    ...state.launcher.config.desktop,
                    avatar_url_override: action.source,
                },
            },
        },
    }),
    [UPDATE_MAIN_HEADER_TITLE]: (state, action) => ({
        ...state,
        headerDisplayName: action.title,
    }),
    [UPDATE_CSS_VARIABLES]: (state, action) => {
        const { config } = state;
        const { publicVars, remoteStyles, variables } = action;
        // Update css variables in app config with merged css variables.
        const newConfig = {
            public: {
                ...config.public,
                __ibm__: {
                    ...config.public.__ibm__,
                    cssVariables: publicVars,
                },
            },
            remote: {
                ...config.remote,
                initConfig: {
                    ...config.remote.initConfig,
                    css_variables: remoteStyles,
                },
            },
        };
        return {
            ...state,
            // This is modifying the original config objects. We may need to hold a reference to the original at some point.
            config: newConfig,
            cssVariableOverrides: variables,
        };
    },
    [UPDATE_THEME_CONFIG]: (state, action) => {
        const useAITheme = action.themeConfig.use_ai_theme !== undefined ? action.themeConfig.use_ai_theme : state.theme.useAITheme;
        return {
            ...state,
            theme: {
                ...state.theme,
                // Use the new carbon theme if one has been set. Otherwise use the current carbon theme.
                carbonTheme: action.themeConfig.carbon_theme || state.theme.carbonTheme,
                // If a value for use_ai_theme has been provided then use that. Otherwise use the current value.
                useAITheme,
            },
            headerDisplayName: !useAITheme && state.headerDisplayName === null ? state.botName : state.headerDisplayName,
        };
    },
    [UPDATE_STREAMING_CONFIG]: (state, action) => {
        return {
            ...state,
            streaming: {
                ...state.streaming,
                enabled: 
                // If a value for is_on has been provided then use that. Otherwise us the current value.
                typeof action.streamingConfig.is_on !== 'undefined' ? action.streamingConfig.is_on : state.streaming.enabled,
            },
        };
    },
    // Right now we just merge here. When we understand home screen enough to open up these values to a developer,
    // we will probably want to split this reducer into individual parts.
    [UPDATE_HOME_SCREEN_CONFIG]: (state, action) => {
        // background_gradient is deprecated. When it's removed the following config manipulation function can be removed
        // and the merge in this reducer can go back to using action.homeScreenConfig for the new value instead of
        // newHomeScreenConfig.
        const newHomeScreenConfig = convertHomeScreenConfigToBackgroundIfBackgroundGradient(action.homeScreenConfig);
        return alternateSuggestionsHandleUpdateHomeScreenConfig({
            ...state,
            homeScreenConfig: mergeWith({}, state.homeScreenConfig, newHomeScreenConfig, replaceCurrentArrayValue),
        });
    },
    [SET_MESSAGE_UI_PROPERTY]: (state, action) => {
        return applyLocalMessageUIState(state, action.localMessageID, action.propertyName, action.propertyValue);
    },
    [SET_MESSAGE_HISTORY_PROPERTY]: (state, action) => {
        const { messageID, propertyName, propertyValue } = action;
        const oldMessage = state.allMessagesByID[messageID];
        if (oldMessage) {
            return {
                ...state,
                allMessagesByID: {
                    ...state.allMessagesByID,
                    [messageID]: {
                        ...oldMessage,
                        history: {
                            ...oldMessage.history,
                            [propertyName]: propertyValue,
                        },
                    },
                },
            };
        }
        return state;
    },
    [MERGE_HISTORY]: (state, action) => {
        const oldMessage = state.allMessagesByID[action.messageID];
        if (oldMessage) {
            return {
                ...state,
                allMessagesByID: {
                    ...state.allMessagesByID,
                    [action.messageID]: {
                        ...oldMessage,
                        history: merge({}, oldMessage.history, action.history),
                    },
                },
            };
        }
        return state;
    },
    [ANNOUNCE_MESSAGE]: (state, action) => ({
        ...state,
        announceMessage: action.message,
    }),
    [ACCEPTED_DISCLAIMER]: (state) => ({
        ...state,
        persistedToBrowserStorage: {
            ...state.persistedToBrowserStorage,
            chatState: {
                ...state.persistedToBrowserStorage.chatState,
                disclaimersAccepted: {
                    ...state.persistedToBrowserStorage.chatState.disclaimersAccepted,
                    [window.location.hostname]: true,
                },
            },
        },
    }),
    [SET_HOME_SCREEN_IS_OPEN]: (state, { isOpen }) => setHomeScreenOpenState(state, isOpen),
    [TOGGLE_HOME_SCREEN]: (state) => setHomeScreenOpenState(state, !state.persistedToBrowserStorage.chatState.homeScreenState.isHomeScreenOpen, true),
    [SET_UPDATE_HISTORY_USER_DEFINED_COUNT]: (state, action) => {
        const { messageID } = action;
        const messageIDExists = messageID in state.persistedToBrowserStorage.chatState.updateHistoryUserDefinedCount;
        return {
            ...state,
            persistedToBrowserStorage: {
                ...state.persistedToBrowserStorage,
                chatState: {
                    ...state.persistedToBrowserStorage.chatState,
                    updateHistoryUserDefinedCount: {
                        ...state.persistedToBrowserStorage.chatState.updateHistoryUserDefinedCount,
                        [messageID]: messageIDExists
                            ? state.persistedToBrowserStorage.chatState.updateHistoryUserDefinedCount[messageID] + 1
                            : 1,
                    },
                },
            },
        };
    },
    [UPDATE_LAUNCHER_CONFIG]: (state, action) => {
        const newConfig = merge({}, state.launcher.config, action.launcherConfig);
        return {
            ...state,
            launcher: {
                ...state.launcher,
                config: newConfig,
            },
            persistedToBrowserStorage: {
                ...state.persistedToBrowserStorage,
                launcherState: {
                    ...state.persistedToBrowserStorage.launcherState,
                    desktopLauncherIsExpanded: newConfig.is_on && newConfig.desktop.is_on
                        ? state.persistedToBrowserStorage.launcherState.desktopLauncherIsExpanded
                        : false,
                    mobileLauncherIsExtended: newConfig.is_on && newConfig.mobile.is_on
                        ? state.persistedToBrowserStorage.launcherState.mobileLauncherIsExtended
                        : false,
                },
            },
        };
    },
    [UPDATE_BRANDING_CONFIG]: (state, action) => {
        const newConfig = merge({}, state.brandingState.config, action.brandingConfig);
        return {
            ...state,
            brandingState: {
                ...state.brandingState,
                config: newConfig,
            },
        };
    },
    [SET_BRANDING_OVERLAY_IS_OPEN]: (state, action) => {
        return {
            ...state,
            brandingState: {
                ...state.brandingState,
                isOpen: action.isOpen,
            },
            announceMessage: calcAnnouncementForBrandingPanel(state, action.isOpen),
        };
    },
    [SET_BRANDING_BANNER_SHOWN]: (state, action) => {
        return {
            ...state,
            brandingState: {
                ...state.brandingState,
                showBanner: action.showBanner,
            },
        };
    },
    [SET_LAUNCHER_PROPERTY]: (state, action) => {
        return {
            ...state,
            persistedToBrowserStorage: {
                ...state.persistedToBrowserStorage,
                launcherState: {
                    ...state.persistedToBrowserStorage.launcherState,
                    [action.propertyName]: action.propertyValue,
                },
            },
        };
    },
    [SET_LAUNCHER_CONFIG_PROPERTY]: (state, action) => {
        const newState = {
            ...state,
            launcher: {
                ...state.launcher,
                config: {
                    ...state.launcher.config,
                },
            },
        };
        if (!action.launcherType || action.launcherType === LauncherType.DESKTOP) {
            newState.launcher.config.desktop = {
                ...state.launcher.config.desktop,
                [action.propertyName]: action.propertyValue,
            };
        }
        if (!action.launcherType || action.launcherType === LauncherType.MOBILE) {
            newState.launcher.config.mobile = {
                ...state.launcher.config.mobile,
                [action.propertyName]: action.propertyValue,
            };
        }
        return newState;
    },
    [SET_CHAT_MESSAGES_PROPERTY]: (state, action) => {
        return applyBotMessageState(state, { [action.propertyName]: action.propertyValue });
    },
    [SET_LAUNCHER_MINIMIZED]: (state) => {
        return {
            ...state,
            persistedToBrowserStorage: {
                ...state.persistedToBrowserStorage,
                launcherState: {
                    ...state.persistedToBrowserStorage.launcherState,
                    desktopLauncherIsExpanded: false,
                    desktopLauncherWasMinimized: true,
                },
            },
        };
    },
    [OPEN_IFRAME_CONTENT]: (state, { messageItem }) => {
        return {
            ...state,
            iFramePanelState: {
                ...state.iFramePanelState,
                messageItem,
                isOpen: true,
            },
            announceMessage: {
                messageID: 'iframe_ariaOpenedPanel',
            },
        };
    },
    [CLOSE_IFRAME_PANEL]: (state) => {
        return {
            ...state,
            iFramePanelState: {
                ...state.iFramePanelState,
                isOpen: false,
            },
            announceMessage: {
                messageID: 'iframe_ariaClosedPanel',
            },
        };
    },
    [SET_CONVERSATIONAL_SEARCH_CITATION_PANEL_IS_OPEN]: (state, action) => {
        return {
            ...state,
            viewSourcePanelState: {
                ...state.viewSourcePanelState,
                citationItem: action.citationItem,
                relatedSearchResult: action.relatedSearchResult,
                isOpen: action.isOpen,
            },
        };
    },
    [SET_CUSTOM_PANEL_OPEN]: (state, action) => {
        return {
            ...state,
            customPanelState: {
                ...state.customPanelState,
                isOpen: action.isOpen,
            },
        };
    },
    [SET_CUSTOM_PANEL_OPTIONS]: (state, action) => {
        return {
            ...state,
            customPanelState: {
                ...state.customPanelState,
                options: action.options,
            },
        };
    },
    [SET_TOUR_DATA]: (state, action) => {
        const newStateWithPersistedTourData = {
            ...state,
            persistedToBrowserStorage: {
                ...state.persistedToBrowserStorage,
                chatState: {
                    ...state.persistedToBrowserStorage.chatState,
                    persistedTourState: {
                        activeTourID: action.newActiveTourMessageID,
                        activeTourCurrentStepIndex: 0,
                    },
                },
                launcherState: {
                    ...state.persistedToBrowserStorage.launcherState,
                    activeTour: true,
                },
            },
        };
        return populateTourStepItems(newStateWithPersistedTourData);
    },
    [CLEAR_TOUR_DATA]: (state) => {
        return clearTourState(state);
    },
    [CHANGE_STEP_IN_TOUR]: (state, action) => {
        return {
            ...state,
            persistedToBrowserStorage: {
                ...state.persistedToBrowserStorage,
                chatState: {
                    ...state.persistedToBrowserStorage.chatState,
                    persistedTourState: {
                        ...state.persistedToBrowserStorage.chatState.persistedTourState,
                        activeTourCurrentStepIndex: Math.max(Math.min(action.newStepNumber, state.tourState.activeTourStepItems.length - 1), 0),
                    },
                },
            },
        };
    },
    [UPDATE_INPUT_STATE]: (state, action) => {
        const currentInputState = getInputState(state, action.isInputToAgent);
        const newInputState = {
            ...currentInputState,
            ...action.newState,
        };
        const newState = applyInputState(state, newInputState, action.isInputToAgent);
        if (newInputState.isReadonly &&
            !currentInputState.isReadonly &&
            newState.alternateSuggestionsState.panelState !== AlternateSuggestionsPanelState.CLOSED) {
            // If we are putting the bot into a read-only state and the suggestions panel is not closed then close it.
            return applyAlternateSuggestionsState(newState, AlternateSuggestionsPanelState.CLOSED);
        }
        return newState;
    },
    [SET_IS_BROWSER_PAGE_VISIBLE]: (state, action) => {
        // If the page becomes visible while the main window is open, then clear the number of unread messages.
        let numUnreadMessages;
        const isMainWindowOpen = state.persistedToBrowserStorage.launcherState.viewState.mainWindow;
        if (isMainWindowOpen && action.isVisible) {
            numUnreadMessages = 0;
        }
        else {
            numUnreadMessages = state.agentState.numUnreadMessages;
        }
        return {
            ...state,
            isBrowserPageVisible: action.isVisible,
            agentState: {
                ...state.agentState,
                numUnreadMessages,
            },
        };
    },
    [ADD_INPUT_FILE]: (state, { file, isInputToAgent }) => {
        const currentInputState = getInputState(state, isInputToAgent);
        return applyInputState(state, {
            ...currentInputState,
            files: [...currentInputState.files, file],
        }, isInputToAgent);
    },
    [REMOVE_INPUT_FILE]: (state, { fileID, isInputToAgent }) => {
        const currentInputState = getInputState(state, isInputToAgent);
        const newUploads = [...currentInputState.files];
        const index = newUploads.findIndex(file => file.id === fileID);
        if (index !== -1) {
            newUploads.splice(index, 1);
        }
        return applyInputState(state, {
            ...currentInputState,
            files: newUploads,
        }, isInputToAgent);
    },
    [REMOVE_LOCAL_MESSAGE_ITEM]: (state, { localMessageItemID }) => {
        const newLocalMessageIDs = state.botMessageState.localMessageIDs.filter(id => id !== localMessageItemID);
        const allMessageItemsByID = {
            ...state.allMessageItemsByID,
        };
        if (allMessageItemsByID[localMessageItemID]) {
            delete allMessageItemsByID[localMessageItemID];
        }
        return {
            ...state,
            allMessageItemsByID,
            botMessageState: {
                ...state.botMessageState,
                localMessageIDs: newLocalMessageIDs,
            },
        };
    },
    [ADD_NOTIFICATION]: (state, { notification, notificationID }) => {
        return {
            ...state,
            notifications: state.notifications.concat({ id: notificationID, notification }),
        };
    },
    [REMOVE_NOTIFICATIONS]: (state, { groupID, notificationID }) => {
        return {
            ...state,
            notifications: state.notifications.filter(notification => {
                if (notificationID) {
                    return notification.id !== notificationID;
                }
                return notification.notification.groupID !== groupID;
            }),
        };
    },
    [REMOVE_ALL_NOTIFICATIONS]: (state) => {
        return {
            ...state,
            notifications: [],
        };
    },
    [CLEAR_INPUT_FILES]: (state, { isInputToAgent }) => {
        const currentInputState = getInputState(state, isInputToAgent);
        return applyInputState(state, {
            ...currentInputState,
            files: [],
        }, isInputToAgent);
    },
    [FILE_UPLOAD_INPUT_ERROR]: (state, { fileID, errorMessage, isInputToAgent }) => {
        const currentInputSate = getInputState(state, isInputToAgent);
        const newUploads = [...currentInputSate.files];
        const index = newUploads.findIndex(file => file.id === fileID);
        if (index !== -1) {
            newUploads[index] = {
                ...newUploads[index],
                isError: true,
                errorMessage,
                status: FileStatusValue.COMPLETE,
            };
        }
        return applyInputState(state, {
            ...currentInputSate,
            files: newUploads,
        }, isInputToAgent);
    },
    [ADD_NESTED_MESSAGES]: (state, { localMessageItems }) => {
        const allMessageItemsByID = { ...state.allMessageItemsByID };
        localMessageItems.forEach(localMessageItem => {
            allMessageItemsByID[localMessageItem.ui_state.id] = localMessageItem;
        });
        return {
            ...state,
            allMessageItemsByID,
        };
    },
    [SET_RESPONSE_PANEL_IS_OPEN]: (state, { isOpen }) => {
        return {
            ...state,
            responsePanelState: {
                ...state.responsePanelState,
                isOpen,
            },
        };
    },
    [SET_RESPONSE_PANEL_CONTENT]: (state, { localMessageItem, isMessageForInput }) => {
        return {
            ...state,
            responsePanelState: {
                ...state.responsePanelState,
                localMessageItem,
                isMessageForInput,
            },
        };
    },
    [STREAMING_START]: (state, { messageID }) => {
        // Add an empty placeholder where we will start adding the streaming chunks as they come in.
        const streamIntoResponse = {
            id: messageID,
            output: {
                generic: [],
            },
            history: {
                timestamp: Date.now(),
            },
        };
        return applyFullMessage(state, streamIntoResponse);
    },
    [STREAMING_MERGE_HISTORY]: (state, { messageID, history }) => {
        const existingMessage = state.allMessagesByID[messageID];
        const newMessage = merge({}, existingMessage, { history });
        if (existingMessage) {
            return {
                ...state,
                allMessagesByID: {
                    ...state.allMessagesByID,
                    [messageID]: newMessage,
                },
            };
        }
        return state;
    },
    [STREAMING_ADD_CHUNK]: (state, { chunkItem, fullMessageID, isCompleteItem, disableFadeAnimation, }) => {
        const message = state.allMessagesByID[fullMessageID];
        // This might be undefined if we haven't seen this item before.
        const localItemID = streamItemID(fullMessageID, chunkItem);
        const existingLocalMessageItem = state.allMessageItemsByID[localItemID];
        let { localMessageIDs } = state.botMessageState;
        let newItem;
        if (!existingLocalMessageItem) {
            // This is a new item we haven't seen before. We will need the response type to know what to with this item which
            // should always be available in the first chunk. We will then need to add this item to the store so it'll appear.
            newItem = outputItemToLocalItem(chunkItem, message, false);
            newItem.ui_state.needsAnnouncement = false;
            newItem.ui_state.disableFadeAnimation = disableFadeAnimation;
            newItem.ui_state.isIntermediateStreaming = true;
            if (isCompleteItem) {
                newItem.ui_state.streamingState = { chunks: [], isDone: true };
            }
            else {
                newItem.ui_state.streamingState = { chunks: [chunkItem], isDone: false };
            }
            localMessageIDs = [...localMessageIDs, localItemID];
            if (!newItem.item.response_type) {
                throw new Error(`New chunk item does not have a response_type: ${JSON.stringify(chunkItem)}`);
            }
        }
        else if (isCompleteItem) {
            // This is a complete item.
            newItem = outputItemToLocalItem(chunkItem, message, false);
            newItem.ui_state.needsAnnouncement = false;
            newItem.ui_state.disableFadeAnimation = disableFadeAnimation;
            newItem.ui_state.streamingState = { chunks: [], isDone: true };
        }
        else {
            // This is a new chunk on an existing item. We need to merge it with the existing item and add the new chunk.
            newItem = {
                ...existingLocalMessageItem,
                ui_state: {
                    ...existingLocalMessageItem?.ui_state,
                    streamingState: {
                        ...existingLocalMessageItem?.ui_state.streamingState,
                        chunks: [...existingLocalMessageItem?.ui_state.streamingState.chunks, chunkItem],
                    },
                },
            };
        }
        return {
            ...state,
            allMessageItemsByID: {
                ...state.allMessageItemsByID,
                [localItemID]: newItem,
            },
            botMessageState: {
                ...state.botMessageState,
                localMessageIDs,
            },
        };
    },
    [UPDATE_CHAT_HEADER_CONFIG]: (state, { chatHeaderConfig }) => {
        return {
            ...state,
            chatHeaderState: {
                ...state.chatHeaderState,
                config: {
                    ...state.chatHeaderState.config,
                    ...chatHeaderConfig,
                },
            },
        };
    },
    [UPDATE_MAX_VISIBLE_HEADER_OBJECTS]: (state, { maxTotal }) => {
        return {
            ...state,
            chatHeaderState: {
                ...state.chatHeaderState,
                maxVisibleHeaderObjects: maxTotal,
            },
        };
    },
    [SET_STOP_STREAMING_BUTTON_VISIBLE]: (state, { isVisible }) => {
        return {
            ...state,
            botInputState: {
                ...state.botInputState,
                stopStreamingButtonState: {
                    ...state.botInputState.stopStreamingButtonState,
                    isVisible,
                },
            },
        };
    },
    [SET_STOP_STREAMING_BUTTON_DISABLED]: (state, { isDisabled }) => {
        return {
            ...state,
            botInputState: {
                ...state.botInputState,
                stopStreamingButtonState: {
                    ...state.botInputState.stopStreamingButtonState,
                    isDisabled,
                },
            },
        };
    },
    [SET_STREAM_ID]: (state, { currentStreamID }) => {
        return {
            ...state,
            botInputState: {
                ...state.botInputState,
                stopStreamingButtonState: {
                    ...state.botInputState.stopStreamingButtonState,
                    currentStreamID,
                },
            },
        };
    },
    [UPDATE_MAIN_HEADER_AVATAR]: (state, { config }) => {
        return {
            ...state,
            headerAvatarConfig: config,
        };
    },
};
/**
 * Adds the given full message to the redux store. This will add it global to the global map as well as add the
 * id to the specific chat type.
 */
function applyFullMessage(state, message) {
    // Add the original message to the global map.
    const newState = {
        ...state,
        allMessagesByID: {
            ...state.allMessagesByID,
            [message.id]: message,
        },
    };
    // Now add the full message ID to the specific ChatMessagesState but only if it's a new message.
    if (!state.allMessagesByID[message.id]) {
        const currentMessageIDs = state.botMessageState.messageIDs;
        const newMessageIDs = [...currentMessageIDs, message.id];
        return applyBotMessageState(newState, { messageIDs: newMessageIDs });
    }
    return newState;
}
/**
 * Applies a change to the current input state. This will determine which input state should be updated based on whether
 * the user is connected to an agent or not.
 */
function applyInputState(state, newInputState, isInputToAgent) {
    if (isInputToAgent) {
        return {
            ...state,
            agentState: {
                ...state.agentState,
                inputState: newInputState,
            },
        };
    }
    return {
        ...state,
        botInputState: newInputState,
    };
}
/**
 * Sets the give property of the {@link LocalMessageUIState} associated with the message of the given ID to the
 * given value.
 *
 * @param state The current state to change.
 * @param localMessageID The ID of the message to update.
 * @param propertyName The name of the property to update.
 * @param propertyValue The value to set on the property.
 */
function applyLocalMessageUIState(state, localMessageID, propertyName, propertyValue) {
    const oldMessage = state.allMessageItemsByID[localMessageID];
    if (oldMessage) {
        return {
            ...state,
            allMessageItemsByID: {
                ...state.allMessageItemsByID,
                [localMessageID]: {
                    ...oldMessage,
                    ui_state: {
                        ...oldMessage.ui_state,
                        [propertyName]: propertyValue,
                    },
                },
            },
        };
    }
    return state;
}
/**
 * Returns the given input state.
 */
function getInputState(state, isInputToAgent) {
    return isInputToAgent ? state.agentState.inputState : state.botInputState;
}
// Merge in the other reducers.
Object.assign(reducers, agentReducers, alternateSuggestionsReducers);

function doCreateStore(config, serviceManager) {
    const { initConfig } = config.remote;
    // Determine the value for useAITheme.
    let useAITheme;
    if (config.public.themeConfig?.useAITheme !== undefined) {
        // If a value is set in the public config then use that.
        useAITheme = config.public.themeConfig?.useAITheme;
    }
    else if (initConfig.theme_config?.use_ai_theme !== undefined) {
        // Otherwise if a value is set in the remote config then use that.
        useAITheme = initConfig.theme_config?.use_ai_theme;
    }
    else {
        // If neither config is setting a value than use the default.
        useAITheme = DEFAULT_THEME_STATE.useAITheme;
    }
    // The theme state uses a default for each property which can be overridden by the public config if specified. If a
    // value for the property is not specified in the public config, then the default can be overridden by the remote
    // config.
    const themeState = {
        carbonTheme: config.public.themeConfig?.carbonTheme ||
            initConfig.theme_config?.carbon_theme ||
            DEFAULT_THEME_STATE.carbonTheme,
        useAITheme,
        corners: getThemeCornersType(config),
    };
    const botName = getBotName(themeState.useAITheme, config);
    const initialState = {
        ...DEFAULT_MESSAGE_STATE,
        notifications: [],
        botInputState: {
            ...DEFAULT_INPUT_STATE(),
            isReadonly: config.public.__ibm__.isReadonly,
            fieldVisible: !config.public.__ibm__.isReadonly,
        },
        agentState: { ...DEFAULT_AGENT_STATE },
        botName,
        headerDisplayName: useAITheme ? null : botName,
        botAvatarURL: config.public.__ibm__?.botAvatarURLOverride || initConfig.botAvatarURL || null,
        headerAvatarConfig: null,
        chatWidthBreakpoint: null,
        chatWidth: null,
        chatHeight: null,
        // Any IBM set variables will override variables coming from remote. We keep this in redux so we can track the
        // current state of the theming variables as they are updated and merged at different times.
        cssVariableOverrides: mergeCSSVariables(config.public.__ibm__?.cssVariables, initConfig.css_variables, themeState.carbonTheme, themeState.useAITheme),
        isHydrated: false,
        // The language pack will start as English. If a different language pack is provided or updated, it will be
        // merged in with a redux action.
        languagePack: enLanguagePack,
        locale: 'en',
        config,
        originalConfig: config,
        accountOwnerDomain: initConfig.accountOwnerDomain || initConfig.account_owner_domain || '',
        recentEditorDomain: initConfig.recentEditorDomain || '',
        userID: null,
        identityToken: null,
        alternateSuggestionsState: merge({}, DEFAULT_ALTERNATE_SUGGESTIONS_STATE, {
            config: initConfig.alternate_suggestions,
        }),
        suspendScrollDetection: false,
        homeScreenConfig: mergeHomeScreenConfig(config.public.__ibm__?.homeScreenConfig, withoutEmptyStarters(initConfig.home_screen)),
        persistedToBrowserStorage: {
            ...DEFAULT_PERSISTED_TO_BROWSER,
            chatState: {
                ...DEFAULT_PERSISTED_TO_BROWSER.chatState,
                sessionID: config.public.sessionID,
                homeScreenState: {
                    ...DEFAULT_PERSISTED_TO_BROWSER.chatState.homeScreenState,
                    // Home Screen should be closed on a channel transfer.
                    isHomeScreenOpen: isChannelTransferPage()
                        ? false
                        : config.public.__ibm__?.homeScreenConfig?.is_on || initConfig.home_screen?.is_on,
                },
            },
        },
        pendingLogs: {
            trackEvents: [],
            eventMessages: [],
        },
        brandingState: merge({}, DEFAULT_BRANDING_STATE, {
            config: initConfig.branding_config,
        }),
        launcher: merge({}, DEFAULT_LAUNCHER, {
            config: merge({}, initConfig.launcher_config, {
                mobile: merge({}, config.public.__ibm__.launcherExtendedConfig),
            }, { is_on: config.public.showLauncher }),
        }),
        iFramePanelState: DEFAULT_IFRAME_PANEL_STATE,
        viewSourcePanelState: DEFAULT_CITATION_PANEL_STATE,
        isDestroyed: false,
        customPanelState: DEFAULT_CUSTOM_PANEL_STATE,
        viewChanging: false,
        initialViewChangeComplete: false,
        targetViewState: 
        // If openChatByDefault is set to true then the web chat should open automatically. This value will be overridden
        // by session history if a session exists. This overwriting is intentional since we only want openChatByDefault to
        // open the main window the first time the chat loads for a user.
        config.public.openChatByDefault ? VIEW_STATE_MAIN_WINDOW_OPEN : VIEW_STATE_LAUNCHER_OPEN,
        responsePanelState: DEFAULT_MESSAGE_PANEL_STATE,
        customMenuOptions: null,
        tourState: DEFAULT_TOUR_STATE,
        isBrowserPageVisible: true,
        showNonHeaderBackgroundCover: false,
        theme: themeState,
        // The streaming state uses a default for the enabled property which can be overridden by the public config if
        // specified. If a value for the property is not specified in the public config than the default can be overridden
        // by the remote config.
        streaming: merge({}, DEFAULT_STREAMING_STATE, {
            enabled: config.public.streaming?.disabled !== undefined
                ? config.public.streaming?.disabled === false
                : initConfig.streaming?.is_on,
        }),
        loadOrchestrateMFEs: config.public.region?.includes(AWS_REGION_FRAGMENT) ||
            config.public.region?.includes(WXO_CLOUD_REGION_FRAGMENT) ||
            Boolean(config.public.orchestratePrivateHostURL),
        layout: getLayoutState(config),
        chatHeaderState: {
            config: null,
            maxVisibleHeaderObjects: 0,
        },
    };
    // Go pre-fill the launcher state from session storage if it exists. We only do launcherState here because chatState
    // requires a userID and we don't know if we have one at this point. Chat state is loaded from session storage in the
    // copyToSessionStorage subscription once we learn the userID.
    const sessionStorageLauncherState = !config.public.disableSessionHistory
        ? serviceManager.userSessionStorageService?.loadLauncherSession(config.public.integrationID)
        : null;
    if (sessionStorageLauncherState) {
        // Use the viewState from session storage as the targetViewState. Note, this overwrites the value that was set for
        // targetViewState above, which took into account if openChatByDefault is true. This overwriting is intentional
        // since we only want those openChatByDefault to open the main window the first time the chat loads for a user.
        // After doCreateStore is finished Chat.startInternal() will try to change the view to this
        // targetViewState.
        initialState.targetViewState = sessionStorageLauncherState.viewState;
        // In order to keep the initial view state as the default view state we need to change the session storage
        // view state to the default before replacing the launcher state with the session storage state.
        sessionStorageLauncherState.viewState = VIEW_STATE_ALL_CLOSED;
        // Replace the launcher state with the session storage state.
        initialState.persistedToBrowserStorage.launcherState = sessionStorageLauncherState;
    }
    const enhancer = config.public.debug || NODE_ENV === 'development'
        ? window.__REDUX_DEVTOOLS_EXTENSION__ &&
            window.__REDUX_DEVTOOLS_EXTENSION__({
                name: config.asCarbonChat ? 'CarbonAIChat' : 'watsonXAIChat',
                instanceId: `Chat${serviceManager.namespace.suffix}`,
            })
        : undefined;
    if (config.public.wxo?.getAssistantList || config.public.wxo?.getMentionList) {
        installWXOServices(config.public, serviceManager);
    }
    return createStore(reducerFunction, initialState, enhancer);
}
/**
 * Returns the corner type for the web chat widget.
 */
function getThemeCornersType(config) {
    if (getLayoutState(config).showFrame === false ||
        IS_PHONE ||
        config.public.__ibm__?.disableRoundCorners ||
        config.public.themeConfig?.corners === CornersType.SQUARE) {
        return CornersType.SQUARE;
    }
    return DEFAULT_THEME_STATE.corners;
}
function getLayoutState(config) {
    if (config.public.themeConfig?.useAITheme) {
        return {
            showFrame: config.public.layout?.showFrame ?? true,
            hasContentMaxWidth: config.public.layout?.hasContentMaxWidth ?? true,
        };
    }
    return merge({}, DEFAULT_LAYOUT_STATE, config.public.layout);
}
/**
 * This is the global reducer for the redux store. It will use the map of reducers from the "reducers" array to map
 * the action type to the sub-reducer for that specific action.
 */
function reducerFunction(state, action) {
    return action && reducers[action.type] ? reducers[action.type](state, action) : state;
}

/**
 * Returns the instance of fetch that can be used to make fetch calls.
 */
function getRealFetch() {
    return fetchWrapper(window.fetch.bind(window));
}
// A sentinel to keep track if a function has been called.
const NOT_CALLED = {};
/**
 * The real fetch does not allow you to call text() or json() more than once. The stream is only readable once. This
 * function will create a wrapper around fetch that saves the response so they can be read more than once.
 */
function fetchWrapper(realFetch) {
    const wrapper = async (...fetchArgs) => {
        const response = await realFetch(...fetchArgs);
        const originalText = response.text.bind(response);
        let capturedText = NOT_CALLED;
        response.text = async () => {
            if (capturedText === NOT_CALLED) {
                capturedText = await originalText();
            }
            return capturedText;
        };
        response.json = async () => {
            const text = await response.text();
            return JSON.parse(text);
        };
        return response;
    };
    return wrapper;
}

/**
 * This file contains the code needed to bootstrap all the shared services in web chat. Services are used to hold
 * functions that are used throughout the application that need access to the current instance of the web chat.
 */
async function createServiceManager(appConfig, additionalChatParameters) {
    const publicConfig = appConfig.public;
    const ibmConfig = publicConfig.__ibm__ || {};
    const serviceManager = new ServiceManager();
    const isTrackingEnabled = Boolean(ibmConfig.isTrackingEnabled && publicConfig.integrationID);
    // Create all the services we will be using.
    serviceManager.fetch = ibmConfig.fetch || additionalChatParameters?.fetch || getRealFetch();
    serviceManager.additionalChatParameters = additionalChatParameters;
    serviceManager.namespace = new NamespaceService(publicConfig.namespace);
    serviceManager.userSessionStorageService = new UserSessionStorageService(serviceManager);
    serviceManager.actions = new ChatActionsImpl(serviceManager);
    serviceManager.eventBus = new EventBus();
    serviceManager.store = doCreateStore(appConfig, serviceManager);
    serviceManager.trackService = new TrackService(serviceManager, isTrackingEnabled, 
    // If it's the agent app, we want to get an identity call, but not all the events.
    isTrackingEnabled && !publicConfig.agentAppConfig.is_on, getAnalyticsPath(publicConfig));
    serviceManager.historyService = new HistoryService(serviceManager);
    serviceManager.messageService = new MessageService(serviceManager, publicConfig);
    serviceManager.store.subscribe(copyToSessionStorage(serviceManager));
    serviceManager.store.subscribe(createHandleAlternateSuggestions(serviceManager));
    serviceManager.store.subscribe(createHandleTrackEvents(serviceManager));
    if (!publicConfig.disableWindowTitleChanges) {
        serviceManager.store.subscribe(createHandleWindowTitle(serviceManager));
    }
    serviceManager.enableUICustomEvents = new Set(ibmConfig.enableUICustomEvents || []);
    serviceManager.customPanelManager = createCustomPanelManager(serviceManager);
    setIntl(serviceManager, serviceManager.store.getState().locale, serviceManager.store.getState().languagePack);
    const initialState = serviceManager.store.getState();
    if (initialState.config.public.identityToken) {
        serviceManager.actions.updateIdentityToken(initialState.config.public.identityToken);
    }
    serviceManager.messageService.localeIsExplicit = Boolean(ibmConfig.locale);
    // Create all custom elements for Deb.
    serviceManager.writeableElements = {
        [WriteableElementName.AI_TOOLTIP_AFTER_DESCRIPTION_ELEMENT]: document.createElement('div'),
        [WriteableElementName.WELCOME_NODE_BEFORE_ELEMENT]: document.createElement('div'),
        [WriteableElementName.HEADER_BOTTOM_ELEMENT]: document.createElement('div'),
        [WriteableElementName.BEFORE_INPUT_ELEMENT]: document.createElement('div'),
        [WriteableElementName.HOME_SCREEN_HEADER_BOTTOM_ELEMENT]: document.createElement('div'),
        [WriteableElementName.HOME_SCREEN_AFTER_STARTERS_ELEMENT]: document.createElement('div'),
        [WriteableElementName.HOME_SCREEN_BEFORE_INPUT_ELEMENT]: document.createElement('div'),
    };
    if (initialState.config.public.__ibm__?.useShadowRoot) {
        serviceManager.writeableElements[WriteableElementName.CUSTOM_PANEL_ELEMENT] = document.createElement('div');
    }
    if (publicConfig.debug) {
        setEnableDebugLog(true);
    }
    if (publicConfig.debugStackTraces) {
        setEnableDebugStackTracesLog(true);
    }
    return serviceManager;
}
/**
 * A simple utility function to set the intl object on the given service manager.
 */
function setIntl(serviceManager, locale, messages) {
    serviceManager.intl = createIntl({ locale, messages });
    serviceManager.store.dispatch(actions.setAppStateValue('languagePack', messages));
    serviceManager.store.dispatch(actions.setAppStateValue('locale', locale));
}

var en$1 = {exports: {}};

(function (module, exports) {
	!function(e,n){module.exports=n();}(commonjsGlobal$1,(function(){return {name:"en",weekdays:"Sunday_Monday_Tuesday_Wednesday_Thursday_Friday_Saturday".split("_"),months:"January_February_March_April_May_June_July_August_September_October_November_December".split("_"),ordinal:function(e){var n=["th","st","nd","rd"],t=e%100;return "["+e+(n[(t-20)%10]||n[t]||n[0])+"]"}}})); 
} (en$1));

var enExports = en$1.exports;
var enLocaleData = /*@__PURE__*/getDefaultExportFromCjs$1(enExports);

var en = /*#__PURE__*/_mergeNamespaces({
  __proto__: null,
  default: enLocaleData
}, [enExports]);

//
// Main
//
function memoize(fn, options) {
    var cache = options && options.cache ? options.cache : cacheDefault;
    var serializer = options && options.serializer ? options.serializer : serializerDefault;
    var strategy = options && options.strategy ? options.strategy : strategyDefault;
    return strategy(fn, {
        cache: cache,
        serializer: serializer,
    });
}
//
// Strategy
//
function isPrimitive(value) {
    return (value == null || typeof value === 'number' || typeof value === 'boolean'); // || typeof value === "string" 'unsafe' primitive for our needs
}
function monadic(fn, cache, serializer, arg) {
    var cacheKey = isPrimitive(arg) ? arg : serializer(arg);
    var computedValue = cache.get(cacheKey);
    if (typeof computedValue === 'undefined') {
        computedValue = fn.call(this, arg);
        cache.set(cacheKey, computedValue);
    }
    return computedValue;
}
function variadic(fn, cache, serializer) {
    var args = Array.prototype.slice.call(arguments, 3);
    var cacheKey = serializer(args);
    var computedValue = cache.get(cacheKey);
    if (typeof computedValue === 'undefined') {
        computedValue = fn.apply(this, args);
        cache.set(cacheKey, computedValue);
    }
    return computedValue;
}
function assemble(fn, context, strategy, cache, serialize) {
    return strategy.bind(context, fn, cache, serialize);
}
function strategyDefault(fn, options) {
    var strategy = fn.length === 1 ? monadic : variadic;
    return assemble(fn, this, strategy, options.cache.create(), options.serializer);
}
function strategyVariadic(fn, options) {
    return assemble(fn, this, variadic, options.cache.create(), options.serializer);
}
function strategyMonadic(fn, options) {
    return assemble(fn, this, monadic, options.cache.create(), options.serializer);
}
//
// Serializer
//
var serializerDefault = function () {
    return JSON.stringify(arguments);
};
//
// Cache
//
var ObjectWithoutPrototypeCache = /** @class */ (function () {
    function ObjectWithoutPrototypeCache() {
        this.cache = Object.create(null);
    }
    ObjectWithoutPrototypeCache.prototype.get = function (key) {
        return this.cache[key];
    };
    ObjectWithoutPrototypeCache.prototype.set = function (key, value) {
        this.cache[key] = value;
    };
    return ObjectWithoutPrototypeCache;
}());
var cacheDefault = {
    create: function create() {
        return new ObjectWithoutPrototypeCache();
    },
};
var strategies = {
    variadic: strategyVariadic,
    monadic: strategyMonadic,
};

var ErrorKind;
(function (ErrorKind) {
    /** Argument is unclosed (e.g. `{0`) */
    ErrorKind[ErrorKind["EXPECT_ARGUMENT_CLOSING_BRACE"] = 1] = "EXPECT_ARGUMENT_CLOSING_BRACE";
    /** Argument is empty (e.g. `{}`). */
    ErrorKind[ErrorKind["EMPTY_ARGUMENT"] = 2] = "EMPTY_ARGUMENT";
    /** Argument is malformed (e.g. `{foo!}``) */
    ErrorKind[ErrorKind["MALFORMED_ARGUMENT"] = 3] = "MALFORMED_ARGUMENT";
    /** Expect an argument type (e.g. `{foo,}`) */
    ErrorKind[ErrorKind["EXPECT_ARGUMENT_TYPE"] = 4] = "EXPECT_ARGUMENT_TYPE";
    /** Unsupported argument type (e.g. `{foo,foo}`) */
    ErrorKind[ErrorKind["INVALID_ARGUMENT_TYPE"] = 5] = "INVALID_ARGUMENT_TYPE";
    /** Expect an argument style (e.g. `{foo, number, }`) */
    ErrorKind[ErrorKind["EXPECT_ARGUMENT_STYLE"] = 6] = "EXPECT_ARGUMENT_STYLE";
    /** The number skeleton is invalid. */
    ErrorKind[ErrorKind["INVALID_NUMBER_SKELETON"] = 7] = "INVALID_NUMBER_SKELETON";
    /** The date time skeleton is invalid. */
    ErrorKind[ErrorKind["INVALID_DATE_TIME_SKELETON"] = 8] = "INVALID_DATE_TIME_SKELETON";
    /** Exepct a number skeleton following the `::` (e.g. `{foo, number, ::}`) */
    ErrorKind[ErrorKind["EXPECT_NUMBER_SKELETON"] = 9] = "EXPECT_NUMBER_SKELETON";
    /** Exepct a date time skeleton following the `::` (e.g. `{foo, date, ::}`) */
    ErrorKind[ErrorKind["EXPECT_DATE_TIME_SKELETON"] = 10] = "EXPECT_DATE_TIME_SKELETON";
    /** Unmatched apostrophes in the argument style (e.g. `{foo, number, 'test`) */
    ErrorKind[ErrorKind["UNCLOSED_QUOTE_IN_ARGUMENT_STYLE"] = 11] = "UNCLOSED_QUOTE_IN_ARGUMENT_STYLE";
    /** Missing select argument options (e.g. `{foo, select}`) */
    ErrorKind[ErrorKind["EXPECT_SELECT_ARGUMENT_OPTIONS"] = 12] = "EXPECT_SELECT_ARGUMENT_OPTIONS";
    /** Expecting an offset value in `plural` or `selectordinal` argument (e.g `{foo, plural, offset}`) */
    ErrorKind[ErrorKind["EXPECT_PLURAL_ARGUMENT_OFFSET_VALUE"] = 13] = "EXPECT_PLURAL_ARGUMENT_OFFSET_VALUE";
    /** Offset value in `plural` or `selectordinal` is invalid (e.g. `{foo, plural, offset: x}`) */
    ErrorKind[ErrorKind["INVALID_PLURAL_ARGUMENT_OFFSET_VALUE"] = 14] = "INVALID_PLURAL_ARGUMENT_OFFSET_VALUE";
    /** Expecting a selector in `select` argument (e.g `{foo, select}`) */
    ErrorKind[ErrorKind["EXPECT_SELECT_ARGUMENT_SELECTOR"] = 15] = "EXPECT_SELECT_ARGUMENT_SELECTOR";
    /** Expecting a selector in `plural` or `selectordinal` argument (e.g `{foo, plural}`) */
    ErrorKind[ErrorKind["EXPECT_PLURAL_ARGUMENT_SELECTOR"] = 16] = "EXPECT_PLURAL_ARGUMENT_SELECTOR";
    /** Expecting a message fragment after the `select` selector (e.g. `{foo, select, apple}`) */
    ErrorKind[ErrorKind["EXPECT_SELECT_ARGUMENT_SELECTOR_FRAGMENT"] = 17] = "EXPECT_SELECT_ARGUMENT_SELECTOR_FRAGMENT";
    /**
     * Expecting a message fragment after the `plural` or `selectordinal` selector
     * (e.g. `{foo, plural, one}`)
     */
    ErrorKind[ErrorKind["EXPECT_PLURAL_ARGUMENT_SELECTOR_FRAGMENT"] = 18] = "EXPECT_PLURAL_ARGUMENT_SELECTOR_FRAGMENT";
    /** Selector in `plural` or `selectordinal` is malformed (e.g. `{foo, plural, =x {#}}`) */
    ErrorKind[ErrorKind["INVALID_PLURAL_ARGUMENT_SELECTOR"] = 19] = "INVALID_PLURAL_ARGUMENT_SELECTOR";
    /**
     * Duplicate selectors in `plural` or `selectordinal` argument.
     * (e.g. {foo, plural, one {#} one {#}})
     */
    ErrorKind[ErrorKind["DUPLICATE_PLURAL_ARGUMENT_SELECTOR"] = 20] = "DUPLICATE_PLURAL_ARGUMENT_SELECTOR";
    /** Duplicate selectors in `select` argument.
     * (e.g. {foo, select, apple {apple} apple {apple}})
     */
    ErrorKind[ErrorKind["DUPLICATE_SELECT_ARGUMENT_SELECTOR"] = 21] = "DUPLICATE_SELECT_ARGUMENT_SELECTOR";
    /** Plural or select argument option must have `other` clause. */
    ErrorKind[ErrorKind["MISSING_OTHER_CLAUSE"] = 22] = "MISSING_OTHER_CLAUSE";
    /** The tag is malformed. (e.g. `<bold!>foo</bold!>) */
    ErrorKind[ErrorKind["INVALID_TAG"] = 23] = "INVALID_TAG";
    /** The tag name is invalid. (e.g. `<123>foo</123>`) */
    ErrorKind[ErrorKind["INVALID_TAG_NAME"] = 25] = "INVALID_TAG_NAME";
    /** The closing tag does not match the opening tag. (e.g. `<bold>foo</italic>`) */
    ErrorKind[ErrorKind["UNMATCHED_CLOSING_TAG"] = 26] = "UNMATCHED_CLOSING_TAG";
    /** The opening tag has unmatched closing tag. (e.g. `<bold>foo`) */
    ErrorKind[ErrorKind["UNCLOSED_TAG"] = 27] = "UNCLOSED_TAG";
})(ErrorKind || (ErrorKind = {}));

var TYPE;
(function (TYPE) {
    /**
     * Raw text
     */
    TYPE[TYPE["literal"] = 0] = "literal";
    /**
     * Variable w/o any format, e.g `var` in `this is a {var}`
     */
    TYPE[TYPE["argument"] = 1] = "argument";
    /**
     * Variable w/ number format
     */
    TYPE[TYPE["number"] = 2] = "number";
    /**
     * Variable w/ date format
     */
    TYPE[TYPE["date"] = 3] = "date";
    /**
     * Variable w/ time format
     */
    TYPE[TYPE["time"] = 4] = "time";
    /**
     * Variable w/ select format
     */
    TYPE[TYPE["select"] = 5] = "select";
    /**
     * Variable w/ plural format
     */
    TYPE[TYPE["plural"] = 6] = "plural";
    /**
     * Only possible within plural argument.
     * This is the `#` symbol that will be substituted with the count.
     */
    TYPE[TYPE["pound"] = 7] = "pound";
    /**
     * XML-like tag
     */
    TYPE[TYPE["tag"] = 8] = "tag";
})(TYPE || (TYPE = {}));
var SKELETON_TYPE;
(function (SKELETON_TYPE) {
    SKELETON_TYPE[SKELETON_TYPE["number"] = 0] = "number";
    SKELETON_TYPE[SKELETON_TYPE["dateTime"] = 1] = "dateTime";
})(SKELETON_TYPE || (SKELETON_TYPE = {}));
/**
 * Type Guards
 */
function isLiteralElement(el) {
    return el.type === TYPE.literal;
}
function isArgumentElement(el) {
    return el.type === TYPE.argument;
}
function isNumberElement(el) {
    return el.type === TYPE.number;
}
function isDateElement(el) {
    return el.type === TYPE.date;
}
function isTimeElement(el) {
    return el.type === TYPE.time;
}
function isSelectElement(el) {
    return el.type === TYPE.select;
}
function isPluralElement(el) {
    return el.type === TYPE.plural;
}
function isPoundElement(el) {
    return el.type === TYPE.pound;
}
function isTagElement(el) {
    return el.type === TYPE.tag;
}
function isNumberSkeleton(el) {
    return !!(el && typeof el === 'object' && el.type === SKELETON_TYPE.number);
}
function isDateTimeSkeleton(el) {
    return !!(el && typeof el === 'object' && el.type === SKELETON_TYPE.dateTime);
}

// @generated from regex-gen.ts
var SPACE_SEPARATOR_REGEX = /[ \xA0\u1680\u2000-\u200A\u202F\u205F\u3000]/;

/**
 * https://unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * Credit: https://github.com/caridy/intl-datetimeformat-pattern/blob/master/index.js
 * with some tweaks
 */
var DATE_TIME_REGEX = /(?:[Eec]{1,6}|G{1,5}|[Qq]{1,5}|(?:[yYur]+|U{1,5})|[ML]{1,5}|d{1,2}|D{1,3}|F{1}|[abB]{1,5}|[hkHK]{1,2}|w{1,2}|W{1}|m{1,2}|s{1,2}|[zZOvVxX]{1,4})(?=([^']*'[^']*')*[^']*$)/g;
/**
 * Parse Date time skeleton into Intl.DateTimeFormatOptions
 * Ref: https://unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * @public
 * @param skeleton skeleton string
 */
function parseDateTimeSkeleton(skeleton) {
    var result = {};
    skeleton.replace(DATE_TIME_REGEX, function (match) {
        var len = match.length;
        switch (match[0]) {
            // Era
            case 'G':
                result.era = len === 4 ? 'long' : len === 5 ? 'narrow' : 'short';
                break;
            // Year
            case 'y':
                result.year = len === 2 ? '2-digit' : 'numeric';
                break;
            case 'Y':
            case 'u':
            case 'U':
            case 'r':
                throw new RangeError('`Y/u/U/r` (year) patterns are not supported, use `y` instead');
            // Quarter
            case 'q':
            case 'Q':
                throw new RangeError('`q/Q` (quarter) patterns are not supported');
            // Month
            case 'M':
            case 'L':
                result.month = ['numeric', '2-digit', 'short', 'long', 'narrow'][len - 1];
                break;
            // Week
            case 'w':
            case 'W':
                throw new RangeError('`w/W` (week) patterns are not supported');
            case 'd':
                result.day = ['numeric', '2-digit'][len - 1];
                break;
            case 'D':
            case 'F':
            case 'g':
                throw new RangeError('`D/F/g` (day) patterns are not supported, use `d` instead');
            // Weekday
            case 'E':
                result.weekday = len === 4 ? 'long' : len === 5 ? 'narrow' : 'short';
                break;
            case 'e':
                if (len < 4) {
                    throw new RangeError('`e..eee` (weekday) patterns are not supported');
                }
                result.weekday = ['short', 'long', 'narrow', 'short'][len - 4];
                break;
            case 'c':
                if (len < 4) {
                    throw new RangeError('`c..ccc` (weekday) patterns are not supported');
                }
                result.weekday = ['short', 'long', 'narrow', 'short'][len - 4];
                break;
            // Period
            case 'a': // AM, PM
                result.hour12 = true;
                break;
            case 'b': // am, pm, noon, midnight
            case 'B': // flexible day periods
                throw new RangeError('`b/B` (period) patterns are not supported, use `a` instead');
            // Hour
            case 'h':
                result.hourCycle = 'h12';
                result.hour = ['numeric', '2-digit'][len - 1];
                break;
            case 'H':
                result.hourCycle = 'h23';
                result.hour = ['numeric', '2-digit'][len - 1];
                break;
            case 'K':
                result.hourCycle = 'h11';
                result.hour = ['numeric', '2-digit'][len - 1];
                break;
            case 'k':
                result.hourCycle = 'h24';
                result.hour = ['numeric', '2-digit'][len - 1];
                break;
            case 'j':
            case 'J':
            case 'C':
                throw new RangeError('`j/J/C` (hour) patterns are not supported, use `h/H/K/k` instead');
            // Minute
            case 'm':
                result.minute = ['numeric', '2-digit'][len - 1];
                break;
            // Second
            case 's':
                result.second = ['numeric', '2-digit'][len - 1];
                break;
            case 'S':
            case 'A':
                throw new RangeError('`S/A` (second) patterns are not supported, use `s` instead');
            // Zone
            case 'z': // 1..3, 4: specific non-location format
                result.timeZoneName = len < 4 ? 'short' : 'long';
                break;
            case 'Z': // 1..3, 4, 5: The ISO8601 varios formats
            case 'O': // 1, 4: milliseconds in day short, long
            case 'v': // 1, 4: generic non-location format
            case 'V': // 1, 2, 3, 4: time zone ID or city
            case 'X': // 1, 2, 3, 4: The ISO8601 varios formats
            case 'x': // 1, 2, 3, 4: The ISO8601 varios formats
                throw new RangeError('`Z/O/v/V/X/x` (timeZone) patterns are not supported, use `z` instead');
        }
        return '';
    });
    return result;
}

// @generated from regex-gen.ts
var WHITE_SPACE_REGEX = /[\t-\r \x85\u200E\u200F\u2028\u2029]/i;

function parseNumberSkeletonFromString(skeleton) {
    if (skeleton.length === 0) {
        throw new Error('Number skeleton cannot be empty');
    }
    // Parse the skeleton
    var stringTokens = skeleton
        .split(WHITE_SPACE_REGEX)
        .filter(function (x) { return x.length > 0; });
    var tokens = [];
    for (var _i = 0, stringTokens_1 = stringTokens; _i < stringTokens_1.length; _i++) {
        var stringToken = stringTokens_1[_i];
        var stemAndOptions = stringToken.split('/');
        if (stemAndOptions.length === 0) {
            throw new Error('Invalid number skeleton');
        }
        var stem = stemAndOptions[0], options = stemAndOptions.slice(1);
        for (var _a = 0, options_1 = options; _a < options_1.length; _a++) {
            var option = options_1[_a];
            if (option.length === 0) {
                throw new Error('Invalid number skeleton');
            }
        }
        tokens.push({ stem: stem, options: options });
    }
    return tokens;
}
function icuUnitToEcma(unit) {
    return unit.replace(/^(.*?)-/, '');
}
var FRACTION_PRECISION_REGEX = /^\.(?:(0+)(\*)?|(#+)|(0+)(#+))$/g;
var SIGNIFICANT_PRECISION_REGEX = /^(@+)?(\+|#+)?[rs]?$/g;
var INTEGER_WIDTH_REGEX = /(\*)(0+)|(#+)(0+)|(0+)/g;
var CONCISE_INTEGER_WIDTH_REGEX = /^(0+)$/;
function parseSignificantPrecision(str) {
    var result = {};
    if (str[str.length - 1] === 'r') {
        result.roundingPriority = 'morePrecision';
    }
    else if (str[str.length - 1] === 's') {
        result.roundingPriority = 'lessPrecision';
    }
    str.replace(SIGNIFICANT_PRECISION_REGEX, function (_, g1, g2) {
        // @@@ case
        if (typeof g2 !== 'string') {
            result.minimumSignificantDigits = g1.length;
            result.maximumSignificantDigits = g1.length;
        }
        // @@@+ case
        else if (g2 === '+') {
            result.minimumSignificantDigits = g1.length;
        }
        // .### case
        else if (g1[0] === '#') {
            result.maximumSignificantDigits = g1.length;
        }
        // .@@## or .@@@ case
        else {
            result.minimumSignificantDigits = g1.length;
            result.maximumSignificantDigits =
                g1.length + (typeof g2 === 'string' ? g2.length : 0);
        }
        return '';
    });
    return result;
}
function parseSign(str) {
    switch (str) {
        case 'sign-auto':
            return {
                signDisplay: 'auto',
            };
        case 'sign-accounting':
        case '()':
            return {
                currencySign: 'accounting',
            };
        case 'sign-always':
        case '+!':
            return {
                signDisplay: 'always',
            };
        case 'sign-accounting-always':
        case '()!':
            return {
                signDisplay: 'always',
                currencySign: 'accounting',
            };
        case 'sign-except-zero':
        case '+?':
            return {
                signDisplay: 'exceptZero',
            };
        case 'sign-accounting-except-zero':
        case '()?':
            return {
                signDisplay: 'exceptZero',
                currencySign: 'accounting',
            };
        case 'sign-never':
        case '+_':
            return {
                signDisplay: 'never',
            };
    }
}
function parseConciseScientificAndEngineeringStem(stem) {
    // Engineering
    var result;
    if (stem[0] === 'E' && stem[1] === 'E') {
        result = {
            notation: 'engineering',
        };
        stem = stem.slice(2);
    }
    else if (stem[0] === 'E') {
        result = {
            notation: 'scientific',
        };
        stem = stem.slice(1);
    }
    if (result) {
        var signDisplay = stem.slice(0, 2);
        if (signDisplay === '+!') {
            result.signDisplay = 'always';
            stem = stem.slice(2);
        }
        else if (signDisplay === '+?') {
            result.signDisplay = 'exceptZero';
            stem = stem.slice(2);
        }
        if (!CONCISE_INTEGER_WIDTH_REGEX.test(stem)) {
            throw new Error('Malformed concise eng/scientific notation');
        }
        result.minimumIntegerDigits = stem.length;
    }
    return result;
}
function parseNotationOptions(opt) {
    var result = {};
    var signOpts = parseSign(opt);
    if (signOpts) {
        return signOpts;
    }
    return result;
}
/**
 * https://github.com/unicode-org/icu/blob/master/docs/userguide/format_parse/numbers/skeletons.md#skeleton-stems-and-options
 */
function parseNumberSkeleton(tokens) {
    var result = {};
    for (var _i = 0, tokens_1 = tokens; _i < tokens_1.length; _i++) {
        var token = tokens_1[_i];
        switch (token.stem) {
            case 'percent':
            case '%':
                result.style = 'percent';
                continue;
            case '%x100':
                result.style = 'percent';
                result.scale = 100;
                continue;
            case 'currency':
                result.style = 'currency';
                result.currency = token.options[0];
                continue;
            case 'group-off':
            case ',_':
                result.useGrouping = false;
                continue;
            case 'precision-integer':
            case '.':
                result.maximumFractionDigits = 0;
                continue;
            case 'measure-unit':
            case 'unit':
                result.style = 'unit';
                result.unit = icuUnitToEcma(token.options[0]);
                continue;
            case 'compact-short':
            case 'K':
                result.notation = 'compact';
                result.compactDisplay = 'short';
                continue;
            case 'compact-long':
            case 'KK':
                result.notation = 'compact';
                result.compactDisplay = 'long';
                continue;
            case 'scientific':
                result = __assign(__assign(__assign({}, result), { notation: 'scientific' }), token.options.reduce(function (all, opt) { return (__assign(__assign({}, all), parseNotationOptions(opt))); }, {}));
                continue;
            case 'engineering':
                result = __assign(__assign(__assign({}, result), { notation: 'engineering' }), token.options.reduce(function (all, opt) { return (__assign(__assign({}, all), parseNotationOptions(opt))); }, {}));
                continue;
            case 'notation-simple':
                result.notation = 'standard';
                continue;
            // https://github.com/unicode-org/icu/blob/master/icu4c/source/i18n/unicode/unumberformatter.h
            case 'unit-width-narrow':
                result.currencyDisplay = 'narrowSymbol';
                result.unitDisplay = 'narrow';
                continue;
            case 'unit-width-short':
                result.currencyDisplay = 'code';
                result.unitDisplay = 'short';
                continue;
            case 'unit-width-full-name':
                result.currencyDisplay = 'name';
                result.unitDisplay = 'long';
                continue;
            case 'unit-width-iso-code':
                result.currencyDisplay = 'symbol';
                continue;
            case 'scale':
                result.scale = parseFloat(token.options[0]);
                continue;
            case 'rounding-mode-floor':
                result.roundingMode = 'floor';
                continue;
            case 'rounding-mode-ceiling':
                result.roundingMode = 'ceil';
                continue;
            case 'rounding-mode-down':
                result.roundingMode = 'trunc';
                continue;
            case 'rounding-mode-up':
                result.roundingMode = 'expand';
                continue;
            case 'rounding-mode-half-even':
                result.roundingMode = 'halfEven';
                continue;
            case 'rounding-mode-half-down':
                result.roundingMode = 'halfTrunc';
                continue;
            case 'rounding-mode-half-up':
                result.roundingMode = 'halfExpand';
                continue;
            // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#integer-width
            case 'integer-width':
                if (token.options.length > 1) {
                    throw new RangeError('integer-width stems only accept a single optional option');
                }
                token.options[0].replace(INTEGER_WIDTH_REGEX, function (_, g1, g2, g3, g4, g5) {
                    if (g1) {
                        result.minimumIntegerDigits = g2.length;
                    }
                    else if (g3 && g4) {
                        throw new Error('We currently do not support maximum integer digits');
                    }
                    else if (g5) {
                        throw new Error('We currently do not support exact integer digits');
                    }
                    return '';
                });
                continue;
        }
        // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#integer-width
        if (CONCISE_INTEGER_WIDTH_REGEX.test(token.stem)) {
            result.minimumIntegerDigits = token.stem.length;
            continue;
        }
        if (FRACTION_PRECISION_REGEX.test(token.stem)) {
            // Precision
            // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#fraction-precision
            // precision-integer case
            if (token.options.length > 1) {
                throw new RangeError('Fraction-precision stems only accept a single optional option');
            }
            token.stem.replace(FRACTION_PRECISION_REGEX, function (_, g1, g2, g3, g4, g5) {
                // .000* case (before ICU67 it was .000+)
                if (g2 === '*') {
                    result.minimumFractionDigits = g1.length;
                }
                // .### case
                else if (g3 && g3[0] === '#') {
                    result.maximumFractionDigits = g3.length;
                }
                // .00## case
                else if (g4 && g5) {
                    result.minimumFractionDigits = g4.length;
                    result.maximumFractionDigits = g4.length + g5.length;
                }
                else {
                    result.minimumFractionDigits = g1.length;
                    result.maximumFractionDigits = g1.length;
                }
                return '';
            });
            var opt = token.options[0];
            // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#trailing-zero-display
            if (opt === 'w') {
                result = __assign(__assign({}, result), { trailingZeroDisplay: 'stripIfInteger' });
            }
            else if (opt) {
                result = __assign(__assign({}, result), parseSignificantPrecision(opt));
            }
            continue;
        }
        // https://unicode-org.github.io/icu/userguide/format_parse/numbers/skeletons.html#significant-digits-precision
        if (SIGNIFICANT_PRECISION_REGEX.test(token.stem)) {
            result = __assign(__assign({}, result), parseSignificantPrecision(token.stem));
            continue;
        }
        var signOpts = parseSign(token.stem);
        if (signOpts) {
            result = __assign(__assign({}, result), signOpts);
        }
        var conciseScientificAndEngineeringOpts = parseConciseScientificAndEngineeringStem(token.stem);
        if (conciseScientificAndEngineeringOpts) {
            result = __assign(__assign({}, result), conciseScientificAndEngineeringOpts);
        }
    }
    return result;
}

// @generated from time-data-gen.ts
// prettier-ignore  
var timeData = {
    "001": [
        "H",
        "h"
    ],
    "419": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "AC": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "AD": [
        "H",
        "hB"
    ],
    "AE": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "AF": [
        "H",
        "hb",
        "hB",
        "h"
    ],
    "AG": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "AI": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "AL": [
        "h",
        "H",
        "hB"
    ],
    "AM": [
        "H",
        "hB"
    ],
    "AO": [
        "H",
        "hB"
    ],
    "AR": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "AS": [
        "h",
        "H"
    ],
    "AT": [
        "H",
        "hB"
    ],
    "AU": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "AW": [
        "H",
        "hB"
    ],
    "AX": [
        "H"
    ],
    "AZ": [
        "H",
        "hB",
        "h"
    ],
    "BA": [
        "H",
        "hB",
        "h"
    ],
    "BB": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "BD": [
        "h",
        "hB",
        "H"
    ],
    "BE": [
        "H",
        "hB"
    ],
    "BF": [
        "H",
        "hB"
    ],
    "BG": [
        "H",
        "hB",
        "h"
    ],
    "BH": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "BI": [
        "H",
        "h"
    ],
    "BJ": [
        "H",
        "hB"
    ],
    "BL": [
        "H",
        "hB"
    ],
    "BM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "BN": [
        "hb",
        "hB",
        "h",
        "H"
    ],
    "BO": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "BQ": [
        "H"
    ],
    "BR": [
        "H",
        "hB"
    ],
    "BS": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "BT": [
        "h",
        "H"
    ],
    "BW": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "BY": [
        "H",
        "h"
    ],
    "BZ": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "CA": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "CC": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "CD": [
        "hB",
        "H"
    ],
    "CF": [
        "H",
        "h",
        "hB"
    ],
    "CG": [
        "H",
        "hB"
    ],
    "CH": [
        "H",
        "hB",
        "h"
    ],
    "CI": [
        "H",
        "hB"
    ],
    "CK": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "CL": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "CM": [
        "H",
        "h",
        "hB"
    ],
    "CN": [
        "H",
        "hB",
        "hb",
        "h"
    ],
    "CO": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "CP": [
        "H"
    ],
    "CR": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "CU": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "CV": [
        "H",
        "hB"
    ],
    "CW": [
        "H",
        "hB"
    ],
    "CX": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "CY": [
        "h",
        "H",
        "hb",
        "hB"
    ],
    "CZ": [
        "H"
    ],
    "DE": [
        "H",
        "hB"
    ],
    "DG": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "DJ": [
        "h",
        "H"
    ],
    "DK": [
        "H"
    ],
    "DM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "DO": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "DZ": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "EA": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "EC": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "EE": [
        "H",
        "hB"
    ],
    "EG": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "EH": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "ER": [
        "h",
        "H"
    ],
    "ES": [
        "H",
        "hB",
        "h",
        "hb"
    ],
    "ET": [
        "hB",
        "hb",
        "h",
        "H"
    ],
    "FI": [
        "H"
    ],
    "FJ": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "FK": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "FM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "FO": [
        "H",
        "h"
    ],
    "FR": [
        "H",
        "hB"
    ],
    "GA": [
        "H",
        "hB"
    ],
    "GB": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "GD": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "GE": [
        "H",
        "hB",
        "h"
    ],
    "GF": [
        "H",
        "hB"
    ],
    "GG": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "GH": [
        "h",
        "H"
    ],
    "GI": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "GL": [
        "H",
        "h"
    ],
    "GM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "GN": [
        "H",
        "hB"
    ],
    "GP": [
        "H",
        "hB"
    ],
    "GQ": [
        "H",
        "hB",
        "h",
        "hb"
    ],
    "GR": [
        "h",
        "H",
        "hb",
        "hB"
    ],
    "GT": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "GU": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "GW": [
        "H",
        "hB"
    ],
    "GY": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "HK": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "HN": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "HR": [
        "H",
        "hB"
    ],
    "HU": [
        "H",
        "h"
    ],
    "IC": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "ID": [
        "H"
    ],
    "IE": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "IL": [
        "H",
        "hB"
    ],
    "IM": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "IN": [
        "h",
        "H"
    ],
    "IO": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "IQ": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "IR": [
        "hB",
        "H"
    ],
    "IS": [
        "H"
    ],
    "IT": [
        "H",
        "hB"
    ],
    "JE": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "JM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "JO": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "JP": [
        "H",
        "K",
        "h"
    ],
    "KE": [
        "hB",
        "hb",
        "H",
        "h"
    ],
    "KG": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "KH": [
        "hB",
        "h",
        "H",
        "hb"
    ],
    "KI": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "KM": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "KN": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "KP": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "KR": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "KW": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "KY": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "KZ": [
        "H",
        "hB"
    ],
    "LA": [
        "H",
        "hb",
        "hB",
        "h"
    ],
    "LB": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "LC": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "LI": [
        "H",
        "hB",
        "h"
    ],
    "LK": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "LR": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "LS": [
        "h",
        "H"
    ],
    "LT": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "LU": [
        "H",
        "h",
        "hB"
    ],
    "LV": [
        "H",
        "hB",
        "hb",
        "h"
    ],
    "LY": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "MA": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "MC": [
        "H",
        "hB"
    ],
    "MD": [
        "H",
        "hB"
    ],
    "ME": [
        "H",
        "hB",
        "h"
    ],
    "MF": [
        "H",
        "hB"
    ],
    "MG": [
        "H",
        "h"
    ],
    "MH": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "MK": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "ML": [
        "H"
    ],
    "MM": [
        "hB",
        "hb",
        "H",
        "h"
    ],
    "MN": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "MO": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "MP": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "MQ": [
        "H",
        "hB"
    ],
    "MR": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "MS": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "MT": [
        "H",
        "h"
    ],
    "MU": [
        "H",
        "h"
    ],
    "MV": [
        "H",
        "h"
    ],
    "MW": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "MX": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "MY": [
        "hb",
        "hB",
        "h",
        "H"
    ],
    "MZ": [
        "H",
        "hB"
    ],
    "NA": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "NC": [
        "H",
        "hB"
    ],
    "NE": [
        "H"
    ],
    "NF": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "NG": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "NI": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "NL": [
        "H",
        "hB"
    ],
    "NO": [
        "H",
        "h"
    ],
    "NP": [
        "H",
        "h",
        "hB"
    ],
    "NR": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "NU": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "NZ": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "OM": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "PA": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "PE": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "PF": [
        "H",
        "h",
        "hB"
    ],
    "PG": [
        "h",
        "H"
    ],
    "PH": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "PK": [
        "h",
        "hB",
        "H"
    ],
    "PL": [
        "H",
        "h"
    ],
    "PM": [
        "H",
        "hB"
    ],
    "PN": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "PR": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "PS": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "PT": [
        "H",
        "hB"
    ],
    "PW": [
        "h",
        "H"
    ],
    "PY": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "QA": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "RE": [
        "H",
        "hB"
    ],
    "RO": [
        "H",
        "hB"
    ],
    "RS": [
        "H",
        "hB",
        "h"
    ],
    "RU": [
        "H"
    ],
    "RW": [
        "H",
        "h"
    ],
    "SA": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "SB": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "SC": [
        "H",
        "h",
        "hB"
    ],
    "SD": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "SE": [
        "H"
    ],
    "SG": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "SH": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "SI": [
        "H",
        "hB"
    ],
    "SJ": [
        "H"
    ],
    "SK": [
        "H"
    ],
    "SL": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "SM": [
        "H",
        "h",
        "hB"
    ],
    "SN": [
        "H",
        "h",
        "hB"
    ],
    "SO": [
        "h",
        "H"
    ],
    "SR": [
        "H",
        "hB"
    ],
    "SS": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "ST": [
        "H",
        "hB"
    ],
    "SV": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "SX": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "SY": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "SZ": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "TA": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "TC": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "TD": [
        "h",
        "H",
        "hB"
    ],
    "TF": [
        "H",
        "h",
        "hB"
    ],
    "TG": [
        "H",
        "hB"
    ],
    "TH": [
        "H",
        "h"
    ],
    "TJ": [
        "H",
        "h"
    ],
    "TL": [
        "H",
        "hB",
        "hb",
        "h"
    ],
    "TM": [
        "H",
        "h"
    ],
    "TN": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "TO": [
        "h",
        "H"
    ],
    "TR": [
        "H",
        "hB"
    ],
    "TT": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "TW": [
        "hB",
        "hb",
        "h",
        "H"
    ],
    "TZ": [
        "hB",
        "hb",
        "H",
        "h"
    ],
    "UA": [
        "H",
        "hB",
        "h"
    ],
    "UG": [
        "hB",
        "hb",
        "H",
        "h"
    ],
    "UM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "US": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "UY": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "UZ": [
        "H",
        "hB",
        "h"
    ],
    "VA": [
        "H",
        "h",
        "hB"
    ],
    "VC": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "VE": [
        "h",
        "H",
        "hB",
        "hb"
    ],
    "VG": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "VI": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "VN": [
        "H",
        "h"
    ],
    "VU": [
        "h",
        "H"
    ],
    "WF": [
        "H",
        "hB"
    ],
    "WS": [
        "h",
        "H"
    ],
    "XK": [
        "H",
        "hB",
        "h"
    ],
    "YE": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "YT": [
        "H",
        "hB"
    ],
    "ZA": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "ZM": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "ZW": [
        "H",
        "h"
    ],
    "af-ZA": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "ar-001": [
        "h",
        "hB",
        "hb",
        "H"
    ],
    "ca-ES": [
        "H",
        "h",
        "hB"
    ],
    "en-001": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "en-HK": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "en-IL": [
        "H",
        "h",
        "hb",
        "hB"
    ],
    "en-MY": [
        "h",
        "hb",
        "H",
        "hB"
    ],
    "es-BR": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "es-ES": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "es-GQ": [
        "H",
        "h",
        "hB",
        "hb"
    ],
    "fr-CA": [
        "H",
        "h",
        "hB"
    ],
    "gl-ES": [
        "H",
        "h",
        "hB"
    ],
    "gu-IN": [
        "hB",
        "hb",
        "h",
        "H"
    ],
    "hi-IN": [
        "hB",
        "h",
        "H"
    ],
    "it-CH": [
        "H",
        "h",
        "hB"
    ],
    "it-IT": [
        "H",
        "h",
        "hB"
    ],
    "kn-IN": [
        "hB",
        "h",
        "H"
    ],
    "ml-IN": [
        "hB",
        "h",
        "H"
    ],
    "mr-IN": [
        "hB",
        "hb",
        "h",
        "H"
    ],
    "pa-IN": [
        "hB",
        "hb",
        "h",
        "H"
    ],
    "ta-IN": [
        "hB",
        "h",
        "hb",
        "H"
    ],
    "te-IN": [
        "hB",
        "h",
        "H"
    ],
    "zu-ZA": [
        "H",
        "hB",
        "hb",
        "h"
    ]
};

/**
 * Returns the best matching date time pattern if a date time skeleton
 * pattern is provided with a locale. Follows the Unicode specification:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#table-mapping-requested-time-skeletons-to-patterns
 * @param skeleton date time skeleton pattern that possibly includes j, J or C
 * @param locale
 */
function getBestPattern(skeleton, locale) {
    var skeletonCopy = '';
    for (var patternPos = 0; patternPos < skeleton.length; patternPos++) {
        var patternChar = skeleton.charAt(patternPos);
        if (patternChar === 'j') {
            var extraLength = 0;
            while (patternPos + 1 < skeleton.length &&
                skeleton.charAt(patternPos + 1) === patternChar) {
                extraLength++;
                patternPos++;
            }
            var hourLen = 1 + (extraLength & 1);
            var dayPeriodLen = extraLength < 2 ? 1 : 3 + (extraLength >> 1);
            var dayPeriodChar = 'a';
            var hourChar = getDefaultHourSymbolFromLocale(locale);
            if (hourChar == 'H' || hourChar == 'k') {
                dayPeriodLen = 0;
            }
            while (dayPeriodLen-- > 0) {
                skeletonCopy += dayPeriodChar;
            }
            while (hourLen-- > 0) {
                skeletonCopy = hourChar + skeletonCopy;
            }
        }
        else if (patternChar === 'J') {
            skeletonCopy += 'H';
        }
        else {
            skeletonCopy += patternChar;
        }
    }
    return skeletonCopy;
}
/**
 * Maps the [hour cycle type](https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Locale/hourCycle)
 * of the given `locale` to the corresponding time pattern.
 * @param locale
 */
function getDefaultHourSymbolFromLocale(locale) {
    var hourCycle = locale.hourCycle;
    if (hourCycle === undefined &&
        // @ts-ignore hourCycle(s) is not identified yet
        locale.hourCycles &&
        // @ts-ignore
        locale.hourCycles.length) {
        // @ts-ignore
        hourCycle = locale.hourCycles[0];
    }
    if (hourCycle) {
        switch (hourCycle) {
            case 'h24':
                return 'k';
            case 'h23':
                return 'H';
            case 'h12':
                return 'h';
            case 'h11':
                return 'K';
            default:
                throw new Error('Invalid hourCycle');
        }
    }
    // TODO: Once hourCycle is fully supported remove the following with data generation
    var languageTag = locale.language;
    var regionTag;
    if (languageTag !== 'root') {
        regionTag = locale.maximize().region;
    }
    var hourCycles = timeData[regionTag || ''] ||
        timeData[languageTag || ''] ||
        timeData["".concat(languageTag, "-001")] ||
        timeData['001'];
    return hourCycles[0];
}

var _a$1;
var SPACE_SEPARATOR_START_REGEX = new RegExp("^".concat(SPACE_SEPARATOR_REGEX.source, "*"));
var SPACE_SEPARATOR_END_REGEX = new RegExp("".concat(SPACE_SEPARATOR_REGEX.source, "*$"));
function createLocation(start, end) {
    return { start: start, end: end };
}
// #region Ponyfills
// Consolidate these variables up top for easier toggling during debugging
var hasNativeStartsWith = !!String.prototype.startsWith && '_a'.startsWith('a', 1);
var hasNativeFromCodePoint = !!String.fromCodePoint;
var hasNativeFromEntries = !!Object.fromEntries;
var hasNativeCodePointAt = !!String.prototype.codePointAt;
var hasTrimStart = !!String.prototype.trimStart;
var hasTrimEnd = !!String.prototype.trimEnd;
var hasNativeIsSafeInteger = !!Number.isSafeInteger;
var isSafeInteger = hasNativeIsSafeInteger
    ? Number.isSafeInteger
    : function (n) {
        return (typeof n === 'number' &&
            isFinite(n) &&
            Math.floor(n) === n &&
            Math.abs(n) <= 0x1fffffffffffff);
    };
// IE11 does not support y and u.
var REGEX_SUPPORTS_U_AND_Y = true;
try {
    var re = RE('([^\\p{White_Space}\\p{Pattern_Syntax}]*)', 'yu');
    /**
     * legacy Edge or Xbox One browser
     * Unicode flag support: supported
     * Pattern_Syntax support: not supported
     * See https://github.com/formatjs/formatjs/issues/2822
     */
    REGEX_SUPPORTS_U_AND_Y = ((_a$1 = re.exec('a')) === null || _a$1 === void 0 ? void 0 : _a$1[0]) === 'a';
}
catch (_) {
    REGEX_SUPPORTS_U_AND_Y = false;
}
var startsWith$1 = hasNativeStartsWith
    ? // Native
        function startsWith(s, search, position) {
            return s.startsWith(search, position);
        }
    : // For IE11
        function startsWith(s, search, position) {
            return s.slice(position, position + search.length) === search;
        };
var fromCodePoint$2 = hasNativeFromCodePoint
    ? String.fromCodePoint
    : // IE11
        function fromCodePoint() {
            var codePoints = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                codePoints[_i] = arguments[_i];
            }
            var elements = '';
            var length = codePoints.length;
            var i = 0;
            var code;
            while (length > i) {
                code = codePoints[i++];
                if (code > 0x10ffff)
                    throw RangeError(code + ' is not a valid code point');
                elements +=
                    code < 0x10000
                        ? String.fromCharCode(code)
                        : String.fromCharCode(((code -= 0x10000) >> 10) + 0xd800, (code % 0x400) + 0xdc00);
            }
            return elements;
        };
var fromEntries = 
// native
hasNativeFromEntries
    ? Object.fromEntries
    : // Ponyfill
        function fromEntries(entries) {
            var obj = {};
            for (var _i = 0, entries_1 = entries; _i < entries_1.length; _i++) {
                var _a = entries_1[_i], k = _a[0], v = _a[1];
                obj[k] = v;
            }
            return obj;
        };
var codePointAt = hasNativeCodePointAt
    ? // Native
        function codePointAt(s, index) {
            return s.codePointAt(index);
        }
    : // IE 11
        function codePointAt(s, index) {
            var size = s.length;
            if (index < 0 || index >= size) {
                return undefined;
            }
            var first = s.charCodeAt(index);
            var second;
            return first < 0xd800 ||
                first > 0xdbff ||
                index + 1 === size ||
                (second = s.charCodeAt(index + 1)) < 0xdc00 ||
                second > 0xdfff
                ? first
                : ((first - 0xd800) << 10) + (second - 0xdc00) + 0x10000;
        };
var trimStart = hasTrimStart
    ? // Native
        function trimStart(s) {
            return s.trimStart();
        }
    : // Ponyfill
        function trimStart(s) {
            return s.replace(SPACE_SEPARATOR_START_REGEX, '');
        };
var trimEnd = hasTrimEnd
    ? // Native
        function trimEnd(s) {
            return s.trimEnd();
        }
    : // Ponyfill
        function trimEnd(s) {
            return s.replace(SPACE_SEPARATOR_END_REGEX, '');
        };
// Prevent minifier to translate new RegExp to literal form that might cause syntax error on IE11.
function RE(s, flag) {
    return new RegExp(s, flag);
}
// #endregion
var matchIdentifierAtIndex;
if (REGEX_SUPPORTS_U_AND_Y) {
    // Native
    var IDENTIFIER_PREFIX_RE_1 = RE('([^\\p{White_Space}\\p{Pattern_Syntax}]*)', 'yu');
    matchIdentifierAtIndex = function matchIdentifierAtIndex(s, index) {
        var _a;
        IDENTIFIER_PREFIX_RE_1.lastIndex = index;
        var match = IDENTIFIER_PREFIX_RE_1.exec(s);
        return (_a = match[1]) !== null && _a !== void 0 ? _a : '';
    };
}
else {
    // IE11
    matchIdentifierAtIndex = function matchIdentifierAtIndex(s, index) {
        var match = [];
        while (true) {
            var c = codePointAt(s, index);
            if (c === undefined || _isWhiteSpace(c) || _isPatternSyntax(c)) {
                break;
            }
            match.push(c);
            index += c >= 0x10000 ? 2 : 1;
        }
        return fromCodePoint$2.apply(void 0, match);
    };
}
var Parser = /** @class */ (function () {
    function Parser(message, options) {
        if (options === void 0) { options = {}; }
        this.message = message;
        this.position = { offset: 0, line: 1, column: 1 };
        this.ignoreTag = !!options.ignoreTag;
        this.locale = options.locale;
        this.requiresOtherClause = !!options.requiresOtherClause;
        this.shouldParseSkeletons = !!options.shouldParseSkeletons;
    }
    Parser.prototype.parse = function () {
        if (this.offset() !== 0) {
            throw Error('parser can only be used once');
        }
        return this.parseMessage(0, '', false);
    };
    Parser.prototype.parseMessage = function (nestingLevel, parentArgType, expectingCloseTag) {
        var elements = [];
        while (!this.isEOF()) {
            var char = this.char();
            if (char === 123 /* `{` */) {
                var result = this.parseArgument(nestingLevel, expectingCloseTag);
                if (result.err) {
                    return result;
                }
                elements.push(result.val);
            }
            else if (char === 125 /* `}` */ && nestingLevel > 0) {
                break;
            }
            else if (char === 35 /* `#` */ &&
                (parentArgType === 'plural' || parentArgType === 'selectordinal')) {
                var position = this.clonePosition();
                this.bump();
                elements.push({
                    type: TYPE.pound,
                    location: createLocation(position, this.clonePosition()),
                });
            }
            else if (char === 60 /* `<` */ &&
                !this.ignoreTag &&
                this.peek() === 47 // char code for '/'
            ) {
                if (expectingCloseTag) {
                    break;
                }
                else {
                    return this.error(ErrorKind.UNMATCHED_CLOSING_TAG, createLocation(this.clonePosition(), this.clonePosition()));
                }
            }
            else if (char === 60 /* `<` */ &&
                !this.ignoreTag &&
                _isAlpha(this.peek() || 0)) {
                var result = this.parseTag(nestingLevel, parentArgType);
                if (result.err) {
                    return result;
                }
                elements.push(result.val);
            }
            else {
                var result = this.parseLiteral(nestingLevel, parentArgType);
                if (result.err) {
                    return result;
                }
                elements.push(result.val);
            }
        }
        return { val: elements, err: null };
    };
    /**
     * A tag name must start with an ASCII lower/upper case letter. The grammar is based on the
     * [custom element name][] except that a dash is NOT always mandatory and uppercase letters
     * are accepted:
     *
     * ```
     * tag ::= "<" tagName (whitespace)* "/>" | "<" tagName (whitespace)* ">" message "</" tagName (whitespace)* ">"
     * tagName ::= [a-z] (PENChar)*
     * PENChar ::=
     *     "-" | "." | [0-9] | "_" | [a-z] | [A-Z] | #xB7 | [#xC0-#xD6] | [#xD8-#xF6] | [#xF8-#x37D] |
     *     [#x37F-#x1FFF] | [#x200C-#x200D] | [#x203F-#x2040] | [#x2070-#x218F] | [#x2C00-#x2FEF] |
     *     [#x3001-#xD7FF] | [#xF900-#xFDCF] | [#xFDF0-#xFFFD] | [#x10000-#xEFFFF]
     * ```
     *
     * [custom element name]: https://html.spec.whatwg.org/multipage/custom-elements.html#valid-custom-element-name
     * NOTE: We're a bit more lax here since HTML technically does not allow uppercase HTML element but we do
     * since other tag-based engines like React allow it
     */
    Parser.prototype.parseTag = function (nestingLevel, parentArgType) {
        var startPosition = this.clonePosition();
        this.bump(); // `<`
        var tagName = this.parseTagName();
        this.bumpSpace();
        if (this.bumpIf('/>')) {
            // Self closing tag
            return {
                val: {
                    type: TYPE.literal,
                    value: "<".concat(tagName, "/>"),
                    location: createLocation(startPosition, this.clonePosition()),
                },
                err: null,
            };
        }
        else if (this.bumpIf('>')) {
            var childrenResult = this.parseMessage(nestingLevel + 1, parentArgType, true);
            if (childrenResult.err) {
                return childrenResult;
            }
            var children = childrenResult.val;
            // Expecting a close tag
            var endTagStartPosition = this.clonePosition();
            if (this.bumpIf('</')) {
                if (this.isEOF() || !_isAlpha(this.char())) {
                    return this.error(ErrorKind.INVALID_TAG, createLocation(endTagStartPosition, this.clonePosition()));
                }
                var closingTagNameStartPosition = this.clonePosition();
                var closingTagName = this.parseTagName();
                if (tagName !== closingTagName) {
                    return this.error(ErrorKind.UNMATCHED_CLOSING_TAG, createLocation(closingTagNameStartPosition, this.clonePosition()));
                }
                this.bumpSpace();
                if (!this.bumpIf('>')) {
                    return this.error(ErrorKind.INVALID_TAG, createLocation(endTagStartPosition, this.clonePosition()));
                }
                return {
                    val: {
                        type: TYPE.tag,
                        value: tagName,
                        children: children,
                        location: createLocation(startPosition, this.clonePosition()),
                    },
                    err: null,
                };
            }
            else {
                return this.error(ErrorKind.UNCLOSED_TAG, createLocation(startPosition, this.clonePosition()));
            }
        }
        else {
            return this.error(ErrorKind.INVALID_TAG, createLocation(startPosition, this.clonePosition()));
        }
    };
    /**
     * This method assumes that the caller has peeked ahead for the first tag character.
     */
    Parser.prototype.parseTagName = function () {
        var startOffset = this.offset();
        this.bump(); // the first tag name character
        while (!this.isEOF() && _isPotentialElementNameChar(this.char())) {
            this.bump();
        }
        return this.message.slice(startOffset, this.offset());
    };
    Parser.prototype.parseLiteral = function (nestingLevel, parentArgType) {
        var start = this.clonePosition();
        var value = '';
        while (true) {
            var parseQuoteResult = this.tryParseQuote(parentArgType);
            if (parseQuoteResult) {
                value += parseQuoteResult;
                continue;
            }
            var parseUnquotedResult = this.tryParseUnquoted(nestingLevel, parentArgType);
            if (parseUnquotedResult) {
                value += parseUnquotedResult;
                continue;
            }
            var parseLeftAngleResult = this.tryParseLeftAngleBracket();
            if (parseLeftAngleResult) {
                value += parseLeftAngleResult;
                continue;
            }
            break;
        }
        var location = createLocation(start, this.clonePosition());
        return {
            val: { type: TYPE.literal, value: value, location: location },
            err: null,
        };
    };
    Parser.prototype.tryParseLeftAngleBracket = function () {
        if (!this.isEOF() &&
            this.char() === 60 /* `<` */ &&
            (this.ignoreTag ||
                // If at the opening tag or closing tag position, bail.
                !_isAlphaOrSlash(this.peek() || 0))) {
            this.bump(); // `<`
            return '<';
        }
        return null;
    };
    /**
     * Starting with ICU 4.8, an ASCII apostrophe only starts quoted text if it immediately precedes
     * a character that requires quoting (that is, "only where needed"), and works the same in
     * nested messages as on the top level of the pattern. The new behavior is otherwise compatible.
     */
    Parser.prototype.tryParseQuote = function (parentArgType) {
        if (this.isEOF() || this.char() !== 39 /* `'` */) {
            return null;
        }
        // Parse escaped char following the apostrophe, or early return if there is no escaped char.
        // Check if is valid escaped character
        switch (this.peek()) {
            case 39 /* `'` */:
                // double quote, should return as a single quote.
                this.bump();
                this.bump();
                return "'";
            // '{', '<', '>', '}'
            case 123:
            case 60:
            case 62:
            case 125:
                break;
            case 35: // '#'
                if (parentArgType === 'plural' || parentArgType === 'selectordinal') {
                    break;
                }
                return null;
            default:
                return null;
        }
        this.bump(); // apostrophe
        var codePoints = [this.char()]; // escaped char
        this.bump();
        // read chars until the optional closing apostrophe is found
        while (!this.isEOF()) {
            var ch = this.char();
            if (ch === 39 /* `'` */) {
                if (this.peek() === 39 /* `'` */) {
                    codePoints.push(39);
                    // Bump one more time because we need to skip 2 characters.
                    this.bump();
                }
                else {
                    // Optional closing apostrophe.
                    this.bump();
                    break;
                }
            }
            else {
                codePoints.push(ch);
            }
            this.bump();
        }
        return fromCodePoint$2.apply(void 0, codePoints);
    };
    Parser.prototype.tryParseUnquoted = function (nestingLevel, parentArgType) {
        if (this.isEOF()) {
            return null;
        }
        var ch = this.char();
        if (ch === 60 /* `<` */ ||
            ch === 123 /* `{` */ ||
            (ch === 35 /* `#` */ &&
                (parentArgType === 'plural' || parentArgType === 'selectordinal')) ||
            (ch === 125 /* `}` */ && nestingLevel > 0)) {
            return null;
        }
        else {
            this.bump();
            return fromCodePoint$2(ch);
        }
    };
    Parser.prototype.parseArgument = function (nestingLevel, expectingCloseTag) {
        var openingBracePosition = this.clonePosition();
        this.bump(); // `{`
        this.bumpSpace();
        if (this.isEOF()) {
            return this.error(ErrorKind.EXPECT_ARGUMENT_CLOSING_BRACE, createLocation(openingBracePosition, this.clonePosition()));
        }
        if (this.char() === 125 /* `}` */) {
            this.bump();
            return this.error(ErrorKind.EMPTY_ARGUMENT, createLocation(openingBracePosition, this.clonePosition()));
        }
        // argument name
        var value = this.parseIdentifierIfPossible().value;
        if (!value) {
            return this.error(ErrorKind.MALFORMED_ARGUMENT, createLocation(openingBracePosition, this.clonePosition()));
        }
        this.bumpSpace();
        if (this.isEOF()) {
            return this.error(ErrorKind.EXPECT_ARGUMENT_CLOSING_BRACE, createLocation(openingBracePosition, this.clonePosition()));
        }
        switch (this.char()) {
            // Simple argument: `{name}`
            case 125 /* `}` */: {
                this.bump(); // `}`
                return {
                    val: {
                        type: TYPE.argument,
                        // value does not include the opening and closing braces.
                        value: value,
                        location: createLocation(openingBracePosition, this.clonePosition()),
                    },
                    err: null,
                };
            }
            // Argument with options: `{name, format, ...}`
            case 44 /* `,` */: {
                this.bump(); // `,`
                this.bumpSpace();
                if (this.isEOF()) {
                    return this.error(ErrorKind.EXPECT_ARGUMENT_CLOSING_BRACE, createLocation(openingBracePosition, this.clonePosition()));
                }
                return this.parseArgumentOptions(nestingLevel, expectingCloseTag, value, openingBracePosition);
            }
            default:
                return this.error(ErrorKind.MALFORMED_ARGUMENT, createLocation(openingBracePosition, this.clonePosition()));
        }
    };
    /**
     * Advance the parser until the end of the identifier, if it is currently on
     * an identifier character. Return an empty string otherwise.
     */
    Parser.prototype.parseIdentifierIfPossible = function () {
        var startingPosition = this.clonePosition();
        var startOffset = this.offset();
        var value = matchIdentifierAtIndex(this.message, startOffset);
        var endOffset = startOffset + value.length;
        this.bumpTo(endOffset);
        var endPosition = this.clonePosition();
        var location = createLocation(startingPosition, endPosition);
        return { value: value, location: location };
    };
    Parser.prototype.parseArgumentOptions = function (nestingLevel, expectingCloseTag, value, openingBracePosition) {
        var _a;
        // Parse this range:
        // {name, type, style}
        //        ^---^
        var typeStartPosition = this.clonePosition();
        var argType = this.parseIdentifierIfPossible().value;
        var typeEndPosition = this.clonePosition();
        switch (argType) {
            case '':
                // Expecting a style string number, date, time, plural, selectordinal, or select.
                return this.error(ErrorKind.EXPECT_ARGUMENT_TYPE, createLocation(typeStartPosition, typeEndPosition));
            case 'number':
            case 'date':
            case 'time': {
                // Parse this range:
                // {name, number, style}
                //              ^-------^
                this.bumpSpace();
                var styleAndLocation = null;
                if (this.bumpIf(',')) {
                    this.bumpSpace();
                    var styleStartPosition = this.clonePosition();
                    var result = this.parseSimpleArgStyleIfPossible();
                    if (result.err) {
                        return result;
                    }
                    var style = trimEnd(result.val);
                    if (style.length === 0) {
                        return this.error(ErrorKind.EXPECT_ARGUMENT_STYLE, createLocation(this.clonePosition(), this.clonePosition()));
                    }
                    var styleLocation = createLocation(styleStartPosition, this.clonePosition());
                    styleAndLocation = { style: style, styleLocation: styleLocation };
                }
                var argCloseResult = this.tryParseArgumentClose(openingBracePosition);
                if (argCloseResult.err) {
                    return argCloseResult;
                }
                var location_1 = createLocation(openingBracePosition, this.clonePosition());
                // Extract style or skeleton
                if (styleAndLocation && startsWith$1(styleAndLocation === null || styleAndLocation === void 0 ? void 0 : styleAndLocation.style, '::', 0)) {
                    // Skeleton starts with `::`.
                    var skeleton = trimStart(styleAndLocation.style.slice(2));
                    if (argType === 'number') {
                        var result = this.parseNumberSkeletonFromString(skeleton, styleAndLocation.styleLocation);
                        if (result.err) {
                            return result;
                        }
                        return {
                            val: { type: TYPE.number, value: value, location: location_1, style: result.val },
                            err: null,
                        };
                    }
                    else {
                        if (skeleton.length === 0) {
                            return this.error(ErrorKind.EXPECT_DATE_TIME_SKELETON, location_1);
                        }
                        var dateTimePattern = skeleton;
                        // Get "best match" pattern only if locale is passed, if not, let it
                        // pass as-is where `parseDateTimeSkeleton()` will throw an error
                        // for unsupported patterns.
                        if (this.locale) {
                            dateTimePattern = getBestPattern(skeleton, this.locale);
                        }
                        var style = {
                            type: SKELETON_TYPE.dateTime,
                            pattern: dateTimePattern,
                            location: styleAndLocation.styleLocation,
                            parsedOptions: this.shouldParseSkeletons
                                ? parseDateTimeSkeleton(dateTimePattern)
                                : {},
                        };
                        var type = argType === 'date' ? TYPE.date : TYPE.time;
                        return {
                            val: { type: type, value: value, location: location_1, style: style },
                            err: null,
                        };
                    }
                }
                // Regular style or no style.
                return {
                    val: {
                        type: argType === 'number'
                            ? TYPE.number
                            : argType === 'date'
                                ? TYPE.date
                                : TYPE.time,
                        value: value,
                        location: location_1,
                        style: (_a = styleAndLocation === null || styleAndLocation === void 0 ? void 0 : styleAndLocation.style) !== null && _a !== void 0 ? _a : null,
                    },
                    err: null,
                };
            }
            case 'plural':
            case 'selectordinal':
            case 'select': {
                // Parse this range:
                // {name, plural, options}
                //              ^---------^
                var typeEndPosition_1 = this.clonePosition();
                this.bumpSpace();
                if (!this.bumpIf(',')) {
                    return this.error(ErrorKind.EXPECT_SELECT_ARGUMENT_OPTIONS, createLocation(typeEndPosition_1, __assign({}, typeEndPosition_1)));
                }
                this.bumpSpace();
                // Parse offset:
                // {name, plural, offset:1, options}
                //                ^-----^
                //
                // or the first option:
                //
                // {name, plural, one {...} other {...}}
                //                ^--^
                var identifierAndLocation = this.parseIdentifierIfPossible();
                var pluralOffset = 0;
                if (argType !== 'select' && identifierAndLocation.value === 'offset') {
                    if (!this.bumpIf(':')) {
                        return this.error(ErrorKind.EXPECT_PLURAL_ARGUMENT_OFFSET_VALUE, createLocation(this.clonePosition(), this.clonePosition()));
                    }
                    this.bumpSpace();
                    var result = this.tryParseDecimalInteger(ErrorKind.EXPECT_PLURAL_ARGUMENT_OFFSET_VALUE, ErrorKind.INVALID_PLURAL_ARGUMENT_OFFSET_VALUE);
                    if (result.err) {
                        return result;
                    }
                    // Parse another identifier for option parsing
                    this.bumpSpace();
                    identifierAndLocation = this.parseIdentifierIfPossible();
                    pluralOffset = result.val;
                }
                var optionsResult = this.tryParsePluralOrSelectOptions(nestingLevel, argType, expectingCloseTag, identifierAndLocation);
                if (optionsResult.err) {
                    return optionsResult;
                }
                var argCloseResult = this.tryParseArgumentClose(openingBracePosition);
                if (argCloseResult.err) {
                    return argCloseResult;
                }
                var location_2 = createLocation(openingBracePosition, this.clonePosition());
                if (argType === 'select') {
                    return {
                        val: {
                            type: TYPE.select,
                            value: value,
                            options: fromEntries(optionsResult.val),
                            location: location_2,
                        },
                        err: null,
                    };
                }
                else {
                    return {
                        val: {
                            type: TYPE.plural,
                            value: value,
                            options: fromEntries(optionsResult.val),
                            offset: pluralOffset,
                            pluralType: argType === 'plural' ? 'cardinal' : 'ordinal',
                            location: location_2,
                        },
                        err: null,
                    };
                }
            }
            default:
                return this.error(ErrorKind.INVALID_ARGUMENT_TYPE, createLocation(typeStartPosition, typeEndPosition));
        }
    };
    Parser.prototype.tryParseArgumentClose = function (openingBracePosition) {
        // Parse: {value, number, ::currency/GBP }
        //
        if (this.isEOF() || this.char() !== 125 /* `}` */) {
            return this.error(ErrorKind.EXPECT_ARGUMENT_CLOSING_BRACE, createLocation(openingBracePosition, this.clonePosition()));
        }
        this.bump(); // `}`
        return { val: true, err: null };
    };
    /**
     * See: https://github.com/unicode-org/icu/blob/af7ed1f6d2298013dc303628438ec4abe1f16479/icu4c/source/common/messagepattern.cpp#L659
     */
    Parser.prototype.parseSimpleArgStyleIfPossible = function () {
        var nestedBraces = 0;
        var startPosition = this.clonePosition();
        while (!this.isEOF()) {
            var ch = this.char();
            switch (ch) {
                case 39 /* `'` */: {
                    // Treat apostrophe as quoting but include it in the style part.
                    // Find the end of the quoted literal text.
                    this.bump();
                    var apostrophePosition = this.clonePosition();
                    if (!this.bumpUntil("'")) {
                        return this.error(ErrorKind.UNCLOSED_QUOTE_IN_ARGUMENT_STYLE, createLocation(apostrophePosition, this.clonePosition()));
                    }
                    this.bump();
                    break;
                }
                case 123 /* `{` */: {
                    nestedBraces += 1;
                    this.bump();
                    break;
                }
                case 125 /* `}` */: {
                    if (nestedBraces > 0) {
                        nestedBraces -= 1;
                    }
                    else {
                        return {
                            val: this.message.slice(startPosition.offset, this.offset()),
                            err: null,
                        };
                    }
                    break;
                }
                default:
                    this.bump();
                    break;
            }
        }
        return {
            val: this.message.slice(startPosition.offset, this.offset()),
            err: null,
        };
    };
    Parser.prototype.parseNumberSkeletonFromString = function (skeleton, location) {
        var tokens = [];
        try {
            tokens = parseNumberSkeletonFromString(skeleton);
        }
        catch (e) {
            return this.error(ErrorKind.INVALID_NUMBER_SKELETON, location);
        }
        return {
            val: {
                type: SKELETON_TYPE.number,
                tokens: tokens,
                location: location,
                parsedOptions: this.shouldParseSkeletons
                    ? parseNumberSkeleton(tokens)
                    : {},
            },
            err: null,
        };
    };
    /**
     * @param nesting_level The current nesting level of messages.
     *     This can be positive when parsing message fragment in select or plural argument options.
     * @param parent_arg_type The parent argument's type.
     * @param parsed_first_identifier If provided, this is the first identifier-like selector of
     *     the argument. It is a by-product of a previous parsing attempt.
     * @param expecting_close_tag If true, this message is directly or indirectly nested inside
     *     between a pair of opening and closing tags. The nested message will not parse beyond
     *     the closing tag boundary.
     */
    Parser.prototype.tryParsePluralOrSelectOptions = function (nestingLevel, parentArgType, expectCloseTag, parsedFirstIdentifier) {
        var _a;
        var hasOtherClause = false;
        var options = [];
        var parsedSelectors = new Set();
        var selector = parsedFirstIdentifier.value, selectorLocation = parsedFirstIdentifier.location;
        // Parse:
        // one {one apple}
        // ^--^
        while (true) {
            if (selector.length === 0) {
                var startPosition = this.clonePosition();
                if (parentArgType !== 'select' && this.bumpIf('=')) {
                    // Try parse `={number}` selector
                    var result = this.tryParseDecimalInteger(ErrorKind.EXPECT_PLURAL_ARGUMENT_SELECTOR, ErrorKind.INVALID_PLURAL_ARGUMENT_SELECTOR);
                    if (result.err) {
                        return result;
                    }
                    selectorLocation = createLocation(startPosition, this.clonePosition());
                    selector = this.message.slice(startPosition.offset, this.offset());
                }
                else {
                    break;
                }
            }
            // Duplicate selector clauses
            if (parsedSelectors.has(selector)) {
                return this.error(parentArgType === 'select'
                    ? ErrorKind.DUPLICATE_SELECT_ARGUMENT_SELECTOR
                    : ErrorKind.DUPLICATE_PLURAL_ARGUMENT_SELECTOR, selectorLocation);
            }
            if (selector === 'other') {
                hasOtherClause = true;
            }
            // Parse:
            // one {one apple}
            //     ^----------^
            this.bumpSpace();
            var openingBracePosition = this.clonePosition();
            if (!this.bumpIf('{')) {
                return this.error(parentArgType === 'select'
                    ? ErrorKind.EXPECT_SELECT_ARGUMENT_SELECTOR_FRAGMENT
                    : ErrorKind.EXPECT_PLURAL_ARGUMENT_SELECTOR_FRAGMENT, createLocation(this.clonePosition(), this.clonePosition()));
            }
            var fragmentResult = this.parseMessage(nestingLevel + 1, parentArgType, expectCloseTag);
            if (fragmentResult.err) {
                return fragmentResult;
            }
            var argCloseResult = this.tryParseArgumentClose(openingBracePosition);
            if (argCloseResult.err) {
                return argCloseResult;
            }
            options.push([
                selector,
                {
                    value: fragmentResult.val,
                    location: createLocation(openingBracePosition, this.clonePosition()),
                },
            ]);
            // Keep track of the existing selectors
            parsedSelectors.add(selector);
            // Prep next selector clause.
            this.bumpSpace();
            (_a = this.parseIdentifierIfPossible(), selector = _a.value, selectorLocation = _a.location);
        }
        if (options.length === 0) {
            return this.error(parentArgType === 'select'
                ? ErrorKind.EXPECT_SELECT_ARGUMENT_SELECTOR
                : ErrorKind.EXPECT_PLURAL_ARGUMENT_SELECTOR, createLocation(this.clonePosition(), this.clonePosition()));
        }
        if (this.requiresOtherClause && !hasOtherClause) {
            return this.error(ErrorKind.MISSING_OTHER_CLAUSE, createLocation(this.clonePosition(), this.clonePosition()));
        }
        return { val: options, err: null };
    };
    Parser.prototype.tryParseDecimalInteger = function (expectNumberError, invalidNumberError) {
        var sign = 1;
        var startingPosition = this.clonePosition();
        if (this.bumpIf('+')) ;
        else if (this.bumpIf('-')) {
            sign = -1;
        }
        var hasDigits = false;
        var decimal = 0;
        while (!this.isEOF()) {
            var ch = this.char();
            if (ch >= 48 /* `0` */ && ch <= 57 /* `9` */) {
                hasDigits = true;
                decimal = decimal * 10 + (ch - 48);
                this.bump();
            }
            else {
                break;
            }
        }
        var location = createLocation(startingPosition, this.clonePosition());
        if (!hasDigits) {
            return this.error(expectNumberError, location);
        }
        decimal *= sign;
        if (!isSafeInteger(decimal)) {
            return this.error(invalidNumberError, location);
        }
        return { val: decimal, err: null };
    };
    Parser.prototype.offset = function () {
        return this.position.offset;
    };
    Parser.prototype.isEOF = function () {
        return this.offset() === this.message.length;
    };
    Parser.prototype.clonePosition = function () {
        // This is much faster than `Object.assign` or spread.
        return {
            offset: this.position.offset,
            line: this.position.line,
            column: this.position.column,
        };
    };
    /**
     * Return the code point at the current position of the parser.
     * Throws if the index is out of bound.
     */
    Parser.prototype.char = function () {
        var offset = this.position.offset;
        if (offset >= this.message.length) {
            throw Error('out of bound');
        }
        var code = codePointAt(this.message, offset);
        if (code === undefined) {
            throw Error("Offset ".concat(offset, " is at invalid UTF-16 code unit boundary"));
        }
        return code;
    };
    Parser.prototype.error = function (kind, location) {
        return {
            val: null,
            err: {
                kind: kind,
                message: this.message,
                location: location,
            },
        };
    };
    /** Bump the parser to the next UTF-16 code unit. */
    Parser.prototype.bump = function () {
        if (this.isEOF()) {
            return;
        }
        var code = this.char();
        if (code === 10 /* '\n' */) {
            this.position.line += 1;
            this.position.column = 1;
            this.position.offset += 1;
        }
        else {
            this.position.column += 1;
            // 0 ~ 0x10000 -> unicode BMP, otherwise skip the surrogate pair.
            this.position.offset += code < 0x10000 ? 1 : 2;
        }
    };
    /**
     * If the substring starting at the current position of the parser has
     * the given prefix, then bump the parser to the character immediately
     * following the prefix and return true. Otherwise, don't bump the parser
     * and return false.
     */
    Parser.prototype.bumpIf = function (prefix) {
        if (startsWith$1(this.message, prefix, this.offset())) {
            for (var i = 0; i < prefix.length; i++) {
                this.bump();
            }
            return true;
        }
        return false;
    };
    /**
     * Bump the parser until the pattern character is found and return `true`.
     * Otherwise bump to the end of the file and return `false`.
     */
    Parser.prototype.bumpUntil = function (pattern) {
        var currentOffset = this.offset();
        var index = this.message.indexOf(pattern, currentOffset);
        if (index >= 0) {
            this.bumpTo(index);
            return true;
        }
        else {
            this.bumpTo(this.message.length);
            return false;
        }
    };
    /**
     * Bump the parser to the target offset.
     * If target offset is beyond the end of the input, bump the parser to the end of the input.
     */
    Parser.prototype.bumpTo = function (targetOffset) {
        if (this.offset() > targetOffset) {
            throw Error("targetOffset ".concat(targetOffset, " must be greater than or equal to the current offset ").concat(this.offset()));
        }
        targetOffset = Math.min(targetOffset, this.message.length);
        while (true) {
            var offset = this.offset();
            if (offset === targetOffset) {
                break;
            }
            if (offset > targetOffset) {
                throw Error("targetOffset ".concat(targetOffset, " is at invalid UTF-16 code unit boundary"));
            }
            this.bump();
            if (this.isEOF()) {
                break;
            }
        }
    };
    /** advance the parser through all whitespace to the next non-whitespace code unit. */
    Parser.prototype.bumpSpace = function () {
        while (!this.isEOF() && _isWhiteSpace(this.char())) {
            this.bump();
        }
    };
    /**
     * Peek at the *next* Unicode codepoint in the input without advancing the parser.
     * If the input has been exhausted, then this returns null.
     */
    Parser.prototype.peek = function () {
        if (this.isEOF()) {
            return null;
        }
        var code = this.char();
        var offset = this.offset();
        var nextCode = this.message.charCodeAt(offset + (code >= 0x10000 ? 2 : 1));
        return nextCode !== null && nextCode !== void 0 ? nextCode : null;
    };
    return Parser;
}());
/**
 * This check if codepoint is alphabet (lower & uppercase)
 * @param codepoint
 * @returns
 */
function _isAlpha(codepoint) {
    return ((codepoint >= 97 && codepoint <= 122) ||
        (codepoint >= 65 && codepoint <= 90));
}
function _isAlphaOrSlash(codepoint) {
    return _isAlpha(codepoint) || codepoint === 47; /* '/' */
}
/** See `parseTag` function docs. */
function _isPotentialElementNameChar(c) {
    return (c === 45 /* '-' */ ||
        c === 46 /* '.' */ ||
        (c >= 48 && c <= 57) /* 0..9 */ ||
        c === 95 /* '_' */ ||
        (c >= 97 && c <= 122) /** a..z */ ||
        (c >= 65 && c <= 90) /* A..Z */ ||
        c == 0xb7 ||
        (c >= 0xc0 && c <= 0xd6) ||
        (c >= 0xd8 && c <= 0xf6) ||
        (c >= 0xf8 && c <= 0x37d) ||
        (c >= 0x37f && c <= 0x1fff) ||
        (c >= 0x200c && c <= 0x200d) ||
        (c >= 0x203f && c <= 0x2040) ||
        (c >= 0x2070 && c <= 0x218f) ||
        (c >= 0x2c00 && c <= 0x2fef) ||
        (c >= 0x3001 && c <= 0xd7ff) ||
        (c >= 0xf900 && c <= 0xfdcf) ||
        (c >= 0xfdf0 && c <= 0xfffd) ||
        (c >= 0x10000 && c <= 0xeffff));
}
/**
 * Code point equivalent of regex `\p{White_Space}`.
 * From: https://www.unicode.org/Public/UCD/latest/ucd/PropList.txt
 */
function _isWhiteSpace(c) {
    return ((c >= 0x0009 && c <= 0x000d) ||
        c === 0x0020 ||
        c === 0x0085 ||
        (c >= 0x200e && c <= 0x200f) ||
        c === 0x2028 ||
        c === 0x2029);
}
/**
 * Code point equivalent of regex `\p{Pattern_Syntax}`.
 * See https://www.unicode.org/Public/UCD/latest/ucd/PropList.txt
 */
function _isPatternSyntax(c) {
    return ((c >= 0x0021 && c <= 0x0023) ||
        c === 0x0024 ||
        (c >= 0x0025 && c <= 0x0027) ||
        c === 0x0028 ||
        c === 0x0029 ||
        c === 0x002a ||
        c === 0x002b ||
        c === 0x002c ||
        c === 0x002d ||
        (c >= 0x002e && c <= 0x002f) ||
        (c >= 0x003a && c <= 0x003b) ||
        (c >= 0x003c && c <= 0x003e) ||
        (c >= 0x003f && c <= 0x0040) ||
        c === 0x005b ||
        c === 0x005c ||
        c === 0x005d ||
        c === 0x005e ||
        c === 0x0060 ||
        c === 0x007b ||
        c === 0x007c ||
        c === 0x007d ||
        c === 0x007e ||
        c === 0x00a1 ||
        (c >= 0x00a2 && c <= 0x00a5) ||
        c === 0x00a6 ||
        c === 0x00a7 ||
        c === 0x00a9 ||
        c === 0x00ab ||
        c === 0x00ac ||
        c === 0x00ae ||
        c === 0x00b0 ||
        c === 0x00b1 ||
        c === 0x00b6 ||
        c === 0x00bb ||
        c === 0x00bf ||
        c === 0x00d7 ||
        c === 0x00f7 ||
        (c >= 0x2010 && c <= 0x2015) ||
        (c >= 0x2016 && c <= 0x2017) ||
        c === 0x2018 ||
        c === 0x2019 ||
        c === 0x201a ||
        (c >= 0x201b && c <= 0x201c) ||
        c === 0x201d ||
        c === 0x201e ||
        c === 0x201f ||
        (c >= 0x2020 && c <= 0x2027) ||
        (c >= 0x2030 && c <= 0x2038) ||
        c === 0x2039 ||
        c === 0x203a ||
        (c >= 0x203b && c <= 0x203e) ||
        (c >= 0x2041 && c <= 0x2043) ||
        c === 0x2044 ||
        c === 0x2045 ||
        c === 0x2046 ||
        (c >= 0x2047 && c <= 0x2051) ||
        c === 0x2052 ||
        c === 0x2053 ||
        (c >= 0x2055 && c <= 0x205e) ||
        (c >= 0x2190 && c <= 0x2194) ||
        (c >= 0x2195 && c <= 0x2199) ||
        (c >= 0x219a && c <= 0x219b) ||
        (c >= 0x219c && c <= 0x219f) ||
        c === 0x21a0 ||
        (c >= 0x21a1 && c <= 0x21a2) ||
        c === 0x21a3 ||
        (c >= 0x21a4 && c <= 0x21a5) ||
        c === 0x21a6 ||
        (c >= 0x21a7 && c <= 0x21ad) ||
        c === 0x21ae ||
        (c >= 0x21af && c <= 0x21cd) ||
        (c >= 0x21ce && c <= 0x21cf) ||
        (c >= 0x21d0 && c <= 0x21d1) ||
        c === 0x21d2 ||
        c === 0x21d3 ||
        c === 0x21d4 ||
        (c >= 0x21d5 && c <= 0x21f3) ||
        (c >= 0x21f4 && c <= 0x22ff) ||
        (c >= 0x2300 && c <= 0x2307) ||
        c === 0x2308 ||
        c === 0x2309 ||
        c === 0x230a ||
        c === 0x230b ||
        (c >= 0x230c && c <= 0x231f) ||
        (c >= 0x2320 && c <= 0x2321) ||
        (c >= 0x2322 && c <= 0x2328) ||
        c === 0x2329 ||
        c === 0x232a ||
        (c >= 0x232b && c <= 0x237b) ||
        c === 0x237c ||
        (c >= 0x237d && c <= 0x239a) ||
        (c >= 0x239b && c <= 0x23b3) ||
        (c >= 0x23b4 && c <= 0x23db) ||
        (c >= 0x23dc && c <= 0x23e1) ||
        (c >= 0x23e2 && c <= 0x2426) ||
        (c >= 0x2427 && c <= 0x243f) ||
        (c >= 0x2440 && c <= 0x244a) ||
        (c >= 0x244b && c <= 0x245f) ||
        (c >= 0x2500 && c <= 0x25b6) ||
        c === 0x25b7 ||
        (c >= 0x25b8 && c <= 0x25c0) ||
        c === 0x25c1 ||
        (c >= 0x25c2 && c <= 0x25f7) ||
        (c >= 0x25f8 && c <= 0x25ff) ||
        (c >= 0x2600 && c <= 0x266e) ||
        c === 0x266f ||
        (c >= 0x2670 && c <= 0x2767) ||
        c === 0x2768 ||
        c === 0x2769 ||
        c === 0x276a ||
        c === 0x276b ||
        c === 0x276c ||
        c === 0x276d ||
        c === 0x276e ||
        c === 0x276f ||
        c === 0x2770 ||
        c === 0x2771 ||
        c === 0x2772 ||
        c === 0x2773 ||
        c === 0x2774 ||
        c === 0x2775 ||
        (c >= 0x2794 && c <= 0x27bf) ||
        (c >= 0x27c0 && c <= 0x27c4) ||
        c === 0x27c5 ||
        c === 0x27c6 ||
        (c >= 0x27c7 && c <= 0x27e5) ||
        c === 0x27e6 ||
        c === 0x27e7 ||
        c === 0x27e8 ||
        c === 0x27e9 ||
        c === 0x27ea ||
        c === 0x27eb ||
        c === 0x27ec ||
        c === 0x27ed ||
        c === 0x27ee ||
        c === 0x27ef ||
        (c >= 0x27f0 && c <= 0x27ff) ||
        (c >= 0x2800 && c <= 0x28ff) ||
        (c >= 0x2900 && c <= 0x2982) ||
        c === 0x2983 ||
        c === 0x2984 ||
        c === 0x2985 ||
        c === 0x2986 ||
        c === 0x2987 ||
        c === 0x2988 ||
        c === 0x2989 ||
        c === 0x298a ||
        c === 0x298b ||
        c === 0x298c ||
        c === 0x298d ||
        c === 0x298e ||
        c === 0x298f ||
        c === 0x2990 ||
        c === 0x2991 ||
        c === 0x2992 ||
        c === 0x2993 ||
        c === 0x2994 ||
        c === 0x2995 ||
        c === 0x2996 ||
        c === 0x2997 ||
        c === 0x2998 ||
        (c >= 0x2999 && c <= 0x29d7) ||
        c === 0x29d8 ||
        c === 0x29d9 ||
        c === 0x29da ||
        c === 0x29db ||
        (c >= 0x29dc && c <= 0x29fb) ||
        c === 0x29fc ||
        c === 0x29fd ||
        (c >= 0x29fe && c <= 0x2aff) ||
        (c >= 0x2b00 && c <= 0x2b2f) ||
        (c >= 0x2b30 && c <= 0x2b44) ||
        (c >= 0x2b45 && c <= 0x2b46) ||
        (c >= 0x2b47 && c <= 0x2b4c) ||
        (c >= 0x2b4d && c <= 0x2b73) ||
        (c >= 0x2b74 && c <= 0x2b75) ||
        (c >= 0x2b76 && c <= 0x2b95) ||
        c === 0x2b96 ||
        (c >= 0x2b97 && c <= 0x2bff) ||
        (c >= 0x2e00 && c <= 0x2e01) ||
        c === 0x2e02 ||
        c === 0x2e03 ||
        c === 0x2e04 ||
        c === 0x2e05 ||
        (c >= 0x2e06 && c <= 0x2e08) ||
        c === 0x2e09 ||
        c === 0x2e0a ||
        c === 0x2e0b ||
        c === 0x2e0c ||
        c === 0x2e0d ||
        (c >= 0x2e0e && c <= 0x2e16) ||
        c === 0x2e17 ||
        (c >= 0x2e18 && c <= 0x2e19) ||
        c === 0x2e1a ||
        c === 0x2e1b ||
        c === 0x2e1c ||
        c === 0x2e1d ||
        (c >= 0x2e1e && c <= 0x2e1f) ||
        c === 0x2e20 ||
        c === 0x2e21 ||
        c === 0x2e22 ||
        c === 0x2e23 ||
        c === 0x2e24 ||
        c === 0x2e25 ||
        c === 0x2e26 ||
        c === 0x2e27 ||
        c === 0x2e28 ||
        c === 0x2e29 ||
        (c >= 0x2e2a && c <= 0x2e2e) ||
        c === 0x2e2f ||
        (c >= 0x2e30 && c <= 0x2e39) ||
        (c >= 0x2e3a && c <= 0x2e3b) ||
        (c >= 0x2e3c && c <= 0x2e3f) ||
        c === 0x2e40 ||
        c === 0x2e41 ||
        c === 0x2e42 ||
        (c >= 0x2e43 && c <= 0x2e4f) ||
        (c >= 0x2e50 && c <= 0x2e51) ||
        c === 0x2e52 ||
        (c >= 0x2e53 && c <= 0x2e7f) ||
        (c >= 0x3001 && c <= 0x3003) ||
        c === 0x3008 ||
        c === 0x3009 ||
        c === 0x300a ||
        c === 0x300b ||
        c === 0x300c ||
        c === 0x300d ||
        c === 0x300e ||
        c === 0x300f ||
        c === 0x3010 ||
        c === 0x3011 ||
        (c >= 0x3012 && c <= 0x3013) ||
        c === 0x3014 ||
        c === 0x3015 ||
        c === 0x3016 ||
        c === 0x3017 ||
        c === 0x3018 ||
        c === 0x3019 ||
        c === 0x301a ||
        c === 0x301b ||
        c === 0x301c ||
        c === 0x301d ||
        (c >= 0x301e && c <= 0x301f) ||
        c === 0x3020 ||
        c === 0x3030 ||
        c === 0xfd3e ||
        c === 0xfd3f ||
        (c >= 0xfe45 && c <= 0xfe46));
}

function pruneLocation(els) {
    els.forEach(function (el) {
        delete el.location;
        if (isSelectElement(el) || isPluralElement(el)) {
            for (var k in el.options) {
                delete el.options[k].location;
                pruneLocation(el.options[k].value);
            }
        }
        else if (isNumberElement(el) && isNumberSkeleton(el.style)) {
            delete el.style.location;
        }
        else if ((isDateElement(el) || isTimeElement(el)) &&
            isDateTimeSkeleton(el.style)) {
            delete el.style.location;
        }
        else if (isTagElement(el)) {
            pruneLocation(el.children);
        }
    });
}
function parse(message, opts) {
    if (opts === void 0) { opts = {}; }
    opts = __assign({ shouldParseSkeletons: true, requiresOtherClause: true }, opts);
    var result = new Parser(message, opts).parse();
    if (result.err) {
        var error = SyntaxError(ErrorKind[result.err.kind]);
        // @ts-expect-error Assign to error object
        error.location = result.err.location;
        // @ts-expect-error Assign to error object
        error.originalMessage = result.err.message;
        throw error;
    }
    if (!(opts === null || opts === void 0 ? void 0 : opts.captureLocation)) {
        pruneLocation(result.val);
    }
    return result.val;
}

var ErrorCode;
(function (ErrorCode) {
    // When we have a placeholder but no value to format
    ErrorCode["MISSING_VALUE"] = "MISSING_VALUE";
    // When value supplied is invalid
    ErrorCode["INVALID_VALUE"] = "INVALID_VALUE";
    // When we need specific Intl API but it's not available
    ErrorCode["MISSING_INTL_API"] = "MISSING_INTL_API";
})(ErrorCode || (ErrorCode = {}));
var FormatError = /** @class */ (function (_super) {
    __extends(FormatError, _super);
    function FormatError(msg, code, originalMessage) {
        var _this = _super.call(this, msg) || this;
        _this.code = code;
        _this.originalMessage = originalMessage;
        return _this;
    }
    FormatError.prototype.toString = function () {
        return "[formatjs Error: ".concat(this.code, "] ").concat(this.message);
    };
    return FormatError;
}(Error));
var InvalidValueError = /** @class */ (function (_super) {
    __extends(InvalidValueError, _super);
    function InvalidValueError(variableId, value, options, originalMessage) {
        return _super.call(this, "Invalid values for \"".concat(variableId, "\": \"").concat(value, "\". Options are \"").concat(Object.keys(options).join('", "'), "\""), ErrorCode.INVALID_VALUE, originalMessage) || this;
    }
    return InvalidValueError;
}(FormatError));
var InvalidValueTypeError = /** @class */ (function (_super) {
    __extends(InvalidValueTypeError, _super);
    function InvalidValueTypeError(value, type, originalMessage) {
        return _super.call(this, "Value for \"".concat(value, "\" must be of type ").concat(type), ErrorCode.INVALID_VALUE, originalMessage) || this;
    }
    return InvalidValueTypeError;
}(FormatError));
var MissingValueError = /** @class */ (function (_super) {
    __extends(MissingValueError, _super);
    function MissingValueError(variableId, originalMessage) {
        return _super.call(this, "The intl string context variable \"".concat(variableId, "\" was not provided to the string \"").concat(originalMessage, "\""), ErrorCode.MISSING_VALUE, originalMessage) || this;
    }
    return MissingValueError;
}(FormatError));

var PART_TYPE;
(function (PART_TYPE) {
    PART_TYPE[PART_TYPE["literal"] = 0] = "literal";
    PART_TYPE[PART_TYPE["object"] = 1] = "object";
})(PART_TYPE || (PART_TYPE = {}));
function mergeLiteral(parts) {
    if (parts.length < 2) {
        return parts;
    }
    return parts.reduce(function (all, part) {
        var lastPart = all[all.length - 1];
        if (!lastPart ||
            lastPart.type !== PART_TYPE.literal ||
            part.type !== PART_TYPE.literal) {
            all.push(part);
        }
        else {
            lastPart.value += part.value;
        }
        return all;
    }, []);
}
function isFormatXMLElementFn(el) {
    return typeof el === 'function';
}
// TODO(skeleton): add skeleton support
function formatToParts(els, locales, formatters, formats, values, currentPluralValue, 
// For debugging
originalMessage) {
    // Hot path for straight simple msg translations
    if (els.length === 1 && isLiteralElement(els[0])) {
        return [
            {
                type: PART_TYPE.literal,
                value: els[0].value,
            },
        ];
    }
    var result = [];
    for (var _i = 0, els_1 = els; _i < els_1.length; _i++) {
        var el = els_1[_i];
        // Exit early for string parts.
        if (isLiteralElement(el)) {
            result.push({
                type: PART_TYPE.literal,
                value: el.value,
            });
            continue;
        }
        // TODO: should this part be literal type?
        // Replace `#` in plural rules with the actual numeric value.
        if (isPoundElement(el)) {
            if (typeof currentPluralValue === 'number') {
                result.push({
                    type: PART_TYPE.literal,
                    value: formatters.getNumberFormat(locales).format(currentPluralValue),
                });
            }
            continue;
        }
        var varName = el.value;
        // Enforce that all required values are provided by the caller.
        if (!(values && varName in values)) {
            throw new MissingValueError(varName, originalMessage);
        }
        var value = values[varName];
        if (isArgumentElement(el)) {
            if (!value || typeof value === 'string' || typeof value === 'number') {
                value =
                    typeof value === 'string' || typeof value === 'number'
                        ? String(value)
                        : '';
            }
            result.push({
                type: typeof value === 'string' ? PART_TYPE.literal : PART_TYPE.object,
                value: value,
            });
            continue;
        }
        // Recursively format plural and select parts' option — which can be a
        // nested pattern structure. The choosing of the option to use is
        // abstracted-by and delegated-to the part helper object.
        if (isDateElement(el)) {
            var style = typeof el.style === 'string'
                ? formats.date[el.style]
                : isDateTimeSkeleton(el.style)
                    ? el.style.parsedOptions
                    : undefined;
            result.push({
                type: PART_TYPE.literal,
                value: formatters
                    .getDateTimeFormat(locales, style)
                    .format(value),
            });
            continue;
        }
        if (isTimeElement(el)) {
            var style = typeof el.style === 'string'
                ? formats.time[el.style]
                : isDateTimeSkeleton(el.style)
                    ? el.style.parsedOptions
                    : formats.time.medium;
            result.push({
                type: PART_TYPE.literal,
                value: formatters
                    .getDateTimeFormat(locales, style)
                    .format(value),
            });
            continue;
        }
        if (isNumberElement(el)) {
            var style = typeof el.style === 'string'
                ? formats.number[el.style]
                : isNumberSkeleton(el.style)
                    ? el.style.parsedOptions
                    : undefined;
            if (style && style.scale) {
                value =
                    value *
                        (style.scale || 1);
            }
            result.push({
                type: PART_TYPE.literal,
                value: formatters
                    .getNumberFormat(locales, style)
                    .format(value),
            });
            continue;
        }
        if (isTagElement(el)) {
            var children = el.children, value_1 = el.value;
            var formatFn = values[value_1];
            if (!isFormatXMLElementFn(formatFn)) {
                throw new InvalidValueTypeError(value_1, 'function', originalMessage);
            }
            var parts = formatToParts(children, locales, formatters, formats, values, currentPluralValue);
            var chunks = formatFn(parts.map(function (p) { return p.value; }));
            if (!Array.isArray(chunks)) {
                chunks = [chunks];
            }
            result.push.apply(result, chunks.map(function (c) {
                return {
                    type: typeof c === 'string' ? PART_TYPE.literal : PART_TYPE.object,
                    value: c,
                };
            }));
        }
        if (isSelectElement(el)) {
            var opt = el.options[value] || el.options.other;
            if (!opt) {
                throw new InvalidValueError(el.value, value, Object.keys(el.options), originalMessage);
            }
            result.push.apply(result, formatToParts(opt.value, locales, formatters, formats, values));
            continue;
        }
        if (isPluralElement(el)) {
            var opt = el.options["=".concat(value)];
            if (!opt) {
                if (!Intl.PluralRules) {
                    throw new FormatError("Intl.PluralRules is not available in this environment.\nTry polyfilling it using \"@formatjs/intl-pluralrules\"\n", ErrorCode.MISSING_INTL_API, originalMessage);
                }
                var rule = formatters
                    .getPluralRules(locales, { type: el.pluralType })
                    .select(value - (el.offset || 0));
                opt = el.options[rule] || el.options.other;
            }
            if (!opt) {
                throw new InvalidValueError(el.value, value, Object.keys(el.options), originalMessage);
            }
            result.push.apply(result, formatToParts(opt.value, locales, formatters, formats, values, value - (el.offset || 0)));
            continue;
        }
    }
    return mergeLiteral(result);
}

/*
Copyright (c) 2014, Yahoo! Inc. All rights reserved.
Copyrights licensed under the New BSD License.
See the accompanying LICENSE file for terms.
*/
// -- MessageFormat --------------------------------------------------------
function mergeConfig(c1, c2) {
    if (!c2) {
        return c1;
    }
    return __assign(__assign(__assign({}, (c1 || {})), (c2 || {})), Object.keys(c1).reduce(function (all, k) {
        all[k] = __assign(__assign({}, c1[k]), (c2[k] || {}));
        return all;
    }, {}));
}
function mergeConfigs(defaultConfig, configs) {
    if (!configs) {
        return defaultConfig;
    }
    return Object.keys(defaultConfig).reduce(function (all, k) {
        all[k] = mergeConfig(defaultConfig[k], configs[k]);
        return all;
    }, __assign({}, defaultConfig));
}
function createFastMemoizeCache(store) {
    return {
        create: function () {
            return {
                get: function (key) {
                    return store[key];
                },
                set: function (key, value) {
                    store[key] = value;
                },
            };
        },
    };
}
function createDefaultFormatters(cache) {
    if (cache === void 0) { cache = {
        number: {},
        dateTime: {},
        pluralRules: {},
    }; }
    return {
        getNumberFormat: memoize(function () {
            var _a;
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return new ((_a = Intl.NumberFormat).bind.apply(_a, __spreadArray$1([void 0], args, false)))();
        }, {
            cache: createFastMemoizeCache(cache.number),
            strategy: strategies.variadic,
        }),
        getDateTimeFormat: memoize(function () {
            var _a;
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return new ((_a = Intl.DateTimeFormat).bind.apply(_a, __spreadArray$1([void 0], args, false)))();
        }, {
            cache: createFastMemoizeCache(cache.dateTime),
            strategy: strategies.variadic,
        }),
        getPluralRules: memoize(function () {
            var _a;
            var args = [];
            for (var _i = 0; _i < arguments.length; _i++) {
                args[_i] = arguments[_i];
            }
            return new ((_a = Intl.PluralRules).bind.apply(_a, __spreadArray$1([void 0], args, false)))();
        }, {
            cache: createFastMemoizeCache(cache.pluralRules),
            strategy: strategies.variadic,
        }),
    };
}
var IntlMessageFormat = /** @class */ (function () {
    function IntlMessageFormat(message, locales, overrideFormats, opts) {
        if (locales === void 0) { locales = IntlMessageFormat.defaultLocale; }
        var _this = this;
        this.formatterCache = {
            number: {},
            dateTime: {},
            pluralRules: {},
        };
        this.format = function (values) {
            var parts = _this.formatToParts(values);
            // Hot path for straight simple msg translations
            if (parts.length === 1) {
                return parts[0].value;
            }
            var result = parts.reduce(function (all, part) {
                if (!all.length ||
                    part.type !== PART_TYPE.literal ||
                    typeof all[all.length - 1] !== 'string') {
                    all.push(part.value);
                }
                else {
                    all[all.length - 1] += part.value;
                }
                return all;
            }, []);
            if (result.length <= 1) {
                return result[0] || '';
            }
            return result;
        };
        this.formatToParts = function (values) {
            return formatToParts(_this.ast, _this.locales, _this.formatters, _this.formats, values, undefined, _this.message);
        };
        this.resolvedOptions = function () {
            var _a;
            return ({
                locale: ((_a = _this.resolvedLocale) === null || _a === void 0 ? void 0 : _a.toString()) ||
                    Intl.NumberFormat.supportedLocalesOf(_this.locales)[0],
            });
        };
        this.getAst = function () { return _this.ast; };
        // Defined first because it's used to build the format pattern.
        this.locales = locales;
        this.resolvedLocale = IntlMessageFormat.resolveLocale(locales);
        if (typeof message === 'string') {
            this.message = message;
            if (!IntlMessageFormat.__parse) {
                throw new TypeError('IntlMessageFormat.__parse must be set to process `message` of type `string`');
            }
            var _a = opts || {}; _a.formatters; var parseOpts = __rest$1(_a, ["formatters"]);
            // Parse string messages into an AST.
            this.ast = IntlMessageFormat.__parse(message, __assign(__assign({}, parseOpts), { locale: this.resolvedLocale }));
        }
        else {
            this.ast = message;
        }
        if (!Array.isArray(this.ast)) {
            throw new TypeError('A message must be provided as a String or AST.');
        }
        // Creates a new object with the specified `formats` merged with the default
        // formats.
        this.formats = mergeConfigs(IntlMessageFormat.formats, overrideFormats);
        this.formatters =
            (opts && opts.formatters) || createDefaultFormatters(this.formatterCache);
    }
    Object.defineProperty(IntlMessageFormat, "defaultLocale", {
        get: function () {
            if (!IntlMessageFormat.memoizedDefaultLocale) {
                IntlMessageFormat.memoizedDefaultLocale =
                    new Intl.NumberFormat().resolvedOptions().locale;
            }
            return IntlMessageFormat.memoizedDefaultLocale;
        },
        enumerable: false,
        configurable: true
    });
    IntlMessageFormat.memoizedDefaultLocale = null;
    IntlMessageFormat.resolveLocale = function (locales) {
        if (typeof Intl.Locale === 'undefined') {
            return;
        }
        var supportedLocales = Intl.NumberFormat.supportedLocalesOf(locales);
        if (supportedLocales.length > 0) {
            return new Intl.Locale(supportedLocales[0]);
        }
        return new Intl.Locale(typeof locales === 'string' ? locales : locales[0]);
    };
    IntlMessageFormat.__parse = parse;
    // Default format options used as the prototype of the `formats` provided to the
    // constructor. These are used when constructing the internal Intl.NumberFormat
    // and Intl.DateTimeFormat instances.
    IntlMessageFormat.formats = {
        number: {
            integer: {
                maximumFractionDigits: 0,
            },
            currency: {
                style: 'currency',
            },
            percent: {
                style: 'percent',
            },
        },
        date: {
            short: {
                month: 'numeric',
                day: 'numeric',
                year: '2-digit',
            },
            medium: {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            },
            long: {
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            },
            full: {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
            },
        },
        time: {
            short: {
                hour: 'numeric',
                minute: 'numeric',
            },
            medium: {
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
            },
            long: {
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                timeZoneName: 'short',
            },
            full: {
                hour: 'numeric',
                minute: 'numeric',
                second: 'numeric',
                timeZoneName: 'short',
            },
        },
    };
    return IntlMessageFormat;
}());

const locales = {
    ar: () => import(/* webpackChunkName: "cds-ai-chat-locale_ar" */ './ar.js').then(function (n) { return n.a; }),
    'ar-dz': () => import(/* webpackChunkName: "cds-ai-chat-locale_ar_dz" */ './ar-dz.js').then(function (n) { return n.a; }),
    'ar-kw': () => import(/* webpackChunkName: "cds-ai-chat-locale_ar_kw" */ './ar-kw.js').then(function (n) { return n.a; }),
    'ar-ly': () => import(/* webpackChunkName: "cds-ai-chat-locale_ar_ly" */ './ar-ly.js').then(function (n) { return n.a; }),
    'ar-ma': () => import(/* webpackChunkName: "cds-ai-chat-locale_ar_ma" */ './ar-ma.js').then(function (n) { return n.a; }),
    'ar-sa': () => import(/* webpackChunkName: "cds-ai-chat-locale_ar_sa" */ './ar-sa.js').then(function (n) { return n.a; }),
    'ar-tn': () => import(/* webpackChunkName: "cds-ai-chat-locale_ar_tn" */ './ar-tn.js').then(function (n) { return n.a; }),
    cs: () => import(/* webpackChunkName: "cds-ai-chat-locale_cs" */ './cs.js').then(function (n) { return n.c; }),
    de: () => import(/* webpackChunkName: "cds-ai-chat-locale_de" */ './de.js').then(function (n) { return n.d; }),
    'de-at': () => import(/* webpackChunkName: "cds-ai-chat-locale_de_at" */ './de-at.js').then(function (n) { return n.d; }),
    'de-ch': () => import(/* webpackChunkName: "cds-ai-chat-locale_de_ch" */ './de-ch.js').then(function (n) { return n.d; }),
    en: () => Promise.resolve().then(function () { return en; }),
    'en-au': () => import(/* webpackChunkName: "cds-ai-chat-locale_en_au" */ './en-au.js').then(function (n) { return n.e; }),
    'en-ca': () => import(/* webpackChunkName: "cds-ai-chat-locale_en_ca" */ './en-ca.js').then(function (n) { return n.e; }),
    'en-gb': () => import(/* webpackChunkName: "cds-ai-chat-locale_en_gb" */ './en-gb.js').then(function (n) { return n.e; }),
    'en-ie': () => import(/* webpackChunkName: "cds-ai-chat-locale_en_ie" */ './en-ie.js').then(function (n) { return n.e; }),
    'en-il': () => import(/* webpackChunkName: "cds-ai-chat-locale_en_il" */ './en-il.js').then(function (n) { return n.e; }),
    'en-nz': () => import(/* webpackChunkName: "cds-ai-chat-locale_en_nz" */ './en-nz.js').then(function (n) { return n.e; }),
    es: () => import(/* webpackChunkName: "cds-ai-chat-locale_es" */ './es.js').then(function (n) { return n.e; }),
    'es-do': () => import(/* webpackChunkName: "cds-ai-chat-locale_es_do" */ './es-do.js').then(function (n) { return n.e; }),
    'es-us': () => import(/* webpackChunkName: "cds-ai-chat-locale_es_us" */ './es-us.js').then(function (n) { return n.e; }),
    nl: () => import(/* webpackChunkName: "cds-ai-chat-locale_nl" */ './nl.js').then(function (n) { return n.n; }),
    fr: () => import(/* webpackChunkName: "cds-ai-chat-locale_fr" */ './fr.js').then(function (n) { return n.f; }),
    'fr-ca': () => import(/* webpackChunkName: "cds-ai-chat-locale_fr_ca" */ './fr-ca.js').then(function (n) { return n.f; }),
    'fr-ch': () => import(/* webpackChunkName: "cds-ai-chat-locale_fr_ch" */ './fr-ch.js').then(function (n) { return n.f; }),
    it: () => import(/* webpackChunkName: "cds-ai-chat-locale_it" */ './it.js').then(function (n) { return n.i; }),
    'it-ch': () => import(/* webpackChunkName: "cds-ai-chat-locale_it_ch" */ './it-ch.js').then(function (n) { return n.i; }),
    ja: () => import(/* webpackChunkName: "cds-ai-chat-locale_ja" */ './ja.js').then(function (n) { return n.j; }),
    ko: () => import(/* webpackChunkName: "cds-ai-chat-locale_ko" */ './ko.js').then(function (n) { return n.k; }),
    pt: () => import(/* webpackChunkName: "cds-ai-chat-locale_pt" */ './pt.js').then(function (n) { return n.p; }),
    'pt-br': () => import(/* webpackChunkName: "cds-ai-chat-locale_pt_br" */ './pt-br.js').then(function (n) { return n.p; }),
    // The translation team has only given us "zh" for text which we'll just map to "zh-cn" for locale as well.
    zh: () => import(/* webpackChunkName: "cds-ai-chat-locale_zh_cn" */ './zh-cn.js').then(function (n) { return n.z; }),
    'zh-cn': () => import(/* webpackChunkName: "cds-ai-chat-locale_zh_cn" */ './zh-cn.js').then(function (n) { return n.z; }),
    'zh-tw': () => import(/* webpackChunkName: "cds-ai-chat-locale_zh_tw" */ './zh-tw.js').then(function (n) { return n.z; }),
    // The zh-mo and zh-hk locales fallback to zh-tw.
    'zh-mo': () => import(/* webpackChunkName: "cds-ai-chat-locale_zh_tw" */ './zh-tw.js').then(function (n) { return n.z; }),
    'zh-hk': () => import(/* webpackChunkName: "cds-ai-chat-locale_zh_tw" */ './zh-tw.js').then(function (n) { return n.z; }),
};
const languages = {
    ar: () => {
        return import(/* webpackChunkName: "cds-ai-chat-lang_ar" */ './ar2.js');
    },
    cs: () => {
        return import(/* webpackChunkName: "cds-ai-chat-lang_cs" */ './cs2.js');
    },
    de: () => {
        return import(/* webpackChunkName: "cds-ai-chat-lang_de" */ './de2.js');
    },
    en: () => {
        return Promise.resolve().then(function () { return en$2; });
    },
    es: () => {
        return import(/* webpackChunkName: "cds-ai-chat-lang_es" */ './es2.js');
    },
    fr: () => {
        return import(/* webpackChunkName: "cds-ai-chat-lang_fr" */ './fr2.js');
    },
    it: () => {
        return import(/* webpackChunkName: "cds-ai-chat-lang_it" */ './it2.js');
    },
    ja: () => {
        return import(/* webpackChunkName: "cds-ai-chat-lang_ja" */ './ja2.js');
    },
    ko: () => {
        return import(/* webpackChunkName: "cds-ai-chat-lang_ko" */ './ko2.js');
    },
    nl: () => {
        return import(/* webpackChunkName: "cds-ai-chat-lang_nl" */ './nl2.js');
    },
    'pt-br': () => {
        return import(/* webpackChunkName: "cds-ai-chat-lang_pt_br" */ './pt-br2.js');
    },
    zh: () => {
        return import(/* webpackChunkName: "cds-ai-chat-lang_zh_cn" */ './zh.js');
    },
    'zh-tw': () => {
        return import(/* webpackChunkName: "cds-ai-chat-lang_zh_tw" */ './zh-tw2.js');
    },
};
// The zh-mo and zh-hk locales fallback to zh-tw.
languages['zh-mo'] = languages['zh-tw'];
languages['zh-hk'] = languages['zh-tw'];
/**
 * Determines if the given object contains a key that supported the given locale. This will determine if there is an
 * exact match from the given object and if so, that key will be returned. If not, the language will be extracted
 * from the locale and that will be checked to see if it supported by the given object. If the language is
 * supported, that will be returned. If nothing is found to support the locale, this will return null.
 *
 * @param locale The locale (which may or may not include a region) to determine if we have a valid match or null if
 * there is no match.
 * @param object The object containing the values to check for support.
 */
function isSupportedLocale(locale, object) {
    if (!locale) {
        return null;
    }
    // Normalize the locale to lower case and change underscores to dashes.
    locale = locale.toLowerCase().replace(/_/g, '-');
    if (object[locale]) {
        // If there's an exact match for the requested locale, then we'll use that.
        return locale;
    }
    // If not, look to see if there's a match for just the language without the region.
    const language = locale.substring(0, 2);
    if (object[language]) {
        return language;
    }
    // No match was found.
    return null;
}
/**
 * Determines if the given object contains a key that supported the given locale. This will determine if there is an
 * exact match from the given object and if so, that key will be returned. If not, the language will be extracted
 * from the locale and that will be checked to see if it supported by the given object. If the language is
 * supported, that will be returned. If nothing is found to support the locale, this will return null. If no locale
 * was requested, then the browser's languages/locales will be used instead.
 *
 * @param requestedLocale The locale (which may or may not include a region) that was provided in the public config.
 * @param object The object containing the values to check for support.
 * @param objectType A user friendly string describing the type of data in the given object. Used for outputting
 * error messages.
 */
function findSupportedKey(requestedLocale, object, objectType) {
    // Check to see if the requested locale is supported.
    const requestedSupported = isSupportedLocale(requestedLocale, object);
    if (requestedSupported) {
        return requestedSupported;
    }
    if (requestedLocale) {
        const keyList = JSON.stringify(Object.keys(object));
        consoleError(`The requested locale "${requestedLocale}" does not contain a supported ${objectType}. We are defaulting to "en". The supported values are ${keyList}.`);
    }
    // Return English as the default.
    return 'en';
}
/**
 * Loads the appropriate {@link LanguagePack} from the corresponding module for the requested locale.
 */
async function loadLocale(requestedLocale) {
    try {
        const localeKey = findSupportedKey(requestedLocale, locales, 'locale');
        const localeModule = await locales[localeKey]();
        if (localeModule) {
            return localeModule.default;
        }
        consoleError(`The locale data for "${localeKey}" did not load. The application will default to "en".`);
    }
    catch (error) {
        consoleError(`An error occurred loading the locale data for "${requestedLocale}". The application will default to "en".`, error);
    }
    return enLocaleData;
}
/**
 * Loads the language pack for the given locale if a language pack was not already provided. This may incur an
 * asynchronous load of the language pack files.
 *
 * @param requestedLocale A string identifying the locale WAC is running in.
 * @param providedLanguagePack A language pack that was provided by the configuration from the host page. If this
 * value is defined, it will always be used instead instead of loading a separate pack.
 * @returns The appropriate language pack for the given locale. Any values that are missing will be set with
 * their English values when the instance's language pack is updated.
 */
async function loadLanguagePack(requestedLocale, providedLanguagePack) {
    if (providedLanguagePack) {
        // Use the language pack that was provided.
        return fillWithEnglish(providedLanguagePack);
    }
    try {
        const languageKey = findSupportedKey(requestedLocale, languages, 'language');
        const languageModule = await languages[languageKey]();
        if (languageModule) {
            return fillWithEnglish(languageModule.default);
        }
        consoleError(`The language pack for "${languageKey}" did not load. The application will default to "en".`);
    }
    catch (error) {
        consoleError(`An error occurred loading the language pack for "${requestedLocale}". The application will default to "en".`, error);
    }
    return enLanguagePack;
}
/**
 * Returns the given language pack filled with english messages for keys that are missing values.
 */
function fillWithEnglish(languagePack) {
    return {
        ...enLanguagePack,
        ...languagePack,
    };
}
/**
 * Handles a "b" tag.
 */
function handleBTag(chunks) {
    return React__default.createElement("b", null, chunks);
}
/**
 * Handles a "br" tag.
 */
function handleBRTag() {
    return React__default.createElement("br", null);
}
/**
 * Adds the functions for processing HTML to the given object for use with FormattedMessage.
 */
function addHTMLSupport(values) {
    values.b = handleBTag;
    values.br = handleBRTag;
    return values;
}
/**
 * Loads a dayjs locale if it hasn't been loaded already, but doesn't replace the current globally set locale.
 *
 * @param locale The dayjs locale to load.
 * @returns returns the locale it succeeded to load.
 */
async function loadDayjsLocale(locale) {
    if (!dayjs.Ls[locale]) {
        const previousLocale = dayjs.locale();
        const localePack = await loadLocale(locale);
        // We need to temporarily set the new locale globally so that it's available and then return to the previously
        // set locale.
        dayjs.locale(localePack);
        dayjs.locale(previousLocale);
        // Determine if the locale we attempted to set was successful.
        const isLoaded = Boolean(dayjs.Ls[locale]);
        // If the locale we attempted to load was not successful, and it's 2 characters long, it's not a locale
        // recognized by dayjs, and we should throw an error.
        if (!isLoaded && locale.length === 2) {
            throw Error('Locale is not recognized.');
        }
        else if (!isLoaded) {
            // If the locale we were provided is more than two characters, we were possibly given a region that's not
            // supported, so let's attempt to load just the language, which is the first two characters.
            return loadDayjsLocale(locale.substring(0, 2));
        }
    }
    return locale;
}
function formatMessage(intl, id, values) {
    return intl.formatMessage({ id }, values);
}
function createEnglishFormat(key) {
    return new IntlMessageFormat(enLanguagePack[key], 'en-US');
}

// The number of times a user can use the instance.updateHistoryUserDefined() function per messageID.
const UPDATE_HISTORY_USER_DEFINED_LIMIT = 20;
/**
 * Creates an instance of the public assistant chat. This value is what is returned to the host page after the chat
 * has been started and this instance is what the host page can use to send requests and get information from the
 * widget.
 *
 * The only values that should be returned in this object are values that may be accessible to customer code.
 */
function createChatInstance({ serviceManager, callRender, widgetVersion }) {
    // A flag to keep track if the instance has already been rendered.
    let wasRendered = false;
    function getMainWindow() {
        return {
            addClassName: (name) => serviceManager.mainWindow?.addClassName(name),
            removeClassName: (name) => serviceManager.mainWindow?.removeClassName(name),
        };
    }
    function getMessageInput() {
        return {
            getHTMLElement: () => serviceManager.mainWindow?.getMessageInput()?.getHTMLElement(),
            setValue: (value) => serviceManager.mainWindow?.getMessageInput()?.setValue(value),
            setEnableEnterKey: (value) => serviceManager.mainWindow?.getMessageInput()?.setEnableEnterKey(value),
            addChangeListener: (listener) => serviceManager.mainWindow?.getMessageInput()?.addChangeListener(listener),
            removeChangeListener: (listener) => serviceManager.mainWindow?.getMessageInput()?.removeChangeListener(listener),
        };
    }
    function getHomeScreenInput() {
        return {
            getHTMLElement: () => serviceManager.mainWindow?.getHomeScreenInput()?.getHTMLElement(),
            setValue: (value) => serviceManager.mainWindow?.getHomeScreenInput()?.setValue(value),
            setEnableEnterKey: (value) => serviceManager.mainWindow?.getHomeScreenInput()?.setEnableEnterKey(value),
            addChangeListener: (listener) => serviceManager.mainWindow?.getHomeScreenInput()?.addChangeListener(listener),
            removeChangeListener: (listener) => serviceManager.mainWindow?.getHomeScreenInput()?.removeChangeListener(listener),
        };
    }
    let instance = {
        render: () => {
            if (wasRendered) {
                consoleError('The render function has already been called!');
                return Promise.resolve(instance);
            }
            wasRendered = true;
            return callRender();
        },
        on: (handlers) => {
            serviceManager.eventBus.on(handlers);
            serviceManager.trackService.eventSystemTrack(handlers, 'on');
            return instance;
        },
        off: (handlers) => {
            serviceManager.eventBus.off(handlers);
            serviceManager.trackService.eventSystemTrack(handlers, 'off');
            return instance;
        },
        once: (handlers) => {
            serviceManager.eventBus.once(handlers);
            serviceManager.trackService.eventSystemTrack(handlers, 'once');
            return instance;
        },
        updateLanguagePack: (newPack) => {
            debugLog('Called instance.updateLanguagePack', newPack);
            return serviceManager.actions.updateLanguagePack(newPack);
        },
        updateLocale: (newLocale) => {
            debugLog('Called instance.updateLocale', newLocale);
            // Load language pack and attach config:language subscription.
            const languagesPromise = loadLanguagePack(newLocale);
            // Get date formatting for locale.
            const localePromise = loadLocale(newLocale);
            return Promise.all([languagesPromise, localePromise]).then(([languagePack, localePack]) => {
                serviceManager.trackService.track({
                    eventName: 'Locale updated',
                    user_properties: {
                        // Update user properties with the locale being used
                        Locale: newLocale,
                    },
                });
                // Update Redux with new values for language, locale, and messages.
                dayjs.locale(localePack);
                setIntl(serviceManager, localePack.name, languagePack);
                serviceManager.messageService.pendingLocale = true;
                serviceManager.messageService.localeIsExplicit = true;
            });
        },
        updateCSSVariables: (variables, remoteStyles) => {
            debugLog('Called instance.updateCSSVariables', variables);
            return serviceManager.actions.updateCSSVariables(variables, remoteStyles);
        },
        getLocale: () => {
            return serviceManager.store.getState().locale;
        },
        send: async (message, options) => {
            debugLog('Called instance.send', message, options);
            if (selectInputState(serviceManager.store.getState()).isReadonly) {
                throw new Error('You are unable to send messages in read only mode.');
            }
            return serviceManager.actions.send(message, MessageSendSource.INSTANCE_SEND, options);
        },
        doAutoScroll: (options = {}) => {
            debugLog('Called instance.doAutoScroll', options);
            serviceManager.mainWindow?.doAutoScroll?.(options);
        },
        destroySession: async () => {
            debugLog('Called instance.destroySession');
            await serviceManager.actions.destroySession(false);
        },
        destroy: () => {
            debugLog('Called instance.destroy');
            // Trigger an unmounting of all the components.
            serviceManager.store.dispatch(actions.setAppStateValue('isDestroyed', true));
            serviceManager.namespace.remove();
            serviceManager.container?.remove();
            instance = undefined;
        },
        updateAssistantInputFieldVisibility: (isVisible) => {
            debugLog('Called instance.updateAssistantInputFieldVisibility', isVisible);
            if (serviceManager.store.getState().config.public.__ibm__.isReadonly) {
                throw new Error('You are unable to change the input field visibility in read only mode.');
            }
            serviceManager.store.dispatch(actions.updateInputState({ fieldVisible: isVisible }, false));
        },
        updateInputIsDisabled: (isDisabled) => {
            debugLog('Called instance.updateInputIsDisabled', isDisabled);
            if (serviceManager.store.getState().config.public.__ibm__.isReadonly) {
                throw new Error('You are unable to change the input field disabled state in read only mode.');
            }
            serviceManager.store.dispatch(actions.updateInputState({ isReadonly: isDisabled }, false));
        },
        updateBotUnreadIndicatorVisibility: (isVisible) => {
            debugLog('Called instance.updateBotUnreadIndicatorVisibility', isVisible);
            serviceManager.store.dispatch(actions.setLauncherProperty('showUnreadIndicator', isVisible));
        },
        getWidgetVersion: () => widgetVersion,
        getIntl: () => serviceManager.intl,
        // Methods below this line require the web chat to have been rendered to show any results. Since they just update
        // Redux store we don't stop people from calling them, but we should warn them they did not call "render", yet.
        closeWindow: async () => {
            consoleWarn(`Called instance.closeWindow. ${WINDOW_WARNING}`);
            if (!wasRendered) {
                consoleError(`You tried to call "closeWindow" without ever having called the "render" method. There is nothing to close!`);
            }
            // As part of the view change work a bug was exposed where someone calling openWindow, closeWindow, or
            // toggleOpen, immediately after calling instance.render() (and without waiting for render to finish), would
            // trigger viewChange to throw an error because it was in the middle of changing the view to set the view to the
            // targetViewState and couldn't accept another view change request at that time. The solution is to force the
            // instance.openWindow, instance.closeWindow, and instance.toggleOpen functions to wait for this renderPromise
            // to complete before allowing them to try and trigger a view change. This can be removed when the deprecated
            // window methods and events are removed.
            await serviceManager.renderPromise;
            if (serviceManager.store.getState().persistedToBrowserStorage.launcherState.viewState.mainWindow) {
                // If the main window is currently open then fire the window:close and view:change events, and try to open
                // the launcher. Checking the current view state before changing the view used to be handled by the
                // closeMainWindow function in ActionsImpl, however now all of the view behavior is being handled by the
                // changeView function, and closeWindow specific logic has been moved here to keep the public facing behavior
                // consistent.
                const newViewState = await serviceManager.actions.changeView(ViewType.LAUNCHER, {
                    mainWindowCloseReason: MainWindowCloseReason.CALLED_CLOSE_WINDOW,
                });
                // If the main window is now closed then return true, otherwise return false.
                return !newViewState.mainWindow;
            }
            // If the main window is not open then return true.
            return true;
        },
        openWindow: async () => {
            consoleWarn(`Called instance.openWindow. ${WINDOW_WARNING}`);
            if (!wasRendered) {
                consoleError(`You tried to call "openWindow" without ever having called the "render" method. There is nothing to open!`);
            }
            // As part of the view change work a bug was exposed where someone calling openWindow, closeWindow, or
            // toggleOpen, immediately after calling instance.render() (and without waiting for render to finish), would
            // trigger viewChange to throw an error because it was in the middle of changing the view to set the view to the
            // targetViewState and couldn't accept another view change request at that time. The solution is to force the
            // instance.openWindow, instance.closeWindow, and instance.toggleOpen functions to wait for this renderPromise
            // to complete before allowing them to try and trigger a view change. This can be removed when the deprecated
            // window methods and events are removed.
            await serviceManager.renderPromise;
            const { mainWindow } = serviceManager.store.getState().persistedToBrowserStorage.launcherState.viewState;
            if (!mainWindow) {
                // If the mainWindow is not open then fire the window:open and view:change events, and try to open the main
                // window. Checking the current view state before changing the view used to be handled by the openMainWindow
                // function in ActionsImpl, however now all of the view behavior is being handled by the changeView function,
                // and openWindow specific logic has been moved here in order to keep the public facing behavior consistent.
                await serviceManager.actions.changeView(ViewType.MAIN_WINDOW, {
                    mainWindowOpenReason: MainWindowOpenReason.CALLED_OPEN_WINDOW,
                });
            }
        },
        toggleOpen: async () => {
            consoleWarn(`Called instance.toggleOpen. ${WINDOW_WARNING}`);
            if (!wasRendered) {
                consoleError(`You tried to call "toggleOpen" without ever having called the "render" method. There is nothing to toggle!`);
            }
            // As part of the view change work a bug was exposed where someone calling openWindow, closeWindow, or
            // toggleOpen, immediately after calling instance.render() (and without waiting for render to finish), would
            // trigger viewChange to throw an error because it was in the middle of changing the view to set the view to the
            // targetViewState and couldn't accept another view change request at that time. The solution is to force the
            // instance.openWindow, instance.closeWindow, and instance.toggleOpen functions to wait for this renderPromise
            // to complete before allowing them to try and trigger a view change. This can be removed when the deprecated
            // window methods and events are removed.
            await serviceManager.renderPromise;
            const { viewState } = serviceManager.store.getState().persistedToBrowserStorage.launcherState;
            if (viewState.mainWindow) {
                // If the main window is open then fire the window:close and view:change events, and try to close to the
                // launcher.
                await serviceManager.actions.changeView(ViewType.LAUNCHER, {
                    mainWindowCloseReason: MainWindowCloseReason.CALLED_CLOSE_WINDOW,
                });
            }
            else {
                // If the mainWindow is not open, then fire the window:open and view:change events, and try to open the main
                // window. This will hydrate the chat if it isn't already hydrated.
                await serviceManager.actions.changeView(ViewType.MAIN_WINDOW, {
                    mainWindowOpenReason: MainWindowOpenReason.CALLED_OPEN_WINDOW,
                });
            }
        },
        changeView: async (newView) => {
            debugLog('Called instance.changeView', newView);
            let issueWithNewView = false;
            if (!wasRendered) {
                consoleError(`You tried to call "changeView" without ever having called the "render" method. There is no view to change!`);
                issueWithNewView = true;
            }
            const viewTypeValues = Object.values(ViewType);
            if (typeof newView === 'string') {
                if (!viewTypeValues.includes(newView)) {
                    consoleError(`You tried to change the view but the view you specified is not a valid view name. Please use` +
                        ` the valid view names; ${viewTypeValues.join(', ')}.`);
                    issueWithNewView = true;
                }
            }
            else if (typeof newView === 'object') {
                Object.keys(newView).forEach(key => {
                    if (!viewTypeValues.includes(key)) {
                        // If an item in the newView object does not match any of the supported view types then log an error.
                        consoleError(`You tried to change the state of multiple views by providing an object, however you included the key` +
                            ` "${key}" within the object which is not a valid view name. Please use the valid view names; ` +
                            `${viewTypeValues.join(', ')}.`);
                        issueWithNewView = true;
                    }
                });
            }
            else {
                consoleError('You tried to change the view but the view you provided was not a string or an object. You can either change' +
                    ' to one of the supported views by providing a string, ex. "launcher", "mainWindow", or "tour". Or you can' +
                    ' change the state of multiple views by providing an object, ex. { "launcher": true, "mainWindow": false,' +
                    ' "tour": true }. Please use one of these supported options.');
                issueWithNewView = true;
            }
            if (!issueWithNewView) {
                // If there are no major issues then try to change the view to the newView.
                await serviceManager.actions.changeView(newView, { viewChangeReason: ViewChangeReason.CALLED_CHANGE_VIEW });
            }
        },
        addNotification: (notification) => {
            consoleWarn('This method is deprecated. Please use instance.notifications.addNotification() instead.');
            serviceManager.actions.addNotification(notification);
        },
        notifications: {
            addNotification: (notification) => {
                debugLog('Called instance.addNotification', notification);
                serviceManager.actions.addNotification(notification);
            },
            removeNotifications: (groupID) => {
                debugLog('Called instance.removeNotifications', groupID);
                serviceManager.actions.removeNotification(groupID);
            },
            removeAllNotifications: () => {
                debugLog('Called instance.removeAllNotifications');
                serviceManager.actions.removeAllNotifications();
            },
        },
        updateIdentityToken: (identityToken) => {
            debugLog('Called instance.updateIdentityToken');
            serviceManager.actions.updateIdentityToken(identityToken);
        },
        updateUserID: (newID) => {
            debugLog('Called instance.updateUserID', newID);
            if (!newID || typeof newID !== 'string') {
                consoleError(`The user ID must be a non-empty string`, newID);
            }
            serviceManager.actions.updateUserID(newID);
        },
        updateMainHeaderTitle: (title) => {
            debugLog('Called instance.updateMainHeaderTitle', title);
            if (!title) {
                title = null;
            }
            serviceManager.actions.updateMainHeaderTitle(title);
        },
        updateHomeScreenConfig: (homeScreenConfig) => {
            debugLog('Called instance.updateHomeScreenConfig', homeScreenConfig);
            const homeScreenConfigClone = cloneDeep(homeScreenConfig);
            const isAIThemeEnabled = serviceManager.store.getState().theme.useAITheme;
            const isToolingType = Boolean(serviceManager.store.getState().config.public.__ibm__?.toolingType);
            if (isAIThemeEnabled && !isToolingType) {
                if (homeScreenConfig?.background_gradient || homeScreenConfig?.background) {
                    // If the AI theme is enabled and the user is trying to change the home screen background then log a warning
                    // and ignore / remove the updates for the background. This is following the same behavior as
                    // updateCSSVariables which logs a warning and ignores updates for variables not supported in the AI theme.
                    consoleWarn('The home screen background can not be changed when the AI theme is enabled.');
                    delete homeScreenConfigClone.background_gradient;
                    delete homeScreenConfigClone.background;
                }
            }
            serviceManager.actions.updateHomeScreenConfig(withoutEmptyStarters(homeScreenConfigClone));
        },
        updateHistoryUserDefined: async (messageID, data) => {
            debugLog('Called instance.updateHistoryUserDefined', messageID, data);
            const { allMessagesByID } = serviceManager.store.getState();
            const { updateHistoryUserDefinedCount } = serviceManager.store.getState().persistedToBrowserStorage.chatState;
            const originalMessage = allMessagesByID[messageID];
            // Check that session history is enabled, that the originalMessage is a response, and that the usage limit hasn't
            // been exceeded before trying to save data.
            if (serviceManager.store.getState().config.public.disableSessionHistory) {
                consoleError(`You tried to save data related to a response but you don't have session history enabled, without session history you wont be able to retrieve the data you're trying to save. Please enable session history and try again.`);
            }
            else if (!isResponse(originalMessage)) {
                consoleError(`The messageID provided to instance.updateHistoryUserDefined() was not recognized as a message response from watsonx Assistant. Please makes sure the messageID's you're passing are for message responses and try again.`);
            }
            else if (messageID in updateHistoryUserDefinedCount &&
                updateHistoryUserDefinedCount[messageID] === UPDATE_HISTORY_USER_DEFINED_LIMIT) {
                consoleError(`You've saved data with updateHistoryUserDefined() ${UPDATE_HISTORY_USER_DEFINED_LIMIT} times for this messageID '${messageID}', that is the limit for the number of calls per messageID.`);
            }
            else {
                await serviceManager.actions.updateHistoryUserDefined(messageID, data);
            }
        },
        getState: () => serviceManager.actions.getPublicWebChatState(),
        writeableElements: createWriteableElementsProxy(serviceManager),
        scrollToMessage: (messageID, animate) => {
            debugLog('Called instance.scrollToMessage', messageID, animate);
            serviceManager.mainWindow?.doScrollToMessage(messageID, animate);
        },
        updateLauncherConfig: (config) => serviceManager.actions.updateLauncherConfig(config),
        updateLauncherAvatarURL: (source) => {
            debugLog('Called instance.updateLauncherAvatarURL', source);
            consoleLog('You called instance.updateLauncherAvatarURL. Please use instance.updateLauncherConfig instead. instance.updateLauncherAvatarURL is deprecated.');
            return serviceManager.actions.updateLauncherAvatarURL(source);
        },
        updateLauncherGreetingMessage: (greetingMessage, launcherType) => {
            debugLog('Called instance.updateLauncherGreetingMessage', greetingMessage, launcherType);
            consoleLog('You called instance.updateLauncherGreetingMessage. Please use instance.updateLauncherConfig instead. instance.updateLauncherGreetingMessage is deprecated.');
            if (launcherType && launcherType !== LauncherType.DESKTOP && launcherType !== LauncherType.MOBILE) {
                consoleError('You tried to set the launcher greeting message for a specific type of launcher but the type you specified was not "desktop" or "mobile". Please use one of the expected launcher types.');
            }
            else {
                serviceManager.actions.updateLauncherGreetingMessage(greetingMessage, launcherType);
            }
        },
        showLauncherGreetingMessage: (time = 0, launcherType) => {
            debugLog('Called instance.showLauncherGreetingMessage', time, launcherType);
            consoleLog('You called instance.showLauncherGreetingMessage. Please use instance.updateLauncherConfig instead. instance.showLauncherGreetingMessage is deprecated.');
            if (launcherType !== undefined && launcherType !== LauncherType.DESKTOP && launcherType !== LauncherType.MOBILE) {
                consoleError('You tried to show the launcher greeting message for a specific type of launcher but the type you specified was not "desktop" or "mobile". Please use one of the expected launcher types.');
            }
            else if (typeof time !== 'number') {
                consoleError('You tried to show the launcher greeting at a specific time but the value you specified was not a number. Please use a number representing the time, in milliseconds from now, that you want the launcher greeting to appear.');
            }
            else {
                serviceManager.actions.showLauncherGreetingMessage(time, launcherType);
            }
        },
        customPanels: serviceManager.customPanelManager,
        updateCustomMenuOptions: (typeOrOptions, options) => {
            debugLog('Called instance.updateCustomMenuOptions', typeOrOptions);
            let useOptions;
            if (typeof typeOrOptions === 'string') {
                if (typeOrOptions === CustomMenuType.BOT) {
                    consoleWarn('The first argument to updateCustomMenuOptions should no longer be "bot". It should be the' +
                        ' array of options to add to the custom menu. The type argument has been deprecated since there is now' +
                        ' only a single custom menu. For backwards compatibility, this method is currently allowing you to' +
                        ' specify "bot" as the first argument but this will be removed in a future release of web chat.');
                    useOptions = options;
                }
                else if (typeOrOptions === CustomMenuType.AGENT) {
                    consoleError('The updateCustomMenuOptions method no longer supports the "agent" menu options. The first argument to' +
                        ' this method should be the array of menu options for displaying in the main (bot) chat window.');
                    return;
                }
                else {
                    consoleError(`Invalid argument to updateCustomMenuOptions: ${typeOrOptions}.`);
                    return;
                }
            }
            else {
                useOptions = typeOrOptions;
            }
            serviceManager.store.dispatch(actions.setAppStateValue('customMenuOptions', useOptions));
        },
        restartConversation: async () => {
            debugLog('Called instance.restartConversation');
            return serviceManager.actions.restartConversation();
        },
        agentEndConversation: () => {
            debugLog('Called instance.agentEndConversation');
            consoleWarn(`The instance.agentEndConversation method is deprecated. Use instance.serviceDesk.endConversation instead.`);
            return serviceManager.actions.agentEndConversation(false);
        },
        updateIsTypingCounter(direction) {
            debugLog('Called instance.updateIsTypingCounter', direction);
            const { store } = serviceManager;
            if (direction === 'increase') {
                store.dispatch(actions.addIsTypingCounter(1));
            }
            else if (direction === 'decrease') {
                if (store.getState().botMessageState.isTypingCounter <= 0) {
                    consoleError('You cannot decrease the typing counter when it is already <= 0');
                    return;
                }
                store.dispatch(actions.addIsTypingCounter(-1));
            }
            else {
                consoleError(`[updateIsTypingCounter] Invalid direction: ${direction}. Valid values are "increase" and "decrease".`);
            }
        },
        updateIsLoadingCounter(direction) {
            debugLog('Called instance.updateIsLoadingCounter', direction);
            const { store } = serviceManager;
            if (direction === 'increase') {
                store.dispatch(actions.addIsLoadingCounter(1));
            }
            else if (direction === 'decrease') {
                if (store.getState().botMessageState.isLoadingCounter <= 0) {
                    consoleError('You cannot decrease the loading counter when it is already <= 0');
                    return;
                }
                store.dispatch(actions.addIsLoadingCounter(-1));
            }
            else {
                consoleError(`[updateIsLoadingCounter] Invalid direction: ${direction}. Valid values are "increase" and "decrease".`);
            }
        },
        updateIsChatLoadingCounter(direction) {
            debugLog('Called instance.updateIsChatLoadingCounter', direction);
            const { store } = serviceManager;
            if (direction === 'increase') {
                store.dispatch(actions.addIsHydratingCounter(1));
            }
            else if (direction === 'decrease') {
                if (store.getState().botMessageState.isHydratingCounter <= 0) {
                    consoleError('You cannot decrease the hydrating counter when it is already <= 0');
                    return;
                }
                store.dispatch(actions.addIsHydratingCounter(-1));
            }
            else {
                consoleError(`[updateIsChatLoadingCounter] Invalid direction: ${direction}. Valid values are "increase" and "decrease".`);
            }
        },
        updateHeaderConfig: (config) => {
            const configCopy = cloneDeep(config);
            serviceManager.store.dispatch(actions.updateChatHeaderConfig(configCopy));
        },
        updateMainHeaderAvatar: (config) => {
            serviceManager.store.dispatch(actions.updateMainHeaderAvatar(config));
        },
        elements: {
            getMainWindow,
            getMessageInput,
            getHomeScreenInput,
        },
        tours: {
            startTour: async (message) => {
                debugLog('Called instance.tours.startTour', message);
                await serviceManager.actions.send(message, MessageSendSource.START_TOUR_METHOD, { skipTourCard: true });
                serviceManager.trackService.track({
                    eventName: 'Tour interaction',
                    eventDescription: 'Instance method used.',
                    type: 'startTour',
                });
            },
            endTour: async () => {
                debugLog('Called instance.tours.endTour');
                serviceManager.actions.endTour({ viewChangeReason: ViewChangeReason.CALLED_END_TOUR });
                serviceManager.trackService.track({
                    eventName: 'Tour interaction',
                    eventDescription: 'Instance method used.',
                    type: 'endTour',
                });
            },
            goToNextStep: async () => {
                debugLog('Called instance.tours.goToNextStep');
                serviceManager.actions.changeStepInTour({ nextStep: true });
                serviceManager.trackService.track({
                    eventName: 'Tour interaction',
                    eventDescription: 'Instance method used.',
                    type: 'goToNextStep',
                });
            },
            goToStep: async (stepId) => {
                debugLog('Called instance.tours.goToStep', stepId);
                serviceManager.actions.goToSpecificTourStep(stepId);
                serviceManager.trackService.track({
                    eventName: 'Tour interaction',
                    eventDescription: 'Instance method used.',
                    type: 'goToStep',
                });
            },
        },
        messaging: {
            // TODO: Make sure tooling is updated to use this instead of the top level addMessage.
            addMessage: (message, options = {}) => {
                debugLog('Called instance.messaging.addMessage', message, options);
                serviceManager.messageService.messageLoadingManager.end();
                return serviceManager.actions.receive(message, options?.isLatestWelcomeNode ?? false, null, {
                    skipPause: true,
                    disableFadeAnimation: options?.disableFadeAnimation,
                });
            },
            addMessageChunk: async (chunk, options = {}) => {
                debugLog('Called instance.messaging.addMessageChunk', chunk, options);
                serviceManager.messageService.messageLoadingManager.end();
                await serviceManager.actions.receiveChunk(chunk, null, options);
            },
            removeMessages: async (messageIDs) => {
                debugLog('Called instance.messaging.removeMessages', messageIDs);
                return serviceManager.actions.removeMessages(messageIDs);
            },
            clearConversation: () => {
                debugLog('Called instance.messaging.clearConversation');
                return serviceManager.actions.restartConversation({
                    skipHydration: true,
                    deleteServerSession: false,
                    endAgentConversation: false,
                    fireEvents: false,
                });
            },
            insertHistory: (messages) => {
                debugLog('Called instance.messaging.insertHistory', messages);
                return serviceManager.actions.insertHistory(messages);
            },
        },
        requestFocus: () => {
            debugLog('Called instance.requestFocus');
            serviceManager.appWindow?.requestFocus();
        },
        serviceDesk: {
            endConversation: () => {
                debugLog('Called instance.serviceDesk.endConversation');
                return serviceManager.actions.agentEndConversation(false);
            },
            updateIsSuspended: async (isSuspended) => {
                debugLog('Called instance.serviceDesk.updateIsSuspended', isSuspended);
                return serviceManager.actions.agentUpdateIsSuspended(isSuspended);
            },
        },
    };
    if (serviceManager.store) {
        const currentState = serviceManager.store.getState();
        // Add the private variables if they are enabled.
        if (currentState.config.public.__ibm__?.allowPrivateMethods) {
            instance.updateBotName = (name) => serviceManager.actions.updateBotName(name);
            instance.updateBotAvatarURL = (url) => serviceManager.actions.updateBotAvatarURL(url);
            instance.updateAlternateSuggestionsConfig = config => serviceManager.actions.updateAlternateSuggestionsConfig(config);
            instance.updateBrandingConfig = config => serviceManager.actions.updateBrandingConfig(config);
            instance.logDebugInfo = () => logDebugInfo(serviceManager);
            // TODO: Remove this when tooling is updated to use the messaging.addMessage method.
            instance.addMessage = (message, isLatestWelcomeNode, requestMessage, sendOptions) => serviceManager.actions.receive(message, isLatestWelcomeNode, requestMessage, sendOptions);
            instance.updateThemeConfig = themeConfig => serviceManager.actions.updateThemeConfig(themeConfig);
            instance.updateStreamingConfig = streamingConfig => serviceManager.actions.updateStreamingConfig(streamingConfig);
            instance.updateCSSVariablesInternal = (publicVars, remoteStyles) => {
                serviceManager.actions.updateCSSVariables(publicVars, remoteStyles);
            };
        }
    }
    if (serviceManager.store.getState().config.public.debug) {
        consoleDebug('[ChatInstanceImpl] Created chat instance', instance);
    }
    return instance;
}
/**
 * Outputs debug information for the current state of the widget.
 */
function logDebugInfo(serviceManager) {
    console.group('Debug Info');
    console.group('Event bus');
    serviceManager.eventBus.logListeners();
    console.groupEnd();
    console.group('Application state');
    consoleLog('State', serviceManager.store.getState());
    console.groupEnd();
    console.groupEnd();
}
/**
 * Returns a proxy object of type WriteableElements that Deb will use to send set her custom content and should only
 * fire amplitude events once.
 */
function createWriteableElementsProxy(serviceManager) {
    const elementSet = new Set();
    const handler = {
        get(target, element) {
            if (!elementSet.has(element)) {
                elementSet.add(element);
                serviceManager.trackService.track({
                    eventName: 'Custom content used',
                    eventDescription: 'Deb has used a writeable element to display custom content.',
                    element,
                });
            }
            return target[element];
        },
    };
    return new Proxy(serviceManager.writeableElements, handler);
}

/**
 * This file contains the instance of the {@link LanguagePackContext} which is used to provide access to the
 * {@link LanguagePack}.
 */
const LanguagePackContext = React__default.createContext(null);

function useLanguagePack() {
    return useContext(LanguagePackContext);
}

var classnames = {exports: {}};

/*!
	Copyright (c) 2018 Jed Watson.
	Licensed under the MIT License (MIT), see
	http://jedwatson.github.io/classnames
*/

(function (module) {
	/* global define */

	(function () {

		var hasOwn = {}.hasOwnProperty;

		function classNames () {
			var classes = '';

			for (var i = 0; i < arguments.length; i++) {
				var arg = arguments[i];
				if (arg) {
					classes = appendClass(classes, parseValue(arg));
				}
			}

			return classes;
		}

		function parseValue (arg) {
			if (typeof arg === 'string' || typeof arg === 'number') {
				return arg;
			}

			if (typeof arg !== 'object') {
				return '';
			}

			if (Array.isArray(arg)) {
				return classNames.apply(null, arg);
			}

			if (arg.toString !== Object.prototype.toString && !arg.toString.toString().includes('[native code]')) {
				return arg.toString();
			}

			var classes = '';

			for (var key in arg) {
				if (hasOwn.call(arg, key) && arg[key]) {
					classes = appendClass(classes, key);
				}
			}

			return classes;
		}

		function appendClass (value, newClass) {
			if (!newClass) {
				return value;
			}
		
			if (value) {
				return value + ' ' + newClass;
			}
		
			return value + newClass;
		}

		if (module.exports) {
			classNames.default = classNames;
			module.exports = classNames;
		} else {
			window.classNames = classNames;
		}
	}()); 
} (classnames));

var classnamesExports = classnames.exports;
var cx = /*@__PURE__*/getDefaultExportFromCjs$1(classnamesExports);

function ErrorIcon(props) {
    return React__default.createElement(ErrorFilled, { className: cx('WACErrorIcon', props.className) });
}

function InlineError({ text }) {
    const languagePack = useLanguagePack();
    return (React__default.createElement("div", { className: "WAC__inlineError" },
        React__default.createElement("div", { className: "WAC__inlineError--iconHolder" },
            React__default.createElement(ErrorIcon, { className: "WAC__inlineError--icon" })),
        React__default.createElement("div", { className: "WAC__inlineError--text" }, text || languagePack.errors_generalContent)));
}

const WXO_SCRIPT_KEY = 'WXOScript';
const componentsPromises = {};
/**
 * Returns a promise that resolves with an MFE React component or the InlineError component on catch.
 */
async function componentPromiseCreator(scope, module, scriptKey) {
    let scriptExists;
    let container;
    let factory;
    let Module;
    let lastStep = 0;
    try {
        // If the script promise has not yet completed than wait for it to complete before continuing. Otherwise
        // we could attempt to access the window scope before it has been appropriately set by the script file.
        scriptExists = Boolean(scripts[scriptKey]);
        if (!scriptExists) {
            throw new Error('remoteEntry.js script does not exist.');
        }
        await scripts[scriptKey];
        lastStep = 1;
        // Initializes the share scope. This fills it with known provided modules from this build and all remotes
        await __webpack_init_sharing__('default');
        lastStep = 2;
        container = window[scope]; // or get the container somewhere else
        // Initialize the container, it may provide shared modules by calling window[scope].init()
        await container.init(__webpack_share_scopes__.default);
        lastStep = 3;
        // Get the actual component once we now that all shared resources are either provided by the parent, of have
        // been fetch as part of the call to init.
        factory = await container.get(module);
        lastStep = 4;
        // Pull it all together.
        Module = factory();
        lastStep = 5;
        // Return the module.
        consoleLog(`${module} from scope ${scope} loaded successfully.`, {
            scope,
            module,
            webpack_init_sharing: __webpack_init_sharing__,
            webpack_modules: __webpack_modules__,
            scriptExists,
            container,
            factory,
            Module,
            webpack_share_scopes: __webpack_share_scopes__,
        });
        return Module;
    }
    catch (error) {
        consoleError(`There was an error loading module ${module} from scope ${scope}. Last step: ${lastStep}.`, error, {
            scope,
            module,
            webpack_init_sharing: __webpack_init_sharing__,
            webpack_modules: __webpack_modules__,
            scriptExists,
            container,
            factory,
            Module,
            webpack_share_scopes: __webpack_share_scopes__,
        });
        return { default: InlineError };
    }
}
/**
 * This function handles fetching the remote module component for orchestrate to render at runtime. Since this is
 * essentially a fetch call, it returns a promise that should be handled through React.lazy.
 */
function loadWXOComponent(scope, module, scriptKey) {
    const key = `${scope}_${module}`;
    if (!componentsPromises[key]) {
        componentsPromises[key] = componentPromiseCreator(scope, module, scriptKey);
    }
    return componentsPromises[key];
}
const scripts = {};
/**
 * This function handles loading the orchestrate scripts needed to load the remote module components to render at
 * runtime. These scripts are tracked using promises so that the attempts to load components can wait until the scripts
 * have finished loading.
 */
function loadWXOScript(key, url, cspNonce) {
    if (!scripts[key]) {
        scripts[key] = resolvablePromise();
        const element = document.createElement('script');
        element.src = url;
        element.type = 'text/javascript';
        element.onerror = () => {
            consoleError(`${WA_CONSOLE_PREFIX} dynamic script error loading ${url}`);
            scripts[key].doReject();
        };
        element.onload = () => {
            scripts[key].doResolve();
        };
        if (cspNonce) {
            element.nonce = cspNonce;
        }
        document.head.appendChild(element);
    }
}

// Dayjs is a date library similar to moment, but that allows you to compose how much functionality you need. Here we
// add the ability to add strings in the format of a given locale.
dayjs.extend(LocalizedFormat);
let Chat$2 = class Chat {
    /**
     * Create new web chat instance.
     *
     * @param publicConfigProvided The public config provided by the user.
     * @param remoteInitConfigProvided The remote config provided by the server.
     * @param customHostElement The host element into which to render the widget. This is provided in the original public
     * config from the host page.
     * @param additionalChatParametersProvided An object with values important to web chat that are separate from the
     * external configs
     */
    constructor(publicConfigProvided, remoteInitConfigProvided, customHostElement, additionalChatParametersProvided) {
        if (publicConfigProvided?.debug) {
            consoleDebug('[WatsonAssistantChat] Constructed widget', publicConfigProvided);
        }
        const agentAppConfigValue = { agentAppConfig: returnAgentAppConfig(publicConfigProvided) };
        const originalConnectToAgentBehavior = publicConfigProvided.__ibm__?.connectToAgentBehavior;
        // Prepare config overrides for agent app, this is loaded last to make sure it always "wins" in the agent app scenario.
        const agentAppPublicOverrides = agentAppConfigValue.agentAppConfig.is_on ? AGENT_APP_DEFAULT_PUBLIC_CONFIG : {};
        const agentAppRemoteOverrides = agentAppConfigValue.agentAppConfig.is_on
            ? AGENT_APP_DEFAULT_REMOTE_INIT_CONFIG
            : {};
        // Make a clone of the public config so we don't get messed up if someone modifies this object outside of our
        // control. Also add in any default values that might be missing.
        const publicConfig = merge({}, DEFAULT_PUBLIC_CONFIG, publicConfigProvided, agentAppPublicOverrides, agentAppConfigValue);
        const { __ibm__ } = publicConfig;
        const remoteInitConfig = merge({}, DEFAULT_REMOTE_INIT_CONFIG, remoteInitConfigProvided, agentAppRemoteOverrides, publicConfig.servers.initConfig);
        this.additionalChatParameters = additionalChatParametersProvided || {};
        const { allowMockServiceDesk, isTrackingEnabled, isReadonly, toolingType } = __ibm__;
        // TODO: This should be removed from the generic component.
        if (toolingType) {
            if (!originalConnectToAgentBehavior) {
                switch (toolingType) {
                    case ToolingType.ANALYTICS:
                        // Note: Analytics doesn't currently have the information necessary for doing this correctly. It needs
                        // the MessageHistory#agent_no_service_desk property which it doesn't have. It will instead also act as
                        // if a service desk was available.
                        __ibm__.connectToAgentBehavior = ConnectToAgentBehaviorType.ERROR_MISSING;
                        break;
                    case ToolingType.WEB_CHAT_SETTINGS:
                    case ToolingType.ASSISTANT_PREVIEW:
                        // Note that there is a difference with the assistant preview in the old and new IAs. The old IA needs
                        // to manually set this value to PREVIEW because it uses a preview link integration that never has a
                        // service desk whereas the new IA uses the draft web chat integration that may have a service desk.
                        __ibm__.connectToAgentBehavior = ConnectToAgentBehaviorType.WARN_MISSING;
                        break;
                    default:
                        __ibm__.connectToAgentBehavior = ConnectToAgentBehaviorType.PREVIEW;
                        break;
                }
            }
        }
        if (isReadonly) {
            if (remoteInitConfig.alternate_suggestions?.is_on) {
                remoteInitConfig.alternate_suggestions.is_on = false;
            }
            if (remoteInitConfig.home_screen?.is_on) {
                remoteInitConfig.home_screen.is_on = false;
            }
        }
        if (__ibm__.overrideRemoteMainConfig) {
            consoleWarn(`IBM overrideRemoteMainConfig is deprecated. Use servers.mainConfig.`);
            publicConfig.servers.mainConfig = __ibm__.overrideRemoteMainConfig;
        }
        if (__ibm__.skipWelcome) {
            consoleWarn(`IBM skipWelcome is deprecated. Use messaging.skipWelcome.`);
            publicConfig.messaging.skipWelcome = true;
        }
        // Make sure tracking is disabled in the preview link and cp4d.
        __ibm__.isTrackingEnabled =
            isTrackingEnabled && toolingType !== ToolingType.PREVIEW_LINK && !publicConfig.cloudPrivateHostURL;
        // Note that because redux objects are immutable (which we ensured with the clone above), we can set the public
        // and publicOriginal values to the same object. If the public value is changed, a new object will be created.
        this.appConfig = {
            remote: {
                initConfig: remoteInitConfig,
            },
            public: publicConfig,
            asCarbonChat: this.additionalChatParameters.asCarbonChat,
        };
        if (allowMockServiceDesk && !hasSupportedServiceDesk(this.appConfig)) {
            remoteInitConfig.service_desk = {
                integration_type: null,
                is_on: true,
                integration_id: null,
            };
            publicConfig.serviceDeskFactory = createMockServiceDesk;
        }
        this.customHostElement = customHostElement;
    }
    /**
     * Starts the chat widget. This will return a promise that resolves to an instance of the started widget that can
     * be used by the host page to interact with the widget.
     */
    async start() {
        try {
            const result = await this.startInternal();
            return result.instance;
        }
        catch (error) {
            consoleError('[WatsonAssistantChat] There was an error starting your watsonx Assistant', error);
            if (this.serviceManager?.namespace) {
                this.serviceManager.namespace.remove();
            }
            return null;
        }
    }
    /**
     * Starts the chat widget. This will return a promise that resolves to an instance of the started widget that can
     * be used by the host page to interact with the widget. This internal version of the function also provides
     * access to helper objects that were constructed at the same time as the chat instance.
     */
    async startInternal() {
        const publicConfig = this.appConfig.public;
        // Check if integration has a service desk configured in the tooling. If they do, we will use it with
        // connect_to_agent response_type.
        const doesHaveServiceDesk = hasServiceDesk(this.appConfig) && !this.appConfig.public.agentAppConfig?.is_on;
        // Asynchronously load all of the various dependencies that the web chat depends on.
        const [languagePack, localePack, render, createHumanAgentService] = await Promise.all([
            loadLanguagePack(publicConfig.__ibm__?.locale, publicConfig.__ibm__?.languagePack),
            loadLocale(publicConfig.__ibm__?.locale),
            this.additionalChatParameters?.renderOverride
                ? Promise.resolve(this.additionalChatParameters.renderOverride)
                : loadRender(),
            doesHaveServiceDesk ? loadHAA() : Promise.resolve(null),
        ]);
        this.serviceManager = await createServiceManager(this.appConfig, this.additionalChatParameters);
        this.serviceManager.customHostElement = this.customHostElement;
        const { loadOrchestrateMFEs } = this.serviceManager.store.getState();
        // TODO: This should be removed and get rid of MFEs.
        if (loadOrchestrateMFEs) {
            // If we are in an environment that supports orchestrate forms then we need to kick off loading the script which
            // will be used to render orchestrate form components at runtime.
            const url = getWXOUrl(publicConfig);
            loadWXOScript(WXO_SCRIPT_KEY, url, publicConfig.cspNonce);
        }
        if (createHumanAgentService) {
            // We'll create the service now but we can't initialize it until we load the service desk information
            // from the remote config.
            this.serviceManager.humanAgentService = createHumanAgentService(this.serviceManager);
        }
        // Update Redux with new values for language, locale, and messages.
        setIntl(this.serviceManager, localePack.name, languagePack);
        // Tell dayjs to globally use the locale.
        dayjs.locale(localePack);
        // Here we render the application. If there was a linkID in the url or, the tour or main window are supposed to
        // be open we fire the view:change events and change the view. If there was a linkID then we will send a silent
        // message for the linkID's text. If the tour or main window are supposed to be open then we will hydrate the
        // chat if sessionHistory is enabled, or fetch the welcome node if it's disabled.
        const reallyRenderAndReturnInstance = async () => {
            // Render the React application.
            await render({ serviceManager: this.serviceManager });
            const linkIDText = this.pullLinkIDText();
            const initialState = this.serviceManager.store.getState();
            const { wasLoadedFromBrowser } = initialState.persistedToBrowserStorage.launcherState;
            const { targetViewState } = initialState;
            const { openChatByDefault } = initialState.config.public;
            const trackObject = {
                eventName: 'Widget Loaded',
                eventDescription: 'The widget has loaded',
                pageLinkType: linkIDText ? 'Text' : 'None',
            };
            if (this.additionalChatParameters.loadStartTime) {
                trackObject.loadTime = Date.now() - this.additionalChatParameters.loadStartTime;
            }
            this.serviceManager.trackService.track(trackObject);
            if (linkIDText) {
                // If linkIDText has been provided then fire the open:window and view:change events and try to open the main
                // window. Specify to not hydrate the chat because this will send a message instead of fetching the welcome
                // node and that process will perform the hydration itself.
                const newViewState = await this.serviceManager.actions.changeView(ViewType.MAIN_WINDOW, { mainWindowOpenReason: MainWindowOpenReason.LINK }, false);
                if (newViewState.mainWindow) {
                    // If the mainWindow is now visible then send a silent message with the linkID text.
                    this.serviceManager.actions.sendWithCatch(createMessageRequestForText(linkIDText), MessageSendSource.LINK_ID, {
                        silent: true,
                    });
                }
            }
            else if (isChannelTransferPage()) {
                // If this is a channel transfer page then fire the open:window and view:change events, and try to open the
                // main window and hydrate the chat.
                await this.serviceManager.actions.changeView(ViewType.MAIN_WINDOW, {
                    mainWindowOpenReason: MainWindowOpenReason.CHANNEL_TRANSFER,
                });
            }
            else if (targetViewState.mainWindow) {
                // If the main window is supposed to be open (because openChatByDefault was set to true, or because the
                // viewState, in session storage, said that the main window was previously open) then fire the open:window
                // and view:change events, and try to change the view and hydrate the chat. The default event reason will be
                // session_history.
                let mainWindowOpenReason = MainWindowOpenReason.SESSION_HISTORY;
                if (openChatByDefault && !wasLoadedFromBrowser) {
                    // If openChatByDefault is true, and this is a new session, then the window:open reason will be
                    // open_by_default and the view:change reason will be webChatDefault.
                    mainWindowOpenReason = MainWindowOpenReason.OPEN_BY_DEFAULT;
                }
                await this.serviceManager.actions.changeView(targetViewState, { mainWindowOpenReason });
            }
            else {
                // If a tour and/or the launcher are supposed to be open, or nothing is supposed to be open, then only fire
                // the view:change events and try to change the view.
                let viewChangeReason = ViewChangeReason.WEB_CHAT_LOADED;
                if (wasLoadedFromBrowser) {
                    // If this is an existing session then the reason should be sessionHistory.
                    viewChangeReason = ViewChangeReason.SESSION_HISTORY;
                }
                // If a tour is supposed to be open then try to hydrate the chat.
                const tryHydrating = targetViewState.tour;
                // If nothing is supposed to be open then force the view:change events to fire. Since the default viewState
                // is all views closed, and the targetViewState is all views closed, changeView would see two equal
                // viewStates and not bother trying to fire the events unless forced to.
                const forceViewChange = isEqual$1(targetViewState, VIEW_STATE_ALL_CLOSED);
                await this.serviceManager.actions.changeView(targetViewState, { viewChangeReason }, tryHydrating, forceViewChange);
            }
            // Lastly set the initialViewChangeComplete so that the launcher and other components can begin their
            // animations if they're visible.
            this.serviceManager.store.dispatch(actions.setInitialViewChangeComplete(true));
            return this.serviceManager.instance;
        };
        // As part of the view change work a bug was exposed where someone calling openWindow, closeWindow, or
        // toggleOpen, immediately after calling instance.render() (and without waiting for render to finish), would
        // trigger viewChange to throw an error because it was in the middle of changing the view to set the view to the
        // targetViewState and couldn't accept another view change request at that time. The solution is to force the
        // instance.openWindow, instance.closeWindow, and instance.toggleOpen functions to wait for this renderPromise
        // to complete before allowing them to try and trigger a view change. This can be removed when the deprecated
        // window methods and events are removed.
        const renderAndReturnInstance = () => {
            const promise = reallyRenderAndReturnInstance();
            this.serviceManager.renderPromise = promise;
            return promise;
        };
        // Create the "started" instance of the widget.
        this.serviceManager.instance = createChatInstance({
            serviceManager: this.serviceManager,
            callRender: renderAndReturnInstance,
            widgetVersion: VERSION,
        });
        const exportInstance = publicConfig.__ibm__?.exportInstance;
        if (exportInstance) {
            const propertyName = `${exportInstance}${this.serviceManager.namespace.suffix}`;
            window[propertyName] = this.serviceManager.instance;
            consoleWarn(`Exported web chat instance to window.${propertyName}`);
        }
        return { instance: this.serviceManager.instance, serviceManager: this.serviceManager };
    }
    /**
     * If the current URL has a link ID in it, this will determine the corresponding text that goes with the link ID and
     * return that text. This will also remove the link ID from the URL.
     */
    pullLinkIDText() {
        const linkID = pullValueParam(PARAM_LINK_ID);
        if (linkID) {
            // Now get the text that is associated with the ID.
            const text = this.serviceManager.store.getState().config.public.pageLinkConfig?.linkIDs?.[linkID]?.text;
            if (text) {
                return text;
            }
        }
        return null;
    }
};
/**
 * This method will check if publicConfig.agentAppConfig exists and populate it if not.
 *
 * @param publicConfigProvided The config object passed into web chat from the web page
 * @returns The normalized config for agent app.
 */
function returnAgentAppConfig(publicConfigProvided) {
    return publicConfigProvided.agentAppConfig || { is_on: false };
}
ensureChatBrowserWindowConfigExists();
const { loadScriptPromises } = getSharedConfig();
// In the testing environment, this is not set. In non-testing environments, this promise resolves with
// WatsonAssistantChat to let the entry script know we have loaded.
if (loadScriptPromises["0.3.2"]) {
    loadScriptPromises["0.3.2"].doResolve(Chat$2);
}

function _typeof$1(obj) {
  "@babel/helpers - typeof";

  return _typeof$1 = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (obj) {
    return typeof obj;
  } : function (obj) {
    return obj && "function" == typeof Symbol && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
  }, _typeof$1(obj);
}
function _classCallCheck$1(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}
function _defineProperties$1(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, _toPropertyKey$2(descriptor.key), descriptor);
  }
}
function _createClass$1(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties$1(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties$1(Constructor, staticProps);
  Object.defineProperty(Constructor, "prototype", {
    writable: false
  });
  return Constructor;
}
function _toPrimitive$2(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint);
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (String )(input);
}
function _toPropertyKey$2(arg) {
  var key = _toPrimitive$2(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}
function _classPrivateFieldGet(receiver, privateMap) {
  var descriptor = _classExtractFieldDescriptor(receiver, privateMap, "get");
  return _classApplyDescriptorGet(receiver, descriptor);
}
function _classPrivateFieldSet(receiver, privateMap, value) {
  var descriptor = _classExtractFieldDescriptor(receiver, privateMap, "set");
  _classApplyDescriptorSet(receiver, descriptor, value);
  return value;
}
function _classExtractFieldDescriptor(receiver, privateMap, action) {
  if (!privateMap.has(receiver)) {
    throw new TypeError("attempted to " + action + " private field on non-instance");
  }
  return privateMap.get(receiver);
}
function _classApplyDescriptorGet(receiver, descriptor) {
  if (descriptor.get) {
    return descriptor.get.call(receiver);
  }
  return descriptor.value;
}
function _classApplyDescriptorSet(receiver, descriptor, value) {
  if (descriptor.set) {
    descriptor.set.call(receiver, value);
  } else {
    if (!descriptor.writable) {
      throw new TypeError("attempted to set read only private field");
    }
    descriptor.value = value;
  }
}
function _checkPrivateRedeclaration(obj, privateCollection) {
  if (privateCollection.has(obj)) {
    throw new TypeError("Cannot initialize the same private elements twice on an object");
  }
}
function _classPrivateFieldInitSpec(obj, privateMap, value) {
  _checkPrivateRedeclaration(obj, privateMap);
  privateMap.set(obj, value);
}

var canonicalizeLocaleList = function canonicalizeLocaleList(locales) {
  if (!locales) return [];
  if (!Array.isArray(locales)) locales = [locales];
  var res = {};
  for (var i = 0; i < locales.length; ++i) {
    var _in$iw$ji$lc;
    var tag = locales[i];
    if (tag && _typeof$1(tag) === 'object') tag = String(tag);
    if (typeof tag !== 'string') {
      // Requiring tag to be a String or Object means that the Number value
      // NaN will not be interpreted as the language tag "nan", which stands
      // for Min Nan Chinese.
      var msg = "Locales should be strings, ".concat(JSON.stringify(tag), " isn't.");
      throw new TypeError(msg);
    }
    var parts = tag.split('-');

    // does not check for duplicate subtags
    if (!parts.every(function (subtag) {
      return /[a-z0-9]+/i.test(subtag);
    })) {
      var strTag = JSON.stringify(tag);
      var _msg = "The locale ".concat(strTag, " is not a structurally valid BCP 47 language tag.");
      throw new RangeError(_msg);
    }

    // always use lower case for primary language subtag
    var lc = parts[0].toLowerCase();
    // replace deprecated codes for Indonesian, Hebrew & Yiddish
    parts[0] = (_in$iw$ji$lc = {
      in: 'id',
      iw: 'he',
      ji: 'yi'
    }[lc]) !== null && _in$iw$ji$lc !== void 0 ? _in$iw$ji$lc : lc;
    res[parts.join('-')] = true;
  }
  return Object.keys(res);
};
function getType(opt) {
  var type = Object.prototype.hasOwnProperty.call(opt, 'type') && opt.type;
  if (!type) return 'cardinal';
  if (type === 'cardinal' || type === 'ordinal') return type;
  throw new RangeError('Not a valid plural type: ' + JSON.stringify(type));
}
function toNumber$1(value) {
  switch (_typeof$1(value)) {
    case 'number':
      return value;
    case 'bigint':
      throw new TypeError('Cannot convert a BigInt value to a number');
    default:
      return Number(value);
  }
}
function getPluralRules$1(NumberFormat, getSelector, getCategories, getRangeSelector) {
  var findLocale = function findLocale(locale) {
    do {
      if (getSelector(locale)) return locale;
      locale = locale.replace(/-?[^-]*$/, '');
    } while (locale);
    return null;
  };
  var resolveLocale = function resolveLocale(locales) {
    var canonicalLocales = canonicalizeLocaleList(locales);
    for (var i = 0; i < canonicalLocales.length; ++i) {
      var _lc = findLocale(canonicalLocales[i]);
      if (_lc) return _lc;
    }
    var lc = new NumberFormat().resolvedOptions().locale;
    return findLocale(lc);
  };
  var _locale = /*#__PURE__*/new WeakMap();
  var _range = /*#__PURE__*/new WeakMap();
  var _select = /*#__PURE__*/new WeakMap();
  var _type = /*#__PURE__*/new WeakMap();
  var _nf = /*#__PURE__*/new WeakMap();
  var PluralRules = /*#__PURE__*/function () {
    function PluralRules() {
      var locales = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
      var opt = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
      _classCallCheck$1(this, PluralRules);
      _classPrivateFieldInitSpec(this, _locale, {
        writable: true,
        value: void 0
      });
      _classPrivateFieldInitSpec(this, _range, {
        writable: true,
        value: void 0
      });
      _classPrivateFieldInitSpec(this, _select, {
        writable: true,
        value: void 0
      });
      _classPrivateFieldInitSpec(this, _type, {
        writable: true,
        value: void 0
      });
      _classPrivateFieldInitSpec(this, _nf, {
        writable: true,
        value: void 0
      });
      _classPrivateFieldSet(this, _locale, resolveLocale(locales));
      _classPrivateFieldSet(this, _select, getSelector(_classPrivateFieldGet(this, _locale)));
      _classPrivateFieldSet(this, _range, getRangeSelector(_classPrivateFieldGet(this, _locale)));
      _classPrivateFieldSet(this, _type, getType(opt));
      _classPrivateFieldSet(this, _nf, new NumberFormat('en', opt)); // make-plural expects latin digits with . decimal separator
    }
    _createClass$1(PluralRules, [{
      key: "resolvedOptions",
      value: function resolvedOptions() {
        var _classPrivateFieldGet2 = _classPrivateFieldGet(this, _nf).resolvedOptions(),
          minimumIntegerDigits = _classPrivateFieldGet2.minimumIntegerDigits,
          minimumFractionDigits = _classPrivateFieldGet2.minimumFractionDigits,
          maximumFractionDigits = _classPrivateFieldGet2.maximumFractionDigits,
          minimumSignificantDigits = _classPrivateFieldGet2.minimumSignificantDigits,
          maximumSignificantDigits = _classPrivateFieldGet2.maximumSignificantDigits,
          roundingPriority = _classPrivateFieldGet2.roundingPriority;
        var opt = {
          locale: _classPrivateFieldGet(this, _locale),
          type: _classPrivateFieldGet(this, _type),
          minimumIntegerDigits: minimumIntegerDigits,
          minimumFractionDigits: minimumFractionDigits,
          maximumFractionDigits: maximumFractionDigits
        };
        if (typeof minimumSignificantDigits === 'number') {
          opt.minimumSignificantDigits = minimumSignificantDigits;
          opt.maximumSignificantDigits = maximumSignificantDigits;
        }
        opt.pluralCategories = getCategories(_classPrivateFieldGet(this, _locale), _classPrivateFieldGet(this, _type) === 'ordinal').slice(0);
        opt.roundingPriority = roundingPriority || 'auto';
        return opt;
      }
    }, {
      key: "select",
      value: function select(number) {
        if (!(this instanceof PluralRules)) throw new TypeError("select() called on incompatible ".concat(this));
        if (typeof number !== 'number') number = Number(number);
        if (!isFinite(number)) return 'other';
        var fmt = _classPrivateFieldGet(this, _nf).format(Math.abs(number));
        return _classPrivateFieldGet(this, _select).call(this, fmt, _classPrivateFieldGet(this, _type) === 'ordinal');
      }
    }, {
      key: "selectRange",
      value: function selectRange(start, end) {
        if (!(this instanceof PluralRules)) throw new TypeError("selectRange() called on incompatible ".concat(this));
        if (start === undefined) throw new TypeError('start is undefined');
        if (end === undefined) throw new TypeError('end is undefined');
        var start_ = toNumber$1(start);
        var end_ = toNumber$1(end);
        if (!isFinite(start_)) throw new RangeError('start must be finite');
        if (!isFinite(end_)) throw new RangeError('end must be finite');
        return _classPrivateFieldGet(this, _range).call(this, this.select(start_), this.select(end_));
      }
    }], [{
      key: "supportedLocalesOf",
      value: function supportedLocalesOf(locales) {
        return canonicalizeLocaleList(locales).filter(findLocale);
      }
    }]);
    return PluralRules;
  }();
  if (typeof Symbol !== 'undefined' && Symbol.toStringTag) {
    Object.defineProperty(PluralRules.prototype, Symbol.toStringTag, {
      value: 'Intl.PluralRules',
      writable: false,
      configurable: true
    });
  }
  Object.defineProperty(PluralRules, 'prototype', {
    writable: false
  });
  return PluralRules;
}

var factory = /*#__PURE__*/Object.freeze({
  __proto__: null,
  default: getPluralRules$1
});

var require$$0$3 = /*@__PURE__*/getDefaultExportFromNamespaceIfNotNamed(factory);

var getPluralRules = require$$0$3;

function _interopDefaultLegacy$1 (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

function _mergeNamespaces$1(n, m) {
	m.forEach(function (e) {
		e && typeof e !== 'string' && !Array.isArray(e) && Object.keys(e).forEach(function (k) {
			if (k !== 'default' && !(k in n)) {
				var d = Object.getOwnPropertyDescriptor(e, k);
				Object.defineProperty(n, k, d.get ? d : {
					enumerable: true,
					get: function () { return e[k]; }
				});
			}
		});
	});
	return Object.freeze(n);
}

var getPluralRules__default = /*#__PURE__*/_interopDefaultLegacy$1(getPluralRules);

var commonjsGlobal = typeof globalThis !== 'undefined' ? globalThis : typeof window !== 'undefined' ? window : typeof commonjsGlobal$1 !== 'undefined' ? commonjsGlobal$1 : typeof self !== 'undefined' ? self : {};

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var plurals$1 = {exports: {}};

(function (module, exports) {
  var a = function a(n, ord) {
    if (ord) return 'other';
    return n == 1 ? 'one' : 'other';
  };
  var b = function b(n, ord) {
    if (ord) return 'other';
    return n == 0 || n == 1 ? 'one' : 'other';
  };
  var c = function c(n, ord) {
    if (ord) return 'other';
    return n >= 0 && n <= 1 ? 'one' : 'other';
  };
  var d = function d(n, ord) {
    var s = String(n).split('.'),
      v0 = !s[1];
    if (ord) return 'other';
    return n == 1 && v0 ? 'one' : 'other';
  };
  var e = function e(n, ord) {
    return 'other';
  };
  var f = function f(n, ord) {
    if (ord) return 'other';
    return n == 1 ? 'one' : n == 2 ? 'two' : 'other';
  };
  (function (root, plurals) {
    Object.defineProperty(plurals, '__esModule', {
      value: true
    });
    module.exports = plurals;
  })(commonjsGlobal, {
    af: a,
    ak: b,
    am: c,
    an: a,
    ar: function ar(n, ord) {
      var s = String(n).split('.'),
        t0 = Number(s[0]) == n,
        n100 = t0 && s[0].slice(-2);
      if (ord) return 'other';
      return n == 0 ? 'zero' : n == 1 ? 'one' : n == 2 ? 'two' : n100 >= 3 && n100 <= 10 ? 'few' : n100 >= 11 && n100 <= 99 ? 'many' : 'other';
    },
    ars: function ars(n, ord) {
      var s = String(n).split('.'),
        t0 = Number(s[0]) == n,
        n100 = t0 && s[0].slice(-2);
      if (ord) return 'other';
      return n == 0 ? 'zero' : n == 1 ? 'one' : n == 2 ? 'two' : n100 >= 3 && n100 <= 10 ? 'few' : n100 >= 11 && n100 <= 99 ? 'many' : 'other';
    },
    as: function as(n, ord) {
      if (ord) return n == 1 || n == 5 || n == 7 || n == 8 || n == 9 || n == 10 ? 'one' : n == 2 || n == 3 ? 'two' : n == 4 ? 'few' : n == 6 ? 'many' : 'other';
      return n >= 0 && n <= 1 ? 'one' : 'other';
    },
    asa: a,
    ast: d,
    az: function az(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        i10 = i.slice(-1),
        i100 = i.slice(-2),
        i1000 = i.slice(-3);
      if (ord) return i10 == 1 || i10 == 2 || i10 == 5 || i10 == 7 || i10 == 8 || i100 == 20 || i100 == 50 || i100 == 70 || i100 == 80 ? 'one' : i10 == 3 || i10 == 4 || i1000 == 100 || i1000 == 200 || i1000 == 300 || i1000 == 400 || i1000 == 500 || i1000 == 600 || i1000 == 700 || i1000 == 800 || i1000 == 900 ? 'few' : i == 0 || i10 == 6 || i100 == 40 || i100 == 60 || i100 == 90 ? 'many' : 'other';
      return n == 1 ? 'one' : 'other';
    },
    bal: function bal(n, ord) {
      return n == 1 ? 'one' : 'other';
    },
    be: function be(n, ord) {
      var s = String(n).split('.'),
        t0 = Number(s[0]) == n,
        n10 = t0 && s[0].slice(-1),
        n100 = t0 && s[0].slice(-2);
      if (ord) return (n10 == 2 || n10 == 3) && n100 != 12 && n100 != 13 ? 'few' : 'other';
      return n10 == 1 && n100 != 11 ? 'one' : n10 >= 2 && n10 <= 4 && (n100 < 12 || n100 > 14) ? 'few' : t0 && n10 == 0 || n10 >= 5 && n10 <= 9 || n100 >= 11 && n100 <= 14 ? 'many' : 'other';
    },
    bem: a,
    bez: a,
    bg: a,
    bho: b,
    bm: e,
    bn: function bn(n, ord) {
      if (ord) return n == 1 || n == 5 || n == 7 || n == 8 || n == 9 || n == 10 ? 'one' : n == 2 || n == 3 ? 'two' : n == 4 ? 'few' : n == 6 ? 'many' : 'other';
      return n >= 0 && n <= 1 ? 'one' : 'other';
    },
    bo: e,
    br: function br(n, ord) {
      var s = String(n).split('.'),
        t0 = Number(s[0]) == n,
        n10 = t0 && s[0].slice(-1),
        n100 = t0 && s[0].slice(-2),
        n1000000 = t0 && s[0].slice(-6);
      if (ord) return 'other';
      return n10 == 1 && n100 != 11 && n100 != 71 && n100 != 91 ? 'one' : n10 == 2 && n100 != 12 && n100 != 72 && n100 != 92 ? 'two' : (n10 == 3 || n10 == 4 || n10 == 9) && (n100 < 10 || n100 > 19) && (n100 < 70 || n100 > 79) && (n100 < 90 || n100 > 99) ? 'few' : n != 0 && t0 && n1000000 == 0 ? 'many' : 'other';
    },
    brx: a,
    bs: function bs(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        f = s[1] || '',
        v0 = !s[1],
        i10 = i.slice(-1),
        i100 = i.slice(-2),
        f10 = f.slice(-1),
        f100 = f.slice(-2);
      if (ord) return 'other';
      return v0 && i10 == 1 && i100 != 11 || f10 == 1 && f100 != 11 ? 'one' : v0 && i10 >= 2 && i10 <= 4 && (i100 < 12 || i100 > 14) || f10 >= 2 && f10 <= 4 && (f100 < 12 || f100 > 14) ? 'few' : 'other';
    },
    ca: function ca(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        v0 = !s[1],
        i1000000 = i.slice(-6);
      if (ord) return n == 1 || n == 3 ? 'one' : n == 2 ? 'two' : n == 4 ? 'few' : 'other';
      return n == 1 && v0 ? 'one' : i != 0 && i1000000 == 0 && v0 ? 'many' : 'other';
    },
    ce: a,
    ceb: function ceb(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        f = s[1] || '',
        v0 = !s[1],
        i10 = i.slice(-1),
        f10 = f.slice(-1);
      if (ord) return 'other';
      return v0 && (i == 1 || i == 2 || i == 3) || v0 && i10 != 4 && i10 != 6 && i10 != 9 || !v0 && f10 != 4 && f10 != 6 && f10 != 9 ? 'one' : 'other';
    },
    cgg: a,
    chr: a,
    ckb: a,
    cs: function cs(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        v0 = !s[1];
      if (ord) return 'other';
      return n == 1 && v0 ? 'one' : i >= 2 && i <= 4 && v0 ? 'few' : !v0 ? 'many' : 'other';
    },
    cy: function cy(n, ord) {
      if (ord) return n == 0 || n == 7 || n == 8 || n == 9 ? 'zero' : n == 1 ? 'one' : n == 2 ? 'two' : n == 3 || n == 4 ? 'few' : n == 5 || n == 6 ? 'many' : 'other';
      return n == 0 ? 'zero' : n == 1 ? 'one' : n == 2 ? 'two' : n == 3 ? 'few' : n == 6 ? 'many' : 'other';
    },
    da: function da(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        t0 = Number(s[0]) == n;
      if (ord) return 'other';
      return n == 1 || !t0 && (i == 0 || i == 1) ? 'one' : 'other';
    },
    de: d,
    doi: c,
    dsb: function dsb(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        f = s[1] || '',
        v0 = !s[1],
        i100 = i.slice(-2),
        f100 = f.slice(-2);
      if (ord) return 'other';
      return v0 && i100 == 1 || f100 == 1 ? 'one' : v0 && i100 == 2 || f100 == 2 ? 'two' : v0 && (i100 == 3 || i100 == 4) || f100 == 3 || f100 == 4 ? 'few' : 'other';
    },
    dv: a,
    dz: e,
    ee: a,
    el: a,
    en: function en(n, ord) {
      var s = String(n).split('.'),
        v0 = !s[1],
        t0 = Number(s[0]) == n,
        n10 = t0 && s[0].slice(-1),
        n100 = t0 && s[0].slice(-2);
      if (ord) return n10 == 1 && n100 != 11 ? 'one' : n10 == 2 && n100 != 12 ? 'two' : n10 == 3 && n100 != 13 ? 'few' : 'other';
      return n == 1 && v0 ? 'one' : 'other';
    },
    eo: a,
    es: function es(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        v0 = !s[1],
        i1000000 = i.slice(-6);
      if (ord) return 'other';
      return n == 1 ? 'one' : i != 0 && i1000000 == 0 && v0 ? 'many' : 'other';
    },
    et: d,
    eu: a,
    fa: c,
    ff: function ff(n, ord) {
      if (ord) return 'other';
      return n >= 0 && n < 2 ? 'one' : 'other';
    },
    fi: d,
    fil: function fil(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        f = s[1] || '',
        v0 = !s[1],
        i10 = i.slice(-1),
        f10 = f.slice(-1);
      if (ord) return n == 1 ? 'one' : 'other';
      return v0 && (i == 1 || i == 2 || i == 3) || v0 && i10 != 4 && i10 != 6 && i10 != 9 || !v0 && f10 != 4 && f10 != 6 && f10 != 9 ? 'one' : 'other';
    },
    fo: a,
    fr: function fr(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        v0 = !s[1],
        i1000000 = i.slice(-6);
      if (ord) return n == 1 ? 'one' : 'other';
      return n >= 0 && n < 2 ? 'one' : i != 0 && i1000000 == 0 && v0 ? 'many' : 'other';
    },
    fur: a,
    fy: d,
    ga: function ga(n, ord) {
      var s = String(n).split('.'),
        t0 = Number(s[0]) == n;
      if (ord) return n == 1 ? 'one' : 'other';
      return n == 1 ? 'one' : n == 2 ? 'two' : t0 && n >= 3 && n <= 6 ? 'few' : t0 && n >= 7 && n <= 10 ? 'many' : 'other';
    },
    gd: function gd(n, ord) {
      var s = String(n).split('.'),
        t0 = Number(s[0]) == n;
      if (ord) return n == 1 || n == 11 ? 'one' : n == 2 || n == 12 ? 'two' : n == 3 || n == 13 ? 'few' : 'other';
      return n == 1 || n == 11 ? 'one' : n == 2 || n == 12 ? 'two' : t0 && n >= 3 && n <= 10 || t0 && n >= 13 && n <= 19 ? 'few' : 'other';
    },
    gl: d,
    gsw: a,
    gu: function gu(n, ord) {
      if (ord) return n == 1 ? 'one' : n == 2 || n == 3 ? 'two' : n == 4 ? 'few' : n == 6 ? 'many' : 'other';
      return n >= 0 && n <= 1 ? 'one' : 'other';
    },
    guw: b,
    gv: function gv(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        v0 = !s[1],
        i10 = i.slice(-1),
        i100 = i.slice(-2);
      if (ord) return 'other';
      return v0 && i10 == 1 ? 'one' : v0 && i10 == 2 ? 'two' : v0 && (i100 == 0 || i100 == 20 || i100 == 40 || i100 == 60 || i100 == 80) ? 'few' : !v0 ? 'many' : 'other';
    },
    ha: a,
    haw: a,
    he: function he(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        v0 = !s[1];
      if (ord) return 'other';
      return i == 1 && v0 || i == 0 && !v0 ? 'one' : i == 2 && v0 ? 'two' : 'other';
    },
    hi: function hi(n, ord) {
      if (ord) return n == 1 ? 'one' : n == 2 || n == 3 ? 'two' : n == 4 ? 'few' : n == 6 ? 'many' : 'other';
      return n >= 0 && n <= 1 ? 'one' : 'other';
    },
    hnj: e,
    hr: function hr(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        f = s[1] || '',
        v0 = !s[1],
        i10 = i.slice(-1),
        i100 = i.slice(-2),
        f10 = f.slice(-1),
        f100 = f.slice(-2);
      if (ord) return 'other';
      return v0 && i10 == 1 && i100 != 11 || f10 == 1 && f100 != 11 ? 'one' : v0 && i10 >= 2 && i10 <= 4 && (i100 < 12 || i100 > 14) || f10 >= 2 && f10 <= 4 && (f100 < 12 || f100 > 14) ? 'few' : 'other';
    },
    hsb: function hsb(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        f = s[1] || '',
        v0 = !s[1],
        i100 = i.slice(-2),
        f100 = f.slice(-2);
      if (ord) return 'other';
      return v0 && i100 == 1 || f100 == 1 ? 'one' : v0 && i100 == 2 || f100 == 2 ? 'two' : v0 && (i100 == 3 || i100 == 4) || f100 == 3 || f100 == 4 ? 'few' : 'other';
    },
    hu: function hu(n, ord) {
      if (ord) return n == 1 || n == 5 ? 'one' : 'other';
      return n == 1 ? 'one' : 'other';
    },
    hy: function hy(n, ord) {
      if (ord) return n == 1 ? 'one' : 'other';
      return n >= 0 && n < 2 ? 'one' : 'other';
    },
    ia: d,
    id: e,
    ig: e,
    ii: e,
    io: d,
    is: function is(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        t = (s[1] || '').replace(/0+$/, ''),
        t0 = Number(s[0]) == n,
        i10 = i.slice(-1),
        i100 = i.slice(-2);
      if (ord) return 'other';
      return t0 && i10 == 1 && i100 != 11 || t % 10 == 1 && t % 100 != 11 ? 'one' : 'other';
    },
    it: function it(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        v0 = !s[1],
        i1000000 = i.slice(-6);
      if (ord) return n == 11 || n == 8 || n == 80 || n == 800 ? 'many' : 'other';
      return n == 1 && v0 ? 'one' : i != 0 && i1000000 == 0 && v0 ? 'many' : 'other';
    },
    iu: f,
    ja: e,
    jbo: e,
    jgo: a,
    jmc: a,
    jv: e,
    jw: e,
    ka: function ka(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        i100 = i.slice(-2);
      if (ord) return i == 1 ? 'one' : i == 0 || i100 >= 2 && i100 <= 20 || i100 == 40 || i100 == 60 || i100 == 80 ? 'many' : 'other';
      return n == 1 ? 'one' : 'other';
    },
    kab: function kab(n, ord) {
      if (ord) return 'other';
      return n >= 0 && n < 2 ? 'one' : 'other';
    },
    kaj: a,
    kcg: a,
    kde: e,
    kea: e,
    kk: function kk(n, ord) {
      var s = String(n).split('.'),
        t0 = Number(s[0]) == n,
        n10 = t0 && s[0].slice(-1);
      if (ord) return n10 == 6 || n10 == 9 || t0 && n10 == 0 && n != 0 ? 'many' : 'other';
      return n == 1 ? 'one' : 'other';
    },
    kkj: a,
    kl: a,
    km: e,
    kn: c,
    ko: e,
    ks: a,
    ksb: a,
    ksh: function ksh(n, ord) {
      if (ord) return 'other';
      return n == 0 ? 'zero' : n == 1 ? 'one' : 'other';
    },
    ku: a,
    kw: function kw(n, ord) {
      var s = String(n).split('.'),
        t0 = Number(s[0]) == n,
        n100 = t0 && s[0].slice(-2),
        n1000 = t0 && s[0].slice(-3),
        n100000 = t0 && s[0].slice(-5),
        n1000000 = t0 && s[0].slice(-6);
      if (ord) return t0 && n >= 1 && n <= 4 || n100 >= 1 && n100 <= 4 || n100 >= 21 && n100 <= 24 || n100 >= 41 && n100 <= 44 || n100 >= 61 && n100 <= 64 || n100 >= 81 && n100 <= 84 ? 'one' : n == 5 || n100 == 5 ? 'many' : 'other';
      return n == 0 ? 'zero' : n == 1 ? 'one' : n100 == 2 || n100 == 22 || n100 == 42 || n100 == 62 || n100 == 82 || t0 && n1000 == 0 && (n100000 >= 1000 && n100000 <= 20000 || n100000 == 40000 || n100000 == 60000 || n100000 == 80000) || n != 0 && n1000000 == 100000 ? 'two' : n100 == 3 || n100 == 23 || n100 == 43 || n100 == 63 || n100 == 83 ? 'few' : n != 1 && (n100 == 1 || n100 == 21 || n100 == 41 || n100 == 61 || n100 == 81) ? 'many' : 'other';
    },
    ky: a,
    lag: function lag(n, ord) {
      var s = String(n).split('.'),
        i = s[0];
      if (ord) return 'other';
      return n == 0 ? 'zero' : (i == 0 || i == 1) && n != 0 ? 'one' : 'other';
    },
    lb: a,
    lg: a,
    lij: function lij(n, ord) {
      var s = String(n).split('.'),
        v0 = !s[1],
        t0 = Number(s[0]) == n;
      if (ord) return n == 11 || n == 8 || t0 && n >= 80 && n <= 89 || t0 && n >= 800 && n <= 899 ? 'many' : 'other';
      return n == 1 && v0 ? 'one' : 'other';
    },
    lkt: e,
    ln: b,
    lo: function lo(n, ord) {
      if (ord) return n == 1 ? 'one' : 'other';
      return 'other';
    },
    lt: function lt(n, ord) {
      var s = String(n).split('.'),
        f = s[1] || '',
        t0 = Number(s[0]) == n,
        n10 = t0 && s[0].slice(-1),
        n100 = t0 && s[0].slice(-2);
      if (ord) return 'other';
      return n10 == 1 && (n100 < 11 || n100 > 19) ? 'one' : n10 >= 2 && n10 <= 9 && (n100 < 11 || n100 > 19) ? 'few' : f != 0 ? 'many' : 'other';
    },
    lv: function lv(n, ord) {
      var s = String(n).split('.'),
        f = s[1] || '',
        v = f.length,
        t0 = Number(s[0]) == n,
        n10 = t0 && s[0].slice(-1),
        n100 = t0 && s[0].slice(-2),
        f100 = f.slice(-2),
        f10 = f.slice(-1);
      if (ord) return 'other';
      return t0 && n10 == 0 || n100 >= 11 && n100 <= 19 || v == 2 && f100 >= 11 && f100 <= 19 ? 'zero' : n10 == 1 && n100 != 11 || v == 2 && f10 == 1 && f100 != 11 || v != 2 && f10 == 1 ? 'one' : 'other';
    },
    mas: a,
    mg: b,
    mgo: a,
    mk: function mk(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        f = s[1] || '',
        v0 = !s[1],
        i10 = i.slice(-1),
        i100 = i.slice(-2),
        f10 = f.slice(-1),
        f100 = f.slice(-2);
      if (ord) return i10 == 1 && i100 != 11 ? 'one' : i10 == 2 && i100 != 12 ? 'two' : (i10 == 7 || i10 == 8) && i100 != 17 && i100 != 18 ? 'many' : 'other';
      return v0 && i10 == 1 && i100 != 11 || f10 == 1 && f100 != 11 ? 'one' : 'other';
    },
    ml: a,
    mn: a,
    mo: function mo(n, ord) {
      var s = String(n).split('.'),
        v0 = !s[1],
        t0 = Number(s[0]) == n,
        n100 = t0 && s[0].slice(-2);
      if (ord) return n == 1 ? 'one' : 'other';
      return n == 1 && v0 ? 'one' : !v0 || n == 0 || n != 1 && n100 >= 1 && n100 <= 19 ? 'few' : 'other';
    },
    mr: function mr(n, ord) {
      if (ord) return n == 1 ? 'one' : n == 2 || n == 3 ? 'two' : n == 4 ? 'few' : 'other';
      return n == 1 ? 'one' : 'other';
    },
    ms: function ms(n, ord) {
      if (ord) return n == 1 ? 'one' : 'other';
      return 'other';
    },
    mt: function mt(n, ord) {
      var s = String(n).split('.'),
        t0 = Number(s[0]) == n,
        n100 = t0 && s[0].slice(-2);
      if (ord) return 'other';
      return n == 1 ? 'one' : n == 2 ? 'two' : n == 0 || n100 >= 3 && n100 <= 10 ? 'few' : n100 >= 11 && n100 <= 19 ? 'many' : 'other';
    },
    my: e,
    nah: a,
    naq: f,
    nb: a,
    nd: a,
    ne: function ne(n, ord) {
      var s = String(n).split('.'),
        t0 = Number(s[0]) == n;
      if (ord) return t0 && n >= 1 && n <= 4 ? 'one' : 'other';
      return n == 1 ? 'one' : 'other';
    },
    nl: d,
    nn: a,
    nnh: a,
    no: a,
    nqo: e,
    nr: a,
    nso: b,
    ny: a,
    nyn: a,
    om: a,
    or: function or(n, ord) {
      var s = String(n).split('.'),
        t0 = Number(s[0]) == n;
      if (ord) return n == 1 || n == 5 || t0 && n >= 7 && n <= 9 ? 'one' : n == 2 || n == 3 ? 'two' : n == 4 ? 'few' : n == 6 ? 'many' : 'other';
      return n == 1 ? 'one' : 'other';
    },
    os: a,
    osa: e,
    pa: b,
    pap: a,
    pcm: c,
    pl: function pl(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        v0 = !s[1],
        i10 = i.slice(-1),
        i100 = i.slice(-2);
      if (ord) return 'other';
      return n == 1 && v0 ? 'one' : v0 && i10 >= 2 && i10 <= 4 && (i100 < 12 || i100 > 14) ? 'few' : v0 && i != 1 && (i10 == 0 || i10 == 1) || v0 && i10 >= 5 && i10 <= 9 || v0 && i100 >= 12 && i100 <= 14 ? 'many' : 'other';
    },
    prg: function prg(n, ord) {
      var s = String(n).split('.'),
        f = s[1] || '',
        v = f.length,
        t0 = Number(s[0]) == n,
        n10 = t0 && s[0].slice(-1),
        n100 = t0 && s[0].slice(-2),
        f100 = f.slice(-2),
        f10 = f.slice(-1);
      if (ord) return 'other';
      return t0 && n10 == 0 || n100 >= 11 && n100 <= 19 || v == 2 && f100 >= 11 && f100 <= 19 ? 'zero' : n10 == 1 && n100 != 11 || v == 2 && f10 == 1 && f100 != 11 || v != 2 && f10 == 1 ? 'one' : 'other';
    },
    ps: a,
    pt: function pt(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        v0 = !s[1],
        i1000000 = i.slice(-6);
      if (ord) return 'other';
      return i == 0 || i == 1 ? 'one' : i != 0 && i1000000 == 0 && v0 ? 'many' : 'other';
    },
    pt_PT: function pt_PT(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        v0 = !s[1],
        i1000000 = i.slice(-6);
      if (ord) return 'other';
      return n == 1 && v0 ? 'one' : i != 0 && i1000000 == 0 && v0 ? 'many' : 'other';
    },
    rm: a,
    ro: function ro(n, ord) {
      var s = String(n).split('.'),
        v0 = !s[1],
        t0 = Number(s[0]) == n,
        n100 = t0 && s[0].slice(-2);
      if (ord) return n == 1 ? 'one' : 'other';
      return n == 1 && v0 ? 'one' : !v0 || n == 0 || n != 1 && n100 >= 1 && n100 <= 19 ? 'few' : 'other';
    },
    rof: a,
    ru: function ru(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        v0 = !s[1],
        i10 = i.slice(-1),
        i100 = i.slice(-2);
      if (ord) return 'other';
      return v0 && i10 == 1 && i100 != 11 ? 'one' : v0 && i10 >= 2 && i10 <= 4 && (i100 < 12 || i100 > 14) ? 'few' : v0 && i10 == 0 || v0 && i10 >= 5 && i10 <= 9 || v0 && i100 >= 11 && i100 <= 14 ? 'many' : 'other';
    },
    rwk: a,
    sah: e,
    saq: a,
    sat: f,
    sc: function sc(n, ord) {
      var s = String(n).split('.'),
        v0 = !s[1];
      if (ord) return n == 11 || n == 8 || n == 80 || n == 800 ? 'many' : 'other';
      return n == 1 && v0 ? 'one' : 'other';
    },
    scn: function scn(n, ord) {
      var s = String(n).split('.'),
        v0 = !s[1];
      if (ord) return n == 11 || n == 8 || n == 80 || n == 800 ? 'many' : 'other';
      return n == 1 && v0 ? 'one' : 'other';
    },
    sd: a,
    sdh: a,
    se: f,
    seh: a,
    ses: e,
    sg: e,
    sh: function sh(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        f = s[1] || '',
        v0 = !s[1],
        i10 = i.slice(-1),
        i100 = i.slice(-2),
        f10 = f.slice(-1),
        f100 = f.slice(-2);
      if (ord) return 'other';
      return v0 && i10 == 1 && i100 != 11 || f10 == 1 && f100 != 11 ? 'one' : v0 && i10 >= 2 && i10 <= 4 && (i100 < 12 || i100 > 14) || f10 >= 2 && f10 <= 4 && (f100 < 12 || f100 > 14) ? 'few' : 'other';
    },
    shi: function shi(n, ord) {
      var s = String(n).split('.'),
        t0 = Number(s[0]) == n;
      if (ord) return 'other';
      return n >= 0 && n <= 1 ? 'one' : t0 && n >= 2 && n <= 10 ? 'few' : 'other';
    },
    si: function si(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        f = s[1] || '';
      if (ord) return 'other';
      return n == 0 || n == 1 || i == 0 && f == 1 ? 'one' : 'other';
    },
    sk: function sk(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        v0 = !s[1];
      if (ord) return 'other';
      return n == 1 && v0 ? 'one' : i >= 2 && i <= 4 && v0 ? 'few' : !v0 ? 'many' : 'other';
    },
    sl: function sl(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        v0 = !s[1],
        i100 = i.slice(-2);
      if (ord) return 'other';
      return v0 && i100 == 1 ? 'one' : v0 && i100 == 2 ? 'two' : v0 && (i100 == 3 || i100 == 4) || !v0 ? 'few' : 'other';
    },
    sma: f,
    smi: f,
    smj: f,
    smn: f,
    sms: f,
    sn: a,
    so: a,
    sq: function sq(n, ord) {
      var s = String(n).split('.'),
        t0 = Number(s[0]) == n,
        n10 = t0 && s[0].slice(-1),
        n100 = t0 && s[0].slice(-2);
      if (ord) return n == 1 ? 'one' : n10 == 4 && n100 != 14 ? 'many' : 'other';
      return n == 1 ? 'one' : 'other';
    },
    sr: function sr(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        f = s[1] || '',
        v0 = !s[1],
        i10 = i.slice(-1),
        i100 = i.slice(-2),
        f10 = f.slice(-1),
        f100 = f.slice(-2);
      if (ord) return 'other';
      return v0 && i10 == 1 && i100 != 11 || f10 == 1 && f100 != 11 ? 'one' : v0 && i10 >= 2 && i10 <= 4 && (i100 < 12 || i100 > 14) || f10 >= 2 && f10 <= 4 && (f100 < 12 || f100 > 14) ? 'few' : 'other';
    },
    ss: a,
    ssy: a,
    st: a,
    su: e,
    sv: function sv(n, ord) {
      var s = String(n).split('.'),
        v0 = !s[1],
        t0 = Number(s[0]) == n,
        n10 = t0 && s[0].slice(-1),
        n100 = t0 && s[0].slice(-2);
      if (ord) return (n10 == 1 || n10 == 2) && n100 != 11 && n100 != 12 ? 'one' : 'other';
      return n == 1 && v0 ? 'one' : 'other';
    },
    sw: d,
    syr: a,
    ta: a,
    te: a,
    teo: a,
    th: e,
    ti: b,
    tig: a,
    tk: function tk(n, ord) {
      var s = String(n).split('.'),
        t0 = Number(s[0]) == n,
        n10 = t0 && s[0].slice(-1);
      if (ord) return n10 == 6 || n10 == 9 || n == 10 ? 'few' : 'other';
      return n == 1 ? 'one' : 'other';
    },
    tl: function tl(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        f = s[1] || '',
        v0 = !s[1],
        i10 = i.slice(-1),
        f10 = f.slice(-1);
      if (ord) return n == 1 ? 'one' : 'other';
      return v0 && (i == 1 || i == 2 || i == 3) || v0 && i10 != 4 && i10 != 6 && i10 != 9 || !v0 && f10 != 4 && f10 != 6 && f10 != 9 ? 'one' : 'other';
    },
    tn: a,
    to: e,
    tpi: e,
    tr: a,
    ts: a,
    tzm: function tzm(n, ord) {
      var s = String(n).split('.'),
        t0 = Number(s[0]) == n;
      if (ord) return 'other';
      return n == 0 || n == 1 || t0 && n >= 11 && n <= 99 ? 'one' : 'other';
    },
    ug: a,
    uk: function uk(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        v0 = !s[1],
        t0 = Number(s[0]) == n,
        n10 = t0 && s[0].slice(-1),
        n100 = t0 && s[0].slice(-2),
        i10 = i.slice(-1),
        i100 = i.slice(-2);
      if (ord) return n10 == 3 && n100 != 13 ? 'few' : 'other';
      return v0 && i10 == 1 && i100 != 11 ? 'one' : v0 && i10 >= 2 && i10 <= 4 && (i100 < 12 || i100 > 14) ? 'few' : v0 && i10 == 0 || v0 && i10 >= 5 && i10 <= 9 || v0 && i100 >= 11 && i100 <= 14 ? 'many' : 'other';
    },
    und: e,
    ur: d,
    uz: a,
    ve: a,
    vec: function vec(n, ord) {
      var s = String(n).split('.'),
        i = s[0],
        v0 = !s[1],
        i1000000 = i.slice(-6);
      if (ord) return n == 11 || n == 8 || n == 80 || n == 800 ? 'many' : 'other';
      return n == 1 && v0 ? 'one' : i != 0 && i1000000 == 0 && v0 ? 'many' : 'other';
    },
    vi: function vi(n, ord) {
      if (ord) return n == 1 ? 'one' : 'other';
      return 'other';
    },
    vo: a,
    vun: a,
    wa: b,
    wae: a,
    wo: e,
    xh: a,
    xog: a,
    yi: d,
    yo: e,
    yue: e,
    zh: e,
    zu: c
  });
})(plurals$1);
var plurals = /*@__PURE__*/getDefaultExportFromCjs(plurals$1.exports);

var P$1 = /*#__PURE__*/_mergeNamespaces$1({
	__proto__: null,
	'default': plurals
}, [plurals$1.exports]);

var pluralCategories$1 = {exports: {}};

(function (module, exports) {
  var z = "zero",
    o = "one",
    t = "two",
    f = "few",
    m = "many",
    x = "other";
  var a = {
    cardinal: [o, x],
    ordinal: [x]
  };
  var b = {
    cardinal: [o, x],
    ordinal: [o, x]
  };
  var c = {
    cardinal: [x],
    ordinal: [x]
  };
  var d = {
    cardinal: [o, t, x],
    ordinal: [x]
  };
  (function (root, pluralCategories) {
    Object.defineProperty(pluralCategories, '__esModule', {
      value: true
    });
    module.exports = pluralCategories;
  })(commonjsGlobal, {
    af: a,
    ak: a,
    am: a,
    an: a,
    ar: {
      cardinal: [z, o, t, f, m, x],
      ordinal: [x]
    },
    ars: {
      cardinal: [z, o, t, f, m, x],
      ordinal: [x]
    },
    as: {
      cardinal: [o, x],
      ordinal: [o, t, f, m, x]
    },
    asa: a,
    ast: a,
    az: {
      cardinal: [o, x],
      ordinal: [o, f, m, x]
    },
    bal: b,
    be: {
      cardinal: [o, f, m, x],
      ordinal: [f, x]
    },
    bem: a,
    bez: a,
    bg: a,
    bho: a,
    bm: c,
    bn: {
      cardinal: [o, x],
      ordinal: [o, t, f, m, x]
    },
    bo: c,
    br: {
      cardinal: [o, t, f, m, x],
      ordinal: [x]
    },
    brx: a,
    bs: {
      cardinal: [o, f, x],
      ordinal: [x]
    },
    ca: {
      cardinal: [o, m, x],
      ordinal: [o, t, f, x]
    },
    ce: a,
    ceb: a,
    cgg: a,
    chr: a,
    ckb: a,
    cs: {
      cardinal: [o, f, m, x],
      ordinal: [x]
    },
    cy: {
      cardinal: [z, o, t, f, m, x],
      ordinal: [z, o, t, f, m, x]
    },
    da: a,
    de: a,
    doi: a,
    dsb: {
      cardinal: [o, t, f, x],
      ordinal: [x]
    },
    dv: a,
    dz: c,
    ee: a,
    el: a,
    en: {
      cardinal: [o, x],
      ordinal: [o, t, f, x]
    },
    eo: a,
    es: {
      cardinal: [o, m, x],
      ordinal: [x]
    },
    et: a,
    eu: a,
    fa: a,
    ff: a,
    fi: a,
    fil: b,
    fo: a,
    fr: {
      cardinal: [o, m, x],
      ordinal: [o, x]
    },
    fur: a,
    fy: a,
    ga: {
      cardinal: [o, t, f, m, x],
      ordinal: [o, x]
    },
    gd: {
      cardinal: [o, t, f, x],
      ordinal: [o, t, f, x]
    },
    gl: a,
    gsw: a,
    gu: {
      cardinal: [o, x],
      ordinal: [o, t, f, m, x]
    },
    guw: a,
    gv: {
      cardinal: [o, t, f, m, x],
      ordinal: [x]
    },
    ha: a,
    haw: a,
    he: d,
    hi: {
      cardinal: [o, x],
      ordinal: [o, t, f, m, x]
    },
    hnj: c,
    hr: {
      cardinal: [o, f, x],
      ordinal: [x]
    },
    hsb: {
      cardinal: [o, t, f, x],
      ordinal: [x]
    },
    hu: b,
    hy: b,
    ia: a,
    id: c,
    ig: c,
    ii: c,
    io: a,
    is: a,
    it: {
      cardinal: [o, m, x],
      ordinal: [m, x]
    },
    iu: d,
    ja: c,
    jbo: c,
    jgo: a,
    jmc: a,
    jv: c,
    jw: c,
    ka: {
      cardinal: [o, x],
      ordinal: [o, m, x]
    },
    kab: a,
    kaj: a,
    kcg: a,
    kde: c,
    kea: c,
    kk: {
      cardinal: [o, x],
      ordinal: [m, x]
    },
    kkj: a,
    kl: a,
    km: c,
    kn: a,
    ko: c,
    ks: a,
    ksb: a,
    ksh: {
      cardinal: [z, o, x],
      ordinal: [x]
    },
    ku: a,
    kw: {
      cardinal: [z, o, t, f, m, x],
      ordinal: [o, m, x]
    },
    ky: a,
    lag: {
      cardinal: [z, o, x],
      ordinal: [x]
    },
    lb: a,
    lg: a,
    lij: {
      cardinal: [o, x],
      ordinal: [m, x]
    },
    lkt: c,
    ln: a,
    lo: {
      cardinal: [x],
      ordinal: [o, x]
    },
    lt: {
      cardinal: [o, f, m, x],
      ordinal: [x]
    },
    lv: {
      cardinal: [z, o, x],
      ordinal: [x]
    },
    mas: a,
    mg: a,
    mgo: a,
    mk: {
      cardinal: [o, x],
      ordinal: [o, t, m, x]
    },
    ml: a,
    mn: a,
    mo: {
      cardinal: [o, f, x],
      ordinal: [o, x]
    },
    mr: {
      cardinal: [o, x],
      ordinal: [o, t, f, x]
    },
    ms: {
      cardinal: [x],
      ordinal: [o, x]
    },
    mt: {
      cardinal: [o, t, f, m, x],
      ordinal: [x]
    },
    my: c,
    nah: a,
    naq: d,
    nb: a,
    nd: a,
    ne: b,
    nl: a,
    nn: a,
    nnh: a,
    no: a,
    nqo: c,
    nr: a,
    nso: a,
    ny: a,
    nyn: a,
    om: a,
    or: {
      cardinal: [o, x],
      ordinal: [o, t, f, m, x]
    },
    os: a,
    osa: c,
    pa: a,
    pap: a,
    pcm: a,
    pl: {
      cardinal: [o, f, m, x],
      ordinal: [x]
    },
    prg: {
      cardinal: [z, o, x],
      ordinal: [x]
    },
    ps: a,
    pt: {
      cardinal: [o, m, x],
      ordinal: [x]
    },
    pt_PT: {
      cardinal: [o, m, x],
      ordinal: [x]
    },
    rm: a,
    ro: {
      cardinal: [o, f, x],
      ordinal: [o, x]
    },
    rof: a,
    ru: {
      cardinal: [o, f, m, x],
      ordinal: [x]
    },
    rwk: a,
    sah: c,
    saq: a,
    sat: d,
    sc: {
      cardinal: [o, x],
      ordinal: [m, x]
    },
    scn: {
      cardinal: [o, x],
      ordinal: [m, x]
    },
    sd: a,
    sdh: a,
    se: d,
    seh: a,
    ses: c,
    sg: c,
    sh: {
      cardinal: [o, f, x],
      ordinal: [x]
    },
    shi: {
      cardinal: [o, f, x],
      ordinal: [x]
    },
    si: a,
    sk: {
      cardinal: [o, f, m, x],
      ordinal: [x]
    },
    sl: {
      cardinal: [o, t, f, x],
      ordinal: [x]
    },
    sma: d,
    smi: d,
    smj: d,
    smn: d,
    sms: d,
    sn: a,
    so: a,
    sq: {
      cardinal: [o, x],
      ordinal: [o, m, x]
    },
    sr: {
      cardinal: [o, f, x],
      ordinal: [x]
    },
    ss: a,
    ssy: a,
    st: a,
    su: c,
    sv: b,
    sw: a,
    syr: a,
    ta: a,
    te: a,
    teo: a,
    th: c,
    ti: a,
    tig: a,
    tk: {
      cardinal: [o, x],
      ordinal: [f, x]
    },
    tl: b,
    tn: a,
    to: c,
    tpi: c,
    tr: a,
    ts: a,
    tzm: a,
    ug: a,
    uk: {
      cardinal: [o, f, m, x],
      ordinal: [f, x]
    },
    und: c,
    ur: a,
    uz: a,
    ve: a,
    vec: {
      cardinal: [o, m, x],
      ordinal: [m, x]
    },
    vi: {
      cardinal: [x],
      ordinal: [o, x]
    },
    vo: a,
    vun: a,
    wa: a,
    wae: a,
    wo: c,
    xh: a,
    xog: a,
    yi: a,
    yo: c,
    yue: c,
    zh: c,
    zu: a
  });
})(pluralCategories$1);
var pluralCategories = /*@__PURE__*/getDefaultExportFromCjs(pluralCategories$1.exports);

var C = /*#__PURE__*/_mergeNamespaces$1({
	__proto__: null,
	'default': pluralCategories
}, [pluralCategories$1.exports]);

var ranges$1 = {exports: {}};

(function (module, exports) {
  var a = function a(start, end) {
    return "other";
  };
  var b = function b(start, end) {
    return start === "other" && end === "one" ? "one" : "other";
  };
  var c = function c(start, end) {
    return end || "other";
  };
  (function (root, pluralRanges) {
    Object.defineProperty(pluralRanges, '__esModule', {
      value: true
    });
    module.exports = pluralRanges;
  })(commonjsGlobal, {
    af: a,
    ak: b,
    am: c,
    an: a,
    ar: function ar(start, end) {
      return end === "few" ? "few" : end === "many" ? "many" : start === "zero" && end === "one" ? "zero" : start === "zero" && end === "two" ? "zero" : "other";
    },
    as: c,
    az: c,
    be: c,
    bg: a,
    bn: c,
    bs: c,
    ca: a,
    cs: c,
    cy: c,
    da: c,
    de: c,
    el: c,
    en: a,
    es: a,
    et: a,
    eu: a,
    fa: b,
    fi: a,
    fil: c,
    fr: c,
    ga: c,
    gl: c,
    gsw: c,
    gu: c,
    he: a,
    hi: c,
    hr: c,
    hu: c,
    hy: c,
    ia: a,
    id: a,
    io: a,
    is: c,
    it: c,
    ja: a,
    ka: function ka(start, end) {
      return start || "other";
    },
    kk: c,
    km: a,
    kn: c,
    ko: a,
    ky: c,
    lij: c,
    lo: a,
    lt: c,
    lv: function lv(start, end) {
      return end === "one" ? "one" : "other";
    },
    mk: a,
    ml: c,
    mn: c,
    mr: c,
    ms: a,
    my: a,
    nb: a,
    ne: c,
    nl: c,
    no: a,
    or: b,
    pa: c,
    pcm: a,
    pl: c,
    ps: c,
    pt: c,
    ro: function ro(start, end) {
      return end === "few" ? "few" : end === "one" ? "few" : "other";
    },
    ru: c,
    sc: c,
    scn: c,
    sd: b,
    si: function si(start, end) {
      return start === "one" && end === "one" ? "one" : "other";
    },
    sk: c,
    sl: function sl(start, end) {
      return end === "few" ? "few" : end === "one" ? "few" : end === "two" ? "two" : "other";
    },
    sq: c,
    sr: c,
    sv: a,
    sw: c,
    ta: c,
    te: c,
    th: a,
    tk: c,
    tr: c,
    ug: c,
    uk: c,
    ur: a,
    uz: c,
    vi: a,
    yue: a,
    zh: a,
    zu: c
  });
})(ranges$1);
var ranges = /*@__PURE__*/getDefaultExportFromCjs(ranges$1.exports);

var R = /*#__PURE__*/_mergeNamespaces$1({
	__proto__: null,
	'default': ranges
}, [ranges$1.exports]);

// In a .mjs context, CommonJS imports only expose the default endpoint. We're
// using them here because with this many small functions, bundlers produce less
// cruft than for ES module exports.
var Plurals = plurals || P$1;
var Categories = pluralCategories || C;
var RangePlurals = ranges || R;

// make-plural exports are cast with safe-identifier to be valid JS identifiers
var id = function id(lc) {
  return lc === 'pt-PT' ? 'pt_PT' : lc;
};
var getSelector = function getSelector(lc) {
  return Plurals[id(lc)];
};
var getCategories = function getCategories(lc, ord) {
  return Categories[id(lc)][ord ? 'ordinal' : 'cardinal'];
};
var getRangeSelector = function getRangeSelector(lc) {
  return RangePlurals[id(lc)];
};
var PluralRules$1 = getPluralRules__default["default"](Intl.NumberFormat, getSelector, getCategories, getRangeSelector);

var pluralRules = PluralRules$1;

var PluralRules = pluralRules;

function _interopDefaultLegacy (e) { return e && typeof e === 'object' && 'default' in e ? e : { 'default': e }; }

var PluralRules__default = /*#__PURE__*/_interopDefaultLegacy(PluralRules);

if (typeof Intl === 'undefined') {
  if (typeof commonjsGlobal$1 !== 'undefined') {
    commonjsGlobal$1.Intl = {
      PluralRules: PluralRules__default["default"]
    };
  } else if (typeof window !== 'undefined') {
    window.Intl = {
      PluralRules: PluralRules__default["default"]
    };
  } else {
    commonjsGlobal$1.Intl = {
      PluralRules: PluralRules__default["default"]
    };
  }
  PluralRules__default["default"].polyfill = true;
} else if (!Intl.PluralRules || !Intl.PluralRules.prototype.selectRange) {
  Intl.PluralRules = PluralRules__default["default"];
  PluralRules__default["default"].polyfill = true;
} else {
  var test$1 = ['en', 'es', 'ru', 'zh'];
  var supported = Intl.PluralRules.supportedLocalesOf(test$1);
  if (supported.length < test$1.length) {
    Intl.PluralRules = PluralRules__default["default"];
    PluralRules__default["default"].polyfill = true;
  }
}

const AriaAnnouncerContext = React__default.createContext(null);

/**
 * This file contains the instance of the {@link ServiceManagerContext} which is used to provide access to the
 * {@link ServiceManager}.
 */
const ServiceManagerContext = React__default.createContext(null);

function useServiceManager() {
    return useContext(ServiceManagerContext);
}

const t=t=>"object"==typeof t&&null!=t&&1===t.nodeType,e=(t,e)=>(!e||"hidden"!==t)&&("visible"!==t&&"clip"!==t),n=(t,n)=>{if(t.clientHeight<t.scrollHeight||t.clientWidth<t.scrollWidth){const o=getComputedStyle(t,null);return e(o.overflowY,n)||e(o.overflowX,n)||(t=>{const e=(t=>{if(!t.ownerDocument||!t.ownerDocument.defaultView)return null;try{return t.ownerDocument.defaultView.frameElement}catch(t){return null}})(t);return !!e&&(e.clientHeight<t.scrollHeight||e.clientWidth<t.scrollWidth)})(t)}return !1},o=(t,e,n,o,l,r,i,s)=>r<t&&i>e||r>t&&i<e?0:r<=t&&s<=n||i>=e&&s>=n?r-t-o:i>e&&s<n||r<t&&s>n?i-e+l:0,l=t=>{const e=t.parentElement;return null==e?t.getRootNode().host||null:e},r=(e,r)=>{var i,s,d,h;if("undefined"==typeof document)return [];const{scrollMode:c,block:f,inline:u,boundary:a,skipOverflowHiddenElements:g}=r,p="function"==typeof a?a:t=>t!==a;if(!t(e))throw new TypeError("Invalid target");const m=document.scrollingElement||document.documentElement,w=[];let W=e;for(;t(W)&&p(W);){if(W=l(W),W===m){w.push(W);break}null!=W&&W===document.body&&n(W)&&!n(document.documentElement)||null!=W&&n(W,g)&&w.push(W);}const b=null!=(s=null==(i=window.visualViewport)?void 0:i.width)?s:innerWidth,H=null!=(h=null==(d=window.visualViewport)?void 0:d.height)?h:innerHeight,{scrollX:y,scrollY:M}=window,{height:v,width:E,top:x,right:C,bottom:I,left:R}=e.getBoundingClientRect(),{top:T,right:B,bottom:F,left:V}=(t=>{const e=window.getComputedStyle(t);return {top:parseFloat(e.scrollMarginTop)||0,right:parseFloat(e.scrollMarginRight)||0,bottom:parseFloat(e.scrollMarginBottom)||0,left:parseFloat(e.scrollMarginLeft)||0}})(e);let k="start"===f||"nearest"===f?x-T:"end"===f?I+F:x+v/2-T+F,D="center"===u?R+E/2-V+B:"end"===u?C+B:R-V;const L=[];for(let t=0;t<w.length;t++){const e=w[t],{height:n,width:l,top:r,right:i,bottom:s,left:d}=e.getBoundingClientRect();if("if-needed"===c&&x>=0&&R>=0&&I<=H&&C<=b&&x>=r&&I<=s&&R>=d&&C<=i)return L;const h=getComputedStyle(e),a=parseInt(h.borderLeftWidth,10),g=parseInt(h.borderTopWidth,10),p=parseInt(h.borderRightWidth,10),W=parseInt(h.borderBottomWidth,10);let T=0,B=0;const F="offsetWidth"in e?e.offsetWidth-e.clientWidth-a-p:0,V="offsetHeight"in e?e.offsetHeight-e.clientHeight-g-W:0,S="offsetWidth"in e?0===e.offsetWidth?0:l/e.offsetWidth:0,X="offsetHeight"in e?0===e.offsetHeight?0:n/e.offsetHeight:0;if(m===e)T="start"===f?k:"end"===f?k-H:"nearest"===f?o(M,M+H,H,g,W,M+k,M+k+v,v):k-H/2,B="start"===u?D:"center"===u?D-b/2:"end"===u?D-b:o(y,y+b,b,a,p,y+D,y+D+E,E),T=Math.max(0,T+M),B=Math.max(0,B+y);else {T="start"===f?k-r-g:"end"===f?k-s+W+V:"nearest"===f?o(r,s,n,g,W+V,k,k+v,v):k-(r+n/2)+V/2,B="start"===u?D-d-a:"center"===u?D-(d+l/2)+F/2:"end"===u?D-i+p+F:o(d,i,l,a,p+F,D,D+E,E);const{scrollLeft:t,scrollTop:h}=e;T=0===X?0:Math.max(0,Math.min(h+T/X,e.scrollHeight-n/X+V)),B=0===S?0:Math.max(0,Math.min(t+B/S,e.scrollWidth-l/S+F)),k+=h-T,D+=t-B;}L.push({el:e,top:T,left:B});}return L};

/*!
* tabbable 6.2.0
* @license MIT, https://github.com/focus-trap/tabbable/blob/master/LICENSE
*/
// NOTE: separate `:not()` selectors has broader browser support than the newer
//  `:not([inert], [inert] *)` (Feb 2023)
// CAREFUL: JSDom does not support `:not([inert] *)` as a selector; using it causes
//  the entire query to fail, resulting in no nodes found, which will break a lot
//  of things... so we have to rely on JS to identify nodes inside an inert container
var candidateSelectors = ['input:not([inert])', 'select:not([inert])', 'textarea:not([inert])', 'a[href]:not([inert])', 'button:not([inert])', '[tabindex]:not(slot):not([inert])', 'audio[controls]:not([inert])', 'video[controls]:not([inert])', '[contenteditable]:not([contenteditable="false"]):not([inert])', 'details>summary:first-of-type:not([inert])', 'details:not([inert])'];
var candidateSelector = /* #__PURE__ */candidateSelectors.join(',');
var NoElement = typeof Element === 'undefined';
var matches = NoElement ? function () {} : Element.prototype.matches || Element.prototype.msMatchesSelector || Element.prototype.webkitMatchesSelector;
var getRootNode = !NoElement && Element.prototype.getRootNode ? function (element) {
  var _element$getRootNode;
  return element === null || element === void 0 ? void 0 : (_element$getRootNode = element.getRootNode) === null || _element$getRootNode === void 0 ? void 0 : _element$getRootNode.call(element);
} : function (element) {
  return element === null || element === void 0 ? void 0 : element.ownerDocument;
};

/**
 * Determines if a node is inert or in an inert ancestor.
 * @param {Element} [node]
 * @param {boolean} [lookUp] If true and `node` is not inert, looks up at ancestors to
 *  see if any of them are inert. If false, only `node` itself is considered.
 * @returns {boolean} True if inert itself or by way of being in an inert ancestor.
 *  False if `node` is falsy.
 */
var isInert = function isInert(node, lookUp) {
  var _node$getAttribute;
  if (lookUp === void 0) {
    lookUp = true;
  }
  // CAREFUL: JSDom does not support inert at all, so we can't use the `HTMLElement.inert`
  //  JS API property; we have to check the attribute, which can either be empty or 'true';
  //  if it's `null` (not specified) or 'false', it's an active element
  var inertAtt = node === null || node === void 0 ? void 0 : (_node$getAttribute = node.getAttribute) === null || _node$getAttribute === void 0 ? void 0 : _node$getAttribute.call(node, 'inert');
  var inert = inertAtt === '' || inertAtt === 'true';

  // NOTE: this could also be handled with `node.matches('[inert], :is([inert] *)')`
  //  if it weren't for `matches()` not being a function on shadow roots; the following
  //  code works for any kind of node
  // CAREFUL: JSDom does not appear to support certain selectors like `:not([inert] *)`
  //  so it likely would not support `:is([inert] *)` either...
  var result = inert || lookUp && node && isInert(node.parentNode); // recursive

  return result;
};

/**
 * Determines if a node's content is editable.
 * @param {Element} [node]
 * @returns True if it's content-editable; false if it's not or `node` is falsy.
 */
var isContentEditable = function isContentEditable(node) {
  var _node$getAttribute2;
  // CAREFUL: JSDom does not support the `HTMLElement.isContentEditable` API so we have
  //  to use the attribute directly to check for this, which can either be empty or 'true';
  //  if it's `null` (not specified) or 'false', it's a non-editable element
  var attValue = node === null || node === void 0 ? void 0 : (_node$getAttribute2 = node.getAttribute) === null || _node$getAttribute2 === void 0 ? void 0 : _node$getAttribute2.call(node, 'contenteditable');
  return attValue === '' || attValue === 'true';
};

/**
 * @param {Element} el container to check in
 * @param {boolean} includeContainer add container to check
 * @param {(node: Element) => boolean} filter filter candidates
 * @returns {Element[]}
 */
var getCandidates = function getCandidates(el, includeContainer, filter) {
  // even if `includeContainer=false`, we still have to check it for inertness because
  //  if it's inert, all its children are inert
  if (isInert(el)) {
    return [];
  }
  var candidates = Array.prototype.slice.apply(el.querySelectorAll(candidateSelector));
  if (includeContainer && matches.call(el, candidateSelector)) {
    candidates.unshift(el);
  }
  candidates = candidates.filter(filter);
  return candidates;
};

/**
 * @callback GetShadowRoot
 * @param {Element} element to check for shadow root
 * @returns {ShadowRoot|boolean} ShadowRoot if available or boolean indicating if a shadowRoot is attached but not available.
 */

/**
 * @callback ShadowRootFilter
 * @param {Element} shadowHostNode the element which contains shadow content
 * @returns {boolean} true if a shadow root could potentially contain valid candidates.
 */

/**
 * @typedef {Object} CandidateScope
 * @property {Element} scopeParent contains inner candidates
 * @property {Element[]} candidates list of candidates found in the scope parent
 */

/**
 * @typedef {Object} IterativeOptions
 * @property {GetShadowRoot|boolean} getShadowRoot true if shadow support is enabled; falsy if not;
 *  if a function, implies shadow support is enabled and either returns the shadow root of an element
 *  or a boolean stating if it has an undisclosed shadow root
 * @property {(node: Element) => boolean} filter filter candidates
 * @property {boolean} flatten if true then result will flatten any CandidateScope into the returned list
 * @property {ShadowRootFilter} shadowRootFilter filter shadow roots;
 */

/**
 * @param {Element[]} elements list of element containers to match candidates from
 * @param {boolean} includeContainer add container list to check
 * @param {IterativeOptions} options
 * @returns {Array.<Element|CandidateScope>}
 */
var getCandidatesIteratively = function getCandidatesIteratively(elements, includeContainer, options) {
  var candidates = [];
  var elementsToCheck = Array.from(elements);
  while (elementsToCheck.length) {
    var element = elementsToCheck.shift();
    if (isInert(element, false)) {
      // no need to look up since we're drilling down
      // anything inside this container will also be inert
      continue;
    }
    if (element.tagName === 'SLOT') {
      // add shadow dom slot scope (slot itself cannot be focusable)
      var assigned = element.assignedElements();
      var content = assigned.length ? assigned : element.children;
      var nestedCandidates = getCandidatesIteratively(content, true, options);
      if (options.flatten) {
        candidates.push.apply(candidates, nestedCandidates);
      } else {
        candidates.push({
          scopeParent: element,
          candidates: nestedCandidates
        });
      }
    } else {
      // check candidate element
      var validCandidate = matches.call(element, candidateSelector);
      if (validCandidate && options.filter(element) && (includeContainer || !elements.includes(element))) {
        candidates.push(element);
      }

      // iterate over shadow content if possible
      var shadowRoot = element.shadowRoot ||
      // check for an undisclosed shadow
      typeof options.getShadowRoot === 'function' && options.getShadowRoot(element);

      // no inert look up because we're already drilling down and checking for inertness
      //  on the way down, so all containers to this root node should have already been
      //  vetted as non-inert
      var validShadowRoot = !isInert(shadowRoot, false) && (!options.shadowRootFilter || options.shadowRootFilter(element));
      if (shadowRoot && validShadowRoot) {
        // add shadow dom scope IIF a shadow root node was given; otherwise, an undisclosed
        //  shadow exists, so look at light dom children as fallback BUT create a scope for any
        //  child candidates found because they're likely slotted elements (elements that are
        //  children of the web component element (which has the shadow), in the light dom, but
        //  slotted somewhere _inside_ the undisclosed shadow) -- the scope is created below,
        //  _after_ we return from this recursive call
        var _nestedCandidates = getCandidatesIteratively(shadowRoot === true ? element.children : shadowRoot.children, true, options);
        if (options.flatten) {
          candidates.push.apply(candidates, _nestedCandidates);
        } else {
          candidates.push({
            scopeParent: element,
            candidates: _nestedCandidates
          });
        }
      } else {
        // there's not shadow so just dig into the element's (light dom) children
        //  __without__ giving the element special scope treatment
        elementsToCheck.unshift.apply(elementsToCheck, element.children);
      }
    }
  }
  return candidates;
};

/**
 * @private
 * Determines if the node has an explicitly specified `tabindex` attribute.
 * @param {HTMLElement} node
 * @returns {boolean} True if so; false if not.
 */
var hasTabIndex = function hasTabIndex(node) {
  return !isNaN(parseInt(node.getAttribute('tabindex'), 10));
};

/**
 * Determine the tab index of a given node.
 * @param {HTMLElement} node
 * @returns {number} Tab order (negative, 0, or positive number).
 * @throws {Error} If `node` is falsy.
 */
var getTabIndex = function getTabIndex(node) {
  if (!node) {
    throw new Error('No node provided');
  }
  if (node.tabIndex < 0) {
    // in Chrome, <details/>, <audio controls/> and <video controls/> elements get a default
    // `tabIndex` of -1 when the 'tabindex' attribute isn't specified in the DOM,
    // yet they are still part of the regular tab order; in FF, they get a default
    // `tabIndex` of 0; since Chrome still puts those elements in the regular tab
    // order, consider their tab index to be 0.
    // Also browsers do not return `tabIndex` correctly for contentEditable nodes;
    // so if they don't have a tabindex attribute specifically set, assume it's 0.
    if ((/^(AUDIO|VIDEO|DETAILS)$/.test(node.tagName) || isContentEditable(node)) && !hasTabIndex(node)) {
      return 0;
    }
  }
  return node.tabIndex;
};

/**
 * Determine the tab index of a given node __for sort order purposes__.
 * @param {HTMLElement} node
 * @param {boolean} [isScope] True for a custom element with shadow root or slot that, by default,
 *  has tabIndex -1, but needs to be sorted by document order in order for its content to be
 *  inserted into the correct sort position.
 * @returns {number} Tab order (negative, 0, or positive number).
 */
var getSortOrderTabIndex = function getSortOrderTabIndex(node, isScope) {
  var tabIndex = getTabIndex(node);
  if (tabIndex < 0 && isScope && !hasTabIndex(node)) {
    return 0;
  }
  return tabIndex;
};
var sortOrderedTabbables = function sortOrderedTabbables(a, b) {
  return a.tabIndex === b.tabIndex ? a.documentOrder - b.documentOrder : a.tabIndex - b.tabIndex;
};
var isInput = function isInput(node) {
  return node.tagName === 'INPUT';
};
var isHiddenInput = function isHiddenInput(node) {
  return isInput(node) && node.type === 'hidden';
};
var isDetailsWithSummary = function isDetailsWithSummary(node) {
  var r = node.tagName === 'DETAILS' && Array.prototype.slice.apply(node.children).some(function (child) {
    return child.tagName === 'SUMMARY';
  });
  return r;
};
var getCheckedRadio = function getCheckedRadio(nodes, form) {
  for (var i = 0; i < nodes.length; i++) {
    if (nodes[i].checked && nodes[i].form === form) {
      return nodes[i];
    }
  }
};
var isTabbableRadio = function isTabbableRadio(node) {
  if (!node.name) {
    return true;
  }
  var radioScope = node.form || getRootNode(node);
  var queryRadios = function queryRadios(name) {
    return radioScope.querySelectorAll('input[type="radio"][name="' + name + '"]');
  };
  var radioSet;
  if (typeof window !== 'undefined' && typeof window.CSS !== 'undefined' && typeof window.CSS.escape === 'function') {
    radioSet = queryRadios(window.CSS.escape(node.name));
  } else {
    try {
      radioSet = queryRadios(node.name);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Looks like you have a radio button with a name attribute containing invalid CSS selector characters and need the CSS.escape polyfill: %s', err.message);
      return false;
    }
  }
  var checked = getCheckedRadio(radioSet, node.form);
  return !checked || checked === node;
};
var isRadio = function isRadio(node) {
  return isInput(node) && node.type === 'radio';
};
var isNonTabbableRadio = function isNonTabbableRadio(node) {
  return isRadio(node) && !isTabbableRadio(node);
};

// determines if a node is ultimately attached to the window's document
var isNodeAttached = function isNodeAttached(node) {
  var _nodeRoot;
  // The root node is the shadow root if the node is in a shadow DOM; some document otherwise
  //  (but NOT _the_ document; see second 'If' comment below for more).
  // If rootNode is shadow root, it'll have a host, which is the element to which the shadow
  //  is attached, and the one we need to check if it's in the document or not (because the
  //  shadow, and all nodes it contains, is never considered in the document since shadows
  //  behave like self-contained DOMs; but if the shadow's HOST, which is part of the document,
  //  is hidden, or is not in the document itself but is detached, it will affect the shadow's
  //  visibility, including all the nodes it contains). The host could be any normal node,
  //  or a custom element (i.e. web component). Either way, that's the one that is considered
  //  part of the document, not the shadow root, nor any of its children (i.e. the node being
  //  tested).
  // To further complicate things, we have to look all the way up until we find a shadow HOST
  //  that is attached (or find none) because the node might be in nested shadows...
  // If rootNode is not a shadow root, it won't have a host, and so rootNode should be the
  //  document (per the docs) and while it's a Document-type object, that document does not
  //  appear to be the same as the node's `ownerDocument` for some reason, so it's safer
  //  to ignore the rootNode at this point, and use `node.ownerDocument`. Otherwise,
  //  using `rootNode.contains(node)` will _always_ be true we'll get false-positives when
  //  node is actually detached.
  // NOTE: If `nodeRootHost` or `node` happens to be the `document` itself (which is possible
  //  if a tabbable/focusable node was quickly added to the DOM, focused, and then removed
  //  from the DOM as in https://github.com/focus-trap/focus-trap-react/issues/905), then
  //  `ownerDocument` will be `null`, hence the optional chaining on it.
  var nodeRoot = node && getRootNode(node);
  var nodeRootHost = (_nodeRoot = nodeRoot) === null || _nodeRoot === void 0 ? void 0 : _nodeRoot.host;

  // in some cases, a detached node will return itself as the root instead of a document or
  //  shadow root object, in which case, we shouldn't try to look further up the host chain
  var attached = false;
  if (nodeRoot && nodeRoot !== node) {
    var _nodeRootHost, _nodeRootHost$ownerDo, _node$ownerDocument;
    attached = !!((_nodeRootHost = nodeRootHost) !== null && _nodeRootHost !== void 0 && (_nodeRootHost$ownerDo = _nodeRootHost.ownerDocument) !== null && _nodeRootHost$ownerDo !== void 0 && _nodeRootHost$ownerDo.contains(nodeRootHost) || node !== null && node !== void 0 && (_node$ownerDocument = node.ownerDocument) !== null && _node$ownerDocument !== void 0 && _node$ownerDocument.contains(node));
    while (!attached && nodeRootHost) {
      var _nodeRoot2, _nodeRootHost2, _nodeRootHost2$ownerD;
      // since it's not attached and we have a root host, the node MUST be in a nested shadow DOM,
      //  which means we need to get the host's host and check if that parent host is contained
      //  in (i.e. attached to) the document
      nodeRoot = getRootNode(nodeRootHost);
      nodeRootHost = (_nodeRoot2 = nodeRoot) === null || _nodeRoot2 === void 0 ? void 0 : _nodeRoot2.host;
      attached = !!((_nodeRootHost2 = nodeRootHost) !== null && _nodeRootHost2 !== void 0 && (_nodeRootHost2$ownerD = _nodeRootHost2.ownerDocument) !== null && _nodeRootHost2$ownerD !== void 0 && _nodeRootHost2$ownerD.contains(nodeRootHost));
    }
  }
  return attached;
};
var isZeroArea = function isZeroArea(node) {
  var _node$getBoundingClie = node.getBoundingClientRect(),
    width = _node$getBoundingClie.width,
    height = _node$getBoundingClie.height;
  return width === 0 && height === 0;
};
var isHidden = function isHidden(node, _ref) {
  var displayCheck = _ref.displayCheck,
    getShadowRoot = _ref.getShadowRoot;
  // NOTE: visibility will be `undefined` if node is detached from the document
  //  (see notes about this further down), which means we will consider it visible
  //  (this is legacy behavior from a very long way back)
  // NOTE: we check this regardless of `displayCheck="none"` because this is a
  //  _visibility_ check, not a _display_ check
  if (getComputedStyle(node).visibility === 'hidden') {
    return true;
  }
  var isDirectSummary = matches.call(node, 'details>summary:first-of-type');
  var nodeUnderDetails = isDirectSummary ? node.parentElement : node;
  if (matches.call(nodeUnderDetails, 'details:not([open]) *')) {
    return true;
  }
  if (!displayCheck || displayCheck === 'full' || displayCheck === 'legacy-full') {
    if (typeof getShadowRoot === 'function') {
      // figure out if we should consider the node to be in an undisclosed shadow and use the
      //  'non-zero-area' fallback
      var originalNode = node;
      while (node) {
        var parentElement = node.parentElement;
        var rootNode = getRootNode(node);
        if (parentElement && !parentElement.shadowRoot && getShadowRoot(parentElement) === true // check if there's an undisclosed shadow
        ) {
          // node has an undisclosed shadow which means we can only treat it as a black box, so we
          //  fall back to a non-zero-area test
          return isZeroArea(node);
        } else if (node.assignedSlot) {
          // iterate up slot
          node = node.assignedSlot;
        } else if (!parentElement && rootNode !== node.ownerDocument) {
          // cross shadow boundary
          node = rootNode.host;
        } else {
          // iterate up normal dom
          node = parentElement;
        }
      }
      node = originalNode;
    }
    // else, `getShadowRoot` might be true, but all that does is enable shadow DOM support
    //  (i.e. it does not also presume that all nodes might have undisclosed shadows); or
    //  it might be a falsy value, which means shadow DOM support is disabled

    // Since we didn't find it sitting in an undisclosed shadow (or shadows are disabled)
    //  now we can just test to see if it would normally be visible or not, provided it's
    //  attached to the main document.
    // NOTE: We must consider case where node is inside a shadow DOM and given directly to
    //  `isTabbable()` or `isFocusable()` -- regardless of `getShadowRoot` option setting.

    if (isNodeAttached(node)) {
      // this works wherever the node is: if there's at least one client rect, it's
      //  somehow displayed; it also covers the CSS 'display: contents' case where the
      //  node itself is hidden in place of its contents; and there's no need to search
      //  up the hierarchy either
      return !node.getClientRects().length;
    }

    // Else, the node isn't attached to the document, which means the `getClientRects()`
    //  API will __always__ return zero rects (this can happen, for example, if React
    //  is used to render nodes onto a detached tree, as confirmed in this thread:
    //  https://github.com/facebook/react/issues/9117#issuecomment-284228870)
    //
    // It also means that even window.getComputedStyle(node).display will return `undefined`
    //  because styles are only computed for nodes that are in the document.
    //
    // NOTE: THIS HAS BEEN THE CASE FOR YEARS. It is not new, nor is it caused by tabbable
    //  somehow. Though it was never stated officially, anyone who has ever used tabbable
    //  APIs on nodes in detached containers has actually implicitly used tabbable in what
    //  was later (as of v5.2.0 on Apr 9, 2021) called `displayCheck="none"` mode -- essentially
    //  considering __everything__ to be visible because of the innability to determine styles.
    //
    // v6.0.0: As of this major release, the default 'full' option __no longer treats detached
    //  nodes as visible with the 'none' fallback.__
    if (displayCheck !== 'legacy-full') {
      return true; // hidden
    }
    // else, fallback to 'none' mode and consider the node visible
  } else if (displayCheck === 'non-zero-area') {
    // NOTE: Even though this tests that the node's client rect is non-zero to determine
    //  whether it's displayed, and that a detached node will __always__ have a zero-area
    //  client rect, we don't special-case for whether the node is attached or not. In
    //  this mode, we do want to consider nodes that have a zero area to be hidden at all
    //  times, and that includes attached or not.
    return isZeroArea(node);
  }

  // visible, as far as we can tell, or per current `displayCheck=none` mode, we assume
  //  it's visible
  return false;
};

// form fields (nested) inside a disabled fieldset are not focusable/tabbable
//  unless they are in the _first_ <legend> element of the top-most disabled
//  fieldset
var isDisabledFromFieldset = function isDisabledFromFieldset(node) {
  if (/^(INPUT|BUTTON|SELECT|TEXTAREA)$/.test(node.tagName)) {
    var parentNode = node.parentElement;
    // check if `node` is contained in a disabled <fieldset>
    while (parentNode) {
      if (parentNode.tagName === 'FIELDSET' && parentNode.disabled) {
        // look for the first <legend> among the children of the disabled <fieldset>
        for (var i = 0; i < parentNode.children.length; i++) {
          var child = parentNode.children.item(i);
          // when the first <legend> (in document order) is found
          if (child.tagName === 'LEGEND') {
            // if its parent <fieldset> is not nested in another disabled <fieldset>,
            // return whether `node` is a descendant of its first <legend>
            return matches.call(parentNode, 'fieldset[disabled] *') ? true : !child.contains(node);
          }
        }
        // the disabled <fieldset> containing `node` has no <legend>
        return true;
      }
      parentNode = parentNode.parentElement;
    }
  }

  // else, node's tabbable/focusable state should not be affected by a fieldset's
  //  enabled/disabled state
  return false;
};
var isNodeMatchingSelectorFocusable = function isNodeMatchingSelectorFocusable(options, node) {
  if (node.disabled ||
  // we must do an inert look up to filter out any elements inside an inert ancestor
  //  because we're limited in the type of selectors we can use in JSDom (see related
  //  note related to `candidateSelectors`)
  isInert(node) || isHiddenInput(node) || isHidden(node, options) ||
  // For a details element with a summary, the summary element gets the focus
  isDetailsWithSummary(node) || isDisabledFromFieldset(node)) {
    return false;
  }
  return true;
};
var isNodeMatchingSelectorTabbable = function isNodeMatchingSelectorTabbable(options, node) {
  if (isNonTabbableRadio(node) || getTabIndex(node) < 0 || !isNodeMatchingSelectorFocusable(options, node)) {
    return false;
  }
  return true;
};
var isValidShadowRootTabbable = function isValidShadowRootTabbable(shadowHostNode) {
  var tabIndex = parseInt(shadowHostNode.getAttribute('tabindex'), 10);
  if (isNaN(tabIndex) || tabIndex >= 0) {
    return true;
  }
  // If a custom element has an explicit negative tabindex,
  // browsers will not allow tab targeting said element's children.
  return false;
};

/**
 * @param {Array.<Element|CandidateScope>} candidates
 * @returns Element[]
 */
var sortByOrder = function sortByOrder(candidates) {
  var regularTabbables = [];
  var orderedTabbables = [];
  candidates.forEach(function (item, i) {
    var isScope = !!item.scopeParent;
    var element = isScope ? item.scopeParent : item;
    var candidateTabindex = getSortOrderTabIndex(element, isScope);
    var elements = isScope ? sortByOrder(item.candidates) : element;
    if (candidateTabindex === 0) {
      isScope ? regularTabbables.push.apply(regularTabbables, elements) : regularTabbables.push(element);
    } else {
      orderedTabbables.push({
        documentOrder: i,
        tabIndex: candidateTabindex,
        item: item,
        isScope: isScope,
        content: elements
      });
    }
  });
  return orderedTabbables.sort(sortOrderedTabbables).reduce(function (acc, sortable) {
    sortable.isScope ? acc.push.apply(acc, sortable.content) : acc.push(sortable.content);
    return acc;
  }, []).concat(regularTabbables);
};
var tabbable = function tabbable(container, options) {
  options = options || {};
  var candidates;
  if (options.getShadowRoot) {
    candidates = getCandidatesIteratively([container], options.includeContainer, {
      filter: isNodeMatchingSelectorTabbable.bind(null, options),
      flatten: false,
      getShadowRoot: options.getShadowRoot,
      shadowRootFilter: isValidShadowRootTabbable
    });
  } else {
    candidates = getCandidates(container, options.includeContainer, isNodeMatchingSelectorTabbable.bind(null, options));
  }
  return sortByOrder(candidates);
};
var focusable = function focusable(container, options) {
  options = options || {};
  var candidates;
  if (options.getShadowRoot) {
    candidates = getCandidatesIteratively([container], options.includeContainer, {
      filter: isNodeMatchingSelectorFocusable.bind(null, options),
      flatten: true,
      getShadowRoot: options.getShadowRoot
    });
  } else {
    candidates = getCandidates(container, options.includeContainer, isNodeMatchingSelectorFocusable.bind(null, options));
  }
  return candidates;
};
var isTabbable = function isTabbable(node, options) {
  options = options || {};
  if (!node) {
    throw new Error('No node provided');
  }
  if (matches.call(node, candidateSelector) === false) {
    return false;
  }
  return isNodeMatchingSelectorTabbable(options, node);
};
var focusableCandidateSelector = /* #__PURE__ */candidateSelectors.concat('iframe').join(',');
var isFocusable$1 = function isFocusable(node, options) {
  options = options || {};
  if (!node) {
    throw new Error('No node provided');
  }
  if (matches.call(node, focusableCandidateSelector) === false) {
    return false;
  }
  return isNodeMatchingSelectorFocusable(options, node);
};

var index_esm = /*#__PURE__*/Object.freeze({
  __proto__: null,
  focusable: focusable,
  getTabIndex: getTabIndex,
  isFocusable: isFocusable$1,
  isTabbable: isTabbable,
  tabbable: tabbable
});

/**
 * The calculated size of scrollbars in the application. Note that this value can vary by browser and operating
 * system. This is memoized so it's calculated lazily after the application has loaded.
 */
const SCROLLBAR_WIDTH = memoizeOne(getScrollbarWidth);
/**
 * Scrolls the given element into view if necessary.
 *
 * @param element The element to scroll.
 * @param animate Indicates if the scroll should be animated.
 * @param boundary An optional boundary element that tells the computation code where to stop when trying to find a
 * scrollable container.
 */
function doScrollElementIntoView(element, animate = false, boundary) {
    if (element) {
        const actions = r(element, {
            boundary,
            scrollMode: 'if-needed',
            block: 'nearest',
            inline: 'nearest',
        });
        actions.forEach(({ el, top, left }) => {
            // noinspection JSSuspiciousNameCombination
            doScrollElement(el, Math.round(top), Math.round(left), animate);
        });
    }
}
/**
 * Sets the scroll position on the given scrollable element to the given top and left values.
 *
 * @param element The scrollable element to set the scroll position of.
 * @param scrollTop The scrollTop value to set.
 * @param scrollLeft The scrollLeft value to set.
 * @param animate Indicates if the scrolling should be done using animation.
 */
function doScrollElement(element, scrollTop, scrollLeft, animate = false) {
    setTimeout(() => {
        if (element) {
            if (animate && element.scroll) {
                element.scroll({
                    top: scrollTop,
                    left: scrollLeft,
                    behavior: 'smooth',
                });
            }
            else {
                element.scrollTop = scrollTop;
                element.scrollLeft = scrollLeft;
            }
        }
    });
}
/**
 * Calculates the width of a scrollbar in the system. This will add a temporary scrollable div to the document with
 * a div inside and then measure the difference in size of the element.
 */
function getScrollbarWidth() {
    // Creating invisible container
    const outer = document.createElement('div');
    outer.style.visibility = 'hidden';
    outer.style.overflow = 'scroll';
    document.body.appendChild(outer);
    // Creating inner element and placing it in the container.
    const inner = document.createElement('div');
    outer.appendChild(inner);
    // Calculating difference between container's full width and the child width.
    const scrollbarWidth = outer.offsetWidth - inner.offsetWidth;
    // Removing temporary elements from the DOM.
    outer.parentNode.removeChild(outer);
    return scrollbarWidth;
}
/**
 * Requests focus be moved to the given element, optionally deferring it to the next event loop.
 *
 * @param element The element to move focus to.
 * @param preventScroll Indicates if scrolling should be prevented as a result of the focus change.
 */
function doFocus(element, preventScroll = false) {
    if (element && document.activeElement !== element && tabbable(element, { getShadowRoot: true })) {
        element.focus({ preventScroll });
    }
}
/**
 * Requests focus be moved to the element referenced by the given react ref if the referenced element is defined.
 *
 * @param ref The reference to the element to move focus to.
 * @param defer Indicates if the focus should be executed now or if it should be deferred to another event loop.
 * @param preventScroll Indicates if scrolling should be prevented as a result of the focus change.
 */
function doFocusRef(ref, defer = false, preventScroll = false) {
    if (ref) {
        if (defer) {
            setTimeout(() => {
                doFocusRef(ref);
            });
        }
        else if (ref.current) {
            doFocus(ref.current, preventScroll);
        }
    }
}
/**
 * Determines if the given node is an Element.
 */
function isElement$1(node) {
    return node?.nodeType === 1;
}
/**
 * Determines if the given node is a Text node.
 */
function isTextNode(node) {
    return node?.nodeType === 3;
}
/**
 * Determines if the given node is a INPUT html element.
 */
function isInputNode(node) {
    return node?.tagName === 'INPUT';
}
/**
 * Determines if the given node is an IMG html element.
 */
function isImageNode(node) {
    return node?.tagName === 'IMG';
}
/**
 * Determines if the given node is a TEXTAREA html element.
 */
function isTextAreaNode(node) {
    return node?.tagName === 'TEXTAREA';
}
/**
 * Looks through an array of elements for the first child element that should receive focus.
 * If no elements should receive focus, returns false.
 *
 * @param elements An array of HTMLElements.
 *
 * @returns boolean explaining if there was a focusable element or not.
 */
function focusOnFirstFocusableItemInArrayOfElements(elements) {
    for (let index = 0; index < elements.length; index++) {
        if (focusOnFirstFocusableElement(elements[index])) {
            return true;
        }
    }
    return false;
}
/**
 * Searches through the children of an element for an element to focus on. The first child that can get focus that is
 * found will receive focus. If no elements are found, returns false.
 *
 * @param parentElement An element with potential focusable children.
 */
function focusOnFirstFocusableElement(parentElement) {
    const focusableElements = tabbable(parentElement, { getShadowRoot: true });
    if (focusableElements?.length) {
        doFocus(focusableElements[0]);
        return true;
    }
    return false;
}
/**
 * Determines if the given keyboard event represent a press of the enter key. This will exclude the key when pressed
 * as part of IME composing and it will also exclude the key if there are any modifier keys that are pressed at the same
 * time. This function supports both the built-in typescript KeyboardEvent type and the React version (which is
 * missing some properties).
 */
function isEnterKey(event) {
    if (event.key === 'Enter' && !hasModifiers(event)) {
        // Users using IMEs could be making a word selection when they hit enter. This check will prevent the user’s
        // message from being sent prematurely.
        return !(event.isComposing || event.keyCode === 229);
    }
    return false;
}
/**
 * Determines if the given keyboard event has any modifier keys pressed.
 */
function hasModifiers(event) {
    return event.shiftKey || event.altKey || event.metaKey || event.ctrlKey;
}
/**
 * Returns the "scrollBottom" value for the given element. This is similar to "scrollTop" except that it represents
 * the distance the element has been scrolled from the bottom.
 */
function getScrollBottom(element) {
    if (element) {
        return element.scrollHeight - element.offsetHeight - element.scrollTop;
    }
    return 0;
}
/**
 * Loads the script at the given URL.
 */
async function loadScript(url) {
    return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.onload = () => resolve();
        script.onerror = reject;
        document.head.appendChild(script);
    });
}

const VisuallyHidden = React__default.forwardRef((props, ref) => {
    return (React__default.createElement("div", { ref: ref, ...props, className: `WACVisuallyHidden ${props.className || ''}` }, props.children));
});

// The set of types for an INPUT node that we want to announce the value of.
const ANNOUNCE_INPUT_TYPES = new Set([
    'button',
    'date',
    'datetime-local',
    'email',
    'file',
    'month',
    'number',
    'range',
    'reset',
    'search',
    'submit',
    'tel',
    'text',
    'time',
    'url',
    'week',
]);
const ANNOUNCE_NODE_EXCLUDE_ATTRIBUTE = 'data-wac-exclude-node-read';
/**
 * This component holds several aria live-regions that are used to make screen reader announcements by the application.
 * This component can announce both plain text as well as the content of complex HTML elements. HTML elements will be
 * converted to a raw text format before being announced.
 *
 * The component makes use of two live-region elements that are permanently attached to the DOM. It will alternate
 * between the usage of these two elements to announce changes. This accomplishes a few things. First, in my initial
 * work, I kept finding cases where the browser would re-read the entire live region when elements are added to it even
 * with aria-relevant="additions" and aria-atomic="false". Clearing the previous content was the only way to stop that
 * happening. However you can't simply add the content and then immediately clear it because sometimes the SR won't read
 * the content without some sort of delay before it's cleared (even waiting a tick isn't enough). In addition, a second
 * element will make sure the SR will read a new message even if it has the same content as a previous message.
 */
class AriaAnnouncerComponent extends React__default.PureComponent {
    constructor() {
        super(...arguments);
        /**
         * The first element into which the messages will be added.
         */
        this.ref1 = React__default.createRef();
        /**
         * The second element into which the messages will be added.
         */
        this.ref2 = React__default.createRef();
        /**
         * Indicates which of the two elements should next to be used to announce a new message.
         */
        this.useRef1 = true;
        /**
         * Performs the actual announcements. A clone of the element is created that is the basis of what is announced. On
         * the clone, all the listeners will be removed and all the elements will be made non-tabbable so the user can't
         * actually interact with them.
         */
        this.doAnnouncements = () => {
            const strings = [];
            // Turn all of the pending elements into strings.
            this.pendingValues.forEach(elementToAnnounce => {
                if (typeof elementToAnnounce === 'string') {
                    strings.push(elementToAnnounce);
                }
                else {
                    nodeToText(elementToAnnounce, strings);
                }
            });
            const useElement = this.useRef1 ? this.ref1.current : this.ref2.current;
            if (useElement) {
                useElement.innerText = strings.join(' ');
                // Clear the previous element and then swap the active one.
                const clearElement = this.useRef1 ? this.ref2.current : this.ref1.current;
                clearElement.innerHTML = '';
            }
            this.useRef1 = !this.useRef1;
            this.pendingValues = null;
        };
    }
    /**
     * This is the public function that will announce the given value or element.
     *
     * This function makes use of a setTimeout which will allow it to announce multiple values that all occurred in the
     * same tick of the VM. All of those messages will be appended to the same live region to be read and the previous set
     * of values that were read will be cleared from the opposite region. If an element is provided it is not converted to
     * text until the setTimeout runs which also allows a chance for custom elements to be populated by the event bus but
     * only if the custom elements are created synchronously.
     */
    announceValue(value) {
        if (!value) {
            return;
        }
        if (!this.pendingValues) {
            this.pendingValues = [];
            // This delay of 250ms is here to work around a problem with NVDA. It seems that sometimes if an element gets
            // focus, that change can interrupt the announcement in a live region even when the live region is changed
            // after the focus change. Smaller numbers seem to be less reliable in working around this.
            setTimeout(this.doAnnouncements, 250);
        }
        if (typeof value === 'string' || hasNodeType(value)) {
            this.pendingValues.push(value);
        }
        else if (value.messageID) {
            const formattedMessage = this.props.intl.formatMessage({ id: value.messageID }, value.messageValues);
            this.pendingValues.push(formattedMessage);
        }
        else {
            this.pendingValues.push(value.messageText);
        }
    }
    render() {
        // On FF+JAWS, it reads parts of the messages twice if you don't have aria-atomic="true". However, if you add this
        // attribute then Chrome will stop announcing buttons :-(.
        return (React__default.createElement(VisuallyHidden, { className: "WACAriaAnnouncer" },
            React__default.createElement("div", { ref: this.ref1, "aria-live": "polite" }),
            React__default.createElement("div", { ref: this.ref2, "aria-live": "polite" })));
    }
}
/**
 * Converts the given node into text by extracting all of the text content from it and any children inside of it.
 * Any resulting pieces of text will be added to the given array.
 */
function nodeToText(node, strings) {
    if (isElement$1(node)) {
        if (window.getComputedStyle(node).display !== 'none' &&
            node.getAttribute('aria-hidden') !== 'true' &&
            !node.hasAttribute(ANNOUNCE_NODE_EXCLUDE_ATTRIBUTE)) {
            trimAndPush(node.getAttribute('aria-label'), strings);
            if (isInputNode(node) && ANNOUNCE_INPUT_TYPES.has(node.type.toLowerCase())) {
                // If the node has a value, announce that. Otherwise announce any placeholder text.
                if (node.value === '') {
                    trimAndPush(node.placeholder, strings);
                }
                else {
                    trimAndPush(node.value, strings);
                }
            }
            else if (isTextAreaNode(node)) {
                // For text areas, the value is built from children so we don't need to add the value to the strings here.
                // The children will get added below.
                if (node.value === '') {
                    trimAndPush(node.placeholder, strings);
                }
            }
            else if (isImageNode(node)) {
                trimAndPush(node.alt, strings);
            }
            // Recursively go through all of the children. Most nodes will have children either in the shadowRoot.childNodes
            // or in childNodes. However web components that take advantage of slots will have both. With such a node
            // the shadowRoot comes first within the DOM and can contain children with aria-label's as well as <slots /> (which don't
            // have aria-label's). After the shadowRoot the childNodes are the next sibling. Some of those childNodes will
            // match to the corresponding slots in the shadowRoot and should have aria-label's. Because the shadowRoot is
            // often just a wrapper around the slots it makes sense to recursively go through those nodes first before
            // processing the slots farther down.
            if (node.shadowRoot) {
                node.shadowRoot.childNodes?.forEach(childNode => {
                    nodeToText(childNode, strings);
                });
            }
            if (node.childNodes) {
                node.childNodes.forEach(childNode => {
                    nodeToText(childNode, strings);
                });
            }
        }
    }
    else if (isTextNode(node)) {
        trimAndPush(node.data, strings);
    }
}
/**
 * Trims the given value and pushes it on to the given array assuming it has any content.
 */
function trimAndPush(value, strings) {
    if (value) {
        value = value.trim();
        if (value) {
            strings.push(value.replaceAll('\n', ' '));
        }
    }
}
/**
 * Determines if the given value is some node type.
 */
function hasNodeType(value) {
    return value.nodeType !== undefined;
}

/**
 * This component is responsible for providing a context that gives access to the component we use to perform aria
 * announcements. It will also listen for changes in {@link AppState.announceMessage} and triggers the announcements
 * of that.
 */
function AriaAnnouncerProvider(props) {
    const intl = useIntl();
    const { store } = useServiceManager();
    const announcerRef = useRef();
    const announcerFunction = useCallback(value => {
        // It's possible for some component to try to do an announcement before this component has actually been mounted
        // (because it's later in the DOM). If that happens, then we need to delay the announcement a bit.
        if (!announcerRef.current) {
            setTimeout(() => announcerRef.current.announceValue(value));
        }
        else {
            announcerRef.current.announceValue(value);
        }
    }, []);
    const previousAnnounceMessageRef = useRef();
    useEffect(() => {
        // This effect will register a listener on the store that watches for changes in the announceMessage property
        // and when it changes, it will issue an announcement request on the announcer component.
        const unsubscribe = store.subscribe(() => {
            const currentAnnounceMessage = store.getState().announceMessage;
            if (currentAnnounceMessage !== previousAnnounceMessageRef.current) {
                announcerFunction(currentAnnounceMessage);
                previousAnnounceMessageRef.current = currentAnnounceMessage;
            }
        });
        return unsubscribe;
    }, [store, announcerFunction]);
    return (React__default.createElement(AriaAnnouncerContext.Provider, { value: announcerFunction },
        props.children,
        React__default.createElement(AriaAnnouncerComponent, { intl: intl, ref: announcerRef })));
}

function useEffectDidUpdate(effect, deps) {
    const hasRunRef = useRef(false);
    useEffect(() => {
        if (hasRunRef.current) {
            return effect();
        }
        hasRunRef.current = true;
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}

/**
 * A custom react hook to be able to set and access previous prop/state values:
 * see https://reactjs.org/docs/hooks-faq.html#how-to-get-the-previous-props-or-state
 */
function usePrevious(value) {
    const ref = useRef();
    useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}

/**
 * Returns the aria-label string from the provided language pack for the launcher button based on the open and view
 * state.
 */
function getLauncherButtonAriaLabel(languagePack, isLauncherHidden, isTourActive) {
    if (isTourActive) {
        return isLauncherHidden ? languagePack.launcher_isTourOpen : languagePack.launcher_isTourClosed;
    }
    return isLauncherHidden ? languagePack.launcher_isOpen : languagePack.launcher_isClosed;
}

function Launcher(props, ref) {
    const { onToggleOpen, languagePack, unreadAgentCount, intl, serviceManager, showUnreadIndicator, className, tabIndex, launcherHidden, activeTour, } = props;
    const launcherAvatarURL = useSelector((state) => state.theme.useAITheme ? undefined : state.launcher.config.desktop.avatar_url_override);
    const useAITheme = useSelector((state) => state.theme.useAITheme);
    /**
     * A React ref to the button in this component.
     */
    const buttonRef = React__default.createRef();
    useImperativeHandle(ref, () => ({
        /**
         * This is a function that will request that focus be moved to the button. This request for focus is normally
         * triggered within App.tsx.
         */
        requestFocus: () => {
            doFocusRef(buttonRef);
        },
    }));
    let ariaLabel = getLauncherButtonAriaLabel(languagePack, launcherHidden, activeTour);
    if (unreadAgentCount !== 0) {
        ariaLabel += `. ${intl.formatMessage({ id: 'icon_ariaUnreadMessages' }, { count: unreadAgentCount })}`;
    }
    let launcherAvatar = useAITheme ? (React__default.createElement(AiLaunch, { size: 24, className: "WACLauncher_svg" })) : (React__default.createElement(ChatLaunch, { size: 24, className: "WACLauncher__svg" }));
    if (launcherAvatarURL) {
        // eslint-disable-next-line jsx-a11y/alt-text
        launcherAvatar = React__default.createElement("img", { className: "WACLauncher__Avatar", src: launcherAvatarURL, "aria-hidden": true });
    }
    /**
     * Renders the corresponding variation of the launcher button.
     */
    return (React__default.createElement("div", { className: cx('WACLauncher__ButtonContainer', 'WACLauncher__ButtonContainer--round', className, {
            'WACLauncher__ButtonContainer--hidden': launcherHidden,
        }) },
        React__default.createElement(Button, { "aria-label": ariaLabel, className: cx('WACLauncher__Button', { WACLauncher__TourButton: activeTour }), id: `WACLauncher__Button${serviceManager.namespace.suffix}`, kind: ButtonKindEnum.PRIMARY, type: "button", onClick: onToggleOpen, ref: buttonRef, tabIndex: tabIndex },
            activeTour ? React__default.createElement(ArrowUpLeft, { size: 24, className: "WACLauncher__svg" }) : launcherAvatar,
            (unreadAgentCount !== 0 || showUnreadIndicator) && (React__default.createElement("div", { className: "WAC__countIndicator" }, unreadAgentCount !== 0 ? unreadAgentCount : '')))));
}
const LauncherExport = forwardRef(Launcher);

function LauncherComplex(props) {
    const { serviceManager, languagePack, intl, launcherConfig, launcherComplexRef, launcherRef, onOpen, onMinimize, unreadAgentCount, showUnreadIndicator, desktopLauncherIsExpanded, isToolingPreview, launcherHidden, activeTour, className, } = props;
    const { launcher_isOpen, launcher_isClosed, launcher_desktopGreeting, launcher_closeButton, launcher_ariaIsExpanded, } = languagePack;
    const tabIndex = isToolingPreview ? -1 : undefined;
    function getLauncherContent() {
        // Use the title from the launcher config.
        if (launcherConfig.desktop.title) {
            return launcherConfig.desktop.title;
        }
        // If there is nothing set in the launcher config then use our own default.
        return launcher_desktopGreeting;
    }
    /**
     * Renders the complex variation of the launcher.
     */
    return (React__default.createElement("div", { className: cx('WACLauncher__ButtonContainer', 'WACLauncherComplex__Container', className, {
            'WACLauncher__ButtonContainer--hidden': launcherHidden,
            'WACLauncherComplex__Container--toolingPreview WACLauncher__ButtonContainer--toolingPreview': isToolingPreview,
        }), ref: launcherComplexRef, "aria-hidden": isToolingPreview },
        React__default.createElement("button", { className: "WACLauncherComplex__ContentButton", type: "button", onClick: onOpen, disabled: !desktopLauncherIsExpanded, tabIndex: tabIndex },
            React__default.createElement("div", { className: cx('WACWidget__textEllipsis', {
                    WACLauncherComplex__Text: !launcherHidden,
                }) }, getLauncherContent())),
        React__default.createElement(LauncherExport, { serviceManager: serviceManager, languagePack: languagePack, intl: intl, ref: launcherRef, onToggleOpen: onOpen, className: "WACLauncherComplex__SmallLauncherContainer", unreadAgentCount: unreadAgentCount, showUnreadIndicator: showUnreadIndicator, tabIndex: tabIndex, launcherHidden: launcherHidden, activeTour: activeTour }),
        React__default.createElement(Tag, { className: "WACLauncher__CloseButton", "aria-label": launcher_ariaIsExpanded, onClick: onMinimize, disabled: !desktopLauncherIsExpanded, tabIndex: tabIndex },
            React__default.createElement("div", { className: "WACLauncher__CloseButtonInnerWrapper" },
                React__default.createElement(Close, { className: "WACLauncher__CloseButtonIcon" }),
                launcher_closeButton))));
}

// The amount of time it takes the desktop launcher to minimize.
const TIME_FOR_MINIMIZE_ANIMATION = 400;
// The amount of time it takes the launcher to bounce.
const TIME_FOR_BOUNCE_ANIMATION = 500;
const LauncherDesktopContainer = (props) => {
    const { launcherRef, onDoToggle, requestFocus, launcherHidden, activeTour } = props;
    const serviceManager = useServiceManager();
    const languagePack = useLanguagePack();
    const intl = useIntl();
    const browserLauncherState = useSelector((state) => state.persistedToBrowserStorage.launcherState);
    const { desktopLauncherWasMinimized, desktopLauncherIsExpanded, bounceTurn, showUnreadIndicator, viewState } = browserLauncherState;
    const launcherState = useSelector((state) => state.launcher);
    const launcherConfig = launcherState.config;
    const { time_to_expand, new_expand_time } = launcherConfig.desktop;
    const isExpandedLauncherEnabled = launcherConfig.desktop.is_on;
    const unreadAgentCount = useSelector((state) => state.agentState.numUnreadMessages);
    const publicConfig = useSelector((state) => state.config.public);
    const { launcherToolingPreview } = publicConfig.__ibm__;
    const desktopLauncherToolingPreview = launcherToolingPreview === LauncherToolingPreviewType.DESKTOP;
    const [smallLauncherClassName, setSmallLauncherClassName] = useState('');
    const [complexLauncherClassName, setComplexLauncherClassName] = useState('');
    const launcherComplexRef = useRef();
    const launcherHiddenRef = useRef();
    launcherHiddenRef.current = launcherHidden;
    const animateOnceVisible = useRef(false);
    const bounceTurnRef = useRef();
    bounceTurnRef.current = bounceTurn;
    const animationStartTimerRef = useRef();
    const animationFinishedTimerRef = useRef();
    const firstBounceAnimationStartTimerRef = useRef();
    const firstBounceAnimationFinishedTimerRef = useRef();
    const secondBounceAnimationStartTimerRef = useRef();
    const secondBounceAnimationFinishedTimerRef = useRef();
    /**
     * Measure the height of the complex launcher and set it as a css variable so that the expand animation can move the
     * greeting message the right distance.
     */
    const determineLauncherHeight = useCallback(() => {
        // In order to move the small launcher up a dynamic distance depending on the amount of content in the launcher we
        // need to measure the height of the launcher and use that as a variable within the launcher expanding animation.
        const expandedLauncherHeight = `${launcherComplexRef.current?.offsetHeight}px`;
        if (launcherComplexRef.current?.style?.setProperty) {
            launcherComplexRef.current.style.setProperty('--cds-chat-LAUNCHER-desktop-expanded-height', expandedLauncherHeight);
        }
    }, []);
    /**
     * If the launcher isn't open then expand the launcher.
     */
    const startExpandLauncher = useCallback(() => {
        // Because these timeouts for the expand animation are triggered within useOnMount the value for launcherHidden will
        // be the same as its initial value when this component mounted and this useOnMount function was created. In order
        // to check against the up-to-date value we need to keep a ref up to date that stores the new value. This works
        // because the pointer to the ref existed when the useOnMount function was created, then the value of the
        // ref.current has been updated on each render to match the current value. If we tried to use an object to keep
        // track of the current value instead of a ref then a new object with a different pointer would be created each
        // render, and this useOnMount function would only be aware of the object that existed on the first render.
        // We are no longer checking if the launcher has been minimized here since our instance methods are supposed to
        // allow the user to pop up a new greeting message even if one has already been minimized.
        if (!launcherHiddenRef.current) {
            serviceManager.store.dispatch(actions.setLauncherProperty('desktopLauncherIsExpanded', true));
            // Determine the height of the complex launcher.
            determineLauncherHeight();
            // Add the className for the intro animation.
            setComplexLauncherClassName('WACLauncherComplex__Container--introAnimation');
        }
    }, [determineLauncherHeight, serviceManager.store]);
    /**
     * Once the launcher has finished expanding, send an event.
     */
    const finishExpandLauncher = useCallback(() => {
        if (!launcherHiddenRef.current) {
            serviceManager.trackService.track({
                eventName: 'Launcher interaction',
                eventDescription: 'Desktop animation finished.',
            });
        }
    }, [serviceManager.trackService]);
    /**
     * Set two timers, one to begin to expand animation on the launcher, the other to fire an event when the expand
     * animation finishes.
     */
    const setExpandAnimationTimers = useCallback(() => {
        // Timer to switch to launcherComplex which will trigger an expand animation and allow focus on the content button and
        // close button.
        animationStartTimerRef.current = setTimeout(() => {
            startExpandLauncher();
        }, time_to_expand);
        animationFinishedTimerRef.current = setTimeout(() => {
            finishExpandLauncher();
            // The time before the animation starts plus the duration of the animation.
        }, time_to_expand + 700);
    }, [finishExpandLauncher, time_to_expand, startExpandLauncher]);
    /**
     * Clear the existing expand animation timers.
     */
    const clearExpandAnimationTimers = useCallback(() => {
        clearTimeout(animationStartTimerRef.current);
        clearTimeout(animationFinishedTimerRef.current);
    }, []);
    /**
     * If the launcher isn't open then bounce the launcher.
     */
    const startBounceAnimation = useCallback(() => {
        if (!launcherHiddenRef.current) {
            // Add the bounce animation class to the launcher.
            setSmallLauncherClassName('WACLauncher__ButtonContainer--bounceAnimation');
        }
    }, []);
    /**
     * Once the launcher has finished bouncing send an event and increment the bounce turn so the user doesn't see that
     * same bounce again on the next page change or reload.
     */
    const finishBounceAnimation = useCallback(() => {
        if (!launcherHiddenRef.current) {
            serviceManager.trackService.track({
                eventName: 'Launcher interaction',
                eventDescription: `Desktop launcher finished bounce animation ${bounceTurnRef.current}.`,
            });
            // Change the launcher animation class from bounce to noAnimation.
            setSmallLauncherClassName('WACLauncher__ButtonContainer--noAnimation');
            // Increment the bounce turn counter, so we know where to pick up from if the page is reloaded/changed.
            bounceTurnRef.current++;
            serviceManager.store.dispatch(actions.setLauncherProperty('bounceTurn', bounceTurnRef.current));
        }
    }, [serviceManager.store, serviceManager.trackService]);
    /**
     * Start the timers to show the bounce animation on the launcher and to send a track event when the animation
     * finishes. If the user hasn't seen any bounce animations yet then 4 timers are started, one set is responsible for a
     * bounce at 15s, the next set will be responsible for a bounce 60s later. If the user has already seen the bounce at
     * 15s, and this is the next page load, then timers are only needed for a bounce animation 60s from now.
     */
    const setBounceAnimationTimers = useCallback(() => {
        if (bounceTurnRef.current === 1) {
            // If there have not been any bounce animations yet then set a timer for 15s from now for the first bounce.
            firstBounceAnimationStartTimerRef.current = setTimeout(() => {
                startBounceAnimation();
            }, BOUNCING_ANIMATION_TIMEOUTS[0]);
            // After the first bounce is done remove the animation className and send a tracking event.
            firstBounceAnimationFinishedTimerRef.current = setTimeout(() => {
                finishBounceAnimation();
            }, BOUNCING_ANIMATION_TIMEOUTS[0] + TIME_FOR_BOUNCE_ANIMATION);
            // Set another timer for 60s after the first bounce has completed to show a second bounce.
            secondBounceAnimationStartTimerRef.current = setTimeout(() => {
                startBounceAnimation();
            }, BOUNCING_ANIMATION_TIMEOUTS[0] + BOUNCING_ANIMATION_TIMEOUTS[1]);
            // After the second bounce is done remove the animation className and send a tracking event.
            secondBounceAnimationFinishedTimerRef.current = setTimeout(() => {
                finishBounceAnimation();
            }, BOUNCING_ANIMATION_TIMEOUTS[0] + BOUNCING_ANIMATION_TIMEOUTS[1] + TIME_FOR_BOUNCE_ANIMATION);
        }
        else if (bounceTurnRef.current === 2) {
            // If there has already been a bounce animation then set a timer for 60s from now for the second bounce.
            secondBounceAnimationStartTimerRef.current = setTimeout(() => {
                startBounceAnimation();
            }, BOUNCING_ANIMATION_TIMEOUTS[1]);
            // After the second bounce is done remove the animation className and send a tracking event.
            secondBounceAnimationFinishedTimerRef.current = setTimeout(() => {
                finishBounceAnimation();
            }, BOUNCING_ANIMATION_TIMEOUTS[1] + TIME_FOR_BOUNCE_ANIMATION);
        }
    }, [finishBounceAnimation, startBounceAnimation]);
    /**
     * Clear the existing bounce animation timers.
     */
    const clearBounceAnimationTimers = useCallback(() => {
        clearTimeout(firstBounceAnimationStartTimerRef.current);
        clearTimeout(firstBounceAnimationFinishedTimerRef.current);
        clearTimeout(secondBounceAnimationStartTimerRef.current);
        clearTimeout(secondBounceAnimationFinishedTimerRef.current);
    }, []);
    /**
     * Clear the expand and bounce timers and set the launcher state to minimized and bounce turn 3. This way if the page
     * is reloaded the launcher will behave as if it has already been opened and won't try and show a greeting.
     */
    const setDefaultLauncherState = useCallback(() => {
        // Clear to expand timers and set the launcher to minimized so that it will stay minimized on page change/reload.
        clearExpandAnimationTimers();
        serviceManager.store.dispatch(actions.setLauncherMinimized());
        // Clear the bounce timers and set the bounce turn to 3 so that no more bounces will occur after page
        // change/reload.
        clearBounceAnimationTimers();
        serviceManager.store.dispatch(actions.setLauncherProperty('bounceTurn', 3));
        // If the launcher was planning on animating once visible then we should no longer do that.
        animateOnceVisible.current = false;
        setSmallLauncherClassName('WACLauncher__ButtonContainer--noAnimation');
    }, [clearBounceAnimationTimers, clearExpandAnimationTimers, serviceManager.store]);
    useOnMount(() => {
        if (desktopLauncherToolingPreview) {
            // The dispatch below causes LauncherComplex to be returned and rendered by this container. Once it's rendered the
            // launcherComplexRef.current will exist, which is required to determineLauncherHeight and to add the
            // tooling preview className. If this isn't done within a setTimeout then react will be in the middle of the
            // component lifecycle method of mounting which prevents this component from re-rendering so the ref will not
            // exist when those lines are reached. Thus, a setTimeout is required so that the component re-renders when the
            // dispatch is called, that way the ref exists.
            setTimeout(() => {
                // If we're in the tooling preview then set the launcher to expanded state.
                serviceManager.store.dispatch(actions.setLauncherProperty('desktopLauncherIsExpanded', true));
                // Determine the height of the complex launcher.
                determineLauncherHeight();
            });
        }
        else if (desktopLauncherIsExpanded) {
            // If the launcher is already expanded then it must have been expanded on a previous page load so a simpler
            // animation should be shown.
            // Because of the view change work all views now start hidden. If we try to run determineLauncherHeight while the
            // launcher is hidden we would get incorrect results since LauncherComplex changes it's height by adding and
            // removing classNames, when the launcher is visible, that effect the size of the text. We also need to wait to
            // add the animation class until the launcher is visible, otherwise the animation will start before the user can
            // see the launcher. For these reasons we simply set a ref to true that is used in combination with
            // viewState.launcher lower down to appropriately calculate the height and add the className once the launcher is
            // actually visible.
            animateOnceVisible.current = true;
        }
        else if (!desktopLauncherWasMinimized && isExpandedLauncherEnabled) {
            // If the launcher hasn't been minimized and isn't expanded then start the timers to expand the launcher.
            setExpandAnimationTimers();
            return () => {
                clearExpandAnimationTimers();
            };
        }
        else if (desktopLauncherWasMinimized && bounceTurn !== 3) {
            // If the launcher was previously minimized, and we haven't preformed the bounce animation twice then we want to
            // continue the bounce animations on whatever step we were on.
            setBounceAnimationTimers();
            return () => {
                clearBounceAnimationTimers();
            };
        }
        return undefined;
    });
    // If the launcher is now visible, and we intended to show a simple animation when the launcher mounted, now is the time
    // to do it since the user can now see the launcher. This will not run again until the page is reloaded since we
    // currently don't unmount the launcher when its not visible, instead we just hide it with css.
    useEffect(() => {
        if (viewState.launcher && animateOnceVisible.current) {
            // Determine the height of the complex launcher.
            determineLauncherHeight();
            // Add the className for the simple intro animation.
            setComplexLauncherClassName('WACLauncherComplex__Container--simpleAnimation');
            animateOnceVisible.current = false;
        }
    }, [determineLauncherHeight, viewState.launcher]);
    // If the main window or tour have been opened then clear all timers and set the launcher state as if it had been
    // clicked open. This is to protect against scenarios where the main window or tour are opened using other methods
    // besides clicking on the launcher.
    useEffect(() => {
        if (viewState.mainWindow || viewState.tour) {
            // Clear timers and update launcher state so that no more greeting messages or bounces occur.
            setDefaultLauncherState();
        }
    }, [viewState, setDefaultLauncherState]);
    // If the launcher time_to_expand changes then we need to clear the existing timers and start new ones with the new
    // time.
    useEffect(() => {
        if (new_expand_time) {
            // End any bounce animation timers that may have been in progress, but do not reset the bounce turns counter.
            // Instead, the user will continue on whatever bounce turn they left off on (15s or 60s) after they've minimized
            // the new greeting message and refreshed the page.
            clearBounceAnimationTimers();
            clearExpandAnimationTimers();
            setExpandAnimationTimers();
            serviceManager.store.dispatch(actions.setLauncherConfigProperty('new_expand_time', false, LauncherType.DESKTOP));
        }
    }, [
        animationFinishedTimerRef,
        animationStartTimerRef,
        new_expand_time,
        setExpandAnimationTimers,
        clearExpandAnimationTimers,
        clearBounceAnimationTimers,
        serviceManager.store,
    ]);
    // If the launcher title has changed then we need to recalculate the height and update the styles.
    const prevLauncherTitle = usePrevious(launcherConfig.desktop.title);
    useEffect(() => {
        // The check at the end that makes sure one of the two values is truthy has been added to stop this from running
        // when prevLauncherTitle was undefined and launcherConfig.desktop.title was an empty string.
        if (prevLauncherTitle !== launcherConfig.desktop.title && (launcherConfig.desktop.title || prevLauncherTitle)) {
            determineLauncherHeight();
        }
    }, [determineLauncherHeight, launcherConfig, prevLauncherTitle]);
    const onMinimize = useCallback(() => {
        if (desktopLauncherToolingPreview) ;
        else {
            setComplexLauncherClassName('WACLauncherComplex__Container--closeAnimation');
            // Wait for the minimize animation to finish before switching back to the original launcher.
            setTimeout(() => {
                serviceManager.trackService.track({
                    eventName: 'Launcher interaction',
                    eventDescription: 'Desktop launcher minimized.',
                });
                // Remove the animation class from the small launcher so that it doesn't try and animate in when we make this
                // switch.
                setSmallLauncherClassName('WACLauncher__ButtonContainer--noAnimation');
                serviceManager.store.dispatch(actions.setLauncherMinimized());
                // Let the component re-render before moving focus.
                setTimeout(requestFocus);
            }, TIME_FOR_MINIMIZE_ANIMATION);
        }
    }, [desktopLauncherToolingPreview, requestFocus, serviceManager.store, serviceManager.trackService]);
    const onOpen = useCallback(() => {
        if (desktopLauncherToolingPreview) ;
        else {
            serviceManager.trackService.track({
                eventName: 'Widget Opened',
                eventDescription: 'User has opened the widget.',
                launcherType: LauncherType.DESKTOP,
            });
            // Clear timers and update launcher state so that no more greeting messages or bounces occur.
            setDefaultLauncherState();
            onDoToggle();
        }
    }, [desktopLauncherToolingPreview, onDoToggle, serviceManager.trackService, setDefaultLauncherState]);
    let launcher;
    if (desktopLauncherIsExpanded) {
        launcher = (React__default.createElement(LauncherComplex, { serviceManager: serviceManager, languagePack: languagePack, intl: intl, launcherComplexRef: launcherComplexRef, launcherRef: launcherRef, launcherConfig: launcherConfig, onOpen: onOpen, onMinimize: onMinimize, unreadAgentCount: unreadAgentCount, showUnreadIndicator: showUnreadIndicator, desktopLauncherIsExpanded: desktopLauncherIsExpanded, isToolingPreview: desktopLauncherToolingPreview, launcherHidden: launcherHidden, activeTour: activeTour, className: complexLauncherClassName }));
    }
    else {
        launcher = (React__default.createElement(LauncherExport, { serviceManager: serviceManager, languagePack: languagePack, intl: intl, ref: launcherRef, onToggleOpen: onOpen, unreadAgentCount: unreadAgentCount, showUnreadIndicator: showUnreadIndicator, className: smallLauncherClassName, launcherHidden: launcherHidden, activeTour: activeTour }));
    }
    return launcher;
};

/**
 * Triggers an animation event by temporarily adding the given classname to the given element. When the animation is
 * finished, the classname will be removed.
 *
 * The end of the animation can be determined a number of ways. If no additional arguments are specified, an
 * animation listener is added to the event and as soon as the first animation is finished, that counts as the end
 * of animations. You can also specify the name of an animation that will trigger the end of the animation. This can
 * be useful if you have multiple animations that run in sequence. Or lastly, you can simply specify a time duration.
 *
 * Keep in mind that the animation events bubble so if there are animations running on child elements, those will
 * trigger the events on this element.
 *
 * @param element This element to trigger the animation on.
 * @param className The name of the class to add and later remove from the element.
 * @param endAnimationNameOrDelay The name of an animation to indicate when the animation is finished or a number
 * indicating the duration in milliseconds to wait before ending the animation.
 * @param endAnimationCallback An optional callback that can be called when the animation is finished.
 */
function animateWithClass(element, className, endAnimationNameOrDelay, endAnimationCallback) {
    if (element) {
        element.classList.add(className);
        if (typeof endAnimationNameOrDelay === 'number') {
            setTimeout(() => {
                element.classList.remove(className);
                if (endAnimationCallback) {
                    endAnimationCallback();
                }
            }, endAnimationNameOrDelay);
        }
        else {
            const listener = (event) => {
                if (!endAnimationNameOrDelay || event.animationName === endAnimationNameOrDelay) {
                    element.removeEventListener('animationend', listener);
                    element.removeEventListener('animationcancel', listener);
                    element.classList.remove(className);
                    if (endAnimationCallback) {
                        endAnimationCallback();
                    }
                }
            };
            element.addEventListener('animationend', listener);
            element.addEventListener('animationcancel', listener);
        }
    }
}
/**
 * Uses the provided element to play the given animation after each amount of time in the given timeouts array.
 *
 * @param element The element to apply the provided animation class to.
 * @param animation The animation class to play after the provided number of timeouts.
 * @param timeouts An array of timeouts that determines how long to wait before playing the animation. The first
 * timeout will be set immediately and a listener will wait for the animation to end before moving to the next timeout.
 * @param options Options with helper functions that fire throughout the lifecycle of the recurring animations.
 *
 * @returns endAnimation A function that will stop playing the provided animation when called.
 */
function setAnimationTimeouts(element, animation, timeouts, options) {
    const { startingIndex, beforeAll, afterAll, beforeEach, afterEach } = options;
    // The index of the current timeout value to start off on in the array.
    let index = startingIndex || 0;
    // Determines if the animation should be prevented from playing.
    let terminate = false;
    // The ID of the setTimeout function.
    let timeoutID = null;
    /**
     * This will recursively play the provided animation class on the provided element after the amount of time specified
     * in the timeouts array.
     */
    function playAnimation() {
        // Call beforeAll immediately after this function is called.
        if (beforeAll && index === 0) {
            beforeAll();
        }
        // If the index equals the total number of values in the array, we are outside the scope of the array and have
        // completed the animations.
        const isComplete = index === timeouts.length;
        if (!isComplete && !terminate) {
            // Capture the timeout ID so we can clear it when needed.
            timeoutID = setTimeout(setAnimationListener, timeouts[index]);
        }
        else if (isComplete && afterAll) {
            afterAll();
        }
    }
    /**
     * Handles replaying the animation on the element using the next timeout in the array and resetting the element
     * to it's original state to prepare for the next animation.
     */
    function replayAnimation() {
        if (afterEach) {
            afterEach();
        }
        // Move onto the next timeout in the array.
        index++;
        // Remove this event listener so that it doesn't get fired again.
        element.removeEventListener('animationend', replayAnimation);
        // Remove the animation class since it has ended.
        element.classList.remove(animation);
        // Attempt to replay the animation.
        playAnimation();
    }
    /**
     * Handles playing and replaying the animation on the provided element.
     */
    function setAnimationListener() {
        if (beforeEach) {
            beforeEach();
        }
        // Begin the process to play the animation.
        element.addEventListener('animationend', replayAnimation);
        element.classList.add(animation);
    }
    playAnimation();
    /**
     * The callback function that will stop the recurring animation.
     */
    return () => {
        // Prevent the animation from playing/replaying.
        terminate = true;
        clearTimeout(timeoutID);
        // Remove the animation class in case it's still applied and the event listener.
        element.classList.remove(animation);
        element.removeEventListener('animationend', replayAnimation);
    };
}

function useAriaAnnouncer() {
    return useContext(AriaAnnouncerContext);
}

const MAX_EXTENDED_LAUNCHER_WIDTH = 400;
/**
 * A new mobile specific launcher experiment that extends its width to the left and transitions between home screen
 * starters with a fade animation.
 */
function LauncherExtended(props, ref) {
    const { unreadAgentCount, showUnreadIndicator, serviceManager, launcherConfig, disableReduce, isExtended, playExtendAnimation, isToolingPreview, onToggleOpen, onSwipeRight, onReduceEnd, className, launcherHidden, activeTour, } = props;
    const ariaAnnouncer = useAriaAnnouncer();
    const languagePack = useLanguagePack();
    const intl = useIntl();
    const launcherAvatarURL = useSelector((state) => state.theme.useAITheme ? undefined : state.launcher.config.mobile.avatar_url_override);
    const [animateExtendedState, setAnimateExtendedState] = useState(playExtendAnimation);
    const [showGreetingMessage, setShowGreetingMessage] = useState(false);
    const prevIsExtended = usePrevious(isExtended);
    const buttonRef = useRef();
    const extendedContainerRef = useRef();
    const greetingMessageRef = useRef();
    const textHolderRef = useRef();
    // The touch coordinates captured when the user touched the launcher.
    const touchStartRef = useRef({ touchStartX: null, touchStartY: null });
    const shouldReduceExtendedLauncher = !isExtended && prevIsExtended;
    const extendWithAnimation = isExtended && animateExtendedState;
    const extendWithoutAnimation = isExtended && !animateExtendedState;
    const launcherGreetingMessage = launcherConfig.mobile.title || languagePack.launcher_mobileGreeting;
    let ariaLabel = getLauncherButtonAriaLabel(languagePack, launcherHidden, activeTour);
    if (unreadAgentCount !== 0) {
        ariaLabel += `. ${intl.formatMessage({ id: 'icon_ariaUnreadMessages' }, { count: unreadAgentCount })}`;
    }
    let launcherAvatar = React__default.createElement(ChatLaunch, { size: 24, className: "WACLauncher__svg" });
    if (launcherAvatarURL) {
        // eslint-disable-next-line jsx-a11y/alt-text
        launcherAvatar = React__default.createElement("img", { className: "WACLauncher__Avatar", src: launcherAvatarURL, "aria-hidden": true });
    }
    useImperativeHandle(ref, () => ({
        requestFocus: () => {
            doFocusRef(buttonRef);
        },
        launcherContainerElement: () => {
            return extendedContainerRef.current;
        },
    }));
    // This effect handles calculating the launcher's extended width. If the greeting message ends up changing, this
    // should also re-calculate it.
    useEffect(() => {
        const textHolderElement = textHolderRef.current;
        const greetingMessageElement = greetingMessageRef.current;
        const extendedContainerElement = extendedContainerRef.current;
        calculateAndSetMaxExtendedLauncherWidth(textHolderElement, greetingMessageElement, extendedContainerElement);
    }, [ariaAnnouncer, extendWithoutAnimation, launcherGreetingMessage]);
    // This handles displaying the greeting message and setting up the touch event listeners to reduce the extended
    // launcher when in the extended state. If the launcher is reducing, the greeting message will be hidden and the
    // launcher state will be update in session storage to reflect the reduced state.
    useEffect(() => {
        if (isExtended) {
            // Fade in and announce the greeting message if the "extend" animation is played.
            if (animateExtendedState) {
                doFadeAnimationForElements({ fadeInElement: greetingMessageRef.current, fadeInTime: 300 }, () => {
                    setAnimateExtendedState(false);
                });
            }
            else {
                // Only un-hide the greeting message.
                setShowGreetingMessage(true);
            }
            // If the launcher is prevented from reducing, there's no need to setup touch listeners.
            if (!disableReduce) {
                const buttonEl = buttonRef.current;
                // Calls a function that will fire a callback function when it detects the user has swiped right on the launcher.
                const handleTouchMove = (event) => {
                    checkIfUserSwipedRight(event.touches[0], touchStartRef.current, onSwipeRight);
                };
                // Capture the coordinates the user has touched the launcher on and add a touchmove listener to determine if the
                // user swiped right on the launcher to close.
                const handleTouchStart = (event) => {
                    const { clientX, clientY } = event.touches[0];
                    const touchStart = touchStartRef.current;
                    touchStart.touchStartX = clientX;
                    touchStart.touchStartY = clientY;
                    buttonRef.current.addEventListener('touchmove', handleTouchMove);
                };
                buttonEl.addEventListener('touchstart', handleTouchStart);
                return () => {
                    buttonEl.removeEventListener('touchmove', handleTouchMove);
                    buttonEl.removeEventListener('touchstart', handleTouchStart);
                };
            }
        }
        else if (shouldReduceExtendedLauncher) {
            // An animationend event handler that's called once the event reduce animation has ended.
            const reduceAnimationEndListener = () => {
                onReduceEnd();
                // Once the "reduce" animation has finished reset the extended state flag so that the fade up animation for the
                // text will play if another greeting message is triggered.
                setAnimateExtendedState(true);
                extendedContainerRef.current.removeEventListener('animationend', reduceAnimationEndListener);
            };
            // Update the extendLauncher flag to false so that the user doesn't see the launcher animation on the next page
            // load.
            extendedContainerRef.current.addEventListener('animationend', reduceAnimationEndListener);
            // Fade out the greeting message.
            doFadeAnimationForElements({ fadeOutElement: greetingMessageRef.current });
        }
        return undefined;
    }, [
        animateExtendedState,
        ariaAnnouncer,
        disableReduce,
        isExtended,
        launcherGreetingMessage,
        onReduceEnd,
        onSwipeRight,
        shouldReduceExtendedLauncher,
    ]);
    return (React__default.createElement("div", { className: cx('WACLauncher__ButtonContainer WACLauncher__ButtonContainer--round WACLauncherExtended__Container', className, {
            'WACLauncher__ButtonContainer--hidden': launcherHidden,
            'WACLauncherExtended__Button--extended': extendWithoutAnimation,
            'WACLauncherExtended__Button--extendedAnimation': extendWithAnimation,
            'WACLauncherExtended__Button--reducedAnimation': shouldReduceExtendedLauncher,
            'WACLauncher__ButtonContainer--toolingPreview': isToolingPreview,
        }), ref: extendedContainerRef, "aria-hidden": isToolingPreview },
        React__default.createElement(Button, { "aria-label": ariaLabel, className: cx('WACLauncher__Button', 'WACLauncherExtended__Button', {
                WACLauncher__TourButton: activeTour,
            }), kind: activeTour ? ButtonKindEnum.GHOST : ButtonKindEnum.PRIMARY, type: "button", ref: buttonRef, onClick: onToggleOpen, tabIndex: isToolingPreview ? -1 : undefined },
            React__default.createElement("div", { className: "WACLauncherExtended__WrapperContainer" },
                React__default.createElement("div", { className: "WACLauncherExtended__Wrapper" },
                    React__default.createElement("div", { className: "WACLauncherExtended__TextHolder", ref: textHolderRef },
                        React__default.createElement("div", { className: cx('WACLauncherExtended__Greeting', {
                                'WACLauncherExtended__Element--hidden': !showGreetingMessage,
                            }), ref: greetingMessageRef },
                            React__default.createElement("div", { className: "WACLauncherExtended__GreetingText", "aria-hidden": !isExtended }, launcherGreetingMessage))),
                    React__default.createElement("div", { className: "WACLauncher__IconHolder", id: `WACLauncher__Button${serviceManager.namespace.suffix}` }, activeTour ? React__default.createElement(ArrowUpLeft, { size: 24, className: "WACLauncher__svg" }) : launcherAvatar))),
            (unreadAgentCount !== 0 || showUnreadIndicator) && (React__default.createElement("div", { className: "WAC__countIndicator" }, unreadAgentCount !== 0 ? unreadAgentCount : '')))));
}
/**
 * This will do the math to determine the max-width in pixels the launcher is allowed to extend on a given device using
 * the provided elements and set the calculated width as a css variable.
 */
function calculateAndSetMaxExtendedLauncherWidth(textHolderEl, greetingMessageEl, extendedContainerEl) {
    // The number in pixels that don't make up the space the launcher text can fill up.
    // 68px = 6px (left/right button border width) + 50px (launcher icon container) + 12px (text holder left padding)
    const nonTextSpace = 68;
    const maxLauncherExtendedWidth = getMaxLauncherExtendedWidth();
    // The max-width in pixels the launcher text can take up if the launcher were to fully extend the devices' width.
    // Add 12px to account for text holder container left padding.
    const maxTextHolderWidth = maxLauncherExtendedWidth - nonTextSpace + 12;
    // Set the text holder width and un-hide the greeting message element so that we can get the width of the greeting
    // message text to help calculate how far the launcher should extend to fit the text.
    textHolderEl.style.setProperty('width', `${maxTextHolderWidth}px`);
    // The width of the greeting message element is the max width minus the border width and left padding.
    greetingMessageEl.style.setProperty('width', `${maxTextHolderWidth - 12}px`);
    greetingMessageEl.style.setProperty('display', 'flex');
    const { clientWidth } = greetingMessageEl.querySelector('.WACLauncherExtended__GreetingText');
    // We should add a pixel to compensate for lack of clientWidth precision. It's possible for 1 line of text to be
    // rendered as 2 lines because HTML may render text with a precise width of 219.266 pixels, but clientWidth will
    // return the floor value which would be just 219.
    let launcherExtendedWidth = clientWidth + nonTextSpace + 1;
    // Make sure the launcher width doesn't exceed the max width.
    if (launcherExtendedWidth > MAX_EXTENDED_LAUNCHER_WIDTH) {
        launcherExtendedWidth = MAX_EXTENDED_LAUNCHER_WIDTH;
    }
    // Remove temporary styles applied.
    greetingMessageEl.removeAttribute('style');
    textHolderEl.removeAttribute('style');
    // Reduce the wrapper's extended width by 6 pixels to account for the launcher button's border width.
    // setWrapperExtendedWidth(launcherExtendedWidth - 6);
    // Set the extended width property to animate to.
    extendedContainerEl.style.setProperty('--cds-chat--LAUNCHER-EXTENDED-width', `${launcherExtendedWidth}px`);
}
/**
 * Performs the fade in/out animations on the provided elements.
 *
 * @param fadeOptions Options to control that elements to fade in and out.
 * @param callback The callback function to fire when the fade animations are complete.
 */
function doFadeAnimationForElements({ fadeOutElement, fadeInElement, fadeInTime = 600 }, callback) {
    if (fadeOutElement) {
        fadeOutElement.classList.remove('WACLauncherExtended__Element--hidden');
        animateWithClass(fadeOutElement, 'WACLauncherExtended__Element--FadeOut', 500, () => {
            fadeOutElement.classList.add('WACLauncherExtended__Element--hidden');
            fadeOutElement.classList.remove('WACLauncherExtended__Element--FadeOut');
            // If there is no element provided to fade in, fire the callback function after the fade out animation is complete.
            if (!fadeInElement && callback) {
                callback();
            }
        });
    }
    if (fadeInElement) {
        setTimeout(() => {
            fadeInElement.classList.remove('WACLauncherExtended__Element--hidden');
            animateWithClass(fadeInElement, 'WACLauncherExtended__Element--FadeIn', 600, () => {
                fadeInElement.classList.remove('WACLauncherExtended__Element--FadeIn');
                // Fire the callback after the fade in animation has ended.
                if (callback) {
                    callback();
                }
            });
        }, fadeInTime);
    }
}
/**
 * Calculates if the user has swiped right on their screen over the extended launcher and fires a callback function if
 * this is the case.
 */
function checkIfUserSwipedRight(touchList, touchStartCoordinates, callback) {
    const { touchStartX, touchStartY } = touchStartCoordinates;
    if (touchStartX === null || touchStartY === null) {
        return;
    }
    // Capture the coordinates the user moved to.
    const { clientX: touchEndX, clientY: touchEndY } = touchList;
    // Get the difference between when the user began swiping and where they ended.
    const differenceX = touchEndX - touchStartX;
    const differenceY = touchEndY - touchStartY;
    // If the absolute value of the X difference is greater than Y, this means the user swiped left or right on their
    // device more than they swiped up or down.
    if (Math.abs(differenceX) > Math.abs(differenceY)) {
        // If the difference is greater than 0, this means the user swiped right, and we should fire the callback function.
        if (differenceX > 0) {
            callback();
        }
    }
    // Reset the starting coordinates
    touchStartCoordinates.touchStartX = null;
    touchStartCoordinates.touchStartY = null;
}
/**
 * Returns the max width the launcher should animate to which is determined by using the lowest value between the
 * screen height or width for mobile devices. If the user is on a tablet, we don't want the launcher to extend
 * unnecessarily far, so we should cap at it 400px.
 */
function getMaxLauncherExtendedWidth() {
    const launcherPosition = IS_MOBILE ? 32 : 64;
    const { width, height } = window.screen;
    const lowestValue = Math.min(height, width);
    const extendedWidth = lowestValue - launcherPosition;
    return Math.min(extendedWidth, MAX_EXTENDED_LAUNCHER_WIDTH);
}
const LauncherExtendedExport = React__default.memo(forwardRef(LauncherExtended));

function LauncherMobileContainer(props) {
    const { launcherRef, onToggleOpen, launcherHidden, activeTour } = props;
    const serviceManager = useServiceManager();
    const { config: launcherConfig } = useSelector((state) => state.launcher);
    const unreadAgentCount = useSelector((state) => state.agentState.numUnreadMessages);
    const { mobileLauncherIsExtended: isExtended, mobileLauncherWasReduced: wasReduced, mobileLauncherDisableBounce: disableBounce, bounceTurn, showUnreadIndicator, viewState, } = useSelector((state) => state.persistedToBrowserStorage.launcherState);
    const launcherToolingPreview = useSelector((state) => state.config.public.__ibm__?.launcherToolingPreview);
    const [isStartingBounceAnimation, setIsStartingBounceAnimation] = useState(false);
    const prevIsExtended = usePrevious(isExtended);
    const prevWasReduced = usePrevious(wasReduced);
    // The bounce turn start off on in the recurring animation flow. We only care about the initial value and not its
    // subsequent values as the user goes through the flow. This will allow the user to continue where they left off in
    // the flow.
    const initialBounceTurn = useRef(bounceTurn).current;
    const previouslyPlayedExtendAnimation = useRef(wasReduced).current;
    const extendLauncherTimeoutIDRef = useRef(null);
    const reduceLauncherTimeoutIDRef = useRef(null);
    const endBounceAnimationRef = useRef(null);
    const shouldBounceRef = useRef(previouslyPlayedExtendAnimation && !disableBounce);
    const { time_to_expand, new_expand_time, time_to_reduce } = launcherConfig.mobile;
    const isExpandedLauncherEnabled = launcherConfig.mobile.is_on;
    const isToolingPreview = launcherToolingPreview === LauncherToolingPreviewType.MOBILE;
    // If the launcher container mounted with the mobile launcher not in the extended state, and it's previous value is
    // undefined, this means the launcher should be in the extended state playing the extended animation if not in the
    // tooling preview.
    const playExtendAnimation = !isToolingPreview && prevIsExtended === undefined && !isExtended;
    // Indicates if the launcher is playing the "extend" animation.
    const isExtending = prevIsExtended !== undefined && !prevIsExtended && isExtended;
    // Indicates if the launcher has completed the "reduce" animation.
    const hasReduced = prevWasReduced !== undefined && !prevWasReduced && wasReduced;
    // Prevents the launcher from playing the fade in animation after a rerender.
    const disableIntroAnimation = isToolingPreview || isExtending || hasReduced || isStartingBounceAnimation;
    const setLauncherStateAsReduced = useCallback(() => {
        if (!wasReduced) {
            serviceManager.store.dispatch(actions.setLauncherProperty('mobileLauncherWasReduced', true));
        }
    }, [wasReduced, serviceManager]);
    // This function kicks off the process of reducing the extended launcher, such as when the user scrolls the page, by
    // setting mobileLauncherIsExtended in launcher state to false. If the user does scroll the page, it will be tracked.
    const reduceLauncher = useCallback((event) => {
        clearTimeouts();
        if (isExtended) {
            document.removeEventListener('scroll', reduceLauncher);
            if (event?.type === 'scroll') {
                serviceManager.trackService.track({
                    eventName: 'Launcher interaction',
                    eventDescription: 'Mobile launcher closed from scrolling.',
                });
            }
            serviceManager.store.dispatch(actions.setLauncherProperty('mobileLauncherIsExtended', false));
        }
    }, [isExtended, serviceManager]);
    const setExpandAnimationTimeout = useCallback(() => {
        // Begin timeout to set launcher in the extended state.
        extendLauncherTimeoutIDRef.current = setTimeout(() => {
            if (!isExtended && !isExtending) {
                // Since the launcher is going to expand, set the reduced flag to false.
                serviceManager.store.dispatch(actions.setLauncherProperty('mobileLauncherWasReduced', false));
                serviceManager.store.dispatch(actions.setLauncherProperty('mobileLauncherIsExtended', true));
                serviceManager.trackService.track({
                    eventName: 'Launcher interaction',
                    eventDescription: 'Mobile launcher opened from animation.',
                });
            }
        }, time_to_expand);
    }, [isExtended, isExtending, serviceManager.store, serviceManager.trackService, time_to_expand]);
    // Clear the expand and bounce timers and set the launcher state to reduced and bounce disabled. This way if the page
    // is reloaded the launcher will behave as if it has already been opened and won't try and show a greeting.
    const setDefaultLauncherState = useCallback(() => {
        const endBounceAnimation = endBounceAnimationRef.current;
        if (endBounceAnimation) {
            endBounceAnimation();
            endBounceAnimationRef.current = null;
        }
        // Prevent the launcher from bouncing if it was toggled and allowed to play the bounce animation.
        serviceManager.store.dispatch(actions.setLauncherProperty('mobileLauncherDisableBounce', true));
        reduceLauncher();
        // The launcher should be set as reduced to prevent it from extending on the next page load.
        setLauncherStateAsReduced();
    }, [reduceLauncher, serviceManager.store, setLauncherStateAsReduced]);
    // When the launcher mounts, we should determine if it should prepare to play the "extend" animation, or kickoff the
    // bounce animation. We should kick off the bounce animation early if we have to so that we can easily determine
    // later on if it should be canceled.
    useOnMount(() => {
        // Determine if the mobile launcher wasn't reduced and can play the "extend" animation.
        if (!wasReduced && playExtendAnimation && isExpandedLauncherEnabled) {
            setExpandAnimationTimeout();
        }
        else if (shouldBounceRef.current) {
            const launcherContainerElement = launcherRef?.current?.launcherContainerElement();
            if (launcherContainerElement) {
                const startRecurringBounceAnimation = () => {
                    // This function is added as an event listener to the container, however the function isn't actually run until
                    // the event listener is triggered. Because of this it's possible that the state has since changed, and we
                    // actually don't want to bounce the launcher after all, so we need to check that we still want to bounce.
                    if (shouldBounceRef.current) {
                        // Track the bounce turn the user is currently on in the recurring animation flow.
                        let turnCounter = initialBounceTurn;
                        launcherContainerElement.removeEventListener('animationend', startRecurringBounceAnimation);
                        setIsStartingBounceAnimation(true);
                        endBounceAnimationRef.current = setAnimationTimeouts(launcherContainerElement, 'WACLauncher__ButtonContainer--bounceAnimation', BOUNCING_ANIMATION_TIMEOUTS, {
                            startingIndex: initialBounceTurn - 1,
                            afterEach: () => {
                                serviceManager.trackService.track({
                                    eventName: 'Launcher interaction',
                                    eventDescription: `Mobile launcher finished bounce animation ${turnCounter}.`,
                                });
                                // Increase the turn counter and have web chat remember where the user left off in the flow.
                                turnCounter++;
                                serviceManager.store.dispatch(actions.setLauncherProperty('bounceTurn', turnCounter));
                            },
                            afterAll: () => {
                                serviceManager.store.dispatch(actions.setLauncherProperty('mobileLauncherDisableBounce', true));
                            },
                        });
                    }
                };
                // Once the launcher container has completed fading in, kick off the recurring bounce animation.
                launcherContainerElement.addEventListener('animationend', startRecurringBounceAnimation);
            }
        }
    });
    // If the main window or tour have been opened then clear all timers and set the launcher state as if it had been
    // clicked open. This is to protect against scenarios where the main window or tour are opened using other methods
    // besides clicking on the launcher.
    useEffect(() => {
        if (viewState.mainWindow || viewState.tour) {
            // Clear timers and update launcher state so that no more greeting messages or bounces occur.
            setDefaultLauncherState();
        }
    }, [viewState, setDefaultLauncherState]);
    // If the launcher time_to_expand changes then we need to clear the existing timers and start new ones with the new time.
    useEffect(() => {
        if (new_expand_time) {
            // If the launcher was supposed to bounce make sure it doesn't.
            if (shouldBounceRef.current) {
                shouldBounceRef.current = false;
            }
            // If there was an existing bounce timer going then clear the timeout so the bounce animation doesn't show.
            const endBounceAnimation = endBounceAnimationRef.current;
            if (endBounceAnimation) {
                endBounceAnimation();
                endBounceAnimationRef.current = null;
            }
            // If there was an existing "extend" timer going then clear the timeout so the original extend doesn't occur.
            if (extendLauncherTimeoutIDRef.current) {
                clearTimeout(extendLauncherTimeoutIDRef.current);
            }
            // Set the "expand" timers again with the new timeout that's been provided.
            setExpandAnimationTimeout();
            serviceManager.store.dispatch(actions.setLauncherConfigProperty('new_expand_time', false, LauncherType.MOBILE));
        }
    }, [setExpandAnimationTimeout, new_expand_time, serviceManager.store, shouldBounceRef]);
    function clearTimeouts() {
        const extendLauncherTimeoutID = extendLauncherTimeoutIDRef.current;
        const reduceLauncherTimeoutID = reduceLauncherTimeoutIDRef.current;
        // Clears all the existing timeouts that were set.
        if (reduceLauncherTimeoutID) {
            clearTimeout(reduceLauncherTimeoutID);
            reduceLauncherTimeoutIDRef.current = null;
        }
        if (extendLauncherTimeoutID) {
            clearTimeout(extendLauncherTimeoutID);
            extendLauncherTimeoutIDRef.current = null;
        }
    }
    // When the launcher is toggled, reduce the launcher if it's extended and open the widget.
    const handleToggleOpen = useCallback(() => {
        // Do nothing for tooling preview.
        if (!isToolingPreview) {
            setDefaultLauncherState();
            serviceManager.actions.track({
                eventName: 'Widget Opened',
                eventDescription: 'User has opened the widget.',
                launcherType: LauncherType.MOBILE,
            });
            onToggleOpen();
        }
    }, [isToolingPreview, onToggleOpen, serviceManager.actions, setDefaultLauncherState]);
    // Track the user swiping to the right over the launcher and reduce the launcher.
    const handleSwipeRight = useCallback(() => {
        if (!isToolingPreview) {
            serviceManager.trackService.track({
                eventName: 'Launcher interaction',
                eventDescription: 'Mobile launcher closed from swiping.',
            });
            reduceLauncher();
        }
    }, [isToolingPreview, reduceLauncher, serviceManager]);
    // When the launcher extends and a proper reduce timeout is set, we should kick off the timeout that will reduce the
    // launcher.
    useEffect(() => {
        if (isExtended) {
            // Begin timeout to reduce extended launcher.
            reduceLauncherTimeoutIDRef.current = setTimeout(() => {
                reduceLauncher();
                serviceManager.trackService.track({
                    eventName: 'Launcher interaction',
                    eventDescription: 'Mobile launcher closed from animation.',
                });
            }, time_to_reduce);
            // Detect the user scrolling the page to begin reducing the launcher.
            document.addEventListener('scroll', reduceLauncher);
        }
    }, [isExtended, reduceLauncher, time_to_reduce, serviceManager]);
    return (React__default.createElement(LauncherExtendedExport, { className: cx({ 'WACLauncher__ButtonContainer--noAnimation': disableIntroAnimation }), ref: launcherRef, serviceManager: serviceManager, launcherConfig: launcherConfig, showUnreadIndicator: showUnreadIndicator, unreadAgentCount: unreadAgentCount, disableReduce: isToolingPreview, isExtended: isExtended || isToolingPreview, playExtendAnimation: playExtendAnimation, onToggleOpen: handleToggleOpen, onSwipeRight: handleSwipeRight, onReduceEnd: setLauncherStateAsReduced, isToolingPreview: isToolingPreview, launcherHidden: launcherHidden, activeTour: activeTour }));
}

function LauncherContainer() {
    const serviceManager = useServiceManager();
    const launcherRef = useRef();
    const publicConfig = useSelector((state) => state.config.public);
    const renderExtendedLauncher = useSelector((state) => state.config.public.__ibm__.renderExtendedLauncher);
    const viewState = useSelector((state) => state.persistedToBrowserStorage.launcherState.viewState);
    const initialViewChangeComplete = useSelector((state) => state.initialViewChangeComplete);
    const launcherHidden = !viewState.launcher;
    const activeTour = useSelector((state) => state.persistedToBrowserStorage.launcherState.activeTour);
    const requestFocus = useCallback(() => {
        launcherRef.current?.requestFocus();
    }, [launcherRef]);
    // If there's an active tour then on launcher click switch to the tour view. If there is not an active tour then on
    // launcher click, fire the window open events, and switch to the main window. After switching to either the tour or
    // the main window kick off hydration if the chat isn't hydrated yet.
    const onDoToggle = useCallback(() => {
        if (activeTour) {
            serviceManager.trackService.track({
                eventName: 'Tour interaction',
                eventDescription: 'Tour continued.',
                type: 'From launcher.',
            });
            // If there's an active tour then try to open the tour on launcher click.
            return serviceManager.actions.changeView(ViewType.TOUR, {
                viewChangeReason: ViewChangeReason.LAUNCHER_CLICKED,
            });
        }
        // Otherwise try to open the main window on launcher click.
        return serviceManager.actions.changeView(ViewType.MAIN_WINDOW, {
            mainWindowOpenReason: MainWindowOpenReason.DEFAULT_LAUNCHER,
        });
    }, [activeTour, serviceManager.actions, serviceManager.trackService]);
    useEffectDidUpdate(() => {
        // If the main window and tour view are closed, and the launcher is visible, then we should request focus on the
        // launcher. We need to wait for the initial view change to complete before requesting focus when the viewState
        // changes. This is because we don't want to request focus after the first view change when
        // Chat.startInternal switches from all views closed to whatever the starting view state is. Instead
        // we want to wait to request focus until after user interactions that trigger changes to the viewState.
        if (viewState.launcher && !viewState.mainWindow && !viewState.tour && initialViewChangeComplete) {
            launcherRef.current?.requestFocus();
        }
    }, [viewState]);
    let launcherContainer;
    if (IS_PHONE ||
        renderExtendedLauncher ||
        publicConfig.__ibm__?.launcherToolingPreview === LauncherToolingPreviewType.MOBILE) {
        launcherContainer = (React__default.createElement(LauncherMobileContainer, { launcherRef: launcherRef, onToggleOpen: onDoToggle, launcherHidden: launcherHidden, activeTour: activeTour }));
    }
    else {
        launcherContainer = (React__default.createElement(LauncherDesktopContainer, { launcherRef: launcherRef, onDoToggle: onDoToggle, requestFocus: requestFocus, launcherHidden: launcherHidden, activeTour: activeTour }));
    }
    return launcherContainer;
}

function withAriaAnnouncer(Component) {
    return React__default.forwardRef((props, ref) => {
        const ariaAnnouncer = useContext(AriaAnnouncerContext);
        return React__default.createElement(Component, { ...props, ref: ref, ariaAnnouncer: ariaAnnouncer });
    });
}

class AnnounceOnMountComponent extends PureComponent {
    constructor() {
        super(...arguments);
        /**
         * Default state.
         */
        this.state = { isMounted: false };
        /**
         * Indicates if the "once" prop message has been announced.
         */
        this.onceAnnounced = false;
    }
    componentDidMount() {
        this.setState({ isMounted: true });
        if (!this.onceAnnounced) {
            if (this.props.announceOnce) {
                setTimeout(() => {
                    this.props.ariaAnnouncer(this.props.announceOnce);
                });
            }
            this.onceAnnounced = true;
        }
    }
    render() {
        return React__default.createElement("div", { "aria-live": "polite" }, this.state.isMounted && this.props.children);
    }
}
const AnnounceOnMountComponentExport = withAriaAnnouncer(AnnounceOnMountComponent);

/**
 * This file contains the context used by the {@link HideComponent} which is used to indicate if the component is
 * hidden or not. The value in the context is a boolean that is true if the component is hidden.
 */
const HideComponentContext = React__default.createContext(false);

/**
 * This component is used to potentially hide it's children and to make that state available through context to all
 * of the component's children. Note that this does not currently work if you nest one of these components inside of
 * another one.
 */
function HideComponent(props) {
    const { hidden, children, className, ...htmlAttributes } = props;
    return (React__default.createElement(HideComponentContext.Provider, { value: hidden },
        React__default.createElement("div", { className: cx(className, { WAC__hidden: hidden }), ...htmlAttributes }, children)));
}

class MountChildrenOnDelay extends PureComponent {
    constructor() {
        super(...arguments);
        this.state = { showChildren: false };
    }
    static { this.defaultProps = { delay: 500 }; }
    componentDidMount() {
        // Once the component is mounted, start the countdown.
        this.onComponentDidMount = setTimeout(() => {
            this.setState({ showChildren: true });
        }, this.props.delay);
    }
    // If we unmount before the delay runs out, we can get browser errors if we don't clear.
    componentWillUnmount() {
        clearTimeout(this.onComponentDidMount);
        this.onComponentDidMount = undefined;
    }
    render() {
        if (!this.state.showChildren) {
            return false;
        }
        return this.props.children;
    }
}

/**
 * This renders the close and minimize buttons for the tour feature.
 */
function TourCloseMinimizeComponent(props) {
    const { hideMinimizeButton, onMinimizeClick, onCloseClick } = props;
    const { tour_ariaMinimizeButton, tour_ariaCloseButton } = useLanguagePack();
    return (React__default.createElement("div", { className: "WACTour__CloseMinimizeWrapper" },
        !hideMinimizeButton && (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Button, { className: "WACTour__CloseMinimizeButton WACTour__MinimizeButton", kind: "ghost", hasIconOnly: true, renderIcon: Subtract, iconDescription: tour_ariaMinimizeButton, onClick: onMinimizeClick, "aria-label": tour_ariaMinimizeButton }),
            React__default.createElement("div", { className: "WACTour__CloseMinimizeDivider" }))),
        React__default.createElement(Button, { className: "WACTour__CloseMinimizeButton WACTour__CloseButton", kind: "ghost", hasIconOnly: true, renderIcon: Close, iconDescription: tour_ariaCloseButton, onClick: onCloseClick, "aria-label": tour_ariaCloseButton })));
}

/**
 * This renders the buttons to open the main window, progress forward and back through the steps, and end the tour once
 * on the last step.
 */
function TourControlsComponent(props, ref) {
    const { reservePreviousButtonSpace, hidePreviousButton, renderDoneButton, disableNextButton, hideButtons, hideChatButton, onOpenMainWindowClick, onNextClick, onPreviousClick, onDoneClick, } = props;
    const { tour_ariaChatButton, tour_ariaNextButton, tour_ariaPreviousButton, tour_doneButton } = useLanguagePack();
    const nextButtonRef = useRef();
    const doneButtonRef = useRef();
    useImperativeHandle(ref, () => ({
        requestFocus: () => {
            setTimeout(() => {
                if (doneButtonRef.current) {
                    doFocusRef(doneButtonRef);
                }
                else {
                    doFocusRef(nextButtonRef);
                }
            });
        },
    }));
    return (React__default.createElement("div", { className: cx('WACTour__Controls', { 'WACTour__Controls--empty': hideButtons }) },
        !hideChatButton && (React__default.createElement("div", { className: "WACTour__ControlsButton" },
            React__default.createElement(Button, { className: "WACTour__MainWindowButton", kind: "ghost", size: "md", hasIconOnly: true, renderIcon: props => React__default.createElement(Chat$3, { ...props }), iconDescription: tour_ariaChatButton, onClick: onOpenMainWindowClick, "aria-label": tour_ariaChatButton }))),
        reservePreviousButtonSpace && (React__default.createElement("div", { className: "WACTour__ControlsButton" },
            React__default.createElement(Button, { className: cx('WACTour__PreviousButton', {
                    'WACTour__ControlsButton--hidden': hidePreviousButton,
                }), kind: "ghost", size: "md", hasIconOnly: true, renderIcon: props => React__default.createElement(ArrowLeft, { ...props }), iconDescription: tour_ariaPreviousButton, onClick: onPreviousClick, "aria-label": tour_ariaPreviousButton }))),
        renderDoneButton && (React__default.createElement("div", { className: "WACTour__ControlsButton" },
            React__default.createElement(Button, { ref: doneButtonRef, className: "WACTour__DoneButton", size: "md", onClick: onDoneClick }, tour_doneButton))),
        !renderDoneButton && (React__default.createElement("div", { className: "WACTour__ControlsButton" },
            React__default.createElement(Button, { ref: nextButtonRef, className: "WACTour__NextButton", size: "md", hasIconOnly: true, renderIcon: props => React__default.createElement(ArrowRight, { ...props }), iconDescription: tour_ariaNextButton, onClick: onNextClick, disabled: disableNextButton, "aria-label": tour_ariaNextButton })))));
}
const TourControlsComponentExport = React__default.forwardRef(TourControlsComponent);

/**
 * This component renders the Textual part of the cards - more specifically Title, description, favicon URL
 * or default fallback icon. In the case of Iframes, this also renders the URL part. Also renders a launch icon in
 * the case of text-only cards
 */
function TextHolderTile({ title, description, displayURL, urlHostName, hideTitle }) {
    return (React__default.createElement("div", { className: "WACTextHolderTile" },
        React__default.createElement("div", { className: cx('WACTextHolderTile__Wrapper', 'WACWidget__textEllipsis', {
                WACTextHolderTile__IconMargin: !displayURL,
            }) },
            !hideTitle && title && React__default.createElement("div", { className: "WACTextHolderTile__Title" }, title),
            description && (React__default.createElement("div", { className: cx('WACTextHolderTile__Description', { WACTextHolderTile__DescriptionMargin: title }) }, description)),
            displayURL && (React__default.createElement(React__default.Fragment, null,
                React__default.createElement(VisuallyHidden, null, urlHostName),
                React__default.createElement("div", { className: cx('WACTextHolderTile__Url', 'WACWidget__textEllipsis', {
                        WACTextHolderTile__UrlMargin: title || description,
                    }), "aria-hidden": true }, displayURL))))));
}

function Image(props) {
    const { imageError, title, description, displayURL, hideIconAndTitle, needsAnnouncement, renderIcon, inline } = props;
    const ariaAnnouncer = useAriaAnnouncer();
    const [isLoaded, setIsLoaded] = useState(false);
    const [isError, setIsError] = useState(false);
    const rootRef = useRef();
    // This ref is for merely saving the initial value of shouldAnnounce prop.
    const needsAnnouncementRef = useRef(needsAnnouncement);
    const hasText = Boolean(title || description || displayURL);
    const Icon = renderIcon;
    // This effect announces the contents of this response type once the image has loaded.
    useEffect(() => {
        if (isLoaded && needsAnnouncementRef.current) {
            ariaAnnouncer(rootRef.current);
        }
    }, [ariaAnnouncer, isLoaded]);
    if (isError) {
        return React__default.createElement(InlineError, { text: imageError });
    }
    if (inline) {
        return (React__default.createElement(ImageOnly, { ...props, setIsError: setIsError, setIsLoaded: setIsLoaded, isError: isError, isLoaded: isLoaded }));
    }
    return (React__default.createElement(Tile, { ref: rootRef, className: cx('WACImage', {
            WACImage__TextAndIcon: hasText && Boolean(renderIcon),
            WACImage__IconOnly: !hideIconAndTitle && !title && !description && Boolean(renderIcon),
        }) },
        React__default.createElement("div", { className: "WACImage__ImageWrapper" },
            React__default.createElement(ImageOnly, { ...props, setIsError: setIsError, setIsLoaded: setIsLoaded, isError: isError, isLoaded: isLoaded })),
        hasText && (React__default.createElement(TextHolderTile, { title: title, description: description, displayURL: displayURL, urlHostName: displayURL && getURLHostName(displayURL), hideTitle: hideIconAndTitle })),
        Boolean(Icon) && (React__default.createElement(Icon, { className: cx('WACImage__Icon', 'WACDirectionHasReversibleSVG', { 'WACImage__Icon--link': displayURL }) }))));
}
function ImageOnly({ source, title, description, altText, displayURL, preventInlineError, onImageLoad, useAITheme, isLoaded, isError, setIsLoaded, setIsError, className, inline, }) {
    const [isImageHidden, setIsImageHidden] = useState(false);
    const imageAlt = altText || title || description || '';
    const hasText = Boolean(title || description || displayURL);
    /**
     * Upon an error, update the error loading flag in order to render an inline error.
     */
    const handleError = useCallback(() => {
        if (preventInlineError && hasText) {
            setIsImageHidden(true);
        }
        else {
            setIsError(true);
        }
    }, [preventInlineError, hasText, setIsError]);
    // This effect sets a timeout that auto error handles after 10 seconds of waiting for the image to load. Once the
    // image has loaded, the skeleton will be hidden, and we can clear the timeout.
    useEffect(() => {
        let errorTimeout = null;
        if (!isLoaded) {
            errorTimeout = setTimeout(handleError, RESPONSE_TYPE_TIMEOUT_MS);
        }
        return () => {
            clearTimeout(errorTimeout);
        };
    }, [isLoaded, handleError]);
    return (React__default.createElement(React__default.Fragment, null,
        !isLoaded && !isImageHidden && !inline && source && (React__default.createElement(React__default.Fragment, null, useAITheme ? (React__default.createElement(unstable__AiSkeletonPlaceholder, { className: "WACImage__Skeleton" })) : (React__default.createElement(SkeletonPlaceholder$1, { className: "WACImage__Skeleton" })))),
        !isError && !isImageHidden && source && (React__default.createElement("img", { className: cx('WACImage__Image', {
                [className]: className,
                'WACImage__Image--loaded': isLoaded,
            }), src: source, alt: imageAlt, onLoad: () => {
                onImageLoad?.();
                setIsLoaded(true);
            }, onError: handleError }))));
}
const ImageExport = React__default.memo(Image);

/* eslint-disable no-multi-assign */

function deepFreeze(obj) {
  if (obj instanceof Map) {
    obj.clear =
      obj.delete =
      obj.set =
        function () {
          throw new Error('map is read-only');
        };
  } else if (obj instanceof Set) {
    obj.add =
      obj.clear =
      obj.delete =
        function () {
          throw new Error('set is read-only');
        };
  }

  // Freeze self
  Object.freeze(obj);

  Object.getOwnPropertyNames(obj).forEach((name) => {
    const prop = obj[name];
    const type = typeof prop;

    // Freeze prop if it is an object or function and also not already frozen
    if ((type === 'object' || type === 'function') && !Object.isFrozen(prop)) {
      deepFreeze(prop);
    }
  });

  return obj;
}

/** @typedef {import('highlight.js').CallbackResponse} CallbackResponse */
/** @typedef {import('highlight.js').CompiledMode} CompiledMode */
/** @implements CallbackResponse */

class Response {
  /**
   * @param {CompiledMode} mode
   */
  constructor(mode) {
    // eslint-disable-next-line no-undefined
    if (mode.data === undefined) mode.data = {};

    this.data = mode.data;
    this.isMatchIgnored = false;
  }

  ignoreMatch() {
    this.isMatchIgnored = true;
  }
}

/**
 * @param {string} value
 * @returns {string}
 */
function escapeHTML(value) {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;');
}

/**
 * performs a shallow merge of multiple objects into one
 *
 * @template T
 * @param {T} original
 * @param {Record<string,any>[]} objects
 * @returns {T} a single new object
 */
function inherit$1(original, ...objects) {
  /** @type Record<string,any> */
  const result = Object.create(null);

  for (const key in original) {
    result[key] = original[key];
  }
  objects.forEach(function(obj) {
    for (const key in obj) {
      result[key] = obj[key];
    }
  });
  return /** @type {T} */ (result);
}

/**
 * @typedef {object} Renderer
 * @property {(text: string) => void} addText
 * @property {(node: Node) => void} openNode
 * @property {(node: Node) => void} closeNode
 * @property {() => string} value
 */

/** @typedef {{scope?: string, language?: string, sublanguage?: boolean}} Node */
/** @typedef {{walk: (r: Renderer) => void}} Tree */
/** */

const SPAN_CLOSE = '</span>';

/**
 * Determines if a node needs to be wrapped in <span>
 *
 * @param {Node} node */
const emitsWrappingTags = (node) => {
  // rarely we can have a sublanguage where language is undefined
  // TODO: track down why
  return !!node.scope;
};

/**
 *
 * @param {string} name
 * @param {{prefix:string}} options
 */
const scopeToCSSClass = (name, { prefix }) => {
  // sub-language
  if (name.startsWith("language:")) {
    return name.replace("language:", "language-");
  }
  // tiered scope: comment.line
  if (name.includes(".")) {
    const pieces = name.split(".");
    return [
      `${prefix}${pieces.shift()}`,
      ...(pieces.map((x, i) => `${x}${"_".repeat(i + 1)}`))
    ].join(" ");
  }
  // simple scope
  return `${prefix}${name}`;
};

/** @type {Renderer} */
class HTMLRenderer {
  /**
   * Creates a new HTMLRenderer
   *
   * @param {Tree} parseTree - the parse tree (must support `walk` API)
   * @param {{classPrefix: string}} options
   */
  constructor(parseTree, options) {
    this.buffer = "";
    this.classPrefix = options.classPrefix;
    parseTree.walk(this);
  }

  /**
   * Adds texts to the output stream
   *
   * @param {string} text */
  addText(text) {
    this.buffer += escapeHTML(text);
  }

  /**
   * Adds a node open to the output stream (if needed)
   *
   * @param {Node} node */
  openNode(node) {
    if (!emitsWrappingTags(node)) return;

    const className = scopeToCSSClass(node.scope,
      { prefix: this.classPrefix });
    this.span(className);
  }

  /**
   * Adds a node close to the output stream (if needed)
   *
   * @param {Node} node */
  closeNode(node) {
    if (!emitsWrappingTags(node)) return;

    this.buffer += SPAN_CLOSE;
  }

  /**
   * returns the accumulated buffer
  */
  value() {
    return this.buffer;
  }

  // helpers

  /**
   * Builds a span element
   *
   * @param {string} className */
  span(className) {
    this.buffer += `<span class="${className}">`;
  }
}

/** @typedef {{scope?: string, language?: string, children: Node[]} | string} Node */
/** @typedef {{scope?: string, language?: string, children: Node[]} } DataNode */
/** @typedef {import('highlight.js').Emitter} Emitter */
/**  */

/** @returns {DataNode} */
const newNode = (opts = {}) => {
  /** @type DataNode */
  const result = { children: [] };
  Object.assign(result, opts);
  return result;
};

class TokenTree {
  constructor() {
    /** @type DataNode */
    this.rootNode = newNode();
    this.stack = [this.rootNode];
  }

  get top() {
    return this.stack[this.stack.length - 1];
  }

  get root() { return this.rootNode; }

  /** @param {Node} node */
  add(node) {
    this.top.children.push(node);
  }

  /** @param {string} scope */
  openNode(scope) {
    /** @type Node */
    const node = newNode({ scope });
    this.add(node);
    this.stack.push(node);
  }

  closeNode() {
    if (this.stack.length > 1) {
      return this.stack.pop();
    }
    // eslint-disable-next-line no-undefined
    return undefined;
  }

  closeAllNodes() {
    while (this.closeNode());
  }

  toJSON() {
    return JSON.stringify(this.rootNode, null, 4);
  }

  /**
   * @typedef { import("./html_renderer").Renderer } Renderer
   * @param {Renderer} builder
   */
  walk(builder) {
    // this does not
    return this.constructor._walk(builder, this.rootNode);
    // this works
    // return TokenTree._walk(builder, this.rootNode);
  }

  /**
   * @param {Renderer} builder
   * @param {Node} node
   */
  static _walk(builder, node) {
    if (typeof node === "string") {
      builder.addText(node);
    } else if (node.children) {
      builder.openNode(node);
      node.children.forEach((child) => this._walk(builder, child));
      builder.closeNode(node);
    }
    return builder;
  }

  /**
   * @param {Node} node
   */
  static _collapse(node) {
    if (typeof node === "string") return;
    if (!node.children) return;

    if (node.children.every(el => typeof el === "string")) {
      // node.text = node.children.join("");
      // delete node.children;
      node.children = [node.children.join("")];
    } else {
      node.children.forEach((child) => {
        TokenTree._collapse(child);
      });
    }
  }
}

/**
  Currently this is all private API, but this is the minimal API necessary
  that an Emitter must implement to fully support the parser.

  Minimal interface:

  - addText(text)
  - __addSublanguage(emitter, subLanguageName)
  - startScope(scope)
  - endScope()
  - finalize()
  - toHTML()

*/

/**
 * @implements {Emitter}
 */
class TokenTreeEmitter extends TokenTree {
  /**
   * @param {*} options
   */
  constructor(options) {
    super();
    this.options = options;
  }

  /**
   * @param {string} text
   */
  addText(text) {
    if (text === "") { return; }

    this.add(text);
  }

  /** @param {string} scope */
  startScope(scope) {
    this.openNode(scope);
  }

  endScope() {
    this.closeNode();
  }

  /**
   * @param {Emitter & {root: DataNode}} emitter
   * @param {string} name
   */
  __addSublanguage(emitter, name) {
    /** @type DataNode */
    const node = emitter.root;
    if (name) node.scope = `language:${name}`;

    this.add(node);
  }

  toHTML() {
    const renderer = new HTMLRenderer(this, this.options);
    return renderer.value();
  }

  finalize() {
    this.closeAllNodes();
    return true;
  }
}

/**
 * @param {string} value
 * @returns {RegExp}
 * */

/**
 * @param {RegExp | string } re
 * @returns {string}
 */
function source(re) {
  if (!re) return null;
  if (typeof re === "string") return re;

  return re.source;
}

/**
 * @param {RegExp | string } re
 * @returns {string}
 */
function lookahead(re) {
  return concat('(?=', re, ')');
}

/**
 * @param {RegExp | string } re
 * @returns {string}
 */
function anyNumberOfTimes(re) {
  return concat('(?:', re, ')*');
}

/**
 * @param {RegExp | string } re
 * @returns {string}
 */
function optional(re) {
  return concat('(?:', re, ')?');
}

/**
 * @param {...(RegExp | string) } args
 * @returns {string}
 */
function concat(...args) {
  const joined = args.map((x) => source(x)).join("");
  return joined;
}

/**
 * @param { Array<string | RegExp | Object> } args
 * @returns {object}
 */
function stripOptionsFromArgs(args) {
  const opts = args[args.length - 1];

  if (typeof opts === 'object' && opts.constructor === Object) {
    args.splice(args.length - 1, 1);
    return opts;
  } else {
    return {};
  }
}

/** @typedef { {capture?: boolean} } RegexEitherOptions */

/**
 * Any of the passed expresssions may match
 *
 * Creates a huge this | this | that | that match
 * @param {(RegExp | string)[] | [...(RegExp | string)[], RegexEitherOptions]} args
 * @returns {string}
 */
function either(...args) {
  /** @type { object & {capture?: boolean} }  */
  const opts = stripOptionsFromArgs(args);
  const joined = '('
    + (opts.capture ? "" : "?:")
    + args.map((x) => source(x)).join("|") + ")";
  return joined;
}

/**
 * @param {RegExp | string} re
 * @returns {number}
 */
function countMatchGroups(re) {
  return (new RegExp(re.toString() + '|')).exec('').length - 1;
}

/**
 * Does lexeme start with a regular expression match at the beginning
 * @param {RegExp} re
 * @param {string} lexeme
 */
function startsWith(re, lexeme) {
  const match = re && re.exec(lexeme);
  return match && match.index === 0;
}

// BACKREF_RE matches an open parenthesis or backreference. To avoid
// an incorrect parse, it additionally matches the following:
// - [...] elements, where the meaning of parentheses and escapes change
// - other escape sequences, so we do not misparse escape sequences as
//   interesting elements
// - non-matching or lookahead parentheses, which do not capture. These
//   follow the '(' with a '?'.
const BACKREF_RE = /\[(?:[^\\\]]|\\.)*\]|\(\??|\\([1-9][0-9]*)|\\./;

// **INTERNAL** Not intended for outside usage
// join logically computes regexps.join(separator), but fixes the
// backreferences so they continue to match.
// it also places each individual regular expression into it's own
// match group, keeping track of the sequencing of those match groups
// is currently an exercise for the caller. :-)
/**
 * @param {(string | RegExp)[]} regexps
 * @param {{joinWith: string}} opts
 * @returns {string}
 */
function _rewriteBackreferences(regexps, { joinWith }) {
  let numCaptures = 0;

  return regexps.map((regex) => {
    numCaptures += 1;
    const offset = numCaptures;
    let re = source(regex);
    let out = '';

    while (re.length > 0) {
      const match = BACKREF_RE.exec(re);
      if (!match) {
        out += re;
        break;
      }
      out += re.substring(0, match.index);
      re = re.substring(match.index + match[0].length);
      if (match[0][0] === '\\' && match[1]) {
        // Adjust the backreference.
        out += '\\' + String(Number(match[1]) + offset);
      } else {
        out += match[0];
        if (match[0] === '(') {
          numCaptures++;
        }
      }
    }
    return out;
  }).map(re => `(${re})`).join(joinWith);
}

/** @typedef {import('highlight.js').Mode} Mode */
/** @typedef {import('highlight.js').ModeCallback} ModeCallback */

// Common regexps
const MATCH_NOTHING_RE = /\b\B/;
const IDENT_RE = '[a-zA-Z]\\w*';
const UNDERSCORE_IDENT_RE = '[a-zA-Z_]\\w*';
const NUMBER_RE = '\\b\\d+(\\.\\d+)?';
const C_NUMBER_RE = '(-?)(\\b0[xX][a-fA-F0-9]+|(\\b\\d+(\\.\\d*)?|\\.\\d+)([eE][-+]?\\d+)?)'; // 0x..., 0..., decimal, float
const BINARY_NUMBER_RE = '\\b(0b[01]+)'; // 0b...
const RE_STARTERS_RE = '!|!=|!==|%|%=|&|&&|&=|\\*|\\*=|\\+|\\+=|,|-|-=|/=|/|:|;|<<|<<=|<=|<|===|==|=|>>>=|>>=|>=|>>>|>>|>|\\?|\\[|\\{|\\(|\\^|\\^=|\\||\\|=|\\|\\||~';

/**
* @param { Partial<Mode> & {binary?: string | RegExp} } opts
*/
const SHEBANG = (opts = {}) => {
  const beginShebang = /^#![ ]*\//;
  if (opts.binary) {
    opts.begin = concat(
      beginShebang,
      /.*\b/,
      opts.binary,
      /\b.*/);
  }
  return inherit$1({
    scope: 'meta',
    begin: beginShebang,
    end: /$/,
    relevance: 0,
    /** @type {ModeCallback} */
    "on:begin": (m, resp) => {
      if (m.index !== 0) resp.ignoreMatch();
    }
  }, opts);
};

// Common modes
const BACKSLASH_ESCAPE = {
  begin: '\\\\[\\s\\S]', relevance: 0
};
const APOS_STRING_MODE = {
  scope: 'string',
  begin: '\'',
  end: '\'',
  illegal: '\\n',
  contains: [BACKSLASH_ESCAPE]
};
const QUOTE_STRING_MODE = {
  scope: 'string',
  begin: '"',
  end: '"',
  illegal: '\\n',
  contains: [BACKSLASH_ESCAPE]
};
const PHRASAL_WORDS_MODE = {
  begin: /\b(a|an|the|are|I'm|isn't|don't|doesn't|won't|but|just|should|pretty|simply|enough|gonna|going|wtf|so|such|will|you|your|they|like|more)\b/
};
/**
 * Creates a comment mode
 *
 * @param {string | RegExp} begin
 * @param {string | RegExp} end
 * @param {Mode | {}} [modeOptions]
 * @returns {Partial<Mode>}
 */
const COMMENT = function(begin, end, modeOptions = {}) {
  const mode = inherit$1(
    {
      scope: 'comment',
      begin,
      end,
      contains: []
    },
    modeOptions
  );
  mode.contains.push({
    scope: 'doctag',
    // hack to avoid the space from being included. the space is necessary to
    // match here to prevent the plain text rule below from gobbling up doctags
    begin: '[ ]*(?=(TODO|FIXME|NOTE|BUG|OPTIMIZE|HACK|XXX):)',
    end: /(TODO|FIXME|NOTE|BUG|OPTIMIZE|HACK|XXX):/,
    excludeBegin: true,
    relevance: 0
  });
  const ENGLISH_WORD = either(
    // list of common 1 and 2 letter words in English
    "I",
    "a",
    "is",
    "so",
    "us",
    "to",
    "at",
    "if",
    "in",
    "it",
    "on",
    // note: this is not an exhaustive list of contractions, just popular ones
    /[A-Za-z]+['](d|ve|re|ll|t|s|n)/, // contractions - can't we'd they're let's, etc
    /[A-Za-z]+[-][a-z]+/, // `no-way`, etc.
    /[A-Za-z][a-z]{2,}/ // allow capitalized words at beginning of sentences
  );
  // looking like plain text, more likely to be a comment
  mode.contains.push(
    {
      // TODO: how to include ", (, ) without breaking grammars that use these for
      // comment delimiters?
      // begin: /[ ]+([()"]?([A-Za-z'-]{3,}|is|a|I|so|us|[tT][oO]|at|if|in|it|on)[.]?[()":]?([.][ ]|[ ]|\))){3}/
      // ---

      // this tries to find sequences of 3 english words in a row (without any
      // "programming" type syntax) this gives us a strong signal that we've
      // TRULY found a comment - vs perhaps scanning with the wrong language.
      // It's possible to find something that LOOKS like the start of the
      // comment - but then if there is no readable text - good chance it is a
      // false match and not a comment.
      //
      // for a visual example please see:
      // https://github.com/highlightjs/highlight.js/issues/2827

      begin: concat(
        /[ ]+/, // necessary to prevent us gobbling up doctags like /* @author Bob Mcgill */
        '(',
        ENGLISH_WORD,
        /[.]?[:]?([.][ ]|[ ])/,
        '){3}') // look for 3 words in a row
    }
  );
  return mode;
};
const C_LINE_COMMENT_MODE = COMMENT('//', '$');
const C_BLOCK_COMMENT_MODE = COMMENT('/\\*', '\\*/');
const HASH_COMMENT_MODE = COMMENT('#', '$');
const NUMBER_MODE = {
  scope: 'number',
  begin: NUMBER_RE,
  relevance: 0
};
const C_NUMBER_MODE = {
  scope: 'number',
  begin: C_NUMBER_RE,
  relevance: 0
};
const BINARY_NUMBER_MODE = {
  scope: 'number',
  begin: BINARY_NUMBER_RE,
  relevance: 0
};
const REGEXP_MODE = {
  scope: "regexp",
  begin: /\/(?=[^/\n]*\/)/,
  end: /\/[gimuy]*/,
  contains: [
    BACKSLASH_ESCAPE,
    {
      begin: /\[/,
      end: /\]/,
      relevance: 0,
      contains: [BACKSLASH_ESCAPE]
    }
  ]
};
const TITLE_MODE = {
  scope: 'title',
  begin: IDENT_RE,
  relevance: 0
};
const UNDERSCORE_TITLE_MODE = {
  scope: 'title',
  begin: UNDERSCORE_IDENT_RE,
  relevance: 0
};
const METHOD_GUARD = {
  // excludes method names from keyword processing
  begin: '\\.\\s*' + UNDERSCORE_IDENT_RE,
  relevance: 0
};

/**
 * Adds end same as begin mechanics to a mode
 *
 * Your mode must include at least a single () match group as that first match
 * group is what is used for comparison
 * @param {Partial<Mode>} mode
 */
const END_SAME_AS_BEGIN = function(mode) {
  return Object.assign(mode,
    {
      /** @type {ModeCallback} */
      'on:begin': (m, resp) => { resp.data._beginMatch = m[1]; },
      /** @type {ModeCallback} */
      'on:end': (m, resp) => { if (resp.data._beginMatch !== m[1]) resp.ignoreMatch(); }
    });
};

var MODES = /*#__PURE__*/Object.freeze({
  __proto__: null,
  APOS_STRING_MODE: APOS_STRING_MODE,
  BACKSLASH_ESCAPE: BACKSLASH_ESCAPE,
  BINARY_NUMBER_MODE: BINARY_NUMBER_MODE,
  BINARY_NUMBER_RE: BINARY_NUMBER_RE,
  COMMENT: COMMENT,
  C_BLOCK_COMMENT_MODE: C_BLOCK_COMMENT_MODE,
  C_LINE_COMMENT_MODE: C_LINE_COMMENT_MODE,
  C_NUMBER_MODE: C_NUMBER_MODE,
  C_NUMBER_RE: C_NUMBER_RE,
  END_SAME_AS_BEGIN: END_SAME_AS_BEGIN,
  HASH_COMMENT_MODE: HASH_COMMENT_MODE,
  IDENT_RE: IDENT_RE,
  MATCH_NOTHING_RE: MATCH_NOTHING_RE,
  METHOD_GUARD: METHOD_GUARD,
  NUMBER_MODE: NUMBER_MODE,
  NUMBER_RE: NUMBER_RE,
  PHRASAL_WORDS_MODE: PHRASAL_WORDS_MODE,
  QUOTE_STRING_MODE: QUOTE_STRING_MODE,
  REGEXP_MODE: REGEXP_MODE,
  RE_STARTERS_RE: RE_STARTERS_RE,
  SHEBANG: SHEBANG,
  TITLE_MODE: TITLE_MODE,
  UNDERSCORE_IDENT_RE: UNDERSCORE_IDENT_RE,
  UNDERSCORE_TITLE_MODE: UNDERSCORE_TITLE_MODE
});

/**
@typedef {import('highlight.js').CallbackResponse} CallbackResponse
@typedef {import('highlight.js').CompilerExt} CompilerExt
*/

// Grammar extensions / plugins
// See: https://github.com/highlightjs/highlight.js/issues/2833

// Grammar extensions allow "syntactic sugar" to be added to the grammar modes
// without requiring any underlying changes to the compiler internals.

// `compileMatch` being the perfect small example of now allowing a grammar
// author to write `match` when they desire to match a single expression rather
// than being forced to use `begin`.  The extension then just moves `match` into
// `begin` when it runs.  Ie, no features have been added, but we've just made
// the experience of writing (and reading grammars) a little bit nicer.

// ------

// TODO: We need negative look-behind support to do this properly
/**
 * Skip a match if it has a preceding dot
 *
 * This is used for `beginKeywords` to prevent matching expressions such as
 * `bob.keyword.do()`. The mode compiler automatically wires this up as a
 * special _internal_ 'on:begin' callback for modes with `beginKeywords`
 * @param {RegExpMatchArray} match
 * @param {CallbackResponse} response
 */
function skipIfHasPrecedingDot(match, response) {
  const before = match.input[match.index - 1];
  if (before === ".") {
    response.ignoreMatch();
  }
}

/**
 *
 * @type {CompilerExt}
 */
function scopeClassName(mode, _parent) {
  // eslint-disable-next-line no-undefined
  if (mode.className !== undefined) {
    mode.scope = mode.className;
    delete mode.className;
  }
}

/**
 * `beginKeywords` syntactic sugar
 * @type {CompilerExt}
 */
function beginKeywords(mode, parent) {
  if (!parent) return;
  if (!mode.beginKeywords) return;

  // for languages with keywords that include non-word characters checking for
  // a word boundary is not sufficient, so instead we check for a word boundary
  // or whitespace - this does no harm in any case since our keyword engine
  // doesn't allow spaces in keywords anyways and we still check for the boundary
  // first
  mode.begin = '\\b(' + mode.beginKeywords.split(' ').join('|') + ')(?!\\.)(?=\\b|\\s)';
  mode.__beforeBegin = skipIfHasPrecedingDot;
  mode.keywords = mode.keywords || mode.beginKeywords;
  delete mode.beginKeywords;

  // prevents double relevance, the keywords themselves provide
  // relevance, the mode doesn't need to double it
  // eslint-disable-next-line no-undefined
  if (mode.relevance === undefined) mode.relevance = 0;
}

/**
 * Allow `illegal` to contain an array of illegal values
 * @type {CompilerExt}
 */
function compileIllegal(mode, _parent) {
  if (!Array.isArray(mode.illegal)) return;

  mode.illegal = either(...mode.illegal);
}

/**
 * `match` to match a single expression for readability
 * @type {CompilerExt}
 */
function compileMatch(mode, _parent) {
  if (!mode.match) return;
  if (mode.begin || mode.end) throw new Error("begin & end are not supported with match");

  mode.begin = mode.match;
  delete mode.match;
}

/**
 * provides the default 1 relevance to all modes
 * @type {CompilerExt}
 */
function compileRelevance(mode, _parent) {
  // eslint-disable-next-line no-undefined
  if (mode.relevance === undefined) mode.relevance = 1;
}

// allow beforeMatch to act as a "qualifier" for the match
// the full match begin must be [beforeMatch][begin]
const beforeMatchExt = (mode, parent) => {
  if (!mode.beforeMatch) return;
  // starts conflicts with endsParent which we need to make sure the child
  // rule is not matched multiple times
  if (mode.starts) throw new Error("beforeMatch cannot be used with starts");

  const originalMode = Object.assign({}, mode);
  Object.keys(mode).forEach((key) => { delete mode[key]; });

  mode.keywords = originalMode.keywords;
  mode.begin = concat(originalMode.beforeMatch, lookahead(originalMode.begin));
  mode.starts = {
    relevance: 0,
    contains: [
      Object.assign(originalMode, { endsParent: true })
    ]
  };
  mode.relevance = 0;

  delete originalMode.beforeMatch;
};

// keywords that should have no default relevance value
const COMMON_KEYWORDS = [
  'of',
  'and',
  'for',
  'in',
  'not',
  'or',
  'if',
  'then',
  'parent', // common variable name
  'list', // common variable name
  'value' // common variable name
];

const DEFAULT_KEYWORD_SCOPE = "keyword";

/**
 * Given raw keywords from a language definition, compile them.
 *
 * @param {string | Record<string,string|string[]> | Array<string>} rawKeywords
 * @param {boolean} caseInsensitive
 */
function compileKeywords(rawKeywords, caseInsensitive, scopeName = DEFAULT_KEYWORD_SCOPE) {
  /** @type {import("highlight.js/private").KeywordDict} */
  const compiledKeywords = Object.create(null);

  // input can be a string of keywords, an array of keywords, or a object with
  // named keys representing scopeName (which can then point to a string or array)
  if (typeof rawKeywords === 'string') {
    compileList(scopeName, rawKeywords.split(" "));
  } else if (Array.isArray(rawKeywords)) {
    compileList(scopeName, rawKeywords);
  } else {
    Object.keys(rawKeywords).forEach(function(scopeName) {
      // collapse all our objects back into the parent object
      Object.assign(
        compiledKeywords,
        compileKeywords(rawKeywords[scopeName], caseInsensitive, scopeName)
      );
    });
  }
  return compiledKeywords;

  // ---

  /**
   * Compiles an individual list of keywords
   *
   * Ex: "for if when while|5"
   *
   * @param {string} scopeName
   * @param {Array<string>} keywordList
   */
  function compileList(scopeName, keywordList) {
    if (caseInsensitive) {
      keywordList = keywordList.map(x => x.toLowerCase());
    }
    keywordList.forEach(function(keyword) {
      const pair = keyword.split('|');
      compiledKeywords[pair[0]] = [scopeName, scoreForKeyword(pair[0], pair[1])];
    });
  }
}

/**
 * Returns the proper score for a given keyword
 *
 * Also takes into account comment keywords, which will be scored 0 UNLESS
 * another score has been manually assigned.
 * @param {string} keyword
 * @param {string} [providedScore]
 */
function scoreForKeyword(keyword, providedScore) {
  // manual scores always win over common keywords
  // so you can force a score of 1 if you really insist
  if (providedScore) {
    return Number(providedScore);
  }

  return commonKeyword(keyword) ? 0 : 1;
}

/**
 * Determines if a given keyword is common or not
 *
 * @param {string} keyword */
function commonKeyword(keyword) {
  return COMMON_KEYWORDS.includes(keyword.toLowerCase());
}

/*

For the reasoning behind this please see:
https://github.com/highlightjs/highlight.js/issues/2880#issuecomment-747275419

*/

/**
 * @type {Record<string, boolean>}
 */
const seenDeprecations = {};

/**
 * @param {string} message
 */
const error$1 = (message) => {
  console.error(message);
};

/**
 * @param {string} message
 * @param {any} args
 */
const warn = (message, ...args) => {
  console.log(`WARN: ${message}`, ...args);
};

/**
 * @param {string} version
 * @param {string} message
 */
const deprecated = (version, message) => {
  if (seenDeprecations[`${version}/${message}`]) return;

  console.log(`Deprecated as of ${version}. ${message}`);
  seenDeprecations[`${version}/${message}`] = true;
};

/* eslint-disable no-throw-literal */

/**
@typedef {import('highlight.js').CompiledMode} CompiledMode
*/

const MultiClassError = new Error();

/**
 * Renumbers labeled scope names to account for additional inner match
 * groups that otherwise would break everything.
 *
 * Lets say we 3 match scopes:
 *
 *   { 1 => ..., 2 => ..., 3 => ... }
 *
 * So what we need is a clean match like this:
 *
 *   (a)(b)(c) => [ "a", "b", "c" ]
 *
 * But this falls apart with inner match groups:
 *
 * (a)(((b)))(c) => ["a", "b", "b", "b", "c" ]
 *
 * Our scopes are now "out of alignment" and we're repeating `b` 3 times.
 * What needs to happen is the numbers are remapped:
 *
 *   { 1 => ..., 2 => ..., 5 => ... }
 *
 * We also need to know that the ONLY groups that should be output
 * are 1, 2, and 5.  This function handles this behavior.
 *
 * @param {CompiledMode} mode
 * @param {Array<RegExp | string>} regexes
 * @param {{key: "beginScope"|"endScope"}} opts
 */
function remapScopeNames(mode, regexes, { key }) {
  let offset = 0;
  const scopeNames = mode[key];
  /** @type Record<number,boolean> */
  const emit = {};
  /** @type Record<number,string> */
  const positions = {};

  for (let i = 1; i <= regexes.length; i++) {
    positions[i + offset] = scopeNames[i];
    emit[i + offset] = true;
    offset += countMatchGroups(regexes[i - 1]);
  }
  // we use _emit to keep track of which match groups are "top-level" to avoid double
  // output from inside match groups
  mode[key] = positions;
  mode[key]._emit = emit;
  mode[key]._multi = true;
}

/**
 * @param {CompiledMode} mode
 */
function beginMultiClass(mode) {
  if (!Array.isArray(mode.begin)) return;

  if (mode.skip || mode.excludeBegin || mode.returnBegin) {
    error$1("skip, excludeBegin, returnBegin not compatible with beginScope: {}");
    throw MultiClassError;
  }

  if (typeof mode.beginScope !== "object" || mode.beginScope === null) {
    error$1("beginScope must be object");
    throw MultiClassError;
  }

  remapScopeNames(mode, mode.begin, { key: "beginScope" });
  mode.begin = _rewriteBackreferences(mode.begin, { joinWith: "" });
}

/**
 * @param {CompiledMode} mode
 */
function endMultiClass(mode) {
  if (!Array.isArray(mode.end)) return;

  if (mode.skip || mode.excludeEnd || mode.returnEnd) {
    error$1("skip, excludeEnd, returnEnd not compatible with endScope: {}");
    throw MultiClassError;
  }

  if (typeof mode.endScope !== "object" || mode.endScope === null) {
    error$1("endScope must be object");
    throw MultiClassError;
  }

  remapScopeNames(mode, mode.end, { key: "endScope" });
  mode.end = _rewriteBackreferences(mode.end, { joinWith: "" });
}

/**
 * this exists only to allow `scope: {}` to be used beside `match:`
 * Otherwise `beginScope` would necessary and that would look weird

  {
    match: [ /def/, /\w+/ ]
    scope: { 1: "keyword" , 2: "title" }
  }

 * @param {CompiledMode} mode
 */
function scopeSugar(mode) {
  if (mode.scope && typeof mode.scope === "object" && mode.scope !== null) {
    mode.beginScope = mode.scope;
    delete mode.scope;
  }
}

/**
 * @param {CompiledMode} mode
 */
function MultiClass(mode) {
  scopeSugar(mode);

  if (typeof mode.beginScope === "string") {
    mode.beginScope = { _wrap: mode.beginScope };
  }
  if (typeof mode.endScope === "string") {
    mode.endScope = { _wrap: mode.endScope };
  }

  beginMultiClass(mode);
  endMultiClass(mode);
}

/**
@typedef {import('highlight.js').Mode} Mode
@typedef {import('highlight.js').CompiledMode} CompiledMode
@typedef {import('highlight.js').Language} Language
@typedef {import('highlight.js').HLJSPlugin} HLJSPlugin
@typedef {import('highlight.js').CompiledLanguage} CompiledLanguage
*/

// compilation

/**
 * Compiles a language definition result
 *
 * Given the raw result of a language definition (Language), compiles this so
 * that it is ready for highlighting code.
 * @param {Language} language
 * @returns {CompiledLanguage}
 */
function compileLanguage(language) {
  /**
   * Builds a regex with the case sensitivity of the current language
   *
   * @param {RegExp | string} value
   * @param {boolean} [global]
   */
  function langRe(value, global) {
    return new RegExp(
      source(value),
      'm'
      + (language.case_insensitive ? 'i' : '')
      + (language.unicodeRegex ? 'u' : '')
      + (global ? 'g' : '')
    );
  }

  /**
    Stores multiple regular expressions and allows you to quickly search for
    them all in a string simultaneously - returning the first match.  It does
    this by creating a huge (a|b|c) regex - each individual item wrapped with ()
    and joined by `|` - using match groups to track position.  When a match is
    found checking which position in the array has content allows us to figure
    out which of the original regexes / match groups triggered the match.

    The match object itself (the result of `Regex.exec`) is returned but also
    enhanced by merging in any meta-data that was registered with the regex.
    This is how we keep track of which mode matched, and what type of rule
    (`illegal`, `begin`, end, etc).
  */
  class MultiRegex {
    constructor() {
      this.matchIndexes = {};
      // @ts-ignore
      this.regexes = [];
      this.matchAt = 1;
      this.position = 0;
    }

    // @ts-ignore
    addRule(re, opts) {
      opts.position = this.position++;
      // @ts-ignore
      this.matchIndexes[this.matchAt] = opts;
      this.regexes.push([opts, re]);
      this.matchAt += countMatchGroups(re) + 1;
    }

    compile() {
      if (this.regexes.length === 0) {
        // avoids the need to check length every time exec is called
        // @ts-ignore
        this.exec = () => null;
      }
      const terminators = this.regexes.map(el => el[1]);
      this.matcherRe = langRe(_rewriteBackreferences(terminators, { joinWith: '|' }), true);
      this.lastIndex = 0;
    }

    /** @param {string} s */
    exec(s) {
      this.matcherRe.lastIndex = this.lastIndex;
      const match = this.matcherRe.exec(s);
      if (!match) { return null; }

      // eslint-disable-next-line no-undefined
      const i = match.findIndex((el, i) => i > 0 && el !== undefined);
      // @ts-ignore
      const matchData = this.matchIndexes[i];
      // trim off any earlier non-relevant match groups (ie, the other regex
      // match groups that make up the multi-matcher)
      match.splice(0, i);

      return Object.assign(match, matchData);
    }
  }

  /*
    Created to solve the key deficiently with MultiRegex - there is no way to
    test for multiple matches at a single location.  Why would we need to do
    that?  In the future a more dynamic engine will allow certain matches to be
    ignored.  An example: if we matched say the 3rd regex in a large group but
    decided to ignore it - we'd need to started testing again at the 4th
    regex... but MultiRegex itself gives us no real way to do that.

    So what this class creates MultiRegexs on the fly for whatever search
    position they are needed.

    NOTE: These additional MultiRegex objects are created dynamically.  For most
    grammars most of the time we will never actually need anything more than the
    first MultiRegex - so this shouldn't have too much overhead.

    Say this is our search group, and we match regex3, but wish to ignore it.

      regex1 | regex2 | regex3 | regex4 | regex5    ' ie, startAt = 0

    What we need is a new MultiRegex that only includes the remaining
    possibilities:

      regex4 | regex5                               ' ie, startAt = 3

    This class wraps all that complexity up in a simple API... `startAt` decides
    where in the array of expressions to start doing the matching. It
    auto-increments, so if a match is found at position 2, then startAt will be
    set to 3.  If the end is reached startAt will return to 0.

    MOST of the time the parser will be setting startAt manually to 0.
  */
  class ResumableMultiRegex {
    constructor() {
      // @ts-ignore
      this.rules = [];
      // @ts-ignore
      this.multiRegexes = [];
      this.count = 0;

      this.lastIndex = 0;
      this.regexIndex = 0;
    }

    // @ts-ignore
    getMatcher(index) {
      if (this.multiRegexes[index]) return this.multiRegexes[index];

      const matcher = new MultiRegex();
      this.rules.slice(index).forEach(([re, opts]) => matcher.addRule(re, opts));
      matcher.compile();
      this.multiRegexes[index] = matcher;
      return matcher;
    }

    resumingScanAtSamePosition() {
      return this.regexIndex !== 0;
    }

    considerAll() {
      this.regexIndex = 0;
    }

    // @ts-ignore
    addRule(re, opts) {
      this.rules.push([re, opts]);
      if (opts.type === "begin") this.count++;
    }

    /** @param {string} s */
    exec(s) {
      const m = this.getMatcher(this.regexIndex);
      m.lastIndex = this.lastIndex;
      let result = m.exec(s);

      // The following is because we have no easy way to say "resume scanning at the
      // existing position but also skip the current rule ONLY". What happens is
      // all prior rules are also skipped which can result in matching the wrong
      // thing. Example of matching "booger":

      // our matcher is [string, "booger", number]
      //
      // ....booger....

      // if "booger" is ignored then we'd really need a regex to scan from the
      // SAME position for only: [string, number] but ignoring "booger" (if it
      // was the first match), a simple resume would scan ahead who knows how
      // far looking only for "number", ignoring potential string matches (or
      // future "booger" matches that might be valid.)

      // So what we do: We execute two matchers, one resuming at the same
      // position, but the second full matcher starting at the position after:

      //     /--- resume first regex match here (for [number])
      //     |/---- full match here for [string, "booger", number]
      //     vv
      // ....booger....

      // Which ever results in a match first is then used. So this 3-4 step
      // process essentially allows us to say "match at this position, excluding
      // a prior rule that was ignored".
      //
      // 1. Match "booger" first, ignore. Also proves that [string] does non match.
      // 2. Resume matching for [number]
      // 3. Match at index + 1 for [string, "booger", number]
      // 4. If #2 and #3 result in matches, which came first?
      if (this.resumingScanAtSamePosition()) {
        if (result && result.index === this.lastIndex) ; else { // use the second matcher result
          const m2 = this.getMatcher(0);
          m2.lastIndex = this.lastIndex + 1;
          result = m2.exec(s);
        }
      }

      if (result) {
        this.regexIndex += result.position + 1;
        if (this.regexIndex === this.count) {
          // wrap-around to considering all matches again
          this.considerAll();
        }
      }

      return result;
    }
  }

  /**
   * Given a mode, builds a huge ResumableMultiRegex that can be used to walk
   * the content and find matches.
   *
   * @param {CompiledMode} mode
   * @returns {ResumableMultiRegex}
   */
  function buildModeRegex(mode) {
    const mm = new ResumableMultiRegex();

    mode.contains.forEach(term => mm.addRule(term.begin, { rule: term, type: "begin" }));

    if (mode.terminatorEnd) {
      mm.addRule(mode.terminatorEnd, { type: "end" });
    }
    if (mode.illegal) {
      mm.addRule(mode.illegal, { type: "illegal" });
    }

    return mm;
  }

  /** skip vs abort vs ignore
   *
   * @skip   - The mode is still entered and exited normally (and contains rules apply),
   *           but all content is held and added to the parent buffer rather than being
   *           output when the mode ends.  Mostly used with `sublanguage` to build up
   *           a single large buffer than can be parsed by sublanguage.
   *
   *             - The mode begin ands ends normally.
   *             - Content matched is added to the parent mode buffer.
   *             - The parser cursor is moved forward normally.
   *
   * @abort  - A hack placeholder until we have ignore.  Aborts the mode (as if it
   *           never matched) but DOES NOT continue to match subsequent `contains`
   *           modes.  Abort is bad/suboptimal because it can result in modes
   *           farther down not getting applied because an earlier rule eats the
   *           content but then aborts.
   *
   *             - The mode does not begin.
   *             - Content matched by `begin` is added to the mode buffer.
   *             - The parser cursor is moved forward accordingly.
   *
   * @ignore - Ignores the mode (as if it never matched) and continues to match any
   *           subsequent `contains` modes.  Ignore isn't technically possible with
   *           the current parser implementation.
   *
   *             - The mode does not begin.
   *             - Content matched by `begin` is ignored.
   *             - The parser cursor is not moved forward.
   */

  /**
   * Compiles an individual mode
   *
   * This can raise an error if the mode contains certain detectable known logic
   * issues.
   * @param {Mode} mode
   * @param {CompiledMode | null} [parent]
   * @returns {CompiledMode | never}
   */
  function compileMode(mode, parent) {
    const cmode = /** @type CompiledMode */ (mode);
    if (mode.isCompiled) return cmode;

    [
      scopeClassName,
      // do this early so compiler extensions generally don't have to worry about
      // the distinction between match/begin
      compileMatch,
      MultiClass,
      beforeMatchExt
    ].forEach(ext => ext(mode, parent));

    language.compilerExtensions.forEach(ext => ext(mode, parent));

    // __beforeBegin is considered private API, internal use only
    mode.__beforeBegin = null;

    [
      beginKeywords,
      // do this later so compiler extensions that come earlier have access to the
      // raw array if they wanted to perhaps manipulate it, etc.
      compileIllegal,
      // default to 1 relevance if not specified
      compileRelevance
    ].forEach(ext => ext(mode, parent));

    mode.isCompiled = true;

    let keywordPattern = null;
    if (typeof mode.keywords === "object" && mode.keywords.$pattern) {
      // we need a copy because keywords might be compiled multiple times
      // so we can't go deleting $pattern from the original on the first
      // pass
      mode.keywords = Object.assign({}, mode.keywords);
      keywordPattern = mode.keywords.$pattern;
      delete mode.keywords.$pattern;
    }
    keywordPattern = keywordPattern || /\w+/;

    if (mode.keywords) {
      mode.keywords = compileKeywords(mode.keywords, language.case_insensitive);
    }

    cmode.keywordPatternRe = langRe(keywordPattern, true);

    if (parent) {
      if (!mode.begin) mode.begin = /\B|\b/;
      cmode.beginRe = langRe(cmode.begin);
      if (!mode.end && !mode.endsWithParent) mode.end = /\B|\b/;
      if (mode.end) cmode.endRe = langRe(cmode.end);
      cmode.terminatorEnd = source(cmode.end) || '';
      if (mode.endsWithParent && parent.terminatorEnd) {
        cmode.terminatorEnd += (mode.end ? '|' : '') + parent.terminatorEnd;
      }
    }
    if (mode.illegal) cmode.illegalRe = langRe(/** @type {RegExp | string} */ (mode.illegal));
    if (!mode.contains) mode.contains = [];

    mode.contains = [].concat(...mode.contains.map(function(c) {
      return expandOrCloneMode(c === 'self' ? mode : c);
    }));
    mode.contains.forEach(function(c) { compileMode(/** @type Mode */ (c), cmode); });

    if (mode.starts) {
      compileMode(mode.starts, parent);
    }

    cmode.matcher = buildModeRegex(cmode);
    return cmode;
  }

  if (!language.compilerExtensions) language.compilerExtensions = [];

  // self is not valid at the top-level
  if (language.contains && language.contains.includes('self')) {
    throw new Error("ERR: contains `self` is not supported at the top-level of a language.  See documentation.");
  }

  // we need a null object, which inherit will guarantee
  language.classNameAliases = inherit$1(language.classNameAliases || {});

  return compileMode(/** @type Mode */ (language));
}

/**
 * Determines if a mode has a dependency on it's parent or not
 *
 * If a mode does have a parent dependency then often we need to clone it if
 * it's used in multiple places so that each copy points to the correct parent,
 * where-as modes without a parent can often safely be re-used at the bottom of
 * a mode chain.
 *
 * @param {Mode | null} mode
 * @returns {boolean} - is there a dependency on the parent?
 * */
function dependencyOnParent(mode) {
  if (!mode) return false;

  return mode.endsWithParent || dependencyOnParent(mode.starts);
}

/**
 * Expands a mode or clones it if necessary
 *
 * This is necessary for modes with parental dependenceis (see notes on
 * `dependencyOnParent`) and for nodes that have `variants` - which must then be
 * exploded into their own individual modes at compile time.
 *
 * @param {Mode} mode
 * @returns {Mode | Mode[]}
 * */
function expandOrCloneMode(mode) {
  if (mode.variants && !mode.cachedVariants) {
    mode.cachedVariants = mode.variants.map(function(variant) {
      return inherit$1(mode, { variants: null }, variant);
    });
  }

  // EXPAND
  // if we have variants then essentially "replace" the mode with the variants
  // this happens in compileMode, where this function is called from
  if (mode.cachedVariants) {
    return mode.cachedVariants;
  }

  // CLONE
  // if we have dependencies on parents then we need a unique
  // instance of ourselves, so we can be reused with many
  // different parents without issue
  if (dependencyOnParent(mode)) {
    return inherit$1(mode, { starts: mode.starts ? inherit$1(mode.starts) : null });
  }

  if (Object.isFrozen(mode)) {
    return inherit$1(mode);
  }

  // no special dependency issues, just return ourselves
  return mode;
}

var version = "11.9.0";

class HTMLInjectionError extends Error {
  constructor(reason, html) {
    super(reason);
    this.name = "HTMLInjectionError";
    this.html = html;
  }
}

/*
Syntax highlighting with language autodetection.
https://highlightjs.org/
*/



/**
@typedef {import('highlight.js').Mode} Mode
@typedef {import('highlight.js').CompiledMode} CompiledMode
@typedef {import('highlight.js').CompiledScope} CompiledScope
@typedef {import('highlight.js').Language} Language
@typedef {import('highlight.js').HLJSApi} HLJSApi
@typedef {import('highlight.js').HLJSPlugin} HLJSPlugin
@typedef {import('highlight.js').PluginEvent} PluginEvent
@typedef {import('highlight.js').HLJSOptions} HLJSOptions
@typedef {import('highlight.js').LanguageFn} LanguageFn
@typedef {import('highlight.js').HighlightedHTMLElement} HighlightedHTMLElement
@typedef {import('highlight.js').BeforeHighlightContext} BeforeHighlightContext
@typedef {import('highlight.js/private').MatchType} MatchType
@typedef {import('highlight.js/private').KeywordData} KeywordData
@typedef {import('highlight.js/private').EnhancedMatch} EnhancedMatch
@typedef {import('highlight.js/private').AnnotatedError} AnnotatedError
@typedef {import('highlight.js').AutoHighlightResult} AutoHighlightResult
@typedef {import('highlight.js').HighlightOptions} HighlightOptions
@typedef {import('highlight.js').HighlightResult} HighlightResult
*/


const escape$2 = escapeHTML;
const inherit = inherit$1;
const NO_MATCH = Symbol("nomatch");
const MAX_KEYWORD_HITS = 7;

/**
 * @param {any} hljs - object that is extended (legacy)
 * @returns {HLJSApi}
 */
const HLJS = function(hljs) {
  // Global internal variables used within the highlight.js library.
  /** @type {Record<string, Language>} */
  const languages = Object.create(null);
  /** @type {Record<string, string>} */
  const aliases = Object.create(null);
  /** @type {HLJSPlugin[]} */
  const plugins = [];

  // safe/production mode - swallows more errors, tries to keep running
  // even if a single syntax or parse hits a fatal error
  let SAFE_MODE = true;
  const LANGUAGE_NOT_FOUND = "Could not find the language '{}', did you forget to load/include a language module?";
  /** @type {Language} */
  const PLAINTEXT_LANGUAGE = { disableAutodetect: true, name: 'Plain text', contains: [] };

  // Global options used when within external APIs. This is modified when
  // calling the `hljs.configure` function.
  /** @type HLJSOptions */
  let options = {
    ignoreUnescapedHTML: false,
    throwUnescapedHTML: false,
    noHighlightRe: /^(no-?highlight)$/i,
    languageDetectRe: /\blang(?:uage)?-([\w-]+)\b/i,
    classPrefix: 'hljs-',
    cssSelector: 'pre code',
    languages: null,
    // beta configuration options, subject to change, welcome to discuss
    // https://github.com/highlightjs/highlight.js/issues/1086
    __emitter: TokenTreeEmitter
  };

  /* Utility functions */

  /**
   * Tests a language name to see if highlighting should be skipped
   * @param {string} languageName
   */
  function shouldNotHighlight(languageName) {
    return options.noHighlightRe.test(languageName);
  }

  /**
   * @param {HighlightedHTMLElement} block - the HTML element to determine language for
   */
  function blockLanguage(block) {
    let classes = block.className + ' ';

    classes += block.parentNode ? block.parentNode.className : '';

    // language-* takes precedence over non-prefixed class names.
    const match = options.languageDetectRe.exec(classes);
    if (match) {
      const language = getLanguage(match[1]);
      if (!language) {
        warn(LANGUAGE_NOT_FOUND.replace("{}", match[1]));
        warn("Falling back to no-highlight mode for this block.", block);
      }
      return language ? match[1] : 'no-highlight';
    }

    return classes
      .split(/\s+/)
      .find((_class) => shouldNotHighlight(_class) || getLanguage(_class));
  }

  /**
   * Core highlighting function.
   *
   * OLD API
   * highlight(lang, code, ignoreIllegals, continuation)
   *
   * NEW API
   * highlight(code, {lang, ignoreIllegals})
   *
   * @param {string} codeOrLanguageName - the language to use for highlighting
   * @param {string | HighlightOptions} optionsOrCode - the code to highlight
   * @param {boolean} [ignoreIllegals] - whether to ignore illegal matches, default is to bail
   *
   * @returns {HighlightResult} Result - an object that represents the result
   * @property {string} language - the language name
   * @property {number} relevance - the relevance score
   * @property {string} value - the highlighted HTML code
   * @property {string} code - the original raw code
   * @property {CompiledMode} top - top of the current mode stack
   * @property {boolean} illegal - indicates whether any illegal matches were found
  */
  function highlight(codeOrLanguageName, optionsOrCode, ignoreIllegals) {
    let code = "";
    let languageName = "";
    if (typeof optionsOrCode === "object") {
      code = codeOrLanguageName;
      ignoreIllegals = optionsOrCode.ignoreIllegals;
      languageName = optionsOrCode.language;
    } else {
      // old API
      deprecated("10.7.0", "highlight(lang, code, ...args) has been deprecated.");
      deprecated("10.7.0", "Please use highlight(code, options) instead.\nhttps://github.com/highlightjs/highlight.js/issues/2277");
      languageName = codeOrLanguageName;
      code = optionsOrCode;
    }

    // https://github.com/highlightjs/highlight.js/issues/3149
    // eslint-disable-next-line no-undefined
    if (ignoreIllegals === undefined) { ignoreIllegals = true; }

    /** @type {BeforeHighlightContext} */
    const context = {
      code,
      language: languageName
    };
    // the plugin can change the desired language or the code to be highlighted
    // just be changing the object it was passed
    fire("before:highlight", context);

    // a before plugin can usurp the result completely by providing it's own
    // in which case we don't even need to call highlight
    const result = context.result
      ? context.result
      : _highlight(context.language, context.code, ignoreIllegals);

    result.code = context.code;
    // the plugin can change anything in result to suite it
    fire("after:highlight", result);

    return result;
  }

  /**
   * private highlight that's used internally and does not fire callbacks
   *
   * @param {string} languageName - the language to use for highlighting
   * @param {string} codeToHighlight - the code to highlight
   * @param {boolean?} [ignoreIllegals] - whether to ignore illegal matches, default is to bail
   * @param {CompiledMode?} [continuation] - current continuation mode, if any
   * @returns {HighlightResult} - result of the highlight operation
  */
  function _highlight(languageName, codeToHighlight, ignoreIllegals, continuation) {
    const keywordHits = Object.create(null);

    /**
     * Return keyword data if a match is a keyword
     * @param {CompiledMode} mode - current mode
     * @param {string} matchText - the textual match
     * @returns {KeywordData | false}
     */
    function keywordData(mode, matchText) {
      return mode.keywords[matchText];
    }

    function processKeywords() {
      if (!top.keywords) {
        emitter.addText(modeBuffer);
        return;
      }

      let lastIndex = 0;
      top.keywordPatternRe.lastIndex = 0;
      let match = top.keywordPatternRe.exec(modeBuffer);
      let buf = "";

      while (match) {
        buf += modeBuffer.substring(lastIndex, match.index);
        const word = language.case_insensitive ? match[0].toLowerCase() : match[0];
        const data = keywordData(top, word);
        if (data) {
          const [kind, keywordRelevance] = data;
          emitter.addText(buf);
          buf = "";

          keywordHits[word] = (keywordHits[word] || 0) + 1;
          if (keywordHits[word] <= MAX_KEYWORD_HITS) relevance += keywordRelevance;
          if (kind.startsWith("_")) {
            // _ implied for relevance only, do not highlight
            // by applying a class name
            buf += match[0];
          } else {
            const cssClass = language.classNameAliases[kind] || kind;
            emitKeyword(match[0], cssClass);
          }
        } else {
          buf += match[0];
        }
        lastIndex = top.keywordPatternRe.lastIndex;
        match = top.keywordPatternRe.exec(modeBuffer);
      }
      buf += modeBuffer.substring(lastIndex);
      emitter.addText(buf);
    }

    function processSubLanguage() {
      if (modeBuffer === "") return;
      /** @type HighlightResult */
      let result = null;

      if (typeof top.subLanguage === 'string') {
        if (!languages[top.subLanguage]) {
          emitter.addText(modeBuffer);
          return;
        }
        result = _highlight(top.subLanguage, modeBuffer, true, continuations[top.subLanguage]);
        continuations[top.subLanguage] = /** @type {CompiledMode} */ (result._top);
      } else {
        result = highlightAuto(modeBuffer, top.subLanguage.length ? top.subLanguage : null);
      }

      // Counting embedded language score towards the host language may be disabled
      // with zeroing the containing mode relevance. Use case in point is Markdown that
      // allows XML everywhere and makes every XML snippet to have a much larger Markdown
      // score.
      if (top.relevance > 0) {
        relevance += result.relevance;
      }
      emitter.__addSublanguage(result._emitter, result.language);
    }

    function processBuffer() {
      if (top.subLanguage != null) {
        processSubLanguage();
      } else {
        processKeywords();
      }
      modeBuffer = '';
    }

    /**
     * @param {string} text
     * @param {string} scope
     */
    function emitKeyword(keyword, scope) {
      if (keyword === "") return;

      emitter.startScope(scope);
      emitter.addText(keyword);
      emitter.endScope();
    }

    /**
     * @param {CompiledScope} scope
     * @param {RegExpMatchArray} match
     */
    function emitMultiClass(scope, match) {
      let i = 1;
      const max = match.length - 1;
      while (i <= max) {
        if (!scope._emit[i]) { i++; continue; }
        const klass = language.classNameAliases[scope[i]] || scope[i];
        const text = match[i];
        if (klass) {
          emitKeyword(text, klass);
        } else {
          modeBuffer = text;
          processKeywords();
          modeBuffer = "";
        }
        i++;
      }
    }

    /**
     * @param {CompiledMode} mode - new mode to start
     * @param {RegExpMatchArray} match
     */
    function startNewMode(mode, match) {
      if (mode.scope && typeof mode.scope === "string") {
        emitter.openNode(language.classNameAliases[mode.scope] || mode.scope);
      }
      if (mode.beginScope) {
        // beginScope just wraps the begin match itself in a scope
        if (mode.beginScope._wrap) {
          emitKeyword(modeBuffer, language.classNameAliases[mode.beginScope._wrap] || mode.beginScope._wrap);
          modeBuffer = "";
        } else if (mode.beginScope._multi) {
          // at this point modeBuffer should just be the match
          emitMultiClass(mode.beginScope, match);
          modeBuffer = "";
        }
      }

      top = Object.create(mode, { parent: { value: top } });
      return top;
    }

    /**
     * @param {CompiledMode } mode - the mode to potentially end
     * @param {RegExpMatchArray} match - the latest match
     * @param {string} matchPlusRemainder - match plus remainder of content
     * @returns {CompiledMode | void} - the next mode, or if void continue on in current mode
     */
    function endOfMode(mode, match, matchPlusRemainder) {
      let matched = startsWith(mode.endRe, matchPlusRemainder);

      if (matched) {
        if (mode["on:end"]) {
          const resp = new Response(mode);
          mode["on:end"](match, resp);
          if (resp.isMatchIgnored) matched = false;
        }

        if (matched) {
          while (mode.endsParent && mode.parent) {
            mode = mode.parent;
          }
          return mode;
        }
      }
      // even if on:end fires an `ignore` it's still possible
      // that we might trigger the end node because of a parent mode
      if (mode.endsWithParent) {
        return endOfMode(mode.parent, match, matchPlusRemainder);
      }
    }

    /**
     * Handle matching but then ignoring a sequence of text
     *
     * @param {string} lexeme - string containing full match text
     */
    function doIgnore(lexeme) {
      if (top.matcher.regexIndex === 0) {
        // no more regexes to potentially match here, so we move the cursor forward one
        // space
        modeBuffer += lexeme[0];
        return 1;
      } else {
        // no need to move the cursor, we still have additional regexes to try and
        // match at this very spot
        resumeScanAtSamePosition = true;
        return 0;
      }
    }

    /**
     * Handle the start of a new potential mode match
     *
     * @param {EnhancedMatch} match - the current match
     * @returns {number} how far to advance the parse cursor
     */
    function doBeginMatch(match) {
      const lexeme = match[0];
      const newMode = match.rule;

      const resp = new Response(newMode);
      // first internal before callbacks, then the public ones
      const beforeCallbacks = [newMode.__beforeBegin, newMode["on:begin"]];
      for (const cb of beforeCallbacks) {
        if (!cb) continue;
        cb(match, resp);
        if (resp.isMatchIgnored) return doIgnore(lexeme);
      }

      if (newMode.skip) {
        modeBuffer += lexeme;
      } else {
        if (newMode.excludeBegin) {
          modeBuffer += lexeme;
        }
        processBuffer();
        if (!newMode.returnBegin && !newMode.excludeBegin) {
          modeBuffer = lexeme;
        }
      }
      startNewMode(newMode, match);
      return newMode.returnBegin ? 0 : lexeme.length;
    }

    /**
     * Handle the potential end of mode
     *
     * @param {RegExpMatchArray} match - the current match
     */
    function doEndMatch(match) {
      const lexeme = match[0];
      const matchPlusRemainder = codeToHighlight.substring(match.index);

      const endMode = endOfMode(top, match, matchPlusRemainder);
      if (!endMode) { return NO_MATCH; }

      const origin = top;
      if (top.endScope && top.endScope._wrap) {
        processBuffer();
        emitKeyword(lexeme, top.endScope._wrap);
      } else if (top.endScope && top.endScope._multi) {
        processBuffer();
        emitMultiClass(top.endScope, match);
      } else if (origin.skip) {
        modeBuffer += lexeme;
      } else {
        if (!(origin.returnEnd || origin.excludeEnd)) {
          modeBuffer += lexeme;
        }
        processBuffer();
        if (origin.excludeEnd) {
          modeBuffer = lexeme;
        }
      }
      do {
        if (top.scope) {
          emitter.closeNode();
        }
        if (!top.skip && !top.subLanguage) {
          relevance += top.relevance;
        }
        top = top.parent;
      } while (top !== endMode.parent);
      if (endMode.starts) {
        startNewMode(endMode.starts, match);
      }
      return origin.returnEnd ? 0 : lexeme.length;
    }

    function processContinuations() {
      const list = [];
      for (let current = top; current !== language; current = current.parent) {
        if (current.scope) {
          list.unshift(current.scope);
        }
      }
      list.forEach(item => emitter.openNode(item));
    }

    /** @type {{type?: MatchType, index?: number, rule?: Mode}}} */
    let lastMatch = {};

    /**
     *  Process an individual match
     *
     * @param {string} textBeforeMatch - text preceding the match (since the last match)
     * @param {EnhancedMatch} [match] - the match itself
     */
    function processLexeme(textBeforeMatch, match) {
      const lexeme = match && match[0];

      // add non-matched text to the current mode buffer
      modeBuffer += textBeforeMatch;

      if (lexeme == null) {
        processBuffer();
        return 0;
      }

      // we've found a 0 width match and we're stuck, so we need to advance
      // this happens when we have badly behaved rules that have optional matchers to the degree that
      // sometimes they can end up matching nothing at all
      // Ref: https://github.com/highlightjs/highlight.js/issues/2140
      if (lastMatch.type === "begin" && match.type === "end" && lastMatch.index === match.index && lexeme === "") {
        // spit the "skipped" character that our regex choked on back into the output sequence
        modeBuffer += codeToHighlight.slice(match.index, match.index + 1);
        if (!SAFE_MODE) {
          /** @type {AnnotatedError} */
          const err = new Error(`0 width match regex (${languageName})`);
          err.languageName = languageName;
          err.badRule = lastMatch.rule;
          throw err;
        }
        return 1;
      }
      lastMatch = match;

      if (match.type === "begin") {
        return doBeginMatch(match);
      } else if (match.type === "illegal" && !ignoreIllegals) {
        // illegal match, we do not continue processing
        /** @type {AnnotatedError} */
        const err = new Error('Illegal lexeme "' + lexeme + '" for mode "' + (top.scope || '<unnamed>') + '"');
        err.mode = top;
        throw err;
      } else if (match.type === "end") {
        const processed = doEndMatch(match);
        if (processed !== NO_MATCH) {
          return processed;
        }
      }

      // edge case for when illegal matches $ (end of line) which is technically
      // a 0 width match but not a begin/end match so it's not caught by the
      // first handler (when ignoreIllegals is true)
      if (match.type === "illegal" && lexeme === "") {
        // advance so we aren't stuck in an infinite loop
        return 1;
      }

      // infinite loops are BAD, this is a last ditch catch all. if we have a
      // decent number of iterations yet our index (cursor position in our
      // parsing) still 3x behind our index then something is very wrong
      // so we bail
      if (iterations > 100000 && iterations > match.index * 3) {
        const err = new Error('potential infinite loop, way more iterations than matches');
        throw err;
      }

      /*
      Why might be find ourselves here?  An potential end match that was
      triggered but could not be completed.  IE, `doEndMatch` returned NO_MATCH.
      (this could be because a callback requests the match be ignored, etc)

      This causes no real harm other than stopping a few times too many.
      */

      modeBuffer += lexeme;
      return lexeme.length;
    }

    const language = getLanguage(languageName);
    if (!language) {
      error$1(LANGUAGE_NOT_FOUND.replace("{}", languageName));
      throw new Error('Unknown language: "' + languageName + '"');
    }

    const md = compileLanguage(language);
    let result = '';
    /** @type {CompiledMode} */
    let top = continuation || md;
    /** @type Record<string,CompiledMode> */
    const continuations = {}; // keep continuations for sub-languages
    const emitter = new options.__emitter(options);
    processContinuations();
    let modeBuffer = '';
    let relevance = 0;
    let index = 0;
    let iterations = 0;
    let resumeScanAtSamePosition = false;

    try {
      if (!language.__emitTokens) {
        top.matcher.considerAll();

        for (;;) {
          iterations++;
          if (resumeScanAtSamePosition) {
            // only regexes not matched previously will now be
            // considered for a potential match
            resumeScanAtSamePosition = false;
          } else {
            top.matcher.considerAll();
          }
          top.matcher.lastIndex = index;

          const match = top.matcher.exec(codeToHighlight);
          // console.log("match", match[0], match.rule && match.rule.begin)

          if (!match) break;

          const beforeMatch = codeToHighlight.substring(index, match.index);
          const processedCount = processLexeme(beforeMatch, match);
          index = match.index + processedCount;
        }
        processLexeme(codeToHighlight.substring(index));
      } else {
        language.__emitTokens(codeToHighlight, emitter);
      }

      emitter.finalize();
      result = emitter.toHTML();

      return {
        language: languageName,
        value: result,
        relevance,
        illegal: false,
        _emitter: emitter,
        _top: top
      };
    } catch (err) {
      if (err.message && err.message.includes('Illegal')) {
        return {
          language: languageName,
          value: escape$2(codeToHighlight),
          illegal: true,
          relevance: 0,
          _illegalBy: {
            message: err.message,
            index,
            context: codeToHighlight.slice(index - 100, index + 100),
            mode: err.mode,
            resultSoFar: result
          },
          _emitter: emitter
        };
      } else if (SAFE_MODE) {
        return {
          language: languageName,
          value: escape$2(codeToHighlight),
          illegal: false,
          relevance: 0,
          errorRaised: err,
          _emitter: emitter,
          _top: top
        };
      } else {
        throw err;
      }
    }
  }

  /**
   * returns a valid highlight result, without actually doing any actual work,
   * auto highlight starts with this and it's possible for small snippets that
   * auto-detection may not find a better match
   * @param {string} code
   * @returns {HighlightResult}
   */
  function justTextHighlightResult(code) {
    const result = {
      value: escape$2(code),
      illegal: false,
      relevance: 0,
      _top: PLAINTEXT_LANGUAGE,
      _emitter: new options.__emitter(options)
    };
    result._emitter.addText(code);
    return result;
  }

  /**
  Highlighting with language detection. Accepts a string with the code to
  highlight. Returns an object with the following properties:

  - language (detected language)
  - relevance (int)
  - value (an HTML string with highlighting markup)
  - secondBest (object with the same structure for second-best heuristically
    detected language, may be absent)

    @param {string} code
    @param {Array<string>} [languageSubset]
    @returns {AutoHighlightResult}
  */
  function highlightAuto(code, languageSubset) {
    languageSubset = languageSubset || options.languages || Object.keys(languages);
    const plaintext = justTextHighlightResult(code);

    const results = languageSubset.filter(getLanguage).filter(autoDetection).map(name =>
      _highlight(name, code, false)
    );
    results.unshift(plaintext); // plaintext is always an option

    const sorted = results.sort((a, b) => {
      // sort base on relevance
      if (a.relevance !== b.relevance) return b.relevance - a.relevance;

      // always award the tie to the base language
      // ie if C++ and Arduino are tied, it's more likely to be C++
      if (a.language && b.language) {
        if (getLanguage(a.language).supersetOf === b.language) {
          return 1;
        } else if (getLanguage(b.language).supersetOf === a.language) {
          return -1;
        }
      }

      // otherwise say they are equal, which has the effect of sorting on
      // relevance while preserving the original ordering - which is how ties
      // have historically been settled, ie the language that comes first always
      // wins in the case of a tie
      return 0;
    });

    const [best, secondBest] = sorted;

    /** @type {AutoHighlightResult} */
    const result = best;
    result.secondBest = secondBest;

    return result;
  }

  /**
   * Builds new class name for block given the language name
   *
   * @param {HTMLElement} element
   * @param {string} [currentLang]
   * @param {string} [resultLang]
   */
  function updateClassName(element, currentLang, resultLang) {
    const language = (currentLang && aliases[currentLang]) || resultLang;

    element.classList.add("hljs");
    element.classList.add(`language-${language}`);
  }

  /**
   * Applies highlighting to a DOM node containing code.
   *
   * @param {HighlightedHTMLElement} element - the HTML element to highlight
  */
  function highlightElement(element) {
    /** @type HTMLElement */
    let node = null;
    const language = blockLanguage(element);

    if (shouldNotHighlight(language)) return;

    fire("before:highlightElement",
      { el: element, language });

    if (element.dataset.highlighted) {
      console.log("Element previously highlighted. To highlight again, first unset `dataset.highlighted`.", element);
      return;
    }

    // we should be all text, no child nodes (unescaped HTML) - this is possibly
    // an HTML injection attack - it's likely too late if this is already in
    // production (the code has likely already done its damage by the time
    // we're seeing it)... but we yell loudly about this so that hopefully it's
    // more likely to be caught in development before making it to production
    if (element.children.length > 0) {
      if (!options.ignoreUnescapedHTML) {
        console.warn("One of your code blocks includes unescaped HTML. This is a potentially serious security risk.");
        console.warn("https://github.com/highlightjs/highlight.js/wiki/security");
        console.warn("The element with unescaped HTML:");
        console.warn(element);
      }
      if (options.throwUnescapedHTML) {
        const err = new HTMLInjectionError(
          "One of your code blocks includes unescaped HTML.",
          element.innerHTML
        );
        throw err;
      }
    }

    node = element;
    const text = node.textContent;
    const result = language ? highlight(text, { language, ignoreIllegals: true }) : highlightAuto(text);

    element.innerHTML = result.value;
    element.dataset.highlighted = "yes";
    updateClassName(element, language, result.language);
    element.result = {
      language: result.language,
      // TODO: remove with version 11.0
      re: result.relevance,
      relevance: result.relevance
    };
    if (result.secondBest) {
      element.secondBest = {
        language: result.secondBest.language,
        relevance: result.secondBest.relevance
      };
    }

    fire("after:highlightElement", { el: element, result, text });
  }

  /**
   * Updates highlight.js global options with the passed options
   *
   * @param {Partial<HLJSOptions>} userOptions
   */
  function configure(userOptions) {
    options = inherit(options, userOptions);
  }

  // TODO: remove v12, deprecated
  const initHighlighting = () => {
    highlightAll();
    deprecated("10.6.0", "initHighlighting() deprecated.  Use highlightAll() now.");
  };

  // TODO: remove v12, deprecated
  function initHighlightingOnLoad() {
    highlightAll();
    deprecated("10.6.0", "initHighlightingOnLoad() deprecated.  Use highlightAll() now.");
  }

  let wantsHighlight = false;

  /**
   * auto-highlights all pre>code elements on the page
   */
  function highlightAll() {
    // if we are called too early in the loading process
    if (document.readyState === "loading") {
      wantsHighlight = true;
      return;
    }

    const blocks = document.querySelectorAll(options.cssSelector);
    blocks.forEach(highlightElement);
  }

  function boot() {
    // if a highlight was requested before DOM was loaded, do now
    if (wantsHighlight) highlightAll();
  }

  // make sure we are in the browser environment
  if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('DOMContentLoaded', boot, false);
  }

  /**
   * Register a language grammar module
   *
   * @param {string} languageName
   * @param {LanguageFn} languageDefinition
   */
  function registerLanguage(languageName, languageDefinition) {
    let lang = null;
    try {
      lang = languageDefinition(hljs);
    } catch (error$1$1) {
      error$1("Language definition for '{}' could not be registered.".replace("{}", languageName));
      // hard or soft error
      if (!SAFE_MODE) { throw error$1$1; } else { error$1(error$1$1); }
      // languages that have serious errors are replaced with essentially a
      // "plaintext" stand-in so that the code blocks will still get normal
      // css classes applied to them - and one bad language won't break the
      // entire highlighter
      lang = PLAINTEXT_LANGUAGE;
    }
    // give it a temporary name if it doesn't have one in the meta-data
    if (!lang.name) lang.name = languageName;
    languages[languageName] = lang;
    lang.rawDefinition = languageDefinition.bind(null, hljs);

    if (lang.aliases) {
      registerAliases(lang.aliases, { languageName });
    }
  }

  /**
   * Remove a language grammar module
   *
   * @param {string} languageName
   */
  function unregisterLanguage(languageName) {
    delete languages[languageName];
    for (const alias of Object.keys(aliases)) {
      if (aliases[alias] === languageName) {
        delete aliases[alias];
      }
    }
  }

  /**
   * @returns {string[]} List of language internal names
   */
  function listLanguages() {
    return Object.keys(languages);
  }

  /**
   * @param {string} name - name of the language to retrieve
   * @returns {Language | undefined}
   */
  function getLanguage(name) {
    name = (name || '').toLowerCase();
    return languages[name] || languages[aliases[name]];
  }

  /**
   *
   * @param {string|string[]} aliasList - single alias or list of aliases
   * @param {{languageName: string}} opts
   */
  function registerAliases(aliasList, { languageName }) {
    if (typeof aliasList === 'string') {
      aliasList = [aliasList];
    }
    aliasList.forEach(alias => { aliases[alias.toLowerCase()] = languageName; });
  }

  /**
   * Determines if a given language has auto-detection enabled
   * @param {string} name - name of the language
   */
  function autoDetection(name) {
    const lang = getLanguage(name);
    return lang && !lang.disableAutodetect;
  }

  /**
   * Upgrades the old highlightBlock plugins to the new
   * highlightElement API
   * @param {HLJSPlugin} plugin
   */
  function upgradePluginAPI(plugin) {
    // TODO: remove with v12
    if (plugin["before:highlightBlock"] && !plugin["before:highlightElement"]) {
      plugin["before:highlightElement"] = (data) => {
        plugin["before:highlightBlock"](
          Object.assign({ block: data.el }, data)
        );
      };
    }
    if (plugin["after:highlightBlock"] && !plugin["after:highlightElement"]) {
      plugin["after:highlightElement"] = (data) => {
        plugin["after:highlightBlock"](
          Object.assign({ block: data.el }, data)
        );
      };
    }
  }

  /**
   * @param {HLJSPlugin} plugin
   */
  function addPlugin(plugin) {
    upgradePluginAPI(plugin);
    plugins.push(plugin);
  }

  /**
   * @param {HLJSPlugin} plugin
   */
  function removePlugin(plugin) {
    const index = plugins.indexOf(plugin);
    if (index !== -1) {
      plugins.splice(index, 1);
    }
  }

  /**
   *
   * @param {PluginEvent} event
   * @param {any} args
   */
  function fire(event, args) {
    const cb = event;
    plugins.forEach(function(plugin) {
      if (plugin[cb]) {
        plugin[cb](args);
      }
    });
  }

  /**
   * DEPRECATED
   * @param {HighlightedHTMLElement} el
   */
  function deprecateHighlightBlock(el) {
    deprecated("10.7.0", "highlightBlock will be removed entirely in v12.0");
    deprecated("10.7.0", "Please use highlightElement now.");

    return highlightElement(el);
  }

  /* Interface definition */
  Object.assign(hljs, {
    highlight,
    highlightAuto,
    highlightAll,
    highlightElement,
    // TODO: Remove with v12 API
    highlightBlock: deprecateHighlightBlock,
    configure,
    initHighlighting,
    initHighlightingOnLoad,
    registerLanguage,
    unregisterLanguage,
    listLanguages,
    getLanguage,
    registerAliases,
    autoDetection,
    inherit,
    addPlugin,
    removePlugin
  });

  hljs.debugMode = function() { SAFE_MODE = false; };
  hljs.safeMode = function() { SAFE_MODE = true; };
  hljs.versionString = version;

  hljs.regex = {
    concat: concat,
    lookahead: lookahead,
    either: either,
    optional: optional,
    anyNumberOfTimes: anyNumberOfTimes
  };

  for (const key in MODES) {
    // @ts-ignore
    if (typeof MODES[key] === "object") {
      // @ts-ignore
      deepFreeze(MODES[key]);
    }
  }

  // merge all the modes/regexes into our main object
  Object.assign(hljs, MODES);

  return hljs;
};

// Other names for the variable may break build script
const highlight = HLJS({});

// returns a new instance of the highlighter to be used for extensions
// check https://github.com/wooorm/lowlight/issues/47
highlight.newInstance = () => HLJS({});

var core = highlight;
highlight.HighlightJS = highlight;
highlight.default = highlight;

/*
Language: HTML, XML
Website: https://www.w3.org/XML/
Category: common, web
Audit: 2020
*/

var xml_1;
var hasRequiredXml;

function requireXml () {
	if (hasRequiredXml) return xml_1;
	hasRequiredXml = 1;
	/** @type LanguageFn */
	function xml(hljs) {
	  const regex = hljs.regex;
	  // XML names can have the following additional letters: https://www.w3.org/TR/xml/#NT-NameChar
	  // OTHER_NAME_CHARS = /[:\-.0-9\u00B7\u0300-\u036F\u203F-\u2040]/;
	  // Element names start with NAME_START_CHAR followed by optional other Unicode letters, ASCII digits, hyphens, underscores, and periods
	  // const TAG_NAME_RE = regex.concat(/[A-Z_a-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD]/, regex.optional(/[A-Z_a-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD\-.0-9\u00B7\u0300-\u036F\u203F-\u2040]*:/), /[A-Z_a-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD\-.0-9\u00B7\u0300-\u036F\u203F-\u2040]*/);;
	  // const XML_IDENT_RE = /[A-Z_a-z:\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD\-.0-9\u00B7\u0300-\u036F\u203F-\u2040]+/;
	  // const TAG_NAME_RE = regex.concat(/[A-Z_a-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD]/, regex.optional(/[A-Z_a-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD\-.0-9\u00B7\u0300-\u036F\u203F-\u2040]*:/), /[A-Z_a-z\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u02FF\u0370-\u037D\u037F-\u1FFF\u200C-\u200D\u2070-\u218F\u2C00-\u2FEF\u3001-\uD7FF\uF900-\uFDCF\uFDF0-\uFFFD\-.0-9\u00B7\u0300-\u036F\u203F-\u2040]*/);
	  // however, to cater for performance and more Unicode support rely simply on the Unicode letter class
	  const TAG_NAME_RE = regex.concat(/[\p{L}_]/u, regex.optional(/[\p{L}0-9_.-]*:/u), /[\p{L}0-9_.-]*/u);
	  const XML_IDENT_RE = /[\p{L}0-9._:-]+/u;
	  const XML_ENTITIES = {
	    className: 'symbol',
	    begin: /&[a-z]+;|&#[0-9]+;|&#x[a-f0-9]+;/
	  };
	  const XML_META_KEYWORDS = {
	    begin: /\s/,
	    contains: [
	      {
	        className: 'keyword',
	        begin: /#?[a-z_][a-z1-9_-]+/,
	        illegal: /\n/
	      }
	    ]
	  };
	  const XML_META_PAR_KEYWORDS = hljs.inherit(XML_META_KEYWORDS, {
	    begin: /\(/,
	    end: /\)/
	  });
	  const APOS_META_STRING_MODE = hljs.inherit(hljs.APOS_STRING_MODE, { className: 'string' });
	  const QUOTE_META_STRING_MODE = hljs.inherit(hljs.QUOTE_STRING_MODE, { className: 'string' });
	  const TAG_INTERNALS = {
	    endsWithParent: true,
	    illegal: /</,
	    relevance: 0,
	    contains: [
	      {
	        className: 'attr',
	        begin: XML_IDENT_RE,
	        relevance: 0
	      },
	      {
	        begin: /=\s*/,
	        relevance: 0,
	        contains: [
	          {
	            className: 'string',
	            endsParent: true,
	            variants: [
	              {
	                begin: /"/,
	                end: /"/,
	                contains: [ XML_ENTITIES ]
	              },
	              {
	                begin: /'/,
	                end: /'/,
	                contains: [ XML_ENTITIES ]
	              },
	              { begin: /[^\s"'=<>`]+/ }
	            ]
	          }
	        ]
	      }
	    ]
	  };
	  return {
	    name: 'HTML, XML',
	    aliases: [
	      'html',
	      'xhtml',
	      'rss',
	      'atom',
	      'xjb',
	      'xsd',
	      'xsl',
	      'plist',
	      'wsf',
	      'svg'
	    ],
	    case_insensitive: true,
	    unicodeRegex: true,
	    contains: [
	      {
	        className: 'meta',
	        begin: /<![a-z]/,
	        end: />/,
	        relevance: 10,
	        contains: [
	          XML_META_KEYWORDS,
	          QUOTE_META_STRING_MODE,
	          APOS_META_STRING_MODE,
	          XML_META_PAR_KEYWORDS,
	          {
	            begin: /\[/,
	            end: /\]/,
	            contains: [
	              {
	                className: 'meta',
	                begin: /<![a-z]/,
	                end: />/,
	                contains: [
	                  XML_META_KEYWORDS,
	                  XML_META_PAR_KEYWORDS,
	                  QUOTE_META_STRING_MODE,
	                  APOS_META_STRING_MODE
	                ]
	              }
	            ]
	          }
	        ]
	      },
	      hljs.COMMENT(
	        /<!--/,
	        /-->/,
	        { relevance: 10 }
	      ),
	      {
	        begin: /<!\[CDATA\[/,
	        end: /\]\]>/,
	        relevance: 10
	      },
	      XML_ENTITIES,
	      // xml processing instructions
	      {
	        className: 'meta',
	        end: /\?>/,
	        variants: [
	          {
	            begin: /<\?xml/,
	            relevance: 10,
	            contains: [
	              QUOTE_META_STRING_MODE
	            ]
	          },
	          {
	            begin: /<\?[a-z][a-z0-9]+/,
	          }
	        ]

	      },
	      {
	        className: 'tag',
	        /*
	        The lookahead pattern (?=...) ensures that 'begin' only matches
	        '<style' as a single word, followed by a whitespace or an
	        ending bracket.
	        */
	        begin: /<style(?=\s|>)/,
	        end: />/,
	        keywords: { name: 'style' },
	        contains: [ TAG_INTERNALS ],
	        starts: {
	          end: /<\/style>/,
	          returnEnd: true,
	          subLanguage: [
	            'css',
	            'xml'
	          ]
	        }
	      },
	      {
	        className: 'tag',
	        // See the comment in the <style tag about the lookahead pattern
	        begin: /<script(?=\s|>)/,
	        end: />/,
	        keywords: { name: 'script' },
	        contains: [ TAG_INTERNALS ],
	        starts: {
	          end: /<\/script>/,
	          returnEnd: true,
	          subLanguage: [
	            'javascript',
	            'handlebars',
	            'xml'
	          ]
	        }
	      },
	      // we need this for now for jSX
	      {
	        className: 'tag',
	        begin: /<>|<\/>/
	      },
	      // open tag
	      {
	        className: 'tag',
	        begin: regex.concat(
	          /</,
	          regex.lookahead(regex.concat(
	            TAG_NAME_RE,
	            // <tag/>
	            // <tag>
	            // <tag ...
	            regex.either(/\/>/, />/, /\s/)
	          ))
	        ),
	        end: /\/?>/,
	        contains: [
	          {
	            className: 'name',
	            begin: TAG_NAME_RE,
	            relevance: 0,
	            starts: TAG_INTERNALS
	          }
	        ]
	      },
	      // close tag
	      {
	        className: 'tag',
	        begin: regex.concat(
	          /<\//,
	          regex.lookahead(regex.concat(
	            TAG_NAME_RE, />/
	          ))
	        ),
	        contains: [
	          {
	            className: 'name',
	            begin: TAG_NAME_RE,
	            relevance: 0
	          },
	          {
	            begin: />/,
	            relevance: 0,
	            endsParent: true
	          }
	        ]
	      }
	    ]
	  };
	}

	xml_1 = xml;
	return xml_1;
}

/*
Language: Bash
Author: vah <vahtenberg@gmail.com>
Contributrors: Benjamin Pannell <contact@sierrasoftworks.com>
Website: https://www.gnu.org/software/bash/
Category: common
*/

var bash_1;
var hasRequiredBash;

function requireBash () {
	if (hasRequiredBash) return bash_1;
	hasRequiredBash = 1;
	/** @type LanguageFn */
	function bash(hljs) {
	  const regex = hljs.regex;
	  const VAR = {};
	  const BRACED_VAR = {
	    begin: /\$\{/,
	    end: /\}/,
	    contains: [
	      "self",
	      {
	        begin: /:-/,
	        contains: [ VAR ]
	      } // default values
	    ]
	  };
	  Object.assign(VAR, {
	    className: 'variable',
	    variants: [
	      { begin: regex.concat(/\$[\w\d#@][\w\d_]*/,
	        // negative look-ahead tries to avoid matching patterns that are not
	        // Perl at all like $ident$, @ident@, etc.
	        `(?![\\w\\d])(?![$])`) },
	      BRACED_VAR
	    ]
	  });

	  const SUBST = {
	    className: 'subst',
	    begin: /\$\(/,
	    end: /\)/,
	    contains: [ hljs.BACKSLASH_ESCAPE ]
	  };
	  const HERE_DOC = {
	    begin: /<<-?\s*(?=\w+)/,
	    starts: { contains: [
	      hljs.END_SAME_AS_BEGIN({
	        begin: /(\w+)/,
	        end: /(\w+)/,
	        className: 'string'
	      })
	    ] }
	  };
	  const QUOTE_STRING = {
	    className: 'string',
	    begin: /"/,
	    end: /"/,
	    contains: [
	      hljs.BACKSLASH_ESCAPE,
	      VAR,
	      SUBST
	    ]
	  };
	  SUBST.contains.push(QUOTE_STRING);
	  const ESCAPED_QUOTE = {
	    match: /\\"/
	  };
	  const APOS_STRING = {
	    className: 'string',
	    begin: /'/,
	    end: /'/
	  };
	  const ESCAPED_APOS = {
	    match: /\\'/
	  };
	  const ARITHMETIC = {
	    begin: /\$?\(\(/,
	    end: /\)\)/,
	    contains: [
	      {
	        begin: /\d+#[0-9a-f]+/,
	        className: "number"
	      },
	      hljs.NUMBER_MODE,
	      VAR
	    ]
	  };
	  const SH_LIKE_SHELLS = [
	    "fish",
	    "bash",
	    "zsh",
	    "sh",
	    "csh",
	    "ksh",
	    "tcsh",
	    "dash",
	    "scsh",
	  ];
	  const KNOWN_SHEBANG = hljs.SHEBANG({
	    binary: `(${SH_LIKE_SHELLS.join("|")})`,
	    relevance: 10
	  });
	  const FUNCTION = {
	    className: 'function',
	    begin: /\w[\w\d_]*\s*\(\s*\)\s*\{/,
	    returnBegin: true,
	    contains: [ hljs.inherit(hljs.TITLE_MODE, { begin: /\w[\w\d_]*/ }) ],
	    relevance: 0
	  };

	  const KEYWORDS = [
	    "if",
	    "then",
	    "else",
	    "elif",
	    "fi",
	    "for",
	    "while",
	    "until",
	    "in",
	    "do",
	    "done",
	    "case",
	    "esac",
	    "function",
	    "select"
	  ];

	  const LITERALS = [
	    "true",
	    "false"
	  ];

	  // to consume paths to prevent keyword matches inside them
	  const PATH_MODE = { match: /(\/[a-z._-]+)+/ };

	  // http://www.gnu.org/software/bash/manual/html_node/Shell-Builtin-Commands.html
	  const SHELL_BUILT_INS = [
	    "break",
	    "cd",
	    "continue",
	    "eval",
	    "exec",
	    "exit",
	    "export",
	    "getopts",
	    "hash",
	    "pwd",
	    "readonly",
	    "return",
	    "shift",
	    "test",
	    "times",
	    "trap",
	    "umask",
	    "unset"
	  ];

	  const BASH_BUILT_INS = [
	    "alias",
	    "bind",
	    "builtin",
	    "caller",
	    "command",
	    "declare",
	    "echo",
	    "enable",
	    "help",
	    "let",
	    "local",
	    "logout",
	    "mapfile",
	    "printf",
	    "read",
	    "readarray",
	    "source",
	    "type",
	    "typeset",
	    "ulimit",
	    "unalias"
	  ];

	  const ZSH_BUILT_INS = [
	    "autoload",
	    "bg",
	    "bindkey",
	    "bye",
	    "cap",
	    "chdir",
	    "clone",
	    "comparguments",
	    "compcall",
	    "compctl",
	    "compdescribe",
	    "compfiles",
	    "compgroups",
	    "compquote",
	    "comptags",
	    "comptry",
	    "compvalues",
	    "dirs",
	    "disable",
	    "disown",
	    "echotc",
	    "echoti",
	    "emulate",
	    "fc",
	    "fg",
	    "float",
	    "functions",
	    "getcap",
	    "getln",
	    "history",
	    "integer",
	    "jobs",
	    "kill",
	    "limit",
	    "log",
	    "noglob",
	    "popd",
	    "print",
	    "pushd",
	    "pushln",
	    "rehash",
	    "sched",
	    "setcap",
	    "setopt",
	    "stat",
	    "suspend",
	    "ttyctl",
	    "unfunction",
	    "unhash",
	    "unlimit",
	    "unsetopt",
	    "vared",
	    "wait",
	    "whence",
	    "where",
	    "which",
	    "zcompile",
	    "zformat",
	    "zftp",
	    "zle",
	    "zmodload",
	    "zparseopts",
	    "zprof",
	    "zpty",
	    "zregexparse",
	    "zsocket",
	    "zstyle",
	    "ztcp"
	  ];

	  const GNU_CORE_UTILS = [
	    "chcon",
	    "chgrp",
	    "chown",
	    "chmod",
	    "cp",
	    "dd",
	    "df",
	    "dir",
	    "dircolors",
	    "ln",
	    "ls",
	    "mkdir",
	    "mkfifo",
	    "mknod",
	    "mktemp",
	    "mv",
	    "realpath",
	    "rm",
	    "rmdir",
	    "shred",
	    "sync",
	    "touch",
	    "truncate",
	    "vdir",
	    "b2sum",
	    "base32",
	    "base64",
	    "cat",
	    "cksum",
	    "comm",
	    "csplit",
	    "cut",
	    "expand",
	    "fmt",
	    "fold",
	    "head",
	    "join",
	    "md5sum",
	    "nl",
	    "numfmt",
	    "od",
	    "paste",
	    "ptx",
	    "pr",
	    "sha1sum",
	    "sha224sum",
	    "sha256sum",
	    "sha384sum",
	    "sha512sum",
	    "shuf",
	    "sort",
	    "split",
	    "sum",
	    "tac",
	    "tail",
	    "tr",
	    "tsort",
	    "unexpand",
	    "uniq",
	    "wc",
	    "arch",
	    "basename",
	    "chroot",
	    "date",
	    "dirname",
	    "du",
	    "echo",
	    "env",
	    "expr",
	    "factor",
	    // "false", // keyword literal already
	    "groups",
	    "hostid",
	    "id",
	    "link",
	    "logname",
	    "nice",
	    "nohup",
	    "nproc",
	    "pathchk",
	    "pinky",
	    "printenv",
	    "printf",
	    "pwd",
	    "readlink",
	    "runcon",
	    "seq",
	    "sleep",
	    "stat",
	    "stdbuf",
	    "stty",
	    "tee",
	    "test",
	    "timeout",
	    // "true", // keyword literal already
	    "tty",
	    "uname",
	    "unlink",
	    "uptime",
	    "users",
	    "who",
	    "whoami",
	    "yes"
	  ];

	  return {
	    name: 'Bash',
	    aliases: [ 'sh' ],
	    keywords: {
	      $pattern: /\b[a-z][a-z0-9._-]+\b/,
	      keyword: KEYWORDS,
	      literal: LITERALS,
	      built_in: [
	        ...SHELL_BUILT_INS,
	        ...BASH_BUILT_INS,
	        // Shell modifiers
	        "set",
	        "shopt",
	        ...ZSH_BUILT_INS,
	        ...GNU_CORE_UTILS
	      ]
	    },
	    contains: [
	      KNOWN_SHEBANG, // to catch known shells and boost relevancy
	      hljs.SHEBANG(), // to catch unknown shells but still highlight the shebang
	      FUNCTION,
	      ARITHMETIC,
	      hljs.HASH_COMMENT_MODE,
	      HERE_DOC,
	      PATH_MODE,
	      QUOTE_STRING,
	      ESCAPED_QUOTE,
	      APOS_STRING,
	      ESCAPED_APOS,
	      VAR
	    ]
	  };
	}

	bash_1 = bash;
	return bash_1;
}

/*
Language: C
Category: common, system
Website: https://en.wikipedia.org/wiki/C_(programming_language)
*/

var c_1;
var hasRequiredC;

function requireC () {
	if (hasRequiredC) return c_1;
	hasRequiredC = 1;
	/** @type LanguageFn */
	function c(hljs) {
	  const regex = hljs.regex;
	  // added for historic reasons because `hljs.C_LINE_COMMENT_MODE` does
	  // not include such support nor can we be sure all the grammars depending
	  // on it would desire this behavior
	  const C_LINE_COMMENT_MODE = hljs.COMMENT('//', '$', { contains: [ { begin: /\\\n/ } ] });
	  const DECLTYPE_AUTO_RE = 'decltype\\(auto\\)';
	  const NAMESPACE_RE = '[a-zA-Z_]\\w*::';
	  const TEMPLATE_ARGUMENT_RE = '<[^<>]+>';
	  const FUNCTION_TYPE_RE = '('
	    + DECLTYPE_AUTO_RE + '|'
	    + regex.optional(NAMESPACE_RE)
	    + '[a-zA-Z_]\\w*' + regex.optional(TEMPLATE_ARGUMENT_RE)
	  + ')';


	  const TYPES = {
	    className: 'type',
	    variants: [
	      { begin: '\\b[a-z\\d_]*_t\\b' },
	      { match: /\batomic_[a-z]{3,6}\b/ }
	    ]

	  };

	  // https://en.cppreference.com/w/cpp/language/escape
	  // \\ \x \xFF \u2837 \u00323747 \374
	  const CHARACTER_ESCAPES = '\\\\(x[0-9A-Fa-f]{2}|u[0-9A-Fa-f]{4,8}|[0-7]{3}|\\S)';
	  const STRINGS = {
	    className: 'string',
	    variants: [
	      {
	        begin: '(u8?|U|L)?"',
	        end: '"',
	        illegal: '\\n',
	        contains: [ hljs.BACKSLASH_ESCAPE ]
	      },
	      {
	        begin: '(u8?|U|L)?\'(' + CHARACTER_ESCAPES + "|.)",
	        end: '\'',
	        illegal: '.'
	      },
	      hljs.END_SAME_AS_BEGIN({
	        begin: /(?:u8?|U|L)?R"([^()\\ ]{0,16})\(/,
	        end: /\)([^()\\ ]{0,16})"/
	      })
	    ]
	  };

	  const NUMBERS = {
	    className: 'number',
	    variants: [
	      { begin: '\\b(0b[01\']+)' },
	      { begin: '(-?)\\b([\\d\']+(\\.[\\d\']*)?|\\.[\\d\']+)((ll|LL|l|L)(u|U)?|(u|U)(ll|LL|l|L)?|f|F|b|B)' },
	      { begin: '(-?)(\\b0[xX][a-fA-F0-9\']+|(\\b[\\d\']+(\\.[\\d\']*)?|\\.[\\d\']+)([eE][-+]?[\\d\']+)?)' }
	    ],
	    relevance: 0
	  };

	  const PREPROCESSOR = {
	    className: 'meta',
	    begin: /#\s*[a-z]+\b/,
	    end: /$/,
	    keywords: { keyword:
	        'if else elif endif define undef warning error line '
	        + 'pragma _Pragma ifdef ifndef include' },
	    contains: [
	      {
	        begin: /\\\n/,
	        relevance: 0
	      },
	      hljs.inherit(STRINGS, { className: 'string' }),
	      {
	        className: 'string',
	        begin: /<.*?>/
	      },
	      C_LINE_COMMENT_MODE,
	      hljs.C_BLOCK_COMMENT_MODE
	    ]
	  };

	  const TITLE_MODE = {
	    className: 'title',
	    begin: regex.optional(NAMESPACE_RE) + hljs.IDENT_RE,
	    relevance: 0
	  };

	  const FUNCTION_TITLE = regex.optional(NAMESPACE_RE) + hljs.IDENT_RE + '\\s*\\(';

	  const C_KEYWORDS = [
	    "asm",
	    "auto",
	    "break",
	    "case",
	    "continue",
	    "default",
	    "do",
	    "else",
	    "enum",
	    "extern",
	    "for",
	    "fortran",
	    "goto",
	    "if",
	    "inline",
	    "register",
	    "restrict",
	    "return",
	    "sizeof",
	    "struct",
	    "switch",
	    "typedef",
	    "union",
	    "volatile",
	    "while",
	    "_Alignas",
	    "_Alignof",
	    "_Atomic",
	    "_Generic",
	    "_Noreturn",
	    "_Static_assert",
	    "_Thread_local",
	    // aliases
	    "alignas",
	    "alignof",
	    "noreturn",
	    "static_assert",
	    "thread_local",
	    // not a C keyword but is, for all intents and purposes, treated exactly like one.
	    "_Pragma"
	  ];

	  const C_TYPES = [
	    "float",
	    "double",
	    "signed",
	    "unsigned",
	    "int",
	    "short",
	    "long",
	    "char",
	    "void",
	    "_Bool",
	    "_Complex",
	    "_Imaginary",
	    "_Decimal32",
	    "_Decimal64",
	    "_Decimal128",
	    // modifiers
	    "const",
	    "static",
	    // aliases
	    "complex",
	    "bool",
	    "imaginary"
	  ];

	  const KEYWORDS = {
	    keyword: C_KEYWORDS,
	    type: C_TYPES,
	    literal: 'true false NULL',
	    // TODO: apply hinting work similar to what was done in cpp.js
	    built_in: 'std string wstring cin cout cerr clog stdin stdout stderr stringstream istringstream ostringstream '
	      + 'auto_ptr deque list queue stack vector map set pair bitset multiset multimap unordered_set '
	      + 'unordered_map unordered_multiset unordered_multimap priority_queue make_pair array shared_ptr abort terminate abs acos '
	      + 'asin atan2 atan calloc ceil cosh cos exit exp fabs floor fmod fprintf fputs free frexp '
	      + 'fscanf future isalnum isalpha iscntrl isdigit isgraph islower isprint ispunct isspace isupper '
	      + 'isxdigit tolower toupper labs ldexp log10 log malloc realloc memchr memcmp memcpy memset modf pow '
	      + 'printf putchar puts scanf sinh sin snprintf sprintf sqrt sscanf strcat strchr strcmp '
	      + 'strcpy strcspn strlen strncat strncmp strncpy strpbrk strrchr strspn strstr tanh tan '
	      + 'vfprintf vprintf vsprintf endl initializer_list unique_ptr',
	  };

	  const EXPRESSION_CONTAINS = [
	    PREPROCESSOR,
	    TYPES,
	    C_LINE_COMMENT_MODE,
	    hljs.C_BLOCK_COMMENT_MODE,
	    NUMBERS,
	    STRINGS
	  ];

	  const EXPRESSION_CONTEXT = {
	    // This mode covers expression context where we can't expect a function
	    // definition and shouldn't highlight anything that looks like one:
	    // `return some()`, `else if()`, `(x*sum(1, 2))`
	    variants: [
	      {
	        begin: /=/,
	        end: /;/
	      },
	      {
	        begin: /\(/,
	        end: /\)/
	      },
	      {
	        beginKeywords: 'new throw return else',
	        end: /;/
	      }
	    ],
	    keywords: KEYWORDS,
	    contains: EXPRESSION_CONTAINS.concat([
	      {
	        begin: /\(/,
	        end: /\)/,
	        keywords: KEYWORDS,
	        contains: EXPRESSION_CONTAINS.concat([ 'self' ]),
	        relevance: 0
	      }
	    ]),
	    relevance: 0
	  };

	  const FUNCTION_DECLARATION = {
	    begin: '(' + FUNCTION_TYPE_RE + '[\\*&\\s]+)+' + FUNCTION_TITLE,
	    returnBegin: true,
	    end: /[{;=]/,
	    excludeEnd: true,
	    keywords: KEYWORDS,
	    illegal: /[^\w\s\*&:<>.]/,
	    contains: [
	      { // to prevent it from being confused as the function title
	        begin: DECLTYPE_AUTO_RE,
	        keywords: KEYWORDS,
	        relevance: 0
	      },
	      {
	        begin: FUNCTION_TITLE,
	        returnBegin: true,
	        contains: [ hljs.inherit(TITLE_MODE, { className: "title.function" }) ],
	        relevance: 0
	      },
	      // allow for multiple declarations, e.g.:
	      // extern void f(int), g(char);
	      {
	        relevance: 0,
	        match: /,/
	      },
	      {
	        className: 'params',
	        begin: /\(/,
	        end: /\)/,
	        keywords: KEYWORDS,
	        relevance: 0,
	        contains: [
	          C_LINE_COMMENT_MODE,
	          hljs.C_BLOCK_COMMENT_MODE,
	          STRINGS,
	          NUMBERS,
	          TYPES,
	          // Count matching parentheses.
	          {
	            begin: /\(/,
	            end: /\)/,
	            keywords: KEYWORDS,
	            relevance: 0,
	            contains: [
	              'self',
	              C_LINE_COMMENT_MODE,
	              hljs.C_BLOCK_COMMENT_MODE,
	              STRINGS,
	              NUMBERS,
	              TYPES
	            ]
	          }
	        ]
	      },
	      TYPES,
	      C_LINE_COMMENT_MODE,
	      hljs.C_BLOCK_COMMENT_MODE,
	      PREPROCESSOR
	    ]
	  };

	  return {
	    name: "C",
	    aliases: [ 'h' ],
	    keywords: KEYWORDS,
	    // Until differentiations are added between `c` and `cpp`, `c` will
	    // not be auto-detected to avoid auto-detect conflicts between C and C++
	    disableAutodetect: true,
	    illegal: '</',
	    contains: [].concat(
	      EXPRESSION_CONTEXT,
	      FUNCTION_DECLARATION,
	      EXPRESSION_CONTAINS,
	      [
	        PREPROCESSOR,
	        {
	          begin: hljs.IDENT_RE + '::',
	          keywords: KEYWORDS
	        },
	        {
	          className: 'class',
	          beginKeywords: 'enum class struct union',
	          end: /[{;:<>=]/,
	          contains: [
	            { beginKeywords: "final class struct" },
	            hljs.TITLE_MODE
	          ]
	        }
	      ]),
	    exports: {
	      preprocessor: PREPROCESSOR,
	      strings: STRINGS,
	      keywords: KEYWORDS
	    }
	  };
	}

	c_1 = c;
	return c_1;
}

/*
Language: C++
Category: common, system
Website: https://isocpp.org
*/

var cpp_1;
var hasRequiredCpp;

function requireCpp () {
	if (hasRequiredCpp) return cpp_1;
	hasRequiredCpp = 1;
	/** @type LanguageFn */
	function cpp(hljs) {
	  const regex = hljs.regex;
	  // added for historic reasons because `hljs.C_LINE_COMMENT_MODE` does
	  // not include such support nor can we be sure all the grammars depending
	  // on it would desire this behavior
	  const C_LINE_COMMENT_MODE = hljs.COMMENT('//', '$', { contains: [ { begin: /\\\n/ } ] });
	  const DECLTYPE_AUTO_RE = 'decltype\\(auto\\)';
	  const NAMESPACE_RE = '[a-zA-Z_]\\w*::';
	  const TEMPLATE_ARGUMENT_RE = '<[^<>]+>';
	  const FUNCTION_TYPE_RE = '(?!struct)('
	    + DECLTYPE_AUTO_RE + '|'
	    + regex.optional(NAMESPACE_RE)
	    + '[a-zA-Z_]\\w*' + regex.optional(TEMPLATE_ARGUMENT_RE)
	  + ')';

	  const CPP_PRIMITIVE_TYPES = {
	    className: 'type',
	    begin: '\\b[a-z\\d_]*_t\\b'
	  };

	  // https://en.cppreference.com/w/cpp/language/escape
	  // \\ \x \xFF \u2837 \u00323747 \374
	  const CHARACTER_ESCAPES = '\\\\(x[0-9A-Fa-f]{2}|u[0-9A-Fa-f]{4,8}|[0-7]{3}|\\S)';
	  const STRINGS = {
	    className: 'string',
	    variants: [
	      {
	        begin: '(u8?|U|L)?"',
	        end: '"',
	        illegal: '\\n',
	        contains: [ hljs.BACKSLASH_ESCAPE ]
	      },
	      {
	        begin: '(u8?|U|L)?\'(' + CHARACTER_ESCAPES + '|.)',
	        end: '\'',
	        illegal: '.'
	      },
	      hljs.END_SAME_AS_BEGIN({
	        begin: /(?:u8?|U|L)?R"([^()\\ ]{0,16})\(/,
	        end: /\)([^()\\ ]{0,16})"/
	      })
	    ]
	  };

	  const NUMBERS = {
	    className: 'number',
	    variants: [
	      { begin: '\\b(0b[01\']+)' },
	      { begin: '(-?)\\b([\\d\']+(\\.[\\d\']*)?|\\.[\\d\']+)((ll|LL|l|L)(u|U)?|(u|U)(ll|LL|l|L)?|f|F|b|B)' },
	      { begin: '(-?)(\\b0[xX][a-fA-F0-9\']+|(\\b[\\d\']+(\\.[\\d\']*)?|\\.[\\d\']+)([eE][-+]?[\\d\']+)?)' }
	    ],
	    relevance: 0
	  };

	  const PREPROCESSOR = {
	    className: 'meta',
	    begin: /#\s*[a-z]+\b/,
	    end: /$/,
	    keywords: { keyword:
	        'if else elif endif define undef warning error line '
	        + 'pragma _Pragma ifdef ifndef include' },
	    contains: [
	      {
	        begin: /\\\n/,
	        relevance: 0
	      },
	      hljs.inherit(STRINGS, { className: 'string' }),
	      {
	        className: 'string',
	        begin: /<.*?>/
	      },
	      C_LINE_COMMENT_MODE,
	      hljs.C_BLOCK_COMMENT_MODE
	    ]
	  };

	  const TITLE_MODE = {
	    className: 'title',
	    begin: regex.optional(NAMESPACE_RE) + hljs.IDENT_RE,
	    relevance: 0
	  };

	  const FUNCTION_TITLE = regex.optional(NAMESPACE_RE) + hljs.IDENT_RE + '\\s*\\(';

	  // https://en.cppreference.com/w/cpp/keyword
	  const RESERVED_KEYWORDS = [
	    'alignas',
	    'alignof',
	    'and',
	    'and_eq',
	    'asm',
	    'atomic_cancel',
	    'atomic_commit',
	    'atomic_noexcept',
	    'auto',
	    'bitand',
	    'bitor',
	    'break',
	    'case',
	    'catch',
	    'class',
	    'co_await',
	    'co_return',
	    'co_yield',
	    'compl',
	    'concept',
	    'const_cast|10',
	    'consteval',
	    'constexpr',
	    'constinit',
	    'continue',
	    'decltype',
	    'default',
	    'delete',
	    'do',
	    'dynamic_cast|10',
	    'else',
	    'enum',
	    'explicit',
	    'export',
	    'extern',
	    'false',
	    'final',
	    'for',
	    'friend',
	    'goto',
	    'if',
	    'import',
	    'inline',
	    'module',
	    'mutable',
	    'namespace',
	    'new',
	    'noexcept',
	    'not',
	    'not_eq',
	    'nullptr',
	    'operator',
	    'or',
	    'or_eq',
	    'override',
	    'private',
	    'protected',
	    'public',
	    'reflexpr',
	    'register',
	    'reinterpret_cast|10',
	    'requires',
	    'return',
	    'sizeof',
	    'static_assert',
	    'static_cast|10',
	    'struct',
	    'switch',
	    'synchronized',
	    'template',
	    'this',
	    'thread_local',
	    'throw',
	    'transaction_safe',
	    'transaction_safe_dynamic',
	    'true',
	    'try',
	    'typedef',
	    'typeid',
	    'typename',
	    'union',
	    'using',
	    'virtual',
	    'volatile',
	    'while',
	    'xor',
	    'xor_eq'
	  ];

	  // https://en.cppreference.com/w/cpp/keyword
	  const RESERVED_TYPES = [
	    'bool',
	    'char',
	    'char16_t',
	    'char32_t',
	    'char8_t',
	    'double',
	    'float',
	    'int',
	    'long',
	    'short',
	    'void',
	    'wchar_t',
	    'unsigned',
	    'signed',
	    'const',
	    'static'
	  ];

	  const TYPE_HINTS = [
	    'any',
	    'auto_ptr',
	    'barrier',
	    'binary_semaphore',
	    'bitset',
	    'complex',
	    'condition_variable',
	    'condition_variable_any',
	    'counting_semaphore',
	    'deque',
	    'false_type',
	    'future',
	    'imaginary',
	    'initializer_list',
	    'istringstream',
	    'jthread',
	    'latch',
	    'lock_guard',
	    'multimap',
	    'multiset',
	    'mutex',
	    'optional',
	    'ostringstream',
	    'packaged_task',
	    'pair',
	    'promise',
	    'priority_queue',
	    'queue',
	    'recursive_mutex',
	    'recursive_timed_mutex',
	    'scoped_lock',
	    'set',
	    'shared_future',
	    'shared_lock',
	    'shared_mutex',
	    'shared_timed_mutex',
	    'shared_ptr',
	    'stack',
	    'string_view',
	    'stringstream',
	    'timed_mutex',
	    'thread',
	    'true_type',
	    'tuple',
	    'unique_lock',
	    'unique_ptr',
	    'unordered_map',
	    'unordered_multimap',
	    'unordered_multiset',
	    'unordered_set',
	    'variant',
	    'vector',
	    'weak_ptr',
	    'wstring',
	    'wstring_view'
	  ];

	  const FUNCTION_HINTS = [
	    'abort',
	    'abs',
	    'acos',
	    'apply',
	    'as_const',
	    'asin',
	    'atan',
	    'atan2',
	    'calloc',
	    'ceil',
	    'cerr',
	    'cin',
	    'clog',
	    'cos',
	    'cosh',
	    'cout',
	    'declval',
	    'endl',
	    'exchange',
	    'exit',
	    'exp',
	    'fabs',
	    'floor',
	    'fmod',
	    'forward',
	    'fprintf',
	    'fputs',
	    'free',
	    'frexp',
	    'fscanf',
	    'future',
	    'invoke',
	    'isalnum',
	    'isalpha',
	    'iscntrl',
	    'isdigit',
	    'isgraph',
	    'islower',
	    'isprint',
	    'ispunct',
	    'isspace',
	    'isupper',
	    'isxdigit',
	    'labs',
	    'launder',
	    'ldexp',
	    'log',
	    'log10',
	    'make_pair',
	    'make_shared',
	    'make_shared_for_overwrite',
	    'make_tuple',
	    'make_unique',
	    'malloc',
	    'memchr',
	    'memcmp',
	    'memcpy',
	    'memset',
	    'modf',
	    'move',
	    'pow',
	    'printf',
	    'putchar',
	    'puts',
	    'realloc',
	    'scanf',
	    'sin',
	    'sinh',
	    'snprintf',
	    'sprintf',
	    'sqrt',
	    'sscanf',
	    'std',
	    'stderr',
	    'stdin',
	    'stdout',
	    'strcat',
	    'strchr',
	    'strcmp',
	    'strcpy',
	    'strcspn',
	    'strlen',
	    'strncat',
	    'strncmp',
	    'strncpy',
	    'strpbrk',
	    'strrchr',
	    'strspn',
	    'strstr',
	    'swap',
	    'tan',
	    'tanh',
	    'terminate',
	    'to_underlying',
	    'tolower',
	    'toupper',
	    'vfprintf',
	    'visit',
	    'vprintf',
	    'vsprintf'
	  ];

	  const LITERALS = [
	    'NULL',
	    'false',
	    'nullopt',
	    'nullptr',
	    'true'
	  ];

	  // https://en.cppreference.com/w/cpp/keyword
	  const BUILT_IN = [ '_Pragma' ];

	  const CPP_KEYWORDS = {
	    type: RESERVED_TYPES,
	    keyword: RESERVED_KEYWORDS,
	    literal: LITERALS,
	    built_in: BUILT_IN,
	    _type_hints: TYPE_HINTS
	  };

	  const FUNCTION_DISPATCH = {
	    className: 'function.dispatch',
	    relevance: 0,
	    keywords: {
	      // Only for relevance, not highlighting.
	      _hint: FUNCTION_HINTS },
	    begin: regex.concat(
	      /\b/,
	      /(?!decltype)/,
	      /(?!if)/,
	      /(?!for)/,
	      /(?!switch)/,
	      /(?!while)/,
	      hljs.IDENT_RE,
	      regex.lookahead(/(<[^<>]+>|)\s*\(/))
	  };

	  const EXPRESSION_CONTAINS = [
	    FUNCTION_DISPATCH,
	    PREPROCESSOR,
	    CPP_PRIMITIVE_TYPES,
	    C_LINE_COMMENT_MODE,
	    hljs.C_BLOCK_COMMENT_MODE,
	    NUMBERS,
	    STRINGS
	  ];

	  const EXPRESSION_CONTEXT = {
	    // This mode covers expression context where we can't expect a function
	    // definition and shouldn't highlight anything that looks like one:
	    // `return some()`, `else if()`, `(x*sum(1, 2))`
	    variants: [
	      {
	        begin: /=/,
	        end: /;/
	      },
	      {
	        begin: /\(/,
	        end: /\)/
	      },
	      {
	        beginKeywords: 'new throw return else',
	        end: /;/
	      }
	    ],
	    keywords: CPP_KEYWORDS,
	    contains: EXPRESSION_CONTAINS.concat([
	      {
	        begin: /\(/,
	        end: /\)/,
	        keywords: CPP_KEYWORDS,
	        contains: EXPRESSION_CONTAINS.concat([ 'self' ]),
	        relevance: 0
	      }
	    ]),
	    relevance: 0
	  };

	  const FUNCTION_DECLARATION = {
	    className: 'function',
	    begin: '(' + FUNCTION_TYPE_RE + '[\\*&\\s]+)+' + FUNCTION_TITLE,
	    returnBegin: true,
	    end: /[{;=]/,
	    excludeEnd: true,
	    keywords: CPP_KEYWORDS,
	    illegal: /[^\w\s\*&:<>.]/,
	    contains: [
	      { // to prevent it from being confused as the function title
	        begin: DECLTYPE_AUTO_RE,
	        keywords: CPP_KEYWORDS,
	        relevance: 0
	      },
	      {
	        begin: FUNCTION_TITLE,
	        returnBegin: true,
	        contains: [ TITLE_MODE ],
	        relevance: 0
	      },
	      // needed because we do not have look-behind on the below rule
	      // to prevent it from grabbing the final : in a :: pair
	      {
	        begin: /::/,
	        relevance: 0
	      },
	      // initializers
	      {
	        begin: /:/,
	        endsWithParent: true,
	        contains: [
	          STRINGS,
	          NUMBERS
	        ]
	      },
	      // allow for multiple declarations, e.g.:
	      // extern void f(int), g(char);
	      {
	        relevance: 0,
	        match: /,/
	      },
	      {
	        className: 'params',
	        begin: /\(/,
	        end: /\)/,
	        keywords: CPP_KEYWORDS,
	        relevance: 0,
	        contains: [
	          C_LINE_COMMENT_MODE,
	          hljs.C_BLOCK_COMMENT_MODE,
	          STRINGS,
	          NUMBERS,
	          CPP_PRIMITIVE_TYPES,
	          // Count matching parentheses.
	          {
	            begin: /\(/,
	            end: /\)/,
	            keywords: CPP_KEYWORDS,
	            relevance: 0,
	            contains: [
	              'self',
	              C_LINE_COMMENT_MODE,
	              hljs.C_BLOCK_COMMENT_MODE,
	              STRINGS,
	              NUMBERS,
	              CPP_PRIMITIVE_TYPES
	            ]
	          }
	        ]
	      },
	      CPP_PRIMITIVE_TYPES,
	      C_LINE_COMMENT_MODE,
	      hljs.C_BLOCK_COMMENT_MODE,
	      PREPROCESSOR
	    ]
	  };

	  return {
	    name: 'C++',
	    aliases: [
	      'cc',
	      'c++',
	      'h++',
	      'hpp',
	      'hh',
	      'hxx',
	      'cxx'
	    ],
	    keywords: CPP_KEYWORDS,
	    illegal: '</',
	    classNameAliases: { 'function.dispatch': 'built_in' },
	    contains: [].concat(
	      EXPRESSION_CONTEXT,
	      FUNCTION_DECLARATION,
	      FUNCTION_DISPATCH,
	      EXPRESSION_CONTAINS,
	      [
	        PREPROCESSOR,
	        { // containers: ie, `vector <int> rooms (9);`
	          begin: '\\b(deque|list|queue|priority_queue|pair|stack|vector|map|set|bitset|multiset|multimap|unordered_map|unordered_set|unordered_multiset|unordered_multimap|array|tuple|optional|variant|function)\\s*<(?!<)',
	          end: '>',
	          keywords: CPP_KEYWORDS,
	          contains: [
	            'self',
	            CPP_PRIMITIVE_TYPES
	          ]
	        },
	        {
	          begin: hljs.IDENT_RE + '::',
	          keywords: CPP_KEYWORDS
	        },
	        {
	          match: [
	            // extra complexity to deal with `enum class` and `enum struct`
	            /\b(?:enum(?:\s+(?:class|struct))?|class|struct|union)/,
	            /\s+/,
	            /\w+/
	          ],
	          className: {
	            1: 'keyword',
	            3: 'title.class'
	          }
	        }
	      ])
	  };
	}

	cpp_1 = cpp;
	return cpp_1;
}

/*
Language: C#
Author: Jason Diamond <jason@diamond.name>
Contributor: Nicolas LLOBERA <nllobera@gmail.com>, Pieter Vantorre <pietervantorre@gmail.com>, David Pine <david.pine@microsoft.com>
Website: https://docs.microsoft.com/dotnet/csharp/
Category: common
*/

var csharp_1;
var hasRequiredCsharp;

function requireCsharp () {
	if (hasRequiredCsharp) return csharp_1;
	hasRequiredCsharp = 1;
	/** @type LanguageFn */
	function csharp(hljs) {
	  const BUILT_IN_KEYWORDS = [
	    'bool',
	    'byte',
	    'char',
	    'decimal',
	    'delegate',
	    'double',
	    'dynamic',
	    'enum',
	    'float',
	    'int',
	    'long',
	    'nint',
	    'nuint',
	    'object',
	    'sbyte',
	    'short',
	    'string',
	    'ulong',
	    'uint',
	    'ushort'
	  ];
	  const FUNCTION_MODIFIERS = [
	    'public',
	    'private',
	    'protected',
	    'static',
	    'internal',
	    'protected',
	    'abstract',
	    'async',
	    'extern',
	    'override',
	    'unsafe',
	    'virtual',
	    'new',
	    'sealed',
	    'partial'
	  ];
	  const LITERAL_KEYWORDS = [
	    'default',
	    'false',
	    'null',
	    'true'
	  ];
	  const NORMAL_KEYWORDS = [
	    'abstract',
	    'as',
	    'base',
	    'break',
	    'case',
	    'catch',
	    'class',
	    'const',
	    'continue',
	    'do',
	    'else',
	    'event',
	    'explicit',
	    'extern',
	    'finally',
	    'fixed',
	    'for',
	    'foreach',
	    'goto',
	    'if',
	    'implicit',
	    'in',
	    'interface',
	    'internal',
	    'is',
	    'lock',
	    'namespace',
	    'new',
	    'operator',
	    'out',
	    'override',
	    'params',
	    'private',
	    'protected',
	    'public',
	    'readonly',
	    'record',
	    'ref',
	    'return',
	    'scoped',
	    'sealed',
	    'sizeof',
	    'stackalloc',
	    'static',
	    'struct',
	    'switch',
	    'this',
	    'throw',
	    'try',
	    'typeof',
	    'unchecked',
	    'unsafe',
	    'using',
	    'virtual',
	    'void',
	    'volatile',
	    'while'
	  ];
	  const CONTEXTUAL_KEYWORDS = [
	    'add',
	    'alias',
	    'and',
	    'ascending',
	    'async',
	    'await',
	    'by',
	    'descending',
	    'equals',
	    'from',
	    'get',
	    'global',
	    'group',
	    'init',
	    'into',
	    'join',
	    'let',
	    'nameof',
	    'not',
	    'notnull',
	    'on',
	    'or',
	    'orderby',
	    'partial',
	    'remove',
	    'select',
	    'set',
	    'unmanaged',
	    'value|0',
	    'var',
	    'when',
	    'where',
	    'with',
	    'yield'
	  ];

	  const KEYWORDS = {
	    keyword: NORMAL_KEYWORDS.concat(CONTEXTUAL_KEYWORDS),
	    built_in: BUILT_IN_KEYWORDS,
	    literal: LITERAL_KEYWORDS
	  };
	  const TITLE_MODE = hljs.inherit(hljs.TITLE_MODE, { begin: '[a-zA-Z](\\.?\\w)*' });
	  const NUMBERS = {
	    className: 'number',
	    variants: [
	      { begin: '\\b(0b[01\']+)' },
	      { begin: '(-?)\\b([\\d\']+(\\.[\\d\']*)?|\\.[\\d\']+)(u|U|l|L|ul|UL|f|F|b|B)' },
	      { begin: '(-?)(\\b0[xX][a-fA-F0-9\']+|(\\b[\\d\']+(\\.[\\d\']*)?|\\.[\\d\']+)([eE][-+]?[\\d\']+)?)' }
	    ],
	    relevance: 0
	  };
	  const VERBATIM_STRING = {
	    className: 'string',
	    begin: '@"',
	    end: '"',
	    contains: [ { begin: '""' } ]
	  };
	  const VERBATIM_STRING_NO_LF = hljs.inherit(VERBATIM_STRING, { illegal: /\n/ });
	  const SUBST = {
	    className: 'subst',
	    begin: /\{/,
	    end: /\}/,
	    keywords: KEYWORDS
	  };
	  const SUBST_NO_LF = hljs.inherit(SUBST, { illegal: /\n/ });
	  const INTERPOLATED_STRING = {
	    className: 'string',
	    begin: /\$"/,
	    end: '"',
	    illegal: /\n/,
	    contains: [
	      { begin: /\{\{/ },
	      { begin: /\}\}/ },
	      hljs.BACKSLASH_ESCAPE,
	      SUBST_NO_LF
	    ]
	  };
	  const INTERPOLATED_VERBATIM_STRING = {
	    className: 'string',
	    begin: /\$@"/,
	    end: '"',
	    contains: [
	      { begin: /\{\{/ },
	      { begin: /\}\}/ },
	      { begin: '""' },
	      SUBST
	    ]
	  };
	  const INTERPOLATED_VERBATIM_STRING_NO_LF = hljs.inherit(INTERPOLATED_VERBATIM_STRING, {
	    illegal: /\n/,
	    contains: [
	      { begin: /\{\{/ },
	      { begin: /\}\}/ },
	      { begin: '""' },
	      SUBST_NO_LF
	    ]
	  });
	  SUBST.contains = [
	    INTERPOLATED_VERBATIM_STRING,
	    INTERPOLATED_STRING,
	    VERBATIM_STRING,
	    hljs.APOS_STRING_MODE,
	    hljs.QUOTE_STRING_MODE,
	    NUMBERS,
	    hljs.C_BLOCK_COMMENT_MODE
	  ];
	  SUBST_NO_LF.contains = [
	    INTERPOLATED_VERBATIM_STRING_NO_LF,
	    INTERPOLATED_STRING,
	    VERBATIM_STRING_NO_LF,
	    hljs.APOS_STRING_MODE,
	    hljs.QUOTE_STRING_MODE,
	    NUMBERS,
	    hljs.inherit(hljs.C_BLOCK_COMMENT_MODE, { illegal: /\n/ })
	  ];
	  const STRING = { variants: [
	    INTERPOLATED_VERBATIM_STRING,
	    INTERPOLATED_STRING,
	    VERBATIM_STRING,
	    hljs.APOS_STRING_MODE,
	    hljs.QUOTE_STRING_MODE
	  ] };

	  const GENERIC_MODIFIER = {
	    begin: "<",
	    end: ">",
	    contains: [
	      { beginKeywords: "in out" },
	      TITLE_MODE
	    ]
	  };
	  const TYPE_IDENT_RE = hljs.IDENT_RE + '(<' + hljs.IDENT_RE + '(\\s*,\\s*' + hljs.IDENT_RE + ')*>)?(\\[\\])?';
	  const AT_IDENTIFIER = {
	    // prevents expressions like `@class` from incorrect flagging
	    // `class` as a keyword
	    begin: "@" + hljs.IDENT_RE,
	    relevance: 0
	  };

	  return {
	    name: 'C#',
	    aliases: [
	      'cs',
	      'c#'
	    ],
	    keywords: KEYWORDS,
	    illegal: /::/,
	    contains: [
	      hljs.COMMENT(
	        '///',
	        '$',
	        {
	          returnBegin: true,
	          contains: [
	            {
	              className: 'doctag',
	              variants: [
	                {
	                  begin: '///',
	                  relevance: 0
	                },
	                { begin: '<!--|-->' },
	                {
	                  begin: '</?',
	                  end: '>'
	                }
	              ]
	            }
	          ]
	        }
	      ),
	      hljs.C_LINE_COMMENT_MODE,
	      hljs.C_BLOCK_COMMENT_MODE,
	      {
	        className: 'meta',
	        begin: '#',
	        end: '$',
	        keywords: { keyword: 'if else elif endif define undef warning error line region endregion pragma checksum' }
	      },
	      STRING,
	      NUMBERS,
	      {
	        beginKeywords: 'class interface',
	        relevance: 0,
	        end: /[{;=]/,
	        illegal: /[^\s:,]/,
	        contains: [
	          { beginKeywords: "where class" },
	          TITLE_MODE,
	          GENERIC_MODIFIER,
	          hljs.C_LINE_COMMENT_MODE,
	          hljs.C_BLOCK_COMMENT_MODE
	        ]
	      },
	      {
	        beginKeywords: 'namespace',
	        relevance: 0,
	        end: /[{;=]/,
	        illegal: /[^\s:]/,
	        contains: [
	          TITLE_MODE,
	          hljs.C_LINE_COMMENT_MODE,
	          hljs.C_BLOCK_COMMENT_MODE
	        ]
	      },
	      {
	        beginKeywords: 'record',
	        relevance: 0,
	        end: /[{;=]/,
	        illegal: /[^\s:]/,
	        contains: [
	          TITLE_MODE,
	          GENERIC_MODIFIER,
	          hljs.C_LINE_COMMENT_MODE,
	          hljs.C_BLOCK_COMMENT_MODE
	        ]
	      },
	      {
	        // [Attributes("")]
	        className: 'meta',
	        begin: '^\\s*\\[(?=[\\w])',
	        excludeBegin: true,
	        end: '\\]',
	        excludeEnd: true,
	        contains: [
	          {
	            className: 'string',
	            begin: /"/,
	            end: /"/
	          }
	        ]
	      },
	      {
	        // Expression keywords prevent 'keyword Name(...)' from being
	        // recognized as a function definition
	        beginKeywords: 'new return throw await else',
	        relevance: 0
	      },
	      {
	        className: 'function',
	        begin: '(' + TYPE_IDENT_RE + '\\s+)+' + hljs.IDENT_RE + '\\s*(<[^=]+>\\s*)?\\(',
	        returnBegin: true,
	        end: /\s*[{;=]/,
	        excludeEnd: true,
	        keywords: KEYWORDS,
	        contains: [
	          // prevents these from being highlighted `title`
	          {
	            beginKeywords: FUNCTION_MODIFIERS.join(" "),
	            relevance: 0
	          },
	          {
	            begin: hljs.IDENT_RE + '\\s*(<[^=]+>\\s*)?\\(',
	            returnBegin: true,
	            contains: [
	              hljs.TITLE_MODE,
	              GENERIC_MODIFIER
	            ],
	            relevance: 0
	          },
	          { match: /\(\)/ },
	          {
	            className: 'params',
	            begin: /\(/,
	            end: /\)/,
	            excludeBegin: true,
	            excludeEnd: true,
	            keywords: KEYWORDS,
	            relevance: 0,
	            contains: [
	              STRING,
	              NUMBERS,
	              hljs.C_BLOCK_COMMENT_MODE
	            ]
	          },
	          hljs.C_LINE_COMMENT_MODE,
	          hljs.C_BLOCK_COMMENT_MODE
	        ]
	      },
	      AT_IDENTIFIER
	    ]
	  };
	}

	csharp_1 = csharp;
	return csharp_1;
}

var css_1;
var hasRequiredCss;

function requireCss () {
	if (hasRequiredCss) return css_1;
	hasRequiredCss = 1;
	const MODES = (hljs) => {
	  return {
	    IMPORTANT: {
	      scope: 'meta',
	      begin: '!important'
	    },
	    BLOCK_COMMENT: hljs.C_BLOCK_COMMENT_MODE,
	    HEXCOLOR: {
	      scope: 'number',
	      begin: /#(([0-9a-fA-F]{3,4})|(([0-9a-fA-F]{2}){3,4}))\b/
	    },
	    FUNCTION_DISPATCH: {
	      className: "built_in",
	      begin: /[\w-]+(?=\()/
	    },
	    ATTRIBUTE_SELECTOR_MODE: {
	      scope: 'selector-attr',
	      begin: /\[/,
	      end: /\]/,
	      illegal: '$',
	      contains: [
	        hljs.APOS_STRING_MODE,
	        hljs.QUOTE_STRING_MODE
	      ]
	    },
	    CSS_NUMBER_MODE: {
	      scope: 'number',
	      begin: hljs.NUMBER_RE + '(' +
	        '%|em|ex|ch|rem' +
	        '|vw|vh|vmin|vmax' +
	        '|cm|mm|in|pt|pc|px' +
	        '|deg|grad|rad|turn' +
	        '|s|ms' +
	        '|Hz|kHz' +
	        '|dpi|dpcm|dppx' +
	        ')?',
	      relevance: 0
	    },
	    CSS_VARIABLE: {
	      className: "attr",
	      begin: /--[A-Za-z_][A-Za-z0-9_-]*/
	    }
	  };
	};

	const TAGS = [
	  'a',
	  'abbr',
	  'address',
	  'article',
	  'aside',
	  'audio',
	  'b',
	  'blockquote',
	  'body',
	  'button',
	  'canvas',
	  'caption',
	  'cite',
	  'code',
	  'dd',
	  'del',
	  'details',
	  'dfn',
	  'div',
	  'dl',
	  'dt',
	  'em',
	  'fieldset',
	  'figcaption',
	  'figure',
	  'footer',
	  'form',
	  'h1',
	  'h2',
	  'h3',
	  'h4',
	  'h5',
	  'h6',
	  'header',
	  'hgroup',
	  'html',
	  'i',
	  'iframe',
	  'img',
	  'input',
	  'ins',
	  'kbd',
	  'label',
	  'legend',
	  'li',
	  'main',
	  'mark',
	  'menu',
	  'nav',
	  'object',
	  'ol',
	  'p',
	  'q',
	  'quote',
	  'samp',
	  'section',
	  'span',
	  'strong',
	  'summary',
	  'sup',
	  'table',
	  'tbody',
	  'td',
	  'textarea',
	  'tfoot',
	  'th',
	  'thead',
	  'time',
	  'tr',
	  'ul',
	  'var',
	  'video'
	];

	const MEDIA_FEATURES = [
	  'any-hover',
	  'any-pointer',
	  'aspect-ratio',
	  'color',
	  'color-gamut',
	  'color-index',
	  'device-aspect-ratio',
	  'device-height',
	  'device-width',
	  'display-mode',
	  'forced-colors',
	  'grid',
	  'height',
	  'hover',
	  'inverted-colors',
	  'monochrome',
	  'orientation',
	  'overflow-block',
	  'overflow-inline',
	  'pointer',
	  'prefers-color-scheme',
	  'prefers-contrast',
	  'prefers-reduced-motion',
	  'prefers-reduced-transparency',
	  'resolution',
	  'scan',
	  'scripting',
	  'update',
	  'width',
	  // TODO: find a better solution?
	  'min-width',
	  'max-width',
	  'min-height',
	  'max-height'
	];

	// https://developer.mozilla.org/en-US/docs/Web/CSS/Pseudo-classes
	const PSEUDO_CLASSES = [
	  'active',
	  'any-link',
	  'blank',
	  'checked',
	  'current',
	  'default',
	  'defined',
	  'dir', // dir()
	  'disabled',
	  'drop',
	  'empty',
	  'enabled',
	  'first',
	  'first-child',
	  'first-of-type',
	  'fullscreen',
	  'future',
	  'focus',
	  'focus-visible',
	  'focus-within',
	  'has', // has()
	  'host', // host or host()
	  'host-context', // host-context()
	  'hover',
	  'indeterminate',
	  'in-range',
	  'invalid',
	  'is', // is()
	  'lang', // lang()
	  'last-child',
	  'last-of-type',
	  'left',
	  'link',
	  'local-link',
	  'not', // not()
	  'nth-child', // nth-child()
	  'nth-col', // nth-col()
	  'nth-last-child', // nth-last-child()
	  'nth-last-col', // nth-last-col()
	  'nth-last-of-type', //nth-last-of-type()
	  'nth-of-type', //nth-of-type()
	  'only-child',
	  'only-of-type',
	  'optional',
	  'out-of-range',
	  'past',
	  'placeholder-shown',
	  'read-only',
	  'read-write',
	  'required',
	  'right',
	  'root',
	  'scope',
	  'target',
	  'target-within',
	  'user-invalid',
	  'valid',
	  'visited',
	  'where' // where()
	];

	// https://developer.mozilla.org/en-US/docs/Web/CSS/Pseudo-elements
	const PSEUDO_ELEMENTS = [
	  'after',
	  'backdrop',
	  'before',
	  'cue',
	  'cue-region',
	  'first-letter',
	  'first-line',
	  'grammar-error',
	  'marker',
	  'part',
	  'placeholder',
	  'selection',
	  'slotted',
	  'spelling-error'
	];

	const ATTRIBUTES = [
	  'align-content',
	  'align-items',
	  'align-self',
	  'all',
	  'animation',
	  'animation-delay',
	  'animation-direction',
	  'animation-duration',
	  'animation-fill-mode',
	  'animation-iteration-count',
	  'animation-name',
	  'animation-play-state',
	  'animation-timing-function',
	  'backface-visibility',
	  'background',
	  'background-attachment',
	  'background-blend-mode',
	  'background-clip',
	  'background-color',
	  'background-image',
	  'background-origin',
	  'background-position',
	  'background-repeat',
	  'background-size',
	  'block-size',
	  'border',
	  'border-block',
	  'border-block-color',
	  'border-block-end',
	  'border-block-end-color',
	  'border-block-end-style',
	  'border-block-end-width',
	  'border-block-start',
	  'border-block-start-color',
	  'border-block-start-style',
	  'border-block-start-width',
	  'border-block-style',
	  'border-block-width',
	  'border-bottom',
	  'border-bottom-color',
	  'border-bottom-left-radius',
	  'border-bottom-right-radius',
	  'border-bottom-style',
	  'border-bottom-width',
	  'border-collapse',
	  'border-color',
	  'border-image',
	  'border-image-outset',
	  'border-image-repeat',
	  'border-image-slice',
	  'border-image-source',
	  'border-image-width',
	  'border-inline',
	  'border-inline-color',
	  'border-inline-end',
	  'border-inline-end-color',
	  'border-inline-end-style',
	  'border-inline-end-width',
	  'border-inline-start',
	  'border-inline-start-color',
	  'border-inline-start-style',
	  'border-inline-start-width',
	  'border-inline-style',
	  'border-inline-width',
	  'border-left',
	  'border-left-color',
	  'border-left-style',
	  'border-left-width',
	  'border-radius',
	  'border-right',
	  'border-right-color',
	  'border-right-style',
	  'border-right-width',
	  'border-spacing',
	  'border-style',
	  'border-top',
	  'border-top-color',
	  'border-top-left-radius',
	  'border-top-right-radius',
	  'border-top-style',
	  'border-top-width',
	  'border-width',
	  'bottom',
	  'box-decoration-break',
	  'box-shadow',
	  'box-sizing',
	  'break-after',
	  'break-before',
	  'break-inside',
	  'caption-side',
	  'caret-color',
	  'clear',
	  'clip',
	  'clip-path',
	  'clip-rule',
	  'color',
	  'column-count',
	  'column-fill',
	  'column-gap',
	  'column-rule',
	  'column-rule-color',
	  'column-rule-style',
	  'column-rule-width',
	  'column-span',
	  'column-width',
	  'columns',
	  'contain',
	  'content',
	  'content-visibility',
	  'counter-increment',
	  'counter-reset',
	  'cue',
	  'cue-after',
	  'cue-before',
	  'cursor',
	  'direction',
	  'display',
	  'empty-cells',
	  'filter',
	  'flex',
	  'flex-basis',
	  'flex-direction',
	  'flex-flow',
	  'flex-grow',
	  'flex-shrink',
	  'flex-wrap',
	  'float',
	  'flow',
	  'font',
	  'font-display',
	  'font-family',
	  'font-feature-settings',
	  'font-kerning',
	  'font-language-override',
	  'font-size',
	  'font-size-adjust',
	  'font-smoothing',
	  'font-stretch',
	  'font-style',
	  'font-synthesis',
	  'font-variant',
	  'font-variant-caps',
	  'font-variant-east-asian',
	  'font-variant-ligatures',
	  'font-variant-numeric',
	  'font-variant-position',
	  'font-variation-settings',
	  'font-weight',
	  'gap',
	  'glyph-orientation-vertical',
	  'grid',
	  'grid-area',
	  'grid-auto-columns',
	  'grid-auto-flow',
	  'grid-auto-rows',
	  'grid-column',
	  'grid-column-end',
	  'grid-column-start',
	  'grid-gap',
	  'grid-row',
	  'grid-row-end',
	  'grid-row-start',
	  'grid-template',
	  'grid-template-areas',
	  'grid-template-columns',
	  'grid-template-rows',
	  'hanging-punctuation',
	  'height',
	  'hyphens',
	  'icon',
	  'image-orientation',
	  'image-rendering',
	  'image-resolution',
	  'ime-mode',
	  'inline-size',
	  'isolation',
	  'justify-content',
	  'left',
	  'letter-spacing',
	  'line-break',
	  'line-height',
	  'list-style',
	  'list-style-image',
	  'list-style-position',
	  'list-style-type',
	  'margin',
	  'margin-block',
	  'margin-block-end',
	  'margin-block-start',
	  'margin-bottom',
	  'margin-inline',
	  'margin-inline-end',
	  'margin-inline-start',
	  'margin-left',
	  'margin-right',
	  'margin-top',
	  'marks',
	  'mask',
	  'mask-border',
	  'mask-border-mode',
	  'mask-border-outset',
	  'mask-border-repeat',
	  'mask-border-slice',
	  'mask-border-source',
	  'mask-border-width',
	  'mask-clip',
	  'mask-composite',
	  'mask-image',
	  'mask-mode',
	  'mask-origin',
	  'mask-position',
	  'mask-repeat',
	  'mask-size',
	  'mask-type',
	  'max-block-size',
	  'max-height',
	  'max-inline-size',
	  'max-width',
	  'min-block-size',
	  'min-height',
	  'min-inline-size',
	  'min-width',
	  'mix-blend-mode',
	  'nav-down',
	  'nav-index',
	  'nav-left',
	  'nav-right',
	  'nav-up',
	  'none',
	  'normal',
	  'object-fit',
	  'object-position',
	  'opacity',
	  'order',
	  'orphans',
	  'outline',
	  'outline-color',
	  'outline-offset',
	  'outline-style',
	  'outline-width',
	  'overflow',
	  'overflow-wrap',
	  'overflow-x',
	  'overflow-y',
	  'padding',
	  'padding-block',
	  'padding-block-end',
	  'padding-block-start',
	  'padding-bottom',
	  'padding-inline',
	  'padding-inline-end',
	  'padding-inline-start',
	  'padding-left',
	  'padding-right',
	  'padding-top',
	  'page-break-after',
	  'page-break-before',
	  'page-break-inside',
	  'pause',
	  'pause-after',
	  'pause-before',
	  'perspective',
	  'perspective-origin',
	  'pointer-events',
	  'position',
	  'quotes',
	  'resize',
	  'rest',
	  'rest-after',
	  'rest-before',
	  'right',
	  'row-gap',
	  'scroll-margin',
	  'scroll-margin-block',
	  'scroll-margin-block-end',
	  'scroll-margin-block-start',
	  'scroll-margin-bottom',
	  'scroll-margin-inline',
	  'scroll-margin-inline-end',
	  'scroll-margin-inline-start',
	  'scroll-margin-left',
	  'scroll-margin-right',
	  'scroll-margin-top',
	  'scroll-padding',
	  'scroll-padding-block',
	  'scroll-padding-block-end',
	  'scroll-padding-block-start',
	  'scroll-padding-bottom',
	  'scroll-padding-inline',
	  'scroll-padding-inline-end',
	  'scroll-padding-inline-start',
	  'scroll-padding-left',
	  'scroll-padding-right',
	  'scroll-padding-top',
	  'scroll-snap-align',
	  'scroll-snap-stop',
	  'scroll-snap-type',
	  'scrollbar-color',
	  'scrollbar-gutter',
	  'scrollbar-width',
	  'shape-image-threshold',
	  'shape-margin',
	  'shape-outside',
	  'speak',
	  'speak-as',
	  'src', // @font-face
	  'tab-size',
	  'table-layout',
	  'text-align',
	  'text-align-all',
	  'text-align-last',
	  'text-combine-upright',
	  'text-decoration',
	  'text-decoration-color',
	  'text-decoration-line',
	  'text-decoration-style',
	  'text-emphasis',
	  'text-emphasis-color',
	  'text-emphasis-position',
	  'text-emphasis-style',
	  'text-indent',
	  'text-justify',
	  'text-orientation',
	  'text-overflow',
	  'text-rendering',
	  'text-shadow',
	  'text-transform',
	  'text-underline-position',
	  'top',
	  'transform',
	  'transform-box',
	  'transform-origin',
	  'transform-style',
	  'transition',
	  'transition-delay',
	  'transition-duration',
	  'transition-property',
	  'transition-timing-function',
	  'unicode-bidi',
	  'vertical-align',
	  'visibility',
	  'voice-balance',
	  'voice-duration',
	  'voice-family',
	  'voice-pitch',
	  'voice-range',
	  'voice-rate',
	  'voice-stress',
	  'voice-volume',
	  'white-space',
	  'widows',
	  'width',
	  'will-change',
	  'word-break',
	  'word-spacing',
	  'word-wrap',
	  'writing-mode',
	  'z-index'
	  // reverse makes sure longer attributes `font-weight` are matched fully
	  // instead of getting false positives on say `font`
	].reverse();

	/*
	Language: CSS
	Category: common, css, web
	Website: https://developer.mozilla.org/en-US/docs/Web/CSS
	*/


	/** @type LanguageFn */
	function css(hljs) {
	  const regex = hljs.regex;
	  const modes = MODES(hljs);
	  const VENDOR_PREFIX = { begin: /-(webkit|moz|ms|o)-(?=[a-z])/ };
	  const AT_MODIFIERS = "and or not only";
	  const AT_PROPERTY_RE = /@-?\w[\w]*(-\w+)*/; // @-webkit-keyframes
	  const IDENT_RE = '[a-zA-Z-][a-zA-Z0-9_-]*';
	  const STRINGS = [
	    hljs.APOS_STRING_MODE,
	    hljs.QUOTE_STRING_MODE
	  ];

	  return {
	    name: 'CSS',
	    case_insensitive: true,
	    illegal: /[=|'\$]/,
	    keywords: { keyframePosition: "from to" },
	    classNameAliases: {
	      // for visual continuity with `tag {}` and because we
	      // don't have a great class for this?
	      keyframePosition: "selector-tag" },
	    contains: [
	      modes.BLOCK_COMMENT,
	      VENDOR_PREFIX,
	      // to recognize keyframe 40% etc which are outside the scope of our
	      // attribute value mode
	      modes.CSS_NUMBER_MODE,
	      {
	        className: 'selector-id',
	        begin: /#[A-Za-z0-9_-]+/,
	        relevance: 0
	      },
	      {
	        className: 'selector-class',
	        begin: '\\.' + IDENT_RE,
	        relevance: 0
	      },
	      modes.ATTRIBUTE_SELECTOR_MODE,
	      {
	        className: 'selector-pseudo',
	        variants: [
	          { begin: ':(' + PSEUDO_CLASSES.join('|') + ')' },
	          { begin: ':(:)?(' + PSEUDO_ELEMENTS.join('|') + ')' }
	        ]
	      },
	      // we may actually need this (12/2020)
	      // { // pseudo-selector params
	      //   begin: /\(/,
	      //   end: /\)/,
	      //   contains: [ hljs.CSS_NUMBER_MODE ]
	      // },
	      modes.CSS_VARIABLE,
	      {
	        className: 'attribute',
	        begin: '\\b(' + ATTRIBUTES.join('|') + ')\\b'
	      },
	      // attribute values
	      {
	        begin: /:/,
	        end: /[;}{]/,
	        contains: [
	          modes.BLOCK_COMMENT,
	          modes.HEXCOLOR,
	          modes.IMPORTANT,
	          modes.CSS_NUMBER_MODE,
	          ...STRINGS,
	          // needed to highlight these as strings and to avoid issues with
	          // illegal characters that might be inside urls that would tigger the
	          // languages illegal stack
	          {
	            begin: /(url|data-uri)\(/,
	            end: /\)/,
	            relevance: 0, // from keywords
	            keywords: { built_in: "url data-uri" },
	            contains: [
	              ...STRINGS,
	              {
	                className: "string",
	                // any character other than `)` as in `url()` will be the start
	                // of a string, which ends with `)` (from the parent mode)
	                begin: /[^)]/,
	                endsWithParent: true,
	                excludeEnd: true
	              }
	            ]
	          },
	          modes.FUNCTION_DISPATCH
	        ]
	      },
	      {
	        begin: regex.lookahead(/@/),
	        end: '[{;]',
	        relevance: 0,
	        illegal: /:/, // break on Less variables @var: ...
	        contains: [
	          {
	            className: 'keyword',
	            begin: AT_PROPERTY_RE
	          },
	          {
	            begin: /\s/,
	            endsWithParent: true,
	            excludeEnd: true,
	            relevance: 0,
	            keywords: {
	              $pattern: /[a-z-]+/,
	              keyword: AT_MODIFIERS,
	              attribute: MEDIA_FEATURES.join(" ")
	            },
	            contains: [
	              {
	                begin: /[a-z-]+(?=:)/,
	                className: "attribute"
	              },
	              ...STRINGS,
	              modes.CSS_NUMBER_MODE
	            ]
	          }
	        ]
	      },
	      {
	        className: 'selector-tag',
	        begin: '\\b(' + TAGS.join('|') + ')\\b'
	      }
	    ]
	  };
	}

	css_1 = css;
	return css_1;
}

/*
Language: Markdown
Requires: xml.js
Author: John Crepezzi <john.crepezzi@gmail.com>
Website: https://daringfireball.net/projects/markdown/
Category: common, markup
*/

var markdown_1;
var hasRequiredMarkdown;

function requireMarkdown () {
	if (hasRequiredMarkdown) return markdown_1;
	hasRequiredMarkdown = 1;
	function markdown(hljs) {
	  const regex = hljs.regex;
	  const INLINE_HTML = {
	    begin: /<\/?[A-Za-z_]/,
	    end: '>',
	    subLanguage: 'xml',
	    relevance: 0
	  };
	  const HORIZONTAL_RULE = {
	    begin: '^[-\\*]{3,}',
	    end: '$'
	  };
	  const CODE = {
	    className: 'code',
	    variants: [
	      // TODO: fix to allow these to work with sublanguage also
	      { begin: '(`{3,})[^`](.|\\n)*?\\1`*[ ]*' },
	      { begin: '(~{3,})[^~](.|\\n)*?\\1~*[ ]*' },
	      // needed to allow markdown as a sublanguage to work
	      {
	        begin: '```',
	        end: '```+[ ]*$'
	      },
	      {
	        begin: '~~~',
	        end: '~~~+[ ]*$'
	      },
	      { begin: '`.+?`' },
	      {
	        begin: '(?=^( {4}|\\t))',
	        // use contains to gobble up multiple lines to allow the block to be whatever size
	        // but only have a single open/close tag vs one per line
	        contains: [
	          {
	            begin: '^( {4}|\\t)',
	            end: '(\\n)$'
	          }
	        ],
	        relevance: 0
	      }
	    ]
	  };
	  const LIST = {
	    className: 'bullet',
	    begin: '^[ \t]*([*+-]|(\\d+\\.))(?=\\s+)',
	    end: '\\s+',
	    excludeEnd: true
	  };
	  const LINK_REFERENCE = {
	    begin: /^\[[^\n]+\]:/,
	    returnBegin: true,
	    contains: [
	      {
	        className: 'symbol',
	        begin: /\[/,
	        end: /\]/,
	        excludeBegin: true,
	        excludeEnd: true
	      },
	      {
	        className: 'link',
	        begin: /:\s*/,
	        end: /$/,
	        excludeBegin: true
	      }
	    ]
	  };
	  const URL_SCHEME = /[A-Za-z][A-Za-z0-9+.-]*/;
	  const LINK = {
	    variants: [
	      // too much like nested array access in so many languages
	      // to have any real relevance
	      {
	        begin: /\[.+?\]\[.*?\]/,
	        relevance: 0
	      },
	      // popular internet URLs
	      {
	        begin: /\[.+?\]\(((data|javascript|mailto):|(?:http|ftp)s?:\/\/).*?\)/,
	        relevance: 2
	      },
	      {
	        begin: regex.concat(/\[.+?\]\(/, URL_SCHEME, /:\/\/.*?\)/),
	        relevance: 2
	      },
	      // relative urls
	      {
	        begin: /\[.+?\]\([./?&#].*?\)/,
	        relevance: 1
	      },
	      // whatever else, lower relevance (might not be a link at all)
	      {
	        begin: /\[.*?\]\(.*?\)/,
	        relevance: 0
	      }
	    ],
	    returnBegin: true,
	    contains: [
	      {
	        // empty strings for alt or link text
	        match: /\[(?=\])/ },
	      {
	        className: 'string',
	        relevance: 0,
	        begin: '\\[',
	        end: '\\]',
	        excludeBegin: true,
	        returnEnd: true
	      },
	      {
	        className: 'link',
	        relevance: 0,
	        begin: '\\]\\(',
	        end: '\\)',
	        excludeBegin: true,
	        excludeEnd: true
	      },
	      {
	        className: 'symbol',
	        relevance: 0,
	        begin: '\\]\\[',
	        end: '\\]',
	        excludeBegin: true,
	        excludeEnd: true
	      }
	    ]
	  };
	  const BOLD = {
	    className: 'strong',
	    contains: [], // defined later
	    variants: [
	      {
	        begin: /_{2}(?!\s)/,
	        end: /_{2}/
	      },
	      {
	        begin: /\*{2}(?!\s)/,
	        end: /\*{2}/
	      }
	    ]
	  };
	  const ITALIC = {
	    className: 'emphasis',
	    contains: [], // defined later
	    variants: [
	      {
	        begin: /\*(?![*\s])/,
	        end: /\*/
	      },
	      {
	        begin: /_(?![_\s])/,
	        end: /_/,
	        relevance: 0
	      }
	    ]
	  };

	  // 3 level deep nesting is not allowed because it would create confusion
	  // in cases like `***testing***` because where we don't know if the last
	  // `***` is starting a new bold/italic or finishing the last one
	  const BOLD_WITHOUT_ITALIC = hljs.inherit(BOLD, { contains: [] });
	  const ITALIC_WITHOUT_BOLD = hljs.inherit(ITALIC, { contains: [] });
	  BOLD.contains.push(ITALIC_WITHOUT_BOLD);
	  ITALIC.contains.push(BOLD_WITHOUT_ITALIC);

	  let CONTAINABLE = [
	    INLINE_HTML,
	    LINK
	  ];

	  [
	    BOLD,
	    ITALIC,
	    BOLD_WITHOUT_ITALIC,
	    ITALIC_WITHOUT_BOLD
	  ].forEach(m => {
	    m.contains = m.contains.concat(CONTAINABLE);
	  });

	  CONTAINABLE = CONTAINABLE.concat(BOLD, ITALIC);

	  const HEADER = {
	    className: 'section',
	    variants: [
	      {
	        begin: '^#{1,6}',
	        end: '$',
	        contains: CONTAINABLE
	      },
	      {
	        begin: '(?=^.+?\\n[=-]{2,}$)',
	        contains: [
	          { begin: '^[=-]*$' },
	          {
	            begin: '^',
	            end: "\\n",
	            contains: CONTAINABLE
	          }
	        ]
	      }
	    ]
	  };

	  const BLOCKQUOTE = {
	    className: 'quote',
	    begin: '^>\\s+',
	    contains: CONTAINABLE,
	    end: '$'
	  };

	  return {
	    name: 'Markdown',
	    aliases: [
	      'md',
	      'mkdown',
	      'mkd'
	    ],
	    contains: [
	      HEADER,
	      INLINE_HTML,
	      LIST,
	      BOLD,
	      ITALIC,
	      BLOCKQUOTE,
	      CODE,
	      HORIZONTAL_RULE,
	      LINK,
	      LINK_REFERENCE
	    ]
	  };
	}

	markdown_1 = markdown;
	return markdown_1;
}

/*
Language: Diff
Description: Unified and context diff
Author: Vasily Polovnyov <vast@whiteants.net>
Website: https://www.gnu.org/software/diffutils/
Category: common
*/

var diff_1;
var hasRequiredDiff;

function requireDiff () {
	if (hasRequiredDiff) return diff_1;
	hasRequiredDiff = 1;
	/** @type LanguageFn */
	function diff(hljs) {
	  const regex = hljs.regex;
	  return {
	    name: 'Diff',
	    aliases: [ 'patch' ],
	    contains: [
	      {
	        className: 'meta',
	        relevance: 10,
	        match: regex.either(
	          /^@@ +-\d+,\d+ +\+\d+,\d+ +@@/,
	          /^\*\*\* +\d+,\d+ +\*\*\*\*$/,
	          /^--- +\d+,\d+ +----$/
	        )
	      },
	      {
	        className: 'comment',
	        variants: [
	          {
	            begin: regex.either(
	              /Index: /,
	              /^index/,
	              /={3,}/,
	              /^-{3}/,
	              /^\*{3} /,
	              /^\+{3}/,
	              /^diff --git/
	            ),
	            end: /$/
	          },
	          { match: /^\*{15}$/ }
	        ]
	      },
	      {
	        className: 'addition',
	        begin: /^\+/,
	        end: /$/
	      },
	      {
	        className: 'deletion',
	        begin: /^-/,
	        end: /$/
	      },
	      {
	        className: 'addition',
	        begin: /^!/,
	        end: /$/
	      }
	    ]
	  };
	}

	diff_1 = diff;
	return diff_1;
}

/*
Language: Ruby
Description: Ruby is a dynamic, open source programming language with a focus on simplicity and productivity.
Website: https://www.ruby-lang.org/
Author: Anton Kovalyov <anton@kovalyov.net>
Contributors: Peter Leonov <gojpeg@yandex.ru>, Vasily Polovnyov <vast@whiteants.net>, Loren Segal <lsegal@soen.ca>, Pascal Hurni <phi@ruby-reactive.org>, Cedric Sohrauer <sohrauer@googlemail.com>
Category: common
*/

var ruby_1;
var hasRequiredRuby;

function requireRuby () {
	if (hasRequiredRuby) return ruby_1;
	hasRequiredRuby = 1;
	function ruby(hljs) {
	  const regex = hljs.regex;
	  const RUBY_METHOD_RE = '([a-zA-Z_]\\w*[!?=]?|[-+~]@|<<|>>|=~|===?|<=>|[<>]=?|\\*\\*|[-/+%^&*~`|]|\\[\\]=?)';
	  // TODO: move concepts like CAMEL_CASE into `modes.js`
	  const CLASS_NAME_RE = regex.either(
	    /\b([A-Z]+[a-z0-9]+)+/,
	    // ends in caps
	    /\b([A-Z]+[a-z0-9]+)+[A-Z]+/,
	  )
	  ;
	  const CLASS_NAME_WITH_NAMESPACE_RE = regex.concat(CLASS_NAME_RE, /(::\w+)*/);
	  // very popular ruby built-ins that one might even assume
	  // are actual keywords (despite that not being the case)
	  const PSEUDO_KWS = [
	    "include",
	    "extend",
	    "prepend",
	    "public",
	    "private",
	    "protected",
	    "raise",
	    "throw"
	  ];
	  const RUBY_KEYWORDS = {
	    "variable.constant": [
	      "__FILE__",
	      "__LINE__",
	      "__ENCODING__"
	    ],
	    "variable.language": [
	      "self",
	      "super",
	    ],
	    keyword: [
	      "alias",
	      "and",
	      "begin",
	      "BEGIN",
	      "break",
	      "case",
	      "class",
	      "defined",
	      "do",
	      "else",
	      "elsif",
	      "end",
	      "END",
	      "ensure",
	      "for",
	      "if",
	      "in",
	      "module",
	      "next",
	      "not",
	      "or",
	      "redo",
	      "require",
	      "rescue",
	      "retry",
	      "return",
	      "then",
	      "undef",
	      "unless",
	      "until",
	      "when",
	      "while",
	      "yield",
	      ...PSEUDO_KWS
	    ],
	    built_in: [
	      "proc",
	      "lambda",
	      "attr_accessor",
	      "attr_reader",
	      "attr_writer",
	      "define_method",
	      "private_constant",
	      "module_function"
	    ],
	    literal: [
	      "true",
	      "false",
	      "nil"
	    ]
	  };
	  const YARDOCTAG = {
	    className: 'doctag',
	    begin: '@[A-Za-z]+'
	  };
	  const IRB_OBJECT = {
	    begin: '#<',
	    end: '>'
	  };
	  const COMMENT_MODES = [
	    hljs.COMMENT(
	      '#',
	      '$',
	      { contains: [ YARDOCTAG ] }
	    ),
	    hljs.COMMENT(
	      '^=begin',
	      '^=end',
	      {
	        contains: [ YARDOCTAG ],
	        relevance: 10
	      }
	    ),
	    hljs.COMMENT('^__END__', hljs.MATCH_NOTHING_RE)
	  ];
	  const SUBST = {
	    className: 'subst',
	    begin: /#\{/,
	    end: /\}/,
	    keywords: RUBY_KEYWORDS
	  };
	  const STRING = {
	    className: 'string',
	    contains: [
	      hljs.BACKSLASH_ESCAPE,
	      SUBST
	    ],
	    variants: [
	      {
	        begin: /'/,
	        end: /'/
	      },
	      {
	        begin: /"/,
	        end: /"/
	      },
	      {
	        begin: /`/,
	        end: /`/
	      },
	      {
	        begin: /%[qQwWx]?\(/,
	        end: /\)/
	      },
	      {
	        begin: /%[qQwWx]?\[/,
	        end: /\]/
	      },
	      {
	        begin: /%[qQwWx]?\{/,
	        end: /\}/
	      },
	      {
	        begin: /%[qQwWx]?</,
	        end: />/
	      },
	      {
	        begin: /%[qQwWx]?\//,
	        end: /\//
	      },
	      {
	        begin: /%[qQwWx]?%/,
	        end: /%/
	      },
	      {
	        begin: /%[qQwWx]?-/,
	        end: /-/
	      },
	      {
	        begin: /%[qQwWx]?\|/,
	        end: /\|/
	      },
	      // in the following expressions, \B in the beginning suppresses recognition of ?-sequences
	      // where ? is the last character of a preceding identifier, as in: `func?4`
	      { begin: /\B\?(\\\d{1,3})/ },
	      { begin: /\B\?(\\x[A-Fa-f0-9]{1,2})/ },
	      { begin: /\B\?(\\u\{?[A-Fa-f0-9]{1,6}\}?)/ },
	      { begin: /\B\?(\\M-\\C-|\\M-\\c|\\c\\M-|\\M-|\\C-\\M-)[\x20-\x7e]/ },
	      { begin: /\B\?\\(c|C-)[\x20-\x7e]/ },
	      { begin: /\B\?\\?\S/ },
	      // heredocs
	      {
	        // this guard makes sure that we have an entire heredoc and not a false
	        // positive (auto-detect, etc.)
	        begin: regex.concat(
	          /<<[-~]?'?/,
	          regex.lookahead(/(\w+)(?=\W)[^\n]*\n(?:[^\n]*\n)*?\s*\1\b/)
	        ),
	        contains: [
	          hljs.END_SAME_AS_BEGIN({
	            begin: /(\w+)/,
	            end: /(\w+)/,
	            contains: [
	              hljs.BACKSLASH_ESCAPE,
	              SUBST
	            ]
	          })
	        ]
	      }
	    ]
	  };

	  // Ruby syntax is underdocumented, but this grammar seems to be accurate
	  // as of version 2.7.2 (confirmed with (irb and `Ripper.sexp(...)`)
	  // https://docs.ruby-lang.org/en/2.7.0/doc/syntax/literals_rdoc.html#label-Numbers
	  const decimal = '[1-9](_?[0-9])*|0';
	  const digits = '[0-9](_?[0-9])*';
	  const NUMBER = {
	    className: 'number',
	    relevance: 0,
	    variants: [
	      // decimal integer/float, optionally exponential or rational, optionally imaginary
	      { begin: `\\b(${decimal})(\\.(${digits}))?([eE][+-]?(${digits})|r)?i?\\b` },

	      // explicit decimal/binary/octal/hexadecimal integer,
	      // optionally rational and/or imaginary
	      { begin: "\\b0[dD][0-9](_?[0-9])*r?i?\\b" },
	      { begin: "\\b0[bB][0-1](_?[0-1])*r?i?\\b" },
	      { begin: "\\b0[oO][0-7](_?[0-7])*r?i?\\b" },
	      { begin: "\\b0[xX][0-9a-fA-F](_?[0-9a-fA-F])*r?i?\\b" },

	      // 0-prefixed implicit octal integer, optionally rational and/or imaginary
	      { begin: "\\b0(_?[0-7])+r?i?\\b" }
	    ]
	  };

	  const PARAMS = {
	    variants: [
	      {
	        match: /\(\)/,
	      },
	      {
	        className: 'params',
	        begin: /\(/,
	        end: /(?=\))/,
	        excludeBegin: true,
	        endsParent: true,
	        keywords: RUBY_KEYWORDS,
	      }
	    ]
	  };

	  const INCLUDE_EXTEND = {
	    match: [
	      /(include|extend)\s+/,
	      CLASS_NAME_WITH_NAMESPACE_RE
	    ],
	    scope: {
	      2: "title.class"
	    },
	    keywords: RUBY_KEYWORDS
	  };

	  const CLASS_DEFINITION = {
	    variants: [
	      {
	        match: [
	          /class\s+/,
	          CLASS_NAME_WITH_NAMESPACE_RE,
	          /\s+<\s+/,
	          CLASS_NAME_WITH_NAMESPACE_RE
	        ]
	      },
	      {
	        match: [
	          /\b(class|module)\s+/,
	          CLASS_NAME_WITH_NAMESPACE_RE
	        ]
	      }
	    ],
	    scope: {
	      2: "title.class",
	      4: "title.class.inherited"
	    },
	    keywords: RUBY_KEYWORDS
	  };

	  const UPPER_CASE_CONSTANT = {
	    relevance: 0,
	    match: /\b[A-Z][A-Z_0-9]+\b/,
	    className: "variable.constant"
	  };

	  const METHOD_DEFINITION = {
	    match: [
	      /def/, /\s+/,
	      RUBY_METHOD_RE
	    ],
	    scope: {
	      1: "keyword",
	      3: "title.function"
	    },
	    contains: [
	      PARAMS
	    ]
	  };

	  const OBJECT_CREATION = {
	    relevance: 0,
	    match: [
	      CLASS_NAME_WITH_NAMESPACE_RE,
	      /\.new[. (]/
	    ],
	    scope: {
	      1: "title.class"
	    }
	  };

	  // CamelCase
	  const CLASS_REFERENCE = {
	    relevance: 0,
	    match: CLASS_NAME_RE,
	    scope: "title.class"
	  };

	  const RUBY_DEFAULT_CONTAINS = [
	    STRING,
	    CLASS_DEFINITION,
	    INCLUDE_EXTEND,
	    OBJECT_CREATION,
	    UPPER_CASE_CONSTANT,
	    CLASS_REFERENCE,
	    METHOD_DEFINITION,
	    {
	      // swallow namespace qualifiers before symbols
	      begin: hljs.IDENT_RE + '::' },
	    {
	      className: 'symbol',
	      begin: hljs.UNDERSCORE_IDENT_RE + '(!|\\?)?:',
	      relevance: 0
	    },
	    {
	      className: 'symbol',
	      begin: ':(?!\\s)',
	      contains: [
	        STRING,
	        { begin: RUBY_METHOD_RE }
	      ],
	      relevance: 0
	    },
	    NUMBER,
	    {
	      // negative-look forward attempts to prevent false matches like:
	      // @ident@ or $ident$ that might indicate this is not ruby at all
	      className: "variable",
	      begin: '(\\$\\W)|((\\$|@@?)(\\w+))(?=[^@$?])' + `(?![A-Za-z])(?![@$?'])`
	    },
	    {
	      className: 'params',
	      begin: /\|/,
	      end: /\|/,
	      excludeBegin: true,
	      excludeEnd: true,
	      relevance: 0, // this could be a lot of things (in other languages) other than params
	      keywords: RUBY_KEYWORDS
	    },
	    { // regexp container
	      begin: '(' + hljs.RE_STARTERS_RE + '|unless)\\s*',
	      keywords: 'unless',
	      contains: [
	        {
	          className: 'regexp',
	          contains: [
	            hljs.BACKSLASH_ESCAPE,
	            SUBST
	          ],
	          illegal: /\n/,
	          variants: [
	            {
	              begin: '/',
	              end: '/[a-z]*'
	            },
	            {
	              begin: /%r\{/,
	              end: /\}[a-z]*/
	            },
	            {
	              begin: '%r\\(',
	              end: '\\)[a-z]*'
	            },
	            {
	              begin: '%r!',
	              end: '![a-z]*'
	            },
	            {
	              begin: '%r\\[',
	              end: '\\][a-z]*'
	            }
	          ]
	        }
	      ].concat(IRB_OBJECT, COMMENT_MODES),
	      relevance: 0
	    }
	  ].concat(IRB_OBJECT, COMMENT_MODES);

	  SUBST.contains = RUBY_DEFAULT_CONTAINS;
	  PARAMS.contains = RUBY_DEFAULT_CONTAINS;

	  // >>
	  // ?>
	  const SIMPLE_PROMPT = "[>?]>";
	  // irb(main):001:0>
	  const DEFAULT_PROMPT = "[\\w#]+\\(\\w+\\):\\d+:\\d+[>*]";
	  const RVM_PROMPT = "(\\w+-)?\\d+\\.\\d+\\.\\d+(p\\d+)?[^\\d][^>]+>";

	  const IRB_DEFAULT = [
	    {
	      begin: /^\s*=>/,
	      starts: {
	        end: '$',
	        contains: RUBY_DEFAULT_CONTAINS
	      }
	    },
	    {
	      className: 'meta.prompt',
	      begin: '^(' + SIMPLE_PROMPT + "|" + DEFAULT_PROMPT + '|' + RVM_PROMPT + ')(?=[ ])',
	      starts: {
	        end: '$',
	        keywords: RUBY_KEYWORDS,
	        contains: RUBY_DEFAULT_CONTAINS
	      }
	    }
	  ];

	  COMMENT_MODES.unshift(IRB_OBJECT);

	  return {
	    name: 'Ruby',
	    aliases: [
	      'rb',
	      'gemspec',
	      'podspec',
	      'thor',
	      'irb'
	    ],
	    keywords: RUBY_KEYWORDS,
	    illegal: /\/\*/,
	    contains: [ hljs.SHEBANG({ binary: "ruby" }) ]
	      .concat(IRB_DEFAULT)
	      .concat(COMMENT_MODES)
	      .concat(RUBY_DEFAULT_CONTAINS)
	  };
	}

	ruby_1 = ruby;
	return ruby_1;
}

/*
Language: Go
Author: Stephan Kountso aka StepLg <steplg@gmail.com>
Contributors: Evgeny Stepanischev <imbolk@gmail.com>
Description: Google go language (golang). For info about language
Website: http://golang.org/
Category: common, system
*/

var go_1;
var hasRequiredGo;

function requireGo () {
	if (hasRequiredGo) return go_1;
	hasRequiredGo = 1;
	function go(hljs) {
	  const LITERALS = [
	    "true",
	    "false",
	    "iota",
	    "nil"
	  ];
	  const BUILT_INS = [
	    "append",
	    "cap",
	    "close",
	    "complex",
	    "copy",
	    "imag",
	    "len",
	    "make",
	    "new",
	    "panic",
	    "print",
	    "println",
	    "real",
	    "recover",
	    "delete"
	  ];
	  const TYPES = [
	    "bool",
	    "byte",
	    "complex64",
	    "complex128",
	    "error",
	    "float32",
	    "float64",
	    "int8",
	    "int16",
	    "int32",
	    "int64",
	    "string",
	    "uint8",
	    "uint16",
	    "uint32",
	    "uint64",
	    "int",
	    "uint",
	    "uintptr",
	    "rune"
	  ];
	  const KWS = [
	    "break",
	    "case",
	    "chan",
	    "const",
	    "continue",
	    "default",
	    "defer",
	    "else",
	    "fallthrough",
	    "for",
	    "func",
	    "go",
	    "goto",
	    "if",
	    "import",
	    "interface",
	    "map",
	    "package",
	    "range",
	    "return",
	    "select",
	    "struct",
	    "switch",
	    "type",
	    "var",
	  ];
	  const KEYWORDS = {
	    keyword: KWS,
	    type: TYPES,
	    literal: LITERALS,
	    built_in: BUILT_INS
	  };
	  return {
	    name: 'Go',
	    aliases: [ 'golang' ],
	    keywords: KEYWORDS,
	    illegal: '</',
	    contains: [
	      hljs.C_LINE_COMMENT_MODE,
	      hljs.C_BLOCK_COMMENT_MODE,
	      {
	        className: 'string',
	        variants: [
	          hljs.QUOTE_STRING_MODE,
	          hljs.APOS_STRING_MODE,
	          {
	            begin: '`',
	            end: '`'
	          }
	        ]
	      },
	      {
	        className: 'number',
	        variants: [
	          {
	            begin: hljs.C_NUMBER_RE + '[i]',
	            relevance: 1
	          },
	          hljs.C_NUMBER_MODE
	        ]
	      },
	      { begin: /:=/ // relevance booster
	      },
	      {
	        className: 'function',
	        beginKeywords: 'func',
	        end: '\\s*(\\{|$)',
	        excludeEnd: true,
	        contains: [
	          hljs.TITLE_MODE,
	          {
	            className: 'params',
	            begin: /\(/,
	            end: /\)/,
	            endsParent: true,
	            keywords: KEYWORDS,
	            illegal: /["']/
	          }
	        ]
	      }
	    ]
	  };
	}

	go_1 = go;
	return go_1;
}

/*
 Language: GraphQL
 Author: John Foster (GH jf990), and others
 Description: GraphQL is a query language for APIs
 Category: web, common
*/

var graphql_1;
var hasRequiredGraphql;

function requireGraphql () {
	if (hasRequiredGraphql) return graphql_1;
	hasRequiredGraphql = 1;
	/** @type LanguageFn */
	function graphql(hljs) {
	  const regex = hljs.regex;
	  const GQL_NAME = /[_A-Za-z][_0-9A-Za-z]*/;
	  return {
	    name: "GraphQL",
	    aliases: [ "gql" ],
	    case_insensitive: true,
	    disableAutodetect: false,
	    keywords: {
	      keyword: [
	        "query",
	        "mutation",
	        "subscription",
	        "type",
	        "input",
	        "schema",
	        "directive",
	        "interface",
	        "union",
	        "scalar",
	        "fragment",
	        "enum",
	        "on"
	      ],
	      literal: [
	        "true",
	        "false",
	        "null"
	      ]
	    },
	    contains: [
	      hljs.HASH_COMMENT_MODE,
	      hljs.QUOTE_STRING_MODE,
	      hljs.NUMBER_MODE,
	      {
	        scope: "punctuation",
	        match: /[.]{3}/,
	        relevance: 0
	      },
	      {
	        scope: "punctuation",
	        begin: /[\!\(\)\:\=\[\]\{\|\}]{1}/,
	        relevance: 0
	      },
	      {
	        scope: "variable",
	        begin: /\$/,
	        end: /\W/,
	        excludeEnd: true,
	        relevance: 0
	      },
	      {
	        scope: "meta",
	        match: /@\w+/,
	        excludeEnd: true
	      },
	      {
	        scope: "symbol",
	        begin: regex.concat(GQL_NAME, regex.lookahead(/\s*:/)),
	        relevance: 0
	      }
	    ],
	    illegal: [
	      /[;<']/,
	      /BEGIN/
	    ]
	  };
	}

	graphql_1 = graphql;
	return graphql_1;
}

/*
Language: TOML, also INI
Description: TOML aims to be a minimal configuration file format that's easy to read due to obvious semantics.
Contributors: Guillaume Gomez <guillaume1.gomez@gmail.com>
Category: common, config
Website: https://github.com/toml-lang/toml
*/

var ini_1;
var hasRequiredIni;

function requireIni () {
	if (hasRequiredIni) return ini_1;
	hasRequiredIni = 1;
	function ini(hljs) {
	  const regex = hljs.regex;
	  const NUMBERS = {
	    className: 'number',
	    relevance: 0,
	    variants: [
	      { begin: /([+-]+)?[\d]+_[\d_]+/ },
	      { begin: hljs.NUMBER_RE }
	    ]
	  };
	  const COMMENTS = hljs.COMMENT();
	  COMMENTS.variants = [
	    {
	      begin: /;/,
	      end: /$/
	    },
	    {
	      begin: /#/,
	      end: /$/
	    }
	  ];
	  const VARIABLES = {
	    className: 'variable',
	    variants: [
	      { begin: /\$[\w\d"][\w\d_]*/ },
	      { begin: /\$\{(.*?)\}/ }
	    ]
	  };
	  const LITERALS = {
	    className: 'literal',
	    begin: /\bon|off|true|false|yes|no\b/
	  };
	  const STRINGS = {
	    className: "string",
	    contains: [ hljs.BACKSLASH_ESCAPE ],
	    variants: [
	      {
	        begin: "'''",
	        end: "'''",
	        relevance: 10
	      },
	      {
	        begin: '"""',
	        end: '"""',
	        relevance: 10
	      },
	      {
	        begin: '"',
	        end: '"'
	      },
	      {
	        begin: "'",
	        end: "'"
	      }
	    ]
	  };
	  const ARRAY = {
	    begin: /\[/,
	    end: /\]/,
	    contains: [
	      COMMENTS,
	      LITERALS,
	      VARIABLES,
	      STRINGS,
	      NUMBERS,
	      'self'
	    ],
	    relevance: 0
	  };

	  const BARE_KEY = /[A-Za-z0-9_-]+/;
	  const QUOTED_KEY_DOUBLE_QUOTE = /"(\\"|[^"])*"/;
	  const QUOTED_KEY_SINGLE_QUOTE = /'[^']*'/;
	  const ANY_KEY = regex.either(
	    BARE_KEY, QUOTED_KEY_DOUBLE_QUOTE, QUOTED_KEY_SINGLE_QUOTE
	  );
	  const DOTTED_KEY = regex.concat(
	    ANY_KEY, '(\\s*\\.\\s*', ANY_KEY, ')*',
	    regex.lookahead(/\s*=\s*[^#\s]/)
	  );

	  return {
	    name: 'TOML, also INI',
	    aliases: [ 'toml' ],
	    case_insensitive: true,
	    illegal: /\S/,
	    contains: [
	      COMMENTS,
	      {
	        className: 'section',
	        begin: /\[+/,
	        end: /\]+/
	      },
	      {
	        begin: DOTTED_KEY,
	        className: 'attr',
	        starts: {
	          end: /$/,
	          contains: [
	            COMMENTS,
	            ARRAY,
	            LITERALS,
	            VARIABLES,
	            STRINGS,
	            NUMBERS
	          ]
	        }
	      }
	    ]
	  };
	}

	ini_1 = ini;
	return ini_1;
}

var java_1;
var hasRequiredJava;

function requireJava () {
	if (hasRequiredJava) return java_1;
	hasRequiredJava = 1;
	// https://docs.oracle.com/javase/specs/jls/se15/html/jls-3.html#jls-3.10
	var decimalDigits = '[0-9](_*[0-9])*';
	var frac = `\\.(${decimalDigits})`;
	var hexDigits = '[0-9a-fA-F](_*[0-9a-fA-F])*';
	var NUMERIC = {
	  className: 'number',
	  variants: [
	    // DecimalFloatingPointLiteral
	    // including ExponentPart
	    { begin: `(\\b(${decimalDigits})((${frac})|\\.)?|(${frac}))` +
	      `[eE][+-]?(${decimalDigits})[fFdD]?\\b` },
	    // excluding ExponentPart
	    { begin: `\\b(${decimalDigits})((${frac})[fFdD]?\\b|\\.([fFdD]\\b)?)` },
	    { begin: `(${frac})[fFdD]?\\b` },
	    { begin: `\\b(${decimalDigits})[fFdD]\\b` },

	    // HexadecimalFloatingPointLiteral
	    { begin: `\\b0[xX]((${hexDigits})\\.?|(${hexDigits})?\\.(${hexDigits}))` +
	      `[pP][+-]?(${decimalDigits})[fFdD]?\\b` },

	    // DecimalIntegerLiteral
	    { begin: '\\b(0|[1-9](_*[0-9])*)[lL]?\\b' },

	    // HexIntegerLiteral
	    { begin: `\\b0[xX](${hexDigits})[lL]?\\b` },

	    // OctalIntegerLiteral
	    { begin: '\\b0(_*[0-7])*[lL]?\\b' },

	    // BinaryIntegerLiteral
	    { begin: '\\b0[bB][01](_*[01])*[lL]?\\b' },
	  ],
	  relevance: 0
	};

	/*
	Language: Java
	Author: Vsevolod Solovyov <vsevolod.solovyov@gmail.com>
	Category: common, enterprise
	Website: https://www.java.com/
	*/


	/**
	 * Allows recursive regex expressions to a given depth
	 *
	 * ie: recurRegex("(abc~~~)", /~~~/g, 2) becomes:
	 * (abc(abc(abc)))
	 *
	 * @param {string} re
	 * @param {RegExp} substitution (should be a g mode regex)
	 * @param {number} depth
	 * @returns {string}``
	 */
	function recurRegex(re, substitution, depth) {
	  if (depth === -1) return "";

	  return re.replace(substitution, _ => {
	    return recurRegex(re, substitution, depth - 1);
	  });
	}

	/** @type LanguageFn */
	function java(hljs) {
	  const regex = hljs.regex;
	  const JAVA_IDENT_RE = '[\u00C0-\u02B8a-zA-Z_$][\u00C0-\u02B8a-zA-Z_$0-9]*';
	  const GENERIC_IDENT_RE = JAVA_IDENT_RE
	    + recurRegex('(?:<' + JAVA_IDENT_RE + '~~~(?:\\s*,\\s*' + JAVA_IDENT_RE + '~~~)*>)?', /~~~/g, 2);
	  const MAIN_KEYWORDS = [
	    'synchronized',
	    'abstract',
	    'private',
	    'var',
	    'static',
	    'if',
	    'const ',
	    'for',
	    'while',
	    'strictfp',
	    'finally',
	    'protected',
	    'import',
	    'native',
	    'final',
	    'void',
	    'enum',
	    'else',
	    'break',
	    'transient',
	    'catch',
	    'instanceof',
	    'volatile',
	    'case',
	    'assert',
	    'package',
	    'default',
	    'public',
	    'try',
	    'switch',
	    'continue',
	    'throws',
	    'protected',
	    'public',
	    'private',
	    'module',
	    'requires',
	    'exports',
	    'do',
	    'sealed',
	    'yield',
	    'permits'
	  ];

	  const BUILT_INS = [
	    'super',
	    'this'
	  ];

	  const LITERALS = [
	    'false',
	    'true',
	    'null'
	  ];

	  const TYPES = [
	    'char',
	    'boolean',
	    'long',
	    'float',
	    'int',
	    'byte',
	    'short',
	    'double'
	  ];

	  const KEYWORDS = {
	    keyword: MAIN_KEYWORDS,
	    literal: LITERALS,
	    type: TYPES,
	    built_in: BUILT_INS
	  };

	  const ANNOTATION = {
	    className: 'meta',
	    begin: '@' + JAVA_IDENT_RE,
	    contains: [
	      {
	        begin: /\(/,
	        end: /\)/,
	        contains: [ "self" ] // allow nested () inside our annotation
	      }
	    ]
	  };
	  const PARAMS = {
	    className: 'params',
	    begin: /\(/,
	    end: /\)/,
	    keywords: KEYWORDS,
	    relevance: 0,
	    contains: [ hljs.C_BLOCK_COMMENT_MODE ],
	    endsParent: true
	  };

	  return {
	    name: 'Java',
	    aliases: [ 'jsp' ],
	    keywords: KEYWORDS,
	    illegal: /<\/|#/,
	    contains: [
	      hljs.COMMENT(
	        '/\\*\\*',
	        '\\*/',
	        {
	          relevance: 0,
	          contains: [
	            {
	              // eat up @'s in emails to prevent them to be recognized as doctags
	              begin: /\w+@/,
	              relevance: 0
	            },
	            {
	              className: 'doctag',
	              begin: '@[A-Za-z]+'
	            }
	          ]
	        }
	      ),
	      // relevance boost
	      {
	        begin: /import java\.[a-z]+\./,
	        keywords: "import",
	        relevance: 2
	      },
	      hljs.C_LINE_COMMENT_MODE,
	      hljs.C_BLOCK_COMMENT_MODE,
	      {
	        begin: /"""/,
	        end: /"""/,
	        className: "string",
	        contains: [ hljs.BACKSLASH_ESCAPE ]
	      },
	      hljs.APOS_STRING_MODE,
	      hljs.QUOTE_STRING_MODE,
	      {
	        match: [
	          /\b(?:class|interface|enum|extends|implements|new)/,
	          /\s+/,
	          JAVA_IDENT_RE
	        ],
	        className: {
	          1: "keyword",
	          3: "title.class"
	        }
	      },
	      {
	        // Exceptions for hyphenated keywords
	        match: /non-sealed/,
	        scope: "keyword"
	      },
	      {
	        begin: [
	          regex.concat(/(?!else)/, JAVA_IDENT_RE),
	          /\s+/,
	          JAVA_IDENT_RE,
	          /\s+/,
	          /=(?!=)/
	        ],
	        className: {
	          1: "type",
	          3: "variable",
	          5: "operator"
	        }
	      },
	      {
	        begin: [
	          /record/,
	          /\s+/,
	          JAVA_IDENT_RE
	        ],
	        className: {
	          1: "keyword",
	          3: "title.class"
	        },
	        contains: [
	          PARAMS,
	          hljs.C_LINE_COMMENT_MODE,
	          hljs.C_BLOCK_COMMENT_MODE
	        ]
	      },
	      {
	        // Expression keywords prevent 'keyword Name(...)' from being
	        // recognized as a function definition
	        beginKeywords: 'new throw return else',
	        relevance: 0
	      },
	      {
	        begin: [
	          '(?:' + GENERIC_IDENT_RE + '\\s+)',
	          hljs.UNDERSCORE_IDENT_RE,
	          /\s*(?=\()/
	        ],
	        className: { 2: "title.function" },
	        keywords: KEYWORDS,
	        contains: [
	          {
	            className: 'params',
	            begin: /\(/,
	            end: /\)/,
	            keywords: KEYWORDS,
	            relevance: 0,
	            contains: [
	              ANNOTATION,
	              hljs.APOS_STRING_MODE,
	              hljs.QUOTE_STRING_MODE,
	              NUMERIC,
	              hljs.C_BLOCK_COMMENT_MODE
	            ]
	          },
	          hljs.C_LINE_COMMENT_MODE,
	          hljs.C_BLOCK_COMMENT_MODE
	        ]
	      },
	      NUMERIC,
	      ANNOTATION
	    ]
	  };
	}

	java_1 = java;
	return java_1;
}

var javascript_1;
var hasRequiredJavascript;

function requireJavascript () {
	if (hasRequiredJavascript) return javascript_1;
	hasRequiredJavascript = 1;
	const IDENT_RE = '[A-Za-z$_][0-9A-Za-z$_]*';
	const KEYWORDS = [
	  "as", // for exports
	  "in",
	  "of",
	  "if",
	  "for",
	  "while",
	  "finally",
	  "var",
	  "new",
	  "function",
	  "do",
	  "return",
	  "void",
	  "else",
	  "break",
	  "catch",
	  "instanceof",
	  "with",
	  "throw",
	  "case",
	  "default",
	  "try",
	  "switch",
	  "continue",
	  "typeof",
	  "delete",
	  "let",
	  "yield",
	  "const",
	  "class",
	  // JS handles these with a special rule
	  // "get",
	  // "set",
	  "debugger",
	  "async",
	  "await",
	  "static",
	  "import",
	  "from",
	  "export",
	  "extends"
	];
	const LITERALS = [
	  "true",
	  "false",
	  "null",
	  "undefined",
	  "NaN",
	  "Infinity"
	];

	// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects
	const TYPES = [
	  // Fundamental objects
	  "Object",
	  "Function",
	  "Boolean",
	  "Symbol",
	  // numbers and dates
	  "Math",
	  "Date",
	  "Number",
	  "BigInt",
	  // text
	  "String",
	  "RegExp",
	  // Indexed collections
	  "Array",
	  "Float32Array",
	  "Float64Array",
	  "Int8Array",
	  "Uint8Array",
	  "Uint8ClampedArray",
	  "Int16Array",
	  "Int32Array",
	  "Uint16Array",
	  "Uint32Array",
	  "BigInt64Array",
	  "BigUint64Array",
	  // Keyed collections
	  "Set",
	  "Map",
	  "WeakSet",
	  "WeakMap",
	  // Structured data
	  "ArrayBuffer",
	  "SharedArrayBuffer",
	  "Atomics",
	  "DataView",
	  "JSON",
	  // Control abstraction objects
	  "Promise",
	  "Generator",
	  "GeneratorFunction",
	  "AsyncFunction",
	  // Reflection
	  "Reflect",
	  "Proxy",
	  // Internationalization
	  "Intl",
	  // WebAssembly
	  "WebAssembly"
	];

	const ERROR_TYPES = [
	  "Error",
	  "EvalError",
	  "InternalError",
	  "RangeError",
	  "ReferenceError",
	  "SyntaxError",
	  "TypeError",
	  "URIError"
	];

	const BUILT_IN_GLOBALS = [
	  "setInterval",
	  "setTimeout",
	  "clearInterval",
	  "clearTimeout",

	  "require",
	  "exports",

	  "eval",
	  "isFinite",
	  "isNaN",
	  "parseFloat",
	  "parseInt",
	  "decodeURI",
	  "decodeURIComponent",
	  "encodeURI",
	  "encodeURIComponent",
	  "escape",
	  "unescape"
	];

	const BUILT_IN_VARIABLES = [
	  "arguments",
	  "this",
	  "super",
	  "console",
	  "window",
	  "document",
	  "localStorage",
	  "sessionStorage",
	  "module",
	  "global" // Node.js
	];

	const BUILT_INS = [].concat(
	  BUILT_IN_GLOBALS,
	  TYPES,
	  ERROR_TYPES
	);

	/*
	Language: JavaScript
	Description: JavaScript (JS) is a lightweight, interpreted, or just-in-time compiled programming language with first-class functions.
	Category: common, scripting, web
	Website: https://developer.mozilla.org/en-US/docs/Web/JavaScript
	*/


	/** @type LanguageFn */
	function javascript(hljs) {
	  const regex = hljs.regex;
	  /**
	   * Takes a string like "<Booger" and checks to see
	   * if we can find a matching "</Booger" later in the
	   * content.
	   * @param {RegExpMatchArray} match
	   * @param {{after:number}} param1
	   */
	  const hasClosingTag = (match, { after }) => {
	    const tag = "</" + match[0].slice(1);
	    const pos = match.input.indexOf(tag, after);
	    return pos !== -1;
	  };

	  const IDENT_RE$1 = IDENT_RE;
	  const FRAGMENT = {
	    begin: '<>',
	    end: '</>'
	  };
	  // to avoid some special cases inside isTrulyOpeningTag
	  const XML_SELF_CLOSING = /<[A-Za-z0-9\\._:-]+\s*\/>/;
	  const XML_TAG = {
	    begin: /<[A-Za-z0-9\\._:-]+/,
	    end: /\/[A-Za-z0-9\\._:-]+>|\/>/,
	    /**
	     * @param {RegExpMatchArray} match
	     * @param {CallbackResponse} response
	     */
	    isTrulyOpeningTag: (match, response) => {
	      const afterMatchIndex = match[0].length + match.index;
	      const nextChar = match.input[afterMatchIndex];
	      if (
	        // HTML should not include another raw `<` inside a tag
	        // nested type?
	        // `<Array<Array<number>>`, etc.
	        nextChar === "<" ||
	        // the , gives away that this is not HTML
	        // `<T, A extends keyof T, V>`
	        nextChar === ","
	        ) {
	        response.ignoreMatch();
	        return;
	      }

	      // `<something>`
	      // Quite possibly a tag, lets look for a matching closing tag...
	      if (nextChar === ">") {
	        // if we cannot find a matching closing tag, then we
	        // will ignore it
	        if (!hasClosingTag(match, { after: afterMatchIndex })) {
	          response.ignoreMatch();
	        }
	      }

	      // `<blah />` (self-closing)
	      // handled by simpleSelfClosing rule

	      let m;
	      const afterMatch = match.input.substring(afterMatchIndex);

	      // some more template typing stuff
	      //  <T = any>(key?: string) => Modify<
	      if ((m = afterMatch.match(/^\s*=/))) {
	        response.ignoreMatch();
	        return;
	      }

	      // `<From extends string>`
	      // technically this could be HTML, but it smells like a type
	      // NOTE: This is ugh, but added specifically for https://github.com/highlightjs/highlight.js/issues/3276
	      if ((m = afterMatch.match(/^\s+extends\s+/))) {
	        if (m.index === 0) {
	          response.ignoreMatch();
	          // eslint-disable-next-line no-useless-return
	          return;
	        }
	      }
	    }
	  };
	  const KEYWORDS$1 = {
	    $pattern: IDENT_RE,
	    keyword: KEYWORDS,
	    literal: LITERALS,
	    built_in: BUILT_INS,
	    "variable.language": BUILT_IN_VARIABLES
	  };

	  // https://tc39.es/ecma262/#sec-literals-numeric-literals
	  const decimalDigits = '[0-9](_?[0-9])*';
	  const frac = `\\.(${decimalDigits})`;
	  // DecimalIntegerLiteral, including Annex B NonOctalDecimalIntegerLiteral
	  // https://tc39.es/ecma262/#sec-additional-syntax-numeric-literals
	  const decimalInteger = `0|[1-9](_?[0-9])*|0[0-7]*[89][0-9]*`;
	  const NUMBER = {
	    className: 'number',
	    variants: [
	      // DecimalLiteral
	      { begin: `(\\b(${decimalInteger})((${frac})|\\.)?|(${frac}))` +
	        `[eE][+-]?(${decimalDigits})\\b` },
	      { begin: `\\b(${decimalInteger})\\b((${frac})\\b|\\.)?|(${frac})\\b` },

	      // DecimalBigIntegerLiteral
	      { begin: `\\b(0|[1-9](_?[0-9])*)n\\b` },

	      // NonDecimalIntegerLiteral
	      { begin: "\\b0[xX][0-9a-fA-F](_?[0-9a-fA-F])*n?\\b" },
	      { begin: "\\b0[bB][0-1](_?[0-1])*n?\\b" },
	      { begin: "\\b0[oO][0-7](_?[0-7])*n?\\b" },

	      // LegacyOctalIntegerLiteral (does not include underscore separators)
	      // https://tc39.es/ecma262/#sec-additional-syntax-numeric-literals
	      { begin: "\\b0[0-7]+n?\\b" },
	    ],
	    relevance: 0
	  };

	  const SUBST = {
	    className: 'subst',
	    begin: '\\$\\{',
	    end: '\\}',
	    keywords: KEYWORDS$1,
	    contains: [] // defined later
	  };
	  const HTML_TEMPLATE = {
	    begin: 'html`',
	    end: '',
	    starts: {
	      end: '`',
	      returnEnd: false,
	      contains: [
	        hljs.BACKSLASH_ESCAPE,
	        SUBST
	      ],
	      subLanguage: 'xml'
	    }
	  };
	  const CSS_TEMPLATE = {
	    begin: 'css`',
	    end: '',
	    starts: {
	      end: '`',
	      returnEnd: false,
	      contains: [
	        hljs.BACKSLASH_ESCAPE,
	        SUBST
	      ],
	      subLanguage: 'css'
	    }
	  };
	  const GRAPHQL_TEMPLATE = {
	    begin: 'gql`',
	    end: '',
	    starts: {
	      end: '`',
	      returnEnd: false,
	      contains: [
	        hljs.BACKSLASH_ESCAPE,
	        SUBST
	      ],
	      subLanguage: 'graphql'
	    }
	  };
	  const TEMPLATE_STRING = {
	    className: 'string',
	    begin: '`',
	    end: '`',
	    contains: [
	      hljs.BACKSLASH_ESCAPE,
	      SUBST
	    ]
	  };
	  const JSDOC_COMMENT = hljs.COMMENT(
	    /\/\*\*(?!\/)/,
	    '\\*/',
	    {
	      relevance: 0,
	      contains: [
	        {
	          begin: '(?=@[A-Za-z]+)',
	          relevance: 0,
	          contains: [
	            {
	              className: 'doctag',
	              begin: '@[A-Za-z]+'
	            },
	            {
	              className: 'type',
	              begin: '\\{',
	              end: '\\}',
	              excludeEnd: true,
	              excludeBegin: true,
	              relevance: 0
	            },
	            {
	              className: 'variable',
	              begin: IDENT_RE$1 + '(?=\\s*(-)|$)',
	              endsParent: true,
	              relevance: 0
	            },
	            // eat spaces (not newlines) so we can find
	            // types or variables
	            {
	              begin: /(?=[^\n])\s/,
	              relevance: 0
	            }
	          ]
	        }
	      ]
	    }
	  );
	  const COMMENT = {
	    className: "comment",
	    variants: [
	      JSDOC_COMMENT,
	      hljs.C_BLOCK_COMMENT_MODE,
	      hljs.C_LINE_COMMENT_MODE
	    ]
	  };
	  const SUBST_INTERNALS = [
	    hljs.APOS_STRING_MODE,
	    hljs.QUOTE_STRING_MODE,
	    HTML_TEMPLATE,
	    CSS_TEMPLATE,
	    GRAPHQL_TEMPLATE,
	    TEMPLATE_STRING,
	    // Skip numbers when they are part of a variable name
	    { match: /\$\d+/ },
	    NUMBER,
	    // This is intentional:
	    // See https://github.com/highlightjs/highlight.js/issues/3288
	    // hljs.REGEXP_MODE
	  ];
	  SUBST.contains = SUBST_INTERNALS
	    .concat({
	      // we need to pair up {} inside our subst to prevent
	      // it from ending too early by matching another }
	      begin: /\{/,
	      end: /\}/,
	      keywords: KEYWORDS$1,
	      contains: [
	        "self"
	      ].concat(SUBST_INTERNALS)
	    });
	  const SUBST_AND_COMMENTS = [].concat(COMMENT, SUBST.contains);
	  const PARAMS_CONTAINS = SUBST_AND_COMMENTS.concat([
	    // eat recursive parens in sub expressions
	    {
	      begin: /\(/,
	      end: /\)/,
	      keywords: KEYWORDS$1,
	      contains: ["self"].concat(SUBST_AND_COMMENTS)
	    }
	  ]);
	  const PARAMS = {
	    className: 'params',
	    begin: /\(/,
	    end: /\)/,
	    excludeBegin: true,
	    excludeEnd: true,
	    keywords: KEYWORDS$1,
	    contains: PARAMS_CONTAINS
	  };

	  // ES6 classes
	  const CLASS_OR_EXTENDS = {
	    variants: [
	      // class Car extends vehicle
	      {
	        match: [
	          /class/,
	          /\s+/,
	          IDENT_RE$1,
	          /\s+/,
	          /extends/,
	          /\s+/,
	          regex.concat(IDENT_RE$1, "(", regex.concat(/\./, IDENT_RE$1), ")*")
	        ],
	        scope: {
	          1: "keyword",
	          3: "title.class",
	          5: "keyword",
	          7: "title.class.inherited"
	        }
	      },
	      // class Car
	      {
	        match: [
	          /class/,
	          /\s+/,
	          IDENT_RE$1
	        ],
	        scope: {
	          1: "keyword",
	          3: "title.class"
	        }
	      },

	    ]
	  };

	  const CLASS_REFERENCE = {
	    relevance: 0,
	    match:
	    regex.either(
	      // Hard coded exceptions
	      /\bJSON/,
	      // Float32Array, OutT
	      /\b[A-Z][a-z]+([A-Z][a-z]*|\d)*/,
	      // CSSFactory, CSSFactoryT
	      /\b[A-Z]{2,}([A-Z][a-z]+|\d)+([A-Z][a-z]*)*/,
	      // FPs, FPsT
	      /\b[A-Z]{2,}[a-z]+([A-Z][a-z]+|\d)*([A-Z][a-z]*)*/,
	      // P
	      // single letters are not highlighted
	      // BLAH
	      // this will be flagged as a UPPER_CASE_CONSTANT instead
	    ),
	    className: "title.class",
	    keywords: {
	      _: [
	        // se we still get relevance credit for JS library classes
	        ...TYPES,
	        ...ERROR_TYPES
	      ]
	    }
	  };

	  const USE_STRICT = {
	    label: "use_strict",
	    className: 'meta',
	    relevance: 10,
	    begin: /^\s*['"]use (strict|asm)['"]/
	  };

	  const FUNCTION_DEFINITION = {
	    variants: [
	      {
	        match: [
	          /function/,
	          /\s+/,
	          IDENT_RE$1,
	          /(?=\s*\()/
	        ]
	      },
	      // anonymous function
	      {
	        match: [
	          /function/,
	          /\s*(?=\()/
	        ]
	      }
	    ],
	    className: {
	      1: "keyword",
	      3: "title.function"
	    },
	    label: "func.def",
	    contains: [ PARAMS ],
	    illegal: /%/
	  };

	  const UPPER_CASE_CONSTANT = {
	    relevance: 0,
	    match: /\b[A-Z][A-Z_0-9]+\b/,
	    className: "variable.constant"
	  };

	  function noneOf(list) {
	    return regex.concat("(?!", list.join("|"), ")");
	  }

	  const FUNCTION_CALL = {
	    match: regex.concat(
	      /\b/,
	      noneOf([
	        ...BUILT_IN_GLOBALS,
	        "super",
	        "import"
	      ]),
	      IDENT_RE$1, regex.lookahead(/\(/)),
	    className: "title.function",
	    relevance: 0
	  };

	  const PROPERTY_ACCESS = {
	    begin: regex.concat(/\./, regex.lookahead(
	      regex.concat(IDENT_RE$1, /(?![0-9A-Za-z$_(])/)
	    )),
	    end: IDENT_RE$1,
	    excludeBegin: true,
	    keywords: "prototype",
	    className: "property",
	    relevance: 0
	  };

	  const GETTER_OR_SETTER = {
	    match: [
	      /get|set/,
	      /\s+/,
	      IDENT_RE$1,
	      /(?=\()/
	    ],
	    className: {
	      1: "keyword",
	      3: "title.function"
	    },
	    contains: [
	      { // eat to avoid empty params
	        begin: /\(\)/
	      },
	      PARAMS
	    ]
	  };

	  const FUNC_LEAD_IN_RE = '(\\(' +
	    '[^()]*(\\(' +
	    '[^()]*(\\(' +
	    '[^()]*' +
	    '\\)[^()]*)*' +
	    '\\)[^()]*)*' +
	    '\\)|' + hljs.UNDERSCORE_IDENT_RE + ')\\s*=>';

	  const FUNCTION_VARIABLE = {
	    match: [
	      /const|var|let/, /\s+/,
	      IDENT_RE$1, /\s*/,
	      /=\s*/,
	      /(async\s*)?/, // async is optional
	      regex.lookahead(FUNC_LEAD_IN_RE)
	    ],
	    keywords: "async",
	    className: {
	      1: "keyword",
	      3: "title.function"
	    },
	    contains: [
	      PARAMS
	    ]
	  };

	  return {
	    name: 'JavaScript',
	    aliases: ['js', 'jsx', 'mjs', 'cjs'],
	    keywords: KEYWORDS$1,
	    // this will be extended by TypeScript
	    exports: { PARAMS_CONTAINS, CLASS_REFERENCE },
	    illegal: /#(?![$_A-z])/,
	    contains: [
	      hljs.SHEBANG({
	        label: "shebang",
	        binary: "node",
	        relevance: 5
	      }),
	      USE_STRICT,
	      hljs.APOS_STRING_MODE,
	      hljs.QUOTE_STRING_MODE,
	      HTML_TEMPLATE,
	      CSS_TEMPLATE,
	      GRAPHQL_TEMPLATE,
	      TEMPLATE_STRING,
	      COMMENT,
	      // Skip numbers when they are part of a variable name
	      { match: /\$\d+/ },
	      NUMBER,
	      CLASS_REFERENCE,
	      {
	        className: 'attr',
	        begin: IDENT_RE$1 + regex.lookahead(':'),
	        relevance: 0
	      },
	      FUNCTION_VARIABLE,
	      { // "value" container
	        begin: '(' + hljs.RE_STARTERS_RE + '|\\b(case|return|throw)\\b)\\s*',
	        keywords: 'return throw case',
	        relevance: 0,
	        contains: [
	          COMMENT,
	          hljs.REGEXP_MODE,
	          {
	            className: 'function',
	            // we have to count the parens to make sure we actually have the
	            // correct bounding ( ) before the =>.  There could be any number of
	            // sub-expressions inside also surrounded by parens.
	            begin: FUNC_LEAD_IN_RE,
	            returnBegin: true,
	            end: '\\s*=>',
	            contains: [
	              {
	                className: 'params',
	                variants: [
	                  {
	                    begin: hljs.UNDERSCORE_IDENT_RE,
	                    relevance: 0
	                  },
	                  {
	                    className: null,
	                    begin: /\(\s*\)/,
	                    skip: true
	                  },
	                  {
	                    begin: /\(/,
	                    end: /\)/,
	                    excludeBegin: true,
	                    excludeEnd: true,
	                    keywords: KEYWORDS$1,
	                    contains: PARAMS_CONTAINS
	                  }
	                ]
	              }
	            ]
	          },
	          { // could be a comma delimited list of params to a function call
	            begin: /,/,
	            relevance: 0
	          },
	          {
	            match: /\s+/,
	            relevance: 0
	          },
	          { // JSX
	            variants: [
	              { begin: FRAGMENT.begin, end: FRAGMENT.end },
	              { match: XML_SELF_CLOSING },
	              {
	                begin: XML_TAG.begin,
	                // we carefully check the opening tag to see if it truly
	                // is a tag and not a false positive
	                'on:begin': XML_TAG.isTrulyOpeningTag,
	                end: XML_TAG.end
	              }
	            ],
	            subLanguage: 'xml',
	            contains: [
	              {
	                begin: XML_TAG.begin,
	                end: XML_TAG.end,
	                skip: true,
	                contains: ['self']
	              }
	            ]
	          }
	        ],
	      },
	      FUNCTION_DEFINITION,
	      {
	        // prevent this from getting swallowed up by function
	        // since they appear "function like"
	        beginKeywords: "while if switch catch for"
	      },
	      {
	        // we have to count the parens to make sure we actually have the correct
	        // bounding ( ).  There could be any number of sub-expressions inside
	        // also surrounded by parens.
	        begin: '\\b(?!function)' + hljs.UNDERSCORE_IDENT_RE +
	          '\\(' + // first parens
	          '[^()]*(\\(' +
	            '[^()]*(\\(' +
	              '[^()]*' +
	            '\\)[^()]*)*' +
	          '\\)[^()]*)*' +
	          '\\)\\s*\\{', // end parens
	        returnBegin:true,
	        label: "func.def",
	        contains: [
	          PARAMS,
	          hljs.inherit(hljs.TITLE_MODE, { begin: IDENT_RE$1, className: "title.function" })
	        ]
	      },
	      // catch ... so it won't trigger the property rule below
	      {
	        match: /\.\.\./,
	        relevance: 0
	      },
	      PROPERTY_ACCESS,
	      // hack: prevents detection of keywords in some circumstances
	      // .keyword()
	      // $keyword = x
	      {
	        match: '\\$' + IDENT_RE$1,
	        relevance: 0
	      },
	      {
	        match: [ /\bconstructor(?=\s*\()/ ],
	        className: { 1: "title.function" },
	        contains: [ PARAMS ]
	      },
	      FUNCTION_CALL,
	      UPPER_CASE_CONSTANT,
	      CLASS_OR_EXTENDS,
	      GETTER_OR_SETTER,
	      {
	        match: /\$[(.]/ // relevance booster for a pattern common to JS libs: `$(something)` and `$.something`
	      }
	    ]
	  };
	}

	javascript_1 = javascript;
	return javascript_1;
}

/*
Language: JSON
Description: JSON (JavaScript Object Notation) is a lightweight data-interchange format.
Author: Ivan Sagalaev <maniac@softwaremaniacs.org>
Website: http://www.json.org
Category: common, protocols, web
*/

var json_1;
var hasRequiredJson;

function requireJson () {
	if (hasRequiredJson) return json_1;
	hasRequiredJson = 1;
	function json(hljs) {
	  const ATTRIBUTE = {
	    className: 'attr',
	    begin: /"(\\.|[^\\"\r\n])*"(?=\s*:)/,
	    relevance: 1.01
	  };
	  const PUNCTUATION = {
	    match: /[{}[\],:]/,
	    className: "punctuation",
	    relevance: 0
	  };
	  const LITERALS = [
	    "true",
	    "false",
	    "null"
	  ];
	  // NOTE: normally we would rely on `keywords` for this but using a mode here allows us
	  // - to use the very tight `illegal: \S` rule later to flag any other character
	  // - as illegal indicating that despite looking like JSON we do not truly have
	  // - JSON and thus improve false-positively greatly since JSON will try and claim
	  // - all sorts of JSON looking stuff
	  const LITERALS_MODE = {
	    scope: "literal",
	    beginKeywords: LITERALS.join(" "),
	  };

	  return {
	    name: 'JSON',
	    keywords:{
	      literal: LITERALS,
	    },
	    contains: [
	      ATTRIBUTE,
	      PUNCTUATION,
	      hljs.QUOTE_STRING_MODE,
	      LITERALS_MODE,
	      hljs.C_NUMBER_MODE,
	      hljs.C_LINE_COMMENT_MODE,
	      hljs.C_BLOCK_COMMENT_MODE
	    ],
	    illegal: '\\S'
	  };
	}

	json_1 = json;
	return json_1;
}

var kotlin_1;
var hasRequiredKotlin;

function requireKotlin () {
	if (hasRequiredKotlin) return kotlin_1;
	hasRequiredKotlin = 1;
	// https://docs.oracle.com/javase/specs/jls/se15/html/jls-3.html#jls-3.10
	var decimalDigits = '[0-9](_*[0-9])*';
	var frac = `\\.(${decimalDigits})`;
	var hexDigits = '[0-9a-fA-F](_*[0-9a-fA-F])*';
	var NUMERIC = {
	  className: 'number',
	  variants: [
	    // DecimalFloatingPointLiteral
	    // including ExponentPart
	    { begin: `(\\b(${decimalDigits})((${frac})|\\.)?|(${frac}))` +
	      `[eE][+-]?(${decimalDigits})[fFdD]?\\b` },
	    // excluding ExponentPart
	    { begin: `\\b(${decimalDigits})((${frac})[fFdD]?\\b|\\.([fFdD]\\b)?)` },
	    { begin: `(${frac})[fFdD]?\\b` },
	    { begin: `\\b(${decimalDigits})[fFdD]\\b` },

	    // HexadecimalFloatingPointLiteral
	    { begin: `\\b0[xX]((${hexDigits})\\.?|(${hexDigits})?\\.(${hexDigits}))` +
	      `[pP][+-]?(${decimalDigits})[fFdD]?\\b` },

	    // DecimalIntegerLiteral
	    { begin: '\\b(0|[1-9](_*[0-9])*)[lL]?\\b' },

	    // HexIntegerLiteral
	    { begin: `\\b0[xX](${hexDigits})[lL]?\\b` },

	    // OctalIntegerLiteral
	    { begin: '\\b0(_*[0-7])*[lL]?\\b' },

	    // BinaryIntegerLiteral
	    { begin: '\\b0[bB][01](_*[01])*[lL]?\\b' },
	  ],
	  relevance: 0
	};

	/*
	 Language: Kotlin
	 Description: Kotlin is an OSS statically typed programming language that targets the JVM, Android, JavaScript and Native.
	 Author: Sergey Mashkov <cy6erGn0m@gmail.com>
	 Website: https://kotlinlang.org
	 Category: common
	 */


	function kotlin(hljs) {
	  const KEYWORDS = {
	    keyword:
	      'abstract as val var vararg get set class object open private protected public noinline '
	      + 'crossinline dynamic final enum if else do while for when throw try catch finally '
	      + 'import package is in fun override companion reified inline lateinit init '
	      + 'interface annotation data sealed internal infix operator out by constructor super '
	      + 'tailrec where const inner suspend typealias external expect actual',
	    built_in:
	      'Byte Short Char Int Long Boolean Float Double Void Unit Nothing',
	    literal:
	      'true false null'
	  };
	  const KEYWORDS_WITH_LABEL = {
	    className: 'keyword',
	    begin: /\b(break|continue|return|this)\b/,
	    starts: { contains: [
	      {
	        className: 'symbol',
	        begin: /@\w+/
	      }
	    ] }
	  };
	  const LABEL = {
	    className: 'symbol',
	    begin: hljs.UNDERSCORE_IDENT_RE + '@'
	  };

	  // for string templates
	  const SUBST = {
	    className: 'subst',
	    begin: /\$\{/,
	    end: /\}/,
	    contains: [ hljs.C_NUMBER_MODE ]
	  };
	  const VARIABLE = {
	    className: 'variable',
	    begin: '\\$' + hljs.UNDERSCORE_IDENT_RE
	  };
	  const STRING = {
	    className: 'string',
	    variants: [
	      {
	        begin: '"""',
	        end: '"""(?=[^"])',
	        contains: [
	          VARIABLE,
	          SUBST
	        ]
	      },
	      // Can't use built-in modes easily, as we want to use STRING in the meta
	      // context as 'meta-string' and there's no syntax to remove explicitly set
	      // classNames in built-in modes.
	      {
	        begin: '\'',
	        end: '\'',
	        illegal: /\n/,
	        contains: [ hljs.BACKSLASH_ESCAPE ]
	      },
	      {
	        begin: '"',
	        end: '"',
	        illegal: /\n/,
	        contains: [
	          hljs.BACKSLASH_ESCAPE,
	          VARIABLE,
	          SUBST
	        ]
	      }
	    ]
	  };
	  SUBST.contains.push(STRING);

	  const ANNOTATION_USE_SITE = {
	    className: 'meta',
	    begin: '@(?:file|property|field|get|set|receiver|param|setparam|delegate)\\s*:(?:\\s*' + hljs.UNDERSCORE_IDENT_RE + ')?'
	  };
	  const ANNOTATION = {
	    className: 'meta',
	    begin: '@' + hljs.UNDERSCORE_IDENT_RE,
	    contains: [
	      {
	        begin: /\(/,
	        end: /\)/,
	        contains: [
	          hljs.inherit(STRING, { className: 'string' }),
	          "self"
	        ]
	      }
	    ]
	  };

	  // https://kotlinlang.org/docs/reference/whatsnew11.html#underscores-in-numeric-literals
	  // According to the doc above, the number mode of kotlin is the same as java 8,
	  // so the code below is copied from java.js
	  const KOTLIN_NUMBER_MODE = NUMERIC;
	  const KOTLIN_NESTED_COMMENT = hljs.COMMENT(
	    '/\\*', '\\*/',
	    { contains: [ hljs.C_BLOCK_COMMENT_MODE ] }
	  );
	  const KOTLIN_PAREN_TYPE = { variants: [
	    {
	      className: 'type',
	      begin: hljs.UNDERSCORE_IDENT_RE
	    },
	    {
	      begin: /\(/,
	      end: /\)/,
	      contains: [] // defined later
	    }
	  ] };
	  const KOTLIN_PAREN_TYPE2 = KOTLIN_PAREN_TYPE;
	  KOTLIN_PAREN_TYPE2.variants[1].contains = [ KOTLIN_PAREN_TYPE ];
	  KOTLIN_PAREN_TYPE.variants[1].contains = [ KOTLIN_PAREN_TYPE2 ];

	  return {
	    name: 'Kotlin',
	    aliases: [
	      'kt',
	      'kts'
	    ],
	    keywords: KEYWORDS,
	    contains: [
	      hljs.COMMENT(
	        '/\\*\\*',
	        '\\*/',
	        {
	          relevance: 0,
	          contains: [
	            {
	              className: 'doctag',
	              begin: '@[A-Za-z]+'
	            }
	          ]
	        }
	      ),
	      hljs.C_LINE_COMMENT_MODE,
	      KOTLIN_NESTED_COMMENT,
	      KEYWORDS_WITH_LABEL,
	      LABEL,
	      ANNOTATION_USE_SITE,
	      ANNOTATION,
	      {
	        className: 'function',
	        beginKeywords: 'fun',
	        end: '[(]|$',
	        returnBegin: true,
	        excludeEnd: true,
	        keywords: KEYWORDS,
	        relevance: 5,
	        contains: [
	          {
	            begin: hljs.UNDERSCORE_IDENT_RE + '\\s*\\(',
	            returnBegin: true,
	            relevance: 0,
	            contains: [ hljs.UNDERSCORE_TITLE_MODE ]
	          },
	          {
	            className: 'type',
	            begin: /</,
	            end: />/,
	            keywords: 'reified',
	            relevance: 0
	          },
	          {
	            className: 'params',
	            begin: /\(/,
	            end: /\)/,
	            endsParent: true,
	            keywords: KEYWORDS,
	            relevance: 0,
	            contains: [
	              {
	                begin: /:/,
	                end: /[=,\/]/,
	                endsWithParent: true,
	                contains: [
	                  KOTLIN_PAREN_TYPE,
	                  hljs.C_LINE_COMMENT_MODE,
	                  KOTLIN_NESTED_COMMENT
	                ],
	                relevance: 0
	              },
	              hljs.C_LINE_COMMENT_MODE,
	              KOTLIN_NESTED_COMMENT,
	              ANNOTATION_USE_SITE,
	              ANNOTATION,
	              STRING,
	              hljs.C_NUMBER_MODE
	            ]
	          },
	          KOTLIN_NESTED_COMMENT
	        ]
	      },
	      {
	        begin: [
	          /class|interface|trait/,
	          /\s+/,
	          hljs.UNDERSCORE_IDENT_RE
	        ],
	        beginScope: {
	          3: "title.class"
	        },
	        keywords: 'class interface trait',
	        end: /[:\{(]|$/,
	        excludeEnd: true,
	        illegal: 'extends implements',
	        contains: [
	          { beginKeywords: 'public protected internal private constructor' },
	          hljs.UNDERSCORE_TITLE_MODE,
	          {
	            className: 'type',
	            begin: /</,
	            end: />/,
	            excludeBegin: true,
	            excludeEnd: true,
	            relevance: 0
	          },
	          {
	            className: 'type',
	            begin: /[,:]\s*/,
	            end: /[<\(,){\s]|$/,
	            excludeBegin: true,
	            returnEnd: true
	          },
	          ANNOTATION_USE_SITE,
	          ANNOTATION
	        ]
	      },
	      STRING,
	      {
	        className: 'meta',
	        begin: "^#!/usr/bin/env",
	        end: '$',
	        illegal: '\n'
	      },
	      KOTLIN_NUMBER_MODE
	    ]
	  };
	}

	kotlin_1 = kotlin;
	return kotlin_1;
}

var less_1;
var hasRequiredLess;

function requireLess () {
	if (hasRequiredLess) return less_1;
	hasRequiredLess = 1;
	const MODES = (hljs) => {
	  return {
	    IMPORTANT: {
	      scope: 'meta',
	      begin: '!important'
	    },
	    BLOCK_COMMENT: hljs.C_BLOCK_COMMENT_MODE,
	    HEXCOLOR: {
	      scope: 'number',
	      begin: /#(([0-9a-fA-F]{3,4})|(([0-9a-fA-F]{2}){3,4}))\b/
	    },
	    FUNCTION_DISPATCH: {
	      className: "built_in",
	      begin: /[\w-]+(?=\()/
	    },
	    ATTRIBUTE_SELECTOR_MODE: {
	      scope: 'selector-attr',
	      begin: /\[/,
	      end: /\]/,
	      illegal: '$',
	      contains: [
	        hljs.APOS_STRING_MODE,
	        hljs.QUOTE_STRING_MODE
	      ]
	    },
	    CSS_NUMBER_MODE: {
	      scope: 'number',
	      begin: hljs.NUMBER_RE + '(' +
	        '%|em|ex|ch|rem' +
	        '|vw|vh|vmin|vmax' +
	        '|cm|mm|in|pt|pc|px' +
	        '|deg|grad|rad|turn' +
	        '|s|ms' +
	        '|Hz|kHz' +
	        '|dpi|dpcm|dppx' +
	        ')?',
	      relevance: 0
	    },
	    CSS_VARIABLE: {
	      className: "attr",
	      begin: /--[A-Za-z_][A-Za-z0-9_-]*/
	    }
	  };
	};

	const TAGS = [
	  'a',
	  'abbr',
	  'address',
	  'article',
	  'aside',
	  'audio',
	  'b',
	  'blockquote',
	  'body',
	  'button',
	  'canvas',
	  'caption',
	  'cite',
	  'code',
	  'dd',
	  'del',
	  'details',
	  'dfn',
	  'div',
	  'dl',
	  'dt',
	  'em',
	  'fieldset',
	  'figcaption',
	  'figure',
	  'footer',
	  'form',
	  'h1',
	  'h2',
	  'h3',
	  'h4',
	  'h5',
	  'h6',
	  'header',
	  'hgroup',
	  'html',
	  'i',
	  'iframe',
	  'img',
	  'input',
	  'ins',
	  'kbd',
	  'label',
	  'legend',
	  'li',
	  'main',
	  'mark',
	  'menu',
	  'nav',
	  'object',
	  'ol',
	  'p',
	  'q',
	  'quote',
	  'samp',
	  'section',
	  'span',
	  'strong',
	  'summary',
	  'sup',
	  'table',
	  'tbody',
	  'td',
	  'textarea',
	  'tfoot',
	  'th',
	  'thead',
	  'time',
	  'tr',
	  'ul',
	  'var',
	  'video'
	];

	const MEDIA_FEATURES = [
	  'any-hover',
	  'any-pointer',
	  'aspect-ratio',
	  'color',
	  'color-gamut',
	  'color-index',
	  'device-aspect-ratio',
	  'device-height',
	  'device-width',
	  'display-mode',
	  'forced-colors',
	  'grid',
	  'height',
	  'hover',
	  'inverted-colors',
	  'monochrome',
	  'orientation',
	  'overflow-block',
	  'overflow-inline',
	  'pointer',
	  'prefers-color-scheme',
	  'prefers-contrast',
	  'prefers-reduced-motion',
	  'prefers-reduced-transparency',
	  'resolution',
	  'scan',
	  'scripting',
	  'update',
	  'width',
	  // TODO: find a better solution?
	  'min-width',
	  'max-width',
	  'min-height',
	  'max-height'
	];

	// https://developer.mozilla.org/en-US/docs/Web/CSS/Pseudo-classes
	const PSEUDO_CLASSES = [
	  'active',
	  'any-link',
	  'blank',
	  'checked',
	  'current',
	  'default',
	  'defined',
	  'dir', // dir()
	  'disabled',
	  'drop',
	  'empty',
	  'enabled',
	  'first',
	  'first-child',
	  'first-of-type',
	  'fullscreen',
	  'future',
	  'focus',
	  'focus-visible',
	  'focus-within',
	  'has', // has()
	  'host', // host or host()
	  'host-context', // host-context()
	  'hover',
	  'indeterminate',
	  'in-range',
	  'invalid',
	  'is', // is()
	  'lang', // lang()
	  'last-child',
	  'last-of-type',
	  'left',
	  'link',
	  'local-link',
	  'not', // not()
	  'nth-child', // nth-child()
	  'nth-col', // nth-col()
	  'nth-last-child', // nth-last-child()
	  'nth-last-col', // nth-last-col()
	  'nth-last-of-type', //nth-last-of-type()
	  'nth-of-type', //nth-of-type()
	  'only-child',
	  'only-of-type',
	  'optional',
	  'out-of-range',
	  'past',
	  'placeholder-shown',
	  'read-only',
	  'read-write',
	  'required',
	  'right',
	  'root',
	  'scope',
	  'target',
	  'target-within',
	  'user-invalid',
	  'valid',
	  'visited',
	  'where' // where()
	];

	// https://developer.mozilla.org/en-US/docs/Web/CSS/Pseudo-elements
	const PSEUDO_ELEMENTS = [
	  'after',
	  'backdrop',
	  'before',
	  'cue',
	  'cue-region',
	  'first-letter',
	  'first-line',
	  'grammar-error',
	  'marker',
	  'part',
	  'placeholder',
	  'selection',
	  'slotted',
	  'spelling-error'
	];

	const ATTRIBUTES = [
	  'align-content',
	  'align-items',
	  'align-self',
	  'all',
	  'animation',
	  'animation-delay',
	  'animation-direction',
	  'animation-duration',
	  'animation-fill-mode',
	  'animation-iteration-count',
	  'animation-name',
	  'animation-play-state',
	  'animation-timing-function',
	  'backface-visibility',
	  'background',
	  'background-attachment',
	  'background-blend-mode',
	  'background-clip',
	  'background-color',
	  'background-image',
	  'background-origin',
	  'background-position',
	  'background-repeat',
	  'background-size',
	  'block-size',
	  'border',
	  'border-block',
	  'border-block-color',
	  'border-block-end',
	  'border-block-end-color',
	  'border-block-end-style',
	  'border-block-end-width',
	  'border-block-start',
	  'border-block-start-color',
	  'border-block-start-style',
	  'border-block-start-width',
	  'border-block-style',
	  'border-block-width',
	  'border-bottom',
	  'border-bottom-color',
	  'border-bottom-left-radius',
	  'border-bottom-right-radius',
	  'border-bottom-style',
	  'border-bottom-width',
	  'border-collapse',
	  'border-color',
	  'border-image',
	  'border-image-outset',
	  'border-image-repeat',
	  'border-image-slice',
	  'border-image-source',
	  'border-image-width',
	  'border-inline',
	  'border-inline-color',
	  'border-inline-end',
	  'border-inline-end-color',
	  'border-inline-end-style',
	  'border-inline-end-width',
	  'border-inline-start',
	  'border-inline-start-color',
	  'border-inline-start-style',
	  'border-inline-start-width',
	  'border-inline-style',
	  'border-inline-width',
	  'border-left',
	  'border-left-color',
	  'border-left-style',
	  'border-left-width',
	  'border-radius',
	  'border-right',
	  'border-right-color',
	  'border-right-style',
	  'border-right-width',
	  'border-spacing',
	  'border-style',
	  'border-top',
	  'border-top-color',
	  'border-top-left-radius',
	  'border-top-right-radius',
	  'border-top-style',
	  'border-top-width',
	  'border-width',
	  'bottom',
	  'box-decoration-break',
	  'box-shadow',
	  'box-sizing',
	  'break-after',
	  'break-before',
	  'break-inside',
	  'caption-side',
	  'caret-color',
	  'clear',
	  'clip',
	  'clip-path',
	  'clip-rule',
	  'color',
	  'column-count',
	  'column-fill',
	  'column-gap',
	  'column-rule',
	  'column-rule-color',
	  'column-rule-style',
	  'column-rule-width',
	  'column-span',
	  'column-width',
	  'columns',
	  'contain',
	  'content',
	  'content-visibility',
	  'counter-increment',
	  'counter-reset',
	  'cue',
	  'cue-after',
	  'cue-before',
	  'cursor',
	  'direction',
	  'display',
	  'empty-cells',
	  'filter',
	  'flex',
	  'flex-basis',
	  'flex-direction',
	  'flex-flow',
	  'flex-grow',
	  'flex-shrink',
	  'flex-wrap',
	  'float',
	  'flow',
	  'font',
	  'font-display',
	  'font-family',
	  'font-feature-settings',
	  'font-kerning',
	  'font-language-override',
	  'font-size',
	  'font-size-adjust',
	  'font-smoothing',
	  'font-stretch',
	  'font-style',
	  'font-synthesis',
	  'font-variant',
	  'font-variant-caps',
	  'font-variant-east-asian',
	  'font-variant-ligatures',
	  'font-variant-numeric',
	  'font-variant-position',
	  'font-variation-settings',
	  'font-weight',
	  'gap',
	  'glyph-orientation-vertical',
	  'grid',
	  'grid-area',
	  'grid-auto-columns',
	  'grid-auto-flow',
	  'grid-auto-rows',
	  'grid-column',
	  'grid-column-end',
	  'grid-column-start',
	  'grid-gap',
	  'grid-row',
	  'grid-row-end',
	  'grid-row-start',
	  'grid-template',
	  'grid-template-areas',
	  'grid-template-columns',
	  'grid-template-rows',
	  'hanging-punctuation',
	  'height',
	  'hyphens',
	  'icon',
	  'image-orientation',
	  'image-rendering',
	  'image-resolution',
	  'ime-mode',
	  'inline-size',
	  'isolation',
	  'justify-content',
	  'left',
	  'letter-spacing',
	  'line-break',
	  'line-height',
	  'list-style',
	  'list-style-image',
	  'list-style-position',
	  'list-style-type',
	  'margin',
	  'margin-block',
	  'margin-block-end',
	  'margin-block-start',
	  'margin-bottom',
	  'margin-inline',
	  'margin-inline-end',
	  'margin-inline-start',
	  'margin-left',
	  'margin-right',
	  'margin-top',
	  'marks',
	  'mask',
	  'mask-border',
	  'mask-border-mode',
	  'mask-border-outset',
	  'mask-border-repeat',
	  'mask-border-slice',
	  'mask-border-source',
	  'mask-border-width',
	  'mask-clip',
	  'mask-composite',
	  'mask-image',
	  'mask-mode',
	  'mask-origin',
	  'mask-position',
	  'mask-repeat',
	  'mask-size',
	  'mask-type',
	  'max-block-size',
	  'max-height',
	  'max-inline-size',
	  'max-width',
	  'min-block-size',
	  'min-height',
	  'min-inline-size',
	  'min-width',
	  'mix-blend-mode',
	  'nav-down',
	  'nav-index',
	  'nav-left',
	  'nav-right',
	  'nav-up',
	  'none',
	  'normal',
	  'object-fit',
	  'object-position',
	  'opacity',
	  'order',
	  'orphans',
	  'outline',
	  'outline-color',
	  'outline-offset',
	  'outline-style',
	  'outline-width',
	  'overflow',
	  'overflow-wrap',
	  'overflow-x',
	  'overflow-y',
	  'padding',
	  'padding-block',
	  'padding-block-end',
	  'padding-block-start',
	  'padding-bottom',
	  'padding-inline',
	  'padding-inline-end',
	  'padding-inline-start',
	  'padding-left',
	  'padding-right',
	  'padding-top',
	  'page-break-after',
	  'page-break-before',
	  'page-break-inside',
	  'pause',
	  'pause-after',
	  'pause-before',
	  'perspective',
	  'perspective-origin',
	  'pointer-events',
	  'position',
	  'quotes',
	  'resize',
	  'rest',
	  'rest-after',
	  'rest-before',
	  'right',
	  'row-gap',
	  'scroll-margin',
	  'scroll-margin-block',
	  'scroll-margin-block-end',
	  'scroll-margin-block-start',
	  'scroll-margin-bottom',
	  'scroll-margin-inline',
	  'scroll-margin-inline-end',
	  'scroll-margin-inline-start',
	  'scroll-margin-left',
	  'scroll-margin-right',
	  'scroll-margin-top',
	  'scroll-padding',
	  'scroll-padding-block',
	  'scroll-padding-block-end',
	  'scroll-padding-block-start',
	  'scroll-padding-bottom',
	  'scroll-padding-inline',
	  'scroll-padding-inline-end',
	  'scroll-padding-inline-start',
	  'scroll-padding-left',
	  'scroll-padding-right',
	  'scroll-padding-top',
	  'scroll-snap-align',
	  'scroll-snap-stop',
	  'scroll-snap-type',
	  'scrollbar-color',
	  'scrollbar-gutter',
	  'scrollbar-width',
	  'shape-image-threshold',
	  'shape-margin',
	  'shape-outside',
	  'speak',
	  'speak-as',
	  'src', // @font-face
	  'tab-size',
	  'table-layout',
	  'text-align',
	  'text-align-all',
	  'text-align-last',
	  'text-combine-upright',
	  'text-decoration',
	  'text-decoration-color',
	  'text-decoration-line',
	  'text-decoration-style',
	  'text-emphasis',
	  'text-emphasis-color',
	  'text-emphasis-position',
	  'text-emphasis-style',
	  'text-indent',
	  'text-justify',
	  'text-orientation',
	  'text-overflow',
	  'text-rendering',
	  'text-shadow',
	  'text-transform',
	  'text-underline-position',
	  'top',
	  'transform',
	  'transform-box',
	  'transform-origin',
	  'transform-style',
	  'transition',
	  'transition-delay',
	  'transition-duration',
	  'transition-property',
	  'transition-timing-function',
	  'unicode-bidi',
	  'vertical-align',
	  'visibility',
	  'voice-balance',
	  'voice-duration',
	  'voice-family',
	  'voice-pitch',
	  'voice-range',
	  'voice-rate',
	  'voice-stress',
	  'voice-volume',
	  'white-space',
	  'widows',
	  'width',
	  'will-change',
	  'word-break',
	  'word-spacing',
	  'word-wrap',
	  'writing-mode',
	  'z-index'
	  // reverse makes sure longer attributes `font-weight` are matched fully
	  // instead of getting false positives on say `font`
	].reverse();

	// some grammars use them all as a single group
	const PSEUDO_SELECTORS = PSEUDO_CLASSES.concat(PSEUDO_ELEMENTS);

	/*
	Language: Less
	Description: It's CSS, with just a little more.
	Author:   Max Mikhailov <seven.phases.max@gmail.com>
	Website: http://lesscss.org
	Category: common, css, web
	*/


	/** @type LanguageFn */
	function less(hljs) {
	  const modes = MODES(hljs);
	  const PSEUDO_SELECTORS$1 = PSEUDO_SELECTORS;

	  const AT_MODIFIERS = "and or not only";
	  const IDENT_RE = '[\\w-]+'; // yes, Less identifiers may begin with a digit
	  const INTERP_IDENT_RE = '(' + IDENT_RE + '|@\\{' + IDENT_RE + '\\})';

	  /* Generic Modes */

	  const RULES = []; const VALUE_MODES = []; // forward def. for recursive modes

	  const STRING_MODE = function(c) {
	    return {
	    // Less strings are not multiline (also include '~' for more consistent coloring of "escaped" strings)
	      className: 'string',
	      begin: '~?' + c + '.*?' + c
	    };
	  };

	  const IDENT_MODE = function(name, begin, relevance) {
	    return {
	      className: name,
	      begin: begin,
	      relevance: relevance
	    };
	  };

	  const AT_KEYWORDS = {
	    $pattern: /[a-z-]+/,
	    keyword: AT_MODIFIERS,
	    attribute: MEDIA_FEATURES.join(" ")
	  };

	  const PARENS_MODE = {
	    // used only to properly balance nested parens inside mixin call, def. arg list
	    begin: '\\(',
	    end: '\\)',
	    contains: VALUE_MODES,
	    keywords: AT_KEYWORDS,
	    relevance: 0
	  };

	  // generic Less highlighter (used almost everywhere except selectors):
	  VALUE_MODES.push(
	    hljs.C_LINE_COMMENT_MODE,
	    hljs.C_BLOCK_COMMENT_MODE,
	    STRING_MODE("'"),
	    STRING_MODE('"'),
	    modes.CSS_NUMBER_MODE, // fixme: it does not include dot for numbers like .5em :(
	    {
	      begin: '(url|data-uri)\\(',
	      starts: {
	        className: 'string',
	        end: '[\\)\\n]',
	        excludeEnd: true
	      }
	    },
	    modes.HEXCOLOR,
	    PARENS_MODE,
	    IDENT_MODE('variable', '@@?' + IDENT_RE, 10),
	    IDENT_MODE('variable', '@\\{' + IDENT_RE + '\\}'),
	    IDENT_MODE('built_in', '~?`[^`]*?`'), // inline javascript (or whatever host language) *multiline* string
	    { // @media features (it’s here to not duplicate things in AT_RULE_MODE with extra PARENS_MODE overriding):
	      className: 'attribute',
	      begin: IDENT_RE + '\\s*:',
	      end: ':',
	      returnBegin: true,
	      excludeEnd: true
	    },
	    modes.IMPORTANT,
	    { beginKeywords: 'and not' },
	    modes.FUNCTION_DISPATCH
	  );

	  const VALUE_WITH_RULESETS = VALUE_MODES.concat({
	    begin: /\{/,
	    end: /\}/,
	    contains: RULES
	  });

	  const MIXIN_GUARD_MODE = {
	    beginKeywords: 'when',
	    endsWithParent: true,
	    contains: [ { beginKeywords: 'and not' } ].concat(VALUE_MODES) // using this form to override VALUE’s 'function' match
	  };

	  /* Rule-Level Modes */

	  const RULE_MODE = {
	    begin: INTERP_IDENT_RE + '\\s*:',
	    returnBegin: true,
	    end: /[;}]/,
	    relevance: 0,
	    contains: [
	      { begin: /-(webkit|moz|ms|o)-/ },
	      modes.CSS_VARIABLE,
	      {
	        className: 'attribute',
	        begin: '\\b(' + ATTRIBUTES.join('|') + ')\\b',
	        end: /(?=:)/,
	        starts: {
	          endsWithParent: true,
	          illegal: '[<=$]',
	          relevance: 0,
	          contains: VALUE_MODES
	        }
	      }
	    ]
	  };

	  const AT_RULE_MODE = {
	    className: 'keyword',
	    begin: '@(import|media|charset|font-face|(-[a-z]+-)?keyframes|supports|document|namespace|page|viewport|host)\\b',
	    starts: {
	      end: '[;{}]',
	      keywords: AT_KEYWORDS,
	      returnEnd: true,
	      contains: VALUE_MODES,
	      relevance: 0
	    }
	  };

	  // variable definitions and calls
	  const VAR_RULE_MODE = {
	    className: 'variable',
	    variants: [
	      // using more strict pattern for higher relevance to increase chances of Less detection.
	      // this is *the only* Less specific statement used in most of the sources, so...
	      // (we’ll still often loose to the css-parser unless there's '//' comment,
	      // simply because 1 variable just can't beat 99 properties :)
	      {
	        begin: '@' + IDENT_RE + '\\s*:',
	        relevance: 15
	      },
	      { begin: '@' + IDENT_RE }
	    ],
	    starts: {
	      end: '[;}]',
	      returnEnd: true,
	      contains: VALUE_WITH_RULESETS
	    }
	  };

	  const SELECTOR_MODE = {
	    // first parse unambiguous selectors (i.e. those not starting with tag)
	    // then fall into the scary lookahead-discriminator variant.
	    // this mode also handles mixin definitions and calls
	    variants: [
	      {
	        begin: '[\\.#:&\\[>]',
	        end: '[;{}]' // mixin calls end with ';'
	      },
	      {
	        begin: INTERP_IDENT_RE,
	        end: /\{/
	      }
	    ],
	    returnBegin: true,
	    returnEnd: true,
	    illegal: '[<=\'$"]',
	    relevance: 0,
	    contains: [
	      hljs.C_LINE_COMMENT_MODE,
	      hljs.C_BLOCK_COMMENT_MODE,
	      MIXIN_GUARD_MODE,
	      IDENT_MODE('keyword', 'all\\b'),
	      IDENT_MODE('variable', '@\\{' + IDENT_RE + '\\}'), // otherwise it’s identified as tag
	      
	      {
	        begin: '\\b(' + TAGS.join('|') + ')\\b',
	        className: 'selector-tag'
	      },
	      modes.CSS_NUMBER_MODE,
	      IDENT_MODE('selector-tag', INTERP_IDENT_RE, 0),
	      IDENT_MODE('selector-id', '#' + INTERP_IDENT_RE),
	      IDENT_MODE('selector-class', '\\.' + INTERP_IDENT_RE, 0),
	      IDENT_MODE('selector-tag', '&', 0),
	      modes.ATTRIBUTE_SELECTOR_MODE,
	      {
	        className: 'selector-pseudo',
	        begin: ':(' + PSEUDO_CLASSES.join('|') + ')'
	      },
	      {
	        className: 'selector-pseudo',
	        begin: ':(:)?(' + PSEUDO_ELEMENTS.join('|') + ')'
	      },
	      {
	        begin: /\(/,
	        end: /\)/,
	        relevance: 0,
	        contains: VALUE_WITH_RULESETS
	      }, // argument list of parametric mixins
	      { begin: '!important' }, // eat !important after mixin call or it will be colored as tag
	      modes.FUNCTION_DISPATCH
	    ]
	  };

	  const PSEUDO_SELECTOR_MODE = {
	    begin: IDENT_RE + ':(:)?' + `(${PSEUDO_SELECTORS$1.join('|')})`,
	    returnBegin: true,
	    contains: [ SELECTOR_MODE ]
	  };

	  RULES.push(
	    hljs.C_LINE_COMMENT_MODE,
	    hljs.C_BLOCK_COMMENT_MODE,
	    AT_RULE_MODE,
	    VAR_RULE_MODE,
	    PSEUDO_SELECTOR_MODE,
	    RULE_MODE,
	    SELECTOR_MODE,
	    MIXIN_GUARD_MODE,
	    modes.FUNCTION_DISPATCH
	  );

	  return {
	    name: 'Less',
	    case_insensitive: true,
	    illegal: '[=>\'/<($"]',
	    contains: RULES
	  };
	}

	less_1 = less;
	return less_1;
}

/*
Language: Lua
Description: Lua is a powerful, efficient, lightweight, embeddable scripting language.
Author: Andrew Fedorov <dmmdrs@mail.ru>
Category: common, scripting
Website: https://www.lua.org
*/

var lua_1;
var hasRequiredLua;

function requireLua () {
	if (hasRequiredLua) return lua_1;
	hasRequiredLua = 1;
	function lua(hljs) {
	  const OPENING_LONG_BRACKET = '\\[=*\\[';
	  const CLOSING_LONG_BRACKET = '\\]=*\\]';
	  const LONG_BRACKETS = {
	    begin: OPENING_LONG_BRACKET,
	    end: CLOSING_LONG_BRACKET,
	    contains: [ 'self' ]
	  };
	  const COMMENTS = [
	    hljs.COMMENT('--(?!' + OPENING_LONG_BRACKET + ')', '$'),
	    hljs.COMMENT(
	      '--' + OPENING_LONG_BRACKET,
	      CLOSING_LONG_BRACKET,
	      {
	        contains: [ LONG_BRACKETS ],
	        relevance: 10
	      }
	    )
	  ];
	  return {
	    name: 'Lua',
	    keywords: {
	      $pattern: hljs.UNDERSCORE_IDENT_RE,
	      literal: "true false nil",
	      keyword: "and break do else elseif end for goto if in local not or repeat return then until while",
	      built_in:
	        // Metatags and globals:
	        '_G _ENV _VERSION __index __newindex __mode __call __metatable __tostring __len '
	        + '__gc __add __sub __mul __div __mod __pow __concat __unm __eq __lt __le assert '
	        // Standard methods and properties:
	        + 'collectgarbage dofile error getfenv getmetatable ipairs load loadfile loadstring '
	        + 'module next pairs pcall print rawequal rawget rawset require select setfenv '
	        + 'setmetatable tonumber tostring type unpack xpcall arg self '
	        // Library methods and properties (one line per library):
	        + 'coroutine resume yield status wrap create running debug getupvalue '
	        + 'debug sethook getmetatable gethook setmetatable setlocal traceback setfenv getinfo setupvalue getlocal getregistry getfenv '
	        + 'io lines write close flush open output type read stderr stdin input stdout popen tmpfile '
	        + 'math log max acos huge ldexp pi cos tanh pow deg tan cosh sinh random randomseed frexp ceil floor rad abs sqrt modf asin min mod fmod log10 atan2 exp sin atan '
	        + 'os exit setlocale date getenv difftime remove time clock tmpname rename execute package preload loadlib loaded loaders cpath config path seeall '
	        + 'string sub upper len gfind rep find match char dump gmatch reverse byte format gsub lower '
	        + 'table setn insert getn foreachi maxn foreach concat sort remove'
	    },
	    contains: COMMENTS.concat([
	      {
	        className: 'function',
	        beginKeywords: 'function',
	        end: '\\)',
	        contains: [
	          hljs.inherit(hljs.TITLE_MODE, { begin: '([_a-zA-Z]\\w*\\.)*([_a-zA-Z]\\w*:)?[_a-zA-Z]\\w*' }),
	          {
	            className: 'params',
	            begin: '\\(',
	            endsWithParent: true,
	            contains: COMMENTS
	          }
	        ].concat(COMMENTS)
	      },
	      hljs.C_NUMBER_MODE,
	      hljs.APOS_STRING_MODE,
	      hljs.QUOTE_STRING_MODE,
	      {
	        className: 'string',
	        begin: OPENING_LONG_BRACKET,
	        end: CLOSING_LONG_BRACKET,
	        contains: [ LONG_BRACKETS ],
	        relevance: 5
	      }
	    ])
	  };
	}

	lua_1 = lua;
	return lua_1;
}

/*
Language: Makefile
Author: Ivan Sagalaev <maniac@softwaremaniacs.org>
Contributors: Joël Porquet <joel@porquet.org>
Website: https://www.gnu.org/software/make/manual/html_node/Introduction.html
Category: common
*/

var makefile_1;
var hasRequiredMakefile;

function requireMakefile () {
	if (hasRequiredMakefile) return makefile_1;
	hasRequiredMakefile = 1;
	function makefile(hljs) {
	  /* Variables: simple (eg $(var)) and special (eg $@) */
	  const VARIABLE = {
	    className: 'variable',
	    variants: [
	      {
	        begin: '\\$\\(' + hljs.UNDERSCORE_IDENT_RE + '\\)',
	        contains: [ hljs.BACKSLASH_ESCAPE ]
	      },
	      { begin: /\$[@%<?\^\+\*]/ }
	    ]
	  };
	  /* Quoted string with variables inside */
	  const QUOTE_STRING = {
	    className: 'string',
	    begin: /"/,
	    end: /"/,
	    contains: [
	      hljs.BACKSLASH_ESCAPE,
	      VARIABLE
	    ]
	  };
	  /* Function: $(func arg,...) */
	  const FUNC = {
	    className: 'variable',
	    begin: /\$\([\w-]+\s/,
	    end: /\)/,
	    keywords: { built_in:
	        'subst patsubst strip findstring filter filter-out sort '
	        + 'word wordlist firstword lastword dir notdir suffix basename '
	        + 'addsuffix addprefix join wildcard realpath abspath error warning '
	        + 'shell origin flavor foreach if or and call eval file value' },
	    contains: [ VARIABLE ]
	  };
	  /* Variable assignment */
	  const ASSIGNMENT = { begin: '^' + hljs.UNDERSCORE_IDENT_RE + '\\s*(?=[:+?]?=)' };
	  /* Meta targets (.PHONY) */
	  const META = {
	    className: 'meta',
	    begin: /^\.PHONY:/,
	    end: /$/,
	    keywords: {
	      $pattern: /[\.\w]+/,
	      keyword: '.PHONY'
	    }
	  };
	  /* Targets */
	  const TARGET = {
	    className: 'section',
	    begin: /^[^\s]+:/,
	    end: /$/,
	    contains: [ VARIABLE ]
	  };
	  return {
	    name: 'Makefile',
	    aliases: [
	      'mk',
	      'mak',
	      'make',
	    ],
	    keywords: {
	      $pattern: /[\w-]+/,
	      keyword: 'define endef undefine ifdef ifndef ifeq ifneq else endif '
	      + 'include -include sinclude override export unexport private vpath'
	    },
	    contains: [
	      hljs.HASH_COMMENT_MODE,
	      VARIABLE,
	      QUOTE_STRING,
	      FUNC,
	      ASSIGNMENT,
	      META,
	      TARGET
	    ]
	  };
	}

	makefile_1 = makefile;
	return makefile_1;
}

/*
Language: Perl
Author: Peter Leonov <gojpeg@yandex.ru>
Website: https://www.perl.org
Category: common
*/

var perl_1;
var hasRequiredPerl;

function requirePerl () {
	if (hasRequiredPerl) return perl_1;
	hasRequiredPerl = 1;
	/** @type LanguageFn */
	function perl(hljs) {
	  const regex = hljs.regex;
	  const KEYWORDS = [
	    'abs',
	    'accept',
	    'alarm',
	    'and',
	    'atan2',
	    'bind',
	    'binmode',
	    'bless',
	    'break',
	    'caller',
	    'chdir',
	    'chmod',
	    'chomp',
	    'chop',
	    'chown',
	    'chr',
	    'chroot',
	    'close',
	    'closedir',
	    'connect',
	    'continue',
	    'cos',
	    'crypt',
	    'dbmclose',
	    'dbmopen',
	    'defined',
	    'delete',
	    'die',
	    'do',
	    'dump',
	    'each',
	    'else',
	    'elsif',
	    'endgrent',
	    'endhostent',
	    'endnetent',
	    'endprotoent',
	    'endpwent',
	    'endservent',
	    'eof',
	    'eval',
	    'exec',
	    'exists',
	    'exit',
	    'exp',
	    'fcntl',
	    'fileno',
	    'flock',
	    'for',
	    'foreach',
	    'fork',
	    'format',
	    'formline',
	    'getc',
	    'getgrent',
	    'getgrgid',
	    'getgrnam',
	    'gethostbyaddr',
	    'gethostbyname',
	    'gethostent',
	    'getlogin',
	    'getnetbyaddr',
	    'getnetbyname',
	    'getnetent',
	    'getpeername',
	    'getpgrp',
	    'getpriority',
	    'getprotobyname',
	    'getprotobynumber',
	    'getprotoent',
	    'getpwent',
	    'getpwnam',
	    'getpwuid',
	    'getservbyname',
	    'getservbyport',
	    'getservent',
	    'getsockname',
	    'getsockopt',
	    'given',
	    'glob',
	    'gmtime',
	    'goto',
	    'grep',
	    'gt',
	    'hex',
	    'if',
	    'index',
	    'int',
	    'ioctl',
	    'join',
	    'keys',
	    'kill',
	    'last',
	    'lc',
	    'lcfirst',
	    'length',
	    'link',
	    'listen',
	    'local',
	    'localtime',
	    'log',
	    'lstat',
	    'lt',
	    'ma',
	    'map',
	    'mkdir',
	    'msgctl',
	    'msgget',
	    'msgrcv',
	    'msgsnd',
	    'my',
	    'ne',
	    'next',
	    'no',
	    'not',
	    'oct',
	    'open',
	    'opendir',
	    'or',
	    'ord',
	    'our',
	    'pack',
	    'package',
	    'pipe',
	    'pop',
	    'pos',
	    'print',
	    'printf',
	    'prototype',
	    'push',
	    'q|0',
	    'qq',
	    'quotemeta',
	    'qw',
	    'qx',
	    'rand',
	    'read',
	    'readdir',
	    'readline',
	    'readlink',
	    'readpipe',
	    'recv',
	    'redo',
	    'ref',
	    'rename',
	    'require',
	    'reset',
	    'return',
	    'reverse',
	    'rewinddir',
	    'rindex',
	    'rmdir',
	    'say',
	    'scalar',
	    'seek',
	    'seekdir',
	    'select',
	    'semctl',
	    'semget',
	    'semop',
	    'send',
	    'setgrent',
	    'sethostent',
	    'setnetent',
	    'setpgrp',
	    'setpriority',
	    'setprotoent',
	    'setpwent',
	    'setservent',
	    'setsockopt',
	    'shift',
	    'shmctl',
	    'shmget',
	    'shmread',
	    'shmwrite',
	    'shutdown',
	    'sin',
	    'sleep',
	    'socket',
	    'socketpair',
	    'sort',
	    'splice',
	    'split',
	    'sprintf',
	    'sqrt',
	    'srand',
	    'stat',
	    'state',
	    'study',
	    'sub',
	    'substr',
	    'symlink',
	    'syscall',
	    'sysopen',
	    'sysread',
	    'sysseek',
	    'system',
	    'syswrite',
	    'tell',
	    'telldir',
	    'tie',
	    'tied',
	    'time',
	    'times',
	    'tr',
	    'truncate',
	    'uc',
	    'ucfirst',
	    'umask',
	    'undef',
	    'unless',
	    'unlink',
	    'unpack',
	    'unshift',
	    'untie',
	    'until',
	    'use',
	    'utime',
	    'values',
	    'vec',
	    'wait',
	    'waitpid',
	    'wantarray',
	    'warn',
	    'when',
	    'while',
	    'write',
	    'x|0',
	    'xor',
	    'y|0'
	  ];

	  // https://perldoc.perl.org/perlre#Modifiers
	  const REGEX_MODIFIERS = /[dualxmsipngr]{0,12}/; // aa and xx are valid, making max length 12
	  const PERL_KEYWORDS = {
	    $pattern: /[\w.]+/,
	    keyword: KEYWORDS.join(" ")
	  };
	  const SUBST = {
	    className: 'subst',
	    begin: '[$@]\\{',
	    end: '\\}',
	    keywords: PERL_KEYWORDS
	  };
	  const METHOD = {
	    begin: /->\{/,
	    end: /\}/
	    // contains defined later
	  };
	  const VAR = { variants: [
	    { begin: /\$\d/ },
	    { begin: regex.concat(
	      /[$%@](\^\w\b|#\w+(::\w+)*|\{\w+\}|\w+(::\w*)*)/,
	      // negative look-ahead tries to avoid matching patterns that are not
	      // Perl at all like $ident$, @ident@, etc.
	      `(?![A-Za-z])(?![@$%])`
	    ) },
	    {
	      begin: /[$%@][^\s\w{]/,
	      relevance: 0
	    }
	  ] };
	  const STRING_CONTAINS = [
	    hljs.BACKSLASH_ESCAPE,
	    SUBST,
	    VAR
	  ];
	  const REGEX_DELIMS = [
	    /!/,
	    /\//,
	    /\|/,
	    /\?/,
	    /'/,
	    /"/, // valid but infrequent and weird
	    /#/ // valid but infrequent and weird
	  ];
	  /**
	   * @param {string|RegExp} prefix
	   * @param {string|RegExp} open
	   * @param {string|RegExp} close
	   */
	  const PAIRED_DOUBLE_RE = (prefix, open, close = '\\1') => {
	    const middle = (close === '\\1')
	      ? close
	      : regex.concat(close, open);
	    return regex.concat(
	      regex.concat("(?:", prefix, ")"),
	      open,
	      /(?:\\.|[^\\\/])*?/,
	      middle,
	      /(?:\\.|[^\\\/])*?/,
	      close,
	      REGEX_MODIFIERS
	    );
	  };
	  /**
	   * @param {string|RegExp} prefix
	   * @param {string|RegExp} open
	   * @param {string|RegExp} close
	   */
	  const PAIRED_RE = (prefix, open, close) => {
	    return regex.concat(
	      regex.concat("(?:", prefix, ")"),
	      open,
	      /(?:\\.|[^\\\/])*?/,
	      close,
	      REGEX_MODIFIERS
	    );
	  };
	  const PERL_DEFAULT_CONTAINS = [
	    VAR,
	    hljs.HASH_COMMENT_MODE,
	    hljs.COMMENT(
	      /^=\w/,
	      /=cut/,
	      { endsWithParent: true }
	    ),
	    METHOD,
	    {
	      className: 'string',
	      contains: STRING_CONTAINS,
	      variants: [
	        {
	          begin: 'q[qwxr]?\\s*\\(',
	          end: '\\)',
	          relevance: 5
	        },
	        {
	          begin: 'q[qwxr]?\\s*\\[',
	          end: '\\]',
	          relevance: 5
	        },
	        {
	          begin: 'q[qwxr]?\\s*\\{',
	          end: '\\}',
	          relevance: 5
	        },
	        {
	          begin: 'q[qwxr]?\\s*\\|',
	          end: '\\|',
	          relevance: 5
	        },
	        {
	          begin: 'q[qwxr]?\\s*<',
	          end: '>',
	          relevance: 5
	        },
	        {
	          begin: 'qw\\s+q',
	          end: 'q',
	          relevance: 5
	        },
	        {
	          begin: '\'',
	          end: '\'',
	          contains: [ hljs.BACKSLASH_ESCAPE ]
	        },
	        {
	          begin: '"',
	          end: '"'
	        },
	        {
	          begin: '`',
	          end: '`',
	          contains: [ hljs.BACKSLASH_ESCAPE ]
	        },
	        {
	          begin: /\{\w+\}/,
	          relevance: 0
	        },
	        {
	          begin: '-?\\w+\\s*=>',
	          relevance: 0
	        }
	      ]
	    },
	    {
	      className: 'number',
	      begin: '(\\b0[0-7_]+)|(\\b0x[0-9a-fA-F_]+)|(\\b[1-9][0-9_]*(\\.[0-9_]+)?)|[0_]\\b',
	      relevance: 0
	    },
	    { // regexp container
	      begin: '(\\/\\/|' + hljs.RE_STARTERS_RE + '|\\b(split|return|print|reverse|grep)\\b)\\s*',
	      keywords: 'split return print reverse grep',
	      relevance: 0,
	      contains: [
	        hljs.HASH_COMMENT_MODE,
	        {
	          className: 'regexp',
	          variants: [
	            // allow matching common delimiters
	            { begin: PAIRED_DOUBLE_RE("s|tr|y", regex.either(...REGEX_DELIMS, { capture: true })) },
	            // and then paired delmis
	            { begin: PAIRED_DOUBLE_RE("s|tr|y", "\\(", "\\)") },
	            { begin: PAIRED_DOUBLE_RE("s|tr|y", "\\[", "\\]") },
	            { begin: PAIRED_DOUBLE_RE("s|tr|y", "\\{", "\\}") }
	          ],
	          relevance: 2
	        },
	        {
	          className: 'regexp',
	          variants: [
	            {
	              // could be a comment in many languages so do not count
	              // as relevant
	              begin: /(m|qr)\/\//,
	              relevance: 0
	            },
	            // prefix is optional with /regex/
	            { begin: PAIRED_RE("(?:m|qr)?", /\//, /\//) },
	            // allow matching common delimiters
	            { begin: PAIRED_RE("m|qr", regex.either(...REGEX_DELIMS, { capture: true }), /\1/) },
	            // allow common paired delmins
	            { begin: PAIRED_RE("m|qr", /\(/, /\)/) },
	            { begin: PAIRED_RE("m|qr", /\[/, /\]/) },
	            { begin: PAIRED_RE("m|qr", /\{/, /\}/) }
	          ]
	        }
	      ]
	    },
	    {
	      className: 'function',
	      beginKeywords: 'sub',
	      end: '(\\s*\\(.*?\\))?[;{]',
	      excludeEnd: true,
	      relevance: 5,
	      contains: [ hljs.TITLE_MODE ]
	    },
	    {
	      begin: '-\\w\\b',
	      relevance: 0
	    },
	    {
	      begin: "^__DATA__$",
	      end: "^__END__$",
	      subLanguage: 'mojolicious',
	      contains: [
	        {
	          begin: "^@@.*",
	          end: "$",
	          className: "comment"
	        }
	      ]
	    }
	  ];
	  SUBST.contains = PERL_DEFAULT_CONTAINS;
	  METHOD.contains = PERL_DEFAULT_CONTAINS;

	  return {
	    name: 'Perl',
	    aliases: [
	      'pl',
	      'pm'
	    ],
	    keywords: PERL_KEYWORDS,
	    contains: PERL_DEFAULT_CONTAINS
	  };
	}

	perl_1 = perl;
	return perl_1;
}

/*
Language: Objective-C
Author: Valerii Hiora <valerii.hiora@gmail.com>
Contributors: Angel G. Olloqui <angelgarcia.mail@gmail.com>, Matt Diephouse <matt@diephouse.com>, Andrew Farmer <ahfarmer@gmail.com>, Minh Nguyễn <mxn@1ec5.org>
Website: https://developer.apple.com/documentation/objectivec
Category: common
*/

var objectivec_1;
var hasRequiredObjectivec;

function requireObjectivec () {
	if (hasRequiredObjectivec) return objectivec_1;
	hasRequiredObjectivec = 1;
	function objectivec(hljs) {
	  const API_CLASS = {
	    className: 'built_in',
	    begin: '\\b(AV|CA|CF|CG|CI|CL|CM|CN|CT|MK|MP|MTK|MTL|NS|SCN|SK|UI|WK|XC)\\w+'
	  };
	  const IDENTIFIER_RE = /[a-zA-Z@][a-zA-Z0-9_]*/;
	  const TYPES = [
	    "int",
	    "float",
	    "char",
	    "unsigned",
	    "signed",
	    "short",
	    "long",
	    "double",
	    "wchar_t",
	    "unichar",
	    "void",
	    "bool",
	    "BOOL",
	    "id|0",
	    "_Bool"
	  ];
	  const KWS = [
	    "while",
	    "export",
	    "sizeof",
	    "typedef",
	    "const",
	    "struct",
	    "for",
	    "union",
	    "volatile",
	    "static",
	    "mutable",
	    "if",
	    "do",
	    "return",
	    "goto",
	    "enum",
	    "else",
	    "break",
	    "extern",
	    "asm",
	    "case",
	    "default",
	    "register",
	    "explicit",
	    "typename",
	    "switch",
	    "continue",
	    "inline",
	    "readonly",
	    "assign",
	    "readwrite",
	    "self",
	    "@synchronized",
	    "id",
	    "typeof",
	    "nonatomic",
	    "IBOutlet",
	    "IBAction",
	    "strong",
	    "weak",
	    "copy",
	    "in",
	    "out",
	    "inout",
	    "bycopy",
	    "byref",
	    "oneway",
	    "__strong",
	    "__weak",
	    "__block",
	    "__autoreleasing",
	    "@private",
	    "@protected",
	    "@public",
	    "@try",
	    "@property",
	    "@end",
	    "@throw",
	    "@catch",
	    "@finally",
	    "@autoreleasepool",
	    "@synthesize",
	    "@dynamic",
	    "@selector",
	    "@optional",
	    "@required",
	    "@encode",
	    "@package",
	    "@import",
	    "@defs",
	    "@compatibility_alias",
	    "__bridge",
	    "__bridge_transfer",
	    "__bridge_retained",
	    "__bridge_retain",
	    "__covariant",
	    "__contravariant",
	    "__kindof",
	    "_Nonnull",
	    "_Nullable",
	    "_Null_unspecified",
	    "__FUNCTION__",
	    "__PRETTY_FUNCTION__",
	    "__attribute__",
	    "getter",
	    "setter",
	    "retain",
	    "unsafe_unretained",
	    "nonnull",
	    "nullable",
	    "null_unspecified",
	    "null_resettable",
	    "class",
	    "instancetype",
	    "NS_DESIGNATED_INITIALIZER",
	    "NS_UNAVAILABLE",
	    "NS_REQUIRES_SUPER",
	    "NS_RETURNS_INNER_POINTER",
	    "NS_INLINE",
	    "NS_AVAILABLE",
	    "NS_DEPRECATED",
	    "NS_ENUM",
	    "NS_OPTIONS",
	    "NS_SWIFT_UNAVAILABLE",
	    "NS_ASSUME_NONNULL_BEGIN",
	    "NS_ASSUME_NONNULL_END",
	    "NS_REFINED_FOR_SWIFT",
	    "NS_SWIFT_NAME",
	    "NS_SWIFT_NOTHROW",
	    "NS_DURING",
	    "NS_HANDLER",
	    "NS_ENDHANDLER",
	    "NS_VALUERETURN",
	    "NS_VOIDRETURN"
	  ];
	  const LITERALS = [
	    "false",
	    "true",
	    "FALSE",
	    "TRUE",
	    "nil",
	    "YES",
	    "NO",
	    "NULL"
	  ];
	  const BUILT_INS = [
	    "dispatch_once_t",
	    "dispatch_queue_t",
	    "dispatch_sync",
	    "dispatch_async",
	    "dispatch_once"
	  ];
	  const KEYWORDS = {
	    "variable.language": [
	      "this",
	      "super"
	    ],
	    $pattern: IDENTIFIER_RE,
	    keyword: KWS,
	    literal: LITERALS,
	    built_in: BUILT_INS,
	    type: TYPES
	  };
	  const CLASS_KEYWORDS = {
	    $pattern: IDENTIFIER_RE,
	    keyword: [
	      "@interface",
	      "@class",
	      "@protocol",
	      "@implementation"
	    ]
	  };
	  return {
	    name: 'Objective-C',
	    aliases: [
	      'mm',
	      'objc',
	      'obj-c',
	      'obj-c++',
	      'objective-c++'
	    ],
	    keywords: KEYWORDS,
	    illegal: '</',
	    contains: [
	      API_CLASS,
	      hljs.C_LINE_COMMENT_MODE,
	      hljs.C_BLOCK_COMMENT_MODE,
	      hljs.C_NUMBER_MODE,
	      hljs.QUOTE_STRING_MODE,
	      hljs.APOS_STRING_MODE,
	      {
	        className: 'string',
	        variants: [
	          {
	            begin: '@"',
	            end: '"',
	            illegal: '\\n',
	            contains: [ hljs.BACKSLASH_ESCAPE ]
	          }
	        ]
	      },
	      {
	        className: 'meta',
	        begin: /#\s*[a-z]+\b/,
	        end: /$/,
	        keywords: { keyword:
	            'if else elif endif define undef warning error line '
	            + 'pragma ifdef ifndef include' },
	        contains: [
	          {
	            begin: /\\\n/,
	            relevance: 0
	          },
	          hljs.inherit(hljs.QUOTE_STRING_MODE, { className: 'string' }),
	          {
	            className: 'string',
	            begin: /<.*?>/,
	            end: /$/,
	            illegal: '\\n'
	          },
	          hljs.C_LINE_COMMENT_MODE,
	          hljs.C_BLOCK_COMMENT_MODE
	        ]
	      },
	      {
	        className: 'class',
	        begin: '(' + CLASS_KEYWORDS.keyword.join('|') + ')\\b',
	        end: /(\{|$)/,
	        excludeEnd: true,
	        keywords: CLASS_KEYWORDS,
	        contains: [ hljs.UNDERSCORE_TITLE_MODE ]
	      },
	      {
	        begin: '\\.' + hljs.UNDERSCORE_IDENT_RE,
	        relevance: 0
	      }
	    ]
	  };
	}

	objectivec_1 = objectivec;
	return objectivec_1;
}

/*
Language: PHP
Author: Victor Karamzin <Victor.Karamzin@enterra-inc.com>
Contributors: Evgeny Stepanischev <imbolk@gmail.com>, Ivan Sagalaev <maniac@softwaremaniacs.org>
Website: https://www.php.net
Category: common
*/

var php_1;
var hasRequiredPhp;

function requirePhp () {
	if (hasRequiredPhp) return php_1;
	hasRequiredPhp = 1;
	/**
	 * @param {HLJSApi} hljs
	 * @returns {LanguageDetail}
	 * */
	function php(hljs) {
	  const regex = hljs.regex;
	  // negative look-ahead tries to avoid matching patterns that are not
	  // Perl at all like $ident$, @ident@, etc.
	  const NOT_PERL_ETC = /(?![A-Za-z0-9])(?![$])/;
	  const IDENT_RE = regex.concat(
	    /[a-zA-Z_\x7f-\xff][a-zA-Z0-9_\x7f-\xff]*/,
	    NOT_PERL_ETC);
	  // Will not detect camelCase classes
	  const PASCAL_CASE_CLASS_NAME_RE = regex.concat(
	    /(\\?[A-Z][a-z0-9_\x7f-\xff]+|\\?[A-Z]+(?=[A-Z][a-z0-9_\x7f-\xff])){1,}/,
	    NOT_PERL_ETC);
	  const VARIABLE = {
	    scope: 'variable',
	    match: '\\$+' + IDENT_RE,
	  };
	  const PREPROCESSOR = {
	    scope: 'meta',
	    variants: [
	      { begin: /<\?php/, relevance: 10 }, // boost for obvious PHP
	      { begin: /<\?=/ },
	      // less relevant per PSR-1 which says not to use short-tags
	      { begin: /<\?/, relevance: 0.1 },
	      { begin: /\?>/ } // end php tag
	    ]
	  };
	  const SUBST = {
	    scope: 'subst',
	    variants: [
	      { begin: /\$\w+/ },
	      {
	        begin: /\{\$/,
	        end: /\}/
	      }
	    ]
	  };
	  const SINGLE_QUOTED = hljs.inherit(hljs.APOS_STRING_MODE, { illegal: null, });
	  const DOUBLE_QUOTED = hljs.inherit(hljs.QUOTE_STRING_MODE, {
	    illegal: null,
	    contains: hljs.QUOTE_STRING_MODE.contains.concat(SUBST),
	  });

	  const HEREDOC = {
	    begin: /<<<[ \t]*(?:(\w+)|"(\w+)")\n/,
	    end: /[ \t]*(\w+)\b/,
	    contains: hljs.QUOTE_STRING_MODE.contains.concat(SUBST),
	    'on:begin': (m, resp) => { resp.data._beginMatch = m[1] || m[2]; },
	    'on:end': (m, resp) => { if (resp.data._beginMatch !== m[1]) resp.ignoreMatch(); },
	  };

	  const NOWDOC = hljs.END_SAME_AS_BEGIN({
	    begin: /<<<[ \t]*'(\w+)'\n/,
	    end: /[ \t]*(\w+)\b/,
	  });
	  // list of valid whitespaces because non-breaking space might be part of a IDENT_RE
	  const WHITESPACE = '[ \t\n]';
	  const STRING = {
	    scope: 'string',
	    variants: [
	      DOUBLE_QUOTED,
	      SINGLE_QUOTED,
	      HEREDOC,
	      NOWDOC
	    ]
	  };
	  const NUMBER = {
	    scope: 'number',
	    variants: [
	      { begin: `\\b0[bB][01]+(?:_[01]+)*\\b` }, // Binary w/ underscore support
	      { begin: `\\b0[oO][0-7]+(?:_[0-7]+)*\\b` }, // Octals w/ underscore support
	      { begin: `\\b0[xX][\\da-fA-F]+(?:_[\\da-fA-F]+)*\\b` }, // Hex w/ underscore support
	      // Decimals w/ underscore support, with optional fragments and scientific exponent (e) suffix.
	      { begin: `(?:\\b\\d+(?:_\\d+)*(\\.(?:\\d+(?:_\\d+)*))?|\\B\\.\\d+)(?:[eE][+-]?\\d+)?` }
	    ],
	    relevance: 0
	  };
	  const LITERALS = [
	    "false",
	    "null",
	    "true"
	  ];
	  const KWS = [
	    // Magic constants:
	    // <https://www.php.net/manual/en/language.constants.predefined.php>
	    "__CLASS__",
	    "__DIR__",
	    "__FILE__",
	    "__FUNCTION__",
	    "__COMPILER_HALT_OFFSET__",
	    "__LINE__",
	    "__METHOD__",
	    "__NAMESPACE__",
	    "__TRAIT__",
	    // Function that look like language construct or language construct that look like function:
	    // List of keywords that may not require parenthesis
	    "die",
	    "echo",
	    "exit",
	    "include",
	    "include_once",
	    "print",
	    "require",
	    "require_once",
	    // These are not language construct (function) but operate on the currently-executing function and can access the current symbol table
	    // 'compact extract func_get_arg func_get_args func_num_args get_called_class get_parent_class ' +
	    // Other keywords:
	    // <https://www.php.net/manual/en/reserved.php>
	    // <https://www.php.net/manual/en/language.types.type-juggling.php>
	    "array",
	    "abstract",
	    "and",
	    "as",
	    "binary",
	    "bool",
	    "boolean",
	    "break",
	    "callable",
	    "case",
	    "catch",
	    "class",
	    "clone",
	    "const",
	    "continue",
	    "declare",
	    "default",
	    "do",
	    "double",
	    "else",
	    "elseif",
	    "empty",
	    "enddeclare",
	    "endfor",
	    "endforeach",
	    "endif",
	    "endswitch",
	    "endwhile",
	    "enum",
	    "eval",
	    "extends",
	    "final",
	    "finally",
	    "float",
	    "for",
	    "foreach",
	    "from",
	    "global",
	    "goto",
	    "if",
	    "implements",
	    "instanceof",
	    "insteadof",
	    "int",
	    "integer",
	    "interface",
	    "isset",
	    "iterable",
	    "list",
	    "match|0",
	    "mixed",
	    "new",
	    "never",
	    "object",
	    "or",
	    "private",
	    "protected",
	    "public",
	    "readonly",
	    "real",
	    "return",
	    "string",
	    "switch",
	    "throw",
	    "trait",
	    "try",
	    "unset",
	    "use",
	    "var",
	    "void",
	    "while",
	    "xor",
	    "yield"
	  ];

	  const BUILT_INS = [
	    // Standard PHP library:
	    // <https://www.php.net/manual/en/book.spl.php>
	    "Error|0",
	    "AppendIterator",
	    "ArgumentCountError",
	    "ArithmeticError",
	    "ArrayIterator",
	    "ArrayObject",
	    "AssertionError",
	    "BadFunctionCallException",
	    "BadMethodCallException",
	    "CachingIterator",
	    "CallbackFilterIterator",
	    "CompileError",
	    "Countable",
	    "DirectoryIterator",
	    "DivisionByZeroError",
	    "DomainException",
	    "EmptyIterator",
	    "ErrorException",
	    "Exception",
	    "FilesystemIterator",
	    "FilterIterator",
	    "GlobIterator",
	    "InfiniteIterator",
	    "InvalidArgumentException",
	    "IteratorIterator",
	    "LengthException",
	    "LimitIterator",
	    "LogicException",
	    "MultipleIterator",
	    "NoRewindIterator",
	    "OutOfBoundsException",
	    "OutOfRangeException",
	    "OuterIterator",
	    "OverflowException",
	    "ParentIterator",
	    "ParseError",
	    "RangeException",
	    "RecursiveArrayIterator",
	    "RecursiveCachingIterator",
	    "RecursiveCallbackFilterIterator",
	    "RecursiveDirectoryIterator",
	    "RecursiveFilterIterator",
	    "RecursiveIterator",
	    "RecursiveIteratorIterator",
	    "RecursiveRegexIterator",
	    "RecursiveTreeIterator",
	    "RegexIterator",
	    "RuntimeException",
	    "SeekableIterator",
	    "SplDoublyLinkedList",
	    "SplFileInfo",
	    "SplFileObject",
	    "SplFixedArray",
	    "SplHeap",
	    "SplMaxHeap",
	    "SplMinHeap",
	    "SplObjectStorage",
	    "SplObserver",
	    "SplPriorityQueue",
	    "SplQueue",
	    "SplStack",
	    "SplSubject",
	    "SplTempFileObject",
	    "TypeError",
	    "UnderflowException",
	    "UnexpectedValueException",
	    "UnhandledMatchError",
	    // Reserved interfaces:
	    // <https://www.php.net/manual/en/reserved.interfaces.php>
	    "ArrayAccess",
	    "BackedEnum",
	    "Closure",
	    "Fiber",
	    "Generator",
	    "Iterator",
	    "IteratorAggregate",
	    "Serializable",
	    "Stringable",
	    "Throwable",
	    "Traversable",
	    "UnitEnum",
	    "WeakReference",
	    "WeakMap",
	    // Reserved classes:
	    // <https://www.php.net/manual/en/reserved.classes.php>
	    "Directory",
	    "__PHP_Incomplete_Class",
	    "parent",
	    "php_user_filter",
	    "self",
	    "static",
	    "stdClass"
	  ];

	  /** Dual-case keywords
	   *
	   * ["then","FILE"] =>
	   *     ["then", "THEN", "FILE", "file"]
	   *
	   * @param {string[]} items */
	  const dualCase = (items) => {
	    /** @type string[] */
	    const result = [];
	    items.forEach(item => {
	      result.push(item);
	      if (item.toLowerCase() === item) {
	        result.push(item.toUpperCase());
	      } else {
	        result.push(item.toLowerCase());
	      }
	    });
	    return result;
	  };

	  const KEYWORDS = {
	    keyword: KWS,
	    literal: dualCase(LITERALS),
	    built_in: BUILT_INS,
	  };

	  /**
	   * @param {string[]} items */
	  const normalizeKeywords = (items) => {
	    return items.map(item => {
	      return item.replace(/\|\d+$/, "");
	    });
	  };

	  const CONSTRUCTOR_CALL = { variants: [
	    {
	      match: [
	        /new/,
	        regex.concat(WHITESPACE, "+"),
	        // to prevent built ins from being confused as the class constructor call
	        regex.concat("(?!", normalizeKeywords(BUILT_INS).join("\\b|"), "\\b)"),
	        PASCAL_CASE_CLASS_NAME_RE,
	      ],
	      scope: {
	        1: "keyword",
	        4: "title.class",
	      },
	    }
	  ] };

	  const CONSTANT_REFERENCE = regex.concat(IDENT_RE, "\\b(?!\\()");

	  const LEFT_AND_RIGHT_SIDE_OF_DOUBLE_COLON = { variants: [
	    {
	      match: [
	        regex.concat(
	          /::/,
	          regex.lookahead(/(?!class\b)/)
	        ),
	        CONSTANT_REFERENCE,
	      ],
	      scope: { 2: "variable.constant", },
	    },
	    {
	      match: [
	        /::/,
	        /class/,
	      ],
	      scope: { 2: "variable.language", },
	    },
	    {
	      match: [
	        PASCAL_CASE_CLASS_NAME_RE,
	        regex.concat(
	          /::/,
	          regex.lookahead(/(?!class\b)/)
	        ),
	        CONSTANT_REFERENCE,
	      ],
	      scope: {
	        1: "title.class",
	        3: "variable.constant",
	      },
	    },
	    {
	      match: [
	        PASCAL_CASE_CLASS_NAME_RE,
	        regex.concat(
	          "::",
	          regex.lookahead(/(?!class\b)/)
	        ),
	      ],
	      scope: { 1: "title.class", },
	    },
	    {
	      match: [
	        PASCAL_CASE_CLASS_NAME_RE,
	        /::/,
	        /class/,
	      ],
	      scope: {
	        1: "title.class",
	        3: "variable.language",
	      },
	    }
	  ] };

	  const NAMED_ARGUMENT = {
	    scope: 'attr',
	    match: regex.concat(IDENT_RE, regex.lookahead(':'), regex.lookahead(/(?!::)/)),
	  };
	  const PARAMS_MODE = {
	    relevance: 0,
	    begin: /\(/,
	    end: /\)/,
	    keywords: KEYWORDS,
	    contains: [
	      NAMED_ARGUMENT,
	      VARIABLE,
	      LEFT_AND_RIGHT_SIDE_OF_DOUBLE_COLON,
	      hljs.C_BLOCK_COMMENT_MODE,
	      STRING,
	      NUMBER,
	      CONSTRUCTOR_CALL,
	    ],
	  };
	  const FUNCTION_INVOKE = {
	    relevance: 0,
	    match: [
	      /\b/,
	      // to prevent keywords from being confused as the function title
	      regex.concat("(?!fn\\b|function\\b|", normalizeKeywords(KWS).join("\\b|"), "|", normalizeKeywords(BUILT_INS).join("\\b|"), "\\b)"),
	      IDENT_RE,
	      regex.concat(WHITESPACE, "*"),
	      regex.lookahead(/(?=\()/)
	    ],
	    scope: { 3: "title.function.invoke", },
	    contains: [ PARAMS_MODE ]
	  };
	  PARAMS_MODE.contains.push(FUNCTION_INVOKE);

	  const ATTRIBUTE_CONTAINS = [
	    NAMED_ARGUMENT,
	    LEFT_AND_RIGHT_SIDE_OF_DOUBLE_COLON,
	    hljs.C_BLOCK_COMMENT_MODE,
	    STRING,
	    NUMBER,
	    CONSTRUCTOR_CALL,
	  ];

	  const ATTRIBUTES = {
	    begin: regex.concat(/#\[\s*/, PASCAL_CASE_CLASS_NAME_RE),
	    beginScope: "meta",
	    end: /]/,
	    endScope: "meta",
	    keywords: {
	      literal: LITERALS,
	      keyword: [
	        'new',
	        'array',
	      ]
	    },
	    contains: [
	      {
	        begin: /\[/,
	        end: /]/,
	        keywords: {
	          literal: LITERALS,
	          keyword: [
	            'new',
	            'array',
	          ]
	        },
	        contains: [
	          'self',
	          ...ATTRIBUTE_CONTAINS,
	        ]
	      },
	      ...ATTRIBUTE_CONTAINS,
	      {
	        scope: 'meta',
	        match: PASCAL_CASE_CLASS_NAME_RE
	      }
	    ]
	  };

	  return {
	    case_insensitive: false,
	    keywords: KEYWORDS,
	    contains: [
	      ATTRIBUTES,
	      hljs.HASH_COMMENT_MODE,
	      hljs.COMMENT('//', '$'),
	      hljs.COMMENT(
	        '/\\*',
	        '\\*/',
	        { contains: [
	          {
	            scope: 'doctag',
	            match: '@[A-Za-z]+'
	          }
	        ] }
	      ),
	      {
	        match: /__halt_compiler\(\);/,
	        keywords: '__halt_compiler',
	        starts: {
	          scope: "comment",
	          end: hljs.MATCH_NOTHING_RE,
	          contains: [
	            {
	              match: /\?>/,
	              scope: "meta",
	              endsParent: true
	            }
	          ]
	        }
	      },
	      PREPROCESSOR,
	      {
	        scope: 'variable.language',
	        match: /\$this\b/
	      },
	      VARIABLE,
	      FUNCTION_INVOKE,
	      LEFT_AND_RIGHT_SIDE_OF_DOUBLE_COLON,
	      {
	        match: [
	          /const/,
	          /\s/,
	          IDENT_RE,
	        ],
	        scope: {
	          1: "keyword",
	          3: "variable.constant",
	        },
	      },
	      CONSTRUCTOR_CALL,
	      {
	        scope: 'function',
	        relevance: 0,
	        beginKeywords: 'fn function',
	        end: /[;{]/,
	        excludeEnd: true,
	        illegal: '[$%\\[]',
	        contains: [
	          { beginKeywords: 'use', },
	          hljs.UNDERSCORE_TITLE_MODE,
	          {
	            begin: '=>', // No markup, just a relevance booster
	            endsParent: true
	          },
	          {
	            scope: 'params',
	            begin: '\\(',
	            end: '\\)',
	            excludeBegin: true,
	            excludeEnd: true,
	            keywords: KEYWORDS,
	            contains: [
	              'self',
	              VARIABLE,
	              LEFT_AND_RIGHT_SIDE_OF_DOUBLE_COLON,
	              hljs.C_BLOCK_COMMENT_MODE,
	              STRING,
	              NUMBER
	            ]
	          },
	        ]
	      },
	      {
	        scope: 'class',
	        variants: [
	          {
	            beginKeywords: "enum",
	            illegal: /[($"]/
	          },
	          {
	            beginKeywords: "class interface trait",
	            illegal: /[:($"]/
	          }
	        ],
	        relevance: 0,
	        end: /\{/,
	        excludeEnd: true,
	        contains: [
	          { beginKeywords: 'extends implements' },
	          hljs.UNDERSCORE_TITLE_MODE
	        ]
	      },
	      // both use and namespace still use "old style" rules (vs multi-match)
	      // because the namespace name can include `\` and we still want each
	      // element to be treated as its own *individual* title
	      {
	        beginKeywords: 'namespace',
	        relevance: 0,
	        end: ';',
	        illegal: /[.']/,
	        contains: [ hljs.inherit(hljs.UNDERSCORE_TITLE_MODE, { scope: "title.class" }) ]
	      },
	      {
	        beginKeywords: 'use',
	        relevance: 0,
	        end: ';',
	        contains: [
	          // TODO: title.function vs title.class
	          {
	            match: /\b(as|const|function)\b/,
	            scope: "keyword"
	          },
	          // TODO: could be title.class or title.function
	          hljs.UNDERSCORE_TITLE_MODE
	        ]
	      },
	      STRING,
	      NUMBER,
	    ]
	  };
	}

	php_1 = php;
	return php_1;
}

/*
Language: PHP Template
Requires: xml.js, php.js
Author: Josh Goebel <hello@joshgoebel.com>
Website: https://www.php.net
Category: common
*/

var phpTemplate_1;
var hasRequiredPhpTemplate;

function requirePhpTemplate () {
	if (hasRequiredPhpTemplate) return phpTemplate_1;
	hasRequiredPhpTemplate = 1;
	function phpTemplate(hljs) {
	  return {
	    name: "PHP template",
	    subLanguage: 'xml',
	    contains: [
	      {
	        begin: /<\?(php|=)?/,
	        end: /\?>/,
	        subLanguage: 'php',
	        contains: [
	          // We don't want the php closing tag ?> to close the PHP block when
	          // inside any of the following blocks:
	          {
	            begin: '/\\*',
	            end: '\\*/',
	            skip: true
	          },
	          {
	            begin: 'b"',
	            end: '"',
	            skip: true
	          },
	          {
	            begin: 'b\'',
	            end: '\'',
	            skip: true
	          },
	          hljs.inherit(hljs.APOS_STRING_MODE, {
	            illegal: null,
	            className: null,
	            contains: null,
	            skip: true
	          }),
	          hljs.inherit(hljs.QUOTE_STRING_MODE, {
	            illegal: null,
	            className: null,
	            contains: null,
	            skip: true
	          })
	        ]
	      }
	    ]
	  };
	}

	phpTemplate_1 = phpTemplate;
	return phpTemplate_1;
}

/*
Language: Plain text
Author: Egor Rogov (e.rogov@postgrespro.ru)
Description: Plain text without any highlighting.
Category: common
*/

var plaintext_1;
var hasRequiredPlaintext;

function requirePlaintext () {
	if (hasRequiredPlaintext) return plaintext_1;
	hasRequiredPlaintext = 1;
	function plaintext(hljs) {
	  return {
	    name: 'Plain text',
	    aliases: [
	      'text',
	      'txt'
	    ],
	    disableAutodetect: true
	  };
	}

	plaintext_1 = plaintext;
	return plaintext_1;
}

/*
Language: Python
Description: Python is an interpreted, object-oriented, high-level programming language with dynamic semantics.
Website: https://www.python.org
Category: common
*/

var python_1;
var hasRequiredPython;

function requirePython () {
	if (hasRequiredPython) return python_1;
	hasRequiredPython = 1;
	function python(hljs) {
	  const regex = hljs.regex;
	  const IDENT_RE = /[\p{XID_Start}_]\p{XID_Continue}*/u;
	  const RESERVED_WORDS = [
	    'and',
	    'as',
	    'assert',
	    'async',
	    'await',
	    'break',
	    'case',
	    'class',
	    'continue',
	    'def',
	    'del',
	    'elif',
	    'else',
	    'except',
	    'finally',
	    'for',
	    'from',
	    'global',
	    'if',
	    'import',
	    'in',
	    'is',
	    'lambda',
	    'match',
	    'nonlocal|10',
	    'not',
	    'or',
	    'pass',
	    'raise',
	    'return',
	    'try',
	    'while',
	    'with',
	    'yield'
	  ];

	  const BUILT_INS = [
	    '__import__',
	    'abs',
	    'all',
	    'any',
	    'ascii',
	    'bin',
	    'bool',
	    'breakpoint',
	    'bytearray',
	    'bytes',
	    'callable',
	    'chr',
	    'classmethod',
	    'compile',
	    'complex',
	    'delattr',
	    'dict',
	    'dir',
	    'divmod',
	    'enumerate',
	    'eval',
	    'exec',
	    'filter',
	    'float',
	    'format',
	    'frozenset',
	    'getattr',
	    'globals',
	    'hasattr',
	    'hash',
	    'help',
	    'hex',
	    'id',
	    'input',
	    'int',
	    'isinstance',
	    'issubclass',
	    'iter',
	    'len',
	    'list',
	    'locals',
	    'map',
	    'max',
	    'memoryview',
	    'min',
	    'next',
	    'object',
	    'oct',
	    'open',
	    'ord',
	    'pow',
	    'print',
	    'property',
	    'range',
	    'repr',
	    'reversed',
	    'round',
	    'set',
	    'setattr',
	    'slice',
	    'sorted',
	    'staticmethod',
	    'str',
	    'sum',
	    'super',
	    'tuple',
	    'type',
	    'vars',
	    'zip'
	  ];

	  const LITERALS = [
	    '__debug__',
	    'Ellipsis',
	    'False',
	    'None',
	    'NotImplemented',
	    'True'
	  ];

	  // https://docs.python.org/3/library/typing.html
	  // TODO: Could these be supplemented by a CamelCase matcher in certain
	  // contexts, leaving these remaining only for relevance hinting?
	  const TYPES = [
	    "Any",
	    "Callable",
	    "Coroutine",
	    "Dict",
	    "List",
	    "Literal",
	    "Generic",
	    "Optional",
	    "Sequence",
	    "Set",
	    "Tuple",
	    "Type",
	    "Union"
	  ];

	  const KEYWORDS = {
	    $pattern: /[A-Za-z]\w+|__\w+__/,
	    keyword: RESERVED_WORDS,
	    built_in: BUILT_INS,
	    literal: LITERALS,
	    type: TYPES
	  };

	  const PROMPT = {
	    className: 'meta',
	    begin: /^(>>>|\.\.\.) /
	  };

	  const SUBST = {
	    className: 'subst',
	    begin: /\{/,
	    end: /\}/,
	    keywords: KEYWORDS,
	    illegal: /#/
	  };

	  const LITERAL_BRACKET = {
	    begin: /\{\{/,
	    relevance: 0
	  };

	  const STRING = {
	    className: 'string',
	    contains: [ hljs.BACKSLASH_ESCAPE ],
	    variants: [
	      {
	        begin: /([uU]|[bB]|[rR]|[bB][rR]|[rR][bB])?'''/,
	        end: /'''/,
	        contains: [
	          hljs.BACKSLASH_ESCAPE,
	          PROMPT
	        ],
	        relevance: 10
	      },
	      {
	        begin: /([uU]|[bB]|[rR]|[bB][rR]|[rR][bB])?"""/,
	        end: /"""/,
	        contains: [
	          hljs.BACKSLASH_ESCAPE,
	          PROMPT
	        ],
	        relevance: 10
	      },
	      {
	        begin: /([fF][rR]|[rR][fF]|[fF])'''/,
	        end: /'''/,
	        contains: [
	          hljs.BACKSLASH_ESCAPE,
	          PROMPT,
	          LITERAL_BRACKET,
	          SUBST
	        ]
	      },
	      {
	        begin: /([fF][rR]|[rR][fF]|[fF])"""/,
	        end: /"""/,
	        contains: [
	          hljs.BACKSLASH_ESCAPE,
	          PROMPT,
	          LITERAL_BRACKET,
	          SUBST
	        ]
	      },
	      {
	        begin: /([uU]|[rR])'/,
	        end: /'/,
	        relevance: 10
	      },
	      {
	        begin: /([uU]|[rR])"/,
	        end: /"/,
	        relevance: 10
	      },
	      {
	        begin: /([bB]|[bB][rR]|[rR][bB])'/,
	        end: /'/
	      },
	      {
	        begin: /([bB]|[bB][rR]|[rR][bB])"/,
	        end: /"/
	      },
	      {
	        begin: /([fF][rR]|[rR][fF]|[fF])'/,
	        end: /'/,
	        contains: [
	          hljs.BACKSLASH_ESCAPE,
	          LITERAL_BRACKET,
	          SUBST
	        ]
	      },
	      {
	        begin: /([fF][rR]|[rR][fF]|[fF])"/,
	        end: /"/,
	        contains: [
	          hljs.BACKSLASH_ESCAPE,
	          LITERAL_BRACKET,
	          SUBST
	        ]
	      },
	      hljs.APOS_STRING_MODE,
	      hljs.QUOTE_STRING_MODE
	    ]
	  };

	  // https://docs.python.org/3.9/reference/lexical_analysis.html#numeric-literals
	  const digitpart = '[0-9](_?[0-9])*';
	  const pointfloat = `(\\b(${digitpart}))?\\.(${digitpart})|\\b(${digitpart})\\.`;
	  // Whitespace after a number (or any lexical token) is needed only if its absence
	  // would change the tokenization
	  // https://docs.python.org/3.9/reference/lexical_analysis.html#whitespace-between-tokens
	  // We deviate slightly, requiring a word boundary or a keyword
	  // to avoid accidentally recognizing *prefixes* (e.g., `0` in `0x41` or `08` or `0__1`)
	  const lookahead = `\\b|${RESERVED_WORDS.join('|')}`;
	  const NUMBER = {
	    className: 'number',
	    relevance: 0,
	    variants: [
	      // exponentfloat, pointfloat
	      // https://docs.python.org/3.9/reference/lexical_analysis.html#floating-point-literals
	      // optionally imaginary
	      // https://docs.python.org/3.9/reference/lexical_analysis.html#imaginary-literals
	      // Note: no leading \b because floats can start with a decimal point
	      // and we don't want to mishandle e.g. `fn(.5)`,
	      // no trailing \b for pointfloat because it can end with a decimal point
	      // and we don't want to mishandle e.g. `0..hex()`; this should be safe
	      // because both MUST contain a decimal point and so cannot be confused with
	      // the interior part of an identifier
	      {
	        begin: `(\\b(${digitpart})|(${pointfloat}))[eE][+-]?(${digitpart})[jJ]?(?=${lookahead})`
	      },
	      {
	        begin: `(${pointfloat})[jJ]?`
	      },

	      // decinteger, bininteger, octinteger, hexinteger
	      // https://docs.python.org/3.9/reference/lexical_analysis.html#integer-literals
	      // optionally "long" in Python 2
	      // https://docs.python.org/2.7/reference/lexical_analysis.html#integer-and-long-integer-literals
	      // decinteger is optionally imaginary
	      // https://docs.python.org/3.9/reference/lexical_analysis.html#imaginary-literals
	      {
	        begin: `\\b([1-9](_?[0-9])*|0+(_?0)*)[lLjJ]?(?=${lookahead})`
	      },
	      {
	        begin: `\\b0[bB](_?[01])+[lL]?(?=${lookahead})`
	      },
	      {
	        begin: `\\b0[oO](_?[0-7])+[lL]?(?=${lookahead})`
	      },
	      {
	        begin: `\\b0[xX](_?[0-9a-fA-F])+[lL]?(?=${lookahead})`
	      },

	      // imagnumber (digitpart-based)
	      // https://docs.python.org/3.9/reference/lexical_analysis.html#imaginary-literals
	      {
	        begin: `\\b(${digitpart})[jJ](?=${lookahead})`
	      }
	    ]
	  };
	  const COMMENT_TYPE = {
	    className: "comment",
	    begin: regex.lookahead(/# type:/),
	    end: /$/,
	    keywords: KEYWORDS,
	    contains: [
	      { // prevent keywords from coloring `type`
	        begin: /# type:/
	      },
	      // comment within a datatype comment includes no keywords
	      {
	        begin: /#/,
	        end: /\b\B/,
	        endsWithParent: true
	      }
	    ]
	  };
	  const PARAMS = {
	    className: 'params',
	    variants: [
	      // Exclude params in functions without params
	      {
	        className: "",
	        begin: /\(\s*\)/,
	        skip: true
	      },
	      {
	        begin: /\(/,
	        end: /\)/,
	        excludeBegin: true,
	        excludeEnd: true,
	        keywords: KEYWORDS,
	        contains: [
	          'self',
	          PROMPT,
	          NUMBER,
	          STRING,
	          hljs.HASH_COMMENT_MODE
	        ]
	      }
	    ]
	  };
	  SUBST.contains = [
	    STRING,
	    NUMBER,
	    PROMPT
	  ];

	  return {
	    name: 'Python',
	    aliases: [
	      'py',
	      'gyp',
	      'ipython'
	    ],
	    unicodeRegex: true,
	    keywords: KEYWORDS,
	    illegal: /(<\/|\?)|=>/,
	    contains: [
	      PROMPT,
	      NUMBER,
	      {
	        // very common convention
	        begin: /\bself\b/
	      },
	      {
	        // eat "if" prior to string so that it won't accidentally be
	        // labeled as an f-string
	        beginKeywords: "if",
	        relevance: 0
	      },
	      STRING,
	      COMMENT_TYPE,
	      hljs.HASH_COMMENT_MODE,
	      {
	        match: [
	          /\bdef/, /\s+/,
	          IDENT_RE,
	        ],
	        scope: {
	          1: "keyword",
	          3: "title.function"
	        },
	        contains: [ PARAMS ]
	      },
	      {
	        variants: [
	          {
	            match: [
	              /\bclass/, /\s+/,
	              IDENT_RE, /\s*/,
	              /\(\s*/, IDENT_RE,/\s*\)/
	            ],
	          },
	          {
	            match: [
	              /\bclass/, /\s+/,
	              IDENT_RE
	            ],
	          }
	        ],
	        scope: {
	          1: "keyword",
	          3: "title.class",
	          6: "title.class.inherited",
	        }
	      },
	      {
	        className: 'meta',
	        begin: /^[\t ]*@/,
	        end: /(?=#)|$/,
	        contains: [
	          NUMBER,
	          PARAMS,
	          STRING
	        ]
	      }
	    ]
	  };
	}

	python_1 = python;
	return python_1;
}

/*
Language: Python REPL
Requires: python.js
Author: Josh Goebel <hello@joshgoebel.com>
Category: common
*/

var pythonRepl_1;
var hasRequiredPythonRepl;

function requirePythonRepl () {
	if (hasRequiredPythonRepl) return pythonRepl_1;
	hasRequiredPythonRepl = 1;
	function pythonRepl(hljs) {
	  return {
	    aliases: [ 'pycon' ],
	    contains: [
	      {
	        className: 'meta.prompt',
	        starts: {
	          // a space separates the REPL prefix from the actual code
	          // this is purely for cleaner HTML output
	          end: / |$/,
	          starts: {
	            end: '$',
	            subLanguage: 'python'
	          }
	        },
	        variants: [
	          { begin: /^>>>(?=[ ]|$)/ },
	          { begin: /^\.\.\.(?=[ ]|$)/ }
	        ]
	      }
	    ]
	  };
	}

	pythonRepl_1 = pythonRepl;
	return pythonRepl_1;
}

/*
Language: R
Description: R is a free software environment for statistical computing and graphics.
Author: Joe Cheng <joe@rstudio.org>
Contributors: Konrad Rudolph <konrad.rudolph@gmail.com>
Website: https://www.r-project.org
Category: common,scientific
*/

var r_1;
var hasRequiredR;

function requireR () {
	if (hasRequiredR) return r_1;
	hasRequiredR = 1;
	/** @type LanguageFn */
	function r(hljs) {
	  const regex = hljs.regex;
	  // Identifiers in R cannot start with `_`, but they can start with `.` if it
	  // is not immediately followed by a digit.
	  // R also supports quoted identifiers, which are near-arbitrary sequences
	  // delimited by backticks (`…`), which may contain escape sequences. These are
	  // handled in a separate mode. See `test/markup/r/names.txt` for examples.
	  // FIXME: Support Unicode identifiers.
	  const IDENT_RE = /(?:(?:[a-zA-Z]|\.[._a-zA-Z])[._a-zA-Z0-9]*)|\.(?!\d)/;
	  const NUMBER_TYPES_RE = regex.either(
	    // Special case: only hexadecimal binary powers can contain fractions
	    /0[xX][0-9a-fA-F]+\.[0-9a-fA-F]*[pP][+-]?\d+i?/,
	    // Hexadecimal numbers without fraction and optional binary power
	    /0[xX][0-9a-fA-F]+(?:[pP][+-]?\d+)?[Li]?/,
	    // Decimal numbers
	    /(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?[Li]?/
	  );
	  const OPERATORS_RE = /[=!<>:]=|\|\||&&|:::?|<-|<<-|->>|->|\|>|[-+*\/?!$&|:<=>@^~]|\*\*/;
	  const PUNCTUATION_RE = regex.either(
	    /[()]/,
	    /[{}]/,
	    /\[\[/,
	    /[[\]]/,
	    /\\/,
	    /,/
	  );

	  return {
	    name: 'R',

	    keywords: {
	      $pattern: IDENT_RE,
	      keyword:
	        'function if in break next repeat else for while',
	      literal:
	        'NULL NA TRUE FALSE Inf NaN NA_integer_|10 NA_real_|10 '
	        + 'NA_character_|10 NA_complex_|10',
	      built_in:
	        // Builtin constants
	        'LETTERS letters month.abb month.name pi T F '
	        // Primitive functions
	        // These are all the functions in `base` that are implemented as a
	        // `.Primitive`, minus those functions that are also keywords.
	        + 'abs acos acosh all any anyNA Arg as.call as.character '
	        + 'as.complex as.double as.environment as.integer as.logical '
	        + 'as.null.default as.numeric as.raw asin asinh atan atanh attr '
	        + 'attributes baseenv browser c call ceiling class Conj cos cosh '
	        + 'cospi cummax cummin cumprod cumsum digamma dim dimnames '
	        + 'emptyenv exp expression floor forceAndCall gamma gc.time '
	        + 'globalenv Im interactive invisible is.array is.atomic is.call '
	        + 'is.character is.complex is.double is.environment is.expression '
	        + 'is.finite is.function is.infinite is.integer is.language '
	        + 'is.list is.logical is.matrix is.na is.name is.nan is.null '
	        + 'is.numeric is.object is.pairlist is.raw is.recursive is.single '
	        + 'is.symbol lazyLoadDBfetch length lgamma list log max min '
	        + 'missing Mod names nargs nzchar oldClass on.exit pos.to.env '
	        + 'proc.time prod quote range Re rep retracemem return round '
	        + 'seq_along seq_len seq.int sign signif sin sinh sinpi sqrt '
	        + 'standardGeneric substitute sum switch tan tanh tanpi tracemem '
	        + 'trigamma trunc unclass untracemem UseMethod xtfrm',
	    },

	    contains: [
	      // Roxygen comments
	      hljs.COMMENT(
	        /#'/,
	        /$/,
	        { contains: [
	          {
	            // Handle `@examples` separately to cause all subsequent code
	            // until the next `@`-tag on its own line to be kept as-is,
	            // preventing highlighting. This code is example R code, so nested
	            // doctags shouldn’t be treated as such. See
	            // `test/markup/r/roxygen.txt` for an example.
	            scope: 'doctag',
	            match: /@examples/,
	            starts: {
	              end: regex.lookahead(regex.either(
	                // end if another doc comment
	                /\n^#'\s*(?=@[a-zA-Z]+)/,
	                // or a line with no comment
	                /\n^(?!#')/
	              )),
	              endsParent: true
	            }
	          },
	          {
	            // Handle `@param` to highlight the parameter name following
	            // after.
	            scope: 'doctag',
	            begin: '@param',
	            end: /$/,
	            contains: [
	              {
	                scope: 'variable',
	                variants: [
	                  { match: IDENT_RE },
	                  { match: /`(?:\\.|[^`\\])+`/ }
	                ],
	                endsParent: true
	              }
	            ]
	          },
	          {
	            scope: 'doctag',
	            match: /@[a-zA-Z]+/
	          },
	          {
	            scope: 'keyword',
	            match: /\\[a-zA-Z]+/
	          }
	        ] }
	      ),

	      hljs.HASH_COMMENT_MODE,

	      {
	        scope: 'string',
	        contains: [ hljs.BACKSLASH_ESCAPE ],
	        variants: [
	          hljs.END_SAME_AS_BEGIN({
	            begin: /[rR]"(-*)\(/,
	            end: /\)(-*)"/
	          }),
	          hljs.END_SAME_AS_BEGIN({
	            begin: /[rR]"(-*)\{/,
	            end: /\}(-*)"/
	          }),
	          hljs.END_SAME_AS_BEGIN({
	            begin: /[rR]"(-*)\[/,
	            end: /\](-*)"/
	          }),
	          hljs.END_SAME_AS_BEGIN({
	            begin: /[rR]'(-*)\(/,
	            end: /\)(-*)'/
	          }),
	          hljs.END_SAME_AS_BEGIN({
	            begin: /[rR]'(-*)\{/,
	            end: /\}(-*)'/
	          }),
	          hljs.END_SAME_AS_BEGIN({
	            begin: /[rR]'(-*)\[/,
	            end: /\](-*)'/
	          }),
	          {
	            begin: '"',
	            end: '"',
	            relevance: 0
	          },
	          {
	            begin: "'",
	            end: "'",
	            relevance: 0
	          }
	        ],
	      },

	      // Matching numbers immediately following punctuation and operators is
	      // tricky since we need to look at the character ahead of a number to
	      // ensure the number is not part of an identifier, and we cannot use
	      // negative look-behind assertions. So instead we explicitly handle all
	      // possible combinations of (operator|punctuation), number.
	      // TODO: replace with negative look-behind when available
	      // { begin: /(?<![a-zA-Z0-9._])0[xX][0-9a-fA-F]+\.[0-9a-fA-F]*[pP][+-]?\d+i?/ },
	      // { begin: /(?<![a-zA-Z0-9._])0[xX][0-9a-fA-F]+([pP][+-]?\d+)?[Li]?/ },
	      // { begin: /(?<![a-zA-Z0-9._])(\d+(\.\d*)?|\.\d+)([eE][+-]?\d+)?[Li]?/ }
	      {
	        relevance: 0,
	        variants: [
	          {
	            scope: {
	              1: 'operator',
	              2: 'number'
	            },
	            match: [
	              OPERATORS_RE,
	              NUMBER_TYPES_RE
	            ]
	          },
	          {
	            scope: {
	              1: 'operator',
	              2: 'number'
	            },
	            match: [
	              /%[^%]*%/,
	              NUMBER_TYPES_RE
	            ]
	          },
	          {
	            scope: {
	              1: 'punctuation',
	              2: 'number'
	            },
	            match: [
	              PUNCTUATION_RE,
	              NUMBER_TYPES_RE
	            ]
	          },
	          {
	            scope: { 2: 'number' },
	            match: [
	              /[^a-zA-Z0-9._]|^/, // not part of an identifier, or start of document
	              NUMBER_TYPES_RE
	            ]
	          }
	        ]
	      },

	      // Operators/punctuation when they're not directly followed by numbers
	      {
	        // Relevance boost for the most common assignment form.
	        scope: { 3: 'operator' },
	        match: [
	          IDENT_RE,
	          /\s+/,
	          /<-/,
	          /\s+/
	        ]
	      },

	      {
	        scope: 'operator',
	        relevance: 0,
	        variants: [
	          { match: OPERATORS_RE },
	          { match: /%[^%]*%/ }
	        ]
	      },

	      {
	        scope: 'punctuation',
	        relevance: 0,
	        match: PUNCTUATION_RE
	      },

	      {
	        // Escaped identifier
	        begin: '`',
	        end: '`',
	        contains: [ { begin: /\\./ } ]
	      }
	    ]
	  };
	}

	r_1 = r;
	return r_1;
}

/*
Language: Rust
Author: Andrey Vlasovskikh <andrey.vlasovskikh@gmail.com>
Contributors: Roman Shmatov <romanshmatov@gmail.com>, Kasper Andersen <kma_untrusted@protonmail.com>
Website: https://www.rust-lang.org
Category: common, system
*/

var rust_1;
var hasRequiredRust;

function requireRust () {
	if (hasRequiredRust) return rust_1;
	hasRequiredRust = 1;
	/** @type LanguageFn */
	function rust(hljs) {
	  const regex = hljs.regex;
	  const FUNCTION_INVOKE = {
	    className: "title.function.invoke",
	    relevance: 0,
	    begin: regex.concat(
	      /\b/,
	      /(?!let|for|while|if|else|match\b)/,
	      hljs.IDENT_RE,
	      regex.lookahead(/\s*\(/))
	  };
	  const NUMBER_SUFFIX = '([ui](8|16|32|64|128|size)|f(32|64))\?';
	  const KEYWORDS = [
	    "abstract",
	    "as",
	    "async",
	    "await",
	    "become",
	    "box",
	    "break",
	    "const",
	    "continue",
	    "crate",
	    "do",
	    "dyn",
	    "else",
	    "enum",
	    "extern",
	    "false",
	    "final",
	    "fn",
	    "for",
	    "if",
	    "impl",
	    "in",
	    "let",
	    "loop",
	    "macro",
	    "match",
	    "mod",
	    "move",
	    "mut",
	    "override",
	    "priv",
	    "pub",
	    "ref",
	    "return",
	    "self",
	    "Self",
	    "static",
	    "struct",
	    "super",
	    "trait",
	    "true",
	    "try",
	    "type",
	    "typeof",
	    "unsafe",
	    "unsized",
	    "use",
	    "virtual",
	    "where",
	    "while",
	    "yield"
	  ];
	  const LITERALS = [
	    "true",
	    "false",
	    "Some",
	    "None",
	    "Ok",
	    "Err"
	  ];
	  const BUILTINS = [
	    // functions
	    'drop ',
	    // traits
	    "Copy",
	    "Send",
	    "Sized",
	    "Sync",
	    "Drop",
	    "Fn",
	    "FnMut",
	    "FnOnce",
	    "ToOwned",
	    "Clone",
	    "Debug",
	    "PartialEq",
	    "PartialOrd",
	    "Eq",
	    "Ord",
	    "AsRef",
	    "AsMut",
	    "Into",
	    "From",
	    "Default",
	    "Iterator",
	    "Extend",
	    "IntoIterator",
	    "DoubleEndedIterator",
	    "ExactSizeIterator",
	    "SliceConcatExt",
	    "ToString",
	    // macros
	    "assert!",
	    "assert_eq!",
	    "bitflags!",
	    "bytes!",
	    "cfg!",
	    "col!",
	    "concat!",
	    "concat_idents!",
	    "debug_assert!",
	    "debug_assert_eq!",
	    "env!",
	    "eprintln!",
	    "panic!",
	    "file!",
	    "format!",
	    "format_args!",
	    "include_bytes!",
	    "include_str!",
	    "line!",
	    "local_data_key!",
	    "module_path!",
	    "option_env!",
	    "print!",
	    "println!",
	    "select!",
	    "stringify!",
	    "try!",
	    "unimplemented!",
	    "unreachable!",
	    "vec!",
	    "write!",
	    "writeln!",
	    "macro_rules!",
	    "assert_ne!",
	    "debug_assert_ne!"
	  ];
	  const TYPES = [
	    "i8",
	    "i16",
	    "i32",
	    "i64",
	    "i128",
	    "isize",
	    "u8",
	    "u16",
	    "u32",
	    "u64",
	    "u128",
	    "usize",
	    "f32",
	    "f64",
	    "str",
	    "char",
	    "bool",
	    "Box",
	    "Option",
	    "Result",
	    "String",
	    "Vec"
	  ];
	  return {
	    name: 'Rust',
	    aliases: [ 'rs' ],
	    keywords: {
	      $pattern: hljs.IDENT_RE + '!?',
	      type: TYPES,
	      keyword: KEYWORDS,
	      literal: LITERALS,
	      built_in: BUILTINS
	    },
	    illegal: '</',
	    contains: [
	      hljs.C_LINE_COMMENT_MODE,
	      hljs.COMMENT('/\\*', '\\*/', { contains: [ 'self' ] }),
	      hljs.inherit(hljs.QUOTE_STRING_MODE, {
	        begin: /b?"/,
	        illegal: null
	      }),
	      {
	        className: 'string',
	        variants: [
	          { begin: /b?r(#*)"(.|\n)*?"\1(?!#)/ },
	          { begin: /b?'\\?(x\w{2}|u\w{4}|U\w{8}|.)'/ }
	        ]
	      },
	      {
	        className: 'symbol',
	        begin: /'[a-zA-Z_][a-zA-Z0-9_]*/
	      },
	      {
	        className: 'number',
	        variants: [
	          { begin: '\\b0b([01_]+)' + NUMBER_SUFFIX },
	          { begin: '\\b0o([0-7_]+)' + NUMBER_SUFFIX },
	          { begin: '\\b0x([A-Fa-f0-9_]+)' + NUMBER_SUFFIX },
	          { begin: '\\b(\\d[\\d_]*(\\.[0-9_]+)?([eE][+-]?[0-9_]+)?)'
	                   + NUMBER_SUFFIX }
	        ],
	        relevance: 0
	      },
	      {
	        begin: [
	          /fn/,
	          /\s+/,
	          hljs.UNDERSCORE_IDENT_RE
	        ],
	        className: {
	          1: "keyword",
	          3: "title.function"
	        }
	      },
	      {
	        className: 'meta',
	        begin: '#!?\\[',
	        end: '\\]',
	        contains: [
	          {
	            className: 'string',
	            begin: /"/,
	            end: /"/
	          }
	        ]
	      },
	      {
	        begin: [
	          /let/,
	          /\s+/,
	          /(?:mut\s+)?/,
	          hljs.UNDERSCORE_IDENT_RE
	        ],
	        className: {
	          1: "keyword",
	          3: "keyword",
	          4: "variable"
	        }
	      },
	      // must come before impl/for rule later
	      {
	        begin: [
	          /for/,
	          /\s+/,
	          hljs.UNDERSCORE_IDENT_RE,
	          /\s+/,
	          /in/
	        ],
	        className: {
	          1: "keyword",
	          3: "variable",
	          5: "keyword"
	        }
	      },
	      {
	        begin: [
	          /type/,
	          /\s+/,
	          hljs.UNDERSCORE_IDENT_RE
	        ],
	        className: {
	          1: "keyword",
	          3: "title.class"
	        }
	      },
	      {
	        begin: [
	          /(?:trait|enum|struct|union|impl|for)/,
	          /\s+/,
	          hljs.UNDERSCORE_IDENT_RE
	        ],
	        className: {
	          1: "keyword",
	          3: "title.class"
	        }
	      },
	      {
	        begin: hljs.IDENT_RE + '::',
	        keywords: {
	          keyword: "Self",
	          built_in: BUILTINS,
	          type: TYPES
	        }
	      },
	      {
	        className: "punctuation",
	        begin: '->'
	      },
	      FUNCTION_INVOKE
	    ]
	  };
	}

	rust_1 = rust;
	return rust_1;
}

var scss_1;
var hasRequiredScss;

function requireScss () {
	if (hasRequiredScss) return scss_1;
	hasRequiredScss = 1;
	const MODES = (hljs) => {
	  return {
	    IMPORTANT: {
	      scope: 'meta',
	      begin: '!important'
	    },
	    BLOCK_COMMENT: hljs.C_BLOCK_COMMENT_MODE,
	    HEXCOLOR: {
	      scope: 'number',
	      begin: /#(([0-9a-fA-F]{3,4})|(([0-9a-fA-F]{2}){3,4}))\b/
	    },
	    FUNCTION_DISPATCH: {
	      className: "built_in",
	      begin: /[\w-]+(?=\()/
	    },
	    ATTRIBUTE_SELECTOR_MODE: {
	      scope: 'selector-attr',
	      begin: /\[/,
	      end: /\]/,
	      illegal: '$',
	      contains: [
	        hljs.APOS_STRING_MODE,
	        hljs.QUOTE_STRING_MODE
	      ]
	    },
	    CSS_NUMBER_MODE: {
	      scope: 'number',
	      begin: hljs.NUMBER_RE + '(' +
	        '%|em|ex|ch|rem' +
	        '|vw|vh|vmin|vmax' +
	        '|cm|mm|in|pt|pc|px' +
	        '|deg|grad|rad|turn' +
	        '|s|ms' +
	        '|Hz|kHz' +
	        '|dpi|dpcm|dppx' +
	        ')?',
	      relevance: 0
	    },
	    CSS_VARIABLE: {
	      className: "attr",
	      begin: /--[A-Za-z_][A-Za-z0-9_-]*/
	    }
	  };
	};

	const TAGS = [
	  'a',
	  'abbr',
	  'address',
	  'article',
	  'aside',
	  'audio',
	  'b',
	  'blockquote',
	  'body',
	  'button',
	  'canvas',
	  'caption',
	  'cite',
	  'code',
	  'dd',
	  'del',
	  'details',
	  'dfn',
	  'div',
	  'dl',
	  'dt',
	  'em',
	  'fieldset',
	  'figcaption',
	  'figure',
	  'footer',
	  'form',
	  'h1',
	  'h2',
	  'h3',
	  'h4',
	  'h5',
	  'h6',
	  'header',
	  'hgroup',
	  'html',
	  'i',
	  'iframe',
	  'img',
	  'input',
	  'ins',
	  'kbd',
	  'label',
	  'legend',
	  'li',
	  'main',
	  'mark',
	  'menu',
	  'nav',
	  'object',
	  'ol',
	  'p',
	  'q',
	  'quote',
	  'samp',
	  'section',
	  'span',
	  'strong',
	  'summary',
	  'sup',
	  'table',
	  'tbody',
	  'td',
	  'textarea',
	  'tfoot',
	  'th',
	  'thead',
	  'time',
	  'tr',
	  'ul',
	  'var',
	  'video'
	];

	const MEDIA_FEATURES = [
	  'any-hover',
	  'any-pointer',
	  'aspect-ratio',
	  'color',
	  'color-gamut',
	  'color-index',
	  'device-aspect-ratio',
	  'device-height',
	  'device-width',
	  'display-mode',
	  'forced-colors',
	  'grid',
	  'height',
	  'hover',
	  'inverted-colors',
	  'monochrome',
	  'orientation',
	  'overflow-block',
	  'overflow-inline',
	  'pointer',
	  'prefers-color-scheme',
	  'prefers-contrast',
	  'prefers-reduced-motion',
	  'prefers-reduced-transparency',
	  'resolution',
	  'scan',
	  'scripting',
	  'update',
	  'width',
	  // TODO: find a better solution?
	  'min-width',
	  'max-width',
	  'min-height',
	  'max-height'
	];

	// https://developer.mozilla.org/en-US/docs/Web/CSS/Pseudo-classes
	const PSEUDO_CLASSES = [
	  'active',
	  'any-link',
	  'blank',
	  'checked',
	  'current',
	  'default',
	  'defined',
	  'dir', // dir()
	  'disabled',
	  'drop',
	  'empty',
	  'enabled',
	  'first',
	  'first-child',
	  'first-of-type',
	  'fullscreen',
	  'future',
	  'focus',
	  'focus-visible',
	  'focus-within',
	  'has', // has()
	  'host', // host or host()
	  'host-context', // host-context()
	  'hover',
	  'indeterminate',
	  'in-range',
	  'invalid',
	  'is', // is()
	  'lang', // lang()
	  'last-child',
	  'last-of-type',
	  'left',
	  'link',
	  'local-link',
	  'not', // not()
	  'nth-child', // nth-child()
	  'nth-col', // nth-col()
	  'nth-last-child', // nth-last-child()
	  'nth-last-col', // nth-last-col()
	  'nth-last-of-type', //nth-last-of-type()
	  'nth-of-type', //nth-of-type()
	  'only-child',
	  'only-of-type',
	  'optional',
	  'out-of-range',
	  'past',
	  'placeholder-shown',
	  'read-only',
	  'read-write',
	  'required',
	  'right',
	  'root',
	  'scope',
	  'target',
	  'target-within',
	  'user-invalid',
	  'valid',
	  'visited',
	  'where' // where()
	];

	// https://developer.mozilla.org/en-US/docs/Web/CSS/Pseudo-elements
	const PSEUDO_ELEMENTS = [
	  'after',
	  'backdrop',
	  'before',
	  'cue',
	  'cue-region',
	  'first-letter',
	  'first-line',
	  'grammar-error',
	  'marker',
	  'part',
	  'placeholder',
	  'selection',
	  'slotted',
	  'spelling-error'
	];

	const ATTRIBUTES = [
	  'align-content',
	  'align-items',
	  'align-self',
	  'all',
	  'animation',
	  'animation-delay',
	  'animation-direction',
	  'animation-duration',
	  'animation-fill-mode',
	  'animation-iteration-count',
	  'animation-name',
	  'animation-play-state',
	  'animation-timing-function',
	  'backface-visibility',
	  'background',
	  'background-attachment',
	  'background-blend-mode',
	  'background-clip',
	  'background-color',
	  'background-image',
	  'background-origin',
	  'background-position',
	  'background-repeat',
	  'background-size',
	  'block-size',
	  'border',
	  'border-block',
	  'border-block-color',
	  'border-block-end',
	  'border-block-end-color',
	  'border-block-end-style',
	  'border-block-end-width',
	  'border-block-start',
	  'border-block-start-color',
	  'border-block-start-style',
	  'border-block-start-width',
	  'border-block-style',
	  'border-block-width',
	  'border-bottom',
	  'border-bottom-color',
	  'border-bottom-left-radius',
	  'border-bottom-right-radius',
	  'border-bottom-style',
	  'border-bottom-width',
	  'border-collapse',
	  'border-color',
	  'border-image',
	  'border-image-outset',
	  'border-image-repeat',
	  'border-image-slice',
	  'border-image-source',
	  'border-image-width',
	  'border-inline',
	  'border-inline-color',
	  'border-inline-end',
	  'border-inline-end-color',
	  'border-inline-end-style',
	  'border-inline-end-width',
	  'border-inline-start',
	  'border-inline-start-color',
	  'border-inline-start-style',
	  'border-inline-start-width',
	  'border-inline-style',
	  'border-inline-width',
	  'border-left',
	  'border-left-color',
	  'border-left-style',
	  'border-left-width',
	  'border-radius',
	  'border-right',
	  'border-right-color',
	  'border-right-style',
	  'border-right-width',
	  'border-spacing',
	  'border-style',
	  'border-top',
	  'border-top-color',
	  'border-top-left-radius',
	  'border-top-right-radius',
	  'border-top-style',
	  'border-top-width',
	  'border-width',
	  'bottom',
	  'box-decoration-break',
	  'box-shadow',
	  'box-sizing',
	  'break-after',
	  'break-before',
	  'break-inside',
	  'caption-side',
	  'caret-color',
	  'clear',
	  'clip',
	  'clip-path',
	  'clip-rule',
	  'color',
	  'column-count',
	  'column-fill',
	  'column-gap',
	  'column-rule',
	  'column-rule-color',
	  'column-rule-style',
	  'column-rule-width',
	  'column-span',
	  'column-width',
	  'columns',
	  'contain',
	  'content',
	  'content-visibility',
	  'counter-increment',
	  'counter-reset',
	  'cue',
	  'cue-after',
	  'cue-before',
	  'cursor',
	  'direction',
	  'display',
	  'empty-cells',
	  'filter',
	  'flex',
	  'flex-basis',
	  'flex-direction',
	  'flex-flow',
	  'flex-grow',
	  'flex-shrink',
	  'flex-wrap',
	  'float',
	  'flow',
	  'font',
	  'font-display',
	  'font-family',
	  'font-feature-settings',
	  'font-kerning',
	  'font-language-override',
	  'font-size',
	  'font-size-adjust',
	  'font-smoothing',
	  'font-stretch',
	  'font-style',
	  'font-synthesis',
	  'font-variant',
	  'font-variant-caps',
	  'font-variant-east-asian',
	  'font-variant-ligatures',
	  'font-variant-numeric',
	  'font-variant-position',
	  'font-variation-settings',
	  'font-weight',
	  'gap',
	  'glyph-orientation-vertical',
	  'grid',
	  'grid-area',
	  'grid-auto-columns',
	  'grid-auto-flow',
	  'grid-auto-rows',
	  'grid-column',
	  'grid-column-end',
	  'grid-column-start',
	  'grid-gap',
	  'grid-row',
	  'grid-row-end',
	  'grid-row-start',
	  'grid-template',
	  'grid-template-areas',
	  'grid-template-columns',
	  'grid-template-rows',
	  'hanging-punctuation',
	  'height',
	  'hyphens',
	  'icon',
	  'image-orientation',
	  'image-rendering',
	  'image-resolution',
	  'ime-mode',
	  'inline-size',
	  'isolation',
	  'justify-content',
	  'left',
	  'letter-spacing',
	  'line-break',
	  'line-height',
	  'list-style',
	  'list-style-image',
	  'list-style-position',
	  'list-style-type',
	  'margin',
	  'margin-block',
	  'margin-block-end',
	  'margin-block-start',
	  'margin-bottom',
	  'margin-inline',
	  'margin-inline-end',
	  'margin-inline-start',
	  'margin-left',
	  'margin-right',
	  'margin-top',
	  'marks',
	  'mask',
	  'mask-border',
	  'mask-border-mode',
	  'mask-border-outset',
	  'mask-border-repeat',
	  'mask-border-slice',
	  'mask-border-source',
	  'mask-border-width',
	  'mask-clip',
	  'mask-composite',
	  'mask-image',
	  'mask-mode',
	  'mask-origin',
	  'mask-position',
	  'mask-repeat',
	  'mask-size',
	  'mask-type',
	  'max-block-size',
	  'max-height',
	  'max-inline-size',
	  'max-width',
	  'min-block-size',
	  'min-height',
	  'min-inline-size',
	  'min-width',
	  'mix-blend-mode',
	  'nav-down',
	  'nav-index',
	  'nav-left',
	  'nav-right',
	  'nav-up',
	  'none',
	  'normal',
	  'object-fit',
	  'object-position',
	  'opacity',
	  'order',
	  'orphans',
	  'outline',
	  'outline-color',
	  'outline-offset',
	  'outline-style',
	  'outline-width',
	  'overflow',
	  'overflow-wrap',
	  'overflow-x',
	  'overflow-y',
	  'padding',
	  'padding-block',
	  'padding-block-end',
	  'padding-block-start',
	  'padding-bottom',
	  'padding-inline',
	  'padding-inline-end',
	  'padding-inline-start',
	  'padding-left',
	  'padding-right',
	  'padding-top',
	  'page-break-after',
	  'page-break-before',
	  'page-break-inside',
	  'pause',
	  'pause-after',
	  'pause-before',
	  'perspective',
	  'perspective-origin',
	  'pointer-events',
	  'position',
	  'quotes',
	  'resize',
	  'rest',
	  'rest-after',
	  'rest-before',
	  'right',
	  'row-gap',
	  'scroll-margin',
	  'scroll-margin-block',
	  'scroll-margin-block-end',
	  'scroll-margin-block-start',
	  'scroll-margin-bottom',
	  'scroll-margin-inline',
	  'scroll-margin-inline-end',
	  'scroll-margin-inline-start',
	  'scroll-margin-left',
	  'scroll-margin-right',
	  'scroll-margin-top',
	  'scroll-padding',
	  'scroll-padding-block',
	  'scroll-padding-block-end',
	  'scroll-padding-block-start',
	  'scroll-padding-bottom',
	  'scroll-padding-inline',
	  'scroll-padding-inline-end',
	  'scroll-padding-inline-start',
	  'scroll-padding-left',
	  'scroll-padding-right',
	  'scroll-padding-top',
	  'scroll-snap-align',
	  'scroll-snap-stop',
	  'scroll-snap-type',
	  'scrollbar-color',
	  'scrollbar-gutter',
	  'scrollbar-width',
	  'shape-image-threshold',
	  'shape-margin',
	  'shape-outside',
	  'speak',
	  'speak-as',
	  'src', // @font-face
	  'tab-size',
	  'table-layout',
	  'text-align',
	  'text-align-all',
	  'text-align-last',
	  'text-combine-upright',
	  'text-decoration',
	  'text-decoration-color',
	  'text-decoration-line',
	  'text-decoration-style',
	  'text-emphasis',
	  'text-emphasis-color',
	  'text-emphasis-position',
	  'text-emphasis-style',
	  'text-indent',
	  'text-justify',
	  'text-orientation',
	  'text-overflow',
	  'text-rendering',
	  'text-shadow',
	  'text-transform',
	  'text-underline-position',
	  'top',
	  'transform',
	  'transform-box',
	  'transform-origin',
	  'transform-style',
	  'transition',
	  'transition-delay',
	  'transition-duration',
	  'transition-property',
	  'transition-timing-function',
	  'unicode-bidi',
	  'vertical-align',
	  'visibility',
	  'voice-balance',
	  'voice-duration',
	  'voice-family',
	  'voice-pitch',
	  'voice-range',
	  'voice-rate',
	  'voice-stress',
	  'voice-volume',
	  'white-space',
	  'widows',
	  'width',
	  'will-change',
	  'word-break',
	  'word-spacing',
	  'word-wrap',
	  'writing-mode',
	  'z-index'
	  // reverse makes sure longer attributes `font-weight` are matched fully
	  // instead of getting false positives on say `font`
	].reverse();

	/*
	Language: SCSS
	Description: Scss is an extension of the syntax of CSS.
	Author: Kurt Emch <kurt@kurtemch.com>
	Website: https://sass-lang.com
	Category: common, css, web
	*/


	/** @type LanguageFn */
	function scss(hljs) {
	  const modes = MODES(hljs);
	  const PSEUDO_ELEMENTS$1 = PSEUDO_ELEMENTS;
	  const PSEUDO_CLASSES$1 = PSEUDO_CLASSES;

	  const AT_IDENTIFIER = '@[a-z-]+'; // @font-face
	  const AT_MODIFIERS = "and or not only";
	  const IDENT_RE = '[a-zA-Z-][a-zA-Z0-9_-]*';
	  const VARIABLE = {
	    className: 'variable',
	    begin: '(\\$' + IDENT_RE + ')\\b',
	    relevance: 0
	  };

	  return {
	    name: 'SCSS',
	    case_insensitive: true,
	    illegal: '[=/|\']',
	    contains: [
	      hljs.C_LINE_COMMENT_MODE,
	      hljs.C_BLOCK_COMMENT_MODE,
	      // to recognize keyframe 40% etc which are outside the scope of our
	      // attribute value mode
	      modes.CSS_NUMBER_MODE,
	      {
	        className: 'selector-id',
	        begin: '#[A-Za-z0-9_-]+',
	        relevance: 0
	      },
	      {
	        className: 'selector-class',
	        begin: '\\.[A-Za-z0-9_-]+',
	        relevance: 0
	      },
	      modes.ATTRIBUTE_SELECTOR_MODE,
	      {
	        className: 'selector-tag',
	        begin: '\\b(' + TAGS.join('|') + ')\\b',
	        // was there, before, but why?
	        relevance: 0
	      },
	      {
	        className: 'selector-pseudo',
	        begin: ':(' + PSEUDO_CLASSES$1.join('|') + ')'
	      },
	      {
	        className: 'selector-pseudo',
	        begin: ':(:)?(' + PSEUDO_ELEMENTS$1.join('|') + ')'
	      },
	      VARIABLE,
	      { // pseudo-selector params
	        begin: /\(/,
	        end: /\)/,
	        contains: [ modes.CSS_NUMBER_MODE ]
	      },
	      modes.CSS_VARIABLE,
	      {
	        className: 'attribute',
	        begin: '\\b(' + ATTRIBUTES.join('|') + ')\\b'
	      },
	      { begin: '\\b(whitespace|wait|w-resize|visible|vertical-text|vertical-ideographic|uppercase|upper-roman|upper-alpha|underline|transparent|top|thin|thick|text|text-top|text-bottom|tb-rl|table-header-group|table-footer-group|sw-resize|super|strict|static|square|solid|small-caps|separate|se-resize|scroll|s-resize|rtl|row-resize|ridge|right|repeat|repeat-y|repeat-x|relative|progress|pointer|overline|outside|outset|oblique|nowrap|not-allowed|normal|none|nw-resize|no-repeat|no-drop|newspaper|ne-resize|n-resize|move|middle|medium|ltr|lr-tb|lowercase|lower-roman|lower-alpha|loose|list-item|line|line-through|line-edge|lighter|left|keep-all|justify|italic|inter-word|inter-ideograph|inside|inset|inline|inline-block|inherit|inactive|ideograph-space|ideograph-parenthesis|ideograph-numeric|ideograph-alpha|horizontal|hidden|help|hand|groove|fixed|ellipsis|e-resize|double|dotted|distribute|distribute-space|distribute-letter|distribute-all-lines|disc|disabled|default|decimal|dashed|crosshair|collapse|col-resize|circle|char|center|capitalize|break-word|break-all|bottom|both|bolder|bold|block|bidi-override|below|baseline|auto|always|all-scroll|absolute|table|table-cell)\\b' },
	      {
	        begin: /:/,
	        end: /[;}{]/,
	        relevance: 0,
	        contains: [
	          modes.BLOCK_COMMENT,
	          VARIABLE,
	          modes.HEXCOLOR,
	          modes.CSS_NUMBER_MODE,
	          hljs.QUOTE_STRING_MODE,
	          hljs.APOS_STRING_MODE,
	          modes.IMPORTANT,
	          modes.FUNCTION_DISPATCH
	        ]
	      },
	      // matching these here allows us to treat them more like regular CSS
	      // rules so everything between the {} gets regular rule highlighting,
	      // which is what we want for page and font-face
	      {
	        begin: '@(page|font-face)',
	        keywords: {
	          $pattern: AT_IDENTIFIER,
	          keyword: '@page @font-face'
	        }
	      },
	      {
	        begin: '@',
	        end: '[{;]',
	        returnBegin: true,
	        keywords: {
	          $pattern: /[a-z-]+/,
	          keyword: AT_MODIFIERS,
	          attribute: MEDIA_FEATURES.join(" ")
	        },
	        contains: [
	          {
	            begin: AT_IDENTIFIER,
	            className: "keyword"
	          },
	          {
	            begin: /[a-z-]+(?=:)/,
	            className: "attribute"
	          },
	          VARIABLE,
	          hljs.QUOTE_STRING_MODE,
	          hljs.APOS_STRING_MODE,
	          modes.HEXCOLOR,
	          modes.CSS_NUMBER_MODE
	        ]
	      },
	      modes.FUNCTION_DISPATCH
	    ]
	  };
	}

	scss_1 = scss;
	return scss_1;
}

/*
Language: Shell Session
Requires: bash.js
Author: TSUYUSATO Kitsune <make.just.on@gmail.com>
Category: common
Audit: 2020
*/

var shell_1;
var hasRequiredShell;

function requireShell () {
	if (hasRequiredShell) return shell_1;
	hasRequiredShell = 1;
	/** @type LanguageFn */
	function shell(hljs) {
	  return {
	    name: 'Shell Session',
	    aliases: [
	      'console',
	      'shellsession'
	    ],
	    contains: [
	      {
	        className: 'meta.prompt',
	        // We cannot add \s (spaces) in the regular expression otherwise it will be too broad and produce unexpected result.
	        // For instance, in the following example, it would match "echo /path/to/home >" as a prompt:
	        // echo /path/to/home > t.exe
	        begin: /^\s{0,3}[/~\w\d[\]()@-]*[>%$#][ ]?/,
	        starts: {
	          end: /[^\\](?=\s*$)/,
	          subLanguage: 'bash'
	        }
	      }
	    ]
	  };
	}

	shell_1 = shell;
	return shell_1;
}

/*
 Language: SQL
 Website: https://en.wikipedia.org/wiki/SQL
 Category: common, database
 */

var sql_1;
var hasRequiredSql;

function requireSql () {
	if (hasRequiredSql) return sql_1;
	hasRequiredSql = 1;
	/*

	Goals:

	SQL is intended to highlight basic/common SQL keywords and expressions

	- If pretty much every single SQL server includes supports, then it's a canidate.
	- It is NOT intended to include tons of vendor specific keywords (Oracle, MySQL,
	  PostgreSQL) although the list of data types is purposely a bit more expansive.
	- For more specific SQL grammars please see:
	  - PostgreSQL and PL/pgSQL - core
	  - T-SQL - https://github.com/highlightjs/highlightjs-tsql
	  - sql_more (core)

	 */

	function sql(hljs) {
	  const regex = hljs.regex;
	  const COMMENT_MODE = hljs.COMMENT('--', '$');
	  const STRING = {
	    className: 'string',
	    variants: [
	      {
	        begin: /'/,
	        end: /'/,
	        contains: [ { begin: /''/ } ]
	      }
	    ]
	  };
	  const QUOTED_IDENTIFIER = {
	    begin: /"/,
	    end: /"/,
	    contains: [ { begin: /""/ } ]
	  };

	  const LITERALS = [
	    "true",
	    "false",
	    // Not sure it's correct to call NULL literal, and clauses like IS [NOT] NULL look strange that way.
	    // "null",
	    "unknown"
	  ];

	  const MULTI_WORD_TYPES = [
	    "double precision",
	    "large object",
	    "with timezone",
	    "without timezone"
	  ];

	  const TYPES = [
	    'bigint',
	    'binary',
	    'blob',
	    'boolean',
	    'char',
	    'character',
	    'clob',
	    'date',
	    'dec',
	    'decfloat',
	    'decimal',
	    'float',
	    'int',
	    'integer',
	    'interval',
	    'nchar',
	    'nclob',
	    'national',
	    'numeric',
	    'real',
	    'row',
	    'smallint',
	    'time',
	    'timestamp',
	    'varchar',
	    'varying', // modifier (character varying)
	    'varbinary'
	  ];

	  const NON_RESERVED_WORDS = [
	    "add",
	    "asc",
	    "collation",
	    "desc",
	    "final",
	    "first",
	    "last",
	    "view"
	  ];

	  // https://jakewheat.github.io/sql-overview/sql-2016-foundation-grammar.html#reserved-word
	  const RESERVED_WORDS = [
	    "abs",
	    "acos",
	    "all",
	    "allocate",
	    "alter",
	    "and",
	    "any",
	    "are",
	    "array",
	    "array_agg",
	    "array_max_cardinality",
	    "as",
	    "asensitive",
	    "asin",
	    "asymmetric",
	    "at",
	    "atan",
	    "atomic",
	    "authorization",
	    "avg",
	    "begin",
	    "begin_frame",
	    "begin_partition",
	    "between",
	    "bigint",
	    "binary",
	    "blob",
	    "boolean",
	    "both",
	    "by",
	    "call",
	    "called",
	    "cardinality",
	    "cascaded",
	    "case",
	    "cast",
	    "ceil",
	    "ceiling",
	    "char",
	    "char_length",
	    "character",
	    "character_length",
	    "check",
	    "classifier",
	    "clob",
	    "close",
	    "coalesce",
	    "collate",
	    "collect",
	    "column",
	    "commit",
	    "condition",
	    "connect",
	    "constraint",
	    "contains",
	    "convert",
	    "copy",
	    "corr",
	    "corresponding",
	    "cos",
	    "cosh",
	    "count",
	    "covar_pop",
	    "covar_samp",
	    "create",
	    "cross",
	    "cube",
	    "cume_dist",
	    "current",
	    "current_catalog",
	    "current_date",
	    "current_default_transform_group",
	    "current_path",
	    "current_role",
	    "current_row",
	    "current_schema",
	    "current_time",
	    "current_timestamp",
	    "current_path",
	    "current_role",
	    "current_transform_group_for_type",
	    "current_user",
	    "cursor",
	    "cycle",
	    "date",
	    "day",
	    "deallocate",
	    "dec",
	    "decimal",
	    "decfloat",
	    "declare",
	    "default",
	    "define",
	    "delete",
	    "dense_rank",
	    "deref",
	    "describe",
	    "deterministic",
	    "disconnect",
	    "distinct",
	    "double",
	    "drop",
	    "dynamic",
	    "each",
	    "element",
	    "else",
	    "empty",
	    "end",
	    "end_frame",
	    "end_partition",
	    "end-exec",
	    "equals",
	    "escape",
	    "every",
	    "except",
	    "exec",
	    "execute",
	    "exists",
	    "exp",
	    "external",
	    "extract",
	    "false",
	    "fetch",
	    "filter",
	    "first_value",
	    "float",
	    "floor",
	    "for",
	    "foreign",
	    "frame_row",
	    "free",
	    "from",
	    "full",
	    "function",
	    "fusion",
	    "get",
	    "global",
	    "grant",
	    "group",
	    "grouping",
	    "groups",
	    "having",
	    "hold",
	    "hour",
	    "identity",
	    "in",
	    "indicator",
	    "initial",
	    "inner",
	    "inout",
	    "insensitive",
	    "insert",
	    "int",
	    "integer",
	    "intersect",
	    "intersection",
	    "interval",
	    "into",
	    "is",
	    "join",
	    "json_array",
	    "json_arrayagg",
	    "json_exists",
	    "json_object",
	    "json_objectagg",
	    "json_query",
	    "json_table",
	    "json_table_primitive",
	    "json_value",
	    "lag",
	    "language",
	    "large",
	    "last_value",
	    "lateral",
	    "lead",
	    "leading",
	    "left",
	    "like",
	    "like_regex",
	    "listagg",
	    "ln",
	    "local",
	    "localtime",
	    "localtimestamp",
	    "log",
	    "log10",
	    "lower",
	    "match",
	    "match_number",
	    "match_recognize",
	    "matches",
	    "max",
	    "member",
	    "merge",
	    "method",
	    "min",
	    "minute",
	    "mod",
	    "modifies",
	    "module",
	    "month",
	    "multiset",
	    "national",
	    "natural",
	    "nchar",
	    "nclob",
	    "new",
	    "no",
	    "none",
	    "normalize",
	    "not",
	    "nth_value",
	    "ntile",
	    "null",
	    "nullif",
	    "numeric",
	    "octet_length",
	    "occurrences_regex",
	    "of",
	    "offset",
	    "old",
	    "omit",
	    "on",
	    "one",
	    "only",
	    "open",
	    "or",
	    "order",
	    "out",
	    "outer",
	    "over",
	    "overlaps",
	    "overlay",
	    "parameter",
	    "partition",
	    "pattern",
	    "per",
	    "percent",
	    "percent_rank",
	    "percentile_cont",
	    "percentile_disc",
	    "period",
	    "portion",
	    "position",
	    "position_regex",
	    "power",
	    "precedes",
	    "precision",
	    "prepare",
	    "primary",
	    "procedure",
	    "ptf",
	    "range",
	    "rank",
	    "reads",
	    "real",
	    "recursive",
	    "ref",
	    "references",
	    "referencing",
	    "regr_avgx",
	    "regr_avgy",
	    "regr_count",
	    "regr_intercept",
	    "regr_r2",
	    "regr_slope",
	    "regr_sxx",
	    "regr_sxy",
	    "regr_syy",
	    "release",
	    "result",
	    "return",
	    "returns",
	    "revoke",
	    "right",
	    "rollback",
	    "rollup",
	    "row",
	    "row_number",
	    "rows",
	    "running",
	    "savepoint",
	    "scope",
	    "scroll",
	    "search",
	    "second",
	    "seek",
	    "select",
	    "sensitive",
	    "session_user",
	    "set",
	    "show",
	    "similar",
	    "sin",
	    "sinh",
	    "skip",
	    "smallint",
	    "some",
	    "specific",
	    "specifictype",
	    "sql",
	    "sqlexception",
	    "sqlstate",
	    "sqlwarning",
	    "sqrt",
	    "start",
	    "static",
	    "stddev_pop",
	    "stddev_samp",
	    "submultiset",
	    "subset",
	    "substring",
	    "substring_regex",
	    "succeeds",
	    "sum",
	    "symmetric",
	    "system",
	    "system_time",
	    "system_user",
	    "table",
	    "tablesample",
	    "tan",
	    "tanh",
	    "then",
	    "time",
	    "timestamp",
	    "timezone_hour",
	    "timezone_minute",
	    "to",
	    "trailing",
	    "translate",
	    "translate_regex",
	    "translation",
	    "treat",
	    "trigger",
	    "trim",
	    "trim_array",
	    "true",
	    "truncate",
	    "uescape",
	    "union",
	    "unique",
	    "unknown",
	    "unnest",
	    "update",
	    "upper",
	    "user",
	    "using",
	    "value",
	    "values",
	    "value_of",
	    "var_pop",
	    "var_samp",
	    "varbinary",
	    "varchar",
	    "varying",
	    "versioning",
	    "when",
	    "whenever",
	    "where",
	    "width_bucket",
	    "window",
	    "with",
	    "within",
	    "without",
	    "year",
	  ];

	  // these are reserved words we have identified to be functions
	  // and should only be highlighted in a dispatch-like context
	  // ie, array_agg(...), etc.
	  const RESERVED_FUNCTIONS = [
	    "abs",
	    "acos",
	    "array_agg",
	    "asin",
	    "atan",
	    "avg",
	    "cast",
	    "ceil",
	    "ceiling",
	    "coalesce",
	    "corr",
	    "cos",
	    "cosh",
	    "count",
	    "covar_pop",
	    "covar_samp",
	    "cume_dist",
	    "dense_rank",
	    "deref",
	    "element",
	    "exp",
	    "extract",
	    "first_value",
	    "floor",
	    "json_array",
	    "json_arrayagg",
	    "json_exists",
	    "json_object",
	    "json_objectagg",
	    "json_query",
	    "json_table",
	    "json_table_primitive",
	    "json_value",
	    "lag",
	    "last_value",
	    "lead",
	    "listagg",
	    "ln",
	    "log",
	    "log10",
	    "lower",
	    "max",
	    "min",
	    "mod",
	    "nth_value",
	    "ntile",
	    "nullif",
	    "percent_rank",
	    "percentile_cont",
	    "percentile_disc",
	    "position",
	    "position_regex",
	    "power",
	    "rank",
	    "regr_avgx",
	    "regr_avgy",
	    "regr_count",
	    "regr_intercept",
	    "regr_r2",
	    "regr_slope",
	    "regr_sxx",
	    "regr_sxy",
	    "regr_syy",
	    "row_number",
	    "sin",
	    "sinh",
	    "sqrt",
	    "stddev_pop",
	    "stddev_samp",
	    "substring",
	    "substring_regex",
	    "sum",
	    "tan",
	    "tanh",
	    "translate",
	    "translate_regex",
	    "treat",
	    "trim",
	    "trim_array",
	    "unnest",
	    "upper",
	    "value_of",
	    "var_pop",
	    "var_samp",
	    "width_bucket",
	  ];

	  // these functions can
	  const POSSIBLE_WITHOUT_PARENS = [
	    "current_catalog",
	    "current_date",
	    "current_default_transform_group",
	    "current_path",
	    "current_role",
	    "current_schema",
	    "current_transform_group_for_type",
	    "current_user",
	    "session_user",
	    "system_time",
	    "system_user",
	    "current_time",
	    "localtime",
	    "current_timestamp",
	    "localtimestamp"
	  ];

	  // those exist to boost relevance making these very
	  // "SQL like" keyword combos worth +1 extra relevance
	  const COMBOS = [
	    "create table",
	    "insert into",
	    "primary key",
	    "foreign key",
	    "not null",
	    "alter table",
	    "add constraint",
	    "grouping sets",
	    "on overflow",
	    "character set",
	    "respect nulls",
	    "ignore nulls",
	    "nulls first",
	    "nulls last",
	    "depth first",
	    "breadth first"
	  ];

	  const FUNCTIONS = RESERVED_FUNCTIONS;

	  const KEYWORDS = [
	    ...RESERVED_WORDS,
	    ...NON_RESERVED_WORDS
	  ].filter((keyword) => {
	    return !RESERVED_FUNCTIONS.includes(keyword);
	  });

	  const VARIABLE = {
	    className: "variable",
	    begin: /@[a-z0-9][a-z0-9_]*/,
	  };

	  const OPERATOR = {
	    className: "operator",
	    begin: /[-+*/=%^~]|&&?|\|\|?|!=?|<(?:=>?|<|>)?|>[>=]?/,
	    relevance: 0,
	  };

	  const FUNCTION_CALL = {
	    begin: regex.concat(/\b/, regex.either(...FUNCTIONS), /\s*\(/),
	    relevance: 0,
	    keywords: { built_in: FUNCTIONS }
	  };

	  // keywords with less than 3 letters are reduced in relevancy
	  function reduceRelevancy(list, {
	    exceptions, when
	  } = {}) {
	    const qualifyFn = when;
	    exceptions = exceptions || [];
	    return list.map((item) => {
	      if (item.match(/\|\d+$/) || exceptions.includes(item)) {
	        return item;
	      } else if (qualifyFn(item)) {
	        return `${item}|0`;
	      } else {
	        return item;
	      }
	    });
	  }

	  return {
	    name: 'SQL',
	    case_insensitive: true,
	    // does not include {} or HTML tags `</`
	    illegal: /[{}]|<\//,
	    keywords: {
	      $pattern: /\b[\w\.]+/,
	      keyword:
	        reduceRelevancy(KEYWORDS, { when: (x) => x.length < 3 }),
	      literal: LITERALS,
	      type: TYPES,
	      built_in: POSSIBLE_WITHOUT_PARENS
	    },
	    contains: [
	      {
	        begin: regex.either(...COMBOS),
	        relevance: 0,
	        keywords: {
	          $pattern: /[\w\.]+/,
	          keyword: KEYWORDS.concat(COMBOS),
	          literal: LITERALS,
	          type: TYPES
	        },
	      },
	      {
	        className: "type",
	        begin: regex.either(...MULTI_WORD_TYPES)
	      },
	      FUNCTION_CALL,
	      VARIABLE,
	      STRING,
	      QUOTED_IDENTIFIER,
	      hljs.C_NUMBER_MODE,
	      hljs.C_BLOCK_COMMENT_MODE,
	      COMMENT_MODE,
	      OPERATOR
	    ]
	  };
	}

	sql_1 = sql;
	return sql_1;
}

/**
 * @param {string} value
 * @returns {RegExp}
 * */

var swift_1;
var hasRequiredSwift;

function requireSwift () {
	if (hasRequiredSwift) return swift_1;
	hasRequiredSwift = 1;
	/**
	 * @param {RegExp | string } re
	 * @returns {string}
	 */
	function source(re) {
	  if (!re) return null;
	  if (typeof re === "string") return re;

	  return re.source;
	}

	/**
	 * @param {RegExp | string } re
	 * @returns {string}
	 */
	function lookahead(re) {
	  return concat('(?=', re, ')');
	}

	/**
	 * @param {...(RegExp | string) } args
	 * @returns {string}
	 */
	function concat(...args) {
	  const joined = args.map((x) => source(x)).join("");
	  return joined;
	}

	/**
	 * @param { Array<string | RegExp | Object> } args
	 * @returns {object}
	 */
	function stripOptionsFromArgs(args) {
	  const opts = args[args.length - 1];

	  if (typeof opts === 'object' && opts.constructor === Object) {
	    args.splice(args.length - 1, 1);
	    return opts;
	  } else {
	    return {};
	  }
	}

	/** @typedef { {capture?: boolean} } RegexEitherOptions */

	/**
	 * Any of the passed expresssions may match
	 *
	 * Creates a huge this | this | that | that match
	 * @param {(RegExp | string)[] | [...(RegExp | string)[], RegexEitherOptions]} args
	 * @returns {string}
	 */
	function either(...args) {
	  /** @type { object & {capture?: boolean} }  */
	  const opts = stripOptionsFromArgs(args);
	  const joined = '('
	    + (opts.capture ? "" : "?:")
	    + args.map((x) => source(x)).join("|") + ")";
	  return joined;
	}

	const keywordWrapper = keyword => concat(
	  /\b/,
	  keyword,
	  /\w$/.test(keyword) ? /\b/ : /\B/
	);

	// Keywords that require a leading dot.
	const dotKeywords = [
	  'Protocol', // contextual
	  'Type' // contextual
	].map(keywordWrapper);

	// Keywords that may have a leading dot.
	const optionalDotKeywords = [
	  'init',
	  'self'
	].map(keywordWrapper);

	// should register as keyword, not type
	const keywordTypes = [
	  'Any',
	  'Self'
	];

	// Regular keywords and literals.
	const keywords = [
	  // strings below will be fed into the regular `keywords` engine while regex
	  // will result in additional modes being created to scan for those keywords to
	  // avoid conflicts with other rules
	  'actor',
	  'any', // contextual
	  'associatedtype',
	  'async',
	  'await',
	  /as\?/, // operator
	  /as!/, // operator
	  'as', // operator
	  'borrowing', // contextual
	  'break',
	  'case',
	  'catch',
	  'class',
	  'consume', // contextual
	  'consuming', // contextual
	  'continue',
	  'convenience', // contextual
	  'copy', // contextual
	  'default',
	  'defer',
	  'deinit',
	  'didSet', // contextual
	  'distributed',
	  'do',
	  'dynamic', // contextual
	  'each',
	  'else',
	  'enum',
	  'extension',
	  'fallthrough',
	  /fileprivate\(set\)/,
	  'fileprivate',
	  'final', // contextual
	  'for',
	  'func',
	  'get', // contextual
	  'guard',
	  'if',
	  'import',
	  'indirect', // contextual
	  'infix', // contextual
	  /init\?/,
	  /init!/,
	  'inout',
	  /internal\(set\)/,
	  'internal',
	  'in',
	  'is', // operator
	  'isolated', // contextual
	  'nonisolated', // contextual
	  'lazy', // contextual
	  'let',
	  'macro',
	  'mutating', // contextual
	  'nonmutating', // contextual
	  /open\(set\)/, // contextual
	  'open', // contextual
	  'operator',
	  'optional', // contextual
	  'override', // contextual
	  'postfix', // contextual
	  'precedencegroup',
	  'prefix', // contextual
	  /private\(set\)/,
	  'private',
	  'protocol',
	  /public\(set\)/,
	  'public',
	  'repeat',
	  'required', // contextual
	  'rethrows',
	  'return',
	  'set', // contextual
	  'some', // contextual
	  'static',
	  'struct',
	  'subscript',
	  'super',
	  'switch',
	  'throws',
	  'throw',
	  /try\?/, // operator
	  /try!/, // operator
	  'try', // operator
	  'typealias',
	  /unowned\(safe\)/, // contextual
	  /unowned\(unsafe\)/, // contextual
	  'unowned', // contextual
	  'var',
	  'weak', // contextual
	  'where',
	  'while',
	  'willSet' // contextual
	];

	// NOTE: Contextual keywords are reserved only in specific contexts.
	// Ideally, these should be matched using modes to avoid false positives.

	// Literals.
	const literals = [
	  'false',
	  'nil',
	  'true'
	];

	// Keywords used in precedence groups.
	const precedencegroupKeywords = [
	  'assignment',
	  'associativity',
	  'higherThan',
	  'left',
	  'lowerThan',
	  'none',
	  'right'
	];

	// Keywords that start with a number sign (#).
	// #(un)available is handled separately.
	const numberSignKeywords = [
	  '#colorLiteral',
	  '#column',
	  '#dsohandle',
	  '#else',
	  '#elseif',
	  '#endif',
	  '#error',
	  '#file',
	  '#fileID',
	  '#fileLiteral',
	  '#filePath',
	  '#function',
	  '#if',
	  '#imageLiteral',
	  '#keyPath',
	  '#line',
	  '#selector',
	  '#sourceLocation',
	  '#warning'
	];

	// Global functions in the Standard Library.
	const builtIns = [
	  'abs',
	  'all',
	  'any',
	  'assert',
	  'assertionFailure',
	  'debugPrint',
	  'dump',
	  'fatalError',
	  'getVaList',
	  'isKnownUniquelyReferenced',
	  'max',
	  'min',
	  'numericCast',
	  'pointwiseMax',
	  'pointwiseMin',
	  'precondition',
	  'preconditionFailure',
	  'print',
	  'readLine',
	  'repeatElement',
	  'sequence',
	  'stride',
	  'swap',
	  'swift_unboxFromSwiftValueWithType',
	  'transcode',
	  'type',
	  'unsafeBitCast',
	  'unsafeDowncast',
	  'withExtendedLifetime',
	  'withUnsafeMutablePointer',
	  'withUnsafePointer',
	  'withVaList',
	  'withoutActuallyEscaping',
	  'zip'
	];

	// Valid first characters for operators.
	const operatorHead = either(
	  /[/=\-+!*%<>&|^~?]/,
	  /[\u00A1-\u00A7]/,
	  /[\u00A9\u00AB]/,
	  /[\u00AC\u00AE]/,
	  /[\u00B0\u00B1]/,
	  /[\u00B6\u00BB\u00BF\u00D7\u00F7]/,
	  /[\u2016-\u2017]/,
	  /[\u2020-\u2027]/,
	  /[\u2030-\u203E]/,
	  /[\u2041-\u2053]/,
	  /[\u2055-\u205E]/,
	  /[\u2190-\u23FF]/,
	  /[\u2500-\u2775]/,
	  /[\u2794-\u2BFF]/,
	  /[\u2E00-\u2E7F]/,
	  /[\u3001-\u3003]/,
	  /[\u3008-\u3020]/,
	  /[\u3030]/
	);

	// Valid characters for operators.
	const operatorCharacter = either(
	  operatorHead,
	  /[\u0300-\u036F]/,
	  /[\u1DC0-\u1DFF]/,
	  /[\u20D0-\u20FF]/,
	  /[\uFE00-\uFE0F]/,
	  /[\uFE20-\uFE2F]/
	  // TODO: The following characters are also allowed, but the regex isn't supported yet.
	  // /[\u{E0100}-\u{E01EF}]/u
	);

	// Valid operator.
	const operator = concat(operatorHead, operatorCharacter, '*');

	// Valid first characters for identifiers.
	const identifierHead = either(
	  /[a-zA-Z_]/,
	  /[\u00A8\u00AA\u00AD\u00AF\u00B2-\u00B5\u00B7-\u00BA]/,
	  /[\u00BC-\u00BE\u00C0-\u00D6\u00D8-\u00F6\u00F8-\u00FF]/,
	  /[\u0100-\u02FF\u0370-\u167F\u1681-\u180D\u180F-\u1DBF]/,
	  /[\u1E00-\u1FFF]/,
	  /[\u200B-\u200D\u202A-\u202E\u203F-\u2040\u2054\u2060-\u206F]/,
	  /[\u2070-\u20CF\u2100-\u218F\u2460-\u24FF\u2776-\u2793]/,
	  /[\u2C00-\u2DFF\u2E80-\u2FFF]/,
	  /[\u3004-\u3007\u3021-\u302F\u3031-\u303F\u3040-\uD7FF]/,
	  /[\uF900-\uFD3D\uFD40-\uFDCF\uFDF0-\uFE1F\uFE30-\uFE44]/,
	  /[\uFE47-\uFEFE\uFF00-\uFFFD]/ // Should be /[\uFE47-\uFFFD]/, but we have to exclude FEFF.
	  // The following characters are also allowed, but the regexes aren't supported yet.
	  // /[\u{10000}-\u{1FFFD}\u{20000-\u{2FFFD}\u{30000}-\u{3FFFD}\u{40000}-\u{4FFFD}]/u,
	  // /[\u{50000}-\u{5FFFD}\u{60000-\u{6FFFD}\u{70000}-\u{7FFFD}\u{80000}-\u{8FFFD}]/u,
	  // /[\u{90000}-\u{9FFFD}\u{A0000-\u{AFFFD}\u{B0000}-\u{BFFFD}\u{C0000}-\u{CFFFD}]/u,
	  // /[\u{D0000}-\u{DFFFD}\u{E0000-\u{EFFFD}]/u
	);

	// Valid characters for identifiers.
	const identifierCharacter = either(
	  identifierHead,
	  /\d/,
	  /[\u0300-\u036F\u1DC0-\u1DFF\u20D0-\u20FF\uFE20-\uFE2F]/
	);

	// Valid identifier.
	const identifier = concat(identifierHead, identifierCharacter, '*');

	// Valid type identifier.
	const typeIdentifier = concat(/[A-Z]/, identifierCharacter, '*');

	// Built-in attributes, which are highlighted as keywords.
	// @available is handled separately.
	// https://docs.swift.org/swift-book/documentation/the-swift-programming-language/attributes
	const keywordAttributes = [
	  'attached',
	  'autoclosure',
	  concat(/convention\(/, either('swift', 'block', 'c'), /\)/),
	  'discardableResult',
	  'dynamicCallable',
	  'dynamicMemberLookup',
	  'escaping',
	  'freestanding',
	  'frozen',
	  'GKInspectable',
	  'IBAction',
	  'IBDesignable',
	  'IBInspectable',
	  'IBOutlet',
	  'IBSegueAction',
	  'inlinable',
	  'main',
	  'nonobjc',
	  'NSApplicationMain',
	  'NSCopying',
	  'NSManaged',
	  concat(/objc\(/, identifier, /\)/),
	  'objc',
	  'objcMembers',
	  'propertyWrapper',
	  'requires_stored_property_inits',
	  'resultBuilder',
	  'Sendable',
	  'testable',
	  'UIApplicationMain',
	  'unchecked',
	  'unknown',
	  'usableFromInline',
	  'warn_unqualified_access'
	];

	// Contextual keywords used in @available and #(un)available.
	const availabilityKeywords = [
	  'iOS',
	  'iOSApplicationExtension',
	  'macOS',
	  'macOSApplicationExtension',
	  'macCatalyst',
	  'macCatalystApplicationExtension',
	  'watchOS',
	  'watchOSApplicationExtension',
	  'tvOS',
	  'tvOSApplicationExtension',
	  'swift'
	];

	/*
	Language: Swift
	Description: Swift is a general-purpose programming language built using a modern approach to safety, performance, and software design patterns.
	Author: Steven Van Impe <steven.vanimpe@icloud.com>
	Contributors: Chris Eidhof <chris@eidhof.nl>, Nate Cook <natecook@gmail.com>, Alexander Lichter <manniL@gmx.net>, Richard Gibson <gibson042@github>
	Website: https://swift.org
	Category: common, system
	*/


	/** @type LanguageFn */
	function swift(hljs) {
	  const WHITESPACE = {
	    match: /\s+/,
	    relevance: 0
	  };
	  // https://docs.swift.org/swift-book/ReferenceManual/LexicalStructure.html#ID411
	  const BLOCK_COMMENT = hljs.COMMENT(
	    '/\\*',
	    '\\*/',
	    { contains: [ 'self' ] }
	  );
	  const COMMENTS = [
	    hljs.C_LINE_COMMENT_MODE,
	    BLOCK_COMMENT
	  ];

	  // https://docs.swift.org/swift-book/ReferenceManual/LexicalStructure.html#ID413
	  // https://docs.swift.org/swift-book/ReferenceManual/zzSummaryOfTheGrammar.html
	  const DOT_KEYWORD = {
	    match: [
	      /\./,
	      either(...dotKeywords, ...optionalDotKeywords)
	    ],
	    className: { 2: "keyword" }
	  };
	  const KEYWORD_GUARD = {
	    // Consume .keyword to prevent highlighting properties and methods as keywords.
	    match: concat(/\./, either(...keywords)),
	    relevance: 0
	  };
	  const PLAIN_KEYWORDS = keywords
	    .filter(kw => typeof kw === 'string')
	    .concat([ "_|0" ]); // seems common, so 0 relevance
	  const REGEX_KEYWORDS = keywords
	    .filter(kw => typeof kw !== 'string') // find regex
	    .concat(keywordTypes)
	    .map(keywordWrapper);
	  const KEYWORD = { variants: [
	    {
	      className: 'keyword',
	      match: either(...REGEX_KEYWORDS, ...optionalDotKeywords)
	    }
	  ] };
	  // find all the regular keywords
	  const KEYWORDS = {
	    $pattern: either(
	      /\b\w+/, // regular keywords
	      /#\w+/ // number keywords
	    ),
	    keyword: PLAIN_KEYWORDS
	      .concat(numberSignKeywords),
	    literal: literals
	  };
	  const KEYWORD_MODES = [
	    DOT_KEYWORD,
	    KEYWORD_GUARD,
	    KEYWORD
	  ];

	  // https://github.com/apple/swift/tree/main/stdlib/public/core
	  const BUILT_IN_GUARD = {
	    // Consume .built_in to prevent highlighting properties and methods.
	    match: concat(/\./, either(...builtIns)),
	    relevance: 0
	  };
	  const BUILT_IN = {
	    className: 'built_in',
	    match: concat(/\b/, either(...builtIns), /(?=\()/)
	  };
	  const BUILT_INS = [
	    BUILT_IN_GUARD,
	    BUILT_IN
	  ];

	  // https://docs.swift.org/swift-book/ReferenceManual/LexicalStructure.html#ID418
	  const OPERATOR_GUARD = {
	    // Prevent -> from being highlighting as an operator.
	    match: /->/,
	    relevance: 0
	  };
	  const OPERATOR = {
	    className: 'operator',
	    relevance: 0,
	    variants: [
	      { match: operator },
	      {
	        // dot-operator: only operators that start with a dot are allowed to use dots as
	        // characters (..., ...<, .*, etc). So there rule here is: a dot followed by one or more
	        // characters that may also include dots.
	        match: `\\.(\\.|${operatorCharacter})+` }
	    ]
	  };
	  const OPERATORS = [
	    OPERATOR_GUARD,
	    OPERATOR
	  ];

	  // https://docs.swift.org/swift-book/ReferenceManual/LexicalStructure.html#grammar_numeric-literal
	  // TODO: Update for leading `-` after lookbehind is supported everywhere
	  const decimalDigits = '([0-9]_*)+';
	  const hexDigits = '([0-9a-fA-F]_*)+';
	  const NUMBER = {
	    className: 'number',
	    relevance: 0,
	    variants: [
	      // decimal floating-point-literal (subsumes decimal-literal)
	      { match: `\\b(${decimalDigits})(\\.(${decimalDigits}))?` + `([eE][+-]?(${decimalDigits}))?\\b` },
	      // hexadecimal floating-point-literal (subsumes hexadecimal-literal)
	      { match: `\\b0x(${hexDigits})(\\.(${hexDigits}))?` + `([pP][+-]?(${decimalDigits}))?\\b` },
	      // octal-literal
	      { match: /\b0o([0-7]_*)+\b/ },
	      // binary-literal
	      { match: /\b0b([01]_*)+\b/ }
	    ]
	  };

	  // https://docs.swift.org/swift-book/ReferenceManual/LexicalStructure.html#grammar_string-literal
	  const ESCAPED_CHARACTER = (rawDelimiter = "") => ({
	    className: 'subst',
	    variants: [
	      { match: concat(/\\/, rawDelimiter, /[0\\tnr"']/) },
	      { match: concat(/\\/, rawDelimiter, /u\{[0-9a-fA-F]{1,8}\}/) }
	    ]
	  });
	  const ESCAPED_NEWLINE = (rawDelimiter = "") => ({
	    className: 'subst',
	    match: concat(/\\/, rawDelimiter, /[\t ]*(?:[\r\n]|\r\n)/)
	  });
	  const INTERPOLATION = (rawDelimiter = "") => ({
	    className: 'subst',
	    label: "interpol",
	    begin: concat(/\\/, rawDelimiter, /\(/),
	    end: /\)/
	  });
	  const MULTILINE_STRING = (rawDelimiter = "") => ({
	    begin: concat(rawDelimiter, /"""/),
	    end: concat(/"""/, rawDelimiter),
	    contains: [
	      ESCAPED_CHARACTER(rawDelimiter),
	      ESCAPED_NEWLINE(rawDelimiter),
	      INTERPOLATION(rawDelimiter)
	    ]
	  });
	  const SINGLE_LINE_STRING = (rawDelimiter = "") => ({
	    begin: concat(rawDelimiter, /"/),
	    end: concat(/"/, rawDelimiter),
	    contains: [
	      ESCAPED_CHARACTER(rawDelimiter),
	      INTERPOLATION(rawDelimiter)
	    ]
	  });
	  const STRING = {
	    className: 'string',
	    variants: [
	      MULTILINE_STRING(),
	      MULTILINE_STRING("#"),
	      MULTILINE_STRING("##"),
	      MULTILINE_STRING("###"),
	      SINGLE_LINE_STRING(),
	      SINGLE_LINE_STRING("#"),
	      SINGLE_LINE_STRING("##"),
	      SINGLE_LINE_STRING("###")
	    ]
	  };

	  const REGEXP_CONTENTS = [
	    hljs.BACKSLASH_ESCAPE,
	    {
	      begin: /\[/,
	      end: /\]/,
	      relevance: 0,
	      contains: [ hljs.BACKSLASH_ESCAPE ]
	    }
	  ];

	  const BARE_REGEXP_LITERAL = {
	    begin: /\/[^\s](?=[^/\n]*\/)/,
	    end: /\//,
	    contains: REGEXP_CONTENTS
	  };

	  const EXTENDED_REGEXP_LITERAL = (rawDelimiter) => {
	    const begin = concat(rawDelimiter, /\//);
	    const end = concat(/\//, rawDelimiter);
	    return {
	      begin,
	      end,
	      contains: [
	        ...REGEXP_CONTENTS,
	        {
	          scope: "comment",
	          begin: `#(?!.*${end})`,
	          end: /$/,
	        },
	      ],
	    };
	  };

	  // https://docs.swift.org/swift-book/documentation/the-swift-programming-language/lexicalstructure/#Regular-Expression-Literals
	  const REGEXP = {
	    scope: "regexp",
	    variants: [
	      EXTENDED_REGEXP_LITERAL('###'),
	      EXTENDED_REGEXP_LITERAL('##'),
	      EXTENDED_REGEXP_LITERAL('#'),
	      BARE_REGEXP_LITERAL
	    ]
	  };

	  // https://docs.swift.org/swift-book/ReferenceManual/LexicalStructure.html#ID412
	  const QUOTED_IDENTIFIER = { match: concat(/`/, identifier, /`/) };
	  const IMPLICIT_PARAMETER = {
	    className: 'variable',
	    match: /\$\d+/
	  };
	  const PROPERTY_WRAPPER_PROJECTION = {
	    className: 'variable',
	    match: `\\$${identifierCharacter}+`
	  };
	  const IDENTIFIERS = [
	    QUOTED_IDENTIFIER,
	    IMPLICIT_PARAMETER,
	    PROPERTY_WRAPPER_PROJECTION
	  ];

	  // https://docs.swift.org/swift-book/ReferenceManual/Attributes.html
	  const AVAILABLE_ATTRIBUTE = {
	    match: /(@|#(un)?)available/,
	    scope: 'keyword',
	    starts: { contains: [
	      {
	        begin: /\(/,
	        end: /\)/,
	        keywords: availabilityKeywords,
	        contains: [
	          ...OPERATORS,
	          NUMBER,
	          STRING
	        ]
	      }
	    ] }
	  };
	  const KEYWORD_ATTRIBUTE = {
	    scope: 'keyword',
	    match: concat(/@/, either(...keywordAttributes))
	  };
	  const USER_DEFINED_ATTRIBUTE = {
	    scope: 'meta',
	    match: concat(/@/, identifier)
	  };
	  const ATTRIBUTES = [
	    AVAILABLE_ATTRIBUTE,
	    KEYWORD_ATTRIBUTE,
	    USER_DEFINED_ATTRIBUTE
	  ];

	  // https://docs.swift.org/swift-book/ReferenceManual/Types.html
	  const TYPE = {
	    match: lookahead(/\b[A-Z]/),
	    relevance: 0,
	    contains: [
	      { // Common Apple frameworks, for relevance boost
	        className: 'type',
	        match: concat(/(AV|CA|CF|CG|CI|CL|CM|CN|CT|MK|MP|MTK|MTL|NS|SCN|SK|UI|WK|XC)/, identifierCharacter, '+')
	      },
	      { // Type identifier
	        className: 'type',
	        match: typeIdentifier,
	        relevance: 0
	      },
	      { // Optional type
	        match: /[?!]+/,
	        relevance: 0
	      },
	      { // Variadic parameter
	        match: /\.\.\./,
	        relevance: 0
	      },
	      { // Protocol composition
	        match: concat(/\s+&\s+/, lookahead(typeIdentifier)),
	        relevance: 0
	      }
	    ]
	  };
	  const GENERIC_ARGUMENTS = {
	    begin: /</,
	    end: />/,
	    keywords: KEYWORDS,
	    contains: [
	      ...COMMENTS,
	      ...KEYWORD_MODES,
	      ...ATTRIBUTES,
	      OPERATOR_GUARD,
	      TYPE
	    ]
	  };
	  TYPE.contains.push(GENERIC_ARGUMENTS);

	  // https://docs.swift.org/swift-book/ReferenceManual/Expressions.html#ID552
	  // Prevents element names from being highlighted as keywords.
	  const TUPLE_ELEMENT_NAME = {
	    match: concat(identifier, /\s*:/),
	    keywords: "_|0",
	    relevance: 0
	  };
	  // Matches tuples as well as the parameter list of a function type.
	  const TUPLE = {
	    begin: /\(/,
	    end: /\)/,
	    relevance: 0,
	    keywords: KEYWORDS,
	    contains: [
	      'self',
	      TUPLE_ELEMENT_NAME,
	      ...COMMENTS,
	      REGEXP,
	      ...KEYWORD_MODES,
	      ...BUILT_INS,
	      ...OPERATORS,
	      NUMBER,
	      STRING,
	      ...IDENTIFIERS,
	      ...ATTRIBUTES,
	      TYPE
	    ]
	  };

	  const GENERIC_PARAMETERS = {
	    begin: /</,
	    end: />/,
	    keywords: 'repeat each',
	    contains: [
	      ...COMMENTS,
	      TYPE
	    ]
	  };
	  const FUNCTION_PARAMETER_NAME = {
	    begin: either(
	      lookahead(concat(identifier, /\s*:/)),
	      lookahead(concat(identifier, /\s+/, identifier, /\s*:/))
	    ),
	    end: /:/,
	    relevance: 0,
	    contains: [
	      {
	        className: 'keyword',
	        match: /\b_\b/
	      },
	      {
	        className: 'params',
	        match: identifier
	      }
	    ]
	  };
	  const FUNCTION_PARAMETERS = {
	    begin: /\(/,
	    end: /\)/,
	    keywords: KEYWORDS,
	    contains: [
	      FUNCTION_PARAMETER_NAME,
	      ...COMMENTS,
	      ...KEYWORD_MODES,
	      ...OPERATORS,
	      NUMBER,
	      STRING,
	      ...ATTRIBUTES,
	      TYPE,
	      TUPLE
	    ],
	    endsParent: true,
	    illegal: /["']/
	  };
	  // https://docs.swift.org/swift-book/ReferenceManual/Declarations.html#ID362
	  // https://docs.swift.org/swift-book/documentation/the-swift-programming-language/declarations/#Macro-Declaration
	  const FUNCTION_OR_MACRO = {
	    match: [
	      /(func|macro)/,
	      /\s+/,
	      either(QUOTED_IDENTIFIER.match, identifier, operator)
	    ],
	    className: {
	      1: "keyword",
	      3: "title.function"
	    },
	    contains: [
	      GENERIC_PARAMETERS,
	      FUNCTION_PARAMETERS,
	      WHITESPACE
	    ],
	    illegal: [
	      /\[/,
	      /%/
	    ]
	  };

	  // https://docs.swift.org/swift-book/ReferenceManual/Declarations.html#ID375
	  // https://docs.swift.org/swift-book/ReferenceManual/Declarations.html#ID379
	  const INIT_SUBSCRIPT = {
	    match: [
	      /\b(?:subscript|init[?!]?)/,
	      /\s*(?=[<(])/,
	    ],
	    className: { 1: "keyword" },
	    contains: [
	      GENERIC_PARAMETERS,
	      FUNCTION_PARAMETERS,
	      WHITESPACE
	    ],
	    illegal: /\[|%/
	  };
	  // https://docs.swift.org/swift-book/ReferenceManual/Declarations.html#ID380
	  const OPERATOR_DECLARATION = {
	    match: [
	      /operator/,
	      /\s+/,
	      operator
	    ],
	    className: {
	      1: "keyword",
	      3: "title"
	    }
	  };

	  // https://docs.swift.org/swift-book/ReferenceManual/Declarations.html#ID550
	  const PRECEDENCEGROUP = {
	    begin: [
	      /precedencegroup/,
	      /\s+/,
	      typeIdentifier
	    ],
	    className: {
	      1: "keyword",
	      3: "title"
	    },
	    contains: [ TYPE ],
	    keywords: [
	      ...precedencegroupKeywords,
	      ...literals
	    ],
	    end: /}/
	  };

	  // Add supported submodes to string interpolation.
	  for (const variant of STRING.variants) {
	    const interpolation = variant.contains.find(mode => mode.label === "interpol");
	    // TODO: Interpolation can contain any expression, so there's room for improvement here.
	    interpolation.keywords = KEYWORDS;
	    const submodes = [
	      ...KEYWORD_MODES,
	      ...BUILT_INS,
	      ...OPERATORS,
	      NUMBER,
	      STRING,
	      ...IDENTIFIERS
	    ];
	    interpolation.contains = [
	      ...submodes,
	      {
	        begin: /\(/,
	        end: /\)/,
	        contains: [
	          'self',
	          ...submodes
	        ]
	      }
	    ];
	  }

	  return {
	    name: 'Swift',
	    keywords: KEYWORDS,
	    contains: [
	      ...COMMENTS,
	      FUNCTION_OR_MACRO,
	      INIT_SUBSCRIPT,
	      {
	        beginKeywords: 'struct protocol class extension enum actor',
	        end: '\\{',
	        excludeEnd: true,
	        keywords: KEYWORDS,
	        contains: [
	          hljs.inherit(hljs.TITLE_MODE, {
	            className: "title.class",
	            begin: /[A-Za-z$_][\u00C0-\u02B80-9A-Za-z$_]*/
	          }),
	          ...KEYWORD_MODES
	        ]
	      },
	      OPERATOR_DECLARATION,
	      PRECEDENCEGROUP,
	      {
	        beginKeywords: 'import',
	        end: /$/,
	        contains: [ ...COMMENTS ],
	        relevance: 0
	      },
	      REGEXP,
	      ...KEYWORD_MODES,
	      ...BUILT_INS,
	      ...OPERATORS,
	      NUMBER,
	      STRING,
	      ...IDENTIFIERS,
	      ...ATTRIBUTES,
	      TYPE,
	      TUPLE
	    ]
	  };
	}

	swift_1 = swift;
	return swift_1;
}

/*
Language: YAML
Description: Yet Another Markdown Language
Author: Stefan Wienert <stwienert@gmail.com>
Contributors: Carl Baxter <carl@cbax.tech>
Requires: ruby.js
Website: https://yaml.org
Category: common, config
*/

var yaml_1;
var hasRequiredYaml;

function requireYaml () {
	if (hasRequiredYaml) return yaml_1;
	hasRequiredYaml = 1;
	function yaml(hljs) {
	  const LITERALS = 'true false yes no null';

	  // YAML spec allows non-reserved URI characters in tags.
	  const URI_CHARACTERS = '[\\w#;/?:@&=+$,.~*\'()[\\]]+';

	  // Define keys as starting with a word character
	  // ...containing word chars, spaces, colons, forward-slashes, hyphens and periods
	  // ...and ending with a colon followed immediately by a space, tab or newline.
	  // The YAML spec allows for much more than this, but this covers most use-cases.
	  const KEY = {
	    className: 'attr',
	    variants: [
	      { begin: '\\w[\\w :\\/.-]*:(?=[ \t]|$)' },
	      { // double quoted keys
	        begin: '"\\w[\\w :\\/.-]*":(?=[ \t]|$)' },
	      { // single quoted keys
	        begin: '\'\\w[\\w :\\/.-]*\':(?=[ \t]|$)' }
	    ]
	  };

	  const TEMPLATE_VARIABLES = {
	    className: 'template-variable',
	    variants: [
	      { // jinja templates Ansible
	        begin: /\{\{/,
	        end: /\}\}/
	      },
	      { // Ruby i18n
	        begin: /%\{/,
	        end: /\}/
	      }
	    ]
	  };
	  const STRING = {
	    className: 'string',
	    relevance: 0,
	    variants: [
	      {
	        begin: /'/,
	        end: /'/
	      },
	      {
	        begin: /"/,
	        end: /"/
	      },
	      { begin: /\S+/ }
	    ],
	    contains: [
	      hljs.BACKSLASH_ESCAPE,
	      TEMPLATE_VARIABLES
	    ]
	  };

	  // Strings inside of value containers (objects) can't contain braces,
	  // brackets, or commas
	  const CONTAINER_STRING = hljs.inherit(STRING, { variants: [
	    {
	      begin: /'/,
	      end: /'/
	    },
	    {
	      begin: /"/,
	      end: /"/
	    },
	    { begin: /[^\s,{}[\]]+/ }
	  ] });

	  const DATE_RE = '[0-9]{4}(-[0-9][0-9]){0,2}';
	  const TIME_RE = '([Tt \\t][0-9][0-9]?(:[0-9][0-9]){2})?';
	  const FRACTION_RE = '(\\.[0-9]*)?';
	  const ZONE_RE = '([ \\t])*(Z|[-+][0-9][0-9]?(:[0-9][0-9])?)?';
	  const TIMESTAMP = {
	    className: 'number',
	    begin: '\\b' + DATE_RE + TIME_RE + FRACTION_RE + ZONE_RE + '\\b'
	  };

	  const VALUE_CONTAINER = {
	    end: ',',
	    endsWithParent: true,
	    excludeEnd: true,
	    keywords: LITERALS,
	    relevance: 0
	  };
	  const OBJECT = {
	    begin: /\{/,
	    end: /\}/,
	    contains: [ VALUE_CONTAINER ],
	    illegal: '\\n',
	    relevance: 0
	  };
	  const ARRAY = {
	    begin: '\\[',
	    end: '\\]',
	    contains: [ VALUE_CONTAINER ],
	    illegal: '\\n',
	    relevance: 0
	  };

	  const MODES = [
	    KEY,
	    {
	      className: 'meta',
	      begin: '^---\\s*$',
	      relevance: 10
	    },
	    { // multi line string
	      // Blocks start with a | or > followed by a newline
	      //
	      // Indentation of subsequent lines must be the same to
	      // be considered part of the block
	      className: 'string',
	      begin: '[\\|>]([1-9]?[+-])?[ ]*\\n( +)[^ ][^\\n]*\\n(\\2[^\\n]+\\n?)*'
	    },
	    { // Ruby/Rails erb
	      begin: '<%[%=-]?',
	      end: '[%-]?%>',
	      subLanguage: 'ruby',
	      excludeBegin: true,
	      excludeEnd: true,
	      relevance: 0
	    },
	    { // named tags
	      className: 'type',
	      begin: '!\\w+!' + URI_CHARACTERS
	    },
	    // https://yaml.org/spec/1.2/spec.html#id2784064
	    { // verbatim tags
	      className: 'type',
	      begin: '!<' + URI_CHARACTERS + ">"
	    },
	    { // primary tags
	      className: 'type',
	      begin: '!' + URI_CHARACTERS
	    },
	    { // secondary tags
	      className: 'type',
	      begin: '!!' + URI_CHARACTERS
	    },
	    { // fragment id &ref
	      className: 'meta',
	      begin: '&' + hljs.UNDERSCORE_IDENT_RE + '$'
	    },
	    { // fragment reference *ref
	      className: 'meta',
	      begin: '\\*' + hljs.UNDERSCORE_IDENT_RE + '$'
	    },
	    { // array listing
	      className: 'bullet',
	      // TODO: remove |$ hack when we have proper look-ahead support
	      begin: '-(?=[ ]|$)',
	      relevance: 0
	    },
	    hljs.HASH_COMMENT_MODE,
	    {
	      beginKeywords: LITERALS,
	      keywords: { literal: LITERALS }
	    },
	    TIMESTAMP,
	    // numbers are any valid C-style number that
	    // sit isolated from other words
	    {
	      className: 'number',
	      begin: hljs.C_NUMBER_RE + '\\b',
	      relevance: 0
	    },
	    OBJECT,
	    ARRAY,
	    STRING
	  ];

	  const VALUE_MODES = [ ...MODES ];
	  VALUE_MODES.pop();
	  VALUE_MODES.push(CONTAINER_STRING);
	  VALUE_CONTAINER.contains = VALUE_MODES;

	  return {
	    name: 'YAML',
	    case_insensitive: true,
	    aliases: [ 'yml' ],
	    contains: MODES
	  };
	}

	yaml_1 = yaml;
	return yaml_1;
}

var typescript_1;
var hasRequiredTypescript;

function requireTypescript () {
	if (hasRequiredTypescript) return typescript_1;
	hasRequiredTypescript = 1;
	const IDENT_RE = '[A-Za-z$_][0-9A-Za-z$_]*';
	const KEYWORDS = [
	  "as", // for exports
	  "in",
	  "of",
	  "if",
	  "for",
	  "while",
	  "finally",
	  "var",
	  "new",
	  "function",
	  "do",
	  "return",
	  "void",
	  "else",
	  "break",
	  "catch",
	  "instanceof",
	  "with",
	  "throw",
	  "case",
	  "default",
	  "try",
	  "switch",
	  "continue",
	  "typeof",
	  "delete",
	  "let",
	  "yield",
	  "const",
	  "class",
	  // JS handles these with a special rule
	  // "get",
	  // "set",
	  "debugger",
	  "async",
	  "await",
	  "static",
	  "import",
	  "from",
	  "export",
	  "extends"
	];
	const LITERALS = [
	  "true",
	  "false",
	  "null",
	  "undefined",
	  "NaN",
	  "Infinity"
	];

	// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects
	const TYPES = [
	  // Fundamental objects
	  "Object",
	  "Function",
	  "Boolean",
	  "Symbol",
	  // numbers and dates
	  "Math",
	  "Date",
	  "Number",
	  "BigInt",
	  // text
	  "String",
	  "RegExp",
	  // Indexed collections
	  "Array",
	  "Float32Array",
	  "Float64Array",
	  "Int8Array",
	  "Uint8Array",
	  "Uint8ClampedArray",
	  "Int16Array",
	  "Int32Array",
	  "Uint16Array",
	  "Uint32Array",
	  "BigInt64Array",
	  "BigUint64Array",
	  // Keyed collections
	  "Set",
	  "Map",
	  "WeakSet",
	  "WeakMap",
	  // Structured data
	  "ArrayBuffer",
	  "SharedArrayBuffer",
	  "Atomics",
	  "DataView",
	  "JSON",
	  // Control abstraction objects
	  "Promise",
	  "Generator",
	  "GeneratorFunction",
	  "AsyncFunction",
	  // Reflection
	  "Reflect",
	  "Proxy",
	  // Internationalization
	  "Intl",
	  // WebAssembly
	  "WebAssembly"
	];

	const ERROR_TYPES = [
	  "Error",
	  "EvalError",
	  "InternalError",
	  "RangeError",
	  "ReferenceError",
	  "SyntaxError",
	  "TypeError",
	  "URIError"
	];

	const BUILT_IN_GLOBALS = [
	  "setInterval",
	  "setTimeout",
	  "clearInterval",
	  "clearTimeout",

	  "require",
	  "exports",

	  "eval",
	  "isFinite",
	  "isNaN",
	  "parseFloat",
	  "parseInt",
	  "decodeURI",
	  "decodeURIComponent",
	  "encodeURI",
	  "encodeURIComponent",
	  "escape",
	  "unescape"
	];

	const BUILT_IN_VARIABLES = [
	  "arguments",
	  "this",
	  "super",
	  "console",
	  "window",
	  "document",
	  "localStorage",
	  "sessionStorage",
	  "module",
	  "global" // Node.js
	];

	const BUILT_INS = [].concat(
	  BUILT_IN_GLOBALS,
	  TYPES,
	  ERROR_TYPES
	);

	/*
	Language: JavaScript
	Description: JavaScript (JS) is a lightweight, interpreted, or just-in-time compiled programming language with first-class functions.
	Category: common, scripting, web
	Website: https://developer.mozilla.org/en-US/docs/Web/JavaScript
	*/


	/** @type LanguageFn */
	function javascript(hljs) {
	  const regex = hljs.regex;
	  /**
	   * Takes a string like "<Booger" and checks to see
	   * if we can find a matching "</Booger" later in the
	   * content.
	   * @param {RegExpMatchArray} match
	   * @param {{after:number}} param1
	   */
	  const hasClosingTag = (match, { after }) => {
	    const tag = "</" + match[0].slice(1);
	    const pos = match.input.indexOf(tag, after);
	    return pos !== -1;
	  };

	  const IDENT_RE$1 = IDENT_RE;
	  const FRAGMENT = {
	    begin: '<>',
	    end: '</>'
	  };
	  // to avoid some special cases inside isTrulyOpeningTag
	  const XML_SELF_CLOSING = /<[A-Za-z0-9\\._:-]+\s*\/>/;
	  const XML_TAG = {
	    begin: /<[A-Za-z0-9\\._:-]+/,
	    end: /\/[A-Za-z0-9\\._:-]+>|\/>/,
	    /**
	     * @param {RegExpMatchArray} match
	     * @param {CallbackResponse} response
	     */
	    isTrulyOpeningTag: (match, response) => {
	      const afterMatchIndex = match[0].length + match.index;
	      const nextChar = match.input[afterMatchIndex];
	      if (
	        // HTML should not include another raw `<` inside a tag
	        // nested type?
	        // `<Array<Array<number>>`, etc.
	        nextChar === "<" ||
	        // the , gives away that this is not HTML
	        // `<T, A extends keyof T, V>`
	        nextChar === ","
	        ) {
	        response.ignoreMatch();
	        return;
	      }

	      // `<something>`
	      // Quite possibly a tag, lets look for a matching closing tag...
	      if (nextChar === ">") {
	        // if we cannot find a matching closing tag, then we
	        // will ignore it
	        if (!hasClosingTag(match, { after: afterMatchIndex })) {
	          response.ignoreMatch();
	        }
	      }

	      // `<blah />` (self-closing)
	      // handled by simpleSelfClosing rule

	      let m;
	      const afterMatch = match.input.substring(afterMatchIndex);

	      // some more template typing stuff
	      //  <T = any>(key?: string) => Modify<
	      if ((m = afterMatch.match(/^\s*=/))) {
	        response.ignoreMatch();
	        return;
	      }

	      // `<From extends string>`
	      // technically this could be HTML, but it smells like a type
	      // NOTE: This is ugh, but added specifically for https://github.com/highlightjs/highlight.js/issues/3276
	      if ((m = afterMatch.match(/^\s+extends\s+/))) {
	        if (m.index === 0) {
	          response.ignoreMatch();
	          // eslint-disable-next-line no-useless-return
	          return;
	        }
	      }
	    }
	  };
	  const KEYWORDS$1 = {
	    $pattern: IDENT_RE,
	    keyword: KEYWORDS,
	    literal: LITERALS,
	    built_in: BUILT_INS,
	    "variable.language": BUILT_IN_VARIABLES
	  };

	  // https://tc39.es/ecma262/#sec-literals-numeric-literals
	  const decimalDigits = '[0-9](_?[0-9])*';
	  const frac = `\\.(${decimalDigits})`;
	  // DecimalIntegerLiteral, including Annex B NonOctalDecimalIntegerLiteral
	  // https://tc39.es/ecma262/#sec-additional-syntax-numeric-literals
	  const decimalInteger = `0|[1-9](_?[0-9])*|0[0-7]*[89][0-9]*`;
	  const NUMBER = {
	    className: 'number',
	    variants: [
	      // DecimalLiteral
	      { begin: `(\\b(${decimalInteger})((${frac})|\\.)?|(${frac}))` +
	        `[eE][+-]?(${decimalDigits})\\b` },
	      { begin: `\\b(${decimalInteger})\\b((${frac})\\b|\\.)?|(${frac})\\b` },

	      // DecimalBigIntegerLiteral
	      { begin: `\\b(0|[1-9](_?[0-9])*)n\\b` },

	      // NonDecimalIntegerLiteral
	      { begin: "\\b0[xX][0-9a-fA-F](_?[0-9a-fA-F])*n?\\b" },
	      { begin: "\\b0[bB][0-1](_?[0-1])*n?\\b" },
	      { begin: "\\b0[oO][0-7](_?[0-7])*n?\\b" },

	      // LegacyOctalIntegerLiteral (does not include underscore separators)
	      // https://tc39.es/ecma262/#sec-additional-syntax-numeric-literals
	      { begin: "\\b0[0-7]+n?\\b" },
	    ],
	    relevance: 0
	  };

	  const SUBST = {
	    className: 'subst',
	    begin: '\\$\\{',
	    end: '\\}',
	    keywords: KEYWORDS$1,
	    contains: [] // defined later
	  };
	  const HTML_TEMPLATE = {
	    begin: 'html`',
	    end: '',
	    starts: {
	      end: '`',
	      returnEnd: false,
	      contains: [
	        hljs.BACKSLASH_ESCAPE,
	        SUBST
	      ],
	      subLanguage: 'xml'
	    }
	  };
	  const CSS_TEMPLATE = {
	    begin: 'css`',
	    end: '',
	    starts: {
	      end: '`',
	      returnEnd: false,
	      contains: [
	        hljs.BACKSLASH_ESCAPE,
	        SUBST
	      ],
	      subLanguage: 'css'
	    }
	  };
	  const GRAPHQL_TEMPLATE = {
	    begin: 'gql`',
	    end: '',
	    starts: {
	      end: '`',
	      returnEnd: false,
	      contains: [
	        hljs.BACKSLASH_ESCAPE,
	        SUBST
	      ],
	      subLanguage: 'graphql'
	    }
	  };
	  const TEMPLATE_STRING = {
	    className: 'string',
	    begin: '`',
	    end: '`',
	    contains: [
	      hljs.BACKSLASH_ESCAPE,
	      SUBST
	    ]
	  };
	  const JSDOC_COMMENT = hljs.COMMENT(
	    /\/\*\*(?!\/)/,
	    '\\*/',
	    {
	      relevance: 0,
	      contains: [
	        {
	          begin: '(?=@[A-Za-z]+)',
	          relevance: 0,
	          contains: [
	            {
	              className: 'doctag',
	              begin: '@[A-Za-z]+'
	            },
	            {
	              className: 'type',
	              begin: '\\{',
	              end: '\\}',
	              excludeEnd: true,
	              excludeBegin: true,
	              relevance: 0
	            },
	            {
	              className: 'variable',
	              begin: IDENT_RE$1 + '(?=\\s*(-)|$)',
	              endsParent: true,
	              relevance: 0
	            },
	            // eat spaces (not newlines) so we can find
	            // types or variables
	            {
	              begin: /(?=[^\n])\s/,
	              relevance: 0
	            }
	          ]
	        }
	      ]
	    }
	  );
	  const COMMENT = {
	    className: "comment",
	    variants: [
	      JSDOC_COMMENT,
	      hljs.C_BLOCK_COMMENT_MODE,
	      hljs.C_LINE_COMMENT_MODE
	    ]
	  };
	  const SUBST_INTERNALS = [
	    hljs.APOS_STRING_MODE,
	    hljs.QUOTE_STRING_MODE,
	    HTML_TEMPLATE,
	    CSS_TEMPLATE,
	    GRAPHQL_TEMPLATE,
	    TEMPLATE_STRING,
	    // Skip numbers when they are part of a variable name
	    { match: /\$\d+/ },
	    NUMBER,
	    // This is intentional:
	    // See https://github.com/highlightjs/highlight.js/issues/3288
	    // hljs.REGEXP_MODE
	  ];
	  SUBST.contains = SUBST_INTERNALS
	    .concat({
	      // we need to pair up {} inside our subst to prevent
	      // it from ending too early by matching another }
	      begin: /\{/,
	      end: /\}/,
	      keywords: KEYWORDS$1,
	      contains: [
	        "self"
	      ].concat(SUBST_INTERNALS)
	    });
	  const SUBST_AND_COMMENTS = [].concat(COMMENT, SUBST.contains);
	  const PARAMS_CONTAINS = SUBST_AND_COMMENTS.concat([
	    // eat recursive parens in sub expressions
	    {
	      begin: /\(/,
	      end: /\)/,
	      keywords: KEYWORDS$1,
	      contains: ["self"].concat(SUBST_AND_COMMENTS)
	    }
	  ]);
	  const PARAMS = {
	    className: 'params',
	    begin: /\(/,
	    end: /\)/,
	    excludeBegin: true,
	    excludeEnd: true,
	    keywords: KEYWORDS$1,
	    contains: PARAMS_CONTAINS
	  };

	  // ES6 classes
	  const CLASS_OR_EXTENDS = {
	    variants: [
	      // class Car extends vehicle
	      {
	        match: [
	          /class/,
	          /\s+/,
	          IDENT_RE$1,
	          /\s+/,
	          /extends/,
	          /\s+/,
	          regex.concat(IDENT_RE$1, "(", regex.concat(/\./, IDENT_RE$1), ")*")
	        ],
	        scope: {
	          1: "keyword",
	          3: "title.class",
	          5: "keyword",
	          7: "title.class.inherited"
	        }
	      },
	      // class Car
	      {
	        match: [
	          /class/,
	          /\s+/,
	          IDENT_RE$1
	        ],
	        scope: {
	          1: "keyword",
	          3: "title.class"
	        }
	      },

	    ]
	  };

	  const CLASS_REFERENCE = {
	    relevance: 0,
	    match:
	    regex.either(
	      // Hard coded exceptions
	      /\bJSON/,
	      // Float32Array, OutT
	      /\b[A-Z][a-z]+([A-Z][a-z]*|\d)*/,
	      // CSSFactory, CSSFactoryT
	      /\b[A-Z]{2,}([A-Z][a-z]+|\d)+([A-Z][a-z]*)*/,
	      // FPs, FPsT
	      /\b[A-Z]{2,}[a-z]+([A-Z][a-z]+|\d)*([A-Z][a-z]*)*/,
	      // P
	      // single letters are not highlighted
	      // BLAH
	      // this will be flagged as a UPPER_CASE_CONSTANT instead
	    ),
	    className: "title.class",
	    keywords: {
	      _: [
	        // se we still get relevance credit for JS library classes
	        ...TYPES,
	        ...ERROR_TYPES
	      ]
	    }
	  };

	  const USE_STRICT = {
	    label: "use_strict",
	    className: 'meta',
	    relevance: 10,
	    begin: /^\s*['"]use (strict|asm)['"]/
	  };

	  const FUNCTION_DEFINITION = {
	    variants: [
	      {
	        match: [
	          /function/,
	          /\s+/,
	          IDENT_RE$1,
	          /(?=\s*\()/
	        ]
	      },
	      // anonymous function
	      {
	        match: [
	          /function/,
	          /\s*(?=\()/
	        ]
	      }
	    ],
	    className: {
	      1: "keyword",
	      3: "title.function"
	    },
	    label: "func.def",
	    contains: [ PARAMS ],
	    illegal: /%/
	  };

	  const UPPER_CASE_CONSTANT = {
	    relevance: 0,
	    match: /\b[A-Z][A-Z_0-9]+\b/,
	    className: "variable.constant"
	  };

	  function noneOf(list) {
	    return regex.concat("(?!", list.join("|"), ")");
	  }

	  const FUNCTION_CALL = {
	    match: regex.concat(
	      /\b/,
	      noneOf([
	        ...BUILT_IN_GLOBALS,
	        "super",
	        "import"
	      ]),
	      IDENT_RE$1, regex.lookahead(/\(/)),
	    className: "title.function",
	    relevance: 0
	  };

	  const PROPERTY_ACCESS = {
	    begin: regex.concat(/\./, regex.lookahead(
	      regex.concat(IDENT_RE$1, /(?![0-9A-Za-z$_(])/)
	    )),
	    end: IDENT_RE$1,
	    excludeBegin: true,
	    keywords: "prototype",
	    className: "property",
	    relevance: 0
	  };

	  const GETTER_OR_SETTER = {
	    match: [
	      /get|set/,
	      /\s+/,
	      IDENT_RE$1,
	      /(?=\()/
	    ],
	    className: {
	      1: "keyword",
	      3: "title.function"
	    },
	    contains: [
	      { // eat to avoid empty params
	        begin: /\(\)/
	      },
	      PARAMS
	    ]
	  };

	  const FUNC_LEAD_IN_RE = '(\\(' +
	    '[^()]*(\\(' +
	    '[^()]*(\\(' +
	    '[^()]*' +
	    '\\)[^()]*)*' +
	    '\\)[^()]*)*' +
	    '\\)|' + hljs.UNDERSCORE_IDENT_RE + ')\\s*=>';

	  const FUNCTION_VARIABLE = {
	    match: [
	      /const|var|let/, /\s+/,
	      IDENT_RE$1, /\s*/,
	      /=\s*/,
	      /(async\s*)?/, // async is optional
	      regex.lookahead(FUNC_LEAD_IN_RE)
	    ],
	    keywords: "async",
	    className: {
	      1: "keyword",
	      3: "title.function"
	    },
	    contains: [
	      PARAMS
	    ]
	  };

	  return {
	    name: 'JavaScript',
	    aliases: ['js', 'jsx', 'mjs', 'cjs'],
	    keywords: KEYWORDS$1,
	    // this will be extended by TypeScript
	    exports: { PARAMS_CONTAINS, CLASS_REFERENCE },
	    illegal: /#(?![$_A-z])/,
	    contains: [
	      hljs.SHEBANG({
	        label: "shebang",
	        binary: "node",
	        relevance: 5
	      }),
	      USE_STRICT,
	      hljs.APOS_STRING_MODE,
	      hljs.QUOTE_STRING_MODE,
	      HTML_TEMPLATE,
	      CSS_TEMPLATE,
	      GRAPHQL_TEMPLATE,
	      TEMPLATE_STRING,
	      COMMENT,
	      // Skip numbers when they are part of a variable name
	      { match: /\$\d+/ },
	      NUMBER,
	      CLASS_REFERENCE,
	      {
	        className: 'attr',
	        begin: IDENT_RE$1 + regex.lookahead(':'),
	        relevance: 0
	      },
	      FUNCTION_VARIABLE,
	      { // "value" container
	        begin: '(' + hljs.RE_STARTERS_RE + '|\\b(case|return|throw)\\b)\\s*',
	        keywords: 'return throw case',
	        relevance: 0,
	        contains: [
	          COMMENT,
	          hljs.REGEXP_MODE,
	          {
	            className: 'function',
	            // we have to count the parens to make sure we actually have the
	            // correct bounding ( ) before the =>.  There could be any number of
	            // sub-expressions inside also surrounded by parens.
	            begin: FUNC_LEAD_IN_RE,
	            returnBegin: true,
	            end: '\\s*=>',
	            contains: [
	              {
	                className: 'params',
	                variants: [
	                  {
	                    begin: hljs.UNDERSCORE_IDENT_RE,
	                    relevance: 0
	                  },
	                  {
	                    className: null,
	                    begin: /\(\s*\)/,
	                    skip: true
	                  },
	                  {
	                    begin: /\(/,
	                    end: /\)/,
	                    excludeBegin: true,
	                    excludeEnd: true,
	                    keywords: KEYWORDS$1,
	                    contains: PARAMS_CONTAINS
	                  }
	                ]
	              }
	            ]
	          },
	          { // could be a comma delimited list of params to a function call
	            begin: /,/,
	            relevance: 0
	          },
	          {
	            match: /\s+/,
	            relevance: 0
	          },
	          { // JSX
	            variants: [
	              { begin: FRAGMENT.begin, end: FRAGMENT.end },
	              { match: XML_SELF_CLOSING },
	              {
	                begin: XML_TAG.begin,
	                // we carefully check the opening tag to see if it truly
	                // is a tag and not a false positive
	                'on:begin': XML_TAG.isTrulyOpeningTag,
	                end: XML_TAG.end
	              }
	            ],
	            subLanguage: 'xml',
	            contains: [
	              {
	                begin: XML_TAG.begin,
	                end: XML_TAG.end,
	                skip: true,
	                contains: ['self']
	              }
	            ]
	          }
	        ],
	      },
	      FUNCTION_DEFINITION,
	      {
	        // prevent this from getting swallowed up by function
	        // since they appear "function like"
	        beginKeywords: "while if switch catch for"
	      },
	      {
	        // we have to count the parens to make sure we actually have the correct
	        // bounding ( ).  There could be any number of sub-expressions inside
	        // also surrounded by parens.
	        begin: '\\b(?!function)' + hljs.UNDERSCORE_IDENT_RE +
	          '\\(' + // first parens
	          '[^()]*(\\(' +
	            '[^()]*(\\(' +
	              '[^()]*' +
	            '\\)[^()]*)*' +
	          '\\)[^()]*)*' +
	          '\\)\\s*\\{', // end parens
	        returnBegin:true,
	        label: "func.def",
	        contains: [
	          PARAMS,
	          hljs.inherit(hljs.TITLE_MODE, { begin: IDENT_RE$1, className: "title.function" })
	        ]
	      },
	      // catch ... so it won't trigger the property rule below
	      {
	        match: /\.\.\./,
	        relevance: 0
	      },
	      PROPERTY_ACCESS,
	      // hack: prevents detection of keywords in some circumstances
	      // .keyword()
	      // $keyword = x
	      {
	        match: '\\$' + IDENT_RE$1,
	        relevance: 0
	      },
	      {
	        match: [ /\bconstructor(?=\s*\()/ ],
	        className: { 1: "title.function" },
	        contains: [ PARAMS ]
	      },
	      FUNCTION_CALL,
	      UPPER_CASE_CONSTANT,
	      CLASS_OR_EXTENDS,
	      GETTER_OR_SETTER,
	      {
	        match: /\$[(.]/ // relevance booster for a pattern common to JS libs: `$(something)` and `$.something`
	      }
	    ]
	  };
	}

	/*
	Language: TypeScript
	Author: Panu Horsmalahti <panu.horsmalahti@iki.fi>
	Contributors: Ike Ku <dempfi@yahoo.com>
	Description: TypeScript is a strict superset of JavaScript
	Website: https://www.typescriptlang.org
	Category: common, scripting
	*/


	/** @type LanguageFn */
	function typescript(hljs) {
	  const tsLanguage = javascript(hljs);

	  const IDENT_RE$1 = IDENT_RE;
	  const TYPES = [
	    "any",
	    "void",
	    "number",
	    "boolean",
	    "string",
	    "object",
	    "never",
	    "symbol",
	    "bigint",
	    "unknown"
	  ];
	  const NAMESPACE = {
	    beginKeywords: 'namespace',
	    end: /\{/,
	    excludeEnd: true,
	    contains: [ tsLanguage.exports.CLASS_REFERENCE ]
	  };
	  const INTERFACE = {
	    beginKeywords: 'interface',
	    end: /\{/,
	    excludeEnd: true,
	    keywords: {
	      keyword: 'interface extends',
	      built_in: TYPES
	    },
	    contains: [ tsLanguage.exports.CLASS_REFERENCE ]
	  };
	  const USE_STRICT = {
	    className: 'meta',
	    relevance: 10,
	    begin: /^\s*['"]use strict['"]/
	  };
	  const TS_SPECIFIC_KEYWORDS = [
	    "type",
	    "namespace",
	    "interface",
	    "public",
	    "private",
	    "protected",
	    "implements",
	    "declare",
	    "abstract",
	    "readonly",
	    "enum",
	    "override"
	  ];
	  const KEYWORDS$1 = {
	    $pattern: IDENT_RE,
	    keyword: KEYWORDS.concat(TS_SPECIFIC_KEYWORDS),
	    literal: LITERALS,
	    built_in: BUILT_INS.concat(TYPES),
	    "variable.language": BUILT_IN_VARIABLES
	  };
	  const DECORATOR = {
	    className: 'meta',
	    begin: '@' + IDENT_RE$1,
	  };

	  const swapMode = (mode, label, replacement) => {
	    const indx = mode.contains.findIndex(m => m.label === label);
	    if (indx === -1) { throw new Error("can not find mode to replace"); }

	    mode.contains.splice(indx, 1, replacement);
	  };


	  // this should update anywhere keywords is used since
	  // it will be the same actual JS object
	  Object.assign(tsLanguage.keywords, KEYWORDS$1);

	  tsLanguage.exports.PARAMS_CONTAINS.push(DECORATOR);
	  tsLanguage.contains = tsLanguage.contains.concat([
	    DECORATOR,
	    NAMESPACE,
	    INTERFACE,
	  ]);

	  // TS gets a simpler shebang rule than JS
	  swapMode(tsLanguage, "shebang", hljs.SHEBANG());
	  // JS use strict rule purposely excludes `asm` which makes no sense
	  swapMode(tsLanguage, "use_strict", USE_STRICT);

	  const functionDeclaration = tsLanguage.contains.find(m => m.label === "func.def");
	  functionDeclaration.relevance = 0; // () => {} is more typical in TypeScript

	  Object.assign(tsLanguage, {
	    name: 'TypeScript',
	    aliases: [
	      'ts',
	      'tsx',
	      'mts',
	      'cts'
	    ]
	  });

	  return tsLanguage;
	}

	typescript_1 = typescript;
	return typescript_1;
}

/*
Language: Visual Basic .NET
Description: Visual Basic .NET (VB.NET) is a multi-paradigm, object-oriented programming language, implemented on the .NET Framework.
Authors: Poren Chiang <ren.chiang@gmail.com>, Jan Pilzer
Website: https://docs.microsoft.com/dotnet/visual-basic/getting-started
Category: common
*/

var vbnet_1;
var hasRequiredVbnet;

function requireVbnet () {
	if (hasRequiredVbnet) return vbnet_1;
	hasRequiredVbnet = 1;
	/** @type LanguageFn */
	function vbnet(hljs) {
	  const regex = hljs.regex;
	  /**
	   * Character Literal
	   * Either a single character ("a"C) or an escaped double quote (""""C).
	   */
	  const CHARACTER = {
	    className: 'string',
	    begin: /"(""|[^/n])"C\b/
	  };

	  const STRING = {
	    className: 'string',
	    begin: /"/,
	    end: /"/,
	    illegal: /\n/,
	    contains: [
	      {
	        // double quote escape
	        begin: /""/ }
	    ]
	  };

	  /** Date Literals consist of a date, a time, or both separated by whitespace, surrounded by # */
	  const MM_DD_YYYY = /\d{1,2}\/\d{1,2}\/\d{4}/;
	  const YYYY_MM_DD = /\d{4}-\d{1,2}-\d{1,2}/;
	  const TIME_12H = /(\d|1[012])(:\d+){0,2} *(AM|PM)/;
	  const TIME_24H = /\d{1,2}(:\d{1,2}){1,2}/;
	  const DATE = {
	    className: 'literal',
	    variants: [
	      {
	        // #YYYY-MM-DD# (ISO-Date) or #M/D/YYYY# (US-Date)
	        begin: regex.concat(/# */, regex.either(YYYY_MM_DD, MM_DD_YYYY), / *#/) },
	      {
	        // #H:mm[:ss]# (24h Time)
	        begin: regex.concat(/# */, TIME_24H, / *#/) },
	      {
	        // #h[:mm[:ss]] A# (12h Time)
	        begin: regex.concat(/# */, TIME_12H, / *#/) },
	      {
	        // date plus time
	        begin: regex.concat(
	          /# */,
	          regex.either(YYYY_MM_DD, MM_DD_YYYY),
	          / +/,
	          regex.either(TIME_12H, TIME_24H),
	          / *#/
	        ) }
	    ]
	  };

	  const NUMBER = {
	    className: 'number',
	    relevance: 0,
	    variants: [
	      {
	        // Float
	        begin: /\b\d[\d_]*((\.[\d_]+(E[+-]?[\d_]+)?)|(E[+-]?[\d_]+))[RFD@!#]?/ },
	      {
	        // Integer (base 10)
	        begin: /\b\d[\d_]*((U?[SIL])|[%&])?/ },
	      {
	        // Integer (base 16)
	        begin: /&H[\dA-F_]+((U?[SIL])|[%&])?/ },
	      {
	        // Integer (base 8)
	        begin: /&O[0-7_]+((U?[SIL])|[%&])?/ },
	      {
	        // Integer (base 2)
	        begin: /&B[01_]+((U?[SIL])|[%&])?/ }
	    ]
	  };

	  const LABEL = {
	    className: 'label',
	    begin: /^\w+:/
	  };

	  const DOC_COMMENT = hljs.COMMENT(/'''/, /$/, { contains: [
	    {
	      className: 'doctag',
	      begin: /<\/?/,
	      end: />/
	    }
	  ] });

	  const COMMENT = hljs.COMMENT(null, /$/, { variants: [
	    { begin: /'/ },
	    {
	      // TODO: Use multi-class for leading spaces
	      begin: /([\t ]|^)REM(?=\s)/ }
	  ] });

	  const DIRECTIVES = {
	    className: 'meta',
	    // TODO: Use multi-class for indentation once available
	    begin: /[\t ]*#(const|disable|else|elseif|enable|end|externalsource|if|region)\b/,
	    end: /$/,
	    keywords: { keyword:
	        'const disable else elseif enable end externalsource if region then' },
	    contains: [ COMMENT ]
	  };

	  return {
	    name: 'Visual Basic .NET',
	    aliases: [ 'vb' ],
	    case_insensitive: true,
	    classNameAliases: { label: 'symbol' },
	    keywords: {
	      keyword:
	        'addhandler alias aggregate ansi as async assembly auto binary by byref byval ' /* a-b */
	        + 'call case catch class compare const continue custom declare default delegate dim distinct do ' /* c-d */
	        + 'each equals else elseif end enum erase error event exit explicit finally for friend from function ' /* e-f */
	        + 'get global goto group handles if implements imports in inherits interface into iterator ' /* g-i */
	        + 'join key let lib loop me mid module mustinherit mustoverride mybase myclass ' /* j-m */
	        + 'namespace narrowing new next notinheritable notoverridable ' /* n */
	        + 'of off on operator option optional order overloads overridable overrides ' /* o */
	        + 'paramarray partial preserve private property protected public ' /* p */
	        + 'raiseevent readonly redim removehandler resume return ' /* r */
	        + 'select set shadows shared skip static step stop structure strict sub synclock ' /* s */
	        + 'take text then throw to try unicode until using when where while widening with withevents writeonly yield' /* t-y */,
	      built_in:
	        // Operators https://docs.microsoft.com/dotnet/visual-basic/language-reference/operators
	        'addressof and andalso await directcast gettype getxmlnamespace is isfalse isnot istrue like mod nameof new not or orelse trycast typeof xor '
	        // Type Conversion Functions https://docs.microsoft.com/dotnet/visual-basic/language-reference/functions/type-conversion-functions
	        + 'cbool cbyte cchar cdate cdbl cdec cint clng cobj csbyte cshort csng cstr cuint culng cushort',
	      type:
	        // Data types https://docs.microsoft.com/dotnet/visual-basic/language-reference/data-types
	        'boolean byte char date decimal double integer long object sbyte short single string uinteger ulong ushort',
	      literal: 'true false nothing'
	    },
	    illegal:
	      '//|\\{|\\}|endif|gosub|variant|wend|^\\$ ' /* reserved deprecated keywords */,
	    contains: [
	      CHARACTER,
	      STRING,
	      DATE,
	      NUMBER,
	      LABEL,
	      DOC_COMMENT,
	      COMMENT,
	      DIRECTIVES
	    ]
	  };
	}

	vbnet_1 = vbnet;
	return vbnet_1;
}

/*
Language: WebAssembly
Website: https://webassembly.org
Description:  Wasm is designed as a portable compilation target for programming languages, enabling deployment on the web for client and server applications.
Category: web, common
Audit: 2020
*/

var wasm_1;
var hasRequiredWasm;

function requireWasm () {
	if (hasRequiredWasm) return wasm_1;
	hasRequiredWasm = 1;
	/** @type LanguageFn */
	function wasm(hljs) {
	  hljs.regex;
	  const BLOCK_COMMENT = hljs.COMMENT(/\(;/, /;\)/);
	  BLOCK_COMMENT.contains.push("self");
	  const LINE_COMMENT = hljs.COMMENT(/;;/, /$/);

	  const KWS = [
	    "anyfunc",
	    "block",
	    "br",
	    "br_if",
	    "br_table",
	    "call",
	    "call_indirect",
	    "data",
	    "drop",
	    "elem",
	    "else",
	    "end",
	    "export",
	    "func",
	    "global.get",
	    "global.set",
	    "local.get",
	    "local.set",
	    "local.tee",
	    "get_global",
	    "get_local",
	    "global",
	    "if",
	    "import",
	    "local",
	    "loop",
	    "memory",
	    "memory.grow",
	    "memory.size",
	    "module",
	    "mut",
	    "nop",
	    "offset",
	    "param",
	    "result",
	    "return",
	    "select",
	    "set_global",
	    "set_local",
	    "start",
	    "table",
	    "tee_local",
	    "then",
	    "type",
	    "unreachable"
	  ];

	  const FUNCTION_REFERENCE = {
	    begin: [
	      /(?:func|call|call_indirect)/,
	      /\s+/,
	      /\$[^\s)]+/
	    ],
	    className: {
	      1: "keyword",
	      3: "title.function"
	    }
	  };

	  const ARGUMENT = {
	    className: "variable",
	    begin: /\$[\w_]+/
	  };

	  const PARENS = {
	    match: /(\((?!;)|\))+/,
	    className: "punctuation",
	    relevance: 0
	  };

	  const NUMBER = {
	    className: "number",
	    relevance: 0,
	    // borrowed from Prism, TODO: split out into variants
	    match: /[+-]?\b(?:\d(?:_?\d)*(?:\.\d(?:_?\d)*)?(?:[eE][+-]?\d(?:_?\d)*)?|0x[\da-fA-F](?:_?[\da-fA-F])*(?:\.[\da-fA-F](?:_?[\da-fA-D])*)?(?:[pP][+-]?\d(?:_?\d)*)?)\b|\binf\b|\bnan(?::0x[\da-fA-F](?:_?[\da-fA-D])*)?\b/
	  };

	  const TYPE = {
	    // look-ahead prevents us from gobbling up opcodes
	    match: /(i32|i64|f32|f64)(?!\.)/,
	    className: "type"
	  };

	  const MATH_OPERATIONS = {
	    className: "keyword",
	    // borrowed from Prism, TODO: split out into variants
	    match: /\b(f32|f64|i32|i64)(?:\.(?:abs|add|and|ceil|clz|const|convert_[su]\/i(?:32|64)|copysign|ctz|demote\/f64|div(?:_[su])?|eqz?|extend_[su]\/i32|floor|ge(?:_[su])?|gt(?:_[su])?|le(?:_[su])?|load(?:(?:8|16|32)_[su])?|lt(?:_[su])?|max|min|mul|nearest|neg?|or|popcnt|promote\/f32|reinterpret\/[fi](?:32|64)|rem_[su]|rot[lr]|shl|shr_[su]|store(?:8|16|32)?|sqrt|sub|trunc(?:_[su]\/f(?:32|64))?|wrap\/i64|xor))\b/
	  };

	  const OFFSET_ALIGN = {
	    match: [
	      /(?:offset|align)/,
	      /\s*/,
	      /=/
	    ],
	    className: {
	      1: "keyword",
	      3: "operator"
	    }
	  };

	  return {
	    name: 'WebAssembly',
	    keywords: {
	      $pattern: /[\w.]+/,
	      keyword: KWS
	    },
	    contains: [
	      LINE_COMMENT,
	      BLOCK_COMMENT,
	      OFFSET_ALIGN,
	      ARGUMENT,
	      PARENS,
	      FUNCTION_REFERENCE,
	      hljs.QUOTE_STRING_MODE,
	      TYPE,
	      MATH_OPERATIONS,
	      NUMBER
	    ]
	  };
	}

	wasm_1 = wasm;
	return wasm_1;
}

var hljs = core;

hljs.registerLanguage('xml', requireXml());
hljs.registerLanguage('bash', requireBash());
hljs.registerLanguage('c', requireC());
hljs.registerLanguage('cpp', requireCpp());
hljs.registerLanguage('csharp', requireCsharp());
hljs.registerLanguage('css', requireCss());
hljs.registerLanguage('markdown', requireMarkdown());
hljs.registerLanguage('diff', requireDiff());
hljs.registerLanguage('ruby', requireRuby());
hljs.registerLanguage('go', requireGo());
hljs.registerLanguage('graphql', requireGraphql());
hljs.registerLanguage('ini', requireIni());
hljs.registerLanguage('java', requireJava());
hljs.registerLanguage('javascript', requireJavascript());
hljs.registerLanguage('json', requireJson());
hljs.registerLanguage('kotlin', requireKotlin());
hljs.registerLanguage('less', requireLess());
hljs.registerLanguage('lua', requireLua());
hljs.registerLanguage('makefile', requireMakefile());
hljs.registerLanguage('perl', requirePerl());
hljs.registerLanguage('objectivec', requireObjectivec());
hljs.registerLanguage('php', requirePhp());
hljs.registerLanguage('php-template', requirePhpTemplate());
hljs.registerLanguage('plaintext', requirePlaintext());
hljs.registerLanguage('python', requirePython());
hljs.registerLanguage('python-repl', requirePythonRepl());
hljs.registerLanguage('r', requireR());
hljs.registerLanguage('rust', requireRust());
hljs.registerLanguage('scss', requireScss());
hljs.registerLanguage('shell', requireShell());
hljs.registerLanguage('sql', requireSql());
hljs.registerLanguage('swift', requireSwift());
hljs.registerLanguage('yaml', requireYaml());
hljs.registerLanguage('typescript', requireTypescript());
hljs.registerLanguage('vbnet', requireVbnet());
hljs.registerLanguage('wasm', requireWasm());

hljs.HighlightJS = hljs;
hljs.default = hljs;
var common = hljs;

var HighlightJS = /*@__PURE__*/getDefaultExportFromCjs$1(common);

/* eslint-disable no-bitwise */

const decodeCache = {};

function getDecodeCache (exclude) {
  let cache = decodeCache[exclude];
  if (cache) { return cache }

  cache = decodeCache[exclude] = [];

  for (let i = 0; i < 128; i++) {
    const ch = String.fromCharCode(i);
    cache.push(ch);
  }

  for (let i = 0; i < exclude.length; i++) {
    const ch = exclude.charCodeAt(i);
    cache[ch] = '%' + ('0' + ch.toString(16).toUpperCase()).slice(-2);
  }

  return cache
}

// Decode percent-encoded string.
//
function decode$1 (string, exclude) {
  if (typeof exclude !== 'string') {
    exclude = decode$1.defaultChars;
  }

  const cache = getDecodeCache(exclude);

  return string.replace(/(%[a-f0-9]{2})+/gi, function (seq) {
    let result = '';

    for (let i = 0, l = seq.length; i < l; i += 3) {
      const b1 = parseInt(seq.slice(i + 1, i + 3), 16);

      if (b1 < 0x80) {
        result += cache[b1];
        continue
      }

      if ((b1 & 0xE0) === 0xC0 && (i + 3 < l)) {
        // 110xxxxx 10xxxxxx
        const b2 = parseInt(seq.slice(i + 4, i + 6), 16);

        if ((b2 & 0xC0) === 0x80) {
          const chr = ((b1 << 6) & 0x7C0) | (b2 & 0x3F);

          if (chr < 0x80) {
            result += '\ufffd\ufffd';
          } else {
            result += String.fromCharCode(chr);
          }

          i += 3;
          continue
        }
      }

      if ((b1 & 0xF0) === 0xE0 && (i + 6 < l)) {
        // 1110xxxx 10xxxxxx 10xxxxxx
        const b2 = parseInt(seq.slice(i + 4, i + 6), 16);
        const b3 = parseInt(seq.slice(i + 7, i + 9), 16);

        if ((b2 & 0xC0) === 0x80 && (b3 & 0xC0) === 0x80) {
          const chr = ((b1 << 12) & 0xF000) | ((b2 << 6) & 0xFC0) | (b3 & 0x3F);

          if (chr < 0x800 || (chr >= 0xD800 && chr <= 0xDFFF)) {
            result += '\ufffd\ufffd\ufffd';
          } else {
            result += String.fromCharCode(chr);
          }

          i += 6;
          continue
        }
      }

      if ((b1 & 0xF8) === 0xF0 && (i + 9 < l)) {
        // 111110xx 10xxxxxx 10xxxxxx 10xxxxxx
        const b2 = parseInt(seq.slice(i + 4, i + 6), 16);
        const b3 = parseInt(seq.slice(i + 7, i + 9), 16);
        const b4 = parseInt(seq.slice(i + 10, i + 12), 16);

        if ((b2 & 0xC0) === 0x80 && (b3 & 0xC0) === 0x80 && (b4 & 0xC0) === 0x80) {
          let chr = ((b1 << 18) & 0x1C0000) | ((b2 << 12) & 0x3F000) | ((b3 << 6) & 0xFC0) | (b4 & 0x3F);

          if (chr < 0x10000 || chr > 0x10FFFF) {
            result += '\ufffd\ufffd\ufffd\ufffd';
          } else {
            chr -= 0x10000;
            result += String.fromCharCode(0xD800 + (chr >> 10), 0xDC00 + (chr & 0x3FF));
          }

          i += 9;
          continue
        }
      }

      result += '\ufffd';
    }

    return result
  })
}

decode$1.defaultChars = ';/?:@&=+$,#';
decode$1.componentChars = '';

const encodeCache = {};

// Create a lookup array where anything but characters in `chars` string
// and alphanumeric chars is percent-encoded.
//
function getEncodeCache (exclude) {
  let cache = encodeCache[exclude];
  if (cache) { return cache }

  cache = encodeCache[exclude] = [];

  for (let i = 0; i < 128; i++) {
    const ch = String.fromCharCode(i);

    if (/^[0-9a-z]$/i.test(ch)) {
      // always allow unencoded alphanumeric characters
      cache.push(ch);
    } else {
      cache.push('%' + ('0' + i.toString(16).toUpperCase()).slice(-2));
    }
  }

  for (let i = 0; i < exclude.length; i++) {
    cache[exclude.charCodeAt(i)] = exclude[i];
  }

  return cache
}

// Encode unsafe characters with percent-encoding, skipping already
// encoded sequences.
//
//  - string       - string to encode
//  - exclude      - list of characters to ignore (in addition to a-zA-Z0-9)
//  - keepEscaped  - don't encode '%' in a correct escape sequence (default: true)
//
function encode$1 (string, exclude, keepEscaped) {
  if (typeof exclude !== 'string') {
    // encode(string, keepEscaped)
    keepEscaped = exclude;
    exclude = encode$1.defaultChars;
  }

  if (typeof keepEscaped === 'undefined') {
    keepEscaped = true;
  }

  const cache = getEncodeCache(exclude);
  let result = '';

  for (let i = 0, l = string.length; i < l; i++) {
    const code = string.charCodeAt(i);

    if (keepEscaped && code === 0x25 /* % */ && i + 2 < l) {
      if (/^[0-9a-f]{2}$/i.test(string.slice(i + 1, i + 3))) {
        result += string.slice(i, i + 3);
        i += 2;
        continue
      }
    }

    if (code < 128) {
      result += cache[code];
      continue
    }

    if (code >= 0xD800 && code <= 0xDFFF) {
      if (code >= 0xD800 && code <= 0xDBFF && i + 1 < l) {
        const nextCode = string.charCodeAt(i + 1);
        if (nextCode >= 0xDC00 && nextCode <= 0xDFFF) {
          result += encodeURIComponent(string[i] + string[i + 1]);
          i++;
          continue
        }
      }
      result += '%EF%BF%BD';
      continue
    }

    result += encodeURIComponent(string[i]);
  }

  return result
}

encode$1.defaultChars = ";/?:@&=+$,-_.!~*'()#";
encode$1.componentChars = "-_.!~*'()";

function format (url) {
  let result = '';

  result += url.protocol || '';
  result += url.slashes ? '//' : '';
  result += url.auth ? url.auth + '@' : '';

  if (url.hostname && url.hostname.indexOf(':') !== -1) {
    // ipv6 address
    result += '[' + url.hostname + ']';
  } else {
    result += url.hostname || '';
  }

  result += url.port ? ':' + url.port : '';
  result += url.pathname || '';
  result += url.search || '';
  result += url.hash || '';

  return result
}

// Copyright Joyent, Inc. and other Node contributors.
//
// Permission is hereby granted, free of charge, to any person obtaining a
// copy of this software and associated documentation files (the
// "Software"), to deal in the Software without restriction, including
// without limitation the rights to use, copy, modify, merge, publish,
// distribute, sublicense, and/or sell copies of the Software, and to permit
// persons to whom the Software is furnished to do so, subject to the
// following conditions:
//
// The above copyright notice and this permission notice shall be included
// in all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS
// OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN
// NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE
// USE OR OTHER DEALINGS IN THE SOFTWARE.

//
// Changes from joyent/node:
//
// 1. No leading slash in paths,
//    e.g. in `url.parse('http://foo?bar')` pathname is ``, not `/`
//
// 2. Backslashes are not replaced with slashes,
//    so `http:\\example.org\` is treated like a relative path
//
// 3. Trailing colon is treated like a part of the path,
//    i.e. in `http://example.org:foo` pathname is `:foo`
//
// 4. Nothing is URL-encoded in the resulting object,
//    (in joyent/node some chars in auth and paths are encoded)
//
// 5. `url.parse()` does not have `parseQueryString` argument
//
// 6. Removed extraneous result properties: `host`, `path`, `query`, etc.,
//    which can be constructed using other parts of the url.
//

function Url () {
  this.protocol = null;
  this.slashes = null;
  this.auth = null;
  this.port = null;
  this.hostname = null;
  this.hash = null;
  this.search = null;
  this.pathname = null;
}

// Reference: RFC 3986, RFC 1808, RFC 2396

// define these here so at least they only have to be
// compiled once on the first module load.
const protocolPattern = /^([a-z0-9.+-]+:)/i;
const portPattern = /:[0-9]*$/;

// Special case for a simple path URL
/* eslint-disable-next-line no-useless-escape */
const simplePathPattern = /^(\/\/?(?!\/)[^\?\s]*)(\?[^\s]*)?$/;

// RFC 2396: characters reserved for delimiting URLs.
// We actually just auto-escape these.
const delims = ['<', '>', '"', '`', ' ', '\r', '\n', '\t'];

// RFC 2396: characters not allowed for various reasons.
const unwise = ['{', '}', '|', '\\', '^', '`'].concat(delims);

// Allowed by RFCs, but cause of XSS attacks.  Always escape these.
const autoEscape = ['\''].concat(unwise);
// Characters that are never ever allowed in a hostname.
// Note that any invalid chars are also handled, but these
// are the ones that are *expected* to be seen, so we fast-path
// them.
const nonHostChars = ['%', '/', '?', ';', '#'].concat(autoEscape);
const hostEndingChars = ['/', '?', '#'];
const hostnameMaxLen = 255;
const hostnamePartPattern = /^[+a-z0-9A-Z_-]{0,63}$/;
const hostnamePartStart = /^([+a-z0-9A-Z_-]{0,63})(.*)$/;
// protocols that can allow "unsafe" and "unwise" chars.
// protocols that never have a hostname.
const hostlessProtocol = {
  javascript: true,
  'javascript:': true
};
// protocols that always contain a // bit.
const slashedProtocol = {
  http: true,
  https: true,
  ftp: true,
  gopher: true,
  file: true,
  'http:': true,
  'https:': true,
  'ftp:': true,
  'gopher:': true,
  'file:': true
};

function urlParse (url, slashesDenoteHost) {
  if (url && url instanceof Url) return url

  const u = new Url();
  u.parse(url, slashesDenoteHost);
  return u
}

Url.prototype.parse = function (url, slashesDenoteHost) {
  let lowerProto, hec, slashes;
  let rest = url;

  // trim before proceeding.
  // This is to support parse stuff like "  http://foo.com  \n"
  rest = rest.trim();

  if (!slashesDenoteHost && url.split('#').length === 1) {
    // Try fast path regexp
    const simplePath = simplePathPattern.exec(rest);
    if (simplePath) {
      this.pathname = simplePath[1];
      if (simplePath[2]) {
        this.search = simplePath[2];
      }
      return this
    }
  }

  let proto = protocolPattern.exec(rest);
  if (proto) {
    proto = proto[0];
    lowerProto = proto.toLowerCase();
    this.protocol = proto;
    rest = rest.substr(proto.length);
  }

  // figure out if it's got a host
  // user@server is *always* interpreted as a hostname, and url
  // resolution will treat //foo/bar as host=foo,path=bar because that's
  // how the browser resolves relative URLs.
  /* eslint-disable-next-line no-useless-escape */
  if (slashesDenoteHost || proto || rest.match(/^\/\/[^@\/]+@[^@\/]+/)) {
    slashes = rest.substr(0, 2) === '//';
    if (slashes && !(proto && hostlessProtocol[proto])) {
      rest = rest.substr(2);
      this.slashes = true;
    }
  }

  if (!hostlessProtocol[proto] &&
      (slashes || (proto && !slashedProtocol[proto]))) {
    // there's a hostname.
    // the first instance of /, ?, ;, or # ends the host.
    //
    // If there is an @ in the hostname, then non-host chars *are* allowed
    // to the left of the last @ sign, unless some host-ending character
    // comes *before* the @-sign.
    // URLs are obnoxious.
    //
    // ex:
    // http://a@b@c/ => user:a@b host:c
    // http://a@b?@c => user:a host:c path:/?@c

    // v0.12 TODO(isaacs): This is not quite how Chrome does things.
    // Review our test case against browsers more comprehensively.

    // find the first instance of any hostEndingChars
    let hostEnd = -1;
    for (let i = 0; i < hostEndingChars.length; i++) {
      hec = rest.indexOf(hostEndingChars[i]);
      if (hec !== -1 && (hostEnd === -1 || hec < hostEnd)) {
        hostEnd = hec;
      }
    }

    // at this point, either we have an explicit point where the
    // auth portion cannot go past, or the last @ char is the decider.
    let auth, atSign;
    if (hostEnd === -1) {
      // atSign can be anywhere.
      atSign = rest.lastIndexOf('@');
    } else {
      // atSign must be in auth portion.
      // http://a@b/c@d => host:b auth:a path:/c@d
      atSign = rest.lastIndexOf('@', hostEnd);
    }

    // Now we have a portion which is definitely the auth.
    // Pull that off.
    if (atSign !== -1) {
      auth = rest.slice(0, atSign);
      rest = rest.slice(atSign + 1);
      this.auth = auth;
    }

    // the host is the remaining to the left of the first non-host char
    hostEnd = -1;
    for (let i = 0; i < nonHostChars.length; i++) {
      hec = rest.indexOf(nonHostChars[i]);
      if (hec !== -1 && (hostEnd === -1 || hec < hostEnd)) {
        hostEnd = hec;
      }
    }
    // if we still have not hit it, then the entire thing is a host.
    if (hostEnd === -1) {
      hostEnd = rest.length;
    }

    if (rest[hostEnd - 1] === ':') { hostEnd--; }
    const host = rest.slice(0, hostEnd);
    rest = rest.slice(hostEnd);

    // pull out port.
    this.parseHost(host);

    // we've indicated that there is a hostname,
    // so even if it's empty, it has to be present.
    this.hostname = this.hostname || '';

    // if hostname begins with [ and ends with ]
    // assume that it's an IPv6 address.
    const ipv6Hostname = this.hostname[0] === '[' &&
        this.hostname[this.hostname.length - 1] === ']';

    // validate a little.
    if (!ipv6Hostname) {
      const hostparts = this.hostname.split(/\./);
      for (let i = 0, l = hostparts.length; i < l; i++) {
        const part = hostparts[i];
        if (!part) { continue }
        if (!part.match(hostnamePartPattern)) {
          let newpart = '';
          for (let j = 0, k = part.length; j < k; j++) {
            if (part.charCodeAt(j) > 127) {
              // we replace non-ASCII char with a temporary placeholder
              // we need this to make sure size of hostname is not
              // broken by replacing non-ASCII by nothing
              newpart += 'x';
            } else {
              newpart += part[j];
            }
          }
          // we test again with ASCII char only
          if (!newpart.match(hostnamePartPattern)) {
            const validParts = hostparts.slice(0, i);
            const notHost = hostparts.slice(i + 1);
            const bit = part.match(hostnamePartStart);
            if (bit) {
              validParts.push(bit[1]);
              notHost.unshift(bit[2]);
            }
            if (notHost.length) {
              rest = notHost.join('.') + rest;
            }
            this.hostname = validParts.join('.');
            break
          }
        }
      }
    }

    if (this.hostname.length > hostnameMaxLen) {
      this.hostname = '';
    }

    // strip [ and ] from the hostname
    // the host field still retains them, though
    if (ipv6Hostname) {
      this.hostname = this.hostname.substr(1, this.hostname.length - 2);
    }
  }

  // chop off from the tail first.
  const hash = rest.indexOf('#');
  if (hash !== -1) {
    // got a fragment string.
    this.hash = rest.substr(hash);
    rest = rest.slice(0, hash);
  }
  const qm = rest.indexOf('?');
  if (qm !== -1) {
    this.search = rest.substr(qm);
    rest = rest.slice(0, qm);
  }
  if (rest) { this.pathname = rest; }
  if (slashedProtocol[lowerProto] &&
      this.hostname && !this.pathname) {
    this.pathname = '';
  }

  return this
};

Url.prototype.parseHost = function (host) {
  let port = portPattern.exec(host);
  if (port) {
    port = port[0];
    if (port !== ':') {
      this.port = port.substr(1);
    }
    host = host.substr(0, host.length - port.length);
  }
  if (host) { this.hostname = host; }
};

var mdurl = /*#__PURE__*/Object.freeze({
  __proto__: null,
  decode: decode$1,
  encode: encode$1,
  format: format,
  parse: urlParse
});

var Any = /[\0-\uD7FF\uE000-\uFFFF]|[\uD800-\uDBFF][\uDC00-\uDFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF]/;

var Cc = /[\0-\x1F\x7F-\x9F]/;

var regex$1 = /[\xAD\u0600-\u0605\u061C\u06DD\u070F\u0890\u0891\u08E2\u180E\u200B-\u200F\u202A-\u202E\u2060-\u2064\u2066-\u206F\uFEFF\uFFF9-\uFFFB]|\uD804[\uDCBD\uDCCD]|\uD80D[\uDC30-\uDC3F]|\uD82F[\uDCA0-\uDCA3]|\uD834[\uDD73-\uDD7A]|\uDB40[\uDC01\uDC20-\uDC7F]/;

var P = /[!-#%-\*,-\/:;\?@\[-\]_\{\}\xA1\xA7\xAB\xB6\xB7\xBB\xBF\u037E\u0387\u055A-\u055F\u0589\u058A\u05BE\u05C0\u05C3\u05C6\u05F3\u05F4\u0609\u060A\u060C\u060D\u061B\u061D-\u061F\u066A-\u066D\u06D4\u0700-\u070D\u07F7-\u07F9\u0830-\u083E\u085E\u0964\u0965\u0970\u09FD\u0A76\u0AF0\u0C77\u0C84\u0DF4\u0E4F\u0E5A\u0E5B\u0F04-\u0F12\u0F14\u0F3A-\u0F3D\u0F85\u0FD0-\u0FD4\u0FD9\u0FDA\u104A-\u104F\u10FB\u1360-\u1368\u1400\u166E\u169B\u169C\u16EB-\u16ED\u1735\u1736\u17D4-\u17D6\u17D8-\u17DA\u1800-\u180A\u1944\u1945\u1A1E\u1A1F\u1AA0-\u1AA6\u1AA8-\u1AAD\u1B5A-\u1B60\u1B7D\u1B7E\u1BFC-\u1BFF\u1C3B-\u1C3F\u1C7E\u1C7F\u1CC0-\u1CC7\u1CD3\u2010-\u2027\u2030-\u2043\u2045-\u2051\u2053-\u205E\u207D\u207E\u208D\u208E\u2308-\u230B\u2329\u232A\u2768-\u2775\u27C5\u27C6\u27E6-\u27EF\u2983-\u2998\u29D8-\u29DB\u29FC\u29FD\u2CF9-\u2CFC\u2CFE\u2CFF\u2D70\u2E00-\u2E2E\u2E30-\u2E4F\u2E52-\u2E5D\u3001-\u3003\u3008-\u3011\u3014-\u301F\u3030\u303D\u30A0\u30FB\uA4FE\uA4FF\uA60D-\uA60F\uA673\uA67E\uA6F2-\uA6F7\uA874-\uA877\uA8CE\uA8CF\uA8F8-\uA8FA\uA8FC\uA92E\uA92F\uA95F\uA9C1-\uA9CD\uA9DE\uA9DF\uAA5C-\uAA5F\uAADE\uAADF\uAAF0\uAAF1\uABEB\uFD3E\uFD3F\uFE10-\uFE19\uFE30-\uFE52\uFE54-\uFE61\uFE63\uFE68\uFE6A\uFE6B\uFF01-\uFF03\uFF05-\uFF0A\uFF0C-\uFF0F\uFF1A\uFF1B\uFF1F\uFF20\uFF3B-\uFF3D\uFF3F\uFF5B\uFF5D\uFF5F-\uFF65]|\uD800[\uDD00-\uDD02\uDF9F\uDFD0]|\uD801\uDD6F|\uD802[\uDC57\uDD1F\uDD3F\uDE50-\uDE58\uDE7F\uDEF0-\uDEF6\uDF39-\uDF3F\uDF99-\uDF9C]|\uD803[\uDEAD\uDF55-\uDF59\uDF86-\uDF89]|\uD804[\uDC47-\uDC4D\uDCBB\uDCBC\uDCBE-\uDCC1\uDD40-\uDD43\uDD74\uDD75\uDDC5-\uDDC8\uDDCD\uDDDB\uDDDD-\uDDDF\uDE38-\uDE3D\uDEA9]|\uD805[\uDC4B-\uDC4F\uDC5A\uDC5B\uDC5D\uDCC6\uDDC1-\uDDD7\uDE41-\uDE43\uDE60-\uDE6C\uDEB9\uDF3C-\uDF3E]|\uD806[\uDC3B\uDD44-\uDD46\uDDE2\uDE3F-\uDE46\uDE9A-\uDE9C\uDE9E-\uDEA2\uDF00-\uDF09]|\uD807[\uDC41-\uDC45\uDC70\uDC71\uDEF7\uDEF8\uDF43-\uDF4F\uDFFF]|\uD809[\uDC70-\uDC74]|\uD80B[\uDFF1\uDFF2]|\uD81A[\uDE6E\uDE6F\uDEF5\uDF37-\uDF3B\uDF44]|\uD81B[\uDE97-\uDE9A\uDFE2]|\uD82F\uDC9F|\uD836[\uDE87-\uDE8B]|\uD83A[\uDD5E\uDD5F]/;

var regex = /[\$\+<->\^`\|~\xA2-\xA6\xA8\xA9\xAC\xAE-\xB1\xB4\xB8\xD7\xF7\u02C2-\u02C5\u02D2-\u02DF\u02E5-\u02EB\u02ED\u02EF-\u02FF\u0375\u0384\u0385\u03F6\u0482\u058D-\u058F\u0606-\u0608\u060B\u060E\u060F\u06DE\u06E9\u06FD\u06FE\u07F6\u07FE\u07FF\u0888\u09F2\u09F3\u09FA\u09FB\u0AF1\u0B70\u0BF3-\u0BFA\u0C7F\u0D4F\u0D79\u0E3F\u0F01-\u0F03\u0F13\u0F15-\u0F17\u0F1A-\u0F1F\u0F34\u0F36\u0F38\u0FBE-\u0FC5\u0FC7-\u0FCC\u0FCE\u0FCF\u0FD5-\u0FD8\u109E\u109F\u1390-\u1399\u166D\u17DB\u1940\u19DE-\u19FF\u1B61-\u1B6A\u1B74-\u1B7C\u1FBD\u1FBF-\u1FC1\u1FCD-\u1FCF\u1FDD-\u1FDF\u1FED-\u1FEF\u1FFD\u1FFE\u2044\u2052\u207A-\u207C\u208A-\u208C\u20A0-\u20C0\u2100\u2101\u2103-\u2106\u2108\u2109\u2114\u2116-\u2118\u211E-\u2123\u2125\u2127\u2129\u212E\u213A\u213B\u2140-\u2144\u214A-\u214D\u214F\u218A\u218B\u2190-\u2307\u230C-\u2328\u232B-\u2426\u2440-\u244A\u249C-\u24E9\u2500-\u2767\u2794-\u27C4\u27C7-\u27E5\u27F0-\u2982\u2999-\u29D7\u29DC-\u29FB\u29FE-\u2B73\u2B76-\u2B95\u2B97-\u2BFF\u2CE5-\u2CEA\u2E50\u2E51\u2E80-\u2E99\u2E9B-\u2EF3\u2F00-\u2FD5\u2FF0-\u2FFF\u3004\u3012\u3013\u3020\u3036\u3037\u303E\u303F\u309B\u309C\u3190\u3191\u3196-\u319F\u31C0-\u31E3\u31EF\u3200-\u321E\u322A-\u3247\u3250\u3260-\u327F\u328A-\u32B0\u32C0-\u33FF\u4DC0-\u4DFF\uA490-\uA4C6\uA700-\uA716\uA720\uA721\uA789\uA78A\uA828-\uA82B\uA836-\uA839\uAA77-\uAA79\uAB5B\uAB6A\uAB6B\uFB29\uFBB2-\uFBC2\uFD40-\uFD4F\uFDCF\uFDFC-\uFDFF\uFE62\uFE64-\uFE66\uFE69\uFF04\uFF0B\uFF1C-\uFF1E\uFF3E\uFF40\uFF5C\uFF5E\uFFE0-\uFFE6\uFFE8-\uFFEE\uFFFC\uFFFD]|\uD800[\uDD37-\uDD3F\uDD79-\uDD89\uDD8C-\uDD8E\uDD90-\uDD9C\uDDA0\uDDD0-\uDDFC]|\uD802[\uDC77\uDC78\uDEC8]|\uD805\uDF3F|\uD807[\uDFD5-\uDFF1]|\uD81A[\uDF3C-\uDF3F\uDF45]|\uD82F\uDC9C|\uD833[\uDF50-\uDFC3]|\uD834[\uDC00-\uDCF5\uDD00-\uDD26\uDD29-\uDD64\uDD6A-\uDD6C\uDD83\uDD84\uDD8C-\uDDA9\uDDAE-\uDDEA\uDE00-\uDE41\uDE45\uDF00-\uDF56]|\uD835[\uDEC1\uDEDB\uDEFB\uDF15\uDF35\uDF4F\uDF6F\uDF89\uDFA9\uDFC3]|\uD836[\uDC00-\uDDFF\uDE37-\uDE3A\uDE6D-\uDE74\uDE76-\uDE83\uDE85\uDE86]|\uD838[\uDD4F\uDEFF]|\uD83B[\uDCAC\uDCB0\uDD2E\uDEF0\uDEF1]|\uD83C[\uDC00-\uDC2B\uDC30-\uDC93\uDCA0-\uDCAE\uDCB1-\uDCBF\uDCC1-\uDCCF\uDCD1-\uDCF5\uDD0D-\uDDAD\uDDE6-\uDE02\uDE10-\uDE3B\uDE40-\uDE48\uDE50\uDE51\uDE60-\uDE65\uDF00-\uDFFF]|\uD83D[\uDC00-\uDED7\uDEDC-\uDEEC\uDEF0-\uDEFC\uDF00-\uDF76\uDF7B-\uDFD9\uDFE0-\uDFEB\uDFF0]|\uD83E[\uDC00-\uDC0B\uDC10-\uDC47\uDC50-\uDC59\uDC60-\uDC87\uDC90-\uDCAD\uDCB0\uDCB1\uDD00-\uDE53\uDE60-\uDE6D\uDE70-\uDE7C\uDE80-\uDE88\uDE90-\uDEBD\uDEBF-\uDEC5\uDECE-\uDEDB\uDEE0-\uDEE8\uDEF0-\uDEF8\uDF00-\uDF92\uDF94-\uDFCA]/;

var Z = /[ \xA0\u1680\u2000-\u200A\u2028\u2029\u202F\u205F\u3000]/;

var ucmicro = /*#__PURE__*/Object.freeze({
  __proto__: null,
  Any: Any,
  Cc: Cc,
  Cf: regex$1,
  P: P,
  S: regex,
  Z: Z
});

// Generated using scripts/write-decode-map.ts
var htmlDecodeTree = new Uint16Array(
// prettier-ignore
"\u1d41<\xd5\u0131\u028a\u049d\u057b\u05d0\u0675\u06de\u07a2\u07d6\u080f\u0a4a\u0a91\u0da1\u0e6d\u0f09\u0f26\u10ca\u1228\u12e1\u1415\u149d\u14c3\u14df\u1525\0\0\0\0\0\0\u156b\u16cd\u198d\u1c12\u1ddd\u1f7e\u2060\u21b0\u228d\u23c0\u23fb\u2442\u2824\u2912\u2d08\u2e48\u2fce\u3016\u32ba\u3639\u37ac\u38fe\u3a28\u3a71\u3ae0\u3b2e\u0800EMabcfglmnoprstu\\bfms\x7f\x84\x8b\x90\x95\x98\xa6\xb3\xb9\xc8\xcflig\u803b\xc6\u40c6P\u803b&\u4026cute\u803b\xc1\u40c1reve;\u4102\u0100iyx}rc\u803b\xc2\u40c2;\u4410r;\uc000\ud835\udd04rave\u803b\xc0\u40c0pha;\u4391acr;\u4100d;\u6a53\u0100gp\x9d\xa1on;\u4104f;\uc000\ud835\udd38plyFunction;\u6061ing\u803b\xc5\u40c5\u0100cs\xbe\xc3r;\uc000\ud835\udc9cign;\u6254ilde\u803b\xc3\u40c3ml\u803b\xc4\u40c4\u0400aceforsu\xe5\xfb\xfe\u0117\u011c\u0122\u0127\u012a\u0100cr\xea\xf2kslash;\u6216\u0176\xf6\xf8;\u6ae7ed;\u6306y;\u4411\u0180crt\u0105\u010b\u0114ause;\u6235noullis;\u612ca;\u4392r;\uc000\ud835\udd05pf;\uc000\ud835\udd39eve;\u42d8c\xf2\u0113mpeq;\u624e\u0700HOacdefhilorsu\u014d\u0151\u0156\u0180\u019e\u01a2\u01b5\u01b7\u01ba\u01dc\u0215\u0273\u0278\u027ecy;\u4427PY\u803b\xa9\u40a9\u0180cpy\u015d\u0162\u017aute;\u4106\u0100;i\u0167\u0168\u62d2talDifferentialD;\u6145leys;\u612d\u0200aeio\u0189\u018e\u0194\u0198ron;\u410cdil\u803b\xc7\u40c7rc;\u4108nint;\u6230ot;\u410a\u0100dn\u01a7\u01adilla;\u40b8terDot;\u40b7\xf2\u017fi;\u43a7rcle\u0200DMPT\u01c7\u01cb\u01d1\u01d6ot;\u6299inus;\u6296lus;\u6295imes;\u6297o\u0100cs\u01e2\u01f8kwiseContourIntegral;\u6232eCurly\u0100DQ\u0203\u020foubleQuote;\u601duote;\u6019\u0200lnpu\u021e\u0228\u0247\u0255on\u0100;e\u0225\u0226\u6237;\u6a74\u0180git\u022f\u0236\u023aruent;\u6261nt;\u622fourIntegral;\u622e\u0100fr\u024c\u024e;\u6102oduct;\u6210nterClockwiseContourIntegral;\u6233oss;\u6a2fcr;\uc000\ud835\udc9ep\u0100;C\u0284\u0285\u62d3ap;\u624d\u0580DJSZacefios\u02a0\u02ac\u02b0\u02b4\u02b8\u02cb\u02d7\u02e1\u02e6\u0333\u048d\u0100;o\u0179\u02a5trahd;\u6911cy;\u4402cy;\u4405cy;\u440f\u0180grs\u02bf\u02c4\u02c7ger;\u6021r;\u61a1hv;\u6ae4\u0100ay\u02d0\u02d5ron;\u410e;\u4414l\u0100;t\u02dd\u02de\u6207a;\u4394r;\uc000\ud835\udd07\u0100af\u02eb\u0327\u0100cm\u02f0\u0322ritical\u0200ADGT\u0300\u0306\u0316\u031ccute;\u40b4o\u0174\u030b\u030d;\u42d9bleAcute;\u42ddrave;\u4060ilde;\u42dcond;\u62c4ferentialD;\u6146\u0470\u033d\0\0\0\u0342\u0354\0\u0405f;\uc000\ud835\udd3b\u0180;DE\u0348\u0349\u034d\u40a8ot;\u60dcqual;\u6250ble\u0300CDLRUV\u0363\u0372\u0382\u03cf\u03e2\u03f8ontourIntegra\xec\u0239o\u0274\u0379\0\0\u037b\xbb\u0349nArrow;\u61d3\u0100eo\u0387\u03a4ft\u0180ART\u0390\u0396\u03a1rrow;\u61d0ightArrow;\u61d4e\xe5\u02cang\u0100LR\u03ab\u03c4eft\u0100AR\u03b3\u03b9rrow;\u67f8ightArrow;\u67faightArrow;\u67f9ight\u0100AT\u03d8\u03derrow;\u61d2ee;\u62a8p\u0241\u03e9\0\0\u03efrrow;\u61d1ownArrow;\u61d5erticalBar;\u6225n\u0300ABLRTa\u0412\u042a\u0430\u045e\u047f\u037crrow\u0180;BU\u041d\u041e\u0422\u6193ar;\u6913pArrow;\u61f5reve;\u4311eft\u02d2\u043a\0\u0446\0\u0450ightVector;\u6950eeVector;\u695eector\u0100;B\u0459\u045a\u61bdar;\u6956ight\u01d4\u0467\0\u0471eeVector;\u695fector\u0100;B\u047a\u047b\u61c1ar;\u6957ee\u0100;A\u0486\u0487\u62a4rrow;\u61a7\u0100ct\u0492\u0497r;\uc000\ud835\udc9frok;\u4110\u0800NTacdfglmopqstux\u04bd\u04c0\u04c4\u04cb\u04de\u04e2\u04e7\u04ee\u04f5\u0521\u052f\u0536\u0552\u055d\u0560\u0565G;\u414aH\u803b\xd0\u40d0cute\u803b\xc9\u40c9\u0180aiy\u04d2\u04d7\u04dcron;\u411arc\u803b\xca\u40ca;\u442dot;\u4116r;\uc000\ud835\udd08rave\u803b\xc8\u40c8ement;\u6208\u0100ap\u04fa\u04fecr;\u4112ty\u0253\u0506\0\0\u0512mallSquare;\u65fberySmallSquare;\u65ab\u0100gp\u0526\u052aon;\u4118f;\uc000\ud835\udd3csilon;\u4395u\u0100ai\u053c\u0549l\u0100;T\u0542\u0543\u6a75ilde;\u6242librium;\u61cc\u0100ci\u0557\u055ar;\u6130m;\u6a73a;\u4397ml\u803b\xcb\u40cb\u0100ip\u056a\u056fsts;\u6203onentialE;\u6147\u0280cfios\u0585\u0588\u058d\u05b2\u05ccy;\u4424r;\uc000\ud835\udd09lled\u0253\u0597\0\0\u05a3mallSquare;\u65fcerySmallSquare;\u65aa\u0370\u05ba\0\u05bf\0\0\u05c4f;\uc000\ud835\udd3dAll;\u6200riertrf;\u6131c\xf2\u05cb\u0600JTabcdfgorst\u05e8\u05ec\u05ef\u05fa\u0600\u0612\u0616\u061b\u061d\u0623\u066c\u0672cy;\u4403\u803b>\u403emma\u0100;d\u05f7\u05f8\u4393;\u43dcreve;\u411e\u0180eiy\u0607\u060c\u0610dil;\u4122rc;\u411c;\u4413ot;\u4120r;\uc000\ud835\udd0a;\u62d9pf;\uc000\ud835\udd3eeater\u0300EFGLST\u0635\u0644\u064e\u0656\u065b\u0666qual\u0100;L\u063e\u063f\u6265ess;\u62dbullEqual;\u6267reater;\u6aa2ess;\u6277lantEqual;\u6a7eilde;\u6273cr;\uc000\ud835\udca2;\u626b\u0400Aacfiosu\u0685\u068b\u0696\u069b\u069e\u06aa\u06be\u06caRDcy;\u442a\u0100ct\u0690\u0694ek;\u42c7;\u405eirc;\u4124r;\u610clbertSpace;\u610b\u01f0\u06af\0\u06b2f;\u610dizontalLine;\u6500\u0100ct\u06c3\u06c5\xf2\u06a9rok;\u4126mp\u0144\u06d0\u06d8ownHum\xf0\u012fqual;\u624f\u0700EJOacdfgmnostu\u06fa\u06fe\u0703\u0707\u070e\u071a\u071e\u0721\u0728\u0744\u0778\u078b\u078f\u0795cy;\u4415lig;\u4132cy;\u4401cute\u803b\xcd\u40cd\u0100iy\u0713\u0718rc\u803b\xce\u40ce;\u4418ot;\u4130r;\u6111rave\u803b\xcc\u40cc\u0180;ap\u0720\u072f\u073f\u0100cg\u0734\u0737r;\u412ainaryI;\u6148lie\xf3\u03dd\u01f4\u0749\0\u0762\u0100;e\u074d\u074e\u622c\u0100gr\u0753\u0758ral;\u622bsection;\u62c2isible\u0100CT\u076c\u0772omma;\u6063imes;\u6062\u0180gpt\u077f\u0783\u0788on;\u412ef;\uc000\ud835\udd40a;\u4399cr;\u6110ilde;\u4128\u01eb\u079a\0\u079ecy;\u4406l\u803b\xcf\u40cf\u0280cfosu\u07ac\u07b7\u07bc\u07c2\u07d0\u0100iy\u07b1\u07b5rc;\u4134;\u4419r;\uc000\ud835\udd0dpf;\uc000\ud835\udd41\u01e3\u07c7\0\u07ccr;\uc000\ud835\udca5rcy;\u4408kcy;\u4404\u0380HJacfos\u07e4\u07e8\u07ec\u07f1\u07fd\u0802\u0808cy;\u4425cy;\u440cppa;\u439a\u0100ey\u07f6\u07fbdil;\u4136;\u441ar;\uc000\ud835\udd0epf;\uc000\ud835\udd42cr;\uc000\ud835\udca6\u0580JTaceflmost\u0825\u0829\u082c\u0850\u0863\u09b3\u09b8\u09c7\u09cd\u0a37\u0a47cy;\u4409\u803b<\u403c\u0280cmnpr\u0837\u083c\u0841\u0844\u084dute;\u4139bda;\u439bg;\u67ealacetrf;\u6112r;\u619e\u0180aey\u0857\u085c\u0861ron;\u413ddil;\u413b;\u441b\u0100fs\u0868\u0970t\u0500ACDFRTUVar\u087e\u08a9\u08b1\u08e0\u08e6\u08fc\u092f\u095b\u0390\u096a\u0100nr\u0883\u088fgleBracket;\u67e8row\u0180;BR\u0899\u089a\u089e\u6190ar;\u61e4ightArrow;\u61c6eiling;\u6308o\u01f5\u08b7\0\u08c3bleBracket;\u67e6n\u01d4\u08c8\0\u08d2eeVector;\u6961ector\u0100;B\u08db\u08dc\u61c3ar;\u6959loor;\u630aight\u0100AV\u08ef\u08f5rrow;\u6194ector;\u694e\u0100er\u0901\u0917e\u0180;AV\u0909\u090a\u0910\u62a3rrow;\u61a4ector;\u695aiangle\u0180;BE\u0924\u0925\u0929\u62b2ar;\u69cfqual;\u62b4p\u0180DTV\u0937\u0942\u094cownVector;\u6951eeVector;\u6960ector\u0100;B\u0956\u0957\u61bfar;\u6958ector\u0100;B\u0965\u0966\u61bcar;\u6952ight\xe1\u039cs\u0300EFGLST\u097e\u098b\u0995\u099d\u09a2\u09adqualGreater;\u62daullEqual;\u6266reater;\u6276ess;\u6aa1lantEqual;\u6a7dilde;\u6272r;\uc000\ud835\udd0f\u0100;e\u09bd\u09be\u62d8ftarrow;\u61daidot;\u413f\u0180npw\u09d4\u0a16\u0a1bg\u0200LRlr\u09de\u09f7\u0a02\u0a10eft\u0100AR\u09e6\u09ecrrow;\u67f5ightArrow;\u67f7ightArrow;\u67f6eft\u0100ar\u03b3\u0a0aight\xe1\u03bfight\xe1\u03caf;\uc000\ud835\udd43er\u0100LR\u0a22\u0a2ceftArrow;\u6199ightArrow;\u6198\u0180cht\u0a3e\u0a40\u0a42\xf2\u084c;\u61b0rok;\u4141;\u626a\u0400acefiosu\u0a5a\u0a5d\u0a60\u0a77\u0a7c\u0a85\u0a8b\u0a8ep;\u6905y;\u441c\u0100dl\u0a65\u0a6fiumSpace;\u605flintrf;\u6133r;\uc000\ud835\udd10nusPlus;\u6213pf;\uc000\ud835\udd44c\xf2\u0a76;\u439c\u0480Jacefostu\u0aa3\u0aa7\u0aad\u0ac0\u0b14\u0b19\u0d91\u0d97\u0d9ecy;\u440acute;\u4143\u0180aey\u0ab4\u0ab9\u0aberon;\u4147dil;\u4145;\u441d\u0180gsw\u0ac7\u0af0\u0b0eative\u0180MTV\u0ad3\u0adf\u0ae8ediumSpace;\u600bhi\u0100cn\u0ae6\u0ad8\xeb\u0ad9eryThi\xee\u0ad9ted\u0100GL\u0af8\u0b06reaterGreate\xf2\u0673essLes\xf3\u0a48Line;\u400ar;\uc000\ud835\udd11\u0200Bnpt\u0b22\u0b28\u0b37\u0b3areak;\u6060BreakingSpace;\u40a0f;\u6115\u0680;CDEGHLNPRSTV\u0b55\u0b56\u0b6a\u0b7c\u0ba1\u0beb\u0c04\u0c5e\u0c84\u0ca6\u0cd8\u0d61\u0d85\u6aec\u0100ou\u0b5b\u0b64ngruent;\u6262pCap;\u626doubleVerticalBar;\u6226\u0180lqx\u0b83\u0b8a\u0b9bement;\u6209ual\u0100;T\u0b92\u0b93\u6260ilde;\uc000\u2242\u0338ists;\u6204reater\u0380;EFGLST\u0bb6\u0bb7\u0bbd\u0bc9\u0bd3\u0bd8\u0be5\u626fqual;\u6271ullEqual;\uc000\u2267\u0338reater;\uc000\u226b\u0338ess;\u6279lantEqual;\uc000\u2a7e\u0338ilde;\u6275ump\u0144\u0bf2\u0bfdownHump;\uc000\u224e\u0338qual;\uc000\u224f\u0338e\u0100fs\u0c0a\u0c27tTriangle\u0180;BE\u0c1a\u0c1b\u0c21\u62eaar;\uc000\u29cf\u0338qual;\u62ecs\u0300;EGLST\u0c35\u0c36\u0c3c\u0c44\u0c4b\u0c58\u626equal;\u6270reater;\u6278ess;\uc000\u226a\u0338lantEqual;\uc000\u2a7d\u0338ilde;\u6274ested\u0100GL\u0c68\u0c79reaterGreater;\uc000\u2aa2\u0338essLess;\uc000\u2aa1\u0338recedes\u0180;ES\u0c92\u0c93\u0c9b\u6280qual;\uc000\u2aaf\u0338lantEqual;\u62e0\u0100ei\u0cab\u0cb9verseElement;\u620cghtTriangle\u0180;BE\u0ccb\u0ccc\u0cd2\u62ebar;\uc000\u29d0\u0338qual;\u62ed\u0100qu\u0cdd\u0d0cuareSu\u0100bp\u0ce8\u0cf9set\u0100;E\u0cf0\u0cf3\uc000\u228f\u0338qual;\u62e2erset\u0100;E\u0d03\u0d06\uc000\u2290\u0338qual;\u62e3\u0180bcp\u0d13\u0d24\u0d4eset\u0100;E\u0d1b\u0d1e\uc000\u2282\u20d2qual;\u6288ceeds\u0200;EST\u0d32\u0d33\u0d3b\u0d46\u6281qual;\uc000\u2ab0\u0338lantEqual;\u62e1ilde;\uc000\u227f\u0338erset\u0100;E\u0d58\u0d5b\uc000\u2283\u20d2qual;\u6289ilde\u0200;EFT\u0d6e\u0d6f\u0d75\u0d7f\u6241qual;\u6244ullEqual;\u6247ilde;\u6249erticalBar;\u6224cr;\uc000\ud835\udca9ilde\u803b\xd1\u40d1;\u439d\u0700Eacdfgmoprstuv\u0dbd\u0dc2\u0dc9\u0dd5\u0ddb\u0de0\u0de7\u0dfc\u0e02\u0e20\u0e22\u0e32\u0e3f\u0e44lig;\u4152cute\u803b\xd3\u40d3\u0100iy\u0dce\u0dd3rc\u803b\xd4\u40d4;\u441eblac;\u4150r;\uc000\ud835\udd12rave\u803b\xd2\u40d2\u0180aei\u0dee\u0df2\u0df6cr;\u414cga;\u43a9cron;\u439fpf;\uc000\ud835\udd46enCurly\u0100DQ\u0e0e\u0e1aoubleQuote;\u601cuote;\u6018;\u6a54\u0100cl\u0e27\u0e2cr;\uc000\ud835\udcaaash\u803b\xd8\u40d8i\u016c\u0e37\u0e3cde\u803b\xd5\u40d5es;\u6a37ml\u803b\xd6\u40d6er\u0100BP\u0e4b\u0e60\u0100ar\u0e50\u0e53r;\u603eac\u0100ek\u0e5a\u0e5c;\u63deet;\u63b4arenthesis;\u63dc\u0480acfhilors\u0e7f\u0e87\u0e8a\u0e8f\u0e92\u0e94\u0e9d\u0eb0\u0efcrtialD;\u6202y;\u441fr;\uc000\ud835\udd13i;\u43a6;\u43a0usMinus;\u40b1\u0100ip\u0ea2\u0eadncareplan\xe5\u069df;\u6119\u0200;eio\u0eb9\u0eba\u0ee0\u0ee4\u6abbcedes\u0200;EST\u0ec8\u0ec9\u0ecf\u0eda\u627aqual;\u6aaflantEqual;\u627cilde;\u627eme;\u6033\u0100dp\u0ee9\u0eeeuct;\u620fortion\u0100;a\u0225\u0ef9l;\u621d\u0100ci\u0f01\u0f06r;\uc000\ud835\udcab;\u43a8\u0200Ufos\u0f11\u0f16\u0f1b\u0f1fOT\u803b\"\u4022r;\uc000\ud835\udd14pf;\u611acr;\uc000\ud835\udcac\u0600BEacefhiorsu\u0f3e\u0f43\u0f47\u0f60\u0f73\u0fa7\u0faa\u0fad\u1096\u10a9\u10b4\u10bearr;\u6910G\u803b\xae\u40ae\u0180cnr\u0f4e\u0f53\u0f56ute;\u4154g;\u67ebr\u0100;t\u0f5c\u0f5d\u61a0l;\u6916\u0180aey\u0f67\u0f6c\u0f71ron;\u4158dil;\u4156;\u4420\u0100;v\u0f78\u0f79\u611cerse\u0100EU\u0f82\u0f99\u0100lq\u0f87\u0f8eement;\u620builibrium;\u61cbpEquilibrium;\u696fr\xbb\u0f79o;\u43a1ght\u0400ACDFTUVa\u0fc1\u0feb\u0ff3\u1022\u1028\u105b\u1087\u03d8\u0100nr\u0fc6\u0fd2gleBracket;\u67e9row\u0180;BL\u0fdc\u0fdd\u0fe1\u6192ar;\u61e5eftArrow;\u61c4eiling;\u6309o\u01f5\u0ff9\0\u1005bleBracket;\u67e7n\u01d4\u100a\0\u1014eeVector;\u695dector\u0100;B\u101d\u101e\u61c2ar;\u6955loor;\u630b\u0100er\u102d\u1043e\u0180;AV\u1035\u1036\u103c\u62a2rrow;\u61a6ector;\u695biangle\u0180;BE\u1050\u1051\u1055\u62b3ar;\u69d0qual;\u62b5p\u0180DTV\u1063\u106e\u1078ownVector;\u694feeVector;\u695cector\u0100;B\u1082\u1083\u61bear;\u6954ector\u0100;B\u1091\u1092\u61c0ar;\u6953\u0100pu\u109b\u109ef;\u611dndImplies;\u6970ightarrow;\u61db\u0100ch\u10b9\u10bcr;\u611b;\u61b1leDelayed;\u69f4\u0680HOacfhimoqstu\u10e4\u10f1\u10f7\u10fd\u1119\u111e\u1151\u1156\u1161\u1167\u11b5\u11bb\u11bf\u0100Cc\u10e9\u10eeHcy;\u4429y;\u4428FTcy;\u442ccute;\u415a\u0280;aeiy\u1108\u1109\u110e\u1113\u1117\u6abcron;\u4160dil;\u415erc;\u415c;\u4421r;\uc000\ud835\udd16ort\u0200DLRU\u112a\u1134\u113e\u1149ownArrow\xbb\u041eeftArrow\xbb\u089aightArrow\xbb\u0fddpArrow;\u6191gma;\u43a3allCircle;\u6218pf;\uc000\ud835\udd4a\u0272\u116d\0\0\u1170t;\u621aare\u0200;ISU\u117b\u117c\u1189\u11af\u65a1ntersection;\u6293u\u0100bp\u118f\u119eset\u0100;E\u1197\u1198\u628fqual;\u6291erset\u0100;E\u11a8\u11a9\u6290qual;\u6292nion;\u6294cr;\uc000\ud835\udcaear;\u62c6\u0200bcmp\u11c8\u11db\u1209\u120b\u0100;s\u11cd\u11ce\u62d0et\u0100;E\u11cd\u11d5qual;\u6286\u0100ch\u11e0\u1205eeds\u0200;EST\u11ed\u11ee\u11f4\u11ff\u627bqual;\u6ab0lantEqual;\u627dilde;\u627fTh\xe1\u0f8c;\u6211\u0180;es\u1212\u1213\u1223\u62d1rset\u0100;E\u121c\u121d\u6283qual;\u6287et\xbb\u1213\u0580HRSacfhiors\u123e\u1244\u1249\u1255\u125e\u1271\u1276\u129f\u12c2\u12c8\u12d1ORN\u803b\xde\u40deADE;\u6122\u0100Hc\u124e\u1252cy;\u440by;\u4426\u0100bu\u125a\u125c;\u4009;\u43a4\u0180aey\u1265\u126a\u126fron;\u4164dil;\u4162;\u4422r;\uc000\ud835\udd17\u0100ei\u127b\u1289\u01f2\u1280\0\u1287efore;\u6234a;\u4398\u0100cn\u128e\u1298kSpace;\uc000\u205f\u200aSpace;\u6009lde\u0200;EFT\u12ab\u12ac\u12b2\u12bc\u623cqual;\u6243ullEqual;\u6245ilde;\u6248pf;\uc000\ud835\udd4bipleDot;\u60db\u0100ct\u12d6\u12dbr;\uc000\ud835\udcafrok;\u4166\u0ae1\u12f7\u130e\u131a\u1326\0\u132c\u1331\0\0\0\0\0\u1338\u133d\u1377\u1385\0\u13ff\u1404\u140a\u1410\u0100cr\u12fb\u1301ute\u803b\xda\u40dar\u0100;o\u1307\u1308\u619fcir;\u6949r\u01e3\u1313\0\u1316y;\u440eve;\u416c\u0100iy\u131e\u1323rc\u803b\xdb\u40db;\u4423blac;\u4170r;\uc000\ud835\udd18rave\u803b\xd9\u40d9acr;\u416a\u0100di\u1341\u1369er\u0100BP\u1348\u135d\u0100ar\u134d\u1350r;\u405fac\u0100ek\u1357\u1359;\u63dfet;\u63b5arenthesis;\u63ddon\u0100;P\u1370\u1371\u62c3lus;\u628e\u0100gp\u137b\u137fon;\u4172f;\uc000\ud835\udd4c\u0400ADETadps\u1395\u13ae\u13b8\u13c4\u03e8\u13d2\u13d7\u13f3rrow\u0180;BD\u1150\u13a0\u13a4ar;\u6912ownArrow;\u61c5ownArrow;\u6195quilibrium;\u696eee\u0100;A\u13cb\u13cc\u62a5rrow;\u61a5own\xe1\u03f3er\u0100LR\u13de\u13e8eftArrow;\u6196ightArrow;\u6197i\u0100;l\u13f9\u13fa\u43d2on;\u43a5ing;\u416ecr;\uc000\ud835\udcb0ilde;\u4168ml\u803b\xdc\u40dc\u0480Dbcdefosv\u1427\u142c\u1430\u1433\u143e\u1485\u148a\u1490\u1496ash;\u62abar;\u6aeby;\u4412ash\u0100;l\u143b\u143c\u62a9;\u6ae6\u0100er\u1443\u1445;\u62c1\u0180bty\u144c\u1450\u147aar;\u6016\u0100;i\u144f\u1455cal\u0200BLST\u1461\u1465\u146a\u1474ar;\u6223ine;\u407ceparator;\u6758ilde;\u6240ThinSpace;\u600ar;\uc000\ud835\udd19pf;\uc000\ud835\udd4dcr;\uc000\ud835\udcb1dash;\u62aa\u0280cefos\u14a7\u14ac\u14b1\u14b6\u14bcirc;\u4174dge;\u62c0r;\uc000\ud835\udd1apf;\uc000\ud835\udd4ecr;\uc000\ud835\udcb2\u0200fios\u14cb\u14d0\u14d2\u14d8r;\uc000\ud835\udd1b;\u439epf;\uc000\ud835\udd4fcr;\uc000\ud835\udcb3\u0480AIUacfosu\u14f1\u14f5\u14f9\u14fd\u1504\u150f\u1514\u151a\u1520cy;\u442fcy;\u4407cy;\u442ecute\u803b\xdd\u40dd\u0100iy\u1509\u150drc;\u4176;\u442br;\uc000\ud835\udd1cpf;\uc000\ud835\udd50cr;\uc000\ud835\udcb4ml;\u4178\u0400Hacdefos\u1535\u1539\u153f\u154b\u154f\u155d\u1560\u1564cy;\u4416cute;\u4179\u0100ay\u1544\u1549ron;\u417d;\u4417ot;\u417b\u01f2\u1554\0\u155boWidt\xe8\u0ad9a;\u4396r;\u6128pf;\u6124cr;\uc000\ud835\udcb5\u0be1\u1583\u158a\u1590\0\u15b0\u15b6\u15bf\0\0\0\0\u15c6\u15db\u15eb\u165f\u166d\0\u1695\u169b\u16b2\u16b9\0\u16becute\u803b\xe1\u40e1reve;\u4103\u0300;Ediuy\u159c\u159d\u15a1\u15a3\u15a8\u15ad\u623e;\uc000\u223e\u0333;\u623frc\u803b\xe2\u40e2te\u80bb\xb4\u0306;\u4430lig\u803b\xe6\u40e6\u0100;r\xb2\u15ba;\uc000\ud835\udd1erave\u803b\xe0\u40e0\u0100ep\u15ca\u15d6\u0100fp\u15cf\u15d4sym;\u6135\xe8\u15d3ha;\u43b1\u0100ap\u15dfc\u0100cl\u15e4\u15e7r;\u4101g;\u6a3f\u0264\u15f0\0\0\u160a\u0280;adsv\u15fa\u15fb\u15ff\u1601\u1607\u6227nd;\u6a55;\u6a5clope;\u6a58;\u6a5a\u0380;elmrsz\u1618\u1619\u161b\u161e\u163f\u164f\u1659\u6220;\u69a4e\xbb\u1619sd\u0100;a\u1625\u1626\u6221\u0461\u1630\u1632\u1634\u1636\u1638\u163a\u163c\u163e;\u69a8;\u69a9;\u69aa;\u69ab;\u69ac;\u69ad;\u69ae;\u69aft\u0100;v\u1645\u1646\u621fb\u0100;d\u164c\u164d\u62be;\u699d\u0100pt\u1654\u1657h;\u6222\xbb\xb9arr;\u637c\u0100gp\u1663\u1667on;\u4105f;\uc000\ud835\udd52\u0380;Eaeiop\u12c1\u167b\u167d\u1682\u1684\u1687\u168a;\u6a70cir;\u6a6f;\u624ad;\u624bs;\u4027rox\u0100;e\u12c1\u1692\xf1\u1683ing\u803b\xe5\u40e5\u0180cty\u16a1\u16a6\u16a8r;\uc000\ud835\udcb6;\u402amp\u0100;e\u12c1\u16af\xf1\u0288ilde\u803b\xe3\u40e3ml\u803b\xe4\u40e4\u0100ci\u16c2\u16c8onin\xf4\u0272nt;\u6a11\u0800Nabcdefiklnoprsu\u16ed\u16f1\u1730\u173c\u1743\u1748\u1778\u177d\u17e0\u17e6\u1839\u1850\u170d\u193d\u1948\u1970ot;\u6aed\u0100cr\u16f6\u171ek\u0200ceps\u1700\u1705\u170d\u1713ong;\u624cpsilon;\u43f6rime;\u6035im\u0100;e\u171a\u171b\u623dq;\u62cd\u0176\u1722\u1726ee;\u62bded\u0100;g\u172c\u172d\u6305e\xbb\u172drk\u0100;t\u135c\u1737brk;\u63b6\u0100oy\u1701\u1741;\u4431quo;\u601e\u0280cmprt\u1753\u175b\u1761\u1764\u1768aus\u0100;e\u010a\u0109ptyv;\u69b0s\xe9\u170cno\xf5\u0113\u0180ahw\u176f\u1771\u1773;\u43b2;\u6136een;\u626cr;\uc000\ud835\udd1fg\u0380costuvw\u178d\u179d\u17b3\u17c1\u17d5\u17db\u17de\u0180aiu\u1794\u1796\u179a\xf0\u0760rc;\u65efp\xbb\u1371\u0180dpt\u17a4\u17a8\u17adot;\u6a00lus;\u6a01imes;\u6a02\u0271\u17b9\0\0\u17becup;\u6a06ar;\u6605riangle\u0100du\u17cd\u17d2own;\u65bdp;\u65b3plus;\u6a04e\xe5\u1444\xe5\u14adarow;\u690d\u0180ako\u17ed\u1826\u1835\u0100cn\u17f2\u1823k\u0180lst\u17fa\u05ab\u1802ozenge;\u69ebriangle\u0200;dlr\u1812\u1813\u1818\u181d\u65b4own;\u65beeft;\u65c2ight;\u65b8k;\u6423\u01b1\u182b\0\u1833\u01b2\u182f\0\u1831;\u6592;\u65914;\u6593ck;\u6588\u0100eo\u183e\u184d\u0100;q\u1843\u1846\uc000=\u20e5uiv;\uc000\u2261\u20e5t;\u6310\u0200ptwx\u1859\u185e\u1867\u186cf;\uc000\ud835\udd53\u0100;t\u13cb\u1863om\xbb\u13cctie;\u62c8\u0600DHUVbdhmptuv\u1885\u1896\u18aa\u18bb\u18d7\u18db\u18ec\u18ff\u1905\u190a\u1910\u1921\u0200LRlr\u188e\u1890\u1892\u1894;\u6557;\u6554;\u6556;\u6553\u0280;DUdu\u18a1\u18a2\u18a4\u18a6\u18a8\u6550;\u6566;\u6569;\u6564;\u6567\u0200LRlr\u18b3\u18b5\u18b7\u18b9;\u655d;\u655a;\u655c;\u6559\u0380;HLRhlr\u18ca\u18cb\u18cd\u18cf\u18d1\u18d3\u18d5\u6551;\u656c;\u6563;\u6560;\u656b;\u6562;\u655fox;\u69c9\u0200LRlr\u18e4\u18e6\u18e8\u18ea;\u6555;\u6552;\u6510;\u650c\u0280;DUdu\u06bd\u18f7\u18f9\u18fb\u18fd;\u6565;\u6568;\u652c;\u6534inus;\u629flus;\u629eimes;\u62a0\u0200LRlr\u1919\u191b\u191d\u191f;\u655b;\u6558;\u6518;\u6514\u0380;HLRhlr\u1930\u1931\u1933\u1935\u1937\u1939\u193b\u6502;\u656a;\u6561;\u655e;\u653c;\u6524;\u651c\u0100ev\u0123\u1942bar\u803b\xa6\u40a6\u0200ceio\u1951\u1956\u195a\u1960r;\uc000\ud835\udcb7mi;\u604fm\u0100;e\u171a\u171cl\u0180;bh\u1968\u1969\u196b\u405c;\u69c5sub;\u67c8\u016c\u1974\u197el\u0100;e\u1979\u197a\u6022t\xbb\u197ap\u0180;Ee\u012f\u1985\u1987;\u6aae\u0100;q\u06dc\u06db\u0ce1\u19a7\0\u19e8\u1a11\u1a15\u1a32\0\u1a37\u1a50\0\0\u1ab4\0\0\u1ac1\0\0\u1b21\u1b2e\u1b4d\u1b52\0\u1bfd\0\u1c0c\u0180cpr\u19ad\u19b2\u19ddute;\u4107\u0300;abcds\u19bf\u19c0\u19c4\u19ca\u19d5\u19d9\u6229nd;\u6a44rcup;\u6a49\u0100au\u19cf\u19d2p;\u6a4bp;\u6a47ot;\u6a40;\uc000\u2229\ufe00\u0100eo\u19e2\u19e5t;\u6041\xee\u0693\u0200aeiu\u19f0\u19fb\u1a01\u1a05\u01f0\u19f5\0\u19f8s;\u6a4don;\u410ddil\u803b\xe7\u40e7rc;\u4109ps\u0100;s\u1a0c\u1a0d\u6a4cm;\u6a50ot;\u410b\u0180dmn\u1a1b\u1a20\u1a26il\u80bb\xb8\u01adptyv;\u69b2t\u8100\xa2;e\u1a2d\u1a2e\u40a2r\xe4\u01b2r;\uc000\ud835\udd20\u0180cei\u1a3d\u1a40\u1a4dy;\u4447ck\u0100;m\u1a47\u1a48\u6713ark\xbb\u1a48;\u43c7r\u0380;Ecefms\u1a5f\u1a60\u1a62\u1a6b\u1aa4\u1aaa\u1aae\u65cb;\u69c3\u0180;el\u1a69\u1a6a\u1a6d\u42c6q;\u6257e\u0261\u1a74\0\0\u1a88rrow\u0100lr\u1a7c\u1a81eft;\u61baight;\u61bb\u0280RSacd\u1a92\u1a94\u1a96\u1a9a\u1a9f\xbb\u0f47;\u64c8st;\u629birc;\u629aash;\u629dnint;\u6a10id;\u6aefcir;\u69c2ubs\u0100;u\u1abb\u1abc\u6663it\xbb\u1abc\u02ec\u1ac7\u1ad4\u1afa\0\u1b0aon\u0100;e\u1acd\u1ace\u403a\u0100;q\xc7\xc6\u026d\u1ad9\0\0\u1ae2a\u0100;t\u1ade\u1adf\u402c;\u4040\u0180;fl\u1ae8\u1ae9\u1aeb\u6201\xee\u1160e\u0100mx\u1af1\u1af6ent\xbb\u1ae9e\xf3\u024d\u01e7\u1afe\0\u1b07\u0100;d\u12bb\u1b02ot;\u6a6dn\xf4\u0246\u0180fry\u1b10\u1b14\u1b17;\uc000\ud835\udd54o\xe4\u0254\u8100\xa9;s\u0155\u1b1dr;\u6117\u0100ao\u1b25\u1b29rr;\u61b5ss;\u6717\u0100cu\u1b32\u1b37r;\uc000\ud835\udcb8\u0100bp\u1b3c\u1b44\u0100;e\u1b41\u1b42\u6acf;\u6ad1\u0100;e\u1b49\u1b4a\u6ad0;\u6ad2dot;\u62ef\u0380delprvw\u1b60\u1b6c\u1b77\u1b82\u1bac\u1bd4\u1bf9arr\u0100lr\u1b68\u1b6a;\u6938;\u6935\u0270\u1b72\0\0\u1b75r;\u62dec;\u62dfarr\u0100;p\u1b7f\u1b80\u61b6;\u693d\u0300;bcdos\u1b8f\u1b90\u1b96\u1ba1\u1ba5\u1ba8\u622arcap;\u6a48\u0100au\u1b9b\u1b9ep;\u6a46p;\u6a4aot;\u628dr;\u6a45;\uc000\u222a\ufe00\u0200alrv\u1bb5\u1bbf\u1bde\u1be3rr\u0100;m\u1bbc\u1bbd\u61b7;\u693cy\u0180evw\u1bc7\u1bd4\u1bd8q\u0270\u1bce\0\0\u1bd2re\xe3\u1b73u\xe3\u1b75ee;\u62ceedge;\u62cfen\u803b\xa4\u40a4earrow\u0100lr\u1bee\u1bf3eft\xbb\u1b80ight\xbb\u1bbde\xe4\u1bdd\u0100ci\u1c01\u1c07onin\xf4\u01f7nt;\u6231lcty;\u632d\u0980AHabcdefhijlorstuwz\u1c38\u1c3b\u1c3f\u1c5d\u1c69\u1c75\u1c8a\u1c9e\u1cac\u1cb7\u1cfb\u1cff\u1d0d\u1d7b\u1d91\u1dab\u1dbb\u1dc6\u1dcdr\xf2\u0381ar;\u6965\u0200glrs\u1c48\u1c4d\u1c52\u1c54ger;\u6020eth;\u6138\xf2\u1133h\u0100;v\u1c5a\u1c5b\u6010\xbb\u090a\u016b\u1c61\u1c67arow;\u690fa\xe3\u0315\u0100ay\u1c6e\u1c73ron;\u410f;\u4434\u0180;ao\u0332\u1c7c\u1c84\u0100gr\u02bf\u1c81r;\u61catseq;\u6a77\u0180glm\u1c91\u1c94\u1c98\u803b\xb0\u40b0ta;\u43b4ptyv;\u69b1\u0100ir\u1ca3\u1ca8sht;\u697f;\uc000\ud835\udd21ar\u0100lr\u1cb3\u1cb5\xbb\u08dc\xbb\u101e\u0280aegsv\u1cc2\u0378\u1cd6\u1cdc\u1ce0m\u0180;os\u0326\u1cca\u1cd4nd\u0100;s\u0326\u1cd1uit;\u6666amma;\u43ddin;\u62f2\u0180;io\u1ce7\u1ce8\u1cf8\u40f7de\u8100\xf7;o\u1ce7\u1cf0ntimes;\u62c7n\xf8\u1cf7cy;\u4452c\u026f\u1d06\0\0\u1d0arn;\u631eop;\u630d\u0280lptuw\u1d18\u1d1d\u1d22\u1d49\u1d55lar;\u4024f;\uc000\ud835\udd55\u0280;emps\u030b\u1d2d\u1d37\u1d3d\u1d42q\u0100;d\u0352\u1d33ot;\u6251inus;\u6238lus;\u6214quare;\u62a1blebarwedg\xe5\xfan\u0180adh\u112e\u1d5d\u1d67ownarrow\xf3\u1c83arpoon\u0100lr\u1d72\u1d76ef\xf4\u1cb4igh\xf4\u1cb6\u0162\u1d7f\u1d85karo\xf7\u0f42\u026f\u1d8a\0\0\u1d8ern;\u631fop;\u630c\u0180cot\u1d98\u1da3\u1da6\u0100ry\u1d9d\u1da1;\uc000\ud835\udcb9;\u4455l;\u69f6rok;\u4111\u0100dr\u1db0\u1db4ot;\u62f1i\u0100;f\u1dba\u1816\u65bf\u0100ah\u1dc0\u1dc3r\xf2\u0429a\xf2\u0fa6angle;\u69a6\u0100ci\u1dd2\u1dd5y;\u445fgrarr;\u67ff\u0900Dacdefglmnopqrstux\u1e01\u1e09\u1e19\u1e38\u0578\u1e3c\u1e49\u1e61\u1e7e\u1ea5\u1eaf\u1ebd\u1ee1\u1f2a\u1f37\u1f44\u1f4e\u1f5a\u0100Do\u1e06\u1d34o\xf4\u1c89\u0100cs\u1e0e\u1e14ute\u803b\xe9\u40e9ter;\u6a6e\u0200aioy\u1e22\u1e27\u1e31\u1e36ron;\u411br\u0100;c\u1e2d\u1e2e\u6256\u803b\xea\u40ealon;\u6255;\u444dot;\u4117\u0100Dr\u1e41\u1e45ot;\u6252;\uc000\ud835\udd22\u0180;rs\u1e50\u1e51\u1e57\u6a9aave\u803b\xe8\u40e8\u0100;d\u1e5c\u1e5d\u6a96ot;\u6a98\u0200;ils\u1e6a\u1e6b\u1e72\u1e74\u6a99nters;\u63e7;\u6113\u0100;d\u1e79\u1e7a\u6a95ot;\u6a97\u0180aps\u1e85\u1e89\u1e97cr;\u4113ty\u0180;sv\u1e92\u1e93\u1e95\u6205et\xbb\u1e93p\u01001;\u1e9d\u1ea4\u0133\u1ea1\u1ea3;\u6004;\u6005\u6003\u0100gs\u1eaa\u1eac;\u414bp;\u6002\u0100gp\u1eb4\u1eb8on;\u4119f;\uc000\ud835\udd56\u0180als\u1ec4\u1ece\u1ed2r\u0100;s\u1eca\u1ecb\u62d5l;\u69e3us;\u6a71i\u0180;lv\u1eda\u1edb\u1edf\u43b5on\xbb\u1edb;\u43f5\u0200csuv\u1eea\u1ef3\u1f0b\u1f23\u0100io\u1eef\u1e31rc\xbb\u1e2e\u0269\u1ef9\0\0\u1efb\xed\u0548ant\u0100gl\u1f02\u1f06tr\xbb\u1e5dess\xbb\u1e7a\u0180aei\u1f12\u1f16\u1f1als;\u403dst;\u625fv\u0100;D\u0235\u1f20D;\u6a78parsl;\u69e5\u0100Da\u1f2f\u1f33ot;\u6253rr;\u6971\u0180cdi\u1f3e\u1f41\u1ef8r;\u612fo\xf4\u0352\u0100ah\u1f49\u1f4b;\u43b7\u803b\xf0\u40f0\u0100mr\u1f53\u1f57l\u803b\xeb\u40ebo;\u60ac\u0180cip\u1f61\u1f64\u1f67l;\u4021s\xf4\u056e\u0100eo\u1f6c\u1f74ctatio\xee\u0559nential\xe5\u0579\u09e1\u1f92\0\u1f9e\0\u1fa1\u1fa7\0\0\u1fc6\u1fcc\0\u1fd3\0\u1fe6\u1fea\u2000\0\u2008\u205allingdotse\xf1\u1e44y;\u4444male;\u6640\u0180ilr\u1fad\u1fb3\u1fc1lig;\u8000\ufb03\u0269\u1fb9\0\0\u1fbdg;\u8000\ufb00ig;\u8000\ufb04;\uc000\ud835\udd23lig;\u8000\ufb01lig;\uc000fj\u0180alt\u1fd9\u1fdc\u1fe1t;\u666dig;\u8000\ufb02ns;\u65b1of;\u4192\u01f0\u1fee\0\u1ff3f;\uc000\ud835\udd57\u0100ak\u05bf\u1ff7\u0100;v\u1ffc\u1ffd\u62d4;\u6ad9artint;\u6a0d\u0100ao\u200c\u2055\u0100cs\u2011\u2052\u03b1\u201a\u2030\u2038\u2045\u2048\0\u2050\u03b2\u2022\u2025\u2027\u202a\u202c\0\u202e\u803b\xbd\u40bd;\u6153\u803b\xbc\u40bc;\u6155;\u6159;\u615b\u01b3\u2034\0\u2036;\u6154;\u6156\u02b4\u203e\u2041\0\0\u2043\u803b\xbe\u40be;\u6157;\u615c5;\u6158\u01b6\u204c\0\u204e;\u615a;\u615d8;\u615el;\u6044wn;\u6322cr;\uc000\ud835\udcbb\u0880Eabcdefgijlnorstv\u2082\u2089\u209f\u20a5\u20b0\u20b4\u20f0\u20f5\u20fa\u20ff\u2103\u2112\u2138\u0317\u213e\u2152\u219e\u0100;l\u064d\u2087;\u6a8c\u0180cmp\u2090\u2095\u209dute;\u41f5ma\u0100;d\u209c\u1cda\u43b3;\u6a86reve;\u411f\u0100iy\u20aa\u20aerc;\u411d;\u4433ot;\u4121\u0200;lqs\u063e\u0642\u20bd\u20c9\u0180;qs\u063e\u064c\u20c4lan\xf4\u0665\u0200;cdl\u0665\u20d2\u20d5\u20e5c;\u6aa9ot\u0100;o\u20dc\u20dd\u6a80\u0100;l\u20e2\u20e3\u6a82;\u6a84\u0100;e\u20ea\u20ed\uc000\u22db\ufe00s;\u6a94r;\uc000\ud835\udd24\u0100;g\u0673\u061bmel;\u6137cy;\u4453\u0200;Eaj\u065a\u210c\u210e\u2110;\u6a92;\u6aa5;\u6aa4\u0200Eaes\u211b\u211d\u2129\u2134;\u6269p\u0100;p\u2123\u2124\u6a8arox\xbb\u2124\u0100;q\u212e\u212f\u6a88\u0100;q\u212e\u211bim;\u62e7pf;\uc000\ud835\udd58\u0100ci\u2143\u2146r;\u610am\u0180;el\u066b\u214e\u2150;\u6a8e;\u6a90\u8300>;cdlqr\u05ee\u2160\u216a\u216e\u2173\u2179\u0100ci\u2165\u2167;\u6aa7r;\u6a7aot;\u62d7Par;\u6995uest;\u6a7c\u0280adels\u2184\u216a\u2190\u0656\u219b\u01f0\u2189\0\u218epro\xf8\u209er;\u6978q\u0100lq\u063f\u2196les\xf3\u2088i\xed\u066b\u0100en\u21a3\u21adrtneqq;\uc000\u2269\ufe00\xc5\u21aa\u0500Aabcefkosy\u21c4\u21c7\u21f1\u21f5\u21fa\u2218\u221d\u222f\u2268\u227dr\xf2\u03a0\u0200ilmr\u21d0\u21d4\u21d7\u21dbrs\xf0\u1484f\xbb\u2024il\xf4\u06a9\u0100dr\u21e0\u21e4cy;\u444a\u0180;cw\u08f4\u21eb\u21efir;\u6948;\u61adar;\u610firc;\u4125\u0180alr\u2201\u220e\u2213rts\u0100;u\u2209\u220a\u6665it\xbb\u220alip;\u6026con;\u62b9r;\uc000\ud835\udd25s\u0100ew\u2223\u2229arow;\u6925arow;\u6926\u0280amopr\u223a\u223e\u2243\u225e\u2263rr;\u61fftht;\u623bk\u0100lr\u2249\u2253eftarrow;\u61a9ightarrow;\u61aaf;\uc000\ud835\udd59bar;\u6015\u0180clt\u226f\u2274\u2278r;\uc000\ud835\udcbdas\xe8\u21f4rok;\u4127\u0100bp\u2282\u2287ull;\u6043hen\xbb\u1c5b\u0ae1\u22a3\0\u22aa\0\u22b8\u22c5\u22ce\0\u22d5\u22f3\0\0\u22f8\u2322\u2367\u2362\u237f\0\u2386\u23aa\u23b4cute\u803b\xed\u40ed\u0180;iy\u0771\u22b0\u22b5rc\u803b\xee\u40ee;\u4438\u0100cx\u22bc\u22bfy;\u4435cl\u803b\xa1\u40a1\u0100fr\u039f\u22c9;\uc000\ud835\udd26rave\u803b\xec\u40ec\u0200;ino\u073e\u22dd\u22e9\u22ee\u0100in\u22e2\u22e6nt;\u6a0ct;\u622dfin;\u69dcta;\u6129lig;\u4133\u0180aop\u22fe\u231a\u231d\u0180cgt\u2305\u2308\u2317r;\u412b\u0180elp\u071f\u230f\u2313in\xe5\u078ear\xf4\u0720h;\u4131f;\u62b7ed;\u41b5\u0280;cfot\u04f4\u232c\u2331\u233d\u2341are;\u6105in\u0100;t\u2338\u2339\u621eie;\u69dddo\xf4\u2319\u0280;celp\u0757\u234c\u2350\u235b\u2361al;\u62ba\u0100gr\u2355\u2359er\xf3\u1563\xe3\u234darhk;\u6a17rod;\u6a3c\u0200cgpt\u236f\u2372\u2376\u237by;\u4451on;\u412ff;\uc000\ud835\udd5aa;\u43b9uest\u803b\xbf\u40bf\u0100ci\u238a\u238fr;\uc000\ud835\udcben\u0280;Edsv\u04f4\u239b\u239d\u23a1\u04f3;\u62f9ot;\u62f5\u0100;v\u23a6\u23a7\u62f4;\u62f3\u0100;i\u0777\u23aelde;\u4129\u01eb\u23b8\0\u23bccy;\u4456l\u803b\xef\u40ef\u0300cfmosu\u23cc\u23d7\u23dc\u23e1\u23e7\u23f5\u0100iy\u23d1\u23d5rc;\u4135;\u4439r;\uc000\ud835\udd27ath;\u4237pf;\uc000\ud835\udd5b\u01e3\u23ec\0\u23f1r;\uc000\ud835\udcbfrcy;\u4458kcy;\u4454\u0400acfghjos\u240b\u2416\u2422\u2427\u242d\u2431\u2435\u243bppa\u0100;v\u2413\u2414\u43ba;\u43f0\u0100ey\u241b\u2420dil;\u4137;\u443ar;\uc000\ud835\udd28reen;\u4138cy;\u4445cy;\u445cpf;\uc000\ud835\udd5ccr;\uc000\ud835\udcc0\u0b80ABEHabcdefghjlmnoprstuv\u2470\u2481\u2486\u248d\u2491\u250e\u253d\u255a\u2580\u264e\u265e\u2665\u2679\u267d\u269a\u26b2\u26d8\u275d\u2768\u278b\u27c0\u2801\u2812\u0180art\u2477\u247a\u247cr\xf2\u09c6\xf2\u0395ail;\u691barr;\u690e\u0100;g\u0994\u248b;\u6a8bar;\u6962\u0963\u24a5\0\u24aa\0\u24b1\0\0\0\0\0\u24b5\u24ba\0\u24c6\u24c8\u24cd\0\u24f9ute;\u413amptyv;\u69b4ra\xee\u084cbda;\u43bbg\u0180;dl\u088e\u24c1\u24c3;\u6991\xe5\u088e;\u6a85uo\u803b\xab\u40abr\u0400;bfhlpst\u0899\u24de\u24e6\u24e9\u24eb\u24ee\u24f1\u24f5\u0100;f\u089d\u24e3s;\u691fs;\u691d\xeb\u2252p;\u61abl;\u6939im;\u6973l;\u61a2\u0180;ae\u24ff\u2500\u2504\u6aabil;\u6919\u0100;s\u2509\u250a\u6aad;\uc000\u2aad\ufe00\u0180abr\u2515\u2519\u251drr;\u690crk;\u6772\u0100ak\u2522\u252cc\u0100ek\u2528\u252a;\u407b;\u405b\u0100es\u2531\u2533;\u698bl\u0100du\u2539\u253b;\u698f;\u698d\u0200aeuy\u2546\u254b\u2556\u2558ron;\u413e\u0100di\u2550\u2554il;\u413c\xec\u08b0\xe2\u2529;\u443b\u0200cqrs\u2563\u2566\u256d\u257da;\u6936uo\u0100;r\u0e19\u1746\u0100du\u2572\u2577har;\u6967shar;\u694bh;\u61b2\u0280;fgqs\u258b\u258c\u0989\u25f3\u25ff\u6264t\u0280ahlrt\u2598\u25a4\u25b7\u25c2\u25e8rrow\u0100;t\u0899\u25a1a\xe9\u24f6arpoon\u0100du\u25af\u25b4own\xbb\u045ap\xbb\u0966eftarrows;\u61c7ight\u0180ahs\u25cd\u25d6\u25derrow\u0100;s\u08f4\u08a7arpoon\xf3\u0f98quigarro\xf7\u21f0hreetimes;\u62cb\u0180;qs\u258b\u0993\u25falan\xf4\u09ac\u0280;cdgs\u09ac\u260a\u260d\u261d\u2628c;\u6aa8ot\u0100;o\u2614\u2615\u6a7f\u0100;r\u261a\u261b\u6a81;\u6a83\u0100;e\u2622\u2625\uc000\u22da\ufe00s;\u6a93\u0280adegs\u2633\u2639\u263d\u2649\u264bppro\xf8\u24c6ot;\u62d6q\u0100gq\u2643\u2645\xf4\u0989gt\xf2\u248c\xf4\u099bi\xed\u09b2\u0180ilr\u2655\u08e1\u265asht;\u697c;\uc000\ud835\udd29\u0100;E\u099c\u2663;\u6a91\u0161\u2669\u2676r\u0100du\u25b2\u266e\u0100;l\u0965\u2673;\u696alk;\u6584cy;\u4459\u0280;acht\u0a48\u2688\u268b\u2691\u2696r\xf2\u25c1orne\xf2\u1d08ard;\u696bri;\u65fa\u0100io\u269f\u26a4dot;\u4140ust\u0100;a\u26ac\u26ad\u63b0che\xbb\u26ad\u0200Eaes\u26bb\u26bd\u26c9\u26d4;\u6268p\u0100;p\u26c3\u26c4\u6a89rox\xbb\u26c4\u0100;q\u26ce\u26cf\u6a87\u0100;q\u26ce\u26bbim;\u62e6\u0400abnoptwz\u26e9\u26f4\u26f7\u271a\u272f\u2741\u2747\u2750\u0100nr\u26ee\u26f1g;\u67ecr;\u61fdr\xeb\u08c1g\u0180lmr\u26ff\u270d\u2714eft\u0100ar\u09e6\u2707ight\xe1\u09f2apsto;\u67fcight\xe1\u09fdparrow\u0100lr\u2725\u2729ef\xf4\u24edight;\u61ac\u0180afl\u2736\u2739\u273dr;\u6985;\uc000\ud835\udd5dus;\u6a2dimes;\u6a34\u0161\u274b\u274fst;\u6217\xe1\u134e\u0180;ef\u2757\u2758\u1800\u65cange\xbb\u2758ar\u0100;l\u2764\u2765\u4028t;\u6993\u0280achmt\u2773\u2776\u277c\u2785\u2787r\xf2\u08a8orne\xf2\u1d8car\u0100;d\u0f98\u2783;\u696d;\u600eri;\u62bf\u0300achiqt\u2798\u279d\u0a40\u27a2\u27ae\u27bbquo;\u6039r;\uc000\ud835\udcc1m\u0180;eg\u09b2\u27aa\u27ac;\u6a8d;\u6a8f\u0100bu\u252a\u27b3o\u0100;r\u0e1f\u27b9;\u601arok;\u4142\u8400<;cdhilqr\u082b\u27d2\u2639\u27dc\u27e0\u27e5\u27ea\u27f0\u0100ci\u27d7\u27d9;\u6aa6r;\u6a79re\xe5\u25f2mes;\u62c9arr;\u6976uest;\u6a7b\u0100Pi\u27f5\u27f9ar;\u6996\u0180;ef\u2800\u092d\u181b\u65c3r\u0100du\u2807\u280dshar;\u694ahar;\u6966\u0100en\u2817\u2821rtneqq;\uc000\u2268\ufe00\xc5\u281e\u0700Dacdefhilnopsu\u2840\u2845\u2882\u288e\u2893\u28a0\u28a5\u28a8\u28da\u28e2\u28e4\u0a83\u28f3\u2902Dot;\u623a\u0200clpr\u284e\u2852\u2863\u287dr\u803b\xaf\u40af\u0100et\u2857\u2859;\u6642\u0100;e\u285e\u285f\u6720se\xbb\u285f\u0100;s\u103b\u2868to\u0200;dlu\u103b\u2873\u2877\u287bow\xee\u048cef\xf4\u090f\xf0\u13d1ker;\u65ae\u0100oy\u2887\u288cmma;\u6a29;\u443cash;\u6014asuredangle\xbb\u1626r;\uc000\ud835\udd2ao;\u6127\u0180cdn\u28af\u28b4\u28c9ro\u803b\xb5\u40b5\u0200;acd\u1464\u28bd\u28c0\u28c4s\xf4\u16a7ir;\u6af0ot\u80bb\xb7\u01b5us\u0180;bd\u28d2\u1903\u28d3\u6212\u0100;u\u1d3c\u28d8;\u6a2a\u0163\u28de\u28e1p;\u6adb\xf2\u2212\xf0\u0a81\u0100dp\u28e9\u28eeels;\u62a7f;\uc000\ud835\udd5e\u0100ct\u28f8\u28fdr;\uc000\ud835\udcc2pos\xbb\u159d\u0180;lm\u2909\u290a\u290d\u43bctimap;\u62b8\u0c00GLRVabcdefghijlmoprstuvw\u2942\u2953\u297e\u2989\u2998\u29da\u29e9\u2a15\u2a1a\u2a58\u2a5d\u2a83\u2a95\u2aa4\u2aa8\u2b04\u2b07\u2b44\u2b7f\u2bae\u2c34\u2c67\u2c7c\u2ce9\u0100gt\u2947\u294b;\uc000\u22d9\u0338\u0100;v\u2950\u0bcf\uc000\u226b\u20d2\u0180elt\u295a\u2972\u2976ft\u0100ar\u2961\u2967rrow;\u61cdightarrow;\u61ce;\uc000\u22d8\u0338\u0100;v\u297b\u0c47\uc000\u226a\u20d2ightarrow;\u61cf\u0100Dd\u298e\u2993ash;\u62afash;\u62ae\u0280bcnpt\u29a3\u29a7\u29ac\u29b1\u29ccla\xbb\u02deute;\u4144g;\uc000\u2220\u20d2\u0280;Eiop\u0d84\u29bc\u29c0\u29c5\u29c8;\uc000\u2a70\u0338d;\uc000\u224b\u0338s;\u4149ro\xf8\u0d84ur\u0100;a\u29d3\u29d4\u666el\u0100;s\u29d3\u0b38\u01f3\u29df\0\u29e3p\u80bb\xa0\u0b37mp\u0100;e\u0bf9\u0c00\u0280aeouy\u29f4\u29fe\u2a03\u2a10\u2a13\u01f0\u29f9\0\u29fb;\u6a43on;\u4148dil;\u4146ng\u0100;d\u0d7e\u2a0aot;\uc000\u2a6d\u0338p;\u6a42;\u443dash;\u6013\u0380;Aadqsx\u0b92\u2a29\u2a2d\u2a3b\u2a41\u2a45\u2a50rr;\u61d7r\u0100hr\u2a33\u2a36k;\u6924\u0100;o\u13f2\u13f0ot;\uc000\u2250\u0338ui\xf6\u0b63\u0100ei\u2a4a\u2a4ear;\u6928\xed\u0b98ist\u0100;s\u0ba0\u0b9fr;\uc000\ud835\udd2b\u0200Eest\u0bc5\u2a66\u2a79\u2a7c\u0180;qs\u0bbc\u2a6d\u0be1\u0180;qs\u0bbc\u0bc5\u2a74lan\xf4\u0be2i\xed\u0bea\u0100;r\u0bb6\u2a81\xbb\u0bb7\u0180Aap\u2a8a\u2a8d\u2a91r\xf2\u2971rr;\u61aear;\u6af2\u0180;sv\u0f8d\u2a9c\u0f8c\u0100;d\u2aa1\u2aa2\u62fc;\u62facy;\u445a\u0380AEadest\u2ab7\u2aba\u2abe\u2ac2\u2ac5\u2af6\u2af9r\xf2\u2966;\uc000\u2266\u0338rr;\u619ar;\u6025\u0200;fqs\u0c3b\u2ace\u2ae3\u2aeft\u0100ar\u2ad4\u2ad9rro\xf7\u2ac1ightarro\xf7\u2a90\u0180;qs\u0c3b\u2aba\u2aealan\xf4\u0c55\u0100;s\u0c55\u2af4\xbb\u0c36i\xed\u0c5d\u0100;r\u0c35\u2afei\u0100;e\u0c1a\u0c25i\xe4\u0d90\u0100pt\u2b0c\u2b11f;\uc000\ud835\udd5f\u8180\xac;in\u2b19\u2b1a\u2b36\u40acn\u0200;Edv\u0b89\u2b24\u2b28\u2b2e;\uc000\u22f9\u0338ot;\uc000\u22f5\u0338\u01e1\u0b89\u2b33\u2b35;\u62f7;\u62f6i\u0100;v\u0cb8\u2b3c\u01e1\u0cb8\u2b41\u2b43;\u62fe;\u62fd\u0180aor\u2b4b\u2b63\u2b69r\u0200;ast\u0b7b\u2b55\u2b5a\u2b5flle\xec\u0b7bl;\uc000\u2afd\u20e5;\uc000\u2202\u0338lint;\u6a14\u0180;ce\u0c92\u2b70\u2b73u\xe5\u0ca5\u0100;c\u0c98\u2b78\u0100;e\u0c92\u2b7d\xf1\u0c98\u0200Aait\u2b88\u2b8b\u2b9d\u2ba7r\xf2\u2988rr\u0180;cw\u2b94\u2b95\u2b99\u619b;\uc000\u2933\u0338;\uc000\u219d\u0338ghtarrow\xbb\u2b95ri\u0100;e\u0ccb\u0cd6\u0380chimpqu\u2bbd\u2bcd\u2bd9\u2b04\u0b78\u2be4\u2bef\u0200;cer\u0d32\u2bc6\u0d37\u2bc9u\xe5\u0d45;\uc000\ud835\udcc3ort\u026d\u2b05\0\0\u2bd6ar\xe1\u2b56m\u0100;e\u0d6e\u2bdf\u0100;q\u0d74\u0d73su\u0100bp\u2beb\u2bed\xe5\u0cf8\xe5\u0d0b\u0180bcp\u2bf6\u2c11\u2c19\u0200;Ees\u2bff\u2c00\u0d22\u2c04\u6284;\uc000\u2ac5\u0338et\u0100;e\u0d1b\u2c0bq\u0100;q\u0d23\u2c00c\u0100;e\u0d32\u2c17\xf1\u0d38\u0200;Ees\u2c22\u2c23\u0d5f\u2c27\u6285;\uc000\u2ac6\u0338et\u0100;e\u0d58\u2c2eq\u0100;q\u0d60\u2c23\u0200gilr\u2c3d\u2c3f\u2c45\u2c47\xec\u0bd7lde\u803b\xf1\u40f1\xe7\u0c43iangle\u0100lr\u2c52\u2c5ceft\u0100;e\u0c1a\u2c5a\xf1\u0c26ight\u0100;e\u0ccb\u2c65\xf1\u0cd7\u0100;m\u2c6c\u2c6d\u43bd\u0180;es\u2c74\u2c75\u2c79\u4023ro;\u6116p;\u6007\u0480DHadgilrs\u2c8f\u2c94\u2c99\u2c9e\u2ca3\u2cb0\u2cb6\u2cd3\u2ce3ash;\u62adarr;\u6904p;\uc000\u224d\u20d2ash;\u62ac\u0100et\u2ca8\u2cac;\uc000\u2265\u20d2;\uc000>\u20d2nfin;\u69de\u0180Aet\u2cbd\u2cc1\u2cc5rr;\u6902;\uc000\u2264\u20d2\u0100;r\u2cca\u2ccd\uc000<\u20d2ie;\uc000\u22b4\u20d2\u0100At\u2cd8\u2cdcrr;\u6903rie;\uc000\u22b5\u20d2im;\uc000\u223c\u20d2\u0180Aan\u2cf0\u2cf4\u2d02rr;\u61d6r\u0100hr\u2cfa\u2cfdk;\u6923\u0100;o\u13e7\u13e5ear;\u6927\u1253\u1a95\0\0\0\0\0\0\0\0\0\0\0\0\0\u2d2d\0\u2d38\u2d48\u2d60\u2d65\u2d72\u2d84\u1b07\0\0\u2d8d\u2dab\0\u2dc8\u2dce\0\u2ddc\u2e19\u2e2b\u2e3e\u2e43\u0100cs\u2d31\u1a97ute\u803b\xf3\u40f3\u0100iy\u2d3c\u2d45r\u0100;c\u1a9e\u2d42\u803b\xf4\u40f4;\u443e\u0280abios\u1aa0\u2d52\u2d57\u01c8\u2d5alac;\u4151v;\u6a38old;\u69bclig;\u4153\u0100cr\u2d69\u2d6dir;\u69bf;\uc000\ud835\udd2c\u036f\u2d79\0\0\u2d7c\0\u2d82n;\u42dbave\u803b\xf2\u40f2;\u69c1\u0100bm\u2d88\u0df4ar;\u69b5\u0200acit\u2d95\u2d98\u2da5\u2da8r\xf2\u1a80\u0100ir\u2d9d\u2da0r;\u69beoss;\u69bbn\xe5\u0e52;\u69c0\u0180aei\u2db1\u2db5\u2db9cr;\u414dga;\u43c9\u0180cdn\u2dc0\u2dc5\u01cdron;\u43bf;\u69b6pf;\uc000\ud835\udd60\u0180ael\u2dd4\u2dd7\u01d2r;\u69b7rp;\u69b9\u0380;adiosv\u2dea\u2deb\u2dee\u2e08\u2e0d\u2e10\u2e16\u6228r\xf2\u1a86\u0200;efm\u2df7\u2df8\u2e02\u2e05\u6a5dr\u0100;o\u2dfe\u2dff\u6134f\xbb\u2dff\u803b\xaa\u40aa\u803b\xba\u40bagof;\u62b6r;\u6a56lope;\u6a57;\u6a5b\u0180clo\u2e1f\u2e21\u2e27\xf2\u2e01ash\u803b\xf8\u40f8l;\u6298i\u016c\u2e2f\u2e34de\u803b\xf5\u40f5es\u0100;a\u01db\u2e3as;\u6a36ml\u803b\xf6\u40f6bar;\u633d\u0ae1\u2e5e\0\u2e7d\0\u2e80\u2e9d\0\u2ea2\u2eb9\0\0\u2ecb\u0e9c\0\u2f13\0\0\u2f2b\u2fbc\0\u2fc8r\u0200;ast\u0403\u2e67\u2e72\u0e85\u8100\xb6;l\u2e6d\u2e6e\u40b6le\xec\u0403\u0269\u2e78\0\0\u2e7bm;\u6af3;\u6afdy;\u443fr\u0280cimpt\u2e8b\u2e8f\u2e93\u1865\u2e97nt;\u4025od;\u402eil;\u6030enk;\u6031r;\uc000\ud835\udd2d\u0180imo\u2ea8\u2eb0\u2eb4\u0100;v\u2ead\u2eae\u43c6;\u43d5ma\xf4\u0a76ne;\u660e\u0180;tv\u2ebf\u2ec0\u2ec8\u43c0chfork\xbb\u1ffd;\u43d6\u0100au\u2ecf\u2edfn\u0100ck\u2ed5\u2eddk\u0100;h\u21f4\u2edb;\u610e\xf6\u21f4s\u0480;abcdemst\u2ef3\u2ef4\u1908\u2ef9\u2efd\u2f04\u2f06\u2f0a\u2f0e\u402bcir;\u6a23ir;\u6a22\u0100ou\u1d40\u2f02;\u6a25;\u6a72n\u80bb\xb1\u0e9dim;\u6a26wo;\u6a27\u0180ipu\u2f19\u2f20\u2f25ntint;\u6a15f;\uc000\ud835\udd61nd\u803b\xa3\u40a3\u0500;Eaceinosu\u0ec8\u2f3f\u2f41\u2f44\u2f47\u2f81\u2f89\u2f92\u2f7e\u2fb6;\u6ab3p;\u6ab7u\xe5\u0ed9\u0100;c\u0ece\u2f4c\u0300;acens\u0ec8\u2f59\u2f5f\u2f66\u2f68\u2f7eppro\xf8\u2f43urlye\xf1\u0ed9\xf1\u0ece\u0180aes\u2f6f\u2f76\u2f7approx;\u6ab9qq;\u6ab5im;\u62e8i\xed\u0edfme\u0100;s\u2f88\u0eae\u6032\u0180Eas\u2f78\u2f90\u2f7a\xf0\u2f75\u0180dfp\u0eec\u2f99\u2faf\u0180als\u2fa0\u2fa5\u2faalar;\u632eine;\u6312urf;\u6313\u0100;t\u0efb\u2fb4\xef\u0efbrel;\u62b0\u0100ci\u2fc0\u2fc5r;\uc000\ud835\udcc5;\u43c8ncsp;\u6008\u0300fiopsu\u2fda\u22e2\u2fdf\u2fe5\u2feb\u2ff1r;\uc000\ud835\udd2epf;\uc000\ud835\udd62rime;\u6057cr;\uc000\ud835\udcc6\u0180aeo\u2ff8\u3009\u3013t\u0100ei\u2ffe\u3005rnion\xf3\u06b0nt;\u6a16st\u0100;e\u3010\u3011\u403f\xf1\u1f19\xf4\u0f14\u0a80ABHabcdefhilmnoprstux\u3040\u3051\u3055\u3059\u30e0\u310e\u312b\u3147\u3162\u3172\u318e\u3206\u3215\u3224\u3229\u3258\u326e\u3272\u3290\u32b0\u32b7\u0180art\u3047\u304a\u304cr\xf2\u10b3\xf2\u03ddail;\u691car\xf2\u1c65ar;\u6964\u0380cdenqrt\u3068\u3075\u3078\u307f\u308f\u3094\u30cc\u0100eu\u306d\u3071;\uc000\u223d\u0331te;\u4155i\xe3\u116emptyv;\u69b3g\u0200;del\u0fd1\u3089\u308b\u308d;\u6992;\u69a5\xe5\u0fd1uo\u803b\xbb\u40bbr\u0580;abcfhlpstw\u0fdc\u30ac\u30af\u30b7\u30b9\u30bc\u30be\u30c0\u30c3\u30c7\u30cap;\u6975\u0100;f\u0fe0\u30b4s;\u6920;\u6933s;\u691e\xeb\u225d\xf0\u272el;\u6945im;\u6974l;\u61a3;\u619d\u0100ai\u30d1\u30d5il;\u691ao\u0100;n\u30db\u30dc\u6236al\xf3\u0f1e\u0180abr\u30e7\u30ea\u30eer\xf2\u17e5rk;\u6773\u0100ak\u30f3\u30fdc\u0100ek\u30f9\u30fb;\u407d;\u405d\u0100es\u3102\u3104;\u698cl\u0100du\u310a\u310c;\u698e;\u6990\u0200aeuy\u3117\u311c\u3127\u3129ron;\u4159\u0100di\u3121\u3125il;\u4157\xec\u0ff2\xe2\u30fa;\u4440\u0200clqs\u3134\u3137\u313d\u3144a;\u6937dhar;\u6969uo\u0100;r\u020e\u020dh;\u61b3\u0180acg\u314e\u315f\u0f44l\u0200;ips\u0f78\u3158\u315b\u109cn\xe5\u10bbar\xf4\u0fa9t;\u65ad\u0180ilr\u3169\u1023\u316esht;\u697d;\uc000\ud835\udd2f\u0100ao\u3177\u3186r\u0100du\u317d\u317f\xbb\u047b\u0100;l\u1091\u3184;\u696c\u0100;v\u318b\u318c\u43c1;\u43f1\u0180gns\u3195\u31f9\u31fcht\u0300ahlrst\u31a4\u31b0\u31c2\u31d8\u31e4\u31eerrow\u0100;t\u0fdc\u31ada\xe9\u30c8arpoon\u0100du\u31bb\u31bfow\xee\u317ep\xbb\u1092eft\u0100ah\u31ca\u31d0rrow\xf3\u0feaarpoon\xf3\u0551ightarrows;\u61c9quigarro\xf7\u30cbhreetimes;\u62ccg;\u42daingdotse\xf1\u1f32\u0180ahm\u320d\u3210\u3213r\xf2\u0feaa\xf2\u0551;\u600foust\u0100;a\u321e\u321f\u63b1che\xbb\u321fmid;\u6aee\u0200abpt\u3232\u323d\u3240\u3252\u0100nr\u3237\u323ag;\u67edr;\u61fer\xeb\u1003\u0180afl\u3247\u324a\u324er;\u6986;\uc000\ud835\udd63us;\u6a2eimes;\u6a35\u0100ap\u325d\u3267r\u0100;g\u3263\u3264\u4029t;\u6994olint;\u6a12ar\xf2\u31e3\u0200achq\u327b\u3280\u10bc\u3285quo;\u603ar;\uc000\ud835\udcc7\u0100bu\u30fb\u328ao\u0100;r\u0214\u0213\u0180hir\u3297\u329b\u32a0re\xe5\u31f8mes;\u62cai\u0200;efl\u32aa\u1059\u1821\u32ab\u65b9tri;\u69celuhar;\u6968;\u611e\u0d61\u32d5\u32db\u32df\u332c\u3338\u3371\0\u337a\u33a4\0\0\u33ec\u33f0\0\u3428\u3448\u345a\u34ad\u34b1\u34ca\u34f1\0\u3616\0\0\u3633cute;\u415bqu\xef\u27ba\u0500;Eaceinpsy\u11ed\u32f3\u32f5\u32ff\u3302\u330b\u330f\u331f\u3326\u3329;\u6ab4\u01f0\u32fa\0\u32fc;\u6ab8on;\u4161u\xe5\u11fe\u0100;d\u11f3\u3307il;\u415frc;\u415d\u0180Eas\u3316\u3318\u331b;\u6ab6p;\u6abaim;\u62e9olint;\u6a13i\xed\u1204;\u4441ot\u0180;be\u3334\u1d47\u3335\u62c5;\u6a66\u0380Aacmstx\u3346\u334a\u3357\u335b\u335e\u3363\u336drr;\u61d8r\u0100hr\u3350\u3352\xeb\u2228\u0100;o\u0a36\u0a34t\u803b\xa7\u40a7i;\u403bwar;\u6929m\u0100in\u3369\xf0nu\xf3\xf1t;\u6736r\u0100;o\u3376\u2055\uc000\ud835\udd30\u0200acoy\u3382\u3386\u3391\u33a0rp;\u666f\u0100hy\u338b\u338fcy;\u4449;\u4448rt\u026d\u3399\0\0\u339ci\xe4\u1464ara\xec\u2e6f\u803b\xad\u40ad\u0100gm\u33a8\u33b4ma\u0180;fv\u33b1\u33b2\u33b2\u43c3;\u43c2\u0400;deglnpr\u12ab\u33c5\u33c9\u33ce\u33d6\u33de\u33e1\u33e6ot;\u6a6a\u0100;q\u12b1\u12b0\u0100;E\u33d3\u33d4\u6a9e;\u6aa0\u0100;E\u33db\u33dc\u6a9d;\u6a9fe;\u6246lus;\u6a24arr;\u6972ar\xf2\u113d\u0200aeit\u33f8\u3408\u340f\u3417\u0100ls\u33fd\u3404lsetm\xe9\u336ahp;\u6a33parsl;\u69e4\u0100dl\u1463\u3414e;\u6323\u0100;e\u341c\u341d\u6aaa\u0100;s\u3422\u3423\u6aac;\uc000\u2aac\ufe00\u0180flp\u342e\u3433\u3442tcy;\u444c\u0100;b\u3438\u3439\u402f\u0100;a\u343e\u343f\u69c4r;\u633ff;\uc000\ud835\udd64a\u0100dr\u344d\u0402es\u0100;u\u3454\u3455\u6660it\xbb\u3455\u0180csu\u3460\u3479\u349f\u0100au\u3465\u346fp\u0100;s\u1188\u346b;\uc000\u2293\ufe00p\u0100;s\u11b4\u3475;\uc000\u2294\ufe00u\u0100bp\u347f\u348f\u0180;es\u1197\u119c\u3486et\u0100;e\u1197\u348d\xf1\u119d\u0180;es\u11a8\u11ad\u3496et\u0100;e\u11a8\u349d\xf1\u11ae\u0180;af\u117b\u34a6\u05b0r\u0165\u34ab\u05b1\xbb\u117car\xf2\u1148\u0200cemt\u34b9\u34be\u34c2\u34c5r;\uc000\ud835\udcc8tm\xee\xf1i\xec\u3415ar\xe6\u11be\u0100ar\u34ce\u34d5r\u0100;f\u34d4\u17bf\u6606\u0100an\u34da\u34edight\u0100ep\u34e3\u34eapsilo\xee\u1ee0h\xe9\u2eafs\xbb\u2852\u0280bcmnp\u34fb\u355e\u1209\u358b\u358e\u0480;Edemnprs\u350e\u350f\u3511\u3515\u351e\u3523\u352c\u3531\u3536\u6282;\u6ac5ot;\u6abd\u0100;d\u11da\u351aot;\u6ac3ult;\u6ac1\u0100Ee\u3528\u352a;\u6acb;\u628alus;\u6abfarr;\u6979\u0180eiu\u353d\u3552\u3555t\u0180;en\u350e\u3545\u354bq\u0100;q\u11da\u350feq\u0100;q\u352b\u3528m;\u6ac7\u0100bp\u355a\u355c;\u6ad5;\u6ad3c\u0300;acens\u11ed\u356c\u3572\u3579\u357b\u3326ppro\xf8\u32faurlye\xf1\u11fe\xf1\u11f3\u0180aes\u3582\u3588\u331bppro\xf8\u331aq\xf1\u3317g;\u666a\u0680123;Edehlmnps\u35a9\u35ac\u35af\u121c\u35b2\u35b4\u35c0\u35c9\u35d5\u35da\u35df\u35e8\u35ed\u803b\xb9\u40b9\u803b\xb2\u40b2\u803b\xb3\u40b3;\u6ac6\u0100os\u35b9\u35bct;\u6abeub;\u6ad8\u0100;d\u1222\u35c5ot;\u6ac4s\u0100ou\u35cf\u35d2l;\u67c9b;\u6ad7arr;\u697bult;\u6ac2\u0100Ee\u35e4\u35e6;\u6acc;\u628blus;\u6ac0\u0180eiu\u35f4\u3609\u360ct\u0180;en\u121c\u35fc\u3602q\u0100;q\u1222\u35b2eq\u0100;q\u35e7\u35e4m;\u6ac8\u0100bp\u3611\u3613;\u6ad4;\u6ad6\u0180Aan\u361c\u3620\u362drr;\u61d9r\u0100hr\u3626\u3628\xeb\u222e\u0100;o\u0a2b\u0a29war;\u692alig\u803b\xdf\u40df\u0be1\u3651\u365d\u3660\u12ce\u3673\u3679\0\u367e\u36c2\0\0\0\0\0\u36db\u3703\0\u3709\u376c\0\0\0\u3787\u0272\u3656\0\0\u365bget;\u6316;\u43c4r\xeb\u0e5f\u0180aey\u3666\u366b\u3670ron;\u4165dil;\u4163;\u4442lrec;\u6315r;\uc000\ud835\udd31\u0200eiko\u3686\u369d\u36b5\u36bc\u01f2\u368b\0\u3691e\u01004f\u1284\u1281a\u0180;sv\u3698\u3699\u369b\u43b8ym;\u43d1\u0100cn\u36a2\u36b2k\u0100as\u36a8\u36aeppro\xf8\u12c1im\xbb\u12acs\xf0\u129e\u0100as\u36ba\u36ae\xf0\u12c1rn\u803b\xfe\u40fe\u01ec\u031f\u36c6\u22e7es\u8180\xd7;bd\u36cf\u36d0\u36d8\u40d7\u0100;a\u190f\u36d5r;\u6a31;\u6a30\u0180eps\u36e1\u36e3\u3700\xe1\u2a4d\u0200;bcf\u0486\u36ec\u36f0\u36f4ot;\u6336ir;\u6af1\u0100;o\u36f9\u36fc\uc000\ud835\udd65rk;\u6ada\xe1\u3362rime;\u6034\u0180aip\u370f\u3712\u3764d\xe5\u1248\u0380adempst\u3721\u374d\u3740\u3751\u3757\u375c\u375fngle\u0280;dlqr\u3730\u3731\u3736\u3740\u3742\u65b5own\xbb\u1dbbeft\u0100;e\u2800\u373e\xf1\u092e;\u625cight\u0100;e\u32aa\u374b\xf1\u105aot;\u65ecinus;\u6a3alus;\u6a39b;\u69cdime;\u6a3bezium;\u63e2\u0180cht\u3772\u377d\u3781\u0100ry\u3777\u377b;\uc000\ud835\udcc9;\u4446cy;\u445brok;\u4167\u0100io\u378b\u378ex\xf4\u1777head\u0100lr\u3797\u37a0eftarro\xf7\u084fightarrow\xbb\u0f5d\u0900AHabcdfghlmoprstuw\u37d0\u37d3\u37d7\u37e4\u37f0\u37fc\u380e\u381c\u3823\u3834\u3851\u385d\u386b\u38a9\u38cc\u38d2\u38ea\u38f6r\xf2\u03edar;\u6963\u0100cr\u37dc\u37e2ute\u803b\xfa\u40fa\xf2\u1150r\u01e3\u37ea\0\u37edy;\u445eve;\u416d\u0100iy\u37f5\u37farc\u803b\xfb\u40fb;\u4443\u0180abh\u3803\u3806\u380br\xf2\u13adlac;\u4171a\xf2\u13c3\u0100ir\u3813\u3818sht;\u697e;\uc000\ud835\udd32rave\u803b\xf9\u40f9\u0161\u3827\u3831r\u0100lr\u382c\u382e\xbb\u0957\xbb\u1083lk;\u6580\u0100ct\u3839\u384d\u026f\u383f\0\0\u384arn\u0100;e\u3845\u3846\u631cr\xbb\u3846op;\u630fri;\u65f8\u0100al\u3856\u385acr;\u416b\u80bb\xa8\u0349\u0100gp\u3862\u3866on;\u4173f;\uc000\ud835\udd66\u0300adhlsu\u114b\u3878\u387d\u1372\u3891\u38a0own\xe1\u13b3arpoon\u0100lr\u3888\u388cef\xf4\u382digh\xf4\u382fi\u0180;hl\u3899\u389a\u389c\u43c5\xbb\u13faon\xbb\u389aparrows;\u61c8\u0180cit\u38b0\u38c4\u38c8\u026f\u38b6\0\0\u38c1rn\u0100;e\u38bc\u38bd\u631dr\xbb\u38bdop;\u630eng;\u416fri;\u65f9cr;\uc000\ud835\udcca\u0180dir\u38d9\u38dd\u38e2ot;\u62f0lde;\u4169i\u0100;f\u3730\u38e8\xbb\u1813\u0100am\u38ef\u38f2r\xf2\u38a8l\u803b\xfc\u40fcangle;\u69a7\u0780ABDacdeflnoprsz\u391c\u391f\u3929\u392d\u39b5\u39b8\u39bd\u39df\u39e4\u39e8\u39f3\u39f9\u39fd\u3a01\u3a20r\xf2\u03f7ar\u0100;v\u3926\u3927\u6ae8;\u6ae9as\xe8\u03e1\u0100nr\u3932\u3937grt;\u699c\u0380eknprst\u34e3\u3946\u394b\u3952\u395d\u3964\u3996app\xe1\u2415othin\xe7\u1e96\u0180hir\u34eb\u2ec8\u3959op\xf4\u2fb5\u0100;h\u13b7\u3962\xef\u318d\u0100iu\u3969\u396dgm\xe1\u33b3\u0100bp\u3972\u3984setneq\u0100;q\u397d\u3980\uc000\u228a\ufe00;\uc000\u2acb\ufe00setneq\u0100;q\u398f\u3992\uc000\u228b\ufe00;\uc000\u2acc\ufe00\u0100hr\u399b\u399fet\xe1\u369ciangle\u0100lr\u39aa\u39afeft\xbb\u0925ight\xbb\u1051y;\u4432ash\xbb\u1036\u0180elr\u39c4\u39d2\u39d7\u0180;be\u2dea\u39cb\u39cfar;\u62bbq;\u625alip;\u62ee\u0100bt\u39dc\u1468a\xf2\u1469r;\uc000\ud835\udd33tr\xe9\u39aesu\u0100bp\u39ef\u39f1\xbb\u0d1c\xbb\u0d59pf;\uc000\ud835\udd67ro\xf0\u0efbtr\xe9\u39b4\u0100cu\u3a06\u3a0br;\uc000\ud835\udccb\u0100bp\u3a10\u3a18n\u0100Ee\u3980\u3a16\xbb\u397en\u0100Ee\u3992\u3a1e\xbb\u3990igzag;\u699a\u0380cefoprs\u3a36\u3a3b\u3a56\u3a5b\u3a54\u3a61\u3a6airc;\u4175\u0100di\u3a40\u3a51\u0100bg\u3a45\u3a49ar;\u6a5fe\u0100;q\u15fa\u3a4f;\u6259erp;\u6118r;\uc000\ud835\udd34pf;\uc000\ud835\udd68\u0100;e\u1479\u3a66at\xe8\u1479cr;\uc000\ud835\udccc\u0ae3\u178e\u3a87\0\u3a8b\0\u3a90\u3a9b\0\0\u3a9d\u3aa8\u3aab\u3aaf\0\0\u3ac3\u3ace\0\u3ad8\u17dc\u17dftr\xe9\u17d1r;\uc000\ud835\udd35\u0100Aa\u3a94\u3a97r\xf2\u03c3r\xf2\u09f6;\u43be\u0100Aa\u3aa1\u3aa4r\xf2\u03b8r\xf2\u09eba\xf0\u2713is;\u62fb\u0180dpt\u17a4\u3ab5\u3abe\u0100fl\u3aba\u17a9;\uc000\ud835\udd69im\xe5\u17b2\u0100Aa\u3ac7\u3acar\xf2\u03cer\xf2\u0a01\u0100cq\u3ad2\u17b8r;\uc000\ud835\udccd\u0100pt\u17d6\u3adcr\xe9\u17d4\u0400acefiosu\u3af0\u3afd\u3b08\u3b0c\u3b11\u3b15\u3b1b\u3b21c\u0100uy\u3af6\u3afbte\u803b\xfd\u40fd;\u444f\u0100iy\u3b02\u3b06rc;\u4177;\u444bn\u803b\xa5\u40a5r;\uc000\ud835\udd36cy;\u4457pf;\uc000\ud835\udd6acr;\uc000\ud835\udcce\u0100cm\u3b26\u3b29y;\u444el\u803b\xff\u40ff\u0500acdefhiosw\u3b42\u3b48\u3b54\u3b58\u3b64\u3b69\u3b6d\u3b74\u3b7a\u3b80cute;\u417a\u0100ay\u3b4d\u3b52ron;\u417e;\u4437ot;\u417c\u0100et\u3b5d\u3b61tr\xe6\u155fa;\u43b6r;\uc000\ud835\udd37cy;\u4436grarr;\u61ddpf;\uc000\ud835\udd6bcr;\uc000\ud835\udccf\u0100jn\u3b85\u3b87;\u600dj;\u600c"
    .split("")
    .map((c) => c.charCodeAt(0)));

// Generated using scripts/write-decode-map.ts
var xmlDecodeTree = new Uint16Array(
// prettier-ignore
"\u0200aglq\t\x15\x18\x1b\u026d\x0f\0\0\x12p;\u4026os;\u4027t;\u403et;\u403cuot;\u4022"
    .split("")
    .map((c) => c.charCodeAt(0)));

// Adapted from https://github.com/mathiasbynens/he/blob/36afe179392226cf1b6ccdb16ebbb7a5a844d93a/src/he.js#L106-L134
var _a;
const decodeMap = new Map([
    [0, 65533],
    // C1 Unicode control character reference replacements
    [128, 8364],
    [130, 8218],
    [131, 402],
    [132, 8222],
    [133, 8230],
    [134, 8224],
    [135, 8225],
    [136, 710],
    [137, 8240],
    [138, 352],
    [139, 8249],
    [140, 338],
    [142, 381],
    [145, 8216],
    [146, 8217],
    [147, 8220],
    [148, 8221],
    [149, 8226],
    [150, 8211],
    [151, 8212],
    [152, 732],
    [153, 8482],
    [154, 353],
    [155, 8250],
    [156, 339],
    [158, 382],
    [159, 376],
]);
/**
 * Polyfill for `String.fromCodePoint`. It is used to create a string from a Unicode code point.
 */
const fromCodePoint$1 = 
// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition, node/no-unsupported-features/es-builtins
(_a = String.fromCodePoint) !== null && _a !== void 0 ? _a : function (codePoint) {
    let output = "";
    if (codePoint > 0xffff) {
        codePoint -= 0x10000;
        output += String.fromCharCode(((codePoint >>> 10) & 0x3ff) | 0xd800);
        codePoint = 0xdc00 | (codePoint & 0x3ff);
    }
    output += String.fromCharCode(codePoint);
    return output;
};
/**
 * Replace the given code point with a replacement character if it is a
 * surrogate or is outside the valid range. Otherwise return the code
 * point unchanged.
 */
function replaceCodePoint(codePoint) {
    var _a;
    if ((codePoint >= 0xd800 && codePoint <= 0xdfff) || codePoint > 0x10ffff) {
        return 0xfffd;
    }
    return (_a = decodeMap.get(codePoint)) !== null && _a !== void 0 ? _a : codePoint;
}

var CharCodes;
(function (CharCodes) {
    CharCodes[CharCodes["NUM"] = 35] = "NUM";
    CharCodes[CharCodes["SEMI"] = 59] = "SEMI";
    CharCodes[CharCodes["EQUALS"] = 61] = "EQUALS";
    CharCodes[CharCodes["ZERO"] = 48] = "ZERO";
    CharCodes[CharCodes["NINE"] = 57] = "NINE";
    CharCodes[CharCodes["LOWER_A"] = 97] = "LOWER_A";
    CharCodes[CharCodes["LOWER_F"] = 102] = "LOWER_F";
    CharCodes[CharCodes["LOWER_X"] = 120] = "LOWER_X";
    CharCodes[CharCodes["LOWER_Z"] = 122] = "LOWER_Z";
    CharCodes[CharCodes["UPPER_A"] = 65] = "UPPER_A";
    CharCodes[CharCodes["UPPER_F"] = 70] = "UPPER_F";
    CharCodes[CharCodes["UPPER_Z"] = 90] = "UPPER_Z";
})(CharCodes || (CharCodes = {}));
/** Bit that needs to be set to convert an upper case ASCII character to lower case */
const TO_LOWER_BIT = 0b100000;
var BinTrieFlags;
(function (BinTrieFlags) {
    BinTrieFlags[BinTrieFlags["VALUE_LENGTH"] = 49152] = "VALUE_LENGTH";
    BinTrieFlags[BinTrieFlags["BRANCH_LENGTH"] = 16256] = "BRANCH_LENGTH";
    BinTrieFlags[BinTrieFlags["JUMP_TABLE"] = 127] = "JUMP_TABLE";
})(BinTrieFlags || (BinTrieFlags = {}));
function isNumber(code) {
    return code >= CharCodes.ZERO && code <= CharCodes.NINE;
}
function isHexadecimalCharacter(code) {
    return ((code >= CharCodes.UPPER_A && code <= CharCodes.UPPER_F) ||
        (code >= CharCodes.LOWER_A && code <= CharCodes.LOWER_F));
}
function isAsciiAlphaNumeric(code) {
    return ((code >= CharCodes.UPPER_A && code <= CharCodes.UPPER_Z) ||
        (code >= CharCodes.LOWER_A && code <= CharCodes.LOWER_Z) ||
        isNumber(code));
}
/**
 * Checks if the given character is a valid end character for an entity in an attribute.
 *
 * Attribute values that aren't terminated properly aren't parsed, and shouldn't lead to a parser error.
 * See the example in https://html.spec.whatwg.org/multipage/parsing.html#named-character-reference-state
 */
function isEntityInAttributeInvalidEnd(code) {
    return code === CharCodes.EQUALS || isAsciiAlphaNumeric(code);
}
var EntityDecoderState;
(function (EntityDecoderState) {
    EntityDecoderState[EntityDecoderState["EntityStart"] = 0] = "EntityStart";
    EntityDecoderState[EntityDecoderState["NumericStart"] = 1] = "NumericStart";
    EntityDecoderState[EntityDecoderState["NumericDecimal"] = 2] = "NumericDecimal";
    EntityDecoderState[EntityDecoderState["NumericHex"] = 3] = "NumericHex";
    EntityDecoderState[EntityDecoderState["NamedEntity"] = 4] = "NamedEntity";
})(EntityDecoderState || (EntityDecoderState = {}));
var DecodingMode;
(function (DecodingMode) {
    /** Entities in text nodes that can end with any character. */
    DecodingMode[DecodingMode["Legacy"] = 0] = "Legacy";
    /** Only allow entities terminated with a semicolon. */
    DecodingMode[DecodingMode["Strict"] = 1] = "Strict";
    /** Entities in attributes have limitations on ending characters. */
    DecodingMode[DecodingMode["Attribute"] = 2] = "Attribute";
})(DecodingMode || (DecodingMode = {}));
/**
 * Token decoder with support of writing partial entities.
 */
class EntityDecoder {
    constructor(
    /** The tree used to decode entities. */
    decodeTree, 
    /**
     * The function that is called when a codepoint is decoded.
     *
     * For multi-byte named entities, this will be called multiple times,
     * with the second codepoint, and the same `consumed` value.
     *
     * @param codepoint The decoded codepoint.
     * @param consumed The number of bytes consumed by the decoder.
     */
    emitCodePoint, 
    /** An object that is used to produce errors. */
    errors) {
        this.decodeTree = decodeTree;
        this.emitCodePoint = emitCodePoint;
        this.errors = errors;
        /** The current state of the decoder. */
        this.state = EntityDecoderState.EntityStart;
        /** Characters that were consumed while parsing an entity. */
        this.consumed = 1;
        /**
         * The result of the entity.
         *
         * Either the result index of a numeric entity, or the codepoint of a
         * numeric entity.
         */
        this.result = 0;
        /** The current index in the decode tree. */
        this.treeIndex = 0;
        /** The number of characters that were consumed in excess. */
        this.excess = 1;
        /** The mode in which the decoder is operating. */
        this.decodeMode = DecodingMode.Strict;
    }
    /** Resets the instance to make it reusable. */
    startEntity(decodeMode) {
        this.decodeMode = decodeMode;
        this.state = EntityDecoderState.EntityStart;
        this.result = 0;
        this.treeIndex = 0;
        this.excess = 1;
        this.consumed = 1;
    }
    /**
     * Write an entity to the decoder. This can be called multiple times with partial entities.
     * If the entity is incomplete, the decoder will return -1.
     *
     * Mirrors the implementation of `getDecoder`, but with the ability to stop decoding if the
     * entity is incomplete, and resume when the next string is written.
     *
     * @param string The string containing the entity (or a continuation of the entity).
     * @param offset The offset at which the entity begins. Should be 0 if this is not the first call.
     * @returns The number of characters that were consumed, or -1 if the entity is incomplete.
     */
    write(str, offset) {
        switch (this.state) {
            case EntityDecoderState.EntityStart: {
                if (str.charCodeAt(offset) === CharCodes.NUM) {
                    this.state = EntityDecoderState.NumericStart;
                    this.consumed += 1;
                    return this.stateNumericStart(str, offset + 1);
                }
                this.state = EntityDecoderState.NamedEntity;
                return this.stateNamedEntity(str, offset);
            }
            case EntityDecoderState.NumericStart: {
                return this.stateNumericStart(str, offset);
            }
            case EntityDecoderState.NumericDecimal: {
                return this.stateNumericDecimal(str, offset);
            }
            case EntityDecoderState.NumericHex: {
                return this.stateNumericHex(str, offset);
            }
            case EntityDecoderState.NamedEntity: {
                return this.stateNamedEntity(str, offset);
            }
        }
    }
    /**
     * Switches between the numeric decimal and hexadecimal states.
     *
     * Equivalent to the `Numeric character reference state` in the HTML spec.
     *
     * @param str The string containing the entity (or a continuation of the entity).
     * @param offset The current offset.
     * @returns The number of characters that were consumed, or -1 if the entity is incomplete.
     */
    stateNumericStart(str, offset) {
        if (offset >= str.length) {
            return -1;
        }
        if ((str.charCodeAt(offset) | TO_LOWER_BIT) === CharCodes.LOWER_X) {
            this.state = EntityDecoderState.NumericHex;
            this.consumed += 1;
            return this.stateNumericHex(str, offset + 1);
        }
        this.state = EntityDecoderState.NumericDecimal;
        return this.stateNumericDecimal(str, offset);
    }
    addToNumericResult(str, start, end, base) {
        if (start !== end) {
            const digitCount = end - start;
            this.result =
                this.result * Math.pow(base, digitCount) +
                    parseInt(str.substr(start, digitCount), base);
            this.consumed += digitCount;
        }
    }
    /**
     * Parses a hexadecimal numeric entity.
     *
     * Equivalent to the `Hexademical character reference state` in the HTML spec.
     *
     * @param str The string containing the entity (or a continuation of the entity).
     * @param offset The current offset.
     * @returns The number of characters that were consumed, or -1 if the entity is incomplete.
     */
    stateNumericHex(str, offset) {
        const startIdx = offset;
        while (offset < str.length) {
            const char = str.charCodeAt(offset);
            if (isNumber(char) || isHexadecimalCharacter(char)) {
                offset += 1;
            }
            else {
                this.addToNumericResult(str, startIdx, offset, 16);
                return this.emitNumericEntity(char, 3);
            }
        }
        this.addToNumericResult(str, startIdx, offset, 16);
        return -1;
    }
    /**
     * Parses a decimal numeric entity.
     *
     * Equivalent to the `Decimal character reference state` in the HTML spec.
     *
     * @param str The string containing the entity (or a continuation of the entity).
     * @param offset The current offset.
     * @returns The number of characters that were consumed, or -1 if the entity is incomplete.
     */
    stateNumericDecimal(str, offset) {
        const startIdx = offset;
        while (offset < str.length) {
            const char = str.charCodeAt(offset);
            if (isNumber(char)) {
                offset += 1;
            }
            else {
                this.addToNumericResult(str, startIdx, offset, 10);
                return this.emitNumericEntity(char, 2);
            }
        }
        this.addToNumericResult(str, startIdx, offset, 10);
        return -1;
    }
    /**
     * Validate and emit a numeric entity.
     *
     * Implements the logic from the `Hexademical character reference start
     * state` and `Numeric character reference end state` in the HTML spec.
     *
     * @param lastCp The last code point of the entity. Used to see if the
     *               entity was terminated with a semicolon.
     * @param expectedLength The minimum number of characters that should be
     *                       consumed. Used to validate that at least one digit
     *                       was consumed.
     * @returns The number of characters that were consumed.
     */
    emitNumericEntity(lastCp, expectedLength) {
        var _a;
        // Ensure we consumed at least one digit.
        if (this.consumed <= expectedLength) {
            (_a = this.errors) === null || _a === void 0 ? void 0 : _a.absenceOfDigitsInNumericCharacterReference(this.consumed);
            return 0;
        }
        // Figure out if this is a legit end of the entity
        if (lastCp === CharCodes.SEMI) {
            this.consumed += 1;
        }
        else if (this.decodeMode === DecodingMode.Strict) {
            return 0;
        }
        this.emitCodePoint(replaceCodePoint(this.result), this.consumed);
        if (this.errors) {
            if (lastCp !== CharCodes.SEMI) {
                this.errors.missingSemicolonAfterCharacterReference();
            }
            this.errors.validateNumericCharacterReference(this.result);
        }
        return this.consumed;
    }
    /**
     * Parses a named entity.
     *
     * Equivalent to the `Named character reference state` in the HTML spec.
     *
     * @param str The string containing the entity (or a continuation of the entity).
     * @param offset The current offset.
     * @returns The number of characters that were consumed, or -1 if the entity is incomplete.
     */
    stateNamedEntity(str, offset) {
        const { decodeTree } = this;
        let current = decodeTree[this.treeIndex];
        // The mask is the number of bytes of the value, including the current byte.
        let valueLength = (current & BinTrieFlags.VALUE_LENGTH) >> 14;
        for (; offset < str.length; offset++, this.excess++) {
            const char = str.charCodeAt(offset);
            this.treeIndex = determineBranch(decodeTree, current, this.treeIndex + Math.max(1, valueLength), char);
            if (this.treeIndex < 0) {
                return this.result === 0 ||
                    // If we are parsing an attribute
                    (this.decodeMode === DecodingMode.Attribute &&
                        // We shouldn't have consumed any characters after the entity,
                        (valueLength === 0 ||
                            // And there should be no invalid characters.
                            isEntityInAttributeInvalidEnd(char)))
                    ? 0
                    : this.emitNotTerminatedNamedEntity();
            }
            current = decodeTree[this.treeIndex];
            valueLength = (current & BinTrieFlags.VALUE_LENGTH) >> 14;
            // If the branch is a value, store it and continue
            if (valueLength !== 0) {
                // If the entity is terminated by a semicolon, we are done.
                if (char === CharCodes.SEMI) {
                    return this.emitNamedEntityData(this.treeIndex, valueLength, this.consumed + this.excess);
                }
                // If we encounter a non-terminated (legacy) entity while parsing strictly, then ignore it.
                if (this.decodeMode !== DecodingMode.Strict) {
                    this.result = this.treeIndex;
                    this.consumed += this.excess;
                    this.excess = 0;
                }
            }
        }
        return -1;
    }
    /**
     * Emit a named entity that was not terminated with a semicolon.
     *
     * @returns The number of characters consumed.
     */
    emitNotTerminatedNamedEntity() {
        var _a;
        const { result, decodeTree } = this;
        const valueLength = (decodeTree[result] & BinTrieFlags.VALUE_LENGTH) >> 14;
        this.emitNamedEntityData(result, valueLength, this.consumed);
        (_a = this.errors) === null || _a === void 0 ? void 0 : _a.missingSemicolonAfterCharacterReference();
        return this.consumed;
    }
    /**
     * Emit a named entity.
     *
     * @param result The index of the entity in the decode tree.
     * @param valueLength The number of bytes in the entity.
     * @param consumed The number of characters consumed.
     *
     * @returns The number of characters consumed.
     */
    emitNamedEntityData(result, valueLength, consumed) {
        const { decodeTree } = this;
        this.emitCodePoint(valueLength === 1
            ? decodeTree[result] & ~BinTrieFlags.VALUE_LENGTH
            : decodeTree[result + 1], consumed);
        if (valueLength === 3) {
            // For multi-byte values, we need to emit the second byte.
            this.emitCodePoint(decodeTree[result + 2], consumed);
        }
        return consumed;
    }
    /**
     * Signal to the parser that the end of the input was reached.
     *
     * Remaining data will be emitted and relevant errors will be produced.
     *
     * @returns The number of characters consumed.
     */
    end() {
        var _a;
        switch (this.state) {
            case EntityDecoderState.NamedEntity: {
                // Emit a named entity if we have one.
                return this.result !== 0 &&
                    (this.decodeMode !== DecodingMode.Attribute ||
                        this.result === this.treeIndex)
                    ? this.emitNotTerminatedNamedEntity()
                    : 0;
            }
            // Otherwise, emit a numeric entity if we have one.
            case EntityDecoderState.NumericDecimal: {
                return this.emitNumericEntity(0, 2);
            }
            case EntityDecoderState.NumericHex: {
                return this.emitNumericEntity(0, 3);
            }
            case EntityDecoderState.NumericStart: {
                (_a = this.errors) === null || _a === void 0 ? void 0 : _a.absenceOfDigitsInNumericCharacterReference(this.consumed);
                return 0;
            }
            case EntityDecoderState.EntityStart: {
                // Return 0 if we have no entity.
                return 0;
            }
        }
    }
}
/**
 * Creates a function that decodes entities in a string.
 *
 * @param decodeTree The decode tree.
 * @returns A function that decodes entities in a string.
 */
function getDecoder(decodeTree) {
    let ret = "";
    const decoder = new EntityDecoder(decodeTree, (str) => (ret += fromCodePoint$1(str)));
    return function decodeWithTrie(str, decodeMode) {
        let lastIndex = 0;
        let offset = 0;
        while ((offset = str.indexOf("&", offset)) >= 0) {
            ret += str.slice(lastIndex, offset);
            decoder.startEntity(decodeMode);
            const len = decoder.write(str, 
            // Skip the "&"
            offset + 1);
            if (len < 0) {
                lastIndex = offset + decoder.end();
                break;
            }
            lastIndex = offset + len;
            // If `len` is 0, skip the current `&` and continue.
            offset = len === 0 ? lastIndex + 1 : lastIndex;
        }
        const result = ret + str.slice(lastIndex);
        // Make sure we don't keep a reference to the final string.
        ret = "";
        return result;
    };
}
/**
 * Determines the branch of the current node that is taken given the current
 * character. This function is used to traverse the trie.
 *
 * @param decodeTree The trie.
 * @param current The current node.
 * @param nodeIdx The index right after the current node and its value.
 * @param char The current character.
 * @returns The index of the next node, or -1 if no branch is taken.
 */
function determineBranch(decodeTree, current, nodeIdx, char) {
    const branchCount = (current & BinTrieFlags.BRANCH_LENGTH) >> 7;
    const jumpOffset = current & BinTrieFlags.JUMP_TABLE;
    // Case 1: Single branch encoded in jump offset
    if (branchCount === 0) {
        return jumpOffset !== 0 && char === jumpOffset ? nodeIdx : -1;
    }
    // Case 2: Multiple branches encoded in jump table
    if (jumpOffset) {
        const value = char - jumpOffset;
        return value < 0 || value >= branchCount
            ? -1
            : decodeTree[nodeIdx + value] - 1;
    }
    // Case 3: Multiple branches encoded in dictionary
    // Binary search for the character.
    let lo = nodeIdx;
    let hi = lo + branchCount - 1;
    while (lo <= hi) {
        const mid = (lo + hi) >>> 1;
        const midVal = decodeTree[mid];
        if (midVal < char) {
            lo = mid + 1;
        }
        else if (midVal > char) {
            hi = mid - 1;
        }
        else {
            return decodeTree[mid + branchCount];
        }
    }
    return -1;
}
const htmlDecoder = getDecoder(htmlDecodeTree);
getDecoder(xmlDecodeTree);
/**
 * Decodes an HTML string.
 *
 * @param str The string to decode.
 * @param mode The decoding mode.
 * @returns The decoded string.
 */
function decodeHTML(str, mode = DecodingMode.Legacy) {
    return htmlDecoder(str, mode);
}

// Utilities
//


function _class$1 (obj) { return Object.prototype.toString.call(obj) }

function isString$1 (obj) { return _class$1(obj) === '[object String]' }

const _hasOwnProperty = Object.prototype.hasOwnProperty;

function has (object, key) {
  return _hasOwnProperty.call(object, key)
}

// Merge objects
//
function assign$1 (obj /* from1, from2, from3, ... */) {
  const sources = Array.prototype.slice.call(arguments, 1);

  sources.forEach(function (source) {
    if (!source) { return }

    if (typeof source !== 'object') {
      throw new TypeError(source + 'must be object')
    }

    Object.keys(source).forEach(function (key) {
      obj[key] = source[key];
    });
  });

  return obj
}

// Remove element from array and put another array at those position.
// Useful for some operations with tokens
function arrayReplaceAt (src, pos, newElements) {
  return [].concat(src.slice(0, pos), newElements, src.slice(pos + 1))
}

function isValidEntityCode (c) {
  /* eslint no-bitwise:0 */
  // broken sequence
  if (c >= 0xD800 && c <= 0xDFFF) { return false }
  // never used
  if (c >= 0xFDD0 && c <= 0xFDEF) { return false }
  if ((c & 0xFFFF) === 0xFFFF || (c & 0xFFFF) === 0xFFFE) { return false }
  // control codes
  if (c >= 0x00 && c <= 0x08) { return false }
  if (c === 0x0B) { return false }
  if (c >= 0x0E && c <= 0x1F) { return false }
  if (c >= 0x7F && c <= 0x9F) { return false }
  // out of range
  if (c > 0x10FFFF) { return false }
  return true
}

function fromCodePoint (c) {
  /* eslint no-bitwise:0 */
  if (c > 0xffff) {
    c -= 0x10000;
    const surrogate1 = 0xd800 + (c >> 10);
    const surrogate2 = 0xdc00 + (c & 0x3ff);

    return String.fromCharCode(surrogate1, surrogate2)
  }
  return String.fromCharCode(c)
}

const UNESCAPE_MD_RE  = /\\([!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~])/g;
const ENTITY_RE       = /&([a-z#][a-z0-9]{1,31});/gi;
const UNESCAPE_ALL_RE = new RegExp(UNESCAPE_MD_RE.source + '|' + ENTITY_RE.source, 'gi');

const DIGITAL_ENTITY_TEST_RE = /^#((?:x[a-f0-9]{1,8}|[0-9]{1,8}))$/i;

function replaceEntityPattern (match, name) {
  if (name.charCodeAt(0) === 0x23/* # */ && DIGITAL_ENTITY_TEST_RE.test(name)) {
    const code = name[1].toLowerCase() === 'x'
      ? parseInt(name.slice(2), 16)
      : parseInt(name.slice(1), 10);

    if (isValidEntityCode(code)) {
      return fromCodePoint(code)
    }

    return match
  }

  const decoded = decodeHTML(match);
  if (decoded !== match) {
    return decoded
  }

  return match
}

/* function replaceEntities(str) {
  if (str.indexOf('&') < 0) { return str; }

  return str.replace(ENTITY_RE, replaceEntityPattern);
} */

function unescapeMd (str) {
  if (str.indexOf('\\') < 0) { return str }
  return str.replace(UNESCAPE_MD_RE, '$1')
}

function unescapeAll (str) {
  if (str.indexOf('\\') < 0 && str.indexOf('&') < 0) { return str }

  return str.replace(UNESCAPE_ALL_RE, function (match, escaped, entity) {
    if (escaped) { return escaped }
    return replaceEntityPattern(match, entity)
  })
}

const HTML_ESCAPE_TEST_RE$1 = /[&<>"]/;
const HTML_ESCAPE_REPLACE_RE$1 = /[&<>"]/g;
const HTML_REPLACEMENTS$1 = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;'
};

function replaceUnsafeChar$1 (ch) {
  return HTML_REPLACEMENTS$1[ch]
}

function escapeHtml (str) {
  if (HTML_ESCAPE_TEST_RE$1.test(str)) {
    return str.replace(HTML_ESCAPE_REPLACE_RE$1, replaceUnsafeChar$1)
  }
  return str
}

const REGEXP_ESCAPE_RE = /[.?*+^$[\]\\(){}|-]/g;

function escapeRE$1 (str) {
  return str.replace(REGEXP_ESCAPE_RE, '\\$&')
}

function isSpace (code) {
  switch (code) {
    case 0x09:
    case 0x20:
      return true
  }
  return false
}

// Zs (unicode class) || [\t\f\v\r\n]
function isWhiteSpace (code) {
  if (code >= 0x2000 && code <= 0x200A) { return true }
  switch (code) {
    case 0x09: // \t
    case 0x0A: // \n
    case 0x0B: // \v
    case 0x0C: // \f
    case 0x0D: // \r
    case 0x20:
    case 0xA0:
    case 0x1680:
    case 0x202F:
    case 0x205F:
    case 0x3000:
      return true
  }
  return false
}

/* eslint-disable max-len */

// Currently without astral characters support.
function isPunctChar (ch) {
  return P.test(ch) || regex.test(ch)
}

// Markdown ASCII punctuation characters.
//
// !, ", #, $, %, &, ', (, ), *, +, ,, -, ., /, :, ;, <, =, >, ?, @, [, \, ], ^, _, `, {, |, }, or ~
// http://spec.commonmark.org/0.15/#ascii-punctuation-character
//
// Don't confuse with unicode punctuation !!! It lacks some chars in ascii range.
//
function isMdAsciiPunct (ch) {
  switch (ch) {
    case 0x21/* ! */:
    case 0x22/* " */:
    case 0x23/* # */:
    case 0x24/* $ */:
    case 0x25/* % */:
    case 0x26/* & */:
    case 0x27/* ' */:
    case 0x28/* ( */:
    case 0x29/* ) */:
    case 0x2A/* * */:
    case 0x2B/* + */:
    case 0x2C/* , */:
    case 0x2D/* - */:
    case 0x2E/* . */:
    case 0x2F/* / */:
    case 0x3A/* : */:
    case 0x3B/* ; */:
    case 0x3C/* < */:
    case 0x3D/* = */:
    case 0x3E/* > */:
    case 0x3F/* ? */:
    case 0x40/* @ */:
    case 0x5B/* [ */:
    case 0x5C/* \ */:
    case 0x5D/* ] */:
    case 0x5E/* ^ */:
    case 0x5F/* _ */:
    case 0x60/* ` */:
    case 0x7B/* { */:
    case 0x7C/* | */:
    case 0x7D/* } */:
    case 0x7E/* ~ */:
      return true
    default:
      return false
  }
}

// Hepler to unify [reference labels].
//
function normalizeReference (str) {
  // Trim and collapse whitespace
  //
  str = str.trim().replace(/\s+/g, ' ');

  // In node v10 'ẞ'.toLowerCase() === 'Ṿ', which is presumed to be a bug
  // fixed in v12 (couldn't find any details).
  //
  // So treat this one as a special case
  // (remove this when node v10 is no longer supported).
  //
  if ('ẞ'.toLowerCase() === 'Ṿ') {
    str = str.replace(/ẞ/g, 'ß');
  }

  // .toLowerCase().toUpperCase() should get rid of all differences
  // between letter variants.
  //
  // Simple .toLowerCase() doesn't normalize 125 code points correctly,
  // and .toUpperCase doesn't normalize 6 of them (list of exceptions:
  // İ, ϴ, ẞ, Ω, K, Å - those are already uppercased, but have differently
  // uppercased versions).
  //
  // Here's an example showing how it happens. Lets take greek letter omega:
  // uppercase U+0398 (Θ), U+03f4 (ϴ) and lowercase U+03b8 (θ), U+03d1 (ϑ)
  //
  // Unicode entries:
  // 0398;GREEK CAPITAL LETTER THETA;Lu;0;L;;;;;N;;;;03B8;
  // 03B8;GREEK SMALL LETTER THETA;Ll;0;L;;;;;N;;;0398;;0398
  // 03D1;GREEK THETA SYMBOL;Ll;0;L;<compat> 03B8;;;;N;GREEK SMALL LETTER SCRIPT THETA;;0398;;0398
  // 03F4;GREEK CAPITAL THETA SYMBOL;Lu;0;L;<compat> 0398;;;;N;;;;03B8;
  //
  // Case-insensitive comparison should treat all of them as equivalent.
  //
  // But .toLowerCase() doesn't change ϑ (it's already lowercase),
  // and .toUpperCase() doesn't change ϴ (already uppercase).
  //
  // Applying first lower then upper case normalizes any character:
  // '\u0398\u03f4\u03b8\u03d1'.toLowerCase().toUpperCase() === '\u0398\u0398\u0398\u0398'
  //
  // Note: this is equivalent to unicode case folding; unicode normalization
  // is a different step that is not required here.
  //
  // Final result should be uppercased, because it's later stored in an object
  // (this avoid a conflict with Object.prototype members,
  // most notably, `__proto__`)
  //
  return str.toLowerCase().toUpperCase()
}

// Re-export libraries commonly used in both markdown-it and its plugins,
// so plugins won't have to depend on them explicitly, which reduces their
// bundled size (e.g. a browser build).
//
const lib = { mdurl, ucmicro };

var utils$2 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  arrayReplaceAt: arrayReplaceAt,
  assign: assign$1,
  escapeHtml: escapeHtml,
  escapeRE: escapeRE$1,
  fromCodePoint: fromCodePoint,
  has: has,
  isMdAsciiPunct: isMdAsciiPunct,
  isPunctChar: isPunctChar,
  isSpace: isSpace,
  isString: isString$1,
  isValidEntityCode: isValidEntityCode,
  isWhiteSpace: isWhiteSpace,
  lib: lib,
  normalizeReference: normalizeReference,
  unescapeAll: unescapeAll,
  unescapeMd: unescapeMd
});

// Parse link label
//
// this function assumes that first character ("[") already matches;
// returns the end of the label
//

function parseLinkLabel (state, start, disableNested) {
  let level, found, marker, prevPos;

  const max = state.posMax;
  const oldPos = state.pos;

  state.pos = start + 1;
  level = 1;

  while (state.pos < max) {
    marker = state.src.charCodeAt(state.pos);
    if (marker === 0x5D /* ] */) {
      level--;
      if (level === 0) {
        found = true;
        break
      }
    }

    prevPos = state.pos;
    state.md.inline.skipToken(state);
    if (marker === 0x5B /* [ */) {
      if (prevPos === state.pos - 1) {
        // increase level if we find text `[`, which is not a part of any token
        level++;
      } else if (disableNested) {
        state.pos = oldPos;
        return -1
      }
    }
  }

  let labelEnd = -1;

  if (found) {
    labelEnd = state.pos;
  }

  // restore old state
  state.pos = oldPos;

  return labelEnd
}

// Parse link destination
//


function parseLinkDestination (str, start, max) {
  let code;
  let pos = start;

  const result = {
    ok: false,
    pos: 0,
    str: ''
  };

  if (str.charCodeAt(pos) === 0x3C /* < */) {
    pos++;
    while (pos < max) {
      code = str.charCodeAt(pos);
      if (code === 0x0A /* \n */) { return result }
      if (code === 0x3C /* < */) { return result }
      if (code === 0x3E /* > */) {
        result.pos = pos + 1;
        result.str = unescapeAll(str.slice(start + 1, pos));
        result.ok = true;
        return result
      }
      if (code === 0x5C /* \ */ && pos + 1 < max) {
        pos += 2;
        continue
      }

      pos++;
    }

    // no closing '>'
    return result
  }

  // this should be ... } else { ... branch

  let level = 0;
  while (pos < max) {
    code = str.charCodeAt(pos);

    if (code === 0x20) { break }

    // ascii control characters
    if (code < 0x20 || code === 0x7F) { break }

    if (code === 0x5C /* \ */ && pos + 1 < max) {
      if (str.charCodeAt(pos + 1) === 0x20) { break }
      pos += 2;
      continue
    }

    if (code === 0x28 /* ( */) {
      level++;
      if (level > 32) { return result }
    }

    if (code === 0x29 /* ) */) {
      if (level === 0) { break }
      level--;
    }

    pos++;
  }

  if (start === pos) { return result }
  if (level !== 0) { return result }

  result.str = unescapeAll(str.slice(start, pos));
  result.pos = pos;
  result.ok = true;
  return result
}

// Parse link title
//


// Parse link title within `str` in [start, max] range,
// or continue previous parsing if `prev_state` is defined (equal to result of last execution).
//
function parseLinkTitle (str, start, max, prev_state) {
  let code;
  let pos = start;

  const state = {
    // if `true`, this is a valid link title
    ok: false,
    // if `true`, this link can be continued on the next line
    can_continue: false,
    // if `ok`, it's the position of the first character after the closing marker
    pos: 0,
    // if `ok`, it's the unescaped title
    str: '',
    // expected closing marker character code
    marker: 0
  };

  if (prev_state) {
    // this is a continuation of a previous parseLinkTitle call on the next line,
    // used in reference links only
    state.str = prev_state.str;
    state.marker = prev_state.marker;
  } else {
    if (pos >= max) { return state }

    let marker = str.charCodeAt(pos);
    if (marker !== 0x22 /* " */ && marker !== 0x27 /* ' */ && marker !== 0x28 /* ( */) { return state }

    start++;
    pos++;

    // if opening marker is "(", switch it to closing marker ")"
    if (marker === 0x28) { marker = 0x29; }

    state.marker = marker;
  }

  while (pos < max) {
    code = str.charCodeAt(pos);
    if (code === state.marker) {
      state.pos = pos + 1;
      state.str += unescapeAll(str.slice(start, pos));
      state.ok = true;
      return state
    } else if (code === 0x28 /* ( */ && state.marker === 0x29 /* ) */) {
      return state
    } else if (code === 0x5C /* \ */ && pos + 1 < max) {
      pos++;
    }

    pos++;
  }

  // no closing marker found, but this link title may continue on the next line (for references)
  state.can_continue = true;
  state.str += unescapeAll(str.slice(start, pos));
  return state
}

// Just a shortcut for bulk export

var helpers = /*#__PURE__*/Object.freeze({
  __proto__: null,
  parseLinkDestination: parseLinkDestination,
  parseLinkLabel: parseLinkLabel,
  parseLinkTitle: parseLinkTitle
});

/**
 * class Renderer
 *
 * Generates HTML from parsed token stream. Each instance has independent
 * copy of rules. Those can be rewritten with ease. Also, you can add new
 * rules if you create plugin and adds new token types.
 **/


const default_rules = {};

default_rules.code_inline = function (tokens, idx, options, env, slf) {
  const token = tokens[idx];

  return  '<code' + slf.renderAttrs(token) + '>' +
          escapeHtml(token.content) +
          '</code>'
};

default_rules.code_block = function (tokens, idx, options, env, slf) {
  const token = tokens[idx];

  return  '<pre' + slf.renderAttrs(token) + '><code>' +
          escapeHtml(tokens[idx].content) +
          '</code></pre>\n'
};

default_rules.fence = function (tokens, idx, options, env, slf) {
  const token = tokens[idx];
  const info = token.info ? unescapeAll(token.info).trim() : '';
  let langName = '';
  let langAttrs = '';

  if (info) {
    const arr = info.split(/(\s+)/g);
    langName = arr[0];
    langAttrs = arr.slice(2).join('');
  }

  let highlighted;
  if (options.highlight) {
    highlighted = options.highlight(token.content, langName, langAttrs) || escapeHtml(token.content);
  } else {
    highlighted = escapeHtml(token.content);
  }

  if (highlighted.indexOf('<pre') === 0) {
    return highlighted + '\n'
  }

  // If language exists, inject class gently, without modifying original token.
  // May be, one day we will add .deepClone() for token and simplify this part, but
  // now we prefer to keep things local.
  if (info) {
    const i = token.attrIndex('class');
    const tmpAttrs = token.attrs ? token.attrs.slice() : [];

    if (i < 0) {
      tmpAttrs.push(['class', options.langPrefix + langName]);
    } else {
      tmpAttrs[i] = tmpAttrs[i].slice();
      tmpAttrs[i][1] += ' ' + options.langPrefix + langName;
    }

    // Fake token just to render attributes
    const tmpToken = {
      attrs: tmpAttrs
    };

    return `<pre><code${slf.renderAttrs(tmpToken)}>${highlighted}</code></pre>\n`
  }

  return `<pre><code${slf.renderAttrs(token)}>${highlighted}</code></pre>\n`
};

default_rules.image = function (tokens, idx, options, env, slf) {
  const token = tokens[idx];

  // "alt" attr MUST be set, even if empty. Because it's mandatory and
  // should be placed on proper position for tests.
  //
  // Replace content with actual value

  token.attrs[token.attrIndex('alt')][1] =
    slf.renderInlineAsText(token.children, options, env);

  return slf.renderToken(tokens, idx, options)
};

default_rules.hardbreak = function (tokens, idx, options /*, env */) {
  return options.xhtmlOut ? '<br />\n' : '<br>\n'
};
default_rules.softbreak = function (tokens, idx, options /*, env */) {
  return options.breaks ? (options.xhtmlOut ? '<br />\n' : '<br>\n') : '\n'
};

default_rules.text = function (tokens, idx /*, options, env */) {
  return escapeHtml(tokens[idx].content)
};

default_rules.html_block = function (tokens, idx /*, options, env */) {
  return tokens[idx].content
};
default_rules.html_inline = function (tokens, idx /*, options, env */) {
  return tokens[idx].content
};

/**
 * new Renderer()
 *
 * Creates new [[Renderer]] instance and fill [[Renderer#rules]] with defaults.
 **/
function Renderer () {
  /**
   * Renderer#rules -> Object
   *
   * Contains render rules for tokens. Can be updated and extended.
   *
   * ##### Example
   *
   * ```javascript
   * var md = require('markdown-it')();
   *
   * md.renderer.rules.strong_open  = function () { return '<b>'; };
   * md.renderer.rules.strong_close = function () { return '</b>'; };
   *
   * var result = md.renderInline(...);
   * ```
   *
   * Each rule is called as independent static function with fixed signature:
   *
   * ```javascript
   * function my_token_render(tokens, idx, options, env, renderer) {
   *   // ...
   *   return renderedHTML;
   * }
   * ```
   *
   * See [source code](https://github.com/markdown-it/markdown-it/blob/master/lib/renderer.mjs)
   * for more details and examples.
   **/
  this.rules = assign$1({}, default_rules);
}

/**
 * Renderer.renderAttrs(token) -> String
 *
 * Render token attributes to string.
 **/
Renderer.prototype.renderAttrs = function renderAttrs (token) {
  let i, l, result;

  if (!token.attrs) { return '' }

  result = '';

  for (i = 0, l = token.attrs.length; i < l; i++) {
    result += ' ' + escapeHtml(token.attrs[i][0]) + '="' + escapeHtml(token.attrs[i][1]) + '"';
  }

  return result
};

/**
 * Renderer.renderToken(tokens, idx, options) -> String
 * - tokens (Array): list of tokens
 * - idx (Numbed): token index to render
 * - options (Object): params of parser instance
 *
 * Default token renderer. Can be overriden by custom function
 * in [[Renderer#rules]].
 **/
Renderer.prototype.renderToken = function renderToken (tokens, idx, options) {
  const token = tokens[idx];
  let result = '';

  // Tight list paragraphs
  if (token.hidden) {
    return ''
  }

  // Insert a newline between hidden paragraph and subsequent opening
  // block-level tag.
  //
  // For example, here we should insert a newline before blockquote:
  //  - a
  //    >
  //
  if (token.block && token.nesting !== -1 && idx && tokens[idx - 1].hidden) {
    result += '\n';
  }

  // Add token name, e.g. `<img`
  result += (token.nesting === -1 ? '</' : '<') + token.tag;

  // Encode attributes, e.g. `<img src="foo"`
  result += this.renderAttrs(token);

  // Add a slash for self-closing tags, e.g. `<img src="foo" /`
  if (token.nesting === 0 && options.xhtmlOut) {
    result += ' /';
  }

  // Check if we need to add a newline after this tag
  let needLf = false;
  if (token.block) {
    needLf = true;

    if (token.nesting === 1) {
      if (idx + 1 < tokens.length) {
        const nextToken = tokens[idx + 1];

        if (nextToken.type === 'inline' || nextToken.hidden) {
          // Block-level tag containing an inline tag.
          //
          needLf = false;
        } else if (nextToken.nesting === -1 && nextToken.tag === token.tag) {
          // Opening tag + closing tag of the same type. E.g. `<li></li>`.
          //
          needLf = false;
        }
      }
    }
  }

  result += needLf ? '>\n' : '>';

  return result
};

/**
 * Renderer.renderInline(tokens, options, env) -> String
 * - tokens (Array): list on block tokens to render
 * - options (Object): params of parser instance
 * - env (Object): additional data from parsed input (references, for example)
 *
 * The same as [[Renderer.render]], but for single token of `inline` type.
 **/
Renderer.prototype.renderInline = function (tokens, options, env) {
  let result = '';
  const rules = this.rules;

  for (let i = 0, len = tokens.length; i < len; i++) {
    const type = tokens[i].type;

    if (typeof rules[type] !== 'undefined') {
      result += rules[type](tokens, i, options, env, this);
    } else {
      result += this.renderToken(tokens, i, options);
    }
  }

  return result
};

/** internal
 * Renderer.renderInlineAsText(tokens, options, env) -> String
 * - tokens (Array): list on block tokens to render
 * - options (Object): params of parser instance
 * - env (Object): additional data from parsed input (references, for example)
 *
 * Special kludge for image `alt` attributes to conform CommonMark spec.
 * Don't try to use it! Spec requires to show `alt` content with stripped markup,
 * instead of simple escaping.
 **/
Renderer.prototype.renderInlineAsText = function (tokens, options, env) {
  let result = '';

  for (let i = 0, len = tokens.length; i < len; i++) {
    switch (tokens[i].type) {
      case 'text':
        result += tokens[i].content;
        break
      case 'image':
        result += this.renderInlineAsText(tokens[i].children, options, env);
        break
      case 'html_inline':
      case 'html_block':
        result += tokens[i].content;
        break
      case 'softbreak':
      case 'hardbreak':
        result += '\n';
        break
        // all other tokens are skipped
    }
  }

  return result
};

/**
 * Renderer.render(tokens, options, env) -> String
 * - tokens (Array): list on block tokens to render
 * - options (Object): params of parser instance
 * - env (Object): additional data from parsed input (references, for example)
 *
 * Takes token stream and generates HTML. Probably, you will never need to call
 * this method directly.
 **/
Renderer.prototype.render = function (tokens, options, env) {
  let result = '';
  const rules = this.rules;

  for (let i = 0, len = tokens.length; i < len; i++) {
    const type = tokens[i].type;

    if (type === 'inline') {
      result += this.renderInline(tokens[i].children, options, env);
    } else if (typeof rules[type] !== 'undefined') {
      result += rules[type](tokens, i, options, env, this);
    } else {
      result += this.renderToken(tokens, i, options, env);
    }
  }

  return result
};

/**
 * class Ruler
 *
 * Helper class, used by [[MarkdownIt#core]], [[MarkdownIt#block]] and
 * [[MarkdownIt#inline]] to manage sequences of functions (rules):
 *
 * - keep rules in defined order
 * - assign the name to each rule
 * - enable/disable rules
 * - add/replace rules
 * - allow assign rules to additional named chains (in the same)
 * - cacheing lists of active rules
 *
 * You will not need use this class directly until write plugins. For simple
 * rules control use [[MarkdownIt.disable]], [[MarkdownIt.enable]] and
 * [[MarkdownIt.use]].
 **/

/**
 * new Ruler()
 **/
function Ruler () {
  // List of added rules. Each element is:
  //
  // {
  //   name: XXX,
  //   enabled: Boolean,
  //   fn: Function(),
  //   alt: [ name2, name3 ]
  // }
  //
  this.__rules__ = [];

  // Cached rule chains.
  //
  // First level - chain name, '' for default.
  // Second level - diginal anchor for fast filtering by charcodes.
  //
  this.__cache__ = null;
}

// Helper methods, should not be used directly

// Find rule index by name
//
Ruler.prototype.__find__ = function (name) {
  for (let i = 0; i < this.__rules__.length; i++) {
    if (this.__rules__[i].name === name) {
      return i
    }
  }
  return -1
};

// Build rules lookup cache
//
Ruler.prototype.__compile__ = function () {
  const self = this;
  const chains = [''];

  // collect unique names
  self.__rules__.forEach(function (rule) {
    if (!rule.enabled) { return }

    rule.alt.forEach(function (altName) {
      if (chains.indexOf(altName) < 0) {
        chains.push(altName);
      }
    });
  });

  self.__cache__ = {};

  chains.forEach(function (chain) {
    self.__cache__[chain] = [];
    self.__rules__.forEach(function (rule) {
      if (!rule.enabled) { return }

      if (chain && rule.alt.indexOf(chain) < 0) { return }

      self.__cache__[chain].push(rule.fn);
    });
  });
};

/**
 * Ruler.at(name, fn [, options])
 * - name (String): rule name to replace.
 * - fn (Function): new rule function.
 * - options (Object): new rule options (not mandatory).
 *
 * Replace rule by name with new function & options. Throws error if name not
 * found.
 *
 * ##### Options:
 *
 * - __alt__ - array with names of "alternate" chains.
 *
 * ##### Example
 *
 * Replace existing typographer replacement rule with new one:
 *
 * ```javascript
 * var md = require('markdown-it')();
 *
 * md.core.ruler.at('replacements', function replace(state) {
 *   //...
 * });
 * ```
 **/
Ruler.prototype.at = function (name, fn, options) {
  const index = this.__find__(name);
  const opt = options || {};

  if (index === -1) { throw new Error('Parser rule not found: ' + name) }

  this.__rules__[index].fn = fn;
  this.__rules__[index].alt = opt.alt || [];
  this.__cache__ = null;
};

/**
 * Ruler.before(beforeName, ruleName, fn [, options])
 * - beforeName (String): new rule will be added before this one.
 * - ruleName (String): name of added rule.
 * - fn (Function): rule function.
 * - options (Object): rule options (not mandatory).
 *
 * Add new rule to chain before one with given name. See also
 * [[Ruler.after]], [[Ruler.push]].
 *
 * ##### Options:
 *
 * - __alt__ - array with names of "alternate" chains.
 *
 * ##### Example
 *
 * ```javascript
 * var md = require('markdown-it')();
 *
 * md.block.ruler.before('paragraph', 'my_rule', function replace(state) {
 *   //...
 * });
 * ```
 **/
Ruler.prototype.before = function (beforeName, ruleName, fn, options) {
  const index = this.__find__(beforeName);
  const opt = options || {};

  if (index === -1) { throw new Error('Parser rule not found: ' + beforeName) }

  this.__rules__.splice(index, 0, {
    name: ruleName,
    enabled: true,
    fn,
    alt: opt.alt || []
  });

  this.__cache__ = null;
};

/**
 * Ruler.after(afterName, ruleName, fn [, options])
 * - afterName (String): new rule will be added after this one.
 * - ruleName (String): name of added rule.
 * - fn (Function): rule function.
 * - options (Object): rule options (not mandatory).
 *
 * Add new rule to chain after one with given name. See also
 * [[Ruler.before]], [[Ruler.push]].
 *
 * ##### Options:
 *
 * - __alt__ - array with names of "alternate" chains.
 *
 * ##### Example
 *
 * ```javascript
 * var md = require('markdown-it')();
 *
 * md.inline.ruler.after('text', 'my_rule', function replace(state) {
 *   //...
 * });
 * ```
 **/
Ruler.prototype.after = function (afterName, ruleName, fn, options) {
  const index = this.__find__(afterName);
  const opt = options || {};

  if (index === -1) { throw new Error('Parser rule not found: ' + afterName) }

  this.__rules__.splice(index + 1, 0, {
    name: ruleName,
    enabled: true,
    fn,
    alt: opt.alt || []
  });

  this.__cache__ = null;
};

/**
 * Ruler.push(ruleName, fn [, options])
 * - ruleName (String): name of added rule.
 * - fn (Function): rule function.
 * - options (Object): rule options (not mandatory).
 *
 * Push new rule to the end of chain. See also
 * [[Ruler.before]], [[Ruler.after]].
 *
 * ##### Options:
 *
 * - __alt__ - array with names of "alternate" chains.
 *
 * ##### Example
 *
 * ```javascript
 * var md = require('markdown-it')();
 *
 * md.core.ruler.push('my_rule', function replace(state) {
 *   //...
 * });
 * ```
 **/
Ruler.prototype.push = function (ruleName, fn, options) {
  const opt = options || {};

  this.__rules__.push({
    name: ruleName,
    enabled: true,
    fn,
    alt: opt.alt || []
  });

  this.__cache__ = null;
};

/**
 * Ruler.enable(list [, ignoreInvalid]) -> Array
 * - list (String|Array): list of rule names to enable.
 * - ignoreInvalid (Boolean): set `true` to ignore errors when rule not found.
 *
 * Enable rules with given names. If any rule name not found - throw Error.
 * Errors can be disabled by second param.
 *
 * Returns list of found rule names (if no exception happened).
 *
 * See also [[Ruler.disable]], [[Ruler.enableOnly]].
 **/
Ruler.prototype.enable = function (list, ignoreInvalid) {
  if (!Array.isArray(list)) { list = [list]; }

  const result = [];

  // Search by name and enable
  list.forEach(function (name) {
    const idx = this.__find__(name);

    if (idx < 0) {
      if (ignoreInvalid) { return }
      throw new Error('Rules manager: invalid rule name ' + name)
    }
    this.__rules__[idx].enabled = true;
    result.push(name);
  }, this);

  this.__cache__ = null;
  return result
};

/**
 * Ruler.enableOnly(list [, ignoreInvalid])
 * - list (String|Array): list of rule names to enable (whitelist).
 * - ignoreInvalid (Boolean): set `true` to ignore errors when rule not found.
 *
 * Enable rules with given names, and disable everything else. If any rule name
 * not found - throw Error. Errors can be disabled by second param.
 *
 * See also [[Ruler.disable]], [[Ruler.enable]].
 **/
Ruler.prototype.enableOnly = function (list, ignoreInvalid) {
  if (!Array.isArray(list)) { list = [list]; }

  this.__rules__.forEach(function (rule) { rule.enabled = false; });

  this.enable(list, ignoreInvalid);
};

/**
 * Ruler.disable(list [, ignoreInvalid]) -> Array
 * - list (String|Array): list of rule names to disable.
 * - ignoreInvalid (Boolean): set `true` to ignore errors when rule not found.
 *
 * Disable rules with given names. If any rule name not found - throw Error.
 * Errors can be disabled by second param.
 *
 * Returns list of found rule names (if no exception happened).
 *
 * See also [[Ruler.enable]], [[Ruler.enableOnly]].
 **/
Ruler.prototype.disable = function (list, ignoreInvalid) {
  if (!Array.isArray(list)) { list = [list]; }

  const result = [];

  // Search by name and disable
  list.forEach(function (name) {
    const idx = this.__find__(name);

    if (idx < 0) {
      if (ignoreInvalid) { return }
      throw new Error('Rules manager: invalid rule name ' + name)
    }
    this.__rules__[idx].enabled = false;
    result.push(name);
  }, this);

  this.__cache__ = null;
  return result
};

/**
 * Ruler.getRules(chainName) -> Array
 *
 * Return array of active functions (rules) for given chain name. It analyzes
 * rules configuration, compiles caches if not exists and returns result.
 *
 * Default chain name is `''` (empty string). It can't be skipped. That's
 * done intentionally, to keep signature monomorphic for high speed.
 **/
Ruler.prototype.getRules = function (chainName) {
  if (this.__cache__ === null) {
    this.__compile__();
  }

  // Chain can be empty, if rules disabled. But we still have to return Array.
  return this.__cache__[chainName] || []
};

// Token class

/**
 * class Token
 **/

/**
 * new Token(type, tag, nesting)
 *
 * Create new token and fill passed properties.
 **/
function Token (type, tag, nesting) {
  /**
   * Token#type -> String
   *
   * Type of the token (string, e.g. "paragraph_open")
   **/
  this.type     = type;

  /**
   * Token#tag -> String
   *
   * html tag name, e.g. "p"
   **/
  this.tag      = tag;

  /**
   * Token#attrs -> Array
   *
   * Html attributes. Format: `[ [ name1, value1 ], [ name2, value2 ] ]`
   **/
  this.attrs    = null;

  /**
   * Token#map -> Array
   *
   * Source map info. Format: `[ line_begin, line_end ]`
   **/
  this.map      = null;

  /**
   * Token#nesting -> Number
   *
   * Level change (number in {-1, 0, 1} set), where:
   *
   * -  `1` means the tag is opening
   * -  `0` means the tag is self-closing
   * - `-1` means the tag is closing
   **/
  this.nesting  = nesting;

  /**
   * Token#level -> Number
   *
   * nesting level, the same as `state.level`
   **/
  this.level    = 0;

  /**
   * Token#children -> Array
   *
   * An array of child nodes (inline and img tokens)
   **/
  this.children = null;

  /**
   * Token#content -> String
   *
   * In a case of self-closing tag (code, html, fence, etc.),
   * it has contents of this tag.
   **/
  this.content  = '';

  /**
   * Token#markup -> String
   *
   * '*' or '_' for emphasis, fence string for fence, etc.
   **/
  this.markup   = '';

  /**
   * Token#info -> String
   *
   * Additional information:
   *
   * - Info string for "fence" tokens
   * - The value "auto" for autolink "link_open" and "link_close" tokens
   * - The string value of the item marker for ordered-list "list_item_open" tokens
   **/
  this.info     = '';

  /**
   * Token#meta -> Object
   *
   * A place for plugins to store an arbitrary data
   **/
  this.meta     = null;

  /**
   * Token#block -> Boolean
   *
   * True for block-level tokens, false for inline tokens.
   * Used in renderer to calculate line breaks
   **/
  this.block    = false;

  /**
   * Token#hidden -> Boolean
   *
   * If it's true, ignore this element when rendering. Used for tight lists
   * to hide paragraphs.
   **/
  this.hidden   = false;
}

/**
 * Token.attrIndex(name) -> Number
 *
 * Search attribute index by name.
 **/
Token.prototype.attrIndex = function attrIndex (name) {
  if (!this.attrs) { return -1 }

  const attrs = this.attrs;

  for (let i = 0, len = attrs.length; i < len; i++) {
    if (attrs[i][0] === name) { return i }
  }
  return -1
};

/**
 * Token.attrPush(attrData)
 *
 * Add `[ name, value ]` attribute to list. Init attrs if necessary
 **/
Token.prototype.attrPush = function attrPush (attrData) {
  if (this.attrs) {
    this.attrs.push(attrData);
  } else {
    this.attrs = [attrData];
  }
};

/**
 * Token.attrSet(name, value)
 *
 * Set `name` attribute to `value`. Override old value if exists.
 **/
Token.prototype.attrSet = function attrSet (name, value) {
  const idx = this.attrIndex(name);
  const attrData = [name, value];

  if (idx < 0) {
    this.attrPush(attrData);
  } else {
    this.attrs[idx] = attrData;
  }
};

/**
 * Token.attrGet(name)
 *
 * Get the value of attribute `name`, or null if it does not exist.
 **/
Token.prototype.attrGet = function attrGet (name) {
  const idx = this.attrIndex(name);
  let value = null;
  if (idx >= 0) {
    value = this.attrs[idx][1];
  }
  return value
};

/**
 * Token.attrJoin(name, value)
 *
 * Join value to existing attribute via space. Or create new attribute if not
 * exists. Useful to operate with token classes.
 **/
Token.prototype.attrJoin = function attrJoin (name, value) {
  const idx = this.attrIndex(name);

  if (idx < 0) {
    this.attrPush([name, value]);
  } else {
    this.attrs[idx][1] = this.attrs[idx][1] + ' ' + value;
  }
};

// Core state object
//


function StateCore (src, md, env) {
  this.src = src;
  this.env = env;
  this.tokens = [];
  this.inlineMode = false;
  this.md = md; // link to parser instance
}

// re-export Token class to use in core rules
StateCore.prototype.Token = Token;

// Normalize input string

// https://spec.commonmark.org/0.29/#line-ending
const NEWLINES_RE  = /\r\n?|\n/g;
const NULL_RE      = /\0/g;

function normalize (state) {
  let str;

  // Normalize newlines
  str = state.src.replace(NEWLINES_RE, '\n');

  // Replace NULL characters
  str = str.replace(NULL_RE, '\uFFFD');

  state.src = str;
}

function block (state) {
  let token;

  if (state.inlineMode) {
    token          = new state.Token('inline', '', 0);
    token.content  = state.src;
    token.map      = [0, 1];
    token.children = [];
    state.tokens.push(token);
  } else {
    state.md.block.parse(state.src, state.md, state.env, state.tokens);
  }
}

function inline (state) {
  const tokens = state.tokens;

  // Parse inlines
  for (let i = 0, l = tokens.length; i < l; i++) {
    const tok = tokens[i];
    if (tok.type === 'inline') {
      state.md.inline.parse(tok.content, state.md, state.env, tok.children);
    }
  }
}

// Replace link-like texts with link nodes.
//
// Currently restricted by `md.validateLink()` to http/https/ftp
//


function isLinkOpen$1 (str) {
  return /^<a[>\s]/i.test(str)
}
function isLinkClose$1 (str) {
  return /^<\/a\s*>/i.test(str)
}

function linkify$1 (state) {
  const blockTokens = state.tokens;

  if (!state.md.options.linkify) { return }

  for (let j = 0, l = blockTokens.length; j < l; j++) {
    if (blockTokens[j].type !== 'inline' ||
        !state.md.linkify.pretest(blockTokens[j].content)) {
      continue
    }

    let tokens = blockTokens[j].children;

    let htmlLinkLevel = 0;

    // We scan from the end, to keep position when new tags added.
    // Use reversed logic in links start/end match
    for (let i = tokens.length - 1; i >= 0; i--) {
      const currentToken = tokens[i];

      // Skip content of markdown links
      if (currentToken.type === 'link_close') {
        i--;
        while (tokens[i].level !== currentToken.level && tokens[i].type !== 'link_open') {
          i--;
        }
        continue
      }

      // Skip content of html tag links
      if (currentToken.type === 'html_inline') {
        if (isLinkOpen$1(currentToken.content) && htmlLinkLevel > 0) {
          htmlLinkLevel--;
        }
        if (isLinkClose$1(currentToken.content)) {
          htmlLinkLevel++;
        }
      }
      if (htmlLinkLevel > 0) { continue }

      if (currentToken.type === 'text' && state.md.linkify.test(currentToken.content)) {
        const text = currentToken.content;
        let links = state.md.linkify.match(text);

        // Now split string to nodes
        const nodes = [];
        let level = currentToken.level;
        let lastPos = 0;

        // forbid escape sequence at the start of the string,
        // this avoids http\://example.com/ from being linkified as
        // http:<a href="//example.com/">//example.com/</a>
        if (links.length > 0 &&
            links[0].index === 0 &&
            i > 0 &&
            tokens[i - 1].type === 'text_special') {
          links = links.slice(1);
        }

        for (let ln = 0; ln < links.length; ln++) {
          const url = links[ln].url;
          const fullUrl = state.md.normalizeLink(url);
          if (!state.md.validateLink(fullUrl)) { continue }

          let urlText = links[ln].text;

          // Linkifier might send raw hostnames like "example.com", where url
          // starts with domain name. So we prepend http:// in those cases,
          // and remove it afterwards.
          //
          if (!links[ln].schema) {
            urlText = state.md.normalizeLinkText('http://' + urlText).replace(/^http:\/\//, '');
          } else if (links[ln].schema === 'mailto:' && !/^mailto:/i.test(urlText)) {
            urlText = state.md.normalizeLinkText('mailto:' + urlText).replace(/^mailto:/, '');
          } else {
            urlText = state.md.normalizeLinkText(urlText);
          }

          const pos = links[ln].index;

          if (pos > lastPos) {
            const token   = new state.Token('text', '', 0);
            token.content = text.slice(lastPos, pos);
            token.level   = level;
            nodes.push(token);
          }

          const token_o   = new state.Token('link_open', 'a', 1);
          token_o.attrs   = [['href', fullUrl]];
          token_o.level   = level++;
          token_o.markup  = 'linkify';
          token_o.info    = 'auto';
          nodes.push(token_o);

          const token_t   = new state.Token('text', '', 0);
          token_t.content = urlText;
          token_t.level   = level;
          nodes.push(token_t);

          const token_c   = new state.Token('link_close', 'a', -1);
          token_c.level   = --level;
          token_c.markup  = 'linkify';
          token_c.info    = 'auto';
          nodes.push(token_c);

          lastPos = links[ln].lastIndex;
        }
        if (lastPos < text.length) {
          const token   = new state.Token('text', '', 0);
          token.content = text.slice(lastPos);
          token.level   = level;
          nodes.push(token);
        }

        // replace current node
        blockTokens[j].children = tokens = arrayReplaceAt(tokens, i, nodes);
      }
    }
  }
}

// Simple typographic replacements
//
// (c) (C) → ©
// (tm) (TM) → ™
// (r) (R) → ®
// +- → ±
// ... → … (also ?.... → ?.., !.... → !..)
// ???????? → ???, !!!!! → !!!, `,,` → `,`
// -- → &ndash;, --- → &mdash;
//

// TODO:
// - fractionals 1/2, 1/4, 3/4 -> ½, ¼, ¾
// - multiplications 2 x 4 -> 2 × 4

const RARE_RE = /\+-|\.\.|\?\?\?\?|!!!!|,,|--/;

// Workaround for phantomjs - need regex without /g flag,
// or root check will fail every second time
const SCOPED_ABBR_TEST_RE = /\((c|tm|r)\)/i;

const SCOPED_ABBR_RE = /\((c|tm|r)\)/ig;
const SCOPED_ABBR = {
  c: '©',
  r: '®',
  tm: '™'
};

function replaceFn (match, name) {
  return SCOPED_ABBR[name.toLowerCase()]
}

function replace_scoped (inlineTokens) {
  let inside_autolink = 0;

  for (let i = inlineTokens.length - 1; i >= 0; i--) {
    const token = inlineTokens[i];

    if (token.type === 'text' && !inside_autolink) {
      token.content = token.content.replace(SCOPED_ABBR_RE, replaceFn);
    }

    if (token.type === 'link_open' && token.info === 'auto') {
      inside_autolink--;
    }

    if (token.type === 'link_close' && token.info === 'auto') {
      inside_autolink++;
    }
  }
}

function replace_rare (inlineTokens) {
  let inside_autolink = 0;

  for (let i = inlineTokens.length - 1; i >= 0; i--) {
    const token = inlineTokens[i];

    if (token.type === 'text' && !inside_autolink) {
      if (RARE_RE.test(token.content)) {
        token.content = token.content
          .replace(/\+-/g, '±')
          // .., ..., ....... -> …
          // but ?..... & !..... -> ?.. & !..
          .replace(/\.{2,}/g, '…').replace(/([?!])…/g, '$1..')
          .replace(/([?!]){4,}/g, '$1$1$1').replace(/,{2,}/g, ',')
          // em-dash
          .replace(/(^|[^-])---(?=[^-]|$)/mg, '$1\u2014')
          // en-dash
          .replace(/(^|\s)--(?=\s|$)/mg, '$1\u2013')
          .replace(/(^|[^-\s])--(?=[^-\s]|$)/mg, '$1\u2013');
      }
    }

    if (token.type === 'link_open' && token.info === 'auto') {
      inside_autolink--;
    }

    if (token.type === 'link_close' && token.info === 'auto') {
      inside_autolink++;
    }
  }
}

function replace (state) {
  let blkIdx;

  if (!state.md.options.typographer) { return }

  for (blkIdx = state.tokens.length - 1; blkIdx >= 0; blkIdx--) {
    if (state.tokens[blkIdx].type !== 'inline') { continue }

    if (SCOPED_ABBR_TEST_RE.test(state.tokens[blkIdx].content)) {
      replace_scoped(state.tokens[blkIdx].children);
    }

    if (RARE_RE.test(state.tokens[blkIdx].content)) {
      replace_rare(state.tokens[blkIdx].children);
    }
  }
}

// Convert straight quotation marks to typographic ones
//


const QUOTE_TEST_RE = /['"]/;
const QUOTE_RE = /['"]/g;
const APOSTROPHE = '\u2019'; /* ’ */

function replaceAt (str, index, ch) {
  return str.slice(0, index) + ch + str.slice(index + 1)
}

function process_inlines (tokens, state) {
  let j;

  const stack = [];

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    const thisLevel = tokens[i].level;

    for (j = stack.length - 1; j >= 0; j--) {
      if (stack[j].level <= thisLevel) { break }
    }
    stack.length = j + 1;

    if (token.type !== 'text') { continue }

    let text = token.content;
    let pos = 0;
    let max = text.length;

    /* eslint no-labels:0,block-scoped-var:0 */
    OUTER:
    while (pos < max) {
      QUOTE_RE.lastIndex = pos;
      const t = QUOTE_RE.exec(text);
      if (!t) { break }

      let canOpen = true;
      let canClose = true;
      pos = t.index + 1;
      const isSingle = (t[0] === "'");

      // Find previous character,
      // default to space if it's the beginning of the line
      //
      let lastChar = 0x20;

      if (t.index - 1 >= 0) {
        lastChar = text.charCodeAt(t.index - 1);
      } else {
        for (j = i - 1; j >= 0; j--) {
          if (tokens[j].type === 'softbreak' || tokens[j].type === 'hardbreak') break // lastChar defaults to 0x20
          if (!tokens[j].content) continue // should skip all tokens except 'text', 'html_inline' or 'code_inline'

          lastChar = tokens[j].content.charCodeAt(tokens[j].content.length - 1);
          break
        }
      }

      // Find next character,
      // default to space if it's the end of the line
      //
      let nextChar = 0x20;

      if (pos < max) {
        nextChar = text.charCodeAt(pos);
      } else {
        for (j = i + 1; j < tokens.length; j++) {
          if (tokens[j].type === 'softbreak' || tokens[j].type === 'hardbreak') break // nextChar defaults to 0x20
          if (!tokens[j].content) continue // should skip all tokens except 'text', 'html_inline' or 'code_inline'

          nextChar = tokens[j].content.charCodeAt(0);
          break
        }
      }

      const isLastPunctChar = isMdAsciiPunct(lastChar) || isPunctChar(String.fromCharCode(lastChar));
      const isNextPunctChar = isMdAsciiPunct(nextChar) || isPunctChar(String.fromCharCode(nextChar));

      const isLastWhiteSpace = isWhiteSpace(lastChar);
      const isNextWhiteSpace = isWhiteSpace(nextChar);

      if (isNextWhiteSpace) {
        canOpen = false;
      } else if (isNextPunctChar) {
        if (!(isLastWhiteSpace || isLastPunctChar)) {
          canOpen = false;
        }
      }

      if (isLastWhiteSpace) {
        canClose = false;
      } else if (isLastPunctChar) {
        if (!(isNextWhiteSpace || isNextPunctChar)) {
          canClose = false;
        }
      }

      if (nextChar === 0x22 /* " */ && t[0] === '"') {
        if (lastChar >= 0x30 /* 0 */ && lastChar <= 0x39 /* 9 */) {
          // special case: 1"" - count first quote as an inch
          canClose = canOpen = false;
        }
      }

      if (canOpen && canClose) {
        // Replace quotes in the middle of punctuation sequence, but not
        // in the middle of the words, i.e.:
        //
        // 1. foo " bar " baz - not replaced
        // 2. foo-"-bar-"-baz - replaced
        // 3. foo"bar"baz     - not replaced
        //
        canOpen = isLastPunctChar;
        canClose = isNextPunctChar;
      }

      if (!canOpen && !canClose) {
        // middle of word
        if (isSingle) {
          token.content = replaceAt(token.content, t.index, APOSTROPHE);
        }
        continue
      }

      if (canClose) {
        // this could be a closing quote, rewind the stack to get a match
        for (j = stack.length - 1; j >= 0; j--) {
          let item = stack[j];
          if (stack[j].level < thisLevel) { break }
          if (item.single === isSingle && stack[j].level === thisLevel) {
            item = stack[j];

            let openQuote;
            let closeQuote;
            if (isSingle) {
              openQuote = state.md.options.quotes[2];
              closeQuote = state.md.options.quotes[3];
            } else {
              openQuote = state.md.options.quotes[0];
              closeQuote = state.md.options.quotes[1];
            }

            // replace token.content *before* tokens[item.token].content,
            // because, if they are pointing at the same token, replaceAt
            // could mess up indices when quote length != 1
            token.content = replaceAt(token.content, t.index, closeQuote);
            tokens[item.token].content = replaceAt(
              tokens[item.token].content, item.pos, openQuote);

            pos += closeQuote.length - 1;
            if (item.token === i) { pos += openQuote.length - 1; }

            text = token.content;
            max = text.length;

            stack.length = j;
            continue OUTER
          }
        }
      }

      if (canOpen) {
        stack.push({
          token: i,
          pos: t.index,
          single: isSingle,
          level: thisLevel
        });
      } else if (canClose && isSingle) {
        token.content = replaceAt(token.content, t.index, APOSTROPHE);
      }
    }
  }
}

function smartquotes (state) {
  /* eslint max-depth:0 */
  if (!state.md.options.typographer) { return }

  for (let blkIdx = state.tokens.length - 1; blkIdx >= 0; blkIdx--) {
    if (state.tokens[blkIdx].type !== 'inline' ||
        !QUOTE_TEST_RE.test(state.tokens[blkIdx].content)) {
      continue
    }

    process_inlines(state.tokens[blkIdx].children, state);
  }
}

// Join raw text tokens with the rest of the text
//
// This is set as a separate rule to provide an opportunity for plugins
// to run text replacements after text join, but before escape join.
//
// For example, `\:)` shouldn't be replaced with an emoji.
//

function text_join (state) {
  let curr, last;
  const blockTokens = state.tokens;
  const l = blockTokens.length;

  for (let j = 0; j < l; j++) {
    if (blockTokens[j].type !== 'inline') continue

    const tokens = blockTokens[j].children;
    const max = tokens.length;

    for (curr = 0; curr < max; curr++) {
      if (tokens[curr].type === 'text_special') {
        tokens[curr].type = 'text';
      }
    }

    for (curr = last = 0; curr < max; curr++) {
      if (tokens[curr].type === 'text' &&
          curr + 1 < max &&
          tokens[curr + 1].type === 'text') {
        // collapse two adjacent text nodes
        tokens[curr + 1].content = tokens[curr].content + tokens[curr + 1].content;
      } else {
        if (curr !== last) { tokens[last] = tokens[curr]; }

        last++;
      }
    }

    if (curr !== last) {
      tokens.length = last;
    }
  }
}

/** internal
 * class Core
 *
 * Top-level rules executor. Glues block/inline parsers and does intermediate
 * transformations.
 **/


const _rules$2 = [
  ['normalize',      normalize],
  ['block',          block],
  ['inline',         inline],
  ['linkify',        linkify$1],
  ['replacements',   replace],
  ['smartquotes',    smartquotes],
  // `text_join` finds `text_special` tokens (for escape sequences)
  // and joins them with the rest of the text
  ['text_join',      text_join]
];

/**
 * new Core()
 **/
function Core () {
  /**
   * Core#ruler -> Ruler
   *
   * [[Ruler]] instance. Keep configuration of core rules.
   **/
  this.ruler = new Ruler();

  for (let i = 0; i < _rules$2.length; i++) {
    this.ruler.push(_rules$2[i][0], _rules$2[i][1]);
  }
}

/**
 * Core.process(state)
 *
 * Executes core chain rules.
 **/
Core.prototype.process = function (state) {
  const rules = this.ruler.getRules('');

  for (let i = 0, l = rules.length; i < l; i++) {
    rules[i](state);
  }
};

Core.prototype.State = StateCore;

// Parser state class


function StateBlock (src, md, env, tokens) {
  this.src = src;

  // link to parser instance
  this.md     = md;

  this.env = env;

  //
  // Internal state vartiables
  //

  this.tokens = tokens;

  this.bMarks = [];  // line begin offsets for fast jumps
  this.eMarks = [];  // line end offsets for fast jumps
  this.tShift = [];  // offsets of the first non-space characters (tabs not expanded)
  this.sCount = [];  // indents for each line (tabs expanded)

  // An amount of virtual spaces (tabs expanded) between beginning
  // of each line (bMarks) and real beginning of that line.
  //
  // It exists only as a hack because blockquotes override bMarks
  // losing information in the process.
  //
  // It's used only when expanding tabs, you can think about it as
  // an initial tab length, e.g. bsCount=21 applied to string `\t123`
  // means first tab should be expanded to 4-21%4 === 3 spaces.
  //
  this.bsCount = [];

  // block parser variables

  // required block content indent (for example, if we are
  // inside a list, it would be positioned after list marker)
  this.blkIndent  = 0;
  this.line       = 0; // line index in src
  this.lineMax    = 0; // lines count
  this.tight      = false;  // loose/tight mode for lists
  this.ddIndent   = -1; // indent of the current dd block (-1 if there isn't any)
  this.listIndent = -1; // indent of the current list block (-1 if there isn't any)

  // can be 'blockquote', 'list', 'root', 'paragraph' or 'reference'
  // used in lists to determine if they interrupt a paragraph
  this.parentType = 'root';

  this.level = 0;

  // Create caches
  // Generate markers.
  const s = this.src;

  for (let start = 0, pos = 0, indent = 0, offset = 0, len = s.length, indent_found = false; pos < len; pos++) {
    const ch = s.charCodeAt(pos);

    if (!indent_found) {
      if (isSpace(ch)) {
        indent++;

        if (ch === 0x09) {
          offset += 4 - offset % 4;
        } else {
          offset++;
        }
        continue
      } else {
        indent_found = true;
      }
    }

    if (ch === 0x0A || pos === len - 1) {
      if (ch !== 0x0A) { pos++; }
      this.bMarks.push(start);
      this.eMarks.push(pos);
      this.tShift.push(indent);
      this.sCount.push(offset);
      this.bsCount.push(0);

      indent_found = false;
      indent = 0;
      offset = 0;
      start = pos + 1;
    }
  }

  // Push fake entry to simplify cache bounds checks
  this.bMarks.push(s.length);
  this.eMarks.push(s.length);
  this.tShift.push(0);
  this.sCount.push(0);
  this.bsCount.push(0);

  this.lineMax = this.bMarks.length - 1; // don't count last fake line
}

// Push new token to "stream".
//
StateBlock.prototype.push = function (type, tag, nesting) {
  const token = new Token(type, tag, nesting);
  token.block = true;

  if (nesting < 0) this.level--; // closing tag
  token.level = this.level;
  if (nesting > 0) this.level++; // opening tag

  this.tokens.push(token);
  return token
};

StateBlock.prototype.isEmpty = function isEmpty (line) {
  return this.bMarks[line] + this.tShift[line] >= this.eMarks[line]
};

StateBlock.prototype.skipEmptyLines = function skipEmptyLines (from) {
  for (let max = this.lineMax; from < max; from++) {
    if (this.bMarks[from] + this.tShift[from] < this.eMarks[from]) {
      break
    }
  }
  return from
};

// Skip spaces from given position.
StateBlock.prototype.skipSpaces = function skipSpaces (pos) {
  for (let max = this.src.length; pos < max; pos++) {
    const ch = this.src.charCodeAt(pos);
    if (!isSpace(ch)) { break }
  }
  return pos
};

// Skip spaces from given position in reverse.
StateBlock.prototype.skipSpacesBack = function skipSpacesBack (pos, min) {
  if (pos <= min) { return pos }

  while (pos > min) {
    if (!isSpace(this.src.charCodeAt(--pos))) { return pos + 1 }
  }
  return pos
};

// Skip char codes from given position
StateBlock.prototype.skipChars = function skipChars (pos, code) {
  for (let max = this.src.length; pos < max; pos++) {
    if (this.src.charCodeAt(pos) !== code) { break }
  }
  return pos
};

// Skip char codes reverse from given position - 1
StateBlock.prototype.skipCharsBack = function skipCharsBack (pos, code, min) {
  if (pos <= min) { return pos }

  while (pos > min) {
    if (code !== this.src.charCodeAt(--pos)) { return pos + 1 }
  }
  return pos
};

// cut lines range from source.
StateBlock.prototype.getLines = function getLines (begin, end, indent, keepLastLF) {
  if (begin >= end) {
    return ''
  }

  const queue = new Array(end - begin);

  for (let i = 0, line = begin; line < end; line++, i++) {
    let lineIndent = 0;
    const lineStart = this.bMarks[line];
    let first = lineStart;
    let last;

    if (line + 1 < end || keepLastLF) {
      // No need for bounds check because we have fake entry on tail.
      last = this.eMarks[line] + 1;
    } else {
      last = this.eMarks[line];
    }

    while (first < last && lineIndent < indent) {
      const ch = this.src.charCodeAt(first);

      if (isSpace(ch)) {
        if (ch === 0x09) {
          lineIndent += 4 - (lineIndent + this.bsCount[line]) % 4;
        } else {
          lineIndent++;
        }
      } else if (first - lineStart < this.tShift[line]) {
        // patched tShift masked characters to look like spaces (blockquotes, list markers)
        lineIndent++;
      } else {
        break
      }

      first++;
    }

    if (lineIndent > indent) {
      // partially expanding tabs in code blocks, e.g '\t\tfoobar'
      // with indent=2 becomes '  \tfoobar'
      queue[i] = new Array(lineIndent - indent + 1).join(' ') + this.src.slice(first, last);
    } else {
      queue[i] = this.src.slice(first, last);
    }
  }

  return queue.join('')
};

// re-export Token class to use in block rules
StateBlock.prototype.Token = Token;

// GFM table, https://github.github.com/gfm/#tables-extension-


// Limit the amount of empty autocompleted cells in a table,
// see https://github.com/markdown-it/markdown-it/issues/1000,
//
// Both pulldown-cmark and commonmark-hs limit the number of cells this way to ~200k.
// We set it to 65k, which can expand user input by a factor of x370
// (256x256 square is 1.8kB expanded into 650kB).
const MAX_AUTOCOMPLETED_CELLS = 0x10000;

function getLine (state, line) {
  const pos = state.bMarks[line] + state.tShift[line];
  const max = state.eMarks[line];

  return state.src.slice(pos, max)
}

function escapedSplit (str) {
  const result = [];
  const max = str.length;

  let pos = 0;
  let ch = str.charCodeAt(pos);
  let isEscaped = false;
  let lastPos = 0;
  let current = '';

  while (pos < max) {
    if (ch === 0x7c/* | */) {
      if (!isEscaped) {
        // pipe separating cells, '|'
        result.push(current + str.substring(lastPos, pos));
        current = '';
        lastPos = pos + 1;
      } else {
        // escaped pipe, '\|'
        current += str.substring(lastPos, pos - 1);
        lastPos = pos;
      }
    }

    isEscaped = (ch === 0x5c/* \ */);
    pos++;

    ch = str.charCodeAt(pos);
  }

  result.push(current + str.substring(lastPos));

  return result
}

function table (state, startLine, endLine, silent) {
  // should have at least two lines
  if (startLine + 2 > endLine) { return false }

  let nextLine = startLine + 1;

  if (state.sCount[nextLine] < state.blkIndent) { return false }

  // if it's indented more than 3 spaces, it should be a code block
  if (state.sCount[nextLine] - state.blkIndent >= 4) { return false }

  // first character of the second line should be '|', '-', ':',
  // and no other characters are allowed but spaces;
  // basically, this is the equivalent of /^[-:|][-:|\s]*$/ regexp

  let pos = state.bMarks[nextLine] + state.tShift[nextLine];
  if (pos >= state.eMarks[nextLine]) { return false }

  const firstCh = state.src.charCodeAt(pos++);
  if (firstCh !== 0x7C/* | */ && firstCh !== 0x2D/* - */ && firstCh !== 0x3A/* : */) { return false }

  if (pos >= state.eMarks[nextLine]) { return false }

  const secondCh = state.src.charCodeAt(pos++);
  if (secondCh !== 0x7C/* | */ && secondCh !== 0x2D/* - */ && secondCh !== 0x3A/* : */ && !isSpace(secondCh)) {
    return false
  }

  // if first character is '-', then second character must not be a space
  // (due to parsing ambiguity with list)
  if (firstCh === 0x2D/* - */ && isSpace(secondCh)) { return false }

  while (pos < state.eMarks[nextLine]) {
    const ch = state.src.charCodeAt(pos);

    if (ch !== 0x7C/* | */ && ch !== 0x2D/* - */ && ch !== 0x3A/* : */ && !isSpace(ch)) { return false }

    pos++;
  }

  let lineText = getLine(state, startLine + 1);
  let columns = lineText.split('|');
  const aligns = [];
  for (let i = 0; i < columns.length; i++) {
    const t = columns[i].trim();
    if (!t) {
      // allow empty columns before and after table, but not in between columns;
      // e.g. allow ` |---| `, disallow ` ---||--- `
      if (i === 0 || i === columns.length - 1) {
        continue
      } else {
        return false
      }
    }

    if (!/^:?-+:?$/.test(t)) { return false }
    if (t.charCodeAt(t.length - 1) === 0x3A/* : */) {
      aligns.push(t.charCodeAt(0) === 0x3A/* : */ ? 'center' : 'right');
    } else if (t.charCodeAt(0) === 0x3A/* : */) {
      aligns.push('left');
    } else {
      aligns.push('');
    }
  }

  lineText = getLine(state, startLine).trim();
  if (lineText.indexOf('|') === -1) { return false }
  if (state.sCount[startLine] - state.blkIndent >= 4) { return false }
  columns = escapedSplit(lineText);
  if (columns.length && columns[0] === '') columns.shift();
  if (columns.length && columns[columns.length - 1] === '') columns.pop();

  // header row will define an amount of columns in the entire table,
  // and align row should be exactly the same (the rest of the rows can differ)
  const columnCount = columns.length;
  if (columnCount === 0 || columnCount !== aligns.length) { return false }

  if (silent) { return true }

  const oldParentType = state.parentType;
  state.parentType = 'table';

  // use 'blockquote' lists for termination because it's
  // the most similar to tables
  const terminatorRules = state.md.block.ruler.getRules('blockquote');

  const token_to = state.push('table_open', 'table', 1);
  const tableLines = [startLine, 0];
  token_to.map = tableLines;

  const token_tho = state.push('thead_open', 'thead', 1);
  token_tho.map = [startLine, startLine + 1];

  const token_htro = state.push('tr_open', 'tr', 1);
  token_htro.map = [startLine, startLine + 1];

  for (let i = 0; i < columns.length; i++) {
    const token_ho = state.push('th_open', 'th', 1);
    if (aligns[i]) {
      token_ho.attrs  = [['style', 'text-align:' + aligns[i]]];
    }

    const token_il = state.push('inline', '', 0);
    token_il.content  = columns[i].trim();
    token_il.children = [];

    state.push('th_close', 'th', -1);
  }

  state.push('tr_close', 'tr', -1);
  state.push('thead_close', 'thead', -1);

  let tbodyLines;
  let autocompletedCells = 0;

  for (nextLine = startLine + 2; nextLine < endLine; nextLine++) {
    if (state.sCount[nextLine] < state.blkIndent) { break }

    let terminate = false;
    for (let i = 0, l = terminatorRules.length; i < l; i++) {
      if (terminatorRules[i](state, nextLine, endLine, true)) {
        terminate = true;
        break
      }
    }

    if (terminate) { break }
    lineText = getLine(state, nextLine).trim();
    if (!lineText) { break }
    if (state.sCount[nextLine] - state.blkIndent >= 4) { break }
    columns = escapedSplit(lineText);
    if (columns.length && columns[0] === '') columns.shift();
    if (columns.length && columns[columns.length - 1] === '') columns.pop();

    // note: autocomplete count can be negative if user specifies more columns than header,
    // but that does not affect intended use (which is limiting expansion)
    autocompletedCells += columnCount - columns.length;
    if (autocompletedCells > MAX_AUTOCOMPLETED_CELLS) { break }

    if (nextLine === startLine + 2) {
      const token_tbo = state.push('tbody_open', 'tbody', 1);
      token_tbo.map = tbodyLines = [startLine + 2, 0];
    }

    const token_tro = state.push('tr_open', 'tr', 1);
    token_tro.map = [nextLine, nextLine + 1];

    for (let i = 0; i < columnCount; i++) {
      const token_tdo = state.push('td_open', 'td', 1);
      if (aligns[i]) {
        token_tdo.attrs  = [['style', 'text-align:' + aligns[i]]];
      }

      const token_il = state.push('inline', '', 0);
      token_il.content  = columns[i] ? columns[i].trim() : '';
      token_il.children = [];

      state.push('td_close', 'td', -1);
    }
    state.push('tr_close', 'tr', -1);
  }

  if (tbodyLines) {
    state.push('tbody_close', 'tbody', -1);
    tbodyLines[1] = nextLine;
  }

  state.push('table_close', 'table', -1);
  tableLines[1] = nextLine;

  state.parentType = oldParentType;
  state.line = nextLine;
  return true
}

// Code block (4 spaces padded)

function code (state, startLine, endLine/*, silent */) {
  if (state.sCount[startLine] - state.blkIndent < 4) { return false }

  let nextLine = startLine + 1;
  let last = nextLine;

  while (nextLine < endLine) {
    if (state.isEmpty(nextLine)) {
      nextLine++;
      continue
    }

    if (state.sCount[nextLine] - state.blkIndent >= 4) {
      nextLine++;
      last = nextLine;
      continue
    }
    break
  }

  state.line = last;

  const token   = state.push('code_block', 'code', 0);
  token.content = state.getLines(startLine, last, 4 + state.blkIndent, false) + '\n';
  token.map     = [startLine, state.line];

  return true
}

// fences (``` lang, ~~~ lang)

function fence (state, startLine, endLine, silent) {
  let pos = state.bMarks[startLine] + state.tShift[startLine];
  let max = state.eMarks[startLine];

  // if it's indented more than 3 spaces, it should be a code block
  if (state.sCount[startLine] - state.blkIndent >= 4) { return false }

  if (pos + 3 > max) { return false }

  const marker = state.src.charCodeAt(pos);

  if (marker !== 0x7E/* ~ */ && marker !== 0x60 /* ` */) {
    return false
  }

  // scan marker length
  let mem = pos;
  pos = state.skipChars(pos, marker);

  let len = pos - mem;

  if (len < 3) { return false }

  const markup = state.src.slice(mem, pos);
  const params = state.src.slice(pos, max);

  if (marker === 0x60 /* ` */) {
    if (params.indexOf(String.fromCharCode(marker)) >= 0) {
      return false
    }
  }

  // Since start is found, we can report success here in validation mode
  if (silent) { return true }

  // search end of block
  let nextLine = startLine;
  let haveEndMarker = false;

  for (;;) {
    nextLine++;
    if (nextLine >= endLine) {
      // unclosed block should be autoclosed by end of document.
      // also block seems to be autoclosed by end of parent
      break
    }

    pos = mem = state.bMarks[nextLine] + state.tShift[nextLine];
    max = state.eMarks[nextLine];

    if (pos < max && state.sCount[nextLine] < state.blkIndent) {
      // non-empty line with negative indent should stop the list:
      // - ```
      //  test
      break
    }

    if (state.src.charCodeAt(pos) !== marker) { continue }

    if (state.sCount[nextLine] - state.blkIndent >= 4) {
      // closing fence should be indented less than 4 spaces
      continue
    }

    pos = state.skipChars(pos, marker);

    // closing code fence must be at least as long as the opening one
    if (pos - mem < len) { continue }

    // make sure tail has spaces only
    pos = state.skipSpaces(pos);

    if (pos < max) { continue }

    haveEndMarker = true;
    // found!
    break
  }

  // If a fence has heading spaces, they should be removed from its inner block
  len = state.sCount[startLine];

  state.line = nextLine + (haveEndMarker ? 1 : 0);

  const token   = state.push('fence', 'code', 0);
  token.info    = params;
  token.content = state.getLines(startLine + 1, nextLine, len, true);
  token.markup  = markup;
  token.map     = [startLine, state.line];

  return true
}

// Block quotes


function blockquote (state, startLine, endLine, silent) {
  let pos = state.bMarks[startLine] + state.tShift[startLine];
  let max = state.eMarks[startLine];

  const oldLineMax = state.lineMax;

  // if it's indented more than 3 spaces, it should be a code block
  if (state.sCount[startLine] - state.blkIndent >= 4) { return false }

  // check the block quote marker
  if (state.src.charCodeAt(pos) !== 0x3E/* > */) { return false }

  // we know that it's going to be a valid blockquote,
  // so no point trying to find the end of it in silent mode
  if (silent) { return true }

  const oldBMarks  = [];
  const oldBSCount = [];
  const oldSCount  = [];
  const oldTShift  = [];

  const terminatorRules = state.md.block.ruler.getRules('blockquote');

  const oldParentType = state.parentType;
  state.parentType = 'blockquote';
  let lastLineEmpty = false;
  let nextLine;

  // Search the end of the block
  //
  // Block ends with either:
  //  1. an empty line outside:
  //     ```
  //     > test
  //
  //     ```
  //  2. an empty line inside:
  //     ```
  //     >
  //     test
  //     ```
  //  3. another tag:
  //     ```
  //     > test
  //      - - -
  //     ```
  for (nextLine = startLine; nextLine < endLine; nextLine++) {
    // check if it's outdented, i.e. it's inside list item and indented
    // less than said list item:
    //
    // ```
    // 1. anything
    //    > current blockquote
    // 2. checking this line
    // ```
    const isOutdented = state.sCount[nextLine] < state.blkIndent;

    pos = state.bMarks[nextLine] + state.tShift[nextLine];
    max = state.eMarks[nextLine];

    if (pos >= max) {
      // Case 1: line is not inside the blockquote, and this line is empty.
      break
    }

    if (state.src.charCodeAt(pos++) === 0x3E/* > */ && !isOutdented) {
      // This line is inside the blockquote.

      // set offset past spaces and ">"
      let initial = state.sCount[nextLine] + 1;
      let spaceAfterMarker;
      let adjustTab;

      // skip one optional space after '>'
      if (state.src.charCodeAt(pos) === 0x20 /* space */) {
        // ' >   test '
        //     ^ -- position start of line here:
        pos++;
        initial++;
        adjustTab = false;
        spaceAfterMarker = true;
      } else if (state.src.charCodeAt(pos) === 0x09 /* tab */) {
        spaceAfterMarker = true;

        if ((state.bsCount[nextLine] + initial) % 4 === 3) {
          // '  >\t  test '
          //       ^ -- position start of line here (tab has width===1)
          pos++;
          initial++;
          adjustTab = false;
        } else {
          // ' >\t  test '
          //    ^ -- position start of line here + shift bsCount slightly
          //         to make extra space appear
          adjustTab = true;
        }
      } else {
        spaceAfterMarker = false;
      }

      let offset = initial;
      oldBMarks.push(state.bMarks[nextLine]);
      state.bMarks[nextLine] = pos;

      while (pos < max) {
        const ch = state.src.charCodeAt(pos);

        if (isSpace(ch)) {
          if (ch === 0x09) {
            offset += 4 - (offset + state.bsCount[nextLine] + (adjustTab ? 1 : 0)) % 4;
          } else {
            offset++;
          }
        } else {
          break
        }

        pos++;
      }

      lastLineEmpty = pos >= max;

      oldBSCount.push(state.bsCount[nextLine]);
      state.bsCount[nextLine] = state.sCount[nextLine] + 1 + (spaceAfterMarker ? 1 : 0);

      oldSCount.push(state.sCount[nextLine]);
      state.sCount[nextLine] = offset - initial;

      oldTShift.push(state.tShift[nextLine]);
      state.tShift[nextLine] = pos - state.bMarks[nextLine];
      continue
    }

    // Case 2: line is not inside the blockquote, and the last line was empty.
    if (lastLineEmpty) { break }

    // Case 3: another tag found.
    let terminate = false;
    for (let i = 0, l = terminatorRules.length; i < l; i++) {
      if (terminatorRules[i](state, nextLine, endLine, true)) {
        terminate = true;
        break
      }
    }

    if (terminate) {
      // Quirk to enforce "hard termination mode" for paragraphs;
      // normally if you call `tokenize(state, startLine, nextLine)`,
      // paragraphs will look below nextLine for paragraph continuation,
      // but if blockquote is terminated by another tag, they shouldn't
      state.lineMax = nextLine;

      if (state.blkIndent !== 0) {
        // state.blkIndent was non-zero, we now set it to zero,
        // so we need to re-calculate all offsets to appear as
        // if indent wasn't changed
        oldBMarks.push(state.bMarks[nextLine]);
        oldBSCount.push(state.bsCount[nextLine]);
        oldTShift.push(state.tShift[nextLine]);
        oldSCount.push(state.sCount[nextLine]);
        state.sCount[nextLine] -= state.blkIndent;
      }

      break
    }

    oldBMarks.push(state.bMarks[nextLine]);
    oldBSCount.push(state.bsCount[nextLine]);
    oldTShift.push(state.tShift[nextLine]);
    oldSCount.push(state.sCount[nextLine]);

    // A negative indentation means that this is a paragraph continuation
    //
    state.sCount[nextLine] = -1;
  }

  const oldIndent = state.blkIndent;
  state.blkIndent = 0;

  const token_o  = state.push('blockquote_open', 'blockquote', 1);
  token_o.markup = '>';
  const lines = [startLine, 0];
  token_o.map    = lines;

  state.md.block.tokenize(state, startLine, nextLine);

  const token_c  = state.push('blockquote_close', 'blockquote', -1);
  token_c.markup = '>';

  state.lineMax = oldLineMax;
  state.parentType = oldParentType;
  lines[1] = state.line;

  // Restore original tShift; this might not be necessary since the parser
  // has already been here, but just to make sure we can do that.
  for (let i = 0; i < oldTShift.length; i++) {
    state.bMarks[i + startLine] = oldBMarks[i];
    state.tShift[i + startLine] = oldTShift[i];
    state.sCount[i + startLine] = oldSCount[i];
    state.bsCount[i + startLine] = oldBSCount[i];
  }
  state.blkIndent = oldIndent;

  return true
}

// Horizontal rule


function hr (state, startLine, endLine, silent) {
  const max = state.eMarks[startLine];
  // if it's indented more than 3 spaces, it should be a code block
  if (state.sCount[startLine] - state.blkIndent >= 4) { return false }

  let pos = state.bMarks[startLine] + state.tShift[startLine];
  const marker = state.src.charCodeAt(pos++);

  // Check hr marker
  if (marker !== 0x2A/* * */ &&
      marker !== 0x2D/* - */ &&
      marker !== 0x5F/* _ */) {
    return false
  }

  // markers can be mixed with spaces, but there should be at least 3 of them

  let cnt = 1;
  while (pos < max) {
    const ch = state.src.charCodeAt(pos++);
    if (ch !== marker && !isSpace(ch)) { return false }
    if (ch === marker) { cnt++; }
  }

  if (cnt < 3) { return false }

  if (silent) { return true }

  state.line = startLine + 1;

  const token  = state.push('hr', 'hr', 0);
  token.map    = [startLine, state.line];
  token.markup = Array(cnt + 1).join(String.fromCharCode(marker));

  return true
}

// Lists


// Search `[-+*][\n ]`, returns next pos after marker on success
// or -1 on fail.
function skipBulletListMarker (state, startLine) {
  const max = state.eMarks[startLine];
  let pos = state.bMarks[startLine] + state.tShift[startLine];

  const marker = state.src.charCodeAt(pos++);
  // Check bullet
  if (marker !== 0x2A/* * */ &&
      marker !== 0x2D/* - */ &&
      marker !== 0x2B/* + */) {
    return -1
  }

  if (pos < max) {
    const ch = state.src.charCodeAt(pos);

    if (!isSpace(ch)) {
      // " -test " - is not a list item
      return -1
    }
  }

  return pos
}

// Search `\d+[.)][\n ]`, returns next pos after marker on success
// or -1 on fail.
function skipOrderedListMarker (state, startLine) {
  const start = state.bMarks[startLine] + state.tShift[startLine];
  const max = state.eMarks[startLine];
  let pos = start;

  // List marker should have at least 2 chars (digit + dot)
  if (pos + 1 >= max) { return -1 }

  let ch = state.src.charCodeAt(pos++);

  if (ch < 0x30/* 0 */ || ch > 0x39/* 9 */) { return -1 }

  for (;;) {
    // EOL -> fail
    if (pos >= max) { return -1 }

    ch = state.src.charCodeAt(pos++);

    if (ch >= 0x30/* 0 */ && ch <= 0x39/* 9 */) {
      // List marker should have no more than 9 digits
      // (prevents integer overflow in browsers)
      if (pos - start >= 10) { return -1 }

      continue
    }

    // found valid marker
    if (ch === 0x29/* ) */ || ch === 0x2e/* . */) {
      break
    }

    return -1
  }

  if (pos < max) {
    ch = state.src.charCodeAt(pos);

    if (!isSpace(ch)) {
      // " 1.test " - is not a list item
      return -1
    }
  }
  return pos
}

function markTightParagraphs (state, idx) {
  const level = state.level + 2;

  for (let i = idx + 2, l = state.tokens.length - 2; i < l; i++) {
    if (state.tokens[i].level === level && state.tokens[i].type === 'paragraph_open') {
      state.tokens[i + 2].hidden = true;
      state.tokens[i].hidden = true;
      i += 2;
    }
  }
}

function list (state, startLine, endLine, silent) {
  let max, pos, start, token;
  let nextLine = startLine;
  let tight = true;

  // if it's indented more than 3 spaces, it should be a code block
  if (state.sCount[nextLine] - state.blkIndent >= 4) { return false }

  // Special case:
  //  - item 1
  //   - item 2
  //    - item 3
  //     - item 4
  //      - this one is a paragraph continuation
  if (state.listIndent >= 0 &&
      state.sCount[nextLine] - state.listIndent >= 4 &&
      state.sCount[nextLine] < state.blkIndent) {
    return false
  }

  let isTerminatingParagraph = false;

  // limit conditions when list can interrupt
  // a paragraph (validation mode only)
  if (silent && state.parentType === 'paragraph') {
    // Next list item should still terminate previous list item;
    //
    // This code can fail if plugins use blkIndent as well as lists,
    // but I hope the spec gets fixed long before that happens.
    //
    if (state.sCount[nextLine] >= state.blkIndent) {
      isTerminatingParagraph = true;
    }
  }

  // Detect list type and position after marker
  let isOrdered;
  let markerValue;
  let posAfterMarker;
  if ((posAfterMarker = skipOrderedListMarker(state, nextLine)) >= 0) {
    isOrdered = true;
    start = state.bMarks[nextLine] + state.tShift[nextLine];
    markerValue = Number(state.src.slice(start, posAfterMarker - 1));

    // If we're starting a new ordered list right after
    // a paragraph, it should start with 1.
    if (isTerminatingParagraph && markerValue !== 1) return false
  } else if ((posAfterMarker = skipBulletListMarker(state, nextLine)) >= 0) {
    isOrdered = false;
  } else {
    return false
  }

  // If we're starting a new unordered list right after
  // a paragraph, first line should not be empty.
  if (isTerminatingParagraph) {
    if (state.skipSpaces(posAfterMarker) >= state.eMarks[nextLine]) return false
  }

  // For validation mode we can terminate immediately
  if (silent) { return true }

  // We should terminate list on style change. Remember first one to compare.
  const markerCharCode = state.src.charCodeAt(posAfterMarker - 1);

  // Start list
  const listTokIdx = state.tokens.length;

  if (isOrdered) {
    token       = state.push('ordered_list_open', 'ol', 1);
    if (markerValue !== 1) {
      token.attrs = [['start', markerValue]];
    }
  } else {
    token       = state.push('bullet_list_open', 'ul', 1);
  }

  const listLines = [nextLine, 0];
  token.map    = listLines;
  token.markup = String.fromCharCode(markerCharCode);

  //
  // Iterate list items
  //

  let prevEmptyEnd = false;
  const terminatorRules = state.md.block.ruler.getRules('list');

  const oldParentType = state.parentType;
  state.parentType = 'list';

  while (nextLine < endLine) {
    pos = posAfterMarker;
    max = state.eMarks[nextLine];

    const initial = state.sCount[nextLine] + posAfterMarker - (state.bMarks[nextLine] + state.tShift[nextLine]);
    let offset = initial;

    while (pos < max) {
      const ch = state.src.charCodeAt(pos);

      if (ch === 0x09) {
        offset += 4 - (offset + state.bsCount[nextLine]) % 4;
      } else if (ch === 0x20) {
        offset++;
      } else {
        break
      }

      pos++;
    }

    const contentStart = pos;
    let indentAfterMarker;

    if (contentStart >= max) {
      // trimming space in "-    \n  3" case, indent is 1 here
      indentAfterMarker = 1;
    } else {
      indentAfterMarker = offset - initial;
    }

    // If we have more than 4 spaces, the indent is 1
    // (the rest is just indented code block)
    if (indentAfterMarker > 4) { indentAfterMarker = 1; }

    // "  -  test"
    //  ^^^^^ - calculating total length of this thing
    const indent = initial + indentAfterMarker;

    // Run subparser & write tokens
    token        = state.push('list_item_open', 'li', 1);
    token.markup = String.fromCharCode(markerCharCode);
    const itemLines = [nextLine, 0];
    token.map    = itemLines;
    if (isOrdered) {
      token.info = state.src.slice(start, posAfterMarker - 1);
    }

    // change current state, then restore it after parser subcall
    const oldTight = state.tight;
    const oldTShift = state.tShift[nextLine];
    const oldSCount = state.sCount[nextLine];

    //  - example list
    // ^ listIndent position will be here
    //   ^ blkIndent position will be here
    //
    const oldListIndent = state.listIndent;
    state.listIndent = state.blkIndent;
    state.blkIndent = indent;

    state.tight = true;
    state.tShift[nextLine] = contentStart - state.bMarks[nextLine];
    state.sCount[nextLine] = offset;

    if (contentStart >= max && state.isEmpty(nextLine + 1)) {
      // workaround for this case
      // (list item is empty, list terminates before "foo"):
      // ~~~~~~~~
      //   -
      //
      //     foo
      // ~~~~~~~~
      state.line = Math.min(state.line + 2, endLine);
    } else {
      state.md.block.tokenize(state, nextLine, endLine, true);
    }

    // If any of list item is tight, mark list as tight
    if (!state.tight || prevEmptyEnd) {
      tight = false;
    }
    // Item become loose if finish with empty line,
    // but we should filter last element, because it means list finish
    prevEmptyEnd = (state.line - nextLine) > 1 && state.isEmpty(state.line - 1);

    state.blkIndent = state.listIndent;
    state.listIndent = oldListIndent;
    state.tShift[nextLine] = oldTShift;
    state.sCount[nextLine] = oldSCount;
    state.tight = oldTight;

    token        = state.push('list_item_close', 'li', -1);
    token.markup = String.fromCharCode(markerCharCode);

    nextLine = state.line;
    itemLines[1] = nextLine;

    if (nextLine >= endLine) { break }

    //
    // Try to check if list is terminated or continued.
    //
    if (state.sCount[nextLine] < state.blkIndent) { break }

    // if it's indented more than 3 spaces, it should be a code block
    if (state.sCount[nextLine] - state.blkIndent >= 4) { break }

    // fail if terminating block found
    let terminate = false;
    for (let i = 0, l = terminatorRules.length; i < l; i++) {
      if (terminatorRules[i](state, nextLine, endLine, true)) {
        terminate = true;
        break
      }
    }
    if (terminate) { break }

    // fail if list has another type
    if (isOrdered) {
      posAfterMarker = skipOrderedListMarker(state, nextLine);
      if (posAfterMarker < 0) { break }
      start = state.bMarks[nextLine] + state.tShift[nextLine];
    } else {
      posAfterMarker = skipBulletListMarker(state, nextLine);
      if (posAfterMarker < 0) { break }
    }

    if (markerCharCode !== state.src.charCodeAt(posAfterMarker - 1)) { break }
  }

  // Finalize list
  if (isOrdered) {
    token = state.push('ordered_list_close', 'ol', -1);
  } else {
    token = state.push('bullet_list_close', 'ul', -1);
  }
  token.markup = String.fromCharCode(markerCharCode);

  listLines[1] = nextLine;
  state.line = nextLine;

  state.parentType = oldParentType;

  // mark paragraphs tight if needed
  if (tight) {
    markTightParagraphs(state, listTokIdx);
  }

  return true
}

function reference (state, startLine, _endLine, silent) {
  let pos = state.bMarks[startLine] + state.tShift[startLine];
  let max = state.eMarks[startLine];
  let nextLine = startLine + 1;

  // if it's indented more than 3 spaces, it should be a code block
  if (state.sCount[startLine] - state.blkIndent >= 4) { return false }

  if (state.src.charCodeAt(pos) !== 0x5B/* [ */) { return false }

  function getNextLine (nextLine) {
    const endLine = state.lineMax;

    if (nextLine >= endLine || state.isEmpty(nextLine)) {
      // empty line or end of input
      return null
    }

    let isContinuation = false;

    // this would be a code block normally, but after paragraph
    // it's considered a lazy continuation regardless of what's there
    if (state.sCount[nextLine] - state.blkIndent > 3) { isContinuation = true; }

    // quirk for blockquotes, this line should already be checked by that rule
    if (state.sCount[nextLine] < 0) { isContinuation = true; }

    if (!isContinuation) {
      const terminatorRules = state.md.block.ruler.getRules('reference');
      const oldParentType = state.parentType;
      state.parentType = 'reference';

      // Some tags can terminate paragraph without empty line.
      let terminate = false;
      for (let i = 0, l = terminatorRules.length; i < l; i++) {
        if (terminatorRules[i](state, nextLine, endLine, true)) {
          terminate = true;
          break
        }
      }

      state.parentType = oldParentType;
      if (terminate) {
        // terminated by another block
        return null
      }
    }

    const pos = state.bMarks[nextLine] + state.tShift[nextLine];
    const max = state.eMarks[nextLine];

    // max + 1 explicitly includes the newline
    return state.src.slice(pos, max + 1)
  }

  let str = state.src.slice(pos, max + 1);

  max = str.length;
  let labelEnd = -1;

  for (pos = 1; pos < max; pos++) {
    const ch = str.charCodeAt(pos);
    if (ch === 0x5B /* [ */) {
      return false
    } else if (ch === 0x5D /* ] */) {
      labelEnd = pos;
      break
    } else if (ch === 0x0A /* \n */) {
      const lineContent = getNextLine(nextLine);
      if (lineContent !== null) {
        str += lineContent;
        max = str.length;
        nextLine++;
      }
    } else if (ch === 0x5C /* \ */) {
      pos++;
      if (pos < max && str.charCodeAt(pos) === 0x0A) {
        const lineContent = getNextLine(nextLine);
        if (lineContent !== null) {
          str += lineContent;
          max = str.length;
          nextLine++;
        }
      }
    }
  }

  if (labelEnd < 0 || str.charCodeAt(labelEnd + 1) !== 0x3A/* : */) { return false }

  // [label]:   destination   'title'
  //         ^^^ skip optional whitespace here
  for (pos = labelEnd + 2; pos < max; pos++) {
    const ch = str.charCodeAt(pos);
    if (ch === 0x0A) {
      const lineContent = getNextLine(nextLine);
      if (lineContent !== null) {
        str += lineContent;
        max = str.length;
        nextLine++;
      }
    } else if (isSpace(ch)) ; else {
      break
    }
  }

  // [label]:   destination   'title'
  //            ^^^^^^^^^^^ parse this
  const destRes = state.md.helpers.parseLinkDestination(str, pos, max);
  if (!destRes.ok) { return false }

  const href = state.md.normalizeLink(destRes.str);
  if (!state.md.validateLink(href)) { return false }

  pos = destRes.pos;

  // save cursor state, we could require to rollback later
  const destEndPos = pos;
  const destEndLineNo = nextLine;

  // [label]:   destination   'title'
  //                       ^^^ skipping those spaces
  const start = pos;
  for (; pos < max; pos++) {
    const ch = str.charCodeAt(pos);
    if (ch === 0x0A) {
      const lineContent = getNextLine(nextLine);
      if (lineContent !== null) {
        str += lineContent;
        max = str.length;
        nextLine++;
      }
    } else if (isSpace(ch)) ; else {
      break
    }
  }

  // [label]:   destination   'title'
  //                          ^^^^^^^ parse this
  let titleRes = state.md.helpers.parseLinkTitle(str, pos, max);
  while (titleRes.can_continue) {
    const lineContent = getNextLine(nextLine);
    if (lineContent === null) break
    str += lineContent;
    pos = max;
    max = str.length;
    nextLine++;
    titleRes = state.md.helpers.parseLinkTitle(str, pos, max, titleRes);
  }
  let title;

  if (pos < max && start !== pos && titleRes.ok) {
    title = titleRes.str;
    pos = titleRes.pos;
  } else {
    title = '';
    pos = destEndPos;
    nextLine = destEndLineNo;
  }

  // skip trailing spaces until the rest of the line
  while (pos < max) {
    const ch = str.charCodeAt(pos);
    if (!isSpace(ch)) { break }
    pos++;
  }

  if (pos < max && str.charCodeAt(pos) !== 0x0A) {
    if (title) {
      // garbage at the end of the line after title,
      // but it could still be a valid reference if we roll back
      title = '';
      pos = destEndPos;
      nextLine = destEndLineNo;
      while (pos < max) {
        const ch = str.charCodeAt(pos);
        if (!isSpace(ch)) { break }
        pos++;
      }
    }
  }

  if (pos < max && str.charCodeAt(pos) !== 0x0A) {
    // garbage at the end of the line
    return false
  }

  const label = normalizeReference(str.slice(1, labelEnd));
  if (!label) {
    // CommonMark 0.20 disallows empty labels
    return false
  }

  // Reference can not terminate anything. This check is for safety only.
  /* istanbul ignore if */
  if (silent) { return true }

  if (typeof state.env.references === 'undefined') {
    state.env.references = {};
  }
  if (typeof state.env.references[label] === 'undefined') {
    state.env.references[label] = { title, href };
  }

  state.line = nextLine;
  return true
}

// List of valid html blocks names, according to commonmark spec
// https://spec.commonmark.org/0.30/#html-blocks

var block_names = [
  'address',
  'article',
  'aside',
  'base',
  'basefont',
  'blockquote',
  'body',
  'caption',
  'center',
  'col',
  'colgroup',
  'dd',
  'details',
  'dialog',
  'dir',
  'div',
  'dl',
  'dt',
  'fieldset',
  'figcaption',
  'figure',
  'footer',
  'form',
  'frame',
  'frameset',
  'h1',
  'h2',
  'h3',
  'h4',
  'h5',
  'h6',
  'head',
  'header',
  'hr',
  'html',
  'iframe',
  'legend',
  'li',
  'link',
  'main',
  'menu',
  'menuitem',
  'nav',
  'noframes',
  'ol',
  'optgroup',
  'option',
  'p',
  'param',
  'search',
  'section',
  'summary',
  'table',
  'tbody',
  'td',
  'tfoot',
  'th',
  'thead',
  'title',
  'tr',
  'track',
  'ul'
];

// Regexps to match html elements

const attr_name     = '[a-zA-Z_:][a-zA-Z0-9:._-]*';

const unquoted      = '[^"\'=<>`\\x00-\\x20]+';
const single_quoted = "'[^']*'";
const double_quoted = '"[^"]*"';

const attr_value  = '(?:' + unquoted + '|' + single_quoted + '|' + double_quoted + ')';

const attribute   = '(?:\\s+' + attr_name + '(?:\\s*=\\s*' + attr_value + ')?)';

const open_tag    = '<[A-Za-z][A-Za-z0-9\\-]*' + attribute + '*\\s*\\/?>';

const close_tag   = '<\\/[A-Za-z][A-Za-z0-9\\-]*\\s*>';
const comment     = '<!---?>|<!--(?:[^-]|-[^-]|--[^>])*-->';
const processing  = '<[?][\\s\\S]*?[?]>';
const declaration = '<![A-Za-z][^>]*>';
const cdata       = '<!\\[CDATA\\[[\\s\\S]*?\\]\\]>';

const HTML_TAG_RE = new RegExp('^(?:' + open_tag + '|' + close_tag + '|' + comment +
                        '|' + processing + '|' + declaration + '|' + cdata + ')');
const HTML_OPEN_CLOSE_TAG_RE = new RegExp('^(?:' + open_tag + '|' + close_tag + ')');

// HTML block


// An array of opening and corresponding closing sequences for html tags,
// last argument defines whether it can terminate a paragraph or not
//
const HTML_SEQUENCES = [
  [/^<(script|pre|style|textarea)(?=(\s|>|$))/i, /<\/(script|pre|style|textarea)>/i, true],
  [/^<!--/,        /-->/,   true],
  [/^<\?/,         /\?>/,   true],
  [/^<![A-Z]/,     />/,     true],
  [/^<!\[CDATA\[/, /\]\]>/, true],
  [new RegExp('^</?(' + block_names.join('|') + ')(?=(\\s|/?>|$))', 'i'), /^$/, true],
  [new RegExp(HTML_OPEN_CLOSE_TAG_RE.source + '\\s*$'),  /^$/, false]
];

function html_block (state, startLine, endLine, silent) {
  let pos = state.bMarks[startLine] + state.tShift[startLine];
  let max = state.eMarks[startLine];

  // if it's indented more than 3 spaces, it should be a code block
  if (state.sCount[startLine] - state.blkIndent >= 4) { return false }

  if (!state.md.options.html) { return false }

  if (state.src.charCodeAt(pos) !== 0x3C/* < */) { return false }

  let lineText = state.src.slice(pos, max);

  let i = 0;
  for (; i < HTML_SEQUENCES.length; i++) {
    if (HTML_SEQUENCES[i][0].test(lineText)) { break }
  }
  if (i === HTML_SEQUENCES.length) { return false }

  if (silent) {
    // true if this sequence can be a terminator, false otherwise
    return HTML_SEQUENCES[i][2]
  }

  let nextLine = startLine + 1;

  // If we are here - we detected HTML block.
  // Let's roll down till block end.
  if (!HTML_SEQUENCES[i][1].test(lineText)) {
    for (; nextLine < endLine; nextLine++) {
      if (state.sCount[nextLine] < state.blkIndent) { break }

      pos = state.bMarks[nextLine] + state.tShift[nextLine];
      max = state.eMarks[nextLine];
      lineText = state.src.slice(pos, max);

      if (HTML_SEQUENCES[i][1].test(lineText)) {
        if (lineText.length !== 0) { nextLine++; }
        break
      }
    }
  }

  state.line = nextLine;

  const token   = state.push('html_block', '', 0);
  token.map     = [startLine, nextLine];
  token.content = state.getLines(startLine, nextLine, state.blkIndent, true);

  return true
}

// heading (#, ##, ...)


function heading (state, startLine, endLine, silent) {
  let pos = state.bMarks[startLine] + state.tShift[startLine];
  let max = state.eMarks[startLine];

  // if it's indented more than 3 spaces, it should be a code block
  if (state.sCount[startLine] - state.blkIndent >= 4) { return false }

  let ch  = state.src.charCodeAt(pos);

  if (ch !== 0x23/* # */ || pos >= max) { return false }

  // count heading level
  let level = 1;
  ch = state.src.charCodeAt(++pos);
  while (ch === 0x23/* # */ && pos < max && level <= 6) {
    level++;
    ch = state.src.charCodeAt(++pos);
  }

  if (level > 6 || (pos < max && !isSpace(ch))) { return false }

  if (silent) { return true }

  // Let's cut tails like '    ###  ' from the end of string

  max = state.skipSpacesBack(max, pos);
  const tmp = state.skipCharsBack(max, 0x23, pos); // #
  if (tmp > pos && isSpace(state.src.charCodeAt(tmp - 1))) {
    max = tmp;
  }

  state.line = startLine + 1;

  const token_o  = state.push('heading_open', 'h' + String(level), 1);
  token_o.markup = '########'.slice(0, level);
  token_o.map    = [startLine, state.line];

  const token_i    = state.push('inline', '', 0);
  token_i.content  = state.src.slice(pos, max).trim();
  token_i.map      = [startLine, state.line];
  token_i.children = [];

  const token_c  = state.push('heading_close', 'h' + String(level), -1);
  token_c.markup = '########'.slice(0, level);

  return true
}

// lheading (---, ===)

function lheading (state, startLine, endLine/*, silent */) {
  const terminatorRules = state.md.block.ruler.getRules('paragraph');

  // if it's indented more than 3 spaces, it should be a code block
  if (state.sCount[startLine] - state.blkIndent >= 4) { return false }

  const oldParentType = state.parentType;
  state.parentType = 'paragraph'; // use paragraph to match terminatorRules

  // jump line-by-line until empty one or EOF
  let level = 0;
  let marker;
  let nextLine = startLine + 1;

  for (; nextLine < endLine && !state.isEmpty(nextLine); nextLine++) {
    // this would be a code block normally, but after paragraph
    // it's considered a lazy continuation regardless of what's there
    if (state.sCount[nextLine] - state.blkIndent > 3) { continue }

    //
    // Check for underline in setext header
    //
    if (state.sCount[nextLine] >= state.blkIndent) {
      let pos = state.bMarks[nextLine] + state.tShift[nextLine];
      const max = state.eMarks[nextLine];

      if (pos < max) {
        marker = state.src.charCodeAt(pos);

        if (marker === 0x2D/* - */ || marker === 0x3D/* = */) {
          pos = state.skipChars(pos, marker);
          pos = state.skipSpaces(pos);

          if (pos >= max) {
            level = (marker === 0x3D/* = */ ? 1 : 2);
            break
          }
        }
      }
    }

    // quirk for blockquotes, this line should already be checked by that rule
    if (state.sCount[nextLine] < 0) { continue }

    // Some tags can terminate paragraph without empty line.
    let terminate = false;
    for (let i = 0, l = terminatorRules.length; i < l; i++) {
      if (terminatorRules[i](state, nextLine, endLine, true)) {
        terminate = true;
        break
      }
    }
    if (terminate) { break }
  }

  if (!level) {
    // Didn't find valid underline
    return false
  }

  const content = state.getLines(startLine, nextLine, state.blkIndent, false).trim();

  state.line = nextLine + 1;

  const token_o    = state.push('heading_open', 'h' + String(level), 1);
  token_o.markup   = String.fromCharCode(marker);
  token_o.map      = [startLine, state.line];

  const token_i    = state.push('inline', '', 0);
  token_i.content  = content;
  token_i.map      = [startLine, state.line - 1];
  token_i.children = [];

  const token_c    = state.push('heading_close', 'h' + String(level), -1);
  token_c.markup   = String.fromCharCode(marker);

  state.parentType = oldParentType;

  return true
}

// Paragraph

function paragraph (state, startLine, endLine) {
  const terminatorRules = state.md.block.ruler.getRules('paragraph');
  const oldParentType = state.parentType;
  let nextLine = startLine + 1;
  state.parentType = 'paragraph';

  // jump line-by-line until empty one or EOF
  for (; nextLine < endLine && !state.isEmpty(nextLine); nextLine++) {
    // this would be a code block normally, but after paragraph
    // it's considered a lazy continuation regardless of what's there
    if (state.sCount[nextLine] - state.blkIndent > 3) { continue }

    // quirk for blockquotes, this line should already be checked by that rule
    if (state.sCount[nextLine] < 0) { continue }

    // Some tags can terminate paragraph without empty line.
    let terminate = false;
    for (let i = 0, l = terminatorRules.length; i < l; i++) {
      if (terminatorRules[i](state, nextLine, endLine, true)) {
        terminate = true;
        break
      }
    }
    if (terminate) { break }
  }

  const content = state.getLines(startLine, nextLine, state.blkIndent, false).trim();

  state.line = nextLine;

  const token_o    = state.push('paragraph_open', 'p', 1);
  token_o.map      = [startLine, state.line];

  const token_i    = state.push('inline', '', 0);
  token_i.content  = content;
  token_i.map      = [startLine, state.line];
  token_i.children = [];

  state.push('paragraph_close', 'p', -1);

  state.parentType = oldParentType;

  return true
}

/** internal
 * class ParserBlock
 *
 * Block-level tokenizer.
 **/


const _rules$1 = [
  // First 2 params - rule name & source. Secondary array - list of rules,
  // which can be terminated by this one.
  ['table',      table,      ['paragraph', 'reference']],
  ['code',       code],
  ['fence',      fence,      ['paragraph', 'reference', 'blockquote', 'list']],
  ['blockquote', blockquote, ['paragraph', 'reference', 'blockquote', 'list']],
  ['hr',         hr,         ['paragraph', 'reference', 'blockquote', 'list']],
  ['list',       list,       ['paragraph', 'reference', 'blockquote']],
  ['reference',  reference],
  ['html_block', html_block, ['paragraph', 'reference', 'blockquote']],
  ['heading',    heading,    ['paragraph', 'reference', 'blockquote']],
  ['lheading',   lheading],
  ['paragraph',  paragraph]
];

/**
 * new ParserBlock()
 **/
function ParserBlock () {
  /**
   * ParserBlock#ruler -> Ruler
   *
   * [[Ruler]] instance. Keep configuration of block rules.
   **/
  this.ruler = new Ruler();

  for (let i = 0; i < _rules$1.length; i++) {
    this.ruler.push(_rules$1[i][0], _rules$1[i][1], { alt: (_rules$1[i][2] || []).slice() });
  }
}

// Generate tokens for input range
//
ParserBlock.prototype.tokenize = function (state, startLine, endLine) {
  const rules = this.ruler.getRules('');
  const len = rules.length;
  const maxNesting = state.md.options.maxNesting;
  let line = startLine;
  let hasEmptyLines = false;

  while (line < endLine) {
    state.line = line = state.skipEmptyLines(line);
    if (line >= endLine) { break }

    // Termination condition for nested calls.
    // Nested calls currently used for blockquotes & lists
    if (state.sCount[line] < state.blkIndent) { break }

    // If nesting level exceeded - skip tail to the end. That's not ordinary
    // situation and we should not care about content.
    if (state.level >= maxNesting) {
      state.line = endLine;
      break
    }

    // Try all possible rules.
    // On success, rule should:
    //
    // - update `state.line`
    // - update `state.tokens`
    // - return true
    const prevLine = state.line;
    let ok = false;

    for (let i = 0; i < len; i++) {
      ok = rules[i](state, line, endLine, false);
      if (ok) {
        if (prevLine >= state.line) {
          throw new Error("block rule didn't increment state.line")
        }
        break
      }
    }

    // this can only happen if user disables paragraph rule
    if (!ok) throw new Error('none of the block rules matched')

    // set state.tight if we had an empty line before current tag
    // i.e. latest empty line should not count
    state.tight = !hasEmptyLines;

    // paragraph might "eat" one newline after it in nested lists
    if (state.isEmpty(state.line - 1)) {
      hasEmptyLines = true;
    }

    line = state.line;

    if (line < endLine && state.isEmpty(line)) {
      hasEmptyLines = true;
      line++;
      state.line = line;
    }
  }
};

/**
 * ParserBlock.parse(str, md, env, outTokens)
 *
 * Process input string and push block tokens into `outTokens`
 **/
ParserBlock.prototype.parse = function (src, md, env, outTokens) {
  if (!src) { return }

  const state = new this.State(src, md, env, outTokens);

  this.tokenize(state, state.line, state.lineMax);
};

ParserBlock.prototype.State = StateBlock;

// Inline parser state


function StateInline (src, md, env, outTokens) {
  this.src = src;
  this.env = env;
  this.md = md;
  this.tokens = outTokens;
  this.tokens_meta = Array(outTokens.length);

  this.pos = 0;
  this.posMax = this.src.length;
  this.level = 0;
  this.pending = '';
  this.pendingLevel = 0;

  // Stores { start: end } pairs. Useful for backtrack
  // optimization of pairs parse (emphasis, strikes).
  this.cache = {};

  // List of emphasis-like delimiters for current tag
  this.delimiters = [];

  // Stack of delimiter lists for upper level tags
  this._prev_delimiters = [];

  // backtick length => last seen position
  this.backticks = {};
  this.backticksScanned = false;

  // Counter used to disable inline linkify-it execution
  // inside <a> and markdown links
  this.linkLevel = 0;
}

// Flush pending text
//
StateInline.prototype.pushPending = function () {
  const token = new Token('text', '', 0);
  token.content = this.pending;
  token.level = this.pendingLevel;
  this.tokens.push(token);
  this.pending = '';
  return token
};

// Push new token to "stream".
// If pending text exists - flush it as text token
//
StateInline.prototype.push = function (type, tag, nesting) {
  if (this.pending) {
    this.pushPending();
  }

  const token = new Token(type, tag, nesting);
  let token_meta = null;

  if (nesting < 0) {
    // closing tag
    this.level--;
    this.delimiters = this._prev_delimiters.pop();
  }

  token.level = this.level;

  if (nesting > 0) {
    // opening tag
    this.level++;
    this._prev_delimiters.push(this.delimiters);
    this.delimiters = [];
    token_meta = { delimiters: this.delimiters };
  }

  this.pendingLevel = this.level;
  this.tokens.push(token);
  this.tokens_meta.push(token_meta);
  return token
};

// Scan a sequence of emphasis-like markers, and determine whether
// it can start an emphasis sequence or end an emphasis sequence.
//
//  - start - position to scan from (it should point at a valid marker);
//  - canSplitWord - determine if these markers can be found inside a word
//
StateInline.prototype.scanDelims = function (start, canSplitWord) {
  const max = this.posMax;
  const marker = this.src.charCodeAt(start);

  // treat beginning of the line as a whitespace
  const lastChar = start > 0 ? this.src.charCodeAt(start - 1) : 0x20;

  let pos = start;
  while (pos < max && this.src.charCodeAt(pos) === marker) { pos++; }

  const count = pos - start;

  // treat end of the line as a whitespace
  const nextChar = pos < max ? this.src.charCodeAt(pos) : 0x20;

  const isLastPunctChar = isMdAsciiPunct(lastChar) || isPunctChar(String.fromCharCode(lastChar));
  const isNextPunctChar = isMdAsciiPunct(nextChar) || isPunctChar(String.fromCharCode(nextChar));

  const isLastWhiteSpace = isWhiteSpace(lastChar);
  const isNextWhiteSpace = isWhiteSpace(nextChar);

  const left_flanking =
    !isNextWhiteSpace && (!isNextPunctChar || isLastWhiteSpace || isLastPunctChar);
  const right_flanking =
    !isLastWhiteSpace && (!isLastPunctChar || isNextWhiteSpace || isNextPunctChar);

  const can_open  = left_flanking  && (canSplitWord || !right_flanking || isLastPunctChar);
  const can_close = right_flanking && (canSplitWord || !left_flanking  || isNextPunctChar);

  return { can_open, can_close, length: count }
};

// re-export Token class to use in block rules
StateInline.prototype.Token = Token;

// Skip text characters for text token, place those to pending buffer
// and increment current pos

// Rule to skip pure text
// '{}$%@~+=:' reserved for extentions

// !, ", #, $, %, &, ', (, ), *, +, ,, -, ., /, :, ;, <, =, >, ?, @, [, \, ], ^, _, `, {, |, }, or ~

// !!!! Don't confuse with "Markdown ASCII Punctuation" chars
// http://spec.commonmark.org/0.15/#ascii-punctuation-character
function isTerminatorChar (ch) {
  switch (ch) {
    case 0x0A/* \n */:
    case 0x21/* ! */:
    case 0x23/* # */:
    case 0x24/* $ */:
    case 0x25/* % */:
    case 0x26/* & */:
    case 0x2A/* * */:
    case 0x2B/* + */:
    case 0x2D/* - */:
    case 0x3A/* : */:
    case 0x3C/* < */:
    case 0x3D/* = */:
    case 0x3E/* > */:
    case 0x40/* @ */:
    case 0x5B/* [ */:
    case 0x5C/* \ */:
    case 0x5D/* ] */:
    case 0x5E/* ^ */:
    case 0x5F/* _ */:
    case 0x60/* ` */:
    case 0x7B/* { */:
    case 0x7D/* } */:
    case 0x7E/* ~ */:
      return true
    default:
      return false
  }
}

function text$1 (state, silent) {
  let pos = state.pos;

  while (pos < state.posMax && !isTerminatorChar(state.src.charCodeAt(pos))) {
    pos++;
  }

  if (pos === state.pos) { return false }

  if (!silent) { state.pending += state.src.slice(state.pos, pos); }

  state.pos = pos;

  return true
}

// Alternative implementation, for memory.
//
// It costs 10% of performance, but allows extend terminators list, if place it
// to `ParserInline` property. Probably, will switch to it sometime, such
// flexibility required.

/*
var TERMINATOR_RE = /[\n!#$%&*+\-:<=>@[\\\]^_`{}~]/;

module.exports = function text(state, silent) {
  var pos = state.pos,
      idx = state.src.slice(pos).search(TERMINATOR_RE);

  // first char is terminator -> empty text
  if (idx === 0) { return false; }

  // no terminator -> text till end of string
  if (idx < 0) {
    if (!silent) { state.pending += state.src.slice(pos); }
    state.pos = state.src.length;
    return true;
  }

  if (!silent) { state.pending += state.src.slice(pos, pos + idx); }

  state.pos += idx;

  return true;
}; */

// Process links like https://example.org/

// RFC3986: scheme = ALPHA *( ALPHA / DIGIT / "+" / "-" / "." )
const SCHEME_RE = /(?:^|[^a-z0-9.+-])([a-z][a-z0-9.+-]*)$/i;

function linkify (state, silent) {
  if (!state.md.options.linkify) return false
  if (state.linkLevel > 0) return false

  const pos = state.pos;
  const max = state.posMax;

  if (pos + 3 > max) return false
  if (state.src.charCodeAt(pos) !== 0x3A/* : */) return false
  if (state.src.charCodeAt(pos + 1) !== 0x2F/* / */) return false
  if (state.src.charCodeAt(pos + 2) !== 0x2F/* / */) return false

  const match = state.pending.match(SCHEME_RE);
  if (!match) return false

  const proto = match[1];

  const link = state.md.linkify.matchAtStart(state.src.slice(pos - proto.length));
  if (!link) return false

  let url = link.url;

  // invalid link, but still detected by linkify somehow;
  // need to check to prevent infinite loop below
  if (url.length <= proto.length) return false

  // disallow '*' at the end of the link (conflicts with emphasis)
  url = url.replace(/\*+$/, '');

  const fullUrl = state.md.normalizeLink(url);
  if (!state.md.validateLink(fullUrl)) return false

  if (!silent) {
    state.pending = state.pending.slice(0, -proto.length);

    const token_o = state.push('link_open', 'a', 1);
    token_o.attrs = [['href', fullUrl]];
    token_o.markup = 'linkify';
    token_o.info = 'auto';

    const token_t = state.push('text', '', 0);
    token_t.content = state.md.normalizeLinkText(url);

    const token_c = state.push('link_close', 'a', -1);
    token_c.markup = 'linkify';
    token_c.info = 'auto';
  }

  state.pos += url.length - proto.length;
  return true
}

// Proceess '\n'


function newline (state, silent) {
  let pos = state.pos;

  if (state.src.charCodeAt(pos) !== 0x0A/* \n */) { return false }

  const pmax = state.pending.length - 1;
  const max = state.posMax;

  // '  \n' -> hardbreak
  // Lookup in pending chars is bad practice! Don't copy to other rules!
  // Pending string is stored in concat mode, indexed lookups will cause
  // convertion to flat mode.
  if (!silent) {
    if (pmax >= 0 && state.pending.charCodeAt(pmax) === 0x20) {
      if (pmax >= 1 && state.pending.charCodeAt(pmax - 1) === 0x20) {
        // Find whitespaces tail of pending chars.
        let ws = pmax - 1;
        while (ws >= 1 && state.pending.charCodeAt(ws - 1) === 0x20) ws--;

        state.pending = state.pending.slice(0, ws);
        state.push('hardbreak', 'br', 0);
      } else {
        state.pending = state.pending.slice(0, -1);
        state.push('softbreak', 'br', 0);
      }
    } else {
      state.push('softbreak', 'br', 0);
    }
  }

  pos++;

  // skip heading spaces for next line
  while (pos < max && isSpace(state.src.charCodeAt(pos))) { pos++; }

  state.pos = pos;
  return true
}

// Process escaped chars and hardbreaks


const ESCAPED = [];

for (let i = 0; i < 256; i++) { ESCAPED.push(0); }

'\\!"#$%&\'()*+,./:;<=>?@[]^_`{|}~-'
  .split('').forEach(function (ch) { ESCAPED[ch.charCodeAt(0)] = 1; });

function escape$1 (state, silent) {
  let pos = state.pos;
  const max = state.posMax;

  if (state.src.charCodeAt(pos) !== 0x5C/* \ */) return false
  pos++;

  // '\' at the end of the inline block
  if (pos >= max) return false

  let ch1 = state.src.charCodeAt(pos);

  if (ch1 === 0x0A) {
    if (!silent) {
      state.push('hardbreak', 'br', 0);
    }

    pos++;
    // skip leading whitespaces from next line
    while (pos < max) {
      ch1 = state.src.charCodeAt(pos);
      if (!isSpace(ch1)) break
      pos++;
    }

    state.pos = pos;
    return true
  }

  let escapedStr = state.src[pos];

  if (ch1 >= 0xD800 && ch1 <= 0xDBFF && pos + 1 < max) {
    const ch2 = state.src.charCodeAt(pos + 1);

    if (ch2 >= 0xDC00 && ch2 <= 0xDFFF) {
      escapedStr += state.src[pos + 1];
      pos++;
    }
  }

  const origStr = '\\' + escapedStr;

  if (!silent) {
    const token = state.push('text_special', '', 0);

    if (ch1 < 256 && ESCAPED[ch1] !== 0) {
      token.content = escapedStr;
    } else {
      token.content = origStr;
    }

    token.markup = origStr;
    token.info   = 'escape';
  }

  state.pos = pos + 1;
  return true
}

// Parse backticks

function backtick (state, silent) {
  let pos = state.pos;
  const ch = state.src.charCodeAt(pos);

  if (ch !== 0x60/* ` */) { return false }

  const start = pos;
  pos++;
  const max = state.posMax;

  // scan marker length
  while (pos < max && state.src.charCodeAt(pos) === 0x60/* ` */) { pos++; }

  const marker = state.src.slice(start, pos);
  const openerLength = marker.length;

  if (state.backticksScanned && (state.backticks[openerLength] || 0) <= start) {
    if (!silent) state.pending += marker;
    state.pos += openerLength;
    return true
  }

  let matchEnd = pos;
  let matchStart;

  // Nothing found in the cache, scan until the end of the line (or until marker is found)
  while ((matchStart = state.src.indexOf('`', matchEnd)) !== -1) {
    matchEnd = matchStart + 1;

    // scan marker length
    while (matchEnd < max && state.src.charCodeAt(matchEnd) === 0x60/* ` */) { matchEnd++; }

    const closerLength = matchEnd - matchStart;

    if (closerLength === openerLength) {
      // Found matching closer length.
      if (!silent) {
        const token = state.push('code_inline', 'code', 0);
        token.markup = marker;
        token.content = state.src.slice(pos, matchStart)
          .replace(/\n/g, ' ')
          .replace(/^ (.+) $/, '$1');
      }
      state.pos = matchEnd;
      return true
    }

    // Some different length found, put it in cache as upper limit of where closer can be found
    state.backticks[closerLength] = matchStart;
  }

  // Scanned through the end, didn't find anything
  state.backticksScanned = true;

  if (!silent) state.pending += marker;
  state.pos += openerLength;
  return true
}

// ~~strike through~~
//

// Insert each marker as a separate text token, and add it to delimiter list
//
function strikethrough_tokenize (state, silent) {
  const start = state.pos;
  const marker = state.src.charCodeAt(start);

  if (silent) { return false }

  if (marker !== 0x7E/* ~ */) { return false }

  const scanned = state.scanDelims(state.pos, true);
  let len = scanned.length;
  const ch = String.fromCharCode(marker);

  if (len < 2) { return false }

  let token;

  if (len % 2) {
    token         = state.push('text', '', 0);
    token.content = ch;
    len--;
  }

  for (let i = 0; i < len; i += 2) {
    token         = state.push('text', '', 0);
    token.content = ch + ch;

    state.delimiters.push({
      marker,
      length: 0,     // disable "rule of 3" length checks meant for emphasis
      token: state.tokens.length - 1,
      end: -1,
      open: scanned.can_open,
      close: scanned.can_close
    });
  }

  state.pos += scanned.length;

  return true
}

function postProcess$1 (state, delimiters) {
  let token;
  const loneMarkers = [];
  const max = delimiters.length;

  for (let i = 0; i < max; i++) {
    const startDelim = delimiters[i];

    if (startDelim.marker !== 0x7E/* ~ */) {
      continue
    }

    if (startDelim.end === -1) {
      continue
    }

    const endDelim = delimiters[startDelim.end];

    token         = state.tokens[startDelim.token];
    token.type    = 's_open';
    token.tag     = 's';
    token.nesting = 1;
    token.markup  = '~~';
    token.content = '';

    token         = state.tokens[endDelim.token];
    token.type    = 's_close';
    token.tag     = 's';
    token.nesting = -1;
    token.markup  = '~~';
    token.content = '';

    if (state.tokens[endDelim.token - 1].type === 'text' &&
        state.tokens[endDelim.token - 1].content === '~') {
      loneMarkers.push(endDelim.token - 1);
    }
  }

  // If a marker sequence has an odd number of characters, it's splitted
  // like this: `~~~~~` -> `~` + `~~` + `~~`, leaving one marker at the
  // start of the sequence.
  //
  // So, we have to move all those markers after subsequent s_close tags.
  //
  while (loneMarkers.length) {
    const i = loneMarkers.pop();
    let j = i + 1;

    while (j < state.tokens.length && state.tokens[j].type === 's_close') {
      j++;
    }

    j--;

    if (i !== j) {
      token = state.tokens[j];
      state.tokens[j] = state.tokens[i];
      state.tokens[i] = token;
    }
  }
}

// Walk through delimiter list and replace text tokens with tags
//
function strikethrough_postProcess (state) {
  const tokens_meta = state.tokens_meta;
  const max = state.tokens_meta.length;

  postProcess$1(state, state.delimiters);

  for (let curr = 0; curr < max; curr++) {
    if (tokens_meta[curr] && tokens_meta[curr].delimiters) {
      postProcess$1(state, tokens_meta[curr].delimiters);
    }
  }
}

var r_strikethrough = {
  tokenize: strikethrough_tokenize,
  postProcess: strikethrough_postProcess
};

// Process *this* and _that_
//

// Insert each marker as a separate text token, and add it to delimiter list
//
function emphasis_tokenize (state, silent) {
  const start = state.pos;
  const marker = state.src.charCodeAt(start);

  if (silent) { return false }

  if (marker !== 0x5F /* _ */ && marker !== 0x2A /* * */) { return false }

  const scanned = state.scanDelims(state.pos, marker === 0x2A);

  for (let i = 0; i < scanned.length; i++) {
    const token = state.push('text', '', 0);
    token.content = String.fromCharCode(marker);

    state.delimiters.push({
      // Char code of the starting marker (number).
      //
      marker,

      // Total length of these series of delimiters.
      //
      length: scanned.length,

      // A position of the token this delimiter corresponds to.
      //
      token: state.tokens.length - 1,

      // If this delimiter is matched as a valid opener, `end` will be
      // equal to its position, otherwise it's `-1`.
      //
      end: -1,

      // Boolean flags that determine if this delimiter could open or close
      // an emphasis.
      //
      open: scanned.can_open,
      close: scanned.can_close
    });
  }

  state.pos += scanned.length;

  return true
}

function postProcess (state, delimiters) {
  const max = delimiters.length;

  for (let i = max - 1; i >= 0; i--) {
    const startDelim = delimiters[i];

    if (startDelim.marker !== 0x5F/* _ */ && startDelim.marker !== 0x2A/* * */) {
      continue
    }

    // Process only opening markers
    if (startDelim.end === -1) {
      continue
    }

    const endDelim = delimiters[startDelim.end];

    // If the previous delimiter has the same marker and is adjacent to this one,
    // merge those into one strong delimiter.
    //
    // `<em><em>whatever</em></em>` -> `<strong>whatever</strong>`
    //
    const isStrong = i > 0 &&
               delimiters[i - 1].end === startDelim.end + 1 &&
               // check that first two markers match and adjacent
               delimiters[i - 1].marker === startDelim.marker &&
               delimiters[i - 1].token === startDelim.token - 1 &&
               // check that last two markers are adjacent (we can safely assume they match)
               delimiters[startDelim.end + 1].token === endDelim.token + 1;

    const ch = String.fromCharCode(startDelim.marker);

    const token_o   = state.tokens[startDelim.token];
    token_o.type    = isStrong ? 'strong_open' : 'em_open';
    token_o.tag     = isStrong ? 'strong' : 'em';
    token_o.nesting = 1;
    token_o.markup  = isStrong ? ch + ch : ch;
    token_o.content = '';

    const token_c   = state.tokens[endDelim.token];
    token_c.type    = isStrong ? 'strong_close' : 'em_close';
    token_c.tag     = isStrong ? 'strong' : 'em';
    token_c.nesting = -1;
    token_c.markup  = isStrong ? ch + ch : ch;
    token_c.content = '';

    if (isStrong) {
      state.tokens[delimiters[i - 1].token].content = '';
      state.tokens[delimiters[startDelim.end + 1].token].content = '';
      i--;
    }
  }
}

// Walk through delimiter list and replace text tokens with tags
//
function emphasis_post_process (state) {
  const tokens_meta = state.tokens_meta;
  const max = state.tokens_meta.length;

  postProcess(state, state.delimiters);

  for (let curr = 0; curr < max; curr++) {
    if (tokens_meta[curr] && tokens_meta[curr].delimiters) {
      postProcess(state, tokens_meta[curr].delimiters);
    }
  }
}

var r_emphasis = {
  tokenize: emphasis_tokenize,
  postProcess: emphasis_post_process
};

// Process [link](<to> "stuff")


function link (state, silent) {
  let code, label, res, ref;
  let href = '';
  let title = '';
  let start = state.pos;
  let parseReference = true;

  if (state.src.charCodeAt(state.pos) !== 0x5B/* [ */) { return false }

  const oldPos = state.pos;
  const max = state.posMax;
  const labelStart = state.pos + 1;
  const labelEnd = state.md.helpers.parseLinkLabel(state, state.pos, true);

  // parser failed to find ']', so it's not a valid link
  if (labelEnd < 0) { return false }

  let pos = labelEnd + 1;
  if (pos < max && state.src.charCodeAt(pos) === 0x28/* ( */) {
    //
    // Inline link
    //

    // might have found a valid shortcut link, disable reference parsing
    parseReference = false;

    // [link](  <href>  "title"  )
    //        ^^ skipping these spaces
    pos++;
    for (; pos < max; pos++) {
      code = state.src.charCodeAt(pos);
      if (!isSpace(code) && code !== 0x0A) { break }
    }
    if (pos >= max) { return false }

    // [link](  <href>  "title"  )
    //          ^^^^^^ parsing link destination
    start = pos;
    res = state.md.helpers.parseLinkDestination(state.src, pos, state.posMax);
    if (res.ok) {
      href = state.md.normalizeLink(res.str);
      if (state.md.validateLink(href)) {
        pos = res.pos;
      } else {
        href = '';
      }

      // [link](  <href>  "title"  )
      //                ^^ skipping these spaces
      start = pos;
      for (; pos < max; pos++) {
        code = state.src.charCodeAt(pos);
        if (!isSpace(code) && code !== 0x0A) { break }
      }

      // [link](  <href>  "title"  )
      //                  ^^^^^^^ parsing link title
      res = state.md.helpers.parseLinkTitle(state.src, pos, state.posMax);
      if (pos < max && start !== pos && res.ok) {
        title = res.str;
        pos = res.pos;

        // [link](  <href>  "title"  )
        //                         ^^ skipping these spaces
        for (; pos < max; pos++) {
          code = state.src.charCodeAt(pos);
          if (!isSpace(code) && code !== 0x0A) { break }
        }
      }
    }

    if (pos >= max || state.src.charCodeAt(pos) !== 0x29/* ) */) {
      // parsing a valid shortcut link failed, fallback to reference
      parseReference = true;
    }
    pos++;
  }

  if (parseReference) {
    //
    // Link reference
    //
    if (typeof state.env.references === 'undefined') { return false }

    if (pos < max && state.src.charCodeAt(pos) === 0x5B/* [ */) {
      start = pos + 1;
      pos = state.md.helpers.parseLinkLabel(state, pos);
      if (pos >= 0) {
        label = state.src.slice(start, pos++);
      } else {
        pos = labelEnd + 1;
      }
    } else {
      pos = labelEnd + 1;
    }

    // covers label === '' and label === undefined
    // (collapsed reference link and shortcut reference link respectively)
    if (!label) { label = state.src.slice(labelStart, labelEnd); }

    ref = state.env.references[normalizeReference(label)];
    if (!ref) {
      state.pos = oldPos;
      return false
    }
    href = ref.href;
    title = ref.title;
  }

  //
  // We found the end of the link, and know for a fact it's a valid link;
  // so all that's left to do is to call tokenizer.
  //
  if (!silent) {
    state.pos = labelStart;
    state.posMax = labelEnd;

    const token_o = state.push('link_open', 'a', 1);
    const attrs = [['href', href]];
    token_o.attrs  = attrs;
    if (title) {
      attrs.push(['title', title]);
    }

    state.linkLevel++;
    state.md.inline.tokenize(state);
    state.linkLevel--;

    state.push('link_close', 'a', -1);
  }

  state.pos = pos;
  state.posMax = max;
  return true
}

// Process ![image](<src> "title")


function image (state, silent) {
  let code, content, label, pos, ref, res, title, start;
  let href = '';
  const oldPos = state.pos;
  const max = state.posMax;

  if (state.src.charCodeAt(state.pos) !== 0x21/* ! */) { return false }
  if (state.src.charCodeAt(state.pos + 1) !== 0x5B/* [ */) { return false }

  const labelStart = state.pos + 2;
  const labelEnd = state.md.helpers.parseLinkLabel(state, state.pos + 1, false);

  // parser failed to find ']', so it's not a valid link
  if (labelEnd < 0) { return false }

  pos = labelEnd + 1;
  if (pos < max && state.src.charCodeAt(pos) === 0x28/* ( */) {
    //
    // Inline link
    //

    // [link](  <href>  "title"  )
    //        ^^ skipping these spaces
    pos++;
    for (; pos < max; pos++) {
      code = state.src.charCodeAt(pos);
      if (!isSpace(code) && code !== 0x0A) { break }
    }
    if (pos >= max) { return false }

    // [link](  <href>  "title"  )
    //          ^^^^^^ parsing link destination
    start = pos;
    res = state.md.helpers.parseLinkDestination(state.src, pos, state.posMax);
    if (res.ok) {
      href = state.md.normalizeLink(res.str);
      if (state.md.validateLink(href)) {
        pos = res.pos;
      } else {
        href = '';
      }
    }

    // [link](  <href>  "title"  )
    //                ^^ skipping these spaces
    start = pos;
    for (; pos < max; pos++) {
      code = state.src.charCodeAt(pos);
      if (!isSpace(code) && code !== 0x0A) { break }
    }

    // [link](  <href>  "title"  )
    //                  ^^^^^^^ parsing link title
    res = state.md.helpers.parseLinkTitle(state.src, pos, state.posMax);
    if (pos < max && start !== pos && res.ok) {
      title = res.str;
      pos = res.pos;

      // [link](  <href>  "title"  )
      //                         ^^ skipping these spaces
      for (; pos < max; pos++) {
        code = state.src.charCodeAt(pos);
        if (!isSpace(code) && code !== 0x0A) { break }
      }
    } else {
      title = '';
    }

    if (pos >= max || state.src.charCodeAt(pos) !== 0x29/* ) */) {
      state.pos = oldPos;
      return false
    }
    pos++;
  } else {
    //
    // Link reference
    //
    if (typeof state.env.references === 'undefined') { return false }

    if (pos < max && state.src.charCodeAt(pos) === 0x5B/* [ */) {
      start = pos + 1;
      pos = state.md.helpers.parseLinkLabel(state, pos);
      if (pos >= 0) {
        label = state.src.slice(start, pos++);
      } else {
        pos = labelEnd + 1;
      }
    } else {
      pos = labelEnd + 1;
    }

    // covers label === '' and label === undefined
    // (collapsed reference link and shortcut reference link respectively)
    if (!label) { label = state.src.slice(labelStart, labelEnd); }

    ref = state.env.references[normalizeReference(label)];
    if (!ref) {
      state.pos = oldPos;
      return false
    }
    href = ref.href;
    title = ref.title;
  }

  //
  // We found the end of the link, and know for a fact it's a valid link;
  // so all that's left to do is to call tokenizer.
  //
  if (!silent) {
    content = state.src.slice(labelStart, labelEnd);

    const tokens = [];
    state.md.inline.parse(
      content,
      state.md,
      state.env,
      tokens
    );

    const token = state.push('image', 'img', 0);
    const attrs = [['src', href], ['alt', '']];
    token.attrs = attrs;
    token.children = tokens;
    token.content = content;

    if (title) {
      attrs.push(['title', title]);
    }
  }

  state.pos = pos;
  state.posMax = max;
  return true
}

// Process autolinks '<protocol:...>'

/* eslint max-len:0 */
const EMAIL_RE    = /^([a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*)$/;
/* eslint-disable-next-line no-control-regex */
const AUTOLINK_RE = /^([a-zA-Z][a-zA-Z0-9+.-]{1,31}):([^<>\x00-\x20]*)$/;

function autolink (state, silent) {
  let pos = state.pos;

  if (state.src.charCodeAt(pos) !== 0x3C/* < */) { return false }

  const start = state.pos;
  const max = state.posMax;

  for (;;) {
    if (++pos >= max) return false

    const ch = state.src.charCodeAt(pos);

    if (ch === 0x3C /* < */) return false
    if (ch === 0x3E /* > */) break
  }

  const url = state.src.slice(start + 1, pos);

  if (AUTOLINK_RE.test(url)) {
    const fullUrl = state.md.normalizeLink(url);
    if (!state.md.validateLink(fullUrl)) { return false }

    if (!silent) {
      const token_o   = state.push('link_open', 'a', 1);
      token_o.attrs   = [['href', fullUrl]];
      token_o.markup  = 'autolink';
      token_o.info    = 'auto';

      const token_t   = state.push('text', '', 0);
      token_t.content = state.md.normalizeLinkText(url);

      const token_c   = state.push('link_close', 'a', -1);
      token_c.markup  = 'autolink';
      token_c.info    = 'auto';
    }

    state.pos += url.length + 2;
    return true
  }

  if (EMAIL_RE.test(url)) {
    const fullUrl = state.md.normalizeLink('mailto:' + url);
    if (!state.md.validateLink(fullUrl)) { return false }

    if (!silent) {
      const token_o   = state.push('link_open', 'a', 1);
      token_o.attrs   = [['href', fullUrl]];
      token_o.markup  = 'autolink';
      token_o.info    = 'auto';

      const token_t   = state.push('text', '', 0);
      token_t.content = state.md.normalizeLinkText(url);

      const token_c   = state.push('link_close', 'a', -1);
      token_c.markup  = 'autolink';
      token_c.info    = 'auto';
    }

    state.pos += url.length + 2;
    return true
  }

  return false
}

// Process html tags


function isLinkOpen (str) {
  return /^<a[>\s]/i.test(str)
}
function isLinkClose (str) {
  return /^<\/a\s*>/i.test(str)
}

function isLetter (ch) {
  /* eslint no-bitwise:0 */
  const lc = ch | 0x20; // to lower case
  return (lc >= 0x61/* a */) && (lc <= 0x7a/* z */)
}

function html_inline (state, silent) {
  if (!state.md.options.html) { return false }

  // Check start
  const max = state.posMax;
  const pos = state.pos;
  if (state.src.charCodeAt(pos) !== 0x3C/* < */ ||
      pos + 2 >= max) {
    return false
  }

  // Quick fail on second char
  const ch = state.src.charCodeAt(pos + 1);
  if (ch !== 0x21/* ! */ &&
      ch !== 0x3F/* ? */ &&
      ch !== 0x2F/* / */ &&
      !isLetter(ch)) {
    return false
  }

  const match = state.src.slice(pos).match(HTML_TAG_RE);
  if (!match) { return false }

  if (!silent) {
    const token = state.push('html_inline', '', 0);
    token.content = match[0];

    if (isLinkOpen(token.content))  state.linkLevel++;
    if (isLinkClose(token.content)) state.linkLevel--;
  }
  state.pos += match[0].length;
  return true
}

// Process html entity - &#123;, &#xAF;, &quot;, ...


const DIGITAL_RE = /^&#((?:x[a-f0-9]{1,6}|[0-9]{1,7}));/i;
const NAMED_RE   = /^&([a-z][a-z0-9]{1,31});/i;

function entity (state, silent) {
  const pos = state.pos;
  const max = state.posMax;

  if (state.src.charCodeAt(pos) !== 0x26/* & */) return false

  if (pos + 1 >= max) return false

  const ch = state.src.charCodeAt(pos + 1);

  if (ch === 0x23 /* # */) {
    const match = state.src.slice(pos).match(DIGITAL_RE);
    if (match) {
      if (!silent) {
        const code = match[1][0].toLowerCase() === 'x' ? parseInt(match[1].slice(1), 16) : parseInt(match[1], 10);

        const token   = state.push('text_special', '', 0);
        token.content = isValidEntityCode(code) ? fromCodePoint(code) : fromCodePoint(0xFFFD);
        token.markup  = match[0];
        token.info    = 'entity';
      }
      state.pos += match[0].length;
      return true
    }
  } else {
    const match = state.src.slice(pos).match(NAMED_RE);
    if (match) {
      const decoded = decodeHTML(match[0]);
      if (decoded !== match[0]) {
        if (!silent) {
          const token   = state.push('text_special', '', 0);
          token.content = decoded;
          token.markup  = match[0];
          token.info    = 'entity';
        }
        state.pos += match[0].length;
        return true
      }
    }
  }

  return false
}

// For each opening emphasis-like marker find a matching closing one
//

function processDelimiters (delimiters) {
  const openersBottom = {};
  const max = delimiters.length;

  if (!max) return

  // headerIdx is the first delimiter of the current (where closer is) delimiter run
  let headerIdx = 0;
  let lastTokenIdx = -2; // needs any value lower than -1
  const jumps = [];

  for (let closerIdx = 0; closerIdx < max; closerIdx++) {
    const closer = delimiters[closerIdx];

    jumps.push(0);

    // markers belong to same delimiter run if:
    //  - they have adjacent tokens
    //  - AND markers are the same
    //
    if (delimiters[headerIdx].marker !== closer.marker || lastTokenIdx !== closer.token - 1) {
      headerIdx = closerIdx;
    }

    lastTokenIdx = closer.token;

    // Length is only used for emphasis-specific "rule of 3",
    // if it's not defined (in strikethrough or 3rd party plugins),
    // we can default it to 0 to disable those checks.
    //
    closer.length = closer.length || 0;

    if (!closer.close) continue

    // Previously calculated lower bounds (previous fails)
    // for each marker, each delimiter length modulo 3,
    // and for whether this closer can be an opener;
    // https://github.com/commonmark/cmark/commit/34250e12ccebdc6372b8b49c44fab57c72443460
    /* eslint-disable-next-line no-prototype-builtins */
    if (!openersBottom.hasOwnProperty(closer.marker)) {
      openersBottom[closer.marker] = [-1, -1, -1, -1, -1, -1];
    }

    const minOpenerIdx = openersBottom[closer.marker][(closer.open ? 3 : 0) + (closer.length % 3)];

    let openerIdx = headerIdx - jumps[headerIdx] - 1;

    let newMinOpenerIdx = openerIdx;

    for (; openerIdx > minOpenerIdx; openerIdx -= jumps[openerIdx] + 1) {
      const opener = delimiters[openerIdx];

      if (opener.marker !== closer.marker) continue

      if (opener.open && opener.end < 0) {
        let isOddMatch = false;

        // from spec:
        //
        // If one of the delimiters can both open and close emphasis, then the
        // sum of the lengths of the delimiter runs containing the opening and
        // closing delimiters must not be a multiple of 3 unless both lengths
        // are multiples of 3.
        //
        if (opener.close || closer.open) {
          if ((opener.length + closer.length) % 3 === 0) {
            if (opener.length % 3 !== 0 || closer.length % 3 !== 0) {
              isOddMatch = true;
            }
          }
        }

        if (!isOddMatch) {
          // If previous delimiter cannot be an opener, we can safely skip
          // the entire sequence in future checks. This is required to make
          // sure algorithm has linear complexity (see *_*_*_*_*_... case).
          //
          const lastJump = openerIdx > 0 && !delimiters[openerIdx - 1].open
            ? jumps[openerIdx - 1] + 1
            : 0;

          jumps[closerIdx] = closerIdx - openerIdx + lastJump;
          jumps[openerIdx] = lastJump;

          closer.open  = false;
          opener.end   = closerIdx;
          opener.close = false;
          newMinOpenerIdx = -1;
          // treat next token as start of run,
          // it optimizes skips in **<...>**a**<...>** pathological case
          lastTokenIdx = -2;
          break
        }
      }
    }

    if (newMinOpenerIdx !== -1) {
      // If match for this delimiter run failed, we want to set lower bound for
      // future lookups. This is required to make sure algorithm has linear
      // complexity.
      //
      // See details here:
      // https://github.com/commonmark/cmark/issues/178#issuecomment-270417442
      //
      openersBottom[closer.marker][(closer.open ? 3 : 0) + ((closer.length || 0) % 3)] = newMinOpenerIdx;
    }
  }
}

function link_pairs (state) {
  const tokens_meta = state.tokens_meta;
  const max = state.tokens_meta.length;

  processDelimiters(state.delimiters);

  for (let curr = 0; curr < max; curr++) {
    if (tokens_meta[curr] && tokens_meta[curr].delimiters) {
      processDelimiters(tokens_meta[curr].delimiters);
    }
  }
}

// Clean up tokens after emphasis and strikethrough postprocessing:
// merge adjacent text nodes into one and re-calculate all token levels
//
// This is necessary because initially emphasis delimiter markers (*, _, ~)
// are treated as their own separate text tokens. Then emphasis rule either
// leaves them as text (needed to merge with adjacent text) or turns them
// into opening/closing tags (which messes up levels inside).
//

function fragments_join (state) {
  let curr, last;
  let level = 0;
  const tokens = state.tokens;
  const max = state.tokens.length;

  for (curr = last = 0; curr < max; curr++) {
    // re-calculate levels after emphasis/strikethrough turns some text nodes
    // into opening/closing tags
    if (tokens[curr].nesting < 0) level--; // closing tag
    tokens[curr].level = level;
    if (tokens[curr].nesting > 0) level++; // opening tag

    if (tokens[curr].type === 'text' &&
        curr + 1 < max &&
        tokens[curr + 1].type === 'text') {
      // collapse two adjacent text nodes
      tokens[curr + 1].content = tokens[curr].content + tokens[curr + 1].content;
    } else {
      if (curr !== last) { tokens[last] = tokens[curr]; }

      last++;
    }
  }

  if (curr !== last) {
    tokens.length = last;
  }
}

/** internal
 * class ParserInline
 *
 * Tokenizes paragraph content.
 **/


// Parser rules

const _rules = [
  ['text',            text$1],
  ['linkify',         linkify],
  ['newline',         newline],
  ['escape',          escape$1],
  ['backticks',       backtick],
  ['strikethrough',   r_strikethrough.tokenize],
  ['emphasis',        r_emphasis.tokenize],
  ['link',            link],
  ['image',           image],
  ['autolink',        autolink],
  ['html_inline',     html_inline],
  ['entity',          entity]
];

// `rule2` ruleset was created specifically for emphasis/strikethrough
// post-processing and may be changed in the future.
//
// Don't use this for anything except pairs (plugins working with `balance_pairs`).
//
const _rules2 = [
  ['balance_pairs',   link_pairs],
  ['strikethrough',   r_strikethrough.postProcess],
  ['emphasis',        r_emphasis.postProcess],
  // rules for pairs separate '**' into its own text tokens, which may be left unused,
  // rule below merges unused segments back with the rest of the text
  ['fragments_join',  fragments_join]
];

/**
 * new ParserInline()
 **/
function ParserInline () {
  /**
   * ParserInline#ruler -> Ruler
   *
   * [[Ruler]] instance. Keep configuration of inline rules.
   **/
  this.ruler = new Ruler();

  for (let i = 0; i < _rules.length; i++) {
    this.ruler.push(_rules[i][0], _rules[i][1]);
  }

  /**
   * ParserInline#ruler2 -> Ruler
   *
   * [[Ruler]] instance. Second ruler used for post-processing
   * (e.g. in emphasis-like rules).
   **/
  this.ruler2 = new Ruler();

  for (let i = 0; i < _rules2.length; i++) {
    this.ruler2.push(_rules2[i][0], _rules2[i][1]);
  }
}

// Skip single token by running all rules in validation mode;
// returns `true` if any rule reported success
//
ParserInline.prototype.skipToken = function (state) {
  const pos = state.pos;
  const rules = this.ruler.getRules('');
  const len = rules.length;
  const maxNesting = state.md.options.maxNesting;
  const cache = state.cache;

  if (typeof cache[pos] !== 'undefined') {
    state.pos = cache[pos];
    return
  }

  let ok = false;

  if (state.level < maxNesting) {
    for (let i = 0; i < len; i++) {
      // Increment state.level and decrement it later to limit recursion.
      // It's harmless to do here, because no tokens are created. But ideally,
      // we'd need a separate private state variable for this purpose.
      //
      state.level++;
      ok = rules[i](state, true);
      state.level--;

      if (ok) {
        if (pos >= state.pos) { throw new Error("inline rule didn't increment state.pos") }
        break
      }
    }
  } else {
    // Too much nesting, just skip until the end of the paragraph.
    //
    // NOTE: this will cause links to behave incorrectly in the following case,
    //       when an amount of `[` is exactly equal to `maxNesting + 1`:
    //
    //       [[[[[[[[[[[[[[[[[[[[[foo]()
    //
    // TODO: remove this workaround when CM standard will allow nested links
    //       (we can replace it by preventing links from being parsed in
    //       validation mode)
    //
    state.pos = state.posMax;
  }

  if (!ok) { state.pos++; }
  cache[pos] = state.pos;
};

// Generate tokens for input range
//
ParserInline.prototype.tokenize = function (state) {
  const rules = this.ruler.getRules('');
  const len = rules.length;
  const end = state.posMax;
  const maxNesting = state.md.options.maxNesting;

  while (state.pos < end) {
    // Try all possible rules.
    // On success, rule should:
    //
    // - update `state.pos`
    // - update `state.tokens`
    // - return true
    const prevPos = state.pos;
    let ok = false;

    if (state.level < maxNesting) {
      for (let i = 0; i < len; i++) {
        ok = rules[i](state, false);
        if (ok) {
          if (prevPos >= state.pos) { throw new Error("inline rule didn't increment state.pos") }
          break
        }
      }
    }

    if (ok) {
      if (state.pos >= end) { break }
      continue
    }

    state.pending += state.src[state.pos++];
  }

  if (state.pending) {
    state.pushPending();
  }
};

/**
 * ParserInline.parse(str, md, env, outTokens)
 *
 * Process input string and push inline tokens into `outTokens`
 **/
ParserInline.prototype.parse = function (str, md, env, outTokens) {
  const state = new this.State(str, md, env, outTokens);

  this.tokenize(state);

  const rules = this.ruler2.getRules('');
  const len = rules.length;

  for (let i = 0; i < len; i++) {
    rules[i](state);
  }
};

ParserInline.prototype.State = StateInline;

function reFactory (opts) {
  const re = {};
  opts = opts || {};

  re.src_Any = Any.source;
  re.src_Cc = Cc.source;
  re.src_Z = Z.source;
  re.src_P = P.source;

  // \p{\Z\P\Cc\CF} (white spaces + control + format + punctuation)
  re.src_ZPCc = [re.src_Z, re.src_P, re.src_Cc].join('|');

  // \p{\Z\Cc} (white spaces + control)
  re.src_ZCc = [re.src_Z, re.src_Cc].join('|');

  // Experimental. List of chars, completely prohibited in links
  // because can separate it from other part of text
  const text_separators = '[><\uff5c]';

  // All possible word characters (everything without punctuation, spaces & controls)
  // Defined via punctuation & spaces to save space
  // Should be something like \p{\L\N\S\M} (\w but without `_`)
  re.src_pseudo_letter = '(?:(?!' + text_separators + '|' + re.src_ZPCc + ')' + re.src_Any + ')';
  // The same as abothe but without [0-9]
  // var src_pseudo_letter_non_d = '(?:(?![0-9]|' + src_ZPCc + ')' + src_Any + ')';

  re.src_ip4 =

    '(?:(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)';

  // Prohibit any of "@/[]()" in user/pass to avoid wrong domain fetch.
  re.src_auth = '(?:(?:(?!' + re.src_ZCc + '|[@/\\[\\]()]).)+@)?';

  re.src_port =

    '(?::(?:6(?:[0-4]\\d{3}|5(?:[0-4]\\d{2}|5(?:[0-2]\\d|3[0-5])))|[1-5]?\\d{1,4}))?';

  re.src_host_terminator =

    '(?=$|' + text_separators + '|' + re.src_ZPCc + ')' +
    '(?!' + (opts['---'] ? '-(?!--)|' : '-|') + '_|:\\d|\\.-|\\.(?!$|' + re.src_ZPCc + '))';

  re.src_path =

    '(?:' +
      '[/?#]' +
        '(?:' +
          '(?!' + re.src_ZCc + '|' + text_separators + '|[()[\\]{}.,"\'?!\\-;]).|' +
          '\\[(?:(?!' + re.src_ZCc + '|\\]).)*\\]|' +
          '\\((?:(?!' + re.src_ZCc + '|[)]).)*\\)|' +
          '\\{(?:(?!' + re.src_ZCc + '|[}]).)*\\}|' +
          '\\"(?:(?!' + re.src_ZCc + '|["]).)+\\"|' +
          "\\'(?:(?!" + re.src_ZCc + "|[']).)+\\'|" +

          // allow `I'm_king` if no pair found
          "\\'(?=" + re.src_pseudo_letter + '|[-])|' +

          // google has many dots in "google search" links (#66, #81).
          // github has ... in commit range links,
          // Restrict to
          // - english
          // - percent-encoded
          // - parts of file path
          // - params separator
          // until more examples found.
          '\\.{2,}[a-zA-Z0-9%/&]|' +

          '\\.(?!' + re.src_ZCc + '|[.]|$)|' +
          (opts['---']
            ? '\\-(?!--(?:[^-]|$))(?:-*)|' // `---` => long dash, terminate
            : '\\-+|'
          ) +
          // allow `,,,` in paths
          ',(?!' + re.src_ZCc + '|$)|' +

          // allow `;` if not followed by space-like char
          ';(?!' + re.src_ZCc + '|$)|' +

          // allow `!!!` in paths, but not at the end
          '\\!+(?!' + re.src_ZCc + '|[!]|$)|' +

          '\\?(?!' + re.src_ZCc + '|[?]|$)' +
        ')+' +
      '|\\/' +
    ')?';

  // Allow anything in markdown spec, forbid quote (") at the first position
  // because emails enclosed in quotes are far more common
  re.src_email_name =

    '[\\-;:&=\\+\\$,\\.a-zA-Z0-9_][\\-;:&=\\+\\$,\\"\\.a-zA-Z0-9_]*';

  re.src_xn =

    'xn--[a-z0-9\\-]{1,59}';

  // More to read about domain names
  // http://serverfault.com/questions/638260/

  re.src_domain_root =

    // Allow letters & digits (http://test1)
    '(?:' +
      re.src_xn +
      '|' +
      re.src_pseudo_letter + '{1,63}' +
    ')';

  re.src_domain =

    '(?:' +
      re.src_xn +
      '|' +
      '(?:' + re.src_pseudo_letter + ')' +
      '|' +
      '(?:' + re.src_pseudo_letter + '(?:-|' + re.src_pseudo_letter + '){0,61}' + re.src_pseudo_letter + ')' +
    ')';

  re.src_host =

    '(?:' +
    // Don't need IP check, because digits are already allowed in normal domain names
    //   src_ip4 +
    // '|' +
      '(?:(?:(?:' + re.src_domain + ')\\.)*' + re.src_domain/* _root */ + ')' +
    ')';

  re.tpl_host_fuzzy =

    '(?:' +
      re.src_ip4 +
    '|' +
      '(?:(?:(?:' + re.src_domain + ')\\.)+(?:%TLDS%))' +
    ')';

  re.tpl_host_no_ip_fuzzy =

    '(?:(?:(?:' + re.src_domain + ')\\.)+(?:%TLDS%))';

  re.src_host_strict =

    re.src_host + re.src_host_terminator;

  re.tpl_host_fuzzy_strict =

    re.tpl_host_fuzzy + re.src_host_terminator;

  re.src_host_port_strict =

    re.src_host + re.src_port + re.src_host_terminator;

  re.tpl_host_port_fuzzy_strict =

    re.tpl_host_fuzzy + re.src_port + re.src_host_terminator;

  re.tpl_host_port_no_ip_fuzzy_strict =

    re.tpl_host_no_ip_fuzzy + re.src_port + re.src_host_terminator;

  //
  // Main rules
  //

  // Rude test fuzzy links by host, for quick deny
  re.tpl_host_fuzzy_test =

    'localhost|www\\.|\\.\\d{1,3}\\.|(?:\\.(?:%TLDS%)(?:' + re.src_ZPCc + '|>|$))';

  re.tpl_email_fuzzy =

      '(^|' + text_separators + '|"|\\(|' + re.src_ZCc + ')' +
      '(' + re.src_email_name + '@' + re.tpl_host_fuzzy_strict + ')';

  re.tpl_link_fuzzy =
      // Fuzzy link can't be prepended with .:/\- and non punctuation.
      // but can start with > (markdown blockquote)
      '(^|(?![.:/\\-_@])(?:[$+<=>^`|\uff5c]|' + re.src_ZPCc + '))' +
      '((?![$+<=>^`|\uff5c])' + re.tpl_host_port_fuzzy_strict + re.src_path + ')';

  re.tpl_link_no_ip_fuzzy =
      // Fuzzy link can't be prepended with .:/\- and non punctuation.
      // but can start with > (markdown blockquote)
      '(^|(?![.:/\\-_@])(?:[$+<=>^`|\uff5c]|' + re.src_ZPCc + '))' +
      '((?![$+<=>^`|\uff5c])' + re.tpl_host_port_no_ip_fuzzy_strict + re.src_path + ')';

  return re
}

//
// Helpers
//

// Merge objects
//
function assign (obj /* from1, from2, from3, ... */) {
  const sources = Array.prototype.slice.call(arguments, 1);

  sources.forEach(function (source) {
    if (!source) { return }

    Object.keys(source).forEach(function (key) {
      obj[key] = source[key];
    });
  });

  return obj
}

function _class (obj) { return Object.prototype.toString.call(obj) }
function isString (obj) { return _class(obj) === '[object String]' }
function isObject (obj) { return _class(obj) === '[object Object]' }
function isRegExp (obj) { return _class(obj) === '[object RegExp]' }
function isFunction (obj) { return _class(obj) === '[object Function]' }

function escapeRE (str) { return str.replace(/[.?*+^$[\]\\(){}|-]/g, '\\$&') }

//

const defaultOptions$1 = {
  fuzzyLink: true,
  fuzzyEmail: true,
  fuzzyIP: false
};

function isOptionsObj (obj) {
  return Object.keys(obj || {}).reduce(function (acc, k) {
    /* eslint-disable-next-line no-prototype-builtins */
    return acc || defaultOptions$1.hasOwnProperty(k)
  }, false)
}

const defaultSchemas = {
  'http:': {
    validate: function (text, pos, self) {
      const tail = text.slice(pos);

      if (!self.re.http) {
        // compile lazily, because "host"-containing variables can change on tlds update.
        self.re.http = new RegExp(
          '^\\/\\/' + self.re.src_auth + self.re.src_host_port_strict + self.re.src_path, 'i'
        );
      }
      if (self.re.http.test(tail)) {
        return tail.match(self.re.http)[0].length
      }
      return 0
    }
  },
  'https:': 'http:',
  'ftp:': 'http:',
  '//': {
    validate: function (text, pos, self) {
      const tail = text.slice(pos);

      if (!self.re.no_http) {
      // compile lazily, because "host"-containing variables can change on tlds update.
        self.re.no_http = new RegExp(
          '^' +
          self.re.src_auth +
          // Don't allow single-level domains, because of false positives like '//test'
          // with code comments
          '(?:localhost|(?:(?:' + self.re.src_domain + ')\\.)+' + self.re.src_domain_root + ')' +
          self.re.src_port +
          self.re.src_host_terminator +
          self.re.src_path,

          'i'
        );
      }

      if (self.re.no_http.test(tail)) {
        // should not be `://` & `///`, that protects from errors in protocol name
        if (pos >= 3 && text[pos - 3] === ':') { return 0 }
        if (pos >= 3 && text[pos - 3] === '/') { return 0 }
        return tail.match(self.re.no_http)[0].length
      }
      return 0
    }
  },
  'mailto:': {
    validate: function (text, pos, self) {
      const tail = text.slice(pos);

      if (!self.re.mailto) {
        self.re.mailto = new RegExp(
          '^' + self.re.src_email_name + '@' + self.re.src_host_strict, 'i'
        );
      }
      if (self.re.mailto.test(tail)) {
        return tail.match(self.re.mailto)[0].length
      }
      return 0
    }
  }
};

// RE pattern for 2-character tlds (autogenerated by ./support/tlds_2char_gen.js)
/* eslint-disable-next-line max-len */
const tlds_2ch_src_re = 'a[cdefgilmnoqrstuwxz]|b[abdefghijmnorstvwyz]|c[acdfghiklmnoruvwxyz]|d[ejkmoz]|e[cegrstu]|f[ijkmor]|g[abdefghilmnpqrstuwy]|h[kmnrtu]|i[delmnoqrst]|j[emop]|k[eghimnprwyz]|l[abcikrstuvy]|m[acdeghklmnopqrstuvwxyz]|n[acefgilopruz]|om|p[aefghklmnrstwy]|qa|r[eosuw]|s[abcdeghijklmnortuvxyz]|t[cdfghjklmnortvwz]|u[agksyz]|v[aceginu]|w[fs]|y[et]|z[amw]';

// DON'T try to make PRs with changes. Extend TLDs with LinkifyIt.tlds() instead
const tlds_default = 'biz|com|edu|gov|net|org|pro|web|xxx|aero|asia|coop|info|museum|name|shop|рф'.split('|');

function resetScanCache (self) {
  self.__index__ = -1;
  self.__text_cache__ = '';
}

function createValidator (re) {
  return function (text, pos) {
    const tail = text.slice(pos);

    if (re.test(tail)) {
      return tail.match(re)[0].length
    }
    return 0
  }
}

function createNormalizer () {
  return function (match, self) {
    self.normalize(match);
  }
}

// Schemas compiler. Build regexps.
//
function compile (self) {
  // Load & clone RE patterns.
  const re = self.re = reFactory(self.__opts__);

  // Define dynamic patterns
  const tlds = self.__tlds__.slice();

  self.onCompile();

  if (!self.__tlds_replaced__) {
    tlds.push(tlds_2ch_src_re);
  }
  tlds.push(re.src_xn);

  re.src_tlds = tlds.join('|');

  function untpl (tpl) { return tpl.replace('%TLDS%', re.src_tlds) }

  re.email_fuzzy = RegExp(untpl(re.tpl_email_fuzzy), 'i');
  re.link_fuzzy = RegExp(untpl(re.tpl_link_fuzzy), 'i');
  re.link_no_ip_fuzzy = RegExp(untpl(re.tpl_link_no_ip_fuzzy), 'i');
  re.host_fuzzy_test = RegExp(untpl(re.tpl_host_fuzzy_test), 'i');

  //
  // Compile each schema
  //

  const aliases = [];

  self.__compiled__ = {}; // Reset compiled data

  function schemaError (name, val) {
    throw new Error('(LinkifyIt) Invalid schema "' + name + '": ' + val)
  }

  Object.keys(self.__schemas__).forEach(function (name) {
    const val = self.__schemas__[name];

    // skip disabled methods
    if (val === null) { return }

    const compiled = { validate: null, link: null };

    self.__compiled__[name] = compiled;

    if (isObject(val)) {
      if (isRegExp(val.validate)) {
        compiled.validate = createValidator(val.validate);
      } else if (isFunction(val.validate)) {
        compiled.validate = val.validate;
      } else {
        schemaError(name, val);
      }

      if (isFunction(val.normalize)) {
        compiled.normalize = val.normalize;
      } else if (!val.normalize) {
        compiled.normalize = createNormalizer();
      } else {
        schemaError(name, val);
      }

      return
    }

    if (isString(val)) {
      aliases.push(name);
      return
    }

    schemaError(name, val);
  });

  //
  // Compile postponed aliases
  //

  aliases.forEach(function (alias) {
    if (!self.__compiled__[self.__schemas__[alias]]) {
      // Silently fail on missed schemas to avoid errons on disable.
      // schemaError(alias, self.__schemas__[alias]);
      return
    }

    self.__compiled__[alias].validate =
      self.__compiled__[self.__schemas__[alias]].validate;
    self.__compiled__[alias].normalize =
      self.__compiled__[self.__schemas__[alias]].normalize;
  });

  //
  // Fake record for guessed links
  //
  self.__compiled__[''] = { validate: null, normalize: createNormalizer() };

  //
  // Build schema condition
  //
  const slist = Object.keys(self.__compiled__)
    .filter(function (name) {
      // Filter disabled & fake schemas
      return name.length > 0 && self.__compiled__[name]
    })
    .map(escapeRE)
    .join('|');
  // (?!_) cause 1.5x slowdown
  self.re.schema_test = RegExp('(^|(?!_)(?:[><\uff5c]|' + re.src_ZPCc + '))(' + slist + ')', 'i');
  self.re.schema_search = RegExp('(^|(?!_)(?:[><\uff5c]|' + re.src_ZPCc + '))(' + slist + ')', 'ig');
  self.re.schema_at_start = RegExp('^' + self.re.schema_search.source, 'i');

  self.re.pretest = RegExp(
    '(' + self.re.schema_test.source + ')|(' + self.re.host_fuzzy_test.source + ')|@',
    'i'
  );

  //
  // Cleanup
  //

  resetScanCache(self);
}

/**
 * class Match
 *
 * Match result. Single element of array, returned by [[LinkifyIt#match]]
 **/
function Match (self, shift) {
  const start = self.__index__;
  const end = self.__last_index__;
  const text = self.__text_cache__.slice(start, end);

  /**
   * Match#schema -> String
   *
   * Prefix (protocol) for matched string.
   **/
  this.schema = self.__schema__.toLowerCase();
  /**
   * Match#index -> Number
   *
   * First position of matched string.
   **/
  this.index = start + shift;
  /**
   * Match#lastIndex -> Number
   *
   * Next position after matched string.
   **/
  this.lastIndex = end + shift;
  /**
   * Match#raw -> String
   *
   * Matched string.
   **/
  this.raw = text;
  /**
   * Match#text -> String
   *
   * Notmalized text of matched string.
   **/
  this.text = text;
  /**
   * Match#url -> String
   *
   * Normalized url of matched string.
   **/
  this.url = text;
}

function createMatch (self, shift) {
  const match = new Match(self, shift);

  self.__compiled__[match.schema].normalize(match, self);

  return match
}

/**
 * class LinkifyIt
 **/

/**
 * new LinkifyIt(schemas, options)
 * - schemas (Object): Optional. Additional schemas to validate (prefix/validator)
 * - options (Object): { fuzzyLink|fuzzyEmail|fuzzyIP: true|false }
 *
 * Creates new linkifier instance with optional additional schemas.
 * Can be called without `new` keyword for convenience.
 *
 * By default understands:
 *
 * - `http(s)://...` , `ftp://...`, `mailto:...` & `//...` links
 * - "fuzzy" links and emails (example.com, foo@bar.com).
 *
 * `schemas` is an object, where each key/value describes protocol/rule:
 *
 * - __key__ - link prefix (usually, protocol name with `:` at the end, `skype:`
 *   for example). `linkify-it` makes shure that prefix is not preceeded with
 *   alphanumeric char and symbols. Only whitespaces and punctuation allowed.
 * - __value__ - rule to check tail after link prefix
 *   - _String_ - just alias to existing rule
 *   - _Object_
 *     - _validate_ - validator function (should return matched length on success),
 *       or `RegExp`.
 *     - _normalize_ - optional function to normalize text & url of matched result
 *       (for example, for @twitter mentions).
 *
 * `options`:
 *
 * - __fuzzyLink__ - recognige URL-s without `http(s):` prefix. Default `true`.
 * - __fuzzyIP__ - allow IPs in fuzzy links above. Can conflict with some texts
 *   like version numbers. Default `false`.
 * - __fuzzyEmail__ - recognize emails without `mailto:` prefix.
 *
 **/
function LinkifyIt (schemas, options) {
  if (!(this instanceof LinkifyIt)) {
    return new LinkifyIt(schemas, options)
  }

  if (!options) {
    if (isOptionsObj(schemas)) {
      options = schemas;
      schemas = {};
    }
  }

  this.__opts__ = assign({}, defaultOptions$1, options);

  // Cache last tested result. Used to skip repeating steps on next `match` call.
  this.__index__ = -1;
  this.__last_index__ = -1; // Next scan position
  this.__schema__ = '';
  this.__text_cache__ = '';

  this.__schemas__ = assign({}, defaultSchemas, schemas);
  this.__compiled__ = {};

  this.__tlds__ = tlds_default;
  this.__tlds_replaced__ = false;

  this.re = {};

  compile(this);
}

/** chainable
 * LinkifyIt#add(schema, definition)
 * - schema (String): rule name (fixed pattern prefix)
 * - definition (String|RegExp|Object): schema definition
 *
 * Add new rule definition. See constructor description for details.
 **/
LinkifyIt.prototype.add = function add (schema, definition) {
  this.__schemas__[schema] = definition;
  compile(this);
  return this
};

/** chainable
 * LinkifyIt#set(options)
 * - options (Object): { fuzzyLink|fuzzyEmail|fuzzyIP: true|false }
 *
 * Set recognition options for links without schema.
 **/
LinkifyIt.prototype.set = function set (options) {
  this.__opts__ = assign(this.__opts__, options);
  return this
};

/**
 * LinkifyIt#test(text) -> Boolean
 *
 * Searches linkifiable pattern and returns `true` on success or `false` on fail.
 **/
LinkifyIt.prototype.test = function test (text) {
  // Reset scan cache
  this.__text_cache__ = text;
  this.__index__ = -1;

  if (!text.length) { return false }

  let m, ml, me, len, shift, next, re, tld_pos, at_pos;

  // try to scan for link with schema - that's the most simple rule
  if (this.re.schema_test.test(text)) {
    re = this.re.schema_search;
    re.lastIndex = 0;
    while ((m = re.exec(text)) !== null) {
      len = this.testSchemaAt(text, m[2], re.lastIndex);
      if (len) {
        this.__schema__ = m[2];
        this.__index__ = m.index + m[1].length;
        this.__last_index__ = m.index + m[0].length + len;
        break
      }
    }
  }

  if (this.__opts__.fuzzyLink && this.__compiled__['http:']) {
    // guess schemaless links
    tld_pos = text.search(this.re.host_fuzzy_test);
    if (tld_pos >= 0) {
      // if tld is located after found link - no need to check fuzzy pattern
      if (this.__index__ < 0 || tld_pos < this.__index__) {
        if ((ml = text.match(this.__opts__.fuzzyIP ? this.re.link_fuzzy : this.re.link_no_ip_fuzzy)) !== null) {
          shift = ml.index + ml[1].length;

          if (this.__index__ < 0 || shift < this.__index__) {
            this.__schema__ = '';
            this.__index__ = shift;
            this.__last_index__ = ml.index + ml[0].length;
          }
        }
      }
    }
  }

  if (this.__opts__.fuzzyEmail && this.__compiled__['mailto:']) {
    // guess schemaless emails
    at_pos = text.indexOf('@');
    if (at_pos >= 0) {
      // We can't skip this check, because this cases are possible:
      // 192.168.1.1@gmail.com, my.in@example.com
      if ((me = text.match(this.re.email_fuzzy)) !== null) {
        shift = me.index + me[1].length;
        next = me.index + me[0].length;

        if (this.__index__ < 0 || shift < this.__index__ ||
            (shift === this.__index__ && next > this.__last_index__)) {
          this.__schema__ = 'mailto:';
          this.__index__ = shift;
          this.__last_index__ = next;
        }
      }
    }
  }

  return this.__index__ >= 0
};

/**
 * LinkifyIt#pretest(text) -> Boolean
 *
 * Very quick check, that can give false positives. Returns true if link MAY BE
 * can exists. Can be used for speed optimization, when you need to check that
 * link NOT exists.
 **/
LinkifyIt.prototype.pretest = function pretest (text) {
  return this.re.pretest.test(text)
};

/**
 * LinkifyIt#testSchemaAt(text, name, position) -> Number
 * - text (String): text to scan
 * - name (String): rule (schema) name
 * - position (Number): text offset to check from
 *
 * Similar to [[LinkifyIt#test]] but checks only specific protocol tail exactly
 * at given position. Returns length of found pattern (0 on fail).
 **/
LinkifyIt.prototype.testSchemaAt = function testSchemaAt (text, schema, pos) {
  // If not supported schema check requested - terminate
  if (!this.__compiled__[schema.toLowerCase()]) {
    return 0
  }
  return this.__compiled__[schema.toLowerCase()].validate(text, pos, this)
};

/**
 * LinkifyIt#match(text) -> Array|null
 *
 * Returns array of found link descriptions or `null` on fail. We strongly
 * recommend to use [[LinkifyIt#test]] first, for best speed.
 *
 * ##### Result match description
 *
 * - __schema__ - link schema, can be empty for fuzzy links, or `//` for
 *   protocol-neutral  links.
 * - __index__ - offset of matched text
 * - __lastIndex__ - index of next char after mathch end
 * - __raw__ - matched text
 * - __text__ - normalized text
 * - __url__ - link, generated from matched text
 **/
LinkifyIt.prototype.match = function match (text) {
  const result = [];
  let shift = 0;

  // Try to take previous element from cache, if .test() called before
  if (this.__index__ >= 0 && this.__text_cache__ === text) {
    result.push(createMatch(this, shift));
    shift = this.__last_index__;
  }

  // Cut head if cache was used
  let tail = shift ? text.slice(shift) : text;

  // Scan string until end reached
  while (this.test(tail)) {
    result.push(createMatch(this, shift));

    tail = tail.slice(this.__last_index__);
    shift += this.__last_index__;
  }

  if (result.length) {
    return result
  }

  return null
};

/**
 * LinkifyIt#matchAtStart(text) -> Match|null
 *
 * Returns fully-formed (not fuzzy) link if it starts at the beginning
 * of the string, and null otherwise.
 **/
LinkifyIt.prototype.matchAtStart = function matchAtStart (text) {
  // Reset scan cache
  this.__text_cache__ = text;
  this.__index__ = -1;

  if (!text.length) return null

  const m = this.re.schema_at_start.exec(text);
  if (!m) return null

  const len = this.testSchemaAt(text, m[2], m[0].length);
  if (!len) return null

  this.__schema__ = m[2];
  this.__index__ = m.index + m[1].length;
  this.__last_index__ = m.index + m[0].length + len;

  return createMatch(this, 0)
};

/** chainable
 * LinkifyIt#tlds(list [, keepOld]) -> this
 * - list (Array): list of tlds
 * - keepOld (Boolean): merge with current list if `true` (`false` by default)
 *
 * Load (or merge) new tlds list. Those are user for fuzzy links (without prefix)
 * to avoid false positives. By default this algorythm used:
 *
 * - hostname with any 2-letter root zones are ok.
 * - biz|com|edu|gov|net|org|pro|web|xxx|aero|asia|coop|info|museum|name|shop|рф
 *   are ok.
 * - encoded (`xn--...`) root zones are ok.
 *
 * If list is replaced, then exact match for 2-chars root zones will be checked.
 **/
LinkifyIt.prototype.tlds = function tlds (list, keepOld) {
  list = Array.isArray(list) ? list : [list];

  if (!keepOld) {
    this.__tlds__ = list.slice();
    this.__tlds_replaced__ = true;
    compile(this);
    return this
  }

  this.__tlds__ = this.__tlds__.concat(list)
    .sort()
    .filter(function (el, idx, arr) {
      return el !== arr[idx - 1]
    })
    .reverse();

  compile(this);
  return this
};

/**
 * LinkifyIt#normalize(match)
 *
 * Default normalizer (if schema does not define it's own).
 **/
LinkifyIt.prototype.normalize = function normalize (match) {
  // Do minimal possible changes by default. Need to collect feedback prior
  // to move forward https://github.com/markdown-it/linkify-it/issues/1

  if (!match.schema) { match.url = 'http://' + match.url; }

  if (match.schema === 'mailto:' && !/^mailto:/i.test(match.url)) {
    match.url = 'mailto:' + match.url;
  }
};

/**
 * LinkifyIt#onCompile()
 *
 * Override to modify basic RegExp-s.
 **/
LinkifyIt.prototype.onCompile = function onCompile () {
};

/** Highest positive signed 32-bit float value */
const maxInt = 2147483647; // aka. 0x7FFFFFFF or 2^31-1

/** Bootstring parameters */
const base = 36;
const tMin = 1;
const tMax = 26;
const skew = 38;
const damp = 700;
const initialBias = 72;
const initialN = 128; // 0x80
const delimiter = '-'; // '\x2D'

/** Regular expressions */
const regexPunycode = /^xn--/;
const regexNonASCII = /[^\0-\x7F]/; // Note: U+007F DEL is excluded too.
const regexSeparators = /[\x2E\u3002\uFF0E\uFF61]/g; // RFC 3490 separators

/** Error messages */
const errors = {
	'overflow': 'Overflow: input needs wider integers to process',
	'not-basic': 'Illegal input >= 0x80 (not a basic code point)',
	'invalid-input': 'Invalid input'
};

/** Convenience shortcuts */
const baseMinusTMin = base - tMin;
const floor = Math.floor;
const stringFromCharCode = String.fromCharCode;

/*--------------------------------------------------------------------------*/

/**
 * A generic error utility function.
 * @private
 * @param {String} type The error type.
 * @returns {Error} Throws a `RangeError` with the applicable error message.
 */
function error(type) {
	throw new RangeError(errors[type]);
}

/**
 * A generic `Array#map` utility function.
 * @private
 * @param {Array} array The array to iterate over.
 * @param {Function} callback The function that gets called for every array
 * item.
 * @returns {Array} A new array of values returned by the callback function.
 */
function map(array, callback) {
	const result = [];
	let length = array.length;
	while (length--) {
		result[length] = callback(array[length]);
	}
	return result;
}

/**
 * A simple `Array#map`-like wrapper to work with domain name strings or email
 * addresses.
 * @private
 * @param {String} domain The domain name or email address.
 * @param {Function} callback The function that gets called for every
 * character.
 * @returns {String} A new string of characters returned by the callback
 * function.
 */
function mapDomain(domain, callback) {
	const parts = domain.split('@');
	let result = '';
	if (parts.length > 1) {
		// In email addresses, only the domain name should be punycoded. Leave
		// the local part (i.e. everything up to `@`) intact.
		result = parts[0] + '@';
		domain = parts[1];
	}
	// Avoid `split(regex)` for IE8 compatibility. See #17.
	domain = domain.replace(regexSeparators, '\x2E');
	const labels = domain.split('.');
	const encoded = map(labels, callback).join('.');
	return result + encoded;
}

/**
 * Creates an array containing the numeric code points of each Unicode
 * character in the string. While JavaScript uses UCS-2 internally,
 * this function will convert a pair of surrogate halves (each of which
 * UCS-2 exposes as separate characters) into a single code point,
 * matching UTF-16.
 * @see `punycode.ucs2.encode`
 * @see <https://mathiasbynens.be/notes/javascript-encoding>
 * @memberOf punycode.ucs2
 * @name decode
 * @param {String} string The Unicode input string (UCS-2).
 * @returns {Array} The new array of code points.
 */
function ucs2decode(string) {
	const output = [];
	let counter = 0;
	const length = string.length;
	while (counter < length) {
		const value = string.charCodeAt(counter++);
		if (value >= 0xD800 && value <= 0xDBFF && counter < length) {
			// It's a high surrogate, and there is a next character.
			const extra = string.charCodeAt(counter++);
			if ((extra & 0xFC00) == 0xDC00) { // Low surrogate.
				output.push(((value & 0x3FF) << 10) + (extra & 0x3FF) + 0x10000);
			} else {
				// It's an unmatched surrogate; only append this code unit, in case the
				// next code unit is the high surrogate of a surrogate pair.
				output.push(value);
				counter--;
			}
		} else {
			output.push(value);
		}
	}
	return output;
}

/**
 * Creates a string based on an array of numeric code points.
 * @see `punycode.ucs2.decode`
 * @memberOf punycode.ucs2
 * @name encode
 * @param {Array} codePoints The array of numeric code points.
 * @returns {String} The new Unicode string (UCS-2).
 */
const ucs2encode = codePoints => String.fromCodePoint(...codePoints);

/**
 * Converts a basic code point into a digit/integer.
 * @see `digitToBasic()`
 * @private
 * @param {Number} codePoint The basic numeric code point value.
 * @returns {Number} The numeric value of a basic code point (for use in
 * representing integers) in the range `0` to `base - 1`, or `base` if
 * the code point does not represent a value.
 */
const basicToDigit = function(codePoint) {
	if (codePoint >= 0x30 && codePoint < 0x3A) {
		return 26 + (codePoint - 0x30);
	}
	if (codePoint >= 0x41 && codePoint < 0x5B) {
		return codePoint - 0x41;
	}
	if (codePoint >= 0x61 && codePoint < 0x7B) {
		return codePoint - 0x61;
	}
	return base;
};

/**
 * Converts a digit/integer into a basic code point.
 * @see `basicToDigit()`
 * @private
 * @param {Number} digit The numeric value of a basic code point.
 * @returns {Number} The basic code point whose value (when used for
 * representing integers) is `digit`, which needs to be in the range
 * `0` to `base - 1`. If `flag` is non-zero, the uppercase form is
 * used; else, the lowercase form is used. The behavior is undefined
 * if `flag` is non-zero and `digit` has no uppercase form.
 */
const digitToBasic = function(digit, flag) {
	//  0..25 map to ASCII a..z or A..Z
	// 26..35 map to ASCII 0..9
	return digit + 22 + 75 * (digit < 26) - ((flag != 0) << 5);
};

/**
 * Bias adaptation function as per section 3.4 of RFC 3492.
 * https://tools.ietf.org/html/rfc3492#section-3.4
 * @private
 */
const adapt = function(delta, numPoints, firstTime) {
	let k = 0;
	delta = firstTime ? floor(delta / damp) : delta >> 1;
	delta += floor(delta / numPoints);
	for (/* no initialization */; delta > baseMinusTMin * tMax >> 1; k += base) {
		delta = floor(delta / baseMinusTMin);
	}
	return floor(k + (baseMinusTMin + 1) * delta / (delta + skew));
};

/**
 * Converts a Punycode string of ASCII-only symbols to a string of Unicode
 * symbols.
 * @memberOf punycode
 * @param {String} input The Punycode string of ASCII-only symbols.
 * @returns {String} The resulting string of Unicode symbols.
 */
const decode = function(input) {
	// Don't use UCS-2.
	const output = [];
	const inputLength = input.length;
	let i = 0;
	let n = initialN;
	let bias = initialBias;

	// Handle the basic code points: let `basic` be the number of input code
	// points before the last delimiter, or `0` if there is none, then copy
	// the first basic code points to the output.

	let basic = input.lastIndexOf(delimiter);
	if (basic < 0) {
		basic = 0;
	}

	for (let j = 0; j < basic; ++j) {
		// if it's not a basic code point
		if (input.charCodeAt(j) >= 0x80) {
			error('not-basic');
		}
		output.push(input.charCodeAt(j));
	}

	// Main decoding loop: start just after the last delimiter if any basic code
	// points were copied; start at the beginning otherwise.

	for (let index = basic > 0 ? basic + 1 : 0; index < inputLength; /* no final expression */) {

		// `index` is the index of the next character to be consumed.
		// Decode a generalized variable-length integer into `delta`,
		// which gets added to `i`. The overflow checking is easier
		// if we increase `i` as we go, then subtract off its starting
		// value at the end to obtain `delta`.
		const oldi = i;
		for (let w = 1, k = base; /* no condition */; k += base) {

			if (index >= inputLength) {
				error('invalid-input');
			}

			const digit = basicToDigit(input.charCodeAt(index++));

			if (digit >= base) {
				error('invalid-input');
			}
			if (digit > floor((maxInt - i) / w)) {
				error('overflow');
			}

			i += digit * w;
			const t = k <= bias ? tMin : (k >= bias + tMax ? tMax : k - bias);

			if (digit < t) {
				break;
			}

			const baseMinusT = base - t;
			if (w > floor(maxInt / baseMinusT)) {
				error('overflow');
			}

			w *= baseMinusT;

		}

		const out = output.length + 1;
		bias = adapt(i - oldi, out, oldi == 0);

		// `i` was supposed to wrap around from `out` to `0`,
		// incrementing `n` each time, so we'll fix that now:
		if (floor(i / out) > maxInt - n) {
			error('overflow');
		}

		n += floor(i / out);
		i %= out;

		// Insert `n` at position `i` of the output.
		output.splice(i++, 0, n);

	}

	return String.fromCodePoint(...output);
};

/**
 * Converts a string of Unicode symbols (e.g. a domain name label) to a
 * Punycode string of ASCII-only symbols.
 * @memberOf punycode
 * @param {String} input The string of Unicode symbols.
 * @returns {String} The resulting Punycode string of ASCII-only symbols.
 */
const encode = function(input) {
	const output = [];

	// Convert the input in UCS-2 to an array of Unicode code points.
	input = ucs2decode(input);

	// Cache the length.
	const inputLength = input.length;

	// Initialize the state.
	let n = initialN;
	let delta = 0;
	let bias = initialBias;

	// Handle the basic code points.
	for (const currentValue of input) {
		if (currentValue < 0x80) {
			output.push(stringFromCharCode(currentValue));
		}
	}

	const basicLength = output.length;
	let handledCPCount = basicLength;

	// `handledCPCount` is the number of code points that have been handled;
	// `basicLength` is the number of basic code points.

	// Finish the basic string with a delimiter unless it's empty.
	if (basicLength) {
		output.push(delimiter);
	}

	// Main encoding loop:
	while (handledCPCount < inputLength) {

		// All non-basic code points < n have been handled already. Find the next
		// larger one:
		let m = maxInt;
		for (const currentValue of input) {
			if (currentValue >= n && currentValue < m) {
				m = currentValue;
			}
		}

		// Increase `delta` enough to advance the decoder's <n,i> state to <m,0>,
		// but guard against overflow.
		const handledCPCountPlusOne = handledCPCount + 1;
		if (m - n > floor((maxInt - delta) / handledCPCountPlusOne)) {
			error('overflow');
		}

		delta += (m - n) * handledCPCountPlusOne;
		n = m;

		for (const currentValue of input) {
			if (currentValue < n && ++delta > maxInt) {
				error('overflow');
			}
			if (currentValue === n) {
				// Represent delta as a generalized variable-length integer.
				let q = delta;
				for (let k = base; /* no condition */; k += base) {
					const t = k <= bias ? tMin : (k >= bias + tMax ? tMax : k - bias);
					if (q < t) {
						break;
					}
					const qMinusT = q - t;
					const baseMinusT = base - t;
					output.push(
						stringFromCharCode(digitToBasic(t + qMinusT % baseMinusT, 0))
					);
					q = floor(qMinusT / baseMinusT);
				}

				output.push(stringFromCharCode(digitToBasic(q, 0)));
				bias = adapt(delta, handledCPCountPlusOne, handledCPCount === basicLength);
				delta = 0;
				++handledCPCount;
			}
		}

		++delta;
		++n;

	}
	return output.join('');
};

/**
 * Converts a Punycode string representing a domain name or an email address
 * to Unicode. Only the Punycoded parts of the input will be converted, i.e.
 * it doesn't matter if you call it on a string that has already been
 * converted to Unicode.
 * @memberOf punycode
 * @param {String} input The Punycoded domain name or email address to
 * convert to Unicode.
 * @returns {String} The Unicode representation of the given Punycode
 * string.
 */
const toUnicode = function(input) {
	return mapDomain(input, function(string) {
		return regexPunycode.test(string)
			? decode(string.slice(4).toLowerCase())
			: string;
	});
};

/**
 * Converts a Unicode string representing a domain name or an email address to
 * Punycode. Only the non-ASCII parts of the domain name will be converted,
 * i.e. it doesn't matter if you call it with a domain that's already in
 * ASCII.
 * @memberOf punycode
 * @param {String} input The domain name or email address to convert, as a
 * Unicode string.
 * @returns {String} The Punycode representation of the given domain name or
 * email address.
 */
const toASCII = function(input) {
	return mapDomain(input, function(string) {
		return regexNonASCII.test(string)
			? 'xn--' + encode(string)
			: string;
	});
};

/*--------------------------------------------------------------------------*/

/** Define the public API */
const punycode = {
	/**
	 * A string representing the current Punycode.js version number.
	 * @memberOf punycode
	 * @type String
	 */
	'version': '2.3.1',
	/**
	 * An object of methods to convert from JavaScript's internal character
	 * representation (UCS-2) to Unicode code points, and back.
	 * @see <https://mathiasbynens.be/notes/javascript-encoding>
	 * @memberOf punycode
	 * @type Object
	 */
	'ucs2': {
		'decode': ucs2decode,
		'encode': ucs2encode
	},
	'decode': decode,
	'encode': encode,
	'toASCII': toASCII,
	'toUnicode': toUnicode
};

// markdown-it default options

var cfg_default = {
  options: {
    // Enable HTML tags in source
    html: false,

    // Use '/' to close single tags (<br />)
    xhtmlOut: false,

    // Convert '\n' in paragraphs into <br>
    breaks: false,

    // CSS language prefix for fenced blocks
    langPrefix: 'language-',

    // autoconvert URL-like texts to links
    linkify: false,

    // Enable some language-neutral replacements + quotes beautification
    typographer: false,

    // Double + single quotes replacement pairs, when typographer enabled,
    // and smartquotes on. Could be either a String or an Array.
    //
    // For example, you can use '«»„“' for Russian, '„“‚‘' for German,
    // and ['«\xA0', '\xA0»', '‹\xA0', '\xA0›'] for French (including nbsp).
    quotes: '\u201c\u201d\u2018\u2019', /* “”‘’ */

    // Highlighter function. Should return escaped HTML,
    // or '' if the source string is not changed and should be escaped externaly.
    // If result starts with <pre... internal wrapper is skipped.
    //
    // function (/*str, lang*/) { return ''; }
    //
    highlight: null,

    // Internal protection, recursion limit
    maxNesting: 100
  },

  components: {
    core: {},
    block: {},
    inline: {}
  }
};

// "Zero" preset, with nothing enabled. Useful for manual configuring of simple
// modes. For example, to parse bold/italic only.

var cfg_zero = {
  options: {
    // Enable HTML tags in source
    html: false,

    // Use '/' to close single tags (<br />)
    xhtmlOut: false,

    // Convert '\n' in paragraphs into <br>
    breaks: false,

    // CSS language prefix for fenced blocks
    langPrefix: 'language-',

    // autoconvert URL-like texts to links
    linkify: false,

    // Enable some language-neutral replacements + quotes beautification
    typographer: false,

    // Double + single quotes replacement pairs, when typographer enabled,
    // and smartquotes on. Could be either a String or an Array.
    //
    // For example, you can use '«»„“' for Russian, '„“‚‘' for German,
    // and ['«\xA0', '\xA0»', '‹\xA0', '\xA0›'] for French (including nbsp).
    quotes: '\u201c\u201d\u2018\u2019', /* “”‘’ */

    // Highlighter function. Should return escaped HTML,
    // or '' if the source string is not changed and should be escaped externaly.
    // If result starts with <pre... internal wrapper is skipped.
    //
    // function (/*str, lang*/) { return ''; }
    //
    highlight: null,

    // Internal protection, recursion limit
    maxNesting: 20
  },

  components: {

    core: {
      rules: [
        'normalize',
        'block',
        'inline',
        'text_join'
      ]
    },

    block: {
      rules: [
        'paragraph'
      ]
    },

    inline: {
      rules: [
        'text'
      ],
      rules2: [
        'balance_pairs',
        'fragments_join'
      ]
    }
  }
};

// Commonmark default options

var cfg_commonmark = {
  options: {
    // Enable HTML tags in source
    html: true,

    // Use '/' to close single tags (<br />)
    xhtmlOut: true,

    // Convert '\n' in paragraphs into <br>
    breaks: false,

    // CSS language prefix for fenced blocks
    langPrefix: 'language-',

    // autoconvert URL-like texts to links
    linkify: false,

    // Enable some language-neutral replacements + quotes beautification
    typographer: false,

    // Double + single quotes replacement pairs, when typographer enabled,
    // and smartquotes on. Could be either a String or an Array.
    //
    // For example, you can use '«»„“' for Russian, '„“‚‘' for German,
    // and ['«\xA0', '\xA0»', '‹\xA0', '\xA0›'] for French (including nbsp).
    quotes: '\u201c\u201d\u2018\u2019', /* “”‘’ */

    // Highlighter function. Should return escaped HTML,
    // or '' if the source string is not changed and should be escaped externaly.
    // If result starts with <pre... internal wrapper is skipped.
    //
    // function (/*str, lang*/) { return ''; }
    //
    highlight: null,

    // Internal protection, recursion limit
    maxNesting: 20
  },

  components: {

    core: {
      rules: [
        'normalize',
        'block',
        'inline',
        'text_join'
      ]
    },

    block: {
      rules: [
        'blockquote',
        'code',
        'fence',
        'heading',
        'hr',
        'html_block',
        'lheading',
        'list',
        'reference',
        'paragraph'
      ]
    },

    inline: {
      rules: [
        'autolink',
        'backticks',
        'emphasis',
        'entity',
        'escape',
        'html_inline',
        'image',
        'link',
        'newline',
        'text'
      ],
      rules2: [
        'balance_pairs',
        'emphasis',
        'fragments_join'
      ]
    }
  }
};

// Main parser class


const config = {
  default: cfg_default,
  zero: cfg_zero,
  commonmark: cfg_commonmark
};

//
// This validator can prohibit more than really needed to prevent XSS. It's a
// tradeoff to keep code simple and to be secure by default.
//
// If you need different setup - override validator method as you wish. Or
// replace it with dummy function and use external sanitizer.
//

const BAD_PROTO_RE = /^(vbscript|javascript|file|data):/;
const GOOD_DATA_RE = /^data:image\/(gif|png|jpeg|webp);/;

function validateLink (url) {
  // url should be normalized at this point, and existing entities are decoded
  const str = url.trim().toLowerCase();

  return BAD_PROTO_RE.test(str) ? GOOD_DATA_RE.test(str) : true
}

const RECODE_HOSTNAME_FOR = ['http:', 'https:', 'mailto:'];

function normalizeLink (url) {
  const parsed = urlParse(url, true);

  if (parsed.hostname) {
    // Encode hostnames in urls like:
    // `http://host/`, `https://host/`, `mailto:user@host`, `//host/`
    //
    // We don't encode unknown schemas, because it's likely that we encode
    // something we shouldn't (e.g. `skype:name` treated as `skype:host`)
    //
    if (!parsed.protocol || RECODE_HOSTNAME_FOR.indexOf(parsed.protocol) >= 0) {
      try {
        parsed.hostname = punycode.toASCII(parsed.hostname);
      } catch (er) { /**/ }
    }
  }

  return encode$1(format(parsed))
}

function normalizeLinkText (url) {
  const parsed = urlParse(url, true);

  if (parsed.hostname) {
    // Encode hostnames in urls like:
    // `http://host/`, `https://host/`, `mailto:user@host`, `//host/`
    //
    // We don't encode unknown schemas, because it's likely that we encode
    // something we shouldn't (e.g. `skype:name` treated as `skype:host`)
    //
    if (!parsed.protocol || RECODE_HOSTNAME_FOR.indexOf(parsed.protocol) >= 0) {
      try {
        parsed.hostname = punycode.toUnicode(parsed.hostname);
      } catch (er) { /**/ }
    }
  }

  // add '%' to exclude list because of https://github.com/markdown-it/markdown-it/issues/720
  return decode$1(format(parsed), decode$1.defaultChars + '%')
}

/**
 * class MarkdownIt
 *
 * Main parser/renderer class.
 *
 * ##### Usage
 *
 * ```javascript
 * // node.js, "classic" way:
 * var MarkdownIt = require('markdown-it'),
 *     md = new MarkdownIt();
 * var result = md.render('# markdown-it rulezz!');
 *
 * // node.js, the same, but with sugar:
 * var md = require('markdown-it')();
 * var result = md.render('# markdown-it rulezz!');
 *
 * // browser without AMD, added to "window" on script load
 * // Note, there are no dash.
 * var md = window.markdownit();
 * var result = md.render('# markdown-it rulezz!');
 * ```
 *
 * Single line rendering, without paragraph wrap:
 *
 * ```javascript
 * var md = require('markdown-it')();
 * var result = md.renderInline('__markdown-it__ rulezz!');
 * ```
 **/

/**
 * new MarkdownIt([presetName, options])
 * - presetName (String): optional, `commonmark` / `zero`
 * - options (Object)
 *
 * Creates parser instanse with given config. Can be called without `new`.
 *
 * ##### presetName
 *
 * MarkdownIt provides named presets as a convenience to quickly
 * enable/disable active syntax rules and options for common use cases.
 *
 * - ["commonmark"](https://github.com/markdown-it/markdown-it/blob/master/lib/presets/commonmark.mjs) -
 *   configures parser to strict [CommonMark](http://commonmark.org/) mode.
 * - [default](https://github.com/markdown-it/markdown-it/blob/master/lib/presets/default.mjs) -
 *   similar to GFM, used when no preset name given. Enables all available rules,
 *   but still without html, typographer & autolinker.
 * - ["zero"](https://github.com/markdown-it/markdown-it/blob/master/lib/presets/zero.mjs) -
 *   all rules disabled. Useful to quickly setup your config via `.enable()`.
 *   For example, when you need only `bold` and `italic` markup and nothing else.
 *
 * ##### options:
 *
 * - __html__ - `false`. Set `true` to enable HTML tags in source. Be careful!
 *   That's not safe! You may need external sanitizer to protect output from XSS.
 *   It's better to extend features via plugins, instead of enabling HTML.
 * - __xhtmlOut__ - `false`. Set `true` to add '/' when closing single tags
 *   (`<br />`). This is needed only for full CommonMark compatibility. In real
 *   world you will need HTML output.
 * - __breaks__ - `false`. Set `true` to convert `\n` in paragraphs into `<br>`.
 * - __langPrefix__ - `language-`. CSS language class prefix for fenced blocks.
 *   Can be useful for external highlighters.
 * - __linkify__ - `false`. Set `true` to autoconvert URL-like text to links.
 * - __typographer__  - `false`. Set `true` to enable [some language-neutral
 *   replacement](https://github.com/markdown-it/markdown-it/blob/master/lib/rules_core/replacements.mjs) +
 *   quotes beautification (smartquotes).
 * - __quotes__ - `“”‘’`, String or Array. Double + single quotes replacement
 *   pairs, when typographer enabled and smartquotes on. For example, you can
 *   use `'«»„“'` for Russian, `'„“‚‘'` for German, and
 *   `['«\xA0', '\xA0»', '‹\xA0', '\xA0›']` for French (including nbsp).
 * - __highlight__ - `null`. Highlighter function for fenced code blocks.
 *   Highlighter `function (str, lang)` should return escaped HTML. It can also
 *   return empty string if the source was not changed and should be escaped
 *   externaly. If result starts with <pre... internal wrapper is skipped.
 *
 * ##### Example
 *
 * ```javascript
 * // commonmark mode
 * var md = require('markdown-it')('commonmark');
 *
 * // default mode
 * var md = require('markdown-it')();
 *
 * // enable everything
 * var md = require('markdown-it')({
 *   html: true,
 *   linkify: true,
 *   typographer: true
 * });
 * ```
 *
 * ##### Syntax highlighting
 *
 * ```js
 * var hljs = require('highlight.js') // https://highlightjs.org/
 *
 * var md = require('markdown-it')({
 *   highlight: function (str, lang) {
 *     if (lang && hljs.getLanguage(lang)) {
 *       try {
 *         return hljs.highlight(str, { language: lang, ignoreIllegals: true }).value;
 *       } catch (__) {}
 *     }
 *
 *     return ''; // use external default escaping
 *   }
 * });
 * ```
 *
 * Or with full wrapper override (if you need assign class to `<pre>` or `<code>`):
 *
 * ```javascript
 * var hljs = require('highlight.js') // https://highlightjs.org/
 *
 * // Actual default values
 * var md = require('markdown-it')({
 *   highlight: function (str, lang) {
 *     if (lang && hljs.getLanguage(lang)) {
 *       try {
 *         return '<pre><code class="hljs">' +
 *                hljs.highlight(str, { language: lang, ignoreIllegals: true }).value +
 *                '</code></pre>';
 *       } catch (__) {}
 *     }
 *
 *     return '<pre><code class="hljs">' + md.utils.escapeHtml(str) + '</code></pre>';
 *   }
 * });
 * ```
 *
 **/
function MarkdownIt (presetName, options) {
  if (!(this instanceof MarkdownIt)) {
    return new MarkdownIt(presetName, options)
  }

  if (!options) {
    if (!isString$1(presetName)) {
      options = presetName || {};
      presetName = 'default';
    }
  }

  /**
   * MarkdownIt#inline -> ParserInline
   *
   * Instance of [[ParserInline]]. You may need it to add new rules when
   * writing plugins. For simple rules control use [[MarkdownIt.disable]] and
   * [[MarkdownIt.enable]].
   **/
  this.inline = new ParserInline();

  /**
   * MarkdownIt#block -> ParserBlock
   *
   * Instance of [[ParserBlock]]. You may need it to add new rules when
   * writing plugins. For simple rules control use [[MarkdownIt.disable]] and
   * [[MarkdownIt.enable]].
   **/
  this.block = new ParserBlock();

  /**
   * MarkdownIt#core -> Core
   *
   * Instance of [[Core]] chain executor. You may need it to add new rules when
   * writing plugins. For simple rules control use [[MarkdownIt.disable]] and
   * [[MarkdownIt.enable]].
   **/
  this.core = new Core();

  /**
   * MarkdownIt#renderer -> Renderer
   *
   * Instance of [[Renderer]]. Use it to modify output look. Or to add rendering
   * rules for new token types, generated by plugins.
   *
   * ##### Example
   *
   * ```javascript
   * var md = require('markdown-it')();
   *
   * function myToken(tokens, idx, options, env, self) {
   *   //...
   *   return result;
   * };
   *
   * md.renderer.rules['my_token'] = myToken
   * ```
   *
   * See [[Renderer]] docs and [source code](https://github.com/markdown-it/markdown-it/blob/master/lib/renderer.mjs).
   **/
  this.renderer = new Renderer();

  /**
   * MarkdownIt#linkify -> LinkifyIt
   *
   * [linkify-it](https://github.com/markdown-it/linkify-it) instance.
   * Used by [linkify](https://github.com/markdown-it/markdown-it/blob/master/lib/rules_core/linkify.mjs)
   * rule.
   **/
  this.linkify = new LinkifyIt();

  /**
   * MarkdownIt#validateLink(url) -> Boolean
   *
   * Link validation function. CommonMark allows too much in links. By default
   * we disable `javascript:`, `vbscript:`, `file:` schemas, and almost all `data:...` schemas
   * except some embedded image types.
   *
   * You can change this behaviour:
   *
   * ```javascript
   * var md = require('markdown-it')();
   * // enable everything
   * md.validateLink = function () { return true; }
   * ```
   **/
  this.validateLink = validateLink;

  /**
   * MarkdownIt#normalizeLink(url) -> String
   *
   * Function used to encode link url to a machine-readable format,
   * which includes url-encoding, punycode, etc.
   **/
  this.normalizeLink = normalizeLink;

  /**
   * MarkdownIt#normalizeLinkText(url) -> String
   *
   * Function used to decode link url to a human-readable format`
   **/
  this.normalizeLinkText = normalizeLinkText;

  // Expose utils & helpers for easy acces from plugins

  /**
   * MarkdownIt#utils -> utils
   *
   * Assorted utility functions, useful to write plugins. See details
   * [here](https://github.com/markdown-it/markdown-it/blob/master/lib/common/utils.mjs).
   **/
  this.utils = utils$2;

  /**
   * MarkdownIt#helpers -> helpers
   *
   * Link components parser functions, useful to write plugins. See details
   * [here](https://github.com/markdown-it/markdown-it/blob/master/lib/helpers).
   **/
  this.helpers = assign$1({}, helpers);

  this.options = {};
  this.configure(presetName);

  if (options) { this.set(options); }
}

/** chainable
 * MarkdownIt.set(options)
 *
 * Set parser options (in the same format as in constructor). Probably, you
 * will never need it, but you can change options after constructor call.
 *
 * ##### Example
 *
 * ```javascript
 * var md = require('markdown-it')()
 *             .set({ html: true, breaks: true })
 *             .set({ typographer, true });
 * ```
 *
 * __Note:__ To achieve the best possible performance, don't modify a
 * `markdown-it` instance options on the fly. If you need multiple configurations
 * it's best to create multiple instances and initialize each with separate
 * config.
 **/
MarkdownIt.prototype.set = function (options) {
  assign$1(this.options, options);
  return this
};

/** chainable, internal
 * MarkdownIt.configure(presets)
 *
 * Batch load of all options and compenent settings. This is internal method,
 * and you probably will not need it. But if you will - see available presets
 * and data structure [here](https://github.com/markdown-it/markdown-it/tree/master/lib/presets)
 *
 * We strongly recommend to use presets instead of direct config loads. That
 * will give better compatibility with next versions.
 **/
MarkdownIt.prototype.configure = function (presets) {
  const self = this;

  if (isString$1(presets)) {
    const presetName = presets;
    presets = config[presetName];
    if (!presets) { throw new Error('Wrong `markdown-it` preset "' + presetName + '", check name') }
  }

  if (!presets) { throw new Error('Wrong `markdown-it` preset, can\'t be empty') }

  if (presets.options) { self.set(presets.options); }

  if (presets.components) {
    Object.keys(presets.components).forEach(function (name) {
      if (presets.components[name].rules) {
        self[name].ruler.enableOnly(presets.components[name].rules);
      }
      if (presets.components[name].rules2) {
        self[name].ruler2.enableOnly(presets.components[name].rules2);
      }
    });
  }
  return this
};

/** chainable
 * MarkdownIt.enable(list, ignoreInvalid)
 * - list (String|Array): rule name or list of rule names to enable
 * - ignoreInvalid (Boolean): set `true` to ignore errors when rule not found.
 *
 * Enable list or rules. It will automatically find appropriate components,
 * containing rules with given names. If rule not found, and `ignoreInvalid`
 * not set - throws exception.
 *
 * ##### Example
 *
 * ```javascript
 * var md = require('markdown-it')()
 *             .enable(['sub', 'sup'])
 *             .disable('smartquotes');
 * ```
 **/
MarkdownIt.prototype.enable = function (list, ignoreInvalid) {
  let result = [];

  if (!Array.isArray(list)) { list = [list]; }

  ['core', 'block', 'inline'].forEach(function (chain) {
    result = result.concat(this[chain].ruler.enable(list, true));
  }, this);

  result = result.concat(this.inline.ruler2.enable(list, true));

  const missed = list.filter(function (name) { return result.indexOf(name) < 0 });

  if (missed.length && !ignoreInvalid) {
    throw new Error('MarkdownIt. Failed to enable unknown rule(s): ' + missed)
  }

  return this
};

/** chainable
 * MarkdownIt.disable(list, ignoreInvalid)
 * - list (String|Array): rule name or list of rule names to disable.
 * - ignoreInvalid (Boolean): set `true` to ignore errors when rule not found.
 *
 * The same as [[MarkdownIt.enable]], but turn specified rules off.
 **/
MarkdownIt.prototype.disable = function (list, ignoreInvalid) {
  let result = [];

  if (!Array.isArray(list)) { list = [list]; }

  ['core', 'block', 'inline'].forEach(function (chain) {
    result = result.concat(this[chain].ruler.disable(list, true));
  }, this);

  result = result.concat(this.inline.ruler2.disable(list, true));

  const missed = list.filter(function (name) { return result.indexOf(name) < 0 });

  if (missed.length && !ignoreInvalid) {
    throw new Error('MarkdownIt. Failed to disable unknown rule(s): ' + missed)
  }
  return this
};

/** chainable
 * MarkdownIt.use(plugin, params)
 *
 * Load specified plugin with given params into current parser instance.
 * It's just a sugar to call `plugin(md, params)` with curring.
 *
 * ##### Example
 *
 * ```javascript
 * var iterator = require('markdown-it-for-inline');
 * var md = require('markdown-it')()
 *             .use(iterator, 'foo_replace', 'text', function (tokens, idx) {
 *               tokens[idx].content = tokens[idx].content.replace(/foo/g, 'bar');
 *             });
 * ```
 **/
MarkdownIt.prototype.use = function (plugin /*, params, ... */) {
  const args = [this].concat(Array.prototype.slice.call(arguments, 1));
  plugin.apply(plugin, args);
  return this
};

/** internal
 * MarkdownIt.parse(src, env) -> Array
 * - src (String): source string
 * - env (Object): environment sandbox
 *
 * Parse input string and return list of block tokens (special token type
 * "inline" will contain list of inline tokens). You should not call this
 * method directly, until you write custom renderer (for example, to produce
 * AST).
 *
 * `env` is used to pass data between "distributed" rules and return additional
 * metadata like reference info, needed for the renderer. It also can be used to
 * inject data in specific cases. Usually, you will be ok to pass `{}`,
 * and then pass updated object to renderer.
 **/
MarkdownIt.prototype.parse = function (src, env) {
  if (typeof src !== 'string') {
    throw new Error('Input data should be a String')
  }

  const state = new this.core.State(src, this, env);

  this.core.process(state);

  return state.tokens
};

/**
 * MarkdownIt.render(src [, env]) -> String
 * - src (String): source string
 * - env (Object): environment sandbox
 *
 * Render markdown string into html. It does all magic for you :).
 *
 * `env` can be used to inject additional metadata (`{}` by default).
 * But you will not need it with high probability. See also comment
 * in [[MarkdownIt.parse]].
 **/
MarkdownIt.prototype.render = function (src, env) {
  env = env || {};

  return this.renderer.render(this.parse(src, env), this.options, env)
};

/** internal
 * MarkdownIt.parseInline(src, env) -> Array
 * - src (String): source string
 * - env (Object): environment sandbox
 *
 * The same as [[MarkdownIt.parse]] but skip all block rules. It returns the
 * block tokens list with the single `inline` element, containing parsed inline
 * tokens in `children` property. Also updates `env` object.
 **/
MarkdownIt.prototype.parseInline = function (src, env) {
  const state = new this.core.State(src, this, env);

  state.inlineMode = true;
  this.core.process(state);

  return state.tokens
};

/**
 * MarkdownIt.renderInline(src [, env]) -> String
 * - src (String): source string
 * - env (Object): environment sandbox
 *
 * Similar to [[MarkdownIt.render]] but for single paragraph content. Result
 * will NOT be wrapped into `<p>` tags.
 **/
MarkdownIt.prototype.renderInline = function (src, env) {
  env = env || {};

  return this.renderer.render(this.parseInline(src, env), this.options, env)
};

var utils$1 = {};

/**
 * parse {.class #id key=val} strings
 * @param {string} str: string to parse
 * @param {int} start: where to start parsing (including {)
 * @returns {2d array}: [['key', 'val'], ['class', 'red']]
 */

utils$1.getAttrs = function (str, start, options) {
  // not tab, line feed, form feed, space, solidus, greater than sign, quotation mark, apostrophe and equals sign
  const allowedKeyChars = /[^\t\n\f />"'=]/;
  const pairSeparator = ' ';
  const keySeparator = '=';
  const classChar = '.';
  const idChar = '#';

  const attrs = [];
  let key = '';
  let value = '';
  let parsingKey = true;
  let valueInsideQuotes = false;

  // read inside {}
  // start + left delimiter length to avoid beginning {
  // breaks when } is found or end of string
  for (let i = start + options.leftDelimiter.length; i < str.length; i++) {
    if (str.slice(i, i + options.rightDelimiter.length) === options.rightDelimiter) {
      if (key !== '') { attrs.push([key, value]); }
      break;
    }
    const char_ = str.charAt(i);

    // switch to reading value if equal sign
    if (char_ === keySeparator && parsingKey) {
      parsingKey = false;
      continue;
    }

    // {.class} {..css-module}
    if (char_ === classChar && key === '') {
      if (str.charAt(i + 1) === classChar) {
        key = 'css-module';
        i += 1;
      } else {
        key = 'class';
      }
      parsingKey = false;
      continue;
    }

    // {#id}
    if (char_ === idChar && key === '') {
      key = 'id';
      parsingKey = false;
      continue;
    }

    // {value="inside quotes"}
    if (char_ === '"' && value === '' && !valueInsideQuotes) {
      valueInsideQuotes = true;
      continue;
    }
    if (char_ === '"' && valueInsideQuotes) {
      valueInsideQuotes = false;
      continue;
    }

    // read next key/value pair
    if ((char_ === pairSeparator && !valueInsideQuotes)) {
      if (key === '') {
        // beginning or ending space: { .red } vs {.red}
        continue;
      }
      attrs.push([key, value]);
      key = '';
      value = '';
      parsingKey = true;
      continue;
    }

    // continue if character not allowed
    if (parsingKey && char_.search(allowedKeyChars) === -1) {
      continue;
    }

    // no other conditions met; append to key/value
    if (parsingKey) {
      key += char_;
      continue;
    }
    value += char_;
  }

  if (options.allowedAttributes && options.allowedAttributes.length) {
    const allowedAttributes = options.allowedAttributes;

    return attrs.filter(function (attrPair) {
      const attr = attrPair[0];

      function isAllowedAttribute (allowedAttribute) {
        return (attr === allowedAttribute
          || (allowedAttribute instanceof RegExp && allowedAttribute.test(attr))
        );
      }

      return allowedAttributes.some(isAllowedAttribute);
    });

  }
  return attrs;

};

/**
 * add attributes from [['key', 'val']] list
 * @param {array} attrs: [['key', 'val']]
 * @param {token} token: which token to add attributes
 * @returns token
 */
utils$1.addAttrs = function (attrs, token) {
  for (let j = 0, l = attrs.length; j < l; ++j) {
    const key = attrs[j][0];
    if (key === 'class') {
      token.attrJoin('class', attrs[j][1]);
    } else if (key === 'css-module') {
      token.attrJoin('css-module', attrs[j][1]);
    } else {
      token.attrPush(attrs[j]);
    }
  }
  return token;
};

/**
 * Does string have properly formatted curly?
 *
 * start: '{.a} asdf'
 * end: 'asdf {.a}'
 * only: '{.a}'
 *
 * @param {string} where to expect {} curly. start, end or only.
 * @return {function(string)} Function which testes if string has curly.
 */
utils$1.hasDelimiters = function (where, options) {

  if (!where) {
    throw new Error('Parameter `where` not passed. Should be "start", "end" or "only".');
  }

  /**
   * @param {string} str
   * @return {boolean}
   */
  return function (str) {
    // we need minimum three chars, for example {b}
    const minCurlyLength = options.leftDelimiter.length + 1 + options.rightDelimiter.length;
    if (!str || typeof str !== 'string' || str.length < minCurlyLength) {
      return false;
    }

    function validCurlyLength (curly) {
      const isClass = curly.charAt(options.leftDelimiter.length) === '.';
      const isId = curly.charAt(options.leftDelimiter.length) === '#';
      return (isClass || isId)
        ? curly.length >= (minCurlyLength + 1)
        : curly.length >= minCurlyLength;
    }

    let start, end, slice, nextChar;
    const rightDelimiterMinimumShift = minCurlyLength - options.rightDelimiter.length;
    switch (where) {
    case 'start':
      // first char should be {, } found in char 2 or more
      slice = str.slice(0, options.leftDelimiter.length);
      start = slice === options.leftDelimiter ? 0 : -1;
      end = start === -1 ? -1 : str.indexOf(options.rightDelimiter, rightDelimiterMinimumShift);
      // check if next character is not one of the delimiters
      nextChar = str.charAt(end + options.rightDelimiter.length);
      if (nextChar && options.rightDelimiter.indexOf(nextChar) !== -1) {
        end = -1;
      }
      break;

    case 'end':
      // last char should be }
      start = str.lastIndexOf(options.leftDelimiter);
      end = start === -1 ? -1 : str.indexOf(options.rightDelimiter, start + rightDelimiterMinimumShift);
      end = end === str.length - options.rightDelimiter.length ? end : -1;
      break;

    case 'only':
      // '{.a}'
      slice = str.slice(0, options.leftDelimiter.length);
      start = slice === options.leftDelimiter ? 0 : -1;
      slice = str.slice(str.length - options.rightDelimiter.length);
      end = slice === options.rightDelimiter ? str.length - options.rightDelimiter.length : -1;
      break;

    default:
      throw new Error(`Unexpected case ${where}, expected 'start', 'end' or 'only'`);
    }

    return start !== -1 && end !== -1 && validCurlyLength(str.substring(start, end + options.rightDelimiter.length));
  };
};

/**
 * Removes last curly from string.
 */
utils$1.removeDelimiter = function (str, options) {
  const start = escapeRegExp(options.leftDelimiter);
  const end = escapeRegExp(options.rightDelimiter);

  const curly = new RegExp(
    '[ \\n]?' + start + '[^' + start + end + ']+' + end + '$'
  );
  const pos = str.search(curly);

  return pos !== -1 ? str.slice(0, pos) : str;
};

/**
 * Escapes special characters in string s such that the string
 * can be used in `new RegExp`. For example "[" becomes "\\[".
 *
 * @param {string} s Regex string.
 * @return {string} Escaped string.
 */
function escapeRegExp (s) {
  return s.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
}
utils$1.escapeRegExp = escapeRegExp;

/**
 * find corresponding opening block
 */
utils$1.getMatchingOpeningToken = function (tokens, i) {
  if (tokens[i].type === 'softbreak') {
    return false;
  }
  // non closing blocks, example img
  if (tokens[i].nesting === 0) {
    return tokens[i];
  }

  const level = tokens[i].level;
  const type = tokens[i].type.replace('_close', '_open');

  for (; i >= 0; --i) {
    if (tokens[i].type === type && tokens[i].level === level) {
      return tokens[i];
    }
  }

  return false;
};


/**
 * from https://github.com/markdown-it/markdown-it/blob/master/lib/common/utils.js
 */
const HTML_ESCAPE_TEST_RE = /[&<>"]/;
const HTML_ESCAPE_REPLACE_RE = /[&<>"]/g;
const HTML_REPLACEMENTS = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;'
};

function replaceUnsafeChar(ch) {
  return HTML_REPLACEMENTS[ch];
}

utils$1.escapeHtml = function (str) {
  if (HTML_ESCAPE_TEST_RE.test(str)) {
    return str.replace(HTML_ESCAPE_REPLACE_RE, replaceUnsafeChar);
  }
  return str;
};

/**
 * If a pattern matches the token stream,
 * then run transform.
 */

const utils = utils$1;

var patterns = options => {
  const __hr = new RegExp('^ {0,3}[-*_]{3,} ?'
                          + utils.escapeRegExp(options.leftDelimiter)
                          + '[^' + utils.escapeRegExp(options.rightDelimiter) + ']');

  return ([
    {
      /**
       * ```python {.cls}
       * for i in range(10):
       *     print(i)
       * ```
       */
      name: 'fenced code blocks',
      tests: [
        {
          shift: 0,
          block: true,
          info: utils.hasDelimiters('end', options)
        }
      ],
      transform: (tokens, i) => {
        const token = tokens[i];
        const start = token.info.lastIndexOf(options.leftDelimiter);
        const attrs = utils.getAttrs(token.info, start, options);
        utils.addAttrs(attrs, token);
        token.info = utils.removeDelimiter(token.info, options);
      }
    }, {
      /**
       * bla `click()`{.c} ![](img.png){.d}
       *
       * differs from 'inline attributes' as it does
       * not have a closing tag (nesting: -1)
       */
      name: 'inline nesting 0',
      tests: [
        {
          shift: 0,
          type: 'inline',
          children: [
            {
              shift: -1,
              type: (str) => str === 'image' || str === 'code_inline'
            }, {
              shift: 0,
              type: 'text',
              content: utils.hasDelimiters('start', options)
            }
          ]
        }
      ],
      transform: (tokens, i, j) => {
        const token = tokens[i].children[j];
        const endChar = token.content.indexOf(options.rightDelimiter);
        const attrToken = tokens[i].children[j - 1];
        const attrs = utils.getAttrs(token.content, 0, options);
        utils.addAttrs(attrs, attrToken);
        if (token.content.length === (endChar + options.rightDelimiter.length)) {
          tokens[i].children.splice(j, 1);
        } else {
          token.content = token.content.slice(endChar + options.rightDelimiter.length);
        }
      }
    }, {
      /**
       * | h1 |
       * | -- |
       * | c1 |
       *
       * {.c}
       */
      name: 'tables',
      tests: [
        {
          // let this token be i, such that for-loop continues at
          // next token after tokens.splice
          shift: 0,
          type: 'table_close'
        }, {
          shift: 1,
          type: 'paragraph_open'
        }, {
          shift: 2,
          type: 'inline',
          content: utils.hasDelimiters('only', options)
        }
      ],
      transform: (tokens, i) => {
        const token = tokens[i + 2];
        const tableOpen = utils.getMatchingOpeningToken(tokens, i);
        const attrs = utils.getAttrs(token.content, 0, options);
        // add attributes
        utils.addAttrs(attrs, tableOpen);
        // remove <p>{.c}</p>
        tokens.splice(i + 1, 3);
      }
    }, {
      /**
       * *emphasis*{.with attrs=1}
       */
      name: 'inline attributes',
      tests: [
        {
          shift: 0,
          type: 'inline',
          children: [
            {
              shift: -1,
              nesting: -1  // closing inline tag, </em>{.a}
            }, {
              shift: 0,
              type: 'text',
              content: utils.hasDelimiters('start', options)
            }
          ]
        }
      ],
      transform: (tokens, i, j) => {
        const token = tokens[i].children[j];
        const content = token.content;
        const attrs = utils.getAttrs(content, 0, options);
        const openingToken = utils.getMatchingOpeningToken(tokens[i].children, j - 1);
        utils.addAttrs(attrs, openingToken);
        token.content = content.slice(content.indexOf(options.rightDelimiter) + options.rightDelimiter.length);
      }
    }, {
      /**
       * - item
       * {.a}
       */
      name: 'list softbreak',
      tests: [
        {
          shift: -2,
          type: 'list_item_open'
        }, {
          shift: 0,
          type: 'inline',
          children: [
            {
              position: -2,
              type: 'softbreak'
            }, {
              position: -1,
              type: 'text',
              content: utils.hasDelimiters('only', options)
            }
          ]
        }
      ],
      transform: (tokens, i, j) => {
        const token = tokens[i].children[j];
        const content = token.content;
        const attrs = utils.getAttrs(content, 0, options);
        let ii = i - 2;
        while (tokens[ii - 1] &&
          tokens[ii - 1].type !== 'ordered_list_open' &&
          tokens[ii - 1].type !== 'bullet_list_open') { ii--; }
        utils.addAttrs(attrs, tokens[ii - 1]);
        tokens[i].children = tokens[i].children.slice(0, -2);
      }
    }, {
      /**
       * - nested list
       *   - with double \n
       *   {.a} <-- apply to nested ul
       *
       * {.b} <-- apply to root <ul>
       */
      name: 'list double softbreak',
      tests: [
        {
          // let this token be i = 0 so that we can erase
          // the <p>{.a}</p> tokens below
          shift: 0,
          type: (str) =>
            str === 'bullet_list_close' ||
            str === 'ordered_list_close'
        }, {
          shift: 1,
          type: 'paragraph_open'
        }, {
          shift: 2,
          type: 'inline',
          content: utils.hasDelimiters('only', options),
          children: (arr) => arr.length === 1
        }, {
          shift: 3,
          type: 'paragraph_close'
        }
      ],
      transform: (tokens, i) => {
        const token = tokens[i + 2];
        const content = token.content;
        const attrs = utils.getAttrs(content, 0, options);
        const openingToken = utils.getMatchingOpeningToken(tokens, i);
        utils.addAttrs(attrs, openingToken);
        tokens.splice(i + 1, 3);
      }
    }, {
      /**
       * - end of {.list-item}
       */
      name: 'list item end',
      tests: [
        {
          shift: -2,
          type: 'list_item_open'
        }, {
          shift: 0,
          type: 'inline',
          children: [
            {
              position: -1,
              type: 'text',
              content: utils.hasDelimiters('end', options)
            }
          ]
        }
      ],
      transform: (tokens, i, j) => {
        const token = tokens[i].children[j];
        const content = token.content;
        const attrs = utils.getAttrs(content, content.lastIndexOf(options.leftDelimiter), options);
        utils.addAttrs(attrs, tokens[i - 2]);
        const trimmed = content.slice(0, content.lastIndexOf(options.leftDelimiter));
        token.content = last$1(trimmed) !== ' ' ?
          trimmed : trimmed.slice(0, -1);
      }
    }, {
      /**
       * something with softbreak
       * {.cls}
       */
      name: '\n{.a} softbreak then curly in start',
      tests: [
        {
          shift: 0,
          type: 'inline',
          children: [
            {
              position: -2,
              type: 'softbreak'
            }, {
              position: -1,
              type: 'text',
              content: utils.hasDelimiters('only', options)
            }
          ]
        }
      ],
      transform: (tokens, i, j) => {
        const token = tokens[i].children[j];
        const attrs = utils.getAttrs(token.content, 0, options);
        // find last closing tag
        let ii = i + 1;
        while (tokens[ii + 1] && tokens[ii + 1].nesting === -1) { ii++; }
        const openingToken = utils.getMatchingOpeningToken(tokens, ii);
        utils.addAttrs(attrs, openingToken);
        tokens[i].children = tokens[i].children.slice(0, -2);
      }
    }, {
      /**
       * horizontal rule --- {#id}
       */
      name: 'horizontal rule',
      tests: [
        {
          shift: 0,
          type: 'paragraph_open'
        },
        {
          shift: 1,
          type: 'inline',
          children: (arr) => arr.length === 1,
          content: (str) => str.match(__hr) !== null,
        },
        {
          shift: 2,
          type: 'paragraph_close'
        }
      ],
      transform: (tokens, i) => {
        const token = tokens[i];
        token.type = 'hr';
        token.tag = 'hr';
        token.nesting = 0;
        const content = tokens[i + 1].content;
        const start = content.lastIndexOf(options.leftDelimiter);
        const attrs = utils.getAttrs(content, start, options);
        utils.addAttrs(attrs, token);
        token.markup = content;
        tokens.splice(i + 1, 2);
      }
    }, {
      /**
       * end of {.block}
       */
      name: 'end of block',
      tests: [
        {
          shift: 0,
          type: 'inline',
          children: [
            {
              position: -1,
              content: utils.hasDelimiters('end', options),
              type: (t) => t !== 'code_inline' && t !== 'math_inline'
            }
          ]
        }
      ],
      transform: (tokens, i, j) => {
        const token = tokens[i].children[j];
        const content = token.content;
        const attrs = utils.getAttrs(content, content.lastIndexOf(options.leftDelimiter), options);
        let ii = i + 1;
        while (tokens[ii + 1] && tokens[ii + 1].nesting === -1) { ii++; }
        const openingToken = utils.getMatchingOpeningToken(tokens, ii);
        utils.addAttrs(attrs, openingToken);
        const trimmed = content.slice(0, content.lastIndexOf(options.leftDelimiter));
        token.content = last$1(trimmed) !== ' ' ?
          trimmed : trimmed.slice(0, -1);
      }
    }
  ]);
};

// get last element of array or string
function last$1(arr) {
  return arr.slice(-1)[0];
}

const patternsConfig = patterns;

const defaultOptions = {
  leftDelimiter: '{',
  rightDelimiter: '}',
  allowedAttributes: []
};

var markdownItAttrs = function attributes(md, options_) {
  let options = Object.assign({}, defaultOptions);
  options = Object.assign(options, options_);

  const patterns = patternsConfig(options);

  function curlyAttrs(state) {
    const tokens = state.tokens;

    for (let i = 0; i < tokens.length; i++) {
      for (let p = 0; p < patterns.length; p++) {
        const pattern = patterns[p];
        let j = null; // position of child with offset 0
        const match = pattern.tests.every(t => {
          const res = test(tokens, i, t);
          if (res.j !== null) { j = res.j; }
          return res.match;
        });
        if (match) {
          pattern.transform(tokens, i, j);
          if (pattern.name === 'inline attributes' || pattern.name === 'inline nesting 0') {
            // retry, may be several inline attributes
            p--;
          }
        }
      }
    }
  }

  md.core.ruler.before('linkify', 'curly_attributes', curlyAttrs);
};

/**
 * Test if t matches token stream.
 *
 * @param {array} tokens
 * @param {number} i
 * @param {object} t Test to match.
 * @return {object} { match: true|false, j: null|number }
 */
function test(tokens, i, t) {
  const res = {
    match: false,
    j: null  // position of child
  };

  const ii = t.shift !== undefined
    ? i + t.shift
    : t.position;

  if (t.shift !== undefined && ii < 0) {
    // we should never shift to negative indexes (rolling around to back of array)
    return res;
  }

  const token = get$1(tokens, ii);  // supports negative ii


  if (token === undefined) { return res; }

  for (const key of Object.keys(t)) {
    if (key === 'shift' || key === 'position') { continue; }

    if (token[key] === undefined) { return res; }

    if (key === 'children' && isArrayOfObjects(t.children)) {
      if (token.children.length === 0) {
        return res;
      }
      let match;
      const childTests = t.children;
      const children = token.children;
      if (childTests.every(tt => tt.position !== undefined)) {
        // positions instead of shifts, do not loop all children
        match = childTests.every(tt => test(children, tt.position, tt).match);
        if (match) {
          // we may need position of child in transform
          const j = last(childTests).position;
          res.j = j >= 0 ? j : children.length + j;
        }
      } else {
        for (let j = 0; j < children.length; j++) {
          match = childTests.every(tt => test(children, j, tt).match);
          if (match) {
            res.j = j;
            // all tests true, continue with next key of pattern t
            break;
          }
        }
      }

      if (match === false) { return res; }

      continue;
    }

    switch (typeof t[key]) {
    case 'boolean':
    case 'number':
    case 'string':
      if (token[key] !== t[key]) { return res; }
      break;
    case 'function':
      if (!t[key](token[key])) { return res; }
      break;
    case 'object':
      if (isArrayOfFunctions(t[key])) {
        const r = t[key].every(tt => tt(token[key]));
        if (r === false) { return res; }
        break;
      }
    // fall through for objects !== arrays of functions
    default:
      throw new Error(`Unknown type of pattern test (key: ${key}). Test should be of type boolean, number, string, function or array of functions.`);
    }
  }

  // no tests returned false -> all tests returns true
  res.match = true;
  return res;
}

function isArrayOfObjects(arr) {
  return Array.isArray(arr) && arr.length && arr.every(i => typeof i === 'object');
}

function isArrayOfFunctions(arr) {
  return Array.isArray(arr) && arr.length && arr.every(i => typeof i === 'function');
}

/**
 * Get n item of array. Supports negative n, where -1 is last
 * element in array.
 * @param {array} arr
 * @param {number} n
 */
function get$1(arr, n) {
  return n >= 0 ? arr[n] : arr[arr.length + n];
}

// get last element of array, safe - returns {} if not found
function last(arr) {
  return arr.slice(-1)[0] || {};
}

var markdownItAttrs$1 = /*@__PURE__*/getDefaultExportFromCjs$1(markdownItAttrs);

// Process ~subscript~

// same as UNESCAPE_MD_RE plus a space
const UNESCAPE_RE$1 = /\\([ \\!"#$%&'()*+,./:;<=>?@[\]^_`{|}~-])/g;

function subscript (state, silent) {
  const max = state.posMax;
  const start = state.pos;

  if (state.src.charCodeAt(start) !== 0x7E/* ~ */) { return false }
  if (silent) { return false } // don't run any pairs in validation mode
  if (start + 2 >= max) { return false }

  state.pos = start + 1;
  let found = false;

  while (state.pos < max) {
    if (state.src.charCodeAt(state.pos) === 0x7E/* ~ */) {
      found = true;
      break
    }

    state.md.inline.skipToken(state);
  }

  if (!found || start + 1 === state.pos) {
    state.pos = start;
    return false
  }

  const content = state.src.slice(start + 1, state.pos);

  // don't allow unescaped spaces/newlines inside
  if (content.match(/(^|[^\\])(\\\\)*\s/)) {
    state.pos = start;
    return false
  }

  // found!
  state.posMax = state.pos;
  state.pos = start + 1;

  // Earlier we checked !silent, but this implementation does not need it
  const token_so = state.push('sub_open', 'sub', 1);
  token_so.markup = '~';

  const token_t = state.push('text', '', 0);
  token_t.content = content.replace(UNESCAPE_RE$1, '$1');

  const token_sc = state.push('sub_close', 'sub', -1);
  token_sc.markup = '~';

  state.pos = state.posMax + 1;
  state.posMax = max;
  return true
}

function sub_plugin (md) {
  md.inline.ruler.after('emphasis', 'sub', subscript);
}

// Process ^superscript^

// same as UNESCAPE_MD_RE plus a space
const UNESCAPE_RE = /\\([ \\!"#$%&'()*+,./:;<=>?@[\]^_`{|}~-])/g;

function superscript (state, silent) {
  const max = state.posMax;
  const start = state.pos;

  if (state.src.charCodeAt(start) !== 0x5E/* ^ */) { return false }
  if (silent) { return false } // don't run any pairs in validation mode
  if (start + 2 >= max) { return false }

  state.pos = start + 1;
  let found = false;

  while (state.pos < max) {
    if (state.src.charCodeAt(state.pos) === 0x5E/* ^ */) {
      found = true;
      break
    }

    state.md.inline.skipToken(state);
  }

  if (!found || start + 1 === state.pos) {
    state.pos = start;
    return false
  }

  const content = state.src.slice(start + 1, state.pos);

  // don't allow unescaped spaces/newlines inside
  if (content.match(/(^|[^\\])(\\\\)*\s/)) {
    state.pos = start;
    return false
  }

  // found!
  state.posMax = state.pos;
  state.pos = start + 1;

  // Earlier we checked !silent, but this implementation does not need it
  const token_so = state.push('sup_open', 'sup', 1);
  token_so.markup = '^';

  const token_t = state.push('text', '', 0);
  token_t.content = content.replace(UNESCAPE_RE, '$1');

  const token_sc = state.push('sup_close', 'sup', -1);
  token_sc.markup = '^';

  state.pos = state.posMax + 1;
  state.posMax = max;
  return true
}

function sup_plugin (md) {
  md.inline.ruler.after('emphasis', 'sup', superscript);
}

const defaultMarkdown = createMarkdownInstance();
/**
 * Creates a new instance of the markdown library with the given configuration rules.
 */
function createMarkdownInstance(options) {
    const markdown = MarkdownIt({
        html: true,
        breaks: true,
        linkify: true,
        highlight: (str, lang) => {
            try {
                if (lang && HighlightJS.getLanguage(lang)) {
                    return HighlightJS.highlight(str, { language: lang }).value;
                }
                return HighlightJS.highlightAuto(str).value;
            }
            catch (error) {
                consoleError(JSON.stringify(error));
                return '';
            }
        },
        ...options?.config,
    });
    // This plugin allows us to use the `{{...}}` syntax to add attributes to generated HTML elements. This is used to
    // allow users to customize the behavior of links by allowing them to set the target and rel attributes.
    markdown.use(markdownItAttrs$1, {
        leftDelimiter: '{{',
        rightDelimiter: '}}',
        allowedAttributes: ['target', 'rel', 'class', 'id'],
    });
    markdown.use(sup_plugin);
    markdown.use(sub_plugin);
    // Default renderers. Some of the renderers do more than just supply an html element all the time, but have fancier
    // logic. Using the default renderer in addition to adding any custom classes we want to add preserves existing behavior.
    const defaultLinkRender = defaultRenderer(markdown, 'link_open');
    const defaultHeadingRender = defaultRenderer(markdown, 'defaultHeadingRender');
    const defaultParagraphRender = defaultRenderer(markdown, 'paragraph_open');
    const defaultBlockquoteRender = defaultRenderer(markdown, 'blockquote_open');
    const defaultFenceRender = defaultRenderer(markdown, 'fence');
    const defaultImageRender = defaultRenderer(markdown, 'image');
    const defaultTableOpenRender = defaultRenderer(markdown, 'table_open');
    const defaultTableCloseRender = defaultRenderer(markdown, 'table_close');
    const defaultBulletListOpenRender = defaultRenderer(markdown, 'bullet_list_open');
    const defaultOrderedListOpenRender = defaultRenderer(markdown, 'ordered_list_open');
    const defaultListItemOpenRender = defaultRenderer(markdown, 'list_item_open');
    // This rule controls the rendering for links. We want to alter the default behavior so that by default, the link
    // will open in a new tab.
    markdown.renderer.rules.link_open = (tokens, index, options, env, self) => {
        // These values may be set using the markdown-it-attrs plugin so only set them if not already set.
        addAttributeIfMissing(tokens[index], 'class', `cds--link cds--link-url`);
        addAttributeIfMissing(tokens[index], 'target', '_blank');
        addAttributeIfMissing(tokens[index], 'rel', 'noopener noreferrer');
        return defaultLinkRender(tokens, index, options, env, self);
    };
    markdown.renderer.rules.heading_open = (tokens, index, options, env, self) => {
        addAttributeIfMissing(tokens[index], 'class', `WACWidget__MarkdownHeading`);
        return defaultHeadingRender(tokens, index, options, env, self);
    };
    // This library adds <p> tags around some elements which introduce unwanted margins. We use this renderer to add a
    // classname to those elements so we can override the margin.
    markdown.renderer.rules.paragraph_open = (tokens, index, options, env, self) => {
        addAttributeIfMissing(tokens[index], 'class', `WACWidget__MarkdownP`);
        return defaultParagraphRender(tokens, index, options, env, self);
    };
    // Make sure tables use Carbon styling and can horizontally scroll if needed.
    markdown.renderer.rules.table_open = (tokens, index, options, env, self) => {
        {
            addAttributeIfMissing(tokens[index], 'class', `cds--data-table cds--data-table--zebra cds--data-table--md`);
        }
        return `<div class="cds--layer-two cds--data-table-content">${defaultTableOpenRender(tokens, index, options, env, self)}`;
    };
    markdown.renderer.rules.table_close = (tokens, index, options, env, self) => {
        return `${defaultTableCloseRender(tokens, index, options, env, self)}</div>`;
    };
    // Add carbon class names.
    markdown.renderer.rules.bullet_list_open = (tokens, index, options, env, self) => {
        const className = `cds--list--unordered${tokens[index].level > 1 ? ' cds--list--nested' : ''}`;
        addAttributeIfMissing(tokens[index], 'class', className);
        return defaultBulletListOpenRender(tokens, index, options, env, self);
    };
    // Add carbon class names.
    markdown.renderer.rules.ordered_list_open = (tokens, index, options, env, self) => {
        const token = tokens[index];
        // Carbon overrides the browser's default rendering of the list item tokens with their own tokens using a
        // "::before" rule that makes use of a CSS counter to count the number of items. This ignores the "start"
        // attribute that is present on the "<ol>". We can fix this by overriding the CSS property to reset the counter
        // to the right value.
        const startAttribute = token.attrGet('start');
        if (typeof startAttribute === 'number') {
            addAttributeIfMissing(tokens[index], 'style', `counter-reset: item ${startAttribute - 1}`);
        }
        const className = `cds--list--ordered${tokens[index].level > 1 ? ' cds--list--nested' : ''}`;
        addAttributeIfMissing(tokens[index], 'class', className);
        return defaultOrderedListOpenRender(tokens, index, options, env, self);
    };
    // Add carbon class names.
    markdown.renderer.rules.list_item_open = (tokens, index, options, env, self) => {
        addAttributeIfMissing(tokens[index], 'class', 'cds--list__item');
        return defaultListItemOpenRender(tokens, index, options, env, self);
    };
    // Add carbon class names.
    markdown.renderer.rules.blockquote_open = (tokens, index, options, env, self) => {
        addAttributeIfMissing(tokens[index], 'class', `WACWidget__MarkdownBlockquote`);
        return defaultBlockquoteRender(tokens, index, options, env, self);
    };
    // Add carbon class names.
    markdown.renderer.rules.fence = (tokens, index, options, env, self) => {
        // The "WACWidget__MarkdownCode" class goes on the "code" tag and the "WACWidget__MarkdownPre" goes on the "pre"
        // tag.
        addAttributeIfMissing(tokens[index], 'class', `WACWidget__MarkdownCode`);
        return defaultFenceRender(tokens, index, options, env, self).replace('<pre>', '<pre class="WACWidget__MarkdownPre">');
    };
    // Add carbon class names.
    markdown.renderer.rules.image = (tokens, index, options, env, self) => {
        addAttributeIfMissing(tokens[index], 'class', `WACWidget__MarkdownImg`);
        return defaultImageRender(tokens, index, options, env, self);
    };
    return markdown;
}
/**
 * Adds the given attribute to the given token if the attribute does not already exist.
 */
function addAttributeIfMissing(token, attributeName, attributeValue) {
    const existingIndex = token.attrIndex(attributeName);
    if (existingIndex < 0) {
        token.attrPush([attributeName, attributeValue]);
    }
}
/**
 * Returns the default renderer for the given rule or a default renderer if one doesn't exist.
 */
function defaultRenderer(markdown, rule) {
    return (markdown.renderer.rules[rule] ||
        ((tokens, index, options, _env, self) => {
            return self.renderToken(tokens, index, options);
        }));
}
/**
 * Formats the given text content into an HTML string for display in the UI. The provided content may be plain
 * text, it may already contain HTML or it may contain Markdown. Any things that "look like" links will be converted
 * to anchor tags to make links using the auto-linker feature of the markdown library. Existing anchor tags are left
 * unchanged.
 *
 * Inside the widget, this is currently only used in <RichText />. We need to do future work to make sure that
 * <RichText /> doesn't need to be loaded just to show the launcher to save us needing to load markdown parsing and
 * syntax highlighting (which are large) just to show a launcher. Of note, Tours also uses RichText.
 *
 * We should be very aware of the effects on the builds when we access this function as it brings in large
 * dependencies.
 */
function processMarkdown(value, markdown = defaultMarkdown) {
    return value ? markdown.render(value).trim() : value;
}

const CSS_CLASS_PREFIX = 'cds--aichat';
const WEB_COMPONENT_PREFIX = 'cds-aichat';

/**
 * ChatHeaderLinkElement view logic.
 */
function chatHeaderLinkTemplate(customElementClass) {
    const { label, url, isNewTab } = customElementClass;
    return html$2 `<cds-custom-button
    class="WACChatHeaderLink"
    href="${url}"
    target="${isNewTab ? '_blank' : '_self'}"
    kind="${ButtonKindEnum.GHOST}"
    size="md"
    >${label}</cds-custom-button
  >`;
}

let ChatHeaderLinkElement = class ChatHeaderLinkElement extends LitElement {
    constructor() {
        super(...arguments);
        this.isNewTab = true;
    }
    render() {
        return chatHeaderLinkTemplate(this);
    }
};
__decorate([
    property({ type: String })
], ChatHeaderLinkElement.prototype, "url", void 0);
__decorate([
    property({ type: String })
], ChatHeaderLinkElement.prototype, "label", void 0);
__decorate([
    property({ type: Boolean })
], ChatHeaderLinkElement.prototype, "isNewTab", void 0);
ChatHeaderLinkElement = __decorate([
    carbonElement(`${WEB_COMPONENT_PREFIX}-chat-header-link`)
], ChatHeaderLinkElement);

const ChatHeaderLink = o$1({
    tagName: `${WEB_COMPONENT_PREFIX}-chat-header-link`,
    elementClass: ChatHeaderLinkElement,
    react: React__default,
});

var css_248z$9 = "cds-chat-header-title{text-align:center}.WACChatHeaderTitle{font-size:var(--cds-chat-BASE-font-size-med);font-weight:400;line-height:var(--cds-chat-BASE-line-height-med);overflow:hidden;overflow-wrap:break-word;text-overflow:ellipsis;word-wrap:break-word;white-space:nowrap}.WACChatHeaderTitle .WACChatHeaderTitle__Title:only-child{padding-inline-end:4px}.WACChatHeaderTitle__Name{font-weight:var(--cds-chat-BASE-weight-semibold)}";

/**
 * ChatHeaderTitleElement view logic.
 */
function chatHeaderTitleTemplate(customElementClass) {
    const { title, name } = customElementClass;
    return html$2 `<div class="WACChatHeaderTitle">
    <span className="WACChatHeaderTitle__Title" ?hidden="${!title}">${title}</span>
    <span class="WACChatHeaderTitle__Name">${name}</span>
  </div>`;
}

let ChatHeaderTitleElement = class ChatHeaderTitleElement extends LitElement {
    static { this.styles = css `
    ${unsafeCSS(css_248z$9)}
  `; }
    render() {
        return chatHeaderTitleTemplate(this);
    }
};
__decorate([
    property({ type: String })
], ChatHeaderTitleElement.prototype, "title", void 0);
__decorate([
    property({ type: String })
], ChatHeaderTitleElement.prototype, "name", void 0);
ChatHeaderTitleElement = __decorate([
    carbonElement(`${WEB_COMPONENT_PREFIX}-chat-header-title`)
], ChatHeaderTitleElement);

const ChatHeaderTitle = o$1({
    tagName: `${WEB_COMPONENT_PREFIX}-chat-header-title`,
    elementClass: ChatHeaderTitleElement,
    react: React__default,
});

var css_248z$8 = "@charset \"UTF-8\";a,abbr,acronym,address,applet,article,aside,audio,b,big,blockquote,body,canvas,caption,center,cite,code,dd,del,details,dfn,div,dl,dt,em,embed,fieldset,figcaption,figure,footer,form,h1,h2,h3,h4,h5,h6,header,hgroup,html,i,iframe,img,ins,kbd,label,legend,li,mark,menu,nav,object,ol,output,p,pre,q,ruby,s,samp,section,small,span,strike,strong,sub,summary,sup,table,tbody,td,tfoot,th,thead,time,tr,tt,u,ul,var,video{padding:0;border:0;margin:0;font:inherit;font-feature-settings:\"liga\" 1;font-size:100%;vertical-align:initial}button,input,select,textarea{border-radius:0;font-family:inherit}article,aside,details,figcaption,figure,footer,header,hgroup,menu,nav,section{display:block}body{background-color:var(--cds-background,#fff);color:var(--cds-text-primary,#161616);line-height:1}ol,ul{list-style:none}blockquote,q{quotes:none}blockquote:after,blockquote:before,q:after,q:before{content:none}table{border-collapse:collapse;border-spacing:0}html{box-sizing:border-box}*,:after,:before{box-sizing:inherit}html{font-size:100%}body{font-weight:400;font-family:IBM Plex Sans,system-ui,-apple-system,BlinkMacSystemFont,\\.SFNSText-Regular,sans-serif;-moz-osx-font-smoothing:grayscale;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility}code{font-family:IBM Plex Mono,system-ui,-apple-system,BlinkMacSystemFont,\\.SFNSText-Regular,monospace}strong{font-weight:600}@media screen and (-ms-high-contrast:active){svg{fill:ButtonText}}h1{font-size:var(--cds-heading-06-font-size,42px);font-weight:var(--cds-heading-06-font-weight,300);line-height:var(--cds-heading-06-line-height,1.199);letter-spacing:var(--cds-heading-06-letter-spacing,0)}h2{font-size:var(--cds-heading-05-font-size,32px);font-weight:var(--cds-heading-05-font-weight,400);line-height:var(--cds-heading-05-line-height,1.25);letter-spacing:var(--cds-heading-05-letter-spacing,0)}h3{font-size:var(--cds-heading-04-font-size,28px);font-weight:var(--cds-heading-04-font-weight,400);line-height:var(--cds-heading-04-line-height,1.28572);letter-spacing:var(--cds-heading-04-letter-spacing,0)}h4{font-size:var(--cds-heading-03-font-size,20px);font-weight:var(--cds-heading-03-font-weight,400);line-height:var(--cds-heading-03-line-height,1.4);letter-spacing:var(--cds-heading-03-letter-spacing,0)}h5{font-size:var(--cds-heading-02-font-size,16px);font-weight:var(--cds-heading-02-font-weight,600);line-height:var(--cds-heading-02-line-height,1.5);letter-spacing:var(--cds-heading-02-letter-spacing,0)}h6{font-size:var(--cds-heading-01-font-size,14px);font-weight:var(--cds-heading-01-font-weight,600);line-height:var(--cds-heading-01-line-height,1.42857);letter-spacing:var(--cds-heading-01-letter-spacing,.16px)}p{font-size:var(--cds-body-02-font-size,16px);font-weight:var(--cds-body-02-font-weight,400);line-height:var(--cds-body-02-line-height,1.5);letter-spacing:var(--cds-body-02-letter-spacing,0)}a{color:var(--cds-link-primary,#0062fe)}em{font-style:italic}.cds--layer-one,:root{--cds-layer:var(--cds-layer-01,#f4f4f4);--cds-layer-active:var(--cds-layer-active-01,#c6c6c6);--cds-layer-hover:var(--cds-layer-hover-01,#e8e8e8);--cds-layer-selected:var(--cds-layer-selected-01,#e0e0e0);--cds-layer-selected-hover:var(--cds-layer-selected-hover-01,#d1d1d1);--cds-layer-accent:var(--cds-layer-accent-01,#e0e0e0);--cds-layer-accent-hover:var(--cds-layer-accent-hover-01,#d1d1d1);--cds-layer-accent-active:var(--cds-layer-accent-active-01,#a8a8a8);--cds-field:var(--cds-field-01,#f4f4f4);--cds-field-hover:var(--cds-field-hover-01,#e8e8e8);--cds-border-subtle:var(--cds-border-subtle-00,#e0e0e0);--cds-border-subtle-selected:var(--cds-border-subtle-selected-01,#c6c6c6);--cds-border-strong:var(--cds-border-strong-01,#8d8d8d);--cds-border-tile:var(--cds-border-tile-01,#c6c6c6)}.cds--layer-two{--cds-layer:var(--cds-layer-02,#fff);--cds-layer-active:var(--cds-layer-active-02,#c6c6c6);--cds-layer-hover:var(--cds-layer-hover-02,#e8e8e8);--cds-layer-selected:var(--cds-layer-selected-02,#e0e0e0);--cds-layer-selected-hover:var(--cds-layer-selected-hover-02,#d1d1d1);--cds-layer-accent:var(--cds-layer-accent-02,#e0e0e0);--cds-layer-accent-hover:var(--cds-layer-accent-hover-02,#d1d1d1);--cds-layer-accent-active:var(--cds-layer-accent-active-02,#a8a8a8);--cds-field:var(--cds-field-02,#fff);--cds-field-hover:var(--cds-field-hover-02,#e8e8e8);--cds-border-subtle:var(--cds-border-subtle-01,#c6c6c6);--cds-border-subtle-selected:var(--cds-border-subtle-selected-02,#c6c6c6);--cds-border-strong:var(--cds-border-strong-02,#8d8d8d);--cds-border-tile:var(--cds-border-tile-02,#a8a8a8)}.cds--layer-three{--cds-layer:var(--cds-layer-03,#f4f4f4);--cds-layer-active:var(--cds-layer-active-03,#c6c6c6);--cds-layer-hover:var(--cds-layer-hover-03,#e8e8e8);--cds-layer-selected:var(--cds-layer-selected-03,#e0e0e0);--cds-layer-selected-hover:var(--cds-layer-selected-hover-03,#d1d1d1);--cds-layer-accent:var(--cds-layer-accent-03,#e0e0e0);--cds-layer-accent-hover:var(--cds-layer-accent-hover-03,#d1d1d1);--cds-layer-accent-active:var(--cds-layer-accent-active-03,#a8a8a8);--cds-field:var(--cds-field-03,#f4f4f4);--cds-field-hover:var(--cds-field-hover-03,#e8e8e8);--cds-border-subtle:var(--cds-border-subtle-02,#e0e0e0);--cds-border-subtle-selected:var(--cds-border-subtle-selected-03,#c6c6c6);--cds-border-strong:var(--cds-border-strong-03,#8d8d8d);--cds-border-tile:var(--cds-border-tile-03,#c6c6c6)}@keyframes cds--hide-feedback{0%{opacity:1;visibility:inherit}to{opacity:0;visibility:hidden}}@keyframes cds--show-feedback{0%{opacity:0;visibility:hidden}to{opacity:1;visibility:inherit}}@keyframes cds--skeleton{0%{opacity:.3;transform:scaleX(0);transform-origin:left}20%{opacity:1;transform:scaleX(1);transform-origin:left}28%{transform:scaleX(1);transform-origin:right}51%{transform:scaleX(0);transform-origin:right}58%{transform:scaleX(0);transform-origin:right}82%{transform:scaleX(1);transform-origin:right}83%{transform:scaleX(1);transform-origin:left}96%{transform:scaleX(0);transform-origin:left}to{opacity:.3;transform:scaleX(0);transform-origin:left}}input:-webkit-autofill,input:-webkit-autofill:focus,input:-webkit-autofill:hover,textarea:-webkit-autofill,textarea:-webkit-autofill:focus,textarea:-webkit-autofill:hover{box-shadow:0 0 0 1000px var(--cds-field) inset;-webkit-text-fill-color:var(--cds-text-primary,#161616)}.cds--fieldset{box-sizing:border-box;padding:0;border:0;margin:0;font-family:inherit;font-size:100%;vertical-align:initial}.cds--fieldset *,.cds--fieldset :after,.cds--fieldset :before{box-sizing:inherit}.cds--form-item{font-size:var(--cds-body-compact-01-font-size,14px);font-weight:var(--cds-body-compact-01-font-weight,400);line-height:var(--cds-body-compact-01-line-height,1.28572);letter-spacing:var(--cds-body-compact-01-letter-spacing,.16px);display:flex;flex:1 1 auto;flex-direction:column;align-items:flex-start}.cds--label{font-size:var(--cds-label-01-font-size,12px);font-weight:var(--cds-label-01-font-weight,400);line-height:var(--cds-label-01-line-height,1.33333);letter-spacing:var(--cds-label-01-letter-spacing,.32px);display:inline-block;color:var(--cds-text-secondary,#525252);font-weight:400;line-height:16px;margin-block-end:8px;vertical-align:initial}.cds--label html{font-size:100%}.cds--label body{font-weight:400;font-family:IBM Plex Sans,system-ui,-apple-system,BlinkMacSystemFont,\\.SFNSText-Regular,sans-serif;-moz-osx-font-smoothing:grayscale;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility}.cds--label code{font-family:IBM Plex Mono,system-ui,-apple-system,BlinkMacSystemFont,\\.SFNSText-Regular,monospace}.cds--label strong{font-weight:600}.cds--label .cds--toggletip-label{font-size:var(--cds-label-01-font-size,12px);font-weight:var(--cds-label-01-font-weight,400);line-height:var(--cds-label-01-line-height,1.33333);letter-spacing:var(--cds-label-01-letter-spacing,.32px)}.cds--label--no-margin{margin-block-end:0}.cds--label+.cds--tooltip{position:relative;inset-block-start:3.2px;inset-inline-start:8px}.cds--label+.cds--tooltip .cds--tooltip__trigger{box-sizing:border-box;margin:0;font-family:inherit;font-size:100%;vertical-align:initial;display:inline-block;padding:0;border:0;-webkit-appearance:none;-moz-appearance:none;appearance:none;background:none;cursor:pointer;text-align:start;inline-size:100%;display:flex;align-items:center;justify-content:center;font-size:var(--cds-label-01-font-size,12px);font-weight:var(--cds-label-01-font-weight,400);line-height:var(--cds-label-01-line-height,1.33333);letter-spacing:var(--cds-label-01-letter-spacing,.32px)}.cds--label+.cds--tooltip .cds--tooltip__trigger *,.cds--label+.cds--tooltip .cds--tooltip__trigger :after,.cds--label+.cds--tooltip .cds--tooltip__trigger :before{box-sizing:inherit}.cds--label+.cds--tooltip .cds--tooltip__trigger::-moz-focus-inner{border:0}.cds--label+.cds--tooltip .cds--tooltip__trigger:focus{outline:1px solid var(--cds-focus,#0f62fe)}.cds--label+.cds--tooltip .cds--tooltip__trigger svg{fill:var(--cds-icon-secondary,#525252)}.cds--label+.cds--tooltip .cds--tooltip__trigger svg :hover{fill:var(--cds-icon-primary,#161616)}.cds--label+.cds--toggletip{inset-block-start:3.2px;inset-inline-start:8px}.cds--label.cds--skeleton{position:relative;padding:0;border:none;background:var(--cds-skeleton-background,#e8e8e8);box-shadow:none;pointer-events:none;block-size:14px;inline-size:75px}.cds--label.cds--skeleton:active,.cds--label.cds--skeleton:focus,.cds--label.cds--skeleton:hover{border:none;cursor:default;outline:none}.cds--label.cds--skeleton:before{position:absolute;animation:cds--skeleton 3s ease-in-out infinite;background:var(--cds-skeleton-element,#c6c6c6);block-size:100%;content:\"\";inline-size:100%;inset-inline-start:0;will-change:transform-origin,transform,opacity}@media (prefers-reduced-motion:reduce){.cds--label.cds--skeleton:before{animation:none}}input[type=number]{font-family:IBM Plex Sans,system-ui,-apple-system,BlinkMacSystemFont,\\.SFNSText-Regular,sans-serif}.cds--combo-box[data-invalid]:not(.cds--multi-select--selected) .cds--text-input:not(:focus),.cds--list-box[data-invalid]:not(.cds--multi-select--invalid--focused,.cds--combo-box--invalid--focused),.cds--number[data-invalid] input[type=number]:not(:focus),.cds--select-input__wrapper[data-invalid] .cds--select-input:not(:focus),.cds--text-area__wrapper[data-invalid]>.cds--text-area--invalid:not(:focus),.cds--text-input__field-wrapper[data-invalid]>.cds--text-input--invalid:not(:focus),input[data-invalid]:not(:focus){outline:2px solid var(--cds-support-error,#da1e28);outline-offset:-2px}@media screen and (prefers-contrast){.cds--combo-box[data-invalid]:not(.cds--multi-select--selected) .cds--text-input:not(:focus),.cds--list-box[data-invalid]:not(.cds--multi-select--invalid--focused,.cds--combo-box--invalid--focused),.cds--number[data-invalid] input[type=number]:not(:focus),.cds--select-input__wrapper[data-invalid] .cds--select-input:not(:focus),.cds--text-area__wrapper[data-invalid]>.cds--text-area--invalid:not(:focus),.cds--text-input__field-wrapper[data-invalid]>.cds--text-input--invalid:not(:focus),input[data-invalid]:not(:focus){outline-style:dotted}}.cds--date-picker-input__wrapper--invalid~.cds--form-requirement,.cds--date-picker-input__wrapper--warn~.cds--form-requirement,.cds--date-picker-input__wrapper~.cds--form-requirement,.cds--list-box--warning~.cds--form-requirement,.cds--list-box[data-invalid]~.cds--form-requirement,.cds--number[data-invalid] .cds--number__input-wrapper~.cds--form-requirement,.cds--number__input-wrapper--warning~.cds--form-requirement,.cds--select--warning .cds--select-input__wrapper~.cds--form-requirement,.cds--select-input__wrapper[data-invalid]~.cds--form-requirement,.cds--text-area__wrapper--warn~.cds--form-requirement,.cds--text-area__wrapper[data-invalid]~.cds--form-requirement,.cds--text-input__field-wrapper--warning>.cds--text-input~.cds--form-requirement,.cds--text-input__field-wrapper--warning~.cds--form-requirement,.cds--text-input__field-wrapper[data-invalid]~.cds--form-requirement,.cds--time-picker--invalid~.cds--form-requirement,.cds--time-picker--warning~.cds--form-requirement,.cds--time-picker[data-invalid]~.cds--form-requirement,input[data-invalid]~.cds--form-requirement{display:block;overflow:visible;font-weight:400;max-block-size:200px}.cds--date-picker-input__wrapper--invalid~.cds--form-requirement,.cds--date-picker-input__wrapper~.cds--form-requirement,.cds--list-box[data-invalid]~.cds--form-requirement,.cds--number[data-invalid] .cds--number__input-wrapper~.cds--form-requirement,.cds--select-input__wrapper[data-invalid]~.cds--form-requirement,.cds--text-area__wrapper[data-invalid]~.cds--form-requirement,.cds--text-input__field-wrapper[data-invalid]~.cds--form-requirement,.cds--time-picker--invalid~.cds--form-requirement,.cds--time-picker[data-invalid]~.cds--form-requirement,input[data-invalid]~.cds--form-requirement{color:var(--cds-text-error,#da1e28)}.cds--form--fluid .cds--text-input__field-wrapper--warning,.cds--form--fluid .cds--text-input__field-wrapper[data-invalid]{display:block}.cds--form--fluid input[data-invalid]{outline:none}.cds--form--fluid .cds--form-requirement{padding:8px 40px 8px 16px;margin:0}input:not(output,[data-invalid]):-moz-ui-invalid{box-shadow:none}.cds--form-requirement{font-size:var(--cds-label-01-font-size,12px);font-weight:var(--cds-label-01-font-weight,400);line-height:var(--cds-label-01-line-height,1.33333);letter-spacing:var(--cds-label-01-letter-spacing,.32px);display:none;overflow:hidden;margin:4px 0 0;max-block-size:0}.cds--form-requirement html{font-size:100%}.cds--form-requirement body{font-weight:400;font-family:IBM Plex Sans,system-ui,-apple-system,BlinkMacSystemFont,\\.SFNSText-Regular,sans-serif;-moz-osx-font-smoothing:grayscale;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility}.cds--form-requirement code{font-family:IBM Plex Mono,system-ui,-apple-system,BlinkMacSystemFont,\\.SFNSText-Regular,monospace}.cds--form-requirement strong{font-weight:600}.cds--select--inline .cds--form__helper-text{margin-block-start:0}.cds--form__helper-text{font-size:var(--cds-helper-text-01-font-size,12px);line-height:var(--cds-helper-text-01-line-height,1.33333);letter-spacing:var(--cds-helper-text-01-letter-spacing,.32px);z-index:0;color:var(--cds-text-secondary,#525252);inline-size:100%;margin-block-start:4px;opacity:1}.cds--form__helper-text--disabled,.cds--label--disabled,fieldset[disabled] .cds--form__helper-text,fieldset[disabled] .cds--label{color:var(--cds-text-disabled,hsla(0,0%,9%,.25))}.cds--assistive-text,.cds--visually-hidden{position:absolute;overflow:hidden;padding:0;border:0;margin:-1px;block-size:1px;clip:rect(0,0,0,0);inline-size:1px;visibility:inherit;white-space:nowrap}.cds--checkbox-group{box-sizing:border-box;padding:0;border:0;margin:0;font-family:inherit;font-size:100%;vertical-align:initial}.cds--checkbox-group *,.cds--checkbox-group :after,.cds--checkbox-group :before{box-sizing:inherit}.cds--form-item.cds--checkbox-wrapper{position:relative;margin-block-end:6px}.cds--form-item.cds--checkbox-wrapper:first-of-type{margin-block-start:0}.cds--label+.cds--form-item.cds--checkbox-wrapper{margin-block-start:-2px}.cds--form-item.cds--checkbox-wrapper:last-of-type{margin-block-end:3px}.cds--checkbox{position:absolute;overflow:hidden;padding:0;border:0;margin:-1px;block-size:1px;clip:rect(0,0,0,0);inline-size:1px;visibility:inherit;white-space:nowrap;inset-block-start:20px;inset-inline-start:11.2px}.cds--checkbox-label{font-size:var(--cds-body-compact-01-font-size,14px);font-weight:var(--cds-body-compact-01-font-weight,400);line-height:var(--cds-body-compact-01-line-height,1.28572);letter-spacing:var(--cds-body-compact-01-letter-spacing,.16px);position:relative;display:flex;cursor:pointer;min-block-size:20px;padding-block-start:1px;padding-inline-start:20px;-webkit-user-select:none;-moz-user-select:none;user-select:none}.cds--checkbox-label html{font-size:100%}.cds--checkbox-label body{font-weight:400;font-family:IBM Plex Sans,system-ui,-apple-system,BlinkMacSystemFont,\\.SFNSText-Regular,sans-serif;-moz-osx-font-smoothing:grayscale;-webkit-font-smoothing:antialiased;text-rendering:optimizeLegibility}.cds--checkbox-label code{font-family:IBM Plex Mono,system-ui,-apple-system,BlinkMacSystemFont,\\.SFNSText-Regular,monospace}.cds--checkbox-label strong{font-weight:600}.cds--checkbox-label-text{padding-inline-start:10px}.cds--checkbox-label:after,.cds--checkbox-label:before{box-sizing:border-box}@media print{.cds--checkbox-label:after,.cds--checkbox-label:before{-webkit-print-color-adjust:exact;print-color-adjust:exact}}.cds--checkbox-label:before{position:absolute;border:1px solid var(--cds-icon-primary,#161616);border-radius:2px;background-color:initial;block-size:16px;content:\"\";inline-size:16px;inset-block-start:2px;inset-inline-start:0;margin-block:1px 2px;margin-inline:3px 0}.cds--checkbox-label:after{position:absolute;background:none;block-size:5px;border-block-end:1.5px solid var(--cds-icon-inverse,#fff);border-inline-start:1.5px solid var(--cds-icon-inverse,#fff);content:\"\";inline-size:9px;inset-block-start:6.5px;inset-inline-start:7px;margin-block-start:-3px;transform:scale(0) rotate(-45deg);transform-origin:bottom right}.cds--checkbox-label[data-contained-checkbox-state=true]:before,.cds--checkbox:checked+.cds--checkbox-label:before,.cds--checkbox:indeterminate+.cds--checkbox-label:before{border:1px;background-color:var(--cds-icon-primary,#161616)}.cds--checkbox-label[data-contained-checkbox-state=true]:after,.cds--checkbox:checked+.cds--checkbox-label:after{transform:scale(1) rotate(-45deg)}.cds--checkbox:indeterminate+.cds--checkbox-label:after{border-block-end:2px solid var(--cds-icon-inverse,#fff);border-inline-start:0 solid var(--cds-icon-inverse,#fff);inline-size:8px;inset-block-start:11px;transform:scale(1) rotate(0deg)}.cds--checkbox-label[data-contained-checkbox-state=true].cds--checkbox-label__focus:before,.cds--checkbox-label__focus:before,.cds--checkbox:checked:focus+.cds--checkbox-label:before,.cds--checkbox:focus+.cds--checkbox-label:before,.cds--checkbox:indeterminate:focus+.cds--checkbox-label:before{outline:2px solid var(--cds-focus,#0f62fe);outline-offset:1px}.cds--checkbox-label[data-contained-checkbox-disabled=true],.cds--checkbox:disabled+.cds--checkbox-label{color:var(--cds-text-disabled,hsla(0,0%,9%,.25));cursor:not-allowed}.cds--checkbox-label[data-contained-checkbox-disabled=true]:before,.cds--checkbox:disabled+.cds--checkbox-label:before{border-color:var(--cds-icon-disabled,hsla(0,0%,9%,.25))}.cds--checkbox-label[data-contained-checkbox-state=true][data-contained-checkbox-disabled=true]:before,.cds--checkbox:checked:disabled+.cds--checkbox-label:before,.cds--checkbox:indeterminate:disabled+.cds--checkbox-label:before{background-color:var(--cds-icon-disabled,hsla(0,0%,9%,.25))}.cds--checkbox-group[data-invalid] .cds--checkbox-label:before,.cds--checkbox-wrapper--invalid .cds--checkbox-label:before,.cds--checkbox-wrapper--invalid .cds--checkbox:checked+.cds--checkbox-label:before{border:1px solid var(--cds-support-error,#da1e28)}.cds--checkbox-group .cds--checkbox-wrapper--invalid>.cds--checkbox__validation-msg,.cds--checkbox-group .cds--checkbox-wrapper--warning>.cds--checkbox__validation-msg,.cds--checkbox-group .cds--checkbox-wrapper>.cds--form__helper-text{display:none}.cds--checkbox-group:not(.cds--checkbox-group[data-invalid]) .cds--checkbox-wrapper--invalid .cds--checkbox-label:before,.cds--checkbox-group:not(.cds--checkbox-group[data-invalid]) .cds--checkbox-wrapper--invalid .cds--checkbox:checked+.cds--checkbox-label:before{border:1px solid var(--cds-icon-primary,#161616)}.cds--checkbox-group__validation-msg,.cds--checkbox__validation-msg{display:none;align-items:flex-start;inline-size:100%;margin-block-start:4px}.cds--checkbox__invalid-icon{margin:1px 1px 0 3px;fill:var(--cds-support-error,#da1e28);min-inline-size:16px}.cds--checkbox__invalid-icon--warning{fill:var(--cds-support-warning,#f1c21b)}.cds--checkbox__invalid-icon--warning path:first-of-type{fill:#000}.cds--checkbox-group--invalid .cds--checkbox-group__validation-msg,.cds--checkbox-group--warning .cds--checkbox-group__validation-msg,.cds--checkbox-wrapper--invalid>.cds--checkbox__validation-msg,.cds--checkbox-wrapper--warning>.cds--checkbox__validation-msg{display:flex}.cds--checkbox-group--invalid .cds--checkbox-group__validation-msg .cds--form-requirement,.cds--checkbox-group--warning .cds--checkbox-group__validation-msg .cds--form-requirement,.cds--checkbox-wrapper--invalid .cds--checkbox__validation-msg .cds--form-requirement,.cds--checkbox-wrapper--warning .cds--checkbox__validation-msg .cds--form-requirement{display:block;overflow:visible;margin-block-start:0;margin-inline-start:8px;max-block-size:100%}.cds--checkbox-group--invalid .cds--checkbox-group__validation-msg .cds--form-requirement,.cds--checkbox-wrapper--invalid .cds--checkbox__validation-msg .cds--form-requirement{color:var(--cds-text-error,#da1e28)}.cds--checkbox-group--readonly .cds--checkbox-label,.cds--checkbox-wrapper--readonly .cds--checkbox-label{cursor:default}.cds--checkbox-group--readonly .cds--checkbox-label-text,.cds--checkbox-wrapper--readonly .cds--checkbox-label-text{cursor:text;-webkit-user-select:text;-moz-user-select:text;user-select:text}.cds--checkbox-group--readonly .cds--checkbox+.cds--checkbox-label:before,.cds--checkbox-wrapper--readonly .cds--checkbox+.cds--checkbox-label:before{border-color:var(--cds-icon-disabled,hsla(0,0%,9%,.25))}.cds--checkbox-group--readonly .cds--checkbox:checked+.cds--checkbox-label:before,.cds--checkbox-wrapper--readonly .cds--checkbox:checked+.cds--checkbox-label:before{border:1px solid var(--cds-icon-disabled,hsla(0,0%,9%,.25));background:transparent}.cds--checkbox-group--readonly .cds--checkbox:checked+.cds--checkbox-label:after,.cds--checkbox-wrapper--readonly .cds--checkbox:checked+.cds--checkbox-label:after{border-color:var(--cds-text-primary,#161616)}.cds--checkbox-skeleton .cds--checkbox-label{cursor:default}.cds--checkbox-label-text.cds--skeleton{position:relative;padding:0;border:none;background:var(--cds-skeleton-background,#e8e8e8);box-shadow:none;pointer-events:none;block-size:16px;inline-size:100px;margin-block:1px 0;margin-inline:6px 0}.cds--checkbox-label-text.cds--skeleton:active,.cds--checkbox-label-text.cds--skeleton:focus,.cds--checkbox-label-text.cds--skeleton:hover{border:none;cursor:default;outline:none}.cds--checkbox-label-text.cds--skeleton:before{position:absolute;animation:cds--skeleton 3s ease-in-out infinite;background:var(--cds-skeleton-element,#c6c6c6);block-size:100%;content:\"\";inline-size:100%;inset-inline-start:0;will-change:transform-origin,transform,opacity}@media (prefers-reduced-motion:reduce){.cds--checkbox-label-text.cds--skeleton:before{animation:none}}.cds--checkbox--inline{position:relative}[dir=rtl] .cds--checkbox-label:after{margin-block-start:0;margin-inline-start:-1px;transform-origin:center}[dir=rtl] .cds--checkbox-label[data-contained-checkbox-state=true]:after,[dir=rtl] .cds--checkbox:checked+.cds--checkbox-label:after{transform:scale(1.2) rotate3d(.5,1,0,158deg)}.cds--checkbox-group--decorator legend.cds--label,.cds--checkbox-group--slug legend.cds--label,.cds--checkbox-wrapper--decorator .cds--checkbox-label-text,.cds--checkbox-wrapper--slug .cds--checkbox-label-text{display:flex}.cds--checkbox-group--decorator legend.cds--label .cds--checkbox-group-inner--decorator>*,.cds--checkbox-group--slug legend.cds--label .cds--ai-label,.cds--checkbox-group--slug legend.cds--label .cds--slug,.cds--checkbox-wrapper--decorator .cds--checkbox-label-text .cds--checkbox-wrapper-inner--decorator>*,.cds--checkbox-wrapper--slug .cds--checkbox-label-text .cds--ai-label,.cds--checkbox-wrapper--slug .cds--checkbox-label-text .cds--slug{margin-inline-start:8px}.cds--checkbox-wrapper--decorator .cds--checkbox-label-text .cds--ai-label__button--inline,.cds--checkbox-wrapper--slug .cds--checkbox-label-text .cds--ai-label__button--inline,.cds--checkbox-wrapper--slug .cds--checkbox-label-text .cds--slug__button--inline{line-height:inherit;margin-block-start:-1px}.cds--checkbox-group--horizontal{position:relative;display:flex;flex-flow:row wrap;justify-content:flex-start}.cds--checkbox-group--horizontal .cds--form-item{flex:none;margin-block-end:0}.cds--checkbox-group--horizontal .cds--form-item:not(:last-of-type){margin-inline-end:16px}.cds--checkbox-group--horizontal .cds--checkbox-label-text{padding-inline-start:8px}.cds--checkbox-group--horizontal .cds--label+.cds--form-item.cds--checkbox-wrapper{margin-block-start:0}.cds--radio-button-group{box-sizing:border-box;padding:0;border:0;margin:0;font-family:inherit;font-size:100%;vertical-align:initial;position:relative;display:flex;align-items:center}.cds--radio-button-group *,.cds--radio-button-group :after,.cds--radio-button-group :before{box-sizing:inherit}.cds--label+.cds--form-item .cds--radio-button-group{margin-block-start:0}.cds--radio-button-group--vertical{flex-direction:column;align-items:flex-start}.cds--radio-button-group--vertical.cds--radio-button-group--label-left{align-items:flex-end}.cds--radio-button-group--vertical .cds--radio-button__label{margin-inline-end:0}.cds--radio-button-group--vertical .cds--radio-button__label:not(:last-of-type){margin-block-end:8px}.cds--radio-button{position:absolute;overflow:hidden;padding:0;border:0;margin:-1px;block-size:1px;clip:rect(0,0,0,0);inline-size:1px;white-space:nowrap;margin-block-start:14.4px;margin-inline-start:10.08px;visibility:inherit}.cds--radio-button__label{display:flex;align-items:center;cursor:pointer;margin-inline-end:16px}.cds--radio-button__label-text{font-size:var(--cds-body-compact-01-font-size,14px);font-weight:var(--cds-body-compact-01-font-weight,400);line-height:var(--cds-body-compact-01-line-height,1.28572);letter-spacing:var(--cds-body-compact-01-letter-spacing,.16px);flex:1}.cds--radio-button__appearance{box-sizing:border-box;padding:0;border:0;margin:0;font-family:inherit;font-size:100%;vertical-align:initial;flex-shrink:0;border:1px solid var(--cds-icon-primary,#161616);border-radius:50%;background-color:initial;block-size:18px;inline-size:18px;margin-block:1px 2px;margin-inline:2px 10px}.cds--radio-button__appearance *,.cds--radio-button__appearance :after,.cds--radio-button__appearance :before{box-sizing:inherit}.cds--radio-button-group--vertical .cds--radio-button__appearance{margin-block:0}.cds--radio-button:checked+.cds--radio-button__label .cds--radio-button__appearance{display:flex;align-items:center;justify-content:center;border-color:var(--cds-icon-primary,#161616)}.cds--radio-button:checked+.cds--radio-button__label .cds--radio-button__appearance:before{position:relative;display:inline-block;border-radius:50%;background-color:var(--cds-icon-primary,#161616);block-size:100%;content:\"\";inline-size:100%;transform:scale(.5)}@media (forced-colors:active),screen and (-ms-high-contrast:active){.cds--radio-button:checked+.cds--radio-button__label .cds--radio-button__appearance:before{fill:ButtonText;background-color:ButtonText}}@media print{.cds--radio-button:checked+.cds--radio-button__label .cds--radio-button__appearance:before{-webkit-print-color-adjust:exact;print-color-adjust:exact}}.cds--radio-button:disabled+.cds--radio-button__label{color:var(--cds-text-disabled,hsla(0,0%,9%,.25));cursor:not-allowed}.cds--radio-button:disabled+.cds--radio-button__label .cds--radio-button__appearance,.cds--radio-button:disabled:checked+.cds--radio-button__label .cds--radio-button__appearance{border-color:var(--cds-icon-disabled,hsla(0,0%,9%,.25))}.cds--radio-button:disabled+.cds--radio-button__label .cds--radio-button__appearance:before,.cds--radio-button:disabled:checked+.cds--radio-button__label .cds--radio-button__appearance:before{background-color:var(--cds-text-disabled,hsla(0,0%,9%,.25))}.cds--radio-button-group--readonly .cds--radio-button+.cds--radio-button__label .cds--radio-button__appearance{border-color:var(--cds-icon-disabled,hsla(0,0%,9%,.25))}.cds--radio-button-group--readonly .cds--radio-button__label{cursor:default}.cds--radio-button-group--readonly .cds--radio-button__label-text{cursor:text;-webkit-user-select:text;-moz-user-select:text;user-select:text}.cds--radio-button-group--invalid .cds--radio-button+.cds--radio-button__label .cds--radio-button__appearance{border-color:var(--cds-support-error,#da1e28)}.cds--radio-button__validation-msg{display:none;align-items:flex-end;margin-block-start:6px}.cds--radio-button__invalid-icon{fill:var(--cds-support-error,#da1e28);margin-inline:3px 1px}.cds--radio-button__invalid-icon--warning{fill:var(--cds-support-warning,#f1c21b)}.cds--radio-button__invalid-icon--warning path:first-of-type{fill:#000}.cds--radio-button-group--invalid+.cds--radio-button__validation-msg,.cds--radio-button-group--warning+.cds--radio-button__validation-msg{display:flex}.cds--radio-button-group--invalid+.cds--radio-button__validation-msg .cds--form-requirement,.cds--radio-button-group--warning+.cds--radio-button__validation-msg .cds--form-requirement{display:block;overflow:visible;margin-block-start:0;margin-inline-start:8px;max-block-size:100%}.cds--radio-button-group--invalid+.cds--radio-button__validation-msg .cds--form-requirement{color:var(--cds-text-error,#da1e28)}.cds--radio-button-group~.cds--form__helper-text{margin-block-start:6px}.cds--radio-button:focus+.cds--radio-button__label .cds--radio-button__appearance{outline:2px solid var(--cds-focus,#0f62fe);outline-offset:1.5px}.cds--radio-button__label.cds--skeleton{position:relative;padding:0;border:none;background:var(--cds-skeleton-background,#e8e8e8);box-shadow:none;pointer-events:none;block-size:18px;inline-size:100px}.cds--radio-button__label.cds--skeleton:active,.cds--radio-button__label.cds--skeleton:focus,.cds--radio-button__label.cds--skeleton:hover{border:none;cursor:default;outline:none}.cds--radio-button__label.cds--skeleton:before{position:absolute;animation:cds--skeleton 3s ease-in-out infinite;background:var(--cds-skeleton-element,#c6c6c6);block-size:100%;content:\"\";inline-size:100%;inset-inline-start:0;will-change:transform-origin,transform,opacity}@media (prefers-reduced-motion:reduce){.cds--radio-button__label.cds--skeleton:before{animation:none}}.cds--radio-button__label.cds--skeleton .cds--radio-button__appearance{display:none}.cds--radio-button-wrapper .cds--radio-button__label{display:flex;align-items:flex-start;justify-content:center;margin:0}.cds--radio-button-wrapper:not(:last-of-type){margin-inline-end:16px}.cds--radio-button-group--vertical .cds--radio-button-wrapper{margin-block-end:6px;margin-inline-end:0}.cds--radio-button-group--vertical .cds--radio-button-wrapper .cds--radio-button__label{padding-block-start:2px}.cds--radio-button-group--label-right .cds--radio-button__label,.cds--radio-button-wrapper.cds--radio-button-wrapper--label-right .cds--radio-button__label{flex-direction:row}.cds--radio-button-group--label-left .cds--radio-button__label,.cds--radio-button-wrapper.cds--radio-button-wrapper--label-left .cds--radio-button__label{flex-direction:row-reverse}.cds--radio-button-group--label-left .cds--radio-button__appearance,.cds--radio-button-wrapper.cds--radio-button-wrapper--label-left .cds--radio-button__appearance{margin-inline:8px 0}.cds--radio-button-group--decorator legend.cds--label,.cds--radio-button-group--slug legend.cds--label,.cds--radio-button-wrapper--decorator .cds--radio-button__label-text,.cds--radio-button-wrapper--slug .cds--radio-button__label-text{display:flex}.cds--radio-button-group--decorator legend.cds--label .cds--radio-button-group-inner--decorator>*,.cds--radio-button-group--slug legend.cds--label .cds--ai-label,.cds--radio-button-group--slug legend.cds--label .cds--slug,.cds--radio-button-wrapper--decorator .cds--radio-button__label-text .cds--radio-button-wrapper-inner--decorator>*,.cds--radio-button-wrapper--slug .cds--radio-button__label-text .cds--ai-label,.cds--radio-button-wrapper--slug .cds--radio-button__label-text .cds--slug{margin-inline-start:8px}.cds--radio-button-wrapper--decorator .cds--radio-button__label-text .cds--ai-label__button--inline,.cds--radio-button-wrapper--slug .cds--radio-button__label-text .cds--ai-label__button--inline,.cds--radio-button-wrapper--slug .cds--radio-button__label-text .cds--slug__button--inline{line-height:inherit;margin-block-start:-1px}.cds--data-table-container{position:relative;padding-block-start:2px}.cds--data-table-content{display:block;overflow-x:auto}.cds--data-table-content:focus{outline:2px solid var(--cds-focus,#0f62fe);outline-offset:-2px}@media screen and (prefers-contrast){.cds--data-table-content:focus{outline-style:dotted}}.cds--data-table-header{background-color:var(--cds-layer);padding-block:16px 24px;padding-inline:16px}.cds--data-table-header__title{font-size:var(--cds-heading-03-font-size,20px);font-weight:var(--cds-heading-03-font-weight,400);line-height:var(--cds-heading-03-line-height,1.4);letter-spacing:var(--cds-heading-03-letter-spacing,0);color:var(--cds-text-primary,#161616)}.cds--data-table-header__description{font-size:var(--cds-body-compact-01-font-size,14px);font-weight:var(--cds-body-compact-01-font-weight,400);line-height:var(--cds-body-compact-01-line-height,1.28572);letter-spacing:var(--cds-body-compact-01-letter-spacing,.16px);color:var(--cds-text-secondary,#525252)}@media (min-width:672px){.cds--data-table-header__description{max-inline-size:50ch}}@media (min-width:1056px){.cds--data-table-header__description{max-inline-size:80ch}}.cds--data-table{border-collapse:collapse;border-spacing:0;inline-size:100%}.cds--data-table thead{font-size:var(--cds-heading-compact-01-font-size,14px);font-weight:var(--cds-heading-compact-01-font-weight,600);line-height:var(--cds-heading-compact-01-line-height,1.28572);letter-spacing:var(--cds-heading-compact-01-letter-spacing,.16px);background-color:var(--cds-layer-accent)}.cds--data-table tbody{font-size:var(--cds-body-compact-01-font-size,14px);font-weight:var(--cds-body-compact-01-font-weight,400);line-height:var(--cds-body-compact-01-line-height,1.28572);letter-spacing:var(--cds-body-compact-01-letter-spacing,.16px);background-color:var(--cds-layer);inline-size:100%}.cds--data-table tr{border:none;block-size:48px;inline-size:100%}.cds--data-table tbody tr,.cds--data-table tbody tr td,.cds--data-table tbody tr th{transition:background-color 70ms cubic-bezier(0,0,.38,.9)}.cds--data-table tbody tr:not([data-child-row]):hover,.cds--data-table tbody tr[data-child-row]:hover>td{background-color:var(--cds-layer-hover)}.cds--data-table tbody tr:hover td,.cds--data-table tbody tr:hover th{border-block-end:1px solid var(--cds-layer-hover);border-block-start:1px solid var(--cds-layer-hover);color:var(--cds-text-primary,#161616)}.cds--data-table tr:hover .cds--link:not(.cds--popover-container .cds--link){color:var(--cds-link-secondary,#0043ce)}.cds--data-table tr:hover .cds--link--disabled:not(.cds--popover-container .cds--link){color:var(--cds-text-disabled,hsla(0,0%,9%,.25))}.cds--data-table td,.cds--data-table th{text-align:start;vertical-align:middle}.cds--data-table.cds--data-table--top-aligned-body.cds--data-table--lg tr:not([data-child-row]) td:not(.cds--table-expand){padding-block:16px 16px}.cds--data-table.cds--data-table--top-aligned-body.cds--data-table--lg tr:not([data-child-row]) td:not(.cds--table-expand).cds--table-column-menu{padding-block-start:8px}.cds--data-table.cds--data-table--top-aligned-body.cds--data-table--lg tr:not([data-child-row]) td:not(.cds--table-expand).cds--table-column-checkbox:not(.cds--table-column-radio){padding-block-start:13px}.cds--data-table.cds--data-table--top-aligned-body td{vertical-align:top}.cds--data-table.cds--data-table--top-aligned-header.cds--data-table--lg th:not(.cds--table-expand):not(.cds--table-sort__header){padding-block:16px 16px}.cds--data-table.cds--data-table--top-aligned-header.cds--data-table--lg th:not(.cds--table-expand):not(.cds--table-sort__header).cds--table-column-menu{padding-block-start:8px}.cds--data-table.cds--data-table--top-aligned-header.cds--data-table--lg th:not(.cds--table-expand):not(.cds--table-sort__header).cds--table-column-checkbox{padding-block-start:13px}.cds--data-table.cds--data-table--top-aligned-header th{vertical-align:top}.cds--data-table td[align=right],.cds--data-table th[align=right]{text-align:end}.cds--data-table td[align=center],.cds--data-table th[align=center]{text-align:center}.cds--data-table th{background-color:var(--cds-layer-accent);color:var(--cds-text-primary,#161616);padding-inline:16px 16px}.cds--data-table th:last-of-type{position:static;inline-size:auto}.cds--data-table .cds--table-header-label{text-align:start}.cds--data-table tbody th,.cds--data-table td{border-block-end:1px solid var(--cds-border-subtle-01,#c6c6c6);border-block-start:1px solid var(--cds-layer);color:var(--cds-text-secondary,#525252);padding-inline:16px 16px}.cds--data-table tbody th+td:first-of-type,.cds--data-table td+td:first-of-type{padding-inline-start:12px}.cds--layer-two .cds--data-table tbody th,.cds--layer-two .cds--data-table td{border-block-end:1px solid var(--cds-border-subtle-02,#e0e0e0)}.cds--layer-three .cds--data-table tbody th,.cds--layer-three .cds--data-table td{border-block-end:1px solid var(--cds-border-subtle-03,#c6c6c6)}@supports (-moz-appearance:none){.cds--data-table td{background-clip:padding-box}}.cds--data-table .cds--dropdown,.cds--data-table .cds--list-box,.cds--data-table .cds--list-box input[role=combobox],.cds--data-table .cds--list-box input[type=text],.cds--data-table .cds--number input[type=number],.cds--data-table .cds--number__control-btn:after,.cds--data-table .cds--number__control-btn:before,.cds--data-table .cds--select-input,.cds--data-table .cds--text-input{background-color:var(--cds-field-02,#fff)}.cds--data-table td.cds--table-column-menu .cds--overflow-menu[aria-expanded=false]:focus{outline:2px solid var(--cds-focus,#0f62fe);outline-offset:-2px}@media screen and (prefers-contrast){.cds--data-table td.cds--table-column-menu .cds--overflow-menu[aria-expanded=false]:focus{outline-style:dotted}}.cds--data-table td.cds--table-column-menu .cds--overflow-menu[aria-expanded=true]:focus{outline:none}@media (-ms-high-contrast:active),(-ms-high-contrast:none),screen and (hover:hover){.cds--data-table td.cds--table-column-menu .cds--overflow-menu .cds--overflow-menu__icon{opacity:0}}.cds--data-table td.cds--table-column-menu .cds--overflow-menu.cds--overflow-menu--open .cds--overflow-menu__icon,.cds--data-table td.cds--table-column-menu .cds--overflow-menu:focus .cds--overflow-menu__icon,.cds--data-table td.cds--table-column-menu .cds--overflow-menu:hover .cds--overflow-menu__icon,.cds--data-table tr:hover td.cds--table-column-menu .cds--overflow-menu .cds--overflow-menu__icon,.cds--data-table.cds--data-table--visible-overflow-menu td.cds--table-column-menu .cds--overflow-menu .cds--overflow-menu__icon{opacity:1}.cds--table-row--menu-option .cds--overflow-menu-options__btn .cds--overflow-menu-options__option-content svg{position:relative;inset-block-start:3px;margin-inline-end:8px}.cds--data-table .cds--overflow-menu:hover,.cds--data-table .cds--overflow-menu__trigger:hover{background-color:var(--cds-layer-selected-hover)}.cds--data-table--selected .cds--overflow-menu:hover,.cds--data-table--selected .cds--overflow-menu__trigger:hover{background-color:var(--cds-layer-hover)}.cds--data-table--selected .cds--link:not(.cds--link--disabled){color:var(--cds-link-secondary,#0043ce)}.cds--data-table--sm td.cds--table-column-menu,.cds--data-table--xs td.cds--table-column-menu{block-size:24px;padding-block:0}.cds--data-table--sm td.cds--table-column-menu{block-size:32px}.cds--data-table--md td.cds--table-column-menu{block-size:40px}.cds--data-table--xl .cds--table-column-menu{padding-block-start:8px}.cds--data-table--zebra tbody tr:not(.cds--parent-row):nth-child(odd) td{border-block-end:1px solid var(--cds-layer)}.cds--data-table--zebra tbody tr:not(.cds--parent-row):nth-child(2n) td{border-block-end:1px solid var(--cds-layer-accent);border-block-start:1px solid var(--cds-layer-accent)}.cds--data-table--zebra tbody tr:not(.cds--parent-row):not(.cds--data-table--selected):nth-child(2n){background-color:var(--cds-layer-accent)}.cds--data-table--zebra tbody tr:not(.cds--parent-row):hover td{border-block-end:1px solid var(--cds-layer-hover);border-block-start:1px solid var(--cds-layer-hover)}.cds--data-table--zebra tbody tr:not(.cds--parent-row):not(.cds--data-table--selected):hover{background-color:var(--cds-layer-hover)}.cds--table-column-checkbox .cds--checkbox-label{min-block-size:24px;padding-inline-start:0}.cds--table-column-checkbox .cds--checkbox-label:before{margin-block-start:2px}.cds--table-column-checkbox .cds--checkbox-label:after{inset-block-start:7.5px}.cds--data-table th.cds--table-column-checkbox{position:static;background-color:var(--cds-layer-accent);inline-size:32px;transition:background-color 70ms cubic-bezier(0,0,.38,.9)}.cds--data-table tbody td.cds--table-column-checkbox,.cds--data-table tbody td.cds--table-expand,.cds--data-table thead th.cds--table-column-checkbox,.cds--data-table thead th.cds--table-expand{min-inline-size:0}.cds--data-table tbody td.cds--table-column-checkbox,.cds--data-table thead th.cds--table-column-checkbox{min-inline-size:40px;padding-inline:16px 4px}.cds--data-table tbody td.cds--table-expand,.cds--data-table thead th.cds--table-expand{block-size:32px;inline-size:32px}.cds--data-table--xs tbody td.cds--table-expand,.cds--data-table--xs thead th.cds--table-expand{padding:0 0 0 8px;block-size:24px;inline-size:24px}.cds--data-table--sm tbody td.cds--table-expand,.cds--data-table--sm thead th.cds--table-expand{padding:0;block-size:32px;inline-size:32px;padding-inline-start:8px}.cds--data-table--md tbody td.cds--table-expand,.cds--data-table--md thead th.cds--table-expand{padding:4px 0 4px 8px;block-size:40px;inline-size:40px}.cds--data-table--xl tbody td.cds--table-expand,.cds--data-table--xl thead th.cds--table-expand{block-size:64px;padding-block:10px 22px}.cds--data-table--xl .cds--table-column-checkbox{padding-block-start:13px}.cds--data-table--xl .cds--table-column-radio{padding-block-start:16px}.cds--table-column-radio{inline-size:48px}.cds--table-column-radio .cds--radio-button__appearance{margin-inline-end:-2px}.cds--data-table--zebra tbody tr:nth-child(odd).cds--data-table--selected td,tr.cds--data-table--selected td{border-block-end:1px solid var(--cds-layer-active);border-block-start:1px solid var(--cds-layer-selected);color:var(--cds-text-primary,#161616)}.cds--data-table--zebra tbody tr:nth-child(odd).cds--data-table--selected,tr.cds--data-table--selected{background-color:var(--cds-layer-selected)}.cds--data-table--zebra tbody tr:first-of-type:nth-child(odd).cds--data-table--selected td,tr.cds--data-table--selected:first-of-type td{border-block-start:1px solid var(--cds-border-subtle-selected)}.cds--data-table--zebra tbody tr:last-of-type:nth-child(2n).cds--data-table--selected td,.cds--data-table--zebra tbody tr:last-of-type:nth-child(odd).cds--data-table--selected td,tr.cds--data-table--selected:last-of-type td{border-block-end:1px solid var(--cds-layer-selected);border-block-start:1px solid var(--cds-layer-selected)}.cds--data-table--zebra tbody tr:nth-child(2n).cds--data-table--selected td{border-block-end:1px solid var(--cds-layer-active)}.cds--data-table--zebra tbody tr:nth-child(2n).cds--data-table--selected:hover td{border-block-end:1px solid var(--cds-layer-selected-hover)}.cds--data-table tbody .cds--data-table--selected:hover td,.cds--data-table--zebra tbody tr:nth-child(odd).cds--data-table--selected:hover td{border-block-end:1px solid var(--cds-layer-selected-hover);border-block-start:1px solid var(--cds-layer-selected-hover);color:var(--cds-text-primary,#161616)}.cds--data-table tbody .cds--data-table--selected:hover,.cds--data-table--zebra tbody tr:nth-child(odd).cds--data-table--selected:hover{background-color:var(--cds-layer-selected-hover)}.cds--data-table--selected .cds--overflow-menu .cds--overflow-menu__icon{opacity:1}.cds--data-table--xs tbody tr,.cds--data-table--xs tbody tr th,.cds--data-table--xs thead tr{block-size:24px}.cds--data-table--xs .cds--table-header-label,.cds--data-table--xs tbody tr th,.cds--data-table--xs td{padding-block:2px 2px}.cds--data-table--xs .cds--overflow-menu{block-size:calc(100% + 1px);inline-size:32px}.cds--data-table.cds--data-table--xs:not(.cds--data-table--top-aligned-body) td.cds--table-column-checkbox,.cds--data-table.cds--data-table--xs:not(.cds--data-table--top-aligned-header) th.cds--table-column-checkbox{padding-block:0}.cds--data-table.cds--data-table--xs .cds--table-column-checkbox .cds--checkbox-label{block-size:23px;min-block-size:23px}.cds--data-table--sm tbody tr,.cds--data-table--sm tbody tr th,.cds--data-table--sm thead tr{block-size:32px}.cds--data-table--sm .cds--table-header-label{padding-block:7px 7px}.cds--data-table--sm tbody tr th,.cds--data-table--sm td,.cds--data-table--sm.cds--data-table--top-aligned-header th.cds--table-column-checkbox{padding-block:7px 6px}.cds--data-table.cds--data-table--sm:not(.cds--data-table--top-aligned-body) td.cds--table-column-checkbox,.cds--data-table.cds--data-table--sm:not(.cds--data-table--top-aligned-header) th.cds--table-column-checkbox{padding-block:3px 3px}.cds--data-table--sm .cds--overflow-menu{block-size:calc(100% + 1px)}.cds--data-table--md tbody tr,.cds--data-table--md tbody tr th,.cds--data-table--md thead tr{block-size:40px}.cds--data-table--md .cds--table-header-label,.cds--data-table--md.cds--data-table--top-aligned-header th.cds--table-column-checkbox{padding-block:7px 7px}.cds--data-table--md tbody tr th,.cds--data-table--md td{padding-block:7px 6px}.cds--data-table--md .cds--table-column-menu,.cds--data-table.cds--data-table--md:not(.cds--data-table--top-aligned-body) td.cds--table-column-checkbox,.cds--data-table.cds--data-table--md:not(.cds--data-table--top-aligned-header) th.cds--table-column-checkbox{padding-block:3px 3px}.cds--data-table--xl tbody tr,.cds--data-table--xl tbody tr th,.cds--data-table--xl thead tr{block-size:64px}.cds--data-table--xl .cds--table-header-label,.cds--data-table--xl tbody tr th,.cds--data-table--xl td{padding-block:16px 16px}.cds--data-table--xl td,.cds--data-table--xl th{vertical-align:top}.cds--data-table--xl .cds--data-table--cell-secondary-text{font-size:var(--cds-label-01-font-size,12px);font-weight:var(--cds-label-01-font-weight,400);line-height:var(--cds-label-01-line-height,1.33333);letter-spacing:var(--cds-label-01-letter-spacing,.32px)}.cds--data-table--static{inline-size:auto}.cds--data-table-container--static{inline-size:-moz-fit-content;inline-size:fit-content}.cds--data-table_inner-container{background-color:var(--cds-layer-accent);transform:translateZ(0)}.cds--data-table--sticky-header{display:block;overflow-y:scroll}.cds--data-table--sticky-header tbody,.cds--data-table--sticky-header td,.cds--data-table--sticky-header th,.cds--data-table--sticky-header thead,.cds--data-table--sticky-header tr{display:flex}.cds--data-table--sticky-header thead{position:sticky;z-index:1;overflow:scroll;inline-size:100%;inset-block-start:0;-ms-overflow-style:none;will-change:transform}.cds--data-table--sticky-header thead tr th{border-block-end:1px solid var(--cds-layer-active)}.cds--data-table--sticky-header tbody{flex-direction:column;-ms-overflow-style:none;overflow-x:scroll;will-change:transform}.cds--data-table--sticky-header tr.cds--parent-row.cds--expandable-row{block-size:auto;min-block-size:48px}.cds--data-table--sticky-header tr.cds--expandable-row:not(.cds--parent-row){block-size:auto}.cds--data-table--sticky-header .cds--table-expand{max-inline-size:48px}.cds--data-table--sticky-header thead .cds--table-expand{align-items:center}.cds--data-table--sticky-header .cds--parent-row{min-block-size:48px}.cds--data-table--sticky-header:not(.cds--data-table--xs):not(.cds--data-table--xl):not(.cds--data-table--sm) td:not(.cds--table-column-menu):not(.cds--table-column-checkbox){padding-block-start:14px}.cds--data-table--sticky-header tr.cds--parent-row.cds--expandable-row:hover+tr[data-child-row] td{border-block-start:1px solid var(--cds-layer-hover)}.cds--data-table--sticky-header tr.cds--expandable-row:last-of-type{overflow:hidden}.cds--data-table--sticky-header tr.cds--data-table--selected:first-of-type td{border-block-start:none}.cds--data-table--sticky-header tbody tr td.cds--table-column-checkbox,.cds--data-table--sticky-header thead th.cds--table-column-checkbox{align-items:center;inline-size:36px;min-inline-size:36px}.cds--data-table--sticky-header.cds--data-table--xl td.cds--table-column-checkbox,.cds--data-table--sticky-header.cds--data-table--xl thead th.cds--table-column-checkbox{align-items:flex-start}.cds--data-table--sticky-header th.cds--table-column-checkbox~th:last-of-type:empty{max-inline-size:64px}.cds--data-table--sticky-header th:empty:not(.cds--table-expand){max-inline-size:36px}.cds--data-table--sticky-header td.cds--table-column-menu{align-items:center;block-size:auto;padding-block-start:0}.cds--data-table--sticky-header tbody::-webkit-scrollbar,.cds--data-table--sticky-header thead::-webkit-scrollbar{display:none}@document url-prefix(){.cds--data-table--sticky-header tbody,.cds--data-table--sticky-header thead{scrollbar-width:none}}.cds--data-table--sticky-header tbody tr:last-of-type{border-block-end:0}.cds--data-table--sticky-header td:not(.cds--table-column-checkbox):not(.cds--table-column-menu):not(.cds--table-expand):not(.cds--table-column-icon),.cds--data-table--sticky-header th:not(.cds--table-column-checkbox):not(.cds--table-column-menu):not(.cds--table-expand):not(.cds--table-column-icon){inline-size:100%;min-inline-size:0}.cds--data-table--sticky-header.cds--data-table--sm tr:not(.cds--expandable-row),.cds--data-table--sticky-header.cds--data-table--xl tr:not(.cds--expandable-row),.cds--data-table--sticky-header.cds--data-table--xs tr:not(.cds--expandable-row){block-size:auto}.cds--data-table--sticky-header.cds--data-table--xs tr:not(.cds--expandable-row){min-block-size:24px}.cds--data-table--sticky-header.cds--data-table--sm tr:not(.cds--expandable-row){min-block-size:32px}.cds--data-table--sticky-header.cds--data-table--xl tr:not(.cds--expandable-row){min-block-size:64px}.cds--data-table--sticky-header.cds--data-table--xs tr td.cds--table-expand{padding-block-start:4px}.cds--data-table--sticky-header.cds--data-table--sm tr td.cds--table-expand{padding-block-start:8px}.cds--data-table--sticky-header .cds--table-header-label{display:block;overflow-x:hidden;text-overflow:ellipsis;white-space:nowrap;max-inline-size:calc(100% - 10px);overflow-y:hidden;padding-block:15px 16px}.cds--data-table--sticky-header.cds--data-table--xs th .cds--table-header-label{padding-block:3px 0}.cds--data-table--sticky-header.cds--data-table--sm th .cds--table-header-label{padding-block:8px 0}.cds--data-table--sticky-header.cds--data-table--xl th .cds--table-header-label{padding-block-start:16px}.cds--data-table--sticky-header.cds--data-table--xl th.cds--table-expand{display:flex;align-items:flex-start}.cds--data-table--sticky-header.cds--data-table--sm tr.cds--parent-row .cds--table-column-checkbox,.cds--data-table--sticky-header.cds--data-table--xs tr.cds--parent-row .cds--table-column-checkbox{align-items:flex-start}.cds--data-table--max-width{max-inline-size:100%}.cds--data-table--sticky-header{max-block-size:300px}.cds--data-table .cds--form-item.cds--checkbox-wrapper:last-of-type{margin:0}.cds--data-table--sm .cds--form-item.cds--checkbox-wrapper:last-of-type,.cds--data-table--xs .cds--form-item.cds--checkbox-wrapper:last-of-type{margin:-3px 0}.cds--data-table .cds--table-column-decorator,.cds--data-table .cds--table-column-slug{inline-size:16px;padding-inline-end:0}tr.cds--data-table--ai-label-row,tr.cds--data-table--ai-label-row+.cds--expandable-row,tr.cds--data-table--slug-row,tr.cds--data-table--slug-row+.cds--expandable-row{background:linear-gradient(to right,var(--cds-ai-aura-start-sm,rgba(69,137,255,.16)) 0,var(--cds-ai-aura-end,hsla(0,0%,100%,0)) 50%,transparent 50%);background-attachment:fixed}.cds--data-table--ai-label-row,.cds--data-table--slug-row{box-shadow:inset 1px 0 var(--cds-ai-border-strong,#4589ff)}.cds--data-table tbody tr.cds--data-table--ai-label-row:hover td,tr.cds--data-table--ai-label-row.cds--expandable-row--hover+.cds--expandable-row[data-child-row]:hover>td,tr.cds--data-table--ai-label-row.cds--expandable-row--hover>td,tr.cds--data-table--ai-label-row.cds--expandable-row:hover+.cds--expandable-row[data-child-row] td,tr.cds--data-table--ai-label-row.cds--parent-row.cds--data-table--selected.cds--expandable-row--hover>td,tr.cds--data-table--ai-label-row.cds--parent-row.cds--data-table--selected.cds--expandable-row--hover>td:first-of-type,tr.cds--data-table--ai-label-row.cds--parent-row.cds--data-table--selected.cds--expandable-row:hover td,tr.cds--data-table--ai-label-row.cds--parent-row.cds--data-table--selected.cds--expandable-row:hover td:first-of-type,tr.cds--data-table--ai-label-row.cds--parent-row.cds--data-table--selected.cds--expandable-row:hover+tr[data-child-row]>td,tr.cds--data-table--ai-label-row.cds--parent-row.cds--data-table--selected:not(.cds--expandable-row):hover>td,tr.cds--data-table--selected.cds--data-table--ai-label-row.cds--expandable-row+tr.cds--expandable-row[data-child-row]>td{background-color:initial}.cds--data-table tbody tr.cds--data-table--ai-label-row:hover,tr.cds--data-table--ai-label-row.cds--expandable-row--hover+.cds--expandable-row[data-child-row]:hover,tr.cds--data-table--ai-label-row:hover+.cds--expandable-row[data-child-row],tr.cds--data-table--selected.cds--parent-row.cds--expandable-row--hover.cds--data-table--ai-label-row,tr.cds--expandable-row--hover.cds--data-table--ai-label-row{background:linear-gradient(to right,var(--cds-ai-aura-hover-start,rgba(69,137,255,.32)) 0,15%,var(--cds-ai-aura-hover-end,hsla(0,0%,100%,0)) 50%),var(--cds-ai-aura-hover-background,#edf5ff);background-attachment:fixed}.cds--data-table--selected.cds--data-table--ai-label-row,.cds--data-table--selected.cds--data-table--slug-row,tr.cds--data-table--selected.cds--data-table--ai-label-row+.cds--expandable-row,tr.cds--data-table--selected.cds--data-table--slug-row+.cds--expandable-row,tr.cds--parent-row.cds--data-table--selected.cds--data-table--ai-label-row,tr.cds--parent-row.cds--data-table--selected.cds--data-table--slug-row{background:linear-gradient(to right,var(--cds-ai-aura-start-sm,rgba(69,137,255,.16)) 0,var(--cds-ai-aura-end,hsla(0,0%,100%,0)) 50%,transparent 50%),var(--cds-layer-selected);background-attachment:fixed}tbody tr.cds--data-table--ai-label-row:hover td,tr.cds--data-table--ai-label-row.cds--data-table--selected td,tr.cds--data-table--ai-label-row.cds--data-table--selected:hover td,tr.cds--data-table--ai-label-row.cds--parent-row.cds--data-table--selected.cds--expandable-row:hover td:not(.cds--table-expand):not(.cds--table-column-checkbox):not(.cds--table-column-slug){border-block-end-color:var(--cds-border-subtle)}tr.cds--expandable-row.cds--data-table--selected.cds--data-table--slug-row[data-parent-row]>td:not(.cds--table-expand):not(.cds--table-column-checkbox):not(.cds--table-column-slug){border-block-end:1px solid var(--cds-layer-selected)}tr.cds--parent-row.cds--data-table--ai-label-row.cds--expandable-row:hover td:first-of-type,tr.cds--parent-row.cds--data-table--decoratorß-row.cds--expandable-row:hover td:first-of-type,tr.cds--parent-row.cds--data-table--slug-row.cds--expandable-row:hover td:first-of-type{border-block-end:1px solid transparent}.cds--data-table thead th.cds--table-sort__header--ai-label .cds--table-sort,.cds--data-table thead th.cds--table-sort__header--slug .cds--table-sort,.cds--data-table thead th:has(>.cds--table-header-label--ai-label),.cds--data-table thead th:has(>.cds--table-header-label--slug){background:linear-gradient(to right,var(--cds-ai-aura-start-sm,rgba(69,137,255,.16)) 0,var(--cds-ai-aura-end,hsla(0,0%,100%,0)) 50%,transparent 50%)}.cds--table-column-decorator .cds--decorator,.cds--table-column-slug .cds--ai-label,.cds--table-column-slug .cds--slug{position:absolute;z-index:2;transform:translateY(-50%)}.cds--data-table--xl .cds--table-column-decorator .cds--decorator,.cds--data-table--xl .cds--table-column-slug .cds--ai-label,.cds--data-table--xl .cds--table-column-slug .cds--slug{transform:translateY(1px)}th .cds--table-header-label.cds--table-header-label--ai-label,th .cds--table-header-label.cds--table-header-label--decorator,th .cds--table-header-label.cds--table-header-label--slug{display:flex;align-items:center}th .cds--table-header-label.cds--table-header-label--ai-label .cds--ai-label,th .cds--table-header-label.cds--table-header-label--ai-label .cds--slug,th .cds--table-header-label.cds--table-header-label--ai-label .cds--table-header-label--decorator-inner,th .cds--table-header-label.cds--table-header-label--decorator .cds--table-header-label--decorator-inner{margin-inline-start:auto}th.cds--table-sort__header--ai-label,th.cds--table-sort__header--slug,th:has(.cds--table-header-label--ai-label),th:has(.cds--table-header-label--slug){box-shadow:inset 0 1px var(--cds-ai-border-strong,#4589ff)}td.cds--table-cell--column-slug{background:linear-gradient(to right,var(--cds-ai-aura-start-sm,rgba(69,137,255,.16)) 0,var(--cds-ai-aura-end,hsla(0,0%,100%,0)) 50%,transparent 50%)}tr.cds--parent-row:not(.cds--expandable-row):not(:first-of-type) td.cds--table-cell--column-slug{border-block-start:linear-gradient(to right,var(--cds-ai-aura-start-sm,rgba(69,137,255,.16)) 0,var(--cds-ai-aura-end,hsla(0,0%,100%,0)) 50%,transparent 50%)}@media (forced-colors:active),screen and (-ms-high-contrast:active){.cds--data-table-content,tr.cds--parent-row:not(.cds--expandable-row):not(:first-of-type) td.cds--table-cell--column-slug{outline:1px solid transparent}}.cds--link{box-sizing:border-box;padding:0;border:0;margin:0;font-family:inherit;font-size:100%;vertical-align:initial;font-size:var(--cds-body-compact-01-font-size,14px);font-weight:var(--cds-body-compact-01-font-weight,400);line-height:var(--cds-body-compact-01-line-height,1.28572);letter-spacing:var(--cds-body-compact-01-letter-spacing,.16px);display:inline-flex;color:var(--cds-link-text-color,var(--cds-link-primary,#0f62fe));outline:none;text-decoration:none;transition:color 70ms cubic-bezier(.2,0,.38,.9)}.cds--link *,.cds--link :after,.cds--link :before{box-sizing:inherit}.cds--link:hover{color:var(--cds-link-hover-text-color,var(--cds-link-primary-hover,#0043ce));text-decoration:underline}.cds--link:active,.cds--link:active:visited,.cds--link:active:visited:hover{outline:1px solid var(--cds-focus,#0f62fe);color:var(--cds-link-text-color,var(--cds-link-primary,#0f62fe));outline-color:var(--cds-link-focus-text-color,var(--cds-focus,#0f62fe));text-decoration:underline}@media screen and (prefers-contrast){.cds--link:active,.cds--link:active:visited,.cds--link:active:visited:hover{outline-style:dotted}}.cds--link:focus{outline:1px solid var(--cds-focus,#0f62fe);outline-color:var(--cds-link-focus-text-color,var(--cds-focus,#0f62fe));text-decoration:underline}@media screen and (prefers-contrast){.cds--link:focus{outline-style:dotted}}.cds--link:visited{color:var(--cds-link-text-color,var(--cds-link-primary,#0f62fe))}.cds--link:visited:hover{color:var(--cds-link-hover-text-color,var(--cds-link-primary-hover,#0043ce))}.cds--link--disabled,.cds--link--disabled:hover{box-sizing:border-box;padding:0;border:0;margin:0;font-family:inherit;font-size:100%;vertical-align:initial;font-size:var(--cds-body-compact-01-font-size,14px);font-weight:var(--cds-body-compact-01-font-weight,400);line-height:var(--cds-body-compact-01-line-height,1.28572);letter-spacing:var(--cds-body-compact-01-letter-spacing,.16px);color:var(--cds-text-disabled,hsla(0,0%,9%,.25));cursor:not-allowed;font-weight:400;text-decoration:none}.cds--link--disabled *,.cds--link--disabled :after,.cds--link--disabled :before,.cds--link--disabled:hover *,.cds--link--disabled:hover :after,.cds--link--disabled:hover :before{box-sizing:inherit}.cds--link.cds--link--visited:visited{color:var(--cds-link-visited-text-color,var(--cds-link-visited,#8a3ffc))}.cds--link.cds--link--visited:visited:hover{color:var(--cds-link-hover-text-color,var(--cds-link-primary-hover,#0043ce))}.cds--link.cds--link--inline{display:inline;text-decoration:underline}.cds--link--disabled.cds--link--inline{text-decoration:underline}.cds--link--sm,.cds--link--sm.cds--link--disabled:hover{font-size:var(--cds-helper-text-01-font-size,12px);line-height:var(--cds-helper-text-01-line-height,1.33333);letter-spacing:var(--cds-helper-text-01-letter-spacing,.32px)}.cds--link--lg,.cds--link--lg.cds--link--disabled:hover{font-size:var(--cds-body-compact-02-font-size,16px);font-weight:var(--cds-body-compact-02-font-weight,400);line-height:var(--cds-body-compact-02-line-height,1.375);letter-spacing:var(--cds-body-compact-02-letter-spacing,0)}.cds--link__icon{display:inline-flex;align-self:center;margin-inline-start:8px}.cds--list--nested,.cds--list--ordered,.cds--list--ordered--native,.cds--list--unordered{box-sizing:border-box;padding:0;border:0;margin:0;font-family:inherit;font-size:100%;vertical-align:initial;font-size:var(--cds-body-01-font-size,14px);font-weight:var(--cds-body-01-font-weight,400);line-height:var(--cds-body-01-line-height,1.42857);letter-spacing:var(--cds-body-01-letter-spacing,.16px);list-style:none}.cds--list--nested *,.cds--list--nested :after,.cds--list--nested :before,.cds--list--ordered *,.cds--list--ordered :after,.cds--list--ordered :before,.cds--list--ordered--native *,.cds--list--ordered--native :after,.cds--list--ordered--native :before,.cds--list--unordered *,.cds--list--unordered :after,.cds--list--unordered :before{box-sizing:inherit}.cds--list--unordered:not(.cds--list--nested){margin-inline-start:16px}.cds--list--expressive,.cds--list--expressive .cds--list--nested{font-size:var(--cds-body-02-font-size,16px);font-weight:var(--cds-body-02-font-weight,400);line-height:var(--cds-body-02-line-height,1.5);letter-spacing:var(--cds-body-02-letter-spacing,0)}.cds--list--ordered--native{list-style:decimal}.cds--list__item{color:var(--cds-text-primary,#161616)}.cds--list--nested{margin-inline-start:32px}.cds--list--nested .cds--list__item{padding-inline-start:4px}.cds--list--ordered:not(.cds--list--nested){counter-reset:item}.cds--list--ordered:not(.cds--list--nested)>.cds--list__item{position:relative}.cds--list--ordered:not(.cds--list--nested)>.cds--list__item:before{position:absolute;content:counter(item) \".\";counter-increment:item;inset-inline-start:-24px}.cds--list--ordered--native.cds--list--nested,.cds--list--ordered.cds--list--nested{list-style-type:lower-latin}.cds--list--unordered>.cds--list__item{position:relative}.cds--list--unordered>.cds--list__item:before{position:absolute;content:\"–\";inset-inline-start:-16px}.cds--list--unordered.cds--list--nested>.cds--list__item:before{content:\"▪\";inset-inline-start:-12px}.cds--layout--size-xs{--cds-layout-size-height-context:var(--cds-layout-size-height-xs,24px);--cds-layout-size-height:var(--cds-layout-size-height-context)}.cds--layout-constraint--size__default-xs{--cds-layout-size-height:var(--cds-layout-size-height-context,var(--cds-layout-size-height-xs,24px))}.cds--layout-constraint--size__min-xs{--cds-layout-size-height-min:var(--cds-layout-size-height-xs,24px)}.cds--layout-constraint--size__max-xs{--cds-layout-size-height-max:var(--cds-layout-size-height-xs,24px)}.cds--layout--size-sm,.ibm-web-chat--default-styles button:not([class*=cds--]),:host button:not([class*=cds--]){--cds-layout-size-height-context:var(--cds-layout-size-height-sm,32px);--cds-layout-size-height:var(--cds-layout-size-height-context)}.cds--layout-constraint--size__default-sm{--cds-layout-size-height:var(--cds-layout-size-height-context,var(--cds-layout-size-height-sm,32px))}.cds--layout-constraint--size__min-sm{--cds-layout-size-height-min:var(--cds-layout-size-height-sm,32px)}.cds--layout-constraint--size__max-sm{--cds-layout-size-height-max:var(--cds-layout-size-height-sm,32px)}.cds--layout--size-md{--cds-layout-size-height-context:var(--cds-layout-size-height-md,40px);--cds-layout-size-height:var(--cds-layout-size-height-context)}.cds--layout-constraint--size__default-md{--cds-layout-size-height:var(--cds-layout-size-height-context,var(--cds-layout-size-height-md,40px))}.cds--layout-constraint--size__min-md{--cds-layout-size-height-min:var(--cds-layout-size-height-md,40px)}.cds--layout-constraint--size__max-md{--cds-layout-size-height-max:var(--cds-layout-size-height-md,40px)}.cds--layout--size-lg{--cds-layout-size-height-context:var(--cds-layout-size-height-lg,48px);--cds-layout-size-height:var(--cds-layout-size-height-context)}.cds--layout-constraint--size__default-lg{--cds-layout-size-height:var(--cds-layout-size-height-context,var(--cds-layout-size-height-lg,48px))}.cds--layout-constraint--size__min-lg{--cds-layout-size-height-min:var(--cds-layout-size-height-lg,48px)}.cds--layout-constraint--size__max-lg{--cds-layout-size-height-max:var(--cds-layout-size-height-lg,48px)}.cds--layout--size-xl{--cds-layout-size-height-context:var(--cds-layout-size-height-xl,64px);--cds-layout-size-height:var(--cds-layout-size-height-context)}.cds--layout-constraint--size__default-xl{--cds-layout-size-height:var(--cds-layout-size-height-context,var(--cds-layout-size-height-xl,64px))}.cds--layout-constraint--size__min-xl{--cds-layout-size-height-min:var(--cds-layout-size-height-xl,64px)}.cds--layout-constraint--size__max-xl{--cds-layout-size-height-max:var(--cds-layout-size-height-xl,64px)}.cds--layout--size-2xl{--cds-layout-size-height-context:var(--cds-layout-size-height-2xl,80px);--cds-layout-size-height:var(--cds-layout-size-height-context)}.cds--layout-constraint--size__default-2xl{--cds-layout-size-height:var(--cds-layout-size-height-context,var(--cds-layout-size-height-2xl,80px))}.cds--layout-constraint--size__min-2xl{--cds-layout-size-height-min:var(--cds-layout-size-height-2xl,80px)}.cds--layout-constraint--size__max-2xl{--cds-layout-size-height-max:var(--cds-layout-size-height-2xl,80px)}.cds--layout--density-condensed{--cds-layout-density-padding-inline-context:var(--cds-layout-density-padding-inline-condensed,8px);--cds-layout-density-padding-inline:var(--cds-layout-density-padding-inline-context)}.cds--layout-constraint--density__default-condensed{--cds-layout-density-padding-inline:var(--cds-layout-density-padding-inline-context,var(--cds-layout-density-padding-inline-condensed,8px))}.cds--layout-constraint--density__min-condensed{--cds-layout-density-padding-inline-min:var(--cds-layout-density-padding-inline-condensed,8px)}.cds--layout-constraint--density__max-condensed{--cds-layout-density-padding-inline-max:var(--cds-layout-density-padding-inline-condensed,8px)}.cds--layout--density-normal{--cds-layout-density-padding-inline-context:var(--cds-layout-density-padding-inline-normal,16px);--cds-layout-density-padding-inline:var(--cds-layout-density-padding-inline-context)}.cds--layout-constraint--density__default-normal{--cds-layout-density-padding-inline:var(--cds-layout-density-padding-inline-context,var(--cds-layout-density-padding-inline-normal,16px))}.cds--layout-constraint--density__min-normal{--cds-layout-density-padding-inline-min:var(--cds-layout-density-padding-inline-normal,16px)}.cds--layout-constraint--density__max-normal{--cds-layout-density-padding-inline-max:var(--cds-layout-density-padding-inline-normal,16px)}:root{--cds-layout-size-height-xs:24px;--cds-layout-size-height-sm:32px;--cds-layout-size-height-md:40px;--cds-layout-size-height-lg:48px;--cds-layout-size-height-xl:64px;--cds-layout-size-height-2xl:80px;--cds-layout-size-height-min:0px;--cds-layout-size-height-max:999999999px;--cds-layout-density-padding-inline-condensed:8px;--cds-layout-density-padding-inline-normal:16px;--cds-layout-density-padding-inline-min:0px;--cds-layout-density-padding-inline-max:999999999px}.cds--popover-container{display:inline-block}.cds--popover-container:not(.cds--popover--auto-align){position:relative}.cds--popover--high-contrast .cds--popover{--cds-popover-background-color:var(--cds-background-inverse,#393939);--cds-popover-text-color:var(--cds-text-inverse,#fff)}.cds--popover--drop-shadow .cds--popover>.cds--popover-content{filter:drop-shadow(0 2px 2px rgba(0,0,0,.2))}.cds--popover--caret{--cds-popover-offset:10px}.cds--popover{position:absolute;z-index:6000;filter:var(--cds-popover-drop-shadow,none);inset:0;pointer-events:none}.cds--popover-content{--cds-layout-size-height-sm:32px;--cds-layout-size-height-md:40px;--cds-layout-size-height-lg:48px;box-sizing:border-box;padding:0;border:0;margin:0;font-family:inherit;font-size:100%;vertical-align:initial;position:absolute;z-index:6000;display:none;border-radius:var(--cds-popover-border-radius,2px);background-color:var(--cds-popover-background-color,var(--cds-layer));color:var(--cds-popover-text-color,var(--cds-text-primary,#161616));inline-size:-moz-max-content;inline-size:max-content;max-inline-size:368px;pointer-events:auto}.cds--layout--size-sm .cds--popover-content,.ibm-web-chat--default-styles button:not([class*=cds--]) .cds--popover-content,:host button:not([class*=cds--]) .cds--popover-content{--cds-layout-size-height:var(--cds-layout-size-height-sm)}.cds--layout--size-md .cds--popover-content{--cds-layout-size-height:var(--cds-layout-size-height-md)}.cds--layout--size-lg .cds--popover-content{--cds-layout-size-height:var(--cds-layout-size-height-lg)}.cds--popover-content *,.cds--popover-content :after,.cds--popover-content :before{box-sizing:inherit}.cds--popover--open>.cds--popover>.cds--popover-content{display:block}.cds--popover-content:before{position:absolute;display:none;content:\"\"}.cds--popover--open>.cds--popover>.cds--popover-content:before{display:block}.cds--popover--auto-align.cds--popover-caret,.cds--popover-caret{position:absolute;z-index:6000;display:none;background-color:var(--cds-popover-background-color,var(--cds-layer));will-change:transform}.cds--popover--auto-align.cds--popover--caret.cds--popover--open>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--caret.cds--popover--open>.cds--popover>.cds--popover-caret{display:block}.cds--popover--tab-tip>.cds--popover>.cds--popover-caret{display:none}.cds--popover--bottom:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-block-end:0;inset-inline-start:50%;transform:translate(-50%,calc(100% + var(--cds-popover-offset, 0)))}[dir=rtl] .cds--popover--bottom:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{transform:translate(50%,calc(100% + var(--cds-popover-offset, 0)))}.cds--popover--bottom-left:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,.cds--popover--bottom-start:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-block-end:0;inset-inline-start:0;transform:translate(calc(var(--cds-popover-offset, 0)*-1),calc(100% + var(--cds-popover-offset, 0)))}[dir=rtl] .cds--popover--bottom-left:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,[dir=rtl] .cds--popover--bottom-start:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-inline-end:0;inset-inline-start:auto}.cds--popover--bottom-end:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,.cds--popover--bottom-right:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-block-end:0;inset-inline-end:0;transform:translate(var(--cds-popover-offset,0),calc(100% + var(--cds-popover-offset, 0)))}.cds--popover--bottom-end:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,.cds--popover--top-end:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret{inset-block-end:0;inset-inline-end:0;inset-inline-start:auto}.cds--popover--bottom-start:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret{inset-block-end:0;inset-inline-end:auto;inset-inline-start:0;transform:translate(50%,calc(var(--cds-popover-offset, 0)))}.cds--popover--top-start:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret{inset-block-end:0;inset-inline-end:auto;inset-inline-start:0;transform:translate(50%,calc(var(--cds-popover-offset, 0)*-1))}[dir=rtl] .cds--popover--bottom-end:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,[dir=rtl] .cds--popover--bottom-right:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-inline-start:0}.cds--popover--bottom-end>.cds--popover>.cds--popover-content:before,.cds--popover--bottom-left>.cds--popover>.cds--popover-content:before,.cds--popover--bottom-right>.cds--popover>.cds--popover-content:before,.cds--popover--bottom-start>.cds--popover>.cds--popover-content:before,.cds--popover--bottom>.cds--popover>.cds--popover-content:before{block-size:var(--cds-popover-offset,0);inset-block-start:0;inset-inline:0;transform:translateY(-100%)}.cds--popover--bottom-end>.cds--popover>.cds--popover-caret,.cds--popover--bottom-left>.cds--popover>.cds--popover-caret,.cds--popover--bottom-right>.cds--popover>.cds--popover-caret,.cds--popover--bottom-start>.cds--popover>.cds--popover-caret,.cds--popover--bottom>.cds--popover>.cds--popover-caret{block-size:var(--cds-popover-caret-height,6px);clip-path:polygon(0 100%,50% 0,100% 100%);inline-size:var(--cds-popover-caret-width,12px);inset-block-end:0;inset-inline-start:50%;transform:translate(-50%,var(--cds-popover-offset,0))}[dir=rtl] .cds--popover--bottom-end>.cds--popover>.cds--popover-caret,[dir=rtl] .cds--popover--bottom-left>.cds--popover>.cds--popover-caret,[dir=rtl] .cds--popover--bottom-right>.cds--popover>.cds--popover-caret,[dir=rtl] .cds--popover--bottom-start>.cds--popover>.cds--popover-caret,[dir=rtl] .cds--popover--bottom>.cds--popover>.cds--popover-caret{transform:translate(50%,var(--cds-popover-offset,0))}.cds--popover--bottom-end.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--bottom-left.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--bottom-right.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--bottom-start.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--bottom.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret{block-size:var(--cds-popover-caret-height,6px);clip-path:polygon(0 100%,50% 0,100% 100%);inline-size:var(--cds-popover-caret-width,12px)}.cds--popover--top:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-block-start:0;inset-inline-start:50%;transform:translate(-50%,calc(-100% - var(--cds-popover-offset, 0)))}[dir=rtl] .cds--popover--top:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{transform:translate(50%,calc(-100% - var(--cds-popover-offset, 0)))}.cds--popover--top-left:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,.cds--popover--top-start:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-block-start:0;inset-inline-start:0;transform:translate(calc(var(--cds-popover-offset, 0)*-1),calc(-100% - var(--cds-popover-offset, 0)))}[dir=rtl] .cds--popover--top-left:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,[dir=rtl] .cds--popover--top-start:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-inline-end:0;inset-inline-start:auto}.cds--popover--top-end:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,.cds--popover--top-right:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-block-start:0;inset-inline-end:0;transform:translate(var(--cds-popover-offset,0),calc(-100% - var(--cds-popover-offset, 0)))}[dir=rtl] .cds--popover--top-end:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,[dir=rtl] .cds--popover--top-right:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-inline-start:0}.cds--popover--top-end>.cds--popover>.cds--popover-content:before,.cds--popover--top-left>.cds--popover>.cds--popover-content:before,.cds--popover--top-right>.cds--popover>.cds--popover-content:before,.cds--popover--top-start>.cds--popover>.cds--popover-content:before,.cds--popover--top>.cds--popover>.cds--popover-content:before{block-size:var(--cds-popover-offset,0);inset-block-end:0;inset-inline:0;transform:translateY(100%)}.cds--popover--top-end>.cds--popover>.cds--popover-caret,.cds--popover--top-left>.cds--popover>.cds--popover-caret,.cds--popover--top-right>.cds--popover>.cds--popover-caret,.cds--popover--top-start>.cds--popover>.cds--popover-caret,.cds--popover--top>.cds--popover>.cds--popover-caret{block-size:var(--cds-popover-caret-height,6px);clip-path:polygon(0 0,50% 100%,100% 0);inline-size:var(--cds-popover-caret-width,12px);inset-block-start:0;inset-inline-start:50%;transform:translate(-50%,calc(var(--cds-popover-offset, 0)*-1))}[dir=rtl] .cds--popover--top-end:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,[dir=rtl] .cds--popover--top-left:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,[dir=rtl] .cds--popover--top-right:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,[dir=rtl] .cds--popover--top-start:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,[dir=rtl] .cds--popover--top:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret{transform:translate(50%,calc(var(--cds-popover-offset, 0)*-1))}.cds--popover--top-end.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--top-left.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--top-right.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--top-start.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--top.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret{block-size:var(--cds-popover-caret-height,6px);clip-path:polygon(0 0,50% 100%,100% 0);inline-size:var(--cds-popover-caret-width,12px)}.cds--popover--right:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-block-start:50%;inset-inline-start:100%;transform:translate(var(--cds-popover-offset,0),-50%)}.cds--popover--right-start:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,.cds--popover--right-top:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-block-start:50%;inset-inline-start:100%;transform:translate(var(--cds-popover-offset,0),calc(var(--cds-popover-offset, 0)*.5*-1 - 16px))}.cds--popover--right-bottom:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,.cds--popover--right-end:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-block-end:50%;inset-inline-start:100%;transform:translate(var(--cds-popover-offset,0),calc(var(--cds-popover-offset, 0)*.5 + 16px))}[dir=rtl] .cds--popover--right-bottom:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,[dir=rtl] .cds--popover--right-end:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,[dir=rtl] .cds--popover--right-start:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,[dir=rtl] .cds--popover--right-top:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,[dir=rtl] .cds--popover--right:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-inline-end:100%;inset-inline-start:auto}.cds--popover--right-bottom>.cds--popover>.cds--popover-content:before,.cds--popover--right-end>.cds--popover>.cds--popover-content:before,.cds--popover--right-start>.cds--popover>.cds--popover-content:before,.cds--popover--right-top>.cds--popover>.cds--popover-content:before,.cds--popover--right>.cds--popover>.cds--popover-content:before{inline-size:var(--cds-popover-offset,0);inset-block:0;inset-inline-start:0;transform:translateX(-100%)}.cds--popover--right-bottom:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,.cds--popover--right-end:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,.cds--popover--right-start:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,.cds--popover--right-top:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,.cds--popover--right:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret{block-size:var(--cds-popover-caret-width,12px);clip-path:polygon(0 50%,100% 0,100% 100%);inline-size:var(--cds-popover-caret-height,6px);inset-block-start:50%;inset-inline-start:100%;transform:translate(calc(var(--cds-popover-offset, 0) - 100%),-50%)}[dir=rtl] .cds--popover--right-bottom:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,[dir=rtl] .cds--popover--right-end:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,[dir=rtl] .cds--popover--right-start:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,[dir=rtl] .cds--popover--right-top:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,[dir=rtl] .cds--popover--right:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret{inset-inline-end:100%;inset-inline-start:auto}.cds--popover--right-bottom.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--right-end.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--right-start.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--right-top.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--right.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret{block-size:var(--cds-popover-caret-width,12px);clip-path:polygon(0 50%,100% 0,100% 100%);inline-size:var(--cds-popover-caret-height,6px)}.cds--popover--left:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-block-start:50%;inset-inline-end:100%;transform:translate(calc(var(--cds-popover-offset, 0)*-1 + .1px),-50%)}.cds--popover--left-start:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,.cds--popover--left-top:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-block-start:50%;inset-inline-end:100%;transform:translate(calc(var(--cds-popover-offset, 0)*-1),calc(var(--cds-popover-offset, 0)*-.5 - 16px))}.cds--popover--left-bottom:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,.cds--popover--left-end:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-block-end:50%;inset-inline-end:100%;transform:translate(calc(var(--cds-popover-offset, 0)*-1),calc(var(--cds-popover-offset, 0)*.5 + 16px))}[dir=rtl] .cds--popover--left-bottom:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,[dir=rtl] .cds--popover--left-end:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,[dir=rtl] .cds--popover--left-start:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,[dir=rtl] .cds--popover--left-top:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content,[dir=rtl] .cds--popover--left:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-content{inset-inline-end:auto;inset-inline-start:100%}.cds--popover--left-bottom>.cds--popover>.cds--popover-content:before,.cds--popover--left-end>.cds--popover>.cds--popover-content:before,.cds--popover--left-start>.cds--popover>.cds--popover-content:before,.cds--popover--left-top>.cds--popover>.cds--popover-content:before,.cds--popover--left>.cds--popover>.cds--popover-content:before{inline-size:var(--cds-popover-offset,0);inset-block:0;inset-inline-end:0;transform:translateX(100%)}.cds--popover--left-bottom:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,.cds--popover--left-end:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,.cds--popover--left-start:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,.cds--popover--left-top:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,.cds--popover--left:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret{block-size:var(--cds-popover-caret-width,12px);clip-path:polygon(0 0,100% 50%,0 100%);inline-size:var(--cds-popover-caret-height,6px);inset-block-start:50%;inset-inline-end:100%;transform:translate(calc(var(--cds-popover-offset, 0)*-1 + 100%),-50%)}[dir=rtl] .cds--popover--left-bottom:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,[dir=rtl] .cds--popover--left-end:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,[dir=rtl] .cds--popover--left-start:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,[dir=rtl] .cds--popover--left-top:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret,[dir=rtl] .cds--popover--left:not(.cds--popover--auto-align)>.cds--popover>.cds--popover-caret{inset-inline-end:auto;inset-inline-start:100%}.cds--popover--left-bottom.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--left-end.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--left-start.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--left-top.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret,.cds--popover--left.cds--popover--auto-align>.cds--popover>.cds--popover-content>.cds--popover-caret{block-size:var(--cds-popover-caret-width,12px);clip-path:polygon(0 0,100% 50%,0 100%);inline-size:var(--cds-popover-caret-height,6px)}.cds--popover--tab-tip>.cds--popover>.cds--popover-content{border-radius:0}.cds--popover--tab-tip .cds--popover{will-change:filter}.cds--popover--tab-tip__button{box-sizing:border-box;margin:0;font-family:inherit;font-size:100%;vertical-align:initial;display:inline-block;padding:0;border:0;-webkit-appearance:none;-moz-appearance:none;appearance:none;background:none;cursor:pointer;text-align:start;inline-size:100%;position:relative;display:inline-flex;align-items:center;justify-content:center;block-size:32px;inline-size:32px}.cds--popover--tab-tip__button *,.cds--popover--tab-tip__button :after,.cds--popover--tab-tip__button :before{box-sizing:inherit}.cds--popover--tab-tip__button::-moz-focus-inner{border:0}.cds--popover--tab-tip__button:focus{outline:2px solid var(--cds-focus,#0f62fe);outline-offset:-2px}@media screen and (prefers-contrast){.cds--popover--tab-tip__button:focus{outline-style:dotted}}.cds--popover--tab-tip__button:hover{background-color:var(--cds-layer-hover)}.cds--popover--tab-tip.cds--popover--open .cds--popover--tab-tip__button{background:var(--cds-layer);box-shadow:0 2px 2px rgba(0,0,0,.2)}.cds--popover--tab-tip.cds--popover--open .cds--popover--tab-tip__button:not(:focus):after{position:absolute;z-index:6001;background:var(--cds-layer);block-size:2px;content:\"\";inline-size:100%;inset-block-end:0}.cds--popover--tab-tip__button svg{fill:var(--cds-icon-primary,#161616)}.cds--tooltip{--cds-popover-offset:12px}.cds--tooltip-content{font-size:var(--cds-body-01-font-size,14px);font-weight:var(--cds-body-01-font-weight,400);line-height:var(--cds-body-01-line-height,1.42857);letter-spacing:var(--cds-body-01-letter-spacing,.16px);padding:var(--cds-tooltip-padding-block,16px) var(--cds-tooltip-padding-inline,16px);max-inline-size:288px}.cds--icon-tooltip{--cds-tooltip-padding-block:2px;--cds-popover-caret-width:8px;--cds-popover-caret-height:4px;--cds-popover-offset:8px}.cds--icon-tooltip .cds--tooltip-content{font-size:var(--cds-body-compact-01-font-size,14px);font-weight:var(--cds-body-compact-01-font-weight,400);line-height:var(--cds-body-compact-01-line-height,1.28572);letter-spacing:var(--cds-body-compact-01-letter-spacing,.16px)}.cds--definition-term{box-sizing:border-box;margin:0;font-family:inherit;font-size:100%;vertical-align:initial;display:inline-block;padding:0;border:0;-webkit-appearance:none;-moz-appearance:none;appearance:none;background:none;cursor:pointer;text-align:start;inline-size:100%;border-radius:0;border-block-end:1px dotted var(--cds-border-strong);color:var(--cds-text-primary,#161616)}.cds--definition-term *,.cds--definition-term :after,.cds--definition-term :before{box-sizing:inherit}.cds--definition-term::-moz-focus-inner{border:0}.cds--definition-term:focus{outline:1px solid var(--cds-focus,#0f62fe);border-block-end-color:var(--cds-border-interactive,#0f62fe)}@media screen and (prefers-contrast){.cds--definition-term:focus{outline-style:dotted}}.cds--definition-term:hover{border-block-end-color:var(--cds-border-interactive,#0f62fe)}.cds--definition-tooltip{font-size:var(--cds-body-01-font-size,14px);font-weight:var(--cds-body-01-font-weight,400);line-height:var(--cds-body-01-line-height,1.42857);letter-spacing:var(--cds-body-01-letter-spacing,.16px);padding:8px 16px;max-inline-size:176px;text-wrap:auto;word-break:break-word}.cds--btn,.ibm-web-chat--default-styles button:not([class*=cds--]),:host button:not([class*=cds--]){--cds-layout-size-height-local:clamp(max(var(--cds-layout-size-height-min),var(--cds-layout-size-height-sm)),var(--cds-layout-size-height,var(--cds-layout-size-height-lg)),min(var(--cds-layout-size-height-max),var(--cds-layout-size-height-2xl)));--cds-layout-density-padding-inline-local:clamp(var(--cds-layout-density-padding-inline-min),var(--cds-layout-density-padding-inline,var(--cds-layout-density-padding-inline-normal)),var(--cds-layout-density-padding-inline-max));--temp-1lh:(var(--cds-body-compact-01-line-height,1.28572) * 1em);--temp-expressive-1lh:(var(--cds-body-compact-02-line-height,1.375) * 1em);--temp-padding-block-max:calc((var(--cds-layout-size-height-lg) - var(--temp-1lh))/2 - 1px);box-sizing:border-box;padding:0;border:0;font-family:inherit;font-size:100%;vertical-align:initial;font-size:var(--cds-body-compact-01-font-size,14px);font-weight:var(--cds-body-compact-01-font-weight,400);line-height:var(--cds-body-compact-01-line-height,1.28572);letter-spacing:var(--cds-body-compact-01-letter-spacing,.16px);position:relative;display:inline-flex;flex-shrink:0;justify-content:space-between;border-radius:0;margin:0;cursor:pointer;inline-size:-moz-max-content;inline-size:max-content;max-inline-size:320px;min-block-size:var(--cds-layout-size-height-local);outline:none;padding-block:min((var(--cds-layout-size-height-local) - var(--temp-1lh))/2 - 1px,var(--temp-padding-block-max));padding-inline:calc(var(--cds-layout-density-padding-inline-local) - 1px) calc(var(--cds-layout-density-padding-inline-local)*3 + 15px);text-align:start;text-decoration:none;transition:background 70ms cubic-bezier(0,0,.38,.9),box-shadow 70ms cubic-bezier(0,0,.38,.9),border-color 70ms cubic-bezier(0,0,.38,.9),outline 70ms cubic-bezier(0,0,.38,.9);vertical-align:top}.cds--btn *,.cds--btn :after,.cds--btn :before,.ibm-web-chat--default-styles button:not([class*=cds--]) *,.ibm-web-chat--default-styles button:not([class*=cds--]) :after,.ibm-web-chat--default-styles button:not([class*=cds--]) :before,:host button:not([class*=cds--]) *,:host button:not([class*=cds--]) :after,:host button:not([class*=cds--]) :before{box-sizing:inherit}.cds--btn.cds--btn--disabled,.cds--btn.cds--btn--disabled:focus,.cds--btn.cds--btn--disabled:hover,.cds--btn:disabled,.cds--btn:focus:disabled,.cds--btn:hover:disabled,.ibm-web-chat--default-styles button.cds--btn--disabled:not([class*=cds--]),.ibm-web-chat--default-styles button:disabled:not([class*=cds--]),:host button.cds--btn--disabled:not([class*=cds--]),:host button:disabled:not([class*=cds--]){border-color:var(--cds-button-disabled,#c6c6c6);background:var(--cds-button-disabled,#c6c6c6);box-shadow:none;color:var(--cds-text-on-color-disabled,#8d8d8d);cursor:not-allowed}.cds--btn .cds--btn__icon,.ibm-web-chat--default-styles button:not([class*=cds--]) .cds--btn__icon,:host button:not([class*=cds--]) .cds--btn__icon{position:absolute;flex-shrink:0;block-size:16px;inline-size:16px;inset-block-start:min((var(--cds-layout-size-height-local) - 16px)/2 - 1px,var(--temp-padding-block-max));inset-inline-end:var(--cds-layout-density-padding-inline-local);margin-block-start:1px}.cds--btn::-moz-focus-inner,.ibm-web-chat--default-styles button:not([class*=cds--])::-moz-focus-inner,:host button:not([class*=cds--])::-moz-focus-inner{padding:0;border:0}.cds--btn--primary{border:1px solid transparent;background-color:var(--cds-button-primary,#0f62fe);color:var(--cds-text-on-color,#fff)}.cds--btn--primary:hover{background-color:var(--cds-button-primary-hover,#0050e6)}.cds--btn--primary:focus{border-color:var(--cds-button-focus-color,var(--cds-focus,#0f62fe));box-shadow:inset 0 0 0 1px var(--cds-button-focus-color,var(--cds-focus,#0f62fe)),inset 0 0 0 2px var(--cds-background,#fff)}.cds--btn--primary:active{background-color:var(--cds-button-primary-active,#002d9c)}.cds--btn--primary .cds--btn__icon,.cds--btn--primary .cds--btn__icon path:not([data-icon-path]):not([fill=none]){fill:currentColor}.cds--btn--primary:hover,.cds--btn--secondary{color:var(--cds-text-on-color,#fff)}.cds--btn--secondary{border:1px solid transparent;background-color:var(--cds-button-secondary,#393939)}.cds--btn--secondary:hover{background-color:var(--cds-button-secondary-hover,#474747)}.cds--btn--secondary:focus{border-color:var(--cds-button-focus-color,var(--cds-focus,#0f62fe));box-shadow:inset 0 0 0 1px var(--cds-button-focus-color,var(--cds-focus,#0f62fe)),inset 0 0 0 2px var(--cds-background,#fff)}.cds--btn--secondary:active{background-color:var(--cds-button-secondary-active,#6f6f6f)}.cds--btn--secondary .cds--btn__icon,.cds--btn--secondary .cds--btn__icon path:not([data-icon-path]):not([fill=none]){fill:currentColor}.cds--btn--secondary:focus,.cds--btn--secondary:hover{color:var(--cds-text-on-color,#fff)}.cds--btn--tertiary{border-width:1px;border-style:solid;border-color:var(--cds-button-tertiary,#0f62fe);background-color:initial;color:var(--cds-button-tertiary,#0f62fe)}.cds--btn--tertiary:hover{background-color:var(--cds-button-tertiary-hover,#0050e6)}.cds--btn--tertiary:focus{border-color:var(--cds-button-focus-color,var(--cds-focus,#0f62fe));box-shadow:inset 0 0 0 1px var(--cds-button-focus-color,var(--cds-focus,#0f62fe)),inset 0 0 0 2px var(--cds-background,#fff)}.cds--btn--tertiary .cds--btn__icon,.cds--btn--tertiary .cds--btn__icon path:not([data-icon-path]):not([fill=none]){fill:currentColor}.cds--btn--tertiary:focus,.cds--btn--tertiary:hover{color:var(--cds-text-inverse,#fff)}.cds--btn--tertiary:focus{background-color:var(--cds-button-tertiary,#0f62fe)}.cds--btn--tertiary:active{border-color:transparent;background-color:var(--cds-button-tertiary-active,#002d9c);color:var(--cds-text-inverse,#fff)}.cds--btn--tertiary.cds--btn--disabled,.cds--btn--tertiary.cds--btn--disabled:focus,.cds--btn--tertiary.cds--btn--disabled:hover,.cds--btn--tertiary:disabled,.cds--btn--tertiary:focus:disabled,.cds--btn--tertiary:hover:disabled{background:transparent;color:var(--cds-text-on-color-disabled,#8d8d8d);outline:none}.cds--btn--ghost,.ibm-web-chat--default-styles button:not([class*=cds--]),:host button:not([class*=cds--]){border:1px solid transparent;background-color:initial;color:var(--cds-link-primary,#0f62fe);padding-inline-end:calc(var(--cds-layout-density-padding-inline-local) - 1px)}.cds--btn--ghost:hover,.ibm-web-chat--default-styles button:hover:not([class*=cds--]),:host button:hover:not([class*=cds--]){background-color:var(--cds-background-hover,hsla(0,0%,55%,.12))}.cds--btn--ghost:focus,.ibm-web-chat--default-styles button:focus:not([class*=cds--]),:host button:focus:not([class*=cds--]){border-color:var(--cds-button-focus-color,var(--cds-focus,#0f62fe));box-shadow:inset 0 0 0 1px var(--cds-button-focus-color,var(--cds-focus,#0f62fe)),inset 0 0 0 2px var(--cds-background,#fff)}.cds--btn--ghost .cds--btn__icon,.cds--btn--ghost .cds--btn__icon path:not([data-icon-path]):not([fill=none]),.ibm-web-chat--default-styles button:not([class*=cds--]) .cds--btn__icon,.ibm-web-chat--default-styles button:not([class*=cds--]) .cds--btn__icon path:not([data-icon-path]):not([fill=none]),:host button:not([class*=cds--]) .cds--btn__icon,:host button:not([class*=cds--]) .cds--btn__icon path:not([data-icon-path]):not([fill=none]){fill:currentColor}.cds--btn--ghost .cds--btn__icon,.ibm-web-chat--default-styles button:not([class*=cds--]) .cds--btn__icon,:host button:not([class*=cds--]) .cds--btn__icon{position:static;align-self:center;margin-inline-start:8px}.cds--btn--ghost:active,.cds--btn--ghost:hover,.ibm-web-chat--default-styles button:active:not([class*=cds--]),.ibm-web-chat--default-styles button:hover:not([class*=cds--]),:host button:active:not([class*=cds--]),:host button:hover:not([class*=cds--]){color:var(--cds-link-primary-hover,#0043ce)}.cds--btn--ghost:active,.ibm-web-chat--default-styles button:active:not([class*=cds--]),:host button:active:not([class*=cds--]){background-color:var(--cds-background-active,hsla(0,0%,55%,.5))}.cds--btn--ghost.cds--btn--disabled,.cds--btn--ghost.cds--btn--disabled:focus,.cds--btn--ghost.cds--btn--disabled:hover,.cds--btn--ghost:disabled,.cds--btn--ghost:focus:disabled,.cds--btn--ghost:hover:disabled,.ibm-web-chat--default-styles button.cds--btn--disabled:not([class*=cds--]),.ibm-web-chat--default-styles button:disabled:not([class*=cds--]),:host button.cds--btn--disabled:not([class*=cds--]),:host button:disabled:not([class*=cds--]){border-color:transparent;background:transparent;color:var(--cds-text-on-color-disabled,#8d8d8d);outline:none}.cds--btn--ghost:not([disabled]) svg,.ibm-web-chat--default-styles button:not([disabled]):not([class*=cds--]) svg,:host button:not([disabled]):not([class*=cds--]) svg{fill:var(--cds-icon-primary,#161616)}.cds--btn--icon-only{justify-content:center;padding:0;block-size:var(--cds-layout-size-height-local);inline-size:var(--cds-layout-size-height-local);padding-block-start:min((var(--cds-layout-size-height-local) - 16px)/2 - 1px,var(--temp-padding-block-max))}.cds--btn--icon-only>:first-child{min-inline-size:16px}.cds--btn--icon-only .cds--btn__icon{position:static}.cds--btn--icon-only.cds--btn--danger--ghost .cds--btn__icon,.cds--btn--icon-only.cds--btn--ghost .cds--btn__icon,.ibm-web-chat--default-styles button.cds--btn--icon-only:not([class*=cds--]) .cds--btn__icon,:host button.cds--btn--icon-only:not([class*=cds--]) .cds--btn__icon{margin:0}.cds--btn--icon-only.cds--btn--danger--ghost{padding-inline-end:calc(var(--cds-layout-density-padding-inline-local) - 16px)}.cds--btn--md:not(.cds--btn--icon-only) .cds--btn__icon,.cds--btn--sm:not(.cds--btn--icon-only) .cds--btn__icon,.ibm-web-chat--default-styles button:not(.cds--btn--icon-only):not([class*=cds--]) .cds--btn__icon,:host button:not(.cds--btn--icon-only):not([class*=cds--]) .cds--btn__icon{margin-block-start:0}.cds--btn--icon-only.cds--btn--selected{background:var(--cds-background-selected,hsla(0,0%,55%,.2))}.cds--btn path[data-icon-path=inner-path],.ibm-web-chat--default-styles button:not([class*=cds--]) path[data-icon-path=inner-path],:host button:not([class*=cds--]) path[data-icon-path=inner-path]{fill:none}.cds--btn--ghost.cds--btn--icon-only .cds--btn__icon,.cds--btn--ghost.cds--btn--icon-only .cds--btn__icon path:not([data-icon-path]):not([fill=none]),.ibm-web-chat--default-styles button.cds--btn--icon-only:not([class*=cds--]) .cds--btn__icon,.ibm-web-chat--default-styles button.cds--btn--icon-only:not([class*=cds--]) .cds--btn__icon path:not([data-icon-path]):not([fill=none]),:host button.cds--btn--icon-only:not([class*=cds--]) .cds--btn__icon,:host button.cds--btn--icon-only:not([class*=cds--]) .cds--btn__icon path:not([data-icon-path]):not([fill=none]){fill:var(--cds-icon-primary,#161616)}.cds--btn--ghost.cds--btn--icon-only[disabled] .cds--btn__icon,.cds--btn--ghost.cds--btn--icon-only[disabled] .cds--btn__icon path:not([data-icon-path]):not([fill=none]),.cds--btn.cds--btn--icon-only.cds--btn--ghost[disabled]:hover .cds--btn__icon,.ibm-web-chat--default-styles button.cds--btn--icon-only:not([class*=cds--]):disabled .cds--btn__icon,.ibm-web-chat--default-styles button.cds--btn--icon-only:not([class*=cds--]):disabled .cds--btn__icon path:not([data-icon-path]):not([fill=none]),.ibm-web-chat--default-styles button.cds--btn--icon-only[disabled]:not([class*=cds--]) .cds--btn__icon,.ibm-web-chat--default-styles button.cds--btn--icon-only[disabled]:not([class*=cds--]) .cds--btn__icon path:not([data-icon-path]):not([fill=none]),:host button.cds--btn--icon-only:not([class*=cds--]):disabled .cds--btn__icon,:host button.cds--btn--icon-only:not([class*=cds--]):disabled .cds--btn__icon path:not([data-icon-path]):not([fill=none]),:host button.cds--btn--icon-only[disabled]:not([class*=cds--]) .cds--btn__icon,:host button.cds--btn--icon-only[disabled]:not([class*=cds--]) .cds--btn__icon path:not([data-icon-path]):not([fill=none]){fill:var(--cds-icon-on-color-disabled,#8d8d8d)}.cds--btn--ghost.cds--btn--icon-only[disabled],.ibm-web-chat--default-styles button.cds--btn--icon-only:not([class*=cds--]):disabled,.ibm-web-chat--default-styles button.cds--btn--icon-only[disabled]:not([class*=cds--]),:host button.cds--btn--icon-only:not([class*=cds--]):disabled,:host button.cds--btn--icon-only[disabled]:not([class*=cds--]){cursor:not-allowed}.cds--icon-tooltip--disabled .cds--tooltip-trigger__wrapper{cursor:not-allowed}.cds--icon-tooltip--disabled .cds--btn--icon-only[disabled],.cds--icon-tooltip--disabled .ibm-web-chat--default-styles button.cds--btn--icon-only:not([class*=cds--]):disabled,.ibm-web-chat--default-styles .cds--icon-tooltip--disabled button.cds--btn--icon-only:not([class*=cds--]):disabled,:host .cds--icon-tooltip--disabled button.cds--btn--icon-only:not([class*=cds--]):disabled{pointer-events:none}.cds--btn--danger{border:1px solid transparent;background-color:var(--cds-button-danger-primary,#da1e28);color:var(--cds-text-on-color,#fff)}.cds--btn--danger:hover{background-color:var(--cds-button-danger-hover,#b81921)}.cds--btn--danger:focus{border-color:var(--cds-button-focus-color,var(--cds-focus,#0f62fe));box-shadow:inset 0 0 0 1px var(--cds-button-focus-color,var(--cds-focus,#0f62fe)),inset 0 0 0 2px var(--cds-background,#fff)}.cds--btn--danger:active{background-color:var(--cds-button-danger-active,#750e13)}.cds--btn--danger .cds--btn__icon,.cds--btn--danger .cds--btn__icon path:not([data-icon-path]):not([fill=none]){fill:currentColor}.cds--btn--danger:hover{color:var(--cds-text-on-color,#fff)}.cds--btn--danger--tertiary{border-width:1px;border-style:solid;border-color:var(--cds-button-danger-secondary,#da1e28);background-color:initial;color:var(--cds-button-danger-secondary,#da1e28)}.cds--btn--danger--tertiary:hover{background-color:var(--cds-button-danger-hover,#b81921)}.cds--btn--danger--tertiary:focus{border-color:var(--cds-button-focus-color,var(--cds-focus,#0f62fe));box-shadow:inset 0 0 0 1px var(--cds-button-focus-color,var(--cds-focus,#0f62fe)),inset 0 0 0 2px var(--cds-background,#fff)}.cds--btn--danger--tertiary .cds--btn__icon,.cds--btn--danger--tertiary .cds--btn__icon path:not([data-icon-path]):not([fill=none]){fill:currentColor}.cds--btn--danger--tertiary:hover{border-color:var(--cds-button-danger-hover,#b81921);color:var(--cds-text-on-color,#fff)}.cds--btn--danger--tertiary:focus{background-color:var(--cds-button-danger-primary,#da1e28);color:var(--cds-text-on-color,#fff)}.cds--btn--danger--tertiary:active{border-color:var(--cds-button-danger-active,#750e13);background-color:var(--cds-button-danger-active,#750e13);color:var(--cds-text-on-color,#fff)}.cds--btn--danger--tertiary.cds--btn--disabled,.cds--btn--danger--tertiary.cds--btn--disabled:focus,.cds--btn--danger--tertiary.cds--btn--disabled:hover,.cds--btn--danger--tertiary:disabled,.cds--btn--danger--tertiary:focus:disabled,.cds--btn--danger--tertiary:hover:disabled{background:transparent;color:var(--cds-text-on-color-disabled,#8d8d8d);outline:none}.cds--btn--danger--ghost{border:1px solid transparent;background-color:initial;color:var(--cds-button-danger-secondary,#da1e28);padding-inline-end:calc(var(--cds-layout-density-padding-inline-local) - 1px)}.cds--btn--danger--ghost:hover{background-color:var(--cds-button-danger-hover,#b81921)}.cds--btn--danger--ghost:focus{border-color:var(--cds-button-focus-color,var(--cds-focus,#0f62fe));box-shadow:inset 0 0 0 1px var(--cds-button-focus-color,var(--cds-focus,#0f62fe)),inset 0 0 0 2px var(--cds-background,#fff)}.cds--btn--danger--ghost:active{background-color:var(--cds-button-danger-active,#750e13)}.cds--btn--danger--ghost .cds--btn__icon,.cds--btn--danger--ghost .cds--btn__icon path:not([data-icon-path]):not([fill=none]){fill:currentColor}.cds--btn--danger--ghost .cds--btn__icon{position:static;margin-inline-start:8px}.cds--btn--danger--ghost:active,.cds--btn--danger--ghost:hover{color:var(--cds-text-on-color,#fff)}.cds--btn--danger--ghost.cds--btn--disabled,.cds--btn--danger--ghost.cds--btn--disabled:focus,.cds--btn--danger--ghost.cds--btn--disabled:hover,.cds--btn--danger--ghost:disabled,.cds--btn--danger--ghost:focus:disabled,.cds--btn--danger--ghost:hover:disabled{border-color:transparent;background:transparent;color:var(--cds-text-disabled,hsla(0,0%,9%,.25));outline:none}.cds--btn--expressive{font-size:var(--cds-body-compact-02-font-size,16px);font-weight:var(--cds-body-compact-02-font-weight,400);line-height:var(--cds-body-compact-02-line-height,1.375);letter-spacing:var(--cds-body-compact-02-letter-spacing,0);padding-block:min((var(--cds-layout-size-height-local) - var(--temp-expressive-1lh))/2 - 1px,var(--temp-padding-block-max))}.cds--btn--icon-only.cds--btn--expressive{padding:12px 13px}.cds--btn.cds--btn--expressive .cds--btn__icon,.ibm-web-chat--default-styles button.cds--btn--expressive:not([class*=cds--]) .cds--btn__icon,:host button.cds--btn--expressive:not([class*=cds--]) .cds--btn__icon{block-size:20px;inline-size:20px}.cds--btn-set .cds--btn.cds--btn--expressive,.cds--btn-set .ibm-web-chat--default-styles button.cds--btn--expressive:not([class*=cds--]),.ibm-web-chat--default-styles .cds--btn-set button.cds--btn--expressive:not([class*=cds--]),:host .cds--btn-set button.cds--btn--expressive:not([class*=cds--]){max-inline-size:320px}.cds--btn.cds--skeleton,.ibm-web-chat--default-styles button.cds--skeleton:not([class*=cds--]),:host button.cds--skeleton:not([class*=cds--]){position:relative;padding:0;border:none;background:var(--cds-skeleton-background,#e8e8e8);box-shadow:none;pointer-events:none;inline-size:150px}.cds--btn.cds--skeleton:active,.cds--btn.cds--skeleton:focus,.cds--btn.cds--skeleton:hover,.ibm-web-chat--default-styles button.cds--skeleton:active:not([class*=cds--]),.ibm-web-chat--default-styles button.cds--skeleton:focus:not([class*=cds--]),.ibm-web-chat--default-styles button.cds--skeleton:hover:not([class*=cds--]),:host button.cds--skeleton:active:not([class*=cds--]),:host button.cds--skeleton:focus:not([class*=cds--]),:host button.cds--skeleton:hover:not([class*=cds--]){border:none;cursor:default;outline:none}.cds--btn.cds--skeleton:before,.ibm-web-chat--default-styles button.cds--skeleton:not([class*=cds--]):before,:host button.cds--skeleton:not([class*=cds--]):before{position:absolute;animation:cds--skeleton 3s ease-in-out infinite;background:var(--cds-skeleton-element,#c6c6c6);block-size:100%;content:\"\";inline-size:100%;inset-inline-start:0;will-change:transform-origin,transform,opacity}@media (prefers-reduced-motion:reduce){.cds--btn.cds--skeleton:before,.ibm-web-chat--default-styles button.cds--skeleton:not([class*=cds--]):before,:host button.cds--skeleton:not([class*=cds--]):before{animation:none}}.cds--btn-set{display:flex}.cds--btn-set--stacked{flex-direction:column}.cds--btn-set .cds--btn,.cds--btn-set .ibm-web-chat--default-styles button:not([class*=cds--]),.ibm-web-chat--default-styles .cds--btn-set button:not([class*=cds--]),:host .cds--btn-set button:not([class*=cds--]){inline-size:100%;max-inline-size:196px}.cds--btn-set .cds--btn:not(:focus),.cds--btn-set .ibm-web-chat--default-styles button:not(:focus):not([class*=cds--]),.ibm-web-chat--default-styles .cds--btn-set button:not(:focus):not([class*=cds--]),:host .cds--btn-set button:not(:focus):not([class*=cds--]){box-shadow:-1px 0 0 0 var(--cds-button-separator,#e0e0e0)}.cds--btn-set .cds--btn:first-of-type:not(:focus),.cds--btn-set .ibm-web-chat--default-styles button:first-of-type:not(:focus):not([class*=cds--]),.ibm-web-chat--default-styles .cds--btn-set button:first-of-type:not(:focus):not([class*=cds--]),:host .cds--btn-set button:first-of-type:not(:focus):not([class*=cds--]){box-shadow:inherit}.cds--btn-set .cds--btn:focus+.cds--btn,.cds--btn-set .ibm-web-chat--default-styles .cds--btn:focus+button:not([class*=cds--]),.cds--btn-set .ibm-web-chat--default-styles button:focus:not([class*=cds--])+.cds--btn,.cds--btn-set .ibm-web-chat--default-styles button:focus:not([class*=cds--])+button:not([class*=cds--]),.ibm-web-chat--default-styles .cds--btn-set .cds--btn:focus+button:not([class*=cds--]),.ibm-web-chat--default-styles .cds--btn-set button:focus:not([class*=cds--])+.cds--btn,.ibm-web-chat--default-styles .cds--btn-set button:focus:not([class*=cds--])+button:not([class*=cds--]),:host .cds--btn-set .cds--btn:focus+button:not([class*=cds--]),:host .cds--btn-set button:focus:not([class*=cds--])+.cds--btn,:host .cds--btn-set button:focus:not([class*=cds--])+button:not([class*=cds--]){box-shadow:inherit}.cds--btn-set--stacked .cds--btn:not(:focus),.cds--btn-set--stacked .ibm-web-chat--default-styles button:not(:focus):not([class*=cds--]),.ibm-web-chat--default-styles .cds--btn-set--stacked button:not(:focus):not([class*=cds--]),:host .cds--btn-set--stacked button:not(:focus):not([class*=cds--]){box-shadow:0 -1px 0 0 var(--cds-button-separator,#e0e0e0)}.cds--btn-set--stacked .cds--btn:first-of-type:not(:focus),.cds--btn-set--stacked .ibm-web-chat--default-styles button:first-of-type:not(:focus):not([class*=cds--]),.ibm-web-chat--default-styles .cds--btn-set--stacked button:first-of-type:not(:focus):not([class*=cds--]),:host .cds--btn-set--stacked button:first-of-type:not(:focus):not([class*=cds--]){box-shadow:inherit}.cds--btn-set .cds--btn.cds--btn--disabled,.cds--btn-set .ibm-web-chat--default-styles button.cds--btn--disabled:not([class*=cds--]),.ibm-web-chat--default-styles .cds--btn-set button.cds--btn--disabled:not([class*=cds--]),:host .cds--btn-set button.cds--btn--disabled:not([class*=cds--]){box-shadow:-1px 0 0 0 var(--cds-icon-on-color-disabled,#8d8d8d)}.cds--btn-set .cds--btn.cds--btn--disabled:first-of-type,.cds--btn-set .ibm-web-chat--default-styles button.cds--btn--disabled:first-of-type:not([class*=cds--]),.ibm-web-chat--default-styles .cds--btn-set button.cds--btn--disabled:first-of-type:not([class*=cds--]),:host .cds--btn-set button.cds--btn--disabled:first-of-type:not([class*=cds--]){box-shadow:none}.cds--btn-set--stacked .cds--btn.cds--btn--disabled,.cds--btn-set--stacked .ibm-web-chat--default-styles button.cds--btn--disabled:not([class*=cds--]),.ibm-web-chat--default-styles .cds--btn-set--stacked button.cds--btn--disabled:not([class*=cds--]),:host .cds--btn-set--stacked button.cds--btn--disabled:not([class*=cds--]){box-shadow:0 -1px 0 0 var(--cds-layer-selected-disabled,#8d8d8d)}.cds--btn-set--stacked .cds--btn.cds--btn--disabled:first-of-type,.cds--btn-set--stacked .ibm-web-chat--default-styles button.cds--btn--disabled:first-of-type:not([class*=cds--]),.ibm-web-chat--default-styles .cds--btn-set--stacked button.cds--btn--disabled:first-of-type:not([class*=cds--]),:host .cds--btn-set--stacked button.cds--btn--disabled:first-of-type:not([class*=cds--]){box-shadow:none}.cds--btn-set .cds--btn.cds--btn--loading,.cds--btn-set .ibm-web-chat--default-styles button.cds--btn--loading:not([class*=cds--]),.ibm-web-chat--default-styles .cds--btn-set button.cds--btn--loading:not([class*=cds--]),:host .cds--btn-set button.cds--btn--loading:not([class*=cds--]){border-color:transparent;background-color:initial;box-shadow:none}.cds--btn--sm .cds--badge-indicator,.ibm-web-chat--default-styles button:not([class*=cds--]) .cds--badge-indicator,:host button:not([class*=cds--]) .cds--badge-indicator{margin-block-start:4px;margin-inline-end:4px}@media (forced-colors:active),screen and (-ms-high-contrast:active){.cds--btn:focus,.ibm-web-chat--default-styles button:focus:not([class*=cds--]),:host button:focus:not([class*=cds--]){color:Highlight;outline:1px solid Highlight}}@media (forced-colors:active),screen and (-ms-high-contrast:active){.cds--btn--ghost.cds--btn--icon-only .cds--btn__icon,.cds--btn--ghost.cds--btn--icon-only .cds--btn__icon path:not([data-icon-path]):not([fill=none]),.ibm-web-chat--default-styles button.cds--btn--icon-only:not([class*=cds--]) .cds--btn__icon,.ibm-web-chat--default-styles button.cds--btn--icon-only:not([class*=cds--]) .cds--btn__icon path:not([data-icon-path]):not([fill=none]),:host button.cds--btn--icon-only:not([class*=cds--]) .cds--btn__icon,:host button.cds--btn--icon-only:not([class*=cds--]) .cds--btn__icon path:not([data-icon-path]):not([fill=none]){fill:ButtonText}}.ibm-web-chat--default-styles [dir=rtl] .cds--btn-set button:not(:focus):not([class*=cds--]),:host [dir=rtl] .cds--btn-set button:not(:focus):not([class*=cds--]),[dir=rtl] .cds--btn-set .cds--btn:not(:focus),[dir=rtl] .cds--btn-set .ibm-web-chat--default-styles button:not(:focus):not([class*=cds--]){box-shadow:1px 0 0 0 var(--cds-button-separator,#e0e0e0)}.cds--chat-btn,.ibm-web-chat--default-styles button:not([class*=cds--]),:host button:not([class*=cds--]){border-radius:24px}.cds--chat-btn:not(.cds--chat-btn--with-icon),.ibm-web-chat--default-styles button:not(.cds--chat-btn--with-icon):not([class*=cds--]),:host button:not(.cds--chat-btn--with-icon):not([class*=cds--]){padding-inline-end:15px}.cds--chat-btn.cds--btn--md,.ibm-web-chat--default-styles button.cds--btn--md:not([class*=cds--]),:host button.cds--btn--md:not([class*=cds--]){border-radius:20px}.cds--chat-btn.cds--btn--sm,.ibm-web-chat--default-styles button:not([class*=cds--]),:host button:not([class*=cds--]){border-radius:16px}.cds--chat-btn--quick-action,.ibm-web-chat--default-styles button:not([class*=cds--]),.ibm-web-chat--default-styles button:not([class*=cds--]):disabled,:host button:not([class*=cds--]),:host button:not([class*=cds--]):disabled{align-items:center;border:1px solid var(--cds-chat-button,#0f62fe);background:transparent;color:var(--cds-chat-button,#0f62fe)}.cds--chat-btn--quick-action:hover:not(:active):not([disabled]),.ibm-web-chat--default-styles button:hover:not(:active):not([disabled]):not([class*=cds--]),:host button:hover:not(:active):not([disabled]):not([class*=cds--]){border-color:transparent;background:var(--cds-chat-button-hover,hsla(0,0%,55%,.12));color:var(--cds-chat-button-text-hover,#0043ce)}.cds--chat-btn--quick-action:active,.ibm-web-chat--default-styles button:active:not([class*=cds--]),:host button:active:not([class*=cds--]){border-color:transparent;background:var(--cds-chat-button-active,hsla(0,0%,55%,.5));color:var(--cds-chat-button-text-hover,#0043ce)}.cds--chat-btn--quick-action.cds--btn--ghost:focus,.ibm-web-chat--default-styles button:focus:not([class*=cds--]),:host button:focus:not([class*=cds--]){border-color:var(--cds-focus,#0f62fe);box-shadow:inset 0 0 0 1px var(--cds-focus,#0f62fe)}.cds--chat-btn--quick-action.cds--btn--ghost:hover:focus,.ibm-web-chat--default-styles button:hover:focus:not([class*=cds--]),:host button:hover:focus:not([class*=cds--]){border-color:var(--cds-focus,#0f62fe);box-shadow:inset 0 0 0 1px var(--cds-focus-inset,#fff)}.cds--chat-btn--quick-action[disabled],.cds--chat-btn--quick-action[disabled]:hover,.ibm-web-chat--default-styles button:not([class*=cds--]):disabled,.ibm-web-chat--default-styles button[disabled]:not([class*=cds--]),:host button:not([class*=cds--]):disabled,:host button[disabled]:not([class*=cds--]){border-color:var(--cds-button-disabled,#c6c6c6);color:var(--cds-button-disabled,#c6c6c6)}.cds--chat-btn--quick-action--selected,.cds--chat-btn--quick-action--selected[disabled],.cds--chat-btn--quick-action--selected[disabled]:hover,.ibm-web-chat--default-styles button.cds--chat-btn--quick-action--selected:not([class*=cds--]):disabled,:host button.cds--chat-btn--quick-action--selected:not([class*=cds--]):disabled{border-color:transparent;background:var(--cds-chat-button-selected,hsla(0,0%,55%,.2));color:var(--cds-chat-button-text-selected,#525252)}.cds--chat-btn--quick-action.cds--chat-btn--quick-action--selected:not([disabled]):active,.cds--chat-btn--quick-action.cds--chat-btn--quick-action--selected:not([disabled]):hover,.ibm-web-chat--default-styles button.cds--chat-btn--quick-action--selected:not([disabled]):active:not([class*=cds--]),.ibm-web-chat--default-styles button.cds--chat-btn--quick-action--selected:not([disabled]):hover:not([class*=cds--]),:host button.cds--chat-btn--quick-action--selected:not([disabled]):active:not([class*=cds--]),:host button.cds--chat-btn--quick-action--selected:not([disabled]):hover:not([class*=cds--]){color:var(--cds-chat-button-text-selected,#525252)}.cds--chat-btn.cds--skeleton,.ibm-web-chat--default-styles button.cds--skeleton:not([class*=cds--]),:host button.cds--skeleton:not([class*=cds--]){overflow:hidden}.ibm-web-chat--default-styles,:host{all:revert;box-sizing:border-box;font-family:var(--cds-chat-BASE-font-family);color:inherit;background:transparent}.ibm-web-chat--default-styles b:not([class*=cds--]),.ibm-web-chat--default-styles strong:not([class*=cds--]),:host b:not([class*=cds--]),:host strong:not([class*=cds--]){font-weight:var(--cds-chat-BASE-weight-semibold)}.ibm-web-chat--default-styles em:not([class*=cds--]),.ibm-web-chat--default-styles i:not([class*=cds--]),:host em:not([class*=cds--]),:host i:not([class*=cds--]){font-style:italic}.ibm-web-chat--default-styles ol:not([class*=cds--]),.ibm-web-chat--default-styles ul:not([class*=cds--]),:host ol:not([class*=cds--]),:host ul:not([class*=cds--]){padding-block:8px;padding-inline-start:16px}.ibm-web-chat--default-styles ol:not([class*=cds--]),:host ol:not([class*=cds--]){counter-reset:ol-counter;list-style-type:decimal}.ibm-web-chat--default-styles ul:not([class*=cds--]),:host ul:not([class*=cds--]){list-style-type:disc}.ibm-web-chat--default-styles ol:not([class*=cds--]) ol:not([class*=cds--]),.ibm-web-chat--default-styles ol:not([class*=cds--]) ul:not([class*=cds--]),.ibm-web-chat--default-styles ul:not([class*=cds--]) ol:not([class*=cds--]),.ibm-web-chat--default-styles ul:not([class*=cds--]) ul:not([class*=cds--]),:host ol:not([class*=cds--]) ol:not([class*=cds--]),:host ol:not([class*=cds--]) ul:not([class*=cds--]),:host ul:not([class*=cds--]) ol:not([class*=cds--]),:host ul:not([class*=cds--]) ul:not([class*=cds--]){margin-inline-start:32px;padding-block:0;padding-inline-start:0}.ibm-web-chat--default-styles ul:not([class*=cds--]) li:not([class*=cds--]),:host ul:not([class*=cds--]) li:not([class*=cds--]){position:relative}.ibm-web-chat--default-styles ol:not([class*=cds--]) li:not([class*=cds--]),:host ol:not([class*=cds--]) li:not([class*=cds--]){position:relative;counter-increment:ol-counter}.ibm-web-chat--default-styles ol:not([class*=cds--]) ol:not([class*=cds--]) li:not([class*=cds--]),.ibm-web-chat--default-styles ol:not([class*=cds--]) ul:not([class*=cds--]) li:not([class*=cds--]),.ibm-web-chat--default-styles ul:not([class*=cds--]) ol:not([class*=cds--]) li:not([class*=cds--]),.ibm-web-chat--default-styles ul:not([class*=cds--]) ul:not([class*=cds--]) li:not([class*=cds--]),:host ol:not([class*=cds--]) ol:not([class*=cds--]) li:not([class*=cds--]),:host ol:not([class*=cds--]) ul:not([class*=cds--]) li:not([class*=cds--]),:host ul:not([class*=cds--]) ol:not([class*=cds--]) li:not([class*=cds--]),:host ul:not([class*=cds--]) ul:not([class*=cds--]) li:not([class*=cds--]){padding-inline-start:4px}.ibm-web-chat--default-styles input[type=text]:not([class*=cds--]),.ibm-web-chat--default-styles textarea:not([class*=cds--]),:host input[type=text]:not([class*=cds--]),:host textarea:not([class*=cds--]){font-size:inherit;font-family:inherit;padding:0;border:var(--cds-chat-BASE-border-width-med) solid transparent}.ibm-web-chat--default-styles input:focus:not([class*=cds--]),.ibm-web-chat--default-styles textarea:focus:not([class*=cds--]),:host input:focus:not([class*=cds--]),:host textarea:focus:not([class*=cds--]){border-color:var(--cds-focus,#0f62fe);outline:none}.ibm-web-chat--default-styles input:not([class*=cds--]),.ibm-web-chat--default-styles textarea:not([class*=cds--]),:host input:not([class*=cds--]),:host textarea:not([class*=cds--]){margin-block:0;margin-inline:8px}.ibm-web-chat--default-styles input[type=checkbox]:focus:not([class*=cds--]),.ibm-web-chat--default-styles input[type=radio]:focus:not([class*=cds--]),:host input[type=checkbox]:focus:not([class*=cds--]),:host input[type=radio]:focus:not([class*=cds--]){outline:solid 2px var(--cds-focus,#0f62fe)}.ibm-web-chat--default-styles table:not([class*=cds--]),:host table:not([class*=cds--]){width:100%}.ibm-web-chat--default-styles table:not([class*=cds--]) th:not([class*=cds--]),:host table:not([class*=cds--]) th:not([class*=cds--]){font-weight:var(--cds-chat-BASE-weight-semibold)}.ibm-web-chat--default-styles p:not([class*=cds--]):not([class*=WACWidget__Markdown]),:host p:not([class*=cds--]):not([class*=WACWidget__Markdown]){margin-block:1em;margin-inline:0;font-size:var(--cds-chat-BASE-font-size-med);line-height:var(--cds-chat-BASE-line-height-med)}.ibm-web-chat--default-styles button:not([class*=cds--]):disabled,:host button:not([class*=cds--]):disabled{cursor:not-allowed}.ibm-web-chat--default-styles sub:not([class*=cds--]),:host sub:not([class*=cds--]){vertical-align:sub;font-size:smaller}.ibm-web-chat--default-styles sup:not([class*=cds--]),:host sup:not([class*=cds--]){vertical-align:super;font-size:smaller}:host{display:block}:host-context(.cds--g100) pre code.hljs,:host-context(.cds--g90) pre code.hljs{display:block;overflow-x:auto;padding:1em}:host-context(.cds--g100) code.hljs,:host-context(.cds--g90) code.hljs{padding:3px 5px}:host-context(.cds--g100) .hljs,:host-context(.cds--g90) .hljs{background:#2b2b2b;color:#f8f8f2}:host-context(.cds--g100) .hljs-comment,:host-context(.cds--g100) .hljs-quote,:host-context(.cds--g90) .hljs-comment,:host-context(.cds--g90) .hljs-quote{color:#d4d0ab}:host-context(.cds--g100) .hljs-deletion,:host-context(.cds--g100) .hljs-name,:host-context(.cds--g100) .hljs-regexp,:host-context(.cds--g100) .hljs-selector-class,:host-context(.cds--g100) .hljs-selector-id,:host-context(.cds--g100) .hljs-tag,:host-context(.cds--g100) .hljs-template-variable,:host-context(.cds--g100) .hljs-variable,:host-context(.cds--g90) .hljs-deletion,:host-context(.cds--g90) .hljs-name,:host-context(.cds--g90) .hljs-regexp,:host-context(.cds--g90) .hljs-selector-class,:host-context(.cds--g90) .hljs-selector-id,:host-context(.cds--g90) .hljs-tag,:host-context(.cds--g90) .hljs-template-variable,:host-context(.cds--g90) .hljs-variable{color:#ffa07a}:host-context(.cds--g100) .hljs-built_in,:host-context(.cds--g100) .hljs-link,:host-context(.cds--g100) .hljs-literal,:host-context(.cds--g100) .hljs-meta,:host-context(.cds--g100) .hljs-number,:host-context(.cds--g100) .hljs-params,:host-context(.cds--g100) .hljs-type,:host-context(.cds--g90) .hljs-built_in,:host-context(.cds--g90) .hljs-link,:host-context(.cds--g90) .hljs-literal,:host-context(.cds--g90) .hljs-meta,:host-context(.cds--g90) .hljs-number,:host-context(.cds--g90) .hljs-params,:host-context(.cds--g90) .hljs-type{color:#f5ab35}:host-context(.cds--g100) .hljs-attribute,:host-context(.cds--g90) .hljs-attribute{color:gold}:host-context(.cds--g100) .hljs-addition,:host-context(.cds--g100) .hljs-bullet,:host-context(.cds--g100) .hljs-string,:host-context(.cds--g100) .hljs-symbol,:host-context(.cds--g90) .hljs-addition,:host-context(.cds--g90) .hljs-bullet,:host-context(.cds--g90) .hljs-string,:host-context(.cds--g90) .hljs-symbol{color:#abe338}:host-context(.cds--g100) .hljs-section,:host-context(.cds--g100) .hljs-title,:host-context(.cds--g90) .hljs-section,:host-context(.cds--g90) .hljs-title{color:#00e0e0}:host-context(.cds--g100) .hljs-keyword,:host-context(.cds--g100) .hljs-selector-tag,:host-context(.cds--g90) .hljs-keyword,:host-context(.cds--g90) .hljs-selector-tag{color:#dcc6e0}:host-context(.cds--g100) .hljs-emphasis,:host-context(.cds--g90) .hljs-emphasis{font-style:italic}:host-context(.cds--g100) .hljs-strong,:host-context(.cds--g90) .hljs-strong{font-weight:700}@media screen and (-ms-high-contrast:active){:host-context(.cds--g100) .hljs-addition,:host-context(.cds--g100) .hljs-attribute,:host-context(.cds--g100) .hljs-built_in,:host-context(.cds--g100) .hljs-bullet,:host-context(.cds--g100) .hljs-comment,:host-context(.cds--g100) .hljs-link,:host-context(.cds--g100) .hljs-literal,:host-context(.cds--g100) .hljs-meta,:host-context(.cds--g100) .hljs-number,:host-context(.cds--g100) .hljs-params,:host-context(.cds--g100) .hljs-quote,:host-context(.cds--g100) .hljs-string,:host-context(.cds--g100) .hljs-symbol,:host-context(.cds--g100) .hljs-type,:host-context(.cds--g90) .hljs-addition,:host-context(.cds--g90) .hljs-attribute,:host-context(.cds--g90) .hljs-built_in,:host-context(.cds--g90) .hljs-bullet,:host-context(.cds--g90) .hljs-comment,:host-context(.cds--g90) .hljs-link,:host-context(.cds--g90) .hljs-literal,:host-context(.cds--g90) .hljs-meta,:host-context(.cds--g90) .hljs-number,:host-context(.cds--g90) .hljs-params,:host-context(.cds--g90) .hljs-quote,:host-context(.cds--g90) .hljs-string,:host-context(.cds--g90) .hljs-symbol,:host-context(.cds--g90) .hljs-type{color:highlight}:host-context(.cds--g100) .hljs-keyword,:host-context(.cds--g100) .hljs-selector-tag,:host-context(.cds--g90) .hljs-keyword,:host-context(.cds--g90) .hljs-selector-tag{font-weight:700}}:host-context(.cds--g10) pre code.hljs,:host-context(.cds--white) pre code.hljs{display:block;overflow-x:auto;padding:1em}:host-context(.cds--g10) code.hljs,:host-context(.cds--white) code.hljs{padding:3px 5px}:host-context(.cds--g10) .hljs,:host-context(.cds--white) .hljs{background:#fefefe;color:#545454}:host-context(.cds--g10) .hljs-comment,:host-context(.cds--g10) .hljs-quote,:host-context(.cds--white) .hljs-comment,:host-context(.cds--white) .hljs-quote{color:#696969}:host-context(.cds--g10) .hljs-deletion,:host-context(.cds--g10) .hljs-name,:host-context(.cds--g10) .hljs-regexp,:host-context(.cds--g10) .hljs-selector-class,:host-context(.cds--g10) .hljs-selector-id,:host-context(.cds--g10) .hljs-tag,:host-context(.cds--g10) .hljs-template-variable,:host-context(.cds--g10) .hljs-variable,:host-context(.cds--white) .hljs-deletion,:host-context(.cds--white) .hljs-name,:host-context(.cds--white) .hljs-regexp,:host-context(.cds--white) .hljs-selector-class,:host-context(.cds--white) .hljs-selector-id,:host-context(.cds--white) .hljs-tag,:host-context(.cds--white) .hljs-template-variable,:host-context(.cds--white) .hljs-variable{color:#d91e18}:host-context(.cds--g10) .hljs-built_in,:host-context(.cds--g10) .hljs-link,:host-context(.cds--g10) .hljs-literal,:host-context(.cds--g10) .hljs-meta,:host-context(.cds--g10) .hljs-number,:host-context(.cds--g10) .hljs-params,:host-context(.cds--g10) .hljs-type,:host-context(.cds--white) .hljs-built_in,:host-context(.cds--white) .hljs-link,:host-context(.cds--white) .hljs-literal,:host-context(.cds--white) .hljs-meta,:host-context(.cds--white) .hljs-number,:host-context(.cds--white) .hljs-params,:host-context(.cds--white) .hljs-type{color:#aa5d00}:host-context(.cds--g10) .hljs-attribute,:host-context(.cds--white) .hljs-attribute{color:#aa5d00}:host-context(.cds--g10) .hljs-addition,:host-context(.cds--g10) .hljs-bullet,:host-context(.cds--g10) .hljs-string,:host-context(.cds--g10) .hljs-symbol,:host-context(.cds--white) .hljs-addition,:host-context(.cds--white) .hljs-bullet,:host-context(.cds--white) .hljs-string,:host-context(.cds--white) .hljs-symbol{color:green}:host-context(.cds--g10) .hljs-section,:host-context(.cds--g10) .hljs-title,:host-context(.cds--white) .hljs-section,:host-context(.cds--white) .hljs-title{color:#007faa}:host-context(.cds--g10) .hljs-keyword,:host-context(.cds--g10) .hljs-selector-tag,:host-context(.cds--white) .hljs-keyword,:host-context(.cds--white) .hljs-selector-tag{color:#7928a1}:host-context(.cds--g10) .hljs-emphasis,:host-context(.cds--white) .hljs-emphasis{font-style:italic}:host-context(.cds--g10) .hljs-strong,:host-context(.cds--white) .hljs-strong{font-weight:700}@media screen and (-ms-high-contrast:active){:host-context(.cds--g10) .hljs-addition,:host-context(.cds--g10) .hljs-attribute,:host-context(.cds--g10) .hljs-built_in,:host-context(.cds--g10) .hljs-bullet,:host-context(.cds--g10) .hljs-comment,:host-context(.cds--g10) .hljs-link,:host-context(.cds--g10) .hljs-literal,:host-context(.cds--g10) .hljs-meta,:host-context(.cds--g10) .hljs-number,:host-context(.cds--g10) .hljs-params,:host-context(.cds--g10) .hljs-quote,:host-context(.cds--g10) .hljs-string,:host-context(.cds--g10) .hljs-symbol,:host-context(.cds--g10) .hljs-type,:host-context(.cds--white) .hljs-addition,:host-context(.cds--white) .hljs-attribute,:host-context(.cds--white) .hljs-built_in,:host-context(.cds--white) .hljs-bullet,:host-context(.cds--white) .hljs-comment,:host-context(.cds--white) .hljs-link,:host-context(.cds--white) .hljs-literal,:host-context(.cds--white) .hljs-meta,:host-context(.cds--white) .hljs-number,:host-context(.cds--white) .hljs-params,:host-context(.cds--white) .hljs-quote,:host-context(.cds--white) .hljs-string,:host-context(.cds--white) .hljs-symbol,:host-context(.cds--white) .hljs-type{color:highlight}:host-context(.cds--g10) .hljs-keyword,:host-context(.cds--g10) .hljs-selector-tag,:host-context(.cds--white) .hljs-keyword,:host-context(.cds--white) .hljs-selector-tag{font-weight:700}}:host-context(.WAC--narrowWidth) .cds--link:not(:has(.cds--link__icon)),:host-context(.WAC--standardWidth) .cds--link:not(:has(.cds--link__icon)){display:inline}:host-context(.WAC--narrowWidth) .cds--list--ordered:not(.cds--list--nested){margin-inline-start:24px}.WACWidget__MarkdownBlockquote,.WACWidget__MarkdownImg,.WACWidget__MarkdownP,.WACWidget__MarkdownPre,.cds--data-table-content,.cds--list--ordered:not(.cds--list--nested),.cds--list--unordered:not(.cds--list--nested){margin-block:0}.cds--list--ordered:not(.cds--list--nested),.cds--list--unordered:not(.cds--list--nested){padding:0;padding-block:8px}.WACWidget__MarkdownP{font-size:var(--cds-body-01-font-size,14px);font-weight:var(--cds-body-01-font-weight,400);line-height:var(--cds-body-01-line-height,1.42857);letter-spacing:var(--cds-body-01-letter-spacing,.16px);font-size:var(--cds-chat-BASE-font-size-med);line-height:var(--cds-chat-BASE-line-height-large)}.WACWidget__MarkdownBlockquote{border-inline-start:4px solid var(--cds-chat-ACCENT-color);padding-inline-start:8px}.WACWidget__MarkdownPre,pre{font-family:var(--cds-code-02-font-family,\"IBM Plex Mono\",system-ui,-apple-system,BlinkMacSystemFont,\".SFNSText-Regular\",monospace);font-size:var(--cds-code-02-font-size,14px);font-weight:var(--cds-code-02-font-weight,400);line-height:var(--cds-code-02-line-height,1.42857);letter-spacing:var(--cds-code-02-letter-spacing,.32px);border:1px solid var(--cds-chat-bubble-border,#e0e0e0);background-color:var(--cds-layer-02,#fff);overflow:auto hidden;padding:16px;white-space:pre-wrap}.WACWidget__MarkdownImg{max-width:100%}h1.WACWidget__MarkdownHeading:not(:first-child),h2.WACWidget__MarkdownHeading:not(:first-child),h3.WACWidget__MarkdownHeading:not(:first-child),h4.WACWidget__MarkdownHeading:not(:first-child),h5.WACWidget__MarkdownHeading:not(:first-child),h6.WACWidget__MarkdownHeading:not(:first-child){margin-block:0}h1.WACWidget__MarkdownHeading{font-size:var(--cds-heading-03-font-size,20px);font-weight:var(--cds-heading-03-font-weight,400);line-height:var(--cds-heading-03-line-height,1.4);letter-spacing:var(--cds-heading-03-letter-spacing,0)}h2.WACWidget__MarkdownHeading{font-size:var(--cds-heading-02-font-size,16px);font-weight:var(--cds-heading-02-font-weight,600);line-height:var(--cds-heading-02-line-height,1.5);letter-spacing:var(--cds-heading-02-letter-spacing,0)}h3.WACWidget__MarkdownHeading,h4.WACWidget__MarkdownHeading,h5.WACWidget__MarkdownHeading,h6.WACWidget__MarkdownHeading{font-size:var(--cds-heading-01-font-size,14px);font-weight:var(--cds-heading-01-font-weight,600);line-height:var(--cds-heading-01-line-height,1.42857);letter-spacing:var(--cds-heading-01-letter-spacing,.16px)}.cds--data-table-content{overflow:auto hidden;overflow-wrap:normal;word-break:normal;word-wrap:normal}.cds-aichat-markdown-text--padded .WACWidget__MarkdownBlockquote:not(:first-child),.cds-aichat-markdown-text--padded .WACWidget__MarkdownImg:not(:first-child),.cds-aichat-markdown-text--padded .WACWidget__MarkdownP:not(:first-child),.cds-aichat-markdown-text--padded .WACWidget__MarkdownPre:not(:first-child),.cds-aichat-markdown-text--padded .cds--data-table-content:not(:first-child),.cds-aichat-markdown-text--padded .cds--list--ordered:not(.cds--list--nested):not(:first-child),.cds-aichat-markdown-text--padded .cds--list--unordered:not(.cds--list--nested):not(:first-child),.cds-aichat-markdown-text--padded hr:not(:first-child),.cds-aichat-markdown-text--padded pre:not(:first-child){margin-block-start:16px}.cds-aichat-markdown-text--padded .cds--list--ordered:not(.cds--list--nested),.cds-aichat-markdown-text--padded .cds--list--unordered:not(.cds--list--nested){padding:0;padding-block:0}.cds-aichat-markdown-text--padded h1.WACWidget__MarkdownHeading:not(:first-child),.cds-aichat-markdown-text--padded h2.WACWidget__MarkdownHeading:not(:first-child),.cds-aichat-markdown-text--padded h3.WACWidget__MarkdownHeading:not(:first-child),.cds-aichat-markdown-text--padded h4.WACWidget__MarkdownHeading:not(:first-child),.cds-aichat-markdown-text--padded h5.WACWidget__MarkdownHeading:not(:first-child),.cds-aichat-markdown-text--padded h6.WACWidget__MarkdownHeading:not(:first-child){margin-block-start:24px}";

/*! @license DOMPurify 3.2.4 | (c) Cure53 and other contributors | Released under the Apache license 2.0 and Mozilla Public License 2.0 | github.com/cure53/DOMPurify/blob/3.2.4/LICENSE */

const {
  entries,
  setPrototypeOf,
  isFrozen,
  getPrototypeOf,
  getOwnPropertyDescriptor
} = Object;
let {
  freeze,
  seal,
  create
} = Object; // eslint-disable-line import/no-mutable-exports
let {
  apply,
  construct
} = typeof Reflect !== 'undefined' && Reflect;
if (!freeze) {
  freeze = function freeze(x) {
    return x;
  };
}
if (!seal) {
  seal = function seal(x) {
    return x;
  };
}
if (!apply) {
  apply = function apply(fun, thisValue, args) {
    return fun.apply(thisValue, args);
  };
}
if (!construct) {
  construct = function construct(Func, args) {
    return new Func(...args);
  };
}
const arrayForEach = unapply(Array.prototype.forEach);
const arrayLastIndexOf = unapply(Array.prototype.lastIndexOf);
const arrayPop = unapply(Array.prototype.pop);
const arrayPush = unapply(Array.prototype.push);
const arraySplice = unapply(Array.prototype.splice);
const stringToLowerCase = unapply(String.prototype.toLowerCase);
const stringToString = unapply(String.prototype.toString);
const stringMatch = unapply(String.prototype.match);
const stringReplace = unapply(String.prototype.replace);
const stringIndexOf = unapply(String.prototype.indexOf);
const stringTrim = unapply(String.prototype.trim);
const objectHasOwnProperty = unapply(Object.prototype.hasOwnProperty);
const regExpTest = unapply(RegExp.prototype.test);
const typeErrorCreate = unconstruct(TypeError);
/**
 * Creates a new function that calls the given function with a specified thisArg and arguments.
 *
 * @param func - The function to be wrapped and called.
 * @returns A new function that calls the given function with a specified thisArg and arguments.
 */
function unapply(func) {
  return function (thisArg) {
    for (var _len = arguments.length, args = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
      args[_key - 1] = arguments[_key];
    }
    return apply(func, thisArg, args);
  };
}
/**
 * Creates a new function that constructs an instance of the given constructor function with the provided arguments.
 *
 * @param func - The constructor function to be wrapped and called.
 * @returns A new function that constructs an instance of the given constructor function with the provided arguments.
 */
function unconstruct(func) {
  return function () {
    for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
      args[_key2] = arguments[_key2];
    }
    return construct(func, args);
  };
}
/**
 * Add properties to a lookup table
 *
 * @param set - The set to which elements will be added.
 * @param array - The array containing elements to be added to the set.
 * @param transformCaseFunc - An optional function to transform the case of each element before adding to the set.
 * @returns The modified set with added elements.
 */
function addToSet(set, array) {
  let transformCaseFunc = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : stringToLowerCase;
  if (setPrototypeOf) {
    // Make 'in' and truthy checks like Boolean(set.constructor)
    // independent of any properties defined on Object.prototype.
    // Prevent prototype setters from intercepting set as a this value.
    setPrototypeOf(set, null);
  }
  let l = array.length;
  while (l--) {
    let element = array[l];
    if (typeof element === 'string') {
      const lcElement = transformCaseFunc(element);
      if (lcElement !== element) {
        // Config presets (e.g. tags.js, attrs.js) are immutable.
        if (!isFrozen(array)) {
          array[l] = lcElement;
        }
        element = lcElement;
      }
    }
    set[element] = true;
  }
  return set;
}
/**
 * Clean up an array to harden against CSPP
 *
 * @param array - The array to be cleaned.
 * @returns The cleaned version of the array
 */
function cleanArray(array) {
  for (let index = 0; index < array.length; index++) {
    const isPropertyExist = objectHasOwnProperty(array, index);
    if (!isPropertyExist) {
      array[index] = null;
    }
  }
  return array;
}
/**
 * Shallow clone an object
 *
 * @param object - The object to be cloned.
 * @returns A new object that copies the original.
 */
function clone(object) {
  const newObject = create(null);
  for (const [property, value] of entries(object)) {
    const isPropertyExist = objectHasOwnProperty(object, property);
    if (isPropertyExist) {
      if (Array.isArray(value)) {
        newObject[property] = cleanArray(value);
      } else if (value && typeof value === 'object' && value.constructor === Object) {
        newObject[property] = clone(value);
      } else {
        newObject[property] = value;
      }
    }
  }
  return newObject;
}
/**
 * This method automatically checks if the prop is function or getter and behaves accordingly.
 *
 * @param object - The object to look up the getter function in its prototype chain.
 * @param prop - The property name for which to find the getter function.
 * @returns The getter function found in the prototype chain or a fallback function.
 */
function lookupGetter(object, prop) {
  while (object !== null) {
    const desc = getOwnPropertyDescriptor(object, prop);
    if (desc) {
      if (desc.get) {
        return unapply(desc.get);
      }
      if (typeof desc.value === 'function') {
        return unapply(desc.value);
      }
    }
    object = getPrototypeOf(object);
  }
  function fallbackValue() {
    return null;
  }
  return fallbackValue;
}

const html$1 = freeze(['a', 'abbr', 'acronym', 'address', 'area', 'article', 'aside', 'audio', 'b', 'bdi', 'bdo', 'big', 'blink', 'blockquote', 'body', 'br', 'button', 'canvas', 'caption', 'center', 'cite', 'code', 'col', 'colgroup', 'content', 'data', 'datalist', 'dd', 'decorator', 'del', 'details', 'dfn', 'dialog', 'dir', 'div', 'dl', 'dt', 'element', 'em', 'fieldset', 'figcaption', 'figure', 'font', 'footer', 'form', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'head', 'header', 'hgroup', 'hr', 'html', 'i', 'img', 'input', 'ins', 'kbd', 'label', 'legend', 'li', 'main', 'map', 'mark', 'marquee', 'menu', 'menuitem', 'meter', 'nav', 'nobr', 'ol', 'optgroup', 'option', 'output', 'p', 'picture', 'pre', 'progress', 'q', 'rp', 'rt', 'ruby', 's', 'samp', 'section', 'select', 'shadow', 'small', 'source', 'spacer', 'span', 'strike', 'strong', 'style', 'sub', 'summary', 'sup', 'table', 'tbody', 'td', 'template', 'textarea', 'tfoot', 'th', 'thead', 'time', 'tr', 'track', 'tt', 'u', 'ul', 'var', 'video', 'wbr']);
const svg$1 = freeze(['svg', 'a', 'altglyph', 'altglyphdef', 'altglyphitem', 'animatecolor', 'animatemotion', 'animatetransform', 'circle', 'clippath', 'defs', 'desc', 'ellipse', 'filter', 'font', 'g', 'glyph', 'glyphref', 'hkern', 'image', 'line', 'lineargradient', 'marker', 'mask', 'metadata', 'mpath', 'path', 'pattern', 'polygon', 'polyline', 'radialgradient', 'rect', 'stop', 'style', 'switch', 'symbol', 'text', 'textpath', 'title', 'tref', 'tspan', 'view', 'vkern']);
const svgFilters = freeze(['feBlend', 'feColorMatrix', 'feComponentTransfer', 'feComposite', 'feConvolveMatrix', 'feDiffuseLighting', 'feDisplacementMap', 'feDistantLight', 'feDropShadow', 'feFlood', 'feFuncA', 'feFuncB', 'feFuncG', 'feFuncR', 'feGaussianBlur', 'feImage', 'feMerge', 'feMergeNode', 'feMorphology', 'feOffset', 'fePointLight', 'feSpecularLighting', 'feSpotLight', 'feTile', 'feTurbulence']);
// List of SVG elements that are disallowed by default.
// We still need to know them so that we can do namespace
// checks properly in case one wants to add them to
// allow-list.
const svgDisallowed = freeze(['animate', 'color-profile', 'cursor', 'discard', 'font-face', 'font-face-format', 'font-face-name', 'font-face-src', 'font-face-uri', 'foreignobject', 'hatch', 'hatchpath', 'mesh', 'meshgradient', 'meshpatch', 'meshrow', 'missing-glyph', 'script', 'set', 'solidcolor', 'unknown', 'use']);
const mathMl$1 = freeze(['math', 'menclose', 'merror', 'mfenced', 'mfrac', 'mglyph', 'mi', 'mlabeledtr', 'mmultiscripts', 'mn', 'mo', 'mover', 'mpadded', 'mphantom', 'mroot', 'mrow', 'ms', 'mspace', 'msqrt', 'mstyle', 'msub', 'msup', 'msubsup', 'mtable', 'mtd', 'mtext', 'mtr', 'munder', 'munderover', 'mprescripts']);
// Similarly to SVG, we want to know all MathML elements,
// even those that we disallow by default.
const mathMlDisallowed = freeze(['maction', 'maligngroup', 'malignmark', 'mlongdiv', 'mscarries', 'mscarry', 'msgroup', 'mstack', 'msline', 'msrow', 'semantics', 'annotation', 'annotation-xml', 'mprescripts', 'none']);
const text = freeze(['#text']);

const html = freeze(['accept', 'action', 'align', 'alt', 'autocapitalize', 'autocomplete', 'autopictureinpicture', 'autoplay', 'background', 'bgcolor', 'border', 'capture', 'cellpadding', 'cellspacing', 'checked', 'cite', 'class', 'clear', 'color', 'cols', 'colspan', 'controls', 'controlslist', 'coords', 'crossorigin', 'datetime', 'decoding', 'default', 'dir', 'disabled', 'disablepictureinpicture', 'disableremoteplayback', 'download', 'draggable', 'enctype', 'enterkeyhint', 'face', 'for', 'headers', 'height', 'hidden', 'high', 'href', 'hreflang', 'id', 'inputmode', 'integrity', 'ismap', 'kind', 'label', 'lang', 'list', 'loading', 'loop', 'low', 'max', 'maxlength', 'media', 'method', 'min', 'minlength', 'multiple', 'muted', 'name', 'nonce', 'noshade', 'novalidate', 'nowrap', 'open', 'optimum', 'pattern', 'placeholder', 'playsinline', 'popover', 'popovertarget', 'popovertargetaction', 'poster', 'preload', 'pubdate', 'radiogroup', 'readonly', 'rel', 'required', 'rev', 'reversed', 'role', 'rows', 'rowspan', 'spellcheck', 'scope', 'selected', 'shape', 'size', 'sizes', 'span', 'srclang', 'start', 'src', 'srcset', 'step', 'style', 'summary', 'tabindex', 'title', 'translate', 'type', 'usemap', 'valign', 'value', 'width', 'wrap', 'xmlns', 'slot']);
const svg = freeze(['accent-height', 'accumulate', 'additive', 'alignment-baseline', 'amplitude', 'ascent', 'attributename', 'attributetype', 'azimuth', 'basefrequency', 'baseline-shift', 'begin', 'bias', 'by', 'class', 'clip', 'clippathunits', 'clip-path', 'clip-rule', 'color', 'color-interpolation', 'color-interpolation-filters', 'color-profile', 'color-rendering', 'cx', 'cy', 'd', 'dx', 'dy', 'diffuseconstant', 'direction', 'display', 'divisor', 'dur', 'edgemode', 'elevation', 'end', 'exponent', 'fill', 'fill-opacity', 'fill-rule', 'filter', 'filterunits', 'flood-color', 'flood-opacity', 'font-family', 'font-size', 'font-size-adjust', 'font-stretch', 'font-style', 'font-variant', 'font-weight', 'fx', 'fy', 'g1', 'g2', 'glyph-name', 'glyphref', 'gradientunits', 'gradienttransform', 'height', 'href', 'id', 'image-rendering', 'in', 'in2', 'intercept', 'k', 'k1', 'k2', 'k3', 'k4', 'kerning', 'keypoints', 'keysplines', 'keytimes', 'lang', 'lengthadjust', 'letter-spacing', 'kernelmatrix', 'kernelunitlength', 'lighting-color', 'local', 'marker-end', 'marker-mid', 'marker-start', 'markerheight', 'markerunits', 'markerwidth', 'maskcontentunits', 'maskunits', 'max', 'mask', 'media', 'method', 'mode', 'min', 'name', 'numoctaves', 'offset', 'operator', 'opacity', 'order', 'orient', 'orientation', 'origin', 'overflow', 'paint-order', 'path', 'pathlength', 'patterncontentunits', 'patterntransform', 'patternunits', 'points', 'preservealpha', 'preserveaspectratio', 'primitiveunits', 'r', 'rx', 'ry', 'radius', 'refx', 'refy', 'repeatcount', 'repeatdur', 'restart', 'result', 'rotate', 'scale', 'seed', 'shape-rendering', 'slope', 'specularconstant', 'specularexponent', 'spreadmethod', 'startoffset', 'stddeviation', 'stitchtiles', 'stop-color', 'stop-opacity', 'stroke-dasharray', 'stroke-dashoffset', 'stroke-linecap', 'stroke-linejoin', 'stroke-miterlimit', 'stroke-opacity', 'stroke', 'stroke-width', 'style', 'surfacescale', 'systemlanguage', 'tabindex', 'tablevalues', 'targetx', 'targety', 'transform', 'transform-origin', 'text-anchor', 'text-decoration', 'text-rendering', 'textlength', 'type', 'u1', 'u2', 'unicode', 'values', 'viewbox', 'visibility', 'version', 'vert-adv-y', 'vert-origin-x', 'vert-origin-y', 'width', 'word-spacing', 'wrap', 'writing-mode', 'xchannelselector', 'ychannelselector', 'x', 'x1', 'x2', 'xmlns', 'y', 'y1', 'y2', 'z', 'zoomandpan']);
const mathMl = freeze(['accent', 'accentunder', 'align', 'bevelled', 'close', 'columnsalign', 'columnlines', 'columnspan', 'denomalign', 'depth', 'dir', 'display', 'displaystyle', 'encoding', 'fence', 'frame', 'height', 'href', 'id', 'largeop', 'length', 'linethickness', 'lspace', 'lquote', 'mathbackground', 'mathcolor', 'mathsize', 'mathvariant', 'maxsize', 'minsize', 'movablelimits', 'notation', 'numalign', 'open', 'rowalign', 'rowlines', 'rowspacing', 'rowspan', 'rspace', 'rquote', 'scriptlevel', 'scriptminsize', 'scriptsizemultiplier', 'selection', 'separator', 'separators', 'stretchy', 'subscriptshift', 'supscriptshift', 'symmetric', 'voffset', 'width', 'xmlns']);
const xml = freeze(['xlink:href', 'xml:id', 'xlink:title', 'xml:space', 'xmlns:xlink']);

// eslint-disable-next-line unicorn/better-regex
const MUSTACHE_EXPR = seal(/\{\{[\w\W]*|[\w\W]*\}\}/gm); // Specify template detection regex for SAFE_FOR_TEMPLATES mode
const ERB_EXPR = seal(/<%[\w\W]*|[\w\W]*%>/gm);
const TMPLIT_EXPR = seal(/\$\{[\w\W]*/gm); // eslint-disable-line unicorn/better-regex
const DATA_ATTR = seal(/^data-[\-\w.\u00B7-\uFFFF]+$/); // eslint-disable-line no-useless-escape
const ARIA_ATTR = seal(/^aria-[\-\w]+$/); // eslint-disable-line no-useless-escape
const IS_ALLOWED_URI = seal(/^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i // eslint-disable-line no-useless-escape
);
const IS_SCRIPT_OR_DATA = seal(/^(?:\w+script|data):/i);
const ATTR_WHITESPACE = seal(/[\u0000-\u0020\u00A0\u1680\u180E\u2000-\u2029\u205F\u3000]/g // eslint-disable-line no-control-regex
);
const DOCTYPE_NAME = seal(/^html$/i);
const CUSTOM_ELEMENT = seal(/^[a-z][.\w]*(-[.\w]+)+$/i);

var EXPRESSIONS = /*#__PURE__*/Object.freeze({
  __proto__: null,
  ARIA_ATTR: ARIA_ATTR,
  ATTR_WHITESPACE: ATTR_WHITESPACE,
  CUSTOM_ELEMENT: CUSTOM_ELEMENT,
  DATA_ATTR: DATA_ATTR,
  DOCTYPE_NAME: DOCTYPE_NAME,
  ERB_EXPR: ERB_EXPR,
  IS_ALLOWED_URI: IS_ALLOWED_URI,
  IS_SCRIPT_OR_DATA: IS_SCRIPT_OR_DATA,
  MUSTACHE_EXPR: MUSTACHE_EXPR,
  TMPLIT_EXPR: TMPLIT_EXPR
});

/* eslint-disable @typescript-eslint/indent */
// https://developer.mozilla.org/en-US/docs/Web/API/Node/nodeType
const NODE_TYPE = {
  element: 1,
  attribute: 2,
  text: 3,
  cdataSection: 4,
  entityReference: 5,
  // Deprecated
  entityNode: 6,
  // Deprecated
  progressingInstruction: 7,
  comment: 8,
  document: 9,
  documentType: 10,
  documentFragment: 11,
  notation: 12 // Deprecated
};
const getGlobal = function getGlobal() {
  return typeof window === 'undefined' ? null : window;
};
/**
 * Creates a no-op policy for internal use only.
 * Don't export this function outside this module!
 * @param trustedTypes The policy factory.
 * @param purifyHostElement The Script element used to load DOMPurify (to determine policy name suffix).
 * @return The policy created (or null, if Trusted Types
 * are not supported or creating the policy failed).
 */
const _createTrustedTypesPolicy = function _createTrustedTypesPolicy(trustedTypes, purifyHostElement) {
  if (typeof trustedTypes !== 'object' || typeof trustedTypes.createPolicy !== 'function') {
    return null;
  }
  // Allow the callers to control the unique policy name
  // by adding a data-tt-policy-suffix to the script element with the DOMPurify.
  // Policy creation with duplicate names throws in Trusted Types.
  let suffix = null;
  const ATTR_NAME = 'data-tt-policy-suffix';
  if (purifyHostElement && purifyHostElement.hasAttribute(ATTR_NAME)) {
    suffix = purifyHostElement.getAttribute(ATTR_NAME);
  }
  const policyName = 'dompurify' + (suffix ? '#' + suffix : '');
  try {
    return trustedTypes.createPolicy(policyName, {
      createHTML(html) {
        return html;
      },
      createScriptURL(scriptUrl) {
        return scriptUrl;
      }
    });
  } catch (_) {
    // Policy creation failed (most likely another DOMPurify script has
    // already run). Skip creating the policy, as this will only cause errors
    // if TT are enforced.
    console.warn('TrustedTypes policy ' + policyName + ' could not be created.');
    return null;
  }
};
const _createHooksMap = function _createHooksMap() {
  return {
    afterSanitizeAttributes: [],
    afterSanitizeElements: [],
    afterSanitizeShadowDOM: [],
    beforeSanitizeAttributes: [],
    beforeSanitizeElements: [],
    beforeSanitizeShadowDOM: [],
    uponSanitizeAttribute: [],
    uponSanitizeElement: [],
    uponSanitizeShadowNode: []
  };
};
function createDOMPurify() {
  let window = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : getGlobal();
  const DOMPurify = root => createDOMPurify(root);
  DOMPurify.version = '3.2.4';
  DOMPurify.removed = [];
  if (!window || !window.document || window.document.nodeType !== NODE_TYPE.document || !window.Element) {
    // Not running in a browser, provide a factory function
    // so that you can pass your own Window
    DOMPurify.isSupported = false;
    return DOMPurify;
  }
  let {
    document
  } = window;
  const originalDocument = document;
  const currentScript = originalDocument.currentScript;
  const {
    DocumentFragment,
    HTMLTemplateElement,
    Node,
    Element,
    NodeFilter,
    NamedNodeMap = window.NamedNodeMap || window.MozNamedAttrMap,
    HTMLFormElement,
    DOMParser,
    trustedTypes
  } = window;
  const ElementPrototype = Element.prototype;
  const cloneNode = lookupGetter(ElementPrototype, 'cloneNode');
  const remove = lookupGetter(ElementPrototype, 'remove');
  const getNextSibling = lookupGetter(ElementPrototype, 'nextSibling');
  const getChildNodes = lookupGetter(ElementPrototype, 'childNodes');
  const getParentNode = lookupGetter(ElementPrototype, 'parentNode');
  // As per issue #47, the web-components registry is inherited by a
  // new document created via createHTMLDocument. As per the spec
  // (http://w3c.github.io/webcomponents/spec/custom/#creating-and-passing-registries)
  // a new empty registry is used when creating a template contents owner
  // document, so we use that as our parent document to ensure nothing
  // is inherited.
  if (typeof HTMLTemplateElement === 'function') {
    const template = document.createElement('template');
    if (template.content && template.content.ownerDocument) {
      document = template.content.ownerDocument;
    }
  }
  let trustedTypesPolicy;
  let emptyHTML = '';
  const {
    implementation,
    createNodeIterator,
    createDocumentFragment,
    getElementsByTagName
  } = document;
  const {
    importNode
  } = originalDocument;
  let hooks = _createHooksMap();
  /**
   * Expose whether this browser supports running the full DOMPurify.
   */
  DOMPurify.isSupported = typeof entries === 'function' && typeof getParentNode === 'function' && implementation && implementation.createHTMLDocument !== undefined;
  const {
    MUSTACHE_EXPR,
    ERB_EXPR,
    TMPLIT_EXPR,
    DATA_ATTR,
    ARIA_ATTR,
    IS_SCRIPT_OR_DATA,
    ATTR_WHITESPACE,
    CUSTOM_ELEMENT
  } = EXPRESSIONS;
  let {
    IS_ALLOWED_URI: IS_ALLOWED_URI$1
  } = EXPRESSIONS;
  /**
   * We consider the elements and attributes below to be safe. Ideally
   * don't add any new ones but feel free to remove unwanted ones.
   */
  /* allowed element names */
  let ALLOWED_TAGS = null;
  const DEFAULT_ALLOWED_TAGS = addToSet({}, [...html$1, ...svg$1, ...svgFilters, ...mathMl$1, ...text]);
  /* Allowed attribute names */
  let ALLOWED_ATTR = null;
  const DEFAULT_ALLOWED_ATTR = addToSet({}, [...html, ...svg, ...mathMl, ...xml]);
  /*
   * Configure how DOMPurify should handle custom elements and their attributes as well as customized built-in elements.
   * @property {RegExp|Function|null} tagNameCheck one of [null, regexPattern, predicate]. Default: `null` (disallow any custom elements)
   * @property {RegExp|Function|null} attributeNameCheck one of [null, regexPattern, predicate]. Default: `null` (disallow any attributes not on the allow list)
   * @property {boolean} allowCustomizedBuiltInElements allow custom elements derived from built-ins if they pass CUSTOM_ELEMENT_HANDLING.tagNameCheck. Default: `false`.
   */
  let CUSTOM_ELEMENT_HANDLING = Object.seal(create(null, {
    tagNameCheck: {
      writable: true,
      configurable: false,
      enumerable: true,
      value: null
    },
    attributeNameCheck: {
      writable: true,
      configurable: false,
      enumerable: true,
      value: null
    },
    allowCustomizedBuiltInElements: {
      writable: true,
      configurable: false,
      enumerable: true,
      value: false
    }
  }));
  /* Explicitly forbidden tags (overrides ALLOWED_TAGS/ADD_TAGS) */
  let FORBID_TAGS = null;
  /* Explicitly forbidden attributes (overrides ALLOWED_ATTR/ADD_ATTR) */
  let FORBID_ATTR = null;
  /* Decide if ARIA attributes are okay */
  let ALLOW_ARIA_ATTR = true;
  /* Decide if custom data attributes are okay */
  let ALLOW_DATA_ATTR = true;
  /* Decide if unknown protocols are okay */
  let ALLOW_UNKNOWN_PROTOCOLS = false;
  /* Decide if self-closing tags in attributes are allowed.
   * Usually removed due to a mXSS issue in jQuery 3.0 */
  let ALLOW_SELF_CLOSE_IN_ATTR = true;
  /* Output should be safe for common template engines.
   * This means, DOMPurify removes data attributes, mustaches and ERB
   */
  let SAFE_FOR_TEMPLATES = false;
  /* Output should be safe even for XML used within HTML and alike.
   * This means, DOMPurify removes comments when containing risky content.
   */
  let SAFE_FOR_XML = true;
  /* Decide if document with <html>... should be returned */
  let WHOLE_DOCUMENT = false;
  /* Track whether config is already set on this instance of DOMPurify. */
  let SET_CONFIG = false;
  /* Decide if all elements (e.g. style, script) must be children of
   * document.body. By default, browsers might move them to document.head */
  let FORCE_BODY = false;
  /* Decide if a DOM `HTMLBodyElement` should be returned, instead of a html
   * string (or a TrustedHTML object if Trusted Types are supported).
   * If `WHOLE_DOCUMENT` is enabled a `HTMLHtmlElement` will be returned instead
   */
  let RETURN_DOM = false;
  /* Decide if a DOM `DocumentFragment` should be returned, instead of a html
   * string  (or a TrustedHTML object if Trusted Types are supported) */
  let RETURN_DOM_FRAGMENT = false;
  /* Try to return a Trusted Type object instead of a string, return a string in
   * case Trusted Types are not supported  */
  let RETURN_TRUSTED_TYPE = false;
  /* Output should be free from DOM clobbering attacks?
   * This sanitizes markups named with colliding, clobberable built-in DOM APIs.
   */
  let SANITIZE_DOM = true;
  /* Achieve full DOM Clobbering protection by isolating the namespace of named
   * properties and JS variables, mitigating attacks that abuse the HTML/DOM spec rules.
   *
   * HTML/DOM spec rules that enable DOM Clobbering:
   *   - Named Access on Window (§7.3.3)
   *   - DOM Tree Accessors (§3.1.5)
   *   - Form Element Parent-Child Relations (§4.10.3)
   *   - Iframe srcdoc / Nested WindowProxies (§4.8.5)
   *   - HTMLCollection (§4.2.10.2)
   *
   * Namespace isolation is implemented by prefixing `id` and `name` attributes
   * with a constant string, i.e., `user-content-`
   */
  let SANITIZE_NAMED_PROPS = false;
  const SANITIZE_NAMED_PROPS_PREFIX = 'user-content-';
  /* Keep element content when removing element? */
  let KEEP_CONTENT = true;
  /* If a `Node` is passed to sanitize(), then performs sanitization in-place instead
   * of importing it into a new Document and returning a sanitized copy */
  let IN_PLACE = false;
  /* Allow usage of profiles like html, svg and mathMl */
  let USE_PROFILES = {};
  /* Tags to ignore content of when KEEP_CONTENT is true */
  let FORBID_CONTENTS = null;
  const DEFAULT_FORBID_CONTENTS = addToSet({}, ['annotation-xml', 'audio', 'colgroup', 'desc', 'foreignobject', 'head', 'iframe', 'math', 'mi', 'mn', 'mo', 'ms', 'mtext', 'noembed', 'noframes', 'noscript', 'plaintext', 'script', 'style', 'svg', 'template', 'thead', 'title', 'video', 'xmp']);
  /* Tags that are safe for data: URIs */
  let DATA_URI_TAGS = null;
  const DEFAULT_DATA_URI_TAGS = addToSet({}, ['audio', 'video', 'img', 'source', 'image', 'track']);
  /* Attributes safe for values like "javascript:" */
  let URI_SAFE_ATTRIBUTES = null;
  const DEFAULT_URI_SAFE_ATTRIBUTES = addToSet({}, ['alt', 'class', 'for', 'id', 'label', 'name', 'pattern', 'placeholder', 'role', 'summary', 'title', 'value', 'style', 'xmlns']);
  const MATHML_NAMESPACE = 'http://www.w3.org/1998/Math/MathML';
  const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
  const HTML_NAMESPACE = 'http://www.w3.org/1999/xhtml';
  /* Document namespace */
  let NAMESPACE = HTML_NAMESPACE;
  let IS_EMPTY_INPUT = false;
  /* Allowed XHTML+XML namespaces */
  let ALLOWED_NAMESPACES = null;
  const DEFAULT_ALLOWED_NAMESPACES = addToSet({}, [MATHML_NAMESPACE, SVG_NAMESPACE, HTML_NAMESPACE], stringToString);
  let MATHML_TEXT_INTEGRATION_POINTS = addToSet({}, ['mi', 'mo', 'mn', 'ms', 'mtext']);
  let HTML_INTEGRATION_POINTS = addToSet({}, ['annotation-xml']);
  // Certain elements are allowed in both SVG and HTML
  // namespace. We need to specify them explicitly
  // so that they don't get erroneously deleted from
  // HTML namespace.
  const COMMON_SVG_AND_HTML_ELEMENTS = addToSet({}, ['title', 'style', 'font', 'a', 'script']);
  /* Parsing of strict XHTML documents */
  let PARSER_MEDIA_TYPE = null;
  const SUPPORTED_PARSER_MEDIA_TYPES = ['application/xhtml+xml', 'text/html'];
  const DEFAULT_PARSER_MEDIA_TYPE = 'text/html';
  let transformCaseFunc = null;
  /* Keep a reference to config to pass to hooks */
  let CONFIG = null;
  /* Ideally, do not touch anything below this line */
  /* ______________________________________________ */
  const formElement = document.createElement('form');
  const isRegexOrFunction = function isRegexOrFunction(testValue) {
    return testValue instanceof RegExp || testValue instanceof Function;
  };
  /**
   * _parseConfig
   *
   * @param cfg optional config literal
   */
  // eslint-disable-next-line complexity
  const _parseConfig = function _parseConfig() {
    let cfg = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    if (CONFIG && CONFIG === cfg) {
      return;
    }
    /* Shield configuration object from tampering */
    if (!cfg || typeof cfg !== 'object') {
      cfg = {};
    }
    /* Shield configuration object from prototype pollution */
    cfg = clone(cfg);
    PARSER_MEDIA_TYPE =
    // eslint-disable-next-line unicorn/prefer-includes
    SUPPORTED_PARSER_MEDIA_TYPES.indexOf(cfg.PARSER_MEDIA_TYPE) === -1 ? DEFAULT_PARSER_MEDIA_TYPE : cfg.PARSER_MEDIA_TYPE;
    // HTML tags and attributes are not case-sensitive, converting to lowercase. Keeping XHTML as is.
    transformCaseFunc = PARSER_MEDIA_TYPE === 'application/xhtml+xml' ? stringToString : stringToLowerCase;
    /* Set configuration parameters */
    ALLOWED_TAGS = objectHasOwnProperty(cfg, 'ALLOWED_TAGS') ? addToSet({}, cfg.ALLOWED_TAGS, transformCaseFunc) : DEFAULT_ALLOWED_TAGS;
    ALLOWED_ATTR = objectHasOwnProperty(cfg, 'ALLOWED_ATTR') ? addToSet({}, cfg.ALLOWED_ATTR, transformCaseFunc) : DEFAULT_ALLOWED_ATTR;
    ALLOWED_NAMESPACES = objectHasOwnProperty(cfg, 'ALLOWED_NAMESPACES') ? addToSet({}, cfg.ALLOWED_NAMESPACES, stringToString) : DEFAULT_ALLOWED_NAMESPACES;
    URI_SAFE_ATTRIBUTES = objectHasOwnProperty(cfg, 'ADD_URI_SAFE_ATTR') ? addToSet(clone(DEFAULT_URI_SAFE_ATTRIBUTES), cfg.ADD_URI_SAFE_ATTR, transformCaseFunc) : DEFAULT_URI_SAFE_ATTRIBUTES;
    DATA_URI_TAGS = objectHasOwnProperty(cfg, 'ADD_DATA_URI_TAGS') ? addToSet(clone(DEFAULT_DATA_URI_TAGS), cfg.ADD_DATA_URI_TAGS, transformCaseFunc) : DEFAULT_DATA_URI_TAGS;
    FORBID_CONTENTS = objectHasOwnProperty(cfg, 'FORBID_CONTENTS') ? addToSet({}, cfg.FORBID_CONTENTS, transformCaseFunc) : DEFAULT_FORBID_CONTENTS;
    FORBID_TAGS = objectHasOwnProperty(cfg, 'FORBID_TAGS') ? addToSet({}, cfg.FORBID_TAGS, transformCaseFunc) : {};
    FORBID_ATTR = objectHasOwnProperty(cfg, 'FORBID_ATTR') ? addToSet({}, cfg.FORBID_ATTR, transformCaseFunc) : {};
    USE_PROFILES = objectHasOwnProperty(cfg, 'USE_PROFILES') ? cfg.USE_PROFILES : false;
    ALLOW_ARIA_ATTR = cfg.ALLOW_ARIA_ATTR !== false; // Default true
    ALLOW_DATA_ATTR = cfg.ALLOW_DATA_ATTR !== false; // Default true
    ALLOW_UNKNOWN_PROTOCOLS = cfg.ALLOW_UNKNOWN_PROTOCOLS || false; // Default false
    ALLOW_SELF_CLOSE_IN_ATTR = cfg.ALLOW_SELF_CLOSE_IN_ATTR !== false; // Default true
    SAFE_FOR_TEMPLATES = cfg.SAFE_FOR_TEMPLATES || false; // Default false
    SAFE_FOR_XML = cfg.SAFE_FOR_XML !== false; // Default true
    WHOLE_DOCUMENT = cfg.WHOLE_DOCUMENT || false; // Default false
    RETURN_DOM = cfg.RETURN_DOM || false; // Default false
    RETURN_DOM_FRAGMENT = cfg.RETURN_DOM_FRAGMENT || false; // Default false
    RETURN_TRUSTED_TYPE = cfg.RETURN_TRUSTED_TYPE || false; // Default false
    FORCE_BODY = cfg.FORCE_BODY || false; // Default false
    SANITIZE_DOM = cfg.SANITIZE_DOM !== false; // Default true
    SANITIZE_NAMED_PROPS = cfg.SANITIZE_NAMED_PROPS || false; // Default false
    KEEP_CONTENT = cfg.KEEP_CONTENT !== false; // Default true
    IN_PLACE = cfg.IN_PLACE || false; // Default false
    IS_ALLOWED_URI$1 = cfg.ALLOWED_URI_REGEXP || IS_ALLOWED_URI;
    NAMESPACE = cfg.NAMESPACE || HTML_NAMESPACE;
    MATHML_TEXT_INTEGRATION_POINTS = cfg.MATHML_TEXT_INTEGRATION_POINTS || MATHML_TEXT_INTEGRATION_POINTS;
    HTML_INTEGRATION_POINTS = cfg.HTML_INTEGRATION_POINTS || HTML_INTEGRATION_POINTS;
    CUSTOM_ELEMENT_HANDLING = cfg.CUSTOM_ELEMENT_HANDLING || {};
    if (cfg.CUSTOM_ELEMENT_HANDLING && isRegexOrFunction(cfg.CUSTOM_ELEMENT_HANDLING.tagNameCheck)) {
      CUSTOM_ELEMENT_HANDLING.tagNameCheck = cfg.CUSTOM_ELEMENT_HANDLING.tagNameCheck;
    }
    if (cfg.CUSTOM_ELEMENT_HANDLING && isRegexOrFunction(cfg.CUSTOM_ELEMENT_HANDLING.attributeNameCheck)) {
      CUSTOM_ELEMENT_HANDLING.attributeNameCheck = cfg.CUSTOM_ELEMENT_HANDLING.attributeNameCheck;
    }
    if (cfg.CUSTOM_ELEMENT_HANDLING && typeof cfg.CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements === 'boolean') {
      CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements = cfg.CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements;
    }
    if (SAFE_FOR_TEMPLATES) {
      ALLOW_DATA_ATTR = false;
    }
    if (RETURN_DOM_FRAGMENT) {
      RETURN_DOM = true;
    }
    /* Parse profile info */
    if (USE_PROFILES) {
      ALLOWED_TAGS = addToSet({}, text);
      ALLOWED_ATTR = [];
      if (USE_PROFILES.html === true) {
        addToSet(ALLOWED_TAGS, html$1);
        addToSet(ALLOWED_ATTR, html);
      }
      if (USE_PROFILES.svg === true) {
        addToSet(ALLOWED_TAGS, svg$1);
        addToSet(ALLOWED_ATTR, svg);
        addToSet(ALLOWED_ATTR, xml);
      }
      if (USE_PROFILES.svgFilters === true) {
        addToSet(ALLOWED_TAGS, svgFilters);
        addToSet(ALLOWED_ATTR, svg);
        addToSet(ALLOWED_ATTR, xml);
      }
      if (USE_PROFILES.mathMl === true) {
        addToSet(ALLOWED_TAGS, mathMl$1);
        addToSet(ALLOWED_ATTR, mathMl);
        addToSet(ALLOWED_ATTR, xml);
      }
    }
    /* Merge configuration parameters */
    if (cfg.ADD_TAGS) {
      if (ALLOWED_TAGS === DEFAULT_ALLOWED_TAGS) {
        ALLOWED_TAGS = clone(ALLOWED_TAGS);
      }
      addToSet(ALLOWED_TAGS, cfg.ADD_TAGS, transformCaseFunc);
    }
    if (cfg.ADD_ATTR) {
      if (ALLOWED_ATTR === DEFAULT_ALLOWED_ATTR) {
        ALLOWED_ATTR = clone(ALLOWED_ATTR);
      }
      addToSet(ALLOWED_ATTR, cfg.ADD_ATTR, transformCaseFunc);
    }
    if (cfg.ADD_URI_SAFE_ATTR) {
      addToSet(URI_SAFE_ATTRIBUTES, cfg.ADD_URI_SAFE_ATTR, transformCaseFunc);
    }
    if (cfg.FORBID_CONTENTS) {
      if (FORBID_CONTENTS === DEFAULT_FORBID_CONTENTS) {
        FORBID_CONTENTS = clone(FORBID_CONTENTS);
      }
      addToSet(FORBID_CONTENTS, cfg.FORBID_CONTENTS, transformCaseFunc);
    }
    /* Add #text in case KEEP_CONTENT is set to true */
    if (KEEP_CONTENT) {
      ALLOWED_TAGS['#text'] = true;
    }
    /* Add html, head and body to ALLOWED_TAGS in case WHOLE_DOCUMENT is true */
    if (WHOLE_DOCUMENT) {
      addToSet(ALLOWED_TAGS, ['html', 'head', 'body']);
    }
    /* Add tbody to ALLOWED_TAGS in case tables are permitted, see #286, #365 */
    if (ALLOWED_TAGS.table) {
      addToSet(ALLOWED_TAGS, ['tbody']);
      delete FORBID_TAGS.tbody;
    }
    if (cfg.TRUSTED_TYPES_POLICY) {
      if (typeof cfg.TRUSTED_TYPES_POLICY.createHTML !== 'function') {
        throw typeErrorCreate('TRUSTED_TYPES_POLICY configuration option must provide a "createHTML" hook.');
      }
      if (typeof cfg.TRUSTED_TYPES_POLICY.createScriptURL !== 'function') {
        throw typeErrorCreate('TRUSTED_TYPES_POLICY configuration option must provide a "createScriptURL" hook.');
      }
      // Overwrite existing TrustedTypes policy.
      trustedTypesPolicy = cfg.TRUSTED_TYPES_POLICY;
      // Sign local variables required by `sanitize`.
      emptyHTML = trustedTypesPolicy.createHTML('');
    } else {
      // Uninitialized policy, attempt to initialize the internal dompurify policy.
      if (trustedTypesPolicy === undefined) {
        trustedTypesPolicy = _createTrustedTypesPolicy(trustedTypes, currentScript);
      }
      // If creating the internal policy succeeded sign internal variables.
      if (trustedTypesPolicy !== null && typeof emptyHTML === 'string') {
        emptyHTML = trustedTypesPolicy.createHTML('');
      }
    }
    // Prevent further manipulation of configuration.
    // Not available in IE8, Safari 5, etc.
    if (freeze) {
      freeze(cfg);
    }
    CONFIG = cfg;
  };
  /* Keep track of all possible SVG and MathML tags
   * so that we can perform the namespace checks
   * correctly. */
  const ALL_SVG_TAGS = addToSet({}, [...svg$1, ...svgFilters, ...svgDisallowed]);
  const ALL_MATHML_TAGS = addToSet({}, [...mathMl$1, ...mathMlDisallowed]);
  /**
   * @param element a DOM element whose namespace is being checked
   * @returns Return false if the element has a
   *  namespace that a spec-compliant parser would never
   *  return. Return true otherwise.
   */
  const _checkValidNamespace = function _checkValidNamespace(element) {
    let parent = getParentNode(element);
    // In JSDOM, if we're inside shadow DOM, then parentNode
    // can be null. We just simulate parent in this case.
    if (!parent || !parent.tagName) {
      parent = {
        namespaceURI: NAMESPACE,
        tagName: 'template'
      };
    }
    const tagName = stringToLowerCase(element.tagName);
    const parentTagName = stringToLowerCase(parent.tagName);
    if (!ALLOWED_NAMESPACES[element.namespaceURI]) {
      return false;
    }
    if (element.namespaceURI === SVG_NAMESPACE) {
      // The only way to switch from HTML namespace to SVG
      // is via <svg>. If it happens via any other tag, then
      // it should be killed.
      if (parent.namespaceURI === HTML_NAMESPACE) {
        return tagName === 'svg';
      }
      // The only way to switch from MathML to SVG is via`
      // svg if parent is either <annotation-xml> or MathML
      // text integration points.
      if (parent.namespaceURI === MATHML_NAMESPACE) {
        return tagName === 'svg' && (parentTagName === 'annotation-xml' || MATHML_TEXT_INTEGRATION_POINTS[parentTagName]);
      }
      // We only allow elements that are defined in SVG
      // spec. All others are disallowed in SVG namespace.
      return Boolean(ALL_SVG_TAGS[tagName]);
    }
    if (element.namespaceURI === MATHML_NAMESPACE) {
      // The only way to switch from HTML namespace to MathML
      // is via <math>. If it happens via any other tag, then
      // it should be killed.
      if (parent.namespaceURI === HTML_NAMESPACE) {
        return tagName === 'math';
      }
      // The only way to switch from SVG to MathML is via
      // <math> and HTML integration points
      if (parent.namespaceURI === SVG_NAMESPACE) {
        return tagName === 'math' && HTML_INTEGRATION_POINTS[parentTagName];
      }
      // We only allow elements that are defined in MathML
      // spec. All others are disallowed in MathML namespace.
      return Boolean(ALL_MATHML_TAGS[tagName]);
    }
    if (element.namespaceURI === HTML_NAMESPACE) {
      // The only way to switch from SVG to HTML is via
      // HTML integration points, and from MathML to HTML
      // is via MathML text integration points
      if (parent.namespaceURI === SVG_NAMESPACE && !HTML_INTEGRATION_POINTS[parentTagName]) {
        return false;
      }
      if (parent.namespaceURI === MATHML_NAMESPACE && !MATHML_TEXT_INTEGRATION_POINTS[parentTagName]) {
        return false;
      }
      // We disallow tags that are specific for MathML
      // or SVG and should never appear in HTML namespace
      return !ALL_MATHML_TAGS[tagName] && (COMMON_SVG_AND_HTML_ELEMENTS[tagName] || !ALL_SVG_TAGS[tagName]);
    }
    // For XHTML and XML documents that support custom namespaces
    if (PARSER_MEDIA_TYPE === 'application/xhtml+xml' && ALLOWED_NAMESPACES[element.namespaceURI]) {
      return true;
    }
    // The code should never reach this place (this means
    // that the element somehow got namespace that is not
    // HTML, SVG, MathML or allowed via ALLOWED_NAMESPACES).
    // Return false just in case.
    return false;
  };
  /**
   * _forceRemove
   *
   * @param node a DOM node
   */
  const _forceRemove = function _forceRemove(node) {
    arrayPush(DOMPurify.removed, {
      element: node
    });
    try {
      // eslint-disable-next-line unicorn/prefer-dom-node-remove
      getParentNode(node).removeChild(node);
    } catch (_) {
      remove(node);
    }
  };
  /**
   * _removeAttribute
   *
   * @param name an Attribute name
   * @param element a DOM node
   */
  const _removeAttribute = function _removeAttribute(name, element) {
    try {
      arrayPush(DOMPurify.removed, {
        attribute: element.getAttributeNode(name),
        from: element
      });
    } catch (_) {
      arrayPush(DOMPurify.removed, {
        attribute: null,
        from: element
      });
    }
    element.removeAttribute(name);
    // We void attribute values for unremovable "is" attributes
    if (name === 'is') {
      if (RETURN_DOM || RETURN_DOM_FRAGMENT) {
        try {
          _forceRemove(element);
        } catch (_) {}
      } else {
        try {
          element.setAttribute(name, '');
        } catch (_) {}
      }
    }
  };
  /**
   * _initDocument
   *
   * @param dirty - a string of dirty markup
   * @return a DOM, filled with the dirty markup
   */
  const _initDocument = function _initDocument(dirty) {
    /* Create a HTML document */
    let doc = null;
    let leadingWhitespace = null;
    if (FORCE_BODY) {
      dirty = '<remove></remove>' + dirty;
    } else {
      /* If FORCE_BODY isn't used, leading whitespace needs to be preserved manually */
      const matches = stringMatch(dirty, /^[\r\n\t ]+/);
      leadingWhitespace = matches && matches[0];
    }
    if (PARSER_MEDIA_TYPE === 'application/xhtml+xml' && NAMESPACE === HTML_NAMESPACE) {
      // Root of XHTML doc must contain xmlns declaration (see https://www.w3.org/TR/xhtml1/normative.html#strict)
      dirty = '<html xmlns="http://www.w3.org/1999/xhtml"><head></head><body>' + dirty + '</body></html>';
    }
    const dirtyPayload = trustedTypesPolicy ? trustedTypesPolicy.createHTML(dirty) : dirty;
    /*
     * Use the DOMParser API by default, fallback later if needs be
     * DOMParser not work for svg when has multiple root element.
     */
    if (NAMESPACE === HTML_NAMESPACE) {
      try {
        doc = new DOMParser().parseFromString(dirtyPayload, PARSER_MEDIA_TYPE);
      } catch (_) {}
    }
    /* Use createHTMLDocument in case DOMParser is not available */
    if (!doc || !doc.documentElement) {
      doc = implementation.createDocument(NAMESPACE, 'template', null);
      try {
        doc.documentElement.innerHTML = IS_EMPTY_INPUT ? emptyHTML : dirtyPayload;
      } catch (_) {
        // Syntax error if dirtyPayload is invalid xml
      }
    }
    const body = doc.body || doc.documentElement;
    if (dirty && leadingWhitespace) {
      body.insertBefore(document.createTextNode(leadingWhitespace), body.childNodes[0] || null);
    }
    /* Work on whole document or just its body */
    if (NAMESPACE === HTML_NAMESPACE) {
      return getElementsByTagName.call(doc, WHOLE_DOCUMENT ? 'html' : 'body')[0];
    }
    return WHOLE_DOCUMENT ? doc.documentElement : body;
  };
  /**
   * Creates a NodeIterator object that you can use to traverse filtered lists of nodes or elements in a document.
   *
   * @param root The root element or node to start traversing on.
   * @return The created NodeIterator
   */
  const _createNodeIterator = function _createNodeIterator(root) {
    return createNodeIterator.call(root.ownerDocument || root, root,
    // eslint-disable-next-line no-bitwise
    NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_COMMENT | NodeFilter.SHOW_TEXT | NodeFilter.SHOW_PROCESSING_INSTRUCTION | NodeFilter.SHOW_CDATA_SECTION, null);
  };
  /**
   * _isClobbered
   *
   * @param element element to check for clobbering attacks
   * @return true if clobbered, false if safe
   */
  const _isClobbered = function _isClobbered(element) {
    return element instanceof HTMLFormElement && (typeof element.nodeName !== 'string' || typeof element.textContent !== 'string' || typeof element.removeChild !== 'function' || !(element.attributes instanceof NamedNodeMap) || typeof element.removeAttribute !== 'function' || typeof element.setAttribute !== 'function' || typeof element.namespaceURI !== 'string' || typeof element.insertBefore !== 'function' || typeof element.hasChildNodes !== 'function');
  };
  /**
   * Checks whether the given object is a DOM node.
   *
   * @param value object to check whether it's a DOM node
   * @return true is object is a DOM node
   */
  const _isNode = function _isNode(value) {
    return typeof Node === 'function' && value instanceof Node;
  };
  function _executeHooks(hooks, currentNode, data) {
    arrayForEach(hooks, hook => {
      hook.call(DOMPurify, currentNode, data, CONFIG);
    });
  }
  /**
   * _sanitizeElements
   *
   * @protect nodeName
   * @protect textContent
   * @protect removeChild
   * @param currentNode to check for permission to exist
   * @return true if node was killed, false if left alive
   */
  const _sanitizeElements = function _sanitizeElements(currentNode) {
    let content = null;
    /* Execute a hook if present */
    _executeHooks(hooks.beforeSanitizeElements, currentNode, null);
    /* Check if element is clobbered or can clobber */
    if (_isClobbered(currentNode)) {
      _forceRemove(currentNode);
      return true;
    }
    /* Now let's check the element's type and name */
    const tagName = transformCaseFunc(currentNode.nodeName);
    /* Execute a hook if present */
    _executeHooks(hooks.uponSanitizeElement, currentNode, {
      tagName,
      allowedTags: ALLOWED_TAGS
    });
    /* Detect mXSS attempts abusing namespace confusion */
    if (currentNode.hasChildNodes() && !_isNode(currentNode.firstElementChild) && regExpTest(/<[/\w]/g, currentNode.innerHTML) && regExpTest(/<[/\w]/g, currentNode.textContent)) {
      _forceRemove(currentNode);
      return true;
    }
    /* Remove any occurrence of processing instructions */
    if (currentNode.nodeType === NODE_TYPE.progressingInstruction) {
      _forceRemove(currentNode);
      return true;
    }
    /* Remove any kind of possibly harmful comments */
    if (SAFE_FOR_XML && currentNode.nodeType === NODE_TYPE.comment && regExpTest(/<[/\w]/g, currentNode.data)) {
      _forceRemove(currentNode);
      return true;
    }
    /* Remove element if anything forbids its presence */
    if (!ALLOWED_TAGS[tagName] || FORBID_TAGS[tagName]) {
      /* Check if we have a custom element to handle */
      if (!FORBID_TAGS[tagName] && _isBasicCustomElement(tagName)) {
        if (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.tagNameCheck, tagName)) {
          return false;
        }
        if (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.tagNameCheck(tagName)) {
          return false;
        }
      }
      /* Keep content except for bad-listed elements */
      if (KEEP_CONTENT && !FORBID_CONTENTS[tagName]) {
        const parentNode = getParentNode(currentNode) || currentNode.parentNode;
        const childNodes = getChildNodes(currentNode) || currentNode.childNodes;
        if (childNodes && parentNode) {
          const childCount = childNodes.length;
          for (let i = childCount - 1; i >= 0; --i) {
            const childClone = cloneNode(childNodes[i], true);
            childClone.__removalCount = (currentNode.__removalCount || 0) + 1;
            parentNode.insertBefore(childClone, getNextSibling(currentNode));
          }
        }
      }
      _forceRemove(currentNode);
      return true;
    }
    /* Check whether element has a valid namespace */
    if (currentNode instanceof Element && !_checkValidNamespace(currentNode)) {
      _forceRemove(currentNode);
      return true;
    }
    /* Make sure that older browsers don't get fallback-tag mXSS */
    if ((tagName === 'noscript' || tagName === 'noembed' || tagName === 'noframes') && regExpTest(/<\/no(script|embed|frames)/i, currentNode.innerHTML)) {
      _forceRemove(currentNode);
      return true;
    }
    /* Sanitize element content to be template-safe */
    if (SAFE_FOR_TEMPLATES && currentNode.nodeType === NODE_TYPE.text) {
      /* Get the element's text content */
      content = currentNode.textContent;
      arrayForEach([MUSTACHE_EXPR, ERB_EXPR, TMPLIT_EXPR], expr => {
        content = stringReplace(content, expr, ' ');
      });
      if (currentNode.textContent !== content) {
        arrayPush(DOMPurify.removed, {
          element: currentNode.cloneNode()
        });
        currentNode.textContent = content;
      }
    }
    /* Execute a hook if present */
    _executeHooks(hooks.afterSanitizeElements, currentNode, null);
    return false;
  };
  /**
   * _isValidAttribute
   *
   * @param lcTag Lowercase tag name of containing element.
   * @param lcName Lowercase attribute name.
   * @param value Attribute value.
   * @return Returns true if `value` is valid, otherwise false.
   */
  // eslint-disable-next-line complexity
  const _isValidAttribute = function _isValidAttribute(lcTag, lcName, value) {
    /* Make sure attribute cannot clobber */
    if (SANITIZE_DOM && (lcName === 'id' || lcName === 'name') && (value in document || value in formElement)) {
      return false;
    }
    /* Allow valid data-* attributes: At least one character after "-"
        (https://html.spec.whatwg.org/multipage/dom.html#embedding-custom-non-visible-data-with-the-data-*-attributes)
        XML-compatible (https://html.spec.whatwg.org/multipage/infrastructure.html#xml-compatible and http://www.w3.org/TR/xml/#d0e804)
        We don't need to check the value; it's always URI safe. */
    if (ALLOW_DATA_ATTR && !FORBID_ATTR[lcName] && regExpTest(DATA_ATTR, lcName)) ; else if (ALLOW_ARIA_ATTR && regExpTest(ARIA_ATTR, lcName)) ; else if (!ALLOWED_ATTR[lcName] || FORBID_ATTR[lcName]) {
      if (
      // First condition does a very basic check if a) it's basically a valid custom element tagname AND
      // b) if the tagName passes whatever the user has configured for CUSTOM_ELEMENT_HANDLING.tagNameCheck
      // and c) if the attribute name passes whatever the user has configured for CUSTOM_ELEMENT_HANDLING.attributeNameCheck
      _isBasicCustomElement(lcTag) && (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.tagNameCheck, lcTag) || CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.tagNameCheck(lcTag)) && (CUSTOM_ELEMENT_HANDLING.attributeNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.attributeNameCheck, lcName) || CUSTOM_ELEMENT_HANDLING.attributeNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.attributeNameCheck(lcName)) ||
      // Alternative, second condition checks if it's an `is`-attribute, AND
      // the value passes whatever the user has configured for CUSTOM_ELEMENT_HANDLING.tagNameCheck
      lcName === 'is' && CUSTOM_ELEMENT_HANDLING.allowCustomizedBuiltInElements && (CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof RegExp && regExpTest(CUSTOM_ELEMENT_HANDLING.tagNameCheck, value) || CUSTOM_ELEMENT_HANDLING.tagNameCheck instanceof Function && CUSTOM_ELEMENT_HANDLING.tagNameCheck(value))) ; else {
        return false;
      }
      /* Check value is safe. First, is attr inert? If so, is safe */
    } else if (URI_SAFE_ATTRIBUTES[lcName]) ; else if (regExpTest(IS_ALLOWED_URI$1, stringReplace(value, ATTR_WHITESPACE, ''))) ; else if ((lcName === 'src' || lcName === 'xlink:href' || lcName === 'href') && lcTag !== 'script' && stringIndexOf(value, 'data:') === 0 && DATA_URI_TAGS[lcTag]) ; else if (ALLOW_UNKNOWN_PROTOCOLS && !regExpTest(IS_SCRIPT_OR_DATA, stringReplace(value, ATTR_WHITESPACE, ''))) ; else if (value) {
      return false;
    } else ;
    return true;
  };
  /**
   * _isBasicCustomElement
   * checks if at least one dash is included in tagName, and it's not the first char
   * for more sophisticated checking see https://github.com/sindresorhus/validate-element-name
   *
   * @param tagName name of the tag of the node to sanitize
   * @returns Returns true if the tag name meets the basic criteria for a custom element, otherwise false.
   */
  const _isBasicCustomElement = function _isBasicCustomElement(tagName) {
    return tagName !== 'annotation-xml' && stringMatch(tagName, CUSTOM_ELEMENT);
  };
  /**
   * _sanitizeAttributes
   *
   * @protect attributes
   * @protect nodeName
   * @protect removeAttribute
   * @protect setAttribute
   *
   * @param currentNode to sanitize
   */
  const _sanitizeAttributes = function _sanitizeAttributes(currentNode) {
    /* Execute a hook if present */
    _executeHooks(hooks.beforeSanitizeAttributes, currentNode, null);
    const {
      attributes
    } = currentNode;
    /* Check if we have attributes; if not we might have a text node */
    if (!attributes || _isClobbered(currentNode)) {
      return;
    }
    const hookEvent = {
      attrName: '',
      attrValue: '',
      keepAttr: true,
      allowedAttributes: ALLOWED_ATTR,
      forceKeepAttr: undefined
    };
    let l = attributes.length;
    /* Go backwards over all attributes; safely remove bad ones */
    while (l--) {
      const attr = attributes[l];
      const {
        name,
        namespaceURI,
        value: attrValue
      } = attr;
      const lcName = transformCaseFunc(name);
      let value = name === 'value' ? attrValue : stringTrim(attrValue);
      /* Execute a hook if present */
      hookEvent.attrName = lcName;
      hookEvent.attrValue = value;
      hookEvent.keepAttr = true;
      hookEvent.forceKeepAttr = undefined; // Allows developers to see this is a property they can set
      _executeHooks(hooks.uponSanitizeAttribute, currentNode, hookEvent);
      value = hookEvent.attrValue;
      /* Full DOM Clobbering protection via namespace isolation,
       * Prefix id and name attributes with `user-content-`
       */
      if (SANITIZE_NAMED_PROPS && (lcName === 'id' || lcName === 'name')) {
        // Remove the attribute with this value
        _removeAttribute(name, currentNode);
        // Prefix the value and later re-create the attribute with the sanitized value
        value = SANITIZE_NAMED_PROPS_PREFIX + value;
      }
      /* Work around a security issue with comments inside attributes */
      if (SAFE_FOR_XML && regExpTest(/((--!?|])>)|<\/(style|title)/i, value)) {
        _removeAttribute(name, currentNode);
        continue;
      }
      /* Did the hooks approve of the attribute? */
      if (hookEvent.forceKeepAttr) {
        continue;
      }
      /* Remove attribute */
      _removeAttribute(name, currentNode);
      /* Did the hooks approve of the attribute? */
      if (!hookEvent.keepAttr) {
        continue;
      }
      /* Work around a security issue in jQuery 3.0 */
      if (!ALLOW_SELF_CLOSE_IN_ATTR && regExpTest(/\/>/i, value)) {
        _removeAttribute(name, currentNode);
        continue;
      }
      /* Sanitize attribute content to be template-safe */
      if (SAFE_FOR_TEMPLATES) {
        arrayForEach([MUSTACHE_EXPR, ERB_EXPR, TMPLIT_EXPR], expr => {
          value = stringReplace(value, expr, ' ');
        });
      }
      /* Is `value` valid for this attribute? */
      const lcTag = transformCaseFunc(currentNode.nodeName);
      if (!_isValidAttribute(lcTag, lcName, value)) {
        continue;
      }
      /* Handle attributes that require Trusted Types */
      if (trustedTypesPolicy && typeof trustedTypes === 'object' && typeof trustedTypes.getAttributeType === 'function') {
        if (namespaceURI) ; else {
          switch (trustedTypes.getAttributeType(lcTag, lcName)) {
            case 'TrustedHTML':
              {
                value = trustedTypesPolicy.createHTML(value);
                break;
              }
            case 'TrustedScriptURL':
              {
                value = trustedTypesPolicy.createScriptURL(value);
                break;
              }
          }
        }
      }
      /* Handle invalid data-* attribute set by try-catching it */
      try {
        if (namespaceURI) {
          currentNode.setAttributeNS(namespaceURI, name, value);
        } else {
          /* Fallback to setAttribute() for browser-unrecognized namespaces e.g. "x-schema". */
          currentNode.setAttribute(name, value);
        }
        if (_isClobbered(currentNode)) {
          _forceRemove(currentNode);
        } else {
          arrayPop(DOMPurify.removed);
        }
      } catch (_) {}
    }
    /* Execute a hook if present */
    _executeHooks(hooks.afterSanitizeAttributes, currentNode, null);
  };
  /**
   * _sanitizeShadowDOM
   *
   * @param fragment to iterate over recursively
   */
  const _sanitizeShadowDOM = function _sanitizeShadowDOM(fragment) {
    let shadowNode = null;
    const shadowIterator = _createNodeIterator(fragment);
    /* Execute a hook if present */
    _executeHooks(hooks.beforeSanitizeShadowDOM, fragment, null);
    while (shadowNode = shadowIterator.nextNode()) {
      /* Execute a hook if present */
      _executeHooks(hooks.uponSanitizeShadowNode, shadowNode, null);
      /* Sanitize tags and elements */
      _sanitizeElements(shadowNode);
      /* Check attributes next */
      _sanitizeAttributes(shadowNode);
      /* Deep shadow DOM detected */
      if (shadowNode.content instanceof DocumentFragment) {
        _sanitizeShadowDOM(shadowNode.content);
      }
    }
    /* Execute a hook if present */
    _executeHooks(hooks.afterSanitizeShadowDOM, fragment, null);
  };
  // eslint-disable-next-line complexity
  DOMPurify.sanitize = function (dirty) {
    let cfg = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    let body = null;
    let importedNode = null;
    let currentNode = null;
    let returnNode = null;
    /* Make sure we have a string to sanitize.
      DO NOT return early, as this will return the wrong type if
      the user has requested a DOM object rather than a string */
    IS_EMPTY_INPUT = !dirty;
    if (IS_EMPTY_INPUT) {
      dirty = '<!-->';
    }
    /* Stringify, in case dirty is an object */
    if (typeof dirty !== 'string' && !_isNode(dirty)) {
      if (typeof dirty.toString === 'function') {
        dirty = dirty.toString();
        if (typeof dirty !== 'string') {
          throw typeErrorCreate('dirty is not a string, aborting');
        }
      } else {
        throw typeErrorCreate('toString is not a function');
      }
    }
    /* Return dirty HTML if DOMPurify cannot run */
    if (!DOMPurify.isSupported) {
      return dirty;
    }
    /* Assign config vars */
    if (!SET_CONFIG) {
      _parseConfig(cfg);
    }
    /* Clean up removed elements */
    DOMPurify.removed = [];
    /* Check if dirty is correctly typed for IN_PLACE */
    if (typeof dirty === 'string') {
      IN_PLACE = false;
    }
    if (IN_PLACE) {
      /* Do some early pre-sanitization to avoid unsafe root nodes */
      if (dirty.nodeName) {
        const tagName = transformCaseFunc(dirty.nodeName);
        if (!ALLOWED_TAGS[tagName] || FORBID_TAGS[tagName]) {
          throw typeErrorCreate('root node is forbidden and cannot be sanitized in-place');
        }
      }
    } else if (dirty instanceof Node) {
      /* If dirty is a DOM element, append to an empty document to avoid
         elements being stripped by the parser */
      body = _initDocument('<!---->');
      importedNode = body.ownerDocument.importNode(dirty, true);
      if (importedNode.nodeType === NODE_TYPE.element && importedNode.nodeName === 'BODY') {
        /* Node is already a body, use as is */
        body = importedNode;
      } else if (importedNode.nodeName === 'HTML') {
        body = importedNode;
      } else {
        // eslint-disable-next-line unicorn/prefer-dom-node-append
        body.appendChild(importedNode);
      }
    } else {
      /* Exit directly if we have nothing to do */
      if (!RETURN_DOM && !SAFE_FOR_TEMPLATES && !WHOLE_DOCUMENT &&
      // eslint-disable-next-line unicorn/prefer-includes
      dirty.indexOf('<') === -1) {
        return trustedTypesPolicy && RETURN_TRUSTED_TYPE ? trustedTypesPolicy.createHTML(dirty) : dirty;
      }
      /* Initialize the document to work on */
      body = _initDocument(dirty);
      /* Check we have a DOM node from the data */
      if (!body) {
        return RETURN_DOM ? null : RETURN_TRUSTED_TYPE ? emptyHTML : '';
      }
    }
    /* Remove first element node (ours) if FORCE_BODY is set */
    if (body && FORCE_BODY) {
      _forceRemove(body.firstChild);
    }
    /* Get node iterator */
    const nodeIterator = _createNodeIterator(IN_PLACE ? dirty : body);
    /* Now start iterating over the created document */
    while (currentNode = nodeIterator.nextNode()) {
      /* Sanitize tags and elements */
      _sanitizeElements(currentNode);
      /* Check attributes next */
      _sanitizeAttributes(currentNode);
      /* Shadow DOM detected, sanitize it */
      if (currentNode.content instanceof DocumentFragment) {
        _sanitizeShadowDOM(currentNode.content);
      }
    }
    /* If we sanitized `dirty` in-place, return it. */
    if (IN_PLACE) {
      return dirty;
    }
    /* Return sanitized string or DOM */
    if (RETURN_DOM) {
      if (RETURN_DOM_FRAGMENT) {
        returnNode = createDocumentFragment.call(body.ownerDocument);
        while (body.firstChild) {
          // eslint-disable-next-line unicorn/prefer-dom-node-append
          returnNode.appendChild(body.firstChild);
        }
      } else {
        returnNode = body;
      }
      if (ALLOWED_ATTR.shadowroot || ALLOWED_ATTR.shadowrootmode) {
        /*
          AdoptNode() is not used because internal state is not reset
          (e.g. the past names map of a HTMLFormElement), this is safe
          in theory but we would rather not risk another attack vector.
          The state that is cloned by importNode() is explicitly defined
          by the specs.
        */
        returnNode = importNode.call(originalDocument, returnNode, true);
      }
      return returnNode;
    }
    let serializedHTML = WHOLE_DOCUMENT ? body.outerHTML : body.innerHTML;
    /* Serialize doctype if allowed */
    if (WHOLE_DOCUMENT && ALLOWED_TAGS['!doctype'] && body.ownerDocument && body.ownerDocument.doctype && body.ownerDocument.doctype.name && regExpTest(DOCTYPE_NAME, body.ownerDocument.doctype.name)) {
      serializedHTML = '<!DOCTYPE ' + body.ownerDocument.doctype.name + '>\n' + serializedHTML;
    }
    /* Sanitize final string template-safe */
    if (SAFE_FOR_TEMPLATES) {
      arrayForEach([MUSTACHE_EXPR, ERB_EXPR, TMPLIT_EXPR], expr => {
        serializedHTML = stringReplace(serializedHTML, expr, ' ');
      });
    }
    return trustedTypesPolicy && RETURN_TRUSTED_TYPE ? trustedTypesPolicy.createHTML(serializedHTML) : serializedHTML;
  };
  DOMPurify.setConfig = function () {
    let cfg = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    _parseConfig(cfg);
    SET_CONFIG = true;
  };
  DOMPurify.clearConfig = function () {
    CONFIG = null;
    SET_CONFIG = false;
  };
  DOMPurify.isValidAttribute = function (tag, attr, value) {
    /* Initialize shared config vars if necessary. */
    if (!CONFIG) {
      _parseConfig({});
    }
    const lcTag = transformCaseFunc(tag);
    const lcName = transformCaseFunc(attr);
    return _isValidAttribute(lcTag, lcName, value);
  };
  DOMPurify.addHook = function (entryPoint, hookFunction) {
    if (typeof hookFunction !== 'function') {
      return;
    }
    arrayPush(hooks[entryPoint], hookFunction);
  };
  DOMPurify.removeHook = function (entryPoint, hookFunction) {
    if (hookFunction !== undefined) {
      const index = arrayLastIndexOf(hooks[entryPoint], hookFunction);
      return index === -1 ? undefined : arraySplice(hooks[entryPoint], index, 1)[0];
    }
    return arrayPop(hooks[entryPoint]);
  };
  DOMPurify.removeHooks = function (entryPoint) {
    hooks[entryPoint] = [];
  };
  DOMPurify.removeAllHooks = function () {
    hooks = _createHooksMap();
  };
  return DOMPurify;
}
var purify = createDOMPurify();

/**
 * MarkdownTextElement view logic.
 */
function markdownTextTemplate(customElementClass) {
    const { markdown, shouldRemovePadding, sanitizeHTML } = customElementClass;
    let processedMarkdown = processMarkdown(markdown);
    if (sanitizeHTML) {
        processedMarkdown = purify.sanitize(processedMarkdown);
    }
    const classNames = shouldRemovePadding
        ? 'cds-aichat-markdown-text'
        : 'cds-aichat-markdown-text cds-aichat-markdown-text--padded';
    return html$2 `<div class="${classNames}">${unsafeHTML(processedMarkdown)}</div>`;
}

/**
 * Constructed class functionality for the MarkdownText custom element
 */
let MarkdownTextElement = class MarkdownTextElement extends LitElement {
    static { this.styles = css `
    ${unsafeCSS(css_248z$8)}
  `; }
    /**
     * Renders the template while passing in class functionality
     */
    render() {
        return markdownTextTemplate(this);
    }
};
__decorate([
    property({ type: String, attribute: 'markdown', reflect: true })
], MarkdownTextElement.prototype, "markdown", void 0);
__decorate([
    property({ type: Boolean, attribute: 'sanitize-html', reflect: true })
], MarkdownTextElement.prototype, "sanitizeHTML", void 0);
__decorate([
    property({ type: Boolean, attribute: 'should-remove-padding', reflect: true })
], MarkdownTextElement.prototype, "shouldRemovePadding", void 0);
MarkdownTextElement = __decorate([
    carbonElement('cds-aichat-markdown-text')
], MarkdownTextElement);
var MarkdownTextElement$1 = MarkdownTextElement;

const MarkdownText = o$1({
    tagName: 'cds-aichat-markdown-text',
    elementClass: MarkdownTextElement$1,
    react: React__default,
});

var bindDecorator = {};

Object.defineProperty(bindDecorator, "__esModule", { value: true });
var constants;
(function (constants) {
    constants.typeOfFunction = 'function';
    constants.boolTrue = true;
})(constants || (constants = {}));
function bind(target, propertyKey, descriptor) {
    if (!descriptor || (typeof descriptor.value !== constants.typeOfFunction)) {
        throw new TypeError("Only methods can be decorated with @bind. <" + propertyKey + "> is not a method!");
    }
    return {
        configurable: constants.boolTrue,
        get: function () {
            var bound = descriptor.value.bind(this);
            // Credits to https://github.com/andreypopp/autobind-decorator for memoizing the result of bind against a symbol on the instance.
            Object.defineProperty(this, propertyKey, {
                value: bound,
                configurable: constants.boolTrue,
                writable: constants.boolTrue
            });
            return bound;
        }
    };
}
var bind_1 = bindDecorator.bind = bind;
var _default = bindDecorator.default = bind;

var global$1 = (typeof global !== "undefined" ? global :
            typeof self !== "undefined" ? self :
            typeof window !== "undefined" ? window : {});

var lookup = [];
var revLookup = [];
var Arr = typeof Uint8Array !== 'undefined' ? Uint8Array : Array;
var inited = false;
function init () {
  inited = true;
  var code = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  for (var i = 0, len = code.length; i < len; ++i) {
    lookup[i] = code[i];
    revLookup[code.charCodeAt(i)] = i;
  }

  revLookup['-'.charCodeAt(0)] = 62;
  revLookup['_'.charCodeAt(0)] = 63;
}

function toByteArray (b64) {
  if (!inited) {
    init();
  }
  var i, j, l, tmp, placeHolders, arr;
  var len = b64.length;

  if (len % 4 > 0) {
    throw new Error('Invalid string. Length must be a multiple of 4')
  }

  // the number of equal signs (place holders)
  // if there are two placeholders, than the two characters before it
  // represent one byte
  // if there is only one, then the three characters before it represent 2 bytes
  // this is just a cheap hack to not do indexOf twice
  placeHolders = b64[len - 2] === '=' ? 2 : b64[len - 1] === '=' ? 1 : 0;

  // base64 is 4/3 + up to two characters of the original data
  arr = new Arr(len * 3 / 4 - placeHolders);

  // if there are placeholders, only get up to the last complete 4 chars
  l = placeHolders > 0 ? len - 4 : len;

  var L = 0;

  for (i = 0, j = 0; i < l; i += 4, j += 3) {
    tmp = (revLookup[b64.charCodeAt(i)] << 18) | (revLookup[b64.charCodeAt(i + 1)] << 12) | (revLookup[b64.charCodeAt(i + 2)] << 6) | revLookup[b64.charCodeAt(i + 3)];
    arr[L++] = (tmp >> 16) & 0xFF;
    arr[L++] = (tmp >> 8) & 0xFF;
    arr[L++] = tmp & 0xFF;
  }

  if (placeHolders === 2) {
    tmp = (revLookup[b64.charCodeAt(i)] << 2) | (revLookup[b64.charCodeAt(i + 1)] >> 4);
    arr[L++] = tmp & 0xFF;
  } else if (placeHolders === 1) {
    tmp = (revLookup[b64.charCodeAt(i)] << 10) | (revLookup[b64.charCodeAt(i + 1)] << 4) | (revLookup[b64.charCodeAt(i + 2)] >> 2);
    arr[L++] = (tmp >> 8) & 0xFF;
    arr[L++] = tmp & 0xFF;
  }

  return arr
}

function tripletToBase64 (num) {
  return lookup[num >> 18 & 0x3F] + lookup[num >> 12 & 0x3F] + lookup[num >> 6 & 0x3F] + lookup[num & 0x3F]
}

function encodeChunk (uint8, start, end) {
  var tmp;
  var output = [];
  for (var i = start; i < end; i += 3) {
    tmp = (uint8[i] << 16) + (uint8[i + 1] << 8) + (uint8[i + 2]);
    output.push(tripletToBase64(tmp));
  }
  return output.join('')
}

function fromByteArray (uint8) {
  if (!inited) {
    init();
  }
  var tmp;
  var len = uint8.length;
  var extraBytes = len % 3; // if we have 1 byte left, pad 2 bytes
  var output = '';
  var parts = [];
  var maxChunkLength = 16383; // must be multiple of 3

  // go through the array every three bytes, we'll deal with trailing stuff later
  for (var i = 0, len2 = len - extraBytes; i < len2; i += maxChunkLength) {
    parts.push(encodeChunk(uint8, i, (i + maxChunkLength) > len2 ? len2 : (i + maxChunkLength)));
  }

  // pad the end with zeros, but make sure to not forget the extra bytes
  if (extraBytes === 1) {
    tmp = uint8[len - 1];
    output += lookup[tmp >> 2];
    output += lookup[(tmp << 4) & 0x3F];
    output += '==';
  } else if (extraBytes === 2) {
    tmp = (uint8[len - 2] << 8) + (uint8[len - 1]);
    output += lookup[tmp >> 10];
    output += lookup[(tmp >> 4) & 0x3F];
    output += lookup[(tmp << 2) & 0x3F];
    output += '=';
  }

  parts.push(output);

  return parts.join('')
}

function read (buffer, offset, isLE, mLen, nBytes) {
  var e, m;
  var eLen = nBytes * 8 - mLen - 1;
  var eMax = (1 << eLen) - 1;
  var eBias = eMax >> 1;
  var nBits = -7;
  var i = isLE ? (nBytes - 1) : 0;
  var d = isLE ? -1 : 1;
  var s = buffer[offset + i];

  i += d;

  e = s & ((1 << (-nBits)) - 1);
  s >>= (-nBits);
  nBits += eLen;
  for (; nBits > 0; e = e * 256 + buffer[offset + i], i += d, nBits -= 8) {}

  m = e & ((1 << (-nBits)) - 1);
  e >>= (-nBits);
  nBits += mLen;
  for (; nBits > 0; m = m * 256 + buffer[offset + i], i += d, nBits -= 8) {}

  if (e === 0) {
    e = 1 - eBias;
  } else if (e === eMax) {
    return m ? NaN : ((s ? -1 : 1) * Infinity)
  } else {
    m = m + Math.pow(2, mLen);
    e = e - eBias;
  }
  return (s ? -1 : 1) * m * Math.pow(2, e - mLen)
}

function write (buffer, value, offset, isLE, mLen, nBytes) {
  var e, m, c;
  var eLen = nBytes * 8 - mLen - 1;
  var eMax = (1 << eLen) - 1;
  var eBias = eMax >> 1;
  var rt = (mLen === 23 ? Math.pow(2, -24) - Math.pow(2, -77) : 0);
  var i = isLE ? 0 : (nBytes - 1);
  var d = isLE ? 1 : -1;
  var s = value < 0 || (value === 0 && 1 / value < 0) ? 1 : 0;

  value = Math.abs(value);

  if (isNaN(value) || value === Infinity) {
    m = isNaN(value) ? 1 : 0;
    e = eMax;
  } else {
    e = Math.floor(Math.log(value) / Math.LN2);
    if (value * (c = Math.pow(2, -e)) < 1) {
      e--;
      c *= 2;
    }
    if (e + eBias >= 1) {
      value += rt / c;
    } else {
      value += rt * Math.pow(2, 1 - eBias);
    }
    if (value * c >= 2) {
      e++;
      c /= 2;
    }

    if (e + eBias >= eMax) {
      m = 0;
      e = eMax;
    } else if (e + eBias >= 1) {
      m = (value * c - 1) * Math.pow(2, mLen);
      e = e + eBias;
    } else {
      m = value * Math.pow(2, eBias - 1) * Math.pow(2, mLen);
      e = 0;
    }
  }

  for (; mLen >= 8; buffer[offset + i] = m & 0xff, i += d, m /= 256, mLen -= 8) {}

  e = (e << mLen) | m;
  eLen += mLen;
  for (; eLen > 0; buffer[offset + i] = e & 0xff, i += d, e /= 256, eLen -= 8) {}

  buffer[offset + i - d] |= s * 128;
}

var toString = {}.toString;

var isArray = Array.isArray || function (arr) {
  return toString.call(arr) == '[object Array]';
};

var INSPECT_MAX_BYTES = 50;

/**
 * If `Buffer.TYPED_ARRAY_SUPPORT`:
 *   === true    Use Uint8Array implementation (fastest)
 *   === false   Use Object implementation (most compatible, even IE6)
 *
 * Browsers that support typed arrays are IE 10+, Firefox 4+, Chrome 7+, Safari 5.1+,
 * Opera 11.6+, iOS 4.2+.
 *
 * Due to various browser bugs, sometimes the Object implementation will be used even
 * when the browser supports typed arrays.
 *
 * Note:
 *
 *   - Firefox 4-29 lacks support for adding new properties to `Uint8Array` instances,
 *     See: https://bugzilla.mozilla.org/show_bug.cgi?id=695438.
 *
 *   - Chrome 9-10 is missing the `TypedArray.prototype.subarray` function.
 *
 *   - IE10 has a broken `TypedArray.prototype.subarray` function which returns arrays of
 *     incorrect length in some situations.

 * We detect these buggy browsers and set `Buffer.TYPED_ARRAY_SUPPORT` to `false` so they
 * get the Object implementation, which is slower but behaves correctly.
 */
Buffer.TYPED_ARRAY_SUPPORT = global$1.TYPED_ARRAY_SUPPORT !== undefined
  ? global$1.TYPED_ARRAY_SUPPORT
  : true;

/*
 * Export kMaxLength after typed array support is determined.
 */
kMaxLength();

function kMaxLength () {
  return Buffer.TYPED_ARRAY_SUPPORT
    ? 0x7fffffff
    : 0x3fffffff
}

function createBuffer (that, length) {
  if (kMaxLength() < length) {
    throw new RangeError('Invalid typed array length')
  }
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    // Return an augmented `Uint8Array` instance, for best performance
    that = new Uint8Array(length);
    that.__proto__ = Buffer.prototype;
  } else {
    // Fallback: Return an object instance of the Buffer class
    if (that === null) {
      that = new Buffer(length);
    }
    that.length = length;
  }

  return that
}

/**
 * The Buffer constructor returns instances of `Uint8Array` that have their
 * prototype changed to `Buffer.prototype`. Furthermore, `Buffer` is a subclass of
 * `Uint8Array`, so the returned instances will have all the node `Buffer` methods
 * and the `Uint8Array` methods. Square bracket notation works as expected -- it
 * returns a single octet.
 *
 * The `Uint8Array` prototype remains unmodified.
 */

function Buffer (arg, encodingOrOffset, length) {
  if (!Buffer.TYPED_ARRAY_SUPPORT && !(this instanceof Buffer)) {
    return new Buffer(arg, encodingOrOffset, length)
  }

  // Common case.
  if (typeof arg === 'number') {
    if (typeof encodingOrOffset === 'string') {
      throw new Error(
        'If encoding is specified then the first argument must be a string'
      )
    }
    return allocUnsafe(this, arg)
  }
  return from(this, arg, encodingOrOffset, length)
}

Buffer.poolSize = 8192; // not used by this implementation

// TODO: Legacy, not needed anymore. Remove in next major version.
Buffer._augment = function (arr) {
  arr.__proto__ = Buffer.prototype;
  return arr
};

function from (that, value, encodingOrOffset, length) {
  if (typeof value === 'number') {
    throw new TypeError('"value" argument must not be a number')
  }

  if (typeof ArrayBuffer !== 'undefined' && value instanceof ArrayBuffer) {
    return fromArrayBuffer(that, value, encodingOrOffset, length)
  }

  if (typeof value === 'string') {
    return fromString(that, value, encodingOrOffset)
  }

  return fromObject(that, value)
}

/**
 * Functionally equivalent to Buffer(arg, encoding) but throws a TypeError
 * if value is a number.
 * Buffer.from(str[, encoding])
 * Buffer.from(array)
 * Buffer.from(buffer)
 * Buffer.from(arrayBuffer[, byteOffset[, length]])
 **/
Buffer.from = function (value, encodingOrOffset, length) {
  return from(null, value, encodingOrOffset, length)
};

if (Buffer.TYPED_ARRAY_SUPPORT) {
  Buffer.prototype.__proto__ = Uint8Array.prototype;
  Buffer.__proto__ = Uint8Array;
  if (typeof Symbol !== 'undefined' && Symbol.species &&
      Buffer[Symbol.species] === Buffer) ;
}

function assertSize (size) {
  if (typeof size !== 'number') {
    throw new TypeError('"size" argument must be a number')
  } else if (size < 0) {
    throw new RangeError('"size" argument must not be negative')
  }
}

function alloc (that, size, fill, encoding) {
  assertSize(size);
  if (size <= 0) {
    return createBuffer(that, size)
  }
  if (fill !== undefined) {
    // Only pay attention to encoding if it's a string. This
    // prevents accidentally sending in a number that would
    // be interpretted as a start offset.
    return typeof encoding === 'string'
      ? createBuffer(that, size).fill(fill, encoding)
      : createBuffer(that, size).fill(fill)
  }
  return createBuffer(that, size)
}

/**
 * Creates a new filled Buffer instance.
 * alloc(size[, fill[, encoding]])
 **/
Buffer.alloc = function (size, fill, encoding) {
  return alloc(null, size, fill, encoding)
};

function allocUnsafe (that, size) {
  assertSize(size);
  that = createBuffer(that, size < 0 ? 0 : checked(size) | 0);
  if (!Buffer.TYPED_ARRAY_SUPPORT) {
    for (var i = 0; i < size; ++i) {
      that[i] = 0;
    }
  }
  return that
}

/**
 * Equivalent to Buffer(num), by default creates a non-zero-filled Buffer instance.
 * */
Buffer.allocUnsafe = function (size) {
  return allocUnsafe(null, size)
};
/**
 * Equivalent to SlowBuffer(num), by default creates a non-zero-filled Buffer instance.
 */
Buffer.allocUnsafeSlow = function (size) {
  return allocUnsafe(null, size)
};

function fromString (that, string, encoding) {
  if (typeof encoding !== 'string' || encoding === '') {
    encoding = 'utf8';
  }

  if (!Buffer.isEncoding(encoding)) {
    throw new TypeError('"encoding" must be a valid string encoding')
  }

  var length = byteLength(string, encoding) | 0;
  that = createBuffer(that, length);

  var actual = that.write(string, encoding);

  if (actual !== length) {
    // Writing a hex string, for example, that contains invalid characters will
    // cause everything after the first invalid character to be ignored. (e.g.
    // 'abxxcd' will be treated as 'ab')
    that = that.slice(0, actual);
  }

  return that
}

function fromArrayLike (that, array) {
  var length = array.length < 0 ? 0 : checked(array.length) | 0;
  that = createBuffer(that, length);
  for (var i = 0; i < length; i += 1) {
    that[i] = array[i] & 255;
  }
  return that
}

function fromArrayBuffer (that, array, byteOffset, length) {
  array.byteLength; // this throws if `array` is not a valid ArrayBuffer

  if (byteOffset < 0 || array.byteLength < byteOffset) {
    throw new RangeError('\'offset\' is out of bounds')
  }

  if (array.byteLength < byteOffset + (length || 0)) {
    throw new RangeError('\'length\' is out of bounds')
  }

  if (byteOffset === undefined && length === undefined) {
    array = new Uint8Array(array);
  } else if (length === undefined) {
    array = new Uint8Array(array, byteOffset);
  } else {
    array = new Uint8Array(array, byteOffset, length);
  }

  if (Buffer.TYPED_ARRAY_SUPPORT) {
    // Return an augmented `Uint8Array` instance, for best performance
    that = array;
    that.__proto__ = Buffer.prototype;
  } else {
    // Fallback: Return an object instance of the Buffer class
    that = fromArrayLike(that, array);
  }
  return that
}

function fromObject (that, obj) {
  if (internalIsBuffer(obj)) {
    var len = checked(obj.length) | 0;
    that = createBuffer(that, len);

    if (that.length === 0) {
      return that
    }

    obj.copy(that, 0, 0, len);
    return that
  }

  if (obj) {
    if ((typeof ArrayBuffer !== 'undefined' &&
        obj.buffer instanceof ArrayBuffer) || 'length' in obj) {
      if (typeof obj.length !== 'number' || isnan(obj.length)) {
        return createBuffer(that, 0)
      }
      return fromArrayLike(that, obj)
    }

    if (obj.type === 'Buffer' && isArray(obj.data)) {
      return fromArrayLike(that, obj.data)
    }
  }

  throw new TypeError('First argument must be a string, Buffer, ArrayBuffer, Array, or array-like object.')
}

function checked (length) {
  // Note: cannot use `length < kMaxLength()` here because that fails when
  // length is NaN (which is otherwise coerced to zero.)
  if (length >= kMaxLength()) {
    throw new RangeError('Attempt to allocate Buffer larger than maximum ' +
                         'size: 0x' + kMaxLength().toString(16) + ' bytes')
  }
  return length | 0
}
Buffer.isBuffer = isBuffer;
function internalIsBuffer (b) {
  return !!(b != null && b._isBuffer)
}

Buffer.compare = function compare (a, b) {
  if (!internalIsBuffer(a) || !internalIsBuffer(b)) {
    throw new TypeError('Arguments must be Buffers')
  }

  if (a === b) return 0

  var x = a.length;
  var y = b.length;

  for (var i = 0, len = Math.min(x, y); i < len; ++i) {
    if (a[i] !== b[i]) {
      x = a[i];
      y = b[i];
      break
    }
  }

  if (x < y) return -1
  if (y < x) return 1
  return 0
};

Buffer.isEncoding = function isEncoding (encoding) {
  switch (String(encoding).toLowerCase()) {
    case 'hex':
    case 'utf8':
    case 'utf-8':
    case 'ascii':
    case 'latin1':
    case 'binary':
    case 'base64':
    case 'ucs2':
    case 'ucs-2':
    case 'utf16le':
    case 'utf-16le':
      return true
    default:
      return false
  }
};

Buffer.concat = function concat (list, length) {
  if (!isArray(list)) {
    throw new TypeError('"list" argument must be an Array of Buffers')
  }

  if (list.length === 0) {
    return Buffer.alloc(0)
  }

  var i;
  if (length === undefined) {
    length = 0;
    for (i = 0; i < list.length; ++i) {
      length += list[i].length;
    }
  }

  var buffer = Buffer.allocUnsafe(length);
  var pos = 0;
  for (i = 0; i < list.length; ++i) {
    var buf = list[i];
    if (!internalIsBuffer(buf)) {
      throw new TypeError('"list" argument must be an Array of Buffers')
    }
    buf.copy(buffer, pos);
    pos += buf.length;
  }
  return buffer
};

function byteLength (string, encoding) {
  if (internalIsBuffer(string)) {
    return string.length
  }
  if (typeof ArrayBuffer !== 'undefined' && typeof ArrayBuffer.isView === 'function' &&
      (ArrayBuffer.isView(string) || string instanceof ArrayBuffer)) {
    return string.byteLength
  }
  if (typeof string !== 'string') {
    string = '' + string;
  }

  var len = string.length;
  if (len === 0) return 0

  // Use a for loop to avoid recursion
  var loweredCase = false;
  for (;;) {
    switch (encoding) {
      case 'ascii':
      case 'latin1':
      case 'binary':
        return len
      case 'utf8':
      case 'utf-8':
      case undefined:
        return utf8ToBytes(string).length
      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        return len * 2
      case 'hex':
        return len >>> 1
      case 'base64':
        return base64ToBytes(string).length
      default:
        if (loweredCase) return utf8ToBytes(string).length // assume utf8
        encoding = ('' + encoding).toLowerCase();
        loweredCase = true;
    }
  }
}
Buffer.byteLength = byteLength;

function slowToString (encoding, start, end) {
  var loweredCase = false;

  // No need to verify that "this.length <= MAX_UINT32" since it's a read-only
  // property of a typed array.

  // This behaves neither like String nor Uint8Array in that we set start/end
  // to their upper/lower bounds if the value passed is out of range.
  // undefined is handled specially as per ECMA-262 6th Edition,
  // Section 13.3.3.7 Runtime Semantics: KeyedBindingInitialization.
  if (start === undefined || start < 0) {
    start = 0;
  }
  // Return early if start > this.length. Done here to prevent potential uint32
  // coercion fail below.
  if (start > this.length) {
    return ''
  }

  if (end === undefined || end > this.length) {
    end = this.length;
  }

  if (end <= 0) {
    return ''
  }

  // Force coersion to uint32. This will also coerce falsey/NaN values to 0.
  end >>>= 0;
  start >>>= 0;

  if (end <= start) {
    return ''
  }

  if (!encoding) encoding = 'utf8';

  while (true) {
    switch (encoding) {
      case 'hex':
        return hexSlice(this, start, end)

      case 'utf8':
      case 'utf-8':
        return utf8Slice(this, start, end)

      case 'ascii':
        return asciiSlice(this, start, end)

      case 'latin1':
      case 'binary':
        return latin1Slice(this, start, end)

      case 'base64':
        return base64Slice(this, start, end)

      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        return utf16leSlice(this, start, end)

      default:
        if (loweredCase) throw new TypeError('Unknown encoding: ' + encoding)
        encoding = (encoding + '').toLowerCase();
        loweredCase = true;
    }
  }
}

// The property is used by `Buffer.isBuffer` and `is-buffer` (in Safari 5-7) to detect
// Buffer instances.
Buffer.prototype._isBuffer = true;

function swap (b, n, m) {
  var i = b[n];
  b[n] = b[m];
  b[m] = i;
}

Buffer.prototype.swap16 = function swap16 () {
  var len = this.length;
  if (len % 2 !== 0) {
    throw new RangeError('Buffer size must be a multiple of 16-bits')
  }
  for (var i = 0; i < len; i += 2) {
    swap(this, i, i + 1);
  }
  return this
};

Buffer.prototype.swap32 = function swap32 () {
  var len = this.length;
  if (len % 4 !== 0) {
    throw new RangeError('Buffer size must be a multiple of 32-bits')
  }
  for (var i = 0; i < len; i += 4) {
    swap(this, i, i + 3);
    swap(this, i + 1, i + 2);
  }
  return this
};

Buffer.prototype.swap64 = function swap64 () {
  var len = this.length;
  if (len % 8 !== 0) {
    throw new RangeError('Buffer size must be a multiple of 64-bits')
  }
  for (var i = 0; i < len; i += 8) {
    swap(this, i, i + 7);
    swap(this, i + 1, i + 6);
    swap(this, i + 2, i + 5);
    swap(this, i + 3, i + 4);
  }
  return this
};

Buffer.prototype.toString = function toString () {
  var length = this.length | 0;
  if (length === 0) return ''
  if (arguments.length === 0) return utf8Slice(this, 0, length)
  return slowToString.apply(this, arguments)
};

Buffer.prototype.equals = function equals (b) {
  if (!internalIsBuffer(b)) throw new TypeError('Argument must be a Buffer')
  if (this === b) return true
  return Buffer.compare(this, b) === 0
};

Buffer.prototype.inspect = function inspect () {
  var str = '';
  var max = INSPECT_MAX_BYTES;
  if (this.length > 0) {
    str = this.toString('hex', 0, max).match(/.{2}/g).join(' ');
    if (this.length > max) str += ' ... ';
  }
  return '<Buffer ' + str + '>'
};

Buffer.prototype.compare = function compare (target, start, end, thisStart, thisEnd) {
  if (!internalIsBuffer(target)) {
    throw new TypeError('Argument must be a Buffer')
  }

  if (start === undefined) {
    start = 0;
  }
  if (end === undefined) {
    end = target ? target.length : 0;
  }
  if (thisStart === undefined) {
    thisStart = 0;
  }
  if (thisEnd === undefined) {
    thisEnd = this.length;
  }

  if (start < 0 || end > target.length || thisStart < 0 || thisEnd > this.length) {
    throw new RangeError('out of range index')
  }

  if (thisStart >= thisEnd && start >= end) {
    return 0
  }
  if (thisStart >= thisEnd) {
    return -1
  }
  if (start >= end) {
    return 1
  }

  start >>>= 0;
  end >>>= 0;
  thisStart >>>= 0;
  thisEnd >>>= 0;

  if (this === target) return 0

  var x = thisEnd - thisStart;
  var y = end - start;
  var len = Math.min(x, y);

  var thisCopy = this.slice(thisStart, thisEnd);
  var targetCopy = target.slice(start, end);

  for (var i = 0; i < len; ++i) {
    if (thisCopy[i] !== targetCopy[i]) {
      x = thisCopy[i];
      y = targetCopy[i];
      break
    }
  }

  if (x < y) return -1
  if (y < x) return 1
  return 0
};

// Finds either the first index of `val` in `buffer` at offset >= `byteOffset`,
// OR the last index of `val` in `buffer` at offset <= `byteOffset`.
//
// Arguments:
// - buffer - a Buffer to search
// - val - a string, Buffer, or number
// - byteOffset - an index into `buffer`; will be clamped to an int32
// - encoding - an optional encoding, relevant is val is a string
// - dir - true for indexOf, false for lastIndexOf
function bidirectionalIndexOf (buffer, val, byteOffset, encoding, dir) {
  // Empty buffer means no match
  if (buffer.length === 0) return -1

  // Normalize byteOffset
  if (typeof byteOffset === 'string') {
    encoding = byteOffset;
    byteOffset = 0;
  } else if (byteOffset > 0x7fffffff) {
    byteOffset = 0x7fffffff;
  } else if (byteOffset < -0x80000000) {
    byteOffset = -0x80000000;
  }
  byteOffset = +byteOffset;  // Coerce to Number.
  if (isNaN(byteOffset)) {
    // byteOffset: it it's undefined, null, NaN, "foo", etc, search whole buffer
    byteOffset = dir ? 0 : (buffer.length - 1);
  }

  // Normalize byteOffset: negative offsets start from the end of the buffer
  if (byteOffset < 0) byteOffset = buffer.length + byteOffset;
  if (byteOffset >= buffer.length) {
    if (dir) return -1
    else byteOffset = buffer.length - 1;
  } else if (byteOffset < 0) {
    if (dir) byteOffset = 0;
    else return -1
  }

  // Normalize val
  if (typeof val === 'string') {
    val = Buffer.from(val, encoding);
  }

  // Finally, search either indexOf (if dir is true) or lastIndexOf
  if (internalIsBuffer(val)) {
    // Special case: looking for empty string/buffer always fails
    if (val.length === 0) {
      return -1
    }
    return arrayIndexOf(buffer, val, byteOffset, encoding, dir)
  } else if (typeof val === 'number') {
    val = val & 0xFF; // Search for a byte value [0-255]
    if (Buffer.TYPED_ARRAY_SUPPORT &&
        typeof Uint8Array.prototype.indexOf === 'function') {
      if (dir) {
        return Uint8Array.prototype.indexOf.call(buffer, val, byteOffset)
      } else {
        return Uint8Array.prototype.lastIndexOf.call(buffer, val, byteOffset)
      }
    }
    return arrayIndexOf(buffer, [ val ], byteOffset, encoding, dir)
  }

  throw new TypeError('val must be string, number or Buffer')
}

function arrayIndexOf (arr, val, byteOffset, encoding, dir) {
  var indexSize = 1;
  var arrLength = arr.length;
  var valLength = val.length;

  if (encoding !== undefined) {
    encoding = String(encoding).toLowerCase();
    if (encoding === 'ucs2' || encoding === 'ucs-2' ||
        encoding === 'utf16le' || encoding === 'utf-16le') {
      if (arr.length < 2 || val.length < 2) {
        return -1
      }
      indexSize = 2;
      arrLength /= 2;
      valLength /= 2;
      byteOffset /= 2;
    }
  }

  function read (buf, i) {
    if (indexSize === 1) {
      return buf[i]
    } else {
      return buf.readUInt16BE(i * indexSize)
    }
  }

  var i;
  if (dir) {
    var foundIndex = -1;
    for (i = byteOffset; i < arrLength; i++) {
      if (read(arr, i) === read(val, foundIndex === -1 ? 0 : i - foundIndex)) {
        if (foundIndex === -1) foundIndex = i;
        if (i - foundIndex + 1 === valLength) return foundIndex * indexSize
      } else {
        if (foundIndex !== -1) i -= i - foundIndex;
        foundIndex = -1;
      }
    }
  } else {
    if (byteOffset + valLength > arrLength) byteOffset = arrLength - valLength;
    for (i = byteOffset; i >= 0; i--) {
      var found = true;
      for (var j = 0; j < valLength; j++) {
        if (read(arr, i + j) !== read(val, j)) {
          found = false;
          break
        }
      }
      if (found) return i
    }
  }

  return -1
}

Buffer.prototype.includes = function includes (val, byteOffset, encoding) {
  return this.indexOf(val, byteOffset, encoding) !== -1
};

Buffer.prototype.indexOf = function indexOf (val, byteOffset, encoding) {
  return bidirectionalIndexOf(this, val, byteOffset, encoding, true)
};

Buffer.prototype.lastIndexOf = function lastIndexOf (val, byteOffset, encoding) {
  return bidirectionalIndexOf(this, val, byteOffset, encoding, false)
};

function hexWrite (buf, string, offset, length) {
  offset = Number(offset) || 0;
  var remaining = buf.length - offset;
  if (!length) {
    length = remaining;
  } else {
    length = Number(length);
    if (length > remaining) {
      length = remaining;
    }
  }

  // must be an even number of digits
  var strLen = string.length;
  if (strLen % 2 !== 0) throw new TypeError('Invalid hex string')

  if (length > strLen / 2) {
    length = strLen / 2;
  }
  for (var i = 0; i < length; ++i) {
    var parsed = parseInt(string.substr(i * 2, 2), 16);
    if (isNaN(parsed)) return i
    buf[offset + i] = parsed;
  }
  return i
}

function utf8Write (buf, string, offset, length) {
  return blitBuffer(utf8ToBytes(string, buf.length - offset), buf, offset, length)
}

function asciiWrite (buf, string, offset, length) {
  return blitBuffer(asciiToBytes(string), buf, offset, length)
}

function latin1Write (buf, string, offset, length) {
  return asciiWrite(buf, string, offset, length)
}

function base64Write (buf, string, offset, length) {
  return blitBuffer(base64ToBytes(string), buf, offset, length)
}

function ucs2Write (buf, string, offset, length) {
  return blitBuffer(utf16leToBytes(string, buf.length - offset), buf, offset, length)
}

Buffer.prototype.write = function write (string, offset, length, encoding) {
  // Buffer#write(string)
  if (offset === undefined) {
    encoding = 'utf8';
    length = this.length;
    offset = 0;
  // Buffer#write(string, encoding)
  } else if (length === undefined && typeof offset === 'string') {
    encoding = offset;
    length = this.length;
    offset = 0;
  // Buffer#write(string, offset[, length][, encoding])
  } else if (isFinite(offset)) {
    offset = offset | 0;
    if (isFinite(length)) {
      length = length | 0;
      if (encoding === undefined) encoding = 'utf8';
    } else {
      encoding = length;
      length = undefined;
    }
  // legacy write(string, encoding, offset, length) - remove in v0.13
  } else {
    throw new Error(
      'Buffer.write(string, encoding, offset[, length]) is no longer supported'
    )
  }

  var remaining = this.length - offset;
  if (length === undefined || length > remaining) length = remaining;

  if ((string.length > 0 && (length < 0 || offset < 0)) || offset > this.length) {
    throw new RangeError('Attempt to write outside buffer bounds')
  }

  if (!encoding) encoding = 'utf8';

  var loweredCase = false;
  for (;;) {
    switch (encoding) {
      case 'hex':
        return hexWrite(this, string, offset, length)

      case 'utf8':
      case 'utf-8':
        return utf8Write(this, string, offset, length)

      case 'ascii':
        return asciiWrite(this, string, offset, length)

      case 'latin1':
      case 'binary':
        return latin1Write(this, string, offset, length)

      case 'base64':
        // Warning: maxLength not taken into account in base64Write
        return base64Write(this, string, offset, length)

      case 'ucs2':
      case 'ucs-2':
      case 'utf16le':
      case 'utf-16le':
        return ucs2Write(this, string, offset, length)

      default:
        if (loweredCase) throw new TypeError('Unknown encoding: ' + encoding)
        encoding = ('' + encoding).toLowerCase();
        loweredCase = true;
    }
  }
};

Buffer.prototype.toJSON = function toJSON () {
  return {
    type: 'Buffer',
    data: Array.prototype.slice.call(this._arr || this, 0)
  }
};

function base64Slice (buf, start, end) {
  if (start === 0 && end === buf.length) {
    return fromByteArray(buf)
  } else {
    return fromByteArray(buf.slice(start, end))
  }
}

function utf8Slice (buf, start, end) {
  end = Math.min(buf.length, end);
  var res = [];

  var i = start;
  while (i < end) {
    var firstByte = buf[i];
    var codePoint = null;
    var bytesPerSequence = (firstByte > 0xEF) ? 4
      : (firstByte > 0xDF) ? 3
      : (firstByte > 0xBF) ? 2
      : 1;

    if (i + bytesPerSequence <= end) {
      var secondByte, thirdByte, fourthByte, tempCodePoint;

      switch (bytesPerSequence) {
        case 1:
          if (firstByte < 0x80) {
            codePoint = firstByte;
          }
          break
        case 2:
          secondByte = buf[i + 1];
          if ((secondByte & 0xC0) === 0x80) {
            tempCodePoint = (firstByte & 0x1F) << 0x6 | (secondByte & 0x3F);
            if (tempCodePoint > 0x7F) {
              codePoint = tempCodePoint;
            }
          }
          break
        case 3:
          secondByte = buf[i + 1];
          thirdByte = buf[i + 2];
          if ((secondByte & 0xC0) === 0x80 && (thirdByte & 0xC0) === 0x80) {
            tempCodePoint = (firstByte & 0xF) << 0xC | (secondByte & 0x3F) << 0x6 | (thirdByte & 0x3F);
            if (tempCodePoint > 0x7FF && (tempCodePoint < 0xD800 || tempCodePoint > 0xDFFF)) {
              codePoint = tempCodePoint;
            }
          }
          break
        case 4:
          secondByte = buf[i + 1];
          thirdByte = buf[i + 2];
          fourthByte = buf[i + 3];
          if ((secondByte & 0xC0) === 0x80 && (thirdByte & 0xC0) === 0x80 && (fourthByte & 0xC0) === 0x80) {
            tempCodePoint = (firstByte & 0xF) << 0x12 | (secondByte & 0x3F) << 0xC | (thirdByte & 0x3F) << 0x6 | (fourthByte & 0x3F);
            if (tempCodePoint > 0xFFFF && tempCodePoint < 0x110000) {
              codePoint = tempCodePoint;
            }
          }
      }
    }

    if (codePoint === null) {
      // we did not generate a valid codePoint so insert a
      // replacement char (U+FFFD) and advance only 1 byte
      codePoint = 0xFFFD;
      bytesPerSequence = 1;
    } else if (codePoint > 0xFFFF) {
      // encode to utf16 (surrogate pair dance)
      codePoint -= 0x10000;
      res.push(codePoint >>> 10 & 0x3FF | 0xD800);
      codePoint = 0xDC00 | codePoint & 0x3FF;
    }

    res.push(codePoint);
    i += bytesPerSequence;
  }

  return decodeCodePointsArray(res)
}

// Based on http://stackoverflow.com/a/22747272/680742, the browser with
// the lowest limit is Chrome, with 0x10000 args.
// We go 1 magnitude less, for safety
var MAX_ARGUMENTS_LENGTH = 0x1000;

function decodeCodePointsArray (codePoints) {
  var len = codePoints.length;
  if (len <= MAX_ARGUMENTS_LENGTH) {
    return String.fromCharCode.apply(String, codePoints) // avoid extra slice()
  }

  // Decode in chunks to avoid "call stack size exceeded".
  var res = '';
  var i = 0;
  while (i < len) {
    res += String.fromCharCode.apply(
      String,
      codePoints.slice(i, i += MAX_ARGUMENTS_LENGTH)
    );
  }
  return res
}

function asciiSlice (buf, start, end) {
  var ret = '';
  end = Math.min(buf.length, end);

  for (var i = start; i < end; ++i) {
    ret += String.fromCharCode(buf[i] & 0x7F);
  }
  return ret
}

function latin1Slice (buf, start, end) {
  var ret = '';
  end = Math.min(buf.length, end);

  for (var i = start; i < end; ++i) {
    ret += String.fromCharCode(buf[i]);
  }
  return ret
}

function hexSlice (buf, start, end) {
  var len = buf.length;

  if (!start || start < 0) start = 0;
  if (!end || end < 0 || end > len) end = len;

  var out = '';
  for (var i = start; i < end; ++i) {
    out += toHex(buf[i]);
  }
  return out
}

function utf16leSlice (buf, start, end) {
  var bytes = buf.slice(start, end);
  var res = '';
  for (var i = 0; i < bytes.length; i += 2) {
    res += String.fromCharCode(bytes[i] + bytes[i + 1] * 256);
  }
  return res
}

Buffer.prototype.slice = function slice (start, end) {
  var len = this.length;
  start = ~~start;
  end = end === undefined ? len : ~~end;

  if (start < 0) {
    start += len;
    if (start < 0) start = 0;
  } else if (start > len) {
    start = len;
  }

  if (end < 0) {
    end += len;
    if (end < 0) end = 0;
  } else if (end > len) {
    end = len;
  }

  if (end < start) end = start;

  var newBuf;
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    newBuf = this.subarray(start, end);
    newBuf.__proto__ = Buffer.prototype;
  } else {
    var sliceLen = end - start;
    newBuf = new Buffer(sliceLen, undefined);
    for (var i = 0; i < sliceLen; ++i) {
      newBuf[i] = this[i + start];
    }
  }

  return newBuf
};

/*
 * Need to make sure that buffer isn't trying to write out of bounds.
 */
function checkOffset (offset, ext, length) {
  if ((offset % 1) !== 0 || offset < 0) throw new RangeError('offset is not uint')
  if (offset + ext > length) throw new RangeError('Trying to access beyond buffer length')
}

Buffer.prototype.readUIntLE = function readUIntLE (offset, byteLength, noAssert) {
  offset = offset | 0;
  byteLength = byteLength | 0;
  if (!noAssert) checkOffset(offset, byteLength, this.length);

  var val = this[offset];
  var mul = 1;
  var i = 0;
  while (++i < byteLength && (mul *= 0x100)) {
    val += this[offset + i] * mul;
  }

  return val
};

Buffer.prototype.readUIntBE = function readUIntBE (offset, byteLength, noAssert) {
  offset = offset | 0;
  byteLength = byteLength | 0;
  if (!noAssert) {
    checkOffset(offset, byteLength, this.length);
  }

  var val = this[offset + --byteLength];
  var mul = 1;
  while (byteLength > 0 && (mul *= 0x100)) {
    val += this[offset + --byteLength] * mul;
  }

  return val
};

Buffer.prototype.readUInt8 = function readUInt8 (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 1, this.length);
  return this[offset]
};

Buffer.prototype.readUInt16LE = function readUInt16LE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 2, this.length);
  return this[offset] | (this[offset + 1] << 8)
};

Buffer.prototype.readUInt16BE = function readUInt16BE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 2, this.length);
  return (this[offset] << 8) | this[offset + 1]
};

Buffer.prototype.readUInt32LE = function readUInt32LE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length);

  return ((this[offset]) |
      (this[offset + 1] << 8) |
      (this[offset + 2] << 16)) +
      (this[offset + 3] * 0x1000000)
};

Buffer.prototype.readUInt32BE = function readUInt32BE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length);

  return (this[offset] * 0x1000000) +
    ((this[offset + 1] << 16) |
    (this[offset + 2] << 8) |
    this[offset + 3])
};

Buffer.prototype.readIntLE = function readIntLE (offset, byteLength, noAssert) {
  offset = offset | 0;
  byteLength = byteLength | 0;
  if (!noAssert) checkOffset(offset, byteLength, this.length);

  var val = this[offset];
  var mul = 1;
  var i = 0;
  while (++i < byteLength && (mul *= 0x100)) {
    val += this[offset + i] * mul;
  }
  mul *= 0x80;

  if (val >= mul) val -= Math.pow(2, 8 * byteLength);

  return val
};

Buffer.prototype.readIntBE = function readIntBE (offset, byteLength, noAssert) {
  offset = offset | 0;
  byteLength = byteLength | 0;
  if (!noAssert) checkOffset(offset, byteLength, this.length);

  var i = byteLength;
  var mul = 1;
  var val = this[offset + --i];
  while (i > 0 && (mul *= 0x100)) {
    val += this[offset + --i] * mul;
  }
  mul *= 0x80;

  if (val >= mul) val -= Math.pow(2, 8 * byteLength);

  return val
};

Buffer.prototype.readInt8 = function readInt8 (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 1, this.length);
  if (!(this[offset] & 0x80)) return (this[offset])
  return ((0xff - this[offset] + 1) * -1)
};

Buffer.prototype.readInt16LE = function readInt16LE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 2, this.length);
  var val = this[offset] | (this[offset + 1] << 8);
  return (val & 0x8000) ? val | 0xFFFF0000 : val
};

Buffer.prototype.readInt16BE = function readInt16BE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 2, this.length);
  var val = this[offset + 1] | (this[offset] << 8);
  return (val & 0x8000) ? val | 0xFFFF0000 : val
};

Buffer.prototype.readInt32LE = function readInt32LE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length);

  return (this[offset]) |
    (this[offset + 1] << 8) |
    (this[offset + 2] << 16) |
    (this[offset + 3] << 24)
};

Buffer.prototype.readInt32BE = function readInt32BE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length);

  return (this[offset] << 24) |
    (this[offset + 1] << 16) |
    (this[offset + 2] << 8) |
    (this[offset + 3])
};

Buffer.prototype.readFloatLE = function readFloatLE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length);
  return read(this, offset, true, 23, 4)
};

Buffer.prototype.readFloatBE = function readFloatBE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 4, this.length);
  return read(this, offset, false, 23, 4)
};

Buffer.prototype.readDoubleLE = function readDoubleLE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 8, this.length);
  return read(this, offset, true, 52, 8)
};

Buffer.prototype.readDoubleBE = function readDoubleBE (offset, noAssert) {
  if (!noAssert) checkOffset(offset, 8, this.length);
  return read(this, offset, false, 52, 8)
};

function checkInt (buf, value, offset, ext, max, min) {
  if (!internalIsBuffer(buf)) throw new TypeError('"buffer" argument must be a Buffer instance')
  if (value > max || value < min) throw new RangeError('"value" argument is out of bounds')
  if (offset + ext > buf.length) throw new RangeError('Index out of range')
}

Buffer.prototype.writeUIntLE = function writeUIntLE (value, offset, byteLength, noAssert) {
  value = +value;
  offset = offset | 0;
  byteLength = byteLength | 0;
  if (!noAssert) {
    var maxBytes = Math.pow(2, 8 * byteLength) - 1;
    checkInt(this, value, offset, byteLength, maxBytes, 0);
  }

  var mul = 1;
  var i = 0;
  this[offset] = value & 0xFF;
  while (++i < byteLength && (mul *= 0x100)) {
    this[offset + i] = (value / mul) & 0xFF;
  }

  return offset + byteLength
};

Buffer.prototype.writeUIntBE = function writeUIntBE (value, offset, byteLength, noAssert) {
  value = +value;
  offset = offset | 0;
  byteLength = byteLength | 0;
  if (!noAssert) {
    var maxBytes = Math.pow(2, 8 * byteLength) - 1;
    checkInt(this, value, offset, byteLength, maxBytes, 0);
  }

  var i = byteLength - 1;
  var mul = 1;
  this[offset + i] = value & 0xFF;
  while (--i >= 0 && (mul *= 0x100)) {
    this[offset + i] = (value / mul) & 0xFF;
  }

  return offset + byteLength
};

Buffer.prototype.writeUInt8 = function writeUInt8 (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 1, 0xff, 0);
  if (!Buffer.TYPED_ARRAY_SUPPORT) value = Math.floor(value);
  this[offset] = (value & 0xff);
  return offset + 1
};

function objectWriteUInt16 (buf, value, offset, littleEndian) {
  if (value < 0) value = 0xffff + value + 1;
  for (var i = 0, j = Math.min(buf.length - offset, 2); i < j; ++i) {
    buf[offset + i] = (value & (0xff << (8 * (littleEndian ? i : 1 - i)))) >>>
      (littleEndian ? i : 1 - i) * 8;
  }
}

Buffer.prototype.writeUInt16LE = function writeUInt16LE (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 2, 0xffff, 0);
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value & 0xff);
    this[offset + 1] = (value >>> 8);
  } else {
    objectWriteUInt16(this, value, offset, true);
  }
  return offset + 2
};

Buffer.prototype.writeUInt16BE = function writeUInt16BE (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 2, 0xffff, 0);
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value >>> 8);
    this[offset + 1] = (value & 0xff);
  } else {
    objectWriteUInt16(this, value, offset, false);
  }
  return offset + 2
};

function objectWriteUInt32 (buf, value, offset, littleEndian) {
  if (value < 0) value = 0xffffffff + value + 1;
  for (var i = 0, j = Math.min(buf.length - offset, 4); i < j; ++i) {
    buf[offset + i] = (value >>> (littleEndian ? i : 3 - i) * 8) & 0xff;
  }
}

Buffer.prototype.writeUInt32LE = function writeUInt32LE (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 4, 0xffffffff, 0);
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    this[offset + 3] = (value >>> 24);
    this[offset + 2] = (value >>> 16);
    this[offset + 1] = (value >>> 8);
    this[offset] = (value & 0xff);
  } else {
    objectWriteUInt32(this, value, offset, true);
  }
  return offset + 4
};

Buffer.prototype.writeUInt32BE = function writeUInt32BE (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 4, 0xffffffff, 0);
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value >>> 24);
    this[offset + 1] = (value >>> 16);
    this[offset + 2] = (value >>> 8);
    this[offset + 3] = (value & 0xff);
  } else {
    objectWriteUInt32(this, value, offset, false);
  }
  return offset + 4
};

Buffer.prototype.writeIntLE = function writeIntLE (value, offset, byteLength, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) {
    var limit = Math.pow(2, 8 * byteLength - 1);

    checkInt(this, value, offset, byteLength, limit - 1, -limit);
  }

  var i = 0;
  var mul = 1;
  var sub = 0;
  this[offset] = value & 0xFF;
  while (++i < byteLength && (mul *= 0x100)) {
    if (value < 0 && sub === 0 && this[offset + i - 1] !== 0) {
      sub = 1;
    }
    this[offset + i] = ((value / mul) >> 0) - sub & 0xFF;
  }

  return offset + byteLength
};

Buffer.prototype.writeIntBE = function writeIntBE (value, offset, byteLength, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) {
    var limit = Math.pow(2, 8 * byteLength - 1);

    checkInt(this, value, offset, byteLength, limit - 1, -limit);
  }

  var i = byteLength - 1;
  var mul = 1;
  var sub = 0;
  this[offset + i] = value & 0xFF;
  while (--i >= 0 && (mul *= 0x100)) {
    if (value < 0 && sub === 0 && this[offset + i + 1] !== 0) {
      sub = 1;
    }
    this[offset + i] = ((value / mul) >> 0) - sub & 0xFF;
  }

  return offset + byteLength
};

Buffer.prototype.writeInt8 = function writeInt8 (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 1, 0x7f, -0x80);
  if (!Buffer.TYPED_ARRAY_SUPPORT) value = Math.floor(value);
  if (value < 0) value = 0xff + value + 1;
  this[offset] = (value & 0xff);
  return offset + 1
};

Buffer.prototype.writeInt16LE = function writeInt16LE (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 2, 0x7fff, -0x8000);
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value & 0xff);
    this[offset + 1] = (value >>> 8);
  } else {
    objectWriteUInt16(this, value, offset, true);
  }
  return offset + 2
};

Buffer.prototype.writeInt16BE = function writeInt16BE (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 2, 0x7fff, -0x8000);
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value >>> 8);
    this[offset + 1] = (value & 0xff);
  } else {
    objectWriteUInt16(this, value, offset, false);
  }
  return offset + 2
};

Buffer.prototype.writeInt32LE = function writeInt32LE (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 4, 0x7fffffff, -0x80000000);
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value & 0xff);
    this[offset + 1] = (value >>> 8);
    this[offset + 2] = (value >>> 16);
    this[offset + 3] = (value >>> 24);
  } else {
    objectWriteUInt32(this, value, offset, true);
  }
  return offset + 4
};

Buffer.prototype.writeInt32BE = function writeInt32BE (value, offset, noAssert) {
  value = +value;
  offset = offset | 0;
  if (!noAssert) checkInt(this, value, offset, 4, 0x7fffffff, -0x80000000);
  if (value < 0) value = 0xffffffff + value + 1;
  if (Buffer.TYPED_ARRAY_SUPPORT) {
    this[offset] = (value >>> 24);
    this[offset + 1] = (value >>> 16);
    this[offset + 2] = (value >>> 8);
    this[offset + 3] = (value & 0xff);
  } else {
    objectWriteUInt32(this, value, offset, false);
  }
  return offset + 4
};

function checkIEEE754 (buf, value, offset, ext, max, min) {
  if (offset + ext > buf.length) throw new RangeError('Index out of range')
  if (offset < 0) throw new RangeError('Index out of range')
}

function writeFloat (buf, value, offset, littleEndian, noAssert) {
  if (!noAssert) {
    checkIEEE754(buf, value, offset, 4);
  }
  write(buf, value, offset, littleEndian, 23, 4);
  return offset + 4
}

Buffer.prototype.writeFloatLE = function writeFloatLE (value, offset, noAssert) {
  return writeFloat(this, value, offset, true, noAssert)
};

Buffer.prototype.writeFloatBE = function writeFloatBE (value, offset, noAssert) {
  return writeFloat(this, value, offset, false, noAssert)
};

function writeDouble (buf, value, offset, littleEndian, noAssert) {
  if (!noAssert) {
    checkIEEE754(buf, value, offset, 8);
  }
  write(buf, value, offset, littleEndian, 52, 8);
  return offset + 8
}

Buffer.prototype.writeDoubleLE = function writeDoubleLE (value, offset, noAssert) {
  return writeDouble(this, value, offset, true, noAssert)
};

Buffer.prototype.writeDoubleBE = function writeDoubleBE (value, offset, noAssert) {
  return writeDouble(this, value, offset, false, noAssert)
};

// copy(targetBuffer, targetStart=0, sourceStart=0, sourceEnd=buffer.length)
Buffer.prototype.copy = function copy (target, targetStart, start, end) {
  if (!start) start = 0;
  if (!end && end !== 0) end = this.length;
  if (targetStart >= target.length) targetStart = target.length;
  if (!targetStart) targetStart = 0;
  if (end > 0 && end < start) end = start;

  // Copy 0 bytes; we're done
  if (end === start) return 0
  if (target.length === 0 || this.length === 0) return 0

  // Fatal error conditions
  if (targetStart < 0) {
    throw new RangeError('targetStart out of bounds')
  }
  if (start < 0 || start >= this.length) throw new RangeError('sourceStart out of bounds')
  if (end < 0) throw new RangeError('sourceEnd out of bounds')

  // Are we oob?
  if (end > this.length) end = this.length;
  if (target.length - targetStart < end - start) {
    end = target.length - targetStart + start;
  }

  var len = end - start;
  var i;

  if (this === target && start < targetStart && targetStart < end) {
    // descending copy from end
    for (i = len - 1; i >= 0; --i) {
      target[i + targetStart] = this[i + start];
    }
  } else if (len < 1000 || !Buffer.TYPED_ARRAY_SUPPORT) {
    // ascending copy from start
    for (i = 0; i < len; ++i) {
      target[i + targetStart] = this[i + start];
    }
  } else {
    Uint8Array.prototype.set.call(
      target,
      this.subarray(start, start + len),
      targetStart
    );
  }

  return len
};

// Usage:
//    buffer.fill(number[, offset[, end]])
//    buffer.fill(buffer[, offset[, end]])
//    buffer.fill(string[, offset[, end]][, encoding])
Buffer.prototype.fill = function fill (val, start, end, encoding) {
  // Handle string cases:
  if (typeof val === 'string') {
    if (typeof start === 'string') {
      encoding = start;
      start = 0;
      end = this.length;
    } else if (typeof end === 'string') {
      encoding = end;
      end = this.length;
    }
    if (val.length === 1) {
      var code = val.charCodeAt(0);
      if (code < 256) {
        val = code;
      }
    }
    if (encoding !== undefined && typeof encoding !== 'string') {
      throw new TypeError('encoding must be a string')
    }
    if (typeof encoding === 'string' && !Buffer.isEncoding(encoding)) {
      throw new TypeError('Unknown encoding: ' + encoding)
    }
  } else if (typeof val === 'number') {
    val = val & 255;
  }

  // Invalid ranges are not set to a default, so can range check early.
  if (start < 0 || this.length < start || this.length < end) {
    throw new RangeError('Out of range index')
  }

  if (end <= start) {
    return this
  }

  start = start >>> 0;
  end = end === undefined ? this.length : end >>> 0;

  if (!val) val = 0;

  var i;
  if (typeof val === 'number') {
    for (i = start; i < end; ++i) {
      this[i] = val;
    }
  } else {
    var bytes = internalIsBuffer(val)
      ? val
      : utf8ToBytes(new Buffer(val, encoding).toString());
    var len = bytes.length;
    for (i = 0; i < end - start; ++i) {
      this[i + start] = bytes[i % len];
    }
  }

  return this
};

// HELPER FUNCTIONS
// ================

var INVALID_BASE64_RE = /[^+\/0-9A-Za-z-_]/g;

function base64clean (str) {
  // Node strips out invalid characters like \n and \t from the string, base64-js does not
  str = stringtrim(str).replace(INVALID_BASE64_RE, '');
  // Node converts strings with length < 2 to ''
  if (str.length < 2) return ''
  // Node allows for non-padded base64 strings (missing trailing ===), base64-js does not
  while (str.length % 4 !== 0) {
    str = str + '=';
  }
  return str
}

function stringtrim (str) {
  if (str.trim) return str.trim()
  return str.replace(/^\s+|\s+$/g, '')
}

function toHex (n) {
  if (n < 16) return '0' + n.toString(16)
  return n.toString(16)
}

function utf8ToBytes (string, units) {
  units = units || Infinity;
  var codePoint;
  var length = string.length;
  var leadSurrogate = null;
  var bytes = [];

  for (var i = 0; i < length; ++i) {
    codePoint = string.charCodeAt(i);

    // is surrogate component
    if (codePoint > 0xD7FF && codePoint < 0xE000) {
      // last char was a lead
      if (!leadSurrogate) {
        // no lead yet
        if (codePoint > 0xDBFF) {
          // unexpected trail
          if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD);
          continue
        } else if (i + 1 === length) {
          // unpaired lead
          if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD);
          continue
        }

        // valid lead
        leadSurrogate = codePoint;

        continue
      }

      // 2 leads in a row
      if (codePoint < 0xDC00) {
        if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD);
        leadSurrogate = codePoint;
        continue
      }

      // valid surrogate pair
      codePoint = (leadSurrogate - 0xD800 << 10 | codePoint - 0xDC00) + 0x10000;
    } else if (leadSurrogate) {
      // valid bmp char, but last char was a lead
      if ((units -= 3) > -1) bytes.push(0xEF, 0xBF, 0xBD);
    }

    leadSurrogate = null;

    // encode utf8
    if (codePoint < 0x80) {
      if ((units -= 1) < 0) break
      bytes.push(codePoint);
    } else if (codePoint < 0x800) {
      if ((units -= 2) < 0) break
      bytes.push(
        codePoint >> 0x6 | 0xC0,
        codePoint & 0x3F | 0x80
      );
    } else if (codePoint < 0x10000) {
      if ((units -= 3) < 0) break
      bytes.push(
        codePoint >> 0xC | 0xE0,
        codePoint >> 0x6 & 0x3F | 0x80,
        codePoint & 0x3F | 0x80
      );
    } else if (codePoint < 0x110000) {
      if ((units -= 4) < 0) break
      bytes.push(
        codePoint >> 0x12 | 0xF0,
        codePoint >> 0xC & 0x3F | 0x80,
        codePoint >> 0x6 & 0x3F | 0x80,
        codePoint & 0x3F | 0x80
      );
    } else {
      throw new Error('Invalid code point')
    }
  }

  return bytes
}

function asciiToBytes (str) {
  var byteArray = [];
  for (var i = 0; i < str.length; ++i) {
    // Node's code seems to be doing this and not & 0x7F..
    byteArray.push(str.charCodeAt(i) & 0xFF);
  }
  return byteArray
}

function utf16leToBytes (str, units) {
  var c, hi, lo;
  var byteArray = [];
  for (var i = 0; i < str.length; ++i) {
    if ((units -= 2) < 0) break

    c = str.charCodeAt(i);
    hi = c >> 8;
    lo = c % 256;
    byteArray.push(lo);
    byteArray.push(hi);
  }

  return byteArray
}


function base64ToBytes (str) {
  return toByteArray(base64clean(str))
}

function blitBuffer (src, dst, offset, length) {
  for (var i = 0; i < length; ++i) {
    if ((i + offset >= dst.length) || (i >= src.length)) break
    dst[i + offset] = src[i];
  }
  return i
}

function isnan (val) {
  return val !== val // eslint-disable-line no-self-compare
}


// the following is from is-buffer, also by Feross Aboukhadijeh and with same lisence
// The _isBuffer check is for Safari 5-7 support, because it's missing
// Object.prototype.constructor. Remove this eventually
function isBuffer(obj) {
  return obj != null && (!!obj._isBuffer || isFastBuffer(obj) || isSlowBuffer(obj))
}

function isFastBuffer (obj) {
  return !!obj.constructor && typeof obj.constructor.isBuffer === 'function' && obj.constructor.isBuffer(obj)
}

// For Node v0.10 support. Remove this eventually.
function isSlowBuffer (obj) {
  return typeof obj.readFloatLE === 'function' && typeof obj.slice === 'function' && isFastBuffer(obj.slice(0, 0))
}

// Lodash implementation of `get`

const charCodeOfDot = ".".charCodeAt(0);
const reEscapeChar = /\\(\\)?/g;
const rePropName = RegExp(
  // Match anything that isn't a dot or bracket.
  "[^.[\\]]+" +
    "|" +
    // Or match property names within brackets.
    "\\[(?:" +
    // Match a non-string expression.
    "([^\"'][^[]*)" +
    "|" +
    // Or match strings (supports escaping characters).
    "([\"'])((?:(?!\\2)[^\\\\]|\\\\.)*?)\\2" +
    ")\\]" +
    "|" +
    // Or match "" as the space between consecutive dots or empty brackets.
    "(?=(?:\\.|\\[\\])(?:\\.|\\[\\]|$))",
  "g",
);
const reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/;
const reIsPlainProp = /^\w*$/;

const getTag = function (value) {
  return Object.prototype.toString.call(value);
};

const isSymbol = function (value) {
  const type = typeof value;
  return (
    type === "symbol" ||
    (type === "object" && value && getTag(value) === "[object Symbol]")
  );
};

const isKey = function (value, object) {
  if (Array.isArray(value)) {
    return false;
  }
  const type = typeof value;
  if (
    type === "number" ||
    type === "symbol" ||
    type === "boolean" ||
    !value ||
    isSymbol(value)
  ) {
    return true;
  }
  return (
    reIsPlainProp.test(value) ||
    !reIsDeepProp.test(value) ||
    (object != null && value in Object(object))
  );
};

const stringToPath = function (string) {
  const result = [];
  if (string.charCodeAt(0) === charCodeOfDot) {
    result.push("");
  }
  string.replace(rePropName, function (match, expression, quote, subString) {
    let key = match;
    if (quote) {
      key = subString.replace(reEscapeChar, "$1");
    } else if (expression) {
      key = expression.trim();
    }
    result.push(key);
  });
  return result;
};

const castPath = function (value, object) {
  if (Array.isArray(value)) {
    return value;
  } else {
    return isKey(value, object) ? [value] : stringToPath(value);
  }
};

const toKey = function (value) {
  if (typeof value === "string" || isSymbol(value)) return value;
  const result = `${value}`;
  // eslint-disable-next-line
  return result == "0" && 1 / value == -INFINITY ? "-0" : result;
};

const get = function (object, path) {
  path = castPath(path, object);
  let index = 0;
  const length = path.length;
  while (object != null && index < length) {
    object = object[toKey(path[index++])];
  }
  return index && index === length ? object : undefined;
};

const is_object = function (obj) {
  return typeof obj === "object" && obj !== null && !Array.isArray(obj);
};

const normalize_columns = function (columns) {
  if (columns === undefined || columns === null) {
    return [undefined, undefined];
  }
  if (typeof columns !== "object") {
    return [Error('Invalid option "columns": expect an array or an object')];
  }
  if (!Array.isArray(columns)) {
    const newcolumns = [];
    for (const k in columns) {
      newcolumns.push({
        key: k,
        header: columns[k],
      });
    }
    columns = newcolumns;
  } else {
    const newcolumns = [];
    for (const column of columns) {
      if (typeof column === "string") {
        newcolumns.push({
          key: column,
          header: column,
        });
      } else if (
        typeof column === "object" &&
        column !== null &&
        !Array.isArray(column)
      ) {
        if (!column.key) {
          return [
            Error('Invalid column definition: property "key" is required'),
          ];
        }
        if (column.header === undefined) {
          column.header = column.key;
        }
        newcolumns.push(column);
      } else {
        return [
          Error("Invalid column definition: expect a string or an object"),
        ];
      }
    }
    columns = newcolumns;
  }
  return [undefined, columns];
};

class CsvError extends Error {
  constructor(code, message, ...contexts) {
    if (Array.isArray(message)) message = message.join(" ");
    super(message);
    if (Error.captureStackTrace !== undefined) {
      Error.captureStackTrace(this, CsvError);
    }
    this.code = code;
    for (const context of contexts) {
      for (const key in context) {
        const value = context[key];
        this[key] = isBuffer(value)
          ? value.toString()
          : value == null
            ? value
            : JSON.parse(JSON.stringify(value));
      }
    }
  }
}

const underscore = function (str) {
  return str.replace(/([A-Z])/g, function (_, match) {
    return "_" + match.toLowerCase();
  });
};

const normalize_options = function (opts) {
  const options = {};
  // Merge with user options
  for (const opt in opts) {
    options[underscore(opt)] = opts[opt];
  }
  // Normalize option `bom`
  if (
    options.bom === undefined ||
    options.bom === null ||
    options.bom === false
  ) {
    options.bom = false;
  } else if (options.bom !== true) {
    return [
      new CsvError("CSV_OPTION_BOOLEAN_INVALID_TYPE", [
        "option `bom` is optional and must be a boolean value,",
        `got ${JSON.stringify(options.bom)}`,
      ]),
    ];
  }
  // Normalize option `delimiter`
  if (options.delimiter === undefined || options.delimiter === null) {
    options.delimiter = ",";
  } else if (isBuffer(options.delimiter)) {
    options.delimiter = options.delimiter.toString();
  } else if (typeof options.delimiter !== "string") {
    return [
      new CsvError("CSV_OPTION_DELIMITER_INVALID_TYPE", [
        "option `delimiter` must be a buffer or a string,",
        `got ${JSON.stringify(options.delimiter)}`,
      ]),
    ];
  }
  // Normalize option `quote`
  if (options.quote === undefined || options.quote === null) {
    options.quote = '"';
  } else if (options.quote === true) {
    options.quote = '"';
  } else if (options.quote === false) {
    options.quote = "";
  } else if (isBuffer(options.quote)) {
    options.quote = options.quote.toString();
  } else if (typeof options.quote !== "string") {
    return [
      new CsvError("CSV_OPTION_QUOTE_INVALID_TYPE", [
        "option `quote` must be a boolean, a buffer or a string,",
        `got ${JSON.stringify(options.quote)}`,
      ]),
    ];
  }
  // Normalize option `quoted`
  if (options.quoted === undefined || options.quoted === null) {
    options.quoted = false;
  }
  // Normalize option `escape_formulas`
  if (
    options.escape_formulas === undefined ||
    options.escape_formulas === null
  ) {
    options.escape_formulas = false;
  } else if (typeof options.escape_formulas !== "boolean") {
    return [
      new CsvError("CSV_OPTION_ESCAPE_FORMULAS_INVALID_TYPE", [
        "option `escape_formulas` must be a boolean,",
        `got ${JSON.stringify(options.escape_formulas)}`,
      ]),
    ];
  }
  // Normalize option `quoted_empty`
  if (options.quoted_empty === undefined || options.quoted_empty === null) {
    options.quoted_empty = undefined;
  }
  // Normalize option `quoted_match`
  if (
    options.quoted_match === undefined ||
    options.quoted_match === null ||
    options.quoted_match === false
  ) {
    options.quoted_match = null;
  } else if (!Array.isArray(options.quoted_match)) {
    options.quoted_match = [options.quoted_match];
  }
  if (options.quoted_match) {
    for (const quoted_match of options.quoted_match) {
      const isString = typeof quoted_match === "string";
      const isRegExp = quoted_match instanceof RegExp;
      if (!isString && !isRegExp) {
        return [
          Error(
            `Invalid Option: quoted_match must be a string or a regex, got ${JSON.stringify(quoted_match)}`,
          ),
        ];
      }
    }
  }
  // Normalize option `quoted_string`
  if (options.quoted_string === undefined || options.quoted_string === null) {
    options.quoted_string = false;
  }
  // Normalize option `eof`
  if (options.eof === undefined || options.eof === null) {
    options.eof = true;
  }
  // Normalize option `escape`
  if (options.escape === undefined || options.escape === null) {
    options.escape = '"';
  } else if (isBuffer(options.escape)) {
    options.escape = options.escape.toString();
  } else if (typeof options.escape !== "string") {
    return [
      Error(
        `Invalid Option: escape must be a buffer or a string, got ${JSON.stringify(options.escape)}`,
      ),
    ];
  }
  if (options.escape.length > 1) {
    return [
      Error(
        `Invalid Option: escape must be one character, got ${options.escape.length} characters`,
      ),
    ];
  }
  // Normalize option `header`
  if (options.header === undefined || options.header === null) {
    options.header = false;
  }
  // Normalize option `columns`
  const [errColumns, columns] = normalize_columns(options.columns);
  if (errColumns !== undefined) return [errColumns];
  options.columns = columns;
  // Normalize option `quoted`
  if (options.quoted === undefined || options.quoted === null) {
    options.quoted = false;
  }
  // Normalize option `cast`
  if (options.cast === undefined || options.cast === null) {
    options.cast = {};
  }
  // Normalize option cast.bigint
  if (options.cast.bigint === undefined || options.cast.bigint === null) {
    // Cast boolean to string by default
    options.cast.bigint = (value) => "" + value;
  }
  // Normalize option cast.boolean
  if (options.cast.boolean === undefined || options.cast.boolean === null) {
    // Cast boolean to string by default
    options.cast.boolean = (value) => (value ? "1" : "");
  }
  // Normalize option cast.date
  if (options.cast.date === undefined || options.cast.date === null) {
    // Cast date to timestamp string by default
    options.cast.date = (value) => "" + value.getTime();
  }
  // Normalize option cast.number
  if (options.cast.number === undefined || options.cast.number === null) {
    // Cast number to string using native casting by default
    options.cast.number = (value) => "" + value;
  }
  // Normalize option cast.object
  if (options.cast.object === undefined || options.cast.object === null) {
    // Stringify object as JSON by default
    options.cast.object = (value) => JSON.stringify(value);
  }
  // Normalize option cast.string
  if (options.cast.string === undefined || options.cast.string === null) {
    // Leave string untouched
    options.cast.string = function (value) {
      return value;
    };
  }
  // Normalize option `on_record`
  if (
    options.on_record !== undefined &&
    typeof options.on_record !== "function"
  ) {
    return [Error(`Invalid Option: "on_record" must be a function.`)];
  }
  // Normalize option `record_delimiter`
  if (
    options.record_delimiter === undefined ||
    options.record_delimiter === null
  ) {
    options.record_delimiter = "\n";
  } else if (isBuffer(options.record_delimiter)) {
    options.record_delimiter = options.record_delimiter.toString();
  } else if (typeof options.record_delimiter !== "string") {
    return [
      Error(
        `Invalid Option: record_delimiter must be a buffer or a string, got ${JSON.stringify(options.record_delimiter)}`,
      ),
    ];
  }
  switch (options.record_delimiter) {
    case "unix":
      options.record_delimiter = "\n";
      break;
    case "mac":
      options.record_delimiter = "\r";
      break;
    case "windows":
      options.record_delimiter = "\r\n";
      break;
    case "ascii":
      options.record_delimiter = "\u001e";
      break;
    case "unicode":
      options.record_delimiter = "\u2028";
      break;
  }
  return [undefined, options];
};

const bom_utf8 = Buffer.from([239, 187, 191]);

const stringifier = function (options, state, info) {
  return {
    options: options,
    state: state,
    info: info,
    __transform: function (chunk, push) {
      // Chunk validation
      if (!Array.isArray(chunk) && typeof chunk !== "object") {
        return Error(
          `Invalid Record: expect an array or an object, got ${JSON.stringify(chunk)}`,
        );
      }
      // Detect columns from the first record
      if (this.info.records === 0) {
        if (Array.isArray(chunk)) {
          if (
            this.options.header === true &&
            this.options.columns === undefined
          ) {
            return Error(
              "Undiscoverable Columns: header option requires column option or object records",
            );
          }
        } else if (this.options.columns === undefined) {
          const [err, columns] = normalize_columns(Object.keys(chunk));
          if (err) return;
          this.options.columns = columns;
        }
      }
      // Emit the header
      if (this.info.records === 0) {
        this.bom(push);
        const err = this.headers(push);
        if (err) return err;
      }
      // Emit and stringify the record if an object or an array
      try {
        // this.emit('record', chunk, this.info.records);
        if (this.options.on_record) {
          this.options.on_record(chunk, this.info.records);
        }
      } catch (err) {
        return err;
      }
      // Convert the record into a string
      let err, chunk_string;
      if (this.options.eof) {
        [err, chunk_string] = this.stringify(chunk);
        if (err) return err;
        if (chunk_string === undefined) {
          return;
        } else {
          chunk_string = chunk_string + this.options.record_delimiter;
        }
      } else {
        [err, chunk_string] = this.stringify(chunk);
        if (err) return err;
        if (chunk_string === undefined) {
          return;
        } else {
          if (this.options.header || this.info.records) {
            chunk_string = this.options.record_delimiter + chunk_string;
          }
        }
      }
      // Emit the csv
      this.info.records++;
      push(chunk_string);
    },
    stringify: function (chunk, chunkIsHeader = false) {
      if (typeof chunk !== "object") {
        return [undefined, chunk];
      }
      const { columns } = this.options;
      const record = [];
      // Record is an array
      if (Array.isArray(chunk)) {
        // We are getting an array but the user has specified output columns. In
        // this case, we respect the columns indexes
        if (columns) {
          chunk.splice(columns.length);
        }
        // Cast record elements
        for (let i = 0; i < chunk.length; i++) {
          const field = chunk[i];
          const [err, value] = this.__cast(field, {
            index: i,
            column: i,
            records: this.info.records,
            header: chunkIsHeader,
          });
          if (err) return [err];
          record[i] = [value, field];
        }
        // Record is a literal object
        // `columns` is always defined: it is either provided or discovered.
      } else {
        for (let i = 0; i < columns.length; i++) {
          const field = get(chunk, columns[i].key);
          const [err, value] = this.__cast(field, {
            index: i,
            column: columns[i].key,
            records: this.info.records,
            header: chunkIsHeader,
          });
          if (err) return [err];
          record[i] = [value, field];
        }
      }
      let csvrecord = "";
      for (let i = 0; i < record.length; i++) {
        let options, err;

        let [value, field] = record[i];
        if (typeof value === "string") {
          options = this.options;
        } else if (is_object(value)) {
          options = value;
          value = options.value;
          delete options.value;
          if (
            typeof value !== "string" &&
            value !== undefined &&
            value !== null
          ) {
            if (err)
              return [
                Error(
                  `Invalid Casting Value: returned value must return a string, null or undefined, got ${JSON.stringify(value)}`,
                ),
              ];
          }
          options = { ...this.options, ...options };
          [err, options] = normalize_options(options);
          if (err !== undefined) {
            return [err];
          }
        } else if (value === undefined || value === null) {
          options = this.options;
        } else {
          return [
            Error(
              `Invalid Casting Value: returned value must return a string, an object, null or undefined, got ${JSON.stringify(value)}`,
            ),
          ];
        }
        const {
          delimiter,
          escape,
          quote,
          quoted,
          quoted_empty,
          quoted_string,
          quoted_match,
          record_delimiter,
          escape_formulas,
        } = options;
        if ("" === value && "" === field) {
          let quotedMatch =
            quoted_match &&
            quoted_match.filter((quoted_match) => {
              if (typeof quoted_match === "string") {
                return value.indexOf(quoted_match) !== -1;
              } else {
                return quoted_match.test(value);
              }
            });
          quotedMatch = quotedMatch && quotedMatch.length > 0;
          const shouldQuote =
            quotedMatch ||
            true === quoted_empty ||
            (true === quoted_string && false !== quoted_empty);
          if (shouldQuote === true) {
            value = quote + value + quote;
          }
          csvrecord += value;
        } else if (value) {
          if (typeof value !== "string") {
            return [
              Error(
                `Formatter must return a string, null or undefined, got ${JSON.stringify(value)}`,
              ),
            ];
          }
          const containsdelimiter =
            delimiter.length && value.indexOf(delimiter) >= 0;
          const containsQuote = quote !== "" && value.indexOf(quote) >= 0;
          const containsEscape = value.indexOf(escape) >= 0 && escape !== quote;
          const containsRecordDelimiter = value.indexOf(record_delimiter) >= 0;
          const quotedString = quoted_string && typeof field === "string";
          let quotedMatch =
            quoted_match &&
            quoted_match.filter((quoted_match) => {
              if (typeof quoted_match === "string") {
                return value.indexOf(quoted_match) !== -1;
              } else {
                return quoted_match.test(value);
              }
            });
          quotedMatch = quotedMatch && quotedMatch.length > 0;
          // See https://github.com/adaltas/node-csv/pull/387
          // More about CSV injection or formula injection, when websites embed
          // untrusted input inside CSV files:
          // https://owasp.org/www-community/attacks/CSV_Injection
          // http://georgemauer.net/2017/10/07/csv-injection.html
          // Apple Numbers unicode normalization is empirical from testing
          if (escape_formulas) {
            switch (value[0]) {
              case "=":
              case "+":
              case "-":
              case "@":
              case "\t":
              case "\r":
              case "\uFF1D": // Unicode '='
              case "\uFF0B": // Unicode '+'
              case "\uFF0D": // Unicode '-'
              case "\uFF20": // Unicode '@'
                value = `'${value}`;
                break;
            }
          }
          const shouldQuote =
            containsQuote === true ||
            containsdelimiter ||
            containsRecordDelimiter ||
            quoted ||
            quotedString ||
            quotedMatch;
          if (shouldQuote === true && containsEscape === true) {
            const regexp =
              escape === "\\"
                ? new RegExp(escape + escape, "g")
                : new RegExp(escape, "g");
            value = value.replace(regexp, escape + escape);
          }
          if (containsQuote === true) {
            const regexp = new RegExp(quote, "g");
            value = value.replace(regexp, escape + quote);
          }
          if (shouldQuote === true) {
            value = quote + value + quote;
          }
          csvrecord += value;
        } else if (
          quoted_empty === true ||
          (field === "" && quoted_string === true && quoted_empty !== false)
        ) {
          csvrecord += quote + quote;
        }
        if (i !== record.length - 1) {
          csvrecord += delimiter;
        }
      }
      return [undefined, csvrecord];
    },
    bom: function (push) {
      if (this.options.bom !== true) {
        return;
      }
      push(bom_utf8);
    },
    headers: function (push) {
      if (this.options.header === false) {
        return;
      }
      if (this.options.columns === undefined) {
        return;
      }
      let err;
      let headers = this.options.columns.map((column) => column.header);
      if (this.options.eof) {
        [err, headers] = this.stringify(headers, true);
        headers += this.options.record_delimiter;
      } else {
        [err, headers] = this.stringify(headers);
      }
      if (err) return err;
      push(headers);
    },
    __cast: function (value, context) {
      const type = typeof value;
      try {
        if (type === "string") {
          // Fine for 99% of the cases
          return [undefined, this.options.cast.string(value, context)];
        } else if (type === "bigint") {
          return [undefined, this.options.cast.bigint(value, context)];
        } else if (type === "number") {
          return [undefined, this.options.cast.number(value, context)];
        } else if (type === "boolean") {
          return [undefined, this.options.cast.boolean(value, context)];
        } else if (value instanceof Date) {
          return [undefined, this.options.cast.date(value, context)];
        } else if (type === "object" && value !== null) {
          return [undefined, this.options.cast.object(value, context)];
        } else {
          return [undefined, value, value];
        }
      } catch (err) {
        return [err];
      }
    },
  };
};

const stringify = function (records, opts = {}) {
  const data = [];
  const [err, options] = normalize_options(opts);
  if (err !== undefined) throw err;
  const state = {
    stop: false,
  };
  // Information
  const info = {
    records: 0,
  };
  const api = stringifier(options, state, info);
  for (const record of records) {
    const err = api.__transform(record, function (record) {
      data.push(record);
    });
    if (err !== undefined) throw err;
  }
  if (data.length === 0) {
    api.bom((d) => {
      data.push(d);
    });
    const err = api.headers((headers) => {
      data.push(headers);
    });
    if (err !== undefined) throw err;
  }
  return data.join("");
};

var css_248z$7 = "cds-custom-table-header-description,cds-custom-table-header-title,cds-custom-table-toolbar{position:sticky;width:var(--cds-chat-table-width,auto);left:0}:dir(rtl) cds-custom-table-header-description,:dir(rtl) cds-custom-table-header-title,:dir(rtl) cds-custom-table-toolbar{left:unset;right:0}cds-custom-table-header-description,cds-custom-table-header-title{width:calc(var(--cds-chat-table-width, auto) - 32px);margin-left:-16px;margin-right:-16px;padding:0 16px}cds-custom-table-header-description{margin-bottom:-8px}cds-custom-pagination{width:100%}";

const Download16svg = toString$1({
    ...Download16,
    attrs: {
        ...Download16.attrs,
        slot: 'icon',
    },
});
/**
 * Table view logic.
 */
function tableTemplate(tableElement) {
    const { tableTitle, tableDescription, headers, containerWidth, filterPlaceholderText, locale, _handleDownload: handleDownload, _rowsWithIDs: tableRowsWithIDs, _allowFiltering: allowTableFiltering, _handleFilterEvent: handleFilterEvent, } = tableElement;
    function toolbarElement() {
        return html$2 `<cds-custom-table-toolbar slot="toolbar">
      <cds-custom-table-toolbar-content>
        ${allowTableFiltering
            ? html$2 `<cds-custom-table-toolbar-search
              persistent
              placeholder=${filterPlaceholderText}
            ></cds-custom-table-toolbar-search>`
            : ''}
        <cds-custom-button @click=${handleDownload}>${unsafeSVG(Download16svg)}</cds-custom-button>
      </cds-custom-table-toolbar-content>
    </cds-custom-table-toolbar>`;
    }
    function headersElement() {
        return html$2 `<cds-custom-table-head>
      <cds-custom-table-header-row>
        ${headers.map(header => html$2 `<cds-custom-table-header-cell>${header}</cds-custom-table-header-cell>`)}
      </cds-custom-table-header-row>
    </cds-custom-table-head>`;
    }
    function rowsElement() {
        return html$2 `<cds-custom-table-body>
      ${repeat(tableRowsWithIDs, row => row.id, row => 
        // TODO TABLE: The Carbon table is suppose to support mixing expandable and non-expandable rows by using the
        // "expandable" prop on a row that should expand (as described towards the bottom of this section
        // https://web-components.carbondesignsystem.com/?path=/docs/components-datatable-basic--overview#expansion).
        // However, that functionality is currently broken. When this issue
        // (https://github.com/carbon-design-system/carbon/issues/17612) is resolved, then we can implement dynamic
        // expansion.
        html$2 `<cds-custom-table-row id=${row.id}
            >${row.cells.map(cell => html$2 `<cds-custom-table-cell>${cell}</cds-custom-table-cell>`)}</cds-custom-table-row
          >`)}
    </cds-custom-table-body>`;
    }
    // TODO TABLE: Do not enable expansion for now since we can't mix expandable and non-expandable rows (and support for
    // styling of user defined sections is not ready at this time). When we do decide to support expansion we will need to
    // render something like <MessageTypeComponent /> within the React container, pass it down to the TableElement
    // component, render the contents in a React portal, and have the root of the portal fill the slot of the appropriate
    // expandable section.
    // TODO TABLE: There is a bug with size="sm" and is-sortable that prevents the header row from being the same size as
    // the rest of the rows https://github.com/carbon-design-system/carbon/issues/17680. For now keep size="md" until that
    // bug is fixed.
    // Enable sorting if filtering is enabled.
    return html$2 `<cds-custom-table
    style="--cds-chat-table-width:${containerWidth}px"
    size="md"
    locale=${locale}
    .isSortable=${allowTableFiltering}
    .useZebraStyles=${true}
    @cds-custom-table-filtered=${handleFilterEvent}
  >
    ${tableTitle && html$2 `<cds-custom-table-header-title slot="title">${tableTitle}</cds-custom-table-header-title>`}
    ${tableDescription &&
        html$2 `<cds-custom-table-header-description slot="description"
      >${tableDescription}</cds-custom-table-header-description
    >`}
    ${toolbarElement()} ${headersElement()} ${rowsElement()}
  </cds-custom-table>`;
}

/**
 * Table pagination view logic.
 */
function tablePaginationTemplate(tableElement) {
    const { _currentPageSize: currentPageSize, _currentPageNumber: currentPageNumber, _filterVisibleRowIDs: filterVisibleRowIDs, rows, previousPageText, nextPageText, itemsPerPageText, getPaginationSupplementalText, getPaginationStatusText, _handlePageChangeEvent: handlePageChangeEvent, _handlePageSizeChangeEvent: handlePageSizeChangeEvent, } = tableElement;
    const totalVisibleRows = filterVisibleRowIDs.size;
    const totalRows = rows.length;
    // Page sizes will only be included if the page size is less than the total number of rows.
    const supportedPageSizes = POSSIBLE_PAGE_SIZES.filter(pageSize => pageSize < totalRows);
    // TODO TABLE: This component is quite wide. Because of the shadow dom we can't select it's contents to hide items
    // with css, nor can we extend this class to manipulate it's styles because of Carbon's use of :host(cds-custom-pagination)
    // within their styles. There is however a smaller variation of this component
    // (https://carbondesignsystem.com/components/pagination/usage/#responsive-behavior) but it's only used at a specific
    // breakpoint, when the viewport is narrow (i.e. a mobile device). A Carbon enhancement request has been made to
    // expose a prop that can be used to enable this smaller variation
    // (https://github.com/carbon-design-system/carbon/issues/17564). When that enhancement is done, and we can
    // dynamically enable a narrow form factor of this pagination component, then we could use the same css trick we used
    // for the header to make the pagination component sticky (if the carbon component doesn't already do it for us).
    return html$2 `<cds-custom-pagination
    page-size=${currentPageSize}
    page=${currentPageNumber}
    total-items=${totalVisibleRows}
    totalPages=${Math.ceil(totalVisibleRows / currentPageSize)}
    backward-text=${previousPageText}
    forward-text=${nextPageText}
    items-per-page-text=${itemsPerPageText}
    .formatSupplementalText=${getPaginationSupplementalText}
    .formatStatusWithDeterminateTotal=${getPaginationStatusText}
    @cds-custom-pagination-changed-current=${handlePageChangeEvent}
    @cds-custom-page-sizes-select-changed=${handlePageSizeChangeEvent}
  >
    ${supportedPageSizes.map(pageSize => html$2 `<cds-custom-select-item value="${pageSize}">${pageSize}</cds-custom-select-item>`)}
    <cds-custom-select-item value="${totalRows}">${totalRows}</cds-custom-select-item>
  </cds-custom-pagination>`;
}

/**
 * Table skeleton view logic.
 */
function tableSkeletonTemplate() {
    // Can add header names to the skeleton if we want to
    // https://web-components.carbondesignsystem.com/?path=/docs/components-datatable-skeleton--overview#custom-headers.
    return html$2 `<cds-custom-table-skeleton row-count="3" column-count="2"> </cds-custom-table-skeleton>`;
}

const DEFAULT_TABLE_PAGE_SIZE = 5;
const DEFAULT_TABLE_PAGE_SIZE_TALL_CHAT = 10;
// The supported possible page sizes are 5, 10, 15, 20, 50, and all rows.
const POSSIBLE_PAGE_SIZES = [DEFAULT_TABLE_PAGE_SIZE, DEFAULT_TABLE_PAGE_SIZE_TALL_CHAT, 15, 20, 50];
const TALL_CHAT_HEIGHT = 850;
// We will have to give this component a unique ID on the name when we register it to avoid conflicts in a world where
// multiple versions of the web chat can be on the same page.
const TABLE_COMPONENT_TAG_NAME = 'cds-aichat-table';
/**
 * Class functionality for the Table custom element.
 */
let TableElement = class TableElement extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * If the table is valid or not.
         */
        this._isValid = true;
        /**
         * The current page of the table we're on.
         */
        this._currentPageNumber = 1;
        /**
         * Whether or not the number of rows per page was changed with the pagination component. If the user used the
         * pagination controls to show all rows, the pagination component would normally hide itself because the number of is
         * no longer greater than the page size. However the pagination component needs to persist in this case, since the
         * user may want to change the page size again. This is used to keep track of whether the pagination controls were
         * used to change page size, and if they have, it forces pagination component to persist.
         */
        this._rowsPerPageChanged = false;
        /**
         * All of the rows for the table with IDs.
         */
        this._rowsWithIDs = [];
    }
    static { this.styles = css `
    ${unsafeCSS(css_248z$7)}
  `; }
    /**
     * Depending on the properties that changed validate the table or change the number of rows displayed in the table.
     * There is also a [firstUpdated](https://lit.dev/docs/components/lifecycle/#firstupdated) function from lit that only
     * fires after the components DOM has been updated the first time. That could potentially be used in these scenarios /
     * similar scenarios as it seems similar to the old componentDidMount. For now use this broader function to make sure
     * we see the prop updates we're expecting.
     */
    updated(changedProperties) {
        // If the headers or rows has recently updated and both are defined than we should validate the table
        // data. This will likely only happen on the web components first render cycle when the props go from undefined to
        // defined.
        if ((changedProperties.has('headers') || changedProperties.has('rows')) &&
            this.headers !== undefined &&
            this.rows !== undefined) {
            this._calcIsTableValid();
        }
        // If the value of tableRows updated then initialize the internal rows arrays.
        if (changedProperties.has('rows') && this.rows !== undefined) {
            this._initializeRowsArrays();
        }
        // If the page size is not currently defined, and the chatHeight has been set, then this is the first render and we
        // need to choose the starting page size based off the chatHeight.
        if (this._currentPageSize === undefined &&
            changedProperties.has('chatHeight') &&
            this.chatHeight !== changedProperties.get('chatHeight')) {
            this._initializePageSize();
        }
    }
    /**
     * Check if the table content is valid. A table is valid if the number of cells in each row is the same as the number
     * of cells in the header.
     */
    _calcIsTableValid() {
        const columnCount = this.headers.length;
        // If one of the rows has a different number of cells than the number of columns in the header then set our internal
        // valid state to false.
        this._isValid = !this.rows.some(row => row.cells.length !== columnCount);
        // In the future, once we have an inlineError web component to use, we can render that when our table is not valid
        // (as well as throwing the below error). Until then our React container is rendering the error component and
        // throwing the error.
        // throw new Error(
        //   `${WA_CONSOLE_PREFIX} Number of cells in the table header does not match the number of cells in one or more of the table rows. In order to render a table there needs to be the same number of columns in the table header and all of the table rows.`,
        // );
    }
    /**
     * Create a new array of rows that includes ids for each row. Also build an array of those same ids representing the
     * rows that have not been filtered out.
     */
    _initializeRowsArrays() {
        // Reset both arrays.
        this._rowsWithIDs = [];
        this._filterVisibleRowIDs = new Set();
        this.rows.forEach((row, index) => {
            const id = index.toString();
            this._rowsWithIDs.push({ ...row, id });
            this._filterVisibleRowIDs.add(id);
        });
    }
    /**
     * If the chat container is over a certain height then show more rows per page.
     */
    _initializePageSize() {
        // If the chat container is above a certain height then show more rows per page.
        if (this.chatHeight > TALL_CHAT_HEIGHT) {
            this._currentPageSize = DEFAULT_TABLE_PAGE_SIZE_TALL_CHAT;
        }
        else {
            this._currentPageSize = DEFAULT_TABLE_PAGE_SIZE;
        }
        // If there are more rows than the page size then enable filtering.
        this._allowFiltering = this.rows.length > this._currentPageSize;
        // Update the visible rows in case the page size has changed or this is the first time this web component has
        // rendered.
        this._updateVisibleRows();
    }
    /**
     * When the page change event is fired by the pagination component, change which rows are visible. For some reason
     * only this event is firing twice. However the page size and number is the same both times so the functionality of
     * the pagination component works as expected. Ignore this double firing for now since it appears to be a bug on
     * Carbon's end that will likely be fixed in a future release.
     */
    _handlePageChangeEvent(event) {
        this._updateVisibleRows(event.detail?.page, event.detail?.pageSize);
        event.stopPropagation();
    }
    /**
     * When the number of rows per page is changed, update the current page size and change which rows are visible.
     */
    _handlePageSizeChangeEvent(event) {
        this._rowsPerPageChanged = true;
        this._currentPageSize = event.detail?.pageSize;
        this._updateVisibleRows();
        event.stopPropagation();
    }
    /**
     * When the filter event is fired, record which rows have not been filtered out, and update which rows are visible.
     * This event is fired when the columns are sorted using the header cells AND when the table is filtered using the
     * search bar.
     */
    _handleFilterEvent(event) {
        // Record the new set of unfiltered row ids.
        this._filterVisibleRowIDs = new Set(event?.detail?.unfilteredRows.map(row => row.id));
        // Go back to the first page.
        this._currentPageNumber = 1;
        // Update which rows are visible.
        this._updateVisibleRows();
        event.stopPropagation();
    }
    /**
     * Given the current page number, page size, and array of rows that have not been filtered out, determine which rows
     * to show and hide using css.
     */
    _updateVisibleRows(page = this._currentPageNumber, pageSize = this._currentPageSize) {
        // Set the current page number and only show the rows for that page.
        this._currentPageNumber = page;
        // Grab all the rows that have been rendered. It's necessary to grab them from the page because the cds-custom-table puts
        // the rows in a specific order when sorting and we want to preserve that order.
        const rows = Array.from(this.renderRoot.querySelectorAll('cds-custom-table-row'));
        // This is similar to the carbon example here https://stackblitz.com/edit/github-kbd9xw-s3y3s6?file=index.html. I
        // originally tried creating and passing an array of the specific rows the template should render for the current
        // page. Unfortunately doing that doesn't work with the combination of the is-sortable prop for the table and
        // page-size prop for pagination. What would happen is old rows would persist if you were on the last page and there
        // weren't enough rows to fill the page (even though the old rows weren't included in the new rows provided to the
        // template). Using Carbon's example of hiding the rows with styling works so I'm extending that concept here even
        // though, ideally, render controls the view and reacts to the state that would be determined here.
        // Hide all the rows to start.
        rows.forEach(row => row.style.setProperty('display', 'none'));
        // Now filter the rows down to what is visible according to the filter.
        const filterVisibleRows = rows.filter(row => this._filterVisibleRowIDs.has(row.id));
        // Now show all the rows that are within the current page.
        const pageStart = (page - 1) * pageSize;
        const pageEnd = page * pageSize - 1;
        for (let index = pageStart; index <= pageEnd; index++) {
            // If there is a row at that index then show it. If there aren't enough rows to fill the page then there won't be
            // a row at that index.
            filterVisibleRows[index]?.removeAttribute('style');
        }
    }
    /**
     * Carbon's data table component by default only downloads rows that have been selected, and only downloads json. We
     * don't want to implement selection at this time and we want our download to be csv so we need to implement own
     * download function.
     */
    _handleDownload() {
        // Don't save content from the expandable rows at this time. This could be added in the future but it's unclear how
        // this would look in the download.
        const tableArray = [this.headers, ...this.rows.map(row => row.cells)];
        // stringify docs here: https://csv.js.org/stringify/api/sync/
        const stringifiedTable = stringify(tableArray);
        const blob = new Blob([stringifiedTable], { type: 'text/csv;charset=utf-8;' });
        // It appears the only way to control the file name of a downloaded file is through an anchor element with a
        // download attribute. So create such an element and add our data to be downloaded.
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'table-data.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        // Click on the link we've created to download the blob with our stringified table data.
        link.click();
        document.body.removeChild(link);
    }
    /**
     * Renders either the table skeleton or the table. If the table is larger than the page size then also render the
     * pagination component to change pages.
     */
    render() {
        // TODO TABLE: Once we have a web component version of the inline error state we could render that here if
        // !this._isValid.
        // This could be used while we wait for a md stream containing a table to complete.
        if (this.loading) {
            return tableSkeletonTemplate();
        }
        // If there are more rows than the page size then we need to add the pagination component. If the rows per page has
        // been changed by the pagination component then we need to keep the pagination component around so the user can
        // change the page size again, even if the current page size is the same as the number of table rows.
        if (this.rows.length > this._currentPageSize || this._rowsPerPageChanged) {
            return html$2 `${tableTemplate(this)} ${tablePaginationTemplate(this)}`;
        }
        // Otherwise, just render the table.
        return html$2 `${tableTemplate(this)}`;
    }
};
__decorate([
    property({ type: String, attribute: 'table-title' })
], TableElement.prototype, "tableTitle", void 0);
__decorate([
    property({ type: String, attribute: 'table-description' })
], TableElement.prototype, "tableDescription", void 0);
__decorate([
    property({ type: Array })
], TableElement.prototype, "headers", void 0);
__decorate([
    property({ type: Array })
], TableElement.prototype, "rows", void 0);
__decorate([
    property({ type: Number, attribute: 'container-width' })
], TableElement.prototype, "containerWidth", void 0);
__decorate([
    property({ type: Number, attribute: 'chat-height' })
], TableElement.prototype, "chatHeight", void 0);
__decorate([
    property({ type: Boolean, attribute: 'loading' })
], TableElement.prototype, "loading", void 0);
__decorate([
    property({ type: String, attribute: 'filter-placeholder-text' })
], TableElement.prototype, "filterPlaceholderText", void 0);
__decorate([
    property({ type: String, attribute: 'previous-page-text' })
], TableElement.prototype, "previousPageText", void 0);
__decorate([
    property({ type: String, attribute: 'next-page-text' })
], TableElement.prototype, "nextPageText", void 0);
__decorate([
    property({ type: String, attribute: 'items-per-page-text' })
], TableElement.prototype, "itemsPerPageText", void 0);
__decorate([
    property({ type: String, attribute: 'locale' })
], TableElement.prototype, "locale", void 0);
__decorate([
    property({ type: Function, attribute: false })
], TableElement.prototype, "getPaginationSupplementalText", void 0);
__decorate([
    property({ type: Function, attribute: false })
], TableElement.prototype, "getPaginationStatusText", void 0);
__decorate([
    state()
], TableElement.prototype, "_isValid", void 0);
__decorate([
    state()
], TableElement.prototype, "_currentPageNumber", void 0);
__decorate([
    state()
], TableElement.prototype, "_currentPageSize", void 0);
__decorate([
    state()
], TableElement.prototype, "_rowsPerPageChanged", void 0);
__decorate([
    state()
], TableElement.prototype, "_filterVisibleRowIDs", void 0);
__decorate([
    state()
], TableElement.prototype, "_rowsWithIDs", void 0);
__decorate([
    state()
], TableElement.prototype, "_allowFiltering", void 0);
__decorate([
    _default
], TableElement.prototype, "_handlePageChangeEvent", null);
__decorate([
    _default
], TableElement.prototype, "_handlePageSizeChangeEvent", null);
__decorate([
    _default
], TableElement.prototype, "_handleFilterEvent", null);
TableElement = __decorate([
    carbonElement(TABLE_COMPONENT_TAG_NAME)
], TableElement);

const Table = o$1({
    tagName: TABLE_COMPONENT_TAG_NAME,
    elementClass: TableElement,
    react: React__default,
});

/**
 * A hook that indicates whether general HTML should be sanitized.
 */
function useShouldSanitizeHTML() {
    const config = useSelector((state) => state.config.public);
    // We want to sanitize the HTML in all the tooling web chats, in the agent app or if the customer has asked for it.
    return Boolean(config.__ibm__?.toolingType || config.shouldSanitizeHTML || config.agentAppConfig?.is_on);
}

var require$$0$2 = [
	"a",
	"abbr",
	"address",
	"area",
	"article",
	"aside",
	"audio",
	"b",
	"base",
	"bdi",
	"bdo",
	"blockquote",
	"body",
	"br",
	"button",
	"canvas",
	"caption",
	"cite",
	"code",
	"col",
	"colgroup",
	"data",
	"datalist",
	"dd",
	"del",
	"details",
	"dfn",
	"dialog",
	"div",
	"dl",
	"dt",
	"em",
	"embed",
	"fieldset",
	"figcaption",
	"figure",
	"footer",
	"form",
	"h1",
	"h2",
	"h3",
	"h4",
	"h5",
	"h6",
	"head",
	"header",
	"hgroup",
	"hr",
	"html",
	"i",
	"iframe",
	"img",
	"input",
	"ins",
	"kbd",
	"label",
	"legend",
	"li",
	"link",
	"main",
	"map",
	"mark",
	"math",
	"menu",
	"menuitem",
	"meta",
	"meter",
	"nav",
	"noscript",
	"object",
	"ol",
	"optgroup",
	"option",
	"output",
	"p",
	"param",
	"picture",
	"pre",
	"progress",
	"q",
	"rb",
	"rp",
	"rt",
	"rtc",
	"ruby",
	"s",
	"samp",
	"script",
	"search",
	"section",
	"select",
	"slot",
	"small",
	"source",
	"span",
	"strong",
	"style",
	"sub",
	"summary",
	"sup",
	"svg",
	"table",
	"tbody",
	"td",
	"template",
	"textarea",
	"tfoot",
	"th",
	"thead",
	"time",
	"title",
	"tr",
	"track",
	"u",
	"ul",
	"var",
	"video",
	"wbr"
];

var htmlTags = require$$0$2;

var htmlTags$1 = /*@__PURE__*/getDefaultExportFromCjs$1(htmlTags);

var require$$0$1 = [
	"area",
	"base",
	"br",
	"col",
	"embed",
	"hr",
	"img",
	"input",
	"link",
	"menuitem",
	"meta",
	"param",
	"source",
	"track",
	"wbr"
];

var _void = require$$0$1;

var voidHtmlTags = /*@__PURE__*/getDefaultExportFromCjs$1(_void);

/**
 * Determines if the given string represents a valid URL. This is a very very very lazy check but a more robust check
 * has performance issues.
 */
function isValidURL(string) {
    if (isEmptyString(string)) {
        return false;
    }
    // For performance, lets short circuit doing the full regex check if the url doesn't have any basics.
    return string.includes('http://') || string.includes('https://');
}
/**
 * Sanitizes the given block of HTML to remove potentially malicious content. This will strip out everything that's
 * not in the allowed set provided by the library we're using here. We use the defaults for DOMPurify, but do allow
 * "target" to be set.
 */
function sanitizeHTML(content) {
    return purify.sanitize(content, { ADD_ATTR: ['target'] });
}
/**
 * Remove all HTML tags. We use an external library to make sure we don't forget any tags to forbid.
 * Custom HTML tags won't hurt anyone.
 */
function removeHTML(content) {
    return purify.sanitize(content, { FORBID_TAGS: [...htmlTags$1, ...voidHtmlTags] });
}

/**
 * This component will render the given HTML using dangerouslySetInnerHTML, but it will sanitize the content first.
 *
 * This was added as an emergency fix to deal with the fact that the web chat is susceptible to script injection
 * attacks from a tooling author. For the web chat this is normally not a problem as we can trust a company's
 * employees to not attack themselves. However, in the case of the preview link which is hosted on ibm.com and
 * requires no authentication to access, all of ibm.com is vulnerable. So we need to avoid being dangerous for ibm.com.
 */
function MaybeDangerouslySetInnerHTML({ html, overrideSanitize, className, id, asSpan, }) {
    let doSanitize = useShouldSanitizeHTML();
    if (overrideSanitize !== undefined) {
        doSanitize = overrideSanitize;
    }
    if (doSanitize) {
        html = sanitizeHTML(html);
    }
    if (asSpan) {
        // eslint-disable-next-line react/no-danger
        return React__default.createElement("span", { id: id, className: className, dangerouslySetInnerHTML: { __html: html } });
    }
    // eslint-disable-next-line react/no-danger
    return React__default.createElement("div", { id: id, className: className, dangerouslySetInnerHTML: { __html: html } });
}
const MaybeDangerouslySetInnerHTMLExport = React__default.memo(MaybeDangerouslySetInnerHTML);

/**
 * This component will display some text as formatted HTML in the browser. It will process the provided text and use
 * {@link processMarkdown} to link for links in the text to convert to anchors as well as looking for a limited set of
 * Markdown to format as HTML.
 *
 * Warning: This should only be used with trusted text. Do NOT use this with text that was entered by the end-user.
 */
function RichText(props) {
    const { store } = useServiceManager();
    const { config } = store.getState();
    const { text, shouldRemoveHTMLBeforeMarkdownConversion, overrideSanitize, shouldRemovePadding } = props;
    const preformattedText = shouldRemoveHTMLBeforeMarkdownConversion ? removeHTML(text) : text;
    let doSanitize = useShouldSanitizeHTML();
    if (overrideSanitize !== undefined) {
        doSanitize = overrideSanitize;
    }
    // TODO: only MarkdownText in 9.0
    if (config.asCarbonChat || IS_JEST) {
        return (React__default.createElement(MarkdownText, { markdown: preformattedText, sanitizeHTML: doSanitize, shouldRemovePadding: shouldRemovePadding }));
    }
    const html = processMarkdown(preformattedText);
    return (React__default.createElement(MaybeDangerouslySetInnerHTMLExport, { className: "ibm-web-chat--default-styles", html: html, overrideSanitize: overrideSanitize }));
}
const RichTextExport = React__default.memo(RichText);

function lazyPDFViewer() {
    return React__default.lazy(() => import(/* webpackChunkName: 'pdf-viewer' */ './PDFViewerContainer.js'));
}
function lazyMediaPlayer() {
    return React__default.lazy(() => import(/* webpackChunkName: 'media-player' */ './ReactPlayer.js'));
}
function lazyCarousel() {
    return React__default.lazy(() => import(/* webpackChunkName: 'carousel' */ './Carousel.js'));
}

function SkeletonText(props) {
    const useAITheme = useSelector((state) => state.theme.useAITheme);
    return useAITheme ? React__default.createElement(AISkeletonText, { ...props }) : React__default.createElement(SkeletonText$1, { ...props });
}
function SkeletonPlaceholder(props) {
    const useAITheme = useSelector((state) => state.theme.useAITheme);
    return useAITheme ? React__default.createElement(AISkeletonPlaceholder, { ...props }) : React__default.createElement(SkeletonPlaceholder$1, { ...props });
}

// https://reactjs.org/docs/code-splitting.html#reactlazy
const ReactPlayerComponent = lazyMediaPlayer();
/**
 * This component uses the React player library to handle rendering video/audio files, as well as handling third-party
 * embeddable video/audio services. Learn more: https://github.com/cookpete/react-player
 *
 * Note: We force media files to render using a video element for accessibility purposes since the audio element doesn't
 * support WebVTT for captioning/transcripts, but the video element does.
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/audio#accessibility_concerns
 */
function MediaPlayerComponent({ type, source, title, description, ariaLabel, isMixcloud, baseHeight, doAutoScroll, playing, onPlay, onPause, hideIconAndTitle, needsAnnouncement, }) {
    const [skeletonHidden, setSkeletonHidden] = useState(false);
    const [errorLoading, setErrorLoading] = useState(false);
    const { errors_audioSource, errors_videoSource } = useLanguagePack();
    const ariaAnnouncer = useAriaAnnouncer();
    const rootElementRef = useRef();
    const paddingTop = isMixcloud ? '120px' : getResponsiveElementPaddingValue(baseHeight);
    const isAudio = type === MessageResponseTypes.AUDIO;
    const errorMessage = isAudio ? errors_audioSource : errors_videoSource;
    const prevSource = usePrevious(source);
    // This ref is for merely saving the initial value of shouldAnnounce prop.
    const shouldAnnounceRef = useRef(needsAnnouncement);
    /**
     * Upon an error, update the error loading flag in order to render an inline error.
     */
    const handleError = useCallback(() => {
        setErrorLoading(true);
        setSkeletonHidden(true);
    }, []);
    useEffect(() => {
        if (source !== prevSource && skeletonHidden) {
            setSkeletonHidden(false);
        }
    }, [prevSource, skeletonHidden, source]);
    // This effect sets a timeout that auto error handles after 10 seconds of waiting for the React player to ready.
    // Once the player has loaded, the skeleton will be hidden, and we can clear the timeout.
    useEffect(() => {
        let errorTimeout = null;
        if (!skeletonHidden) {
            errorTimeout = setTimeout(handleError, RESPONSE_TYPE_TIMEOUT_MS);
        }
        return () => {
            clearTimeout(errorTimeout);
        };
    }, [skeletonHidden, handleError]);
    useEffect(() => {
        if (skeletonHidden && shouldAnnounceRef.current) {
            ariaAnnouncer(rootElementRef.current);
        }
    }, [ariaAnnouncer, skeletonHidden]);
    /**
     * Once the video player is finished loading, remove the skeleton.
     */
    const handleReady = useCallback(() => {
        if (!skeletonHidden) {
            setSkeletonHidden(true);
            doAutoScroll?.();
        }
    }, [doAutoScroll, skeletonHidden]);
    /**
     * Renders a media player skeleton. This is rendered until the react-player has loaded the provided url.
     */
    function renderMediaPlayerSkeleton() {
        return (React__default.createElement(Tile, { className: "WACMediaPlayer__Skeleton" },
            React__default.createElement("div", { className: "WACMediaPlayer__SkeletonContainer", style: { paddingTop } },
                React__default.createElement(SkeletonPlaceholder, { className: "WACMediaPlayer__SkeletonPlayer" })),
            (title || description) && (React__default.createElement("div", { className: "WACMediaPlayer__SkeletonTextContainer" },
                React__default.createElement(SkeletonText, { paragraph: true, lineCount: 2 })))));
    }
    /**
     * Render a media player background that adds letterboxes to urls utilizing iframes that may not have them included
     * already. If an audio file is being played, we should display a music icon in the center since audio files will be
     * loaded using a video element and a 16:9 blank video element with controls playing audio would look weird.
     */
    function renderMediaPlayerBackground() {
        return (React__default.createElement("div", { className: cx('WACMediaPlayer__Background', {
                'WACMediaPlayer__Background--audio': isAudio,
            }) }, isAudio && React__default.createElement(Music, { size: 32, className: "WACMediaPlayer__MusicIcon" })));
    }
    /**
     * If the dynamically imported react-player component is in suspense, we don't need to display some loading indicator
     * since the media player skeleton handles that for us and the media player is hidden with `display: none` until the
     * react-player has loaded the url.
     */
    function renderSuspenseFallback() {
        return React__default.createElement(React__default.Fragment, null);
    }
    return (React__default.createElement(React__default.Fragment, null,
        !skeletonHidden && renderMediaPlayerSkeleton(),
        React__default.createElement("div", { className: "WACMediaPlayer__Root", ref: rootElementRef },
            errorLoading && React__default.createElement(InlineError, { text: errorMessage }),
            !errorLoading && (React__default.createElement(Tile, { className: cx('WACMediaPlayer', { WAC__hidden: !skeletonHidden }) },
                React__default.createElement("div", { className: "WACMediaPlayer__Wrapper", style: { paddingTop } },
                    renderMediaPlayerBackground(),
                    React__default.createElement(Suspense, { fallback: renderSuspenseFallback() },
                        React__default.createElement(ReactPlayerComponent, { className: "WACMediaPlayer__Player", url: source, controls: true, width: "100%", height: "100%", config: {
                                file: {
                                    forceVideo: true,
                                    attributes: {
                                        controlsList: 'nodownload',
                                        'aria-label': ariaLabel || description || title,
                                    },
                                },
                            }, playsinline: true, playing: playing, onPlay: onPlay, onPause: onPause, onReady: handleReady, onError: handleError, pip: true }))),
                (title || description) && (React__default.createElement(TextHolderTile, { title: title, description: description, hideTitle: hideIconAndTitle })))))));
}
const MediaPlayerExport = React__default.memo(MediaPlayerComponent);

function VideoComponent({ ...props }) {
    return React__default.createElement(MediaPlayerExport, { type: MessageResponseTypes.VIDEO, ...props });
}
const VideoComponentExport = React__default.memo(VideoComponent);

/**
 * This renders the content for a particular step within the tour.
 */
function TourStepContentComponent(props) {
    const { stepGenericItem, isCurrentStep } = props;
    const serviceManager = useServiceManager();
    const ariaAnnouncer = useAriaAnnouncer();
    const rootRef = useRef();
    /**
     * Determine if a border should be shown between the step content and the tour controls.
     */
    function showBottomBorder() {
        const responseType = stepGenericItem.response_type;
        switch (responseType) {
            case MessageResponseTypes.TEXT:
                // For a text step we want to show a border between the text and the tour controls.
                return true;
            case MessageResponseTypes.IMAGE:
                // If the Image is going to have a description then we want to show a border between the description and the
                // tour controls.
                return stepGenericItem?.description;
            case MessageResponseTypes.VIDEO:
                // If the Video is going to have a description then we want to show a border between the description and the
                // tour controls.
                return stepGenericItem?.description;
            default:
                // For an unsupported step we want to show a border between the error and the tour controls.
                return true;
        }
    }
    /**
     * Render the appropriate component for the given message responseType.
     */
    function renderStepContent() {
        const responseType = stepGenericItem.response_type;
        switch (responseType) {
            case MessageResponseTypes.TEXT:
                return React__default.createElement(StepContentText, { messageItem: stepGenericItem });
            case MessageResponseTypes.IMAGE:
                return React__default.createElement(StepContentImage, { messageItem: stepGenericItem });
            case MessageResponseTypes.VIDEO:
                return React__default.createElement(StepContentVideo, { messageItem: stepGenericItem, isCurrentStep: isCurrentStep });
            default:
                // If the responseType is not one of the three supported types for tours then render an inline error.
                return React__default.createElement(StepContentUnsupportedError, { messageItem: stepGenericItem, isCurrentStep: isCurrentStep });
        }
    }
    // If this is the current step then send an event and track the type of response being shown.
    useEffect(() => {
        if (isCurrentStep) {
            serviceManager.trackService.track({
                eventName: 'Tour interaction',
                eventDescription: 'Tour step shown.',
                type: stepGenericItem.response_type,
            });
            // Announce the content of the current step. The announcement is handled in setTimeout so that it doesn't prevent
            // the initial announcement from being heard when the tour is open.
            setTimeout(() => ariaAnnouncer(rootRef.current));
        }
    }, [isCurrentStep, serviceManager.trackService, stepGenericItem.response_type, ariaAnnouncer]);
    return (React__default.createElement("div", { className: "WACTourStep__Wrapper", ref: rootRef },
        React__default.createElement("div", { className: cx('WACTourStep', 'WAC__message', {
                'WACTourStep--hidden': !isCurrentStep,
                WACTourStep__BottomBorder: showBottomBorder(),
            }) }, renderStepContent())));
}
/**
 * Given a TextItem return a RichText component containing the text from the message.
 */
function StepContentText(props) {
    const { messageItem } = props;
    const { text } = messageItem;
    // For text provided by the assistant, pass it through some HTML formatting before displaying it.
    return (React__default.createElement("div", { className: "WACTourStep__Text" },
        React__default.createElement(RichTextExport, { text: text })));
}
/**
 * Given an ImageItem return an Image component containing the image from the message.
 */
function StepContentImage(props) {
    const { messageItem } = props;
    const { source, description, alt_text } = messageItem;
    const languagePack = useLanguagePack();
    return (
    // Don't pass the title to the Image component since tours only support descriptions.
    React__default.createElement(ImageExport, { imageError: languagePack.errors_imageSource, source: source, description: description, altText: alt_text, hideIconAndTitle: true }));
}
/**
 * Given a VideoItem return a VideoComponent containing the video from the message.
 */
function StepContentVideo(props) {
    const { messageItem, isCurrentStep } = props;
    const { source, description, alt_text } = messageItem;
    const { viewState } = useSelector((state) => state.persistedToBrowserStorage.launcherState);
    const [isVideoPlaying, setIsVideoPlaying] = useState(false);
    // If this isn't the current step or the tour isn't visible then the video needs to be paused.
    useEffect(() => {
        if (!isCurrentStep || !viewState.tour) {
            setIsVideoPlaying(false);
        }
    }, [isCurrentStep, viewState.tour]);
    return (
    // Don't pass the title to the VideoComponent since tours only support descriptions.
    React__default.createElement(VideoComponentExport, { source: source, description: description, baseHeight: getMediaDimensions(messageItem)?.base_height, ariaLabel: alt_text, playing: isVideoPlaying, onPlay: () => setIsVideoPlaying(true), onPause: () => setIsVideoPlaying(false), hideIconAndTitle: true }));
}
/**
 * If the responseType is not one of the three supported types for tours then render an inline error.
 */
function StepContentUnsupportedError(props) {
    const { messageItem, isCurrentStep } = props;
    const languagePack = useLanguagePack();
    // If the current response type isn't a supported response type then log an error.
    useEffect(() => {
        if (isCurrentStep) {
            consoleError(`The response type you tried to use "${messageItem.response_type}" is not valid within a tour step. The supported response types in tour steps are text, video, and image.`);
        }
    }, [isCurrentStep, messageItem.response_type]);
    return (React__default.createElement("div", { className: "WACTourStep__UnsupportedResponse" },
        React__default.createElement(InlineError, { text: languagePack.errors_generalContent })));
}
const TourStepContentComponentExport = React__default.memo(TourStepContentComponent);

/**
 * This renders the skeleton of a step within the tour.
 */
function TourStepSkeletonComponent() {
    return (React__default.createElement("div", { className: "WACTourStep WACTourStep__Skeleton" },
        React__default.createElement(SkeletonPlaceholder, { className: "WACTourStep__SkeletonPlaceholder" })));
}

/**
 * This manages the tour feature controls and renders the tour content before passing both to either a mobile or desktop
 * container depending.
 */
function TourContainer(props, ref) {
    const languagePack = useLanguagePack();
    const serviceManager = useServiceManager();
    const ariaAnnouncer = useAriaAnnouncer();
    const intl = useIntl();
    const tourConfig = useSelector((state) => state.config.public.tourConfig);
    const { isHydrated } = useSelector((state) => state);
    const { viewState } = useSelector((state) => state.persistedToBrowserStorage.launcherState);
    const { activeTourCurrentStepIndex } = useSelector((state) => state.persistedToBrowserStorage.chatState.persistedTourState);
    const { activeTourStepItems } = useSelector((state) => state.tourState);
    // These three state variables are for future use when dynamic steps are introduced.
    const [renderLoadingBar, setRenderLoadingBar] = useState(false);
    const [renderErrorBanner, setRenderErrorBanner] = useState(false);
    const [renderErrorScreen, setRenderErrorScreen] = useState(false);
    const rootRef = useRef();
    const tourControlsRef = useRef();
    const namespace = serviceManager.namespace.originalName;
    const languageKey = namespace ? 'window_ariaTourRegionNamespace' : 'window_ariaTourRegion';
    const regionLabel = formatMessage(intl, languageKey, { namespace });
    // If we're showing the launcher or main window then hide the tour. We do this instead of unmounting so that we don't
    // lose scroll position, video playback position, etc.
    const hideTour = !viewState.tour;
    // Set default values for the tour controls that match what we want to show during the loading state. These values
    // will be updated when the web chat is hydrated and the activeTourStepItems become available.
    let reservePreviousButtonSpace = true;
    let hidePreviousButton = false;
    let renderDoneButton = false;
    let stepContentComponentsArray = [];
    // Once the web chat is hydrated, and we have the activeTourStepItems, create TourStepContentComponent's for each step
    // and set the correct values for the tour controls properties.
    if (isHydrated && activeTourStepItems) {
        // If there is more than one step in the tour then save space in the tour controls for a previous button.
        // This doesn't guarantee that the previous button will be visible, just that there will be room for it in the
        // controls.
        reservePreviousButtonSpace = activeTourStepItems.length > 1;
        // If there has been space reserved for the previous button then hide it if this is the first step of the tour.
        // Any other step will show the previous button in the space reserved.
        hidePreviousButton = activeTourCurrentStepIndex <= 0;
        // If this is the last step of the tour then show a done button instead of a next button.
        renderDoneButton = activeTourCurrentStepIndex >= activeTourStepItems.length - 1;
        // For each step (GenericItem) in the array of steps, create a TourStepContentComponent, and return it. Assign this
        // new array of TourStepContentComponents to our local array.
        stepContentComponentsArray = activeTourStepItems.map((step, index) => (React__default.createElement(TourStepContentComponentExport
        // eslint-disable-next-line react/no-array-index-key
        , { 
            // eslint-disable-next-line react/no-array-index-key
            key: index, stepGenericItem: step, isCurrentStep: index === activeTourCurrentStepIndex })));
    }
    else if (!isHydrated) {
        // If the web chat is not hydrated then show a skeleton component.
        stepContentComponentsArray = [React__default.createElement(TourStepSkeletonComponent, { key: 0 })];
    }
    /**
     * When the user clicks on the main window icon then fire the window open events and switch to the main window.
     */
    async function onOpenMainWindowClick() {
        // Try to open the main window.
        await serviceManager.actions.changeView(ViewType.MAIN_WINDOW, {
            mainWindowOpenReason: MainWindowOpenReason.TOUR_OPENED_OTHER_VIEW,
        });
        // Fire a track event for clicking the open main window button.
        serviceManager.trackService.track({
            eventName: 'Tour interaction',
            eventDescription: 'Tour opened main window.',
        });
    }
    /**
     * When the tour is finished switch to the launcher and clear the tour data. When the launcher is clicked the main
     * window will open.
     */
    async function onDoneClick() {
        // Fire the tour end event before switching to the launcher.
        await serviceManager.eventBus.fire({ type: "tour:end" /* BusEventType.TOUR_END */, reason: TourEndReason.DONE_CLICKED }, serviceManager.instance);
        // Try to open the launcher and clear the tour data from store.
        await serviceManager.actions.endTour({ viewChangeReason: ViewChangeReason.TOUR_DONE });
        // Fire a track event for clicking the done button.
        serviceManager.trackService.track({
            eventName: 'Tour interaction',
            eventDescription: 'Tour completed.',
        });
    }
    /**
     * When the tour is closed switch to the launcher and clear the tour data. When the launcher is clicked the main
     * window will open.
     */
    async function onCloseClick() {
        // Try to open the launcher and clear the tour data from store.
        await serviceManager.actions.endTour({
            viewChangeReason: ViewChangeReason.TOUR_CLOSED,
        });
        // Fire a track event for clicking the close button.
        serviceManager.trackService.track({
            eventName: 'Tour interaction',
            eventDescription: 'Tour closed.',
        });
    }
    /**
     * When the tour is minimized switch to the launcher. When the launcher is clicked the tour will open.
     */
    async function onMinimizeClick() {
        // Try to open the launcher.
        await serviceManager.actions.changeView(ViewType.LAUNCHER, {
            viewChangeReason: ViewChangeReason.TOUR_MINIMIZED,
        });
        // Fire a track event for clicking the minimize button.
        serviceManager.trackService.track({
            eventName: 'Tour interaction',
            eventDescription: 'Tour minimized.',
        });
    }
    /**
     * Now that we have the necessary pieces render the controls for the tour feature.
     */
    function renderTourControlsComponent() {
        return (React__default.createElement(TourControlsComponentExport, { ref: tourControlsRef, hideChatButton: tourConfig?.hideChatButton, reservePreviousButtonSpace: reservePreviousButtonSpace, hidePreviousButton: hidePreviousButton, disableNextButton: renderLoadingBar, hideButtons: !isHydrated, renderDoneButton: renderDoneButton, onNextClick: () => {
                serviceManager.actions.changeStepInTour({ nextStep: true });
                // If the next step is the final step, request focus so that the "done" button has focus.
                if (activeTourCurrentStepIndex + 1 === activeTourStepItems.length - 1) {
                    tourControlsRef.current?.requestFocus();
                }
            }, onPreviousClick: () => {
                serviceManager.actions.changeStepInTour({ previousStep: true });
                // If the previous step is the first step, request focus so that the "next" button has focus.
                if (activeTourCurrentStepIndex - 1 === 0) {
                    tourControlsRef.current?.requestFocus();
                }
            }, onOpenMainWindowClick: onOpenMainWindowClick, onDoneClick: onDoneClick }));
    }
    function renderTourCloseMinimizeComponent() {
        return (React__default.createElement(TourCloseMinimizeComponent, { hideMinimizeButton: tourConfig?.hideMinimizeButton, onCloseClick: onCloseClick, onMinimizeClick: onMinimizeClick }));
    }
    /**
     * Handles rendering a loading bar or error notification based on the state of the active tour.
     */
    function renderTourStepStatusContainer() {
        return (React__default.createElement("div", { className: "WACTour__StatusContainer" },
            renderErrorBanner && (React__default.createElement(AnnounceOnMountComponentExport, null,
                React__default.createElement(InlineNotification, { kind: "error", title: languagePack.tour_errorFetchingStep, lowContrast: true, hideCloseButton: true, onClick: () => setRenderErrorBanner(false) }))),
            renderLoadingBar && (React__default.createElement(MountChildrenOnDelay, { delay: 500 },
                React__default.createElement("div", { className: "WACLoadingBar__ConnectingAnimation" })))));
    }
    /**
     * The error screen when a tour fails to load.
     */
    function renderTourErrorScreen() {
        return (React__default.createElement(AnnounceOnMountComponentExport, null,
            React__default.createElement("div", { className: "WACTour__ErrorScreen" },
                React__default.createElement("div", { className: "WACTour__ErrorHeader" },
                    React__default.createElement(ErrorFilled, { size: 20, className: "WACTour__ErrorIcon" }),
                    languagePack.tour_errorTitle),
                React__default.createElement("div", { className: "WACTour__ErrorBody" },
                    React__default.createElement("div", { className: "WACTour__ErrorText" }, languagePack.tour_errorBody),
                    React__default.createElement(Button, { className: "WACTour__ErrorButton", size: "sm", kind: "tertiary", renderIcon: props => React__default.createElement(Restart, { size: 32, ...props }) }, languagePack.buttons_retry)))));
    }
    useImperativeHandle(ref, () => tourControlsRef.current);
    useEffect(() => {
        if (isHydrated && !hideTour && !renderErrorScreen) {
            const tourInstructionsText = intl.formatMessage({ id: 'tour_instructions' }, { chatButtonText: languagePack.tour_ariaChatButton });
            ariaAnnouncer(tourInstructionsText);
        }
    }, [hideTour, ariaAnnouncer, languagePack, intl, isHydrated, renderErrorScreen]);
    // This effect automatically applies focus to the tour when it hydrates or is opened. Checking for hydration will
    // allow the "done" button do get focus when it renders after hydration.
    useEffect(() => {
        if (isHydrated || !hideTour) {
            tourControlsRef.current?.requestFocus();
        }
    }, [hideTour, isHydrated]);
    return (
    // In the future this hiding may have to be done with visibility: hidden so that dynamic height animations can be
    // preformed.
    React__default.createElement(HideComponent, { hidden: hideTour },
        React__default.createElement("div", { ref: rootRef, className: "WACTour", role: "region", "aria-label": regionLabel },
            renderTourCloseMinimizeComponent(),
            renderErrorScreen && renderTourErrorScreen(),
            !renderErrorScreen && (React__default.createElement(React__default.Fragment, null,
                stepContentComponentsArray,
                renderTourStepStatusContainer(),
                renderTourControlsComponent())))));
}
const TourContainerExport = React__default.memo(React__default.forwardRef(TourContainer));

const AnalyticsContext = React__default.createContext(null);

/**
 * This context provides access to the current window size and is updated as the window size changes.
 */
const WindowSizeContext = React__default.createContext(null);

function withServiceManager(Component) {
    return React__default.forwardRef((props, ref) => {
        const serviceManager = useContext(ServiceManagerContext);
        return React__default.createElement(Component, { ...props, ref: ref, serviceManager: serviceManager });
    });
}

var require$$0 = /*@__PURE__*/getDefaultExportFromNamespaceIfNotNamed(React$1);

var propTypes = {exports: {}};

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var ReactPropTypesSecret_1;
var hasRequiredReactPropTypesSecret;

function requireReactPropTypesSecret () {
	if (hasRequiredReactPropTypesSecret) return ReactPropTypesSecret_1;
	hasRequiredReactPropTypesSecret = 1;

	var ReactPropTypesSecret = 'SECRET_DO_NOT_PASS_THIS_OR_YOU_WILL_BE_FIRED';

	ReactPropTypesSecret_1 = ReactPropTypesSecret;
	return ReactPropTypesSecret_1;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

var factoryWithThrowingShims;
var hasRequiredFactoryWithThrowingShims;

function requireFactoryWithThrowingShims () {
	if (hasRequiredFactoryWithThrowingShims) return factoryWithThrowingShims;
	hasRequiredFactoryWithThrowingShims = 1;

	var ReactPropTypesSecret = requireReactPropTypesSecret();

	function emptyFunction() {}
	function emptyFunctionWithReset() {}
	emptyFunctionWithReset.resetWarningCache = emptyFunction;

	factoryWithThrowingShims = function() {
	  function shim(props, propName, componentName, location, propFullName, secret) {
	    if (secret === ReactPropTypesSecret) {
	      // It is still safe when called from React.
	      return;
	    }
	    var err = new Error(
	      'Calling PropTypes validators directly is not supported by the `prop-types` package. ' +
	      'Use PropTypes.checkPropTypes() to call them. ' +
	      'Read more at http://fb.me/use-check-prop-types'
	    );
	    err.name = 'Invariant Violation';
	    throw err;
	  }	  shim.isRequired = shim;
	  function getShim() {
	    return shim;
	  }	  // Important!
	  // Keep this list in sync with production version in `./factoryWithTypeCheckers.js`.
	  var ReactPropTypes = {
	    array: shim,
	    bigint: shim,
	    bool: shim,
	    func: shim,
	    number: shim,
	    object: shim,
	    string: shim,
	    symbol: shim,

	    any: shim,
	    arrayOf: getShim,
	    element: shim,
	    elementType: shim,
	    instanceOf: getShim,
	    node: shim,
	    objectOf: getShim,
	    oneOf: getShim,
	    oneOfType: getShim,
	    shape: getShim,
	    exact: getShim,

	    checkPropTypes: emptyFunctionWithReset,
	    resetWarningCache: emptyFunction
	  };

	  ReactPropTypes.PropTypes = ReactPropTypes;

	  return ReactPropTypes;
	};
	return factoryWithThrowingShims;
}

/**
 * Copyright (c) 2013-present, Facebook, Inc.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

{
  // By explicitly using `prop-types` you are opting into new production behavior.
  // http://fb.me/prop-types-in-prod
  propTypes.exports = requireFactoryWithThrowingShims()();
}

var propTypesExports = propTypes.exports;

/*!
* focus-trap 7.5.4
* @license MIT, https://github.com/focus-trap/focus-trap/blob/master/LICENSE
*/

function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread2(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty$1(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
function _defineProperty$1(obj, key, value) {
  key = _toPropertyKey$1(key);
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function _toPrimitive$1(input, hint) {
  if (typeof input !== "object" || input === null) return input;
  var prim = input[Symbol.toPrimitive];
  if (prim !== undefined) {
    var res = prim.call(input, hint || "default");
    if (typeof res !== "object") return res;
    throw new TypeError("@@toPrimitive must return a primitive value.");
  }
  return (hint === "string" ? String : Number)(input);
}
function _toPropertyKey$1(arg) {
  var key = _toPrimitive$1(arg, "string");
  return typeof key === "symbol" ? key : String(key);
}

var activeFocusTraps = {
  activateTrap: function activateTrap(trapStack, trap) {
    if (trapStack.length > 0) {
      var activeTrap = trapStack[trapStack.length - 1];
      if (activeTrap !== trap) {
        activeTrap.pause();
      }
    }
    var trapIndex = trapStack.indexOf(trap);
    if (trapIndex === -1) {
      trapStack.push(trap);
    } else {
      // move this existing trap to the front of the queue
      trapStack.splice(trapIndex, 1);
      trapStack.push(trap);
    }
  },
  deactivateTrap: function deactivateTrap(trapStack, trap) {
    var trapIndex = trapStack.indexOf(trap);
    if (trapIndex !== -1) {
      trapStack.splice(trapIndex, 1);
    }
    if (trapStack.length > 0) {
      trapStack[trapStack.length - 1].unpause();
    }
  }
};
var isSelectableInput = function isSelectableInput(node) {
  return node.tagName && node.tagName.toLowerCase() === 'input' && typeof node.select === 'function';
};
var isEscapeEvent = function isEscapeEvent(e) {
  return (e === null || e === void 0 ? void 0 : e.key) === 'Escape' || (e === null || e === void 0 ? void 0 : e.key) === 'Esc' || (e === null || e === void 0 ? void 0 : e.keyCode) === 27;
};
var isTabEvent = function isTabEvent(e) {
  return (e === null || e === void 0 ? void 0 : e.key) === 'Tab' || (e === null || e === void 0 ? void 0 : e.keyCode) === 9;
};

// checks for TAB by default
var isKeyForward = function isKeyForward(e) {
  return isTabEvent(e) && !e.shiftKey;
};

// checks for SHIFT+TAB by default
var isKeyBackward = function isKeyBackward(e) {
  return isTabEvent(e) && e.shiftKey;
};
var delay = function delay(fn) {
  return setTimeout(fn, 0);
};

// Array.find/findIndex() are not supported on IE; this replicates enough
//  of Array.findIndex() for our needs
var findIndex = function findIndex(arr, fn) {
  var idx = -1;
  arr.every(function (value, i) {
    if (fn(value)) {
      idx = i;
      return false; // break
    }

    return true; // next
  });

  return idx;
};

/**
 * Get an option's value when it could be a plain value, or a handler that provides
 *  the value.
 * @param {*} value Option's value to check.
 * @param {...*} [params] Any parameters to pass to the handler, if `value` is a function.
 * @returns {*} The `value`, or the handler's returned value.
 */
var valueOrHandler = function valueOrHandler(value) {
  for (var _len = arguments.length, params = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
    params[_key - 1] = arguments[_key];
  }
  return typeof value === 'function' ? value.apply(void 0, params) : value;
};
var getActualTarget = function getActualTarget(event) {
  // NOTE: If the trap is _inside_ a shadow DOM, event.target will always be the
  //  shadow host. However, event.target.composedPath() will be an array of
  //  nodes "clicked" from inner-most (the actual element inside the shadow) to
  //  outer-most (the host HTML document). If we have access to composedPath(),
  //  then use its first element; otherwise, fall back to event.target (and
  //  this only works for an _open_ shadow DOM; otherwise,
  //  composedPath()[0] === event.target always).
  return event.target.shadowRoot && typeof event.composedPath === 'function' ? event.composedPath()[0] : event.target;
};

// NOTE: this must be _outside_ `createFocusTrap()` to make sure all traps in this
//  current instance use the same stack if `userOptions.trapStack` isn't specified
var internalTrapStack = [];
var createFocusTrap$1 = function createFocusTrap(elements, userOptions) {
  // SSR: a live trap shouldn't be created in this type of environment so this
  //  should be safe code to execute if the `document` option isn't specified
  var doc = (userOptions === null || userOptions === void 0 ? void 0 : userOptions.document) || document;
  var trapStack = (userOptions === null || userOptions === void 0 ? void 0 : userOptions.trapStack) || internalTrapStack;
  var config = _objectSpread2({
    returnFocusOnDeactivate: true,
    escapeDeactivates: true,
    delayInitialFocus: true,
    isKeyForward: isKeyForward,
    isKeyBackward: isKeyBackward
  }, userOptions);
  var state = {
    // containers given to createFocusTrap()
    // @type {Array<HTMLElement>}
    containers: [],
    // list of objects identifying tabbable nodes in `containers` in the trap
    // NOTE: it's possible that a group has no tabbable nodes if nodes get removed while the trap
    //  is active, but the trap should never get to a state where there isn't at least one group
    //  with at least one tabbable node in it (that would lead to an error condition that would
    //  result in an error being thrown)
    // @type {Array<{
    //   container: HTMLElement,
    //   tabbableNodes: Array<HTMLElement>, // empty if none
    //   focusableNodes: Array<HTMLElement>, // empty if none
    //   posTabIndexesFound: boolean,
    //   firstTabbableNode: HTMLElement|undefined,
    //   lastTabbableNode: HTMLElement|undefined,
    //   firstDomTabbableNode: HTMLElement|undefined,
    //   lastDomTabbableNode: HTMLElement|undefined,
    //   nextTabbableNode: (node: HTMLElement, forward: boolean) => HTMLElement|undefined
    // }>}
    containerGroups: [],
    // same order/length as `containers` list

    // references to objects in `containerGroups`, but only those that actually have
    //  tabbable nodes in them
    // NOTE: same order as `containers` and `containerGroups`, but __not necessarily__
    //  the same length
    tabbableGroups: [],
    nodeFocusedBeforeActivation: null,
    mostRecentlyFocusedNode: null,
    active: false,
    paused: false,
    // timer ID for when delayInitialFocus is true and initial focus in this trap
    //  has been delayed during activation
    delayInitialFocusTimer: undefined,
    // the most recent KeyboardEvent for the configured nav key (typically [SHIFT+]TAB), if any
    recentNavEvent: undefined
  };
  var trap; // eslint-disable-line prefer-const -- some private functions reference it, and its methods reference private functions, so we must declare here and define later

  /**
   * Gets a configuration option value.
   * @param {Object|undefined} configOverrideOptions If true, and option is defined in this set,
   *  value will be taken from this object. Otherwise, value will be taken from base configuration.
   * @param {string} optionName Name of the option whose value is sought.
   * @param {string|undefined} [configOptionName] Name of option to use __instead of__ `optionName`
   *  IIF `configOverrideOptions` is not defined. Otherwise, `optionName` is used.
   */
  var getOption = function getOption(configOverrideOptions, optionName, configOptionName) {
    return configOverrideOptions && configOverrideOptions[optionName] !== undefined ? configOverrideOptions[optionName] : config[configOptionName || optionName];
  };

  /**
   * Finds the index of the container that contains the element.
   * @param {HTMLElement} element
   * @param {Event} [event] If available, and `element` isn't directly found in any container,
   *  the event's composed path is used to see if includes any known trap containers in the
   *  case where the element is inside a Shadow DOM.
   * @returns {number} Index of the container in either `state.containers` or
   *  `state.containerGroups` (the order/length of these lists are the same); -1
   *  if the element isn't found.
   */
  var findContainerIndex = function findContainerIndex(element, event) {
    var composedPath = typeof (event === null || event === void 0 ? void 0 : event.composedPath) === 'function' ? event.composedPath() : undefined;
    // NOTE: search `containerGroups` because it's possible a group contains no tabbable
    //  nodes, but still contains focusable nodes (e.g. if they all have `tabindex=-1`)
    //  and we still need to find the element in there
    return state.containerGroups.findIndex(function (_ref) {
      var container = _ref.container,
        tabbableNodes = _ref.tabbableNodes;
      return container.contains(element) || ( // fall back to explicit tabbable search which will take into consideration any
      //  web components if the `tabbableOptions.getShadowRoot` option was used for
      //  the trap, enabling shadow DOM support in tabbable (`Node.contains()` doesn't
      //  look inside web components even if open)
      composedPath === null || composedPath === void 0 ? void 0 : composedPath.includes(container)) || tabbableNodes.find(function (node) {
        return node === element;
      });
    });
  };

  /**
   * Gets the node for the given option, which is expected to be an option that
   *  can be either a DOM node, a string that is a selector to get a node, `false`
   *  (if a node is explicitly NOT given), or a function that returns any of these
   *  values.
   * @param {string} optionName
   * @returns {undefined | false | HTMLElement | SVGElement} Returns
   *  `undefined` if the option is not specified; `false` if the option
   *  resolved to `false` (node explicitly not given); otherwise, the resolved
   *  DOM node.
   * @throws {Error} If the option is set, not `false`, and is not, or does not
   *  resolve to a node.
   */
  var getNodeForOption = function getNodeForOption(optionName) {
    var optionValue = config[optionName];
    if (typeof optionValue === 'function') {
      for (var _len2 = arguments.length, params = new Array(_len2 > 1 ? _len2 - 1 : 0), _key2 = 1; _key2 < _len2; _key2++) {
        params[_key2 - 1] = arguments[_key2];
      }
      optionValue = optionValue.apply(void 0, params);
    }
    if (optionValue === true) {
      optionValue = undefined; // use default value
    }

    if (!optionValue) {
      if (optionValue === undefined || optionValue === false) {
        return optionValue;
      }
      // else, empty string (invalid), null (invalid), 0 (invalid)

      throw new Error("`".concat(optionName, "` was specified but was not a node, or did not return a node"));
    }
    var node = optionValue; // could be HTMLElement, SVGElement, or non-empty string at this point

    if (typeof optionValue === 'string') {
      node = doc.querySelector(optionValue); // resolve to node, or null if fails
      if (!node) {
        throw new Error("`".concat(optionName, "` as selector refers to no known node"));
      }
    }
    return node;
  };
  var getInitialFocusNode = function getInitialFocusNode() {
    var node = getNodeForOption('initialFocus');

    // false explicitly indicates we want no initialFocus at all
    if (node === false) {
      return false;
    }
    if (node === undefined || !isFocusable$1(node, config.tabbableOptions)) {
      // option not specified nor focusable: use fallback options
      if (findContainerIndex(doc.activeElement) >= 0) {
        node = doc.activeElement;
      } else {
        var firstTabbableGroup = state.tabbableGroups[0];
        var firstTabbableNode = firstTabbableGroup && firstTabbableGroup.firstTabbableNode;

        // NOTE: `fallbackFocus` option function cannot return `false` (not supported)
        node = firstTabbableNode || getNodeForOption('fallbackFocus');
      }
    }
    if (!node) {
      throw new Error('Your focus-trap needs to have at least one focusable element');
    }
    return node;
  };
  var updateTabbableNodes = function updateTabbableNodes() {
    state.containerGroups = state.containers.map(function (container) {
      var tabbableNodes = tabbable(container, config.tabbableOptions);

      // NOTE: if we have tabbable nodes, we must have focusable nodes; focusable nodes
      //  are a superset of tabbable nodes since nodes with negative `tabindex` attributes
      //  are focusable but not tabbable
      var focusableNodes = focusable(container, config.tabbableOptions);
      var firstTabbableNode = tabbableNodes.length > 0 ? tabbableNodes[0] : undefined;
      var lastTabbableNode = tabbableNodes.length > 0 ? tabbableNodes[tabbableNodes.length - 1] : undefined;
      var firstDomTabbableNode = focusableNodes.find(function (node) {
        return isTabbable(node);
      });
      var lastDomTabbableNode = focusableNodes.slice().reverse().find(function (node) {
        return isTabbable(node);
      });
      var posTabIndexesFound = !!tabbableNodes.find(function (node) {
        return getTabIndex(node) > 0;
      });
      return {
        container: container,
        tabbableNodes: tabbableNodes,
        focusableNodes: focusableNodes,
        /** True if at least one node with positive `tabindex` was found in this container. */
        posTabIndexesFound: posTabIndexesFound,
        /** First tabbable node in container, __tabindex__ order; `undefined` if none. */
        firstTabbableNode: firstTabbableNode,
        /** Last tabbable node in container, __tabindex__ order; `undefined` if none. */
        lastTabbableNode: lastTabbableNode,
        // NOTE: DOM order is NOT NECESSARILY "document position" order, but figuring that out
        //  would require more than just https://developer.mozilla.org/en-US/docs/Web/API/Node/compareDocumentPosition
        //  because that API doesn't work with Shadow DOM as well as it should (@see
        //  https://github.com/whatwg/dom/issues/320) and since this first/last is only needed, so far,
        //  to address an edge case related to positive tabindex support, this seems like a much easier,
        //  "close enough most of the time" alternative for positive tabindexes which should generally
        //  be avoided anyway...
        /** First tabbable node in container, __DOM__ order; `undefined` if none. */
        firstDomTabbableNode: firstDomTabbableNode,
        /** Last tabbable node in container, __DOM__ order; `undefined` if none. */
        lastDomTabbableNode: lastDomTabbableNode,
        /**
         * Finds the __tabbable__ node that follows the given node in the specified direction,
         *  in this container, if any.
         * @param {HTMLElement} node
         * @param {boolean} [forward] True if going in forward tab order; false if going
         *  in reverse.
         * @returns {HTMLElement|undefined} The next tabbable node, if any.
         */
        nextTabbableNode: function nextTabbableNode(node) {
          var forward = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : true;
          var nodeIdx = tabbableNodes.indexOf(node);
          if (nodeIdx < 0) {
            // either not tabbable nor focusable, or was focused but not tabbable (negative tabindex):
            //  since `node` should at least have been focusable, we assume that's the case and mimic
            //  what browsers do, which is set focus to the next node in __document position order__,
            //  regardless of positive tabindexes, if any -- and for reasons explained in the NOTE
            //  above related to `firstDomTabbable` and `lastDomTabbable` properties, we fall back to
            //  basic DOM order
            if (forward) {
              return focusableNodes.slice(focusableNodes.indexOf(node) + 1).find(function (el) {
                return isTabbable(el);
              });
            }
            return focusableNodes.slice(0, focusableNodes.indexOf(node)).reverse().find(function (el) {
              return isTabbable(el);
            });
          }
          return tabbableNodes[nodeIdx + (forward ? 1 : -1)];
        }
      };
    });
    state.tabbableGroups = state.containerGroups.filter(function (group) {
      return group.tabbableNodes.length > 0;
    });

    // throw if no groups have tabbable nodes and we don't have a fallback focus node either
    if (state.tabbableGroups.length <= 0 && !getNodeForOption('fallbackFocus') // returning false not supported for this option
    ) {
      throw new Error('Your focus-trap must have at least one container with at least one tabbable node in it at all times');
    }

    // NOTE: Positive tabindexes are only properly supported in single-container traps because
    //  doing it across multiple containers where tabindexes could be all over the place
    //  would require Tabbable to support multiple containers, would require additional
    //  specialized Shadow DOM support, and would require Tabbable's multi-container support
    //  to look at those containers in document position order rather than user-provided
    //  order (as they are treated in Focus-trap, for legacy reasons). See discussion on
    //  https://github.com/focus-trap/focus-trap/issues/375 for more details.
    if (state.containerGroups.find(function (g) {
      return g.posTabIndexesFound;
    }) && state.containerGroups.length > 1) {
      throw new Error("At least one node with a positive tabindex was found in one of your focus-trap's multiple containers. Positive tabindexes are only supported in single-container focus-traps.");
    }
  };

  /**
   * Gets the current activeElement. If it's a web-component and has open shadow-root
   * it will recursively search inside shadow roots for the "true" activeElement.
   *
   * @param {Document | ShadowRoot} el
   *
   * @returns {HTMLElement} The element that currently has the focus
   **/
  var getActiveElement = function getActiveElement(el) {
    var activeElement = el.activeElement;
    if (!activeElement) {
      return;
    }
    if (activeElement.shadowRoot && activeElement.shadowRoot.activeElement !== null) {
      return getActiveElement(activeElement.shadowRoot);
    }
    return activeElement;
  };
  var tryFocus = function tryFocus(node) {
    if (node === false) {
      return;
    }
    if (node === getActiveElement(document)) {
      return;
    }
    if (!node || !node.focus) {
      tryFocus(getInitialFocusNode());
      return;
    }
    node.focus({
      preventScroll: !!config.preventScroll
    });
    // NOTE: focus() API does not trigger focusIn event so set MRU node manually
    state.mostRecentlyFocusedNode = node;
    if (isSelectableInput(node)) {
      node.select();
    }
  };
  var getReturnFocusNode = function getReturnFocusNode(previousActiveElement) {
    var node = getNodeForOption('setReturnFocus', previousActiveElement);
    return node ? node : node === false ? false : previousActiveElement;
  };

  /**
   * Finds the next node (in either direction) where focus should move according to a
   *  keyboard focus-in event.
   * @param {Object} params
   * @param {Node} [params.target] Known target __from which__ to navigate, if any.
   * @param {KeyboardEvent|FocusEvent} [params.event] Event to use if `target` isn't known (event
   *  will be used to determine the `target`). Ignored if `target` is specified.
   * @param {boolean} [params.isBackward] True if focus should move backward.
   * @returns {Node|undefined} The next node, or `undefined` if a next node couldn't be
   *  determined given the current state of the trap.
   */
  var findNextNavNode = function findNextNavNode(_ref2) {
    var target = _ref2.target,
      event = _ref2.event,
      _ref2$isBackward = _ref2.isBackward,
      isBackward = _ref2$isBackward === void 0 ? false : _ref2$isBackward;
    target = target || getActualTarget(event);
    updateTabbableNodes();
    var destinationNode = null;
    if (state.tabbableGroups.length > 0) {
      // make sure the target is actually contained in a group
      // NOTE: the target may also be the container itself if it's focusable
      //  with tabIndex='-1' and was given initial focus
      var containerIndex = findContainerIndex(target, event);
      var containerGroup = containerIndex >= 0 ? state.containerGroups[containerIndex] : undefined;
      if (containerIndex < 0) {
        // target not found in any group: quite possible focus has escaped the trap,
        //  so bring it back into...
        if (isBackward) {
          // ...the last node in the last group
          destinationNode = state.tabbableGroups[state.tabbableGroups.length - 1].lastTabbableNode;
        } else {
          // ...the first node in the first group
          destinationNode = state.tabbableGroups[0].firstTabbableNode;
        }
      } else if (isBackward) {
        // REVERSE

        // is the target the first tabbable node in a group?
        var startOfGroupIndex = findIndex(state.tabbableGroups, function (_ref3) {
          var firstTabbableNode = _ref3.firstTabbableNode;
          return target === firstTabbableNode;
        });
        if (startOfGroupIndex < 0 && (containerGroup.container === target || isFocusable$1(target, config.tabbableOptions) && !isTabbable(target, config.tabbableOptions) && !containerGroup.nextTabbableNode(target, false))) {
          // an exception case where the target is either the container itself, or
          //  a non-tabbable node that was given focus (i.e. tabindex is negative
          //  and user clicked on it or node was programmatically given focus)
          //  and is not followed by any other tabbable node, in which
          //  case, we should handle shift+tab as if focus were on the container's
          //  first tabbable node, and go to the last tabbable node of the LAST group
          startOfGroupIndex = containerIndex;
        }
        if (startOfGroupIndex >= 0) {
          // YES: then shift+tab should go to the last tabbable node in the
          //  previous group (and wrap around to the last tabbable node of
          //  the LAST group if it's the first tabbable node of the FIRST group)
          var destinationGroupIndex = startOfGroupIndex === 0 ? state.tabbableGroups.length - 1 : startOfGroupIndex - 1;
          var destinationGroup = state.tabbableGroups[destinationGroupIndex];
          destinationNode = getTabIndex(target) >= 0 ? destinationGroup.lastTabbableNode : destinationGroup.lastDomTabbableNode;
        } else if (!isTabEvent(event)) {
          // user must have customized the nav keys so we have to move focus manually _within_
          //  the active group: do this based on the order determined by tabbable()
          destinationNode = containerGroup.nextTabbableNode(target, false);
        }
      } else {
        // FORWARD

        // is the target the last tabbable node in a group?
        var lastOfGroupIndex = findIndex(state.tabbableGroups, function (_ref4) {
          var lastTabbableNode = _ref4.lastTabbableNode;
          return target === lastTabbableNode;
        });
        if (lastOfGroupIndex < 0 && (containerGroup.container === target || isFocusable$1(target, config.tabbableOptions) && !isTabbable(target, config.tabbableOptions) && !containerGroup.nextTabbableNode(target))) {
          // an exception case where the target is the container itself, or
          //  a non-tabbable node that was given focus (i.e. tabindex is negative
          //  and user clicked on it or node was programmatically given focus)
          //  and is not followed by any other tabbable node, in which
          //  case, we should handle tab as if focus were on the container's
          //  last tabbable node, and go to the first tabbable node of the FIRST group
          lastOfGroupIndex = containerIndex;
        }
        if (lastOfGroupIndex >= 0) {
          // YES: then tab should go to the first tabbable node in the next
          //  group (and wrap around to the first tabbable node of the FIRST
          //  group if it's the last tabbable node of the LAST group)
          var _destinationGroupIndex = lastOfGroupIndex === state.tabbableGroups.length - 1 ? 0 : lastOfGroupIndex + 1;
          var _destinationGroup = state.tabbableGroups[_destinationGroupIndex];
          destinationNode = getTabIndex(target) >= 0 ? _destinationGroup.firstTabbableNode : _destinationGroup.firstDomTabbableNode;
        } else if (!isTabEvent(event)) {
          // user must have customized the nav keys so we have to move focus manually _within_
          //  the active group: do this based on the order determined by tabbable()
          destinationNode = containerGroup.nextTabbableNode(target);
        }
      }
    } else {
      // no groups available
      // NOTE: the fallbackFocus option does not support returning false to opt-out
      destinationNode = getNodeForOption('fallbackFocus');
    }
    return destinationNode;
  };

  // This needs to be done on mousedown and touchstart instead of click
  // so that it precedes the focus event.
  var checkPointerDown = function checkPointerDown(e) {
    var target = getActualTarget(e);
    if (findContainerIndex(target, e) >= 0) {
      // allow the click since it ocurred inside the trap
      return;
    }
    if (valueOrHandler(config.clickOutsideDeactivates, e)) {
      // immediately deactivate the trap
      trap.deactivate({
        // NOTE: by setting `returnFocus: false`, deactivate() will do nothing,
        //  which will result in the outside click setting focus to the node
        //  that was clicked (and if not focusable, to "nothing"); by setting
        //  `returnFocus: true`, we'll attempt to re-focus the node originally-focused
        //  on activation (or the configured `setReturnFocus` node), whether the
        //  outside click was on a focusable node or not
        returnFocus: config.returnFocusOnDeactivate
      });
      return;
    }

    // This is needed for mobile devices.
    // (If we'll only let `click` events through,
    // then on mobile they will be blocked anyways if `touchstart` is blocked.)
    if (valueOrHandler(config.allowOutsideClick, e)) {
      // allow the click outside the trap to take place
      return;
    }

    // otherwise, prevent the click
    e.preventDefault();
  };

  // In case focus escapes the trap for some strange reason, pull it back in.
  // NOTE: the focusIn event is NOT cancelable, so if focus escapes, it may cause unexpected
  //  scrolling if the node that got focused was out of view; there's nothing we can do to
  //  prevent that from happening by the time we discover that focus escaped
  var checkFocusIn = function checkFocusIn(event) {
    var target = getActualTarget(event);
    var targetContained = findContainerIndex(target, event) >= 0;

    // In Firefox when you Tab out of an iframe the Document is briefly focused.
    if (targetContained || target instanceof Document) {
      if (targetContained) {
        state.mostRecentlyFocusedNode = target;
      }
    } else {
      // escaped! pull it back in to where it just left
      event.stopImmediatePropagation();

      // focus will escape if the MRU node had a positive tab index and user tried to nav forward;
      //  it will also escape if the MRU node had a 0 tab index and user tried to nav backward
      //  toward a node with a positive tab index
      var nextNode; // next node to focus, if we find one
      var navAcrossContainers = true;
      if (state.mostRecentlyFocusedNode) {
        if (getTabIndex(state.mostRecentlyFocusedNode) > 0) {
          // MRU container index must be >=0 otherwise we wouldn't have it as an MRU node...
          var mruContainerIdx = findContainerIndex(state.mostRecentlyFocusedNode);
          // there MAY not be any tabbable nodes in the container if there are at least 2 containers
          //  and the MRU node is focusable but not tabbable (focus-trap requires at least 1 container
          //  with at least one tabbable node in order to function, so this could be the other container
          //  with nothing tabbable in it)
          var tabbableNodes = state.containerGroups[mruContainerIdx].tabbableNodes;
          if (tabbableNodes.length > 0) {
            // MRU tab index MAY not be found if the MRU node is focusable but not tabbable
            var mruTabIdx = tabbableNodes.findIndex(function (node) {
              return node === state.mostRecentlyFocusedNode;
            });
            if (mruTabIdx >= 0) {
              if (config.isKeyForward(state.recentNavEvent)) {
                if (mruTabIdx + 1 < tabbableNodes.length) {
                  nextNode = tabbableNodes[mruTabIdx + 1];
                  navAcrossContainers = false;
                }
                // else, don't wrap within the container as focus should move to next/previous
                //  container
              } else {
                if (mruTabIdx - 1 >= 0) {
                  nextNode = tabbableNodes[mruTabIdx - 1];
                  navAcrossContainers = false;
                }
                // else, don't wrap within the container as focus should move to next/previous
                //  container
              }
              // else, don't find in container order without considering direction too
            }
          }
          // else, no tabbable nodes in that container (which means we must have at least one other
          //  container with at least one tabbable node in it, otherwise focus-trap would've thrown
          //  an error the last time updateTabbableNodes() was run): find next node among all known
          //  containers
        } else {
          // check to see if there's at least one tabbable node with a positive tab index inside
          //  the trap because focus seems to escape when navigating backward from a tabbable node
          //  with tabindex=0 when this is the case (instead of wrapping to the tabbable node with
          //  the greatest positive tab index like it should)
          if (!state.containerGroups.some(function (g) {
            return g.tabbableNodes.some(function (n) {
              return getTabIndex(n) > 0;
            });
          })) {
            // no containers with tabbable nodes with positive tab indexes which means the focus
            //  escaped for some other reason and we should just execute the fallback to the
            //  MRU node or initial focus node, if any
            navAcrossContainers = false;
          }
        }
      } else {
        // no MRU node means we're likely in some initial condition when the trap has just
        //  been activated and initial focus hasn't been given yet, in which case we should
        //  fall through to trying to focus the initial focus node, which is what should
        //  happen below at this point in the logic
        navAcrossContainers = false;
      }
      if (navAcrossContainers) {
        nextNode = findNextNavNode({
          // move FROM the MRU node, not event-related node (which will be the node that is
          //  outside the trap causing the focus escape we're trying to fix)
          target: state.mostRecentlyFocusedNode,
          isBackward: config.isKeyBackward(state.recentNavEvent)
        });
      }
      if (nextNode) {
        tryFocus(nextNode);
      } else {
        tryFocus(state.mostRecentlyFocusedNode || getInitialFocusNode());
      }
    }
    state.recentNavEvent = undefined; // clear
  };

  // Hijack key nav events on the first and last focusable nodes of the trap,
  // in order to prevent focus from escaping. If it escapes for even a
  // moment it can end up scrolling the page and causing confusion so we
  // kind of need to capture the action at the keydown phase.
  var checkKeyNav = function checkKeyNav(event) {
    var isBackward = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    state.recentNavEvent = event;
    var destinationNode = findNextNavNode({
      event: event,
      isBackward: isBackward
    });
    if (destinationNode) {
      if (isTabEvent(event)) {
        // since tab natively moves focus, we wouldn't have a destination node unless we
        //  were on the edge of a container and had to move to the next/previous edge, in
        //  which case we want to prevent default to keep the browser from moving focus
        //  to where it normally would
        event.preventDefault();
      }
      tryFocus(destinationNode);
    }
    // else, let the browser take care of [shift+]tab and move the focus
  };

  var checkKey = function checkKey(event) {
    if (isEscapeEvent(event) && valueOrHandler(config.escapeDeactivates, event) !== false) {
      event.preventDefault();
      trap.deactivate();
      return;
    }
    if (config.isKeyForward(event) || config.isKeyBackward(event)) {
      checkKeyNav(event, config.isKeyBackward(event));
    }
  };
  var checkClick = function checkClick(e) {
    var target = getActualTarget(e);
    if (findContainerIndex(target, e) >= 0) {
      return;
    }
    if (valueOrHandler(config.clickOutsideDeactivates, e)) {
      return;
    }
    if (valueOrHandler(config.allowOutsideClick, e)) {
      return;
    }
    e.preventDefault();
    e.stopImmediatePropagation();
  };

  //
  // EVENT LISTENERS
  //

  var addListeners = function addListeners() {
    if (!state.active) {
      return;
    }

    // There can be only one listening focus trap at a time
    activeFocusTraps.activateTrap(trapStack, trap);

    // Delay ensures that the focused element doesn't capture the event
    // that caused the focus trap activation.
    state.delayInitialFocusTimer = config.delayInitialFocus ? delay(function () {
      tryFocus(getInitialFocusNode());
    }) : tryFocus(getInitialFocusNode());
    doc.addEventListener('focusin', checkFocusIn, true);
    doc.addEventListener('mousedown', checkPointerDown, {
      capture: true,
      passive: false
    });
    doc.addEventListener('touchstart', checkPointerDown, {
      capture: true,
      passive: false
    });
    doc.addEventListener('click', checkClick, {
      capture: true,
      passive: false
    });
    doc.addEventListener('keydown', checkKey, {
      capture: true,
      passive: false
    });
    return trap;
  };
  var removeListeners = function removeListeners() {
    if (!state.active) {
      return;
    }
    doc.removeEventListener('focusin', checkFocusIn, true);
    doc.removeEventListener('mousedown', checkPointerDown, true);
    doc.removeEventListener('touchstart', checkPointerDown, true);
    doc.removeEventListener('click', checkClick, true);
    doc.removeEventListener('keydown', checkKey, true);
    return trap;
  };

  //
  // MUTATION OBSERVER
  //

  var checkDomRemoval = function checkDomRemoval(mutations) {
    var isFocusedNodeRemoved = mutations.some(function (mutation) {
      var removedNodes = Array.from(mutation.removedNodes);
      return removedNodes.some(function (node) {
        return node === state.mostRecentlyFocusedNode;
      });
    });

    // If the currently focused is removed then browsers will move focus to the
    // <body> element. If this happens, try to move focus back into the trap.
    if (isFocusedNodeRemoved) {
      tryFocus(getInitialFocusNode());
    }
  };

  // Use MutationObserver - if supported - to detect if focused node is removed
  // from the DOM.
  var mutationObserver = typeof window !== 'undefined' && 'MutationObserver' in window ? new MutationObserver(checkDomRemoval) : undefined;
  var updateObservedNodes = function updateObservedNodes() {
    if (!mutationObserver) {
      return;
    }
    mutationObserver.disconnect();
    if (state.active && !state.paused) {
      state.containers.map(function (container) {
        mutationObserver.observe(container, {
          subtree: true,
          childList: true
        });
      });
    }
  };

  //
  // TRAP DEFINITION
  //

  trap = {
    get active() {
      return state.active;
    },
    get paused() {
      return state.paused;
    },
    activate: function activate(activateOptions) {
      if (state.active) {
        return this;
      }
      var onActivate = getOption(activateOptions, 'onActivate');
      var onPostActivate = getOption(activateOptions, 'onPostActivate');
      var checkCanFocusTrap = getOption(activateOptions, 'checkCanFocusTrap');
      if (!checkCanFocusTrap) {
        updateTabbableNodes();
      }
      state.active = true;
      state.paused = false;
      state.nodeFocusedBeforeActivation = doc.activeElement;
      onActivate === null || onActivate === void 0 || onActivate();
      var finishActivation = function finishActivation() {
        if (checkCanFocusTrap) {
          updateTabbableNodes();
        }
        addListeners();
        updateObservedNodes();
        onPostActivate === null || onPostActivate === void 0 || onPostActivate();
      };
      if (checkCanFocusTrap) {
        checkCanFocusTrap(state.containers.concat()).then(finishActivation, finishActivation);
        return this;
      }
      finishActivation();
      return this;
    },
    deactivate: function deactivate(deactivateOptions) {
      if (!state.active) {
        return this;
      }
      var options = _objectSpread2({
        onDeactivate: config.onDeactivate,
        onPostDeactivate: config.onPostDeactivate,
        checkCanReturnFocus: config.checkCanReturnFocus
      }, deactivateOptions);
      clearTimeout(state.delayInitialFocusTimer); // noop if undefined
      state.delayInitialFocusTimer = undefined;
      removeListeners();
      state.active = false;
      state.paused = false;
      updateObservedNodes();
      activeFocusTraps.deactivateTrap(trapStack, trap);
      var onDeactivate = getOption(options, 'onDeactivate');
      var onPostDeactivate = getOption(options, 'onPostDeactivate');
      var checkCanReturnFocus = getOption(options, 'checkCanReturnFocus');
      var returnFocus = getOption(options, 'returnFocus', 'returnFocusOnDeactivate');
      onDeactivate === null || onDeactivate === void 0 || onDeactivate();
      var finishDeactivation = function finishDeactivation() {
        delay(function () {
          if (returnFocus) {
            tryFocus(getReturnFocusNode(state.nodeFocusedBeforeActivation));
          }
          onPostDeactivate === null || onPostDeactivate === void 0 || onPostDeactivate();
        });
      };
      if (returnFocus && checkCanReturnFocus) {
        checkCanReturnFocus(getReturnFocusNode(state.nodeFocusedBeforeActivation)).then(finishDeactivation, finishDeactivation);
        return this;
      }
      finishDeactivation();
      return this;
    },
    pause: function pause(pauseOptions) {
      if (state.paused || !state.active) {
        return this;
      }
      var onPause = getOption(pauseOptions, 'onPause');
      var onPostPause = getOption(pauseOptions, 'onPostPause');
      state.paused = true;
      onPause === null || onPause === void 0 || onPause();
      removeListeners();
      updateObservedNodes();
      onPostPause === null || onPostPause === void 0 || onPostPause();
      return this;
    },
    unpause: function unpause(unpauseOptions) {
      if (!state.paused || !state.active) {
        return this;
      }
      var onUnpause = getOption(unpauseOptions, 'onUnpause');
      var onPostUnpause = getOption(unpauseOptions, 'onPostUnpause');
      state.paused = false;
      onUnpause === null || onUnpause === void 0 || onUnpause();
      updateTabbableNodes();
      addListeners();
      updateObservedNodes();
      onPostUnpause === null || onPostUnpause === void 0 || onPostUnpause();
      return this;
    },
    updateContainerElements: function updateContainerElements(containerElements) {
      var elementsAsArray = [].concat(containerElements).filter(Boolean);
      state.containers = elementsAsArray.map(function (element) {
        return typeof element === 'string' ? doc.querySelector(element) : element;
      });
      if (state.active) {
        updateTabbableNodes();
      }
      updateObservedNodes();
      return this;
    }
  };

  // initialize container elements
  trap.updateContainerElements(elements);
  return trap;
};

var focusTrap_esm = /*#__PURE__*/Object.freeze({
  __proto__: null,
  createFocusTrap: createFocusTrap$1
});

function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }
function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, _toPropertyKey(descriptor.key), descriptor); } }
function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); Object.defineProperty(Constructor, "prototype", { writable: false }); return Constructor; }
function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); Object.defineProperty(subClass, "prototype", { writable: false }); if (superClass) _setPrototypeOf(subClass, superClass); }
function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf ? Object.setPrototypeOf.bind() : function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }
function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }
function _possibleConstructorReturn(self, call) { if (call && (_typeof(call) === "object" || typeof call === "function")) { return call; } else if (call !== void 0) { throw new TypeError("Derived constructors may only return object or undefined"); } return _assertThisInitialized(self); }
function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }
function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }
function _getPrototypeOf(o) { _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf.bind() : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf(o); }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
var React = require$$0;
var PropTypes = propTypesExports;
var _require = focusTrap_esm,
  createFocusTrap = _require.createFocusTrap;
var _require2 = index_esm,
  isFocusable = _require2.isFocusable;
var FocusTrap = /*#__PURE__*/function (_React$Component) {
  _inherits(FocusTrap, _React$Component);
  var _super = _createSuper(FocusTrap);
  function FocusTrap(props) {
    var _this;
    _classCallCheck(this, FocusTrap);
    _this = _super.call(this, props);
    /**
     * Gets the node for the given option, which is expected to be an option that
     *  can be either a DOM node, a string that is a selector to get a node, `false`
     *  (if a node is explicitly NOT given), or a function that returns any of these
     *  values.
     * @param {string} optionName
     * @returns {undefined | false | HTMLElement | SVGElement} Returns
     *  `undefined` if the option is not specified; `false` if the option
     *  resolved to `false` (node explicitly not given); otherwise, the resolved
     *  DOM node.
     * @throws {Error} If the option is set, not `false`, and is not, or does not
     *  resolve to a node.
     */
    _defineProperty(_assertThisInitialized(_this), "getNodeForOption", function (optionName) {
      var _this$internalOptions;
      // use internal options first, falling back to original options
      var optionValue = (_this$internalOptions = this.internalOptions[optionName]) !== null && _this$internalOptions !== void 0 ? _this$internalOptions : this.originalOptions[optionName];
      if (typeof optionValue === 'function') {
        for (var _len = arguments.length, params = new Array(_len > 1 ? _len - 1 : 0), _key = 1; _key < _len; _key++) {
          params[_key - 1] = arguments[_key];
        }
        optionValue = optionValue.apply(void 0, params);
      }
      if (optionValue === true) {
        optionValue = undefined; // use default value
      }

      if (!optionValue) {
        if (optionValue === undefined || optionValue === false) {
          return optionValue;
        }
        // else, empty string (invalid), null (invalid), 0 (invalid)

        throw new Error("`".concat(optionName, "` was specified but was not a node, or did not return a node"));
      }
      var node = optionValue; // could be HTMLElement, SVGElement, or non-empty string at this point

      if (typeof optionValue === 'string') {
        var _this$getDocument;
        node = (_this$getDocument = this.getDocument()) === null || _this$getDocument === void 0 ? void 0 : _this$getDocument.querySelector(optionValue); // resolve to node, or null if fails
        if (!node) {
          throw new Error("`".concat(optionName, "` as selector refers to no known node"));
        }
      }
      return node;
    });
    _this.handleDeactivate = _this.handleDeactivate.bind(_assertThisInitialized(_this));
    _this.handlePostDeactivate = _this.handlePostDeactivate.bind(_assertThisInitialized(_this));
    _this.handleClickOutsideDeactivates = _this.handleClickOutsideDeactivates.bind(_assertThisInitialized(_this));

    // focus-trap options used internally when creating the trap
    _this.internalOptions = {
      // We need to hijack the returnFocusOnDeactivate option,
      // because React can move focus into the element before we arrived at
      // this lifecycle hook (e.g. with autoFocus inputs). So the component
      // captures the previouslyFocusedElement in componentWillMount,
      // then (optionally) returns focus to it in componentWillUnmount.
      returnFocusOnDeactivate: false,
      // the rest of these are also related to deactivation of the trap, and we
      //  need to use them and control them as well
      checkCanReturnFocus: null,
      onDeactivate: _this.handleDeactivate,
      onPostDeactivate: _this.handlePostDeactivate,
      // we need to special-case this setting as well so that we can know if we should
      //  NOT return focus if the trap gets auto-deactivated as the result of an
      //  outside click (otherwise, we'll always think we should return focus because
      //  of how we manage that flag internally here)
      clickOutsideDeactivates: _this.handleClickOutsideDeactivates
    };

    // original options provided by the consumer
    _this.originalOptions = {
      // because of the above `internalOptions`, we maintain our own flag for
      //  this option, and default it to `true` because that's focus-trap's default
      returnFocusOnDeactivate: true,
      // because of the above `internalOptions`, we keep these separate since
      //  they're part of the deactivation process which we configure (internally) to
      //  be shared between focus-trap and focus-trap-react
      onDeactivate: null,
      onPostDeactivate: null,
      checkCanReturnFocus: null,
      // the user's setting, defaulted to false since focus-trap defaults this to false
      clickOutsideDeactivates: false
    };
    var focusTrapOptions = props.focusTrapOptions;
    for (var optionName in focusTrapOptions) {
      if (!Object.prototype.hasOwnProperty.call(focusTrapOptions, optionName)) {
        continue;
      }
      if (optionName === 'returnFocusOnDeactivate' || optionName === 'onDeactivate' || optionName === 'onPostDeactivate' || optionName === 'checkCanReturnFocus' || optionName === 'clickOutsideDeactivates') {
        _this.originalOptions[optionName] = focusTrapOptions[optionName];
        continue; // exclude from internalOptions
      }

      _this.internalOptions[optionName] = focusTrapOptions[optionName];
    }

    // if set, `{ target: Node, allowDeactivation: boolean }` where `target` is the outside
    //  node that was clicked, and `allowDeactivation` is the result of the consumer's
    //  option (stored in `this.originalOptions.clickOutsideDeactivates`, which may be a
    //  function) whether to allow or deny auto-deactivation on click on this outside node
    _this.outsideClick = null;

    // elements from which to create the focus trap on mount; if a child is used
    //  instead of the `containerElements` prop, we'll get the child's related
    //  element when the trap renders and then is declared 'mounted'
    _this.focusTrapElements = props.containerElements || [];

    // now we remember what the currently focused element is, not relying on focus-trap
    _this.updatePreviousElement();
    return _this;
  }

  /**
   * Gets the configured document.
   * @returns {Document|undefined} Configured document, falling back to the main
   *  document, if it exists. During SSR, `undefined` is returned since the
   *  document doesn't exist.
   */
  _createClass(FocusTrap, [{
    key: "getDocument",
    value: function getDocument() {
      // SSR: careful to check if `document` exists before accessing it as a variable
      return this.props.focusTrapOptions.document || (typeof document !== 'undefined' ? document : undefined);
    }
  }, {
    key: "getReturnFocusNode",
    value: function getReturnFocusNode() {
      var node = this.getNodeForOption('setReturnFocus', this.previouslyFocusedElement);
      return node ? node : node === false ? false : this.previouslyFocusedElement;
    }

    /** Update the previously focused element with the currently focused element. */
  }, {
    key: "updatePreviousElement",
    value: function updatePreviousElement() {
      var currentDocument = this.getDocument();
      if (currentDocument) {
        this.previouslyFocusedElement = currentDocument.activeElement;
      }
    }
  }, {
    key: "deactivateTrap",
    value: function deactivateTrap() {
      // NOTE: it's possible the focus trap has already been deactivated without our knowing it,
      //  especially if the user set the `clickOutsideDeactivates: true` option on the trap,
      //  and the mouse was clicked on some element outside the trap; at that point, focus-trap
      //  will initiate its auto-deactivation process, which will call our own
      //  handleDeactivate(), which will call into this method
      if (!this.focusTrap || !this.focusTrap.active) {
        return;
      }
      this.focusTrap.deactivate({
        // NOTE: we never let the trap return the focus since we do that ourselves
        returnFocus: false,
        // we'll call this in our own post deactivate handler so make sure the trap doesn't
        //  do it prematurely
        checkCanReturnFocus: null,
        // let it call the user's original deactivate handler, if any, instead of
        //  our own which calls back into this function
        onDeactivate: this.originalOptions.onDeactivate
        // NOTE: for post deactivate, don't specify anything so that it calls the
        //  onPostDeactivate handler specified on `this.internalOptions`
        //  which will always be our own `handlePostDeactivate()` handler, which
        //  will finish things off by calling the user's provided onPostDeactivate
        //  handler, if any, at the right time
        // onPostDeactivate: NOTHING
      });
    }
  }, {
    key: "handleClickOutsideDeactivates",
    value: function handleClickOutsideDeactivates(event) {
      // use consumer's option (or call their handler) as the permission or denial
      var allowDeactivation = typeof this.originalOptions.clickOutsideDeactivates === 'function' ? this.originalOptions.clickOutsideDeactivates.call(null, event) // call out of context
      : this.originalOptions.clickOutsideDeactivates; // boolean

      if (allowDeactivation) {
        // capture the outside target that was clicked so we can use it in the deactivation
        //  process since the consumer allowed it to cause auto-deactivation
        this.outsideClick = {
          target: event.target,
          allowDeactivation: allowDeactivation
        };
      }
      return allowDeactivation;
    }
  }, {
    key: "handleDeactivate",
    value: function handleDeactivate() {
      if (this.originalOptions.onDeactivate) {
        this.originalOptions.onDeactivate.call(null); // call user's handler out of context
      }

      this.deactivateTrap();
    }
  }, {
    key: "handlePostDeactivate",
    value: function handlePostDeactivate() {
      var _this2 = this;
      var finishDeactivation = function finishDeactivation() {
        var returnFocusNode = _this2.getReturnFocusNode();
        var canReturnFocus = !!(
        // did the consumer allow it?

        _this2.originalOptions.returnFocusOnDeactivate && // can we actually focus the node?
        returnFocusNode !== null && returnFocusNode !== void 0 && returnFocusNode.focus && (
        // was there an outside click that allowed deactivation?
        !_this2.outsideClick ||
        // did the consumer allow deactivation when the outside node was clicked?
        _this2.outsideClick.allowDeactivation &&
        // is the outside node NOT focusable (implying that it did NOT receive focus
        //  as a result of the click-through) -- in which case do NOT restore focus
        //  to `returnFocusNode` because focus should remain on the outside node
        !isFocusable(_this2.outsideClick.target, _this2.internalOptions.tabbableOptions))

        // if no, the restore focus to `returnFocusNode` at this point
        );

        var _this2$internalOption = _this2.internalOptions.preventScroll,
          preventScroll = _this2$internalOption === void 0 ? false : _this2$internalOption;
        if (canReturnFocus) {
          // return focus to the element that had focus when the trap was activated
          returnFocusNode.focus({
            preventScroll: preventScroll
          });
        }
        if (_this2.originalOptions.onPostDeactivate) {
          _this2.originalOptions.onPostDeactivate.call(null); // don't call it in context of "this"
        }

        _this2.outsideClick = null; // reset: no longer needed
      };

      if (this.originalOptions.checkCanReturnFocus) {
        this.originalOptions.checkCanReturnFocus.call(null, this.getReturnFocusNode()) // call out of context
        .then(finishDeactivation, finishDeactivation);
      } else {
        finishDeactivation();
      }
    }
  }, {
    key: "setupFocusTrap",
    value: function setupFocusTrap() {
      if (this.focusTrap) {
        // trap already exists: it's possible we're in StrictMode and we're being remounted,
        //  in which case, we will have deactivated the trap when we got unmounted (remember,
        //  StrictMode, in development, purposely unmounts and remounts components after
        //  mounting them the first time to make sure they have reusable state,
        //  @see https://reactjs.org/docs/strict-mode.html#ensuring-reusable-state) so now
        //  we need to restore the state of the trap according to our component state
        // NOTE: Strict mode __violates__ assumptions about the `componentWillUnmount()` API
        //  which clearly states -- even for React 18 -- that, "Once a component instance is
        //  unmounted, __it will never be mounted again.__" (emphasis ours). So when we get
        //  unmounted, we assume we're gone forever and we deactivate the trap. But then
        //  we get remounted and we're supposed to restore state. But if you had paused,
        //  we've now deactivated (we don't know we're amount to get remounted again)
        //  which means we need to reactivate and then pause. Otherwise, do nothing.
        if (this.props.active && !this.focusTrap.active) {
          this.focusTrap.activate();
          if (this.props.paused) {
            this.focusTrap.pause();
          }
        }
      } else {
        var nodesExist = this.focusTrapElements.some(Boolean);
        if (nodesExist) {
          // eslint-disable-next-line react/prop-types -- _createFocusTrap is an internal prop
          this.focusTrap = this.props._createFocusTrap(this.focusTrapElements, this.internalOptions);
          if (this.props.active) {
            this.focusTrap.activate();
          }
          if (this.props.paused) {
            this.focusTrap.pause();
          }
        }
      }
    }
  }, {
    key: "componentDidMount",
    value: function componentDidMount() {
      if (this.props.active) {
        this.setupFocusTrap();
      }
      // else, wait for later activation in case the `focusTrapOptions` will be updated
      //  again before the trap is activated (e.g. if waiting to know what the document
      //  object will be, so the Trap must be rendered, but the consumer is waiting to
      //  activate until they have obtained the document from a ref)
      //  @see https://github.com/focus-trap/focus-trap-react/issues/539
    }
  }, {
    key: "componentDidUpdate",
    value: function componentDidUpdate(prevProps) {
      if (this.focusTrap) {
        if (prevProps.containerElements !== this.props.containerElements) {
          this.focusTrap.updateContainerElements(this.props.containerElements);
        }
        var hasActivated = !prevProps.active && this.props.active;
        var hasDeactivated = prevProps.active && !this.props.active;
        var hasPaused = !prevProps.paused && this.props.paused;
        var hasUnpaused = prevProps.paused && !this.props.paused;
        if (hasActivated) {
          this.updatePreviousElement();
          this.focusTrap.activate();
        }
        if (hasDeactivated) {
          this.deactivateTrap();
          return; // un/pause does nothing on an inactive trap
        }

        if (hasPaused) {
          this.focusTrap.pause();
        }
        if (hasUnpaused) {
          this.focusTrap.unpause();
        }
      } else {
        // NOTE: if we're in `componentDidUpdate` and we don't have a trap yet,
        //  it either means it shouldn't be active, or it should be but none of
        //  of given `containerElements` were present in the DOM the last time
        //  we tried to create the trap

        if (prevProps.containerElements !== this.props.containerElements) {
          this.focusTrapElements = this.props.containerElements;
        }

        // don't create the trap unless it should be active in case the consumer
        //  is still updating `focusTrapOptions`
        //  @see https://github.com/focus-trap/focus-trap-react/issues/539
        if (this.props.active) {
          this.updatePreviousElement();
          this.setupFocusTrap();
        }
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      this.deactivateTrap();
    }
  }, {
    key: "render",
    value: function render() {
      var _this3 = this;
      var child = this.props.children ? React.Children.only(this.props.children) : undefined;
      if (child) {
        if (child.type && child.type === React.Fragment) {
          throw new Error('A focus-trap cannot use a Fragment as its child container. Try replacing it with a <div> element.');
        }
        var callbackRef = function callbackRef(element) {
          var containerElements = _this3.props.containerElements;
          if (child) {
            if (typeof child.ref === 'function') {
              child.ref(element);
            } else if (child.ref) {
              child.ref.current = element;
            }
          }
          _this3.focusTrapElements = containerElements ? containerElements : [element];
        };
        var childWithRef = React.cloneElement(child, {
          ref: callbackRef
        });
        return childWithRef;
      }
      return null;
    }
  }]);
  return FocusTrap;
}(React.Component); // support server-side rendering where `Element` will not be defined
var ElementType = typeof Element === 'undefined' ? Function : Element;
FocusTrap.propTypes = {
  active: PropTypes.bool,
  paused: PropTypes.bool,
  focusTrapOptions: PropTypes.shape({
    document: PropTypes.object,
    onActivate: PropTypes.func,
    onPostActivate: PropTypes.func,
    checkCanFocusTrap: PropTypes.func,
    onPause: PropTypes.func,
    onPostPause: PropTypes.func,
    onUnpause: PropTypes.func,
    onPostUnpause: PropTypes.func,
    onDeactivate: PropTypes.func,
    onPostDeactivate: PropTypes.func,
    checkCanReturnFocus: PropTypes.func,
    initialFocus: PropTypes.oneOfType([PropTypes.instanceOf(ElementType), PropTypes.string, PropTypes.bool, PropTypes.func]),
    fallbackFocus: PropTypes.oneOfType([PropTypes.instanceOf(ElementType), PropTypes.string,
    // NOTE: does not support `false` as value (or return value from function)
    PropTypes.func]),
    escapeDeactivates: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    clickOutsideDeactivates: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    returnFocusOnDeactivate: PropTypes.bool,
    setReturnFocus: PropTypes.oneOfType([PropTypes.instanceOf(ElementType), PropTypes.string, PropTypes.bool, PropTypes.func]),
    allowOutsideClick: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    preventScroll: PropTypes.bool,
    tabbableOptions: PropTypes.shape({
      displayCheck: PropTypes.oneOf(['full', 'legacy-full', 'non-zero-area', 'none']),
      getShadowRoot: PropTypes.oneOfType([PropTypes.bool, PropTypes.func])
    }),
    trapStack: PropTypes.array,
    isKeyForward: PropTypes.func,
    isKeyBackward: PropTypes.func
  }),
  containerElements: PropTypes.arrayOf(PropTypes.instanceOf(ElementType)),
  // DOM element ONLY
  children: PropTypes.oneOfType([PropTypes.element,
  // React element
  PropTypes.instanceOf(ElementType) // DOM element
  ])

  // NOTE: _createFocusTrap is internal, for testing purposes only, so we don't
  //  specify it here. It's expected to be set to the function returned from
  //  require('focus-trap'), or one with a compatible interface.
};

FocusTrap.defaultProps = {
  active: true,
  paused: false,
  focusTrapOptions: {},
  _createFocusTrap: createFocusTrap
};
var focusTrapReact = FocusTrap;

var FocusTrap$1 = /*@__PURE__*/getDefaultExportFromCjs$1(focusTrapReact);

/**
 * The Web Chat branding banner is for bringing more awareness and recognition to IBM Watson. This banner should always
 * be visible on instances on a lite plan. Otherwise, this banner can be toggle off.
 */
function BrandingBannerComponent() {
    const serviceManager = useServiceManager();
    const { branding_headline, branding_ariaInformationButton } = useLanguagePack();
    const handleClick = () => {
        serviceManager.store.dispatch(actions.setBrandingBannerShown(false));
        serviceManager.store.dispatch(actions.setBrandingOverlayIsOpen(true));
        // Track when the user has opened the branding overlay.
        serviceManager.actions.track({ eventName: 'Branding overlay opened' });
    };
    return (React__default.createElement("div", { className: "WACBrandingBanner" },
        React__default.createElement("div", { className: "WACBrandingBanner__text" },
            React__default.createElement(MaybeDangerouslySetInnerHTMLExport, { html: branding_headline }),
            React__default.createElement(Button, { kind: ButtonKindEnum.GHOST, type: "button", size: ButtonSizeEnum.SMALL, onClick: handleClick, "aria-label": branding_ariaInformationButton },
                React__default.createElement(Information, { className: "WACBrandingBanner__icon" })))));
}
const BrandingBannerComponentExport = React__default.memo(BrandingBannerComponent);

function Avatar({ theme }) {
    const a = `a-${uuid()}`;
    const b = `b-${uuid()}`;
    const c = `c-${uuid()}`;
    const d = `d-${uuid()}`;
    const e = `e-${uuid()}`;
    if (theme === CarbonTheme.WHITE || theme === CarbonTheme.G10) {
        return (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 32 32", className: "cds--watsonx-avatar" },
            React__default.createElement("defs", null,
                React__default.createElement("linearGradient", { id: a, x1: "1186.526", y1: "2863.168", x2: "1199.825", y2: "2845.109", gradientTransform: "matrix(.8312 .55596 -.27409 .40979 -198.894 -1827.398)", gradientUnits: "userSpaceOnUse" },
                    React__default.createElement("stop", { offset: ".3" }),
                    React__default.createElement("stop", { offset: "1", stopOpacity: "0" })),
                React__default.createElement("linearGradient", { id: b, x1: "1189.388", y1: "2911.794", x2: "1200.478", y2: "2896.735", gradientTransform: "rotate(146.223 380.87 -882.286) scale(1 -.493)", gradientUnits: "userSpaceOnUse" },
                    React__default.createElement("stop", { offset: ".3" }),
                    React__default.createElement("stop", { offset: ".9", stopOpacity: "0" })),
                React__default.createElement("linearGradient", { id: c, x1: "-4995.033", y1: "-20162.835", x2: "-4981.733", y2: "-20180.895", gradientTransform: "rotate(-146.223 -971.422 -5714.55) scale(1 .493)", gradientUnits: "userSpaceOnUse" },
                    React__default.createElement("stop", { offset: ".32" }),
                    React__default.createElement("stop", { offset: ".354", stopOpacity: ".798" }),
                    React__default.createElement("stop", { offset: ".7", stopOpacity: "0" })),
                React__default.createElement("linearGradient", { id: d, x1: "0", y1: "32", x2: "32", y2: "0", gradientUnits: "userSpaceOnUse" },
                    React__default.createElement("stop", { offset: ".1", stopColor: "#a56eff" }),
                    React__default.createElement("stop", { offset: ".9", stopColor: "#0f62fe" })),
                React__default.createElement("mask", { id: e, x: "0", y: "0", width: "32", height: "32", maskUnits: "userSpaceOnUse" },
                    React__default.createElement("path", { d: "M16 1A14.915 14.915 0 0 0 5.502 5.286l1.4 1.429A12.922 12.922 0 0 1 16 3.001c.977 0 1.929.109 2.845.315-3.402.921-5.916 4.026-5.916 7.715 0 .782.118 1.537.328 2.252a7.978 7.978 0 0 0-2.188-.312c-3.704 0-6.819 2.534-7.726 5.957a12.954 12.954 0 0 1-.345-2.927c0-2.117.492-4.134 1.462-5.996l-1.773-.924A15.037 15.037 0 0 0 .999 16c0 8.271 6.729 15 15 15 3.949 0 7.678-1.522 10.498-4.286l-1.4-1.428A12.926 12.926 0 0 1 15.999 29c-3.648 0-6.945-1.516-9.309-3.945a5.959 5.959 0 0 1-1.621-4.086c0-3.309 2.691-6 6-6a6.006 6.006 0 0 1 5.897 7.107l1.967.367a7.971 7.971 0 0 0-.192-3.726 7.976 7.976 0 0 0 2.187.312c3.71 0 6.829-2.542 7.73-5.974.22.947.34 1.931.34 2.944 0 2.117-.492 4.134-1.462 5.995l1.773.924a15.034 15.034 0 0 0 1.688-6.919C31 7.729 24.272 1 16 1zm4.93 16.03c-3.309 0-6-2.692-6-6s2.691-6 6-6 6 2.691 6 6-2.691 6-6 6z", style: { fill: '#fff', strokeWidth: 0 } }),
                    React__default.createElement("path", { style: { fill: `url(#${a})`, strokeWidth: 0 }, d: "M8 9 0 0h16l2.305 3.304L8 9z" }),
                    React__default.createElement("path", { style: { fill: `url(#${b})`, strokeWidth: 0 }, d: "m12 31 4.386-9L6 21 2 31h10z" }),
                    React__default.createElement("path", { style: { fill: `url(#${c})`, strokeWidth: 0 }, d: "m24 23 8 9H16l-2.304-3.305L24 23z" }),
                    React__default.createElement("path", { style: { strokeWidth: 0 }, d: "M16 31h-4.283L15 22h2l-1 9z" }))),
            React__default.createElement("g", { style: { mask: `url(#${e})` } },
                React__default.createElement("path", { style: { fill: `url(#${d})`, strokeWidth: 0 }, d: "M0 0h32v32H0z" })),
            React__default.createElement("circle", { cx: "6", cy: "6", r: "2", style: { fill: '#001d6c', strokeWidth: 0 } }),
            React__default.createElement("circle", { cx: "26", cy: "26", r: "2", style: { fill: '#001d6c', strokeWidth: 0 } }),
            React__default.createElement("path", { d: "M16 31c-2.757 0-5-2.243-5-5s2.243-5 5-5 5 2.243 5 5-2.243 5-5 5zm0-8c-1.654 0-3 1.346-3 3s1.346 3 3 3 3-1.346 3-3-1.346-3-3-3z", style: { fill: '#001d6c', strokeWidth: 0 } })));
    }
    return (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 32 32", className: "cds--watsonx-avatar" },
        React__default.createElement("defs", null,
            React__default.createElement("linearGradient", { id: a, x1: "1196.653", y1: "2930.892", x2: "1209.953", y2: "2912.832", gradientTransform: "matrix(.8312 .55596 -.27409 .40979 -188.767 -1860.755)", gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: ".3" }),
                React__default.createElement("stop", { offset: "1", stopOpacity: "0" })),
            React__default.createElement("linearGradient", { id: b, x1: "1299.261", y1: "2844.072", x2: "1310.351", y2: "2829.012", gradientTransform: "rotate(146.223 440.869 -882.286) scale(1 -.493)", gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: ".3" }),
                React__default.createElement("stop", { offset: ".9", stopOpacity: "0" })),
            React__default.createElement("linearGradient", { id: c, x1: "-4885.16", y1: "-20230.559", x2: "-4871.86", y2: "-20248.618", gradientTransform: "rotate(-146.223 -911.421 -5714.55) scale(1 .493)", gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: ".32" }),
                React__default.createElement("stop", { offset: ".354", stopOpacity: ".798" }),
                React__default.createElement("stop", { offset: ".7", stopOpacity: "0" })),
            React__default.createElement("linearGradient", { id: d, x1: "0", y1: "32", x2: "32", y2: "0", gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: ".1", stopColor: "#be95ff" }),
                React__default.createElement("stop", { offset: ".9", stopColor: "#4589ff" })),
            React__default.createElement("mask", { id: e, x: "0", y: "0", width: "32", height: "32", maskUnits: "userSpaceOnUse" },
                React__default.createElement("path", { d: "M16 1A14.915 14.915 0 0 0 5.502 5.286l1.4 1.429A12.922 12.922 0 0 1 16 3.001c.977 0 1.929.109 2.845.315-3.402.921-5.916 4.026-5.916 7.715 0 .782.118 1.537.328 2.252a7.978 7.978 0 0 0-2.188-.312c-3.704 0-6.819 2.534-7.726 5.957a12.954 12.954 0 0 1-.345-2.927c0-2.117.492-4.134 1.462-5.996l-1.773-.924A15.037 15.037 0 0 0 .999 16c0 8.271 6.729 15 15 15 3.949 0 7.678-1.522 10.498-4.286l-1.4-1.428A12.926 12.926 0 0 1 15.999 29c-3.648 0-6.945-1.516-9.309-3.945a5.959 5.959 0 0 1-1.621-4.086c0-3.309 2.691-6 6-6a6.006 6.006 0 0 1 5.897 7.107l1.967.367a7.971 7.971 0 0 0-.192-3.726 7.976 7.976 0 0 0 2.187.312c3.71 0 6.829-2.542 7.73-5.974.22.947.34 1.931.34 2.944 0 2.117-.492 4.134-1.462 5.995l1.773.924a15.034 15.034 0 0 0 1.688-6.919c0-8.271-6.729-15-15-15zm4.93 16.03c-3.309 0-6-2.692-6-6s2.691-6 6-6 6 2.691 6 6-2.691 6-6 6z", style: { fill: '#fff', strokeWidth: 0 } }),
                React__default.createElement("path", { style: { fill: `url(#${a})`, strokeWidth: 0 }, d: "M8 9 0 0h16l2.305 3.305L8 9z" }),
                React__default.createElement("path", { style: { fill: `url(#${b})`, strokeWidth: 0 }, d: "m12 31 4.386-9L6 21 2 31h10z" }),
                React__default.createElement("path", { style: { fill: `url(#${c})`, strokeWidth: 0 }, d: "m24 23 8 9H16l-2.305-3.305L24 23z" }),
                React__default.createElement("path", { style: { strokeWidth: 0 }, d: "M16 31h-4.283L15 22h2l-1 9z" }))),
        React__default.createElement("g", { style: { mask: `url(#${e})` } },
            React__default.createElement("path", { style: { fill: `url(#${d})`, strokeWidth: 0 }, d: "M0 0h32v32H0z" })),
        React__default.createElement("circle", { cx: "6", cy: "6", r: "2", style: { fill: '#f4f4f4', strokeWidth: 0 } }),
        React__default.createElement("circle", { cx: "26", cy: "26", r: "2", style: { fill: '#f4f4f4', strokeWidth: 0 } }),
        React__default.createElement("path", { d: "M16 31c-2.757 0-5-2.243-5-5s2.243-5 5-5 5 2.243 5 5-2.243 5-5 5zm0-8c-1.654 0-3 1.346-3 3s1.346 3 3 3 3-1.346 3-3-1.346-3-3-3z", style: { fill: '#f4f4f4', strokeWidth: 0 } })));
}
const AvatarExport = React__default.memo(Avatar);

/**
 * The branding overlay that appears everytime a user clicks on the information icon in the branding banner to learn a
 * little about IBM Watson.
 *
 * Note: We should only make the close button visible when the overlay is open. The reason for this is because VoiceOver
 * still places it's virtual cursor on the close button even after the close animation for the panel has ended and
 * announces to close the overlay.
 */
function BrandingOverlayComponent({ brandingCloseButtonRef, carbonTheme }) {
    const { branding_headline, branding_description, branding_sign_up, general_ariaAnnounceClosedInformationOverlay, general_ariaAnnounceEscapeOverlay, } = useLanguagePack();
    const serviceManager = useServiceManager();
    const brandingState = useSelector((app) => app.brandingState);
    const ariaAnnouncer = useContext(AriaAnnouncerContext);
    const overlayRef = useRef();
    const overlayTextHolderRef = useRef();
    const { isOpen } = brandingState;
    // Gets the sign up url that includes the hostname as the utm_source.
    const getSignUpURL = `https://www.ibm.com/cloud/watson-assistant/?utm_medium=webchat&utm_source=${window.location.hostname}`;
    // Closes branding overlay, but show branding banner.
    const handleClose = () => {
        serviceManager.store.dispatch(actions.setBrandingOverlayIsOpen(false));
    };
    useEffect(() => {
        if (isOpen) {
            ariaAnnouncer(overlayTextHolderRef.current);
            ariaAnnouncer(general_ariaAnnounceEscapeOverlay);
        }
    }, [ariaAnnouncer, general_ariaAnnounceEscapeOverlay, isOpen]);
    return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    React__default.createElement("div", { className: "WACBrandingOverlay WACPanelContent", ref: overlayRef, onKeyDown: event => {
            // Close the branding overlay when the user presses the escape key.
            if (event.key === 'Escape') {
                handleClose();
            }
        } },
        React__default.createElement("div", { className: "WACBrandingOverlay__header" }, isOpen && (React__default.createElement(Button, { kind: ButtonKindEnum.GHOST, size: ButtonSizeEnum.SMALL, onClick: handleClose, type: "button", "aria-label": general_ariaAnnounceClosedInformationOverlay, ref: brandingCloseButtonRef },
            React__default.createElement(Close, null)))),
        React__default.createElement("div", { className: "WACBrandingOverlay__body" },
            React__default.createElement("div", { className: "WACBrandingOverlay__main-content" },
                React__default.createElement("div", { className: "WACBrandingOverlay__logoContainer" },
                    React__default.createElement(AvatarExport, { theme: carbonTheme })),
                React__default.createElement("div", { className: "WACBrandingOverlay__textHolder", ref: overlayTextHolderRef },
                    React__default.createElement(MaybeDangerouslySetInnerHTMLExport, { html: branding_headline }),
                    React__default.createElement("div", { className: "WACBrandingOverlay__headline" }),
                    React__default.createElement("div", { className: "WACBrandingOverlay__text" },
                        branding_description,
                        ' ',
                        React__default.createElement("a", { href: getSignUpURL, rel: "noreferrer noopener", target: "_blank" }, branding_sign_up)))))));
}
const BrandingOverlayExport = React__default.memo(BrandingOverlayComponent);

/**
 * This component renders an error message svg to indicate an error has occurred.
 */
function ErrorMessageDark() {
    const linearGradientID = uuid();
    return (React__default.createElement("svg", { viewBox: "0 0 80 80" },
        React__default.createElement("defs", null,
            React__default.createElement("linearGradient", { id: `${linearGradientID}-1`, x1: "38.91", y1: "4.92", x2: "38.91", y2: "73.85", gradientTransform: "matrix(1, 0, 0, -1, 0, 82)", gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: "0", stopColor: "#262626" }),
                React__default.createElement("stop", { offset: "1", stopColor: "#393939" })),
            React__default.createElement("linearGradient", { id: `${linearGradientID}-2`, x1: "12.44", y1: "71.21", x2: "76.34", y2: "34.31", gradientTransform: "matrix(1, 0, 0, -1, 0, 82)", gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: "0", stopColor: "#525252" }),
                React__default.createElement("stop", { offset: "0.52", stopColor: "#393939" }),
                React__default.createElement("stop", { offset: "0.61", stopColor: "#393939" }),
                React__default.createElement("stop", { offset: "0.99", stopColor: "#161616" })),
            React__default.createElement("linearGradient", { id: `${linearGradientID}-3`, x1: "39.38", y1: "50.63", x2: "52.04", y2: "72.55", gradientTransform: "matrix(1, 0, 0, -1, 0, 82)", gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: "0.11", stopColor: "#6f6f6f", stopOpacity: "0" }),
                React__default.createElement("stop", { offset: "0.94", stopColor: "#6f6f6f" }))),
        React__default.createElement("rect", { width: "80", height: "80", fill: "none" }),
        React__default.createElement("polygon", { points: "59.91 78.34 16.91 53.51 21.77 50.7 64.77 75.53 59.91 78.34", opacity: "0.25", style: { isolation: 'isolate' } }),
        React__default.createElement("path", { d: "M39,6.92c12.15,7,22,24,21.92,38S51,64.49,38.83,57.48s-22-24-21.92-38S26.83-.09,39,6.92Z", fill: `url(#${linearGradientID}-1)` }),
        React__default.createElement("path", { d: "M42.85,4.68C36.74,1.15,31.2.82,27.2,3.15L23.54,5.28C27.52,3.08,33,3.45,39,6.92c12.15,7,22,24,21.92,38,0,6.77-2.35,11.58-6.13,13.94h-.07c-.32.2,3.66-2.1,3.66-2.1,4-2.3,6.39-7.18,6.41-14.12C64.81,28.7,55,11.69,42.85,4.68Z", fill: `url(#${linearGradientID}-2)` }),
        React__default.createElement("path", { d: "M29.11,3.91v.36a19.59,19.59,0,0,1,9.68,3c12,6.94,21.78,23.84,21.74,37.65,0,9.4-4.56,15.23-11.83,15.23a19.59,19.59,0,0,1-9.68-3C27,50.21,17.24,33.32,17.28,19.5c0-9.39,4.56-15.23,11.83-15.23V3.91m0,0c-7.21,0-12.17,5.71-12.2,15.59,0,14,9.77,31,21.92,38a20.18,20.18,0,0,0,9.87,3c7.21,0,12.17-5.71,12.2-15.6C60.9,31,51.13,14,39,6.9a19.94,19.94,0,0,0-9.87-3Z", fill: `url(#${linearGradientID}-3)` }),
        React__default.createElement("path", { className: "cls-6", d: "M38.93,49.79a6.83,6.83,0,0,1-2.66-2.51,6.19,6.19,0,0,1-.81-3v-1a2.26,2.26,0,0,1,.81-2c.54-.35,1.43-.17,2.66.54a6.67,6.67,0,0,1,2.61,2.5,6,6,0,0,1,.81,3v1a2.23,2.23,0,0,1-.81,2C41,50.66,40.13,50.49,38.93,49.79ZM37.77,38.16,36,22.77V13l5.81,3.36v9.73L40.17,39.55Z", fill: "#525252" })));
}

/**
 * This component renders an error message svg to indicate an error has occurred.
 */
function ErrorMessageLight() {
    const linearGradientID = uuid();
    return (React__default.createElement("svg", { viewBox: "0 0 80 80" },
        React__default.createElement("defs", null,
            React__default.createElement("linearGradient", { id: `${linearGradientID}-1`, x1: "29.96", y1: "36.32", x2: "53.15", y2: "-3.84", gradientTransform: "matrix(1, 0, 0, -1, 0, 82)", gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: "0", stopColor: "#525252", stopOpacity: "0.05" }),
                React__default.createElement("stop", { offset: "1", stopOpacity: "0.1" })),
            React__default.createElement("linearGradient", { id: `${linearGradientID}-2`, x1: "38.91", y1: "29.41", x2: "38.91", y2: "78.7", gradientTransform: "matrix(1, 0, 0, -1, 0, 82)", gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: "0", stopColor: "#c6c6c6" }),
                React__default.createElement("stop", { offset: "0.78", stopColor: "#e0e0e0" })),
            React__default.createElement("linearGradient", { id: `${linearGradientID}-3`, x1: "18.08", y1: "67.95", x2: "71.65", y2: "37.02", gradientTransform: "matrix(1, 0, 0, -1, 0, 82)", gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: "0", stopColor: "#e0e0e0" }),
                React__default.createElement("stop", { offset: "0.13", stopColor: "#f4f4f4" }),
                React__default.createElement("stop", { offset: "0.56", stopColor: "#e0e0e0" }),
                React__default.createElement("stop", { offset: "0.62", stopColor: "#d8d8d8" }),
                React__default.createElement("stop", { offset: "0.7", stopColor: "#c6c6c6" }),
                React__default.createElement("stop", { offset: "0.89", stopColor: "#a8a8a8" }),
                React__default.createElement("stop", { offset: "1", stopColor: "#8d8d8d" })),
            React__default.createElement("linearGradient", { id: `${linearGradientID}-4`, x1: "27.93", y1: "30.78", x2: "49.86", y2: "68.76", gradientTransform: "matrix(1, 0, 0, -1, 0, 82)", gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: "0.54", stopColor: "#d0d0d0", stopOpacity: "0" }),
                React__default.createElement("stop", { offset: "0.82", stopColor: "#f1f1f1", stopOpacity: "0.7" }),
                React__default.createElement("stop", { offset: "0.94", stopColor: "#fff" })),
            React__default.createElement("linearGradient", { id: `${linearGradientID}-5`, x1: "28.67", y1: "55.68", x2: "47.16", y2: "45.01", gradientTransform: "matrix(1, 0, 0, -1, 0, 82)", gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: "0", stopColor: "#fff" }),
                React__default.createElement("stop", { offset: "0.05", stopColor: "#fdfdfd" }),
                React__default.createElement("stop", { offset: "0.3", stopColor: "#f6f6f6" }),
                React__default.createElement("stop", { offset: "1", stopColor: "#f4f4f4" }))),
        React__default.createElement("rect", { width: "80", height: "80", fill: "none" }),
        React__default.createElement("polygon", { points: "59.91 78.34 16.91 53.51 21.77 50.7 64.77 75.53 59.91 78.34", fill: `url(#${linearGradientID}-1)` }),
        React__default.createElement("path", { d: "M39,6.92c12.15,7,22,24,21.92,38S51,64.49,38.83,57.48s-22-24-21.92-38S26.83-.09,39,6.92Z", fill: `url(#${linearGradientID}-2)` }),
        React__default.createElement("path", { d: "M42.85,4.68C36.74,1.15,31.2.82,27.2,3.15L23.54,5.28C27.52,3.08,33,3.45,39,6.92c12.15,7,22,24,21.92,38,0,6.77-2.35,11.58-6.13,13.94h-.07c-.32.2,3.66-2.1,3.66-2.1,4-2.3,6.39-7.18,6.41-14.12C64.81,28.7,55,11.69,42.85,4.68Z", fill: `url(#${linearGradientID}-3)` }),
        React__default.createElement("path", { d: "M29.11,3.91v.36a19.59,19.59,0,0,1,9.68,3c12,6.94,21.78,23.84,21.74,37.65,0,9.4-4.56,15.23-11.83,15.23a19.59,19.59,0,0,1-9.68-3C27,50.21,17.24,33.32,17.28,19.5c0-9.39,4.56-15.23,11.83-15.23V3.91m0,0c-7.21,0-12.17,5.71-12.2,15.59,0,14,9.77,31,21.92,38a20.18,20.18,0,0,0,9.87,3c7.21,0,12.17-5.71,12.2-15.6C60.9,31,51.13,14,39,6.9a19.94,19.94,0,0,0-9.87-3Z", fill: `url(#${linearGradientID}-4)` }),
        React__default.createElement("path", { d: "M38.93,49.79a6.83,6.83,0,0,1-2.66-2.51,6.19,6.19,0,0,1-.81-3v-1a2.26,2.26,0,0,1,.81-2c.54-.35,1.43-.17,2.66.54a6.67,6.67,0,0,1,2.61,2.5,6,6,0,0,1,.81,3v1a2.23,2.23,0,0,1-.81,2C41,50.66,40.13,50.49,38.93,49.79ZM37.77,38.16,36,22.77V13l5.81,3.36v9.73L40.17,39.55Z", fill: `url(#${linearGradientID}-5)` })));
}

function WriteableElement({ slotName, element, id, className }) {
    const config = useSelector((state) => state.config);
    const useShadowRoot = config.public.__ibm__?.useShadowRoot;
    const reactHostRef = useRef();
    // Attach the host element to the ref for this component.
    useEffect(() => {
        if (!useShadowRoot) {
            const hostElement = reactHostRef.current;
            if (element) {
                hostElement.appendChild(element);
            }
            return () => {
                if (hostElement && element && hostElement.contains(element)) {
                    hostElement.removeChild(element);
                }
            };
        }
        return undefined;
    }, [element, useShadowRoot]);
    if (useShadowRoot) {
        return (React__default.createElement("div", { className: className, id: id, "data-floating-menu-container": true },
            React__default.createElement("slot", { name: slotName })));
    }
    return element && React__default.createElement("div", { ref: reactHostRef, className: cx('ibm-web-chat--reset-styles', className), id: id });
}
var WriteableElement$1 = React__default.memo(WriteableElement);

function chatHeaderAvatarTemplate(classElement) {
    const { url, corners, alt, onError } = classElement;
    return html$2 `
    <img
      class="${cx(`${CSS_CLASS_PREFIX}-chat-header-avatar`, {
        [`${CSS_CLASS_PREFIX}-chat-header-avatar--round`]: corners === CornersType.ROUND,
    })}"
      src="${url}"
      alt="${alt}"
      @error="${onError}"
    />
  `;
}

var css_248z$6 = ".cds--aichat-chat-header-avatar{max-height:20px;height:20px;display:block}.cds--aichat-chat-header-avatar--round{border-radius:10px}";

class ChatHeaderAvatarElement extends LitElement {
    constructor() {
        super(...arguments);
        this.isLoaded = false;
    }
    static { this.styles = css `
    ${unsafeCSS(css_248z$6)}
  `; }
    /**
     * Called when the onError callback function is fired.
     */
    _handleOnError() {
        this.onError?.();
    }
}
__decorate([
    property({ type: String })
], ChatHeaderAvatarElement.prototype, "url", void 0);
__decorate([
    property({ type: String })
], ChatHeaderAvatarElement.prototype, "corners", void 0);
__decorate([
    property({ type: String })
], ChatHeaderAvatarElement.prototype, "alt", void 0);
__decorate([
    property({ type: Object })
], ChatHeaderAvatarElement.prototype, "onError", void 0);
__decorate([
    state()
], ChatHeaderAvatarElement.prototype, "isLoaded", void 0);
__decorate([
    bind_1
], ChatHeaderAvatarElement.prototype, "_handleOnError", null);

const CHAT_HEADER_AVATAR_TAG_NAME = 'cds-aichat-chat-header-avatar';
/**
 * Constructed class functionality for the chat header avatar.
 */
let CDSChatHeaderAvatarElement = class CDSChatHeaderAvatarElement extends ChatHeaderAvatarElement {
    render() {
        return chatHeaderAvatarTemplate(this);
    }
};
CDSChatHeaderAvatarElement = __decorate([
    carbonElement(CHAT_HEADER_AVATAR_TAG_NAME)
], CDSChatHeaderAvatarElement);
var CDSChatHeaderAvatarElement$1 = CDSChatHeaderAvatarElement;

const ChatHeaderAvatar = o$1({
    tagName: CHAT_HEADER_AVATAR_TAG_NAME,
    elementClass: CDSChatHeaderAvatarElement$1,
    react: React__default,
});

function getNodeName(node) {
  if (isNode(node)) {
    return (node.nodeName || '').toLowerCase();
  }
  // Mocked nodes in testing environments may not be instances of Node. By
  // returning `#document` an infinite loop won't occur.
  // https://github.com/floating-ui/floating-ui/issues/2317
  return '#document';
}
function getWindow(node) {
  var _node$ownerDocument;
  return (node == null || (_node$ownerDocument = node.ownerDocument) == null ? void 0 : _node$ownerDocument.defaultView) || window;
}
function getDocumentElement(node) {
  var _ref;
  return (_ref = (isNode(node) ? node.ownerDocument : node.document) || window.document) == null ? void 0 : _ref.documentElement;
}
function isNode(value) {
  return value instanceof Node || value instanceof getWindow(value).Node;
}
function isElement(value) {
  return value instanceof Element || value instanceof getWindow(value).Element;
}
function isHTMLElement(value) {
  return value instanceof HTMLElement || value instanceof getWindow(value).HTMLElement;
}
function isShadowRoot(value) {
  // Browsers without `ShadowRoot` support.
  if (typeof ShadowRoot === 'undefined') {
    return false;
  }
  return value instanceof ShadowRoot || value instanceof getWindow(value).ShadowRoot;
}
function isOverflowElement(element) {
  const {
    overflow,
    overflowX,
    overflowY,
    display
  } = getComputedStyle$1(element);
  return /auto|scroll|overlay|hidden|clip/.test(overflow + overflowY + overflowX) && !['inline', 'contents'].includes(display);
}
function isTableElement(element) {
  return ['table', 'td', 'th'].includes(getNodeName(element));
}
function isContainingBlock(element) {
  const webkit = isWebKit();
  const css = getComputedStyle$1(element);

  // https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block
  return css.transform !== 'none' || css.perspective !== 'none' || (css.containerType ? css.containerType !== 'normal' : false) || !webkit && (css.backdropFilter ? css.backdropFilter !== 'none' : false) || !webkit && (css.filter ? css.filter !== 'none' : false) || ['transform', 'perspective', 'filter'].some(value => (css.willChange || '').includes(value)) || ['paint', 'layout', 'strict', 'content'].some(value => (css.contain || '').includes(value));
}
function getContainingBlock(element) {
  let currentNode = getParentNode(element);
  while (isHTMLElement(currentNode) && !isLastTraversableNode(currentNode)) {
    if (isContainingBlock(currentNode)) {
      return currentNode;
    } else {
      currentNode = getParentNode(currentNode);
    }
  }
  return null;
}
function isWebKit() {
  if (typeof CSS === 'undefined' || !CSS.supports) return false;
  return CSS.supports('-webkit-backdrop-filter', 'none');
}
function isLastTraversableNode(node) {
  return ['html', 'body', '#document'].includes(getNodeName(node));
}
function getComputedStyle$1(element) {
  return getWindow(element).getComputedStyle(element);
}
function getNodeScroll(element) {
  if (isElement(element)) {
    return {
      scrollLeft: element.scrollLeft,
      scrollTop: element.scrollTop
    };
  }
  return {
    scrollLeft: element.pageXOffset,
    scrollTop: element.pageYOffset
  };
}
function getParentNode(node) {
  if (getNodeName(node) === 'html') {
    return node;
  }
  const result =
  // Step into the shadow DOM of the parent of a slotted node.
  node.assignedSlot ||
  // DOM Element detected.
  node.parentNode ||
  // ShadowRoot detected.
  isShadowRoot(node) && node.host ||
  // Fallback.
  getDocumentElement(node);
  return isShadowRoot(result) ? result.host : result;
}
function getNearestOverflowAncestor(node) {
  const parentNode = getParentNode(node);
  if (isLastTraversableNode(parentNode)) {
    return node.ownerDocument ? node.ownerDocument.body : node.body;
  }
  if (isHTMLElement(parentNode) && isOverflowElement(parentNode)) {
    return parentNode;
  }
  return getNearestOverflowAncestor(parentNode);
}
function getOverflowAncestors(node, list, traverseIframes) {
  var _node$ownerDocument2;
  if (list === void 0) {
    list = [];
  }
  if (traverseIframes === void 0) {
    traverseIframes = true;
  }
  const scrollableAncestor = getNearestOverflowAncestor(node);
  const isBody = scrollableAncestor === ((_node$ownerDocument2 = node.ownerDocument) == null ? void 0 : _node$ownerDocument2.body);
  const win = getWindow(scrollableAncestor);
  if (isBody) {
    return list.concat(win, win.visualViewport || [], isOverflowElement(scrollableAncestor) ? scrollableAncestor : [], win.frameElement && traverseIframes ? getOverflowAncestors(win.frameElement) : []);
  }
  return list.concat(scrollableAncestor, getOverflowAncestors(scrollableAncestor, [], traverseIframes));
}

function contains(parent, child) {
  if (!parent || !child) {
    return false;
  }
  const rootNode = child.getRootNode == null ? void 0 : child.getRootNode();

  // First, attempt with faster native method
  if (parent.contains(child)) {
    return true;
  }

  // then fallback to custom implementation with Shadow DOM support
  if (rootNode && isShadowRoot(rootNode)) {
    let next = child;
    while (next) {
      if (parent === next) {
        return true;
      }
      // @ts-ignore
      next = next.parentNode || next.host;
    }
  }

  // Give up, the result is false
  return false;
}
function isMouseLikePointerType(pointerType, strict) {
  // On some Linux machines with Chromium, mouse inputs return a `pointerType`
  // of "pen": https://github.com/floating-ui/floating-ui/issues/2015
  const values = ['mouse', 'pen'];
  return values.includes(pointerType);
}
function isReactEvent(event) {
  return 'nativeEvent' in event;
}
function isRootElement(element) {
  return element.matches('html,body');
}
function getDocument(node) {
  return (node == null ? void 0 : node.ownerDocument) || document;
}
function isEventTargetWithin(event, node) {
  if (node == null) {
    return false;
  }
  if ('composedPath' in event) {
    return event.composedPath().includes(node);
  }

  // TS thinks `event` is of type never as it assumes all browsers support composedPath, but browsers without shadow dom don't
  const e = event;
  return e.target != null && node.contains(e.target);
}
function getTarget(event) {
  if ('composedPath' in event) {
    return event.composedPath()[0];
  }

  // TS thinks `event` is of type never as it assumes all browsers support
  // `composedPath()`, but browsers without shadow DOM don't.
  return event.target;
}
const TYPEABLE_SELECTOR = "input:not([type='hidden']):not([disabled])," + "[contenteditable]:not([contenteditable='false']),textarea:not([disabled])";
function isTypeableElement(element) {
  return isHTMLElement(element) && element.matches(TYPEABLE_SELECTOR);
}

/**
 * Custom positioning reference element.
 * @see https://floating-ui.com/docs/virtual-elements
 */

const min = Math.min;
const max = Math.max;
const round = Math.round;
const createCoords = v => ({
  x: v,
  y: v
});
function getSide(placement) {
  return placement.split('-')[0];
}
function getAlignment(placement) {
  return placement.split('-')[1];
}
function getOppositeAxis(axis) {
  return axis === 'x' ? 'y' : 'x';
}
function getAxisLength(axis) {
  return axis === 'y' ? 'height' : 'width';
}
function getSideAxis(placement) {
  return ['top', 'bottom'].includes(getSide(placement)) ? 'y' : 'x';
}
function getAlignmentAxis(placement) {
  return getOppositeAxis(getSideAxis(placement));
}
function rectToClientRect(rect) {
  return {
    ...rect,
    top: rect.y,
    left: rect.x,
    right: rect.x + rect.width,
    bottom: rect.y + rect.height
  };
}

function computeCoordsFromPlacement(_ref, placement, rtl) {
  let {
    reference,
    floating
  } = _ref;
  const sideAxis = getSideAxis(placement);
  const alignmentAxis = getAlignmentAxis(placement);
  const alignLength = getAxisLength(alignmentAxis);
  const side = getSide(placement);
  const isVertical = sideAxis === 'y';
  const commonX = reference.x + reference.width / 2 - floating.width / 2;
  const commonY = reference.y + reference.height / 2 - floating.height / 2;
  const commonAlign = reference[alignLength] / 2 - floating[alignLength] / 2;
  let coords;
  switch (side) {
    case 'top':
      coords = {
        x: commonX,
        y: reference.y - floating.height
      };
      break;
    case 'bottom':
      coords = {
        x: commonX,
        y: reference.y + reference.height
      };
      break;
    case 'right':
      coords = {
        x: reference.x + reference.width,
        y: commonY
      };
      break;
    case 'left':
      coords = {
        x: reference.x - floating.width,
        y: commonY
      };
      break;
    default:
      coords = {
        x: reference.x,
        y: reference.y
      };
  }
  switch (getAlignment(placement)) {
    case 'start':
      coords[alignmentAxis] -= commonAlign * (rtl && isVertical ? -1 : 1);
      break;
    case 'end':
      coords[alignmentAxis] += commonAlign * (rtl && isVertical ? -1 : 1);
      break;
  }
  return coords;
}

/**
 * Computes the `x` and `y` coordinates that will place the floating element
 * next to a given reference element.
 *
 * This export does not have any `platform` interface logic. You will need to
 * write one for the platform you are using Floating UI with.
 */
const computePosition$1 = async (reference, floating, config) => {
  const {
    placement = 'bottom',
    strategy = 'absolute',
    middleware = [],
    platform
  } = config;
  const validMiddleware = middleware.filter(Boolean);
  const rtl = await (platform.isRTL == null ? void 0 : platform.isRTL(floating));
  let rects = await platform.getElementRects({
    reference,
    floating,
    strategy
  });
  let {
    x,
    y
  } = computeCoordsFromPlacement(rects, placement, rtl);
  let statefulPlacement = placement;
  let middlewareData = {};
  let resetCount = 0;
  for (let i = 0; i < validMiddleware.length; i++) {
    const {
      name,
      fn
    } = validMiddleware[i];
    const {
      x: nextX,
      y: nextY,
      data,
      reset
    } = await fn({
      x,
      y,
      initialPlacement: placement,
      placement: statefulPlacement,
      strategy,
      middlewareData,
      rects,
      platform,
      elements: {
        reference,
        floating
      }
    });
    x = nextX != null ? nextX : x;
    y = nextY != null ? nextY : y;
    middlewareData = {
      ...middlewareData,
      [name]: {
        ...middlewareData[name],
        ...data
      }
    };
    if (reset && resetCount <= 50) {
      resetCount++;
      if (typeof reset === 'object') {
        if (reset.placement) {
          statefulPlacement = reset.placement;
        }
        if (reset.rects) {
          rects = reset.rects === true ? await platform.getElementRects({
            reference,
            floating,
            strategy
          }) : reset.rects;
        }
        ({
          x,
          y
        } = computeCoordsFromPlacement(rects, statefulPlacement, rtl));
      }
      i = -1;
    }
  }
  return {
    x,
    y,
    placement: statefulPlacement,
    strategy,
    middlewareData
  };
};

function getCssDimensions(element) {
  const css = getComputedStyle$1(element);
  // In testing environments, the `width` and `height` properties are empty
  // strings for SVG elements, returning NaN. Fallback to `0` in this case.
  let width = parseFloat(css.width) || 0;
  let height = parseFloat(css.height) || 0;
  const hasOffset = isHTMLElement(element);
  const offsetWidth = hasOffset ? element.offsetWidth : width;
  const offsetHeight = hasOffset ? element.offsetHeight : height;
  const shouldFallback = round(width) !== offsetWidth || round(height) !== offsetHeight;
  if (shouldFallback) {
    width = offsetWidth;
    height = offsetHeight;
  }
  return {
    width,
    height,
    $: shouldFallback
  };
}

function unwrapElement(element) {
  return !isElement(element) ? element.contextElement : element;
}

function getScale(element) {
  const domElement = unwrapElement(element);
  if (!isHTMLElement(domElement)) {
    return createCoords(1);
  }
  const rect = domElement.getBoundingClientRect();
  const {
    width,
    height,
    $
  } = getCssDimensions(domElement);
  let x = ($ ? round(rect.width) : rect.width) / width;
  let y = ($ ? round(rect.height) : rect.height) / height;

  // 0, NaN, or Infinity should always fallback to 1.

  if (!x || !Number.isFinite(x)) {
    x = 1;
  }
  if (!y || !Number.isFinite(y)) {
    y = 1;
  }
  return {
    x,
    y
  };
}

const noOffsets = /*#__PURE__*/createCoords(0);
function getVisualOffsets(element) {
  const win = getWindow(element);
  if (!isWebKit() || !win.visualViewport) {
    return noOffsets;
  }
  return {
    x: win.visualViewport.offsetLeft,
    y: win.visualViewport.offsetTop
  };
}
function shouldAddVisualOffsets(element, isFixed, floatingOffsetParent) {
  if (isFixed === void 0) {
    isFixed = false;
  }
  if (!floatingOffsetParent || isFixed && floatingOffsetParent !== getWindow(element)) {
    return false;
  }
  return isFixed;
}

function getBoundingClientRect(element, includeScale, isFixedStrategy, offsetParent) {
  if (includeScale === void 0) {
    includeScale = false;
  }
  if (isFixedStrategy === void 0) {
    isFixedStrategy = false;
  }
  const clientRect = element.getBoundingClientRect();
  const domElement = unwrapElement(element);
  let scale = createCoords(1);
  if (includeScale) {
    if (offsetParent) {
      if (isElement(offsetParent)) {
        scale = getScale(offsetParent);
      }
    } else {
      scale = getScale(element);
    }
  }
  const visualOffsets = shouldAddVisualOffsets(domElement, isFixedStrategy, offsetParent) ? getVisualOffsets(domElement) : createCoords(0);
  let x = (clientRect.left + visualOffsets.x) / scale.x;
  let y = (clientRect.top + visualOffsets.y) / scale.y;
  let width = clientRect.width / scale.x;
  let height = clientRect.height / scale.y;
  if (domElement) {
    const win = getWindow(domElement);
    const offsetWin = offsetParent && isElement(offsetParent) ? getWindow(offsetParent) : offsetParent;
    let currentWin = win;
    let currentIFrame = currentWin.frameElement;
    while (currentIFrame && offsetParent && offsetWin !== currentWin) {
      const iframeScale = getScale(currentIFrame);
      const iframeRect = currentIFrame.getBoundingClientRect();
      const css = getComputedStyle$1(currentIFrame);
      const left = iframeRect.left + (currentIFrame.clientLeft + parseFloat(css.paddingLeft)) * iframeScale.x;
      const top = iframeRect.top + (currentIFrame.clientTop + parseFloat(css.paddingTop)) * iframeScale.y;
      x *= iframeScale.x;
      y *= iframeScale.y;
      width *= iframeScale.x;
      height *= iframeScale.y;
      x += left;
      y += top;
      currentWin = getWindow(currentIFrame);
      currentIFrame = currentWin.frameElement;
    }
  }
  return rectToClientRect({
    width,
    height,
    x,
    y
  });
}

const topLayerSelectors = [':popover-open', ':modal'];
function isTopLayer(floating) {
  return topLayerSelectors.some(selector => {
    try {
      return floating.matches(selector);
    } catch (e) {
      return false;
    }
  });
}

function convertOffsetParentRelativeRectToViewportRelativeRect(_ref) {
  let {
    elements,
    rect,
    offsetParent,
    strategy
  } = _ref;
  const isFixed = strategy === 'fixed';
  const documentElement = getDocumentElement(offsetParent);
  const topLayer = elements ? isTopLayer(elements.floating) : false;
  if (offsetParent === documentElement || topLayer && isFixed) {
    return rect;
  }
  let scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  let scale = createCoords(1);
  const offsets = createCoords(0);
  const isOffsetParentAnElement = isHTMLElement(offsetParent);
  if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
    if (getNodeName(offsetParent) !== 'body' || isOverflowElement(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }
    if (isHTMLElement(offsetParent)) {
      const offsetRect = getBoundingClientRect(offsetParent);
      scale = getScale(offsetParent);
      offsets.x = offsetRect.x + offsetParent.clientLeft;
      offsets.y = offsetRect.y + offsetParent.clientTop;
    }
  }
  return {
    width: rect.width * scale.x,
    height: rect.height * scale.y,
    x: rect.x * scale.x - scroll.scrollLeft * scale.x + offsets.x,
    y: rect.y * scale.y - scroll.scrollTop * scale.y + offsets.y
  };
}

function getClientRects(element) {
  return Array.from(element.getClientRects());
}

function getWindowScrollBarX(element) {
  // If <html> has a CSS width greater than the viewport, then this will be
  // incorrect for RTL.
  return getBoundingClientRect(getDocumentElement(element)).left + getNodeScroll(element).scrollLeft;
}

// Gets the entire size of the scrollable document area, even extending outside
// of the `<html>` and `<body>` rect bounds if horizontally scrollable.
function getDocumentRect(element) {
  const html = getDocumentElement(element);
  const scroll = getNodeScroll(element);
  const body = element.ownerDocument.body;
  const width = max(html.scrollWidth, html.clientWidth, body.scrollWidth, body.clientWidth);
  const height = max(html.scrollHeight, html.clientHeight, body.scrollHeight, body.clientHeight);
  let x = -scroll.scrollLeft + getWindowScrollBarX(element);
  const y = -scroll.scrollTop;
  if (getComputedStyle$1(body).direction === 'rtl') {
    x += max(html.clientWidth, body.clientWidth) - width;
  }
  return {
    width,
    height,
    x,
    y
  };
}

function getViewportRect(element, strategy) {
  const win = getWindow(element);
  const html = getDocumentElement(element);
  const visualViewport = win.visualViewport;
  let width = html.clientWidth;
  let height = html.clientHeight;
  let x = 0;
  let y = 0;
  if (visualViewport) {
    width = visualViewport.width;
    height = visualViewport.height;
    const visualViewportBased = isWebKit();
    if (!visualViewportBased || visualViewportBased && strategy === 'fixed') {
      x = visualViewport.offsetLeft;
      y = visualViewport.offsetTop;
    }
  }
  return {
    width,
    height,
    x,
    y
  };
}

// Returns the inner client rect, subtracting scrollbars if present.
function getInnerBoundingClientRect(element, strategy) {
  const clientRect = getBoundingClientRect(element, true, strategy === 'fixed');
  const top = clientRect.top + element.clientTop;
  const left = clientRect.left + element.clientLeft;
  const scale = isHTMLElement(element) ? getScale(element) : createCoords(1);
  const width = element.clientWidth * scale.x;
  const height = element.clientHeight * scale.y;
  const x = left * scale.x;
  const y = top * scale.y;
  return {
    width,
    height,
    x,
    y
  };
}
function getClientRectFromClippingAncestor(element, clippingAncestor, strategy) {
  let rect;
  if (clippingAncestor === 'viewport') {
    rect = getViewportRect(element, strategy);
  } else if (clippingAncestor === 'document') {
    rect = getDocumentRect(getDocumentElement(element));
  } else if (isElement(clippingAncestor)) {
    rect = getInnerBoundingClientRect(clippingAncestor, strategy);
  } else {
    const visualOffsets = getVisualOffsets(element);
    rect = {
      ...clippingAncestor,
      x: clippingAncestor.x - visualOffsets.x,
      y: clippingAncestor.y - visualOffsets.y
    };
  }
  return rectToClientRect(rect);
}
function hasFixedPositionAncestor(element, stopNode) {
  const parentNode = getParentNode(element);
  if (parentNode === stopNode || !isElement(parentNode) || isLastTraversableNode(parentNode)) {
    return false;
  }
  return getComputedStyle$1(parentNode).position === 'fixed' || hasFixedPositionAncestor(parentNode, stopNode);
}

// A "clipping ancestor" is an `overflow` element with the characteristic of
// clipping (or hiding) child elements. This returns all clipping ancestors
// of the given element up the tree.
function getClippingElementAncestors(element, cache) {
  const cachedResult = cache.get(element);
  if (cachedResult) {
    return cachedResult;
  }
  let result = getOverflowAncestors(element, [], false).filter(el => isElement(el) && getNodeName(el) !== 'body');
  let currentContainingBlockComputedStyle = null;
  const elementIsFixed = getComputedStyle$1(element).position === 'fixed';
  let currentNode = elementIsFixed ? getParentNode(element) : element;

  // https://developer.mozilla.org/en-US/docs/Web/CSS/Containing_block#identifying_the_containing_block
  while (isElement(currentNode) && !isLastTraversableNode(currentNode)) {
    const computedStyle = getComputedStyle$1(currentNode);
    const currentNodeIsContaining = isContainingBlock(currentNode);
    if (!currentNodeIsContaining && computedStyle.position === 'fixed') {
      currentContainingBlockComputedStyle = null;
    }
    const shouldDropCurrentNode = elementIsFixed ? !currentNodeIsContaining && !currentContainingBlockComputedStyle : !currentNodeIsContaining && computedStyle.position === 'static' && !!currentContainingBlockComputedStyle && ['absolute', 'fixed'].includes(currentContainingBlockComputedStyle.position) || isOverflowElement(currentNode) && !currentNodeIsContaining && hasFixedPositionAncestor(element, currentNode);
    if (shouldDropCurrentNode) {
      // Drop non-containing blocks.
      result = result.filter(ancestor => ancestor !== currentNode);
    } else {
      // Record last containing block for next iteration.
      currentContainingBlockComputedStyle = computedStyle;
    }
    currentNode = getParentNode(currentNode);
  }
  cache.set(element, result);
  return result;
}

// Gets the maximum area that the element is visible in due to any number of
// clipping ancestors.
function getClippingRect(_ref) {
  let {
    element,
    boundary,
    rootBoundary,
    strategy
  } = _ref;
  const elementClippingAncestors = boundary === 'clippingAncestors' ? getClippingElementAncestors(element, this._c) : [].concat(boundary);
  const clippingAncestors = [...elementClippingAncestors, rootBoundary];
  const firstClippingAncestor = clippingAncestors[0];
  const clippingRect = clippingAncestors.reduce((accRect, clippingAncestor) => {
    const rect = getClientRectFromClippingAncestor(element, clippingAncestor, strategy);
    accRect.top = max(rect.top, accRect.top);
    accRect.right = min(rect.right, accRect.right);
    accRect.bottom = min(rect.bottom, accRect.bottom);
    accRect.left = max(rect.left, accRect.left);
    return accRect;
  }, getClientRectFromClippingAncestor(element, firstClippingAncestor, strategy));
  return {
    width: clippingRect.right - clippingRect.left,
    height: clippingRect.bottom - clippingRect.top,
    x: clippingRect.left,
    y: clippingRect.top
  };
}

function getDimensions(element) {
  const {
    width,
    height
  } = getCssDimensions(element);
  return {
    width,
    height
  };
}

function getRectRelativeToOffsetParent(element, offsetParent, strategy) {
  const isOffsetParentAnElement = isHTMLElement(offsetParent);
  const documentElement = getDocumentElement(offsetParent);
  const isFixed = strategy === 'fixed';
  const rect = getBoundingClientRect(element, true, isFixed, offsetParent);
  let scroll = {
    scrollLeft: 0,
    scrollTop: 0
  };
  const offsets = createCoords(0);
  if (isOffsetParentAnElement || !isOffsetParentAnElement && !isFixed) {
    if (getNodeName(offsetParent) !== 'body' || isOverflowElement(documentElement)) {
      scroll = getNodeScroll(offsetParent);
    }
    if (isOffsetParentAnElement) {
      const offsetRect = getBoundingClientRect(offsetParent, true, isFixed, offsetParent);
      offsets.x = offsetRect.x + offsetParent.clientLeft;
      offsets.y = offsetRect.y + offsetParent.clientTop;
    } else if (documentElement) {
      offsets.x = getWindowScrollBarX(documentElement);
    }
  }
  const x = rect.left + scroll.scrollLeft - offsets.x;
  const y = rect.top + scroll.scrollTop - offsets.y;
  return {
    x,
    y,
    width: rect.width,
    height: rect.height
  };
}

function getTrueOffsetParent(element, polyfill) {
  if (!isHTMLElement(element) || getComputedStyle$1(element).position === 'fixed') {
    return null;
  }
  if (polyfill) {
    return polyfill(element);
  }
  return element.offsetParent;
}

// Gets the closest ancestor positioned element. Handles some edge cases,
// such as table ancestors and cross browser bugs.
function getOffsetParent(element, polyfill) {
  const window = getWindow(element);
  if (!isHTMLElement(element) || isTopLayer(element)) {
    return window;
  }
  let offsetParent = getTrueOffsetParent(element, polyfill);
  while (offsetParent && isTableElement(offsetParent) && getComputedStyle$1(offsetParent).position === 'static') {
    offsetParent = getTrueOffsetParent(offsetParent, polyfill);
  }
  if (offsetParent && (getNodeName(offsetParent) === 'html' || getNodeName(offsetParent) === 'body' && getComputedStyle$1(offsetParent).position === 'static' && !isContainingBlock(offsetParent))) {
    return window;
  }
  return offsetParent || getContainingBlock(element) || window;
}

const getElementRects = async function (data) {
  const getOffsetParentFn = this.getOffsetParent || getOffsetParent;
  const getDimensionsFn = this.getDimensions;
  return {
    reference: getRectRelativeToOffsetParent(data.reference, await getOffsetParentFn(data.floating), data.strategy),
    floating: {
      x: 0,
      y: 0,
      ...(await getDimensionsFn(data.floating))
    }
  };
};

function isRTL(element) {
  return getComputedStyle$1(element).direction === 'rtl';
}

const platform = {
  convertOffsetParentRelativeRectToViewportRelativeRect,
  getDocumentElement,
  getClippingRect,
  getOffsetParent,
  getElementRects,
  getClientRects,
  getDimensions,
  getScale,
  isElement,
  isRTL
};

/**
 * Computes the `x` and `y` coordinates that will place the floating element
 * next to a given reference element.
 */
const computePosition = (reference, floating, options) => {
  // This caches the expensive `getClippingElementAncestors` function so that
  // multiple lifecycle resets re-use the same result. It only lives for a
  // single call. If other functions become expensive, we can add them as well.
  const cache = new Map();
  const mergedOptions = {
    platform,
    ...options
  };
  const platformWithCache = {
    ...mergedOptions.platform,
    _c: cache
  };
  return computePosition$1(reference, floating, {
    ...mergedOptions,
    platform: platformWithCache
  });
};

var index$1 = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

// Fork of `fast-deep-equal` that only does the comparisons we need and compares
// functions
function deepEqual(a, b) {
  if (a === b) {
    return true;
  }
  if (typeof a !== typeof b) {
    return false;
  }
  if (typeof a === 'function' && a.toString() === b.toString()) {
    return true;
  }
  let length;
  let i;
  let keys;
  if (a && b && typeof a === 'object') {
    if (Array.isArray(a)) {
      length = a.length;
      if (length !== b.length) return false;
      for (i = length; i-- !== 0;) {
        if (!deepEqual(a[i], b[i])) {
          return false;
        }
      }
      return true;
    }
    keys = Object.keys(a);
    length = keys.length;
    if (length !== Object.keys(b).length) {
      return false;
    }
    for (i = length; i-- !== 0;) {
      if (!{}.hasOwnProperty.call(b, keys[i])) {
        return false;
      }
    }
    for (i = length; i-- !== 0;) {
      const key = keys[i];
      if (key === '_owner' && a.$$typeof) {
        continue;
      }
      if (!deepEqual(a[key], b[key])) {
        return false;
      }
    }
    return true;
  }

  // biome-ignore lint/suspicious/noSelfCompare: in source
  return a !== a && b !== b;
}

function getDPR(element) {
  if (typeof window === 'undefined') {
    return 1;
  }
  const win = element.ownerDocument.defaultView || window;
  return win.devicePixelRatio || 1;
}

function roundByDPR(element, value) {
  const dpr = getDPR(element);
  return Math.round(value * dpr) / dpr;
}

function useLatestRef(value) {
  const ref = React$1.useRef(value);
  index$1(() => {
    ref.current = value;
  });
  return ref;
}

/**
 * Provides data to position a floating element.
 * @see https://floating-ui.com/docs/useFloating
 */
function useFloating$1(options) {
  if (options === void 0) {
    options = {};
  }
  const {
    placement = 'bottom',
    strategy = 'absolute',
    middleware = [],
    platform,
    elements: {
      reference: externalReference,
      floating: externalFloating
    } = {},
    transform = true,
    whileElementsMounted,
    open
  } = options;
  const [data, setData] = React$1.useState({
    x: 0,
    y: 0,
    strategy,
    placement,
    middlewareData: {},
    isPositioned: false
  });
  const [latestMiddleware, setLatestMiddleware] = React$1.useState(middleware);
  if (!deepEqual(latestMiddleware, middleware)) {
    setLatestMiddleware(middleware);
  }
  const [_reference, _setReference] = React$1.useState(null);
  const [_floating, _setFloating] = React$1.useState(null);
  const setReference = React$1.useCallback(node => {
    if (node !== referenceRef.current) {
      referenceRef.current = node;
      _setReference(node);
    }
  }, []);
  const setFloating = React$1.useCallback(node => {
    if (node !== floatingRef.current) {
      floatingRef.current = node;
      _setFloating(node);
    }
  }, []);
  const referenceEl = externalReference || _reference;
  const floatingEl = externalFloating || _floating;
  const referenceRef = React$1.useRef(null);
  const floatingRef = React$1.useRef(null);
  const dataRef = React$1.useRef(data);
  const hasWhileElementsMounted = whileElementsMounted != null;
  const whileElementsMountedRef = useLatestRef(whileElementsMounted);
  const platformRef = useLatestRef(platform);
  const update = React$1.useCallback(() => {
    if (!referenceRef.current || !floatingRef.current) {
      return;
    }
    const config = {
      placement,
      strategy,
      middleware: latestMiddleware
    };
    if (platformRef.current) {
      config.platform = platformRef.current;
    }
    computePosition(referenceRef.current, floatingRef.current, config).then(data => {
      const fullData = {
        ...data,
        isPositioned: true
      };
      if (isMountedRef.current && !deepEqual(dataRef.current, fullData)) {
        dataRef.current = fullData;
        ReactDOM.flushSync(() => {
          setData(fullData);
        });
      }
    });
  }, [latestMiddleware, placement, strategy, platformRef]);
  index$1(() => {
    if (open === false && dataRef.current.isPositioned) {
      dataRef.current.isPositioned = false;
      setData(data => ({
        ...data,
        isPositioned: false
      }));
    }
  }, [open]);
  const isMountedRef = React$1.useRef(false);
  index$1(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `hasWhileElementsMounted` is intentionally included.
  index$1(() => {
    if (referenceEl) referenceRef.current = referenceEl;
    if (floatingEl) floatingRef.current = floatingEl;
    if (referenceEl && floatingEl) {
      if (whileElementsMountedRef.current) {
        return whileElementsMountedRef.current(referenceEl, floatingEl, update);
      }
      update();
    }
  }, [referenceEl, floatingEl, update, whileElementsMountedRef, hasWhileElementsMounted]);
  const refs = React$1.useMemo(() => ({
    reference: referenceRef,
    floating: floatingRef,
    setReference,
    setFloating
  }), [setReference, setFloating]);
  const elements = React$1.useMemo(() => ({
    reference: referenceEl,
    floating: floatingEl
  }), [referenceEl, floatingEl]);
  const floatingStyles = React$1.useMemo(() => {
    const initialStyles = {
      position: strategy,
      left: 0,
      top: 0
    };
    if (!elements.floating) {
      return initialStyles;
    }
    const x = roundByDPR(elements.floating, data.x);
    const y = roundByDPR(elements.floating, data.y);
    if (transform) {
      return {
        ...initialStyles,
        transform: "translate(" + x + "px, " + y + "px)",
        ...(getDPR(elements.floating) >= 1.5 && {
          willChange: 'transform'
        })
      };
    }
    return {
      position: strategy,
      left: x,
      top: y
    };
  }, [strategy, transform, elements.floating, data.x, data.y]);
  return React$1.useMemo(() => ({
    ...data,
    update,
    refs,
    elements,
    floatingStyles
  }), [data, update, refs, elements, floatingStyles]);
}

// https://github.com/mui/material-ui/issues/41190#issuecomment-2040873379
const SafeReact = {
  ...React$1
};

const useInsertionEffect = SafeReact.useInsertionEffect;
const useSafeInsertionEffect = useInsertionEffect || (fn => fn());
function useEffectEvent(callback) {
  const ref = React$1.useRef(() => {
  });
  useSafeInsertionEffect(() => {
    ref.current = callback;
  });
  return React$1.useCallback(function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    return ref.current == null ? void 0 : ref.current(...args);
  }, []);
}

var index = typeof document !== 'undefined' ? useLayoutEffect : useEffect;

let serverHandoffComplete = false;
let count = 0;
const genId = () => // Ensure the id is unique with multiple independent versions of Floating UI
// on <React 18
"floating-ui-" + Math.random().toString(36).slice(2, 6) + count++;
function useFloatingId() {
  const [id, setId] = React$1.useState(() => serverHandoffComplete ? genId() : undefined);
  index(() => {
    if (id == null) {
      setId(genId());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  React$1.useEffect(() => {
    serverHandoffComplete = true;
  }, []);
  return id;
}
const useReactId = SafeReact.useId;

/**
 * Uses React 18's built-in `useId()` when available, or falls back to a
 * slightly less performant (requiring a double render) implementation for
 * earlier React versions.
 * @see https://floating-ui.com/docs/react-utils#useid
 */
const useId = useReactId || useFloatingId;

function createPubSub() {
  const map = new Map();
  return {
    emit(event, data) {
      var _map$get;
      (_map$get = map.get(event)) == null || _map$get.forEach(handler => handler(data));
    },
    on(event, listener) {
      map.set(event, [...(map.get(event) || []), listener]);
    },
    off(event, listener) {
      var _map$get2;
      map.set(event, ((_map$get2 = map.get(event)) == null ? void 0 : _map$get2.filter(l => l !== listener)) || []);
    }
  };
}

const FloatingNodeContext = /*#__PURE__*/React$1.createContext(null);
const FloatingTreeContext = /*#__PURE__*/React$1.createContext(null);

/**
 * Returns the parent node id for nested floating elements, if available.
 * Returns `null` for top-level floating elements.
 */
const useFloatingParentNodeId = () => {
  var _React$useContext;
  return ((_React$useContext = React$1.useContext(FloatingNodeContext)) == null ? void 0 : _React$useContext.id) || null;
};

/**
 * Returns the nearest floating tree context, if available.
 */
const useFloatingTree = () => React$1.useContext(FloatingTreeContext);

function createAttribute(name) {
  return "data-floating-ui-" + name;
}

function getChildren(nodes, id) {
  let allChildren = nodes.filter(node => {
    var _node$context;
    return node.parentId === id && ((_node$context = node.context) == null ? void 0 : _node$context.open);
  });
  let currentChildren = allChildren;
  while (currentChildren.length) {
    currentChildren = nodes.filter(node => {
      var _currentChildren;
      return (_currentChildren = currentChildren) == null ? void 0 : _currentChildren.some(n => {
        var _node$context2;
        return node.parentId === n.id && ((_node$context2 = node.context) == null ? void 0 : _node$context2.open);
      });
    });
    allChildren = allChildren.concat(currentChildren);
  }
  return allChildren;
}

function isButtonTarget(event) {
  return isHTMLElement(event.target) && event.target.tagName === 'BUTTON';
}
function isSpaceIgnored(element) {
  return isTypeableElement(element);
}
/**
 * Opens or closes the floating element when clicking the reference element.
 * @see https://floating-ui.com/docs/useClick
 */
function useClick(context, props) {
  if (props === void 0) {
    props = {};
  }
  const {
    open,
    onOpenChange,
    dataRef,
    elements: {
      domReference
    }
  } = context;
  const {
    enabled = true,
    event: eventOption = 'click',
    toggle = true,
    ignoreMouse = false,
    keyboardHandlers = true
  } = props;
  const pointerTypeRef = React$1.useRef();
  const didKeyDownRef = React$1.useRef(false);
  return React$1.useMemo(() => {
    if (!enabled) return {};
    return {
      reference: {
        onPointerDown(event) {
          pointerTypeRef.current = event.pointerType;
        },
        onMouseDown(event) {
          // Ignore all buttons except for the "main" button.
          // https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
          if (event.button !== 0) {
            return;
          }
          if (isMouseLikePointerType(pointerTypeRef.current) && ignoreMouse) {
            return;
          }
          if (eventOption === 'click') {
            return;
          }
          if (open && toggle && (dataRef.current.openEvent ? dataRef.current.openEvent.type === 'mousedown' : true)) {
            onOpenChange(false, event.nativeEvent, 'click');
          } else {
            // Prevent stealing focus from the floating element
            event.preventDefault();
            onOpenChange(true, event.nativeEvent, 'click');
          }
        },
        onClick(event) {
          if (eventOption === 'mousedown' && pointerTypeRef.current) {
            pointerTypeRef.current = undefined;
            return;
          }
          if (isMouseLikePointerType(pointerTypeRef.current) && ignoreMouse) {
            return;
          }
          if (open && toggle && (dataRef.current.openEvent ? dataRef.current.openEvent.type === 'click' : true)) {
            onOpenChange(false, event.nativeEvent, 'click');
          } else {
            onOpenChange(true, event.nativeEvent, 'click');
          }
        },
        onKeyDown(event) {
          pointerTypeRef.current = undefined;
          if (event.defaultPrevented || !keyboardHandlers || isButtonTarget(event)) {
            return;
          }
          if (event.key === ' ' && !isSpaceIgnored(domReference)) {
            // Prevent scrolling
            event.preventDefault();
            didKeyDownRef.current = true;
          }
          if (event.key === 'Enter') {
            if (open && toggle) {
              onOpenChange(false, event.nativeEvent, 'click');
            } else {
              onOpenChange(true, event.nativeEvent, 'click');
            }
          }
        },
        onKeyUp(event) {
          if (event.defaultPrevented || !keyboardHandlers || isButtonTarget(event) || isSpaceIgnored(domReference)) {
            return;
          }
          if (event.key === ' ' && didKeyDownRef.current) {
            didKeyDownRef.current = false;
            if (open && toggle) {
              onOpenChange(false, event.nativeEvent, 'click');
            } else {
              onOpenChange(true, event.nativeEvent, 'click');
            }
          }
        }
      }
    };
  }, [enabled, dataRef, eventOption, ignoreMouse, keyboardHandlers, domReference, toggle, open, onOpenChange]);
}

const bubbleHandlerKeys = {
  pointerdown: 'onPointerDown',
  mousedown: 'onMouseDown',
  click: 'onClick'
};
const captureHandlerKeys = {
  pointerdown: 'onPointerDownCapture',
  mousedown: 'onMouseDownCapture',
  click: 'onClickCapture'
};
const normalizeProp = normalizable => {
  var _normalizable$escapeK, _normalizable$outside;
  return {
    escapeKey: typeof normalizable === 'boolean' ? normalizable : (_normalizable$escapeK = normalizable == null ? void 0 : normalizable.escapeKey) != null ? _normalizable$escapeK : false,
    outsidePress: typeof normalizable === 'boolean' ? normalizable : (_normalizable$outside = normalizable == null ? void 0 : normalizable.outsidePress) != null ? _normalizable$outside : true
  };
};
/**
 * Closes the floating element when a dismissal is requested — by default, when
 * the user presses the `escape` key or outside of the floating element.
 * @see https://floating-ui.com/docs/useDismiss
 */
function useDismiss(context, props) {
  if (props === void 0) {
    props = {};
  }
  const {
    open,
    onOpenChange,
    nodeId,
    elements: {
      reference,
      domReference,
      floating
    },
    dataRef
  } = context;
  const {
    enabled = true,
    escapeKey = true,
    outsidePress: unstable_outsidePress = true,
    outsidePressEvent = 'pointerdown',
    referencePress = false,
    referencePressEvent = 'pointerdown',
    ancestorScroll = false,
    bubbles,
    capture
  } = props;
  const tree = useFloatingTree();
  const outsidePressFn = useEffectEvent(typeof unstable_outsidePress === 'function' ? unstable_outsidePress : () => false);
  const outsidePress = typeof unstable_outsidePress === 'function' ? outsidePressFn : unstable_outsidePress;
  const insideReactTreeRef = React$1.useRef(false);
  const endedOrStartedInsideRef = React$1.useRef(false);
  const {
    escapeKey: escapeKeyBubbles,
    outsidePress: outsidePressBubbles
  } = normalizeProp(bubbles);
  const {
    escapeKey: escapeKeyCapture,
    outsidePress: outsidePressCapture
  } = normalizeProp(capture);
  const closeOnEscapeKeyDown = useEffectEvent(event => {
    if (!open || !enabled || !escapeKey || event.key !== 'Escape') {
      return;
    }
    const children = tree ? getChildren(tree.nodesRef.current, nodeId) : [];
    if (!escapeKeyBubbles) {
      event.stopPropagation();
      if (children.length > 0) {
        let shouldDismiss = true;
        children.forEach(child => {
          var _child$context;
          if ((_child$context = child.context) != null && _child$context.open && !child.context.dataRef.current.__escapeKeyBubbles) {
            shouldDismiss = false;
            return;
          }
        });
        if (!shouldDismiss) {
          return;
        }
      }
    }
    onOpenChange(false, isReactEvent(event) ? event.nativeEvent : event, 'escape-key');
  });
  const closeOnEscapeKeyDownCapture = useEffectEvent(event => {
    var _getTarget2;
    const callback = () => {
      var _getTarget;
      closeOnEscapeKeyDown(event);
      (_getTarget = getTarget(event)) == null || _getTarget.removeEventListener('keydown', callback);
    };
    (_getTarget2 = getTarget(event)) == null || _getTarget2.addEventListener('keydown', callback);
  });
  const closeOnPressOutside = useEffectEvent(event => {
    // Given developers can stop the propagation of the synthetic event,
    // we can only be confident with a positive value.
    const insideReactTree = insideReactTreeRef.current;
    insideReactTreeRef.current = false;

    // When click outside is lazy (`click` event), handle dragging.
    // Don't close if:
    // - The click started inside the floating element.
    // - The click ended inside the floating element.
    const endedOrStartedInside = endedOrStartedInsideRef.current;
    endedOrStartedInsideRef.current = false;
    if (outsidePressEvent === 'click' && endedOrStartedInside) {
      return;
    }
    if (insideReactTree) {
      return;
    }
    if (typeof outsidePress === 'function' && !outsidePress(event)) {
      return;
    }
    const target = getTarget(event);
    const inertSelector = "[" + createAttribute('inert') + "]";
    const markers = getDocument(floating).querySelectorAll(inertSelector);
    let targetRootAncestor = isElement(target) ? target : null;
    while (targetRootAncestor && !isLastTraversableNode(targetRootAncestor)) {
      const nextParent = getParentNode(targetRootAncestor);
      if (isLastTraversableNode(nextParent) || !isElement(nextParent)) {
        break;
      }
      targetRootAncestor = nextParent;
    }

    // Check if the click occurred on a third-party element injected after the
    // floating element rendered.
    if (markers.length && isElement(target) && !isRootElement(target) &&
    // Clicked on a direct ancestor (e.g. FloatingOverlay).
    !contains(target, floating) &&
    // If the target root element contains none of the markers, then the
    // element was injected after the floating element rendered.
    Array.from(markers).every(marker => !contains(targetRootAncestor, marker))) {
      return;
    }

    // Check if the click occurred on the scrollbar
    if (isHTMLElement(target) && floating) {
      // In Firefox, `target.scrollWidth > target.clientWidth` for inline
      // elements.
      const canScrollX = target.clientWidth > 0 && target.scrollWidth > target.clientWidth;
      const canScrollY = target.clientHeight > 0 && target.scrollHeight > target.clientHeight;
      let xCond = canScrollY && event.offsetX > target.clientWidth;

      // In some browsers it is possible to change the <body> (or window)
      // scrollbar to the left side, but is very rare and is difficult to
      // check for. Plus, for modal dialogs with backdrops, it is more
      // important that the backdrop is checked but not so much the window.
      if (canScrollY) {
        const isRTL = getComputedStyle$1(target).direction === 'rtl';
        if (isRTL) {
          xCond = event.offsetX <= target.offsetWidth - target.clientWidth;
        }
      }
      if (xCond || canScrollX && event.offsetY > target.clientHeight) {
        return;
      }
    }
    const targetIsInsideChildren = tree && getChildren(tree.nodesRef.current, nodeId).some(node => {
      var _node$context;
      return isEventTargetWithin(event, (_node$context = node.context) == null ? void 0 : _node$context.elements.floating);
    });
    if (isEventTargetWithin(event, floating) || isEventTargetWithin(event, domReference) || targetIsInsideChildren) {
      return;
    }
    const children = tree ? getChildren(tree.nodesRef.current, nodeId) : [];
    if (children.length > 0) {
      let shouldDismiss = true;
      children.forEach(child => {
        var _child$context2;
        if ((_child$context2 = child.context) != null && _child$context2.open && !child.context.dataRef.current.__outsidePressBubbles) {
          shouldDismiss = false;
          return;
        }
      });
      if (!shouldDismiss) {
        return;
      }
    }
    onOpenChange(false, event, 'outside-press');
  });
  const closeOnPressOutsideCapture = useEffectEvent(event => {
    var _getTarget4;
    const callback = () => {
      var _getTarget3;
      closeOnPressOutside(event);
      (_getTarget3 = getTarget(event)) == null || _getTarget3.removeEventListener(outsidePressEvent, callback);
    };
    (_getTarget4 = getTarget(event)) == null || _getTarget4.addEventListener(outsidePressEvent, callback);
  });
  React$1.useEffect(() => {
    if (!open || !enabled) {
      return;
    }
    dataRef.current.__escapeKeyBubbles = escapeKeyBubbles;
    dataRef.current.__outsidePressBubbles = outsidePressBubbles;
    function onScroll(event) {
      onOpenChange(false, event, 'ancestor-scroll');
    }
    const doc = getDocument(floating);
    escapeKey && doc.addEventListener('keydown', escapeKeyCapture ? closeOnEscapeKeyDownCapture : closeOnEscapeKeyDown, escapeKeyCapture);
    outsidePress && doc.addEventListener(outsidePressEvent, outsidePressCapture ? closeOnPressOutsideCapture : closeOnPressOutside, outsidePressCapture);
    let ancestors = [];
    if (ancestorScroll) {
      if (isElement(domReference)) {
        ancestors = getOverflowAncestors(domReference);
      }
      if (isElement(floating)) {
        ancestors = ancestors.concat(getOverflowAncestors(floating));
      }
      if (!isElement(reference) && reference && reference.contextElement) {
        ancestors = ancestors.concat(getOverflowAncestors(reference.contextElement));
      }
    }

    // Ignore the visual viewport for scrolling dismissal (allow pinch-zoom)
    ancestors = ancestors.filter(ancestor => {
      var _doc$defaultView;
      return ancestor !== ((_doc$defaultView = doc.defaultView) == null ? void 0 : _doc$defaultView.visualViewport);
    });
    ancestors.forEach(ancestor => {
      ancestor.addEventListener('scroll', onScroll, {
        passive: true
      });
    });
    return () => {
      escapeKey && doc.removeEventListener('keydown', escapeKeyCapture ? closeOnEscapeKeyDownCapture : closeOnEscapeKeyDown, escapeKeyCapture);
      outsidePress && doc.removeEventListener(outsidePressEvent, outsidePressCapture ? closeOnPressOutsideCapture : closeOnPressOutside, outsidePressCapture);
      ancestors.forEach(ancestor => {
        ancestor.removeEventListener('scroll', onScroll);
      });
    };
  }, [dataRef, floating, domReference, reference, escapeKey, outsidePress, outsidePressEvent, open, onOpenChange, ancestorScroll, enabled, escapeKeyBubbles, outsidePressBubbles, closeOnEscapeKeyDown, escapeKeyCapture, closeOnEscapeKeyDownCapture, closeOnPressOutside, outsidePressCapture, closeOnPressOutsideCapture]);
  React$1.useEffect(() => {
    insideReactTreeRef.current = false;
  }, [outsidePress, outsidePressEvent]);
  return React$1.useMemo(() => {
    if (!enabled) {
      return {};
    }
    return {
      reference: {
        onKeyDown: closeOnEscapeKeyDown,
        [bubbleHandlerKeys[referencePressEvent]]: event => {
          if (referencePress) {
            onOpenChange(false, event.nativeEvent, 'reference-press');
          }
        }
      },
      floating: {
        onKeyDown: closeOnEscapeKeyDown,
        onMouseDown() {
          endedOrStartedInsideRef.current = true;
        },
        onMouseUp() {
          endedOrStartedInsideRef.current = true;
        },
        [captureHandlerKeys[outsidePressEvent]]: () => {
          insideReactTreeRef.current = true;
        }
      }
    };
  }, [enabled, referencePress, outsidePressEvent, referencePressEvent, onOpenChange, closeOnEscapeKeyDown]);
}

/**
 * Provides data to position a floating element and context to add interactions.
 * @see https://floating-ui.com/docs/useFloating
 */
function useFloating(options) {
  var _options$elements;
  if (options === void 0) {
    options = {};
  }
  const {
    open = false,
    onOpenChange: unstable_onOpenChange,
    nodeId
  } = options;
  const [_domReference, setDomReference] = React$1.useState(null);
  const [positionReference, _setPositionReference] = React$1.useState(null);
  const optionDomReference = (_options$elements = options.elements) == null ? void 0 : _options$elements.reference;
  const domReference = optionDomReference || _domReference;
  index(() => {
    if (domReference) {
      domReferenceRef.current = domReference;
    }
  }, [domReference]);
  const position = useFloating$1({
    ...options,
    elements: {
      ...options.elements,
      ...(positionReference && {
        reference: positionReference
      })
    }
  });
  const tree = useFloatingTree();
  const nested = useFloatingParentNodeId() != null;
  const onOpenChange = useEffectEvent((open, event, reason) => {
    dataRef.current.openEvent = open ? event : undefined;
    events.emit('openchange', {
      open,
      event,
      reason,
      nested
    });
    unstable_onOpenChange == null || unstable_onOpenChange(open, event, reason);
  });
  const domReferenceRef = React$1.useRef(null);
  const dataRef = React$1.useRef({});
  const events = React$1.useState(() => createPubSub())[0];
  const floatingId = useId();
  const setPositionReference = React$1.useCallback(node => {
    const computedPositionReference = isElement(node) ? {
      getBoundingClientRect: () => node.getBoundingClientRect(),
      contextElement: node
    } : node;
    // Store the positionReference in state if the DOM reference is specified externally via the
    // `elements.reference` option. This ensures that it won't be overridden on future renders.
    _setPositionReference(computedPositionReference);
    position.refs.setReference(computedPositionReference);
  }, [position.refs]);
  const setReference = React$1.useCallback(node => {
    if (isElement(node) || node === null) {
      domReferenceRef.current = node;
      setDomReference(node);
    }

    // Backwards-compatibility for passing a virtual element to `reference`
    // after it has set the DOM reference.
    if (isElement(position.refs.reference.current) || position.refs.reference.current === null ||
    // Don't allow setting virtual elements using the old technique back to
    // `null` to support `positionReference` + an unstable `reference`
    // callback ref.
    node !== null && !isElement(node)) {
      position.refs.setReference(node);
    }
  }, [position.refs]);
  const refs = React$1.useMemo(() => ({
    ...position.refs,
    setReference,
    setPositionReference,
    domReference: domReferenceRef
  }), [position.refs, setReference, setPositionReference]);
  const elements = React$1.useMemo(() => ({
    ...position.elements,
    domReference: domReference
  }), [position.elements, domReference]);
  const context = React$1.useMemo(() => ({
    ...position,
    refs,
    elements,
    dataRef,
    nodeId,
    floatingId,
    events,
    open,
    onOpenChange
  }), [position, nodeId, floatingId, events, open, onOpenChange, refs, elements]);
  index(() => {
    const node = tree == null ? void 0 : tree.nodesRef.current.find(node => node.id === nodeId);
    if (node) {
      node.context = context;
    }
  });
  return React$1.useMemo(() => ({
    ...position,
    context,
    refs,
    elements
  }), [position, refs, elements, context]);
}

const ACTIVE_KEY = 'active';
const SELECTED_KEY = 'selected';
function mergeProps(userProps, propsList, elementKey) {
  const map = new Map();
  const isItem = elementKey === 'item';
  let domUserProps = userProps;
  if (isItem && userProps) {
    const {
      [ACTIVE_KEY]: _,
      [SELECTED_KEY]: __,
      ...validProps
    } = userProps;
    domUserProps = validProps;
  }
  return {
    ...(elementKey === 'floating' && {
      tabIndex: -1
    }),
    ...domUserProps,
    ...propsList.map(value => {
      const propsOrGetProps = value ? value[elementKey] : null;
      if (typeof propsOrGetProps === 'function') {
        return userProps ? propsOrGetProps(userProps) : null;
      }
      return propsOrGetProps;
    }).concat(userProps).reduce((acc, props) => {
      if (!props) {
        return acc;
      }
      Object.entries(props).forEach(_ref => {
        let [key, value] = _ref;
        if (isItem && [ACTIVE_KEY, SELECTED_KEY].includes(key)) {
          return;
        }
        if (key.indexOf('on') === 0) {
          if (!map.has(key)) {
            map.set(key, []);
          }
          if (typeof value === 'function') {
            var _map$get;
            (_map$get = map.get(key)) == null || _map$get.push(value);
            acc[key] = function () {
              var _map$get2;
              for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
                args[_key] = arguments[_key];
              }
              return (_map$get2 = map.get(key)) == null ? void 0 : _map$get2.map(fn => fn(...args)).find(val => val !== undefined);
            };
          }
        } else {
          acc[key] = value;
        }
      });
      return acc;
    }, {})
  };
}

/**
 * Merges an array of interaction hooks' props into prop getters, allowing
 * event handler functions to be composed together without overwriting one
 * another.
 * @see https://floating-ui.com/docs/useInteractions
 */
function useInteractions(propsList) {
  if (propsList === void 0) {
    propsList = [];
  }
  // The dependencies are a dynamic array, so we can't use the linter's
  // suggestion to add it to the deps array.
  const deps = propsList;
  const getReferenceProps = React$1.useCallback(userProps => mergeProps(userProps, propsList, 'reference'),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  deps);
  const getFloatingProps = React$1.useCallback(userProps => mergeProps(userProps, propsList, 'floating'),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  deps);
  const getItemProps = React$1.useCallback(userProps => mergeProps(userProps, propsList, 'item'),
  // Granularly check for `item` changes, because the `getItemProps` getter
  // should be as referentially stable as possible since it may be passed as
  // a prop to many components. All `item` key values must therefore be
  // memoized.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  propsList.map(key => key == null ? void 0 : key.item));
  return React$1.useMemo(() => ({
    getReferenceProps,
    getFloatingProps,
    getItemProps
  }), [getReferenceProps, getFloatingProps, getItemProps]);
}

/**
 * A hook that returns a UUID that lives for the life of the component.
 */
function useUUID() {
    const ref = useRef();
    if (ref.current === undefined) {
        ref.current = uuid(UUIDType.COMPONENT);
    }
    return ref.current;
}

/**
 * This renders a Carbon Menu component for the purposes of rendering in the chat header.
 */
function ChatHeaderMenu({ id, className, label, isOpen, target, children, menuAlignment, containerRef, }) {
    return (React__default.createElement(Menu, { id: id, className: className, open: isOpen, target: target, label: label, size: "md", menuAlignment: menuAlignment, mode: "full", legacyAutoalign: false, containerRef: containerRef }, children));
}

/**
 * This component renders a button that opens a menu when clicked. This button is specifically used in the chat header.
 */
/**
 * This component renders a button that opens a menu when clicked. This button is specifically used in the chat header.
 */
function ChatHeaderMenuButton(props) {
    const [isOpen, setIsOpen] = useState(false);
    useRef();
    // These hooks handle closing the menu when the user clicks outside of the menu. The native Carbon button menu
    // component uses the same library.
    const { refs: menuRefs, context: menuContext } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
    });
    const { getReferenceProps: getMenuReferenceProps, getFloatingProps: getMenuFloatingProps } = useInteractions([
        useClick(menuContext),
        useDismiss(menuContext),
    ]);
    const id = useUUID();
    return (React__default.createElement("div", { ref: menuRefs.setReference, "aria-owns": id, ...getMenuReferenceProps() },
        React__default.createElement(Button, { size: ButtonSizeEnum.MEDIUM, kind: ButtonKindEnum.GHOST, onClick: () => setIsOpen(!isOpen), renderIcon: isOpen ? ChevronUp : ChevronDown, onMouseDown: event => event.preventDefault(), "aria-label": props.ariaLabel, "aria-haspopup": true, "aria-expanded": isOpen, "aria-controls": id }, props.label),
        React__default.createElement(ChatHeaderMenu, { id: id, isOpen: isOpen, target: menuRefs.floating.current, label: props.label, containerRef: props.containerRef, menuAlignment: props.menuAlignment }, props.children),
        React__default.createElement("div", { ref: menuRefs.setFloating, className: "WAChatHeaderMenuButton_HostElement", ...getMenuFloatingProps() })));
}

/**
 * This component renders a menu item for the purposes of being rendered in the menu in the chat header.
 * See {@link ChatHeaderMenuButton}. The Carbon MenuItem component doesn't support anchor element attributes that
 * allow the item be a link, so the this behavior is handled by manually opening the provided url.
 */
function ChatHeaderMenuItem({ label, url, isNewTab = true, onClick, children }) {
    return (React__default.createElement(MenuItem, { label: label, onClick: () => {
            onClick?.();
            if (url) {
                window.open(url, isNewTab ? '_blank' : '_self');
            }
        } }, children));
}

/**
 * This component renders the Carbon MenuItemRadioGroup component specifically in the {@link ChatHeaderMenu} component.
 */
function ChatHeaderMenuItemRadioGroup(props) {
    const { label, items, defaultSelectedItem, selectedItem, onChange } = props;
    return (React__default.createElement(MenuItemRadioGroup, { label: label, items: items, defaultSelectedItem: defaultSelectedItem, selectedItem: selectedItem, onChange: (selectedItem) => {
            // The onChange callback gets fired twice. Check if the object returned does not contain an onFocus event
            // object method.
            if (!selectedItem.preventDefault) {
                onChange(selectedItem);
            }
        }, itemToString: (menuItem) => typeof menuItem === 'string' ? menuItem : menuItem.label || menuItem.value }));
}

function ChatHeaderOverflowMenu(props) {
    const [isOpen, setIsOpen] = useState(false);
    // These hooks handle closing the menu when the user clicks outside of the menu. The native Carbon overflow menu
    // component uses the same library.
    const { refs: menuRefs, context: menuContext } = useFloating({
        open: isOpen,
        onOpenChange: isOpen => {
            setIsOpen(isOpen);
            if (isOpen) {
                props.onOpen?.();
            }
            else {
                props.onClose?.();
            }
        },
    });
    const { getReferenceProps: getMenuReferenceProps, getFloatingProps: getMenuFloatingProps } = useInteractions([
        useClick(menuContext),
        useDismiss(menuContext),
    ]);
    const id = useUUID();
    return (React__default.createElement("div", { ref: menuRefs.setReference, "aria-owns": id, ...getMenuReferenceProps() },
        React__default.createElement(Button, { className: cx('WACChatHeaderOverflowMenu__Button', props.className), size: ButtonSizeEnum.MEDIUM, kind: ButtonKindEnum.GHOST, onClick: () => setIsOpen(!isOpen), tooltipPosition: props.tooltipPosition, renderIcon: props.renderIcon, onMouseDown: event => event.preventDefault(), iconDescription: props.iconDescription, hasIconOnly: true, "aria-label": props.ariaLabel, "aria-haspopup": true, "aria-expanded": isOpen, "aria-controls": id }),
        React__default.createElement(ChatHeaderMenu, { id: id, label: props.ariaLabel, isOpen: isOpen, target: menuRefs.floating.current, containerRef: props.containerRef, menuAlignment: props.menuAlignment }, props.children),
        React__default.createElement("div", { ref: menuRefs.setFloating, className: "WACChatHeaderOverflowMenu__HostElement", ...getMenuFloatingProps() })));
}

/**
 * This context provides access to the portal root element that acts as the host element for instances of
 * {@link ModalPortal}. Instances of that react component are mounted here in the DOM using React's portal
 * functionality.
 */
const ModalPortalRootContext = React__default.createContext(null);

// eslint-disable-next-line @typescript-eslint/ban-types
class ModalPortal extends Component {
    constructor() {
        super(...arguments);
        /**
         * Default state.
         */
        this.state = { attachedToHost: null };
        /**
         * The element that will contain this modal instance.
         */
        this.modalElement = document.createElement('div');
    }
    // Specify the context type and redefine the context property so it's got the right type.
    static { this.contextType = ModalPortalRootContext; }
    componentDidMount() {
        // Attach to the host if it's available right away.
        this.attachIfNeeded();
    }
    componentDidUpdate() {
        // Attach to the host later if it wasn't available when we mounted.
        this.attachIfNeeded();
    }
    componentWillUnmount() {
        if (this.state.attachedToHost) {
            this.state.attachedToHost.removeChild(this.modalElement);
            // this.attachedToHost = null;
        }
    }
    /**
     * This function will attach this component to the host element if needed and if the host element is available.
     */
    attachIfNeeded() {
        const hostElement = this.context;
        if (hostElement && !this.state.attachedToHost) {
            // The value in the context is the host element. When we see it come in the first time, we'll attach to it and
            // store it in state to make sure we re-render when that happens.
            this.setState({ attachedToHost: hostElement });
            hostElement.appendChild(this.modalElement);
        }
    }
    render() {
        if (!this.state.attachedToHost) {
            // Don't render anything until after we've gotten attached to the host.
            return null;
        }
        return ReactDOM__default.createPortal(this.props.children, this.modalElement);
    }
}

/**
 * This component acts as a wrapper around content that should be announced by a screen reader.
 */
function AriaLiveMessage(props) {
    const ariaAnnouncer = useContext(AriaAnnouncerContext);
    useEffect(() => {
        ariaAnnouncer(props.message);
    }, [ariaAnnouncer, props.message]);
    return React__default.createElement("div", null);
}
const AriaLiveMessageExport = React__default.memo(AriaLiveMessage);

class ConfirmModal extends Component {
    constructor() {
        super(...arguments);
        /**
         * The callback that is called when the user clicks the yes button confirming that they do want to end the chat.
         */
        this.onYesClick = () => {
            // End the chat and close the panel.
            this.props.onConfirm();
        };
        /**
         * The callback that is called when the user clicks the no button indicating they want to continue the chat.
         */
        this.onNoClick = () => {
            // Just close the panel.
            this.props.onCancel();
        };
        /**
         * A keyboard listener added to both buttons that will close the panel if the user presses escape.
         */
        this.onKeyDown = (event) => {
            if (event.key === 'Escape') {
                this.props.onCancel();
            }
        };
    }
    render() {
        const { title, message, cancelButtonLabel, confirmButtonLabel, modalAnnounceMessage, serviceManager } = this.props;
        return (React__default.createElement(ModalPortal, null,
            React__default.createElement(FocusTrap$1, null,
                React__default.createElement("div", { className: "WACConfirmModal", role: "dialog", "aria-labelledby": "WACConfirmModal__title", "aria-describedby": "WACConfirmModal__message" },
                    React__default.createElement("div", { className: "WACConfirmModal__container" },
                        React__default.createElement(AriaLiveMessageExport, { message: modalAnnounceMessage }),
                        React__default.createElement("div", { className: "WACConfirmModal__title", id: `WACConfirmModal__title${serviceManager.namespace.suffix}` }, title),
                        React__default.createElement("div", { className: "WACConfirmModal__message", id: `WACConfirmModal__message${serviceManager.namespace.suffix}` }, message),
                        React__default.createElement("div", { className: "WACConfirmModal__buttonContainer" },
                            React__default.createElement(Button, { className: "WACConfirmModal__NoButton", kind: "secondary", onClick: this.onNoClick, onKeyDown: this.onKeyDown, size: "md" }, cancelButtonLabel),
                            React__default.createElement(Button, { className: "WACConfirmModal__YesButton", onClick: this.onYesClick, onKeyDown: this.onKeyDown, size: "md" }, confirmButtonLabel)))))));
    }
}

const AISlug = o$1({
    tagName: 'cds-custom-ai-label',
    elementClass: CDSAILabel,
    react: React__default,
});

// The minimum width in pixels of the center gap, that separates the left and right header objects, until the number
// of objects that are allowed to be visible in the header is re-calculated.
const HEADER_OBJECTS_GAP_MIN_SIZE = 100;
/**
 * This displays the main header.
 */
function Header(props, ref) {
    const { displayName, backContent, showRestartButton, showBackButton, useAITheme, labelBackButton, onClickClose, onClickRestart, onCloseAndRestart, onClickBack, overflowItems, overflowClicked, backButtonType, showCenter, hideCloseButton, hideCloseAndRestartButton, brandColor = 'primary', enableChatHeaderConfig, headerAvatarConfig, } = props;
    const backButtonRef = useRef();
    const restartButtonRef = useRef();
    const closeAndRestartButtonRef = useRef();
    const closeButtonRef = useRef();
    const centerObjectsRef = useRef();
    const centerGapRef = useRef();
    const overflowRef = useRef();
    const serviceManager = useServiceManager();
    const languagePack = useLanguagePack();
    const publicConfig = useSelector((state) => state.config.public);
    const chatHeaderConfig = useSelector((state) => state.chatHeaderState.config);
    const chatWidthBreakpoint = useSelector((state) => state.chatWidthBreakpoint);
    const maxVisibleHeaderObjects = useSelector((state) => state.chatHeaderState.maxVisibleHeaderObjects);
    const [overflowIsOpen, setOverflowIsOpen] = useState(false);
    const [confirmModelOpen, setConfirmModelOpen] = useState(false);
    // The object map state that keeps track of the values selected in a radio group menus in the chat header.
    const [selectedGroupMenuitems, setSelectedGroupMenuItems] = useState({});
    const [isImageError, setIsImageError] = useState(false);
    const hasHeaderAvatar = Boolean(headerAvatarConfig) && !isImageError;
    const isHidden = useContext(HideComponentContext);
    const prevChatHeaderAvatarURL = usePrevious(headerAvatarConfig?.url);
    const { closePanelButtonConfig, headerConfig, __ibm__ } = publicConfig;
    const isWideWidth = chatWidthBreakpoint === ChatWidthBreakpoint.WIDE;
    // The title and name to display in the header from the chat header config.
    const chatHeaderTitle = enableChatHeaderConfig ? chatHeaderConfig?.headerTitle?.title : undefined;
    const chatHeaderName = enableChatHeaderConfig ? chatHeaderConfig?.headerTitle?.name : undefined;
    // The chat name to display in the chat header, the configured chat header name should take priority.
    const chatHeaderDisplayName = chatHeaderName || displayName;
    const closeConfirmModel = useCallback(() => {
        setConfirmModelOpen(false);
    }, []);
    const showCloseAndRestartButton = (publicConfig.showCloseAndRestartButton || headerConfig?.showCloseAndRestartButton) &&
        !hideCloseAndRestartButton &&
        onCloseAndRestart;
    const useHideCloseButton = publicConfig.hideCloseButton || headerConfig?.hideMinimizeButton || hideCloseButton;
    const useClosePanelIcon = Boolean(closePanelButtonConfig?.is_on);
    // The icon to use for the close button.
    let closeIcon;
    let closeReverseIcon = false;
    let closeIsReversible = true;
    const minimizeButtonIconType = headerConfig?.minimizeButtonIconType;
    switch (minimizeButtonIconType) {
        case MinimizeButtonIconType.CLOSE:
            closeIcon = React__default.createElement(CloseLarge, { className: "WACIcon__Close", size: 16 });
            break;
        case MinimizeButtonIconType.MINIMIZE:
            closeIcon = React__default.createElement(SubtractLarge, { className: "WACIcon__Subtract", size: 16 });
            break;
        case MinimizeButtonIconType.SIDE_PANEL_LEFT:
            closeIsReversible = false;
            closeIcon = React__default.createElement(SidePanelClose, { className: "WACIcon__SidePanelClose" });
            break;
        case MinimizeButtonIconType.SIDE_PANEL_RIGHT:
            closeIsReversible = false;
            closeReverseIcon = true;
            closeIcon = React__default.createElement(SidePanelClose, { className: "WACIcon__SidePanelClose" });
            break;
        default: {
            if (useClosePanelIcon) {
                closeReverseIcon = shouldReverseCloseIcon(closePanelButtonConfig);
                closeIcon = React__default.createElement(SidePanelClose, { className: "WACIcon__SidePanelClose" });
            }
            else if (__ibm__.useCloseIcon) {
                closeIcon = React__default.createElement(CloseLarge, { className: "WACIcon__Close", size: 16 });
            }
            else {
                closeIcon = React__default.createElement(SubtractLarge, { className: "WACIcon__Subtract", size: 16 });
            }
            break;
        }
    }
    if (showCloseAndRestartButton && showRestartButton) {
        throw new Error('You cannot enable both the restart button and the close-and-restart buttons.');
    }
    const onConfirm = useCallback(() => {
        setConfirmModelOpen(false);
        onCloseAndRestart();
    }, [onCloseAndRestart]);
    // Add a "requestFocus" imperative function to the ref so other components can trigger focus here.
    useImperativeHandle(ref, () => ({
        requestFocus: () => {
            if (closeButtonRef.current) {
                doFocusRef(closeButtonRef, false, true);
                return true;
            }
            if (backButtonRef.current) {
                doFocusRef(backButtonRef, false, true);
                return true;
            }
            if (restartButtonRef.current) {
                doFocusRef(restartButtonRef, false, true);
                return true;
            }
            return false;
        },
    }));
    /**
     * A function that takes the given menu item type and returns it's associated chat header menu item component.
     */
    function getComponentInMenu(item, index, list) {
        switch (item.type) {
            case ChatHeaderObjectType.LINK:
                return (React__default.createElement(ChatHeaderMenuItem
                // eslint-disable-next-line react/no-array-index-key
                , { 
                    // eslint-disable-next-line react/no-array-index-key
                    key: index, label: item.label, url: item.url, isNewTab: item.isNewTab, onClick: () => {
                        // Move focus back to the overflow menu button.
                        doFocusRef(overflowRef);
                    } }));
            case ChatHeaderObjectType.BUTTON:
                // eslint-disable-next-line react/no-array-index-key
                return (React__default.createElement(ChatHeaderMenuItem, { key: index, label: item.label, onClick: () => {
                        item.onClick?.();
                        // Move focus back to the overflow menu button.
                        doFocusRef(overflowRef);
                    } }));
            case ChatHeaderObjectType.RADIO_GROUP: {
                const nextObject = list[index + 1];
                return (
                // eslint-disable-next-line react/no-array-index-key
                React__default.createElement(Fragment, { key: index },
                    index !== 0 && React__default.createElement(MenuItemDivider, null),
                    React__default.createElement(ChatHeaderMenuItemRadioGroup, { label: item.label, items: item.items, defaultSelectedItem: item.defaultSelectedItem, selectedItem: selectedGroupMenuitems[item.label], onChange: selectedItem => {
                            setSelectedGroupMenuItems(prevState => ({
                                ...prevState,
                                [item.label]: selectedItem,
                            }));
                            item.onChange?.(selectedItem);
                        } }),
                    Boolean(nextObject) && nextObject.type !== ChatHeaderObjectType.RADIO_GROUP && React__default.createElement(MenuItemDivider, null)));
            }
            case ChatHeaderObjectType.MENU:
                return (React__default.createElement(ChatHeaderMenuItem
                // eslint-disable-next-line react/no-array-index-key
                , { 
                    // eslint-disable-next-line react/no-array-index-key
                    key: index, label: item.label }, item.items
                    // Filter out menu objects from submenus to prevent having more than 1 level of submenus.
                    .filter(menuItem => menuItem.type !== ChatHeaderObjectType.MENU)
                    .map(getComponentInMenu)));
            default:
                return null;
        }
    }
    /**
     * A function that takes the given chat header object and returns the associated component.
     */
    function getComponentInChatHeader(headerObject, index) {
        switch (headerObject.type) {
            case ChatHeaderObjectType.LINK:
                // eslint-disable-next-line react/no-array-index-key
                return (React__default.createElement(ChatHeaderLink, { key: index, label: headerObject.label, url: headerObject.url, isNewTab: headerObject.isNewTab }));
            case ChatHeaderObjectType.MENU:
                return (
                // eslint-disable-next-line react/no-array-index-key
                React__default.createElement(ChatHeaderMenuButton, { key: index, label: headerObject.label, menuAlignment: "bottom-start" }, headerObject.items.map(getComponentInMenu)));
            case ChatHeaderObjectType.BUTTON:
                return (React__default.createElement(Button
                // eslint-disable-next-line react/no-array-index-key
                , { 
                    // eslint-disable-next-line react/no-array-index-key
                    key: index, kind: ButtonKindEnum.GHOST, size: ButtonSizeEnum.MEDIUM, onClick: headerObject.onClick }, headerObject.label));
            default:
                return null;
        }
    }
    // Determine if there are chat header object that need to be displayed in the overflow menu.
    let leftObjectsLength = 0;
    let rightObjectsLength = 0;
    if (enableChatHeaderConfig) {
        leftObjectsLength = chatHeaderConfig?.left?.length ?? 0;
        rightObjectsLength = chatHeaderConfig?.right?.length ?? 0;
    }
    const isLeftObjectsInOverflow = leftObjectsLength > maxVisibleHeaderObjects;
    const isRightObjectsInOverflow = rightObjectsLength > maxVisibleHeaderObjects;
    const hasChatHeaderObjects = leftObjectsLength || rightObjectsLength;
    const renderChatHeaderObjectsInOverflow = hasChatHeaderObjects && (!isWideWidth || isLeftObjectsInOverflow || isRightObjectsInOverflow);
    const hasOverflow = Boolean(overflowItems?.length) || renderChatHeaderObjectsInOverflow;
    const isRTL = document.dir === 'rtl';
    // The list of component to render in the left chat header container.
    let leftChatHeaderComponents = null;
    // The list of component to render in the right chat header container.
    let rightChatHeaderComponents = null;
    // The list of components that don't fit in the left chat header container to render in the overflow menu.
    let leftChatHeaderMenuComponents = null;
    // The list of components that don't fit in the right chat header container to render in the overflow menu.
    let rightChatHeaderMenuComponents = null;
    if (enableChatHeaderConfig) {
        leftChatHeaderComponents = chatHeaderConfig?.left
            ?.slice(0, isLeftObjectsInOverflow ? maxVisibleHeaderObjects : undefined)
            .map(getComponentInChatHeader);
        rightChatHeaderComponents = chatHeaderConfig?.right
            ?.slice(0, isRightObjectsInOverflow ? maxVisibleHeaderObjects : undefined)
            .map(getComponentInChatHeader);
        leftChatHeaderMenuComponents =
            isLeftObjectsInOverflow && chatHeaderConfig?.left?.slice(maxVisibleHeaderObjects).map(getComponentInMenu);
        rightChatHeaderMenuComponents =
            isRightObjectsInOverflow && chatHeaderConfig?.right?.slice(maxVisibleHeaderObjects).map(getComponentInMenu);
    }
    let leftContent;
    if (hasOverflow) {
        // If there are overflow items, we need to show the overflow menu. This overrides any back button that may be
        // present.
        leftContent = (React__default.createElement(ChatHeaderOverflowMenu, { className: "WACHeader__OverflowMenu", renderIcon: overflowIsOpen ? Close : Menu$1, iconDescription: languagePack.header_overflowMenu_options, ariaLabel: languagePack.components_overflow_ariaLabel, containerRef: overflowRef, tooltipPosition: isRTL ? 'left' : 'right', menuAlignment: "bottom-start", onOpen: () => {
                // This requires a setTimeout because of an apparent bug in the Carbon components. If the icon changes
                // when you click on it, the component swaps in a new icon meaning the old icon is no longer part of the
                // DOM and the component detects this as a "clicked outside" which triggers the component to close. This will
                // delay the swapping of the icon until after component is open.
                setTimeout(() => {
                    setOverflowIsOpen(true);
                });
            }, onClose: () => {
                setOverflowIsOpen(false);
            } },
            overflowItems?.map((item, index) => (React__default.createElement(MenuItem, { key: item, label: item, onClick: () => {
                    // Move focus back to the overflow menu button.
                    doFocusRef(overflowRef);
                    overflowClicked(index);
                } }))),
            leftChatHeaderMenuComponents,
            rightChatHeaderMenuComponents));
    }
    else if (showBackButton) {
        // With no overflow items, just show the back button.
        leftContent = (React__default.createElement(HeaderButton, { className: "WACHeader__BackButton", label: labelBackButton, onClick: onClickBack, buttonRef: backButtonRef, buttonKind: backButtonType, tooltipPosition: isRTL ? 'left' : 'right' }, backContent || React__default.createElement(DownToBottom, null)));
    }
    // This effect sets a resize observer for the center objects container to set the max number of visible objects in
    // This helps determine how many elements can be in the chat header and in the overflow menu.
    useEffect(() => {
        if (!isWideWidth) {
            serviceManager.store.dispatch(actions.setMaxVisibleHeaderObjects(0));
            return undefined;
        }
        if (!enableChatHeaderConfig) {
            return undefined;
        }
        const centerResizeObserver = new ResizeObserver(() => {
            const centerGapElement = centerGapRef.current;
            if (centerGapElement) {
                if (centerGapElement.offsetWidth > HEADER_OBJECTS_GAP_MIN_SIZE || centerGapElement.offsetWidth === 0) {
                    // Determine the new max number of visible header objects by:
                    // * Subtracting minimum gap size from current center content width
                    //   - We subtract the gap size from the center content width so it's accounted for when determining max
                    //     number of visible header objects. There will always be a gap between the left and right objects.
                    // * Divide the minimum gap size, multiply by 2
                    //   - The number of header objects on both the left and right side should be even so we mutiply the
                    //     the assumed size of a chat header object (which can be arbitrary) by 2.
                    //
                    // Example: (500 - 100) / (200) = 2 objects on both the left and right side with 100px of space for the
                    //          center gap.
                    let newTotal = Math.floor((centerObjectsRef.current.offsetWidth - HEADER_OBJECTS_GAP_MIN_SIZE) / (HEADER_OBJECTS_GAP_MIN_SIZE * 2));
                    if (newTotal < 0) {
                        newTotal = 0;
                    }
                    setTimeout(() => {
                        serviceManager.store.dispatch(actions.setMaxVisibleHeaderObjects(newTotal));
                    });
                }
            }
        });
        centerResizeObserver.observe(centerObjectsRef.current);
        return () => {
            centerResizeObserver.disconnect();
        };
    }, [isWideWidth, enableChatHeaderConfig, serviceManager]);
    useEffect(() => {
        if (isImageError && prevChatHeaderAvatarURL !== headerAvatarConfig?.url) {
            setIsImageError(false);
        }
    }, [prevChatHeaderAvatarURL, headerAvatarConfig?.url, isImageError]);
    return (React__default.createElement("div", { className: cx('WACHeader', `WAC--${brandColor}Color`, { 'WACHeader--withAvatar': hasHeaderAvatar }) },
        React__default.createElement("div", { className: cx('WACHeader--content', `WAC--${brandColor}Color`), "data-floating-menu-container": true },
            leftContent && React__default.createElement("div", { className: "WACHeader__Buttons WACHeader__LeftButtons" }, leftContent),
            showCenter && (React__default.createElement("div", { className: "WACHeader__CenterContainer" },
                hasHeaderAvatar && (React__default.createElement(ChatHeaderAvatar, { url: headerAvatarConfig.url, corners: headerAvatarConfig.corners, alt: languagePack.header_ariaBotAvatar, onError: () => setIsImageError(true) })),
                (chatHeaderTitle || chatHeaderDisplayName) && (React__default.createElement("div", { className: "WACHeader__TitleContainer" },
                    React__default.createElement(ChatHeaderTitle, { title: chatHeaderTitle, name: chatHeaderDisplayName }))),
                isWideWidth && Boolean(leftObjectsLength) && React__default.createElement("div", { className: "WACHeader__Separator" }),
                React__default.createElement("div", { ref: centerObjectsRef, className: "WACHeader__HeaderObjects" },
                    isWideWidth && Boolean(leftObjectsLength) && (React__default.createElement("div", { className: "WACHeader__LeftItems" }, leftChatHeaderComponents)),
                    React__default.createElement("div", { ref: centerGapRef, className: "WACHeader__CenterGap" }),
                    isWideWidth && Boolean(rightObjectsLength) && (React__default.createElement("div", { className: "WACHeader__RightItems" }, rightChatHeaderComponents))))),
            React__default.createElement("div", { className: "WACHeader__Buttons WACHeader__RightButtons" },
                useAITheme && (React__default.createElement(AISlug, { className: "WACHeader__Slug", size: AI_LABEL_SIZE.EXTRA_SMALL, alignment: isRTL ? POPOVER_ALIGNMENT.BOTTOM_LEFT : POPOVER_ALIGNMENT.BOTTOM_RIGHT },
                    React__default.createElement("div", { slot: "body-text" },
                        React__default.createElement("h4", { className: "WACHeader__Slug-title" }, languagePack.ai_slug_title),
                        React__default.createElement("div", { className: "WACHeader__Slug-description" },
                            React__default.createElement("div", null, languagePack.ai_slug_description),
                            !isHidden && (React__default.createElement(WriteableElement$1, { slotName: WriteableElementName.AI_TOOLTIP_AFTER_DESCRIPTION_ELEMENT, id: `aiTooltipAfterDescription${serviceManager.namespace.suffix}`, element: serviceManager.writeableElements[WriteableElementName.AI_TOOLTIP_AFTER_DESCRIPTION_ELEMENT] })))))),
                showRestartButton && (React__default.createElement(HeaderButton, { className: "WACHeader__RestartButton", label: languagePack.buttons_restart, onClick: onClickRestart, buttonRef: restartButtonRef, tooltipPosition: isRTL ? 'right' : 'left' },
                    React__default.createElement(Restart, null))),
                !useHideCloseButton && (React__default.createElement(HeaderButton, { className: cx('WACHeader__CloseButton', {
                        WACReverseIcon: closeReverseIcon,
                    }), isReversible: closeIsReversible, label: languagePack.launcher_isOpen, onClick: async () => {
                        if (closePanelButtonConfig?.is_on) {
                            await serviceManager.eventBus.fire({ type: "closePanelButton:toggled" /* BusEventType.CLOSE_PANEL_BUTTON_TOGGLED */ }, serviceManager.instance);
                        }
                        else {
                            onClickClose();
                        }
                    }, buttonRef: closeButtonRef, tooltipPosition: isRTL ? 'right' : 'left' }, closeIcon)),
                showCloseAndRestartButton && (React__default.createElement(HeaderButton, { className: "WACHeader__CloseAndRestartButton", label: languagePack.header_ariaCloseRestart, onClick: () => setConfirmModelOpen(true), buttonRef: closeAndRestartButtonRef, tooltipPosition: isRTL ? 'right' : 'left' },
                    React__default.createElement(CloseLarge, { className: "WACIcon__Close" })))),
            confirmModelOpen && (React__default.createElement(ConfirmModal, { title: languagePack.closeAndRestartModal_title, message: languagePack.closeAndRestartModal_message, onConfirm: onConfirm, onCancel: closeConfirmModel, cancelButtonLabel: languagePack.closeAndRestartModal_cancel, confirmButtonLabel: languagePack.closeAndRestartModal_confirm, modalAnnounceMessage: languagePack.closeAndRestartModal_message, serviceManager: serviceManager })))));
}
/**
 * This component is a button that appears in the header.
 */
function HeaderButton({ onClick, buttonRef, label, className, children, buttonKind, isReversible = true, tooltipPosition, }) {
    return (React__default.createElement(Button, { ref: buttonRef, className: cx(className, { WACDirectionHasReversibleSVG: isReversible }), onClick: onClick, hasIconOnly: true, iconDescription: label, size: ButtonSizeEnum.MEDIUM, kind: buttonKind || ButtonKindEnum.GHOST, tooltipPosition: tooltipPosition }, children));
}
/**
 * Determines if close button icon should be reversed.
 */
function shouldReverseCloseIcon(closePanelButtonConfig) {
    const isRTL = document.dir === 'rtl';
    if (!closePanelButtonConfig?.is_on) {
        return false;
    }
    // Reverse the icon automatically to match the page direction.
    if (!closePanelButtonConfig.closeIconDirection) {
        return true;
    }
    if (isRTL) {
        return closePanelButtonConfig.closeIconDirection === ClosePanelIconDirection.LEFT;
    }
    return closePanelButtonConfig.closeIconDirection === ClosePanelIconDirection.RIGHT;
}
const HeaderExport = React__default.memo(forwardRef(Header));

function BotHeader(props, ref) {
    const { onClose, onCloseAndRestart, onRestart, onToggleHomeScreen, headerDisplayName, includeWriteableElement, enableChatHeaderConfig, headerAvatarConfig, } = props;
    const serviceManager = useServiceManager();
    const languagePack = useLanguagePack();
    const homeScreenIsOn = useSelector((state) => state.homeScreenConfig.is_on && state.homeScreenConfig.allow_return);
    const publicConfig = useSelector((state) => state.config.public);
    const customMenuOptions = useSelector((state) => state.customMenuOptions);
    const { isConnectingOrConnected } = useSelector(selectAgentDisplayState);
    const useAITheme = useSelector((state) => state.theme.useAITheme);
    const maxVisibleHeaderObjects = useSelector((state) => state.chatHeaderState.maxVisibleHeaderObjects);
    const leftObjectsLength = useSelector((state) => state.chatHeaderState.config?.left?.length || 0);
    const rightObjectsLength = useSelector((state) => state.chatHeaderState.config?.right?.length || 0);
    const headerRef = useRef();
    const isHeaderObjectsInOverflow = leftObjectsLength > maxVisibleHeaderObjects || rightObjectsLength > maxVisibleHeaderObjects;
    const hasChatHeaderObjects = enableChatHeaderConfig && isHeaderObjectsInOverflow;
    const showRestartButton = publicConfig.showRestartButton || publicConfig.headerConfig?.showRestartButton;
    // We can't allow the user to return to the home screen if the user is connecting or connected to an agent.
    const allowHomeScreen = homeScreenIsOn && !isConnectingOrConnected;
    const overflowClicked = useCallback((index) => {
        if (index === 0 && allowHomeScreen) {
            onToggleHomeScreen?.();
        }
        else {
            const { handler } = customMenuOptions[allowHomeScreen ? index - 1 : index];
            handler();
        }
    }, [customMenuOptions, onToggleHomeScreen, allowHomeScreen]);
    let overflowItems = customMenuOptions?.map(option => option.text);
    if (overflowItems && allowHomeScreen) {
        // Insert a "Home screen" option at the top.
        overflowItems.splice(0, 0, languagePack.homeScreen_overflowMenuHomeScreen);
    }
    else if (!overflowItems && allowHomeScreen && hasChatHeaderObjects) {
        // If there are header objects in the overflow menu, insert the "Home screen" option.
        overflowItems = [languagePack.homeScreen_overflowMenuHomeScreen];
    }
    // Reuse the imperative handles from the header.
    useImperativeHandle(ref, () => headerRef.current);
    return (React__default.createElement("div", { className: "WACHeader__Container" },
        React__default.createElement(HeaderExport, { ref: headerRef, showCenter: true, headerAvatarConfig: headerAvatarConfig, displayName: headerDisplayName, showBackButton: Boolean(allowHomeScreen && onToggleHomeScreen), showRestartButton: showRestartButton, useAITheme: useAITheme, backContent: React__default.createElement(Home, null), labelBackButton: languagePack.homeScreen_returnToHome, onClickRestart: onRestart, onClickClose: onClose, onCloseAndRestart: onCloseAndRestart, onClickBack: onToggleHomeScreen, overflowItems: overflowItems, overflowClicked: overflowClicked, enableChatHeaderConfig: enableChatHeaderConfig }),
        includeWriteableElement && (React__default.createElement(WriteableElement$1, { slotName: WriteableElementName.HEADER_BOTTOM_ELEMENT, id: `headerBottomElement${serviceManager.namespace.suffix}`, className: "WACHeader__HeaderBottomElement", element: serviceManager.writeableElements[WriteableElementName.HEADER_BOTTOM_ELEMENT] }))));
}
const BotHeaderExport = React__default.memo(forwardRef(BotHeader));

/**
 * This component is rendered while the web chat is hydrating.
 */
function CatastrophicError({ serviceManager, onClose, languagePack, onRestart, showHeader, botName, headerDisplayName, catastrophicErrorType, }) {
    const intl = useIntl();
    const carbonTheme = useSelector((state) => state.theme.carbonTheme);
    const isDarkTheme = carbonTheme === CarbonTheme.G90 || carbonTheme === CarbonTheme.G100;
    let errorKey;
    switch (catastrophicErrorType) {
        case CatastrophicErrorType.AGENT_APP_EXPIRED_SESSION:
            errorKey = 'errors_agentAppSessionExpired';
            break;
        case CatastrophicErrorType.THROTTLED:
            errorKey = 'errors_busy';
            break;
        default:
            errorKey = 'errors_communicating';
    }
    const errorBodyText = intl.formatMessage({ id: errorKey }, { botName });
    return (React__default.createElement("div", { className: "WAC" },
        showHeader && (React__default.createElement(BotHeaderExport, { headerDisplayName: headerDisplayName, onClose: onClose, onToggleHomeScreen: null, includeWriteableElement: false })),
        React__default.createElement("div", { className: cx('WACCatastrophicError', 'WACPanelContent', { 'WACCatastrophicError--withHeader': showHeader }) },
            React__default.createElement("div", { className: "WACCatastrophicError__ErrorTextContainer" },
                isDarkTheme && React__default.createElement(ErrorMessageDark, null),
                !isDarkTheme && React__default.createElement(ErrorMessageLight, null),
                React__default.createElement("div", { className: "WACCatastrophicError__ErrorHeading" }, languagePack.errors_somethingWrong),
                React__default.createElement("div", { className: "WACCatastrophicError__ErrorBody" }, errorBodyText),
                catastrophicErrorType !== CatastrophicErrorType.AGENT_APP_EXPIRED_SESSION && onRestart && (React__default.createElement(unstable__ChatButton, { className: "WACCatastrophicError__RestartButton", kind: ButtonKindEnum.TERTIARY, size: ButtonSizeEnum.SMALL, id: `WAC__error-reset${serviceManager.namespace.suffix}`, "aria-label": languagePack.buttons_restart, onClick: onRestart, renderIcon: Restart, type: "button" }, languagePack.buttons_retry))))));
}
const CatastrophicErrorExport = React__default.memo(CatastrophicError);

/**
 * Gets the timestamp of the number of milliseconds that have elapsed since
 * the Unix epoch (1 January 1970 00:00:00 UTC).
 *
 * @static
 * @memberOf _
 * @since 2.4.0
 * @category Date
 * @returns {number} Returns the timestamp.
 * @example
 *
 * _.defer(function(stamp) {
 *   console.log(_.now() - stamp);
 * }, _.now());
 * // => Logs the number of milliseconds it took for the deferred invocation.
 */
var now = function() {
  return root.Date.now();
};

/** Used to match a single whitespace character. */
var reWhitespace = /\s/;

/**
 * Used by `_.trim` and `_.trimEnd` to get the index of the last non-whitespace
 * character of `string`.
 *
 * @private
 * @param {string} string The string to inspect.
 * @returns {number} Returns the index of the last non-whitespace character.
 */
function trimmedEndIndex(string) {
  var index = string.length;

  while (index-- && reWhitespace.test(string.charAt(index))) {}
  return index;
}

/** Used to match leading whitespace. */
var reTrimStart = /^\s+/;

/**
 * The base implementation of `_.trim`.
 *
 * @private
 * @param {string} string The string to trim.
 * @returns {string} Returns the trimmed string.
 */
function baseTrim(string) {
  return string
    ? string.slice(0, trimmedEndIndex(string) + 1).replace(reTrimStart, '')
    : string;
}

/** Used as references for various `Number` constants. */
var NAN = 0 / 0;

/** Used to detect bad signed hexadecimal string values. */
var reIsBadHex = /^[-+]0x[0-9a-f]+$/i;

/** Used to detect binary string values. */
var reIsBinary = /^0b[01]+$/i;

/** Used to detect octal string values. */
var reIsOctal = /^0o[0-7]+$/i;

/** Built-in method references without a dependency on `root`. */
var freeParseInt = parseInt;

/**
 * Converts `value` to a number.
 *
 * @static
 * @memberOf _
 * @since 4.0.0
 * @category Lang
 * @param {*} value The value to process.
 * @returns {number} Returns the number.
 * @example
 *
 * _.toNumber(3.2);
 * // => 3.2
 *
 * _.toNumber(Number.MIN_VALUE);
 * // => 5e-324
 *
 * _.toNumber(Infinity);
 * // => Infinity
 *
 * _.toNumber('3.2');
 * // => 3.2
 */
function toNumber(value) {
  if (typeof value == 'number') {
    return value;
  }
  if (isSymbol$1(value)) {
    return NAN;
  }
  if (isObject$1(value)) {
    var other = typeof value.valueOf == 'function' ? value.valueOf() : value;
    value = isObject$1(other) ? (other + '') : other;
  }
  if (typeof value != 'string') {
    return value === 0 ? value : +value;
  }
  value = baseTrim(value);
  var isBinary = reIsBinary.test(value);
  return (isBinary || reIsOctal.test(value))
    ? freeParseInt(value.slice(2), isBinary ? 2 : 8)
    : (reIsBadHex.test(value) ? NAN : +value);
}

/** Error message constants. */
var FUNC_ERROR_TEXT$1 = 'Expected a function';

/* Built-in method references for those with the same name as other `lodash` methods. */
var nativeMax = Math.max,
    nativeMin = Math.min;

/**
 * Creates a debounced function that delays invoking `func` until after `wait`
 * milliseconds have elapsed since the last time the debounced function was
 * invoked. The debounced function comes with a `cancel` method to cancel
 * delayed `func` invocations and a `flush` method to immediately invoke them.
 * Provide `options` to indicate whether `func` should be invoked on the
 * leading and/or trailing edge of the `wait` timeout. The `func` is invoked
 * with the last arguments provided to the debounced function. Subsequent
 * calls to the debounced function return the result of the last `func`
 * invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the debounced function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.debounce` and `_.throttle`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to debounce.
 * @param {number} [wait=0] The number of milliseconds to delay.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=false]
 *  Specify invoking on the leading edge of the timeout.
 * @param {number} [options.maxWait]
 *  The maximum time `func` is allowed to be delayed before it's invoked.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new debounced function.
 * @example
 *
 * // Avoid costly calculations while the window size is in flux.
 * jQuery(window).on('resize', _.debounce(calculateLayout, 150));
 *
 * // Invoke `sendMail` when clicked, debouncing subsequent calls.
 * jQuery(element).on('click', _.debounce(sendMail, 300, {
 *   'leading': true,
 *   'trailing': false
 * }));
 *
 * // Ensure `batchLog` is invoked once after 1 second of debounced calls.
 * var debounced = _.debounce(batchLog, 250, { 'maxWait': 1000 });
 * var source = new EventSource('/stream');
 * jQuery(source).on('message', debounced);
 *
 * // Cancel the trailing debounced invocation.
 * jQuery(window).on('popstate', debounced.cancel);
 */
function debounce(func, wait, options) {
  var lastArgs,
      lastThis,
      maxWait,
      result,
      timerId,
      lastCallTime,
      lastInvokeTime = 0,
      leading = false,
      maxing = false,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT$1);
  }
  wait = toNumber(wait) || 0;
  if (isObject$1(options)) {
    leading = !!options.leading;
    maxing = 'maxWait' in options;
    maxWait = maxing ? nativeMax(toNumber(options.maxWait) || 0, wait) : maxWait;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }

  function invokeFunc(time) {
    var args = lastArgs,
        thisArg = lastThis;

    lastArgs = lastThis = undefined;
    lastInvokeTime = time;
    result = func.apply(thisArg, args);
    return result;
  }

  function leadingEdge(time) {
    // Reset any `maxWait` timer.
    lastInvokeTime = time;
    // Start the timer for the trailing edge.
    timerId = setTimeout(timerExpired, wait);
    // Invoke the leading edge.
    return leading ? invokeFunc(time) : result;
  }

  function remainingWait(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime,
        timeWaiting = wait - timeSinceLastCall;

    return maxing
      ? nativeMin(timeWaiting, maxWait - timeSinceLastInvoke)
      : timeWaiting;
  }

  function shouldInvoke(time) {
    var timeSinceLastCall = time - lastCallTime,
        timeSinceLastInvoke = time - lastInvokeTime;

    // Either this is the first call, activity has stopped and we're at the
    // trailing edge, the system time has gone backwards and we're treating
    // it as the trailing edge, or we've hit the `maxWait` limit.
    return (lastCallTime === undefined || (timeSinceLastCall >= wait) ||
      (timeSinceLastCall < 0) || (maxing && timeSinceLastInvoke >= maxWait));
  }

  function timerExpired() {
    var time = now();
    if (shouldInvoke(time)) {
      return trailingEdge(time);
    }
    // Restart the timer.
    timerId = setTimeout(timerExpired, remainingWait(time));
  }

  function trailingEdge(time) {
    timerId = undefined;

    // Only invoke if we have `lastArgs` which means `func` has been
    // debounced at least once.
    if (trailing && lastArgs) {
      return invokeFunc(time);
    }
    lastArgs = lastThis = undefined;
    return result;
  }

  function cancel() {
    if (timerId !== undefined) {
      clearTimeout(timerId);
    }
    lastInvokeTime = 0;
    lastArgs = lastCallTime = lastThis = timerId = undefined;
  }

  function flush() {
    return timerId === undefined ? result : trailingEdge(now());
  }

  function debounced() {
    var time = now(),
        isInvoking = shouldInvoke(time);

    lastArgs = arguments;
    lastThis = this;
    lastCallTime = time;

    if (isInvoking) {
      if (timerId === undefined) {
        return leadingEdge(lastCallTime);
      }
      if (maxing) {
        // Handle invocations in a tight loop.
        clearTimeout(timerId);
        timerId = setTimeout(timerExpired, wait);
        return invokeFunc(lastCallTime);
      }
    }
    if (timerId === undefined) {
      timerId = setTimeout(timerExpired, wait);
    }
    return result;
  }
  debounced.cancel = cancel;
  debounced.flush = flush;
  return debounced;
}

/** Error message constants. */
var FUNC_ERROR_TEXT = 'Expected a function';

/**
 * Creates a throttled function that only invokes `func` at most once per
 * every `wait` milliseconds. The throttled function comes with a `cancel`
 * method to cancel delayed `func` invocations and a `flush` method to
 * immediately invoke them. Provide `options` to indicate whether `func`
 * should be invoked on the leading and/or trailing edge of the `wait`
 * timeout. The `func` is invoked with the last arguments provided to the
 * throttled function. Subsequent calls to the throttled function return the
 * result of the last `func` invocation.
 *
 * **Note:** If `leading` and `trailing` options are `true`, `func` is
 * invoked on the trailing edge of the timeout only if the throttled function
 * is invoked more than once during the `wait` timeout.
 *
 * If `wait` is `0` and `leading` is `false`, `func` invocation is deferred
 * until to the next tick, similar to `setTimeout` with a timeout of `0`.
 *
 * See [David Corbacho's article](https://css-tricks.com/debouncing-throttling-explained-examples/)
 * for details over the differences between `_.throttle` and `_.debounce`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @category Function
 * @param {Function} func The function to throttle.
 * @param {number} [wait=0] The number of milliseconds to throttle invocations to.
 * @param {Object} [options={}] The options object.
 * @param {boolean} [options.leading=true]
 *  Specify invoking on the leading edge of the timeout.
 * @param {boolean} [options.trailing=true]
 *  Specify invoking on the trailing edge of the timeout.
 * @returns {Function} Returns the new throttled function.
 * @example
 *
 * // Avoid excessively updating the position while scrolling.
 * jQuery(window).on('scroll', _.throttle(updatePosition, 100));
 *
 * // Invoke `renewToken` when the click event is fired, but not more than once every 5 minutes.
 * var throttled = _.throttle(renewToken, 300000, { 'trailing': false });
 * jQuery(element).on('click', throttled);
 *
 * // Cancel the trailing throttled invocation.
 * jQuery(window).on('popstate', throttled.cancel);
 */
function throttle(func, wait, options) {
  var leading = true,
      trailing = true;

  if (typeof func != 'function') {
    throw new TypeError(FUNC_ERROR_TEXT);
  }
  if (isObject$1(options)) {
    leading = 'leading' in options ? !!options.leading : leading;
    trailing = 'trailing' in options ? !!options.trailing : trailing;
  }
  return debounce(func, wait, {
    'leading': leading,
    'maxWait': wait,
    'trailing': trailing
  });
}

var css_248z$5 = ".dots{width:32px;height:32px}.dot{fill:none;r:0;stroke-width:0;transform:translateY(0)}[data-carbon-theme=g10] .dot,[data-carbon-theme=white] .dot{stroke:#001d6c}[data-carbon-theme=g100] .dot,[data-carbon-theme=g90] .dot{stroke:#f4f4f4}.linear .dot--left{transform-origin:25% 50%;animation:linear-load-size,linear-load-stroke,linear-loop-size,linear-loop-stroke;animation-duration:1s;animation-iteration-count:1,1,infinite,infinite;animation-fill-mode:forwards;animation-delay:1s,1s,2s,2s}.linear .dot--center{transform-origin:50% 50%;animation:linear-load-size,linear-load-stroke,linear-loop-size,linear-loop-stroke;animation-duration:1s;animation-iteration-count:1,1,infinite,infinite;animation-fill-mode:forwards;animation-delay:1.167s,1.167s,2.167s,2.167s}.linear .dot--right{transform-origin:75% 50%;animation:linear-load-size,linear-load-stroke,linear-loop-size,linear-loop-stroke;animation-duration:1s;animation-iteration-count:1,1,infinite,infinite;animation-fill-mode:forwards;animation-delay:1.334s,1.334s,2.334s,2.334s}[dir=rtl] .linear .dot--left{animation-delay:1.334s,1.334s,2.334s,2.334s,7.334s,7.334s}[dir=rtl] .linear .dot--center{animation-delay:1.167s,1.167s,2.167s,2.167s,7.167s,7.167s}[dir=rtl] .linear .dot--right{animation-delay:1s,1s,2s,2s,7s,7s}.linear--no-loop .dot--left{transform-origin:25% 50%;animation:linear-load-size,linear-load-stroke,linear-unload-size,linear-unload-stroke;animation-duration:1s;animation-iteration-count:1;animation-fill-mode:forwards;animation-delay:1s,1s,2s,2s}.linear--no-loop .dot--center{transform-origin:50% 50%;animation:linear-load-size,linear-load-stroke,linear-unload-size,linear-unload-stroke;animation-duration:1s;animation-iteration-count:1;animation-fill-mode:forwards;animation-delay:1.167s,1.167s,2.167s,2.167s}.linear--no-loop .dot--right{transform-origin:75% 50%;animation:linear-load-size,linear-load-stroke,linear-unload-size,linear-unload-stroke;animation-duration:1s;animation-iteration-count:1;animation-fill-mode:forwards}.linear--no-loop .dot--right,[dir=rtl] .linear--no-loop .dot--left{animation-delay:1.334s,1.334s,2.334s,2.334s}[dir=rtl] .linear--no-loop .dot--center{animation-delay:1.167s,1.167s,2.167s,2.167s}[dir=rtl] .linear--no-loop .dot--right{animation-delay:1s,1s,2s,2s}@keyframes linear-load-size{0%{r:0;animation-timing-function:cubic-bezier(0,0,.3,1)}25%{r:2.5px;animation-timing-function:cubic-bezier(0,0,.3,1)}83.3%{r:.875px}to{r:.875px}}@keyframes linear-load-stroke{0%{stroke-width:0;animation-timing-function:cubic-bezier(0,0,.3,1)}8.33%{stroke-width:1.72}to{stroke-width:1.72}}@keyframes linear-loop-size{0%{r:.875px;animation-timing-function:cubic-bezier(0,0,.3,1)}25%{r:2.5px;animation-timing-function:cubic-bezier(0,0,.3,1)}91.66%{r:.875px}to{r:.875px}}@keyframes linear-loop-stroke{0%{stroke-width:1.72;animation-timing-function:cubic-bezier(.4,.14,1,1)}to{stroke-width:1.72}}@keyframes linear-unload-size{0%{r:.875px}8.33%{r:.875px}33.33%{r:2.5px;animation-timing-function:cubic-bezier(.4,.14,1,1)}58.33%{r:0}to{r:0}}@keyframes linear-unload-stroke{0%{stroke-width:1.72}50%{stroke-width:1.72}58.33%{stroke-width:0}to{stroke-width:0}}@media (prefers-reduced-motion:reduce){.dot--center,.dot--left,.dot--right{transition:none;animation:none}}";

const INLINE_LOADING_TAG_NAME = `${WEB_COMPONENT_PREFIX}-inline-loading`;
let CDSInlineLoadingElement = class CDSInlineLoadingElement extends LitElement {
    constructor() {
        super(...arguments);
        this.bounce = false;
        this.loop = false;
        this.quickLoad = false;
        this.carbonTheme = 'g10';
    }
    static { this.styles = css `
    ${unsafeCSS(css_248z$5)}
  `; }
    getAnimationEffect() {
        const classNames = [];
        if (this.quickLoad === true) {
            classNames.push('quick-load');
        }
        if (this.bounce === true && this.loop === true) {
            classNames.push('vertical');
        }
        if (this.bounce === false && this.loop === true) {
            classNames.push('linear');
        }
        if (this.bounce === true && this.loop === false) {
            classNames.push('vertical--no-loop');
        }
        if (classNames.length) {
            return classNames.join(' ');
        }
        return 'linear--no-loop';
    }
    render() {
        return html$2 `<div data-carbon-theme=${this.carbonTheme} class=${this.getAnimationEffect()}>
      <svg class="dots" viewBox="0 0 32 32">
        <circle class="dot dot--left" cx="8" cy="16" />
        <circle class="dot dot--center" cx="16" cy="16" r="2" />
        <circle class="dot dot--right" cx="24" cy="16" r="2" />
      </svg>
    </div>`;
    }
};
__decorate([
    property({ type: Boolean })
], CDSInlineLoadingElement.prototype, "bounce", void 0);
__decorate([
    property({ type: Boolean })
], CDSInlineLoadingElement.prototype, "loop", void 0);
__decorate([
    property({ type: Boolean })
], CDSInlineLoadingElement.prototype, "quickLoad", void 0);
__decorate([
    property({ type: String })
], CDSInlineLoadingElement.prototype, "carbonTheme", void 0);
CDSInlineLoadingElement = __decorate([
    carbonElement(INLINE_LOADING_TAG_NAME)
], CDSInlineLoadingElement);
var CDSInlineLoadingElement$1 = CDSInlineLoadingElement;

const InlineLoadingComponent = o$1({
    tagName: INLINE_LOADING_TAG_NAME,
    elementClass: CDSInlineLoadingElement$1,
    react: React__default,
});

function AgentAvatar(props) {
    const { agentProfile, languagePack, width, height } = props;
    const agentName = agentProfile?.nickname;
    const avatarStyle = { width, height };
    const avatarUrl = agentProfile?.profile_picture_url;
    // Indicates if the avatar for a human agent failed to load.
    const [hasError, setHasError] = useState(false);
    let component;
    // If the avatar Url changes, then hasError should reset to allow an attempt at loading the new avatar url.
    useEffect(() => {
        setHasError(false);
    }, [avatarUrl]);
    if (!hasError && avatarUrl) {
        component = React__default.createElement("img", { src: avatarUrl, alt: languagePack.agent_ariaAgentAvatar, onError: () => setHasError(true) });
    }
    else if (agentName?.match(/^[\x20-\xFE]+$/)) {
        // If the agentName only contains ASCII characters (and at least one), then show the first letter of the agentName
        // as the agentAvatar. For most Latin languages, we can infer that the first letter of the name is an appropriate
        // representation for that person. For other languages such as Chinese, it's not clear what the correct letter
        // would be so if we see any such characters at all, we'll just fall back to showing a picture instead of a letter.
        // We're only accepting ASCII (and extended ASCII) because proper browser detection for Latin characters is lacking.
        component = (React__default.createElement("div", { "aria-label": languagePack.agent_ariaAgentAvatar, className: "WACAgentAvatar__Circle", style: avatarStyle },
            React__default.createElement("div", { className: "WACAgentAvatar__Letter" }, agentName.charAt(0))));
    }
    else {
        // If the agentName contains any non-ASCII characters, then show the default agent avatar.
        component = (React__default.createElement(UserAvatar, { size: 32, width: width ? Number(width.replace('px', '')) : undefined, height: height ? Number(height.replace('px', '')) : undefined, "aria-label": languagePack.agent_ariaAgentAvatar }));
    }
    return React__default.createElement("div", { className: "WACAgentAvatar" }, component);
}

/**
 * Miscellaneous JavaScript utilities.
 */
/**
 * Determines if the given value is null or undefined.
 */
function isNil(value) {
    return value === undefined || value === null;
}
/**
 * Determines if a given number is odd.
 */
function isOdd(value) {
    if (value % 2 !== 1) {
        return true;
    }
    return false;
}

/**
 * Returns the details necessary for building the message to display the current agent availability.
 */
function AvailabilityMessage({ availability, fallbackText }) {
    let availabilityKey;
    let availabilityValues;
    let availabilityText;
    if (!isNil(availability?.estimated_wait_time)) {
        availabilityKey = 'agent_connectingMinutes';
        availabilityValues = { time: availability.estimated_wait_time };
    }
    else if (!isNil(availability?.position_in_queue)) {
        availabilityKey = 'agent_connectingQueue';
        availabilityValues = { position: availability.position_in_queue };
    }
    else if (availability?.message) {
        availabilityText = availability.message;
    }
    else {
        availabilityText = fallbackText;
    }
    if (availabilityText) {
        return React__default.createElement(RichTextExport, { overrideSanitize: true, text: availabilityText });
    }
    return React__default.createElement(FormattedMessage, { id: availabilityKey, values: addHTMLSupport(availabilityValues) });
}

/**
 * This component is a banner that appears at the top of the screen when the user is connecting or connected to a
 * live agent. It will display a cancel button in the case where the user is waiting for an agent or an "end chat"
 * button for when the user is connected to an agent.
 */
function AgentBanner(props, ref) {
    const { onButtonClick } = props;
    const languagePack = useLanguagePack();
    const serviceManager = useServiceManager();
    const persistedAgentState = useSelector((state) => state.persistedToBrowserStorage.chatState.agentState);
    const agentState = useSelector((state) => state.agentState);
    const { isConnecting, availability, isScreenSharing } = agentState;
    const displayState = useSelector(selectAgentDisplayState);
    const { agentProfile } = persistedAgentState;
    const buttonRef = useRef();
    let line1;
    let line2;
    let avatar;
    let buttonLabel;
    let animation;
    if (isConnecting) {
        animation = React__default.createElement("div", { className: "WACLoadingBar__ConnectingAnimation" });
        line1 = languagePack.agent_connecting;
        line2 = (React__default.createElement(AnnounceOnMountComponentExport, { announceOnce: languagePack.agent_connecting },
            React__default.createElement(AvailabilityMessage, { availability: availability, fallbackText: languagePack.agent_connectWaiting })));
        buttonLabel = languagePack.agent_connectButtonCancel;
    }
    else {
        line1 = agentProfile?.nickname || languagePack.agent_noName;
        buttonLabel = languagePack.agent_connectedButtonEndChat;
        avatar = React__default.createElement(AgentAvatar, { agentProfile: agentProfile, languagePack: languagePack, width: "32px", height: "32px" });
    }
    const onStopSharing = () => {
        serviceManager.humanAgentService.screenShareStop();
    };
    // Add a "requestFocus" imperative function to the ref so other components can trigger focus here.
    useImperativeHandle(ref, () => ({
        requestFocus: () => {
            if (buttonRef.current) {
                doFocusRef(buttonRef);
                return true;
            }
            return false;
        },
    }));
    return (React__default.createElement("div", { className: cx('WACAgentBanner', { 'WACAgentBanner--connected': !isConnecting }) },
        displayState.isConnectingOrConnected && (React__default.createElement("div", { className: "WACAgentBanner__Body" },
            avatar,
            React__default.createElement("div", { className: "WACAgentBanner__AgentInfo" },
                React__default.createElement("div", { className: "WACAgentBanner__AgentLine1" }, line1),
                line2 && React__default.createElement("div", { className: "WACAgentBanner__AgentLine2" }, line2)),
            React__default.createElement(Button, { ref: buttonRef, className: "WACAgentBanner__Button WACAgentBanner__CancelButton", onClick: onButtonClick, size: "sm" }, buttonLabel))),
        isScreenSharing && (React__default.createElement(Button, { className: "WACAgentBanner__Button WACAgentBanner__StopSharingButton", kind: "danger", size: "sm", renderIcon: ScreenOff, onClick: onStopSharing }, languagePack.agent_sharingStopSharingButton)),
        animation));
}
const AgentBannerExport = React__default.memo(forwardRef(AgentBanner));

/**
 * A simple container for the agent banner that avoids rendering it if it is hidden.
 */
function AgentBannerContainer({ onButtonClick, bannerRef }) {
    const agentState = useSelector((state) => state.agentState);
    const displayState = useSelector(selectAgentDisplayState);
    if (displayState.isConnectingOrConnected || agentState.isScreenSharing) {
        return React__default.createElement(AgentBannerExport, { ref: bannerRef, onButtonClick: onButtonClick });
    }
    return null;
}

function QuestionMarkIcon(props) {
    return (React__default.createElement("svg", { className: props.className, xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 12.75 20.64" },
        React__default.createElement("circle", { cx: "6.07", cy: "18.82", r: "1.82" }),
        React__default.createElement("path", { d: "M7.28,0H5.46A5.45,5.45,0,0,0,0,5.44v.63H2.43V5.46a3,3,0,0,1,3-3H7.28a3,3,0,1,1,0,6.07H4.86V14H7.28v-3A5.47,5.47,0,1,0,7.28,0Z" })));
}

function AlternateSuggestionsButton() {
    const serviceManager = useServiceManager();
    const alternateSuggestionsState = useSelector((state) => state.alternateSuggestionsState);
    const { isReadonly } = useSelector(selectInputState);
    const languagePack = useSelector((state) => state.languagePack);
    const { showButton } = alternateSuggestionsState;
    const buttonRef = useRef();
    const buttonLabel = languagePack.suggestions_ariaButtonToOpen;
    if (!showButton) {
        return null;
    }
    function onMenuClick() {
        serviceManager.store.dispatch(actions.setAlternateSuggestionsPanelState(AlternateSuggestionsPanelState.OPEN_FULL, 'User'));
    }
    return (React__default.createElement("div", { className: "WACAlternateSuggestionsButton" },
        React__default.createElement("div", { className: "WACAlternateSuggestionsButton__ButtonContainer" },
            React__default.createElement(Button, { type: "button", className: "WACAlternateSuggestionsButton__button", ref: buttonRef, onClick: onMenuClick, "aria-label": buttonLabel, disabled: isReadonly },
                React__default.createElement(QuestionMarkIcon, null)))));
}
const AlternateSuggestionsButtonExport = React__default.memo(AlternateSuggestionsButton);

function LatestWelcomeNodes({ welcomeNodeBeforeElement, children }) {
    const { namespace } = useServiceManager();
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(WriteableElement$1, { slotName: WriteableElementName.WELCOME_NODE_BEFORE_ELEMENT, id: `welcomeNodeBeforeElement${namespace.suffix}`, element: welcomeNodeBeforeElement }),
        children));
}
var LatestWelcomeNodes$1 = React__default.memo(LatestWelcomeNodes);

function Notifications({ notifications, serviceManager }) {
    const languagePack = useLanguagePack();
    if (notifications.length) {
        return (React__default.createElement("div", { className: "WACNotifications" }, notifications.map((notification) => {
            const item = notification.notification;
            const onClose = () => {
                serviceManager.store.dispatch(actions.removeNotifications({ notificationID: notification.id }));
            };
            let onActionButtonClick;
            let actionButtonLabel;
            if (item.actionButtonLabel && item.onActionButtonClick) {
                onActionButtonClick = () => {
                    item.onActionButtonClick();
                    onClose();
                };
                actionButtonLabel = item.actionButtonLabel;
            }
            return (React__default.createElement("div", { className: "WACNotifications__Notification", key: notification.id },
                React__default.createElement(ActionableNotification, { "aria-label": languagePack.notifications_toastClose, actionButtonLabel: actionButtonLabel, onActionButtonClick: onActionButtonClick, kind: item.kind, onClose: () => {
                        onClose();
                        item.onCloseButtonClick?.();
                    }, subtitle: item.message, title: item.title, 
                    // Instead of this property, we are supposed to use the StaticNotification component which does not
                    // steal focus but it's still experimental and does not provide the functionality we need (it does not
                    // have a close button).
                    hasFocus: false })));
        })));
    }
    return null;
}

function IconHolder(props) {
    return React__default.createElement("div", { className: "WACIconHolder" }, props.icon);
}

function ImageWithFallback(props) {
    const { url, alt, fallback } = props;
    // Indicates if the image failed to load.
    const [hasError, setHasError] = useState(false);
    // If the url changes, then hasError should reset to allow an attempt at loading the new image.
    useEffect(() => {
        setHasError(false);
    }, [url]);
    let component;
    if (!hasError && url) {
        component = React__default.createElement("img", { src: url, alt: alt, onError: () => setHasError(true) });
    }
    else {
        component = fallback;
    }
    return React__default.createElement("div", { className: "WACImageWithFallback" }, component);
}

/**
 * This component with render an element in the DOM and on mount, will fire a bus event with the given element.
 */
function UICustomizationElement(props) {
    const { event, zIndex = 1 } = props;
    const ref = useRef();
    const serviceManager = useServiceManager();
    useOnMount(() => {
        const busEvent = {
            ...event,
            element: ref.current,
        };
        serviceManager.eventBus.fireSync(busEvent, serviceManager.instance);
    });
    return (React__default.createElement("div", { className: cx('WACUICustomizationElement', {
            'WACUICustomizationElement--request': event.type === "customization:message:request:item:left" /* BusEventType.CUSTOMIZATION_REQUEST_LEFT */,
            'WACUICustomizationElement--response': event.type === "customization:message:response:item:left" /* BusEventType.CUSTOMIZATION_RESPONSE_LEFT */,
        }), style: { zIndex }, ref: ref }));
}

var css_248z$4 = ":host{display:block;margin-top:16px}:host([open]) .cds--aichat-chain-of-thought-content{display:block;overflow:visible;max-block-size:-moz-fit-content;max-block-size:fit-content;opacity:1}:host([open]) .cds--aichat-chain-of-thought-button-chevron svg{transform:rotate(-90deg)}.cds--aichat-chain-of-thought-button-chevron{flex-basis:24px;display:flex}.cds--aichat-chain-of-thought-button-chevron svg{transform:rotate(-270deg);transition:all .11s cubic-bezier(.2,0,.38,.9)}.cds--aichat-chain-of-thought-content{display:none;overflow:hidden;max-block-size:0;opacity:0;transition:all allow-discrete .11s cubic-bezier(0,0,.38,.9)}.cds--aichat-chain-of-thought-inner-content{background-color:var(--cds-layer-01,#f4f4f4);border:1px solid var(--cds-border-subtle-01,#c6c6c6);margin-top:8px}.cds--aichat-chain-of-thought-item:not(:last-child){padding-bottom:12px}.cds--aichat-chain-of-thought-item cds-aichat-markdown-text:not(:first-child){padding-top:8px}.cds--aichat-chain-of-thought-item-label{font-size:var(--cds-heading-01-font-size,14px)}.cds--aichat-chain-of-thought-item-label,button.cds--aichat-chain-of-thought-button{font-weight:var(--cds-heading-01-font-weight,600);line-height:var(--cds-heading-01-line-height,1.42857);letter-spacing:var(--cds-heading-01-letter-spacing,.16px)}button.cds--aichat-chain-of-thought-button{box-sizing:border-box;margin:0;font-family:inherit;font-size:100%;vertical-align:initial;display:inline-block;padding:0;border:0;-webkit-appearance:none;-moz-appearance:none;appearance:none;background:none;cursor:pointer;text-align:start;inline-size:100%;font-size:var(--cds-heading-01-font-size,14px);color:var(--cds-text-primary,#161616);display:flex;align-items:center}button.cds--aichat-chain-of-thought-button *,button.cds--aichat-chain-of-thought-button :after,button.cds--aichat-chain-of-thought-button :before{box-sizing:inherit}button.cds--aichat-chain-of-thought-button::-moz-focus-inner{border:0}button.cds--aichat-chain-of-thought-button:focus,button.cds--aichat-chain-of-thought-button:focus-visible{outline:2px solid var(--cds-focus)}.cds--aichat-chain-of-thought-accordion-item-content{color:var(--cds-text-primary,#161616);background:var(--cds-layer-01,#f4f4f4);display:block;overflow:visible;max-block-size:-moz-fit-content;max-block-size:fit-content;opacity:1;transition:all allow-discrete .11s cubic-bezier(0,0,.38,.9)}.cds--aichat-chain-of-thought-item{padding-top:16px;padding-bottom:16px;padding-inline:32px 16px}.cds--aichat-chain-of-thought-accordion-item-content[hidden]{display:none;overflow:hidden;padding-top:0;padding-bottom:0;padding-inline:0 0;max-block-size:0;opacity:0}.cds--aichat-chain-of-thought-accordion-item-header-chevron{flex:0 1 32px;display:flex;justify-content:center;color:var(--cds-text-primary,#161616)}.cds--aichat-chain-of-thought-accordion-item-header-chevron svg{fill:var(--cds-text-primary,#161616);transform:rotate(-270deg);transition:all .11s cubic-bezier(.2,0,.38,.9)}.cds--aichat-chain-of-thought-accordion-item-header-chevron[data-open] svg{transform:rotate(-90deg)}.cds--aichat-chain-of-thought-accordion-item button.cds--aichat-chain-of-thought-accordion-item-header{box-sizing:border-box;margin:0;font-family:inherit;font-size:100%;vertical-align:initial;display:inline-block;border:0;-webkit-appearance:none;-moz-appearance:none;appearance:none;background:none;cursor:pointer;text-align:start;inline-size:100%;font-size:var(--cds-body-01-font-size,14px);font-weight:var(--cds-body-01-font-weight,400);line-height:var(--cds-body-01-line-height,1.42857);letter-spacing:var(--cds-body-01-letter-spacing,.16px);background:var(--cds-layer-accent-02,#e0e0e0);display:flex;padding:8px 0;height:32px;align-items:center}.cds--aichat-chain-of-thought-accordion-item button.cds--aichat-chain-of-thought-accordion-item-header *,.cds--aichat-chain-of-thought-accordion-item button.cds--aichat-chain-of-thought-accordion-item-header :after,.cds--aichat-chain-of-thought-accordion-item button.cds--aichat-chain-of-thought-accordion-item-header :before{box-sizing:inherit}.cds--aichat-chain-of-thought-accordion-item button.cds--aichat-chain-of-thought-accordion-item-header::-moz-focus-inner{border:0}.cds--aichat-chain-of-thought-accordion-item button.cds--aichat-chain-of-thought-accordion-item-header:focus,.cds--aichat-chain-of-thought-accordion-item button.cds--aichat-chain-of-thought-accordion-item-header:focus-visible{box-shadow:0 -1px 0 0 var(--cds-focus,#0f62fe),inset 0 1px 0 0 var(--cds-focus,#0f62fe),inset 2px 0 0 0 var(--cds-focus,#0f62fe),0 1px 0 0 var(--cds-focus,#0f62fe),inset 0 -1px 0 0 var(--cds-focus,#0f62fe),inset -2px 0 0 0 var(--cds-focus,#0f62fe);outline:none;position:relative;z-index:2}.cds--aichat-chain-of-thought-accordion-item:nth-child(odd) button.cds--aichat-chain-of-thought-accordion-item-header{background:var(--cds-layer-02,#fff)}.cds--aichat-chain-of-thought-accordion-item-header-title{color:var(--cds-text-primary,#161616);text-overflow:ellipsis;overflow:hidden;flex:1 1;white-space:nowrap;margin-inline-end:8px}.cds--aichat-chain-of-thought-accordion-item-header-status{display:flex;align-items:center;justify-content:center;flex:0 0 24px}.cds--aichat-chain-of-thought-accordion-item-header-status--failure{margin-inline-end:8px;display:flex;align-items:center;justify-content:center}.cds--aichat-chain-of-thought-accordion-item-header-status--failure svg{fill:var(--cds-support-error,#da1e28)}.cds--aichat-chain-of-thought-accordion-item-header-status--success{margin-inline-end:8px;display:flex;align-items:center;justify-content:center}.cds--aichat-chain-of-thought-accordion-item-header-status--success svg{fill:var(--cds-support-success,#24a148)}";

const stepTitleFormatter = createEnglishFormat('chainOfThought_stepTitle');
class ChainOfThoughtElement extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Indicates if the details panel for the chain of thought is open.
         */
        this.open = false;
        /**
         * Formatting for label of each step item.
         */
        this.formatStepLabelText = ({ stepNumber, stepTitle, }) => stepTitleFormatter.format({
            stepNumber,
            stepTitle: stepTitle || '',
        });
        /**
         * Text string used to label step input.
         */
        this.inputLabelText = enLanguagePack.chainOfThought_inputLabel;
        /**
         * Text string used to label step output.
         */
        this.outputLabelText = enLanguagePack.chainOfThought_outputLabel;
        /**
         * Text string used to label the tool.
         */
        this.toolLabelText = enLanguagePack.chainOfThought_toolLabel;
        /**
         * Text string used to label the button to open the chain of thought panel.
         */
        this.explainabilityText = enLanguagePack.chainOfThought_explainabilityLabel;
        /**
         * Text string used to label the succeeded status icon.
         */
        this.statusSucceededLabelText = enLanguagePack.chainOfThought_statusSucceededLabel;
        /**
         * Text string used to label the failed status icon.
         */
        this.statusFailedLabelText = enLanguagePack.chainOfThought_statusFailedLabel;
        /**
         * Text string used to label the processing status icon.
         */
        this.statusProcessingLabelText = enLanguagePack.chainOfThought_statusProcessingLabel;
        /**
         * ID we use for a11y.
         */
        this._chainOfThoughtPanelID = `${CSS_CLASS_PREFIX}-chain-of-thought-panel-id-${uuid()}`;
    }
    static { this.styles = css `
    ${unsafeCSS(css_248z$4)}
  `; }
    firstUpdated(_changedProperties) {
        // Update the steps saying they are all closed.
        this._steps = this.steps.map(item => ({ ...item, open: false }));
    }
}
__decorate([
    property({ type: Boolean, attribute: 'open', reflect: true })
], ChainOfThoughtElement.prototype, "open", void 0);
__decorate([
    property({ type: Array, attribute: 'steps', reflect: true })
], ChainOfThoughtElement.prototype, "steps", void 0);
__decorate([
    property({ type: Function, attribute: false })
], ChainOfThoughtElement.prototype, "formatStepLabelText", void 0);
__decorate([
    property({ type: String, attribute: 'input-label-text', reflect: true })
], ChainOfThoughtElement.prototype, "inputLabelText", void 0);
__decorate([
    property({ type: String, attribute: 'output-label-text', reflect: true })
], ChainOfThoughtElement.prototype, "outputLabelText", void 0);
__decorate([
    property({ type: String, attribute: 'tool-label-text', reflect: true })
], ChainOfThoughtElement.prototype, "toolLabelText", void 0);
__decorate([
    property({ type: String, attribute: 'explainability-text', reflect: true })
], ChainOfThoughtElement.prototype, "explainabilityText", void 0);
__decorate([
    property({ type: Function, attribute: false })
], ChainOfThoughtElement.prototype, "onToggle", void 0);
__decorate([
    property({ type: Function, attribute: false })
], ChainOfThoughtElement.prototype, "onStepToggle", void 0);
__decorate([
    property({ type: String, attribute: 'status-succeeded-label-text', reflect: true })
], ChainOfThoughtElement.prototype, "statusSucceededLabelText", void 0);
__decorate([
    property({ type: String, attribute: 'status-failed-label-text', reflect: true })
], ChainOfThoughtElement.prototype, "statusFailedLabelText", void 0);
__decorate([
    property({ type: String, attribute: 'status-processing-label-text', reflect: true })
], ChainOfThoughtElement.prototype, "statusProcessingLabelText", void 0);
__decorate([
    state()
], ChainOfThoughtElement.prototype, "_steps", void 0);
__decorate([
    state()
], ChainOfThoughtElement.prototype, "_chainOfThoughtPanelID", void 0);

const CSS_CLASS_ITEM_PREFIX = `${CSS_CLASS_PREFIX}-chain-of-thought-item`;
const CSS_CLASS_STATUS_PREFIX = `${CSS_CLASS_PREFIX}-chain-of-thought-accordion-item-header-status`;
const ChevronRight16svg = toString$1(ChevronRight16);
const CheckmarkFilled16svg = toString$1(CheckmarkFilled16);
const ErrorFilled16svg = toString$1(ErrorFilled16);
/**
 * Returns the correct icon given the status of the step. If there is no status, we assume it is successful.
 */
function stepStatus(status, statusSucceededLabelText, statusFailedLabelText, statusProcessingLabelText) {
    switch (status) {
        case "processing" /* ChainOfThoughtStepStatus.PROCESSING */:
            return html$2 `<cds-custom-inline-loading
        status="active"
        aria-label="${statusProcessingLabelText}"
      ></cds-custom-inline-loading>`;
        case "failure" /* ChainOfThoughtStepStatus.FAILURE */:
            return html$2 `<span
        class="${CSS_CLASS_STATUS_PREFIX}--${"failure" /* ChainOfThoughtStepStatus.FAILURE */}"
        aria-label="${statusFailedLabelText}"
        >${unsafeSVG(ErrorFilled16svg)}</span
      >`;
        default:
            return html$2 `<span
        class="${CSS_CLASS_STATUS_PREFIX}--${"success" /* ChainOfThoughtStepStatus.SUCCESS */}"
        aria-label="${statusSucceededLabelText}"
        >${unsafeSVG(CheckmarkFilled16svg)}</span
      >`;
    }
}
/**
 * Takes the input/output data that is unknown and then renders it in the correct format or returns nothing.
 */
function renderToolData(data, label, classPostfix) {
    // Once we have a Code component instead of just a markdown component, we will need to loop back here.
    const content = parseUnknownDataToMarkdown(data);
    if (content) {
        return renderToolDataAsMarkdown(content, label, classPostfix);
    }
    return html$2 ``;
}
function renderToolDataAsMarkdown(content, label, classPostfix) {
    return html$2 `<div class="${CSS_CLASS_ITEM_PREFIX} ${CSS_CLASS_ITEM_PREFIX}-${classPostfix}">
    <div class="${CSS_CLASS_ITEM_PREFIX}-label">${label}</div>
    <cds-aichat-markdown-text sanitize-html markdown=${content}></cds-aichat-markdown-text>
  </div>`;
}
function accordionItemContent(customElementClass, item) {
    const { inputLabelText, outputLabelText, toolLabelText } = customElementClass;
    if (item.open) {
        return html$2 ` ${item.description
            ? html$2 `<div class="${CSS_CLASS_ITEM_PREFIX} ${CSS_CLASS_ITEM_PREFIX}-description">
          <cds-aichat-markdown-text sanitize-html markdown=${item.description}></cds-aichat-markdown-text>
        </div>`
            : null}
    ${item.tool_name
            ? html$2 `<div class="${CSS_CLASS_ITEM_PREFIX} ${CSS_CLASS_ITEM_PREFIX}-toolName">
          <div class="${CSS_CLASS_ITEM_PREFIX}-label">${toolLabelText}</div>
          ${item.tool_name}
        </div>`
            : null}
    ${renderToolData(item.request?.args, inputLabelText, 'input')}
    ${renderToolData(item.response?.content, outputLabelText, 'output')}`;
    }
    return html$2 ``;
}
function accordionContent(customElementClass) {
    const { _steps, open, _chainOfThoughtPanelID, onStepToggle, formatStepLabelText, statusSucceededLabelText, statusFailedLabelText, statusProcessingLabelText, } = customElementClass;
    if (open) {
        return html$2 `${_steps.map((item, index) => {
            const stepNumber = index + 1;
            const content_id = `${_chainOfThoughtPanelID}-step-${stepNumber}-content`;
            return html$2 `<div class="${CSS_CLASS_PREFIX}-chain-of-thought-accordion-item">
        <button
          class="${CSS_CLASS_PREFIX}-chain-of-thought-accordion-item-header"
          @click=${() => {
                item.open = !item.open;
                customElementClass.requestUpdate();
                // This was an unfortunate discovery after refactoring.
                // I need to move the accordionItemContent back out into its own component for two reasons:
                // 1. The next line and not being able to create a ref here.
                // 2. We really should allow someone to pass in a custom template for this.
                const element = customElementClass.shadowRoot.querySelector(`#${content_id}`);
                onStepToggle?.(item.open, element);
            }}
          aria-expanded=${item.open}
          aria-controls=${content_id}
        >
          <span class="${CSS_CLASS_PREFIX}-chain-of-thought-accordion-item-header-chevron" ?data-open=${item.open}
            >${unsafeSVG(ChevronRight16svg)}</span
          >
          <span class="${CSS_CLASS_PREFIX}-chain-of-thought-accordion-item-header-title"
            >${formatStepLabelText({ stepNumber, stepTitle: item.title })}</span
          >
          <span class="${CSS_CLASS_STATUS_PREFIX}"
            >${stepStatus(item.status, statusSucceededLabelText, statusFailedLabelText, statusProcessingLabelText)}</span
          >
        </button>
        <div class="${CSS_CLASS_PREFIX}-chain-of-thought-accordion-item-content" id=${content_id} ?hidden=${!item.open}>
          ${accordionItemContent(customElementClass, item)}
        </div>
      </div>`;
        })}`;
    }
    return html$2 ``;
}
function chainOfThoughtElementTemplate(customElementClass) {
    const { _chainOfThoughtPanelID, explainabilityText, open } = customElementClass;
    /**
     * Function called when someone clicks on the button to toggle if chain of thought is open or closed.
     */
    function toggle() {
        customElementClass.open = !customElementClass.open;
        customElementClass.onToggle?.(customElementClass.open, customElementClass);
    }
    return html$2 `<div class="${CSS_CLASS_PREFIX}-chain-of-thought">
    <button
      class="${CSS_CLASS_PREFIX}-chain-of-thought-button"
      @click=${toggle}
      aria-expanded=${open}
      aria-controls=${_chainOfThoughtPanelID}
    >
      <span class="${CSS_CLASS_PREFIX}-chain-of-thought-button-chevron">${unsafeSVG(ChevronRight16svg)}</span>
      ${explainabilityText}
    </button>
    <div id=${_chainOfThoughtPanelID} class="${CSS_CLASS_PREFIX}-chain-of-thought-content" ?hidden=${!open}>
      <div class="${CSS_CLASS_PREFIX}-chain-of-thought-inner-content">${accordionContent(customElementClass)}</div>
    </div>
  </div>`;
}

const CHAIN_OF_THOUGHT_COMPONENT_TAG_NAME = `cds-aichat-chain-of-thought`;
/**
 * This class is used to display two feedback buttons (thumbs up and thumbs down).
 */
let CDSChatChainOfThoughtElement = class CDSChatChainOfThoughtElement extends ChainOfThoughtElement {
    /**
     * Renders the template while passing in class functionality.
     */
    render() {
        return chainOfThoughtElementTemplate(this);
    }
};
CDSChatChainOfThoughtElement = __decorate([
    carbonElement(CHAIN_OF_THOUGHT_COMPONENT_TAG_NAME)
], CDSChatChainOfThoughtElement);
var CDSChatChainOfThoughtElement$1 = CDSChatChainOfThoughtElement;

const ChainOfThought = o$1({
    tagName: CHAIN_OF_THOUGHT_COMPONENT_TAG_NAME,
    elementClass: CDSChatChainOfThoughtElement$1,
    react: React__default,
});

var css_248z$3 = ".cds--aichat-feedback-buttons{display:flex;margin-top:8px}";

class FeedbackButtonsElement extends LitElement {
    static { this.styles = css `
    ${unsafeCSS(css_248z$3)}
  `; }
}
__decorate([
    property({ type: Boolean, attribute: 'is-positive-open', reflect: true })
], FeedbackButtonsElement.prototype, "isPositiveOpen", void 0);
__decorate([
    property({ type: Boolean, attribute: 'is-negative-open', reflect: true })
], FeedbackButtonsElement.prototype, "isNegativeOpen", void 0);
__decorate([
    property({ type: Boolean, attribute: 'is-positive-selected', reflect: true })
], FeedbackButtonsElement.prototype, "isPositiveSelected", void 0);
__decorate([
    property({ type: Boolean, attribute: 'has-positive-details', reflect: true })
], FeedbackButtonsElement.prototype, "hasPositiveDetails", void 0);
__decorate([
    property({ type: Boolean, attribute: 'has-negative-details', reflect: true })
], FeedbackButtonsElement.prototype, "hasNegativeDetails", void 0);
__decorate([
    property({ type: Boolean, attribute: 'is-negative-selected', reflect: true })
], FeedbackButtonsElement.prototype, "isNegativeSelected", void 0);
__decorate([
    property({ type: Boolean, attribute: 'is-positive-disabled', reflect: true })
], FeedbackButtonsElement.prototype, "isPositiveDisabled", void 0);
__decorate([
    property({ type: Boolean, attribute: 'is-negative-disabled', reflect: true })
], FeedbackButtonsElement.prototype, "isNegativeDisabled", void 0);
__decorate([
    property({ type: String, attribute: 'positive-label', reflect: true })
], FeedbackButtonsElement.prototype, "positiveLabel", void 0);
__decorate([
    property({ type: String, attribute: 'negative-label', reflect: true })
], FeedbackButtonsElement.prototype, "negativeLabel", void 0);
__decorate([
    property({ type: String, attribute: 'panel-id', reflect: true })
], FeedbackButtonsElement.prototype, "panelID", void 0);
__decorate([
    property({ type: Object, attribute: 'on-click' })
], FeedbackButtonsElement.prototype, "onClick", void 0);

const ThumbsDown16svg = toString$1(ThumbsDown16);
const ThumbsDownFilled16svg = toString$1(ThumbsDownFilled16);
const ThumbsUp16svg = toString$1(ThumbsUp16);
const ThumbsUpFilled16svg = toString$1(ThumbsUpFilled16);
function feedbackButtonsElementTemplate(customElementClass) {
    const { isPositiveOpen, isNegativeOpen, isPositiveSelected, isNegativeSelected, hasPositiveDetails, hasNegativeDetails, isPositiveDisabled, isNegativeDisabled, positiveLabel, negativeLabel, panelID, onClick, } = customElementClass;
    return html$2 `<div class="${CSS_CLASS_PREFIX}-feedback-buttons">
    <cds-custom-icon-button
      class="${CSS_CLASS_PREFIX}-feedback-buttons-positive"
      size="sm"
      align="top-left"
      kind="ghost"
      role="button"
      disabled="${isPositiveDisabled || nothing}"
      aria-expanded="${isPositiveDisabled || !hasPositiveDetails ? nothing : isPositiveOpen}"
      aria-pressed="${isPositiveSelected || nothing}"
      aria-controls="${panelID}-feedback-positive"
      @click="${() => onClick(true)}"
    >
      <span slot="icon">${unsafeSVG(isPositiveSelected ? ThumbsUpFilled16svg : ThumbsUp16svg)}</span>
      <span slot="tooltip-content">${positiveLabel || enLanguagePack.feedback_positiveLabel}</span>
    </cds-custom-icon-button>
    <cds-custom-icon-button
      class="${CSS_CLASS_PREFIX}-feedback-buttons-negative"
      size="sm"
      align="top-left"
      kind="ghost"
      role="button"
      disabled="${isNegativeDisabled || nothing}"
      aria-expanded="${isNegativeDisabled || !hasNegativeDetails ? nothing : isNegativeOpen}"
      aria-pressed="${isNegativeSelected || nothing}"
      aria-controls="${panelID}-feedback-positive"
      @click="${() => onClick(false)}"
    >
      <span slot="icon">${unsafeSVG(isNegativeSelected ? ThumbsDownFilled16svg : ThumbsDown16svg)}</span>
      <span slot="tooltip-content">${negativeLabel || enLanguagePack.feedback_negativeLabel}</span>
    </cds-custom-icon-button>
  </div>`;
}

const FEEDBACK_BUTTONS_COMPONENT_TAG_NAME = `cds-aichat-feedback-buttons`;
/**
 * This class is used to display two feedback buttons (thumbs up and thumbs down).
 */
let CDSChatFeedbackButtonsElement = class CDSChatFeedbackButtonsElement extends FeedbackButtonsElement {
    /**
     * Renders the template while passing in class functionality.
     */
    render() {
        return feedbackButtonsElementTemplate(this);
    }
};
CDSChatFeedbackButtonsElement = __decorate([
    carbonElement(FEEDBACK_BUTTONS_COMPONENT_TAG_NAME)
], CDSChatFeedbackButtonsElement);
var CDSChatFeedbackButtonsElement$1 = CDSChatFeedbackButtonsElement;

const FeedbackButtonsComponent = o$1({
    tagName: FEEDBACK_BUTTONS_COMPONENT_TAG_NAME,
    elementClass: CDSChatFeedbackButtonsElement$1,
    react: React__default,
});

var css_248z$2 = ".cds--aichat-tag-list-container{margin:0;padding:0;list-style:none;display:flex;flex-wrap:wrap;align-items:center;gap:8px}.cds--aichat-tag-list-item{box-sizing:border-box;padding:2px;animation:fade-in .6s forwards;font-size:var(--cds-chat-BASE-font-size-small);opacity:0}@keyframes fade-in{0%{opacity:0}to{opacity:1}}";

class TagListElement extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * The set of tags that have been selected.
         */
        this.selectedTags = new Set();
    }
    static { this.styles = css `
    ${unsafeCSS(css_248z$2)}
  `; }
    /**
     * Called when the properties of the component have changed.
     */
    updated(changedProperties) {
        if (changedProperties.has('initialSelectedTags')) {
            this._setInitialValues(this.initialSelectedTags);
        }
    }
    /**
     * Updates the initial values used in the component.
     */
    _setInitialValues(values) {
        if (values) {
            this.selectedTags = new Set(this.initialSelectedTags);
        }
    }
    /**
     * Called when a tag is clicked.
     */
    _handleTagClick(event) {
        const tagValue = event.target.getAttribute('data-content');
        const isSelected = this.selectedTags.has(tagValue);
        if (isSelected) {
            this.selectedTags.delete(tagValue);
        }
        else {
            this.selectedTags.add(tagValue);
        }
        this.onTagsChanged?.(Array.from(this.selectedTags));
        // Force an update because we changed selectedTags internally but the property reference itself didn't change.
        this.requestUpdate();
    }
}
__decorate([
    property({ type: Array, attribute: 'tags' })
], TagListElement.prototype, "tags", void 0);
__decorate([
    property({ type: Array, attribute: 'initial-selected-tags' })
], TagListElement.prototype, "initialSelectedTags", void 0);
__decorate([
    property({ type: Boolean, attribute: 'multi-select' })
], TagListElement.prototype, "multiSelect", void 0);
__decorate([
    property({ type: Object, attribute: 'on-tags-changed' })
], TagListElement.prototype, "onTagsChanged", void 0);
__decorate([
    state()
], TagListElement.prototype, "selectedTags", void 0);

/**
 * Lit template for code.
 */
function tagListElementTemplate(customElementClass) {
    const { selectedTags, tags, _handleTagClick: handleTagClick } = customElementClass;
    return html$2 `<div class="${CSS_CLASS_PREFIX}-tag-list">
    ${html$2 `<ul class="${CSS_CLASS_PREFIX}-tag-list-container">
      ${tags.map(value => html$2 `<li class="${CSS_CLASS_PREFIX}-tag-list-item}">
            <cds-custom-chat-button
              class="${CSS_CLASS_PREFIX}-tag-list-button"
              kind="primary"
              size="sm"
              type="button"
              is-quick-action
              role="option"
              aria-pressed="${selectedTags.has(value)}"
              ?is-selected="${selectedTags.has(value)}"
              data-content="${value}"
              @click="${handleTagClick}"
            >
              ${value}
            </cds-custom-chat-button>
          </li>`)}
    </div>`}
  </div>`;
}

const TAG_LIST_TAG_NAME = `${WEB_COMPONENT_PREFIX}-tag-list`;
/**
 * This component is used to display a list of tags that the user can choose from.
 */
let CDSTagListElement = class CDSTagListElement extends TagListElement {
    /**
     * Renders the template while passing in class functionality.
     */
    render() {
        return tagListElementTemplate(this);
    }
};
CDSTagListElement = __decorate([
    carbonElement(TAG_LIST_TAG_NAME)
], CDSTagListElement);

const ROUNDED_BUTTON_TAG_NAME = `cds-aichat-rounded-button`;
const styles = `
  .cds-custom--btn {
    border-start-start-radius: var(--${ROUNDED_BUTTON_TAG_NAME}-top-left);
    border-start-end-radius: var(--${ROUNDED_BUTTON_TAG_NAME}-top-right);
    border-end-start-radius: var(--${ROUNDED_BUTTON_TAG_NAME}-bottom-left);
    border-end-end-radius: var(--${ROUNDED_BUTTON_TAG_NAME}-bottom-right);
    width: var(--${ROUNDED_BUTTON_TAG_NAME}-width);
    max-width: var(--${ROUNDED_BUTTON_TAG_NAME}-max-width);
  }`;
/**
 * This is an extension of the carbon button that allows us to round the corners.
 */
let CDSRoundedButtonElement = class CDSRoundedButtonElement extends CDSButton {
    static { this.styles = css `
    ${CDSButton.styles}
    ${unsafeCSS(styles)}
  `; }
};
CDSRoundedButtonElement = __decorate([
    carbonElement(ROUNDED_BUTTON_TAG_NAME)
], CDSRoundedButtonElement);

var css_248z$1 = ".cds--aichat-container{margin-block-start:4px;box-sizing:border-box;border:1px solid var(--cds-chat-bubble-border,#e0e0e0);border-radius:var(--cds-chat-BASE-border-radius-med);animation:fade-in .6s forwards;background-color:var(--cds-chat-shell-background,#fff);inline-size:100%;container-type:inline-size}.cds--aichat-is-closed{display:none}.cds--aichat-title-row{margin-inline:16px 8px;margin-block-start:12px;display:flex}.cds--aichat-title{font-size:var(--cds-chat-BASE-font-size-xlarge);line-height:var(--cds-chat-BASE-line-height-xlarge);flex:1}.cds--aichat-close{margin-inline-start:8px}.cds--aichat-disclaimer,.cds--aichat-prompt{margin-inline:16px;margin-block-start:8px;color:var(--cds-text-secondary,#525252);font-size:var(--cds-chat-BASE-font-size-small);line-height:var(--cds-chat-BASE-line-height-small)}.cds--aichat-categories,.cds--aichat-feedback-text{margin-inline:16px;margin-block-start:8px}.cds--aichat-buttons{display:flex;width:100%;margin-block-start:16px}.cds--aichat-submit{flex:1;--cds-aichat-rounded-button-bottom-right:var(--cds-chat-BASE-border-radius-med);--cds-aichat-rounded-button-width:100%;--cds-aichat-rounded-button-max-width:100%}.cds--aichat-cancel{flex:1;--cds-aichat-rounded-button-bottom-left:var(--cds-chat-BASE-border-radius-med);--cds-aichat-rounded-button-width:100%;--cds-aichat-rounded-button-max-width:100%}@keyframes fade-in{0%{opacity:0}to{opacity:1}}";

/**
 * The component for displaying a panel requesting feedback from a user.
 */
class FeedbackElement extends LitElement {
    constructor() {
        super(...arguments);
        /**
         * Indicates whether the text area should be shown.
         */
        this.showTextArea = true;
        /**
         * Indicates whether the prompt line should be shown.
         */
        this.showPrompt = true;
    }
    static { this.styles = css `
    ${unsafeCSS(css_248z$1)}
  `; }
    /**
     * Called when the properties of the component have changed.
     */
    updated(changedProperties) {
        if (changedProperties.has('initialValues')) {
            this._setInitialValues(this.initialValues);
        }
    }
    /**
     * Updates the initial values used in the component.
     */
    _setInitialValues(values) {
        if (values) {
            this._textInput = values.text;
            this._initialSelectedCategories = values.selectedCategories;
        }
    }
    /**
     * Stores the current value of the text area used to collect feedback.
     */
    _handleTextInput(event) {
        this._textInput = event.currentTarget.value;
    }
    /**
     * Called when the user clicks the submit button.
     */
    _handleSubmit() {
        this.onSubmit?.({
            text: this._textInput,
            selectedCategories: this._selectedCategories,
        });
    }
    /**
     * Called then the user clicks the close button.
     */
    _handleCancel() {
        this.onClose?.();
    }
    /**
     * Called when the selected categories changes.
     */
    _handleCategoryChange(selectedCategories) {
        this._selectedCategories = selectedCategories;
    }
}
__decorate([
    property({ type: String, attribute: 'class', reflect: true })
], FeedbackElement.prototype, "class", void 0);
__decorate([
    property({ type: String, reflect: true })
], FeedbackElement.prototype, "id", void 0);
__decorate([
    property({ type: Boolean, attribute: 'is-open', reflect: true })
], FeedbackElement.prototype, "isOpen", void 0);
__decorate([
    property({ type: Boolean, attribute: 'is-readonly', reflect: true })
], FeedbackElement.prototype, "isReadonly", void 0);
__decorate([
    property({ type: Object, attribute: 'on-close' })
], FeedbackElement.prototype, "onClose", void 0);
__decorate([
    property({ type: Object, attribute: 'on-submit' })
], FeedbackElement.prototype, "onSubmit", void 0);
__decorate([
    property({ type: Object, attribute: 'initial-values', reflect: true })
], FeedbackElement.prototype, "initialValues", void 0);
__decorate([
    property({ type: String, attribute: 'title', reflect: true })
], FeedbackElement.prototype, "title", void 0);
__decorate([
    property({ type: String, attribute: 'prompt', reflect: true })
], FeedbackElement.prototype, "prompt", void 0);
__decorate([
    property({ type: Object, attribute: 'categories', reflect: true })
], FeedbackElement.prototype, "categories", void 0);
__decorate([
    property({ type: String, attribute: 'disclaimer', reflect: true })
], FeedbackElement.prototype, "disclaimer", void 0);
__decorate([
    property({ type: String, attribute: 'text-area-placeholder', reflect: true })
], FeedbackElement.prototype, "placeholder", void 0);
__decorate([
    property({ type: String, attribute: 'cancel-label', reflect: true })
], FeedbackElement.prototype, "cancelLabel", void 0);
__decorate([
    property({ type: String, attribute: 'submit-label', reflect: true })
], FeedbackElement.prototype, "submitLabel", void 0);
__decorate([
    property({ type: Boolean, attribute: 'show-text-area', reflect: true })
], FeedbackElement.prototype, "showTextArea", void 0);
__decorate([
    property({ type: Boolean, attribute: 'show-prompt', reflect: true })
], FeedbackElement.prototype, "showPrompt", void 0);
__decorate([
    state()
], FeedbackElement.prototype, "_textInput", void 0);
__decorate([
    state()
], FeedbackElement.prototype, "_initialSelectedCategories", void 0);
__decorate([
    state()
], FeedbackElement.prototype, "_selectedCategories", void 0);
__decorate([
    bind_1
], FeedbackElement.prototype, "_handleCategoryChange", null);

// The maximum number of characters the user is allowed to type into the text area.
const MAX_TEXT_COUNT = 1000;
/**
 * Lit template for feedback.
 */
function feedbackElementTemplate(customElementClass) {
    const { _handleCancel: handleCancel, _handleSubmit: handleSubmit, _handleTextInput: handleTextInput, _initialSelectedCategories: initialSelectedCategories, _textInput: textInput, _handleCategoryChange: handleCategoryChange, id, isReadonly, isOpen, title, prompt, placeholder, categories, disclaimer, showTextArea, showPrompt, submitLabel, cancelLabel, } = customElementClass;
    return html$2 `<div
    class="${cx(`${CSS_CLASS_PREFIX}-container`, {
        [`${CSS_CLASS_PREFIX}-is-closed`]: !isOpen,
    })}"
  >
    <div class="${CSS_CLASS_PREFIX}-title-row">
      <div class="${CSS_CLASS_PREFIX}-title">${title || enLanguagePack.feedback_defaultTitle}</div>
    </div>
    ${showPrompt
        ? html$2 `<div class="${CSS_CLASS_PREFIX}-prompt">${prompt || enLanguagePack.feedback_defaultPrompt}</div>`
        : ''}
    ${categories?.length
        ? html$2 `<div class="${CSS_CLASS_PREFIX}-categories">
          <cds-aichat-tag-list
            .tags=${categories}
            .initialSelectedTags=${initialSelectedCategories}
            .onTagsChanged=${handleCategoryChange}
          >
          </cds-aichat-tag-list>
        </div>`
        : ''}
    ${showTextArea
        ? html$2 `<div class="${CSS_CLASS_PREFIX}-feedback-text">
          <cds-custom-layer level="1">
            <cds-custom-textarea
              id="${id}-text-area"
              value="${textInput}"
              class="${CSS_CLASS_PREFIX}-feedback-text-area"
              ?disabled="${isReadonly}"
              placeholder="${placeholder || enLanguagePack.feedback_defaultPlaceholder}"
              rows="3"
              max-count="${MAX_TEXT_COUNT}"
              @input=${handleTextInput}
            ></cds-custom-textarea>
          </cds-custom-layer>
        </div>`
        : ''}
    ${disclaimer
        ? html$2 `<div class="${CSS_CLASS_PREFIX}-disclaimer">
          <cds-aichat-markdown-text markdown="${disclaimer}"></cds-aichat-markdown-text>
        </div>`
        : ''}
    <div class="${CSS_CLASS_PREFIX}-buttons">
      <cds-aichat-rounded-button
        class="${CSS_CLASS_PREFIX}-cancel"
        disabled=${isReadonly || nothing}
        size="lg"
        kind="secondary"
        @click=${handleCancel}
      >
        ${cancelLabel || enLanguagePack.feedback_cancelLabel}
      </cds-aichat-rounded-button>
      <cds-aichat-rounded-button
        class="${CSS_CLASS_PREFIX}-submit"
        disabled=${isReadonly || nothing}
        size="lg"
        @click=${handleSubmit}
      >
        ${submitLabel || enLanguagePack.feedback_submitLabel}
      </cds-aichat-rounded-button>
    </div>
  </div>`;
}

const FEEDBACK_COMPONENT_TAG_NAME = `cds-aichat-feedback`;
/**
 * Constructed class functionality for the test input custom element
 */
let CDSChatFeedbackElement = class CDSChatFeedbackElement extends FeedbackElement {
    /**
     * Renders the template while passing in class functionality.
     */
    render() {
        return feedbackElementTemplate(this);
    }
};
CDSChatFeedbackElement = __decorate([
    carbonElement(FEEDBACK_COMPONENT_TAG_NAME)
], CDSChatFeedbackElement);
var CDSChatFeedbackElement$1 = CDSChatFeedbackElement;

const FeedbackComponent = o$1({
    tagName: FEEDBACK_COMPONENT_TAG_NAME,
    elementClass: CDSChatFeedbackElement$1,
    react: React__default,
});

/**
 * Used to display a generic status message in the chat window.
 */
function ChatStatusMessage(props) {
    const intl = useIntl();
    let messageKey;
    let messageText;
    const responseType = props.message.item.response_type;
    if (responseType === InternalMessageResponseType.SESSION_EXPIRED) {
        messageKey = 'sessionHistory_expired';
        messageText = intl.formatMessage({ id: messageKey });
    }
    else if (responseType === InternalMessageResponseType.CHANNEL_TRANSFER_FAILED) {
        messageKey = 'channelTransfer_failed';
        messageText = intl.formatMessage({ id: messageKey });
    }
    return React__default.createElement(React__default.Fragment, null, messageText);
}

/**
 * This component is for displaying a message under streamed responses that were stopped.
 */
function ResponseStopped() {
    const { messages_responseStopped } = useLanguagePack();
    return React__default.createElement("div", { className: "WACResponseStopped" }, messages_responseStopped);
}

/**
 * Displays a modal asking if the user wants to end a chat with an agent. This also covers the case where the user
 * cancels a request for an agent before an agent has joined.
 */
function EndAgentChatModal(props) {
    const { onConfirm, onCancel, title, message } = props;
    const languagePack = useLanguagePack();
    const serviceManager = useServiceManager();
    const { isConnected, isSuspended } = useSelector((state) => state.persistedToBrowserStorage.chatState.agentState);
    const useTitle = title || (isConnected ? languagePack.agent_endChat : languagePack.agent_confirmCancelRequestTitle);
    const useMessage = message || (isConnected ? languagePack.agent_confirmEndChat : languagePack.agent_confirmCancelRequestMessage);
    const cancelButtonLabel = languagePack.agent_confirmEndChatNo;
    let confirmButtonLabel;
    if (isSuspended) {
        confirmButtonLabel = languagePack.agent_confirmEndSuspendedYes;
    }
    else if (isConnected) {
        confirmButtonLabel = languagePack.agent_confirmEndChatYes;
    }
    else {
        confirmButtonLabel = languagePack.agent_confirmCancelRequestYes;
    }
    return (React__default.createElement(ConfirmModal, { title: useTitle, message: useMessage, onConfirm: onConfirm, onCancel: onCancel, cancelButtonLabel: cancelButtonLabel, confirmButtonLabel: confirmButtonLabel, modalAnnounceMessage: useMessage, serviceManager: serviceManager }));
}

/**
 * This component is displayed to the user when a "connect to agent" response comes back from the server. This
 * informs the user that we are able to connect them to a human agent and display a confirmation asking if they do
 * want to connect.
 *
 * This is the "real" component that is displayed to users as opposed to the "preview" component that is displayed
 * for users using the preview link.
 */
function RealConnectToAgent(props) {
    const { languagePack, localMessage, originalMessage, disableUserInputs, serviceManager, agentState, requestFocus, agentDisplayState, persistedAgentState, } = props;
    const { activeLocalMessageID, availability, isConnecting } = agentState;
    const { isSuspended } = persistedAgentState;
    const [showConfirmSuspended, setShowConfirmSuspended] = useState(false);
    if (!isSuspended && showConfirmSuspended) {
        // This can happen if the user is disconnected while waiting for the confirmation.
        setShowConfirmSuspended(false);
    }
    function doStartChat() {
        if (isSuspended && !showConfirmSuspended) {
            // If there is already a suspended chat and we're not showing the confirmation modal, then we need to confirm
            // first.
            setShowConfirmSuspended(true);
        }
        else {
            setShowConfirmSuspended(false);
            serviceManager.humanAgentService.startChat(localMessage, originalMessage);
            // The connect button will become disabled. We need to move focus to the cancel button but do so in a timeout to
            // give it a chance to render.
            setTimeout(requestFocus);
        }
    }
    const noAgentsWereOnline = originalMessage.history.agent_availability === AgentsOnlineStatus.OFFLINE;
    if (noAgentsWereOnline) {
        // Display the "agents are not available" message that was configured in the skill or show a default value if
        // there is none.
        const agentUnavailableMessage = localMessage.item.agent_unavailable?.message || languagePack.default_agent_unavailableMessage;
        return React__default.createElement("div", null, agentUnavailableMessage);
    }
    let textFromMessage;
    const reasonType = localMessage.item.transfer_info?.reason?.type;
    switch (reasonType) {
        case ConnectToAgentReason.GENERAL_ERROR:
        case ConnectToAgentReason.TRANSFER_FAILURE:
            textFromMessage = languagePack.agent_reason_error;
            break;
        default:
            // Display the "agents are available" message that was configured in the skill or show a default value if there is
            // none.
            textFromMessage = localMessage.item.agent_available?.message || languagePack.default_agent_availableMessage;
    }
    let buttonIcon; // CarbonIconType is not exported, currently.
    let buttonText;
    let showDisabled = disableUserInputs || agentDisplayState.isConnectingOrConnected;
    let messageToUser = textFromMessage;
    if (localMessage.ui_state.id === activeLocalMessageID) {
        // This card is the active card in a chat that has been started.
        showDisabled = true;
        if (isConnecting) {
            // In the connecting state, the text on the card changes as the availability information is updated by the
            // service desk integration.
            buttonIcon = Checkmark;
            buttonText = languagePack.agent_cardButtonChatRequested;
            messageToUser = (React__default.createElement(AvailabilityMessage, { availability: availability, fallbackText: languagePack.agent_connectWaiting }));
        }
        else {
            buttonIcon = Headset;
            buttonText = languagePack.agent_cardButtonConnected;
            messageToUser = languagePack.agent_cardMessageConnected;
        }
    }
    else if (disableUserInputs) {
        if (localMessage.ui_state.wasAgentChatEnded) {
            buttonIcon = Logout;
            buttonText = languagePack.agent_cardButtonChatEnded;
            messageToUser = languagePack.agent_cardMessageChatEnded;
        }
        else {
            buttonIcon = Headset;
            buttonText = languagePack.agent_startChat;
        }
    }
    else {
        buttonIcon = HelpDesk;
        buttonText = languagePack.agent_startChat;
    }
    return (React__default.createElement(Tile, { className: "WACConnectToAgent" },
        React__default.createElement("div", { className: "WACConnectToAgent__Title" },
            React__default.createElement("span", null, languagePack.agent_chatTitle)),
        React__default.createElement("div", { className: "WACConnectToAgent__Text" }, messageToUser),
        React__default.createElement(Button, { className: "WACConnectToAgent__RequestButton", size: "md", disabled: showDisabled, onClick: doStartChat, renderIcon: buttonIcon }, buttonText),
        !showDisabled && isSuspended && (React__default.createElement("div", { className: "WACConnectToAgent__SuspendedWarning" }, languagePack.agent_suspendedWarning)),
        showConfirmSuspended && (React__default.createElement(EndAgentChatModal, { title: languagePack.agent_confirmSuspendedEndChatTitle, message: languagePack.agent_confirmSuspendedEndChatMessage, onConfirm: doStartChat, onCancel: () => setShowConfirmSuspended(false) }))));
}

/**
 * This component is displayed to the user when a "connect to agent" response comes back from the server. This
 * informs the user that we are able to connect them to a human agent and displays a confirmation asking if they do
 * want to connect.
 *
 * This component will display the appropriate panel depending on whether the user is viewing the preview link.
 */
function ConnectToAgent(props) {
    const { languagePack, localMessage, originalMessage, config, serviceManager, disableUserInputs, agentState, requestFocus, agentDisplayState, persistedAgentState, } = props;
    const { connectToAgentBehavior, isReadonly } = config.public.__ibm__;
    const { agent_sdMissingWarning } = languagePack;
    const { agent_no_service_desk } = originalMessage?.history;
    // Note, the ERROR_MISSING case is handled by the MessageComponent before it gets here.
    const showWarning = agent_no_service_desk && connectToAgentBehavior === ConnectToAgentBehaviorType.WARN_MISSING;
    // The "preview" case just means we show a disabled card (unless there really is a service desk connected).
    const showPreview = connectToAgentBehavior === ConnectToAgentBehaviorType.PREVIEW && !hasServiceDesk(config);
    // Disable the "start chat" button if the widget is in a readonly mode or a preview mode with no service desk.
    const childDisableUserInputs = disableUserInputs || isReadonly || showWarning || showPreview || !hasServiceDesk(config);
    // The Carbon InlineNotification component doesn't allow HTML anymore, so faking it here.
    return (React__default.createElement("div", null,
        showWarning && (React__default.createElement("div", { role: "status", className: "cds--inline-notification cds--inline-notification--error" },
            React__default.createElement("div", { className: "cds--inline-notification__details" },
                React__default.createElement("div", { className: "cds--inline-notification__text-wrapper" },
                    React__default.createElement("div", { className: "cds--inline-notification__subtitle" },
                        React__default.createElement(MaybeDangerouslySetInnerHTMLExport, { html: agent_sdMissingWarning })))))),
        React__default.createElement(RealConnectToAgent, { localMessage: localMessage, originalMessage: originalMessage, languagePack: languagePack, serviceManager: serviceManager, disableUserInputs: childDisableUserInputs, agentState: agentState, persistedAgentState: persistedAgentState, agentDisplayState: agentDisplayState, requestFocus: requestFocus })));
}

function AudioComponent({ source, ...props }) {
    // Determine if the provided source is a Mixcloud link in order to set a fixed height it uses.
    const isMixCloud = source?.startsWith('https://www.mixcloud.com');
    return React__default.createElement(MediaPlayerExport, { type: MessageResponseTypes.AUDIO, source: source, isMixcloud: isMixCloud, ...props });
}
const AudioComponentExport = React__default.memo(AudioComponent);

/**
 * This component is the same as the {@link Image} component, but makes it clickable. Depending on the props, it can
 * be a normal clickable tile or link.
 */
function ClickableImage({ buttonAltText, isLink, target, disabled, onClick, ...imageProps }) {
    if (isLink) {
        return (React__default.createElement("a", { className: "WACClickableImage", href: imageProps.displayURL, rel: "noopener noreferrer", target: target, onClick: onClick },
            React__default.createElement(ImageExport, { ...imageProps }),
            buttonAltText && React__default.createElement(VisuallyHidden, null, buttonAltText)));
    }
    return (React__default.createElement("button", { className: "WACClickableImage", type: "button", onClick: onClick, disabled: disabled },
        React__default.createElement(ImageExport, { ...imageProps }),
        buttonAltText && React__default.createElement(VisuallyHidden, null, buttonAltText)));
}

/**
 * This is the base button component for the "button" response type.
 */
function WACButtonItemComponent({ className, label, kind, url, target = '_blank', disabled, renderIcon, imageURL, altText, onClick, }) {
    const { errors_imageSource } = useLanguagePack();
    const useAITheme = useSelector((state) => state.theme.useAITheme);
    const text = label || url;
    const linkTarget = url ? target : undefined;
    if (imageURL) {
        return (React__default.createElement(ClickableImage, { imageError: errors_imageSource, source: imageURL, target: target, title: label, displayURL: url, altText: altText, renderIcon: renderIcon, onClick: onClick, disabled: disabled, isLink: Boolean(url), useAITheme: useAITheme }));
    }
    return (React__default.createElement(Button, { className: cx('WACButtonItem', className), as: url ? 'a' : undefined, kind: getButtonKind(kind), href: url, target: linkTarget, rel: url ? 'noopener noreferrer' : undefined, disabled: disabled, renderIcon: renderIcon, onClick: onClick }, text));
}
function getButtonKind(style) {
    switch (style) {
        case ButtonItemKind.LINK:
        case ButtonItemKind.TERTIARY:
            return 'ghost';
        case ButtonItemKind.DEFAULT:
            return 'primary';
        default:
            return style;
    }
}

/**
 * This component is for a button response type where the button_type is "custom_event" that fires an event when
 * clicked that developers can hook into.
 */
function ButtonItemCustomEventComponent({ localMessageItem, fullMessage, }) {
    const serviceManager = useServiceManager();
    const messageItem = localMessageItem.item;
    const { ui_state } = localMessageItem;
    const { image_url, alt_text, label, kind, value } = messageItem;
    const isDisabled = Boolean(value && ui_state.optionSelected);
    const onClickHandler = useCallback(async () => {
        await serviceManager.fire({
            type: "messageItemCustom" /* BusEventType.MESSAGE_ITEM_CUSTOM */,
            customEventType: CustomEventType.BUTTON_ITEM_CLICKED,
            messageItem,
            fullMessage,
        });
    }, [messageItem, serviceManager, fullMessage]);
    return (React__default.createElement(WACButtonItemComponent, { imageURL: image_url, altText: alt_text, label: label, kind: kind, disabled: isDisabled, renderIcon: (image_url && TouchInteraction) || undefined, onClick: onClickHandler }));
}

/**
 * This component is for a button response type where the button_type is "post_back" that can send a message request
 * to the assistant. When the button is clicked, it gets disabled.
 */
function ButtonItemPostBackComponent({ localMessageItem, requestFocus, isMessageForInput, }) {
    const serviceManager = useServiceManager();
    const messageItem = localMessageItem.item;
    const { ui_state, fullMessageID } = localMessageItem;
    const { image_url, alt_text, label, kind } = messageItem;
    const isDisabled = !isMessageForInput || Boolean(ui_state.optionSelected);
    const onClickHandler = useCallback(() => {
        const isInputAvailable = Boolean(messageItem.value?.input?.text || label);
        if (isInputAvailable) {
            const messageRequest = createMessageRequestForButtonItemOption(messageItem, fullMessageID);
            requestFocus();
            serviceManager.store.dispatch(actions.messageSetOptionSelected(ui_state.id, messageRequest));
            serviceManager.actions.sendWithCatch(messageRequest, MessageSendSource.POST_BACK_BUTTON, {
                trackEventProps: {
                    eventName: 'Assistant Message Received from User',
                    type: 'post_back',
                },
            });
        }
        else {
            consoleError(`${WA_CONSOLE_PREFIX} post_back button with label "${messageItem.label}" has no input message to send.`);
        }
    }, [messageItem, label, fullMessageID, requestFocus, serviceManager.store, serviceManager.actions, ui_state.id]);
    return (React__default.createElement(WACButtonItemComponent, { imageURL: image_url, altText: alt_text, label: label, kind: kind, onClick: onClickHandler, renderIcon: (image_url && Send) || undefined, disabled: isDisabled }));
}

/**
 * This component is for a button response type where the button_type is "show_panel". This component is a button
 * opens a panel when clicked. The panel content is authored similarly to normal response types, but instead of
 * appearing in the chat they appear in a panel under the "panel.body" or "panel.footer" property.
 */
function ButtonItemShowPanelComponent({ localMessageItem, isMessageForInput }) {
    const serviceManager = useServiceManager();
    const { image_url, alt_text, label, kind } = localMessageItem.item;
    /**
     * Once the button is clicked, render the panel content and update the message history to remember the panel being.
     */
    const onClickHandler = useCallback(async () => {
        serviceManager.store.dispatch(actions.setResponsePanelIsOpen(true));
        serviceManager.store.dispatch(actions.setResponsePanelContent(localMessageItem, isMessageForInput));
    }, [localMessageItem, isMessageForInput, serviceManager]);
    return (React__default.createElement(WACButtonItemComponent, { className: "WACButtonItemComponent__ShowPanel", imageURL: image_url, altText: alt_text, label: label, kind: kind, renderIcon: (image_url && ArrowRight) || undefined, onClick: onClickHandler }));
}

/**
 * This component is for a button response type where the button_type is "url". Clicking this button will take the
 * user to the provided url. A target location can also be provided.
 */
function ButtonItemURLComponent({ localMessageItem }) {
    const { image_url, alt_text, url, target, label, kind } = localMessageItem.item;
    // If no image url is provided and should is a normal link, then render a carbon link.
    if (!image_url && kind === ButtonItemKind.LINK) {
        return (React__default.createElement("div", { className: "WAC__received--textContent WACButtonItem" },
            React__default.createElement(Link, { className: "WACWidget__breakWord", href: url, target: target, rel: "noopener noreferrer", renderIcon: Launch }, label || url)));
    }
    return (React__default.createElement(WACButtonItemComponent, { imageURL: image_url, altText: alt_text, label: label, kind: kind, url: url, target: target, renderIcon: Launch }));
}

/**
 * This component determines the button response type to render.
 */
function ButtonItemComponent(props) {
    switch (props.localMessageItem.item.button_type) {
        case ButtonItemType.URL:
            return React__default.createElement(ButtonItemURLComponent, { localMessageItem: props.localMessageItem });
        case ButtonItemType.SHOW_PANEL:
            return (React__default.createElement(ButtonItemShowPanelComponent, { localMessageItem: props.localMessageItem, isMessageForInput: props.isMessageForInput }));
        case ButtonItemType.CUSTOM_EVENT:
            return (React__default.createElement(ButtonItemCustomEventComponent, { localMessageItem: props.localMessageItem, fullMessage: props.fullMessage }));
        default:
            return (React__default.createElement(ButtonItemPostBackComponent, { localMessageItem: props.localMessageItem, requestFocus: props.requestFocus, isMessageForInput: props.isMessageForInput }));
    }
}

/**
 * This component handles rendering nested messages for response types that have a "body" property.
 */
function BodyMessageComponents(props) {
    const { bodyLocalMessageItemIDs } = props.message.ui_state;
    const allMessageItemsByID = useSelector((state) => state.allMessageItemsByID);
    // Loop through the list of supported local message ids and return a message component for each.
    const messageComponents = bodyLocalMessageItemIDs?.map((nestedMessageID, index) => {
        const nestedLocalMessage = allMessageItemsByID[nestedMessageID];
        const isFullWidthMessage = isFullWidthResponseType(nestedLocalMessage.item.response_type);
        // Determine if the next message is a full width message, or not. Messages next to each other that aren't full
        // width should have small bottom padding separating them.
        const nextLocalMessageID = bodyLocalMessageItemIDs[index + 1];
        const nextLocalMessage = allMessageItemsByID[nextLocalMessageID];
        const isNextMessageFullWidth = isFullWidthResponseType(nextLocalMessage?.item.response_type);
        const isLastElement = index === bodyLocalMessageItemIDs.length - 1;
        const withShortBottomPadding = !isLastElement && !isFullWidthMessage && !isNextMessageFullWidth;
        return (React__default.createElement("div", { key: nestedMessageID, className: cx('WACBodyMessageComponents__MessageWrapper', {
                'WACBodyMessageComponents__MessageWrapper--fullWidth': isFullWidthMessage,
                'WACBodyMessageComponents__MessageWrapper--shortBottomPadding': withShortBottomPadding,
            }) },
            React__default.createElement(MessageTypeComponent, { ...props, message: nestedLocalMessage, isNestedMessageItem: true })));
    });
    if (!messageComponents?.length) {
        return null;
    }
    return React__default.createElement("div", { className: "WACBodyMessageComponents" }, messageComponents);
}
/**
 * Determines if the given response type should be rendered in full width.
 */
function isFullWidthResponseType(responseType) {
    switch (responseType) {
        case MessageResponseTypes.IMAGE:
        case MessageResponseTypes.IFRAME:
        case MessageResponseTypes.VIDEO:
        case MessageResponseTypes.AUDIO:
        case MessageResponseTypes.USER_DEFINED:
            return true;
        default:
            return false;
    }
}
const BodyMessageTypeComponentsExport = React__default.memo(BodyMessageComponents);

/**
 * This component handles rendering button response types in the footer of a container, under "body" content. See
 * {@link #BodyMessageComponents}.
 */
function FooterButtonComponents(props) {
    const allMessageItemsByID = useSelector((state) => state.allMessageItemsByID);
    const buttonComponents = props.message.ui_state.footerLocalMessageItemIDs?.map(nestedMessageID => {
        const nestedLocalMessage = allMessageItemsByID[nestedMessageID];
        return React__default.createElement(MessageTypeComponent, { key: nestedMessageID, ...props, message: nestedLocalMessage, isNestedMessageItem: true });
    });
    const totalButtons = buttonComponents?.length ?? 0;
    const isColumnList = totalButtons > 2;
    return (React__default.createElement(React__default.Fragment, null, Boolean(totalButtons) && (React__default.createElement("div", { className: cx('WACFooterButtonComponents', { 'WACFooterButtonComponents--column': isColumnList }) }, buttonComponents))));
}

/**
 * This component handles rendering nested message items for response types with "body" and "footer" support.
 */
function BodyWithFooterComponent({ localMessageItem, fullMessage, isMessageForInput, requestFocus, }) {
    const serviceManager = useServiceManager();
    const languagePack = useLanguagePack();
    const appConfig = useSelector((state) => state.config);
    const inputState = useSelector(selectInputState);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(BodyMessageTypeComponentsExport, { message: localMessageItem, originalMessage: fullMessage, languagePack: languagePack, toolingType: appConfig.public.__ibm__.toolingType, requestInputFocus: requestFocus, disableUserInputs: inputState.isReadonly, config: appConfig, isMessageForInput: isMessageForInput, scrollElementIntoView: THROW_ERROR, serviceManager: serviceManager, hideFeedback: true, allowNewFeedback: false }),
        React__default.createElement(FooterButtonComponents, { message: localMessageItem, originalMessage: fullMessage, languagePack: languagePack, toolingType: appConfig.public.__ibm__.toolingType, requestInputFocus: requestFocus, disableUserInputs: inputState.isReadonly, config: appConfig, isMessageForInput: isMessageForInput, scrollElementIntoView: THROW_ERROR, serviceManager: serviceManager, hideFeedback: true, allowNewFeedback: false })));
}

/**
 * This component renders the card response type. A card can be used to author a custom card containing existing
 * response types.
 */
function CardItemComponent(props) {
    const { ignoreMaxWidth } = props;
    const item = props.localMessageItem.item;
    return (React__default.createElement(Tile, { className: cx('WACCardMessageComponent', {
            WACMaxWidthSmall: !ignoreMaxWidth && item.max_width === WidthOptions.SMALL,
            WACMaxWidthMedium: !ignoreMaxWidth && item.max_width === WidthOptions.MEDIUM,
            WACMaxWidthLarge: !ignoreMaxWidth && item.max_width === WidthOptions.LARGE,
        }) },
        React__default.createElement(BodyWithFooterComponent, { ...props })));
}
const CardComponentExport = React__default.memo(CardItemComponent);

const Carousel$2 = lazyCarousel();
function CarouselItemComponent(props) {
    const { localMessageItem, fullMessage, isMessageForInput, requestFocus } = props;
    const allMessageItemsByID = useSelector((state) => state.allMessageItemsByID);
    const { itemsLocalMessageItemIDs } = localMessageItem.ui_state;
    return (React__default.createElement(Suspense, { fallback: React__default.createElement(SkeletonPlaceholder, null) },
        React__default.createElement(Carousel$2, null, itemsLocalMessageItemIDs.map(nestedLocalMessageItemID => {
            const localMessageItem = allMessageItemsByID[nestedLocalMessageItemID];
            return (React__default.createElement(CardComponentExport, { key: nestedLocalMessageItemID, localMessageItem: localMessageItem, fullMessage: fullMessage, isMessageForInput: isMessageForInput, ignoreMaxWidth: true, requestFocus: requestFocus }));
        }))));
}

/**
 * This hooks will observe the given value and will call the given function when the chunks have changed.
 */
function useCallbackOnChange(value, callback) {
    const doDebounced = useMemo(() => {
        return callback && debounce(callback, 100, { maxWait: 100, leading: true });
    }, [callback]);
    if (usePrevious(value) !== value && callback) {
        setTimeout(doDebounced);
    }
}

function useWindowSize() {
    return useContext(WindowSizeContext);
}

function getSearchResultURLMetaData(searchResult) {
    let { url } = searchResult;
    url = url ? convertPossibleStringifiedArrayToFirstString(url) : undefined;
    const urlIsValid = url && isValidURL(url);
    return {
        url,
        urlIsValid,
    };
}
function getSearchResultMetaData(searchResult) {
    // First we check the searchResults.highlight.title which should be an array. If that is not set, we fall back to
    // searchResult.title which is either a string OR a stringified array. If it is the latter, we only want the first
    // item in the stringified array. https://github.ibm.com/watson-engagement-advisor/wea-backlog/issues/31262 has an
    // explanation.
    let title = searchResult.highlight?.title?.[0]
        ? stripHighlightsFromString(convertToEmptyStringIfStringifiedNull(searchResult.highlight.title[0]))
        : convertPossibleStringifiedArrayToFirstString(convertToEmptyStringIfStringifiedNull(searchResult.title));
    const { url, urlIsValid } = getSearchResultURLMetaData(searchResult);
    // If title is null and url is valid, use the URL in place of title see bug
    // https://github.ibm.com/Watson-Discovery/customer-care/issues/297.
    let titleIsURL = false;
    if (!title && urlIsValid) {
        title = url;
        titleIsURL = true;
    }
    let answer;
    // If answers param is present, use it on header and render searchResult.highlight.body as a body.
    if (searchResult.answers?.length > 0) {
        answer = searchResult.answers?.[0].text;
    }
    return { title, titleIsURL, url, urlIsValid, answer };
}
/**
 * Under the covers before it gets to us, a title/body may have been a stringified array. We need to remove the [""]
 * if that is the case.
 *
 * @param str A string for the title or body of the search card.
 */
function convertPossibleStringifiedArrayToFirstString(str) {
    if (typeof str === 'string' && str.startsWith('["') && str.endsWith('"]')) {
        try {
            [str] = JSON.parse(str);
        }
        catch (error) {
            // Not an array of strings, so do nothing.
        }
    }
    return str;
}
/**
 * Removes the highlights from the string in this case mostly from title field as it does not add any value in
 * the single line ellipses title.
 *
 * @param str A string for the title of the search card.
 */
function stripHighlightsFromString(str) {
    return str ? str.replace(new RegExp('<em>|</em>', 'g'), '') : null;
}
/**
 * Returns the label that should be used on a link intended to open a reference search document from a search result.
 */
function getSearchLinkLabel(title, url, titleIsURL) {
    let label;
    if (title && titleIsURL) {
        // If the title is a URL, then extract the filename from it.
        label = getFileName(title);
    }
    else if (!title && url) {
        // If there is no title, but we do have an url, then extract the filename from that.
        label = getFileName(url);
    }
    // Try to return something.
    label = label || title || url;
    return label;
}
/**
 * Determines if we should show a button for opening the PDF viewer for viewing the document at the given URL. This
 * will determine if the URL appears to reference a PDF document and if the device and window size will support the
 * viewer.
 */
function shouldShowAsPDFButton(url, windowWidth, disablePDFViewer) {
    if (!url || disablePDFViewer) {
        return false;
    }
    // If the window is too small, the PDF viewer doesn't work very well so just leave this as a regular link that
    // will open using the browser's native capabilities.
    const filename = getFileName(url);
    return !IS_MOBILE && windowWidth > MIN_PDF_VIEWER_WIDTH && filename?.toLowerCase()?.endsWith('.pdf');
}
/**
 * Parses out the filename from the given URL (the last element of the path) if there is one. This will only return
 * a filename is the last element of the path contains an extension.
 */
function getFileName(url) {
    try {
        const parsed = new URL(url);
        const pathname = parsed?.pathname;
        if (pathname) {
            const separator = pathname.lastIndexOf('/');
            if (separator !== -1) {
                const filename = pathname.substring(separator + 1);
                if (filename.indexOf('.') > 0) {
                    // Only return the filename if it has some extension.
                    return decodeURI(filename);
                }
            }
        }
    }
    catch (error) {
        // Ignore.
    }
    return null;
}

/**
 * Returns a {@link ReactNode} that represents the given text with `<em>` tags in the string replaced with actual
 * JSX elements to form highlighted portions.
 */
function formatHighlightFields(str) {
    const strArray = str.split(new RegExp('<em>|</em>', 'g'));
    // If there is an odd number of '<em>' separators, then just return the whole string, minus the '<em>' elements.
    if (isOdd(strArray.length)) {
        return [strArray.join('')];
    }
    return strArray.map((strSegment, index) => {
        if (isOdd(index)) {
            return strSegment;
        }
        return (
        // eslint-disable-next-line react/no-array-index-key
        React__default.createElement("em", { key: index, className: "WAC__highlight" }, strSegment));
    });
}
function shouldTruncateBody(searchResult, charactersForEllipsis) {
    return searchResult.highlight.body[0].length > charactersForEllipsis;
}
function hasSearchResultBody(searchResult) {
    if (searchResult.highlight?.body?.[0]) {
        return Boolean(formatHighlightFields(convertToEmptyStringIfStringifiedNull(searchResult.highlight?.body[0])));
    }
    return false;
}
/**
 * Determine which element to render as the body and the amount of text that will be displayed in it.
 * Always use highlight.body, Use body as a fallback based on certain conditions
 * This was driven by https://github.ibm.com/Watson-Discovery/customer-care/issues/660
 * We check the searchResults.highlight.body which should be an array. If that is not set, we fall back to
 * searchResult.body which is either a string OR a stringified array. If it is the latter, we only want the first
 * item in the stringified array. https://github.ibm.com/watson-engagement-advisor/wea-backlog/issues/31262 has an
 * explanation.
 */
function SearchResultBody({ searchResult }) {
    if (searchResult && 'highlight' in searchResult) {
        if (searchResult.highlight?.body?.[0]) {
            return formatHighlightFields(convertToEmptyStringIfStringifiedNull(searchResult.highlight.body[0]));
        }
    }
    if (searchResult && 'body' in searchResult && searchResult.body) {
        return convertPossibleStringifiedArrayToFirstString$1(convertToEmptyStringIfStringifiedNull(searchResult.body));
    }
    return null;
}
const SearchResultBodyExport = React__default.memo(SearchResultBody);
/**
 * In conversational search citation panels we show the search result body instead of the citation text or highlight
 * body because it will contain the most context for the user. This will only be used if there is no url or
 * pdf attached to the source, so the assumption is that the data will be nicely formatted from document ingestion
 * instead of a web crawler. We also make sure to highlight the citation text within the search result body to help the
 * user find the citation.
 */
function SearchResultBodyWithCitationHighlighted({ relatedSearchResult, citationItem, }) {
    const elementsArray = [];
    let searchString;
    let citationString;
    if (relatedSearchResult?.body) {
        const searchStringFromBody = convertPossibleStringifiedArrayToFirstString$1(convertToEmptyStringIfStringifiedNull(relatedSearchResult.body));
        // Search result body's can contain <em> and </em> tags which need to be removed. After remove the em tags, it
        // should be safe to assume that the citation text is a direct substring of either the search_result body or title.
        searchString = searchStringFromBody.replace('<em>', '').replace('</em>', '');
    }
    if (citationItem?.text) {
        citationString = convertPossibleStringifiedArrayToFirstString$1(convertToEmptyStringIfStringifiedNull(citationItem.text));
    }
    if (searchString && citationString) {
        const startOfCitation = searchString.indexOf(citationString);
        // If the citation string is not within the search string from the search_result body than the citation was from the
        // search_result title which doesn't get this highlight treatment.
        if (startOfCitation !== -1) {
            // Add the text prior to the citation to the array.
            elementsArray.push(React__default.createElement("span", { key: 1 }, searchString.substring(0, startOfCitation)));
            // Add the highlighted citation text to the array.
            elementsArray.push(React__default.createElement("em", { key: 2, className: "WAC__highlight" }, searchString.substring(startOfCitation, startOfCitation + citationString.length)));
            // Add the text after the citation to the array.
            elementsArray.push(React__default.createElement("span", { key: 3 }, searchString.substring(startOfCitation + citationString.length)));
        }
    }
    if (elementsArray.length) {
        // If we had a search string and a citation string then we were able to form a highlighted search body which should
        // be used.
        return elementsArray;
    }
    if (searchString.length) {
        // If we couldn't form a highlighted search body then just use the search string. This could happen if the citation
        // string was in the title of the search_result instead of in the body.
        return [React__default.createElement("span", null, searchString)];
    }
    // If for some reason we couldn't create a search string then use the citation string.
    return [React__default.createElement("span", null, citationString)];
}
const SearchResultBodyWithCitationHighlightedExport = React__default.memo(SearchResultBodyWithCitationHighlighted);

function CitationCardContent({ citation, type, setIsExpandable, isExpandable }) {
    const languagePack = useLanguagePack();
    const { width } = useWindowSize();
    const { conversationalSearch_viewPDF, conversationalSearch_viewSourceDocument } = languagePack;
    const ref = useRef(null);
    // If citation has a "text" property, we know its from conversational search. If not, its legacy search and needs to
    // be processed differently.
    const textContent = 'text' in citation && citation.text;
    const content = textContent || React__default.createElement(SearchResultBodyExport, { searchResult: citation });
    // Checks if there is any need to allow expanding on the title. Watching window size changes as a clunky attempt to
    // deal with re-sizes, but that isn't really perfect.
    useLayoutEffect(() => {
        // If the tile is not currently expandable then check if there's enough content for the tile to expand.
        if (ref.current && !isExpandable && setIsExpandable) {
            if (ref.current.clientHeight && ref.current.scrollHeight) {
                setIsExpandable(ref.current.clientHeight < ref.current.scrollHeight);
            }
        }
    }, [textContent, isExpandable, setIsExpandable, width]);
    let label;
    let icon;
    if (type === CitationType.PDF) {
        label = conversationalSearch_viewPDF;
        icon = React__default.createElement(DocumentPdf, { size: 16 });
    }
    else if (type === CitationType.URL && citation.url) {
        label = new URL(citation.url).hostname;
        icon = React__default.createElement(Link$1, { size: 16 });
    }
    else if (isExpandable) {
        icon = React__default.createElement(Maximize, { size: 16 });
        label = conversationalSearch_viewSourceDocument;
    }
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("div", { className: "WACCitationCard_Header" },
            React__default.createElement("div", { className: "WACCitationCard_Title WACWidget__textEllipsis" }, citation.title),
            React__default.createElement("div", { ref: ref, className: "WACCitationCard_Text" }, content)),
        React__default.createElement("div", { className: "WACCitationCard_Footer" }, (label || icon) && (React__default.createElement(React__default.Fragment, null,
            React__default.createElement("div", { className: "WACCitationCard_Label WACWidget__textEllipsis" }, label),
            React__default.createElement("div", { className: "WACCitationCard_Icon" }, icon))))));
}

function CitationClickableCard({ title, onClick, onSelectCitation, children, className }) {
    return (React__default.createElement("button", { type: "button", className: `${className} WACCitationCard--clickable`, "aria-label": title, onClick: () => {
            onClick();
            onSelectCitation?.();
        }, onFocus: onSelectCitation }, children));
}

function ExpandToPanelCard({ className, citation, onSelectCitation, relatedSearchResult }) {
    const serviceManager = useServiceManager();
    const { title } = getSearchResultMetaData(citation);
    // If there's a searchResult that isn't empty than the card is expandable. Otherwise The CitationCardContent component
    // will measure itself with the citation text as its content and will let this component know if it can fit the
    // contents in the tile. If it can't fit the contents then it needs to be a clickable tile that can expand into a
    // panel.
    const [isExpandable, setIsExpandable] = useState(Boolean(relatedSearchResult?.body));
    function onViewSourcePanelButtonClick() {
        // If a search result is provided we want to show that in the panel with the citation text highlighted, otherwise
        // just show the citation.
        serviceManager.store.dispatch(actions.setViewSourcePanelIsOpen(true, citation, relatedSearchResult));
    }
    function renderTile(className) {
        return (React__default.createElement(Tile, { className: className },
            React__default.createElement(CitationCardContent, { citation: citation, type: CitationType.EXPAND_IF_NEEDED, setIsExpandable: setIsExpandable, isExpandable: isExpandable })));
    }
    if (isExpandable) {
        return (React__default.createElement(CitationClickableCard, { className: className, title: title, onClick: onViewSourcePanelButtonClick, onSelectCitation: onSelectCitation }, renderTile()));
    }
    return renderTile(className);
}

function LoadingPDFViewer() {
    const languagePack = useLanguagePack();
    return (React__default.createElement(ModalPortal, null,
        React__default.createElement("div", { className: "WACPDFViewer", role: "dialog", "aria-label": languagePack.documentViewer_ariaRoleLabel },
            React__default.createElement("div", { className: "WACBackgroundCover" }),
            React__default.createElement("div", { className: "WACPDFViewer__Inner" },
                React__default.createElement("div", { className: "WACPDFViewer__Document" },
                    React__default.createElement("div", { className: "WACPDFViewer__Loading" },
                        React__default.createElement(Loading, { withOverlay: false })))))));
}

const PDFViewerContainer$1 = lazyPDFViewer();
function PDFCard({ className, citation, children, onSelectCitation }) {
    const { title, url, titleIsURL } = getSearchResultMetaData(citation);
    const label = getSearchLinkLabel(title, url, titleIsURL);
    const [showPDFViewer, setShowPDFViewer] = useState(false);
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement(CitationClickableCard, { className: className, onClick: () => {
                setShowPDFViewer(true);
            }, onSelectCitation: onSelectCitation, title: label },
            React__default.createElement(Tile, null, children)),
        showPDFViewer && (React__default.createElement(Suspense, { fallback: React__default.createElement(LoadingPDFViewer, null) },
            React__default.createElement(PDFViewerContainer$1, { key: url, url: url, title: label, onClose: () => setShowPDFViewer(false) })))));
}

/**
 * This component takes a ConversationalSearchItemCitation OR a SearchResult and decides which kind of tile to display
 * it in.
 */
var CitationType;
(function (CitationType) {
    /**
     * If the citation has a url that isn't a pdf.
     */
    CitationType["URL"] = "url";
    /**
     * If the citation is a pdf that will be using the pdf viewer.
     */
    CitationType["PDF"] = "pdf";
    /**
     * If the citation has no url, if its full contents don't fit in the card, you can click on it to see a panel with the results.
     */
    CitationType["EXPAND_IF_NEEDED"] = "expand";
})(CitationType || (CitationType = {}));
function CitationCard({ citation, isSelected, onSelectCitation, relatedSearchResult }) {
    const windowWidth = useWindowSize().width;
    const disablePDFViewer = useSelector((state) => state.config.public.disablePDFViewer);
    const { url, urlIsValid } = getSearchResultMetaData(citation);
    const showAsPDFButton = shouldShowAsPDFButton(url, windowWidth, disablePDFViewer);
    function getType() {
        if (showAsPDFButton) {
            return CitationType.PDF;
        }
        if (url && urlIsValid) {
            return CitationType.URL;
        }
        return CitationType.EXPAND_IF_NEEDED;
    }
    const type = getType();
    const className = cx('WACCitationCard', {
        'WACCitationCard--selected': isSelected,
        'WACCitationCard--clickable': type === CitationType.URL,
        'WACCitationCard--url': type === CitationType.URL,
        'WACCitationCard--no-url': type !== CitationType.URL,
    }, 'WACWidget__textEllipsis');
    if (type === CitationType.PDF) {
        return (React__default.createElement(PDFCard, { citation: citation, className: className, onSelectCitation: onSelectCitation },
            React__default.createElement(CitationCardContent, { citation: citation, type: type })));
    }
    if (type === CitationType.URL) {
        return (
        // eslint-disable-next-line jsx-a11y/control-has-associated-label
        React__default.createElement("a", { className: className, href: url, target: "_blank", rel: "noopener noreferrer", onClick: onSelectCitation, onFocus: onSelectCitation },
            React__default.createElement(Tile, null,
                React__default.createElement(CitationCardContent, { citation: citation, type: type }))));
    }
    return (React__default.createElement(ExpandToPanelCard, { citation: citation, className: className, onSelectCitation: onSelectCitation, relatedSearchResult: relatedSearchResult }));
}
const CitationCardExport = React__default.memo(CitationCard);

let nextNumber = 1;
/**
 * A hook that returns a counter that increases by one for each component it is used in.
 */
function useCounter() {
    const counterRef = useRef();
    if (counterRef.current === undefined) {
        counterRef.current = nextNumber++;
    }
    return counterRef.current;
}

/**
 * Just a simple component that mounts to a host element.
 */
function PortalComponent({ hostElement, children }) {
    return hostElement ? ReactDOM__default.createPortal(children, hostElement) : null;
}

const TOGGLE_CONTAINER_CLASSNAME = 'WACConversationalSearchText__CitationsToggleContainer';
function ConversationalSearchText(props, ref) {
    const { highlightCitation, onToggleCitations, citationsOpen, searchItem, showCitationsToggle } = props;
    const languagePack = useLanguagePack();
    const serviceManager = useServiceManager();
    const { streamingState } = searchItem.ui_state;
    const toggleID = `WACConversationalSearchText-${useCounter()}${serviceManager.namespace.suffix}`;
    const [toggleContainer, setToggleContainer] = useState();
    const rootRef = useRef();
    let text;
    if (streamingState && !streamingState.isDone) {
        text = streamingState.chunks.map(chunk => chunk.text).join('');
    }
    else {
        text = searchItem.item.text;
    }
    const html = useMemo(() => createHTMLWithHighlights(text, highlightCitation, showCitationsToggle), [text, highlightCitation, showCitationsToggle]);
    useImperativeHandle(ref, () => ({
        /**
         * Use the root element to query the citations toggle button because the OperationalTag does not use forwardRef so
         * we have to rely on fetching the element manually.
         */
        getToggleCitationsElement: () => rootRef.current?.querySelector('.cds--tag--operational'),
    }));
    useLayoutEffect(() => {
        const container = rootRef.current?.querySelector(`.${TOGGLE_CONTAINER_CLASSNAME}`);
        setToggleContainer(container);
    }, [html]);
    return (React__default.createElement("div", { className: "WACConversationalSearchText", ref: rootRef },
        React__default.createElement(MaybeDangerouslySetInnerHTMLExport, { html: html, overrideSanitize: false /* The html is sanitized already in createHTMLWithHighlights. */ }),
        toggleContainer && (
        // Use a portal to attach the toggle button to its container element in the search result.
        React__default.createElement(PortalComponent, { hostElement: toggleContainer },
            React__default.createElement(React__default.Fragment, null,
                React__default.createElement("span", { className: "WACConversationalSearchText__CitationsToggle" },
                    React__default.createElement(OperationalTag, { id: toggleID, onClick: onToggleCitations, "aria-expanded": citationsOpen, 
                        // The text prop is required and Carbon will complain if this prop is not defined, but if it's an empty
                        // string it's ok.
                        text: "", renderIcon: citationsOpen ? ChevronUp : ChevronDown, "aria-label": languagePack.conversationalSearch_toggleCitations })),
                React__default.createElement("span", null, "\u00A0"))))));
}
const HIGHLIGHT_TOKEN_START = '@@:wc-source:@@';
const HIGHLIGHT_TOKEN_END = '@@/:wc-source:@@';
const HIGHLIGHT_TOKEN_REGEXP = /@@\/?:wc-source:@@/g;
/**
 * Takes the given text as markdown and converts it to html with the given citation highlighted in the html. This
 * will insert span elements into the html that represent the text ranges present in the citation.
 *
 * @param text The text to convert to html and highlight.
 * @param highlightCitation The citation that indicates what should be highlighted.
 * @param showCitationsToggle Indicates if the citations toggle should be displayed. If so, this code will examine
 * the html content to determine where it should be shown and will insert an element at that location that can be
 * found later.
 */
function createHTMLWithHighlights(text, highlightCitation, showCitationsToggle) {
    // Highlighting a citation is a bit messy. The back-end provides us with text ranges in the original search result
    // but those ranges don't pay attention to the structure of the content and thus it's possible for a range to
    // cross boundaries of structures in the rich text. For example, it's possible for a range to start half-way
    // through a paragraph and extend down to the middle point of a list item in a list below (although something so
    // crazy is probably not actually going to happen). To address this, we need to process all the rich content into the
    // entire tree of DOM nodes and then highlight every individual text (leaf) node that comes between the start of the
    // range and the end of the range.
    //
    // When highlighting a citation, this code will first inject tokens into the markdown at the start and end of each
    // highlight range. Then the markdown is converted into html and the tokens should survive the process. This will
    // allow us to keep track of where the highlighting starts and ends. After that, the html is dropped into a DOM
    // element so the browser will parser it into a tree. Then we walk the element tree looking for text nodes that
    // contain the highlight tokens. We will then insert span elements as appropriate where each of those text nodes
    // appear in order to achieve highlight and remove the tokens. The spans are inserted inside of every node that is
    // highlighted which allows us to highlight a range that crosses block boundaries.
    let ranges = highlightCitation?.ranges;
    if (!ranges && highlightCitation?.range_start !== undefined) {
        // The API is changing from a singular range to an array of ranges. We're adding some temporary backwards
        // compatibility that will convert the old range into an array of one range.
        ranges = [{ start: highlightCitation.range_start, end: highlightCitation.range_end }];
    }
    if (ranges?.length) {
        // Inject tokens into the text at the boundaries of the text highlight.
        const pieces = [];
        let startIndex = 0;
        ranges.forEach(range => {
            const beforeHighlight = text.substring(startIndex, range.start);
            const highlight = text.substring(range.start, range.end);
            pieces.push(beforeHighlight);
            pieces.push(HIGHLIGHT_TOKEN_START);
            pieces.push(highlight);
            pieces.push(HIGHLIGHT_TOKEN_END);
            startIndex = range.end;
        });
        const lastPiece = text.substring(startIndex, text.length);
        pieces.push(lastPiece);
        text = pieces.join('');
    }
    const afterMarkdownHTML = sanitizeHTML(processMarkdown(text));
    if (ranges || showCitationsToggle) {
        try {
            // Now stuff the html into an element so we can walk through it looking for the tokens.
            const rootElement = document.createElement('div');
            rootElement.innerHTML = afterMarkdownHTML;
            if (ranges) {
                // Look for the highlight tokens and add highlights as necessary.
                insertHighlights(rootElement, false);
            }
            if (showCitationsToggle) {
                // Insert the container element for the citation toggle.
                const toggleContainer = document.createElement('span');
                toggleContainer.className = TOGGLE_CONTAINER_CLASSNAME;
                // Figure out what the last element is.
                const lastElement = rootElement.lastElementChild;
                const lastTag = lastElement?.tagName;
                if (lastTag === 'P') {
                    // Put the toggle inside the last paragraph so it'll be inline with that content.
                    lastElement.appendChild(document.createTextNode(' '));
                    lastElement.appendChild(toggleContainer);
                }
                else {
                    // Make it a sibling of the last content. This will cause the toggle to appear below as a block element.
                    rootElement.appendChild(toggleContainer);
                    toggleContainer.className += ` ${TOGGLE_CONTAINER_CLASSNAME}--block`;
                }
            }
            return rootElement.innerHTML;
        }
        catch (error) {
            consoleError('An error occurred processing source highlights.', text, highlightCitation, error);
        }
    }
    return afterMarkdownHTML;
}
/**
 * Inserts highlights into the given Node by looking for the highlight tokens in any text nodes located inside.
 *
 * @param parent The node is insert highlights into.
 * @param isInHighlight Indicates if a "begin highlight" token has already been seen previously and the content of this
 * Node start off highlighted.
 */
function insertHighlights(parent, isInHighlight) {
    const childNodes = Array.from(parent.childNodes);
    childNodes.forEach(child => {
        if (child instanceof Text) {
            const text = child.textContent;
            const tokenMatches = Array.from(text.matchAll(HIGHLIGHT_TOKEN_REGEXP));
            if (!tokenMatches.length && !isInHighlight) ;
            else {
                // We need to add some highlighting, so this child needs to be replaced by nodes that have highlighting. The
                // child may have multiple tokens so it may result in multiple nodes being added.
                let startIndex = 0;
                tokenMatches.forEach(match => {
                    // If we get an end token then all the text before is inside a highlight.
                    const isInHighlightHere = match[0] === HIGHLIGHT_TOKEN_END;
                    const textContent = text.substring(startIndex, match.index);
                    startIndex = match.index + match[0].length;
                    // Add the text for before this token.
                    addTextSegment(textContent, isInHighlightHere, parent, child);
                    // Highlighting will flip to the opposite value after this node until the next highlight token appears.
                    isInHighlight = !isInHighlightHere;
                });
                // If there is any text after the last token, we need to include that too.
                const lastText = text.substring(startIndex, text.length);
                addTextSegment(lastText, isInHighlight, parent, child);
                // Remove the child now that it was replaced.
                child.remove();
            }
        }
        else if (child instanceof Element) {
            // If our tokens somehow found their way into an attribute, remove them.
            child.getAttributeNames().forEach(name => {
                const value = child.getAttribute(name);
                if (value) {
                    child.setAttribute(name, value.replaceAll(HIGHLIGHT_TOKEN_REGEXP, ''));
                }
            });
        }
        // Recurse through the tree.
        isInHighlight = insertHighlights(child, isInHighlight);
    });
    return isInHighlight;
}
/**
 * Adds the given text as a child node of the given parent.
 *
 * @param text The text to add.
 * @param highlighted Indicates if the text should be highlighted.
 * @param parent The parent to add the text to.
 * @param beforeChild The existing child of the parent to insert the text before.
 */
function addTextSegment(text, highlighted, parent, beforeChild) {
    if (text) {
        const textNode = document.createTextNode(text);
        // Don't highlight empty node. This avoids us inserting spans where they're not allowed just because there was a
        // newline inserted into the document. This may occasionally cause the highlight to miss intended whitespace but
        // that should be rare and not important.
        if (highlighted && text.trim()) {
            // We need to insert a highlighted span containing this text.
            const highlightNode = document.createElement('span');
            highlightNode.className = `WACConversationalSearchText--hasCitation`;
            highlightNode.appendChild(textNode);
            parent.insertBefore(highlightNode, beforeChild);
        }
        else {
            parent.insertBefore(textNode, beforeChild);
        }
    }
}
const ConversationalSearchTextExport = React__default.memo(React__default.forwardRef(ConversationalSearchText));

const Carousel$1 = lazyCarousel();
function ConversationalSearch({ localMessageItem, scrollElementIntoView, isStreamingError, doAutoScroll, }) {
    const [selectedCitationIndex, setSelectedCitationIndex] = useState(0);
    const [citationsOpen, setCitationsOpen] = useState(false);
    const scrollIntoViewArea = useRef();
    const swiperRef = useRef();
    const languagePack = useLanguagePack();
    const conversationalSearchTextFunctionsRef = useRef();
    const messageItem = localMessageItem.item;
    const sortedCitations = useMemo(() => sortCitations(messageItem.citations), [messageItem.citations]);
    function scrollCitations() {
        // The large bottom padding is to push the citations panel above the suggestions button (if it happens to be
        // present). The larger top padding also ensures the toggle button (which is just above the carousel) is also
        // still in view.
        setTimeout(() => scrollElementIntoView(scrollIntoViewArea.current, 32, 64));
    }
    function focusToggleButton() {
        setTimeout(() => conversationalSearchTextFunctionsRef.current.getToggleCitationsElement()?.focus());
    }
    // If the chunks change, kick off an auto-scroll.
    useCallbackOnChange(localMessageItem.ui_state.streamingState?.chunks, doAutoScroll);
    // Note: the AI button below has a specific component in the latest Carbon library we could swap in after we
    // upgrade our Carbon version.
    function onSelectCitation(index) {
        setCitationsOpen(true);
        setSelectedCitationIndex(index);
        setTimeout(() => swiperRef.current?.swiper.slideTo(index));
        scrollCitations();
    }
    function onToggleCitations() {
        setCitationsOpen(!citationsOpen);
        if (!citationsOpen) {
            scrollCitations();
            focusToggleButton();
        }
    }
    function renderCitations() {
        const tiles = sortedCitations?.map((citation, index) => (React__default.createElement(CitationCardExport
        // eslint-disable-next-line react/no-array-index-key
        , { 
            // eslint-disable-next-line react/no-array-index-key
            key: index, citation: citation, isSelected: index === selectedCitationIndex, onSelectCitation: () => onSelectCitation(index), 
            // If there is no url or pdf on the citation than the citation will open a panel to show more information. The
            // citation should include a search_result_idx that we can use that to get the corresponding search_result from
            // the search_results array. We then use this search result in the panel to give the user extra context about
            // the citation they clicked on.
            relatedSearchResult: messageItem.search_results?.[citation.search_result_idx] })));
        return (React__default.createElement("div", { className: "WACConversationalSearch_Citations" },
            React__default.createElement(Suspense, { fallback: React__default.createElement(SkeletonPlaceholder, null) },
                React__default.createElement(Carousel$1, { swiperRef: swiperRef, initialSlide: selectedCitationIndex, onSlideChange: setSelectedCitationIndex }, tiles))));
    }
    return (React__default.createElement("div", { className: "WACConversationalSearch" },
        React__default.createElement("div", { className: "ibm-web-chat--default-styles" },
            React__default.createElement(ConversationalSearchTextExport, { ref: conversationalSearchTextFunctionsRef, searchItem: localMessageItem, showCitationsToggle: Boolean(sortedCitations?.length), highlightCitation: citationsOpen ? sortedCitations?.[selectedCitationIndex] : null, onToggleCitations: onToggleCitations, citationsOpen: citationsOpen }),
            isStreamingError && React__default.createElement(InlineError, { text: languagePack.conversationalSearch_streamingIncomplete })),
        React__default.createElement("div", { ref: scrollIntoViewArea }, citationsOpen && renderCitations())));
}
/**
 * Sorts the given array of citations. This will move any citation without any highlight ranges to the end of the array.
 */
function sortCitations(citations) {
    if (!citations) {
        return null;
    }
    const withRanges = citations.filter(citation => citation.ranges?.length);
    const withoutRanges = citations.filter(citation => !citation.ranges?.length);
    return withRanges.concat(withoutRanges);
}

function UserDefinedResponse(props) {
    const { doAutoScroll, isStreamingError, streamingState, serviceManager } = props;
    const languagePack = useLanguagePack();
    const useShadowRoot = serviceManager.store.getState().config.public.__ibm__?.useShadowRoot;
    // The element that was previously created that we'll attach to this React component. The custom code should
    // already have attached its own element to this element that contains the custom rendering for the message.
    const userDefinedRegistryItem = serviceManager.actions.getOrCreateUserDefinedElement(props.localMessageID);
    const reactHostRef = useRef();
    useCallbackOnChange(streamingState?.chunks, doAutoScroll);
    // Attach the host element to the ref for this component.
    useLayoutEffect(() => {
        if (!useShadowRoot) {
            const hostElement = reactHostRef.current;
            if (userDefinedRegistryItem.element) {
                hostElement.appendChild(userDefinedRegistryItem.element);
                return () => {
                    hostElement.removeChild(userDefinedRegistryItem.element);
                };
            }
        }
        return undefined;
    });
    // If we are in shadowRoot mode, we render the content into a slot.
    if (useShadowRoot) {
        return (React__default.createElement("div", { className: "WAC__message-userDefinedResponse", "data-floating-menu-container": true },
            React__default.createElement("slot", { name: userDefinedRegistryItem.slotName }),
            isStreamingError && React__default.createElement(InlineError, { text: languagePack.conversationalSearch_streamingIncomplete })));
    }
    return (React__default.createElement(React__default.Fragment, null,
        React__default.createElement("div", { ref: reactHostRef, className: "ibm-web-chat--reset-styles WAC__message-userDefinedResponse", "data-floating-menu-container": true }),
        isStreamingError && React__default.createElement(InlineError, { text: languagePack.conversationalSearch_streamingIncomplete })));
}
var UserDefinedResponse$1 = React__default.memo(UserDefinedResponse);

// A regex object that checks for white space/right-left-mark characters in a string and a period at the end of a
// string.
const UNWANTED_CHARACTERS_REGEX = new RegExp(`[ ${RIGHT_TO_LEFT_MARK}]|\\.$`, 'g');
/**
 * Returns the date format data that tells us the character separating the date values. The provided value can either
 * be a date format like "mm/dd/yyyy" or an actual date like "01/01/1997".
 *
 * @param value The value to use to get format data.
 */
function sanitizeDateFormat(value) {
    let format = value.replace(UNWANTED_CHARACTERS_REGEX, '');
    // If there is no mm token, this means the month if represented with just one m.
    if (!format.includes('mm')) {
        format = format.replace('m', 'mm');
    }
    // If there is no dd token, this means the month if represented with just one d.
    if (!format.includes('dd')) {
        format = format.replace('d', 'dd');
    }
    return format;
}
/**
 * Returns a date string from the provided date format using a date object. This function assumes the format tokens
 * include mm, dd, and yyyy.
 *
 * @param date A Date object.
 * @param format The date format to reference.
 */
function toUserDateFormat(date, format) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());
    return format.replace('dd', day).replace('mm', month).replace('yyyy', year);
}
/**
 * Returns a date string that is formatted in the manner that the assistant expects. This format is yyyy-mm-dd to
 * help avoid ambiguity with the meaning of the date.
 */
function toAssistantDateFormat(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear());
    return `${year}-${month}-${day}`;
}

/**
 * This component handles rendering a carbon date picker for the date response type. It handles sending the selected
 * date value as the standard ISO date format and ensuring the message request displays the user's input selection.
 */
function DatePickerComponent(props) {
    const { localMessage, disabled, scrollElementIntoView } = props;
    const serviceManager = useServiceManager();
    const intl = useIntl();
    const webChatLocale = useSelector((state) => state.locale);
    const originalMessage = useSelector((state) => state.allMessagesByID[localMessage.fullMessageID]);
    const showSuggestionsButton = useSelector((state) => state.alternateSuggestionsState.showButton);
    const uuidRef = useRef(uuid(UUIDType.MISCELLANEOUS));
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [userDisplayValue, setUserDisplayValue] = useState();
    const [flatpickrFormat, setFlatpickrFormat] = useState();
    const [userDisplayFormat, setUserDisplayFormat] = useState();
    const [flatpickrLocale, setFlatpickrLocale] = useState();
    const [dayjsLocale, setDayjsLocale] = useState();
    const [datePickerHostElement, setDatePickerHostElement] = useState();
    const valueForAssistantRef = useRef();
    // This ref is to track when the user uses the date picker input through some pointer that's not a keyboard
    // (e.g. mouse, touch, etc.) to open the date picker. If the date picker is opened and the user's pointer is not
    // over the date picker input, such as when the date picker is scrolled into view, the date picker will close.
    // This is intentional behavior from the DatePicker component, so a way around this will be to scroll the calendar
    // into view after the user has lifted their pointer from the input and not when the calendar is opened.
    const isInputPointerDownEventFiredRef = useRef(false);
    const inputLabel = intl.formatMessage({ id: 'datePicker_chooseDate' }, { format: userDisplayFormat });
    const confirmButtonLabel = intl.formatMessage({ id: 'datePicker_confirmDate' });
    const isDateInfoReady = Boolean(flatpickrFormat && userDisplayFormat && flatpickrLocale && dayjsLocale);
    /**
     * Handles setting the necessary date info that will handle formatting the user's selected date value using the
     * provided locale.
     */
    function setDateInfoForLocale(locale) {
        // Get the date format for the given locale from dayjs, or default to the mm/dd/yyyy.
        const format = dayjs.Ls[locale]?.formats?.L?.toLocaleLowerCase() || ENGLISH_US_DATE_FORMAT;
        const dateFormat = sanitizeDateFormat(format);
        setDayjsLocale(locale);
        setFlatpickrLocale(calcFlatpickrLocale(locale));
        setUserDisplayFormat(dateFormat);
        setFlatpickrFormat(getFlatpickrDateFormat(dateFormat));
    }
    /**
     * When the user confirms their date selection we should send that date info to the assistant.
     */
    const handlerSendDate = useCallback(() => {
        const { ui_state, fullMessageID: responseID } = localMessage;
        const localMessageID = ui_state.id;
        const request = createMessageRequestForDate(valueForAssistantRef.current, userDisplayValue, responseID);
        serviceManager.actions.sendWithCatch(request, MessageSendSource.DATE_PICKER, {
            trackEventProps: {
                eventName: 'Assistant Message Received from User',
                type: 'datepicker',
            },
            setValueSelectedForMessageID: localMessageID,
        });
    }, [localMessage, serviceManager, userDisplayValue]);
    /**
     * Scrolls the date picker host element into view.
     */
    const doScrollElementIntoView = useCallback(() => {
        scrollElementIntoView(datePickerHostElement, 0, showSuggestionsButton ? 56 : 24);
    }, [datePickerHostElement, scrollElementIntoView, showSuggestionsButton]);
    useOnMount(() => {
        const localeFromMessage = originalMessage.context?.global?.system?.locale || webChatLocale;
        const { originalUserText } = localMessage.ui_state;
        const fromHistory = originalMessage.history.from_history;
        // If this message is from history and a user has made a previous selection, set the value in the input.
        if (fromHistory && originalUserText) {
            setUserDisplayValue(originalUserText);
        }
        try {
            // Load the date formats for the given locale if it was previously loaded.
            if (dayjs.Ls[localeFromMessage]) {
                setDateInfoForLocale(localeFromMessage);
            }
            else {
                loadDayjsLocale(localeFromMessage).then(locale => {
                    setDateInfoForLocale(locale);
                });
            }
        }
        catch {
            consoleError(`Locale ${dayjsLocale} is not recognized by web chat. Defaulting to English(US).`);
            setDateInfoForLocale('en');
        }
    });
    return (React__default.createElement("div", { className: "WACDatePicker" },
        isDateInfoReady && datePickerHostElement && (React__default.createElement(Layer, null,
            React__default.createElement(DatePicker, { className: "WACDatePicker__Calendar", datePickerType: "single", allowInput: false, locale: flatpickrLocale, appendTo: datePickerHostElement, onChange: dates => {
                    if (dates.length) {
                        const date = dates[0];
                        // The assistant should receive the date value in ISO format.
                        valueForAssistantRef.current = toAssistantDateFormat(date);
                        // Use the date object to get a date string in the expected format.
                        setUserDisplayValue(toUserDateFormat(date, userDisplayFormat));
                    }
                }, dateFormat: flatpickrFormat, onOpen: () => {
                    setIsCalendarOpen(true);
                    // The carbon date picker uses a "handleClickOutside" functionality to detect when the user has clicked
                    // outside of the component so that it will auto-close the calendar popup. There is a bug that occurs
                    // when the component scrolls at the same time the popup opens. If the user clicks on the picker input
                    // field, the mouse down part causes the field to get focus which causes the popup to open. We had code
                    // below that would then cause the chat to scroll which moved the input field so it was no longer under
                    // the mouse cursor. Unless the user does this very fast, the mouse up half of the click will not occur
                    // on the input field (because it moved) and the component detects this as a "click outside" and closes
                    // the popup. The popup can open either because the user clicks on the input field or when the field gets
                    // focus from keyboard input. With keyboard input, we want continue to scroll when the popup opens.
                    // However, if the user clicks on the input, we want to delay the scroll until the click is fully
                    // processed (this will ensure the cursor stays on the input field until the mouse up occurs).
                    if (isInputPointerDownEventFiredRef.current) {
                        isInputPointerDownEventFiredRef.current = false;
                    }
                    else {
                        doScrollElementIntoView();
                    }
                }, onClose: () => setIsCalendarOpen(false) },
                React__default.createElement(DatePickerInput, { id: uuidRef.current, labelText: inputLabel, placeholder: userDisplayFormat, disabled: disabled, 
                    // Set this prop value to an empty string. The component will set a default text telling the user to match
                    // the requests date format, which is useless since we don't allow the user to type a date.
                    title: "", 
                    // This event listener is fired before the DatePicker component's onOpen listener so we'll set the flag
                    // to prevent the onOpen listener from moving the input away from the user's pointer and close the
                    // calendar as result.
                    onPointerDown: () => {
                        isInputPointerDownEventFiredRef.current = true;
                    }, 
                    // This event listener gets fired once the user's pointer is lifted from the input which is when we
                    // scroll the calendar into view.
                    onClick: () => doScrollElementIntoView() })))),
        React__default.createElement("div", { className: "WACDatePicker__CalendarContainer", ref: setDatePickerHostElement }),
        !disabled && !isCalendarOpen && userDisplayValue && (React__default.createElement(Button, { className: "WACDatePicker__ConfirmButton", onClick: handlerSendDate, renderIcon: props => React__default.createElement(Checkmark, { size: 32, ...props }) }, confirmButtonLabel))));
}
/**
 * Returns an object of locales that are accepted as locale values for flatpickr library used in the carbon date picker
 * component and dayjs library.
 */
function calcFlatpickrLocale(localeValue) {
    // flatpickr does support the locale zh-tw, but it won't recognize it unless it has an underscore instead of a dash.
    if (localeValue === 'zh-tw') {
        return 'zh_tw';
    }
    // For the flatpickr library, if the value provided contains a region in the locale, only the language will be
    // returned since the library seems to mostly support locales without the region.
    //
    // flatpickr - https://github.com/flatpickr/flatpickr/tree/master/src/l10n
    return (localeValue.includes('-') ? localeValue.split('-')[0] : localeValue);
}
/**
 * Returns a date format that would be valid for the flatpickr library used in the carbon date picker component.
 */
function getFlatpickrDateFormat(format) {
    const dash = format.includes('-') ? '-' : '/';
    const firstChar = format.toLocaleLowerCase().trim()[0];
    if (firstChar === 'm') {
        return `m${dash}d${dash}Y`;
    }
    if (firstChar === 'd') {
        return `d${dash}m${dash}Y`;
    }
    if (firstChar === 'y') {
        return `Y${dash}m${dash}d`;
    }
    throw Error(`The provided format ${format} is invalid.`);
}
const DatePickerComponentExport = React__default.memo(DatePickerComponent);

// This regex is for validating a number (1) or pixel value (10px) which are the only valid width values for a column.
const VALID_COLUMN_WIDTH_REGEX = /^[0-9]*(px)?$/;
const DEFAULT_COLUMN_WIDTH = '1';
/**
 * This component renders the grid response type. It will handle creating the rows/columns and renders the response
 * types within them.
 */
function GridItemComponent({ localMessageItem, originalMessage, }) {
    const serviceManager = useServiceManager();
    const languagePack = useLanguagePack();
    const appConfig = useSelector((state) => state.config);
    const inputState = useSelector(selectInputState);
    const allMessageItemsByID = useSelector((state) => state.allMessageItemsByID);
    const { columns, horizontal_alignment, vertical_alignment, max_width } = localMessageItem.item;
    const gridTemplateColumns = columns?.map(column => column.width).join(' ') || DEFAULT_COLUMN_WIDTH;
    return (React__default.createElement("div", { className: cx('WACGrid', {
            WACMaxWidthSmall: max_width === WidthOptions.SMALL,
            WACMaxWidthMedium: max_width === WidthOptions.MEDIUM,
            WACMaxWidthLarge: max_width === WidthOptions.LARGE,
        }), style: { gridTemplateColumns } }, localMessageItem.ui_state.gridLocalMessageItemIDs.map((row, rowIndex) => (
    // eslint-disable-next-line react/no-array-index-key
    React__default.createElement("div", { key: `row-${rowIndex}`, className: "WACGrid__Row" }, row.map((cell, columnIndex) => {
        const cellData = localMessageItem.item.rows[rowIndex]?.cells[columnIndex];
        let columnWidthString = columns?.[columnIndex]?.width || DEFAULT_COLUMN_WIDTH;
        let isPixelValue;
        if (columnWidthString.match(VALID_COLUMN_WIDTH_REGEX)) {
            isPixelValue = columnWidthString.endsWith('px');
        }
        else {
            columnWidthString = DEFAULT_COLUMN_WIDTH;
            isPixelValue = false;
        }
        return (React__default.createElement("div", { className: "WACGrid__Cell", style: {
                width: isPixelValue ? columnWidthString : undefined,
                flex: isPixelValue ? undefined : Number(columnWidthString),
                alignItems: getFlexAlignment(cellData?.horizontal_alignment || horizontal_alignment),
                justifyContent: getFlexAlignment(cellData?.vertical_alignment || vertical_alignment),
            }, 
            // eslint-disable-next-line react/no-array-index-key
            key: `cell-${rowIndex}-${columnIndex}` }, cell.map((localMessageItemID, itemIndex) => {
            const message = allMessageItemsByID[localMessageItemID];
            return (React__default.createElement(MessageTypeComponent
            // eslint-disable-next-line react/no-array-index-key
            , { 
                // eslint-disable-next-line react/no-array-index-key
                key: `item-${rowIndex}-${columnIndex}-${itemIndex}`, message: message, originalMessage: originalMessage, languagePack: languagePack, toolingType: appConfig.public.__ibm__.toolingType, requestInputFocus: THROW_ERROR, disableUserInputs: inputState.isReadonly, config: appConfig, isMessageForInput: false, scrollElementIntoView: THROW_ERROR, serviceManager: serviceManager, isNestedMessageItem: true, hideFeedback: true, allowNewFeedback: false }));
        })));
    }))))));
}
/**
 * Returns the CSS flex alignment for the given horizontal/vertical alignment value;
 */
function getFlexAlignment(value) {
    switch (value) {
        case 'bottom':
        case 'right':
            return 'flex-end';
        case 'center':
            return 'center';
        case 'top':
        case 'left':
        default:
            return 'flex-start';
    }
}
const GridItemComponentExport = React__default.memo(GridItemComponent);

/**
 * The iframe preview card for the page source. This is a button that can be clicked in order to open the frame panel.
 */
function IFramePreviewCardComponent({ messageItem, doAutoScroll }) {
    const { source, image_url, title, description } = messageItem;
    const useAITheme = useSelector((state) => state.theme.useAITheme);
    const urlHostName = getURLHostName(source);
    const { store } = useServiceManager();
    const { iframe_ariaImageAltText } = useLanguagePack();
    const intl = useIntl();
    const iframeAriaClickPreviewCardMessage = intl.formatMessage({ id: 'iframe_ariaClickPreviewCard' }, { source: urlHostName });
    /**
     * Set iframe content to be loaded in the iframe panel.
     */
    function handleCardClick() {
        // If tanya has authored an iframe response type and has provided no page source, we don't want the preview card to
        // open the iframe panel.
        if (source) {
            store.dispatch(actions.setIFrameContent(messageItem));
        }
    }
    /**
     * Handles making the image element visible by updating the image loading flag and triggering an auto scroll.
     */
    function handleImageLoaded() {
        // Call doAutoScroll to account for the image container height change after the image has loaded.
        doAutoScroll?.();
    }
    return (React__default.createElement("div", null,
        React__default.createElement(ClickableImage, { title: title, description: description, source: image_url, displayURL: source, altText: iframe_ariaImageAltText, onImageLoad: handleImageLoaded, renderIcon: ArrowRight, onClick: handleCardClick, preventInlineError: true, useAITheme: useAITheme }),
        React__default.createElement(VisuallyHidden, null, iframeAriaClickPreviewCardMessage)));
}
const IFramePreviewCardExport = React__default.memo(IFramePreviewCardComponent);

/**
 * This component renders an iframe element for the iframe response type with preconfigured sandbox permissions.
 *
 * Here is a list of attributes of iframe sandbox
 * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/iframe#attr-sandbox
 *
 * allow-scripts: Lets the resource run scripts (but not create popup windows).
 * allow-download: Allows for downloads to occur with a gesture from the user.
 * allow-forms: Allows the resource to submit forms. If this keyword is not used, form submission is blocked.
 * allow-same-origin: Allows for the resource to access its own data storage/cookies and some JavaScript APIs
 *
 * Note: The iframe element will not fire the onerror event listener if it fails to load the source. It will still fire
 * onload, and so we aren't able to easily determine how to display an error message. We will have to rely on browsers
 * to display a native error message, which some browsers already do.
 */
function IFrameComponent({ title, source, onTimeoutOverride, onLoad }) {
    const { errors_iframeSource, iframe_ariaSourceLoaded } = useLanguagePack();
    const ariaAnnouncer = useAriaAnnouncer();
    const [showSpinner, setShowSpinner] = useState(true);
    const [showError, setShowError] = useState(false);
    /**
     * Hide the spinner amd show/announce the error message.
     */
    const handleIFrameTimeout = useCallback(() => {
        setShowSpinner(false);
        setShowError(true);
        ariaAnnouncer(errors_iframeSource);
    }, [ariaAnnouncer, errors_iframeSource]);
    /**
     * Hide the spinner and announce the iframe loaded once it's ready.
     */
    const handleIFrameLoaded = useCallback(() => {
        setShowSpinner(false);
        ariaAnnouncer(iframe_ariaSourceLoaded);
        onLoad?.();
    }, [ariaAnnouncer, iframe_ariaSourceLoaded, onLoad]);
    // This effect sets a timeout that auto error handles after 10 seconds of waiting for the React player to ready.
    // Once the player has loaded, the skeleton will be hidden, and we can clear the timeout.
    useEffect(() => {
        let errorTimeout = null;
        if (showSpinner) {
            const timeoutHandler = onTimeoutOverride || handleIFrameTimeout;
            errorTimeout = setTimeout(timeoutHandler, RESPONSE_TYPE_TIMEOUT_MS);
        }
        return () => {
            clearTimeout(errorTimeout);
        };
    }, [showSpinner, handleIFrameTimeout, onTimeoutOverride]);
    return (React__default.createElement("div", { className: "WACIFrameComponent__Wrapper" },
        showError && renderErrorMessage(errors_iframeSource),
        !showError && (React__default.createElement("iframe", { className: "WACIFrameComponent__IFrame", title: title, src: source, sandbox: "allow-scripts allow-downloads allow-forms allow-same-origin allow-popups", referrerPolicy: "origin", onLoad: handleIFrameLoaded })),
        showSpinner && renderLoadingSpinner()));
}
function renderLoadingSpinner() {
    return (React__default.createElement(MountChildrenOnDelay, null,
        React__default.createElement("div", { className: "WACIFrameComponent__StatusContainer" },
            React__default.createElement(Loading, { withOverlay: false }))));
}
function renderErrorMessage(errorText) {
    return (React__default.createElement("div", { className: "WACIFrameComponent__StatusContainer" },
        React__default.createElement(InlineError, { text: errorText })));
}

/**
 * This component renders an inline iframe for iframe response types using the chat display "inline" option.
 */
function InlineIFrame({ messageItem, doAutoScroll }) {
    const ariaAnnouncer = useAriaAnnouncer();
    const { errors_iframeSource } = useLanguagePack();
    const [isError, setIsError] = useState(false);
    const { source, title } = messageItem;
    const baseHeight = getMediaDimensions(messageItem)?.base_height;
    const paddingTop = getResponsiveElementPaddingValue(baseHeight);
    const iframeTitle = title || source;
    /**
     * Render an error message and announce it when the iframe component times out.
     */
    const onTimeoutOverride = useCallback(() => {
        setIsError(true);
        ariaAnnouncer(errors_iframeSource);
    }, [ariaAnnouncer, errors_iframeSource]);
    if (isError) {
        return React__default.createElement(InlineError, { text: errors_iframeSource });
    }
    return (React__default.createElement("div", { className: "WACInlineIFrame", style: { paddingTop } },
        React__default.createElement(IFrameComponent, { source: source, title: iframeTitle, onTimeoutOverride: onTimeoutOverride, onLoad: () => doAutoScroll?.() })));
}

function IFrameMessage({ doAutoScroll, localMessage, displayOverride }) {
    const { item } = localMessage;
    const itemDisplay = item.display || item.channel_options?.chat?.display;
    // Render an inline iframe if it was configured to do so or it's being rendered in a card.
    if (itemDisplay === IFrameItemDisplayOption.INLINE || displayOverride === IFrameItemDisplayOption.INLINE) {
        // The key prop is important since it causes the IFrame to re-mount when the source changes.
        return React__default.createElement(InlineIFrame, { key: item.source, doAutoScroll: doAutoScroll, messageItem: item });
    }
    return React__default.createElement(IFramePreviewCardExport, { doAutoScroll: doAutoScroll, messageItem: item });
}

function Description({ className, text, shouldRemoveHTMLBeforeMarkdownConversion = false, }) {
    return (React__default.createElement("div", { className: `WAC__description ${className}` },
        React__default.createElement(RichTextExport, { text: text, shouldRemoveHTMLBeforeMarkdownConversion: shouldRemoveHTMLBeforeMarkdownConversion })));
}

function Metablock({ title = null, description = null, id = null, shouldRemoveHTMLBeforeMarkdownConversion = false, }) {
    return title || description ? (React__default.createElement("div", { className: "WAC__received--metablock", id: id },
        title && (React__default.createElement(Description, { className: "WAC__received--metablock-content WACMetablock__Title", text: title, shouldRemoveHTMLBeforeMarkdownConversion: shouldRemoveHTMLBeforeMarkdownConversion })),
        description && (React__default.createElement(Description, { className: "WAC__received--metablock-content WACMetablock__Description", text: description, shouldRemoveHTMLBeforeMarkdownConversion: shouldRemoveHTMLBeforeMarkdownConversion })))) : null;
}

/**
 * Downshift doesn't play well with shadow dom OOTB, so we need to feed it a custom environment.
 */
function createProxyEnvironment(shadowRoot) {
    const properties = {
        document: shadowRoot.ownerDocument,
        addEventListener: shadowRoot.ownerDocument.addEventListener.bind(shadowRoot),
        removeEventListener: shadowRoot.ownerDocument.removeEventListener.bind(shadowRoot),
        Node,
    };
    return new Proxy(shadowRoot, {
        get: (_, prop) => properties[prop],
    });
}
function SelectComponent(props) {
    const { title, description, options, onChange, languagePack, disableUserInputs, serviceManager, shouldRemoveHTMLBeforeMarkdownConversion, } = props;
    const useShadowRoot = useSelector((state) => state.config.public.__ibm__?.useShadowRoot);
    const [isBeingOpened, setIsBeingOpened] = useState(false);
    const rootRef = useRef();
    // Generate a unique ID that we can use for each instance of our dropdowns.
    const counter = useCounter();
    const id = `${counter}${serviceManager.namespace.suffix}`;
    const environment = useShadowRoot && rootRef.current?.getRootNode
        ? createProxyEnvironment(rootRef.current.getRootNode())
        : undefined;
    function findOptionForValue() {
        const { value, options } = props;
        const selectedItem = options.find(item => {
            return item.value === value;
        });
        return selectedItem;
    }
    function onIsOpenChange(changes) {
        /**
         * This is called when a state change occurs on the downshift component. We use this to take action when the dropdown
         * is opened.
         */
        if (changes.isOpen && rootRef.current) {
            // When the dropdown is opened, make sure it gets scrolled into view. To give a little extra padding to the
            // scrollable area, we'll temporarily add some bottom padding to the item, let the scroll calculations run and
            // then we'll remove it.
            setTimeout(() => {
                if (rootRef?.current) {
                    setIsBeingOpened(true);
                    doScrollElementIntoView(rootRef.current, true);
                    setIsBeingOpened(false);
                }
            }, 70 * 2);
            // Carbon animates the menu opening, so we sadly need to provide a value here (fast01 token === 70) using the
            // value from @carbon/motion (which is not exported to JS).
        }
    }
    return (React__default.createElement("div", { ref: rootRef },
        React__default.createElement(Metablock, { title: title, description: description, id: `WAC__selectUUID_${id}-label`, shouldRemoveHTMLBeforeMarkdownConversion: shouldRemoveHTMLBeforeMarkdownConversion }),
        React__default.createElement("div", { className: cx('WAC__selectHolder', { WAC__customSelectTemporaryPadding: isBeingOpened }) },
            React__default.createElement(Layer, null,
                React__default.createElement(Dropdown, { id: `WAC__selectUUID_${id}`, items: options, label: languagePack.options_select, titleText: languagePack.options_select, hideLabel: true, "aria-label": disableUserInputs ? languagePack.options_ariaOptionsDisabled : title, disabled: disableUserInputs, onChange: onChange, selectedItem: findOptionForValue(), downshiftProps: {
                        environment,
                        onIsOpenChange,
                        id: `WACSelectComponent__Downshift-${id}`,
                    } })))));
}

class OptionComponent extends Component {
    constructor() {
        super(...arguments);
        /**
         * This function is called when the user has selected one of the available options.
         *
         * @param selectedOption The option that was selected.
         * @param type The specific type of the option response type ('button' vs 'dropdown').
         */
        this.onOptionSelected = (selectedOption, type) => {
            const { localMessage, serviceManager, originalMessage } = this.props;
            const { id: responseID } = originalMessage;
            const messageRequest = createMessageRequestForChoice(selectedOption, responseID);
            const localMessageID = localMessage.ui_state.id;
            // Track when user gives a response through a button or dropdown option.
            const source = type === 'button' ? MessageSendSource.OPTION_BUTTON : MessageSendSource.OPTION_DROP_DOWN;
            serviceManager.actions.sendWithCatch(messageRequest, source, {
                trackEventProps: {
                    eventName: 'Assistant Message Received from User',
                    type,
                },
                setValueSelectedForMessageID: localMessageID,
            });
            // Move focus back to the input field
            this.props.requestInputFocus();
        };
        /**
         * This is called when the option displays a list of buttons and one of the buttons is clicked.
         * It also sets the responseType to 'button'.
         */
        this.onButtonClick = (event, item) => {
            this.onOptionSelected(item, 'button');
        };
        /**
         * This is called when the option displays a dropdown component of options and one options is clicked.
         * It also sets the responseType to 'dropdown'.
         */
        this.onSelectChange = (data) => {
            this.onOptionSelected(data.selectedItem, 'dropdown');
        };
    }
    render() {
        const { localMessage, languagePack, disableUserInputs, serviceManager, shouldRemoveHTMLBeforeMarkdownConversion } = this.props;
        const { options, title, description, preference } = localMessage.item;
        const { optionSelected } = localMessage.ui_state;
        const type = getOptionType(preference, options.length);
        return (React__default.createElement(React__default.Fragment, null, type === 'button' ? (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Metablock, { title: title, description: description, shouldRemoveHTMLBeforeMarkdownConversion: shouldRemoveHTMLBeforeMarkdownConversion }),
            React__default.createElement("div", { className: "WAC__button-holder" },
                React__default.createElement("ul", null, options.map((item, index) => {
                    const isSelected = optionSelected ? item.value.input.text === optionSelected.input.text : false;
                    return (React__default.createElement("li", { key: item.label },
                        React__default.createElement(unstable__ChatButton, { kind: ButtonKindEnum.TERTIARY, isQuickAction: true, size: ButtonSizeEnum.SMALL, className: `WAC__button-${index}`, disabled: disableUserInputs, isSelected: isSelected, onClick: (event) => {
                                this.onButtonClick(event, item);
                            } }, item.label)));
                }))))) : (React__default.createElement(SelectComponent, { serviceManager: serviceManager, languagePack: languagePack, title: title, description: description, options: options, disableUserInputs: disableUserInputs, onChange: this.onSelectChange, value: optionSelected, shouldRemoveHTMLBeforeMarkdownConversion: shouldRemoveHTMLBeforeMarkdownConversion }))));
    }
}

class SuggestionComponent extends Component {
    constructor() {
        super(...arguments);
        this.onSelect = (event, data) => {
            const { localMessage, serviceManager, originalMessage } = this.props;
            const isDialog = !isActionsResponse(originalMessage);
            serviceManager.actions.sendDisambiguationOption(data.item, localMessage, originalMessage, data.index, isDialog);
            // Move focus back to the input field
            this.props.requestInputFocus();
        };
    }
    render() {
        const { localMessage, originalMessage, disableUserInputs, shouldRemoveHTMLBeforeMarkdownConversion } = this.props;
        const { suggestions, title } = localMessage.item;
        const { optionSelected } = localMessage.ui_state;
        const isDialog = !isActionsResponse(originalMessage);
        return (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(Metablock, { description: title, shouldRemoveHTMLBeforeMarkdownConversion: shouldRemoveHTMLBeforeMarkdownConversion }),
            React__default.createElement("div", { className: "WAC__button-holder" },
                React__default.createElement("ul", null, suggestions.map((item, index) => {
                    !isDialog && optionSelected && item.value.input.suggestion_id === optionSelected.input.suggestion_id;
                    return (React__default.createElement("li", { key: item.label },
                        React__default.createElement(unstable__ChatButton, { kind: ButtonKindEnum.TERTIARY, isQuickAction: true, size: ButtonSizeEnum.SMALL, className: `WAC__disambigButton-${index}`, disabled: disableUserInputs, onClick: (event) => {
                                this.onSelect(event, { index, item });
                            } }, item.label)));
                })))));
    }
}

const Carousel = lazyCarousel();
function SearchCarousel({ localMessageItem }) {
    const languagePack = useLanguagePack();
    const chatWidth = useSelector((state) => state.chatWidth);
    const { header, title } = localMessageItem.item;
    // We use a carousel to show the citations card in a width larger than standard, but also before we get all the way
    // to fullscreen.
    const isCarousel = chatWidth < CAROUSEL_MAX_WIDTH;
    // We can get results either from results or from primary_results and additional_results. We just smash them all together anyway.
    const searchResults = localMessageItem.item.results || [
        ...(localMessageItem.item.primary_results || []),
        ...(localMessageItem.item.additional_results || []),
    ];
    function renderSearchResults() {
        const tiles = searchResults.map((searchResult, index) => {
            // eslint-disable-next-line react/no-array-index-key
            return React__default.createElement(CitationCardExport, { citation: searchResult, key: index });
        });
        if (isCarousel) {
            return (React__default.createElement("div", { className: "WACConversationalSearch_Citations" },
                React__default.createElement(Suspense, { fallback: React__default.createElement("div", null) },
                    React__default.createElement(Carousel, { initialSlide: 0 }, tiles))));
        }
        return (React__default.createElement("div", { className: cx('WACConversationalSearch_Citations', 'WACConversationalSearch_Citations--inline', {
                'WACConversationalSearch_Citations--stretch': chatWidth < FULL_WIDTH_CITATION_MAX_WIDTH,
            }) }, tiles));
    }
    return (React__default.createElement("div", { className: "WACConversationalSearch" },
        React__default.createElement("div", { className: "ibm-web-chat--default-styles" },
            React__default.createElement("div", { className: "WAC__searchMessage--metablock WAC__searchMessage--metablock--carousel" }, header || title || languagePack.messages_searchResults),
            renderSearchResults())));
}
const SearchCarouselExport = React__default.memo(SearchCarousel);

/**
 * Renders a table response type. The table has a header, rows, and optionally a title and description.
 */
function TableContainer(props) {
    const { tableItem } = props;
    const { title, description, headers, rows } = tableItem;
    const carbonTheme = useSelector((state) => state.theme.carbonTheme);
    const chatHeight = useSelector((state) => state.chatHeight);
    const locale = useSelector((state) => state.locale);
    const disableComponent = useSelector((state) => state.config.public.__ibm__?.disableWebComponent?.table);
    const languagePack = useLanguagePack();
    const intl = useIntl();
    const tableContainerRef = useRef(null);
    const [tableContainerWidth, setTableContainerWidth] = useState();
    function onResize() {
        setTableContainerWidth(tableContainerRef.current?.clientWidth);
    }
    useLayoutEffect(() => {
        const tableResizeObserver = new ResizeObserver(onResize);
        tableResizeObserver.observe(tableContainerRef.current);
    });
    const isValidTable = useMemo(() => {
        const columnCount = headers.length;
        // If the the number of cells in one of the rows is different than the number of cells in the header, then the table
        // is not valid.
        const isValid = !rows.some(row => row.cells.length !== columnCount);
        if (!isValid) {
            consoleError(`Number of cells in the table header does not match the number of cells in one or more of the table rows. In order to render a table there needs to be the same number of columns in the table header and all of the table rows.`);
        }
        return isValid;
    }, [rows, headers]);
    function getTablePaginationSupplementalText({ count }) {
        return intl.formatMessage({ id: 'table_paginationSupplementalText' }, { pagesCount: count });
    }
    function getTablePaginationStatusText({ start, end, count }) {
        return intl.formatMessage({ id: 'table_paginationStatus' }, { start, end, count });
    }
    if (isValidTable && !disableComponent) {
        // TODO TABLE: When table md is streaming from an LLM use loading prop to render skeleton state.
        // Because of issues with our layering variables from carbon we need to force the table to be either in white or g90
        // theme. In the other themes the table background blends into the chat background causing the table to look like
        // it's floating.
        return (React__default.createElement(Theme, { theme: LIGHT_THEMES.includes(carbonTheme) ? 'white' : 'g90' },
            React__default.createElement("div", { className: "WACTableContainer", ref: tableContainerRef },
                React__default.createElement(Suspense, null,
                    React__default.createElement(Table, { tableTitle: title, tableDescription: description, headers: headers, rows: rows, containerWidth: tableContainerWidth, chatHeight: chatHeight, filterPlaceholderText: languagePack.table_filterPlaceholder, previousPageText: languagePack.table_previousPage, nextPageText: languagePack.table_nextPage, itemsPerPageText: languagePack.table_itemsPerPage, getPaginationSupplementalText: getTablePaginationSupplementalText, getPaginationStatusText: getTablePaginationStatusText, locale: locale })))));
    }
    return (
    // Use inlineError.tsx here if the table is not valid. In the future when we have a web component version of the
    // inlineError component the table web component can render that instead of the React container doing it here.
    React__default.createElement(InlineError, null));
}
const TableContainerExport = React__default.memo(TableContainer);

/**
 * When a message is received that has info for a tour then a tour card is shown to allow the user to start the tour. Once
 * the tour is started the tour card then changes to have a resume and restart button instead of a start button.
 */
function TourCard({ message, serviceManager }) {
    const { tour_resumeButton, tour_restartButton, tour_startButton } = useLanguagePack();
    const { activeTourID } = useSelector((state) => state.persistedToBrowserStorage.chatState.persistedTourState);
    // If the id of the original message that generated this card matches the active tour ID then we should show a resume
    // and restart button instead of a start button.
    const isCurrentTour = message.fullMessageID === activeTourID;
    // Use the provided title and description if there is one. If there isn't one fall back to the defaults.
    // TODO TOUR: I18N: These default values are being kept around for tours in beta. When we switch to our GA solution of
    // using a real tour card response type the defaults will either come from tooling or will need to be moved to our
    // en.json file in web chat and translated.
    const tourTitle = message.item.user_defined?.card_title || 'Guided journey';
    const tourDescription = message.item.user_defined?.card_description ||
        'Follow along as the assistant guides you to where you want to be.';
    /**
     * When resume is clicked switch to the tour view.
     */
    async function onResumeClick() {
        // Try to open the tour.
        await serviceManager.actions.changeView(ViewType.TOUR, {
            mainWindowCloseReason: MainWindowCloseReason.TOUR_CARD_RESUMED_TOUR,
        });
        // Fire a track event for clicking to resume the tour.
        serviceManager.trackService.track({
            eventName: 'Tour interaction',
            eventDescription: 'Tour continued.',
            type: 'From resume button.',
        });
    }
    /**
     * When restart is clicked switch to the tour view and then change to the first step in the tour.
     */
    async function onRestartClick() {
        // Try to open the tour.
        const newViewState = await serviceManager.actions.changeView(ViewType.TOUR, {
            mainWindowCloseReason: MainWindowCloseReason.TOUR_CARD_RESTARTED_TOUR,
        });
        if (newViewState.tour) {
            // If the tour is now visible then change to the first step in the tour.
            serviceManager.actions.changeStepInTour({ newStepIndex: 0 });
        }
        // Fire a track event for clicking to restart the tour.
        serviceManager.trackService.track({
            eventName: 'Tour interaction',
            eventDescription: 'Tour continued.',
            type: 'From restart button.',
        });
    }
    return (React__default.createElement(Tile, { className: "WACTourCard" },
        React__default.createElement("div", { className: "WACTourCard__Title" }, tourTitle),
        React__default.createElement("div", { className: "WACTourCard__Description" }, tourDescription),
        isCurrentTour && (React__default.createElement("div", null,
            React__default.createElement(Button, { className: "WACTourCard__ResumeButton", renderIcon: ArrowRight, onClick: onResumeClick }, tour_resumeButton),
            React__default.createElement(Button, { className: "WACTourCard__RestartButton", kind: "ghost", renderIcon: Restart, onClick: onRestartClick }, tour_restartButton))),
        !isCurrentTour && (React__default.createElement(Button, { className: "WACTourCard__StartButton", renderIcon: ArrowRight, onClick: () => serviceManager.actions.startTour(message, TourStartReason.START_TOUR_CLICKED, {
                mainWindowCloseReason: MainWindowCloseReason.TOUR_CARD_STARTED_TOUR,
            }) }, tour_startButton))));
}
const TourCardExport = React__default.memo(TourCard);

function Text$1({ text }) {
    return React__default.createElement("div", null, text);
}

/**
 * The channel transfer response type is a means of transferring the user from an external channel they are on, such as
 * whatsapp, to web chat where they can continue their conversation. The transfer can be due to a need for an agent or
 * or something else. The user will be able to see the link they visited that initiated the transfer as well.
 */
function ChannelTransferMessageComponent({ message }) {
    const { message_to_user, transfer_info } = message;
    const { chat } = transfer_info?.target;
    return (React__default.createElement("div", { className: "WACChannelTransfer" },
        React__default.createElement(Text$1, { text: message_to_user }),
        chat?.url && React__default.createElement("div", { className: "WACChannelTransfer__url" }, `${new URL(chat.url).origin}/[...]`)));
}
const ChannelTransferMessageComponentExport = React__default.memo(ChannelTransferMessageComponent);

/**
 * This item renders some text that may contain rich content. This component also supports the display of text as it
 * is streaming in chunks (currently only TextItem chunks are supported).
 */
function StreamingRichText(props) {
    const { text, streamingState, removeHTML, isStreamingError, doAutoScroll, shouldRemovePadding } = props;
    const languagePack = useLanguagePack();
    // If the chunks change, kick off an auto-scroll.
    useCallbackOnChange(streamingState?.chunks, doAutoScroll);
    let textToUse;
    if (streamingState && !streamingState.isDone) {
        // If we're streaming, then concatenate all the chunks together.
        textToUse = streamingState.chunks.map(chunk => chunk.text).join('');
    }
    else {
        textToUse = text;
    }
    return (React__default.createElement("div", { className: "WACStreamingRichText" },
        React__default.createElement(RichTextExport, { text: textToUse, shouldRemoveHTMLBeforeMarkdownConversion: removeHTML, shouldRemovePadding: shouldRemovePadding }),
        isStreamingError && React__default.createElement(InlineError, { text: languagePack.conversationalSearch_streamingIncomplete })));
}

let mcspFetchPromise;
const fetchMCSPIdentityToken = async (serviceManager) => {
    const state = serviceManager.store.getState();
    const { config } = state;
    const publicConfig = config.public;
    const url = `${getConfigURL(publicConfig)}?type=verify`;
    let result;
    try {
        result = await doWithRetry(() => doConfigFetch(url, serviceManager.fetch, state.config.remote.initConfig.authorization?.is_on), [100, 1000, 2000, 5000], 'fetch verify config');
    }
    catch (error) {
        serviceManager.actions.errorOccurred({
            errorType: OnErrorType.MCSP_IDENTITY_TOKEN,
            message: 'The verification token could not be loaded.',
            errorCode: error?.status || undefined,
            otherData: await safeFetchTextWithTimeout(error),
        });
        throw new Error(`${WA_CONSOLE_PREFIX} The verification token data could not be loaded.`);
    }
    if (!result) {
        throw new Error(`${WA_CONSOLE_PREFIX} The verification token data could not be loaded because there was no result.`);
    }
    if (!result.headers?.get('x-watson-mcsp-jwt')) {
        throw new Error(`${WA_CONSOLE_PREFIX} The verification token data could not be loaded because the header didn't exist.`);
    }
    return result.headers.get('x-watson-mcsp-jwt');
};
/**
 * When we are in tooling in the aws environment. This means we need to get an MCSP token instead of a regular JWT. In
 * tooling, Deb can't provide a JWT. We normally solve this by routing through tooling server and store to pick up auth
 * headers that integrations server looks for. With the direct to skill communication that happens in the wxo widget,
 * we don't have that option, so instead we pass this JWT availble within tooling that they will be able to understand.
 *
 * If we make a call to /config?verify=true, the MCSP token will be included as a value in the header.
 *
 * We wrap this with mcspFetchPromise to make sure we don't make multiple concurrent calls needlessly if the form
 * component makes a coding mistake.
 */
const getMCSPIdentityToken = async (serviceManager) => {
    if (mcspFetchPromise) {
        return mcspFetchPromise;
    }
    mcspFetchPromise = new Promise((resolve, reject) => {
        return fetchMCSPIdentityToken(serviceManager)
            .then(resolve)
            .catch(reject)
            .finally(() => {
            mcspFetchPromise = undefined;
        });
    });
    return mcspFetchPromise;
};

class RemoteComponentErrorBoundary extends Component {
    constructor() {
        super(...arguments);
        this.state = {
            hasError: false,
        };
    }
    static getDerivedStateFromError() {
        // Update state so the next render will show the fallback UI.
        return { hasError: true };
    }
    componentDidCatch(error, errorInfo) {
        console.error(WA_CONSOLE_PREFIX, { error, errorInfo });
    }
    render() {
        if (this.state.hasError) {
            // You can render any custom fallback UI
            return React__default.createElement(InlineError, { text: this.props.languagePack.errors_singleMessage });
        }
        return this.props.children;
    }
}

const WxOLazyLoadedFormComponent = React__default.lazy(() => loadWXOComponent('WxOHome', './WxOForm', WXO_SCRIPT_KEY));
/**
 * This component wraps loading and error states when remotely grabbing a web pack based MFE.
 */
function WxORemoteComponent({ authorization_is_on, integrationID, subscriptionID, useMCSP, ...rest }) {
    const languagePack = useLanguagePack();
    const serviceManager = useServiceManager();
    const authorization = useMemo(() => ({
        is_on: authorization_is_on || false,
        requestToken: async () => {
            let identityToken;
            if (useMCSP) {
                // We are in tooling in the aws environment. This means we need to get an MCSP token instead of a
                // regular JWT. In tooling, Deb can't provide a JWT. We normally solve this by routing through tooling
                // server and store to pick up auth headers that integrations server looks for. With the direct to skill
                // communication that happens in the wxo widget, we don't have that option, so instead we pass this JWT
                // available within tooling that they will be able to understand.
                identityToken = await getMCSPIdentityToken(serviceManager);
            }
            else if (authorization_is_on) {
                // If authorization isn't on and we are not using MCSP, we shouldn't get stuck here.
                await getNewIdentityTokenIfExpiring(serviceManager);
                identityToken = serviceManager.store.getState().identityToken;
            }
            return identityToken;
        },
        integrationID,
        subscriptionID: subscriptionID || 'public', // defaults to 'public'
        useMCSP,
    }), [authorization_is_on, integrationID, serviceManager, subscriptionID, useMCSP]);
    const WxORemoteComponentRef = useRef();
    const [hasAutoScrolled, setHasAutoScrolled] = useState(false);
    // The first time the height of the WxoRemoteComponent changes by a significant amount we should try auto scrolling.
    // This solves two different scroll placement issues with forms. First, when a form is rendered early in the
    // conversation (before the web chat history is long enough to overflow) the chat gets scrolled to the bottom instead
    // of to the most recent user message. Second, on reload when a form is rendered the chat is scrolled to the wrong
    // spot. The issue here is that normally when a message is rendered we use our auto scrolling functions to scroll to
    // the right place for the user. However, these lazy loaded remote components take time to render. Because they take
    // time to render, when we first try to auto scroll for this message, we just scroll to the loading state instead of
    // the actual remote component. When the remote component finally does render it grows in size from the initial
    // loading state which offsets the scroll position. To account for this we need to call doAutoScroll() to scroll back
    // to the right place. We only want to do this once instead of every time the remote component changes size. And we
    // only want to do it when the component is of a significant size - otherwise we may be trying to scroll when going
    // from 0 to scroll bar which is not what we want.
    useEffect(() => {
        // If the remote component is bigger than the size of the SkeletonText component (16px) we should try auto
        // scrolling. We don't want to auto scroll more than once so we will not auto scroll on a future height change.
        if (!hasAutoScrolled && WxORemoteComponentRef?.current?.offsetHeight > 20) {
            serviceManager.instance.doAutoScroll();
            setHasAutoScrolled(true);
        }
    }, [WxORemoteComponentRef?.current?.offsetHeight, hasAutoScrolled, serviceManager.instance]);
    return (React__default.createElement("div", { ref: WxORemoteComponentRef },
        React__default.createElement(RemoteComponentErrorBoundary, { languagePack: languagePack },
            React__default.createElement(Suspense, { fallback: React__default.createElement(SkeletonText, null) },
                React__default.createElement(WxOLazyLoadedFormComponent, { authorization: authorization, namespaceSuffix: serviceManager.namespace.suffix, ...rest })))));
}

/**
 * This component renders a specific message component based on a message's type.
 */
function MessageTypeComponent(props) {
    const { allowNewFeedback, hideFeedback, serviceManager, originalMessage, message } = props;
    const intl = useIntl();
    const languagePack = useLanguagePack();
    const feedbackDetailsRef = useRef();
    const agentDisplayState = useSelector(selectAgentDisplayState);
    const agentState = useSelector((state) => state.agentState);
    const persistedAgentState = useSelector((state) => state.persistedToBrowserStorage.chatState.agentState);
    const feedbackID = message.item.message_options?.feedback?.id;
    const feedbackPanelID = useUUID();
    const feedbackHistory = originalMessage.history?.feedback?.[feedbackID];
    const feedbackInitialValues = useMemo(() => {
        if (!feedbackHistory) {
            return null;
        }
        return {
            text: feedbackHistory.text,
            selectedCategories: feedbackHistory.categories,
        };
    }, [feedbackHistory]);
    // Indicates if the one of the feedback details are open.
    const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
    // Indicates if the negative or positive feedback buttons are marked as selected.
    const [isPositiveFeedbackSelected, setIsPositiveFeedbackSelected] = useState(feedbackHistory && feedbackHistory.is_positive);
    const [isNegativeFeedbackSelected, setIsNegativeFeedbackSelected] = useState(feedbackHistory && !feedbackHistory.is_positive);
    // Indicates if details have been submitted.
    const [isFeedbackSubmitted, setIsFeedbackSubmitted] = useState(Boolean(feedbackHistory));
    /**
     * Returns the appropriate component to render the given message.
     */
    function renderSpecificMessage(localMessageItem, message) {
        if (isRequest(message)) {
            return renderRequest(localMessageItem, message);
        }
        if (isResponse(message)) {
            const response = renderResponse(localMessageItem, message);
            const isResponseStopped = localMessageItem.item.streaming_metadata?.stream_stopped;
            return (React__default.createElement(React__default.Fragment, null,
                response,
                isResponseStopped && React__default.createElement(ResponseStopped, null),
                renderChainOfThought(localMessageItem),
                renderFeedback(localMessageItem, message)));
        }
        return false;
    }
    /**
     * Returns the appropriate component to render the given message.
     */
    function renderRequest(localMessageItem, originalMessage) {
        const messageItem = localMessageItem.item;
        if (isTextItem(messageItem)) {
            const text = originalMessage.history?.label || messageItem.text;
            // If this was user entered text, show the user's original text before showing the text that was actually sent to
            // the assistant.
            const userText = localMessageItem.ui_state.originalUserText || text;
            return (React__default.createElement("div", { className: "WAC__sent--text" },
                originalMessage.input.message_type === InternalMessageRequestType.FILE && (React__default.createElement(Attachment, { className: "WAC__sent-fileIcon", "aria-label": props.languagePack.fileSharing_fileIcon })),
                React__default.createElement("span", { role: "heading", "aria-level": 2 }, userText)));
        }
        return null;
    }
    /**
     * Returns the appropriate component to render the given message.
     */
    function renderResponse(localMessageItem, message) {
        if (renderAsTour(localMessageItem.item)) {
            // Render an invalid component who's user_defined_type is specifically for the tour beta feature, as a tour.
            return renderTour(localMessageItem);
        }
        if (renderAsUserDefinedMessage(localMessageItem.item)) {
            // Render all invalid components as a user defined response
            return renderUserDefinedResponse(localMessageItem, message);
        }
        const responseType = localMessageItem.item.response_type;
        const withHumanAgent = Boolean(localMessageItem.item.agent_message_type);
        switch (responseType) {
            case MessageResponseTypes.TEXT:
                return renderText(localMessageItem, message, withHumanAgent);
            case MessageResponseTypes.IMAGE:
                return renderImage(localMessageItem);
            case MessageResponseTypes.OPTION:
                return renderOption(localMessageItem, message);
            case MessageResponseTypes.CONNECT_TO_AGENT:
                return renderConnectToAgent(localMessageItem, message);
            case MessageResponseTypes.SUGGESTION:
                return renderSuggestion(localMessageItem, message);
            case MessageResponseTypes.SEARCH:
                return renderSearch(localMessageItem);
            case MessageResponseTypes.CHANNEL_TRANSFER:
                return renderChannelTransfer(localMessageItem);
            case MessageResponseTypes.INLINE_ERROR:
                return renderInlineError(localMessageItem);
            case InternalMessageResponseType.STREAM_LOADING:
                return renderStreamLoading();
            case InternalMessageResponseType.CHANNEL_TRANSFER_FAILED:
            case InternalMessageResponseType.SESSION_EXPIRED:
                return renderChatStatusMessage(localMessageItem);
            case MessageResponseTypes.IFRAME:
                return renderIFrameMessage(localMessageItem);
            case MessageResponseTypes.VIDEO:
                return renderVideoMessage(localMessageItem);
            case MessageResponseTypes.AUDIO:
                return renderAudioMessage(localMessageItem);
            case MessageResponseTypes.DATE:
                return renderDateMessage(localMessageItem);
            case MessageResponseTypes.CONVERSATIONAL_SEARCH:
                return renderConversationalSearchMessage(localMessageItem, message);
            case MessageResponseTypes.TABLE:
                return renderTable(localMessageItem);
            case MessageResponseTypes.CARD:
                return renderCard(localMessageItem, message);
            case MessageResponseTypes.CAROUSEL:
                return renderCarouselMessage(localMessageItem, message);
            case MessageResponseTypes.BUTTON:
                return renderButtonItem(localMessageItem, message);
            case MessageResponseTypes.GRID:
                return renderGrid(localMessageItem, message);
            case InternalMessageResponseType.WATSON_ORCHESTRATE_FORM:
                return renderWOForm(localMessageItem, message);
            default:
                return renderUserDefinedResponse(localMessageItem, message);
        }
    }
    function renderText(message, originalMessage, removeHTML) {
        if (props.isNestedMessageItem) {
            return renderRichText(message, removeHTML, originalMessage, props.isNestedMessageItem);
        }
        // For text provided by the assistant, pass it through some HTML formatting before displaying it.
        return (React__default.createElement("div", { className: cx('WAC__received--textContent', {
                'WAC__received--withSpacing': !shouldRemovePadding(message), // TODO: remove in 9.0
            }) }, renderRichText(message, removeHTML, originalMessage)));
    }
    /**
     * Actions RTE and Dialog make use of <br /> to manage spacing. LLMs don't. This property lets us know if we should
     * rely on <br /> for padding or add our own. "true" means we should add our own. If it's a carbon chat, we don't have
     * to worry about the legacy crap, so they don't have to worry about adding format.use_padding.
     */
    function shouldRemovePadding(message, isNestedMessageItem) {
        const { serviceManager } = props;
        if (isNestedMessageItem) {
            return false;
        }
        return !message.item.format?.use_padding && !serviceManager.store.getState().config.asCarbonChat;
    }
    function renderRichText(localMessageItem, removeHTML, originalMessage, isNestedMessageItem) {
        return (React__default.createElement(StreamingRichText, { text: localMessageItem.item.text, streamingState: localMessageItem.ui_state.streamingState, isStreamingError: originalMessage?.history?.is_streaming_error, removeHTML: removeHTML, doAutoScroll: props.doAutoScroll, shouldRemovePadding: shouldRemovePadding(localMessageItem, isNestedMessageItem) }));
    }
    function renderOption(message, originalMessage) {
        const { languagePack, requestInputFocus, serviceManager, disableUserInputs, isMessageForInput } = props;
        const withHumanAgent = Boolean(message.item.agent_message_type);
        return (React__default.createElement(OptionComponent, { localMessage: message, originalMessage: originalMessage, languagePack: languagePack, disableUserInputs: disableUserInputs || !isMessageForInput, requestInputFocus: requestInputFocus, serviceManager: serviceManager, shouldRemoveHTMLBeforeMarkdownConversion: withHumanAgent }));
    }
    function renderSuggestion(message, originalMessage) {
        const { requestInputFocus, serviceManager, disableUserInputs, isMessageForInput, config } = props;
        const withHumanAgent = Boolean(message.item.agent_message_type);
        // If the message is from dialog, then we won't disable it even if it's not the most recent message. Users can
        // go back and choose a different disambiguation response at any point in the conversation. But this only
        // applies to dialog. Also, disable the inputs if we're connecting or connect to an agent. One exception to the
        // dialog rule is if copyPreviousContextOnRequest is set to false. Copying the context is required to go make a
        // different choice and if that is disabled, we will disable the buttons.
        const isDialog = !isActionsResponse(originalMessage);
        const derivedDisableUserInputs = disableUserInputs ||
            (!config.public.messaging.copyPreviousContextOnRequest && !isMessageForInput) ||
            (!isDialog && !isMessageForInput) ||
            agentDisplayState.isConnectingOrConnected;
        return (React__default.createElement(SuggestionComponent, { localMessage: message, originalMessage: originalMessage, serviceManager: serviceManager, disableUserInputs: derivedDisableUserInputs, requestInputFocus: requestInputFocus, shouldRemoveHTMLBeforeMarkdownConversion: withHumanAgent }));
    }
    function renderImage(message) {
        const { languagePack, serviceManager } = props;
        const { useAITheme } = serviceManager.store.getState().theme;
        return (React__default.createElement(ImageExport, { imageError: languagePack.errors_imageSource, source: message.item.source, title: message.item.title, description: message.item.description, altText: message.item.alt_text, needsAnnouncement: message.ui_state.needsAnnouncement, useAITheme: useAITheme }));
    }
    function renderSearch(message) {
        return React__default.createElement(SearchCarouselExport, { localMessageItem: message });
    }
    function renderInlineError(message) {
        return React__default.createElement(InlineError, { text: message.item.text });
    }
    function renderStreamLoading() {
        return React__default.createElement(SkeletonText, { paragraph: true, lineCount: 2 });
    }
    function renderChatStatusMessage(message) {
        return React__default.createElement(ChatStatusMessage, { message: message });
    }
    function renderIFrameMessage(message) {
        const { doAutoScroll, isNestedMessageItem } = props;
        const display = isNestedMessageItem ? IFrameItemDisplayOption.INLINE : undefined;
        return React__default.createElement(IFrameMessage, { localMessage: message, doAutoScroll: doAutoScroll, displayOverride: display });
    }
    function renderVideoMessage(message) {
        const { doAutoScroll } = props;
        const { item } = message;
        const { source, title, description, alt_text } = item;
        return (React__default.createElement(VideoComponentExport, { source: source, title: title, description: description, baseHeight: getMediaDimensions(item)?.base_height, ariaLabel: alt_text, doAutoScroll: doAutoScroll, needsAnnouncement: message.ui_state.needsAnnouncement }));
    }
    function renderAudioMessage(message) {
        const { doAutoScroll } = props;
        const { source, title, description, alt_text } = message.item;
        return (React__default.createElement(AudioComponentExport, { source: source, title: title, description: description, ariaLabel: alt_text, doAutoScroll: doAutoScroll, needsAnnouncement: message.ui_state.needsAnnouncement }));
    }
    function renderDateMessage(message) {
        return (React__default.createElement(DatePickerComponentExport, { localMessage: message, disabled: !props.isMessageForInput, scrollElementIntoView: props.scrollElementIntoView }));
    }
    function renderUserDefinedResponse(message, originalMessage) {
        const { serviceManager } = props;
        return (React__default.createElement(UserDefinedResponse$1, { streamingState: message.ui_state.streamingState, isStreamingError: originalMessage?.history?.is_streaming_error, doAutoScroll: props.doAutoScroll, localMessageID: message.ui_state.id, serviceManager: serviceManager }));
    }
    function renderTour(message) {
        const { serviceManager } = props;
        return React__default.createElement(TourCardExport, { message: message, serviceManager: serviceManager });
    }
    function renderConnectToAgent(message, originalMessage) {
        const { languagePack, config, serviceManager, disableUserInputs, isMessageForInput } = props;
        return (React__default.createElement(ConnectToAgent, { localMessage: message, originalMessage: originalMessage, languagePack: languagePack, config: config, serviceManager: serviceManager, agentState: agentState, agentDisplayState: agentDisplayState, persistedAgentState: persistedAgentState, disableUserInputs: disableUserInputs || !isMessageForInput, requestFocus: props.requestInputFocus }));
    }
    function renderChannelTransfer(message) {
        return React__default.createElement(ChannelTransferMessageComponentExport, { message: message.item });
    }
    function renderCard(message, originalMessage) {
        const { isMessageForInput, requestInputFocus } = props;
        return (React__default.createElement(CardComponentExport, { localMessageItem: message, fullMessage: originalMessage, isMessageForInput: isMessageForInput, requestFocus: requestInputFocus }));
    }
    function renderConversationalSearchMessage(localMessageItem, fullMessage) {
        const { scrollElementIntoView, doAutoScroll } = props;
        return (React__default.createElement(ConversationalSearch, { localMessageItem: localMessageItem, scrollElementIntoView: scrollElementIntoView, isStreamingError: fullMessage.history?.is_streaming_error, doAutoScroll: doAutoScroll }));
    }
    function renderButtonItem(message, originalMessage) {
        const { isMessageForInput, requestInputFocus } = props;
        return (React__default.createElement(ButtonItemComponent, { localMessageItem: message, fullMessage: originalMessage, isMessageForInput: isMessageForInput, requestFocus: requestInputFocus }));
    }
    function renderCarouselMessage(message, originalMessage) {
        const { isMessageForInput, requestInputFocus } = props;
        return (React__default.createElement(CarouselItemComponent, { localMessageItem: message, fullMessage: originalMessage, isMessageForInput: isMessageForInput, requestFocus: requestInputFocus }));
    }
    function renderGrid(message, originalMessage) {
        return React__default.createElement(GridItemComponentExport, { localMessageItem: message, originalMessage: originalMessage });
    }
    function renderTable(message) {
        return React__default.createElement(TableContainerExport, { tableItem: message.item });
    }
    function renderWOForm(message, originalMessage) {
        const { disableUserInputs, isMessageForInput, serviceManager } = props;
        const state = serviceManager.store.getState();
        const { authorization } = state.config.remote.initConfig;
        const { integrationID, subscriptionID, region } = state.config.public;
        // If we are in tooling and we're supposed to load the Orchestrate mfe, we should use MCSP tokens.
        const useMCSP = Boolean(state.config.public.__ibm__?.toolingType) &&
            state.config.public.__ibm__.toolingType !== ToolingType.PREVIEW_LINK &&
            state.loadOrchestrateMFEs;
        return (React__default.createElement(WxORemoteComponent, { messageItem: message, transactionID: originalMessage.history?.transaction_id, fullMessage: originalMessage, instance: serviceManager.instance, authorization_is_on: Boolean(authorization?.is_on), integrationID: integrationID, subscriptionID: subscriptionID, useMCSP: useMCSP, isCP4D: Boolean(state.config.public.cloudPrivateHostURL), disableUserInputs: disableUserInputs || !isMessageForInput, orchestratePrivateHostURL: state.config.public.orchestratePrivateHostURL, region: region }));
    }
    function scrollChainOfThought(open, element) {
        if (open) {
            setTimeout(() => {
                props.scrollElementIntoView(element, 64, 64);
            });
        }
    }
    function formatStepLabelText({ stepNumber, stepTitle }) {
        return intl.formatMessage({ id: 'chainOfThought_stepTitle' }, { stepNumber, stepTitle });
    }
    /**
     * Renders chain of thought component for the given message item if appropriate.
     */
    function renderChainOfThought(localMessageItem, message) {
        const chainOfThought = localMessageItem.item.message_options?.chain_of_thought;
        if (!chainOfThought || props.isNestedMessageItem) {
            return false;
        }
        return (React__default.createElement(ChainOfThought, { steps: chainOfThought, onToggle: scrollChainOfThought, onStepToggle: scrollChainOfThought, formatStepLabelText: formatStepLabelText, explainabilityText: languagePack.chainOfThought_explainabilityLabel, inputLabelText: languagePack.chainOfThought_inputLabel, outputLabelText: languagePack.chainOfThought_outputLabel, toolLabelText: languagePack.chainOfThought_toolLabel }));
    }
    /**
     * Renders the feedback options for the given message item if appropriate.
     */
    function renderFeedback(localMessageItem, message) {
        const feedbackOptions = localMessageItem.item.message_options?.feedback || {};
        const { id: feedbackID, is_on, show_positive_details = true, show_negative_details = true, show_text_area = true, show_prompt = true, title, prompt, categories, placeholder, disclaimer, } = feedbackOptions;
        if (props.isNestedMessageItem || hideFeedback || (!allowNewFeedback && !feedbackHistory) || !is_on) {
            return false;
        }
        /**
         * Updates the message history with the feedback data provided.
         */
        function updateHistory(data) {
            if (feedbackID) {
                const history = {
                    feedback: {
                        [feedbackID]: data,
                    },
                };
                serviceManager.actions.sendUpdateHistoryEvent(localMessageItem.fullMessageID, { history });
                serviceManager.store.dispatch(actions.mergeMessageHistory(localMessageItem.fullMessageID, history));
            }
        }
        /**
         * Handles when one of the feedback buttons is clicked. We also treat clicking the cancel button the same way as
         * clicking the feedback button.
         */
        function onFeedbackClicked(isPositive) {
            const toggleToSelected = isPositive ? !isPositiveFeedbackSelected : !isNegativeFeedbackSelected;
            const openDetails = (isPositive ? show_positive_details : show_negative_details) && toggleToSelected;
            if (toggleToSelected && !openDetails) {
                // If the button has been toggled to selected but we're not showing details, that means the option is considered
                // immediately submitted.
                updateHistory({ is_positive: isPositive });
                setIsFeedbackSubmitted(true);
                serviceManager.fire({
                    type: "feedback" /* BusEventType.FEEDBACK */,
                    messageItem: localMessageItem.item,
                    message,
                    interactionType: "submitted" /* FeedbackInteractionType.SUBMITTED */,
                    isPositive,
                });
            }
            else {
                setIsFeedbackOpen(openDetails);
                if (openDetails) {
                    setTimeout(() => {
                        props.scrollElementIntoView(feedbackDetailsRef.current, 48, 56);
                    });
                }
                serviceManager.fire({
                    type: "feedback" /* BusEventType.FEEDBACK */,
                    messageItem: localMessageItem.item,
                    message,
                    interactionType: openDetails
                        ? "detailsOpened" /* FeedbackInteractionType.DETAILS_OPENED */
                        : "detailsClosed" /* FeedbackInteractionType.DETAILS_CLOSED */,
                    isPositive,
                });
            }
            setIsPositiveFeedbackSelected(isPositive ? toggleToSelected : false);
            setIsNegativeFeedbackSelected(isPositive ? false : toggleToSelected);
        }
        /**
         * Handles when the feedback submit button is clicked.
         */
        function onSubmit(isPositive, details) {
            setIsFeedbackSubmitted(true);
            setIsFeedbackOpen(false);
            const event = {
                type: "feedback" /* BusEventType.FEEDBACK */,
                messageItem: localMessageItem.item,
                message,
                interactionType: "submitted" /* FeedbackInteractionType.SUBMITTED */,
                isPositive,
                text: details.text,
                categories: details.selectedCategories,
            };
            serviceManager.fire(event);
            // Submit this update to be recorded in history.
            updateHistory({
                is_positive: event.isPositive,
                text: event.text,
                categories: event.categories,
            });
        }
        /**
         * Render the feedback popup for either positive or negative feedback.
         */
        function renderFeedbackPopup(isPositive) {
            // Only one popup can be open and which one is opened depends on which feedback is selected.
            const isOpen = isFeedbackOpen && (isPositive ? isPositiveFeedbackSelected : isNegativeFeedbackSelected);
            return (React__default.createElement(FeedbackComponent, { class: `${CSS_CLASS_PREFIX}-feedbackDetails-${isPositive ? 'positive' : 'negative'}`, id: `${feedbackPanelID}-feedback-${isPositive ? 'positive' : 'negative'}`, isOpen: isOpen, isReadonly: isFeedbackSubmitted, onClose: () => onFeedbackClicked(isPositive), onSubmit: details => onSubmit(isPositive, details), initialValues: feedbackHistory && feedbackHistory?.is_positive === isPositive ? feedbackInitialValues : null, showTextArea: show_text_area, showPrompt: show_prompt, title: title || languagePack.feedback_defaultTitle, prompt: prompt || languagePack.feedback_defaultPrompt, categories: categories, placeholder: placeholder || languagePack.feedback_defaultPlaceholder, disclaimer: disclaimer, submitLabel: languagePack.feedback_submitLabel, cancelLabel: languagePack.feedback_cancelLabel }));
        }
        return (React__default.createElement("div", { className: "WAC__received--feedback" },
            React__default.createElement(FeedbackButtonsComponent, { isPositiveOpen: isFeedbackOpen && isPositiveFeedbackSelected, isNegativeOpen: isFeedbackOpen && isNegativeFeedbackSelected, isPositiveSelected: isPositiveFeedbackSelected, isNegativeSelected: isNegativeFeedbackSelected, hasPositiveDetails: show_positive_details, hasNegativeDetails: show_negative_details, isPositiveDisabled: isNegativeFeedbackSelected || isFeedbackSubmitted, isNegativeDisabled: isPositiveFeedbackSelected || isFeedbackSubmitted, positiveLabel: languagePack.feedback_positiveLabel, negativeLabel: languagePack.feedback_negativeLabel, panelID: feedbackPanelID, onClick: onFeedbackClicked }),
            React__default.createElement("div", { ref: feedbackDetailsRef },
                renderFeedbackPopup(true),
                renderFeedbackPopup(false))));
    }
    return renderSpecificMessage(props.message, props.originalMessage);
}

class MessageComponent extends PureComponent {
    constructor() {
        super(...arguments);
        /**
         * Default state.
         */
        this.state = { didRenderErrorOccur: false, focusHandleHasFocus: false };
        /**
         * A reference to the root element in this component.
         */
        this.ref = React__default.createRef();
        /**
         * A reference to the pure message element in this component.
         */
        this.messageRef = React__default.createRef();
        /**
         * A reference to the focus handle element in this component.
         */
        this.focusHandleRef = React__default.createRef();
        /**
         * Returns the value of the local message for the component.
         */
        this.getLocalMessage = () => {
            return this.props.localMessageItem;
        };
        /**
         * Called when the focus handle gets focus.
         */
        this.onHandleFocus = () => {
            this.setState({ focusHandleHasFocus: true });
        };
        /**
         * Called when the focus handle loses focus.
         */
        this.onHandleBlur = () => {
            this.setState({ focusHandleHasFocus: false });
        };
        /**
         * Sets the z-index value used for the message.
         */
        this.setZIndex = (zIndex) => {
            this.setState({ zIndex });
        };
        /**
         * Called when a key down event occurs while the focus handle has focus.
         */
        this.onHandleKeyDown = (event) => {
            if (event.altKey || event.metaKey || event.ctrlKey || event.shiftKey) {
                // Don't do anything if any modifiers are present.
                return;
            }
            let moveFocus;
            if (event.key === 'ArrowUp') {
                moveFocus = MoveFocusType.PREVIOUS;
            }
            else if (event.key === 'ArrowDown') {
                moveFocus = MoveFocusType.NEXT;
            }
            else if (event.key === 'Home') {
                moveFocus = MoveFocusType.FIRST;
            }
            else if (event.key === 'End') {
                moveFocus = MoveFocusType.LAST;
            }
            else if (event.key === 'Escape') {
                moveFocus = MoveFocusType.INPUT;
            }
            if (moveFocus) {
                // This will stop the scroll panel from moving as a result of the keypress. We only want it to move as a
                // result of the focus change.
                event.preventDefault();
                this.props.requestMoveFocus(moveFocus, this.props.messagesIndex);
            }
        };
    }
    /**
     * Returns an ARIA message that can be used to indicate that the widget (either bot or agent) was responsible for
     * saying a specific message.
     */
    getWidgetSaidMessage() {
        const { intl, botName, localMessageItem } = this.props;
        let messageId;
        if (localMessageItem.item.agent_message_type) {
            // For the human agent view, we only want to say "agent said" for messages that are text. The status messages
            // do not need this announcement.
            if (localMessageItem.item.response_type === MessageResponseTypes.TEXT) {
                messageId = 'messages_agentSaid';
            }
        }
        else {
            messageId = 'messages_botSaid';
        }
        return messageId ? intl.formatMessage({ id: messageId }, { botName }) : null;
    }
    componentDidCatch(error, errorInfo) {
        this.props.serviceManager.actions.errorOccurred(createDidCatchErrorData('Message', error, errorInfo));
        this.setState({ didRenderErrorOccur: true });
    }
    componentDidMount() {
        const uiState = this.props.localMessageItem.ui_state;
        if (uiState.needsAnnouncement) {
            this.props.ariaAnnouncer(this.ref.current);
            this.props.serviceManager.store.dispatch(actions.setMessageWasAnnounced(uiState.id));
        }
    }
    componentDidUpdate() {
        const uiState = this.props.localMessageItem.ui_state;
        if (uiState.needsAnnouncement) {
            this.props.ariaAnnouncer(this.ref.current);
            this.props.serviceManager.store.dispatch(actions.setMessageWasAnnounced(uiState.id));
        }
    }
    /**
     * Indicates if we should render the failed message instead of the actual message.
     */
    shouldRenderFailedMessage() {
        if (this.state.didRenderErrorOccur) {
            return true;
        }
        const { localMessageItem, message, config } = this.props;
        const { connectToAgentBehavior } = config.public.__ibm__;
        // If the message is a CTA, has a service desk error, and we're supposed to report service desk errors, then we
        // need to render the failed message.
        return (isConnectToAgent(localMessageItem.item) &&
            message.history?.agent_no_service_desk &&
            connectToAgentBehavior === ConnectToAgentBehaviorType.ERROR_MISSING);
    }
    /**
     * Moves focus to this message's focus handle.
     *
     * @see renderFocusHandle
     */
    requestHandleFocus() {
        const { languagePack, intl, message, botName } = this.props;
        // Announce who said it and then the actual message. The "Bot said" text is normally only read once per
        // MessageResponse instead of once per LocalMessage but since we're moving focus between each LocalMessage, go
        // ahead and announce the "who" part for each one.
        const whoAnnouncement = isRequest(message)
            ? languagePack.messages_youSaid
            : intl.formatMessage({ id: 'messages_botSaid' }, { botName });
        const strings = [whoAnnouncement];
        nodeToText(this.messageRef.current, strings);
        // Using this aria-label allows us to make sure that this text is read out loud before JAWS reads its "1 of 2"
        // list item message that it adds after reading the aria-label.
        this.focusHandleRef.current.setAttribute('aria-label', strings.join(' '));
        doFocusRef(this.focusHandleRef, true);
    }
    /**
     * Renders the error state version of this message. This code carefully avoids touching the message data as it
     * could be data that doesn't match what we were expecting.
     */
    renderFailedRenderMessage() {
        const { messagesIndex } = this.props;
        return (React__default.createElement("div", { className: `WAC__message WAC__message--inlineError WAC__message-${messagesIndex} ${this.props.className || ''}`, ref: this.ref },
            React__default.createElement("div", { className: "WAC__message--padding" },
                React__default.createElement("div", { className: "WAC__bot-message" },
                    React__default.createElement(VisuallyHidden, null, this.getWidgetSaidMessage()),
                    React__default.createElement("div", { className: "WAC__received WAC__message-vertical-padding WAC__received--text" },
                        React__default.createElement(InlineError, { text: this.props.languagePack.errors_singleMessage }))))));
    }
    /**
     * This will render the avatar for the bot message. This is normally the little blue bar next to the message but
     * in the case of a human agent, it can be the agent's avatar image.
     */
    renderCustomLeft(eventType) {
        const { serviceManager } = this.props;
        const { zIndex } = this.state;
        if (serviceManager.enableUICustomEvents.has(eventType)) {
            const { message, localMessageItem } = this.props;
            const event = {
                element: null, // This is filled in when the component is mounted.
                type: eventType,
                messageItem: localMessageItem.item,
                message,
                setItemZIndex: this.setZIndex,
            };
            return React__default.createElement(UICustomizationElement, { event: event, zIndex: zIndex });
        }
        return null;
    }
    /**
     * Renders the avatar line that appears above each message that has the avatar (for responses) and timestamps.
     */
    renderAvatarLine(localMessageItem, message) {
        let avatar;
        const { languagePack, botAvatarURL, useAITheme, carbonTheme } = this.props;
        const timestamp = timestampToTimeString(message.history.timestamp);
        let label;
        let actorName;
        let iconClassName = '';
        if (isResponse(message)) {
            // We'll use the first message item for deciding if we should show the agent's avatar.
            const agentMessageType = localMessageItem.item.agent_message_type;
            const agentProfile = message.history.agent_profile;
            if (isAgentStatusMessage(agentMessageType) || agentProfile?.hidden) {
                // These messages don't show an avatar line.
                return null;
            }
            const fromAgent = agentMessageType === AgentMessageType.FROM_AGENT;
            if (fromAgent || agentProfile?.profile_picture_url) {
                avatar = (React__default.createElement(ImageWithFallback, { url: agentProfile?.profile_picture_url, alt: fromAgent ? languagePack.agent_ariaAgentAvatar : languagePack.agent_ariaGenericAvatar, fallback: React__default.createElement(IconHolder, { icon: React__default.createElement(Headset, null) }) }));
                iconClassName = 'WACMessage__Avatar--agent';
            }
            else {
                const icon = useAITheme ? React__default.createElement(AvatarExport, { theme: carbonTheme }) : React__default.createElement(IconHolder, { icon: React__default.createElement(ChatBot, null) });
                const imageUrl = useAITheme ? undefined : botAvatarURL;
                avatar = React__default.createElement(ImageWithFallback, { url: imageUrl, alt: languagePack.agent_ariaGenericBotAvatar, fallback: icon });
                iconClassName = 'WACMessage__Avatar--bot';
            }
            if (fromAgent || agentProfile?.nickname) {
                actorName = agentProfile?.nickname || languagePack.agent_agentNoNameTitle;
            }
            else if (useAITheme) {
                actorName = 'watsonx';
            }
            label = (React__default.createElement("span", { "data-wac-exclude-node-read": true },
                React__default.createElement(FormattedMessage, { id: "message_labelBot", values: { timestamp, actorName } })));
        }
        else {
            label = React__default.createElement(FormattedMessage, { id: "message_labelYou", values: { timestamp } });
        }
        return (React__default.createElement("div", { className: "WACMessage__AvatarLine", key: `${message.id}-avatar-line` },
            avatar && React__default.createElement("div", { className: `WACMessage__Avatar ${iconClassName}` }, avatar),
            React__default.createElement("div", { className: "WACMessage__Label" }, label)));
    }
    /**
     * Renders the state indicator for the message sent by the user. This can appear on the left of message or beneath the
     * message.
     */
    renderMessageState(message) {
        const { languagePack } = this.props;
        let element;
        let className;
        let showBelowMessage = false;
        const errorState = message.history?.error_state;
        const fileStatus = message.history?.file_upload_status;
        if (errorState === MessageErrorState.FAILED) {
            element = React__default.createElement(InlineError, { text: languagePack.errors_singleMessage });
            className = 'WAC__message-error-failed';
            showBelowMessage = true;
        }
        else if (fileStatus === FileStatusValue.UPLOADING) {
            element = React__default.createElement(Loading, { withOverlay: false, small: true, "aria-label": languagePack.fileSharing_statusUploading });
            className = 'WAC__message-status-file-uploading';
        }
        else if (fileStatus === 'success') {
            element = React__default.createElement(CheckmarkFilled, { "aria-label": languagePack.fileSharing_statusUploading });
            className = 'WAC__message-status-file-success';
        }
        // We probably should include an aria-label here but since we explicit announce state changes in the message
        // service and this icon is contained in a live region, that would result in duplicate text being announced. We
        // can't rely solely on the aria-label here in this live region because the SRs don't seem to reliably announce
        // what we want to announce, moving to success for example. Our a11y expert says it's okay to leave it out here.
        return (element && {
            element: React__default.createElement("div", { className: `WAC__message-status ${className}` }, element),
            showBelowMessage,
        });
    }
    /**
     * Renders a focus "handle" for this message. When this message gets focus, we actually move focus to an element
     * inside it instead of the entire message. This is only done when the user clicks the scroll handle button on the
     * scroll container that moves focus into the scroll panel or when focus moves from one message to another. We move
     * focus to the handle which is inside the message instead of the message itself because if we make the whole message
     * actually focusable then a screen reader will read the entire message whenever any item inside it gets focus which
     * is not desirable.
     */
    renderFocusHandle() {
        return (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        React__default.createElement("div", { className: "WACMessage--focusHandle", ref: this.focusHandleRef, tabIndex: -1, onFocus: this.onHandleFocus, onBlur: this.onHandleBlur, onKeyDown: this.onHandleKeyDown, role: "listitem" }));
    }
    render() {
        if (this.shouldRenderFailedMessage()) {
            // If an error occurred, don't attempt to do anything with the message. Just show an error.
            return this.renderFailedRenderMessage();
        }
        const { serviceManager, config, localMessageItem, message, languagePack, requestInputFocus, toolingType, messagesIndex, disableUserInputs, showAvatarLine, className, doAutoScroll, isMessageForInput, scrollElementIntoView, isFirstMessageItem, hideFeedback, allowNewFeedback, } = this.props;
        const { isIntermediateStreaming, isWelcomeResponse, disableFadeAnimation } = localMessageItem.ui_state;
        const messageItem = localMessageItem.item;
        const responseType = messageItem.response_type;
        const agentMessageType = messageItem.agent_message_type;
        const fromHistory = message.history.from_history;
        const readWidgetSaid = isFirstMessageItem;
        if (isIntermediateStreaming && !canRenderIntermediateStreaming(messageItem.response_type)) {
            return false;
        }
        const messageComponent = (React__default.createElement(MessageTypeComponent, { serviceManager: serviceManager, toolingType: toolingType, languagePack: languagePack, requestInputFocus: requestInputFocus, message: localMessageItem, originalMessage: message, disableUserInputs: disableUserInputs, isMessageForInput: isMessageForInput, config: config, doAutoScroll: doAutoScroll, scrollElementIntoView: scrollElementIntoView, hideFeedback: hideFeedback, allowNewFeedback: allowNewFeedback }));
        const isCustomMessage = renderAsUserDefinedMessage(localMessageItem.item);
        const isChatStatusMessage = isChatStatusResponseType(localMessageItem);
        let styleObject;
        if (this.state.zIndex !== null && this.state.zIndex !== undefined) {
            styleObject = { zIndex: this.state.zIndex };
        }
        // Don't show animation on the welcome node or for messages that explicitly turn it off.
        const noAnimation = isWelcomeResponse || disableFadeAnimation;
        // If this is a user_defined response type with silent set, we don't want to render all the extra cruft around it.
        const agentClassName = getAgentMessageClassName(agentMessageType, responseType, isCustomMessage);
        const messageIsRequest = isRequest(message);
        const isSystemMessage = isAgentStatusMessage(localMessageItem.item.agent_message_type);
        let isOptionResponseWithoutTitleOrDescription = false;
        if (isOptionItem(localMessageItem.item)) {
            if (!localMessageItem.item.title && !localMessageItem.item.description) {
                isOptionResponseWithoutTitleOrDescription = true;
            }
        }
        let messageState;
        if (messageIsRequest) {
            messageState = this.renderMessageState(message);
        }
        return (React__default.createElement("div", { id: `WAC__message-${messagesIndex}${serviceManager.namespace.suffix}`, className: cx(`WAC__message WAC__message-${messagesIndex}`, className, agentMessageType && 'WAC__message--agentMessage', {
                'WAC__message--withAvatarLine': showAvatarLine,
                'WAC__message--request': messageIsRequest,
                'WAC__message--systemMessage': isSystemMessage,
                'WAC__message--response': !messageIsRequest,
                'WAC__message--no-animation': noAnimation,
                'WAC__message--custom': isCustomMessage,
                'WAC__message--disabled-inputs': disableUserInputs,
                'WAC__message--has-focus': this.state.focusHandleHasFocus,
                'WAC__message--option-response-without-title-or-description': isOptionResponseWithoutTitleOrDescription,
            }), ref: this.ref, style: styleObject },
            this.renderFocusHandle(),
            showAvatarLine && this.renderAvatarLine(localMessageItem, message),
            React__default.createElement("div", { className: "WAC__message--padding" },
                isResponse(message) && (React__default.createElement("div", { className: "WAC__bot-message" },
                    readWidgetSaid && React__default.createElement(VisuallyHidden, null, this.getWidgetSaidMessage()),
                    this.renderCustomLeft("customization:message:response:item:left" /* BusEventType.CUSTOMIZATION_RESPONSE_LEFT */),
                    React__default.createElement("div", { className: cx('WAC__received', 'WAC__message-vertical-padding', agentClassName, {
                            'WAC__received--text': responseType === MessageResponseTypes.TEXT,
                            'WAC__received--image': responseType === MessageResponseTypes.IMAGE,
                            'WAC__received--options': responseType === MessageResponseTypes.OPTION,
                            'WAC__received--search': responseType === MessageResponseTypes.SEARCH,
                            'WAC__received--suggestion': responseType === MessageResponseTypes.SUGGESTION,
                            'WAC__received--welcome': responseType === 'welcome',
                            'WAC__received--inlineError': responseType === MessageResponseTypes.INLINE_ERROR,
                            'WAC__received--streamLoading': responseType === InternalMessageResponseType.STREAM_LOADING,
                            'WAC__received--channelTransfer': responseType === MessageResponseTypes.CHANNEL_TRANSFER,
                            'WAC__received--iframePreviewCard': responseType === MessageResponseTypes.IFRAME,
                            'WAC__received--video': responseType === MessageResponseTypes.VIDEO,
                            'WAC__received--audio': responseType === MessageResponseTypes.AUDIO,
                            'WAC__received--date': responseType === MessageResponseTypes.DATE,
                            'WAC__received--card': responseType === MessageResponseTypes.CARD,
                            'WAC__received--carousel': responseType === MessageResponseTypes.CAROUSEL,
                            'WAC__received--conversationalSearch': responseType === MessageResponseTypes.CONVERSATIONAL_SEARCH,
                            'WAC__received--carouselSingle': isSingleItemCarousel(localMessageItem.item),
                            'WAC__received--button': responseType === MessageResponseTypes.BUTTON,
                            'WAC__received--grid': responseType === MessageResponseTypes.GRID,
                            'WAC__received--chatStatusMessage': isChatStatusMessage,
                            'WAC__received--fullWidth': localMessageItem.ui_state.fullWidth,
                            'WAC__message--historical': fromHistory,
                        }), ref: this.messageRef },
                        React__default.createElement("div", { className: "WAC__received--inner" }, messageComponent)))),
                messageIsRequest && (React__default.createElement("div", { className: "WAC__sent-container" },
                    this.renderCustomLeft("customization:message:request:item:left" /* BusEventType.CUSTOMIZATION_REQUEST_LEFT */),
                    React__default.createElement("div", { className: cx('WAC__sentAndMessageState-container', 'WAC__message-vertical-padding', {
                            'WAC__sentAndMessageState--belowMessage': messageState?.showBelowMessage,
                        }) },
                        !messageState?.showBelowMessage && messageState?.element,
                        React__default.createElement("div", { className: "WAC__sent" },
                            React__default.createElement(VisuallyHidden, null, languagePack.messages_youSaid),
                            React__default.createElement("div", { className: "WAC__sent--bubble" },
                                React__default.createElement("div", { ref: this.messageRef }, messageComponent))),
                        messageState?.showBelowMessage && messageState?.element))))));
    }
}
/**
 * Returns the class name to add to messages with the given agent message type.
 */
function getAgentMessageClassName(agentMessageType, messageResponseType, isUserDefinedResponse) {
    if (isUserDefinedResponse) {
        return 'WAC__received--agentCustom';
    }
    if (!messageResponseType ||
        (messageResponseType !== MessageResponseTypes.TEXT && messageResponseType !== MessageResponseTypes.BUTTON)) {
        return '';
    }
    switch (agentMessageType) {
        case null:
        case undefined:
        case AgentMessageType.FROM_USER:
            return null;
        case AgentMessageType.RELOAD_WARNING:
        case AgentMessageType.DISCONNECTED:
            return 'WAC__received--chatStatusMessage';
        case AgentMessageType.FROM_AGENT:
            return 'WAC__received--fromAgent';
        default:
            return 'WAC__received--agentStatusMessage';
    }
}
/**
 * Indicates if this message is a status message. These are messages that are centered in the view.
 */
function isAgentStatusMessage(agentMessageType) {
    switch (agentMessageType) {
        case null:
        case undefined:
        case AgentMessageType.FROM_USER:
        case AgentMessageType.RELOAD_WARNING:
        case AgentMessageType.DISCONNECTED:
        case AgentMessageType.FROM_AGENT:
        case AgentMessageType.INLINE_ERROR:
            return false;
        default:
            return true;
    }
}
/**
 * Indicates if an item with the given response type is allowed to be rendered in an intermediate stream state.
 */
function canRenderIntermediateStreaming(type) {
    switch (type) {
        case MessageResponseTypes.IMAGE:
        case MessageResponseTypes.VIDEO:
        case MessageResponseTypes.AUDIO:
        case MessageResponseTypes.OPTION:
        case MessageResponseTypes.IFRAME:
        case MessageResponseTypes.INLINE_ERROR:
        case MessageResponseTypes.SUGGESTION:
        case MessageResponseTypes.CONVERSATIONAL_SEARCH:
        case MessageResponseTypes.USER_DEFINED:
        case MessageResponseTypes.TEXT:
            return true;
        default:
            return false;
    }
}
var MessageComponent$1 = withAriaAnnouncer(injectIntl(MessageComponent, { forwardRef: true }));

var MoveFocusType;
(function (MoveFocusType) {
    /**
     * Indicates that focus should be moved to the first message.
     */
    MoveFocusType[MoveFocusType["FIRST"] = 1] = "FIRST";
    /**
     * Indicates that focus should be moved to the last message.
     */
    MoveFocusType[MoveFocusType["LAST"] = 2] = "LAST";
    /**
     * Indicates that focus should be moved to the next message.
     */
    MoveFocusType[MoveFocusType["NEXT"] = 3] = "NEXT";
    /**
     * Indicates that focus should be moved to the previous message.
     */
    MoveFocusType[MoveFocusType["PREVIOUS"] = 4] = "PREVIOUS";
    /**
     * Indicates that focus should be moved back to the input field.
     */
    MoveFocusType[MoveFocusType["INPUT"] = 5] = "INPUT";
})(MoveFocusType || (MoveFocusType = {}));
class MessagesComponent extends PureComponent {
    constructor() {
        super(...arguments);
        /**
         * Default state.
         */
        this.state = {
            scrollHandleHasFocus: false,
        };
        /**
         * A registry of references to the child {@link MessageComponent} instances. The keys of the map are the IDs of
         * each message item and the value is the ref to the component.
         */
        this.messageRefs = new Map();
        /**
         * A ref to the scrollable container that contains the messages.
         */
        this.messagesContainerWithScrollingRef = React__default.createRef();
        /**
         * A ref to the element that acts as a handle for scrolling.
         */
        this.scrollHandleRef = React__default.createRef();
        /**
         * A ref to the element that acts as a handle for scrolling.
         */
        this.agentBannerRef = React__default.createRef();
        /**
         * This will check to see if the messages list is anchored to the bottom of the panel and if so, ensure that the
         * list is still scrolled to the bottom.
         */
        this.onResize = () => {
            if (this.props.messageState.isScrollAnchored) {
                const element = this.messagesContainerWithScrollingRef.current;
                if (element) {
                    element.scrollTop = element.scrollHeight;
                }
            }
        };
        /**
         * This will execute an auto-scroll operation based on the current state of messages in the component. This should
         * be called whenever the messages change.
         *
         * The scrolling rules are as follows.
         *
         * 1. If the last message is a welcome node, auto-scroll to the top of the message without animating. This
         * means the user has just started a new chat, and we want to just jump to the top.
         * 2. If the component has just mounted and the last message is not a welcome node, just jump to the bottom
         * without animating.
         * 3. If the typing indicator is visible, then scroll that into view.
         * 4. Scroll to the top of the last user message. This means that the bot messages will auto-scroll until the user's
         * last message reaches the top of the window, and then they'll stop and not scroll anymore.
         * 5. If the there is no user message that can be scrolled to, scroll to the last bot message.
         * 6. If the last bot message has an empty output. Just scroll to bottom.
         *
         * @param options The options to control how the scrolling should occur.
         */
        this.doAutoScroll = throttle((options = {}) => {
            try {
                debugAutoScroll('[doAutoScroll] Running doAutoScroll', options);
                const { scrollToTop, scrollToBottom } = options;
                const { localMessageItems, messageState, allMessagesByID } = this.props;
                const { isTypingCounter, isLoadingCounter } = messageState;
                const { isAgentTyping } = selectAgentDisplayState(this.props);
                const scrollElement = this.messagesContainerWithScrollingRef.current;
                if (scrollToTop !== undefined) {
                    doScrollElement(scrollElement, scrollToTop, 0, false);
                    return;
                }
                if (scrollToBottom !== undefined) {
                    const scrollTop = scrollElement.scrollHeight - scrollElement.offsetHeight - scrollToBottom;
                    doScrollElement(scrollElement, scrollTop, 0, false);
                    return;
                }
                let animate = true;
                let setScrollTop;
                const lastLocalItemIndex = localMessageItems.length - 1;
                const lastLocalItem = localMessageItems.length ? localMessageItems[lastLocalItemIndex] : null;
                const lastMessage = allMessagesByID[lastLocalItem?.fullMessageID];
                if (!lastLocalItem) {
                    debugAutoScroll('[doAutoScroll] No last time');
                    // No messages, so set the scroll position to the top. If we don't set this explicitly, the browser may
                    // decide it remembers the previous scroll position and set it for us.
                    animate = false;
                    setScrollTop = 0;
                }
                else if (isTypingCounter > 0 || isLoadingCounter > 0 || isAgentTyping) {
                    // The typing indicator is visible, so scroll to the bottom.
                    setScrollTop = scrollElement.scrollHeight;
                    debugAutoScroll('[doAutoScroll] isTyping visible', isTypingCounter);
                }
                else {
                    /**
                     * Determines if the message should be scrolled to. The last user message should be scrolled to by default.
                     * However, the last bot message should be scrolled to if there is no user message that can be scrolled to.
                     */
                    const shouldScrollToMessage = (localItem, message) => {
                        if (isResponse(message)) {
                            const messageRequest = allMessagesByID[message?.request_id];
                            if (isChatStatusResponseType(localItem)) {
                                // Scroll to chat status messages.
                                return true;
                            }
                            // If the request for this response was silent, then scroll to it instead of scrolling to where the
                            // silent user message would be. But don't do this if it's an empty message (which happens with a
                            // skip_use_input message from an extension).
                            return messageRequest?.history?.silent && messageRequest.input?.text !== '';
                        }
                        return isRequest(message);
                    };
                    // Iterate backwards until we find the last message to scroll to. By default, the user's last message should be
                    // scrolled to. However, if the user's message was silent, the last response should be scrolled to.
                    let messageIndex = localMessageItems.length - 1;
                    let localItem = localMessageItems[messageIndex];
                    let lastScrollableMessageComponent = this.messageRefs.get(localItem?.ui_state.id);
                    while (messageIndex >= 1) {
                        localItem = localMessageItems[messageIndex];
                        const message = allMessagesByID[localItem?.fullMessageID];
                        if (shouldScrollToMessage(localItem, message)) {
                            lastScrollableMessageComponent = this.messageRefs.get(localItem?.ui_state.id);
                            debugAutoScroll(`[doAutoScroll] lastScrollableMessageComponent=${messageIndex}`, localMessageItems[messageIndex], message);
                            break;
                        }
                        messageIndex--;
                    }
                    if (lastScrollableMessageComponent) {
                        // Scroll to the top of the user's message. Those messages have 28px of padding on the top so let's cut
                        // that down to just 8 by scrolling a little bit more.
                        const offsetTop = lastScrollableMessageComponent.ref.current?.offsetTop;
                        setScrollTop = offsetTop + AUTO_SCROLL_EXTRA;
                        debugAutoScroll(`[doAutoScroll] Scrolling to message offsetTop=${offsetTop}`);
                    }
                    else {
                        // No message found.
                        setScrollTop = -1;
                        debugAutoScroll('[doAutoScroll] No message found');
                    }
                }
                if (setScrollTop !== -1) {
                    if (setScrollTop >= scrollElement.scrollTop) {
                        // If this is from history, we don't want to animate.
                        if (lastMessage?.history?.from_history) {
                            animate = false;
                        }
                        debugAutoScroll(`[doAutoScroll] doScrollElement`, scrollElement, setScrollTop, animate);
                        doScrollElement(scrollElement, setScrollTop, 0, animate);
                        // Update the scroll anchor setting based on this new position.
                        this.checkScrollAnchor(true, setScrollTop);
                    }
                }
            }
            catch (error) {
                // Just ignore any errors. It's not the end of the world if scrolling doesn't work for any reason.
                consoleError('An error occurred while attempting to scroll.', error);
            }
        }, AUTO_SCROLL_THROTTLE_TIMEOUT);
        /**
         * Returns the current scrollBottom value for the message scroll panel.
         */
        this.getContainerScrollBottom = () => {
            return getScrollBottom(this.messagesContainerWithScrollingRef?.current);
        };
        /**
         * Scrolls the given element into view so that it is fully visible. If the element is already visible, then no
         * scrolling will be done.
         *
         * @param element The element to scroll into view.
         * @param paddingTop An additional pixel value that will over scroll by this amount to give a little padding between
         * the element and the top of the scroll area.
         * @param paddingBottom An additional pixel value that will over scroll by this amount to give a little padding
         * between the element and the top of the scroll area.
         */
        this.scrollElementIntoView = (element, paddingTop = 8, paddingBottom = 8) => {
            const scrollElement = this.messagesContainerWithScrollingRef.current;
            const scrollRect = scrollElement.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();
            // The distance the top and bottom of the element is from the top of the message list.
            const topDistanceFromTop = elementRect.top - scrollRect.top + scrollElement.scrollTop - paddingTop;
            const bottomDistanceFromTop = elementRect.bottom - scrollRect.top + scrollElement.scrollTop + paddingBottom;
            const elementHeight = element.offsetHeight + paddingTop + paddingBottom;
            if (topDistanceFromTop < scrollElement.scrollTop || elementHeight > scrollElement.offsetHeight) {
                // The top of the element is above the fold or the element doesn't fully fit. Scroll it so its top is at the top
                // of the scroll panel.
                doScrollElement(scrollElement, topDistanceFromTop, 0);
            }
            else if (bottomDistanceFromTop > scrollElement.scrollTop + scrollElement.offsetHeight) {
                // The bottom of the element is below the fold. Scroll it so its bottom is at the bottom of the scroll panel.
                doScrollElement(scrollElement, bottomDistanceFromTop - scrollElement.offsetHeight, 0);
            }
        };
        /**
         * This is a callback called by a child message component to request that it move focus to a different message.
         */
        this.requestMoveFocus = (moveFocusType, currentMessageIndex) => {
            if (moveFocusType === MoveFocusType.INPUT) {
                this.props.requestInputFocus();
            }
            else {
                const { localMessageItems } = this.props;
                let index;
                switch (moveFocusType) {
                    case MoveFocusType.LAST:
                        index = localMessageItems.length - 1;
                        break;
                    case MoveFocusType.NEXT:
                        index = currentMessageIndex + 1;
                        if (index >= localMessageItems.length) {
                            index = 0;
                        }
                        break;
                    case MoveFocusType.PREVIOUS:
                        index = currentMessageIndex - 1;
                        if (index < 0) {
                            index = localMessageItems.length - 1;
                        }
                        break;
                    default:
                        index = 0;
                        break;
                }
                const messageItem = localMessageItems[index];
                const ref = this.messageRefs.get(messageItem?.ui_state.id);
                if (ref) {
                    ref.requestHandleFocus();
                }
            }
        };
    }
    componentDidMount() {
        this.scrollPanelObserver = new ResizeObserver(this.onResize);
        this.scrollPanelObserver.observe(this.messagesContainerWithScrollingRef.current);
        this.previousScrollOffsetHeight = this.messagesContainerWithScrollingRef.current.offsetHeight;
    }
    componentDidUpdate(oldProps) {
        const newProps = this.props;
        // If the number of messages changes (usually because of new messages) or the state of the "is typing" indicator
        // changes, then we need to check to see if we want to perform some auto-scrolling behavior.
        const numMessagesChanged = oldProps.localMessageItems.length !== newProps.localMessageItems.length;
        const oldAgentDisplayState = selectAgentDisplayState(oldProps);
        const newAgentDisplayState = selectAgentDisplayState(newProps);
        const typingChanged = oldProps.messageState.isTypingCounter !== newProps.messageState.isTypingCounter ||
            oldProps.messageState.isLoadingCounter !== newProps.messageState.isLoadingCounter ||
            oldAgentDisplayState.isAgentTyping !== newAgentDisplayState.isAgentTyping;
        if (numMessagesChanged || typingChanged) {
            const newLastItem = arrayLastValue(newProps.localMessageItems);
            const oldLastItem = arrayLastValue(oldProps.localMessageItems);
            // If the last message has changed, then do an auto scroll.
            const lastItemChanged = newLastItem !== oldLastItem;
            if (lastItemChanged || typingChanged) {
                this.doAutoScroll();
            }
        }
    }
    componentWillUnmount() {
        // Remove the listeners and observer we added previously.
        this.scrollPanelObserver.unobserve(this.messagesContainerWithScrollingRef.current);
    }
    /**
     * This function is called when the scrollable messages list is scrolled. It will determine if the scroll panel
     * has been scrolled all the way to the bottom and if so, it will enable the scroll anchor that will keep it there.
     * Note that this callback is not attached via the normal react method with an `onScroll` prop as that doesn't
     * work with under a shadow DOM. This callback is attached directly in {@link componentDidMount}.
     *
     * This function will also make a somewhat crude attempt to distinguish if a scroll event has occurred because the
     * user initiated a scroll or if the application initiated a scroll as the result of a changing in size of the
     * widget. If the user initiates a scroll, then we use that event to anchor or un-anchor the scroll panel. If the
     * application did the scroll, we want the anchor state to remain unchanged.
     *
     * @param fromAutoScroll Indicates if the reason we are checking the anchor is due to an auto-scroll action.
     * @param assumeScrollTop A value to assume the scroll panel is (or will be) scrolled to. This can be useful when
     * an animation is occurring and the current scroll position isn't the final scroll position.
     */
    checkScrollAnchor(fromAutoScroll, assumeScrollTop) {
        const scrollElement = this.messagesContainerWithScrollingRef.current;
        // If we're checking because of auto-scrolling, we want check the scroll position even if the scroll detection
        // is normally suspended because of something like an animation in progress.
        if (fromAutoScroll ||
            (this.previousScrollOffsetHeight === scrollElement.offsetHeight && !this.props.suspendScrollDetection)) {
            // If the scroll panel has been scrolled all the way to the bottom, turn on the anchor.
            const assumedScrollTop = assumeScrollTop !== undefined ? assumeScrollTop : scrollElement.scrollTop;
            const isScrollAnchored = assumedScrollTop >= scrollElement.scrollHeight - scrollElement.offsetHeight;
            if (isScrollAnchored !== this.props.messageState.isScrollAnchored) {
                this.props.serviceManager.store.dispatch(actions.setChatMessagesStateProperty('isScrollAnchored', isScrollAnchored));
            }
        }
        this.previousScrollOffsetHeight = scrollElement.offsetHeight;
    }
    /**
     * Moves focus to the button in the agent header.
     */
    requestAgentBannerFocus() {
        if (this.agentBannerRef.current) {
            return this.agentBannerRef.current.requestFocus();
        }
        return false;
    }
    /**
     * Scrolls to the (full) message with the given ID. Since there may be multiple message items in a given
     * message, this will scroll the first message to the top of the message window.
     *
     * @param messageID The (full) message ID to scroll to.
     * @param animate Whether or not the scroll should be animated. Defaults to true.
     */
    doScrollToMessage(messageID, animate = true) {
        try {
            // Find the component that has the message we want to scroll to.
            const { localMessageItems } = this.props;
            let panelComponent;
            for (let index = 0; index <= localMessageItems.length; index++) {
                const messageItem = localMessageItems[index];
                if (messageItem.fullMessageID === messageID) {
                    panelComponent = this.messageRefs.get(messageItem.ui_state.id);
                    break;
                }
            }
            if (panelComponent) {
                const scrollElement = this.messagesContainerWithScrollingRef.current;
                // Scroll to the top of the message.
                const setScrollTop = panelComponent.ref.current.offsetTop;
                // Do the scrolling.
                doScrollElement(scrollElement, setScrollTop, 0, animate);
                // Update the scroll anchor setting based on this new position.
                this.checkScrollAnchor(true, setScrollTop);
            }
        }
        catch (error) {
            // Just ignore any errors. It's not the end of the world if scrolling doesn't work for any reason.
            consoleError('An error occurred while attempting to scroll.', error);
        }
    }
    /**
     * Get all the elements inside the lastBotMessageGroupID.
     */
    getLastOutputMessageElements() {
        const { localMessageItems, allMessagesByID } = this.props;
        const lastMessageItem = arrayLastValue(localMessageItems);
        const lastMessage = allMessagesByID[lastMessageItem?.fullMessageID];
        if (isResponse(lastMessage)) {
            const elements = [];
            let hasFoundLastBotMessageGroupID = false;
            // Loop from end of messages array until we find the elements with the lastBotMessageGroupID.
            for (let index = localMessageItems.length - 1; index >= 0; index--) {
                const messageItem = localMessageItems[index];
                const componentRef = this.messageRefs.get(messageItem?.ui_state.id);
                if (componentRef) {
                    const { getLocalMessage } = componentRef;
                    if (getLocalMessage().fullMessageID === lastMessage.id) {
                        hasFoundLastBotMessageGroupID = true;
                        const element = componentRef.ref?.current;
                        if (element) {
                            elements.push(element);
                        }
                        else {
                            // If there are no refs to the elements yet, there is nothing to do here.
                            break;
                        }
                    }
                    else if (hasFoundLastBotMessageGroupID) {
                        break;
                    }
                }
            }
            // Reverse so the older messages are first.
            return elements.reverse();
        }
        return [];
    }
    /**
     * JSX to show typing indicator.
     *
     * @param isTypingMessage The aria label for the typing indicator.
     * @param index The index of this message.
     */
    renderTypingIndicator(isTypingMessage, index) {
        return (React__default.createElement("div", { className: `WAC__message WAC__message-${index} WAC__message--lastMessage` },
            React__default.createElement("div", { className: "WAC__message--padding" },
                isTypingMessage && React__default.createElement(AriaLiveMessageExport, { message: isTypingMessage }),
                React__default.createElement("div", { className: "WAC__bot-message" },
                    React__default.createElement("div", { className: "WAC__received WAC__received--loading WAC__message-vertical-padding" },
                        React__default.createElement("div", { className: "WAC__received--inner" },
                            React__default.createElement(InlineLoadingComponent, { loop: true, carbonTheme: this.props.carbonTheme })))))));
    }
    /**
     * Renders the given message.
     *
     * @param localMessage The localMessage to be processed.
     * @param fullMessage The full message to be processed.
     * @param messagesIndex The index of the message.
     * @param showBeforeWelcomeNodeElement Boolean indicating if this is the first message in the most recent welcome
     * node.
     * @param isMessageForInput Indicates if this message is part the most recent message response that allows for input.
     * @param isFirstMessageItem Indicates if this message item is the first item in a message response.
     * @param lastMessageID The ID of the last full message shown.
     */
    renderMessage(localMessage, fullMessage, messagesIndex, showBeforeWelcomeNodeElement, isMessageForInput, isFirstMessageItem, lastMessageID) {
        const { serviceManager, config, languagePack, requestInputFocus, toolingType, persistedToBrowserStorage, botName, messageState, alternateSuggestionsState, locale, botAvatarURL, carbonTheme, useAITheme, } = this.props;
        const inputState = selectInputState(this.props);
        const { isAgentTyping } = selectAgentDisplayState(this.props);
        const { isTypingCounter, isLoadingCounter } = messageState;
        const { chatState } = persistedToBrowserStorage;
        const { disclaimersAccepted } = chatState;
        // If there is a disclaimer, messages should only be rendered once it's accepted.
        if (config.public.disclaimer?.isOn && !disclaimersAccepted[window.location.hostname]) {
            return null;
        }
        const totalMessagesWithTyping = this.props.localMessageItems.length + (isTypingCounter > 0 || isLoadingCounter > 0 || isAgentTyping ? 1 : 0);
        const isLastMessage = messagesIndex === totalMessagesWithTyping - 1;
        const className = cx({
            'WAC__message--firstMessage': messagesIndex === 0,
            'WAC__message--lastMessage': isLastMessage,
            'WAC__message--suggestionsPadding': isLastMessage &&
                alternateSuggestionsState.config.is_on &&
                !persistedToBrowserStorage.chatState.agentState.isConnected,
        });
        // The user can only provide feedback on the last message.
        const allowNewFeedback = localMessage.fullMessageID === lastMessageID;
        // We hide all feedback messages in the agent app.
        const hideFeedback = this.props.config.public.agentAppConfig?.is_on;
        const messageItemID = localMessage.ui_state.id;
        const message = (React__default.createElement(MessageComponent$1, { ref: (component) => {
                if (component) {
                    this.messageRefs.set(messageItemID, component);
                }
                else {
                    this.messageRefs.delete(messageItemID);
                }
            }, className: className, config: config, localMessageItem: localMessage, message: fullMessage, languagePack: languagePack, requestInputFocus: requestInputFocus, serviceManager: serviceManager, toolingType: toolingType, messagesIndex: messagesIndex, botName: botName, disableUserInputs: inputState.isReadonly, isMessageForInput: isMessageForInput, showAvatarLine: isFirstMessageItem, botAvatarURL: botAvatarURL, requestMoveFocus: this.requestMoveFocus, doAutoScroll: this.doAutoScroll, scrollElementIntoView: this.scrollElementIntoView, isFirstMessageItem: isFirstMessageItem, locale: locale, carbonTheme: carbonTheme, useAITheme: useAITheme, allowNewFeedback: allowNewFeedback, hideFeedback: hideFeedback }));
        if (showBeforeWelcomeNodeElement) {
            return (React__default.createElement(LatestWelcomeNodes$1, { welcomeNodeBeforeElement: serviceManager.writeableElements[WriteableElementName.WELCOME_NODE_BEFORE_ELEMENT], key: messageItemID }, message));
        }
        return React__default.createElement(Fragment, { key: messageItemID }, message);
    }
    /**
     * Renders the agent banner that appears at the top of the messages list when connecting to an agent.
     */
    renderAgentBanner() {
        return React__default.createElement(AgentBannerContainer, { bannerRef: this.agentBannerRef, onButtonClick: this.props.onEndAgentChat });
    }
    /**
     * Renders an element that acts as a "handle" for the scroll panel. This is provided to allow the scroll panel to be
     * moved using the keyboard. When this element gets focus the keyboard can be used. Normally we would add
     * tabIndex=0 to the scroll panel itself but that has the unfortunate consequence of causing the scroll panel
     * to get focus when you click on it which we don't want. When this element gets focus it causes an extra class
     * name to be added to the scroll panel which displays a focus indicator on the scroll panel even though it
     * doesn't actually have focus. This element is not actually visible.
     *
     * In addition to providing the ability to scroll the panel, this acts as a button that will move focus to one of
     * the messages inside the scroll panel to provide additional navigation options.
     *
     * @param atTop Indicates if we're rendering the scroll handle at the top or bottom of the scroll panel.
     */
    renderScrollHandle(atTop) {
        const { languagePack } = this.props;
        let labelKey;
        if (IS_MOBILE) {
            labelKey = atTop ? 'messages_scrollHandle' : 'messages_scrollHandleEnd';
        }
        else {
            labelKey = atTop ? 'messages_scrollHandleDetailed' : 'messages_scrollHandleEndDetailed';
        }
        const onClick = IS_MOBILE
            ? undefined
            : () => this.requestMoveFocus(atTop ? MoveFocusType.FIRST : MoveFocusType.LAST, 0);
        return (React__default.createElement("button", { type: "button", className: "WACMessages--scrollHandle", ref: this.scrollHandleRef, tabIndex: 0, "aria-label": languagePack[labelKey] || languagePack.messages_scrollHandle, onClick: onClick, onFocus: () => this.setState({ scrollHandleHasFocus: true }), onBlur: () => this.setState({ scrollHandleHasFocus: false }) }));
    }
    /**
     * As soon as the user sends a message, we want to disable all the previous message responses to prevent the user
     * from interacting with them again. However, if the user's message results in an error, we want to re-enable the
     * last response from the bot to prevent the user from getting stuck in the case where the input bar is disabled.
     * This function returns the id of the last message that is permitted to be enabled.
     */
    getMessageIDForUserInput() {
        const { localMessageItems, allMessagesByID } = this.props;
        for (let index = localMessageItems.length - 1; index >= 0; index--) {
            const message = localMessageItems[index];
            const originalMessage = allMessagesByID[message.fullMessageID];
            if (isRequest(originalMessage) && originalMessage?.history?.error_state !== MessageErrorState.FAILED) {
                // If we find a request that was not an error, then we need to disable everything.
                return null;
            }
            if (isResponse(originalMessage)) {
                // If we didn't find a successful request, then the first response we find can be enabled.
                return message.fullMessageID;
            }
        }
        // Nothing should be enabled.
        return null;
    }
    /**
     * Returns an array of React elements created by this.renderMessage starting from a given index and until the end of
     * the array OR optionally until we hit a new welcome node.
     *
     * @param messageIDForInput The ID of the last message response that can receive input.
     */
    renderMessages(messageIDForInput) {
        const { localMessageItems, allMessagesByID } = this.props;
        const renderMessageArray = [];
        const lastMessageID = arrayLastValue(localMessageItems)?.fullMessageID;
        let previousMessageID = null;
        for (let currentIndex = 0; currentIndex < localMessageItems.length; currentIndex++) {
            const localMessageItem = localMessageItems[currentIndex];
            const fullMessage = allMessagesByID[localMessageItem.fullMessageID];
            const isMessageForInput = messageIDForInput === localMessageItem.fullMessageID;
            const isFirstMessageItem = previousMessageID !== localMessageItem.fullMessageID;
            const showBeforeWelcomeNodeElement = localMessageItem.ui_state.isWelcomeResponse && isFirstMessageItem;
            previousMessageID = localMessageItem.fullMessageID;
            renderMessageArray.push(this.renderMessage(localMessageItem, fullMessage, currentIndex, showBeforeWelcomeNodeElement, isMessageForInput, isFirstMessageItem, lastMessageID));
        }
        return renderMessageArray;
    }
    render() {
        const { localMessageItems, messageState, intl, botName, serviceManager, notifications, persistedToBrowserStorage } = this.props;
        const isConnectedToAgent = persistedToBrowserStorage.chatState.agentState.isConnected;
        const { isTypingCounter, isLoadingCounter } = messageState;
        const { isAgentTyping } = selectAgentDisplayState(this.props);
        const { scrollHandleHasFocus } = this.state;
        const messageIDForInput = this.getMessageIDForUserInput();
        const regularMessages = this.renderMessages(messageIDForInput);
        let isTypingMessage;
        if (isAgentTyping) {
            isTypingMessage = intl.formatMessage({ id: 'messages_agentIsTyping' });
        }
        else if (isTypingCounter) {
            isTypingMessage = intl.formatMessage({ id: 'messages_botIsTyping' }, { botName });
        }
        else if (isLoadingCounter) {
            isTypingMessage = intl.formatMessage({ id: 'messages_botIsLoading' }, { botName });
        }
        return (React__default.createElement("div", { id: `WACMessages--holder${serviceManager.namespace.suffix}`, className: "WACMessages--holder" },
            this.renderAgentBanner(),
            React__default.createElement("div", { className: cx('WACMessages__Wrapper', {
                    'WACMessages__Wrapper--scrollHandleHasFocus': scrollHandleHasFocus,
                }) },
                React__default.createElement("div", { id: `WAC__messages${serviceManager.namespace.suffix}`, className: "WAC__messages", ref: this.messagesContainerWithScrollingRef, onScroll: () => this.checkScrollAnchor(), role: "list" },
                    this.renderScrollHandle(true),
                    regularMessages,
                    (Boolean(isTypingCounter) || Boolean(isLoadingCounter) || isAgentTyping) &&
                        this.renderTypingIndicator(isTypingMessage, localMessageItems.length),
                    React__default.createElement(Notifications, { serviceManager: serviceManager, notifications: notifications }),
                    this.renderScrollHandle(false))),
            !isConnectedToAgent && React__default.createElement(AlternateSuggestionsButtonExport, null)));
    }
}
function debugAutoScroll(message, ...args) {
}
var MessagesComponent$1 = withServiceManager(connect((state) => state, null, null, {
    forwardRef: true,
})(MessagesComponent));

/**
 * Creates a memoizer that will take an array of keys and a map of those keys to values and return an array of
 * values that corresponds to those keys.
 *
 * This is optimized to only return a new array of values if the array would actually contain different values
 * (either in a different order or different values). It performs element-wise comparisons of both the requested
 * array of keys as well as only the specific values from the map. If the map has extra values that are not used,
 * those will be ignored.
 *
 * For example:
 * ['key1'], {key1: 'value1', key2: 'value2'} => ['value1']
 */
function createUnmappingMemoizer() {
    return memoizeOne((keys, map) => keys.map(key => map[key]), isUnmappingEqual);
}
/**
 * This function will determine if any of the specific messages used by this class have changed. Note: this could
 * be a little more efficient if it was pushed up higher in the component chain.
 */
function isUnmappingEqual(newArgs, oldArgs) {
    const [keys1, map1] = newArgs;
    const [keys2, map2] = oldArgs;
    if (keys1 === keys2 && map1 === map2) {
        // Both sets of arguments are the same.
        return true;
    }
    if (keys1.length !== keys2.length) {
        // The array are different sizes, so the values will not be the same.
        return false;
    }
    // Check each value one by one.
    for (let index = 0; index <= keys1.length; index++) {
        const key1 = keys1[index];
        const key2 = keys2[index];
        if (key1 !== key2) {
            // If a key is different, the values will be different.
            return false;
        }
        const value1 = map1[key1];
        const value2 = map2[key2];
        if (value1 !== value2) {
            // A value was found to be different.
            return false;
        }
    }
    // Everything found to be the same.
    return true;
}

function AlternateSuggestionsBackground() {
    const panelState = useSelector(state => state.alternateSuggestionsState.panelState);
    const serviceManager = useServiceManager();
    if (panelState === AlternateSuggestionsPanelState.CLOSED) {
        return null;
    }
    // If the user clicks on this background, then auto-close the panel.
    const onClick = () => serviceManager.store.dispatch(actions.setAlternateSuggestionsPanelState(AlternateSuggestionsPanelState.CLOSED, 'Event'));
    // eslint-disable-next-line
    return React__default.createElement("div", { className: "WACBackgroundCover", onClick: onClick });
}
const AlternateSuggestionsBackgroundExport = React__default.memo(AlternateSuggestionsBackground);

function useLayoutEffectDidUpdate(effect, deps) {
    const hasRunRef = useRef(false);
    useLayoutEffect(() => {
        if (hasRunRef.current) {
            return effect();
        }
        hasRunRef.current = true;
        return undefined;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
}

/**
 * Compares two numbers in a way that is compatible with the sort() method; it will result in an ascending sort order.
 *
 * @param {number} number1 First number to be compared.
 * @param {number} number2 Second number to be compared.
 * @returns {number} -1 if the first number is smaller than the second one; 1 if the reverse is true;
 * or 0 if both numbers are equal.
 */
function compareNumbers(number1, number2) {
    if (number1 < number2) {
        return -1;
    }
    if (number1 > number2) {
        return 1;
    }
    return 0;
}

const PDFViewerContainer = lazyPDFViewer();
function OpenPDFButton(props) {
    const { buttonLabel, documentTitle, url, tooltipText } = props;
    const direction = document.dir;
    const [showPDFViewer, setShowPDFViewer] = useState(false);
    const onClose = useCallback(() => setShowPDFViewer(false), []);
    return (React__default.createElement("div", { className: "WACOpenPDFButton" },
        React__default.createElement(Button, { className: "WACOpenPDFButton__Button", kind: "tertiary", iconDescription: tooltipText || buttonLabel, hasIconOnly: true, size: "sm", tooltipPosition: direction === 'rtl' ? 'left' : 'right', "aria-label": buttonLabel, onClick: () => setShowPDFViewer(true) },
            React__default.createElement(DocumentView, null)),
        React__default.createElement("div", { className: "WACOpenPDFButton__Text WACWidget__textEllipsis" }, buttonLabel),
        showPDFViewer && (React__default.createElement(Suspense, { fallback: React__default.createElement(LoadingPDFViewer, null) },
            React__default.createElement(PDFViewerContainer, { key: url, url: url, title: documentTitle, onClose: onClose })))));
}

const trackProps = {
    eventName: 'Assistant Message Received from User',
    type: 'search',
};
const suggestionsTrackProps = {
    eventName: 'Suggestion Selected',
    eventDescription: 'User clicks on search suggestions bookmark',
    type: 'Search',
};
function SearchResultComponentBody(props) {
    const { answer, title, titleIsURL, body, shouldTruncateBody, url, innerRef, showBookmarkButton, urlIsValid, disablePDFViewer, } = props;
    const languagePack = useLanguagePack();
    const serviceManager = useServiceManager();
    const intl = useIntl();
    const windowWidth = useWindowSize().width;
    const direction = document.dir;
    const [bodyExpanded, setBodyExpanded] = useState(props.isBookmark);
    const toggleExpandedState = () => {
        setBodyExpanded(!bodyExpanded);
    };
    function onBookmarkClick() {
        serviceManager.actions.track(suggestionsTrackProps);
        if (props.onBookmarkClick) {
            props.onBookmarkClick();
        }
    }
    function onLinkClick(event) {
        serviceManager.actions.track(trackProps);
        if (!props.url) {
            // If the link doesn't actually have any content, we need to stop the browser's normal open behavior
            event.preventDefault();
        }
    }
    function renderLink() {
        if (urlIsValid) {
            const label = getSearchLinkLabel(title, url, titleIsURL);
            if (shouldShowAsPDFButton(url, windowWidth, disablePDFViewer)) {
                const buttonLabel = intl.formatMessage({ id: 'messages_searchResultsOpenDocumentWithLabel' }, { documentName: label });
                return (React__default.createElement(OpenPDFButton, { buttonLabel: buttonLabel, documentTitle: label, url: url, tooltipText: languagePack.messages_searchResultsOpenDocument }));
            }
            return (React__default.createElement("a", { onClick: onLinkClick, className: "WAC__button--link", href: url || '#', target: "_blank", rel: "noopener noreferrer" },
                React__default.createElement("div", { className: "WACSearchMessage__TitleUrl WACWidget__textEllipsis" }, label)));
        }
        return React__default.createElement("div", { className: "WACSearchMessage__TitleUrl WACWidget__textEllipsis" }, title || url);
    }
    // If the user interacts with a search result we send an event to the back-end for tracking.
    // App looks for clicks on this attribute to send that event.
    const bodyContent = body && (React__default.createElement("div", { className: cx({
            WAC__searchResponseBody: shouldTruncateBody,
            'WACSearchMessage__Result--full': bodyExpanded,
        }) }, body));
    let icon;
    if (showBookmarkButton) {
        icon = (React__default.createElement(Button, { renderIcon: Bookmark, iconDescription: languagePack.suggestions_searchBookmarkButtonDescription, hasIconOnly: true, tooltipPosition: direction === 'rtl' ? 'right' : 'left', onClick: onBookmarkClick, kind: "ghost", size: "sm" }));
    }
    else if (body && shouldTruncateBody) {
        icon = (React__default.createElement(Button, { renderIcon: shouldTruncateBody && !bodyExpanded ? ChevronDown : ChevronUp, iconDescription: shouldTruncateBody && !bodyExpanded
                ? languagePack.messages_searchResultsExpand
                : languagePack.messages_searchResultsCollapse, hasIconOnly: true, tooltipPosition: direction === 'rtl' ? 'right' : 'left', onClick: toggleExpandedState, kind: "ghost", size: "sm" }));
    }
    // Content is the actual content of the result cards
    let content;
    // ResultBottom is the title/url combination to display at the bottom of the card.
    let resultBottom;
    if (title || url) {
        resultBottom = renderLink();
        content = bodyContent;
    }
    else if (body) {
        content = bodyContent;
    }
    return (React__default.createElement("div", { className: cx('WAC__searchResult', {
            'WACSearchMessage--hasAnswer': answer,
            'WACSearchMessage--hasContent': content,
        }), ref: innerRef },
        content && React__default.createElement("div", { className: "WACSearchMessage__ResultContent" }, content),
        React__default.createElement("div", { className: "WACSearchMessage__ResultBottom-wrapper" },
            resultBottom && React__default.createElement("div", { className: "WACSearchMessage__ResultBottom" }, resultBottom),
            icon && React__default.createElement("div", { className: "WACSearchMessage__ResultIcon" }, icon))));
}

/**
 * Displays a single search result from a search response message that can have multiple results.
 */
// This is set to how many characters can show BEFORE we include the ellipsis styling. Note that the actual
// number of characters displayed may be more or less than this number because the position of the ellipsis is
// decided by the browser.
const CHARACTERS_FOR_ELLIPSIS = 200;
function SearchResultComponent(props) {
    const { searchResult, announceOnMount, onBookmarkClick, showBookmarkButton, isBookmark } = props;
    const disablePDFViewer = useSelector((state) => state.config.public.disablePDFViewer);
    const charactersForEllipsis = props.charactersForEllipsis || CHARACTERS_FOR_ELLIPSIS;
    // If requested, make sure this element gets announced when it is mounted.
    const ref = useRef();
    const ariaAnnouncer = useContext(AriaAnnouncerContext);
    useEffect(() => {
        if (ref.current && announceOnMount) {
            ariaAnnouncer(ref.current);
        }
    }, [announceOnMount, ariaAnnouncer]);
    const onBookmarkClickInternal = useCallback(() => {
        if (onBookmarkClick) {
            onBookmarkClick(searchResult);
        }
    }, [onBookmarkClick, searchResult]);
    const { title, url, titleIsURL, urlIsValid, answer } = getSearchResultMetaData(searchResult);
    return ((title || hasSearchResultBody(searchResult)) && (React__default.createElement(SearchResultComponentBody, { answer: answer, title: title, titleIsURL: titleIsURL, body: React__default.createElement(SearchResultBodyExport, { searchResult: searchResult }), shouldTruncateBody: shouldTruncateBody(searchResult, charactersForEllipsis), urlIsValid: urlIsValid, url: url, innerRef: ref, onBookmarkClick: onBookmarkClickInternal, showBookmarkButton: showBookmarkButton, isBookmark: isBookmark, disablePDFViewer: disablePDFViewer })));
}

/**
 * The default number of results to show. Any additional results are dropped and not displayed.
 */
const DEFAULT_RESULTS_TO_SHOW = 3;
class SearchMessage extends PureComponent {
    constructor() {
        super(...arguments);
        this.state = { showMoreResults: false };
        /**
         * A (memoized) sorted list of the results in this component. This is sorted by confidence with the highest score
         * being first in the array. This is likely unneeded as the search results appear to come back already sorted by
         * confidence. An issue has been created to re-visit this code and remove if unneeded when not under time crunch.
         */
        this.sortedResults = memoizeOne((results) => filterAndSortResults(results));
        this.toggleShowMore = () => {
            this.setState(state => ({ showMoreResults: !state.showMoreResults }));
        };
    }
    /**
     * Creates components for each of the responses that we want to render.
     */
    getSearchResultComponents(results, countToShow, areAdditionResults) {
        const { charactersForEllipsis, onBookmarkClick, showBookmarkButton, isBookmark } = this.props;
        const responseComponents = [];
        for (let index = 0; index < countToShow; index++) {
            const item = results[index];
            const searchResultComponent = (React__default.createElement(SearchResultComponent, { searchResult: item, announceOnMount: areAdditionResults, charactersForEllipsis: charactersForEllipsis, onBookmarkClick: onBookmarkClick, showBookmarkButton: showBookmarkButton, isBookmark: isBookmark }));
            responseComponents.push(React__default.createElement("li", { key: index },
                React__default.createElement("div", null,
                    React__default.createElement("div", { className: "WACSearchMessage__ResultDivider" }),
                    searchResultComponent)));
        }
        return responseComponents;
    }
    /**
     * Creates components for each of the responses that we want to render.
     */
    renderSearchResults() {
        const { primary_results = [], results = [] } = this.props.searchResultItem;
        let sortedResults;
        // Figure out how many results to show.
        let countToShow;
        // The API proposal https://github.ibm.com/Watson/developer-cloud--api-proposals/pull/293 introduced a breaking
        // change to switch to display primary and additional search results instead of results. We need to also support
        // the old behavior so check to see if we've got any of the new results.
        if (primary_results.length > 0) {
            sortedResults = this.sortedResults(primary_results);
            countToShow = sortedResults.length;
        }
        else {
            // Handle the legacy behavior.
            sortedResults = this.sortedResults(results);
            countToShow = Math.min(sortedResults.length, DEFAULT_RESULTS_TO_SHOW);
        }
        return this.getSearchResultComponents(sortedResults, countToShow, false);
    }
    /**
     * Creates components for each of the responses for additional_results that we want to render.
     */
    renderAdditionalResults() {
        const sortedAdditionalResults = this.sortedResults(this.props.searchResultItem.additional_results || []);
        return this.getSearchResultComponents(sortedAdditionalResults, sortedAdditionalResults.length, true);
    }
    render() {
        const { searchResultItem, languagePack, hideHeader } = this.props;
        const { showMoreResults } = this.state;
        const { header, title } = searchResultItem;
        const primaryResults = this.renderSearchResults();
        return (React__default.createElement("div", { className: "WACSearchMessage" },
            !hideHeader && (React__default.createElement("div", { className: "WAC__searchMessage--metablock" }, header || title || languagePack.messages_searchResults)),
            primaryResults.length > 0 && (React__default.createElement(Tile, { className: "WACSearchMessage__ResultsTile" },
                React__default.createElement("ul", { className: "WACSearchMessage__Results" },
                    primaryResults,
                    showMoreResults && this.renderAdditionalResults()))),
            !showMoreResults && Boolean(searchResultItem.additional_results?.length) && (React__default.createElement("button", { onClick: this.toggleShowMore, className: "WACSearchMessage__ToggleShowMore WAC__button--link", type: "button" },
                React__default.createElement("span", null, languagePack.showMoreResults),
                React__default.createElement(ChevronDown, null)))));
    }
}
/**
 * Filters and sorts a copy of the given search results. This is sorted by confidence from highest to lowest.
 * first in the array. The original array is not modified.
 */
function filterAndSortResults(results) {
    if (!results) {
        return [];
    }
    const filteredResults = results.filter(isNotEmptyResult);
    return filteredResults.sort(compareResults);
}
/**
 * Compares two search results by confidence with the highest confidence being first.
 */
function compareResults(result1, result2) {
    const score1 = result1 && result1.result_metadata ? result1.result_metadata.confidence : 0;
    const score2 = result2 && result2.result_metadata ? result2.result_metadata.confidence : 0;
    return -compareNumbers(score1, score2);
}
/**
 * Determines if the given search result has any content.
 */
function isNotEmptyResult(result) {
    const isEmpty = !result ||
        (isEmptyString(result.body) &&
            isEmptyString(result.url) &&
            isEmptyString(result.title) &&
            isEmptyString(result.answers?.[0]?.text) &&
            isEmptyString(result.highlight?.title?.[0]) &&
            isEmptyString(result.highlight?.body?.[0]));
    return !isEmpty;
}
const SearchMessageExport = React__default.memo(SearchMessage);

/**
 * This components renders a tile that looks like a search tile in a skeleton state.
 */
function SearchTileSkeleton() {
    return (React__default.createElement(Tile, { className: "WACSearchTileSkeleton" },
        React__default.createElement("div", { className: "WACSearchTileSkeleton__Title" },
            React__default.createElement(SkeletonText, { heading: true })),
        React__default.createElement("div", { className: "WACSearchTileSkeleton__Text" },
            React__default.createElement(SkeletonText, { paragraph: true, lineCount: 2 })),
        React__default.createElement("div", { className: "WACSearchTileSkeleton__ResultDivider" })));
}

const { CLOSED: CLOSED$1, OPEN_FULL: OPEN_FULL$1, OPEN_STRIKE: OPEN_STRIKE$1 } = AlternateSuggestionsPanelState;
const { CONTACT_OPTIONS, ALTERNATE_RESPONSES, STARTERS } = AlternateSuggestionsSection;
function AlternateSuggestionsOptionsPanel(props) {
    const { requestInputFocus, alternateSuggestionsState, showSearchSkeleton, languagePack } = props;
    const closeButtonRef = useRef();
    const serviceManager = useServiceManager();
    const { panelState, suggestionListItems, sourceResponse, starters, contactItem, searchResults, allowCollapse, showSearchSection, } = alternateSuggestionsState;
    const { suggestions_sectionTitleAlternateResponses, suggestions_sectionTitleSearch, suggestions_sectionTitleContact, suggestions_sectionTitleStarters, suggestions_ariaPanelButtonToggle, suggestions_ariaButtonToClose, suggestions_title, } = languagePack;
    const onCloseClick = useCallback(() => {
        serviceManager.store.dispatch(actions.setAlternateSuggestionsPanelState(CLOSED$1, 'User'));
    }, [serviceManager]);
    const onToggleClick = useCallback(() => {
        const newState = panelState === OPEN_FULL$1 ? OPEN_STRIKE$1 : OPEN_FULL$1;
        serviceManager.store.dispatch(actions.setAlternateSuggestionsPanelState(newState, 'User'));
    }, [serviceManager, panelState]);
    const onBookmarkClick = useCallback((searchResult) => {
        injectSearchResultMessage(searchResult, serviceManager);
    }, [serviceManager]);
    const onButtonClick = (selectedItem, index, section) => {
        serviceManager.actions.sendSuggestionChoice(selectedItem, sourceResponse, index, section);
        // Move focus back to the input field.
        setTimeout(requestInputFocus);
    };
    useLayoutEffect(() => {
        doFocus(closeButtonRef.current);
    }, []);
    function renderSearchResults() {
        if (!showSearchSection) {
            return null;
        }
        let content;
        if (showSearchSkeleton) {
            content = (React__default.createElement("div", { className: "WACAlternateSuggestionsOptionsPanel__SearchSkeleton" },
                React__default.createElement(SearchTileSkeleton, null),
                React__default.createElement(SearchTileSkeleton, null),
                React__default.createElement(SearchTileSkeleton, null)));
        }
        else if (!searchResults) {
            content = (React__default.createElement("div", { className: "WACAlternateSuggestionsOptionsPanel__NoSearchResults" },
                React__default.createElement(SatelliteRadar, { size: 24 }),
                React__default.createElement("div", null, languagePack.suggestions_noSearchResults)));
        }
        else {
            content = (React__default.createElement(SearchMessageExport, { serviceManager: serviceManager, searchResultItem: searchResults, languagePack: languagePack, charactersForEllipsis: 100, onBookmarkClick: onBookmarkClick, showBookmarkButton: true, hideHeader: true }));
        }
        return (React__default.createElement(PanelSection, { label: suggestions_sectionTitleSearch, testId: "Search" }, content));
    }
    return (React__default.createElement("div", { className: cx('WACAlternateSuggestionsOptionsPanel', {
            'WACAlternateSuggestionsOptionsPanel--Full': panelState === OPEN_FULL$1,
            'WACAlternateSuggestionsOptionsPanel--Strike': panelState === OPEN_STRIKE$1,
        }) },
        React__default.createElement("div", { className: "WACAlternateSuggestionsOptionsPanel__Header" },
            React__default.createElement("div", { className: "WACAlternateSuggestionsOptionsPanel__Icon" },
                React__default.createElement(QuestionMarkIcon, null)),
            React__default.createElement("div", { className: "WACAlternateSuggestionsOptionsPanel__Title" }, suggestions_title),
            allowCollapse && (React__default.createElement(Button, { type: "button", kind: ButtonKindEnum.GHOST, className: "WACAlternateSuggestionsOptionsPanel__HeaderButton", onClick: onToggleClick, "aria-label": suggestions_ariaPanelButtonToggle, "data-test-id": "Toggle" },
                panelState === OPEN_STRIKE$1 && React__default.createElement(UpToTop, { size: 20 }),
                panelState === OPEN_FULL$1 && React__default.createElement(DownToBottom, { size: 20 }))),
            React__default.createElement(Button, { type: "button", kind: ButtonKindEnum.GHOST, className: "WACAlternateSuggestionsOptionsPanel__HeaderButton", onClick: onCloseClick, "aria-label": suggestions_ariaButtonToClose, ref: closeButtonRef, "data-test-id": "Close" },
                React__default.createElement(Close, { size: 20 }))),
        React__default.createElement("div", { className: "WACAlternateSuggestionsOptionsPanel__ScrollPanel" },
            Boolean(suggestionListItems?.length) && (React__default.createElement(ButtonsPanel, { items: suggestionListItems, label: suggestions_sectionTitleAlternateResponses, onButtonClick: onButtonClick, section: ALTERNATE_RESPONSES, testId: "AlternateResponses" })),
            panelState === OPEN_FULL$1 && renderSearchResults(),
            contactItem && (React__default.createElement(ContactOptionButton, { item: contactItem, label: suggestions_sectionTitleContact, onButtonClick: onButtonClick })),
            Boolean(starters?.length) && (React__default.createElement(ButtonsPanel, { items: starters, label: suggestions_sectionTitleStarters, onButtonClick: onButtonClick, section: STARTERS, testId: "Starters" })))));
}
/**
 * This panel renders a list of buttons.
 */
function ButtonsPanel(props) {
    const { items, label, onButtonClick, section, testId } = props;
    return (React__default.createElement(PanelSection, { label: label, testId: testId },
        React__default.createElement("ul", { className: "WAC__button-holder" }, items &&
            items.map((item, index) => {
                let clickSection = section;
                let label;
                if (isSuggestionListItem(item)) {
                    label = item.option.label;
                    // If the user clicks on the contact option (which can appear either in its own section or added to the
                    // end of the alternate responses section) we need to use a different index and section.
                    if (item.isContactSuggestion) {
                        clickSection = AlternateSuggestionsSection.CONTACT_OPTIONS;
                    }
                }
                else {
                    label = item.label;
                }
                return (
                // eslint-disable-next-line react/no-array-index-key
                React__default.createElement("li", { key: index },
                    React__default.createElement(unstable__ChatButton, { type: "button", size: ButtonSizeEnum.SMALL, kind: ButtonKindEnum.TERTIARY, isQuickAction: true, disabled: item.isSelected, isSelected: item.isSelected, onClick: () => onButtonClick(item, index, clickSection) }, label)));
            }))));
}
/**
 * The wrapper for a section of the panel.
 */
function PanelSection(props) {
    return (React__default.createElement("div", { className: "WACAlternateSuggestionsOptionsPanel__Section", "data-test-id": props.testId },
        React__default.createElement("div", { className: "WACAlternateSuggestionsOptionsPanel__SectionTitle" },
            React__default.createElement("div", { className: "WACAlternateSuggestionsOptionsPanel__SectionTitleLabel" }, props.label),
            React__default.createElement("div", { className: "WACAlternateSuggestionsOptionsPanel__SectionTitleDivider" })),
        props.children));
}
/**
 * The contact option button.
 */
function ContactOptionButton(props) {
    const { item, label, onButtonClick } = props;
    return (React__default.createElement(PanelSection, { label: label, testId: "Contact" },
        React__default.createElement("div", { className: "WACAlternateSuggestionsOptionsPanel__ContactOption" },
            React__default.createElement(Button, { type: "button", kind: ButtonKindEnum.GHOST, disabled: item.isSelected, onClick: () => onButtonClick(item, 0, CONTACT_OPTIONS) },
                React__default.createElement("div", { className: "WACAlternateSuggestionsOptionsPanel__ContactOptionInner" },
                    React__default.createElement("div", { className: "WACAlternateSuggestionsOptionsPanel__ContactOptionInnerColor" }),
                    React__default.createElement("div", { className: "WACAlternateSuggestionsOptionsPanel__ContactOptionInnerText" }, item.option.label),
                    React__default.createElement("div", { className: "WACAlternateSuggestionsOptionsPanel__ContactOptionInnerIcon" },
                        React__default.createElement(HelpDesk, { size: 20 })))))));
}
/**
 * This will inject a message for the given search result into the main message list.
 */
function injectSearchResultMessage(searchResult, serviceManager) {
    // Clicking a search result will insert this search result into the main message list (and also send and update
    // event to the back-end to make sure it gets stored in history).
    const { suggestions_searchBookmarkHeader, suggestions_searchBookmarkFooter } = serviceManager.store.getState().languagePack;
    const newMessage = {
        id: null,
        thread_id: THREAD_ID_MAIN,
        output: {
            generic: [
                assertType({
                    response_type: MessageResponseTypes.SEARCH,
                    primary_results: [searchResult],
                    header: suggestions_searchBookmarkHeader,
                }),
                createTextItem(suggestions_searchBookmarkFooter),
            ],
        },
        history: {
            is_bookmark: true,
        },
    };
    serviceManager.actions.insertLocalMessageResponse(newMessage, true);
    // Close the suggestions menu.
    serviceManager.store.dispatch(actions.setAlternateSuggestionsPanelState(AlternateSuggestionsPanelState.CLOSED, 'Search'));
}

const { CLOSED, OPEN_FULL, OPEN_STRIKE } = AlternateSuggestionsPanelState;
let shouldDisableFocusTrap = false;
function AlternateSuggestionsContainer(props) {
    const { requestInputFocus } = props;
    const alternateSuggestionsState = useSelector((state) => state.alternateSuggestionsState);
    const languagePack = useSelector((state) => state.languagePack);
    const isChatOpen = useSelector((state) => state.persistedToBrowserStorage.launcherState.viewState.mainWindow);
    const isHidden = useContext(HideComponentContext);
    const { panelState, searchResultsLoading } = alternateSuggestionsState;
    const serviceManager = useServiceManager();
    const containerRef = useRef();
    const previousHeightRef = useRef(0);
    const prevStateRef = useRef(alternateSuggestionsState);
    // This is called when the focus trap becomes deactivated (like if the user presses Escape). We'll close the panel
    // when that happens.
    const onDeactivate = useCallback(() => {
        serviceManager.store.dispatch(actions.setAlternateSuggestionsPanelState(CLOSED, 'User'));
    }, [serviceManager]);
    const newClassName = getPanelClassName(panelState);
    useLayoutEffectDidUpdate(() => {
        // At this point the components have been rendered to the DOM in the desired end state. We need to examine the
        // previous state to determine what changed and decide if we need to animate a transition from that previous
        // state to the current state. If so, we'll use the current state to tells us the end goal and then we'll reset
        // the elements manually to the previous state and execute the animation between them.
        const { panelState: prevPanelState } = prevStateRef.current;
        if (panelState !== prevPanelState) {
            if (panelState !== CLOSED) {
                // The panel is currently in its end state so we'll grab the current height and then animate from the
                // previous height to the current height.
                const startHeight = `${previousHeightRef.current}px`;
                const endHeight = `${containerRef.current.offsetHeight}px`;
                if (containerRef.current?.style?.setProperty) {
                    containerRef.current.style.setProperty('--cds-chat-Animate-Start-Height', startHeight);
                    containerRef.current.style.setProperty('--cds-chat-Animate-End-Height', endHeight);
                    animateWithClass(containerRef.current, 'WACAlternateSuggestionsContainer--animate', 333, () => {
                        if (containerRef.current) {
                            containerRef.current.style.height = null;
                            previousHeightRef.current = containerRef.current.offsetHeight;
                        }
                    });
                }
            }
            else {
                previousHeightRef.current = 0;
            }
        }
        prevStateRef.current = alternateSuggestionsState;
    });
    return (panelState !== CLOSED && (React__default.createElement("div", { className: `WACAlternateSuggestionsContainer ${newClassName}`, ref: containerRef },
        React__default.createElement(FocusTrap$1, { active: isChatOpen && !isHidden && !shouldDisableFocusTrap, focusTrapOptions: { clickOutsideDeactivates: true, onDeactivate, returnFocusOnDeactivate: !IS_MOBILE } },
            React__default.createElement("div", { className: "WACAlternateSuggestionsContainer__FocusTrap" },
                React__default.createElement(AlternateSuggestionsOptionsPanel, { alternateSuggestionsState: alternateSuggestionsState, requestInputFocus: requestInputFocus, panelState: panelState, showSearchSkeleton: searchResultsLoading && panelState === OPEN_FULL, languagePack: languagePack }))))));
}
/**
 * Returns the classname for the container that represents the given panel state.
 */
function getPanelClassName(panelState) {
    switch (panelState) {
        case OPEN_STRIKE:
            return 'WACAlternateSuggestionsContainer--Strike';
        case OPEN_FULL:
            return 'WACAlternateSuggestionsContainer--Full';
        default:
            return 'WACAlternateSuggestionsContainer--Closed';
    }
}
const AlternateSuggestionsContainerExport = React__default.memo(AlternateSuggestionsContainer);

const StopFilled16svg = toString$1(StopFilled16);
function stopStreamingButtonTemplate({ label, disabled, tooltipAlignment, onClick, }) {
    return html$2 `
    <cds-custom-icon-button
      class="${CSS_CLASS_PREFIX}-stop-streaming-button"
      align="${tooltipAlignment}"
      kind="${ButtonKindEnum.GHOST}"
      size="${ButtonSizeEnum.SMALL}"
      ?disabled=${disabled}
      @click="${onClick}"
    >
      <span class="${disabled ? `${CSS_CLASS_PREFIX}-stop-icon` : ''}" slot="icon">${unsafeSVG(StopFilled16svg)}</span>
      <span slot="tooltip-content">${label}</span>
    </cds-custom-icon-button>
  `;
}

var css_248z = ".cds--aichat-stop-icon svg{fill:var(--cds-icon-disabled,hsla(0,0%,9%,.25))}";

class StopStreamingButtonElement extends LitElement {
    static { this.styles = css `
    ${unsafeCSS(css_248z)}
  `; }
    /**
     * Called when the user clicks the stop streaming button.
     */
    _handleOnClick() {
        this.onClick();
    }
}
__decorate([
    property({ type: String, attribute: 'label' })
], StopStreamingButtonElement.prototype, "label", void 0);
__decorate([
    property({ type: String, attribute: 'tooltip-alignment' })
], StopStreamingButtonElement.prototype, "tooltipAlignment", void 0);
__decorate([
    property({ type: Boolean, attribute: 'disabled' })
], StopStreamingButtonElement.prototype, "disabled", void 0);
__decorate([
    property({ type: Object })
], StopStreamingButtonElement.prototype, "onClick", void 0);
__decorate([
    bind_1
], StopStreamingButtonElement.prototype, "_handleOnClick", null);

const STOP_STREAMING_BUTTON_TAG_NAME = 'cds-aichat-stop-streaming-button';
/**
 * Constructed class functionality for the stop streaming button.
 */
let CDSChatStopStreamingButtonElement = class CDSChatStopStreamingButtonElement extends StopStreamingButtonElement {
    render() {
        return stopStreamingButtonTemplate(this);
    }
};
CDSChatStopStreamingButtonElement = __decorate([
    carbonElement(STOP_STREAMING_BUTTON_TAG_NAME)
], CDSChatStopStreamingButtonElement);
var CDSChatStopStreamingButtonElement$1 = CDSChatStopStreamingButtonElement;

const StopStreamingButton = o$1({
    tagName: 'cds-aichat-stop-streaming-button',
    elementClass: CDSChatStopStreamingButtonElement$1,
    react: React__default,
});

/**
 * A simple class that can track a set of listeners and then fire them when needed.
 */
class ListenerList {
    constructor() {
        /**
         * The current set of listeners.
         */
        this.listeners = [];
    }
    /**
     * Adds the given listener to the list.
     */
    addListener(listenerToAdd) {
        this.listeners = [...this.listeners, listenerToAdd];
    }
    /**
     * Removes all occurrences of the given listener from the list.
     */
    removeListener(listenerToRemove) {
        this.listeners = this.listeners.filter(listener => listener !== listenerToRemove);
    }
    /**
     * Calls all the listeners in the list passing each the given arguments.
     */
    fireListeners(...args) {
        if (this.listeners.length) {
            this.listeners.forEach(listener => listener(...args));
        }
    }
}

class TextArea extends PureComponent {
    constructor() {
        super(...arguments);
        /**
         * A React ref to the TextArea component.
         */
        this.textAreaRef = React__default.createRef();
    }
    static { this.defaultProps = {
        // Default value for whether input in the text area is required before form submission.
        isRequired: false,
        // Default max character length for a text area is 10,000 characters.
        maxLength: 10000,
    }; }
    /**
     * Returns the HTML element.
     */
    getHTMLElement() {
        return this.textAreaRef.current;
    }
    /**
     * Instructs this component to put focus into the input text area.
     */
    takeFocus() {
        doFocusRef(this.textAreaRef, false, true);
    }
    /**
     * Causes the text area to blur.
     */
    doBlur() {
        this.textAreaRef.current.blur();
    }
    render() {
        const { isRequired, name, id, onFocus, onBlur, onClick, onChange, onKeyDown, rows, value, autoSize, maxLength, disabled, placeholder, ariaLabel, testId, onSelect, } = this.props;
        // The extra ' ' in the sizer div below makes sure there's at least a space in the area to ensure that we get a
        // min-height of one line of text.
        return (React__default.createElement("div", { className: cx('WAC__TextArea', { 'WAC__TextArea--autoSize': autoSize, 'WAC__TextArea--disabled': disabled }) },
            React__default.createElement("textarea", { ref: this.textAreaRef, "aria-label": ariaLabel, "aria-required": isRequired, className: "WAC__TextArea-textarea", disabled: disabled, id: id, maxLength: maxLength, name: name, onFocus: onFocus, onBlur: onBlur, onClick: onClick, onChange: onChange, onKeyDown: onKeyDown, onSelect: onSelect, placeholder: placeholder, rows: rows, value: value || '', "data-enable-grammarly": false, "data-test-id": testId }),
            autoSize && React__default.createElement("div", { className: "WAC__TextArea-sizer" }, value || placeholder || ' ')));
    }
}

/**
 * The size of the gap between input changes before we indicate that the user has stopped typing.
 */
const STOP_TYPING_PERIOD = 5000;
/**
 * The maximum number of characters to all the user to enter into the input field. The number was chosen to match
 * the limit imposed by the API.
 */
const INPUT_MAX_CHARS = 2048;
function Input(props, ref) {
    const { inputType, isInputVisible, placeholder, disableInput, disableSend, disableUploadButton, pendingUploads, allowedFileUploadTypes, allowMultipleFileUploads, showUploadButton, onFilesSelectedForUpload, onSendInput, blurOnSend, serviceManager, onUserTyping, languagePack, inputCustomizations, isStopStreamingButtonVisible, isStopStreamingButtonDisabled, } = props;
    const { onBeforeSend, getCompletions, onCompletionSelected } = inputCustomizations || {};
    const inputID = `${serviceManager.namespace.suffix}-${useCounter()}`;
    // Indicates if the text area currently has focus.
    const [textAreaHasFocus, setTextAreaHasFocus] = useState(false);
    // The current controlled value of the text area.
    const [inputValue, setInputValue] = useState('');
    // Indicates if handling of the enter key is enabled or not. If it's enabled, this component will call the
    // onSendInput prop when a press of the enter key is detected.
    const enterKeyEnabled = useRef(true);
    // Indicates the user is currently typing.
    const isTypingTimeout = useRef(null);
    // A React ref to the TextArea component.
    const textAreaRef = useRef();
    // A React ref to the file Input element.
    const fileInputRef = useRef();
    // An array of functions that will be called when the text value changes.
    const changeListeners = useRef(new ListenerList());
    // The last text value that was sent to the change listeners.
    const lastValue = useRef('');
    // The reusable {@link InstanceInputElement} object that allows access and control over the input.
    const instanceInputElement = useRef(createInstanceInputElement());
    // A ref to the last completion Promise that was made.
    const lastCompletion = useRef(null);
    // A ref to the list box for the completions popup.
    const completionsPopupRef = useRef(null);
    // The current list of completions to display in the completions popup.
    const [completionResults, setCompletionResults] = useState(null);
    // The index of the currently highlighted completion. A value of -1 means none.
    const [highlightedCompletion, setHighlightedCompletion] = useState(-1);
    /**
     * Scrolls the completion at the given index into view.
     */
    function scrollCompletion(index) {
        const element = completionsPopupRef.current?.querySelector(`.WACInputCompletion:nth-child(${index + 1})`);
        doScrollElementIntoView(element);
    }
    /**
     * Closes the completion list and cancels any outstanding request.
     */
    function closeCompletions() {
        if (completionResults) {
            setCompletionResults(null);
            setHighlightedCompletion(-1);
        }
        if (lastCompletion.current) {
            lastCompletion.current.cancelled = true;
            lastCompletion.current = null;
        }
    }
    /**
     * This is called when we have detected that the user has stopped typing.
     */
    function doTypingStopped() {
        if (isTypingTimeout.current) {
            clearTimeout(isTypingTimeout.current);
            isTypingTimeout.current = null;
            onUserTyping?.(false);
        }
    }
    function doCompletionSelected(completionResults, index) {
        const { completionStart, completionEnd, completions } = completionResults;
        const completion = completions[index];
        const { targetValue } = completion;
        const newInput = inputValue.substring(0, completionStart) + targetValue + inputValue.substring(completionEnd, inputValue.length);
        closeCompletions();
        setInputValue(newInput);
        onCompletionSelected?.(completion);
        // Move the cursor to the end up the completion we just inserted.
        setTimeout(() => {
            const cursorPosition = completionStart + targetValue.length;
            textAreaRef.current.getHTMLElement().setSelectionRange(cursorPosition, cursorPosition);
        });
    }
    /**
     * This is a callback which is called on each keydown event that occurs on the text area. This is used to capture
     * the enter key, so we can send the entered text to the server.
     */
    function onKeyDown(event) {
        if (isEnterKey(event)) {
            if (highlightedCompletion !== -1) {
                doCompletionSelected(completionResults, highlightedCompletion);
                event.preventDefault();
            }
            else if (disableSend || !enterKeyEnabled.current) {
                // If sending is disabled, stop the field from inserting a newline into the field.
                event.preventDefault();
            }
            else {
                send(event);
            }
        }
        else if (completionResults) {
            switch (event.key) {
                case 'ArrowUp': {
                    setHighlightedCompletion(currentValue => {
                        const newValue = currentValue <= 0 ? completionResults.completions.length - 1 : currentValue - 1;
                        scrollCompletion(newValue);
                        return newValue;
                    });
                    event.preventDefault();
                    break;
                }
                case 'ArrowDown': {
                    setHighlightedCompletion(currentValue => {
                        const newValue = currentValue >= completionResults.completions.length - 1 ? 0 : currentValue + 1;
                        scrollCompletion(newValue);
                        return newValue;
                    });
                    event.preventDefault();
                    break;
                }
                case 'ArrowLeft':
                case 'ArrowRight':
                case 'Home':
                case 'End':
                case 'Escape': {
                    // If the user tries to move the cursor or presses escape, close the completions. We close on changes to
                    // the cursor since it can be a little awkward if the user moves the cursor but then selects a value from
                    // the completions list which will not be inserted where the cursor is. This also happens if a click
                    // occurs on the input field.
                    closeCompletions();
                    break;
                }
            }
        }
    }
    /**
     * This is a callback which is called when a change event occurs on the textarea inside this input.
     */
    function onChange(event) {
        const inputText = event.target.value;
        if (onUserTyping) {
            if (!isTypingTimeout.current) {
                onUserTyping(true);
            }
            else {
                clearTimeout(isTypingTimeout.current);
            }
            isTypingTimeout.current = setTimeout(doTypingStopped, STOP_TYPING_PERIOD);
        }
        if (getCompletions) {
            closeCompletions();
            handleCompletions(inputText).catch(error => consoleError('Error handling completions', error));
        }
        setInputValue(inputText);
    }
    /**
     * Each time the text in the field changes, this will handle any possible completions that may need to be displayed.
     */
    async function handleCompletions(inputText) {
        const { selectionStart } = textAreaRef.current.getHTMLElement();
        // If a completion is currently running, then cancel it.
        if (lastCompletion.current) {
            lastCompletion.current.cancelled = true;
        }
        const completionPromise = getCompletions?.(inputText, selectionStart);
        const cancelable = { cancelled: false, item: completionPromise };
        lastCompletion.current = cancelable;
        const result = await cancelable.item;
        if (!cancelable.cancelled) {
            if (!result?.completions?.length) {
                setCompletionResults(null);
            }
            else {
                setCompletionResults(result);
            }
            setHighlightedCompletion(-1);
        }
    }
    function send(event) {
        if (doHasValidInput()) {
            event.preventDefault();
            doTypingStopped();
            closeCompletions();
            const text = inputValue.trim();
            const payload = onBeforeSend?.();
            onSendInput(text, payload);
            // Reset the value of the field.
            setInputValue('');
            if (blurOnSend) {
                textAreaRef.current.doBlur();
            }
            else {
                textAreaRef.current.takeFocus();
            }
        }
    }
    /**
     * Called when the input field gets focus.
     */
    function onInputFocus() {
        setTextAreaHasFocus(true);
    }
    /**
     * Called when the input field loses focus.
     */
    function onInputBlur() {
        setTextAreaHasFocus(false);
    }
    /**
     * Instructs this component to put focus into the input text area. This only applies to desktop devices.
     */
    function takeFocus() {
        if (!IS_MOBILE && isInputVisible) {
            textAreaRef.current.takeFocus();
        }
    }
    /**
     * Creates an instance of {@link InstanceInputElement}.
     */
    function createInstanceInputElement() {
        return {
            getHTMLElement: () => textAreaRef?.current?.getHTMLElement(),
            setValue: (value) => setInputValue(value),
            setEnableEnterKey: (isEnabled) => {
                enterKeyEnabled.current = isEnabled;
            },
            addChangeListener: (listener) => changeListeners.current.addListener(listener),
            removeChangeListener: (listener) => changeListeners.current.removeListener(listener),
        };
    }
    /**
     * Given a base string, add modifiers to an element id to ensure it is unique based off of context.
     */
    function getElementId(baseId, isTestingId) {
        const elementID = baseId;
        const withInputType = inputType ? `--${inputType}` : '';
        const withNamespace = isTestingId ? '' : serviceManager.namespace.suffix;
        return `${elementID}${withInputType}${withNamespace}`;
    }
    /**
     * The callback that is called when the user removes a file from the upload area.
     */
    function onRemoveFile(fileID) {
        const isInputToAgent = selectIsInputToAgent(serviceManager.store.getState());
        serviceManager.store.dispatch(actions.removeFileUpload(fileID, isInputToAgent));
        // After we remove the file, we need to move focus back to the input field.
        textAreaRef.current.takeFocus();
    }
    /**
     * The callback that is called when the user selects a file using the file input.
     */
    function onFileChange(event) {
        const isInputToAgent = selectIsInputToAgent(serviceManager.store.getState());
        const { dispatch } = serviceManager.store;
        const { files } = event.target;
        const newFiles = [];
        for (let index = 0; index < files.length; index++) {
            const newFile = { id: uuid(UUIDType.FILE), status: FileStatusValue.EDIT, file: files[index] };
            newFiles.push(newFile);
            dispatch(actions.addInputFile(newFile, isInputToAgent));
        }
        onFilesSelectedForUpload?.(newFiles);
        // Clear the file input. We're controlling the file list.
        fileInputRef.current.value = null;
    }
    /**
     * Determines if there is anything valid that the user could send.
     */
    function doHasValidInput() {
        const hasUploads = Boolean(pendingUploads?.length);
        if (hasUploads) {
            if (pendingUploads.find(upload => !isValidForUpload(upload))) {
                // If there are any files that are in error, the user cannot send the message.
                return false;
            }
        }
        return Boolean(inputValue.trim()) || hasUploads;
    }
    // If the input field becomes disabled, we don't get a blur event so make sure to remove the focus indicator.
    if (textAreaHasFocus && disableInput) {
        setTextAreaHasFocus(false);
    }
    // If the input field loses focus, close the completions.
    if (!textAreaHasFocus && completionResults) {
        closeCompletions();
    }
    useImperativeHandle(ref, () => ({
        takeFocus,
        getMessageInput: () => instanceInputElement.current,
    }));
    const { input_buttonLabel, input_placeholder, input_ariaLabel, input_uploadButtonLabel, input_stopResponse } = languagePack;
    const useInputValue = disableInput ? '' : inputValue;
    const hasValidInput = doHasValidInput();
    const showDisabledSend = !hasValidInput || disableInput || disableSend;
    const showUploadButtonDisabled = disableUploadButton || disableInput;
    const uploadButtonID = `WACInputContainer__UploadInput-${inputID}`;
    const isRTL = document.dir === 'rtl';
    // If the input field is disabled, don't show a placeholder (unless one is provided).
    const usePlaceHolder = placeholder || (disableInput ? undefined : input_placeholder);
    if (lastValue.current !== inputValue) {
        lastValue.current = inputValue;
        changeListeners.current.fireListeners(inputValue);
    }
    return (isInputVisible && (React__default.createElement("div", { className: cx('WACInputAndCompletions', {
            'WACInputAndCompletions--completionsOpen': completionResults,
        }) },
        completionResults && (React__default.createElement("div", { className: "WACInputCompletions" },
            React__default.createElement("ul", { ref: completionsPopupRef, className: "WACInputCompletions__Menu cds--list-box__menu" }, completionResults.completions.map((completion, index) => {
                return (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions
                React__default.createElement("li", { 
                    // eslint-disable-next-line react/no-array-index-key
                    key: index, className: cx('WACInputCompletion cds--list-box__menu-item', {
                        'WACInputCompletion--selected': index === highlightedCompletion,
                        'WACInputCompletion--afterSelected': index === highlightedCompletion + 1,
                    }), onClick: () => doCompletionSelected(completionResults, index), 
                    // This onMouseDown prevents the browser from moving focus away from the input field.
                    onMouseDown: event => event.preventDefault() },
                    React__default.createElement("div", { className: "WACInputCompletions__MenuItem cds--list-box__menu-item__option" },
                        React__default.createElement("div", { className: "WACInputCompletions__MenuItemLabel" }, completion.label),
                        completion.tag && (React__default.createElement(Tag, { className: "WACInputCompletions__MenuItemTag", type: completion.tag.type, size: TagSize.SMALL }, completion.tag.label)))));
            })))),
        React__default.createElement("div", { className: cx('WACInputContainer', {
                'WACInputContainer--hasFocus': textAreaHasFocus,
                'WACInputContainer--showUploadButton': showUploadButton,
            }) },
            React__default.createElement("div", { className: "WACInputContainer__LeftContainer" },
                React__default.createElement("div", { className: "WACInputContainer__TextAndUpload" },
                    showUploadButton && (React__default.createElement("div", { className: "WACInputContainer__UploadButtonContainer" },
                        React__default.createElement("input", { ref: fileInputRef, accept: allowedFileUploadTypes, id: uploadButtonID, className: "WACVisuallyHidden WACInputContainer__UploadInput", type: "file", "aria-label": input_uploadButtonLabel, onChange: onFileChange, multiple: allowMultipleFileUploads, disabled: showUploadButtonDisabled }),
                        React__default.createElement("label", { className: cx('WACInputContainer__UploadButton', {
                                'WACInputContainer__UploadButton--disabled': showUploadButtonDisabled,
                            }), htmlFor: uploadButtonID },
                            React__default.createElement(Attachment, null)))),
                    React__default.createElement(TextArea, { autoSize: true, ariaLabel: input_ariaLabel, disabled: disableInput, maxLength: INPUT_MAX_CHARS, onChange: onChange, onKeyDown: onKeyDown, onClick: closeCompletions, placeholder: usePlaceHolder, value: useInputValue, ref: textAreaRef, onFocus: onInputFocus, onBlur: onInputBlur, id: getElementId('WACInputContainer-TextArea'), testId: getElementId('WACInputContainer-TextArea', true) })),
                Boolean(pendingUploads?.length) && (React__default.createElement("div", { className: "WACInputContainer__FilesContainer" }, pendingUploads.map((fileUpload, index) => {
                    return (React__default.createElement(FileUploaderItem
                    // eslint-disable-next-line react/no-array-index-key
                    , { 
                        // eslint-disable-next-line react/no-array-index-key
                        key: index, iconDescription: languagePack.fileSharing_removeButtonTitle, name: fileUpload.file.name, status: FileStatusValue.EDIT, errorSubject: fileUpload.errorMessage, invalid: fileUpload.isError, size: ButtonSizeEnum.SMALL, onDelete: () => onRemoveFile(fileUpload.id) }));
                })))),
            React__default.createElement("div", { className: "WACInputContainer__SendButtonContainer" },
                isStopStreamingButtonVisible && (React__default.createElement(StopStreamingButton, { label: input_stopResponse, disabled: isStopStreamingButtonDisabled, tooltipAlignment: "top", onClick: () => {
                        const { store } = serviceManager;
                        const { currentStreamID } = store.getState().botInputState.stopStreamingButtonState;
                        store.dispatch(actions.setStopStreamingButtonDisabled(true));
                        serviceManager.fire({ type: "stopStreaming" /* BusEventType.STOP_STREAMING */ });
                        // If we have an assistant stream ID, then tell the message service to stop the stream.
                        if (currentStreamID) {
                            serviceManager.messageService.stopStreaming(currentStreamID);
                        }
                    } })),
                React__default.createElement(Button, { className: "WACInputContainer__SendButton", kind: ButtonKindEnum.GHOST, size: ButtonSizeEnum.SMALL, id: getElementId('WACInputContainer__SendButton'), type: "button", onClick: send, "aria-label": input_buttonLabel, disabled: showDisabledSend, renderIcon: hasValidInput ? SendFilled : Send, iconDescription: input_buttonLabel, tooltipAlignment: isRTL ? 'start' : 'end', tooltipPosition: "top", hasIconOnly: true }))))));
}
const InputExport = React__default.memo(forwardRef(Input));

/**
 * Displays a modal asking if the user wants to end a chat with an agent. This also covers the case where the user
 * cancels a request for an agent before an agent has joined.
 */
function RequestScreenShareModal() {
    const serviceManager = useServiceManager();
    const languagePack = useLanguagePack();
    const onConfirm = () => {
        serviceManager.humanAgentService?.screenShareUpdateRequestState(ScreenShareState.ACCEPTED);
    };
    const onCancel = () => {
        serviceManager.humanAgentService?.screenShareUpdateRequestState(ScreenShareState.DECLINED);
    };
    const title = languagePack.agent_sharingRequestTitle;
    const message = languagePack.agent_sharingRequestMessage;
    const cancelButtonLabel = languagePack.agent_sharingDeclineButton;
    const confirmButtonLabel = languagePack.agent_sharingAcceptButton;
    return (React__default.createElement(ConfirmModal, { title: title, message: message, onConfirm: onConfirm, onCancel: onCancel, cancelButtonLabel: cancelButtonLabel, confirmButtonLabel: confirmButtonLabel, modalAnnounceMessage: message, serviceManager: serviceManager }));
}

class Chat extends Component {
    constructor() {
        super(...arguments);
        /**
         * Default state.
         */
        this.state = {
            showEndChatConfirmation: false,
            hasCaughtError: false,
        };
        /**
         * A React ref to the Input component.
         */
        this.inputRef = React__default.createRef();
        /**
         * A React ref to the Header component.
         */
        this.headerRef = React__default.createRef();
        /**
         * A React ref to the Messages component.
         */
        this.messagesRef = React__default.createRef();
        /**
         * This is the memoized messages used in this component. This is the step that pulls the messages from
         * the map and puts them in the right order in an array.
         */
        this.messagesToArray = createUnmappingMemoizer();
        /**
         * The callback that can be called when the end chat confirmation panel should be hidden.
         */
        this.hideConfirmEndChat = () => {
            // Hide the modal and then move focus back to the input field.
            this.setState({ showEndChatConfirmation: false });
            setTimeout(() => {
                // The input field may still be disabled until the state change is re-rendered, so defer the focus move.
                this.requestInputFocus();
            });
        };
        /**
         * The callback that can be called when the end chat confirmation panel should be shown.
         */
        this.showConfirmEndChat = () => {
            this.setState({ showEndChatConfirmation: true });
        };
        /**
         * The callback that can be called when the end agent chat confirmation panel should be shown.
         */
        this.confirmAgentEndChat = () => {
            this.hideConfirmEndChat();
            this.props.serviceManager.humanAgentService.endChat(true);
        };
        /**
         * If we have nothing to focus on correctly in the chat window, we focus on the header. If the header has no buttons
         * available, we can fall back to focusing on the messages scroll handle.
         */
        this.requestDefaultFocus = () => {
            if (!this.headerRef?.current?.requestFocus()) {
                doFocusRef(this.messagesRef.current?.scrollHandleRef);
            }
        };
        /**
         * This is a callback function that will request that focus be moved to the main input field where the user types
         * in their requests to the assistant. If the input is currently disabled or hidden, then we will try to focus on
         * a focusable option in the latest responses from the assistant. If that doesn't exist then focus will be moved
         * to the close button instead.
         *
         */
        this.requestInputFocus = () => {
            const { agentDisplayState } = this.props;
            try {
                // If the agent banner is visible and the input field is disabled, move focus there.
                if (agentDisplayState.isConnectingOrConnected && agentDisplayState.disableInput) {
                    if (this.messagesRef.current.requestAgentBannerFocus()) {
                        return;
                    }
                }
                if (this.inputRef.current) {
                    if (this.props.inputState.fieldVisible && !this.shouldDisableInput()) {
                        this.inputRef.current.takeFocus();
                    }
                    else {
                        // This will attempt to move focus to the last output message in the message list. This is intended to cover
                        // the use case where the customer has disabled the input field. When the assistant returns a response and
                        // there is no input field, the expectation is that something in that response will be focusable and we'll
                        // try to move focus to it. If the last message is not a response and the input field is disabled, that
                        // means the assistant is processing a request. In that case, we don't want to move focus to a message but
                        // rather fallback to something else like the close button.
                        const htmlElements = this.messagesRef.current.getLastOutputMessageElements();
                        if (!focusOnFirstFocusableItemInArrayOfElements(htmlElements)) {
                            this.requestDefaultFocus();
                        }
                    }
                }
                else {
                    this.requestDefaultFocus();
                }
            }
            catch (error) {
                consoleError('An error occurred in Chat.requestInputFocus', error);
            }
        };
        /**
         * Requests an auto scroll operation to happen on the messages panel. See {@link MessagesComponent#doAutoScroll} for
         * more information.
         */
        this.doAutoScroll = (options) => {
            this.messagesRef.current?.doAutoScroll(options);
        };
        /**
         * Returns the current scrollBottom value for the message scroll panel.
         */
        this.getMessagesScrollBottom = () => {
            return this.messagesRef?.current?.getContainerScrollBottom();
        };
        /**
         * The callback that is called when the user selects one or more files to be uploaded.
         */
        this.onFilesSelectedForUpload = (uploads) => {
            const isInputToAgent = this.props.agentDisplayState.isConnectingOrConnected;
            if (isInputToAgent) {
                this.props.serviceManager.humanAgentService.filesSelectedForUpload(uploads);
                // If the user chose a file and multiple files are not allowed, the file input will become disabled so we need to
                // move focus back to the text input.
                if (!this.props.inputState.allowMultipleFileUploads) {
                    this.requestInputFocus();
                }
            }
        };
    }
    async scrollOnHydrationComplete() {
        // Once the hydration is complete, we want to scroll to the very bottom.
        if (this.props.config.public.__ibm__.disableInitialScroll) {
            // If the initial scrolling is disabled, scroll to the top anyway. This overrides the browser's default
            // behavior where it will remember the last scroll position.
            this.doAutoScroll({ scrollToTop: 0 });
        }
        else {
            this.doAutoScroll();
        }
    }
    componentDidMount() {
        if (this.props.isHydrationAnimationComplete) {
            setTimeout(() => {
                // We need to make sure React has finished rendering updates before we scroll.
                this.scrollOnHydrationComplete();
            });
        }
    }
    componentDidUpdate(prevProps) {
        const { isHydrationAnimationComplete, agentState } = this.props;
        // If we don't wait for the animation to complete, the auto scroll can not work correctly.
        // Thankfully, we have this property already to look at and kick off the autoscroll.
        if (isHydrationAnimationComplete && !prevProps.isHydrationAnimationComplete) {
            setTimeout(() => {
                // We need to make sure React has finished rendering updates before we scroll.
                this.scrollOnHydrationComplete();
            });
        }
        // This covers the case where an agent joins while the confirmation modal is visible.
        const connectingChanged = agentState.isConnecting !== prevProps.agentState.isConnecting;
        if (this.state.showEndChatConfirmation && connectingChanged) {
            this.hideConfirmEndChat();
        }
    }
    componentDidCatch(error, errorInfo) {
        this.props.serviceManager.actions.errorOccurred(createDidCatchErrorData('Chat', error, errorInfo));
        this.setState({ hasCaughtError: true });
    }
    /**
     * Scrolls to the (full) message with the given ID. Since there may be multiple message items in a given
     * message, this will scroll the first message to the top of the message window.
     *
     * @param messageID The (full) message ID to scroll to.
     * @param animate Whether or not the scroll should be animated. Defaults to true.
     */
    doScrollToMessage(messageID, animate = true) {
        this.messagesRef.current?.doScrollToMessage(messageID, animate);
    }
    getMessageInput() {
        return this.inputRef.current?.getMessageInput();
    }
    /**
     * Determines if the input field should be disabled based on the current props.
     */
    shouldDisableInput() {
        return this.props.inputState.isReadonly || this.props.agentDisplayState.disableInput;
    }
    /**
     * Determines if the send button should be disabled based on the current props. The send button is disabled if the
     * input field should be disabled or if the messages are not yet hydrated (ready to be shown). We let the user
     * type into the input field before the messages are ready to avoid messing with moving focus around, but we don't
     * let them send the message.
     */
    shouldDisableSend() {
        const { isHydrated } = this.props;
        return this.shouldDisableInput() || !isHydrated;
    }
    renderMessagesAndInput() {
        const { languagePack, toolingType, messageState, intl, allMessageItemsByID, isHydrated, serviceManager, inputState, onUserTyping, agentState, botName, onSendInput, locale, useAITheme, carbonTheme, agentDisplayState, } = this.props;
        const { fieldVisible, files, allowFileUploads, allowedFileUploadTypes, allowMultipleFileUploads, stopStreamingButtonState, } = inputState;
        const { fileUploadInProgress } = agentState;
        const { inputPlaceholderKey } = agentDisplayState;
        const inputCustomizations = this.props.config.public.messaging.inputs?.mainWindow;
        // If there are any files currently selected or being uploaded and multiple files are not allowed, then show the
        // button as disabled.
        const numFiles = files?.length ?? 0;
        const anyCurrentFiles = numFiles > 0 || fileUploadInProgress;
        const showUploadButtonDisabled = anyCurrentFiles && !allowMultipleFileUploads;
        return (React__default.createElement(React__default.Fragment, null,
            isHydrated && (React__default.createElement("div", { className: "WACMessagesContainer__NonInputContainer" },
                React__default.createElement(MessagesComponent$1, { ref: this.messagesRef, messageState: messageState, localMessageItems: this.messagesToArray(messageState.localMessageIDs, allMessageItemsByID), requestInputFocus: this.requestInputFocus, toolingType: toolingType, botName: botName, intl: intl, onEndAgentChat: this.showConfirmEndChat, locale: locale, useAITheme: useAITheme, carbonTheme: carbonTheme }))),
            React__default.createElement(WriteableElement$1, { slotName: WriteableElementName.BEFORE_INPUT_ELEMENT, id: `beforeInputElement${serviceManager.namespace.suffix}`, element: serviceManager.writeableElements[WriteableElementName.BEFORE_INPUT_ELEMENT] }),
            React__default.createElement(InputExport, { ref: this.inputRef, languagePack: languagePack, serviceManager: serviceManager, disableInput: this.shouldDisableInput(), disableSend: this.shouldDisableSend(), isInputVisible: fieldVisible, onSendInput: onSendInput, onUserTyping: onUserTyping, showUploadButton: allowFileUploads, disableUploadButton: showUploadButtonDisabled, allowedFileUploadTypes: allowedFileUploadTypes, allowMultipleFileUploads: allowMultipleFileUploads, pendingUploads: files, onFilesSelectedForUpload: this.onFilesSelectedForUpload, placeholder: languagePack[inputPlaceholderKey], inputCustomizations: inputCustomizations, isStopStreamingButtonVisible: stopStreamingButtonState.isVisible, isStopStreamingButtonDisabled: stopStreamingButtonState.isDisabled }),
            this.state.showEndChatConfirmation && (React__default.createElement(EndAgentChatModal, { onConfirm: this.confirmAgentEndChat, onCancel: this.hideConfirmEndChat })),
            this.props.agentState.showScreenShareRequest && React__default.createElement(RequestScreenShareModal, null)));
    }
    render() {
        const { serviceManager, languagePack, onClose, onCloseAndRestart, onRestart, config, onToggleHomeScreen, botName, headerDisplayName, headerAvatarConfig, } = this.props;
        const { hasCaughtError } = this.state;
        const className = `WAC ${config.public.agentAppConfig.is_on ? 'WACAgentApp' : ''}`;
        return (React__default.createElement("div", { className: className },
            React__default.createElement(BotHeaderExport, { ref: this.headerRef, onClose: onClose, onCloseAndRestart: onCloseAndRestart, onRestart: onRestart, headerDisplayName: headerDisplayName, headerAvatarConfig: headerAvatarConfig, onToggleHomeScreen: onToggleHomeScreen, enableChatHeaderConfig: true, includeWriteableElement: true }),
            React__default.createElement(NonHeaderBackground, null),
            React__default.createElement("div", { className: "WACPanelContent WAC__ChatNonHeaderContainer" },
                hasCaughtError && (React__default.createElement("div", { className: "WAC__MessagesErrorHandler" },
                    React__default.createElement(CatastrophicErrorExport, { serviceManager: serviceManager, languagePack: languagePack, onRestart: onRestart, showHeader: false, botName: botName, headerDisplayName: headerDisplayName, catastrophicErrorType: CatastrophicErrorType.FAILED }))),
                !hasCaughtError && React__default.createElement("div", { className: "WAC__messagesAndInputContainer" }, this.renderMessagesAndInput()),
                React__default.createElement(AlternateSuggestionsContainerExport, { requestInputFocus: this.requestInputFocus })),
            React__default.createElement(AlternateSuggestionsBackgroundExport, null)));
    }
}
/**
 * Displays a background that covers the non-header area of the chat.
 */
function NonHeaderBackground() {
    const isVisible = useSelector(state => state.showNonHeaderBackgroundCover);
    return isVisible ? React__default.createElement("div", { className: "WACBackgroundCover WACBackgroundCover__NonHeader" }) : null;
}
var Chat$1 = injectIntl(Chat, { forwardRef: true });

/**
 * This component renders an a chat bubble for dark themes.
 */
function ChatBubbleDark() {
    const chatBubbleID = 'cbl-';
    return (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", viewBox: "0 0 80 80" },
        React__default.createElement("defs", null,
            React__default.createElement("linearGradient", { id: `${chatBubbleID}-a`, x1: 30.047, x2: 35.499, y1: 54.31, y2: 54.31, gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: 0, stopColor: "#393939" }),
                React__default.createElement("stop", { offset: 1, stopColor: "#262626" })),
            React__default.createElement("linearGradient", { id: `${chatBubbleID}-b`, x1: 28.608, x2: 70.691, y1: -3.968, y2: 68.921, gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: 0, stopColor: "#6f6f6f" }),
                React__default.createElement("stop", { offset: 0.19, stopColor: "#6c6c6c" }),
                React__default.createElement("stop", { offset: 0.316, stopColor: "#636363" }),
                React__default.createElement("stop", { offset: 0.423, stopColor: "#555" }),
                React__default.createElement("stop", { offset: 0.518, stopColor: "#3f3f3f" }),
                React__default.createElement("stop", { offset: 0.545, stopColor: "#383838" }),
                React__default.createElement("stop", { offset: 1, stopColor: "#262626" })),
            React__default.createElement("linearGradient", { id: `${chatBubbleID}-c`, x1: 15.125, x2: 60.902, y1: 36.198, y2: 36.198, gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: 0, stopColor: "#525252" }),
                React__default.createElement("stop", { offset: 1, stopColor: "#393939" })),
            React__default.createElement("linearGradient", { id: `${chatBubbleID}-d`, x1: 15.14, x2: 63.056, y1: 5.723, y2: 33.517, gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: 0.777, stopColor: "#8d8d8d" }),
                React__default.createElement("stop", { offset: 0.806, stopColor: "#8a8a8a", stopOpacity: 0.967 }),
                React__default.createElement("stop", { offset: 0.839, stopColor: "gray", stopOpacity: 0.872 }),
                React__default.createElement("stop", { offset: 0.873, stopColor: "#6f6f6f", stopOpacity: 0.713 }),
                React__default.createElement("stop", { offset: 0.908, stopColor: "#595959", stopOpacity: 0.491 }),
                React__default.createElement("stop", { offset: 0.944, stopColor: "#3b3b3b", stopOpacity: 0.209 }),
                React__default.createElement("stop", { offset: 0.967, stopColor: "#262626", stopOpacity: 0 }))),
        React__default.createElement("path", { d: "m15.129 52.11 45.498 26.279 4.248-2.507-45.473-26.255-4.273 2.483z", opacity: 0.25 }),
        React__default.createElement("path", { fill: `url(#${chatBubbleID}-a)`, d: "m32.663 52.846-2.258 4.227a1.138 1.138 0 0 1-.358.35l2.837-1.649a1.148 1.148 0 0 0 .358-.35L35.5 51.2Z" }),
        React__default.createElement("path", { fill: `url(#${chatBubbleID}-b)`, d: "M63.454 26.582 20.631 1.858a1.006 1.006 0 0 0-1.014-.1l-3.973 2.3a1.006 1.006 0 0 1 1.014.1l42.823 24.725a3.148 3.148 0 0 1 1.419 2.462l-.1 36.084a1 1 0 0 1-.419.907l3.973-2.3a1 1 0 0 0 .419-.907l.1-36.084a3.145 3.145 0 0 0-1.419-2.463Z" }),
        React__default.createElement("path", { fill: `url(#${chatBubbleID}-c)`, d: "M59.481 28.883a3.151 3.151 0 0 1 1.419 2.462l-.1 36.084c-.009.9-.647 1.26-1.424.812l-26.695-15.4-2.257 4.226a.9.9 0 0 1-1.333.273 3.086 3.086 0 0 1-1.224-1.527l-2.322-7.092-9-5.2a3.143 3.143 0 0 1-1.421-2.461l.1-36.084c0-.9.641-1.272 1.431-.816Z" }),
        React__default.createElement("path", { fill: "#6f6f6f", d: "m57.995 37.068-.011 3.902-39.952-23.066.011-3.902 39.952 23.066zM57.995 45.114l-.011 3.903-39.952-23.066.011-3.903 39.952 23.066zM44.62 45.041l-.011 3.902-26.577-15.344.011-3.902L44.62 45.041z" }),
        React__default.createElement("path", { fill: `url(#${chatBubbleID}-d)`, d: "M60.756 30.548a2.507 2.507 0 0 1 .146.8l-.011 3.952a3.98 3.98 0 0 1 .413-.125l.011-3.826a3.541 3.541 0 0 0-1.628-2.821L16.864 3.8a1.976 1.976 0 0 0-.445-.192l-.775.45c.006 0 .015 0 .021-.008a.722.722 0 0 1 .188-.071h.015a.822.822 0 0 1 .151-.015h.101a1.087 1.087 0 0 1 .233.051c.014 0 .027.01.041.015a1.654 1.654 0 0 1 .264.121l21.411 12.37 21.412 12.362a3.155 3.155 0 0 1 1.275 1.665Z" })));
}

/**
 * This component renders a chat bubble for light themes.
 */
function ChatBubbleLight() {
    const chatBubbleID = 'cbl-';
    return (React__default.createElement("svg", { xmlns: "http://www.w3.org/2000/svg", xmlnsXlink: "http://www.w3.org/1999/xlink", viewBox: "0 0 80 80" },
        React__default.createElement("defs", null,
            React__default.createElement("linearGradient", { id: `${chatBubbleID}-a`, x1: 61.44, x2: 61.44, y1: 66.99, y2: 60.01, gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: 0, stopColor: "#c6c6c6" }),
                React__default.createElement("stop", { offset: 0.78, stopColor: "#e0e0e0" })),
            React__default.createElement("linearGradient", { id: `${chatBubbleID}-b`, x1: 28.49, x2: 53.04, y1: 44.06, y2: 86.58, gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: 0, stopColor: "#525252", stopOpacity: 0.05 }),
                React__default.createElement("stop", { offset: 1, stopOpacity: 0.1 })),
            React__default.createElement("linearGradient", { id: `${chatBubbleID}-c`, x1: 30.05, x2: 35.5, y1: 54.31, y2: 54.31, gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: 0, stopColor: "#a4a4a4" }),
                React__default.createElement("stop", { offset: 1, stopColor: "#bebebe" })),
            React__default.createElement("linearGradient", { id: `${chatBubbleID}-d`, x1: 28.61, x2: 70.69, y1: -3.97, y2: 68.92, gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: 0, stopColor: "#f4f4f4" }),
                React__default.createElement("stop", { offset: 0.52, stopColor: "#e0e0e0" }),
                React__default.createElement("stop", { offset: 0.56, stopColor: "#d8d8d8" }),
                React__default.createElement("stop", { offset: 0.61, stopColor: "#c6c6c6" }),
                React__default.createElement("stop", { offset: 0.89, stopColor: "#a8a8a8" }),
                React__default.createElement("stop", { offset: 0.96, stopColor: "#8d8d8d" })),
            React__default.createElement("linearGradient", { xlinkHref: `#${chatBubbleID}-a`, id: `${chatBubbleID}-e`, x1: 38.01, x2: 38.01, y1: 59.43, y2: 3.27 }),
            React__default.createElement("linearGradient", { id: `${chatBubbleID}-f`, x1: 21.52, x2: 61.39, y1: 36.2, y2: 36.2, gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: 0, stopColor: "#e0e0e0" }),
                React__default.createElement("stop", { offset: 1, stopColor: "#c6c6c6" })),
            React__default.createElement("linearGradient", { id: `${chatBubbleID}-h`, x1: 17.68, x2: 55.37, y1: 15.75, y2: 37.5, gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: 0, stopColor: "#fff" }),
                React__default.createElement("stop", { offset: 0.05, stopColor: "#fdfdfd" }),
                React__default.createElement("stop", { offset: 0.3, stopColor: "#f6f6f6" }),
                React__default.createElement("stop", { offset: 1, stopColor: "#f4f4f4" })),
            React__default.createElement("linearGradient", { xlinkHref: `#${chatBubbleID}-h`, id: `${chatBubbleID}-i`, x1: 14.24, x2: 51.92, y1: 21.81, y2: 43.56 }),
            React__default.createElement("linearGradient", { xlinkHref: `#${chatBubbleID}-h`, id: `${chatBubbleID}-j`, x1: 10.96, x2: 48.66, y1: 27.56, y2: 49.33 }),
            React__default.createElement("linearGradient", { id: `${chatBubbleID}-k`, x1: 15.14, x2: 63.06, y1: 5.72, y2: 33.52, gradientUnits: "userSpaceOnUse" },
                React__default.createElement("stop", { offset: 0.78, stopColor: "#fff" }),
                React__default.createElement("stop", { offset: 0.8, stopColor: "#fefefe", stopOpacity: 0.98 }),
                React__default.createElement("stop", { offset: 0.82, stopColor: "#fcfcfc", stopOpacity: 0.93 }),
                React__default.createElement("stop", { offset: 0.85, stopColor: "#f8f8f8", stopOpacity: 0.84 }),
                React__default.createElement("stop", { offset: 0.87, stopColor: "#f2f2f2", stopOpacity: 0.72 }),
                React__default.createElement("stop", { offset: 0.9, stopColor: "#eaeaea", stopOpacity: 0.56 }),
                React__default.createElement("stop", { offset: 0.93, stopColor: "#e1e1e1", stopOpacity: 0.37 }),
                React__default.createElement("stop", { offset: 0.95, stopColor: "#d7d7d7", stopOpacity: 0.14 }),
                React__default.createElement("stop", { offset: 0.97, stopColor: "#d0d0d0", stopOpacity: 0 })),
            React__default.createElement("style", null, `.f{fill:url(#${chatBubbleID}-e)}`)),
        React__default.createElement("path", { d: "M0 0h80v80H0z", style: {
                fill: 'none',
            } }),
        React__default.createElement("path", { d: "M61.3 68.11a.67.67 0 0 0 .09-.14.67.67 0 0 1-.09.14Zm.22-.46a1.58 1.58 0 0 0 0-.32v-7.24 7.24a1.58 1.58 0 0 1 0 .32Zm-.09.26a1.18 1.18 0 0 0 .07-.2 1.18 1.18 0 0 1-.07.2Z", style: {
                fill: `url(#${chatBubbleID}-a)`,
            } }),
        React__default.createElement("path", { d: "m15.13 52.11 45.5 26.28 4.25-2.51L19.4 49.63l-4.27 2.48z", style: {
                fill: `url(#${chatBubbleID}-b)`,
            } }),
        React__default.createElement("path", { d: "m32.66 52.85-2.25 4.22a1.08 1.08 0 0 1-.36.35l2.83-1.65a1.08 1.08 0 0 0 .36-.35l2.26-4.22Z", style: {
                fill: `url(#${chatBubbleID}-c)`,
            } }),
        React__default.createElement("path", { d: "M63.45 26.58 20.63 1.86a1 1 0 0 0-1-.1l-4 2.3a1 1 0 0 1 1 .1l42.85 24.72a3.17 3.17 0 0 1 1.42 2.47l-.1 36.08a1 1 0 0 1-.42.91l4-2.3a1 1 0 0 0 .42-.91L64.88 29a3.14 3.14 0 0 0-1.43-2.42Z", style: {
                fill: `url(#${chatBubbleID}-d)`,
            } }),
        React__default.createElement("path", { d: "M59.48 28.88a3.17 3.17 0 0 1 1.42 2.47l-.1 36.08c0 .9-.65 1.26-1.42.81l-26.7-15.4-2.26 4.22a.9.9 0 0 1-1.33.28 3.07 3.07 0 0 1-1.22-1.53l-2.33-7.09-9-5.2a3.15 3.15 0 0 1-1.43-2.46L15.23 5c0-.9.64-1.27 1.43-.81Z", className: "f" }),
        React__default.createElement("path", { d: "M59.48 28.88a3.17 3.17 0 0 1 1.42 2.47l-.1 36.08c0 .9-.65 1.26-1.42.81l-26.7-15.4-2.26 4.22a.9.9 0 0 1-1.33.28 3.07 3.07 0 0 1-1.22-1.53l-2.33-7.09-9-5.2a3.15 3.15 0 0 1-1.43-2.46L15.23 5c0-.9.64-1.27 1.43-.81Z", style: {
                fill: `url(#${chatBubbleID}-f)`,
            } }),
        React__default.createElement("path", { d: "M59.48 28.88a3.17 3.17 0 0 1 1.42 2.47l-.1 36.08c0 .9-.65 1.26-1.42.81l-26.7-15.4-2.26 4.22a.9.9 0 0 1-1.33.28 3.07 3.07 0 0 1-1.22-1.53l-2.33-7.09-9-5.2a3.15 3.15 0 0 1-1.43-2.46L15.23 5c0-.9.64-1.27 1.43-.81Z", className: "f" }),
        React__default.createElement("path", { d: "m57.99 37.07-.01 3.9L18.03 17.9l.01-3.9 39.95 23.07z", style: {
                fill: `url(#${chatBubbleID}-h)`,
            } }),
        React__default.createElement("path", { d: "m57.99 45.11-.01 3.91-39.95-23.07.01-3.9 39.95 23.06z", style: {
                fill: `url(#${chatBubbleID}-i)`,
            } }),
        React__default.createElement("path", { d: "m44.62 45.04-.01 3.9L18.03 33.6l.01-3.9 26.58 15.34z", style: {
                fill: `url(#${chatBubbleID}-j)`,
            } }),
        React__default.createElement("path", { d: "M60.76 30.55a2.54 2.54 0 0 1 .14.8v3.95l.41-.13v-3.82a3.54 3.54 0 0 0-1.63-2.82L16.86 3.8a2.09 2.09 0 0 0-.44-.19l-.78.45a1 1 0 0 1 .21-.06h.48l.27.12 21.47 12.4 21.41 12.36a3.19 3.19 0 0 1 1.28 1.67Z", style: {
                fill: `url(#${chatBubbleID}-k)`,
            } })));
}

function SimpleHeader(props, ref) {
    const { useAITheme, onClose } = props;
    const headerRef = useRef();
    // Reuse the imperative handles from the header.
    useImperativeHandle(ref, () => headerRef.current);
    return React__default.createElement(HeaderExport, { ref: headerRef, onClickClose: onClose, showCenter: true, useAITheme: useAITheme });
}
const SimpleHeaderExport = React__default.memo(forwardRef(SimpleHeader));

function Disclaimer({ onAcceptDisclaimer, onClose, disclaimerHTML, disclaimerAcceptButtonRef }) {
    const languagePack = useLanguagePack();
    const chatWidthBreakpoint = useSelector((state) => state.chatWidthBreakpoint);
    const { carbonTheme, useAITheme } = useSelector((state) => state.theme);
    const isDarkTheme = carbonTheme === CarbonTheme.G90 || carbonTheme === CarbonTheme.G100;
    const [hasReadDisclaimer, setHasReadDisclaimer] = useState(false);
    const disclaimerContent = useRef();
    const onScroll = () => {
        const { scrollTop, scrollHeight, clientHeight } = disclaimerContent.current;
        const scrollBottom = clientHeight - scrollHeight + scrollTop;
        if (scrollBottom >= 0) {
            setHasReadDisclaimer(true);
        }
    };
    // Make sure that we don't enable the Accept button until the user reads the whole disclaimer.
    useOnMount(() => {
        onScroll();
    });
    function renderChatBubble() {
        return isDarkTheme ? React__default.createElement(ChatBubbleDark, null) : React__default.createElement(ChatBubbleLight, null);
    }
    return (React__default.createElement("div", { className: "WACDisclaimerContainer" },
        React__default.createElement("div", { className: "WAC__disclaimer" },
            React__default.createElement(SimpleHeaderExport, { useAITheme: useAITheme, onClose: onClose }),
            React__default.createElement("div", { className: "WACPanelContent WAC__disclaimer-content", onScroll: onScroll, ref: disclaimerContent },
                React__default.createElement("div", { className: "WAC__disclaimer-icon" }, renderChatBubble()),
                React__default.createElement("h1", { className: "WAC__disclaimer-title" }, languagePack.disclaimer_title),
                React__default.createElement(MaybeDangerouslySetInnerHTMLExport, { className: "WAC__disclaimer-description", html: disclaimerHTML })),
            React__default.createElement("div", { className: "WAC__disclaimer-buttons" },
                React__default.createElement("div", { className: "WAC__disclaimer-buttonsPadding" },
                    React__default.createElement(Button, { className: "WAC__disclaimer-acceptButton", ref: disclaimerAcceptButtonRef, onClick: onAcceptDisclaimer, size: chatWidthBreakpoint === ChatWidthBreakpoint.WIDE ? '2xl' : 'lg', disabled: !hasReadDisclaimer }, languagePack.disclaimer_accept))))));
}

/**
 * The set of possible animations for OverlayPanel animation into view.
 */
var AnimationInType;
(function (AnimationInType) {
    /**
     * The panel does not animate.
     */
    AnimationInType["NONE"] = "none";
    /**
     * The panel fades in from 0 opacity.
     */
    AnimationInType["FADE_IN"] = "fadeIn";
    /**
     * The panel slides in from the left over previous content.
     */
    AnimationInType["SLIDE_IN_FROM_LEFT"] = "slideInFromLeft";
    /**
     * The panel slides in from the right over previous content.
     */
    AnimationInType["SLIDE_IN_FROM_RIGHT"] = "slideInFromRight";
    /**
     * The panel slides in from the bottom over the previous context.
     */
    AnimationInType["SLIDE_IN_FROM_BOTTOM"] = "slideInFromBottom";
    /**
     * The panel slides in from the bottom over the previous context fast for branding.
     */
    AnimationInType["BRANDING_SLIDE_IN_FROM_BOTTOM"] = "brandingSlideInFromBottom";
    /**
     * The custom animation for the home screen.
     */
    // HOME_SCREEN = 'homeScreen',
})(AnimationInType || (AnimationInType = {}));
/**
 * The set of possible animations for OverlayPanel animation out of view.
 */
var AnimationOutType;
(function (AnimationOutType) {
    /**
     * The panel does not animate.
     */
    AnimationOutType["NONE"] = "none";
    /**
     * The panel fades to 0 opacity.
     */
    AnimationOutType["FADE_OUT"] = "fadeOut";
    /**
     * The panel slides out to left.
     */
    AnimationOutType["SLIDE_OUT_TO_LEFT"] = "slideOutToLeft";
    /**
     * The panel slides out to right.
     */
    AnimationOutType["SLIDE_OUT_TO_RIGHT"] = "slideOutToRight";
    /**
     * The panel slides out to top.
     */
    AnimationOutType["SLIDE_OUT_TO_TOP"] = "slideOutToTop";
    /**
     * The panel slides out to bottom.
     */
    AnimationOutType["SLIDE_OUT_TO_BOTTOM"] = "slideOutToBottom";
    /**
     * The custom animation for the home screen.
     */
    // HOME_SCREEN = 'homeScreen',
})(AnimationOutType || (AnimationOutType = {}));

/**
 * The possible overlay panels.
 */
var OverlayPanelName;
(function (OverlayPanelName) {
    OverlayPanelName["DISCLAIMER"] = "disclaimer";
    OverlayPanelName["HOME_SCREEN"] = "homeScreen";
    OverlayPanelName["AGENT"] = "agent";
    OverlayPanelName["HYDRATING"] = "hydrating";
    OverlayPanelName["CATASTROPHIC"] = "catastrophic";
    OverlayPanelName["BRANDING"] = "branding";
    OverlayPanelName["IFRAME"] = "iframe";
    OverlayPanelName["CONVERSATIONAL_SEARCH_CITATION"] = "conversational_search_citation";
    OverlayPanelName["CUSTOM"] = "custom";
    OverlayPanelName["SHOW_PANEL"] = "show_panel";
    OverlayPanelName["PANEL_RESPONSE"] = "panel_response";
})(OverlayPanelName || (OverlayPanelName = {}));
/**
 * Static map of the displayed z-index of possible overlay panels. Eventually this should be managed by some service
 * and be dynamic.
 */
const OverlayPanelZIndex = {
    [OverlayPanelName.BRANDING]: 105,
    [OverlayPanelName.DISCLAIMER]: 99,
    [OverlayPanelName.CUSTOM]: 95,
    [OverlayPanelName.PANEL_RESPONSE]: 94,
    [OverlayPanelName.SHOW_PANEL]: 93,
    [OverlayPanelName.HYDRATING]: 90,
    [OverlayPanelName.CATASTROPHIC]: 80,
    [OverlayPanelName.HOME_SCREEN]: 30,
    [OverlayPanelName.AGENT]: 20,
    [OverlayPanelName.CONVERSATIONAL_SEARCH_CITATION]: 6,
    [OverlayPanelName.IFRAME]: 5,
};
const ANIMATION_DURATION_IN_MS = 240;
class OverlayPanel extends PureComponent {
    constructor() {
        super(...arguments);
        this.state = { isClosing: false, isOpening: false };
        this.openPanelTimeout = null;
        this.closePanelTimeout = null;
        this.openPanel = () => {
            const { onOpenEnd, onOpenStart, animationOnOpen, animationDurationOpen } = this.props;
            onOpenStart?.();
            this.setState({
                isClosing: false,
                isOpening: true,
            });
            // Use a conditional setTimeout to avoid unnecessary flickering if there is no animation.
            const durationInMS = animationOnOpen === AnimationInType.NONE ? 0 : animationDurationOpen || ANIMATION_DURATION_IN_MS;
            this.openPanelTimeout = conditionalSetTimeout(() => {
                this.setState({
                    isClosing: false,
                    isOpening: false,
                });
                onOpenEnd?.();
            }, durationInMS);
        };
        this.closePanel = () => {
            const { onCloseEnd, onCloseStart, animationOnClose, animationDurationClose } = this.props;
            onCloseStart?.();
            this.setState({
                isClosing: true,
                isOpening: false,
            });
            // Use a conditional setTimeout to avoid unnecessary flickering if there is no animation.
            const durationInMS = animationOnClose === AnimationOutType.NONE ? 0 : animationDurationClose || ANIMATION_DURATION_IN_MS;
            this.closePanelTimeout = conditionalSetTimeout(() => {
                this.setState({
                    isClosing: false,
                    isOpening: false,
                });
                onCloseEnd?.();
            }, durationInMS);
        };
    }
    componentDidMount() {
        const { shouldOpen } = this.props;
        // If the panel is open by default, we should open it.
        if (shouldOpen) {
            this.openPanel();
        }
    }
    componentDidUpdate(prevProps) {
        const { shouldOpen } = this.props;
        // If the value of shouldOpen changes we kick off the animations to open/close the panel.
        if (shouldOpen !== prevProps.shouldOpen) {
            if (shouldOpen) {
                this.openPanel();
            }
            else {
                this.closePanel();
            }
        }
    }
    getStyleObject() {
        const { overlayPanelName, shouldHide } = this.props;
        const styleObject = { zIndex: OverlayPanelZIndex[overlayPanelName] };
        if (shouldHide) {
            styleObject.display = 'none';
        }
        return styleObject;
    }
    componentWillUnmount() {
        if (this.openPanelTimeout) {
            clearTimeout(this.openPanelTimeout);
        }
        if (this.closePanelTimeout) {
            clearTimeout(this.closePanelTimeout);
        }
        if (this.props.shouldOpen) {
            if (this.props.onCloseStart) {
                this.props.onCloseStart();
            }
            if (this.props.onCloseEnd) {
                this.props.onCloseEnd();
            }
        }
    }
    render() {
        const { children, className, shouldOpen, animationOnClose, animationOnOpen, panelName } = this.props;
        const { isClosing, isOpening } = this.state;
        const styleObject = this.getStyleObject();
        return (React__default.createElement(HideComponent, { hidden: !isClosing && !shouldOpen, className: cx('WAC__overlay-panelContainer', `WAC__overlay--${panelName}`, className, {
                'WAC__overlay-panelContainer--animating': isOpening || isClosing,
            }) },
            React__default.createElement("div", { className: cx('WAC__overlay-panel', {
                    [`WAC__overlay-panel--closing--${animationOnClose}`]: isClosing,
                    'WAC__overlay-panel--closed': !isClosing && !shouldOpen,
                    [`WAC__overlay-panel--opening--${animationOnOpen}`]: isOpening,
                    'WAC__overlay-panel--open': !isOpening && shouldOpen,
                }), style: styleObject }, children)));
    }
}

function HomeScreenHeader(props, ref) {
    const { brandColor, onClose, onRestart, onCloseAndRestart } = props;
    const showRestartButton = useSelector((state) => state.config.public.showRestartButton || state.config.public.headerConfig?.showRestartButton);
    const showBackButton = useSelector((state) => state.persistedToBrowserStorage.chatState.homeScreenState.showBackToBot);
    const displayName = useSelector((state) => state.headerDisplayName);
    const customMenuOptions = useSelector((state) => state.customMenuOptions);
    const useAITheme = useSelector((state) => state.theme.useAITheme);
    const headerRef = useRef();
    // Reuse the imperative handles from the header.
    useImperativeHandle(ref, () => headerRef.current);
    // If there's no back button, it means this is the start of the conversation in which case there's no point in
    // showing the close-and-restart button.
    const hideCloseAndRestartButton = !showBackButton;
    const overflowClicked = useCallback((index) => {
        const { handler } = customMenuOptions[index];
        handler();
    }, [customMenuOptions]);
    const overflowItems = customMenuOptions?.map(option => option.text);
    return (React__default.createElement("div", { className: "WACHomeScreenHeader" },
        React__default.createElement(HeaderExport, { ref: headerRef, displayName: displayName, showCenter: true, showRestartButton: showRestartButton, hideCloseAndRestartButton: hideCloseAndRestartButton, onClickRestart: onRestart, onClickClose: onClose, onCloseAndRestart: onCloseAndRestart, overflowClicked: overflowClicked, overflowItems: overflowItems, useAITheme: useAITheme, brandColor: brandColor })));
}
const HomeScreenHeaderExport = React__default.memo(forwardRef(HomeScreenHeader));

function HomeScreenComponent({ botName, botAvatarURL, homeScreenConfig, homeScreenMessageInputRef, onStarterClick, onSendInput, isHydrated, onClose, onCloseAndRestart, onRestart, onToggleHomeScreen, }) {
    const intl = useIntl();
    const languagePack = useLanguagePack();
    const serviceManager = useServiceManager();
    const [avatarURLFailed, setAvatarURLFailed] = useState(false);
    const { showBackToBot } = useSelector((state) => state.persistedToBrowserStorage.chatState.homeScreenState);
    const inputCustomizations = useSelector((state) => state.config.public.messaging.inputs?.homeScreen);
    const prevIsHydrated = usePrevious(isHydrated);
    const useAITheme = useSelector((state) => state.theme.useAITheme);
    const homeScreenWriteableElement = serviceManager.writeableElements[WriteableElementName.HOME_SCREEN_AFTER_STARTERS_ELEMENT];
    const hasCustomContent = homeScreenWriteableElement.hasChildNodes();
    const { greeting, starters, bot_avatar_url, background, custom_content_only, allow_return } = homeScreenConfig;
    const homeScreenWithStarters = starters?.is_on && Boolean(starters.buttons?.length);
    const { suffix } = serviceManager.namespace;
    const starterBtnIDSuffix = suffix ? `--${suffix}` : '';
    const avatarURL = bot_avatar_url || botAvatarURL;
    const ariaBotAvatar = intl.formatMessage({ id: 'header_ariaBotAvatar' }, { botName });
    const backgroundSolid = !useAITheme && background === HomeScreenBackgroundType.SOLID;
    const backgroundTopCornerOut = !useAITheme && background === HomeScreenBackgroundType.TOP_CORNER_OUT;
    const backgroundBottomUp = !useAITheme && background === HomeScreenBackgroundType.BOTTOM_UP;
    const showAvatar = avatarURL && !avatarURLFailed && !useAITheme && !custom_content_only;
    const firstRender = isHydrated && !prevIsHydrated;
    return (React__default.createElement("div", { className: cx('WACHomeScreen', {
            'WACHomeScreen--backgroundAITheme': useAITheme,
            'WACHomeScreen--hydrationComplete': isHydrated,
            'WACHomeScreen--firstRender': firstRender,
            'WACHomeScreen--backgroundSolid': backgroundSolid,
            'WACHomeScreen--backgroundGradientTopCornerOut': backgroundTopCornerOut,
            'WACHomeScreen--backgroundGradientBottomUp': backgroundBottomUp,
        }) },
        React__default.createElement(HomeScreenHeaderExport, { brandColor: backgroundSolid ? BrandColorKind.ACCENT : BrandColorKind.PRIMARY, onRestart: onRestart, onClose: onClose, onCloseAndRestart: onCloseAndRestart }),
        React__default.createElement(WriteableElement$1, { slotName: WriteableElementName.HOME_SCREEN_HEADER_BOTTOM_ELEMENT, className: "WACHomeScreen__HomeScreenBottomElement", id: `homeScreenHeaderBottomElement${serviceManager.namespace.suffix}`, element: serviceManager.writeableElements[WriteableElementName.HOME_SCREEN_HEADER_BOTTOM_ELEMENT] }),
        React__default.createElement("div", { className: "WACPanelContent WACHomeScreen__content" },
            React__default.createElement("div", { className: "WACHomeScreen__bodyWrapper" },
                React__default.createElement("div", { className: cx('WACHomeScreen__body', {
                        'WACHomeScreen__body--noCustomContent': !hasCustomContent,
                        'WACHomeScreen__body--customContent': hasCustomContent,
                        'WACHomeScreen__body--customContentOnly': custom_content_only,
                        'WACHomeScreen__body--noAvatar': !showAvatar,
                    }) },
                    showAvatar && (React__default.createElement("div", { className: "WACHomeScreen__avatarHolder" },
                        React__default.createElement("img", { className: "WACHomeScreen__botAvatar", src: avatarURL, alt: ariaBotAvatar, onError: () => {
                                setAvatarURLFailed(true);
                                consoleError('The bot avatar URL failed to load.');
                            } }))),
                    React__default.createElement("div", { className: "WACHomeScreen__initialContent" },
                        !custom_content_only && React__default.createElement("div", { className: "WACHomeScreen__greeting" }, greeting),
                        !custom_content_only && homeScreenWithStarters && (React__default.createElement("div", { className: cx('WACHomeScreen__starters', {
                                // If there are more than 5 starters, animate in all starters at once.
                                'WACHomeScreen__starters--animateGroup': starters.buttons.length > 5,
                            }) }, starters.buttons.map((starter, index) => (React__default.createElement(unstable__ChatButton, { type: "button", size: ButtonSizeEnum.SMALL, kind: ButtonKindEnum.TERTIARY, isQuickAction: true, 
                            // eslint-disable-next-line react/no-array-index-key
                            key: index, id: `WACHomeScreen__starter-${index}${starterBtnIDSuffix}`, className: "WACHomeScreen__starter", onClick: () => onStarterClick(starter) }, starter.label))))))),
                React__default.createElement("div", { className: cx('WACHomeScreen__customContent', {
                        'WACHomeScreen__customContent--customContentOnly': custom_content_only,
                        'WACHomeScreen__customContent--animation': (hasCustomContent || custom_content_only) && !backgroundSolid,
                    }) },
                    React__default.createElement(WriteableElement$1, { slotName: WriteableElementName.HOME_SCREEN_AFTER_STARTERS_ELEMENT, id: `homeScreenAfterStartersElement${serviceManager.namespace.suffix}`, element: serviceManager.writeableElements[WriteableElementName.HOME_SCREEN_AFTER_STARTERS_ELEMENT] }))),
            React__default.createElement("div", { className: cx('WACHomeScreen__inputContainerWrapper', {
                    'WACHomeScreen__inputContainerWrapper--noCustomContent': !hasCustomContent && !custom_content_only,
                }) },
                showBackToBot && (React__default.createElement(unstable__ChatButton, { type: "button", size: ButtonSizeEnum.SMALL, kind: ButtonKindEnum.SECONDARY, id: "WACHomeScreen__backButton", className: "WACHomeScreen__backButton", onClick: onToggleHomeScreen },
                    React__default.createElement("span", { className: "WACHomeScreen__backButtonContent" },
                        React__default.createElement("span", { className: "WACHomeScreen__backButtonContentText" }, languagePack.homeScreen_returnToAssistant),
                        React__default.createElement(ArrowRight, null)))),
                React__default.createElement(WriteableElement$1, { slotName: WriteableElementName.HOME_SCREEN_BEFORE_INPUT_ELEMENT, id: `homeScreenBeforeInputElement${serviceManager.namespace.suffix}`, element: serviceManager.writeableElements[WriteableElementName.HOME_SCREEN_BEFORE_INPUT_ELEMENT] }),
                React__default.createElement("div", { className: "WACHomeScreen__inputContainer" },
                    React__default.createElement(InputExport, { ref: homeScreenMessageInputRef, onSendInput: onSendInput, disableInput: false, isInputVisible: true, disableSend: false, inputType: "homeScreen", languagePack: languagePack, serviceManager: serviceManager, inputCustomizations: inputCustomizations }))))));
}
const HomeScreenExport = React__default.memo(HomeScreenComponent);

/**
 * This home screen container is for experimental purposes that renders a home screen variation provided by launch
 * darkly.
 */
function HomeScreenContainer({ onClose, onCloseAndRestart, onPanelCloseStart, onPanelOpenStart, onPanelCloseEnd, onPanelOpenEnd, onSendBotInput, onSendButtonInput, onRestart, showHomeScreen, isHydrationAnimationComplete, homeScreenInputRef, onToggleHomeScreen, requestFocus, }) {
    const serviceManager = useServiceManager();
    const homeScreenConfig = useSelector((state) => state.homeScreenConfig);
    const botAvatarURL = useSelector((state) => state.botAvatarURL);
    const botName = useSelector((state) => state.botName);
    const isBrandingPanelOpen = useSelector((state) => state.brandingState.isOpen);
    const isCustomPanelOpen = useSelector((state) => state.customPanelState.isOpen);
    const prevIsHydrationAnimationComplete = usePrevious(isHydrationAnimationComplete);
    const shouldHide = isBrandingPanelOpen || isCustomPanelOpen;
    // If we're showing the home screen, hydration has completed, and hydration was complete on the previous render, then
    // this is a subsequent render.
    const subsequentRender = showHomeScreen && isHydrationAnimationComplete && prevIsHydrationAnimationComplete;
    const handleSendInput = useCallback((text) => {
        serviceManager.trackService.track({
            eventName: 'Home screen input message sent',
            eventDescription: 'The user sent a query by using the input field on the home screen',
        });
        onSendBotInput(text, { additionalInputOptions: { restart: true } });
    }, [onSendBotInput, serviceManager]);
    const handlerStarterClick = useCallback((starter) => {
        serviceManager.trackService.track({
            eventName: 'Home screen starter button sent',
            eventDescription: 'The user sent a query by using one of the conversation starters on the home screen',
        });
        onSendButtonInput({
            label: starter.label,
            value: {
                input: {
                    text: starter.label,
                },
            },
        }, THREAD_ID_MAIN);
    }, [onSendButtonInput, serviceManager]);
    return (React__default.createElement(OverlayPanel, { panelName: "HomeScreen", onOpenStart: onPanelOpenStart, onOpenEnd: onPanelOpenEnd, onCloseStart: onPanelCloseStart, onCloseEnd: onPanelCloseEnd, 
        // If this is a subsequent render lets fade the panel in. On first render don't animate the panel otherwise the
        // message container will be briefly visible.
        animationOnOpen: subsequentRender ? AnimationInType.FADE_IN : AnimationInType.NONE, animationOnClose: AnimationOutType.FADE_OUT, shouldOpen: showHomeScreen, shouldHide: shouldHide, serviceManager: serviceManager, overlayPanelName: OverlayPanelName.HOME_SCREEN },
        React__default.createElement(HomeScreenExport, { botName: botName, botAvatarURL: botAvatarURL, isHydrated: isHydrationAnimationComplete, homeScreenMessageInputRef: homeScreenInputRef, homeScreenConfig: homeScreenConfig, onSendInput: handleSendInput, onStarterClick: handlerStarterClick, onClose: onClose, onCloseAndRestart: onCloseAndRestart, onRestart: onRestart, onToggleHomeScreen: onToggleHomeScreen })));
}

/**
 * This component is rendered while the web chat is hydrating.
 */
function HydrationPanel({ onClose, languagePack, isHydrated, headerDisplayName, useHomeScreenVersion, }) {
    // This panel gets hidden instead of unmounted by the overlay container. We want to unmount the loading spinner
    // below when that happens.
    const isHidden = useContext(HideComponentContext);
    let header;
    if (useHomeScreenVersion) {
        header = React__default.createElement(HomeScreenHeaderExport, { onClose: onClose });
    }
    else {
        header = (React__default.createElement(BotHeaderExport, { onClose: onClose, headerDisplayName: headerDisplayName, onToggleHomeScreen: null, includeWriteableElement: false }));
    }
    return (React__default.createElement("div", { className: "WAC WAC__hydratingContainer" },
        header,
        React__default.createElement("div", { className: cx('WAC__hydrating', 'WACPanelContent', { 'WAC__hydrating--homeScreen': useHomeScreenVersion }) }, !isHidden && (React__default.createElement(MountChildrenOnDelay, { delay: 1500 },
            !isHydrated && React__default.createElement(AnnounceOnMountComponentExport, { announceOnce: languagePack.window_ariaWindowLoading }),
            React__default.createElement(Loading, { withOverlay: false, "aria-label": languagePack.window_ariaWindowLoading }))))));
}

/**
 * This component is a custom panel that renders external content similar to custom response types.
 */
function BasePanelComponent({ className, children, isOpen, hidePanelHeader, labelBackButton, title, hideBackButton, eventName, eventDescription, useAITheme, onClickCloseAndRestart, onClickRestart, ...headerProps }, ref) {
    const serviceManager = useServiceManager();
    const showRestartButton = useSelector((state) => state.config.public.showRestartButton || state.config.public.headerConfig?.showRestartButton);
    const headerRef = useRef();
    // Reuse the imperative handles from the header.
    useImperativeHandle(ref, () => headerRef.current);
    // Track the custom panel opening only when the panel has opened.
    useEffect(() => {
        if (isOpen && eventName) {
            serviceManager.trackService.track({ eventName, eventDescription });
        }
    }, [eventDescription, eventName, isOpen, serviceManager]);
    return (React__default.createElement(FocusTrap$1, { active: isOpen, focusTrapOptions: { clickOutsideDeactivates: true, returnFocusOnDeactivate: !IS_MOBILE, preventScroll: true } },
        React__default.createElement("div", { className: className },
            !hidePanelHeader && (React__default.createElement(HeaderExport, { ...headerProps, ref: headerRef, showRestartButton: showRestartButton, onClickRestart: onClickRestart, onCloseAndRestart: onClickCloseAndRestart, showBackButton: !hideBackButton, labelBackButton: labelBackButton, displayName: title, useAITheme: useAITheme, showCenter: true })),
            React__default.createElement("div", { className: "WACPanelContent" }, children))));
}
const BasePanelComponentExport = React__default.memo(React__default.forwardRef(BasePanelComponent));

/**
 * This component handles rendering a panel with body/footer content.
 */
function BodyAndFooterPanelComponent(props) {
    const { isOpen, isMessageForInput, localMessageItem, eventName, eventDescription, overlayPanelName, className, title, useAITheme, requestFocus, onClickBack, onClose, onClickRestart, onCloseAndRestart, onPanelOpenEnd, onPanelCloseEnd, onPanelOpenStart, onPanelCloseStart, } = props;
    const languagePack = useLanguagePack();
    const serviceManager = useServiceManager();
    const originalMessage = useSelector((state) => state.allMessagesByID[localMessageItem?.fullMessageID]);
    const showAnimations = props.showAnimations ?? true;
    const disableAnimation = !showAnimations;
    const openAnimation = disableAnimation ? AnimationInType.NONE : AnimationInType.SLIDE_IN_FROM_BOTTOM;
    const closeAnimation = disableAnimation ? AnimationOutType.NONE : AnimationOutType.SLIDE_OUT_TO_BOTTOM;
    return (React__default.createElement(OverlayPanel, { className: "WAC__overlay--covering", panelName: "BodyAndFooter", onOpenStart: onPanelOpenStart, onOpenEnd: onPanelOpenEnd, onCloseStart: onPanelCloseStart, onCloseEnd: onPanelCloseEnd, animationOnOpen: openAnimation, animationOnClose: closeAnimation, shouldOpen: isOpen, serviceManager: serviceManager, overlayPanelName: overlayPanelName },
        React__default.createElement(BasePanelComponentExport, { className: cx('WACBodyAndFooterComponent', className), eventName: eventName, eventDescription: eventDescription, isOpen: isOpen, title: title, disableAnimation: disableAnimation, useAITheme: useAITheme, labelBackButton: languagePack.general_returnToAssistant, onClickBack: onClickBack, onClickClose: onClose, onClickRestart: onClickRestart, onClickCloseAndRestart: onCloseAndRestart }, originalMessage && (React__default.createElement(BodyWithFooterComponent, { localMessageItem: localMessageItem, fullMessage: originalMessage, isMessageForInput: isMessageForInput, requestFocus: requestFocus })))));
}

/**
 * This component is a custom panel that renders external content similar to custom response types.
 */
function CustomPanel(props) {
    const { useAITheme, onPanelOpenEnd, onPanelCloseEnd, onPanelOpenStart, onPanelCloseStart, onClose, onCloseAndRestart, onClickRestart, } = props;
    const languagePack = useLanguagePack();
    const { isOpen, options } = useSelector((state) => state.customPanelState);
    const { title, hidePanelHeader, disableDefaultCloseAction, disableAnimation, onClickBack, onClickClose, onClickCloseAndRestart, } = options;
    const serviceManager = useServiceManager();
    const ariaAnnouncer = useAriaAnnouncer();
    const prevIsOpen = usePrevious(isOpen);
    const { hostElement } = serviceManager.customPanelManager.getPanel();
    const openAnimation = disableAnimation ? AnimationInType.NONE : AnimationInType.SLIDE_IN_FROM_BOTTOM;
    const closeAnimation = disableAnimation ? AnimationOutType.NONE : AnimationOutType.SLIDE_OUT_TO_BOTTOM;
    useEffect(() => {
        if (prevIsOpen !== isOpen && isOpen) {
            // Announce the title if it's visible.
            if (!hidePanelHeader && title) {
                ariaAnnouncer(title);
            }
        }
    }, [ariaAnnouncer, hidePanelHeader, isOpen, prevIsOpen, title]);
    const onClickBackLocal = useCallback(() => {
        serviceManager.store.dispatch(actions.setCustomPanelOpen(false));
        onClickBack?.();
    }, [serviceManager, onClickBack]);
    const onClickCloseLocal = useCallback(() => {
        if (!disableDefaultCloseAction) {
            checkAllowClose(serviceManager.store.getState().viewChanging);
            onClose();
        }
        onClickClose?.();
    }, [disableDefaultCloseAction, onClickClose, onClose, serviceManager]);
    // Note that this is not called until after the user has confirmed the action.
    const onCloseAndRestartLocal = useCallback(() => {
        if (!disableDefaultCloseAction) {
            checkAllowClose(serviceManager.store.getState().viewChanging);
            onCloseAndRestart();
        }
        onClickCloseAndRestart?.();
    }, [disableDefaultCloseAction, onClickCloseAndRestart, onCloseAndRestart, serviceManager]);
    return (React__default.createElement(OverlayPanel, { className: "WAC__overlay--covering", panelName: "Custom", onOpenStart: () => {
            serviceManager.eventBus.fire({ type: "customPanel:pre:open" /* BusEventType.CUSTOM_PANEL_PRE_OPEN */ }, serviceManager.instance);
            onPanelOpenStart();
        }, onOpenEnd: () => {
            serviceManager.eventBus.fire({ type: "customPanel:open" /* BusEventType.CUSTOM_PANEL_OPEN */ }, serviceManager.instance);
            onPanelOpenEnd();
        }, onCloseStart: () => {
            serviceManager.eventBus.fire({ type: "customPanel:pre:close" /* BusEventType.CUSTOM_PANEL_PRE_CLOSE */ }, serviceManager.instance);
            onPanelCloseStart();
        }, onCloseEnd: () => {
            serviceManager.eventBus.fire({ type: "customPanel:close" /* BusEventType.CUSTOM_PANEL_CLOSE */ }, serviceManager.instance);
            onPanelCloseEnd();
            serviceManager.store.dispatch(actions.setCustomPanelConfigOptions(DEFAULT_CUSTOM_PANEL_CONFIG_OPTIONS));
        }, animationOnOpen: openAnimation, animationOnClose: closeAnimation, shouldOpen: isOpen, serviceManager: serviceManager, overlayPanelName: OverlayPanelName.CUSTOM },
        React__default.createElement(BasePanelComponentExport, { className: "WACCustomPanel", eventName: "Custom panel opened", eventDescription: "A user opened a custom panel.", labelBackButton: languagePack.general_returnToAssistant, isOpen: isOpen, title: title, useAITheme: useAITheme, onClickBack: onClickBackLocal, onClickClose: onClickCloseLocal, onClickCloseAndRestart: onCloseAndRestartLocal, onClickRestart: onClickRestart, hidePanelHeader: hidePanelHeader, hideBackButton: options.hideBackButton, hideCloseButton: options.hideCloseButton, hideCloseAndRestartButton: options.hideCloseAndRestartButton },
            React__default.createElement(WriteableElement$1, { slotName: "customPanelElement", className: "WACCustomPanel__ContentContainer", element: hostElement }))));
}
/**
 * Checks the given flag and throws an error if it is true.
 */
function checkAllowClose(viewChanging) {
    if (viewChanging) {
        const message = 'You are attempting to close web chat from a custom panel while web chat is currently running a view' +
            ' change event which is not permitted. Please use the disableDefaultCloseAction option to disable' +
            ' this behavior for the custom panel and then use onClickClose to resolve your Promise that is handling' +
            ' the event; that Promise will allow you to close web chat.';
        consoleError(message);
        throw new Error(message);
    }
}
const CustomPanelExport = React__default.memo(CustomPanel);

/**
 * This panel is used to load the provided source url in an iframe for users to complete some action or view a piece
 * of content without having them leave web chat.
 */
function IFramePanelComponent(props, ref) {
    const languagePack = useLanguagePack();
    const { store } = useServiceManager();
    const { isOpen, messageItem } = useSelector((state) => state.iFramePanelState);
    const iframeTitle = messageItem?.title || messageItem?.source;
    return (React__default.createElement(BasePanelComponentExport, { ...props, ref: ref, className: "WACIFramePanel", isOpen: isOpen, onClickBack: () => store.dispatch(actions.closeIFramePanel()), title: iframeTitle, labelBackButton: languagePack.iframe_ariaClosePanel, eventName: "IFrame panel opened", eventDescription: "A user has opened the IFrame panel" },
        React__default.createElement("div", { className: "WACIFramePanel__Content" },
            React__default.createElement(IFrameComponent, { key: messageItem?.source /* This is important since it causes the IFrame to re-mount when the source changes */, source: messageItem?.source, title: iframeTitle }))));
}
const IFramePanelExport = React__default.memo(forwardRef(IFramePanelComponent));

/**
 * This panel is used to show the text of a conversational search citation.
 */
function ViewSourcePanel(props, ref) {
    const languagePack = useLanguagePack();
    const { store } = useServiceManager();
    const { isOpen, citationItem, relatedSearchResult } = useSelector((state) => state.viewSourcePanelState);
    let content;
    if (citationItem && 'text' in citationItem) {
        // If text is in the citation item then this is a conversational search source that is being shown.
        if (relatedSearchResult) {
            // If there is a related search result than show the search result body with the citation text highlighted.
            content = (React__default.createElement(SearchResultBodyWithCitationHighlightedExport, { relatedSearchResult: relatedSearchResult, citationItem: citationItem }));
        }
        else {
            // If there is no related search result than show the citation text.
            content = citationItem.text;
        }
    }
    else {
        // If the citation is not for a conversational search response then it must be for a legacy search result so we
        // should show the search result body.
        content = React__default.createElement(SearchResultBodyExport, { searchResult: citationItem });
    }
    return (React__default.createElement(BasePanelComponentExport, { ...props, ref: ref, className: "WACViewSourcePanel", isOpen: isOpen, onClickBack: () => store.dispatch(actions.setViewSourcePanelIsOpen(false)), title: citationItem?.title, labelBackButton: languagePack.general_ariaCloseInformationOverlay, eventName: "Search citation panel opened", eventDescription: "A user has opened the search citation panel" },
        React__default.createElement("div", { className: "WACViewSourcePanel__Content" }, content)));
}
const ViewSourcePanelExport = forwardRef(ViewSourcePanel);

/**
 * Returns a function that can be used to modify a state object to add a class name.
 */
function addClassNameToState(name) {
    return (previousState) => {
        const currentIndex = previousState.extraClassNames.indexOf(name);
        if (currentIndex === -1) {
            return {
                extraClassNames: [...previousState.extraClassNames, name],
            };
        }
        return null;
    };
}
/**
 * Returns a function that can be used to modify a state object to remove a class name.
 */
function removeClassNameFromState(name) {
    return (previousState) => {
        return {
            extraClassNames: previousState.extraClassNames.filter(value => value !== name),
        };
    };
}

// Indicates the messages container is at the standard, default width.
const WIDTH_BREAKPOINT_STANDARD = 'WAC--standardWidth';
// Indicates the messages container is at a narrow width breakpoint (small phones).
const WIDTH_BREAKPOINT_NARROW = 'WAC--narrowWidth';
// Indicates the messages container is at a wide width breakpoint.
const WIDTH_BREAKPOINT_WIDE = 'WAC--wideWidth';
class MainWindow extends Component {
    constructor() {
        super(...arguments);
        /**
         * Default state.
         */
        this.state = {
            closing: false,
            open: this.props.persistedToBrowserStorage.launcherState.viewState.mainWindow,
            modalPortalHostElement: null,
            numPanelsOpen: 0,
            numPanelsAnimating: 0,
            numPanelsCovering: 0,
            isHydrationAnimationComplete: this.props.isHydrated,
            shouldAutoFocus: this.props.config.public.shouldTakeFocusIfOpensAutomatically,
            extraClassNames: [],
        };
        /**
         * A React ref to the "WACMainWindow" element.
         */
        this.mainWindowRef = React__default.createRef();
        /**
         * A React ref to the "WACWidget" element.
         */
        this.containerRef = React__default.createRef();
        /**
         * A React ref to the bot {@link Chat} component.
         */
        this.botChatRef = React__default.createRef();
        /**
         * A React ref to the bot {@link Input} component.
         */
        this.homeScreenInputRef = React__default.createRef();
        /**
         * A React ref to the bot {@link Disclaimer} component.
         */
        this.disclaimerRef = React__default.createRef();
        /**
         * A React ref to the {@link BrandingOverlayComponent} component.
         */
        this.brandingRef = React__default.createRef();
        /**
         * A React ref to the animation container element.
         */
        this.animationContainerRef = React__default.createRef();
        /**
         * A React ref to the {@link IFramePanel} component.
         */
        this.iframePanelRef = React__default.createRef();
        /**
         * A React ref to the {@link ViewSourcePanel}.
         */
        this.viewSourcePanelRef = React__default.createRef();
        /**
         * The previous value of the body "visibility" property before the widget was opened.
         */
        this.previousBodyVisibility = undefined;
        /**
         * The previous value of the body "position" property before the widget was opened.
         */
        this.previousBodyPosition = undefined;
        /**
         * This will check to see if the messages list is anchored to the bottom of the panel and if so, ensure that the
         * list is still scrolled to the bottom. It will also set the classname appropriate to the current width.
         */
        this.onResize = () => {
            let appChatWidthBreakpoint;
            const height = this.containerRef?.current?.offsetHeight;
            const width = this.containerRef?.current?.offsetWidth;
            // The minimum width of the wide size + 1rem of padding on each side.
            if (width >= 672 + 16 + 16) {
                appChatWidthBreakpoint = ChatWidthBreakpoint.WIDE;
            }
            else if (width >= 360) {
                appChatWidthBreakpoint = ChatWidthBreakpoint.STANDARD;
            }
            else {
                appChatWidthBreakpoint = ChatWidthBreakpoint.NARROW;
            }
            this.props.serviceManager.store.dispatch(actions.setAppStateValue('chatWidth', width));
            this.props.serviceManager.store.dispatch(actions.setAppStateValue('chatHeight', height));
            this.props.serviceManager.store.dispatch(actions.setAppStateValue('chatWidthBreakpoint', appChatWidthBreakpoint));
        };
        /**
         * This is called when the visual viewport is resized.
         */
        this.onVisualViewportResize = () => {
            this.updateFromVisualViewport();
        };
        /**
         * The visual viewport is a relatively new API that provide this information about the actual visible area of the
         * browser. This takes into account things like the navigation bars and the keyboard. When the browser supports it
         * we can get more accurate about adjusting based on those things.
         */
        this.updateFromVisualViewport = () => {
            const element = this.props.serviceManager.container;
            const { visualViewport } = window;
            if (visualViewport) {
                // The viewport height is the visible area and the offset top is how much the viewport has been scrolled. The
                // viewport scrolling occurs on iOS devices when the keyboard is open but not on android.
                element.style.setProperty('--cds-chat-viewport-height', `${visualViewport.height}px`);
                element.style.setProperty('--cds-chat-viewport-width', `${visualViewport.width}px`);
                element.style.setProperty('--cds-chat-viewport-offsetTop', `${visualViewport.offsetTop}px`);
                element.style.setProperty('--cds-chat-viewport-offsetLeft', `${visualViewport.offsetLeft}px`);
            }
            else {
                // For browsers that don't support the visual viewport, for now we'll just settle on these values which only
                // sort of works.
                let height = '100vh';
                if (IS_IOS) {
                    const offset = getIPhoneHeightOffset();
                    height = `calc(100vh - ${offset}px)`;
                }
                element.style.setProperty('--cds-chat-viewport-height', height);
                element.style.setProperty('--cds-chat-viewport-width', '100vw');
                element.style.setProperty('--cds-chat-viewport-offsetTop', '0');
                element.style.setProperty('--cds-chat-viewport-offsetLeft', '0');
            }
        };
        /**
         * Sets the element that is used as the host for {@link ModalPortal}.
         */
        this.setModalPortalHostElement = (ref) => {
            if (this.state.modalPortalHostElement !== ref) {
                this.setState({ modalPortalHostElement: ref });
            }
        };
        this.onSendInput = async (text, source, options) => {
            const isInputToAgent = selectIsInputToAgent(this.props);
            const { serviceManager } = this.props;
            const state = serviceManager.store.getState();
            const { files } = selectInputState(state);
            if (isInputToAgent) {
                // If we're connected to an agent, then send the message to the agent instead of the bot.
                serviceManager.humanAgentService.sendMessageToAgent(text, files);
            }
            else {
                const messageRequest = createMessageRequestForText(text);
                // Track when user gives a response through input text.
                const trackProps = {
                    eventName: 'Assistant Message Received from User',
                    type: 'text',
                };
                serviceManager.actions.sendWithCatch(messageRequest, source, { ...options, trackEventProps: trackProps });
            }
            if (files.length) {
                serviceManager.store.dispatch(actions.clearInputFiles(isInputToAgent));
            }
        };
        this.onSendHomeButtonInput = (input) => {
            const messageRequest = createMessageRequestForChoice(input);
            // Track when user gives a response through input text.
            const trackProps = {
                eventName: 'Assistant Message Received from User',
                type: 'text',
            };
            this.props.serviceManager.actions.sendWithCatch(messageRequest, MessageSendSource.HOME_SCREEN_STARTER, {
                trackEventProps: trackProps,
                additionalInputOptions: { restart: true },
            });
        };
        // When the animation is complete, we go ahead and set the state to remove chat from DOM
        this.removeChatFromDom = () => {
            this.containerRef.current.removeEventListener('animationend', this.removeChatFromDom);
            this.setState({
                open: false,
                closing: false,
            });
        };
        // Reset web chat to new session.
        this.onRestart = async () => {
            await this.props.serviceManager.actions.restartConversation();
            this.requestFocus();
        };
        // Close window.
        this.onClose = async () => {
            return this.doClose(false);
        };
        /**
         * Called when the close-and-restart button is clicked. This will close the chat window and restart the bot
         * conversation. It will in addition, end any agent conversation that is active and switch back to the bot view. This
         * will be called after the user is presented with a confirmation panel.
         */
        this.onCloseAndRestart = async () => {
            return this.doClose(true);
        };
        /**
         * The callback that can be called to toggle between the home screen and the bot view.
         */
        this.onToggleHomeScreen = () => {
            const { isHomeScreenOpen } = this.props.serviceManager.store.getState().persistedToBrowserStorage.chatState.homeScreenState;
            const trackProps = {
                eventName: 'Home Screen Toggled',
                eventDescription: isHomeScreenOpen ? 'User went to the bot.' : 'User went to the home screen.',
            };
            this.props.serviceManager.actions.track(trackProps);
            this.props.serviceManager.store.dispatch(actions.toggleHomeScreen());
        };
        /**
         * Puts focus on the default focusable item for the current state of the application.
         */
        this.requestFocus = () => {
            try {
                if (this.state.shouldAutoFocus && !IS_MOBILE) {
                    // Put focus either on the input field or on the launcher button.
                    if (this.getShowBranding()) {
                        if (this.brandingRef.current) {
                            // Focus the branding close button.
                            this.brandingRef.current.focus();
                        }
                    }
                    else if (this.getShowDisclaimer()) {
                        if (this.disclaimerRef.current) {
                            // Focus the disclaimer accept button.
                            doFocusRef(this.disclaimerRef);
                        }
                    }
                    else if (this.getShowHomeScreen()) {
                        if (this.homeScreenInputRef.current) {
                            // Focus the home screen input field. Must be on timeout because of the home screen's own internal animations.
                            this.homeScreenInputRef.current.takeFocus();
                        }
                    }
                    else if (this.props.iFramePanelState.isOpen) {
                        if (this.iframePanelRef.current) {
                            // Focus the iframe panel close button.
                            this.iframePanelRef.current.requestFocus();
                        }
                    }
                    else if (this.botChatRef.current) {
                        // Focus the bot input field.
                        this.botChatRef.current.requestInputFocus();
                    }
                }
            }
            catch (error) {
                consoleError('An error occurred in MainWindow.requestFocus', error);
            }
        };
        /**
         * Called when the user starts or stops typing.
         */
        this.onUserTyping = (isTyping) => {
            if (this.props.serviceManager.store.getState().persistedToBrowserStorage.chatState.agentState.isConnected) {
                this.props.serviceManager.humanAgentService.userTyping(isTyping);
            }
        };
        /**
         * After accepting the disclaimer, we animate the disclaimer screen away.
         */
        this.onAcceptDisclaimer = () => {
            this.props.serviceManager.store.dispatch(actions.acceptDisclaimer());
        };
        /**
         * Update the panel counter to show a panel has opened, and add any proper focus.
         */
        this.onPanelOpenStart = (coverBackground) => {
            this.setState(prevState => ({
                numPanelsOpen: prevState.numPanelsOpen + 1,
                numPanelsAnimating: prevState.numPanelsAnimating + 1,
                numPanelsCovering: prevState.numPanelsCovering + (coverBackground ? 1 : 0),
            }), this.requestFocus);
        };
        /**
         * Update the panel counter to show a panel has opened.
         */
        this.onPanelOpenEnd = () => {
            this.setState(prevState => ({ numPanelsAnimating: prevState.numPanelsAnimating - 1 }));
        };
        /**
         * Update the panel counter to show a panel has started to close.
         */
        this.onPanelCloseStart = () => {
            this.setState(prevState => ({ numPanelsAnimating: prevState.numPanelsAnimating + 1 }), this.requestFocus);
        };
        /**
         * Update the panel counter to show a panel has started to close.
         */
        this.onPanelCloseEnd = (coverBackground) => {
            this.setState(prevState => ({
                numPanelsOpen: prevState.numPanelsOpen - 1,
                numPanelsAnimating: prevState.numPanelsAnimating - 1,
                numPanelsCovering: prevState.numPanelsCovering - (coverBackground ? 1 : 0),
            }));
        };
        /**
         * Mark the hydration panel as closed with animation completed.
         */
        this.onHydrationPanelClose = () => {
            this.setState({ isHydrationAnimationComplete: true }, this.requestFocus);
        };
    }
    componentDidMount() {
        const { public: publicConfig } = this.props.config;
        this.props.serviceManager.mainWindow = this;
        this.props.mainWindowRef.current = this;
        this.mainWindowObserver = new ResizeObserver(this.onResize);
        this.mainWindowObserver.observe(this.containerRef.current);
        if (IS_MOBILE && !publicConfig.disableCustomElementMobileEnhancements) {
            const { visualViewport } = window;
            if (visualViewport) {
                visualViewport.addEventListener('resize', this.onVisualViewportResize);
                visualViewport.addEventListener('scroll', this.updateFromVisualViewport);
            }
            // For devices that don't support the visual viewport we'll set some default values anyway.
            this.updateFromVisualViewport();
            this.updateBody(false);
        }
        // Make the scrollbar width available to CSS.
        this.containerRef.current.style.setProperty('--cds-chat-scrollbar-width', `${SCROLLBAR_WIDTH()}px`);
    }
    componentWillUnmount() {
        // Remove the listeners and observer we added previously.
        this.mainWindowObserver.unobserve(this.containerRef.current);
    }
    /**
     * This function is called when the app is destroyed. This component is never actually unmounted; the entire
     * container holding the web chat is simply removed from the DOM.
     */
    destroy() {
        if (IS_MOBILE && !this.props.config.public.disableCustomElementMobileEnhancements) {
            const { visualViewport } = window;
            if (visualViewport) {
                visualViewport.removeEventListener('resize', this.onVisualViewportResize);
                visualViewport.removeEventListener('scroll', this.updateFromVisualViewport);
            }
        }
        this.updateBody(true);
    }
    componentDidUpdate(oldProps, oldState) {
        const newProps = this.props;
        const newState = this.state;
        const { persistedToBrowserStorage, useCustomHostElement, isDestroyed } = newProps;
        const { viewState } = persistedToBrowserStorage.launcherState;
        const { open } = newState;
        const prevViewState = oldProps.persistedToBrowserStorage.launcherState.viewState;
        if (viewState.mainWindow !== prevViewState.mainWindow) {
            // If viewState.mainWindow has changed then perform the necessary updates.
            this.updateBody(false);
            this.updateFromVisualViewport();
        }
        if (isDestroyed && !oldProps.isDestroyed) {
            this.destroy();
        }
        if (viewState.mainWindow && (!prevViewState.mainWindow || !open)) {
            // If the main web chat window is now open, and it was not previously then perform the necessary updates.
            // See https://reactjs.org/docs/react-component.html#componentdidupdate.
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ open: true, isHydrationAnimationComplete: newProps.isHydrated }, () => {
                this.requestFocus();
            });
        }
        else if (!viewState.mainWindow && prevViewState.mainWindow && oldState.open && open) {
            // If the main web chat window was previously open but is now no longer open then preform the necessary updates.
            // See https://reactjs.org/docs/react-component.html#componentdidupdate.
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ closing: true });
            if (useCustomHostElement) {
                this.removeChatFromDom();
            }
            else {
                this.containerRef.current.addEventListener('animationend', this.removeChatFromDom);
                this.requestFocus();
            }
        }
        if (newProps.config.public.shouldTakeFocusIfOpensAutomatically) {
            // This code is to prevent the widget from grabbing focus when a reset occurs. The autofocus value starts as
            // true but when we detect a reset, we turn the autofocus off until the user sends a message.
            if (!oldProps.persistedToBrowserStorage.chatState.hasSentNonWelcomeMessage &&
                newProps.persistedToBrowserStorage.chatState.hasSentNonWelcomeMessage &&
                !this.state.shouldAutoFocus) {
                // eslint-disable-next-line react/no-did-update-set-state
                this.setState({ shouldAutoFocus: true });
            }
            else if (oldProps.botMessageState.localMessageIDs.length > newProps.botMessageState.localMessageIDs.length &&
                this.state.shouldAutoFocus) {
                // If there are fewer messages than there were previously, we infer that the web chat has been restarted.
                // In that case, don't do any autofocusing.
                this.setState({ shouldAutoFocus: false });
            }
            else if (oldProps.botMessageState.localMessageIDs.length < newProps.botMessageState.localMessageIDs.length &&
                !this.state.shouldAutoFocus) {
                // If a new message comes in, turn autofocusing back on.
                this.setState({ shouldAutoFocus: true });
            }
        }
        const newLastItemID = arrayLastValue(newProps.botMessageState.localMessageIDs);
        const oldLastItemID = arrayLastValue(oldProps.botMessageState.localMessageIDs);
        if (newLastItemID !== oldLastItemID && newState.shouldAutoFocus) {
            // The last item has changed. If it's not from history, then request focus.
            const lastMessageItem = newProps.allMessageItemsByID[newLastItemID];
            const lastMessage = newProps.allMessagesByID[lastMessageItem?.fullMessageID];
            if (!lastMessage?.history?.from_history) {
                this.requestFocus();
            }
        }
    }
    componentDidCatch(error, errorInfo) {
        this.props.serviceManager.actions.errorOccurred(createDidCatchErrorData('MainWindow', error, errorInfo, true));
    }
    /**
     * This function will apply the necessary updates to the body element. This is primarily used to deal with
     * adjustments made to mobile devices.
     */
    updateBody(unmounting) {
        if (IS_IOS && !this.props.config.public.disableCustomElementMobileEnhancements) {
            if ((window.screen.width <= 500 || window.screen.height <= 500) &&
                this.props.persistedToBrowserStorage.launcherState.viewState.mainWindow &&
                !unmounting) {
                this.previousBodyVisibility = document.body.style.getPropertyValue('visibility');
                this.previousBodyPosition = document.body.style.getPropertyValue('position');
                // On iOS devices when the keyboard is opened the viewport is immediately resized to the shorter view that is
                // visible between the navigation bar and the keyboard. However, this occurs before the keyboard has fully slid
                // into view. When the resize occurs we shrink the widget to the size of the viewport but this means that
                // during the animation the widget is too short and what is behind the widget becomes momentarily visible. To
                // deal with that we hide the body while the widget is open. For code searchability adding the words
                // "visibility: hidden !important" since that is how this styling is rendered on the body.
                document.body.style.setProperty('visibility', 'hidden', 'important');
                // To prevent the widget from being scrollable in a way that gets it into a bad state, we can set the body
                // to a fixed position. For code searchability adding the words "position: fixed !important" since that is how
                // this styling is rendered on the body.
                document.body.style.setProperty('position', 'fixed', 'important');
            }
            else {
                document.body.style.setProperty('visibility', this.previousBodyVisibility);
                document.body.style.setProperty('position', this.previousBodyPosition);
            }
        }
    }
    /**
     * Closes the main window and optional performs a close-and-restart.
     */
    async doClose(fromCloseAndRestart) {
        const { serviceManager } = this.props;
        const { activeTour } = serviceManager.store.getState().persistedToBrowserStorage.launcherState;
        const trackProps = {
            eventName: 'Widget Closed',
            eventDescription: 'User has closed the widget.',
            closeType: fromCloseAndRestart ? 'Close' : 'Close and restart',
        };
        serviceManager.actions.track(trackProps);
        if (activeTour) {
            serviceManager.trackService.track({
                eventName: 'Tour interaction',
                eventDescription: 'Tour continued.',
                type: 'From main window minimize button.',
            });
        }
        // Fire the view:change and window:close events. If the view change is canceled then the main window will stay open.
        if (fromCloseAndRestart) {
            // If the chat is restarting then try to open the launcher.
            const newViewState = await serviceManager.actions.changeView(ViewType.LAUNCHER, {
                mainWindowCloseReason: MainWindowCloseReason.MAIN_WINDOW_CLOSED_AND_RESTARTED,
            });
            if (!newViewState.mainWindow) {
                // If the main window is no longer visible restart the conversation.
                await serviceManager.actions.restartConversation();
            }
        }
        else {
            // If the chat is not restarting and there is an active tour then try to open the tour view, otherwise try to open
            // the launcher.
            await serviceManager.actions.changeView(activeTour ? ViewType.TOUR : ViewType.LAUNCHER, {
                mainWindowCloseReason: MainWindowCloseReason.DEFAULT_MINIMIZE,
            });
        }
    }
    /**
     * Note: This function relies on a lie which is  homeScreenState.isHomeScreenOpen which by default is true when store
     * is created based on whether Home Screen is enabled and only later updated to its real value after hydration.
     */
    getShowHomeScreen() {
        return (this.props.homeScreenConfig.is_on &&
            this.props.persistedToBrowserStorage.chatState.homeScreenState.isHomeScreenOpen &&
            !this.getShowDisclaimer() &&
            !this.getShowBranding());
    }
    getShowDisclaimer() {
        return (this.props.config.public.disclaimer?.isOn &&
            !this.props.persistedToBrowserStorage.chatState.disclaimersAccepted[window.location.hostname] &&
            !this.getShowBranding());
    }
    getShowBranding() {
        return this.props.brandingState.config.is_on && this.props.brandingState.isOpen;
    }
    /**
     * Initiates a {@link Chat#doAutoScroll} on the currently visible chat panel.
     */
    doAutoScroll(options) {
        this.botChatRef?.current?.doAutoScroll(options);
    }
    /**
     * Returns the current scrollBottom value for the message scroll panel.
     */
    getMessagesScrollBottom() {
        return this.botChatRef?.current?.getMessagesScrollBottom();
    }
    /**
     * Scrolls to the (full) message with the given ID. Since there may be multiple message items in a given
     * message, this will scroll the first message to the top of the message window.
     *
     * @param messageID The (full) message ID to scroll to.
     * @param animate Whether or not the scroll should be animated. Defaults to true.
     */
    doScrollToMessage(messageID, animate = true) {
        this.botChatRef.current?.doScrollToMessage(messageID, animate);
    }
    /**
     * Returns the element that represents the input field (text area) on the main message area.
     */
    getMessageInput() {
        return this.botChatRef.current?.getMessageInput();
    }
    /**
     * Returns the element that represents the input field (text area) on the home screen.
     */
    getHomeScreenInput() {
        return this.homeScreenInputRef.current?.getMessageInput();
    }
    /**
     * Adds the given class name to the main window element.
     */
    addClassName(name) {
        this.setState(addClassNameToState(name));
    }
    /**
     * Removes the given class name from the main window element.
     */
    removeClassName(name) {
        this.setState(removeClassNameFromState(name));
    }
    /**
     * Returns the chat widget portion of the application.
     *
     * Note: If the home screen mini variation is to be displayed, the hydration panel shouldn't be rendered because
     * otherwise it appears for a split second before home screen is loaded.
     */
    renderChat() {
        const { isHydrated, config, chatWidthBreakpoint } = this.props;
        const showCovering = this.state.numPanelsCovering > 0 &&
            config.public.layout?.hasContentMaxWidth &&
            chatWidthBreakpoint === ChatWidthBreakpoint.WIDE;
        return (React__default.createElement("div", { className: "WACWidget--content" },
            this.renderCustomPanel(),
            this.renderHydrationPanel(),
            isHydrated && (React__default.createElement(React__default.Fragment, null,
                this.renderDisclaimerPanel(),
                this.renderResponsePanel(),
                this.renderHomeScreenPanel(),
                this.renderIFramePanel(),
                this.renderBrandingPanel(),
                this.renderViewSourcePanel(),
                showCovering && React__default.createElement("div", { className: "WACBackgroundCover" }),
                this.renderBotChat()))));
    }
    /**
     * Render the chat with the assistant.
     */
    renderBotChat() {
        const { botName, languagePack, config, serviceManager, botMessageState, agentState, allMessageItemsByID, isHydrated, locale, theme, headerDisplayName, headerAvatarConfig: headerAvatar, } = this.props;
        const { numPanelsAnimating, numPanelsOpen, isHydrationAnimationComplete } = this.state;
        const inputState = selectInputState(this.props);
        const agentDisplayState = selectAgentDisplayState(this.props);
        const showDisclaimer = this.getShowDisclaimer();
        let hideBotContainer;
        if (!isHydrationAnimationComplete) {
            // If the Hydration animation is still running then hide the bot container.
            hideBotContainer = true;
        }
        else if (numPanelsAnimating > 0) {
            // If any panel is animating then show the bot container for the duration of the animation.
            hideBotContainer = false;
        }
        else if (numPanelsOpen > 0) {
            // Otherwise if any panel is open than hide the bot container.
            hideBotContainer = true;
        }
        return (React__default.createElement(HideComponent, { className: "WACBotContainer", hidden: hideBotContainer },
            React__default.createElement(Chat$1, { botName: botName, headerDisplayName: headerDisplayName, headerAvatarConfig: headerAvatar, ref: this.botChatRef, languagePack: languagePack, config: config, serviceManager: serviceManager, toolingType: config.public.__ibm__.toolingType, onClose: this.onClose, onCloseAndRestart: this.onCloseAndRestart, messageState: botMessageState, onSendInput: (text, beforeSendPayload) => this.onSendInput(text, MessageSendSource.MESSAGE_INPUT, { sendEventPayload: beforeSendPayload }), agentState: agentState, agentDisplayState: agentDisplayState, allMessageItemsByID: allMessageItemsByID, onRestart: this.onRestart, isHydrated: isHydrated, isHydrationAnimationComplete: isHydrationAnimationComplete && !showDisclaimer, inputState: inputState, onToggleHomeScreen: this.onToggleHomeScreen, onUserTyping: this.onUserTyping, locale: locale, useAITheme: theme.useAITheme, carbonTheme: theme.carbonTheme })));
    }
    /**
     * Render the panel with the loading state when we are hydrating the web chat.
     */
    renderHydrationPanel() {
        const { botMessageState, serviceManager, languagePack, catastrophicErrorType, headerDisplayName, persistedToBrowserStorage, homeScreenConfig, } = this.props;
        const { viewState } = persistedToBrowserStorage.launcherState;
        // We need to make an educated guess whether the home screen is going to be displayed after hydration is
        // complete, so we can show a version of the hydration panel that matches to avoid a flickering transition when
        // the hydration panel is only displayed very briefly. If the user's assistant session has expired, this will be
        // wrong, but it's rare enough to be not worth addressing.
        const useHomeScreenVersion = homeScreenConfig.is_on && !persistedToBrowserStorage.launcherState.hasSentNonWelcomeMessage;
        return (React__default.createElement(OverlayPanel, { panelName: "Hydration", onOpenStart: () => this.onPanelOpenStart(false), onCloseStart: this.onPanelCloseStart, onOpenEnd: this.onPanelOpenEnd, onCloseEnd: () => {
                this.onHydrationPanelClose();
                this.onPanelCloseEnd(false);
            }, animationOnOpen: AnimationInType.NONE, animationOnClose: AnimationOutType.NONE, shouldOpen: botMessageState.isHydratingCounter > 0 && !catastrophicErrorType && viewState.mainWindow, shouldHide: false, serviceManager: serviceManager, overlayPanelName: OverlayPanelName.HYDRATING },
            React__default.createElement(HydrationPanel, { headerDisplayName: headerDisplayName, isHydrated: botMessageState.isHydratingCounter === 0, serviceManager: serviceManager, onClose: this.onClose, languagePack: languagePack, useHomeScreenVersion: useHomeScreenVersion })));
    }
    /**
     * Render the panel for when the web chat completely fails.
     */
    renderCatastrophicPanel() {
        const { serviceManager, botName, languagePack, catastrophicErrorType, headerDisplayName } = this.props;
        return (React__default.createElement(OverlayPanel, { panelName: "Catastrophic", animationOnOpen: AnimationInType.NONE, animationOnClose: AnimationOutType.NONE, shouldOpen: true, serviceManager: serviceManager, overlayPanelName: OverlayPanelName.CATASTROPHIC },
            React__default.createElement(CatastrophicErrorExport, { onClose: this.onClose, headerDisplayName: headerDisplayName, serviceManager: serviceManager, languagePack: languagePack, onRestart: this.onRestart, showHeader: true, botName: botName, catastrophicErrorType: catastrophicErrorType })));
    }
    /**
     * Render the disclaimer panel.
     */
    renderDisclaimerPanel() {
        const { serviceManager, config } = this.props;
        const showDisclaimer = this.getShowDisclaimer();
        return config.public.disclaimer?.isOn ? (React__default.createElement(OverlayPanel, { panelName: "Disclaimer", onOpenStart: () => this.onPanelOpenStart(false), onCloseStart: this.onPanelCloseStart, onOpenEnd: this.onPanelOpenEnd, onCloseEnd: () => this.onPanelCloseEnd(false), animationOnOpen: AnimationInType.FADE_IN, animationOnClose: AnimationOutType.FADE_OUT, shouldOpen: showDisclaimer, serviceManager: serviceManager, overlayPanelName: OverlayPanelName.DISCLAIMER },
            React__default.createElement(Disclaimer, { onAcceptDisclaimer: this.onAcceptDisclaimer, onClose: this.onClose, disclaimerHTML: config.public.disclaimer?.disclaimerHTML, disclaimerAcceptButtonRef: this.disclaimerRef }))) : null;
    }
    /**
     * Render the home screen panel.
     */
    renderHomeScreenPanel() {
        const { isHydrationAnimationComplete } = this.state;
        const showHomeScreen = this.getShowHomeScreen();
        return (React__default.createElement(HomeScreenContainer, { onPanelOpenStart: () => this.onPanelOpenStart(false), onPanelOpenEnd: this.onPanelOpenEnd, onPanelCloseStart: this.onPanelCloseStart, onPanelCloseEnd: () => this.onPanelCloseEnd(false), onClose: this.onClose, onCloseAndRestart: this.onCloseAndRestart, onSendBotInput: (text, beforeSendPayload) => this.onSendInput(text, MessageSendSource.HOME_SCREEN_INPUT, { sendEventPayload: beforeSendPayload }), onSendButtonInput: this.onSendHomeButtonInput, onRestart: this.onRestart, showHomeScreen: showHomeScreen, isHydrationAnimationComplete: isHydrationAnimationComplete, homeScreenInputRef: this.homeScreenInputRef, onToggleHomeScreen: this.onToggleHomeScreen, requestFocus: this.requestFocus }));
    }
    /**
     * Render the branding panel.
     */
    renderBrandingPanel() {
        const { serviceManager, brandingState, theme } = this.props;
        const { config, isOpen } = brandingState;
        // Close the branding overlay if branding has been toggled off and the overlay is open.
        if (!config.is_on && isOpen) {
            serviceManager.store.dispatch(actions.setBrandingOverlayIsOpen(false));
        }
        return (React__default.createElement(OverlayPanel, { className: "WAC__overlay--covering", panelName: "Branding", onOpenStart: () => this.onPanelOpenStart(true), onCloseStart: this.onPanelCloseStart, onOpenEnd: this.onPanelOpenEnd, onCloseEnd: () => {
                serviceManager.store.dispatch(actions.setBrandingBannerShown(true));
                this.onPanelCloseEnd(true);
            }, animationOnOpen: AnimationInType.BRANDING_SLIDE_IN_FROM_BOTTOM, animationOnClose: AnimationOutType.SLIDE_OUT_TO_BOTTOM, shouldOpen: isOpen, serviceManager: serviceManager, overlayPanelName: OverlayPanelName.BRANDING },
            React__default.createElement(BrandingOverlayExport, { brandingCloseButtonRef: this.brandingRef, carbonTheme: theme.carbonTheme })));
    }
    /**
     * Renders a panel containing an iframe to load the provided source. Only render the iframe panel if there's a source
     * to load. The reason being that the panel will receive an empty source and after 10 seconds will announce that the
     * source is not available.
     */
    renderIFramePanel() {
        const { serviceManager, iFramePanelState } = this.props;
        return (React__default.createElement(OverlayPanel, { className: "WAC__overlay--covering", panelName: "IFrame", onOpenStart: () => this.onPanelOpenStart(true), onCloseStart: this.onPanelCloseStart, onOpenEnd: this.onPanelOpenEnd, onCloseEnd: () => this.onPanelCloseEnd(true), animationOnOpen: AnimationInType.SLIDE_IN_FROM_BOTTOM, animationOnClose: AnimationOutType.SLIDE_OUT_TO_BOTTOM, shouldOpen: iFramePanelState.isOpen, serviceManager: serviceManager, overlayPanelName: OverlayPanelName.IFRAME },
            React__default.createElement(IFramePanelExport, { useAITheme: this.props.theme.useAITheme, ref: this.iframePanelRef, onClickClose: this.onClose, onClickRestart: this.onRestart, onClickCloseAndRestart: this.onCloseAndRestart })));
    }
    renderViewSourcePanel() {
        const { serviceManager, viewSourcePanelState } = this.props;
        return (React__default.createElement(OverlayPanel, { className: "WAC__overlay--covering", panelName: "ConversationalSearchCitation", onOpenStart: () => this.onPanelOpenStart(true), onCloseStart: this.onPanelCloseStart, onOpenEnd: this.onPanelOpenEnd, onCloseEnd: () => this.onPanelCloseEnd(true), animationOnOpen: AnimationInType.SLIDE_IN_FROM_BOTTOM, animationOnClose: AnimationOutType.SLIDE_OUT_TO_BOTTOM, shouldOpen: viewSourcePanelState.isOpen, serviceManager: serviceManager, overlayPanelName: OverlayPanelName.CONVERSATIONAL_SEARCH_CITATION },
            React__default.createElement(ViewSourcePanelExport, { ref: this.viewSourcePanelRef, onClickClose: this.onClose, onClickRestart: this.onRestart, onClickCloseAndRestart: this.onCloseAndRestart })));
    }
    /**
     * Renders a custom panel that can host Deb content.
     */
    renderCustomPanel() {
        return (React__default.createElement(CustomPanelExport, { useAITheme: this.props.theme.useAITheme, onClose: this.onClose, onClickRestart: this.onRestart, onCloseAndRestart: this.onCloseAndRestart, onPanelOpenStart: () => this.onPanelOpenStart(true), onPanelOpenEnd: this.onPanelOpenEnd, onPanelCloseStart: this.onPanelCloseStart, onPanelCloseEnd: () => this.onPanelCloseEnd(true) }));
    }
    /**
     * Renders a panel that is surfaced by an authored response type that supports opening a panel either through user
     * interaction or automatically.
     */
    renderResponsePanel() {
        if (!this.props.responsePanelState.localMessageItem) {
            return null;
        }
        const { isOpen, localMessageItem, isMessageForInput } = this.props.responsePanelState;
        const messageItem = localMessageItem?.item;
        const isPanelItem = isPanelResponseType(messageItem);
        let panelOptions;
        let eventName;
        let eventDescription;
        let overlayPanelName;
        if (isPanelItem) {
            panelOptions = localMessageItem?.item;
            eventName = `"panel" response panel opened`;
            eventDescription = 'Panel opened through "show_panel" button type';
            overlayPanelName = OverlayPanelName.SHOW_PANEL;
        }
        else {
            panelOptions = (localMessageItem?.item).panel;
            eventName = `"Show panel" opened`;
            eventDescription = 'Panel opened through panel response type';
            overlayPanelName = OverlayPanelName.PANEL_RESPONSE;
        }
        return (React__default.createElement(BodyAndFooterPanelComponent, { eventName: eventName, eventDescription: eventDescription, overlayPanelName: overlayPanelName, isOpen: isOpen, isMessageForInput: isMessageForInput, localMessageItem: localMessageItem, title: panelOptions?.title, showAnimations: panelOptions?.show_animations, useAITheme: this.props.theme.useAITheme, requestFocus: this.requestFocus, onClose: this.onClose, onClickRestart: this.onRestart, onCloseAndRestart: this.onCloseAndRestart, onClickBack: () => this.props.serviceManager.store.dispatch(actions.setResponsePanelIsOpen(false)), onPanelOpenStart: () => this.onPanelOpenStart(true), onPanelOpenEnd: this.onPanelOpenEnd, onPanelCloseStart: this.onPanelCloseStart, onPanelCloseEnd: () => {
                this.onPanelCloseEnd(true);
                this.props.serviceManager.store.dispatch(actions.setResponsePanelContent(null, false));
                if (isPanelItem) {
                    try {
                        this.props.serviceManager.actions.sendUpdateHistoryEvent(localMessageItem.fullMessageID, {
                            history: { is_panel_closed: true },
                        });
                    }
                    catch {
                        consoleLog('Failed to send event message');
                    }
                }
            } }));
    }
    renderWidget() {
        const { serviceManager, useCustomHostElement, locale, catastrophicErrorType, brandingState, config, isHydrated, theme, chatWidthBreakpoint, layout, languagePack, } = this.props;
        const { closing, open, extraClassNames } = this.state;
        const localeClassName = `WACLocale-${locale || 'en'}`;
        const { config: brandingConfig, showBanner } = brandingState;
        const showGlass = config.public.enableFocusTrap &&
            open &&
            !config.public.hideCloseButton &&
            !config.public.headerConfig.hideMinimizeButton;
        const trapActive = Boolean(showGlass && isHydrated);
        // The branding banner should not be visible when using the AI theme.
        const brandingBannerVisible = !theme.useAITheme && brandingConfig.is_on && showBanner;
        const isWideWidth = chatWidthBreakpoint === ChatWidthBreakpoint.WIDE;
        // The empty div below is required because FocusTrap will attach a ref to it overwriting our ref which we have
        // on the next div below that.
        /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
        return (React__default.createElement(FocusTrap$1, { active: trapActive },
            React__default.createElement("div", { className: cx('WACMainWindow', 'WACWidget__FocusTrapContainer', ...extraClassNames), ref: this.mainWindowRef },
                showGlass && React__default.createElement("div", { className: "WACWidget__FocusTrapGlass" }),
                React__default.createElement("div", { id: `WACWidget${serviceManager.namespace.suffix}`, className: cx(`WACWidget ${localeClassName}`, {
                        'WACWidget--rounded': theme.corners === CornersType.ROUND,
                        'WACWidget--defaultElement': !useCustomHostElement,
                        'WACWidget--launched': !closing,
                        'WACWidget--closing': closing,
                        'WACWidget--closed': !open,
                        'WACWidget--maxWidth': isWideWidth && layout.hasContentMaxWidth,
                        [WIDTH_BREAKPOINT_NARROW]: chatWidthBreakpoint === ChatWidthBreakpoint.NARROW,
                        [WIDTH_BREAKPOINT_STANDARD]: chatWidthBreakpoint === ChatWidthBreakpoint.STANDARD,
                        [WIDTH_BREAKPOINT_WIDE]: isWideWidth,
                    }), ref: this.containerRef },
                    React__default.createElement(VisuallyHidden, null,
                        React__default.createElement("h1", null, languagePack.window_title)),
                    catastrophicErrorType && this.renderCatastrophicPanel(),
                    !catastrophicErrorType && (React__default.createElement(React__default.Fragment, null,
                        React__default.createElement("div", { ref: this.animationContainerRef, className: cx('WACWidget__animationContainer', {
                                'WACWidget__animationContainer--withBrandingBanner': brandingBannerVisible,
                            }), onScroll: () => {
                                // When web chat initially opens, it's possible for focusable elements inside a custom panel to
                                // cause the element to scroll during the opening animations. The listener to reset any
                                // scrolling that is happening.
                                if (this.animationContainerRef.current.scrollTop !== 0) {
                                    this.animationContainerRef.current.scrollTop = 0;
                                }
                            } }, this.renderChat()),
                        brandingBannerVisible && React__default.createElement(BrandingBannerComponentExport, null))),
                    React__default.createElement("div", { className: "WACMainWindowModalHost", ref: this.setModalPortalHostElement })))));
    }
    render() {
        return (React__default.createElement(ModalPortalRootContext.Provider, { value: this.state.modalPortalHostElement }, this.renderWidget()));
    }
}
var MainWindow$1 = connect((state) => state, null, null, {
    forwardRef: true,
})(MainWindow);

function App({ serviceManager, hostElement, applicationStyles, fontStyles }) {
    const { store, trackService, namespace } = serviceManager;
    const { userID, config } = store.getState();
    if (config.public.debug) {
        consoleDebug('[render] Called render');
    }
    const serviceInstanceIDOrNotSet = config.remote.initConfig.service_instance_id || config.public.serviceInstanceID || NOT_SET;
    // Get the unique tracking id for amplitude purposes.
    const trackUserID = useMemo(() => ({ trackUserID: trackService.getTrackUserID(serviceInstanceIDOrNotSet, userID) }), [serviceInstanceIDOrNotSet, trackService, userID]);
    const combinedStyles = config.public.__ibm__?.useShadowRoot
        ? `${fontStyles ? `${fontStyles} ` : ''}${applicationStyles}`
        : replaceWithNamespaceIfRequired(namespace, `${fontStyles} ${applicationStyles}`);
    return (React__default.createElement(Provider, { store: store },
        React__default.createElement(AnalyticsContext.Provider, { value: trackUserID },
            React__default.createElement(AppContainer$2, { serviceManager: serviceManager, hostElement: hostElement, applicationStyles: combinedStyles }))));
}
function AppContainer$2({ serviceManager, hostElement, applicationStyles }) {
    const languagePack = useSelector((state) => state.languagePack);
    const cssVariableOverrides = useSelector((state) => state.cssVariableOverrides);
    const theme = useSelector((state) => state.theme);
    const config = useSelector((state) => state.config);
    const layout = useSelector((state) => state.layout);
    const useShadowRoot = useSelector((state) => state.config.public.__ibm__?.useShadowRoot);
    const { namespace } = serviceManager;
    const { suffix, originalName } = namespace;
    const dispatch = useDispatch();
    const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });
    const cssVariableOverrideString = useMemo(() => {
        return convertCSSVariablesToString(cssVariableOverrides, namespace, useShadowRoot);
    }, [cssVariableOverrides, namespace, useShadowRoot]);
    // If direction is "rtl" then the web chat will render with the right-to-left styles.
    // If direction is anything else, the web chat uses left-to-right styles by default.
    // If document.dir cannot be determined, using auto will inherit directionality from the page.
    const dir = document.dir || 'auto';
    useOnMount(() => {
        // Add the listener for updating the window size.
        const windowListener = () => {
            setWindowSize({ width: window.innerWidth, height: window.innerHeight });
        };
        window.addEventListener('resize', windowListener);
        // Add the listener for detecting page visibilities changes.
        const visibilityListener = () => {
            dispatch(actions.setIsBrowserPageVisible(document.visibilityState === 'visible'));
        };
        document.addEventListener('visibilitychange', visibilityListener);
        return () => {
            window.removeEventListener('resize', windowListener);
            document.removeEventListener('visibilitychange', visibilityListener);
        };
    });
    return (React__default.createElement("div", { id: config.public.__ibm__?.useShadowRoot ? undefined : `WACContainer${suffix}`, className: "WACContainer", "data-namespace": originalName, ref: node => {
            if (node && hostElement) {
                // React doesn't let us set "!important" in a style value inline.
                node.style.setProperty('height', '100%', 'important');
                node.style.setProperty('width', '100%', 'important');
            }
        } },
        React__default.createElement("div", { className: "WACContainer--styles" },
            React__default.createElement("style", { "data-base-styles": "true", nonce: config.public.cspNonce }, applicationStyles || `.WACContainer { visibility: hidden; }`),
            React__default.createElement("style", { "data-variables-custom": "true", nonce: config.public.cspNonce }, cssVariableOverrideString)),
        React__default.createElement("div", { className: cx(`WACContainer--render`, getThemeClassNames(theme), {
                'WACContainer-disableMobileEnhancements': hostElement && config.public.disableCustomElementMobileEnhancements,
                'WAC-isPhone': IS_PHONE && !config.public.disableCustomElementMobileEnhancements,
                'WAC-isPhonePortraitMode': IS_PHONE_IN_PORTRAIT_MODE && !config.public.disableCustomElementMobileEnhancements,
                'WAC--frameless': !layout?.showFrame,
            }), dir: dir },
            React__default.createElement(unstable_LayoutDirection, { className: "WACContainer__LayoutDirection", dir: !document?.dir || document?.dir === 'auto' ? 'ltr' : document.dir },
                React__default.createElement(unstable_TextDirection, { getTextDirection: () => document?.dir || 'auto' },
                    React__default.createElement(WindowSizeContext.Provider, { value: windowSize },
                        React__default.createElement(ServiceManagerContext.Provider, { value: serviceManager },
                            React__default.createElement(RawIntlProvider, { value: serviceManager.intl },
                                React__default.createElement(LanguagePackContext.Provider, { value: languagePack },
                                    React__default.createElement(AriaAnnouncerProvider, null,
                                        React__default.createElement(MainContainer, { serviceManager: serviceManager, hostElement: hostElement })))))))))));
}
function MainContainer(props) {
    const { hostElement, serviceManager } = props;
    // We always render the launcher (unless state.launcher.config.is_on is set to false), but we hide it with CSS
    // if the tour or main web chat window are open.
    const showLauncher = useSelector((state) => state.launcher.config.is_on);
    const { viewState } = useSelector((state) => state.persistedToBrowserStorage.launcherState);
    const tourContainerRef = useRef();
    const mainWindowRef = useRef();
    const showTour = viewState.tour;
    // This indicates if the tour has been opened at least once. The tour isn't rendered until it's been opened the first
    // time. Rendering of the tour is delayed to prevent the tour buttons being clicked by pen testing tools. After the
    // tour has been opened once it's simply shown and hidden using CSS, instead of unmounting it. CSS is used to show
    // and hide the tour so that the scroll position and video playback positions can be preserved within steps when
    // the tour is hidden.
    const showedTourOnce = useRef(showTour);
    showedTourOnce.current = showTour || showedTourOnce.current;
    const intl = useIntl();
    const namespace = serviceManager.namespace.originalName;
    const languageKey = namespace ? 'window_ariaChatRegionNamespace' : 'window_ariaChatRegion';
    const regionLabel = intl.formatMessage({ id: languageKey }, { namespace });
    useOnMount(() => {
        /**
         * Puts focus on the default element for the currently open window in the application.
         */
        function requestFocus() {
            try {
                const { persistedToBrowserStorage } = serviceManager.store.getState();
                const { viewState } = persistedToBrowserStorage.launcherState;
                if (viewState.tour) {
                    // If there is a tour visible prioritize focusing on that over the main window.
                    tourContainerRef.current?.requestFocus();
                }
                else if (viewState.mainWindow) {
                    // If there is no tour visible then focus on the main window.
                    mainWindowRef.current?.requestFocus();
                }
            }
            catch (error) {
                consoleError('An error occurred in App.requestFocus', error);
            }
        }
        serviceManager.appWindow = { requestFocus };
    });
    // Always render the main window and let it control whether it should be visible with css. Only render the tour
    // once it's been opened once.
    return (React__default.createElement("div", { className: "WACWidget__regionContainer", role: "region", "aria-label": regionLabel },
        React__default.createElement(MainWindow$1, { mainWindowRef: mainWindowRef, useCustomHostElement: Boolean(hostElement), serviceManager: serviceManager }),
        showedTourOnce.current && React__default.createElement(TourContainerExport, { ref: tourContainerRef }),
        showLauncher && React__default.createElement(LauncherContainer, null)));
}
function replaceWithNamespaceIfRequired(namespace, styles) {
    if (namespace.originalName) {
        return styles.replace(/#WACContainer/g, `#WACContainer${namespace.suffix}`);
    }
    return styles;
}

/**
 * This is a utility component that is used to manage all the user defined responses that are rendered by web chat.
 * When a user defined response message is received by web chat, it will fire a "userDefinedResponse" event that
 * provides an HTML element to which your application can attach user defined content. React portals are a mechanism
 * that allows you to render a component in your React application but attach that component to the HTML element
 * that was provided by web chat.
 *
 * This component will render a portal for each user defined response. The contents of that portal will be
 * determined by calling the provided "renderResponse" render prop.
 */
function UserDefinedResponsePortalsContainer({ chatInstance, renderUserDefinedResponse, userDefinedResponseEventsBySlot, }) {
    // All we need to do to enable the React portals is to render each portal somewhere in your application (it
    // doesn't really matter where).
    return (React__default.createElement(React__default.Fragment, null, renderUserDefinedResponse &&
        Object.entries(userDefinedResponseEventsBySlot).map(([slot, slotState]) => {
            const { element } = slotState;
            if (!element) {
                return null;
            }
            return (React__default.createElement(UserDefinedResponseComponentPortal, { key: slot, hostElement: element }, renderUserDefinedResponse(slotState, chatInstance)));
        })));
}
/**
 * This is the component that will attach a React portal to the given host element. The host element is the element
 * provided by web chat where your user defined response will be displayed in the DOM. This portal will attach any React
 * children passed to it under this component so you can render the response using your own React application. Those
 * children will be rendered under the given element where it lives in the DOM.
 */
function UserDefinedResponseComponentPortal({ hostElement, children, }) {
    return ReactDOM__default.createPortal(children, hostElement);
}
const UserDefinedResponsePortalsContainerExport = React__default.memo(UserDefinedResponsePortalsContainer);

/**
 * This is a utility component that is used to manage all the writeable elements that are rendered by web chat.
 * React portals are a mechanism that allows you to render a component in your React application but attach that
 * component to the HTML element that was provided by web chat.
 *
 * This component will render a portal for each user defined response. The contents of that portal will be
 * determined by calling the provided "renderResponse" render prop.
 */
function WriteableElementsPortalsContainer({ chatInstance, renderResponseMap }) {
    // All we need to do to enable the React portals is to render each portal somewhere in your application (it
    // doesn't really matter where).
    return (React__default.createElement(React__default.Fragment, null, Object.keys(chatInstance.writeableElements).map((key) => {
        const responseItem = renderResponseMap[key];
        return responseItem ? (React__default.createElement(WriteableElementsComponentPortal, { key: key, hostElement: chatInstance.writeableElements[key] }, responseItem)) : null;
    })));
}
/**
 * This is the component that will attach a React portal to the given host element. The host element is the element
 * provided by web chat where your user defined response will be displayed in the DOM. This portal will attach any React
 * children passed to it under this component so you can render the response using your own React application. Those
 * children will be rendered under the given element where it lives in the DOM.
 */
function WriteableElementsComponentPortal({ hostElement, children, }) {
    return ReactDOM__default.createPortal(children, hostElement);
}
const WriteableElementsPortalsContainerExport = React__default.memo(WriteableElementsPortalsContainer);

function AppContainer({ config, onBeforeRender, onAfterRender, renderUserDefinedResponse, renderWriteableElements, container, setParentInstance, }) {
    // A state value that contains the current instance of web chat.
    const [instance, setInstance] = useState(null);
    const [renderProps, setRenderProps] = useState(null);
    const [applicationStyles, setApplicationStyles] = useState(null);
    /**
     * In the ChatContainer React application, we need to know if instance has been nulled out because the chat
     * was destroyed to be able to re-create the container element that was destroyed.
     */
    const setInstances = (instance) => {
        setInstance(instance);
        setParentInstance?.(instance);
    };
    // This state will be used to record all the user defined response events that are fired from the widget. These
    // events contain the HTML elements that we will attach our portals to as well as the messages that we wish to
    // render in the message.
    const [userDefinedResponseEventsBySlot, setUserDefinedResponseEventsBySlot] = useState({});
    // The most recent web chat that was load by this component.
    const managedWebChatRef = useRef(null);
    // The previous web chat config.
    const previousConfigRef = useRef(null);
    useEffect(() => {
        const previousConfig = previousConfigRef.current;
        previousConfigRef.current = config;
        async function renderOverride({ serviceManager }) {
            // For the npm package, we currently assume Carbon fonts are loaded on the page already.
            // This might have to change if we allow white labeling some day.
            const applicationStyles = await loadStyles();
            serviceManager.container = container;
            if (serviceManager.customHostElement) {
                // Set container to grow to size of provided element. We don't do this sooner because "body" might be set to
                // display: flex;
                container.style.setProperty('width', '100%', 'important');
                container.style.setProperty('height', '100%', 'important');
            }
            else {
                // We want to make sure it doesn't interfere with the body by covering anything up so we'll set it to a 0 size.
                // The child elements use position: fixed along with a size that break out of the container.
                container.style.setProperty('width', '0', 'important');
                container.style.setProperty('height', '0', 'important');
            }
            // Make sure that the renderProps are set before we resolve the render function to make sure the component is
            // actually rendered on time.
            setApplicationStyles(applicationStyles);
            setRenderProps({
                serviceManager,
            });
            await sleep(0);
        }
        // isEqual performs a deep check, but for elements only checks the reference.
        if (!isEqual$1(previousConfig, config)) {
            // We'll use this managed object to keep track of the web chat instance we are creating for this effect.
            const managedWebChat = {
                instance: null,
                shouldDestroy: false,
                config,
            };
            if (config) {
                loadChat({
                    managedWebChatRef,
                    managedWebChat,
                    renderOverride,
                    setInstance: setInstances,
                    onBeforeRender,
                    onAfterRender,
                    setUserDefinedResponseEventsBySlot,
                });
            }
            return () => {
                destroyWebChat(managedWebChat, setInstances);
                previousConfigRef.current = null;
            };
        }
        return undefined;
        // We purposely exclude onBeforeRender and onAfterRender here to prevent unneeded tear downs.
        // We will only actually re-render if the config changes, so those two functions changing doesn't bother us.
        // If there is a new config, we will pick up the new onBeforeRender and onAfterRender anyway.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [config, container]);
    if (renderProps && instance) {
        return (React__default.createElement(React__default.Fragment, null,
            React__default.createElement(App, { serviceManager: renderProps.serviceManager, hostElement: renderProps.serviceManager.customHostElement, applicationStyles: applicationStyles }),
            renderUserDefinedResponse && (React__default.createElement(UserDefinedResponsePortalsContainerExport, { chatInstance: instance, renderUserDefinedResponse: renderUserDefinedResponse, userDefinedResponseEventsBySlot: userDefinedResponseEventsBySlot })),
            renderWriteableElements && (React__default.createElement(WriteableElementsPortalsContainerExport, { chatInstance: instance, renderResponseMap: renderWriteableElements }))));
    }
    return null;
}
/**
 * Destroys an instance of web chat and marks it destroyed.
 */
async function destroyWebChat(managedWebChat, setInstance) {
    if (managedWebChat) {
        if (managedWebChat.instance) {
            managedWebChat.instance.destroy();
            await sleep(0);
        }
        managedWebChat.shouldDestroy = true;
        managedWebChat.instance = null;
    }
    // Some cloak and daggers because setInstance isn't an async function and we want to make sure that in the case of
    // the React Chat Container that the clearing of the instance has been registered and a new container element
    // created to be used.
    setInstance(null);
    await sleep(0);
}
/**
 * Adds a "userDefinedResponse" event listener to the given web chat instance that will use the given set function
 * to add new events to the list.
 */
function addUserDefinedResponseHandler(webChatInstance, setUserDefinedResponseEventsBySlot) {
    /**
     * This handler will fire each time a user defined response occurs and we will update our state by appending the
     * event to the end of our events list. We have to make sure to create a new array in order to trigger a re-render.
     */
    function userDefinedResponseHandler(event) {
        setUserDefinedResponseEventsBySlot(userDefinedEventsBySlot => {
            return {
                ...userDefinedEventsBySlot,
                [event.data.slot]: {
                    fullMessage: event.data.fullMessage,
                    messageItem: event.data.message,
                    element: event.data.element,
                },
            };
        });
    }
    /**
     * This handler will fire each time a user defined response occurs and we will update our state by appending the event to the end of our events list.
     * We have to make sure to create a new array in order to trigger a re-render.
     * We use a map here to make sure there is only one event per slot name (taking the latest).
     */
    function userDefinedChunkHandler(event) {
        if ('complete_item' in event.data.chunk) {
            const messageItem = event.data.chunk.complete_item;
            setUserDefinedResponseEventsBySlot(userDefinedEventsBySlot => {
                return {
                    ...userDefinedEventsBySlot,
                    [event.data.slot]: {
                        messageItem,
                        element: event.data.element,
                        // We blow away partial items here so we don't have to track them forever for no reason.
                    },
                };
            });
        }
        else if ('partial_item' in event.data.chunk) {
            const itemChunk = event.data.chunk.partial_item;
            setUserDefinedResponseEventsBySlot(userDefinedEventsBySlot => {
                return {
                    ...userDefinedEventsBySlot,
                    [event.data.slot]: {
                        partialItems: [...(userDefinedEventsBySlot[event.data.slot]?.partialItems || []), itemChunk],
                        element: event.data.element,
                    },
                };
            });
        }
    }
    // Also make sure to clear the list if a restart occurs.
    function restartHandler() {
        setUserDefinedResponseEventsBySlot({});
    }
    webChatInstance.on({ type: "chunk:userDefinedResponse" /* BusEventType.CHUNK_USER_DEFINED_RESPONSE */, handler: userDefinedChunkHandler });
    webChatInstance.on({ type: "userDefinedResponse" /* BusEventType.USER_DEFINED_RESPONSE */, handler: userDefinedResponseHandler });
    webChatInstance.on({ type: "restartConversation" /* BusEventType.RESTART_CONVERSATION */, handler: restartHandler });
}
async function loadChat({ managedWebChatRef, managedWebChat, renderOverride, setInstance, onBeforeRender, onAfterRender, setUserDefinedResponseEventsBySlot, }) {
    // Each time the web chat config settings change (or this component is mounted), we need to destroy any previous
    // web chat and create a new web chat.
    // First look at the old ref and destroy it if its supposed to be destroyed.
    await destroyWebChat(managedWebChatRef.current, setInstance);
    // Update the ref to the new managedWebChat.
    managedWebChatRef.current = managedWebChat;
    // Just double check that the new one also isn't supposed to be destroyed.
    if (managedWebChat.shouldDestroy) {
        await destroyWebChat(managedWebChat, setInstance);
        return;
    }
    // Create web chat here.
    const widget = await instantiateWidget(managedWebChat.config, undefined, // No need to pass in a fetch since its bring your own back-end.
    () => Promise.resolve(Chat$2), undefined, true, Promise.resolve(renderOverride));
    const instance = await widget.start();
    addUserDefinedResponseHandler(instance, setUserDefinedResponseEventsBySlot);
    onBeforeRender?.(instance);
    await instance.render();
    onAfterRender?.(instance);
    setInstance(instance);
    managedWebChat.instance = instance;
    if (managedWebChat.shouldDestroy) {
        await destroyWebChat(managedWebChat, setInstance);
    }
}
const AppContainerExport = React__default.memo(AppContainer);

var AppContainer$1 = /*#__PURE__*/Object.freeze({
  __proto__: null,
  AppContainer: AppContainerExport
});

export { doFocusRef as $, AppContainerExport as A, BuiltInServiceDesk as B, setIsReconnecting as C, setShowScreenShareRequest as D, createAgentLocalMessage as E, updateCapabilities as F, setAgentAvailability as G, setAgentJoined as H, agentUpdateIsTyping as I, createMessageResponseForText as J, addDefaultsToMessage as K, outputItemToLocalItem as L, MessageErrorState as M, setAgentLeftChat as N, resolvablePromise as O, merge as P, setPersistedServiceDeskState as Q, sleep as R, assertType as S, getDefaultExportFromCjs$1 as T, dayjs_minExports as U, VERSION as V, commonjsGlobal$1 as W, useLanguagePack as X, cx as Y, ButtonSizeEnum as Z, isEnterKey as _, loadStylesWithReset as a, useServiceManager as a0, useCounter as a1, useWindowSize as a2, IS_JEST as a3, parseTextDirective as a4, ModalPortal as a5, FocusTrap$1 as a6, MountChildrenOnDelay as a7, ChatWidthBreakpoint as a8, ButtonKindEnum as a9, WA_CONSOLE_PREFIX as aa, isConnectToAgent as ab, isEmptyObject as ac, isEnableDebugLog as ad, mergeNoArrays as ae, getSummaryMessages as af, asyncForEach as ag, safeFetchText as ah, pushAll as ai, StatusCodes as aj, ANONYMOUS_USER_ID_PREFIX as ak, uuid as al, UUIDType as am, loadScript as an, AppContainer$1 as ao, App as b, consoleWarn as c, getCDNURL as d, cloneDeep as e, debugLog as f, getVersionLoaded as g, getRegionHostname as h, isEqual$1 as i, consoleError as j, addAgentEndChatMessage as k, loadStyles as l, endChat as m, addBotReturnMessage as n, deepFreeze$1 as o, createMessageRequestForText as p, inputItemToLocalItem as q, resolveOrTimeout as r, setIsConnecting as s, createMessageRequestForFileUpload as t, updateFilesUploadInProgress as u, addMessages as v, actions as w, createLocalMessageForInlineError as x, toPair as y, setIsScreenSharing as z };
